// Translation dictionary — 12 languages, all UI strings the platform
// renders for the player live here. Keep keys short, dotted by section
// (login.*, register.*, home.*) so a glance tells you where they're
// used.
//
// HOW TO ADD A LANGUAGE:
//   1. Add the code to SUPPORTED_LANGS below.
//   2. Add the same code to LANGUAGE_CODES in
//      backend/src/core/controllers/me.controller.js (server-side
//      whitelist), otherwise PATCH /me will reject it.
//   3. Add an entry below with the same keys translated. Missing keys
//      fall back to English at runtime, so you can ship partial
//      coverage and fill in over time.
//
// HOW TO ADD A STRING:
//   Add the key to `en` first (the source of truth), then to every
//   other language. Anything not yet translated gracefully shows in
//   English instead of crashing.

export type Lang =
  | 'en' | 'hi' | 'bn' | 'mr' | 'ta' | 'te'
  | 'gu' | 'ur' | 'kn' | 'or' | 'pa' | 'ml';

export type LangMeta = {
  code: Lang;
  /** Native name — what the user reads in the picker. */
  native: string;
  /** English name — for fallback / a11y. */
  english: string;
};

// 12 languages: English + the 11 most-spoken languages of India.
export const SUPPORTED_LANGS: readonly LangMeta[] = [
  { code: 'en', native: 'English',   english: 'English'   },
  { code: 'hi', native: 'हिन्दी',      english: 'Hindi'     },
  { code: 'bn', native: 'বাংলা',     english: 'Bengali'   },
  { code: 'mr', native: 'मराठी',     english: 'Marathi'   },
  { code: 'ta', native: 'தமிழ்',     english: 'Tamil'     },
  { code: 'te', native: 'తెలుగు',     english: 'Telugu'    },
  { code: 'gu', native: 'ગુજરાતી',   english: 'Gujarati'  },
  { code: 'ur', native: 'اردو',      english: 'Urdu'      },
  { code: 'kn', native: 'ಕನ್ನಡ',     english: 'Kannada'   },
  { code: 'or', native: 'ଓଡ଼ିଆ',      english: 'Odia'      },
  { code: 'pa', native: 'ਪੰਜਾਬੀ',     english: 'Punjabi'   },
  { code: 'ml', native: 'മലയാളം',    english: 'Malayalam' },
] as const;

export const DEFAULT_LANG: Lang = 'en';

type Dict = Record<string, string>;

const en: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "My details",
  'profile.group.security': "Sign-in & security",
  'profile.personal.title': "Personal details",
  'profile.personal.editTitle': "Edit personal details",
  'common.edit': "Edit",
  'profile.field.name': "Name",
  'profile.field.phone': "Phone number",
  'profile.field.gender': "Gender",
  'profile.field.location': "Location",
  'profile.gender.male': "Male",
  'profile.gender.female': "Female",
  'profile.gender.other': "Other",
  'profile.gender.preferNot': "Prefer not to say",
  'profile.gender.select': "Select…",
  'profile.location.optionalHint': "Optional — pick your area or type your pincode (GPS makes it more accurate).",
  'profile.lang.title': "Language",
  'profile.password.title': "Password",
  'profile.password.desc': "Change your account password. Other devices will be signed out.",
  'profile.password.cta': "Change",
  'profile.password.editTitle': "Change password",
  'profile.password.current': "Current password",
  'profile.password.new': "New password",
  'profile.password.confirm': "Confirm new password",
  'profile.password.currentPh': "Your current password",
  'profile.password.newPh': "Choose a strong password",
  'profile.password.confirmPh': "Type the new password again",
  'profile.password.rule': "At least 8 characters with a letter and a digit.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "Linked as",
  'profile.telegram.otpHere': "Password-reset OTPs arrive here.",
  'profile.telegram.unlinkChange': "Unlink & change",
  'profile.telegram.unlinkTitle': "Unlink Telegram?",
  'profile.telegram.unlink': "Unlink",
  'profile.telegram.keepLinked': "Keep linked",
  'profile.telegram.unlinkVerified': "You can re-link a different Telegram account afterwards. Email verification stays intact.",
  'profile.telegram.unlinkOnly': "Telegram is your only verified channel — you may need to re-verify your account before continuing to play.",
  'profile.telegram.notLinkedDesc': "Link Telegram to receive password-reset OTPs as a backup.",
  'profile.telegram.openBot': "Open @BaaliGames_bot",
  'profile.telegram.generate': "Generate link",
  'profile.telegram.generating': "Generating…",
  'profile.avatar.cropTitle': "Adjust your photo",
  'profile.toast.detailsSaved': "Details saved",
  'profile.toast.photoUpdated': "Photo updated",
  'profile.toast.pwChanged': "Password changed. Other devices have been signed out.",
  'profile.toast.tgUnlinked': "Telegram unlinked. Generate a new link to connect a different chat.",
  'profile.err.phone': "Enter a 10-digit mobile number (or leave it unchanged)",
  'profile.err.pwMismatch': "New passwords do not match",
  'profile.err.pwWeak': "Password must be 8+ chars with a letter and a digit",
  'profile.err.pwSame': "New password must be different from current",
  'wallet.payout.title': "Saved payout details",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "None saved yet",
  'wallet.payout.hint': "Your UPI ID is saved automatically when you make a withdrawal. You can update it during your next withdrawal request.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "Install {app}",
  "pwa.install.subhint": "Add it to your home screen for instant, full-screen access — no app store needed.",
  "pwa.install.listen": "Listen",
  "pwa.install.listening": "Stop",
  "pwa.install.installBtn": "Install",
  "pwa.install.notNow": "Not now",
  "pwa.install.howToShow": "How to install",
  "pwa.install.howToHide": "Hide steps",
  "pwa.install.guide.iosSafari": "At the bottom of the Safari screen, tap the Share button — it looks like a square with an arrow pointing up.\nIn the menu that opens, slide up to scroll down a little.\nTap the option that says Add to Home Screen.\nTap Add in the top-right corner. The app will now sit on your home screen like any other app.",
  "pwa.install.guide.iosOther": "On an iPhone or iPad, adding to the home screen only works in the Safari browser.\nOpen Safari and go to this same website.\nAt the bottom of Safari, tap the Share button — a square with an arrow pointing up.\nScroll down, tap Add to Home Screen, then tap Add.",
  "pwa.install.guide.android": "Tap the Install button above.\nWhen your browser asks, tap Install to confirm.\nDone — the app is now on your home screen. Open it anytime, just like a normal app.\nDon't see a pop-up? Open your browser menu — the three dots, usually at the top-right — and tap Install app.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "Direct recharge",
  "agent.directRecharge.subtitle.player": "Pick a player and recharge from your pool. No request needed.",
  "agent.directRecharge.subtitle.subagent": "Pick a sub-agent and recharge their pool from yours.",
  "agent.directRecharge.search.player": "Search a player to recharge (name · phone · email)…",
  "agent.directRecharge.search.subagent": "Search a sub-agent to recharge (name · email · UAID)…",
  "agent.directRecharge.clearSearch": "Clear search",
  "agent.directRecharge.searching": "Searching…",
  "agent.directRecharge.noMatches": "No matches.",
  "agent.directRecharge.empty.player": "No players in your roster yet.",
  "agent.directRecharge.empty.subagent": "No direct sub-agents yet.",
  "agent.directRecharge.searchFailed": "Search failed",
  "agent.directRecharge.label.wallet": "wallet",
  "agent.directRecharge.label.pool": "pool",
  "agent.directRecharge.modal.title.player": "Direct player recharge",
  "agent.directRecharge.modal.title.subagent": "Direct sub-agent recharge",
  "agent.directRecharge.modal.close": "Close",
  "agent.directRecharge.modal.targetPool": "Target pool",
  "agent.directRecharge.modal.operatingWallet": "Operating wallet",
  "agent.directRecharge.modal.dayToDay": "Day-to-day balance",
  "agent.directRecharge.modal.securityDeposit": "Security deposit",
  "agent.directRecharge.modal.collateral": "Collateral pool",
  "agent.directRecharge.modal.amount": "Amount (₹)",
  "agent.directRecharge.modal.overdraw": "You can't pay more than your pool (₹{pool}). Lower the amount.",
  "agent.directRecharge.modal.reason": "Reason / note",
  "agent.directRecharge.modal.reasonPlaceholder": "Cash collected · ref 4291",
  "agent.directRecharge.modal.reasonHint": "Written to your wallet ledger + audit log.",
  "agent.directRecharge.modal.proofLabel": "Upload screenshot of the payment (optional)",
  "agent.directRecharge.modal.proofHint": "Visible to the recipient via History.",
  "agent.directRecharge.modal.previewEmpty": "Enter an amount to preview the move.",
  "agent.directRecharge.modal.previewBody": "sent from your wallet to {name}'s {pool}. Secure transfer; recorded in ledger + audit.",
  "agent.directRecharge.modal.pool.playerWallet": "wallet",
  "agent.directRecharge.modal.pool.operatingWallet": "operating wallet",
  "agent.directRecharge.modal.pool.securityDeposit": "security deposit",
  "agent.directRecharge.modal.cancel": "Cancel",
  "agent.directRecharge.modal.send": "Send ₹{amount}",
  "agent.directRecharge.badge.player": "Player",
  "agent.directRecharge.badge.subagent": "Sub-agent",
  "agent.directRecharge.modal.yourDirectSubagent": "your direct sub-agent",
  "agent.directRecharge.modal.fundingSource": "Funding source",
  "agent.directRecharge.modal.you": "(you)",
  "agent.directRecharge.modal.yourPool": "your pool",
  "agent.directRecharge.modal.notSignedIn": "Not signed in as agent",
  "agent.directRecharge.modal.failed": "Failed",
  // Common controls
  'common.signIn': 'Sign in',
  'common.signUp': 'Sign up',
  'common.login': 'Login',
  'common.logout': 'Log out',
  'common.continue': 'Continue',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.update': 'Update',
  'common.clear': 'Clear',
  'common.back': 'Back',
  'common.loading': 'Loading…',
  'common.sending': 'Sending…',
  'common.saving': 'Saving…',
  'common.optional': 'optional',
  'common.show': 'Show',
  'common.hide': 'Hide',
  'common.profile': 'Profile',
  'common.settings': 'Settings',

  // NavBar / BottomNav
  'nav.brand': 'Baali Games',
  'nav.home': 'Home',
  'nav.games': 'Games',
  'nav.wallet': 'Wallet',
  'nav.rewards': 'Rewards',
  'nav.profile': 'Profile',

  // Login page
  'login.title': 'Welcome back',
  'login.subtitle': 'Sign in to continue your run.',
  'login.idLabel': 'Username, email or phone',
  'login.idPlaceholder': 'Username, email or mobile',
  'login.passwordLabel': 'Password',
  'login.passwordPlaceholder': 'Enter your password',
  'login.forgot': 'Forgot?',
  'login.cta': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.newHere': 'New here?',
  'login.createAccount': 'Create account',

  // Register page
  'register.title': 'Create your account',
  'register.subtitle': "Pick a tag and you're in.",
  'register.usernameLabel': 'Username',
  'register.usernamePlaceholder': 'Pick a unique handle',
  'register.usernameHelp': 'Letters, numbers, and underscores. 3–24 characters.',
  'register.emailLabel': 'Email',
  'register.emailPlaceholder': 'Your email address',
  'register.mobileLabel': 'Mobile number',
  'register.mobilePlaceholder': '10-digit mobile',
  'register.mobileHelp': "We'll add the country code automatically.",
  'register.passwordLabel': 'Password',
  'register.passwordPlaceholder': 'Choose a strong password',
  'register.passwordHelp': 'At least 8 characters with a letter and a digit.',
  'register.referralLabel': 'Referral code',
  'register.referralPlaceholder': "Friend's invite code",
  'register.cta': 'Create account',
  'register.creating': 'Creating…',
  'register.alreadyPlayer': 'Already a player?',
  // Phase 22 (2026-05-21) — Signup wizard rewrite.
  'register.step.about':  'About you',
  'register.step.signin': 'Sign-in',
  'register.step.finish': 'Finish',
  'register.step.aboutSubtitle':  'Tell us a little about yourself',
  'register.step.signinSubtitle': 'Set up how you\'ll sign in',
  'register.step.finishSubtitle': 'Pick a location and finish up',
  'register.firstName':   'First name',
  'register.firstNamePh': 'Aarav',
  'register.lastName':    'Last name',
  'register.lastNamePh':  'Sharma',
  'register.dob':         'Date of birth',
  'register.dobHelp':     'You must be 18 or older to play',
  'register.v.required':       'Required',
  'register.v.nameMin':        'At least 2 letters',
  'register.v.tooLong':        'Too long',
  'register.v.nameChars':      'Letters, spaces, hyphens only',
  'register.v.invalidDate':    'Invalid date',
  'register.v.minAge':         'You must be at least 18',
  'register.v.tooOld':         'Please check the year',
  'register.v.usernameMin':    'At least 3 characters',
  'register.v.usernameMax':    'Too long (max 24)',
  'register.v.usernameChars':  'Letters, numbers, underscores only',
  'register.v.emailShape':     'Looks like an invalid email',
  'register.v.phoneDigits':    'Must be a 10-digit number',
  'register.v.phonePrefix':    'Indian mobile starts with 6–9',
  'register.v.pwdMin':         'At least 8 characters',
  'register.v.pwdLetter':      'Must contain at least one letter',
  'register.v.pwdNumber':      'Must contain at least one number',
  'register.v.referralShort':  'Looks too short',
  'register.v.referralChars':  'Letters and digits only',
  'register.v.usernameTaken':  'This username is already taken',
  'register.v.emailTaken':     'This email is already registered',
  'register.v.phoneTaken':     'This number is already registered',
  'register.v.noLocation':     'Please share at least one location method to continue.',
  'register.welcome.title':         'Welcome aboard',
  'register.welcome.subtitle':      'Your account is ready. Secure it now or jump straight in?',
  'register.welcome.verifyTelegram':'Verify via Telegram',
  'register.welcome.verifyExplain': '⚠ Important: verify via Telegram now. Without verification you CANNOT reset your password yourself if you forget it — only your agent or admin will be able to reset it for you. You can also verify later from your profile.',
  'register.welcome.verifyHeads': 'Save this for later',
  'register.welcome.verifyConsequence': "Email verification isn't live right now. Verify via Telegram to enable password reset later. If you skip it, only your agent or admin can reset your password if you forget it.",
  'register.welcome.continue':      'Continue to the app',
  'register.welcome.continueLogin': 'Sign in to continue',
  'register.welcome.useDifferent':  'Use a different account',
  'register.geo.title':         'Where are you playing from?',
  'register.geo.subtitle':      'We use your location to pair you with the nearest agent.',
  'register.geo.showAlt':       "Can't share location? Use a different method",
  'register.geo.hideAlt':       'Hide other methods',
  'register.geo.captured':      'Location captured',
  'register.geo.capturedHint':  'You can update this anytime from your profile.',
  'register.geo.verifyNote':    'After signup, verify your account anytime via Telegram (powers password reset) or email from your profile. Verification is optional.',

  // Verify (post-signup)
  'verify.title': 'Verify your account',
  'verify.subtitle': "Pick one option — that's all it takes.",
  'verify.tabTelegram': 'Telegram',
  'verify.tabEmail': 'Email',
  'verify.emailSent': 'We sent a verification link to {email}.',
  'verify.emailHint': "Open it on this device. Don't see it? Check spam / promotions.",
  'verify.telegramOpen': 'Open @BaaliGames_bot',
  'verify.telegramExpiry': 'Link expires in 30 minutes. Telegram requires you to press Start once.',
  'verify.goToLogin': 'Go to login',
  'verify.email.title': 'Email verified',
  'verify.email.sub': 'Your account is active. Time to drop in.',
  'verify.email.failed': 'Verification failed',
  'verify.email.verifying': 'Verifying…',
  'verify.backRegister': 'Back to register',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'Resend email',
  'verify.gate.resent': 'Sent!',
  'verify.gate.checking': 'Working…',
  'verify.gate.tgFallback': 'Open link manually',
  'verify.gate.waiting': 'We will detect verification automatically.',
  'verify.gate.signOut': 'Sign out',

  // Forgot password
  'forgot.title': 'Recover your account',
  'forgot.subtitle': "We'll send you a way back in.",
  'forgot.idLabel': 'Username, email or phone',
  'forgot.idPlaceholder': 'Username, email or mobile',
  'forgot.sendVia': 'Send via',
  'forgot.email': 'Email',
  'forgot.emailUnavailable': 'Email delivery is unavailable right now — use Telegram',
  'forgot.telegram': 'Telegram OTP',
  'forgot.telegramHelp': "Telegram only works if your account is linked to our bot.",
  'forgot.cta': 'Send recovery',
  'forgot.sentEmail': 'If the account exists, a reset link has been emailed.',
  'forgot.sentTelegram': 'If the account exists, a 6-digit code has been sent via Telegram.',
  'forgot.redirecting': 'Redirecting to the reset page…',
  'forgot.back': 'Back to login',

  // Reset password
  'reset.title': 'Set a new password',
  'reset.subOtp': 'Use the code we sent you on Telegram.',
  'reset.subLink': 'Pick a fresh combo and step right back in.',
  'reset.otpLabel': '6-digit code',
  'reset.newPasswordLabel': 'New password',
  'reset.cta': 'Update password',
  'reset.updating': 'Updating…',
  'reset.successTitle': 'Password updated',
  'reset.successSub': "You're set. Time to play.",

  // Home (logged-out)
  'home.welcomeTo': 'Welcome to',
  'home.heroSub': 'A skill-based gaming hub. Sign in or create your player account to drop in.',
  'home.skillBased': 'Skill-based',
  'home.skillBasedSub': 'Strategy wins',
  'home.fairPlay': 'Fair play',
  'home.fairPlaySub': 'Anti-cheat ON',
  'home.instantPayout': 'UPI payouts',
  'home.instantPayoutSub': 'Within 24 hrs',
  'home.featured': 'Featured Games',
  'home.tapToPlay': 'Tap to play',
  'home.ready': 'Ready to play?',
  'home.dropIn': 'Drop in and win real rewards.',
  'home.start': 'Start',
  // Public homepage CTA button on the "Live rewards" sign-up card. Was
  // rendering as the raw key 'HOME.CTA.SIGNUP' before because no
  // English fallback existed for it — the i18n shim returns the key
  // string when the dict lookup misses.
  'home.cta.signup': 'Sign up',

  // Home (logged-in)
  'home.welcomeBack': 'Welcome back',
  'home.balance': 'Your balance',
  'home.live': 'Live',
  'home.add': 'Add',
  'home.manage': 'Manage',
  'home.rewards': 'Rewards',
  'home.todayChallenge': "Today's challenge",
  'home.todayChallengeSub': 'Win a match — earn a streak bonus.',
  'home.view': 'View',
  'home.yourGames': 'Your Games',
  'home.topGames': 'Top Games',
  'home.resultOriented': 'Result Oriented',
  'home.comingSoon': 'Coming soon',
  'home.viewAll': 'View all',
  'home.noMatches': 'No matches right now',
  'home.referEarn': 'Refer & Earn',
  'home.refer': 'Refer',
  'home.perFriend': 'Invite friends',
  'home.cashback': 'Cashback',
  'home.everyBet': 'On every bet',
  'home.offersLive': '{count} live',
  'home.noResultsYet': 'No results yet',

  // Profile tabs
  'profile.details': 'Details',
  'profile.address': 'Address',
  'profile.languages': 'Languages',
  'profile.account': 'Account',
  'profile.photo': 'Photo',
  'profile.preferred': 'Preferred language',

  // Profile → Languages tab
  'profile.lang.primary': 'Primary language',
  'profile.lang.primarySub': 'App-wide default',
  'profile.lang.locked': 'Locked',
  'profile.lang.second': 'Second language',
  'profile.lang.pickHelp': 'Pick the language you also speak. Tap a card to select.',
  'profile.lang.saved': 'Secondary language saved',
  'profile.lang.cleared': 'Secondary language cleared',

  // Language switcher
  'lang.switcher.aria': 'Choose language',
  'lang.switcher.title': 'Choose your language',
  'lang.switcher.sub': 'The whole app updates instantly.',
  'lang.switcher.current': 'Current',
  'lang.savePermanent': 'Save as my language',
  'lang.savedPermanent': 'Saved. This is now your language.',

  // Rewards page
  'rewards.title': 'Rewards & Offers',
  'rewards.subtitle': 'Bonuses you can earn or claim right now.',
  'rewards.tab.available': 'Available',
  'rewards.tab.inReview': 'In Review',
  'rewards.tab.claimed': 'Claimed',
  'rewards.empty.available.title': 'No offers right now',
  'rewards.empty.available.body': 'Check back soon — the admin posts new offers regularly.',
  'rewards.empty.review.title': 'Nothing in review',
  'rewards.empty.review.body': 'Claims that need admin approval appear here.',
  'rewards.empty.claimed.title': 'No claims yet',
  'rewards.empty.claimed.body': 'Once you earn a bonus, it shows up here as a claim.',
  'rewards.expires': 'Ends {date}',
  'rewards.remaining': '{amount} left',
  'rewards.cap': 'cap',
  'rewards.claim.cta': 'Claim',
  'rewards.claim.submit': 'Submit',
  'rewards.claimed.credited': 'Claimed {amount} on {date}',
  'rewards.claimed.pending': 'Pending review',
  'rewards.claimed.rejected': 'Rejected',
  'rewards.auto.willCredit': 'Auto-credits when conditions are met',
  'rewards.auto.willReview': 'Lands in admin review queue',
  'rewards.row.creditedOn': 'Credited on {date}',
  'rewards.row.submittedOn': 'Submitted on {date}',
  'rewards.toast.credited': 'Bonus credited — {amount} added to your wallet',
  'rewards.toast.submitted': 'Submitted for review',
  'rewards.toast.alreadyClaimed': 'Already claimed',
  'rewards.toast.rejected': 'Claim was rejected',

  // Wallet page
  'wallet.balance': 'Balance',
  'wallet.balance.note': 'Entries debit instantly; prizes credit on match end.',
  'wallet.addMoney': 'Add money',
  'wallet.withdraw': 'Withdraw',
  'wallet.withdraw.minBalance': 'Need at least ₹200 to withdraw',
  'wallet.history.byGame': 'Your history by game',
  'wallet.history.empty': 'No activity yet. Your first match will appear here.',
  'wallet.activity': 'Recent activity',
  'wallet.activity.empty': 'No transactions yet.',
  'wallet.net': 'Net',
  'wallet.newer': 'Newer',
  'wallet.older': 'Older',
  'wallet.showOlder': 'Show older transactions',
  'wallet.pending.title': 'Pending requests',
  'wallet.pending.recharge': 'Recharge of {amount} — awaiting admin approval',
  'wallet.pending.withdrawal': 'Withdrawal of {amount} — awaiting payout',
  'wallet.state.pending': 'Pending',
  'wallet.state.paid': 'Paid',
  // Phase 22 (2026-05-21) — i18n sweep across Wallet + proof widgets.
  // Every string a player ever sees on the Wallet page or in a payment
  // proof picker/viewer now flows through these keys so the page stays
  // coherent when language flips. The fallback chain in i18n.tsx is
  // dict[key] → English[key] → raw key, so languages that haven't
  // translated a particular key fall back to readable English rather
  // than the bare key identifier.
  'wallet.pending.txnPrefix': 'txn:',
  'wallet.pending.upload.label': 'Upload screenshot of the payment for fast action.',
  'wallet.pending.upload.hint':  'You have 24 hours after creating the request.',
  'wallet.pending.upload.failed': 'Upload failed',
  'wallet.activity.range': '{from}–{to} of {total}',
  'wallet.activity.balanceAfter': 'bal {balance}',
  'wallet.activity.window': 'Showing your last {days} days of activity',
  'wallet.history.summary.one':   '{matches} match · Won {wins} · Spent {bets}',
  'wallet.history.summary.other': '{matches} matches · Won {wins} · Spent {bets}',
  'wallet.history.summary.refunded': ' · Refunded {refunds}',
  // Ledger row labels — keys mirror the KIND column on the server.
  'ledger.kind.bet':                'Match entry',
  'ledger.kind.win':                'Match prize',
  'ledger.kind.refund':             'Match refund',
  'ledger.kind.forfeit':            'Match forfeit',
  'ledger.kind.fee':                'Platform fee',
  'ledger.kind.recharge':           'Top-up',
  'ledger.kind.admin_credit':       'Admin credit',
  'ledger.kind.admin_debit':        'Admin debit',
  'ledger.kind.referral_bonus':     'Referral bonus',
  'ledger.kind.signup_bonus':       'Sign-up bonus',
  'ledger.kind.offer_credit':       'Offer reward',
  'ledger.kind.withdrawal_hold':    'Withdrawal hold',
  'ledger.kind.withdrawal_refund':  'Withdrawal refund',
  'ledger.kind.agent_topup_in':     'Agent top-up',
  'ledger.kind.agent_co_fund':      'Agent contribution',
  'ledger.kind.turnover_reward':    'Cashback',
  // Withdrawal-hold row swaps once the parent request is terminal.
  'ledger.withdrawalPaid':          'Withdrawal paid',
  'ledger.withdrawalPaidPartial':   'Withdrawal paid (partial)',
  'ledger.withdrawalPaid.txn':      'Paid · txn {txnId}',
  'ledger.withdrawalRejected':      'Withdrawal rejected',
  'ledger.withdrawalRejected.note': 'Held balance refunded',
  'ledger.withdrawalExpired':       'Withdrawal expired',
  'ledger.awaitingProof':           'Awaiting proof',
  // Proof image picker (used by recharge proof upload + future flows).
  'proofPicker.button.compact':     'Choose screenshot',
  'proofPicker.button.prominent':   'Add Screenshot',
  'proofPicker.processing':         'Processing…',
  'proofPicker.error.mime':         'Only JPG, PNG, or WebP images are allowed.',
  'proofPicker.error.size':         'File too large — max 5 MB.',
  'proofPicker.error.processing':   'Could not process image',
  'proofPicker.previewAlt':         'Selected screenshot preview',
  // Proof image viewer (gated lightbox for payment proof).
  'proofViewer.state.none':         'Awaiting screenshot',
  'proofViewer.state.available':    'View screenshot',
  'proofViewer.state.expired':      'Screenshot no longer available',
  'proofViewer.state.viewLimit':    'View limit reached',
  'proofViewer.loading':            'Loading…',
  'proofViewer.title.viewLimit':    'You opened this screenshot the maximum 2 times',
  'proofViewer.error.load':         'Could not load screenshot',
  'proofViewer.lastView':           'Last view — image will expire',
  'proofViewer.viewsRemaining.one':   '{n} view remaining',
  'proofViewer.viewsRemaining.other': '{n} views remaining',
  'proofViewer.imageAlt':           'Payment proof',
  'proofViewer.close':              'Close',

  // Recharge flow
  'recharge.title': 'Add money',
  'recharge.step1.subtitle': 'How much do you want to add?',
  'recharge.step1.amount': 'Amount',
  'recharge.step1.range': 'Min {min} · Max {max}',
  'recharge.step1.minHint': 'Min {min} recharge',
  'recharge.step1.maxHint': 'Max {max}',
  'recharge.step1.quickPick': 'Quick pick',
  'recharge.step2.subtitle': 'Pay using UPI, then continue',
  'recharge.step2.payAmount': 'Pay this amount',
  'recharge.step2.iPaid': "I've paid",
  'recharge.step2.howto.1': 'Open your UPI app (Google Pay / PhonePe / Paytm).',
  'recharge.step2.howto.2': 'Pay the amount above to the UPI ID or scan the QR.',
  'recharge.step2.howto.3': 'Copy the transaction ID from your UPI app — you\'ll need it next.',
  // Phase 21 (2026-05-21) — Step 2 UPI-app-required gating.
  'recharge.step2.payFirst.title':   'Pay first, then come back',
  'recharge.step2.payFirst.body':    'Pick a UPI app below to send the payment. Once you return, the confirm button will unlock.',
  'recharge.step2.waiting.title':    'Finish payment in your UPI app',
  'recharge.step2.waiting.body':     'Come back here when you\'re done. The confirm button will appear in a moment.',
  'recharge.step2.didYouPay.title':  'Payment done?',
  'recharge.step2.didYouPay.body':   'Tap the confirm button below if you finished the payment.',
  'recharge.step2.confirmLocked':    'Pay in a UPI app first',
  'recharge.step2.alreadyPaid':      "I've already paid",
  "recharge.locked.title": "Recharge temporarily unavailable",
  "recharge.locked.body": "We are working to resolve a problem with this recharge route. Your wallet and withdrawals are not affected. Please try again shortly.",
  "recharge.locked.reassure.withdrawals": "Withdrawals continue to work normally",
  "recharge.locked.reassure.existing": "Any payment you have already submitted is still being processed",
  "recharge.cap.banner": "Max recharge available right now: {max}. The recipient's capacity is temporarily limited.",
  "recharge.cap.tooHigh": "Amount exceeds the available capacity. The most you can recharge right now is {max}.",
  "recharge.notConfigured.title": "Recharge will be available soon",
  "recharge.notConfigured.body": "We are finalising setup for this recharge route. Your wallet and withdrawals are not affected. Please check back shortly.",
  'recharge.step3.subtitle': 'Confirm your payment',
  'recharge.step3.amountPaid': 'Amount you paid',
  'recharge.step3.txnId': 'UPI transaction ID',
  'recharge.step3.txnIdHelp': 'Find this in your UPI app under the payment receipt — usually 12 digits.',
  'recharge.step3.note': 'Note for admin',
  'recharge.step3.notePlaceholder': "e.g. paid from spouse's number",
  'recharge.step3.submit': 'Submit for review',
  'recharge.step3.disclaimer': 'Admin verifies your payment in the UPI app. Wallet is credited after approval — within 24 hrs (often sooner).',
  // Phase 21 (2026-05-21) — Step 3 screenshot-first redesign. The
  // screenshot upload card is the visually loudest element so even a
  // user who can't read the labels lands on the right action first.
  'recharge.step3.proof.firstStep':  'Do this first',
  'recharge.step3.proof.done':       'Done',
  'recharge.step3.proof.title':      'Upload payment screenshot',
  'recharge.step3.proof.subtitle':   'Fastest way to get approved — recommended for everyone.',
  'recharge.step3.proof.pickerLabel': 'Tap here to pick a screenshot from your phone',
  'recharge.step3.proof.btn':         'Add Screenshot',
  'recharge.step3.proof.flow.upi':    'Open UPI app',
  'recharge.step3.proof.flow.find':   'Find payment',
  'recharge.step3.proof.flow.snap':   'Screenshot',
  'recharge.step3.proof.flow.upload': 'Upload here',
  'recharge.step3.or':                'Or',
  'recharge.success.title': 'Submitted — awaiting review',
  'recharge.success.body': 'Your recharge of {amount} is in the admin queue. Reflected within 24 hrs (often sooner).',
  'recharge.success.refId': 'Reference',
  'recharge.toast.submitted': 'Recharge submitted for admin review',

  // Withdraw flow
  'withdraw.title': 'Withdraw',
  'withdraw.step1.subtitle': 'Where should we send the money?',
  'withdraw.step2.subtitle': 'One last check',
  'withdraw.balance': 'Available balance',
  'withdraw.amount': 'Amount',
  'withdraw.max': 'Max',
  'withdraw.upiId': 'Your UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': "We'll send the money here. Double-check before submitting.",
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'pre-filled from your last withdrawal',
  'withdraw.upiShapeErr': 'UPI ID must look like yourname@psp (e.g. lucky@oksbi)',
  'withdraw.phoneShapeErr': 'Enter a valid 10-digit Indian mobile (must start with 6, 7, 8 or 9)',
  'withdraw.whatsapp': 'WhatsApp number',
  'withdraw.errMin': 'Minimum withdrawal is {min}',
  'withdraw.errExceed': 'Amount exceeds your balance',
  'withdraw.minHint': 'Min {min} withdrawal',
  'withdraw.step2.review': 'Review',
  'withdraw.step2.heldNotice': 'When you submit, this amount is held immediately so it can\'t be spent elsewhere. Admin pays it out to your UPI ID and marks it paid.',
  'withdraw.heldAfter': 'Held',
  'withdraw.spendableAfter': 'Spendable',
  'withdraw.step2.disclaimer': 'If admin rejects, the held amount returns to your wallet.',
  'withdraw.submit': 'Submit',
  'withdraw.success.title': 'Submitted — awaiting payout',
  'withdraw.success.body': 'Your withdrawal of {amount} is queued. Paid within 24 hrs (often sooner). The held amount stays reserved until then.',
  'withdraw.success.refId': 'Reference',
  'withdraw.toast.submitted': 'Withdrawal queued — held in your wallet',

  // Payment receiver card
  'payment.upi.label': 'Pay to this UPI ID',
  'payment.upi.copy': 'Copy',
  'payment.upiCopied': 'UPI ID copied',
  'payment.openUpiApp': 'Pay now',
  'payment.openWhatsapp': 'WhatsApp',
  'payment.helpAt': 'Need help? Call',
  'payment.qr.label': 'Scan QR to pay',
  'payment.qr.tap': 'Tap to enlarge',
  'payment.qr.tapToClose': 'Tap anywhere to close',
  'payment.notConfigured.title': 'Recharge unavailable right now',
  'payment.notConfigured.contactBody': 'No UPI is set up yet. Contact us via WhatsApp or phone and we\'ll help you recharge.',
  'payment.notConfigured.unavailable': 'Recharge is temporarily unavailable. Please try again later.',
  'payment.chooser.title': 'Choose your UPI app',
  'payment.chooser.subtitle': 'You\'ll pay',
  'payment.chooser.hint': 'Tap an app to open it with the amount pre-filled. The app must already be installed on your phone — if you see an "address invalid" error, install the app from the App Store / Play Store first.',

  // Common
  'common.next': 'Next',
  'common.close': 'Close',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'To ensure fast and smooth payment service, please provide your accurate location and contact number.',
  'geo.change': 'Change',
  'geo.gps.button': 'Use my current location',
  'geo.gps.benefit': 'Fastest — one tap, no typing.',
  'geo.gps.denied': 'Location permission denied. Use pincode or pick manually below.',
  'geo.gps.failed': 'Couldn\'t get your location. Try pincode instead.',
  'geo.gps.notResolved': 'We couldn\'t map your spot to a known area. Please pick manually.',
  'geo.pincode.label': 'Pincode',
  'geo.pincode.notFound': 'Pincode not found. Please pick your area manually.',
  'geo.pincode.lookupFailed': 'Pincode lookup failed. Please pick manually.',
  'geo.multipleAreas.label': 'Multiple areas share this pincode — pick yours',
  'geo.multipleAreas.placeholder': 'Select your area',
  'geo.manual.title': 'Pick manually',
  'geo.manual.close': 'Hide manual picker',
  'geo.manual.pickPincode': 'Pick your pin code (1 of {count})',
  'geo.manual.pickPincodePlaceholder': 'Select pin code',
  'geo.state.label': 'State',
  'geo.state.placeholder': 'Select state',
  'geo.district.label': 'District',
  'geo.district.placeholder': 'Select district',
  'geo.area.label': 'Area / Town',
  'geo.area.placeholder': 'Select area',
  'geo.privacy.note': 'Used only to assign your local agent. Never shared.',
  'geo.required.hint': 'Location is required when you don\'t have a referral code.',
  // Saved-location summary (read-only card shown when value already
  // carries a resolved location; user clicks Edit to switch to editor).
  'geo.saved.label':       'Saved location',
  'geo.saved.fallback':    'Your saved location',
  'geo.saved.gpsPinned':   'GPS pin saved',
  'geo.saved.edit':        'Edit location',
  'geo.saved.pincode':            'Pin code',
  'geo.saved.sourcePinPriority':  'Pin code · GPS matched',
  'geo.saved.sourceGpsPriority':  'GPS · pin code overridden',
  'geo.saved.areaOnly':           'Area saved',
  // Phase R6 (2026-05-13) — separate self-mode toast keys per role.
  // The legacy `agent.geo.toast.success` baked agent-specific copy
  // ("you can now receive players") that's wrong for players. Split
  // into role-aware keys so each surface shows the right message.
  'geo.toast.success.agent':   'Location saved — you can now receive players.',
  'geo.toast.success.player':  'Location saved — you\'re all set.',
  // Self-mode (pin-first → GPS-lock → 15-km Haversine validation).
  'geo.self.pinFirst':       'Pin code first',
  'geo.self.thenGps':        'Now lock your GPS',
  'geo.self.pinThenGpsHint': 'Enter a pincode above first — the GPS lock appears once we know roughly where you are.',
  'geo.validation.matched':     'GPS matches your pin code — saving.',
  'geo.validation.gpsOverride': 'GPS is far from your pin code — using GPS instead.',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'Dashboard',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'Hisaab',
  'agent.nav.players':   'Players',
  'agent.nav.agents':    'Agents',
  'agent.nav.rewards':   'Rewards',
  'agent.nav.profile':   'Profile',
  'agent.nav.money':     'Money',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home':    'Home',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'Hisaab',
  'agent.bottomnav.players': 'Players',
  'agent.bottomnav.agents':  'Agents',
  'agent.bottomnav.rewards': 'Rewards',
  'agent.bottomnav.profile': 'Profile',
  'agent.bottomnav.money':   'Money',

  // ────────────────────────────────────────────────────────────────────
  // AGENT GAMES HUB — U3 (2026-07-09). The unified Games tab that
  // replaced the Cricket-only nav slot. One question per screen:
  // "what did each game earn me?" — plain words only.
  'agent.games.hub.title':        'Hisaab',
  'agent.games.hub.subtitle':     'Your full statement — by game and by people',
  // Hisaab view selector — owner request 2026-07-09.
  'agent.hisaab.tab.game':           'Game Hisaab',
  'agent.hisaab.tab.people':         'People Hisaab',
  'agent.hisaab.people.playersCard': 'From direct players',
  'agent.hisaab.person.desc':        'Which game, at what %, and your cut:',
  'agent.hisaab.person.myCut':       'my cut',
  'agent.hisaab.person.viewProfile': 'View full profile',
  'agent.hisaab.rate.my':            'My rate',
  'agent.hisaab.rate.their':         'Their rate',
  'agent.hisaab.eq.lost':            'lost',
  'agent.hisaab.eq.won':             'won',
  'agent.hisaab.eq.turnover':        'turnover',
  'agent.hisaab.pillar.short.win_loss':         'W/L',
  'agent.hisaab.pillar.short.profit_share':     'P&L',
  'agent.hisaab.pillar.short.commission_share': 'Exch',
  'agent.hisaab.pillar.short.adjust':           'Adj',
  'agent.hisaab.pillar.short.turnover_share':   'Return',
  'agent.games.hero.kicker':      'All games together',
  'agent.games.hero.sub':         'Cricket and casino games, everything combined.',
  'agent.games.section.main':     'Main game',
  'agent.games.section.casino':   'Casino & small games',
  'agent.games.cricket.name':     'Cricket',
  'agent.games.cricket.balance':  'Cricket wallet',
  'agent.games.cricket.details':  'See full cricket details',
  'agent.games.row.nothing':      'No earnings yet',
  'agent.games.empty.casino':     'As soon as your players start playing, every game’s earnings show up here.',
  'agent.games.error':            'Could not load your games right now. Please try again shortly.',
  'agent.games.range.today':      'Today',
  'agent.games.range.week':       'This week',
  'agent.games.range.month':      'This month',
  'agent.games.range.all':        'All time',
  // "Who caused it" modal — owner request 2026-07-09.
  'agent.games.detail.allGames':         'All games',
  'agent.games.detail.dateLine':         'Showing {from} to {to}',
  'agent.games.detail.desc':             'Who made or lost you this money in this period:',
  'agent.games.detail.net':              'Total this period',
  'agent.games.detail.players.section':  'From your players',
  'agent.games.detail.downline.section': 'Through your {label}',
  'agent.games.detail.other':            'Other / adjustments',
  'agent.games.detail.empty':            'No entries in this period.',
  'agent.games.detail.tapHint':          'Tap to see who caused it',

  // ────────────────────────────────────────────────────────────────────
  // AGENT PROFILE — U4 (2026-07-09) hub-and-spoke rebuild. The landing
  // is a plain card menu; every card = key number + one plain sentence.
  'agent.profile.landing.wallet.title':   'My wallet',
  'agent.profile.landing.wallet.sub':     'Recharge & withdrawal requests, full history',
  'agent.profile.landing.deposit.title':  'Security deposit',
  'agent.profile.landing.deposit.sub':    'Your safety money kept with the house',
  'agent.profile.landing.deposit.safe':   'Safe',
  'agent.profile.landing.deposit.zero':   'Empty',
  'agent.profile.landing.rates.title':    'My rates',
  'agent.profile.landing.rates.sub':      'What you earn from every game',
  'agent.profile.landing.rewards.title':  'Rewards & referrals',
  'agent.profile.landing.rewards.sub':    'Offers you can join, and your network growth',
  'agent.profile.landing.account.title':  'Account & security',
  'agent.profile.landing.account.sub':    'Name, phone, password, sign out',
  'agent.profile.landing.danger.wallet':  'Your wallet is below zero — please recharge soon.',
  'agent.profile.landing.danger.deposit': 'Your security deposit is empty — new players can’t be routed to you.',
  'agent.profile.rewards.offers.section':    'Offers',
  'agent.profile.rewards.cost.title':        'What offers cost me',
  'agent.profile.rewards.cost.line':         'Offers have cost you {x} so far. The most they can ever cost you: {y}.',
  'agent.profile.rewards.referrals.section': 'Referrals',

  // ────────────────────────────────────────────────────────────────────
  // U5 STRING SWEEP (2026-07-09) — AgentDashboard.tsx (agent.home.*),
  // AgentCricketPanel.tsx (agent.cricketPage.*), ReferralsPanel.tsx
  // (agent.referrals.*), BrowseOffersPanel.tsx leftovers (agent.offers.*),
  // MyRatesPanel.tsx (agent.myRates.*), AccountPanel.tsx additions
  // (agent.account.personal.*). Generic "Close"/"Loading…" reuse common.*.

  // Agent home dashboard.
  'agent.home.welcome':             'Welcome back — you’re a',
  'agent.home.error.load':          'Failed to load dashboard.',
  'agent.home.error.detail':        'Failed to load.',
  'agent.home.hero.title':          'You earned from games',
  'agent.home.hero.tap':            'tap to see every game',
  'agent.home.tile.players':        'Players',
  'agent.home.tile.requests':       'Requests',
  'agent.home.tile.myAccount':      'My account',
  'agent.home.tile.totalBalance':   '{x} total balance',
  'agent.home.tile.inOut':          '+{in} in · −{out} out',
  'agent.home.tile.totalWorth':     '{x} total worth',
  'agent.home.tile.partnership':    '{x}% partnership share',
  'agent.home.modal.game':          'Game earnings',
  'agent.home.modal.pendingNow':    'Pending now',
  'agent.home.table.filter':        'Filter…',
  'agent.home.table.clearFilter':   'Clear filter',
  'agent.home.table.noMatches':     'No matches.',
  'agent.home.table.total':         'Total',
  'agent.home.table.totalCount':    'Total ({n})',
  'agent.home.game.col.game':       'Game',
  'agent.home.game.col.bet':        'Bet',
  'agent.home.game.col.won':        'Won',
  'agent.home.game.col.lost':       'Lost',
  'agent.home.game.col.myPct':      'Your %',
  'agent.home.game.col.earned':     'You earned',
  'agent.home.game.col.toUpline':   'To upline',
  'agent.home.game.empty':          'No game activity in this period.',
  'agent.home.game.filter':         'Filter games…',
  'agent.home.downline.col.name':     'Name',
  'agent.home.downline.col.theirPct': 'Their %',
  'agent.home.downline.col.youEarn':  'You earn',
  'agent.home.downline.col.theyEarn': 'They earn',
  'agent.home.downline.col.balance':  'Balance',
  'agent.home.downline.empty':      'No {x} yet.',
  'agent.home.downline.filter':     'Filter {x}…',
  'agent.home.users.col.player':    'Player',
  'agent.home.users.col.winLoss':   'Win / loss',
  'agent.home.users.empty':         'No players yet.',
  'agent.home.users.filter':        'Filter players…',
  'agent.home.requests.col.from':   'From',
  'agent.home.requests.col.type':   'Type',
  'agent.home.requests.col.amount': 'Amount',
  'agent.home.requests.recharge':   'Recharge',
  'agent.home.requests.withdrawal': 'Withdrawal',
  'agent.home.requests.net':        'Net',
  'agent.home.requests.empty':      'No pending requests.',
  'agent.home.requests.filter':     'Filter requests…',

  // Agent cricket earnings page.
  'agent.cricketPage.loading':          'Loading cricket earnings…',
  'agent.cricketPage.error.loadFailed': 'Failed to load',
  'agent.cricketPage.title':            'Cricket Earnings',
  'agent.cricketPage.subtitle':         'Your commission on the cricket exchange — live, from cricket’s own books.',
  'agent.cricketPage.upline':           'Your upline:',
  'agent.cricketPage.uplineHouse':      'You report directly to the house.',
  'agent.cricketPage.chip.pl':          'P&L share',
  'agent.cricketPage.chip.comm':        'Commission',
  'agent.cricketPage.stat.balance':     'Cricket balance',
  'agent.cricketPage.stat.balance.sub': 'Accrued cricket commission wallet',
  'agent.cricketPage.stat.week':        'This week',
  'agent.cricketPage.stat.week.sub':    'All pillars, last 7 days',
  'agent.cricketPage.stat.allTime':     'All time',
  'agent.cricketPage.stat.allTime.sub': 'Since your cricket book opened',
  'agent.cricketPage.pillars.title':    'Where it comes from',
  'agent.cricketPage.pillar.profitShare': 'P&L partnership',
  'agent.cricketPage.pillar.commShare':   'P2P commission',
  'agent.cricketPage.pillar.adjust':      'Adjustments',
  'agent.cricketPage.hint.profitShare': 'Your partnership % of your downline players’ net results.',
  'agent.cricketPage.hint.commShare':   'Your share of the exchange commission on P2P winnings.',
  'agent.cricketPage.hint.turnoverReturn': 'House-paid return on your players’ turnover — win or lose.',
  'agent.cricketPage.entries':          '{n} entries',
  'agent.cricketPage.players.title':    'My players — cricket standings',
  'agent.cricketPage.players.empty':    'No players under you yet.',
  'agent.cricketPage.players.col.player':   'Player',
  'agent.cricketPage.players.col.turnover': 'Week turnover',
  'agent.cricketPage.players.col.atRisk':   'Open at-risk',
  'agent.cricketPage.players.col.balance':  'Balance',
  'agent.cricketPage.players.col.earnedMe': 'Earned me',
  'agent.cricketPage.limits.open':       'Limits',
  'agent.cricketPage.limits.loading':    'Loading limits…',
  'agent.cricketPage.limits.saved':      'Saved.',
  'agent.cricketPage.limits.maxMO':      'Max stake · Match Odds',
  'agent.cricketPage.limits.maxBM':      'Max stake · Bookmaker',
  'agent.cricketPage.limits.maxSession': 'Max stake · Session',
  'agent.cricketPage.limits.maxExposure':'Max exposure / match',
  'agent.cricketPage.limits.noCap':      'no cap',
  'agent.cricketPage.limits.save':       'Save limits',
  'agent.cricketPage.limits.perGame':    'Max bet per game',
  'agent.cricketPage.limits.casino':     'Casino games',
  'agent.cricketPage.limits.board':      'Board games',
  'agent.cricketPage.limits.small':      'Small games',
  'agent.cricketPage.limits.cricket':    'Cricket',
  'agent.cricketPage.limits.hint':       'blank = no cap · 0 = blocked',
  'agent.cricketPage.subs.title':       'My sub-agents — rates & cricket balances',
  'agent.cricketPage.subs.col.agent':   'Agent',
  'agent.cricketPage.subs.col.match':   'Match %',
  'agent.cricketPage.subs.col.session': 'Session %',
  'agent.cricketPage.subs.col.pl':      'P&L %',
  'agent.cricketPage.subs.col.comm':    'Comm %',
  'agent.cricketPage.subs.col.players': 'Players',
  'agent.cricketPage.subs.col.theyEarned': 'They earned',
  'agent.cricketPage.subs.col.residual':   'My residual from them',
  'agent.cricketPage.subs.col.balance':    'Their balance',
  'agent.cricketPage.subs.note':        'A sub-agent’s rate can never exceed yours (Golden Limit). “My residual from them” is what YOU earned off their players — the gap between your % and the % you gave them.',
  'agent.cricketPage.weekly.title':     'Weekly statement',
  'agent.cricketPage.weekly.empty':     'Nothing settled yet.',
  'agent.cricketPage.weekly.weekOf':    'Week of {date}',
  'agent.cricketPage.recent.title':     'Recent entries',
  'agent.cricketPage.recent.empty':     'No entries yet — they appear the moment your players’ bets settle.',
  'agent.cricketPage.recent.player':    'player #{id}',

  // Agent referrals panel.
  'agent.referrals.loading':          'Loading referrals…',
  'agent.referrals.error.loadFailed': 'Failed to load referrals.',
  'agent.referrals.hero.kicker':      'Network Growth',
  'agent.referrals.hero.paidLabel':   'Admin-funded bonus paid',
  'agent.referrals.hero.explainer':   'Referral bonuses are 100% platform-funded. Your wallet is not charged for any of these payouts — this dashboard is visibility into the viral growth happening across your network.',
  'agent.referrals.stat.total':       'Total',
  'agent.referrals.status.pending':   'Pending',
  'agent.referrals.status.credited':  'Credited',
  'agent.referrals.status.rejected':  'Rejected',
  'agent.referrals.filter.all':       'All ({n})',
  'agent.referrals.empty.all':        'No referrals yet from anyone in your network.',
  'agent.referrals.empty.filtered':   'No {status} referrals.',
  'agent.referrals.pagination':       'Showing {shown} of {total} — paginated load coming soon.',
  'agent.referrals.card.code':        'code',
  'agent.referrals.card.referred':    'referred {date}',
  'agent.referrals.card.credited':    'credited {date}',
  'agent.referrals.card.adminPaid':   'admin paid',
  'agent.referrals.card.recharge':    'recharge #{id}',

  // Agent browse-offers leftovers.
  'agent.offers.badge.new':     'NEW',
  'agent.offers.badge.newAria': 'New offer — not yet activated',

  // Agent my-rates panel. Group titles reuse agent.games.section.casino
  // and agent.games.cricket.name.
  'agent.myRates.hero.subtitle': 'What you earn from your players — set by your upline, can’t be changed here.',
  'agent.myRates.loading':       'Loading your rates…',
  'agent.myRates.error':         'Could not load your rates.',
  'agent.myRates.footer.zero':   '0% simply means that earning type is switched off for you right now.',
  'agent.myRates.row.winLoss.label':     'Win / Loss share',
  'agent.myRates.row.winLoss.plain':     'Your share of what your players lose (you also carry the same share when they win).',
  'agent.myRates.row.profitShare.label': 'P&L share',
  'agent.myRates.row.profitShare.plain': 'Your share of what your players lose on cricket (and of what they win).',
  'agent.myRates.row.commShare.label':   'Exchange commission share',
  'agent.myRates.row.commShare.plain':   'Your share of the platform’s small cut on player-vs-player winnings.',

  // Agent account panel — U5 additions (name/dob fields + hints).
  'agent.account.personal.field.firstName': 'First name',
  'agent.account.personal.field.lastName':  'Last name',
  'agent.account.personal.field.dob':       'Date of birth',
  'agent.account.personal.field.name.hint':     'Shown to your downline / upline',
  'agent.account.personal.field.dob.hint':      'Private · never shown to peers',
  'agent.account.personal.field.username.hint': 'Login only — never displayed to peers',
  'agent.account.personal.error.firstNameShort': 'First name must be at least 2 characters',
  'agent.account.personal.error.lastNameShort':  'Last name must be at least 2 characters',
  'agent.account.personal.error.dobFormat':      'Birthdate must be in YYYY-MM-DD format',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix':  'Welcome back, ',
  'agent.dashboard.header.welcomeFallback': 'there',
  'agent.dashboard.header.subtitle':       'Your business at a glance — pool, deposit, players, sub-agents, P&L, exposure',
  'agent.dashboard.range.today':           'Today',
  'agent.dashboard.range.yesterday':       'Yesterday',
  'agent.dashboard.range.thisWeek':        'This week',
  'agent.dashboard.range.lastWeek':        'Last week',
  'agent.dashboard.range.thisMonth':       'This month',
  'agent.dashboard.range.lastMonth':       'Last month',
  'agent.dashboard.range.complete':        'Complete',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading':         'Loading deposit coverage…',
  'agent.dashboard.depositBar.label':           'Deposit coverage',
  'agent.dashboard.depositBar.balanceOfTarget': '· {balance} of {target} target',
  'agent.dashboard.depositBar.tapToView':       'Tap to view full deposit page.',
  'agent.dashboard.depositBar.caption.suspended': 'Account suspended. Contact admin.',
  'agent.dashboard.depositBar.caption.breach':  'Your wallet has consumed 100% of your deposit ceiling. Withdrawals are locked. Top up immediately.',
  'agent.dashboard.depositBar.caption.warning': 'Your wallet is at {pct}% of your deposit ceiling. Warning fires at 80%, lockdown at 100%.',
  'agent.dashboard.depositBar.caption.caution': 'Your wallet has eaten {pct}% of your deposit coverage. Below 80% — still safe.',
  'agent.dashboard.depositBar.caption.safe':    'Deposit fully intact. No coverage being consumed.',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'Alert · zero security deposit',
  'agent.dashboard.locatorSkip.body':  'Your security deposit is {amount}. You will not receive new players from the geographic locator until you fund it. Existing players + direct referrals + manual assignments are unaffected — only organic geo-routed signups skip you.',
  'agent.dashboard.locatorSkip.cta':   'Fund deposit',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title':            'Downline defaulter alert',
  'agent.dashboard.defaulter.summary.critical.one':   '{n} of your direct sub-agent is in default — they are locked or have breached their deposit.',
  'agent.dashboard.defaulter.summary.critical.other': '{n} of your direct sub-agents are in default — they are locked or have breached their deposit.',
  'agent.dashboard.defaulter.summary.warning.one':    '{n} of your direct sub-agent is at 80%+ of their deposit ceiling — defaulter risk.',
  'agent.dashboard.defaulter.summary.warning.other':  '{n} of your direct sub-agents are at 80%+ of their deposit ceiling — defaulter risk.',
  'agent.dashboard.defaulter.worst':             'Worst: @{username} at {pct}% of deposit ceiling.',
  'agent.dashboard.defaulter.tapHint':           'Tap to jump to the Direct sub-agents list. Defaulter rows are highlighted.',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked':  'Locked',
  'agent.dashboard.marginPill.caution': 'caution',
  'agent.dashboard.marginPill.warning': 'warning',
  'agent.dashboard.marginPill.breach':  'breach',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title':    'Morning briefing',
  'agent.dashboard.sections.morning.subtitle': 'Live snapshot — every number refreshes automatically',
  'agent.dashboard.sections.activity.title':    'Activity',
  'agent.dashboard.sections.activity.subtitle': 'Top spenders and most recent player payment events',
  'agent.dashboard.sections.forensic.title':    'Forensic detail',
  'agent.dashboard.sections.forensic.subtitle': 'Tap to expand any section — closed by default to keep this page calm',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title':      'Profit & Loss deep-dive',
  'agent.dashboard.accordions.pnlDeep.helper':     'Per-pillar + per-game breakdown of your earnings. Click a game row for the underlying matches.',
  'agent.dashboard.accordions.leaderboard.title':  'Downline leaderboard',
  'agent.dashboard.accordions.leaderboard.helper': 'Your sub-agents ranked by net earnings. Negative numbers mean that sub-agent paid out more than they brought in.',
  'agent.dashboard.accordions.activity.title':     'Downline activity feed',
  'agent.dashboard.accordions.activity.helper':    'Every match settlement, recharge and rate change across your sub-agents — the forensic timeline.',
  'agent.dashboard.accordions.earnings.title':     'Earnings breakdown (lifetime)',
  'agent.dashboard.accordions.earnings.helper':    'Lifetime totals by kind. Ignores the range toggle above — these never change with the time filter.',
  'agent.dashboard.accordions.directSubAgents.title':  'Direct sub-agents',
  'agent.dashboard.accordions.directSubAgents.helper': 'Your direct lieutenants. Each runs their own players + their own sub-agents. Rows highlighted rose/amber are in default territory.',
  'agent.dashboard.accordions.chain.title':        'Chain visibility',
  'agent.dashboard.accordions.chain.helper':       'Your total downline footprint, separated into direct (edited by you) and indirect (managed by your sub-agents).',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'Live data — refreshes on focus, every 60s, and when money moves.',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label':       'My pool balance',
  'agent.dashboard.cards.poolBalance.helper':      'Your spendable balance. The money you can use to recharge players or send to sub-agents.',
  'agent.dashboard.cards.poolBalance.netInRange':  'Net {value} in {range}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading':  'Security deposit',
  'agent.dashboard.cards.depositStatus.titleLoaded':   'Deposit status',
  'agent.dashboard.cards.depositStatus.helperLoading': 'Loading deposit status…',
  'agent.dashboard.cards.depositStatus.helper':        'Collateral against your pool. Status flips to caution at 50% of target, warning at 80%, breach at 100%.',
  'agent.dashboard.cards.depositStatus.suspendedSince':           'since {when}',
  'agent.dashboard.cards.depositStatus.field.operatingBalance':   'Operating balance',
  'agent.dashboard.cards.depositStatus.field.depositBalance':     'Deposit balance',
  'agent.dashboard.cards.depositStatus.field.depositTarget':      'Deposit target',
  'agent.dashboard.cards.depositStatus.field.negativeExposure':   'Negative exposure',
  'agent.dashboard.cards.depositStatus.field.coverageRatio':      'Coverage ratio',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint':  'of deposit target consumed by negative exposure',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe':      'safe',
  'agent.dashboard.depositStatus.status.caution':   'caution',
  'agent.dashboard.depositStatus.status.warning':   'warning',
  'agent.dashboard.depositStatus.status.breach':    'breach',
  'agent.dashboard.depositStatus.status.suspended': 'suspended',
  'agent.dashboard.depositStatus.status.healthy':   'healthy',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title':     "Players' wallet total",
  'agent.dashboard.cards.playersWallet.helper':    "Combined live balance across every one of your players' wallets right now. Sum of every wallet_points row scoped to you.",
  'agent.dashboard.cards.playersWallet.foot.one':  '{n} player · {active} active',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} players · {active} active',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title':         'Sub-agents wallet + deposit',
  'agent.dashboard.cards.subAgents.helper':        'Combined pool balance and security deposit held by your DIRECT sub-agents. Each sub-agent runs their own players + their own downline.',
  'agent.dashboard.cards.subAgents.field.pool':    'Pool',
  'agent.dashboard.cards.subAgents.field.deposit': 'Deposit',
  'agent.dashboard.cards.subAgents.foot.one':      '{n} direct sub-agent',
  'agent.dashboard.cards.subAgents.foot.other':    '{n} direct sub-agents',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title':            'My pending requests',
  'agent.dashboard.cards.myPending.helper':           "Priority 3. Your OWN submissions to admin — you can't action these yourself, you're waiting on them.",
  'agent.dashboard.cards.myPending.recharge.one':     '{n} recharge awaiting admin',
  'agent.dashboard.cards.myPending.recharge.other':   '{n} recharges awaiting admin',
  'agent.dashboard.cards.myPending.withdrawal.one':   '{n} withdrawal awaiting admin',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} withdrawals awaiting admin',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title':            "Players' pending requests",
  'agent.dashboard.cards.playersPending.helper':           'Priority 1. Your players are waiting on YOU to approve or pay these. Tap to action them.',
  'agent.dashboard.cards.playersPending.recharge.one':     '{n} recharge to approve',
  'agent.dashboard.cards.playersPending.recharge.other':   '{n} recharges to approve',
  'agent.dashboard.cards.playersPending.withdrawal.one':   '{n} withdrawal to pay',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} withdrawals to pay',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title':  "Sub-agents' pending requests",
  'agent.dashboard.cards.subAgentsPending.helper': 'Priority 2. Your sub-agents (full downline) are waiting on YOU to approve or pay these. Tap to action.',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title':  'True Net P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'Your real profit/loss in this range. Platform fee share + win/loss share − offer co-funding. Expand the P&L deep-dive below for per-game details.',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'Plat',
  'agent.dashboard.pillarChip.winLoss':     'W/L',
  'agent.dashboard.pillarChip.offer':       'Offer',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title':     'Offer utilization · {range}',
  'agent.dashboard.cards.rewardUtil.helper':    'Total reward ₹ paid out to your players through offers in this range. Cross-reference with your exposure page to see your share of that cost.',
  'agent.dashboard.cards.rewardUtil.foot.one':  '{n} claim credited',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} claims credited',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title':   'Top spenders',
  'agent.dashboard.activity.topSpenders.helper':  'Your top-spending players in {range}.',
  'agent.dashboard.activity.topSpenders.empty':   'No spend in this range yet.',
  'agent.dashboard.activity.recent.title':        'Recent activity',
  'agent.dashboard.activity.recent.helper':       'The five most recent recharge events from your players.',
  'agent.dashboard.activity.recent.empty':        'No recent activity.',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'approved',
  'agent.dashboard.activity.recent.state.rejected': 'rejected',
  'agent.dashboard.activity.recent.state.pending':  'pending',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel':         'Per game · tap a row to explain',
  'agent.dashboard.pnl.noEarnings':           'No earnings in this range.',
  'agent.dashboard.pnl.table.col.pillarGame': 'Pillar / Game',
  'agent.dashboard.pnl.table.col.net':        'Net',
  'agent.dashboard.pnl.table.col.matches':    'Matches',
  'agent.dashboard.pnl.ledgerRows.one':       '{n} ledger row',
  'agent.dashboard.pnl.ledgerRows.other':     '{n} ledger rows',
  'agent.dashboard.pnl.pillar.platformFee':   'Platform fee',
  'agent.dashboard.pnl.pillar.winLoss':       'Win / Loss',
  'agent.dashboard.pnl.pillar.offerReward':   'Offer reward',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel':       "Window: {range} — strict subset of the dashboard's range",
  'agent.dashboard.pnlDrill.loading':           'Loading match-level details…',
  'agent.dashboard.pnlDrill.windowTotal':       'Window total:',
  'agent.dashboard.pnlDrill.matchCount.one':    'across {n} match',
  'agent.dashboard.pnlDrill.matchCount.other':  'across {n} matches',
  'agent.dashboard.pnlDrill.noContribution':    'No matches contributed to this cell.',
  'agent.dashboard.pnlDrill.errorFallback':     'Could not load drilldown',
  'agent.dashboard.pnlDrill.table.col.match':   'Match',
  'agent.dashboard.pnlDrill.table.col.role':    'Role',
  'agent.dashboard.pnlDrill.table.col.base':    'Base',
  'agent.dashboard.pnlDrill.table.col.rate':    'Rate',
  'agent.dashboard.pnlDrill.table.col.earned':  'Earned',
  'agent.dashboard.pnlDrill.table.col.settled': 'Settled',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading':       'Loading…',
  'agent.dashboard.directDownline.empty':         'No direct sub-agents yet.',
  'agent.dashboard.directDownline.row.player.one':   '{n} player',
  'agent.dashboard.directDownline.row.player.other': '{n} players',
  'agent.dashboard.directDownline.row.subAgent.one':   '{n} sub-agent',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} sub-agents',
  'agent.dashboard.directDownline.row.walletDeposit':  'wallet {wallet} · deposit {deposit}',
  'agent.dashboard.directDownline.footer':        'True Net is for the {range} window.',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading':                  'Loading…',
  'agent.dashboard.chain.directAgents.title':       'Direct agents',
  'agent.dashboard.chain.directAgents.subtitle':    'Your direct sub-agents — you can action their requests.',
  'agent.dashboard.chain.indirectAgents.title':     'Indirect agents',
  'agent.dashboard.chain.indirectAgents.subtitle':  'Managed by your sub-agents. Read-only for you.',
  'agent.dashboard.chain.directPlayers.title':      'Direct players',
  'agent.dashboard.chain.directPlayers.subtitle':   'Your players — you action their recharges and withdrawals.',
  'agent.dashboard.chain.indirectPlayers.title':    'Indirect players',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'Players of your sub-agents. Read-only for you.',
  'agent.dashboard.chain.field.walletTotal':        'Wallet total',
  'agent.dashboard.chain.field.depositTotal':       'Deposit total',
  'agent.dashboard.chain.field.avgPerHead':         'Avg per head',
  'agent.dashboard.chain.field.pendingRecharge':    'Pending recharge',
  'agent.dashboard.chain.field.pendingWithdrawal':  'Pending withdrawal',
  'agent.dashboard.chain.pendingBadge':             '{n} pending',
  'agent.dashboard.chain.openInbox':                'Open inbox',
  'agent.dashboard.chain.footer':                   "Direct = headcount you manage yourself. Indirect = managed by your sub-agents (you see the numbers but can't action their pending requests — those go to their parent).",

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed':  'Failed to load',
  'agent.activity.filter.window':     'Window',
  'agent.activity.filter.kind':       'Kind',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all':        'All',
  // Kind filter pill labels.
  'agent.activity.kind.all':              'All',
  'agent.activity.kind.matches':          'Matches',
  'agent.activity.kind.recharges':        'Recharges',
  'agent.activity.kind.rateChanges':      'Rate changes',
  // Empty + loading states.
  'agent.activity.loading':           'Loading…',
  'agent.activity.empty.title':       'Quiet',
  'agent.activity.empty.subtitle':    'No {kindWord} events in the {window} window.',
  'agent.activity.empty.kindWord.all':                  '',
  'agent.activity.empty.kindWord.match':                'match ',
  'agent.activity.empty.kindWord.recharge':             'recharge ',
  'agent.activity.empty.kindWord.partnership_change':   'partnership_change ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'Settlement',
  'agent.activity.row.earned':             'earned',
  'agent.activity.row.fromPillarOn':       'from {pillar} on ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee':  'platform fee',
  'agent.activity.row.pillar.winLoss':      'win/loss',
  'agent.activity.row.pillar.offerReward':  'offer reward',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'Recharge',
  'agent.activity.row.recharge.of':     'of',
  'agent.activity.row.recharge.byUser': 'by user #{id}',
  'agent.activity.row.recharge.via':    ' via ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved':  'approved',
  'agent.activity.row.state.rejected':  'rejected',
  'agent.activity.row.state.pending':   'pending',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix':  'Rate change',
  'agent.activity.row.rateChange.dashOn':  '— {pillar} on ',
  'agent.activity.row.rateChange.cascade': 'cascade',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'This view is for agents only.',
  'agent.earnings.stat.poolBalance': 'Pool balance',
  'agent.earnings.stat.totalIn':     'Total in',
  'agent.earnings.stat.totalOut':    'Total out',
  'agent.earnings.section.in':       'Earnings (in)',
  'agent.earnings.section.out':      'Outflows (out)',
  'agent.earnings.empty.in':  'No earnings yet. Once your players recharge or play, the cuts will appear here.',
  'agent.earnings.empty.out': 'No outflows yet.',
  'agent.earnings.viewLedger': 'View full ledger',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label':  'Topup from admin',
  'agent.earnings.kind.topupFromAdmin.hint':   'Admin credited your pool',
  'agent.earnings.kind.topupToPlayer.label':   'Topup to player',
  'agent.earnings.kind.topupToPlayer.hint':    'You recharged a player',
  'agent.earnings.kind.topupFromPlayer.label': 'Player recharge',
  'agent.earnings.kind.topupFromPlayer.hint':  'Your players paid into your pool',
  'agent.earnings.kind.revenueShare.label':    'Revenue share',
  'agent.earnings.kind.revenueShare.hint':     'Cut of platform fees from matches',
  'agent.earnings.kind.coFundOffer.label':     'Co-fund offer',
  'agent.earnings.kind.coFundOffer.hint':      'Your share of offer payouts',
  'agent.earnings.kind.adminAdjust.label':     'Admin adjustment',
  'agent.earnings.kind.adminAdjust.hint':      'Manual correction',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one':   'entry',
  'agent.earnings.row.entry.other': 'entries',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window':   'Window',
  'agent.leaderboard.filter.pillar':   'Pillar',
  'agent.leaderboard.window.all':      'All',
  'agent.leaderboard.pillar.all':         'All pillars',
  'agent.leaderboard.pillar.platformFee': 'Platform fee',
  'agent.leaderboard.pillar.winLoss':     'Win / Loss',
  'agent.leaderboard.pillar.offerReward': 'Offer reward',
  'agent.leaderboard.loading':         'Loading…',
  'agent.leaderboard.empty.title':         'No earnings yet',
  'agent.leaderboard.empty.subtitle.admin':    'No settlement activity in the {window} window across all agents.',
  'agent.leaderboard.empty.subtitle.downline': 'No earnings from your downline in the {window} window. As your sub-agents drive matches, this board will populate.',
  'agent.leaderboard.row.matches.one':   '{n} match',
  'agent.leaderboard.row.matches.other': '{n} matches',
  'agent.leaderboard.footer':          'Earnings sourced from match_settlement_snapshots. A negative number means that agent paid more out than they took in across the window.',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'Plat',
  'agent.leaderboard.chip.winLoss':     'W/L',
  'agent.leaderboard.chip.offer':       'Offer',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow':   'Margin warning · 80% threshold reached',
  'agent.warning.header.title':     'One bad settlement away from lockout.',
  'agent.warning.header.body':      "You're not locked yet — but your deposit safety cushion is over 80% spent. Top up now and you stay in control.",
  'agent.warning.header.dismissAria': 'Dismiss',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow':      'Why this is showing',
  'agent.warning.why.body':         "Recent game settlements pushed your wallet into the red. Your security deposit of {deposit} is the safety net that absorbs this — and it's now over 80% spent. If just a little more negative arrives, your wallet + deposit drops to zero and the platform locks the account.",
  'agent.warning.why.reassurance':  'This alert fires early on purpose — while you can still use the whole app. The lockout screen only appears if you cross 100%, and once it does, most features are blocked until you recover. Acting now is much less disruptive than acting later.',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label':      'Headroom before lockout',
  'agent.warning.gauge.suffix':     'of room left',
  'agent.warning.gauge.dangerBadge': '{pct}% into danger zone',
  'agent.warning.gauge.field.wallet':  'Your wallet',
  'agent.warning.gauge.field.deposit': 'Your deposit',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow':       'If you ignore this and hit 100%',
  'agent.warning.ignore.cardTitle':     'You will immediately lose',
  'agent.warning.ignore.item.dashboard': 'Access to your dashboard, P&L, and leaderboards',
  'agent.warning.ignore.item.pages':     'The Players and Agents pages',
  'agent.warning.ignore.item.manualTopup': 'Manual top-ups to your players from your pool',
  'agent.warning.ignore.item.newWithdrawal': 'Submitting new withdrawals to your upline',
  'agent.warning.ignore.item.subAgents':  'Adding or managing sub-agents',
  'agent.warning.ignore.item.profile':   'Editing your profile or payment settings',
  'agent.warning.ignore.footer':         "You'd still be able to approve pending requests + submit an emergency top-up — but everything else stops until your wallet + deposit climbs back above zero.",

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow':    'Pay your upline now to recover',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta':       "I'll deal with this later",
  'agent.warning.dismiss.footnote':  "We'll remind you again in 10 minutes if you stay in the warning zone. Closes automatically once you recover.",

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'Account locked · Bailout Portal',
  'agent.lockout.header.title':   'You owe more than your deposit covers.',
  'agent.lockout.header.body':    "Don't worry — this is recoverable. Follow the two steps below and you'll be unlocked automatically.",

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow':       'Why this happened',
  'agent.lockout.why.body':          "Game settlements drove your wallet below zero. Your security deposit is the safety net that absorbs this — but you've now used 100% of it. Until your wallet plus deposit climbs back above zero, you can't operate normally.",
  'agent.lockout.why.reassurance':   "The platform does not lock you because you did something wrong. It locks you so the network can't accumulate more debt against your deposit while you stabilise.",

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label':         'Safe recovery target',
  'agent.lockout.gauge.suffix':        'to pay safely',
  'agent.lockout.gauge.recoveredBadge': '{pct}% recovered',
  'agent.lockout.gauge.field.wallet':  'Your wallet',
  'agent.lockout.gauge.field.deposit': 'Your deposit',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'Just unlock',
  'agent.lockout.tier.justUnlock.tag':   'Risky',
  'agent.lockout.tier.justUnlock.note':  'Brings you back to ₹0 — any new loss re-locks you instantly.',
  'agent.lockout.tier.safeMin.label':    'Safe minimum',
  'agent.lockout.tier.safeMin.tag':      'Recommended',
  'agent.lockout.tier.safeMin.note':     'Deposit ({deposit}) + half your negative wallet. Leaves a real cushion.',
  'agent.lockout.tier.fullRecovery.label': 'Full recovery',
  'agent.lockout.tier.fullRecovery.tag':  'Best',
  'agent.lockout.tier.fullRecovery.note': 'Deposit fully restored AND wallet brought back to zero.',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'Two ways to recover — do whichever (or both)',
  'agent.lockout.waysOut.inbox.title':  'Action your inbox',
  'agent.lockout.waysOut.inbox.body':   'Approve player recharges (the cash is already with you) and mark player withdrawals as paid. Every approval credits your wallet.',
  'agent.lockout.waysOut.inbox.ctaOpen':  'Open inbox',
  'agent.lockout.waysOut.inbox.ctaOpened': 'Inbox is open below',
  'agent.lockout.waysOut.upline.title': 'Pay your upline',
  'agent.lockout.waysOut.upline.body':  'Send cash to your parent agent (or admin) via UPI, then submit the txn id. Once they approve, your wallet is credited instantly.',
  'agent.lockout.waysOut.upline.ctaOpen':  'Open form',
  'agent.lockout.waysOut.upline.ctaOpened': 'Form is open below',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': "What you can still do (and what's blocked)",
  'agent.lockout.capabilities.canHeader':    'You CAN still',
  'agent.lockout.capabilities.cantHeader':   "You CAN'T (temporarily)",
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge':    'Approve incoming player recharges',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'Mark player withdrawals as paid',
  'agent.lockout.can.approveSubAgentRecharge':  'Approve sub-agent recharges',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'Mark sub-agent withdrawals as paid',
  'agent.lockout.can.submitToUpline':           'Submit a recharge to your upline',
  'agent.lockout.can.receiveSseAndSignOut':     'Receive SSE alerts + sign out',

  // Section A/B/C headers.
  'agent.lockout.section.step':                 'Step {n} · {title}',
  'agent.lockout.section.inbox.title':          'Your inbox — every approval credits your wallet',
  'agent.lockout.section.inbox.subtitle':       'Pending payment requests from players and sub-agents waiting on you to action.',
  'agent.lockout.section.inbox.fromPlayers':    'From your players',
  'agent.lockout.section.inbox.fromSubAgents':  'From your sub-agents',
  'agent.lockout.section.upline.title':         'Pay your upline to top up your wallet',
  'agent.lockout.section.upline.subtitle':      "Quick once you have your parent's UPI in front of you. Approval is usually within minutes.",

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title':    'Recharge · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · submitted {when}',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · submitted {when} · "{note}"',
  'agent.lockout.queue.player.withdrawal.title':    'Withdrawal · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': 'Pay to {upi} · submitted {when}',
  'agent.lockout.queue.player.withdrawal.subtitleWa': 'Pay to {upi} · {whatsapp} · submitted {when}',
  'agent.lockout.queue.subAgent.recharge.title':    'Sub-agent recharge · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · submitted {when}',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · submitted {when} · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title':    'Sub-agent withdrawal · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': 'Pay to {upi} · submitted {when}',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': 'Pay to {upi} · submitted {when} · "{note}"',

  'agent.lockout.queue.action.approve':  'Approve',
  'agent.lockout.queue.action.reject':   'Reject',
  'agent.lockout.queue.action.markPaid': 'Mark paid',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'Enter the UPI transaction ID you used to pay the player:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'Enter the UPI transaction ID you used to pay the sub-agent:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'Reject this withdrawal? The held amount refunds to the player.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'Reject this sub-agent withdrawal?',
  'agent.lockout.queue.toast.rechargeApproved': 'Recharge approved — player credited',
  'agent.lockout.queue.toast.rechargeRejected': 'Recharge rejected',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'Sub-agent recharge approved',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'Recharge rejected',
  'agent.lockout.queue.toast.withdrawalPaid':   'Withdrawal marked paid — your wallet was credited',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'Sub-agent withdrawal marked paid — wallet credited',
  'agent.lockout.queue.toast.withdrawalRejected': 'Withdrawal rejected (refunded to player)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'Withdrawal rejected',
  'agent.lockout.queue.toast.actionFailed': 'Action failed',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player':   'Nothing from your players right now.',
  'agent.lockout.queue.empty.subAgent': 'Nothing from your sub-agents right now.',
  'agent.lockout.queue.loading':        'Loading queue…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta':       'Refresh — am I unlocked yet?',
  'agent.lockout.refresh.checking':  'Checking…',
  'agent.lockout.refresh.toast':     'Status refreshed',
  'agent.lockout.refresh.footnote':  'This page closes automatically the moment your wallet + deposit climbs back above zero.',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed':   'Could not load recharge details',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title':  'Pay now to unlock your account',
  'agent.emergencyForm.banner.locked.body':   'Your players have won more than your deposit collateral can cover. You cannot use the platform until you clear your negative balance.',
  'agent.emergencyForm.banner.warning.title': 'Top up soon — you are close to the limit',
  'agent.emergencyForm.banner.warning.body':  'Your wallet has crossed 80% of your deposit. Top up before it reaches 100% — once it does, your account will be locked.',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet':  'Your wallet',
  'agent.emergencyForm.row.deposit': 'Your deposit',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked':  "Why you're stuck",
  'agent.emergencyForm.tier.eyebrow.warning': "Why you're at risk",
  'agent.emergencyForm.tier.context.locked':  "Your wallet went {wallet} — that's beyond your deposit safety cushion of {deposit}. The platform locks you here so your downline's losses can't keep mounting against an empty buffer.",
  'agent.emergencyForm.tier.context.warning': "Your wallet went {wallet} — that's more than 80% of your deposit safety cushion of {deposit}. One more bad settlement will push you over 100% and lock the account.",
  'agent.emergencyForm.tier.pickHowMuch':     'Pick how much to pay',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked':  'Just unlock',
  'agent.emergencyForm.tier.bareMin.label.warning': 'Just escape warning',
  'agent.emergencyForm.tier.bareMin.tag':           'Risky',
  'agent.emergencyForm.tier.bareMin.note.locked':   'Only brings your wallet + deposit back to ₹0. Any new loss locks you again instantly.',
  'agent.emergencyForm.tier.bareMin.note.warning':  'Only brings you just back over the 80% line. Any new loss re-triggers the warning — or pushes you into lockout.',
  'agent.emergencyForm.tier.safeMin.label':         'Safe minimum',
  'agent.emergencyForm.tier.safeMin.tag':           'Recommended',
  'agent.emergencyForm.tier.safeMin.note':          'Your deposit ({deposit}) + half your negative wallet ({half}). Leaves you with a real cushion.',
  'agent.emergencyForm.tier.full.label':            'Full recovery',
  'agent.emergencyForm.tier.full.tag':              'Best',
  'agent.emergencyForm.tier.full.note':             'Deposit + your full negative wallet ({neg}). Deposit fully restored AND wallet back to zero.',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin':       'Pay your Admin',
  'agent.emergencyForm.parent.label.parentAgent': 'Pay your parent agent',
  'agent.emergencyForm.parent.role.admin':        'Platform Admin',
  'agent.emergencyForm.parent.role.parentAgent':  'Parent agent',
  'agent.emergencyForm.parent.upi':               'UPI:',
  'agent.emergencyForm.parent.whatsapp':          'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile':         "Your parent's payment details aren't on file yet. Please contact platform support directly.",

  // Submit form.
  'agent.emergencyForm.field.amount.label':   'Amount paid (₹)',
  'agent.emergencyForm.field.txn.label':      'UPI transaction ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'e.g. 412312345678',
  'agent.emergencyForm.field.note.label':     'Note (optional)',
  'agent.emergencyForm.field.note.placeholder': "Anything your parent should know",
  'agent.emergencyForm.validation.amount':    'Enter the amount you paid (minimum ₹10)',
  'agent.emergencyForm.validation.txn':       'Enter the UPI transaction ID / UTR (at least 6 characters)',
  'agent.emergencyForm.cta':                  'Submit recharge request',
  'agent.emergencyForm.ctaSubmitting':        'Submitting…',
  // Success state.
  'agent.emergencyForm.success.title':        'Recharge submitted',
  'agent.emergencyForm.success.bodyAdmin':    'Request #{id} is waiting for the Admin to approve it. Once approved, your wallet is credited and your account unlocks automatically.',
  'agent.emergencyForm.success.bodyAgent':    'Request #{id} is waiting for @{username} to approve it. Once approved, your wallet is credited and your account unlocks automatically.',
  'agent.emergencyForm.toast.submitted':      'Recharge request #{id} submitted. Waiting for approval.',
  'agent.emergencyForm.toast.cantSave':       'Could not save language',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title':            'One-time setup: confirm your location',
  'agent.geo.subtitle':         'Required before you can use the dashboard.',
  'agent.geo.body.primary':     'The Baali Games platform routes new players to the agent physically nearest to them. Until you share your exact GPS location, you will not appear in the locator and will receive zero new players from organic signups — you only get players who type your referral code directly.',
  'agent.geo.body.secondary':   'Tap "Use my current location" once. We capture only your coordinates, never anything else.',
  'agent.geo.hint.disabled':    'Tap "Use my current location" above. A pincode or manual area pick alone is not enough — the locator needs precise GPS coordinates.',
  'agent.geo.error.fallback':   'Could not save location. Please try again.',
  'agent.geo.cta.save':         'Save and continue',
  'agent.geo.cta.saving':       'Saving location…',
  'agent.geo.toast.success':    'Location saved — you can now receive players.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label':       'Pool balance',
  'agent.wallet.hero.helper':      'Available to spend on player recharges and sub-agent top-ups. Earnings and admin top-ups land here.',

  'agent.wallet.action.recharge':  'Request Recharge',
  'agent.wallet.action.withdraw':  'Request Withdrawal',

  // Sub-tabs.
  'agent.wallet.tab.pending':      'Pending',
  'agent.wallet.tab.history':      'History',
  // Phase 17.2 — Deposit ledger tab (security-deposit collateral).
  'agent.wallet.tab.deposit':      'Deposit',
  'agent.wallet.deposit.currentLabel': 'Current deposit',
  'agent.wallet.deposit.subtitle':     'Security deposit collateral parked with the platform.',
  'agent.wallet.deposit.empty':        'No deposit movements yet.',
  'agent.wallet.deposit.kind.in':           'Deposit in',
  'agent.wallet.deposit.kind.outToSettle':  'Deposit used to settle',
  'agent.wallet.deposit.kind.refundOnExit': 'Deposit refunded on exit',
  'agent.wallet.deposit.kind.withdrawal':   'Deposit withdrawal',
  'agent.wallet.deposit.entry.manualAdmin':         'Manual entry by admin',
  'agent.wallet.deposit.entry.manualAdminWithdraw': 'Manual withdrawal by admin',
  'agent.wallet.deposit.entry.manualParent':        'Manual entry by parent agent',
  'agent.wallet.deposit.entry.request':             'Approved request',
  'agent.wallet.deposit.entry.exit':                'Exit refund',
  'agent.wallet.deposit.entry.settlement':          'Platform settlement',
  'agent.wallet.deposit.entry.system':              'System event',

  // Pending feed.
  'agent.wallet.pending.loading':  'Loading…',
  'agent.wallet.pending.empty':    'No pending requests right now.',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title':   'Recharge request · +{amount}',
  'agent.wallet.pending.recharge.meta':    'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'Withdrawal request · −{amount}',
  'agent.wallet.pending.withdrawal.meta':  'To {upi} · {when}',
  'agent.wallet.pending.badge':            'Pending',

  // History feed.
  'agent.wallet.history.loading':  'Loading…',
  'agent.wallet.history.empty':    'No wallet activity yet.',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'Recharge accepted · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'Recharge rejected · {amount}',
  'agent.wallet.history.recharge.meta':          'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid':     'Withdrawal paid · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'Withdrawal rejected · {amount}',
  'agent.wallet.history.withdrawal.meta':          'To {upi} · {when}',
  'agent.wallet.history.withdrawal.paidTxn':       'paid · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'Accepted',
  'agent.wallet.state.paid':     'Paid',
  'agent.wallet.state.rejected': 'Rejected',
  'agent.wallet.state.expired':  'Expired',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin':   'Topup from admin',
  'agent.wallet.ledger.kind.topupToPlayer':    'Topup to player',
  'agent.wallet.ledger.kind.topupFromPlayer':  'Player recharge',
  'agent.wallet.ledger.kind.revenueShare':     'Revenue share',
  'agent.wallet.ledger.kind.coFundOffer':      'Co-fund offer',
  'agent.wallet.ledger.kind.adminAdjust':      'Admin adjustment',
  'agent.wallet.ledger.kind.withdrawalHold':   'Withdrawal hold',
  'agent.wallet.ledger.kind.withdrawalPaid':   'Withdrawal paid',
  'agent.wallet.ledger.kind.withdrawalRefund': 'Withdrawal refund',
  'agent.wallet.ledger.balanceAfter':          'bal {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label':   'Security deposit',
  'agent.deposit.hero.helper':  'Collateral against your pool. Stays untouched as long as your wallet is in good standing.',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe':    'Safe',
  'agent.deposit.margin.warning': 'Warning',
  'agent.deposit.margin.locked':  'Locked',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'Recharge deposit',
  'agent.deposit.action.withdraw': 'Withdraw deposit',

  // Coverage gauge.
  'agent.deposit.gauge.label':   'Coverage gauge',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn':  'warn',
  'agent.deposit.gauge.thresholdLock':  'lock',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': "At {pct}, you'll get a warning",
  'agent.deposit.gauge.legend.lock': 'At {pct}, your account locks',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked':  'Pool fully covered by deposit — withdrawals are blocked until you top up.',
  'agent.deposit.gauge.caption.warning': 'Pool has consumed {pct}% of your deposit. Top up before it hits 100% to avoid lockout.',
  'agent.deposit.gauge.caption.caution': "Pool is {pct}% into deposit coverage. You're still safely below the 80% warning.",
  'agent.deposit.gauge.caption.safe':    'Your deposit is fully intact.',

  // Ledger feed.
  'agent.deposit.ledger.loading':         'Loading…',
  'agent.deposit.ledger.empty.title':     'No deposit activity yet',
  'agent.deposit.ledger.empty.body':      'Once the admin credits your deposit (or processes a refund), every entry will appear here.',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet':  'Recharge your wallet',
  'agent.recharge.banner.eyebrow.deposit': 'Recharge your deposit',
  'agent.recharge.banner.body.wallet':     'Pay your upline first, then submit the proof here so they can top up your operating wallet.',
  'agent.recharge.banner.body.deposit':    'Pay your upline first, then submit the proof here so they can top up your security deposit.',
  'agent.recharge.banner.closeAria':       'Close form',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin':       'Pay your Admin',
  'agent.recharge.parent.label.parentAgent': 'Pay your parent agent',
  'agent.recharge.parent.role.admin':        'Platform Admin',
  'agent.recharge.parent.role.parentAgent':  'Parent agent',
  'agent.recharge.parent.noQr':              'No QR set',
  'agent.recharge.parent.upi':               'UPI:',
  'agent.recharge.parent.loading':           "Loading your upline's payment details…",
  'agent.recharge.parent.loadError':         'Could not load your upline payment details',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow':  'How did you pay?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note':  'Online digital payment',
  'agent.recharge.method.cash.label': 'Cash',
  'agent.recharge.method.cash.note':  'In-person hand-off',

  // Form fields.
  'agent.recharge.field.amount.label':       'Amount (₹) · minimum ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi':      'UPI transaction ID / UTR',
  'agent.recharge.field.txn.label.cash':     'Cash receipt marker (any short reference)',
  'agent.recharge.field.txn.placeholder.upi':  'e.g. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'e.g. CASH-Sun-eve',
  'agent.recharge.field.note.label':         'Note (optional, shown to your upline)',
  'agent.recharge.field.note.placeholder':   'Any context for your upline',

  // Validation errors.
  'agent.recharge.error.amountMin':  'Amount must be a whole rupee ≥ 10',
  'agent.recharge.error.txnUpi':     'Transaction ID looks too short — paste the UPI reference',
  'agent.recharge.error.txnCash':    'Add a short marker (e.g. "CASH-Sun-eve") so your upline can match the hand-off',

  // CTAs.
  'agent.recharge.cta.submit': 'Submit request',
  'agent.recharge.cta.cancel': 'Cancel',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet':  'Withdraw from wallet',
  'agent.withdrawal.banner.eyebrow.deposit': 'Withdraw from deposit',
  'agent.withdrawal.banner.body.wallet':     "The amount is held instantly so you can't double-spend. Your upline pays you and marks paid.",
  'agent.withdrawal.banner.body.deposit':    'The amount is held from your security deposit immediately. Your upline pays you and marks it paid.',
  'agent.withdrawal.banner.closeAria':       'Close form',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow':    'How do you want to be paid?',
  'agent.withdrawal.method.upi.label':  'UPI / QR',
  'agent.withdrawal.method.upi.note':   'Online digital payment',
  'agent.withdrawal.method.cash.label': 'Cash',
  'agent.withdrawal.method.cash.note':  'In-person hand-off',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label':        'Amount (₹) · available {balance}',
  'agent.withdrawal.field.amount.placeholder':  '500',
  'agent.withdrawal.field.upi.label':           'Your UPI ID (where your upline will pay you)',
  'agent.withdrawal.field.upi.placeholder':     'you@bank',
  'agent.withdrawal.field.cashMarker.label':    'Meeting marker (e.g. "Sun evening cash")',
  'agent.withdrawal.field.cashMarker.placeholder': 'When/where to hand over the cash',
  'agent.withdrawal.field.whatsapp.label':      'WhatsApp (optional, for your upline to reach you)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label':          'Note (optional)',
  'agent.withdrawal.field.note.placeholder':    'Any context for your upline',

  // Validation errors.
  'agent.withdrawal.error.amountMin':       'Amount must be a whole rupee ≥ 10',
  'agent.withdrawal.error.amountOverflow':  'You only have {balance} available',
  'agent.withdrawal.error.upi':             'Enter a valid UPI ID',

  // CTAs.
  'agent.withdrawal.cta.submit': 'Submit request',
  'agent.withdrawal.cta.cancel': 'Cancel',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title':     'Players',
  'agent.people.hub.players.subtitle':  'Manage players and their requests',
  'agent.people.hub.players.subtab.requests': 'Payments',
  'agent.people.hub.players.subtab.list':     'My players',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title':      'Agents',
  'agent.people.hub.agents.subtitle':   'Manage sub-agents and their requests',
  'agent.people.hub.agents.subtab.requests': 'Agent requests',
  'agent.people.hub.agents.subtab.list':     'Agents',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label':       'Receiving new players',
  'agent.people.acceptingToggle.bodyAccepting':  "You're ACCEPTING new players from the geographic locator. Cold signups in your area route to you automatically.",
  'agent.people.acceptingToggle.bodyOptedOut':   "You've OPTED OUT. The locator is skipping you — only direct referrals + manually-assigned players will reach you. Existing players are unaffected.",
  'agent.people.acceptingToggle.error':       'Could not update',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop':   'Stop accepting new players',
  'agent.people.acceptingToggle.aria.start':  'Start accepting new players',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player':     'players',
  'agent.qr.audience.subAgent':   'agents',
  'agent.qr.alt':                 'Your payment QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player':    'My QR · what players see',
  'agent.qr.cardLabel.subAgent':  'My QR · what agents see',
  'agent.qr.upi.empty':           'No UPI set yet',
  // Status pill.
  'agent.qr.status.loading':      'Loading…',
  'agent.qr.status.live':         'Live',
  'agent.qr.status.notSet':       'Not set',
  'agent.qr.status.paused':       'Paused',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit':            'Edit QR & UPI',
  'agent.qr.cta.setup':           'Set up QR',
  'agent.qr.cta.viewOnly':        'View only — ask admin to edit',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title':         'Edit QR & UPI',
  'agent.qr.modal.closeAria':     'Close',
  // Upload + remove buttons.
  'agent.qr.modal.upload':        'Upload QR',
  'agent.qr.modal.replace':       'Replace QR',
  'agent.qr.modal.remove':        'Remove',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player':   'Remove the QR image? Your players will see the contact fallback only.',
  'agent.qr.modal.confirmRemove.subAgent': 'Remove the QR image? Your agents will see the contact fallback only.',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label':         'UPI ID',
  'agent.qr.modal.field.upi.placeholder':   'e.g. baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player':   'WhatsApp number (fallback)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'WhatsApp number for agents',
  'agent.qr.modal.field.whatsapp.placeholder.default':  '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'Blank → falls back to {number}',
  'agent.qr.modal.field.whatsapp.fallbackHint':         'Sub-agents will see {number} (your player-facing number).',
  'agent.qr.modal.field.phone.label.player':   'Support phone (fallback)',
  'agent.qr.modal.field.phone.label.subAgent': 'Support phone for agents',
  'agent.qr.modal.field.phone.placeholder.default':  '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'Blank → falls back to {number}',
  'agent.qr.modal.field.phone.fallbackHint':         'Sub-agents will see {number} (your player-facing number).',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label':        'Active',
  'agent.qr.modal.active.description':  'players can submit recharges',
  'agent.qr.modal.active.on':           'On',
  'agent.qr.modal.active.paused':       'Paused',

  // Footer buttons.
  'agent.qr.modal.cancel': 'Cancel',
  'agent.qr.modal.save':   'Save',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow':   'Manual recharge',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'Send money to a ',
  'agent.manualRecharge.banner.body.target': 'player',
  'agent.manualRecharge.banner.body.after':  ' straight from your pool.',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label':    'Your pool',
  'agent.manualRecharge.pool.dailyCap': 'cap · {amount}/day',

  // Player picker.
  'agent.manualRecharge.field.player.label':       'To player',
  'agent.manualRecharge.field.player.placeholder': 'Select a player…',
  'agent.manualRecharge.field.player.option':      '@{username} · {contact} · bal {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'To player',
  'agent.manualRecharge.field.player.lockedMeta':  '{contact} · current bal {balance}',
  'agent.manualRecharge.field.player.notFound':    'Player not found in your owned list.',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label':       'Amount (₹) — {min} to {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool':        'Exceeds your pool balance.',
  'agent.manualRecharge.error.belowMin':           'Below minimum ({min}).',
  'agent.manualRecharge.error.aboveMax':           'Above maximum ({max}).',

  // Note field.
  'agent.manualRecharge.field.note.label':       'Note (optional, shown on player ledger)',
  'agent.manualRecharge.field.note.placeholder': 'e.g. Cash collected at shop',

  // Loading + success states.
  'agent.manualRecharge.loading':       'Loading your pool…',
  'agent.manualRecharge.cta.cancel':    'Cancel',
  'agent.manualRecharge.cta.send':      'Send {amount}',
  'agent.manualRecharge.success.title': 'Sent {amount} to @{username}',
  'agent.manualRecharge.success.meta':  'Your pool · {pool} · their wallet · {playerBal}',
  'agent.manualRecharge.success.again': 'Send another',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow':   'Manual recharge',
  'agent.manualSubAgentRecharge.banner.body.before': 'Send money to a ',
  'agent.manualSubAgentRecharge.banner.body.target': 'sub-agent',
  'agent.manualSubAgentRecharge.banner.body.after':  ' straight from your pool.',

  'agent.manualSubAgentRecharge.pool.label':       'Your pool',

  'agent.manualSubAgentRecharge.field.subAgent.label':       'To sub-agent',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'Select a sub-agent…',
  'agent.manualSubAgentRecharge.field.subAgent.option':      '@{username} · {email} · pool {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'No active direct sub-agents',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'To sub-agent',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta':  '{email} · current pool {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound':    'Sub-agent not found in your direct downline.',

  'agent.manualSubAgentRecharge.field.amount.label':       'Amount (₹) — {min} to {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool':        'Exceeds your pool balance.',
  'agent.manualSubAgentRecharge.error.belowMin':           'Below minimum ({min}).',

  'agent.manualSubAgentRecharge.field.note.label':       'Note (optional, shown on both ledgers)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'e.g. Float advance',

  'agent.manualSubAgentRecharge.loading':       'Loading your pool…',
  'agent.manualSubAgentRecharge.cta.cancel':    'Cancel',
  'agent.manualSubAgentRecharge.cta.send':      'Send {amount}',
  'agent.manualSubAgentRecharge.success.title': 'Sent {amount} to @{username}',
  'agent.manualSubAgentRecharge.success.meta':  'Your pool · {parent} · their pool · {child}',
  'agent.manualSubAgentRecharge.success.again': 'Send another',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge':   'Recharge',
  'agent.playerRequests.direction.withdrawal': 'Withdrawal',
  'agent.playerRequests.tab.pending':          'Pending',
  'agent.playerRequests.tab.history':          'History',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading':         'Loading…',
  'agent.playerRequests.empty.pending.recharge':   'No pending recharges right now.',
  'agent.playerRequests.empty.pending.withdrawal': 'No pending withdrawals right now.',
  'agent.playerRequests.empty.history':            'No history yet.',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge':   'Rejection reason (optional):',
  'agent.playerRequests.prompt.rejectWithdrawal': "Rejection reason (the player's held balance will be refunded):",
  // Reject reason sheet (replaces window.prompt — 2026-08-06).
  'agent.playerRequests.reject.title.recharge':    'Reject this recharge?',
  'agent.playerRequests.reject.title.withdrawal':  'Reject this withdrawal?',
  'agent.playerRequests.reject.body.recharge':     'The request is declined. No money moves. You can add a reason for the player.',
  'agent.playerRequests.reject.body.withdrawal':   "The request is declined and the player's held balance is refunded to their wallet.",
  'agent.playerRequests.reject.reasonLabel':       'Reason (optional)',
  'agent.playerRequests.reject.reasonPlaceholder': 'e.g. wrong amount, no payment received…',

  // Toasts.
  'agent.playerRequests.toast.rejected':              'Rejected',
  'agent.playerRequests.toast.rejectedRefunded':      'Rejected · held balance refunded',
  'agent.playerRequests.toast.approved':              'Approved {amount}',
  'agent.playerRequests.toast.approvedWithBonus':     'Approved {amount} · principal + ₹{bonus} bonus',
  'agent.playerRequests.toast.markedPaid':            'Marked paid {amount}',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel':            'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel':          'Pay to (UPI)',
  'agent.playerRequests.card.whatsapp':               'WhatsApp',
  'agent.playerRequests.card.viewQr':                 'View QR',
  'agent.playerRequests.card.bonusChip':              'bonus',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved':        'Approved {approved} of requested {requested}',
  'agent.playerRequests.card.partialPaid':            'Paid {approved} of requested {requested} · refunded {refund}',
  'agent.playerRequests.card.paidTxnPrefix':          'paid · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject':     'Reject',
  'agent.playerRequests.action.approve':    'Approve',
  'agent.playerRequests.action.edit':       'Edit',
  'agent.playerRequests.action.markPaid':   'Mark paid',
  'agent.playerRequests.state.pending':       'Pending',
  'agent.playerRequests.state.rejected':      'Rejected',
  'agent.playerRequests.state.expired':       'Expired',
  'agent.playerRequests.state.accepted':      'Accepted',
  'agent.playerRequests.state.manuallyPaid':  'Manually paid',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination':  '{from}–{to} of {total}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge':   'Recharge',
  'agent.agentRequests.direction.withdrawal': 'Withdrawal',
  'agent.agentRequests.tab.pending':          'Pending',
  'agent.agentRequests.tab.history':          'History',
  'agent.agentRequests.loading':         'Loading…',
  'agent.agentRequests.empty.pending.recharge':   'No pending recharges right now.',
  'agent.agentRequests.empty.pending.withdrawal': 'No pending withdrawals right now.',
  'agent.agentRequests.empty.history':            'No history yet.',

  'agent.agentRequests.prompt.rejectRecharge':   'Rejection reason (optional):',
  'agent.agentRequests.prompt.rejectWithdrawal': "Rejection reason (the sub-agent's held balance will be refunded):",
  // Reject reason sheet (replaces window.prompt — 2026-08-06).
  'agent.agentRequests.reject.title.recharge':    'Reject this recharge?',
  'agent.agentRequests.reject.title.withdrawal':  'Reject this withdrawal?',
  'agent.agentRequests.reject.body.recharge':     'The request is declined. No money moves. You can add a reason.',
  'agent.agentRequests.reject.body.withdrawal':   "The request is declined and the sub-agent's held balance is refunded to their pool.",
  'agent.agentRequests.reject.reasonLabel':       'Reason (optional)',
  'agent.agentRequests.reject.reasonPlaceholder': 'e.g. wrong amount, no payment received…',

  'agent.agentRequests.toast.rejected':         'Rejected',
  'agent.agentRequests.toast.rejectedRefunded': 'Rejected · held balance refunded',
  'agent.agentRequests.toast.approved':         'Approved {amount} · pool credited',
  'agent.agentRequests.toast.markedPaid':       'Marked paid {amount}',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill':       'agent',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel':         'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel':     'Cash marker',
  'agent.agentRequests.card.payToUpiLabel':       'Pay to (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel':  'Hand-off marker',
  'agent.agentRequests.card.whatsapp':            'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved':     'Approved {approved} of requested {requested}',
  'agent.agentRequests.card.partialPaid':         'Paid {approved} of requested {requested} · refunded {refund}',
  'agent.agentRequests.card.paidTxnPrefix':       'paid · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit':  'Deposit',
  'agent.agentRequests.card.chip.cash':     'Cash',
  'agent.agentRequests.card.chip.upi':      'UPI',

  'agent.agentRequests.action.reject':    'Reject',
  'agent.agentRequests.action.approve':   'Approve',
  'agent.agentRequests.action.edit':      'Edit',
  'agent.agentRequests.action.markPaid':  'Mark paid',
  'agent.agentRequests.state.pending':      'Pending',
  'agent.agentRequests.state.rejected':     'Rejected',
  'agent.agentRequests.state.expired':      'Expired',
  'agent.agentRequests.state.accepted':     'Accepted',
  'agent.agentRequests.state.manuallyPaid': 'Manually paid',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add':         'Add player',
  'agent.playersList.loading':     'Loading…',
  'agent.playersList.empty':       'No players match',
  'agent.playersList.results':     'Showing {from}–{to} of {total}',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'Search username, email, phone…',

  // Status filter pills.
  'agent.playersList.filter.all':       'All',
  'agent.playersList.filter.active':    'Active',
  'agent.playersList.filter.pending':   'Pending',
  'agent.playersList.filter.suspended': 'Suspended',
  'agent.playersList.filter.banned':    'Banned',

  // Empty-results panel.
  'agent.playersList.empty.title': 'No players found',
  'agent.playersList.empty.body':  'Try a different search or status filter.',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'Page {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active':    'active',
  'agent.playersList.card.status.pending':   'pending',
  'agent.playersList.card.status.suspended': 'suspended',
  'agent.playersList.card.status.banned':    'banned',
  'agent.playersList.card.verifiedBadge':    'verified',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet':  'Wallet',
  'agent.playersList.column.joined':  'Joined',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro':     'Agents you create work under you. They start with 0% on every pillar — grant rates per game from their detail page.',
  'agent.subAgentsList.cta.new':   'New sub-agent',
  'agent.subAgentsList.loading':   'Loading…',
  'agent.subAgentsList.searchPlaceholder': 'Search name, @username, phone…',
  'agent.subAgentsList.noMatch':   'No match. Try a different name or number.',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title':            'No sub-agents yet',
  'agent.subAgentsList.empty.body.canCreate':   'Tap "+ New sub-agent" to create one. They\'ll show up here with their own pool, players, and earnings — managed by you.',
  'agent.subAgentsList.empty.body.cantCreate':  "Your admin hasn't granted you sub-agent creation rights yet. Ask them to flip create_subagent to \"edit\" on your permissions.",

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'suspended',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents':       'Agents',
  'agent.subAgentsList.column.players':      'Players',
  'agent.subAgentsList.column.pool':         'Pool',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel':      'direct',
  'agent.subAgentsList.row.indirectLabel':    'indirect',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title':  'New sub-agent',
  'agent.subAgentsList.modal.body':   "They'll start at 0% on every pillar. Grant their rates per game from their detail page after creating them.",
  'agent.subAgentsList.modal.field.username.label':       'Username',
  'agent.subAgentsList.modal.field.username.placeholder': 'alphanumeric, ._- allowed; min 3 chars',
  'agent.subAgentsList.modal.field.email.label':          'Email',
  'agent.subAgentsList.modal.field.email.placeholder':    'they@example.com',
  'agent.subAgentsList.modal.field.password.label':       'Initial password',
  'agent.subAgentsList.modal.field.password.placeholder': 'min 8 characters',
  'agent.subAgentsList.modal.field.password.hint':        'Share this with the new agent so they can log in. They should change it on first login.',
  'agent.subAgentsList.modal.field.location.label':       "Sub-agent's location",
  'agent.subAgentsList.modal.field.location.optional':    '(strongly recommended)',
  'agent.subAgentsList.modal.field.location.missing':     "Without a location, the geographic locator can't route any new players to this sub-agent. They'll only receive players via direct referral until they sign in and complete their own GPS.",
  'agent.subAgentsList.modal.cta.cancel':   'Cancel',
  'agent.subAgentsList.modal.cta.create':   'Create sub-agent',
  'agent.subAgentsList.modal.cta.creating': 'Creating…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge':    'Approve recharge from {who}',
  'agent.approvalEdit.title.withdrawal':  'Mark withdrawal paid for {who}',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel':    'Requested:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'Approve amount (₹)',
  'agent.approvalEdit.field.amount.hint':  'Cannot exceed requested {amount}.',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund':   '{refund} will be refunded to {who}\'s wallet automatically.',
  'agent.approvalEdit.partial.recharge.bonusWarn':  'Partial approval. Any attached bonus will be auto-rejected.',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label':         'Reason',
  'agent.approvalEdit.field.reason.requiredHint':  '(required, ≥5 chars)',
  'agent.approvalEdit.field.reason.optionalHint':  '(optional)',
  'agent.approvalEdit.field.reason.placeholder.partial':  'e.g. Player only paid 30 rupees',
  'agent.approvalEdit.field.reason.placeholder.optional': 'Optional note',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label':       'UPI transaction ID',
  'agent.approvalEdit.field.txn.placeholder': 'e.g. 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'Your pool ({balance}) cannot cover this approval. Top up first.',

  // Validation errors.
  'agent.approvalEdit.error.amountMin':      'Approved amount must be at least ₹1',
  'agent.approvalEdit.error.amountMax':      'Approved amount cannot exceed requested {amount}',
  'agent.approvalEdit.error.reasonRequired': 'Reason (≥5 chars) is required when approving a different amount',
  'agent.approvalEdit.error.txnRequired':    'UPI transaction ID is required (≥6 chars)',
  'agent.approvalEdit.error.fallback':       'Failed — try again',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel':       'Cancel',
  'agent.approvalEdit.cta.approve':      'Approve',
  'agent.approvalEdit.cta.markPaid':     'Mark paid',
  'agent.approvalEdit.cta.submitting':   'Submitting…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title':         'Money',
  'agent.profile.hub.subtitle':      'Wallet, deposit, rates and account in one place',
  'agent.profile.hub.tab.wallet':    'Wallet',
  'agent.profile.hub.tab.deposit':   'Deposit',
  'agent.profile.hub.tab.account':   'Account',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta':            'Sign out',
  'agent.profile.signOut.modal.title':    'Sign out?',
  'agent.profile.signOut.modal.body':     'You will need to sign in again to access your account.',
  'agent.profile.signOut.modal.stay':     'Stay signed in',
  'agent.profile.signOut.modal.confirm':  'Yes, sign out',
  'agent.profile.signOut.modal.busy':     'Signing out…',
  'agent.profile.signOut.error':          'Could not sign out',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn':            'Not signed in.',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent':        'Agent',
  'agent.account.hero.role.admin':        'Platform admin',
  'agent.account.hero.changePhoto':       'Change photo',
  'agent.account.hero.removePhoto':       'Remove photo',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle':       'Adjust your photo',
  'agent.account.avatar.confirmRemove':   'Remove your profile photo?',
  'agent.account.toast.photoUpdated':     'Photo updated',
  'agent.account.toast.photoRemoved':     'Photo removed',
  'agent.account.toast.saved':            'Saved',
  'agent.account.toast.locationSaved':    'Location saved',
  'agent.account.toast.passwordChanged':  'Password changed — sign in again',
  'agent.account.error.uploadFailed':     'Upload failed',
  'agent.account.error.removeFailed':     'Failed',
  'agent.account.error.saveFailed':       'Save failed',
  'agent.account.error.changeFailed':     'Change failed',

  // Account info card (read-only platform metadata).
  'agent.account.info.title':             'Account info',
  'agent.account.info.subtitle':          'Read-only platform metadata',
  'agent.account.info.memberSince':       'Member since',
  'agent.account.info.lastLogin':         'Last login',
  'agent.account.info.lastLoginIp':       'Last login IP',
  'agent.account.info.reportsTo':         'Reports to',
  'agent.account.info.reportsToValue':    'Admin #{id}',
  'agent.account.info.agentId':           'Agent ID',
  'agent.account.info.firstLogin':        'first time',
  'agent.account.info.emptyDash':         '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title':                   'Personal details',
  'agent.account.personal.subtitle.locked':         'Identity locked by admin · view-only',
  'agent.account.personal.subtitle.agent':          'Username, email, and contact number',
  'agent.account.personal.subtitle.admin':          'Username and email',
  'agent.account.personal.field.username':          'Username',
  'agent.account.personal.field.email':             'Email',
  'agent.account.personal.field.phone':             'Contact number',
  'agent.account.personal.field.phone.placeholder': 'e.g. +91 9876543210',
  'agent.account.personal.field.phone.hint':        'Players and admin reach you on this number. Leave blank to clear.',
  'agent.account.personal.fieldLockedNote':         'locked',
  'agent.account.personal.cta.save':                'Save changes',
  'agent.account.personal.cta.saving':              'Saving…',
  'agent.account.personal.cta.noChanges':           'No changes',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title':              'Your location',
  'agent.account.location.subtitle.summary':   'The locator routes new players to your nearest area',
  'agent.account.location.subtitle.edit':      'Update your area so the locator routes the right players to you',
  'agent.account.location.cta.edit':           'Edit',
  'agent.account.location.cta.cancel':         'Cancel',
  'agent.account.location.cta.save':           'Save',
  'agent.account.location.areaLabel':          'Area',
  'agent.account.location.gpsLabel':           'GPS',
  'agent.account.location.setLabel':           'Location set',
  'agent.account.location.setBody':            'GPS coordinates on file — tap Edit to view the area details.',
  'agent.account.location.lastUpdated':        'Last updated · {date}',

  // Security card (password change).
  'agent.account.security.title':                   'Security',
  'agent.account.security.subtitle.locked':         'Locked by admin · view-only',
  'agent.account.security.subtitle.unlocked':       'Change your password · signs out other devices',
  'agent.account.security.field.current':           'Current password',
  'agent.account.security.field.new':               'New password',
  'agent.account.security.field.new.hint':          'At least 8 characters, with one letter and one digit',
  'agent.account.security.field.confirm':           'Confirm new',
  'agent.account.security.cta.update':              'Update password',
  'agent.account.security.cta.updating':            'Updating…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch':          'New passwords do not match',
  'agent.account.security.error.tooShort':          'Password must be ≥ 8 characters',
  'agent.account.security.error.weak':              'Password must contain at least one letter and one number',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow':          'Shareable Agent ID',
  'agent.shareableId.tooltip.copy':     'Copy UAID',
  'agent.shareableId.tooltip.copied':   'UAID copied',
  'agent.shareableId.cta.copy':         'Copy',
  'agent.shareableId.cta.copied':       'Copied!',
  'agent.shareableId.body.before':      'Share this code with another agent so they can transfer players to you. It is safe to send via chat or WhatsApp — sharing it does ',
  'agent.shareableId.body.not':         'not',
  'agent.shareableId.body.after':       ' grant any access to your account.',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title':            'Rewards',
  'agent.rewards.hub.subtitle':         'Browse offers and track your exposure',
  'agent.rewards.hub.tab.browse':       'Browse offers',
  'agent.rewards.hub.tab.exposure':     'My exposure',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn':       'Opted in',
  'agent.rewards.status.optedOut':      'Opted out',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow':         'Featured for your players',
  'agent.rewards.browse.empty.title':          'No active rewards right now',
  'agent.rewards.browse.empty.body':           "Admin posts new offers regularly. Check back soon — or ask your upline to opt you in to anything you've missed.",
  'agent.rewards.browse.error.loadFailed':     'Could not load rewards',
  'agent.rewards.browse.card.aria.manage':     'Manage opt-in for {title}',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn':         'Opted in',
  'agent.rewards.browse.chip.optInPrompt':     'Tap to opt in',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix':     '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading':            'Computing your exposure…',
  'agent.rewards.exposure.error.loadFailed':   'Could not load exposure',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow':                 'Offer exposure',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one':        '{count} active opt-in',
  'agent.rewards.exposure.hero.activeOptIns.other':      '{count} active opt-ins',

  'agent.rewards.exposure.hero.actualSpend.label':       'Actual spend',
  'agent.rewards.exposure.hero.actualSpend.helper':      'Claimed by your downline so far.',

  'agent.rewards.exposure.hero.maxExposure.label':       'Max exposure',
  'agent.rewards.exposure.hero.maxExposure.helper':      'Worst-case ceiling — every remaining budget rupee claimed.',

  'agent.rewards.exposure.hero.ceiling.label':           'Ceiling consumed',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label':                  'Risk gauge',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix':              '/ {amount} max',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before':    'At ',
  'agent.rewards.exposure.gauge.legend.amber.value':     '50%',
  'agent.rewards.exposure.gauge.legend.amber.after':     ', watch your opt-ins',
  'agent.rewards.exposure.gauge.legend.rose.before':     'At ',
  'agent.rewards.exposure.gauge.legend.rose.value':      '80%',
  'agent.rewards.exposure.gauge.legend.rose.after':      ", you're near your ceiling",
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax':          "You're not opted into anything that has a projected ceiling yet.",
  'agent.rewards.exposure.gauge.caption.high':           "You've burned {pct}% of your max exposure. Most opt-ins are heavily claimed.",
  'agent.rewards.exposure.gauge.caption.mid':            "You're at {pct}% of your max ceiling. Keep an eye on the per-offer breakdown.",
  'agent.rewards.exposure.gauge.caption.low':            'Comfortable — only {pct}% of your max ceiling consumed so far.',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading':             'How max exposure is calculated:',
  'agent.rewards.exposure.footnote.prefix':              ' For each opted-in offer: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula':             'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix':              ' — the conservative upper bound. Your actual liability is usually ',
  'agent.rewards.exposure.footnote.emphasis':            'less',
  'agent.rewards.exposure.footnote.suffix2':             ' when sub-agents below you also opt in (they pay the bigger slice; you pay the residual).',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title':                  'No active opt-ins',
  'agent.rewards.exposure.empty.body':                   'Once you opt into an offer from the Browse offers tab, it appears here with your spend and projected exposure.',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share':                    '{pct}% share',
  'agent.rewards.exposure.row.claims.one':               '{count} claim',
  'agent.rewards.exposure.row.claims.other':             '{count} claims',
  'agent.rewards.exposure.row.overridden':               'admin-overridden',
  'agent.rewards.exposure.row.actualMaxLabel':           'Actual / Max',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash':                '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow':                    'Cost-share opt-in',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'Game profits',
  'agent.profile.rates.infoBanner.standardPctText': ' use standard percentages. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'Promotional costs',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (Offers, Cashbacks) use a Dynamic Upline Pool. Both are managed globally by the platform admin based on your level (L{level}).',
  'agent.profile.rates.section.gameProfits': 'Game Profits — what you earn per match',
  'agent.profile.rates.section.promotionalCosts': 'Promotional Costs — your upline subsidy contribution',
  'agent.profile.rates.pillar.platformFee': 'Platform Fee %',
  'agent.profile.rates.pillar.winLoss': 'Win / Loss Profit %',
  'agent.profile.rates.pillar.promoWeight': 'Promotional Pool Weight',
  'agent.profile.rates.guarantee.title': 'Direct Referral Guarantee',
  'agent.profile.rates.guarantee.body': 'If you directly referred the player claiming an offer, you pay ₹0.00. Your uplines subsidize 100% of the cost to help you grow your network.',
  'agent.profile.rates.poolExplainer.title': 'How the pool works:',
  'agent.profile.rates.poolExplainer.body': 'when a sub-agent\'s player claims an offer, your weight is added to a pool with the rest of the active upline + the platform. You pay (your weight / total pool) × the offer cost. Higher weight = larger share.',
  'agent.profile.rates.weightLabel': 'weight:',
  'agent.profile.rates.updatedPrefix': 'updated',
  'agent.profile.rates.notSetYet': 'not set yet',
  'agent.rewards.optInModal.guarantee.title': 'Direct Referral Guarantee',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'If <strong>you directly referred</strong> the player claiming this offer, you pay <strong>₹0.00</strong>. Your uplines subsidize 100% of the cost to help you grow your network.',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'When <strong>your players</strong> claim this offer, you pay <strong>₹0.00</strong>. Your uplines subsidize 100% of the cost to help you grow your network.',
  'agent.rewards.optInModal.uplineReality.title': 'Upline Reality Check',
  'agent.rewards.optInModal.uplineReality.body': 'You have <strong>{count}</strong> direct sub-agent{plural}. If a sub-agent\'s player claims this offer, you will pay a proportional share based on your <strong>Pool Weight ({weight})</strong> compared to the rest of the active upline and the platform.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'Direct',
  'agent.rates.col.indirect': 'Indirect',
  'agent.rates.indirect.dynamic': 'Dynamic',
  'agent.rates.indirect.notApplicable': 'N/A',
  'agent.rates.indirect.rangeTo': 'to',
  'agent.rewards.optInModal.close':                      'Close',
  'agent.rewards.optInModal.cancel':                     'Cancel',
  'agent.rewards.optInModal.loading':                    'Loading offer details…',
  'agent.rewards.optInModal.error.loadFailed':           'Could not load offer details',
  'agent.rewards.optInModal.error.optInFailed':          'Opt-in failed',
  'agent.rewards.optInModal.error.optOutFailed':         'Opt-out failed',
  'agent.rewards.optInModal.error.saveFailed':           'Could not save sub-agent rates',
  'agent.rewards.optInModal.toast.optedIn':              'Opted in. Your downline can now see this offer.',
  'agent.rewards.optInModal.toast.optedOut':             'Opted out. Offer removed from your downline.',
  'agent.rewards.optInModal.toast.saved.one':            'Saved {count} sub-agent rate change.',
  'agent.rewards.optInModal.toast.saved.other':          'Saved {count} sub-agent rate changes.',
  'agent.rewards.optInModal.confirmOptOut':              'Opt out of this offer? Your downline will no longer see it.',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote':                  'Set via opt-in modal for offer #{id}',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel':           'Your share',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked':               'Locked at opt-in',
  'agent.rewards.optInModal.stake.current':              'Current rate',
  'agent.rewards.optInModal.stake.noRate':               "Admin hasn't granted you a rate yet",
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label':     'Spent so far',
  'agent.rewards.optInModal.stake.spentSoFar.helper':    'Cumulative rupees already paid out of your wallet for downline claims on this offer.',
  'agent.rewards.optInModal.stake.maxPossible.label':    'Max possible',
  'agent.rewards.optInModal.stake.maxPossible.helper':   'Worst-case rupees you could pay if every remaining budget rupee is claimed.',
  'agent.rewards.optInModal.stake.maxPossible.noCap':    '— no budget cap —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase':            'Worst case: if every remaining ₹{amount} of platform budget is claimed by your downline, you pay {pct}% of it.',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn':       'You are opted in',
  'agent.rewards.optInModal.toggle.title.optedOut':      'Opt in to share with your downline',
  'agent.rewards.optInModal.toggle.body.optedIn':        'Your downline players see this offer. You co-fund every claim from them at your locked share.',
  'agent.rewards.optInModal.toggle.body.optedOut':       'Until you opt in, none of your players see this offer. Once you opt in, your share rate is LOCKED for this offer.',
  'agent.rewards.optInModal.toggle.optOutLink':          'Opt out of this offer',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title':            'Sub-agent rates',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'Max each can take: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value':  '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved':          '{count} unsaved',
  'agent.rewards.optInModal.subAgents.empty':            'No direct sub-agents.',
  'agent.rewards.optInModal.subAgents.cascadeNote':      'Saving cascades — if you raise a child here that already has deeper sub-agents, those rates may be auto-clamped to keep the chain consistent.',
  'agent.rewards.optInModal.subAgents.noChildren':       'You have no direct sub-agents. The offer is visible to your own players; admin pays the full residual.',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired':         'Enter a rate (0 to clear)',
  'agent.rewards.optInModal.error.numbersOnly':          'Numbers only',
  'agent.rewards.optInModal.error.negative':             'Cannot be negative',
  'agent.rewards.optInModal.error.max100':               'Max 100',
  'agent.rewards.optInModal.error.aboveCeiling':         'Max {ceiling}% (your share)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving':                 'Saving…',
  'agent.rewards.optInModal.cta.done':                   'Done',
  'agent.rewards.optInModal.cta.save.one':               'Save {count} change',
  'agent.rewards.optInModal.cta.save.other':             'Save {count} changes',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title':     'Recharge a player',
  'agent.adminRechargePlayer.subtitle':  "From your pool to a player's wallet",
  'agent.adminRechargePlayer.backLink':  'Back to player',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title':                     'Onboard a new player',
  'agent.createPlayer.subtitle.agent':            'They’ll be auto-owned by you.',
  'agent.createPlayer.subtitle.admin':            'Pick an owning agent below, or leave blank for platform-direct.',
  'agent.createPlayer.close':                     'Close',
  'agent.createPlayer.section.identity.title':    'Identity',
  'agent.createPlayer.section.identity.hint':     'Required to create the account',
  'agent.createPlayer.field.username.label':      'Username',
  'agent.createPlayer.field.username.placeholder':'kumar_42',
  'agent.createPlayer.field.email.label':         'Email',
  'agent.createPlayer.field.email.placeholder':   'kumar@example.com',
  'agent.createPlayer.field.phone.label':         'Mobile',
  'agent.createPlayer.field.phone.placeholder':   '9876543210',
  'agent.createPlayer.field.phone.hint':          '10-digit India mobile, +91 added automatically',
  'agent.createPlayer.field.password.label':      'Initial password',
  'agent.createPlayer.field.password.placeholder':'StrongP4ss',
  'agent.createPlayer.field.password.hint':       '8+ chars, letter + digit',
  'agent.createPlayer.section.owner.title':       'Owning agent',
  'agent.createPlayer.section.owner.hint':        'Optional — leave blank for a platform-direct player',
  'agent.createPlayer.field.owner.label':         'Agent',
  'agent.createPlayer.field.owner.none':          '— None (platform-direct) —',
  'agent.createPlayer.section.profile.title':     'Profile',
  'agent.createPlayer.section.profile.hint':      'First name, last name and date of birth are required for every player.',
  'agent.createPlayer.field.firstName.label':     'First name',
  'agent.createPlayer.field.lastName.label':      'Last name',
  'agent.createPlayer.field.dob.label':           'Date of birth',
  'agent.createPlayer.field.gender.label':        'Gender',
  // Gender select options.
  'agent.createPlayer.gender.unspecified':        '— Not specified —',
  'agent.createPlayer.gender.male':               'Male',
  'agent.createPlayer.gender.female':             'Female',
  'agent.createPlayer.gender.other':              'Other',
  'agent.createPlayer.gender.preferNot':          'Prefer not to say',
  'agent.createPlayer.field.language.label':      'Preferred language',
  'agent.createPlayer.section.location.title':    'Location',
  'agent.createPlayer.section.location.hint':     'Optional — player can finish on first login',
  // Footer.
  'agent.createPlayer.disclaimer':                'The player is created with status=active and email auto-verified — no link is sent. Make sure you trust the contact info.',
  'agent.createPlayer.cta.cancel':                'Cancel',
  'agent.createPlayer.cta.creating':              'Creating…',
  'agent.createPlayer.cta.submit':                'Create player',
  // Errors.
  'agent.createPlayer.error.missingAgentId':      'Missing agent id',
  'agent.createPlayer.error.createFailed':        'Could not create player',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today':        'Today',
  'agent.adminHome.range.yesterday':    'Yesterday',
  'agent.adminHome.range.week':         'This week',
  'agent.adminHome.range.lastWeek':     'Last week',
  'agent.adminHome.range.month':        'This month',
  'agent.adminHome.range.lastMonth':    'Last month',
  'agent.adminHome.range.all':          'Complete',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle':  'Welcome back',
  'agent.adminHome.greeting.fallback':  'there',
  'agent.adminHome.errorBanner.fallback': 'Failed to load',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label':                       'Platform inflow',
  'agent.adminHome.admin.action.moneyQueue':                'Money queue',
  'agent.adminHome.admin.action.agents':                    'Agents',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches':                 'Matches',
  'agent.adminHome.admin.snapshot.newSignups':              'New signups',
  'agent.adminHome.admin.snapshot.active':                  'Active',
  'agent.adminHome.admin.snapshot.pending':                 'Pending',

  // Section titles between rows.
  'agent.adminHome.section.games':                          'Games',
  'agent.adminHome.section.games.subtitle':                 'Matches and platform fee · {range}',
  'agent.adminHome.section.activity':                       'Activity',
  'agent.adminHome.section.ops':                            'Operations & insights',
  'agent.adminHome.section.ops.subtitle':                   'Live snapshots — click any card to drill into the full page',
  'agent.adminHome.section.gameSettings':                   'Game settings',
  'agent.adminHome.section.gameSettings.subtitle':          'Configure each game',
  'agent.adminHome.section.downline':                       'My downline insights',
  'agent.adminHome.section.downline.subtitle':              'Click any card to drill into the full page',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title':               'Players Wallet',
  'agent.adminHome.admin.card.players.headlineLabel':       'Recharged in range',
  'agent.adminHome.admin.card.players.flow.recharges':      'Recharges',
  'agent.adminHome.admin.card.players.flow.withdrawals':    'Withdrawals',
  'agent.adminHome.admin.card.players.pending.recharges':   'Pending recharges',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'Pending withdrawals',
  'agent.adminHome.admin.card.players.stat.total':          'Total players',
  'agent.adminHome.admin.card.players.stat.active':         'Active',
  'agent.adminHome.admin.card.players.stat.suspended':      'Suspended',

  'agent.adminHome.admin.card.agents.title':                'Agents Wallet',
  'agent.adminHome.admin.card.agents.headlineLabel':        'In agent pools',
  'agent.adminHome.admin.card.agents.flow.topups':          'Topups (admin → agent)',
  'agent.adminHome.admin.card.agents.flow.outflow':         'Outflow (paid out / topup)',
  'agent.adminHome.admin.card.agents.pending.withdrawals':  'Pending agent withdrawals',
  'agent.adminHome.admin.card.agents.stat.total':           'Total agents',
  'agent.adminHome.admin.card.agents.stat.active':          'Active',
  'agent.adminHome.admin.card.agents.stat.suspended':       'Suspended',

  'agent.adminHome.admin.card.bots.title':                  'Bots Wallet',
  'agent.adminHome.admin.card.bots.headlineLabel':          'In bot wallets',
  'agent.adminHome.admin.card.bots.stat.total':             'Total bots',
  'agent.adminHome.admin.card.bots.stat.active':            'Active',
  'agent.adminHome.admin.card.bots.stat.matches':           'Matches',

  'agent.adminHome.admin.card.rewards.title':               'Rewards',
  'agent.adminHome.admin.card.rewards.headlineLabel':       'Paid this week',
  'agent.adminHome.admin.card.rewards.pending.claims':      'Pending claims',
  'agent.adminHome.admin.card.rewards.stat.activeOffers':   'Active offers',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs':       'Bulk jobs',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches':                       'matches',
  // Game registry display labels.
  'agent.adminHome.game.ludo':                              'Ludo',
  'agent.adminHome.game.xo':                                'XO',
  'agent.adminHome.game.rummy':                             'Rummy',
  'agent.adminHome.game.carrom':                            'Carrom',
  'agent.adminHome.game.chess':                             'Chess',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title':              'Top players',
  'agent.adminHome.activity.topPlayers.empty':              'No matches in this range.',
  'agent.adminHome.activity.topPlayers.metric':             '{count} matches',
  'agent.adminHome.activity.topAgents.title':               'Top agents',
  'agent.adminHome.activity.topAgents.subtitle':            'By owned players',
  'agent.adminHome.activity.topAgents.empty':               'No active agents yet.',
  'agent.adminHome.activity.topAgents.metric':              '{count} players',
  'agent.adminHome.activity.recent.title':                  'Recent transactions',
  'agent.adminHome.activity.recent.subtitle':               'Last 5',
  'agent.adminHome.activity.recent.empty':                  'No transactions yet.',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title':                   'Top earners (7d)',
  'agent.adminHome.ops.topEarners.subtitle':                'By True Net',
  'agent.adminHome.ops.topEarners.empty':                   'No earnings in the last 7 days.',
  'agent.adminHome.ops.liveActivity.title':                 'Live activity',
  'agent.adminHome.ops.liveActivity.subtitle':              'Last 5 events platform-wide',
  'agent.adminHome.ops.liveActivity.empty':                 'Quiet — no events in the last 24h.',
  'agent.adminHome.ops.botPerf.title':                      'Bot performance (7d)',
  'agent.adminHome.ops.botPerf.subtitle':                   'Per-category P&L',
  'agent.adminHome.ops.rateChanges.title':                  'Recent rate changes',
  'agent.adminHome.ops.rateChanges.subtitle':               'Latest partnership edits',
  'agent.adminHome.ops.rateChanges.empty':                  'No rate-change events yet.',
  'agent.adminHome.ops.opsHealth.title':                    'Operations health',
  'agent.adminHome.ops.opsHealth.subtitle':                 'Dead-letter queue · pending settlements',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb':                  'earned',
  'agent.adminHome.feed.match.onGameSuffix':                'on {game}',
  'agent.adminHome.feed.recharge.label':                    'Recharge',
  'agent.adminHome.feed.recharge.amountInfix':              '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix':                'via',
  'agent.adminHome.feed.rateChange.label':                  'Rate change',
  'agent.adminHome.feed.cascadeBadge':                      'cascade',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved':           'Approved',
  'agent.adminHome.feed.recharge.state.rejected':           'Rejected',
  'agent.adminHome.feed.recharge.state.pending':            'Pending',
  'agent.adminHome.feed.recharge.state.paid':               'Paid',
  'agent.adminHome.feed.recharge.state.expired':            'Expired',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one':                '{count} match',
  'agent.adminHome.leaderboard.matches.other':              '{count} matches',
  'agent.adminHome.leaderboard.levelPrefix':                'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel':                  'Platform profit (7d)',
  'agent.adminHome.botMatrix.cat.easy':                     'easy',
  'agent.adminHome.botMatrix.cat.medium':                   'medium',
  'agent.adminHome.botMatrix.cat.hard':                     'hard',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort':                 '{matches}m',
  'agent.adminHome.botMatrix.activeSummary':                '{active} of {total} bots active',
  'agent.adminHome.botMatrix.loading':                      'Loading…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title':               'DLQ is clean',
  'agent.adminHome.opsHealth.dlqClean.body':                'No failed settlement jobs.',
  'agent.adminHome.opsHealth.dlqDirty.title.one':           '{count} unresolved DLQ job',
  'agent.adminHome.opsHealth.dlqDirty.title.other':         '{count} unresolved DLQ jobs',
  'agent.adminHome.opsHealth.dlqDirty.body':                'Click to retry / resolve.',
  'agent.adminHome.opsHealth.replay.eyebrow':               'Replay',
  'agent.adminHome.opsHealth.replay.body':                  'Re-run settlement',
  'agent.adminHome.opsHealth.reassign.eyebrow':             'Reassign',
  'agent.adminHome.opsHealth.reassign.body':                'Bulk move',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label':                   'My pool balance',
  'agent.adminHome.agentDash.hero.netPrefix':               'Net',
  'agent.adminHome.agentDash.action.myWallet':              'My wallet',
  'agent.adminHome.agentDash.action.myPlayers':             'My players',
  'agent.adminHome.agentDash.snapshot.myMatches':           'My matches',
  'agent.adminHome.agentDash.snapshot.active':              'Active',
  'agent.adminHome.agentDash.snapshot.netRange':            'Net (range)',
  'agent.adminHome.agentDash.snapshot.pending':             'Pending',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title':           'My Players Wallet',
  'agent.adminHome.agentDash.card.players.headlineLabel':   'Recharged in range',
  'agent.adminHome.agentDash.card.players.stat.total':      'My players',
  'agent.adminHome.agentDash.card.wallet.title':            'My Wallet',
  'agent.adminHome.agentDash.card.wallet.headlineLabel':    'Pool balance',
  'agent.adminHome.agentDash.card.wallet.flow.earned':      'Earned (commissions)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut':     'Paid out',
  'agent.adminHome.agentDash.card.wallet.pending':          'My pending withdrawal',
  'agent.adminHome.agentDash.card.wallet.stat.topups':      'Topups received',
  'agent.adminHome.agentDash.card.wallet.stat.netRange':    'Net (range)',
  'agent.adminHome.agentDash.card.activity.title':          'Activity',
  'agent.adminHome.agentDash.card.activity.headlineLabel':  'My matches in range',
  'agent.adminHome.agentDash.card.activity.stat.topSpender':'Top spender',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend':'Their spend',
  'agent.adminHome.agentDash.card.activity.stat.pending':   'Pending items',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title':   'Top spenders',
  'agent.adminHome.agentDash.activity.topSpenders.empty':   'No spend in this range yet.',
  'agent.adminHome.agentDash.activity.recent.title':        'Recent activity',
  'agent.adminHome.agentDash.activity.recent.subtitle':     'Last 5 from your players',
  'agent.adminHome.agentDash.activity.recent.empty':        'No transactions yet.',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title':    'Top earners (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'Your sub-agents ranked',
  'agent.adminHome.agentDash.insights.downline.title':      'Downline activity',
  'agent.adminHome.agentDash.insights.downline.subtitle':   'Last 5 events from your downline',
  'agent.adminHome.agentDash.insights.deposit.title':       'Deposit ledger',
  'agent.adminHome.agentDash.insights.deposit.subtitle':    'Recent deposit credits / refunds',
  'agent.adminHome.agentDash.insights.deposit.empty':       'No deposit activity yet.',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title':                       'Sub-agent',
  'agent.subAgentDetail.subtitle':                    'Direct sub-agent',
  'agent.subAgentDetail.error.loadFailed':            'Failed to load sub-agent',
  'agent.subAgentDetail.backToList':                  'Back to sub-agents list',
  'agent.subAgentDetail.loading':                     'Loading…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity':                'Identity',
  'agent.subAgentDetail.tab.players':                 'Players',
  'agent.subAgentDetail.tab.wallet':                  'Wallet',
  'agent.subAgentDetail.tab.rates':                   'Rates',
  'agent.subAgentDetail.tab.ops':                     'Operations',
  // Header card.
  'agent.subAgentDetail.header.players.one':          '{count} player',
  'agent.subAgentDetail.header.players.other':        '{count} players',
  'agent.subAgentDetail.header.pool':                 'Pool: {amount}',
  'agent.subAgentDetail.header.lastLogin':            'last login {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active':               'Active',
  'agent.subAgentDetail.status.suspended':            'Suspended',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid':           'UAID',
  'agent.subAgentDetail.identity.row.username':       'Username',
  'agent.subAgentDetail.identity.row.email':          'Email',
  'agent.subAgentDetail.identity.row.status':         'Status',
  'agent.subAgentDetail.identity.row.parent':         'Parent agent',
  'agent.subAgentDetail.identity.parentValue':        '@{username} (you)',
  'agent.subAgentDetail.identity.parentEmpty':        '—',
  'agent.subAgentDetail.identity.row.ownedPlayers':   'Owned players',
  'agent.subAgentDetail.identity.row.wallet':         'Wallet balance',
  'agent.subAgentDetail.identity.row.created':        'Created',
  'agent.subAgentDetail.identity.row.lastLogin':      'Last login',
  'agent.subAgentDetail.identity.row.identityLocked': 'Identity locked',
  'agent.subAgentDetail.identity.identityLocked.yes': 'Yes (admin-only edits)',
  'agent.subAgentDetail.identity.identityLocked.no':  'No (sub-agent can self-edit)',
  'agent.subAgentDetail.identity.row.receiving':      'Receiving players',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting':            'accepting',
  'agent.subAgentDetail.optOut.optedOut':             'opted out',
  'agent.subAgentDetail.optOut.toast.accepting':      'Sub-agent now accepting players',
  'agent.subAgentDetail.optOut.toast.optedOut':       'Sub-agent opted out of locator',
  'agent.subAgentDetail.optOut.error.updateFailed':   'Could not update',
  // Location editor card.
  'agent.subAgentDetail.location.title':              'Location',
  'agent.subAgentDetail.location.lastUpdated':        '· last updated {date}',
  'agent.subAgentDetail.location.cta.save':           'Save location',
  'agent.subAgentDetail.location.nothingToSave':      'Nothing to save',
  'agent.subAgentDetail.location.toast.saved.one':    'Location saved ({count} field)',
  'agent.subAgentDetail.location.toast.saved.other':  'Location saved ({count} fields)',
  'agent.subAgentDetail.location.hint':               'Pick a different state / district / area to enable Save. (The sub-agent themselves overlays precise GPS via the forced pop-up on their first login.)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty':               "This sub-agent doesn't own any players yet.",
  'agent.subAgentDetail.players.count.one':           '{count} player',
  'agent.subAgentDetail.players.count.other':         '{count} players',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel':         'Current sub-agent balance',
  'agent.subAgentDetail.wallet.ledger.title':         "@{username}'s full wallet ledger",
  'agent.subAgentDetail.wallet.ledger.subtitle':      'every movement · sub-agent sees the same in their wallet',
  'agent.subAgentDetail.wallet.ledger.empty':         'No wallet activity yet.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge':  'you ↔ them',
  'agent.subAgentDetail.wallet.ledger.balanceAfter':  'bal ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin':      'Topup from admin',
  'agent.subAgentDetail.wallet.kind.topupToPlayer':       'Payout to player',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer':     'Player recharge',
  'agent.subAgentDetail.wallet.kind.revenueShare':        'Revenue share',
  'agent.subAgentDetail.wallet.kind.rechargeCommission':  'Recharge commission',
  'agent.subAgentDetail.wallet.kind.coFundOffer':         'Co-fund offer',
  'agent.subAgentDetail.wallet.kind.adminAdjust':         'Admin adjustment',
  'agent.subAgentDetail.wallet.kind.withdrawalHold':      'Withdrawal hold',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund':    'Withdrawal refund',
  'agent.subAgentDetail.wallet.kind.platformFeeShare':    'Platform fee share',
  'agent.subAgentDetail.wallet.kind.winLossShare':        'Win/loss share',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title':           'Pulling coins back?',
  'agent.subAgentDetail.wallet.pullback.body.before':     'Ask ',
  'agent.subAgentDetail.wallet.pullback.body.middle':     ' to submit a withdrawal request from their own pool. You can mark it paid (full or partial) from ',
  'agent.subAgentDetail.wallet.pullback.financeLink':     'Finance › Sub-agents',
  'agent.subAgentDetail.wallet.pullback.body.after':      '.',

  // "Manual Transaction" CTA — replaces the inline TransferForm that
  // used to live in WalletTab. Deep-links to the central agent-
  // requests panel with this sub-agent pre-selected via ?preselect=,
  // so the entire money-movement UX lives in one place.
  'agent.subAgentDetail.wallet.cta.manualTransaction':     'Manual transaction',
  'agent.subAgentDetail.wallet.cta.manualTransactionHint': 'Top up or claw back via the central money queue.',

  // TransferForm keys are retained for back-compat with non-English
  // translations of the now-removed inline form; not used by current
  // WalletTab.
  'agent.subAgentDetail.transfer.topup.heading':            'Transfer to @{username}',
  'agent.subAgentDetail.transfer.withdraw.heading':         'Withdraw from @{username}',
  'agent.subAgentDetail.transfer.topup.body':              'Atomic transfer: your wallet down, their wallet up. Both pools may go negative (signed-pool design).',
  'agent.subAgentDetail.transfer.withdraw.body':           'Atomic reverse transfer: their wallet down, your wallet up. Use this when settling a payout request from the sub-agent.',
  'agent.subAgentDetail.transfer.amountLabel':             'Amount (₹)',
  'agent.subAgentDetail.transfer.noteLabel':               'Note (optional)',
  'agent.subAgentDetail.transfer.note.placeholder.topup':  'e.g. weekly top-up',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'e.g. payout settlement',
  'agent.subAgentDetail.transfer.error.amountPositive':    'Amount must be a positive integer.',
  'agent.subAgentDetail.transfer.confirm.topup':           'Transfer {amount} from YOUR pool to @{username}? Your balance will go down by exactly this amount.',
  'agent.subAgentDetail.transfer.confirm.withdraw':        "Pull {amount} from @{username}'s pool back to YOURS? Their balance will go down by exactly this amount.",
  'agent.subAgentDetail.transfer.toast.topup':             'Sent {amount} to @{username}. Their balance: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw':          'Pulled {amount} back from @{username}. Their balance: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working':             'Working…',
  'agent.subAgentDetail.transfer.cta.suspended':           'Sub-agent suspended',
  'agent.subAgentDetail.transfer.cta.topup':               'Transfer {amount}',
  'agent.subAgentDetail.transfer.cta.withdraw':            'Withdraw {amount}',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading':                    'Loading rates…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1':                'Each card is one commission pillar; each row inside is one (pillar × game) share. The ',
  'agent.subAgentDetail.rates.intro.ceiling':              'ceiling',
  'agent.subAgentDetail.rates.intro.part2':                ' shown next to each input is your own rate — by the Golden Limit, you cannot grant more than that to a child. Any value you change becomes editable; click ',
  'agent.subAgentDetail.rates.intro.applyWord':            'Apply',
  'agent.subAgentDetail.rates.intro.part3':                ' to commit. The cascade clamp is enforced server-side.',
  'agent.subAgentDetail.rates.toast.applied':              'Rates applied.',
  'agent.subAgentDetail.rates.confirm.one':                'Apply {count} rate change? Golden-limit + cascade clamp will be enforced server-side.',
  'agent.subAgentDetail.rates.confirm.other':              'Apply {count} rate changes? Golden-limit + cascade clamp will be enforced server-side.',
  'agent.subAgentDetail.rates.error.blocked':              'Blocked: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee':         'Platform Fee %',
  'agent.subAgentDetail.rates.pillar.winLoss':             'Win/Loss Profit %',
  'agent.subAgentDetail.rates.pillar.offerReward':         'Offer Reward %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel':            '— global —',
  'agent.subAgentDetail.rates.row.ceilingSuffix':          '/ ceiling {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge':           'EXCEEDS PARENT (Golden Limit)',
  'agent.subAgentDetail.rates.row.editedBadge':            'edited',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one':            'Discard {count} change',
  'agent.subAgentDetail.rates.cta.discard.other':          'Discard {count} changes',
  'agent.subAgentDetail.rates.cta.apply.one':              'Apply {count} change',
  'agent.subAgentDetail.rates.cta.apply.other':            'Apply {count} changes',
  'agent.subAgentDetail.rates.cta.applying':               'Applying…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title':                 'Status',
  'agent.subAgentDetail.ops.status.body':                  "Suspending blocks the sub-agent's logins and downline operations until you flip back to active. A reason is optional but lands in the audit trail.",
  'agent.subAgentDetail.ops.status.reasonPlaceholder':     'Reason (optional)',
  'agent.subAgentDetail.ops.status.cta.suspend':           'Suspend sub-agent',
  'agent.subAgentDetail.ops.status.cta.unsuspend':         'Unsuspend sub-agent',
  'agent.subAgentDetail.ops.status.cta.working':           'Working…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend':       'Suspend @{username}? They will not be able to log in or operate while suspended.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend':     'Unsuspend @{username}? They regain full access.',
  'agent.subAgentDetail.ops.status.toast.suspended':       '@{username} is now suspended.',
  'agent.subAgentDetail.ops.status.toast.active':          '@{username} is now active.',

  'agent.subAgentDetail.ops.password.title':               'Reset password',
  'agent.subAgentDetail.ops.password.body':                'Sets a new password for the sub-agent and revokes their existing refresh tokens. They will be forced to log in again with the new password. Share it with them through a secure channel.',
  'agent.subAgentDetail.ops.password.placeholder':         'New password (min 8 characters)',
  'agent.subAgentDetail.ops.password.confirm':             "Reset @{username}'s password? Their existing sessions will be invalidated and they will need to log in with the new password.",
  'agent.subAgentDetail.ops.password.toast.reset':         'Password reset. Share the new password with @{username}.',
  'agent.subAgentDetail.ops.password.error.tooShort':      'Password must be at least 8 characters.',
  'agent.subAgentDetail.ops.password.cta.reset':           'Reset password',
  'agent.subAgentDetail.ops.password.cta.resetting':       'Resetting…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers':                 'Back to Players',
  'agent.playerDetail.loading':                       'Loading…',
  'agent.playerDetail.error.loadFailed':              'Failed to load user',

  // Header card.
  'agent.playerDetail.header.noName':                 '(no name set)',
  'agent.playerDetail.header.joined':                 'Joined {date}',
  'agent.playerDetail.header.ownedBy.prefix':         ' · owned by ',
  'agent.playerDetail.header.walletLabel':            'Wallet',

  // Status pill (4 states).
  'agent.playerDetail.status.pending':                'pending',
  'agent.playerDetail.status.active':                 'active',
  'agent.playerDetail.status.suspended':              'suspended',
  'agent.playerDetail.status.banned':                 'banned',

  // Tab labels.
  'agent.playerDetail.tab.identity':                  'Identity',
  'agent.playerDetail.tab.profile':                   'Profile',
  'agent.playerDetail.tab.wallet':                    'Wallet',
  'agent.playerDetail.tab.security':                  'Security',
  'agent.playerDetail.tab.ownership':                 'Ownership',
  'agent.playerDetail.tab.operations':                'Operations',

  // Identity tab.
  'agent.playerDetail.identity.title':                'Identity',
  'agent.playerDetail.identity.row.username':         'Username',
  'agent.playerDetail.identity.row.email':            'Email',
  'agent.playerDetail.identity.row.phone':            'Phone',
  'agent.playerDetail.identity.row.country':          'Country',
  'agent.playerDetail.identity.row.joined':           'Joined',
  'agent.playerDetail.identity.row.emailVerified':    'Email verified',
  'agent.playerDetail.identity.row.telegram':         'Telegram',
  'agent.playerDetail.identity.row.failedLogins':     'Failed logins',
  'agent.playerDetail.identity.row.lockedUntil':      'Locked until',
  'agent.playerDetail.identity.value.yes':            'Yes',
  'agent.playerDetail.identity.value.no':             'No',
  'agent.playerDetail.identity.value.dash':           '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title':       'Transfer this player',
  'agent.playerDetail.identity.transfer.body':        'Hand off to another agent by their Unique Agent ID — works across the entire network, not just your downline.',
  'agent.playerDetail.identity.transfer.cta':         'Transfer by UAID',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle':            'Change photo',
  'agent.playerDetail.avatar.cropTitle':              'Adjust photo for @{username}',
  'agent.playerDetail.avatar.toast.updated':          'Photo updated',
  'agent.playerDetail.avatar.error.uploadFailed':     'Upload failed',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin':          'Player transferred via UAID',
  'agent.playerDetail.transfer.toast.agent':          '@{username} transferred successfully',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title':       'Account status',
  'agent.playerDetail.ops.accountStatus.body':        'Active means the player can log in and play. Suspend blocks logins until you flip back. Ban is a stronger lock — keep it for policy violations.',
  'agent.playerDetail.ops.statusBtn.activate':        'Activate',
  'agent.playerDetail.ops.statusBtn.suspend':         'Suspend',
  'agent.playerDetail.ops.statusBtn.ban':             'Ban',
  'agent.playerDetail.ops.statusBtn.current':         'current',
  'agent.playerDetail.ops.statusBtn.toast':           'User is now {status}',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title':              'Danger zone',
  'agent.playerDetail.ops.danger.body':               'Hard-deleting removes the user record. The wallet ledger is preserved for audit, but the player will lose all access. This action cannot be undone.',
  'agent.playerDetail.ops.delete.eyebrow':            'Danger zone',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before':     'Permanently delete this account. This cannot be undone. Type ',
  'agent.playerDetail.ops.delete.confirm.after':      ' to confirm.',
  'agent.playerDetail.ops.delete.cta':                'Delete',
  'agent.playerDetail.ops.delete.toast':              'User deleted',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title':            'Adjust wallet',
  'agent.playerDetail.walletAdjust.mode.credit':      '+ Credit',
  'agent.playerDetail.walletAdjust.mode.debit':       '− Debit',
  'agent.playerDetail.walletAdjust.field.amount':     'Amount (₹)',
  'agent.playerDetail.walletAdjust.field.reason':     'Reason · stored in the ledger',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'e.g. deposit reversal, bonus, refund',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'Mark as recharge',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before':  'Record this credit as ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold':    'recharge',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after':   ' in the ledger. If this user was referred by someone, the referrer auto-gets a bonus.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'Enter a positive amount',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'Reason is required',
  'agent.playerDetail.walletAdjust.cta.applying':     'Applying…',
  'agent.playerDetail.walletAdjust.cta.applyCredit':  'Apply credit',
  'agent.playerDetail.walletAdjust.cta.applyDebit':   'Apply debit',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited':   'Credited {amount}',
  'agent.playerDetail.walletAdjust.toast.debited':    'Debited {amount}',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · referrer earned {bonus} bonus',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title':             'Recharge this player',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before':       'Topping up @',
  'agent.playerDetail.rechargeCta.body.middle':       ' transfers coins from ',
  'agent.playerDetail.rechargeCta.body.bold':         'your wallet',
  'agent.playerDetail.rechargeCta.body.after':        ' to theirs. Your pool is checked first — the move is atomic and rejected if you have insufficient balance.',
  'agent.playerDetail.rechargeCta.thisPlayer':        'this player',
  'agent.playerDetail.rechargeCta.cta':               'Open recharge form',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title':                'Reset password',
  'agent.playerDetail.security.subtitle':             'Sets a new password and signs the user out of every device.',
  'agent.playerDetail.security.field.new':            'New password',
  'agent.playerDetail.security.field.confirm':        'Confirm',
  'agent.playerDetail.security.error.mismatch':       'Passwords do not match',
  'agent.playerDetail.security.error.tooShort':       'At least 8 characters, with letters and digits',
  'agent.playerDetail.security.toast.reset':          'Password reset and all sessions revoked',
  'agent.playerDetail.security.cta.reset':            'Reset password',
  'agent.playerDetail.security.cta.resetting':        'Resetting…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title':                  'Wallet ledger',
  'agent.playerDetail.ledger.entries.one':            '{count} entry',
  'agent.playerDetail.ledger.entries.other':          '{count} entries',
  'agent.playerDetail.ledger.empty':                  'No transactions yet.',
  'agent.playerDetail.ledger.loading':                'Loading…',
  'agent.playerDetail.ledger.actorSuffix':            ' · by admin #{id}',
  'agent.playerDetail.ledger.pagination.prev':        'Prev',
  'agent.playerDetail.ledger.pagination.next':        'Next',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet':               'Match bet',
  'agent.playerDetail.ledger.kind.win':               'Match prize',
  'agent.playerDetail.ledger.kind.refund':            'Refund',
  'agent.playerDetail.ledger.kind.fee':               'Platform fee',
  'agent.playerDetail.ledger.kind.adminCredit':       'Admin credit',
  'agent.playerDetail.ledger.kind.adminDebit':        'Admin debit',
  'agent.playerDetail.ledger.kind.recharge':          'Recharge',
  'agent.playerDetail.ledger.kind.referralBonus':     'Referral bonus',
  'agent.playerDetail.ledger.kind.signupBonus':       'Signup bonus',
  'agent.playerDetail.ledger.kind.offerCredit':       'Offer credit',
  'agent.playerDetail.ledger.kind.withdrawalHold':    'Withdrawal',
  'agent.playerDetail.ledger.kind.withdrawalRefund':  'Refund',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform':          'Platform',

  // ── Profile editor.
  'agent.playerDetail.profile.title':                 'Edit profile',
  'agent.playerDetail.profile.field.username':        'Username',
  'agent.playerDetail.profile.field.email':           'Email',
  'agent.playerDetail.profile.field.phone':           'Phone',
  'agent.playerDetail.profile.field.firstName':       'First name',
  'agent.playerDetail.profile.field.lastName':        'Last name',
  'agent.playerDetail.profile.field.dob':             'Date of birth',
  'agent.playerDetail.profile.field.gender':          'Gender',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified':    '—',
  'agent.playerDetail.profile.gender.male':           'Male',
  'agent.playerDetail.profile.gender.female':         'Female',
  'agent.playerDetail.profile.gender.other':          'Other',
  'agent.playerDetail.profile.gender.preferNot':      'Prefer not to say',
  // Location section.
  'agent.playerDetail.profile.location.title':        'Location',
  'agent.playerDetail.profile.location.lastUpdated':  '· last updated {date}',
  'agent.playerDetail.profile.location.legacy.label': 'Legacy address on file',
  'agent.playerDetail.profile.location.legacy.note':  ' (read-only — pre-R3, not used by the locator):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave':   'Nothing to save',
  'agent.playerDetail.profile.toast.saved.one':       'Saved {count} field',
  'agent.playerDetail.profile.toast.saved.other':     'Saved {count} fields',
  'agent.playerDetail.profile.cta.save':              'Save changes',
  'agent.playerDetail.profile.cta.saving':            'Saving…',
  'agent.playerDetail.profile.cta.reset':             'Reset',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title':               'Owner agent',
  'agent.playerDetail.ownership.row.eyebrow':         'Ownership',
  'agent.playerDetail.ownership.platform.title':      'Platform-direct',
  'agent.playerDetail.ownership.platform.body':       'No agent owns this player. Admin manages directly.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'agent #{id}',
  'agent.playerDetail.ownership.owned.body':          "Earns commissions on this player's recharges + matches.",
  'agent.playerDetail.ownership.cta.change':          'Change',
  'agent.playerDetail.ownership.toast.changedTo':     'Ownership → {label}',
  'agent.playerDetail.ownership.platformLabel':       'Platform-direct',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title':            'Reassign @{username}',
  'agent.playerDetail.ownership.picker.body':             "Pick a new agent — or detach to platform-direct (no agent owns this player). Future commissions follow the new owner; already-credited rows aren't touched.",
  'agent.playerDetail.ownership.picker.detach.title':     'Detach to platform',
  'agent.playerDetail.ownership.picker.detach.body':      'Remove agent ownership. Admin manages directly.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'Search agents…',
  'agent.playerDetail.ownership.picker.loading':          'Loading agents…',
  'agent.playerDetail.ownership.picker.empty.search':     'No agents match your search.',
  'agent.playerDetail.ownership.picker.empty.none':       'No active agents yet.',
  'agent.playerDetail.ownership.picker.players.one':      '{count} player',
  'agent.playerDetail.ownership.picker.players.other':    '{count} players',
  'agent.playerDetail.ownership.picker.current':          'current',
  'agent.playerDetail.ownership.picker.currentUpper':     'CURRENT',
  'agent.playerDetail.ownership.picker.close':            'Close',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand':              'Baali Agent',
  'agent.login.subtitle':           'Sign in to manage your players',
  'agent.login.field.identifier':   'Username or email',
  'agent.login.field.password':     'Password',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before':  "Agents only. If you're the platform owner, use ",
  'agent.login.disclaimer.link':    '/admin/login',
  'agent.login.disclaimer.after':   '.',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed':     'Login failed',
  'agent.login.error.invalidCredentials': 'Invalid credentials',
  'agent.login.error.accountSuspended':   'Account suspended — contact your administrator.',
  'agent.login.error.networkError':       'Network error — check your connection and try again.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag':    'Game',
  'agent.misc.gameTile.actionSuffix':   '· Configure',
};

// ─────────────────────────────────────────────────────────────────────
// Translations.
//
// Coverage approach: every language ships full coverage of the most-
// visible flow (login, register, forgot, reset, home, nav, profile).
// Anything missing falls back to English so a partial entry never
// breaks the page.
// ─────────────────────────────────────────────────────────────────────

const hi: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "मेरी जानकारी",
  'profile.group.security': "साइन-इन और सुरक्षा",
  'profile.personal.title': "निजी जानकारी",
  'profile.personal.editTitle': "निजी जानकारी बदलें",
  'common.edit': "बदलें",
  'profile.field.name': "नाम",
  'profile.field.phone': "फ़ोन नंबर",
  'profile.field.gender': "लिंग",
  'profile.field.location': "लोकेशन",
  'profile.gender.male': "पुरुष",
  'profile.gender.female': "महिला",
  'profile.gender.other': "अन्य",
  'profile.gender.preferNot': "बताना नहीं चाहते",
  'profile.gender.select': "चुनें…",
  'profile.location.optionalHint': "वैकल्पिक — अपना इलाका चुनें या अपना पिनकोड टाइप करें (GPS से यह ज़्यादा सटीक होता है)।",
  'profile.lang.title': "भाषा",
  'profile.password.title': "पासवर्ड",
  'profile.password.desc': "अपने अकाउंट का पासवर्ड बदलें। बाकी डिवाइस साइन आउट हो जाएँगे।",
  'profile.password.cta': "बदलें",
  'profile.password.editTitle': "पासवर्ड बदलें",
  'profile.password.current': "मौजूदा पासवर्ड",
  'profile.password.new': "नया पासवर्ड",
  'profile.password.confirm': "नया पासवर्ड दोबारा डालें",
  'profile.password.currentPh': "आपका मौजूदा पासवर्ड",
  'profile.password.newPh': "एक मज़बूत पासवर्ड चुनें",
  'profile.password.confirmPh': "नया पासवर्ड दोबारा टाइप करें",
  'profile.password.rule': "कम से कम 8 अक्षर, जिनमें एक अक्षर और एक अंक हो।",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "इस रूप में लिंक",
  'profile.telegram.otpHere': "पासवर्ड रीसेट के OTP यहीं आते हैं।",
  'profile.telegram.unlinkChange': "अनलिंक करके बदलें",
  'profile.telegram.unlinkTitle': "Telegram अनलिंक करें?",
  'profile.telegram.unlink': "अनलिंक करें",
  'profile.telegram.keepLinked': "लिंक रहने दें",
  'profile.telegram.unlinkVerified': "आप बाद में कोई दूसरा Telegram अकाउंट फिर से लिंक कर सकते हैं। ईमेल वेरिफ़िकेशन बरकरार रहेगा।",
  'profile.telegram.unlinkOnly': "Telegram आपका इकलौता वेरिफ़ाइड चैनल है — खेलना जारी रखने से पहले आपको अपना अकाउंट दोबारा वेरिफ़ाई करना पड़ सकता है।",
  'profile.telegram.notLinkedDesc': "बैकअप के तौर पर पासवर्ड रीसेट के OTP पाने के लिए Telegram लिंक करें।",
  'profile.telegram.openBot': "@BaaliGames_bot खोलें",
  'profile.telegram.generate': "लिंक बनाएँ",
  'profile.telegram.generating': "बन रहा है…",
  'profile.avatar.cropTitle': "अपनी फ़ोटो सेट करें",
  'profile.toast.detailsSaved': "जानकारी सेव हो गई",
  'profile.toast.photoUpdated': "फ़ोटो अपडेट हो गई",
  'profile.toast.pwChanged': "पासवर्ड बदल गया। बाकी डिवाइस साइन आउट कर दिए गए हैं।",
  'profile.toast.tgUnlinked': "Telegram अनलिंक हो गया। कोई दूसरा चैट जोड़ने के लिए नया लिंक बनाएँ।",
  'profile.err.phone': "10 अंकों का मोबाइल नंबर डालें (या इसे वैसे ही रहने दें)",
  'profile.err.pwMismatch': "नए पासवर्ड मेल नहीं खा रहे",
  'profile.err.pwWeak': "पासवर्ड में कम से कम 8 अक्षर हों, जिनमें एक अक्षर और एक अंक हो",
  'profile.err.pwSame': "नया पासवर्ड मौजूदा पासवर्ड से अलग होना चाहिए",
  'wallet.payout.title': "सेव की गई पेआउट जानकारी",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "अभी तक कुछ सेव नहीं है",
  'wallet.payout.hint': "जब आप पैसे निकालते हैं, तब आपकी UPI ID अपने-आप सेव हो जाती है। आप इसे अगली निकासी रिक्वेस्ट के दौरान बदल सकते हैं।",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} इंस्टॉल करें",
  "pwa.install.subhint": "तुरंत, फ़ुल-स्क्रीन एक्सेस के लिए इसे अपनी होम स्क्रीन पर जोड़ें — किसी ऐप स्टोर की ज़रूरत नहीं।",
  "pwa.install.listen": "सुनें",
  "pwa.install.listening": "रोकें",
  "pwa.install.installBtn": "इंस्टॉल करें",
  "pwa.install.notNow": "अभी नहीं",
  "pwa.install.howToShow": "कैसे इंस्टॉल करें",
  "pwa.install.howToHide": "चरण छिपाएँ",
  "pwa.install.guide.iosSafari": "Safari स्क्रीन के नीचे, Share बटन पर टैप करें — यह ऊपर की ओर तीर वाले चौकोर जैसा दिखता है।\nखुलने वाले मेन्यू में थोड़ा ऊपर की ओर स्क्रॉल करें।\n\"Add to Home Screen\" (होम स्क्रीन में जोड़ें) विकल्प पर टैप करें।\nऊपर दाएँ कोने में Add पर टैप करें। अब ऐप किसी सामान्य ऐप की तरह आपकी होम स्क्रीन पर दिखेगा।",
  "pwa.install.guide.iosOther": "iPhone या iPad पर, होम स्क्रीन में जोड़ना केवल Safari ब्राउज़र में काम करता है।\nSafari खोलें और इसी वेबसाइट पर जाएँ।\nSafari के नीचे, Share बटन पर टैप करें — ऊपर की ओर तीर वाला चौकोर।\nनीचे स्क्रॉल करें, \"Add to Home Screen\" पर टैप करें, फिर Add पर टैप करें।",
  "pwa.install.guide.android": "ऊपर दिए Install बटन पर टैप करें।\nजब आपका ब्राउज़र पूछे, पुष्टि के लिए Install पर टैप करें।\nहो गया — अब ऐप आपकी होम स्क्रीन पर है। इसे किसी सामान्य ऐप की तरह कभी भी खोलें।\nपॉप-अप नहीं दिख रहा? अपने ब्राउज़र का मेन्यू खोलें — तीन बिंदु, आमतौर पर ऊपर दाएँ — और \"Install app\" पर टैप करें।",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "सीधा रिचार्ज",
  "agent.directRecharge.subtitle.player": "एक खिलाड़ी चुनें और अपने पूल से रिचार्ज करें। किसी अनुरोध की ज़रूरत नहीं।",
  "agent.directRecharge.subtitle.subagent": "एक सब-एजेंट चुनें और अपने पूल से उनके पूल में रिचार्ज करें।",
  "agent.directRecharge.search.player": "रिचार्ज करने के लिए खिलाड़ी खोजें (नाम · फ़ोन · ईमेल)…",
  "agent.directRecharge.search.subagent": "रिचार्ज करने के लिए सब-एजेंट खोजें (नाम · ईमेल · UAID)…",
  "agent.directRecharge.clearSearch": "खोज साफ़ करें",
  "agent.directRecharge.searching": "खोज रहे हैं…",
  "agent.directRecharge.noMatches": "कोई मेल नहीं मिला।",
  "agent.directRecharge.empty.player": "आपकी सूची में अभी कोई खिलाड़ी नहीं है।",
  "agent.directRecharge.empty.subagent": "अभी कोई सीधा सब-एजेंट नहीं है।",
  "agent.directRecharge.searchFailed": "खोज विफल",
  "agent.directRecharge.label.wallet": "वॉलेट",
  "agent.directRecharge.label.pool": "पूल",
  "agent.directRecharge.modal.title.player": "सीधा खिलाड़ी रिचार्ज",
  "agent.directRecharge.modal.title.subagent": "सीधा सब-एजेंट रिचार्ज",
  "agent.directRecharge.modal.close": "बंद करें",
  "agent.directRecharge.modal.targetPool": "लक्ष्य पूल",
  "agent.directRecharge.modal.operatingWallet": "ऑपरेटिंग वॉलेट",
  "agent.directRecharge.modal.dayToDay": "रोज़मर्रा का बैलेंस",
  "agent.directRecharge.modal.securityDeposit": "सुरक्षा जमा",
  "agent.directRecharge.modal.collateral": "संपार्श्विक पूल",
  "agent.directRecharge.modal.amount": "राशि (₹)",
  "agent.directRecharge.modal.overdraw": "आप अपने पूल (₹{pool}) से अधिक भुगतान नहीं कर सकते। राशि कम करें।",
  "agent.directRecharge.modal.reason": "कारण / टिप्पणी",
  "agent.directRecharge.modal.reasonPlaceholder": "नकद प्राप्त · ref 4291",
  "agent.directRecharge.modal.reasonHint": "आपके वॉलेट लेजर + ऑडिट लॉग में दर्ज किया जाता है।",
  "agent.directRecharge.modal.proofLabel": "भुगतान का स्क्रीनशॉट अपलोड करें (वैकल्पिक)",
  "agent.directRecharge.modal.proofHint": "इतिहास के माध्यम से प्राप्तकर्ता को दिखाई देता है।",
  "agent.directRecharge.modal.previewEmpty": "लेनदेन देखने के लिए राशि दर्ज करें।",
  "agent.directRecharge.modal.previewBody": "आपके वॉलेट से {name} के {pool} में भेजा गया। सुरक्षित स्थानांतरण; लेजर + ऑडिट में दर्ज।",
  "agent.directRecharge.modal.pool.playerWallet": "वॉलेट",
  "agent.directRecharge.modal.pool.operatingWallet": "ऑपरेटिंग वॉलेट",
  "agent.directRecharge.modal.pool.securityDeposit": "सुरक्षा जमा",
  "agent.directRecharge.modal.cancel": "रद्द करें",
  "agent.directRecharge.modal.send": "₹{amount} भेजें",
  "agent.directRecharge.badge.player": "खिलाड़ी",
  "agent.directRecharge.badge.subagent": "सब-एजेंट",
  "agent.directRecharge.modal.yourDirectSubagent": "आपका सीधा सब-एजेंट",
  "agent.directRecharge.modal.fundingSource": "फंडिंग स्रोत",
  "agent.directRecharge.modal.you": "(आप)",
  "agent.directRecharge.modal.yourPool": "आपका पूल",
  "agent.directRecharge.modal.notSignedIn": "एजेंट के रूप में साइन इन नहीं हैं",
  "agent.directRecharge.modal.failed": "विफल",
  'common.signIn': 'साइन इन करें', 'common.signUp': 'साइन अप करें',
  'common.login': 'लॉगिन', 'common.logout': 'लॉग आउट',
  'common.continue': 'जारी रखें', 'common.cancel': 'रद्द करें',
  'common.save': 'सहेजें', 'common.update': 'अपडेट करें',
  'common.clear': 'साफ़ करें', 'common.back': 'वापस',
  'common.loading': 'लोड हो रहा है…', 'common.sending': 'भेज रहे हैं…',
  'common.saving': 'सहेज रहे हैं…', 'common.optional': 'वैकल्पिक',
  'common.show': 'दिखाएँ', 'common.hide': 'छिपाएँ',
  'common.profile': 'प्रोफ़ाइल', 'common.settings': 'सेटिंग्स',
  'nav.brand': 'बाली गेम्स', 'nav.home': 'होम', 'nav.games': 'गेम्स',
  'nav.wallet': 'वॉलेट', 'nav.rewards': 'इनाम', 'nav.profile': 'प्रोफ़ाइल',
  'login.title': 'वापस स्वागत है', 'login.subtitle': 'खेल जारी रखने के लिए साइन इन करें।',
  'login.idLabel': 'यूज़रनेम, ईमेल या फ़ोन',
  'login.idPlaceholder': 'यूज़रनेम, ईमेल या मोबाइल',
  'login.passwordLabel': 'पासवर्ड', 'login.passwordPlaceholder': 'अपना पासवर्ड दर्ज करें',
  'login.forgot': 'भूल गए?', 'login.cta': 'साइन इन करें',
  'login.signingIn': 'साइन इन हो रहा है…',
  'login.newHere': 'नए हैं?', 'login.createAccount': 'खाता बनाएँ',
  'register.title': 'अपना खाता बनाएँ', 'register.subtitle': 'एक नाम चुनें और शुरू करें।',
  'register.usernameLabel': 'यूज़रनेम', 'register.usernamePlaceholder': 'अनोखा नाम चुनें',
  'register.usernameHelp': 'अक्षर, अंक और अंडरस्कोर। 3–24 अक्षर।',
  'register.emailLabel': 'ईमेल', 'register.emailPlaceholder': 'आपका ईमेल पता',
  'register.mobileLabel': 'मोबाइल नंबर', 'register.mobilePlaceholder': '10 अंकों का मोबाइल',
  'register.mobileHelp': 'हम देश का कोड अपने आप जोड़ देंगे।',
  'register.passwordLabel': 'पासवर्ड', 'register.passwordPlaceholder': 'मज़बूत पासवर्ड चुनें',
  'register.passwordHelp': 'कम से कम 8 अक्षर — एक अक्षर और एक अंक के साथ।',
  'register.referralLabel': 'रेफ़रल कोड', 'register.referralPlaceholder': 'दोस्त का इनवाइट कोड',
  'register.cta': 'खाता बनाएँ', 'register.creating': 'बना रहे हैं…',
  'register.alreadyPlayer': 'पहले से खिलाड़ी हैं?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about':  'आपके बारे में',
  'register.step.signin': 'साइन-इन',
  'register.step.finish': 'समाप्त',
  'register.step.aboutSubtitle':  'अपने बारे में थोड़ा बताएँ',
  'register.step.signinSubtitle': 'अपने साइन-इन का तरीक़ा सेट करें',
  'register.step.finishSubtitle': 'स्थान चुनें और पूरा करें',
  'register.firstName':   'पहला नाम',
  'register.firstNamePh': 'आरव',
  'register.lastName':    'अंतिम नाम',
  'register.lastNamePh':  'शर्मा',
  'register.dob':         'जन्म तिथि',
  'register.dobHelp':     'खेलने के लिए आपकी आयु 18 या उससे अधिक होनी चाहिए',
  'register.v.required':       'आवश्यक',
  'register.v.nameMin':        'कम से कम 2 अक्षर',
  'register.v.tooLong':        'बहुत लंबा',
  'register.v.nameChars':      'केवल अक्षर, स्पेस और हाइफ़न',
  'register.v.invalidDate':    'अमान्य तिथि',
  'register.v.minAge':         'आपकी आयु कम से कम 18 होनी चाहिए',
  'register.v.tooOld':         'कृपया वर्ष जाँचें',
  'register.v.usernameMin':    'कम से कम 3 अक्षर',
  'register.v.usernameMax':    'बहुत लंबा (अधिकतम 24)',
  'register.v.usernameChars':  'केवल अक्षर, अंक और अंडरस्कोर',
  'register.v.emailShape':     'अमान्य ईमेल लगता है',
  'register.v.phoneDigits':    '10 अंकों का नंबर होना चाहिए',
  'register.v.phonePrefix':    'भारतीय मोबाइल 6–9 से शुरू होता है',
  'register.v.pwdMin':         'कम से कम 8 अक्षर',
  'register.v.pwdLetter':      'कम से कम एक अक्षर होना चाहिए',
  'register.v.pwdNumber':      'कम से कम एक अंक होना चाहिए',
  'register.v.referralShort':  'बहुत छोटा लगता है',
  'register.v.referralChars':  'केवल अक्षर और अंक',
  'register.v.usernameTaken':  'यह यूज़रनेम पहले से लिया जा चुका है',
  'register.v.emailTaken':     'यह ईमेल पहले से रजिस्टर्ड है',
  'register.v.phoneTaken':     'यह नंबर पहले से रजिस्टर्ड है',
  'register.v.noLocation':     'जारी रखने के लिए कम से कम एक स्थान विधि साझा करें।',
  'register.welcome.title':         'स्वागत है',
  'register.welcome.subtitle':      'आपका खाता तैयार है। अभी सुरक्षित करें या सीधे शुरू करें?',
  'register.welcome.verifyTelegram':'टेलीग्राम से सत्यापित करें',
  'register.welcome.verifyExplain': '⚠ ज़रूरी: अभी टेलीग्राम से सत्यापित करें। बिना सत्यापन के, पासवर्ड भूल जाने पर आप खुद उसे रीसेट नहीं कर पाएंगे — केवल आपका एजेंट या एडमिन ही उसे रीसेट कर सकेगा। आप बाद में प्रोफ़ाइल से भी सत्यापित कर सकते हैं।',
  'register.welcome.verifyHeads': 'इसे बाद के लिए सहेजें',
  'register.welcome.verifyConsequence': 'ईमेल सत्यापन अभी उपलब्ध नहीं है। बाद में पासवर्ड रीसेट करने के लिए टेलीग्राम से सत्यापित करें। अगर आप ऐसा नहीं करते तो पासवर्ड भूलने पर केवल आपका एजेंट या एडमिन ही उसे रीसेट कर सकता है।',
  'register.welcome.continue':      'ऐप में जारी रखें',
  'register.welcome.continueLogin': 'जारी रखने के लिए साइन इन करें',
  'register.welcome.useDifferent':  'अलग खाते से लॉगिन करें',
  'register.geo.title':         'आप कहाँ से खेल रहे हैं?',
  'register.geo.subtitle':      'हम आपको निकटतम एजेंट से जोड़ने के लिए आपकी स्थिति का उपयोग करते हैं।',
  'register.geo.showAlt':       'स्थान साझा नहीं कर सकते? कोई अन्य विधि उपयोग करें',
  'register.geo.hideAlt':       'अन्य विधियाँ छुपाएँ',
  'register.geo.captured':      'स्थान कैप्चर किया गया',
  'register.geo.capturedHint':  'आप इसे अपनी प्रोफ़ाइल से कभी भी बदल सकते हैं।',
  'register.geo.verifyNote':    'साइन-अप के बाद, आप टेलीग्राम (पासवर्ड रीसेट के लिए) या ईमेल से अपनी प्रोफ़ाइल से कभी भी खाता सत्यापित कर सकते हैं। सत्यापन वैकल्पिक है।',
  'verify.title': 'अपना खाता सत्यापित करें', 'verify.subtitle': 'कोई एक विकल्प चुनें — इतना ही काफ़ी है।',
  'verify.tabTelegram': 'टेलीग्राम', 'verify.tabEmail': 'ईमेल',
  'verify.emailSent': 'हमने {email} पर सत्यापन लिंक भेजा है।',
  'verify.emailHint': 'इसी डिवाइस पर खोलें। नहीं दिख रहा? स्पैम/प्रोमोशन देखें।',
  'verify.telegramOpen': '@BaaliGames_bot खोलें',
  'verify.telegramExpiry': 'लिंक 30 मिनट में समाप्त। टेलीग्राम पर एक बार Start दबाना ज़रूरी है।',
  'verify.goToLogin': 'लॉगिन पर जाएँ',
  'verify.email.title': 'ईमेल सत्यापित', 'verify.email.sub': 'खाता सक्रिय। अब खेलने का समय।',
  'verify.email.failed': 'सत्यापन विफल', 'verify.email.verifying': 'सत्यापित हो रहा है…',
  'verify.backRegister': 'रजिस्टर पर वापस',
  'forgot.title': 'खाता पुनः प्राप्त करें', 'forgot.subtitle': 'हम वापस आने का रास्ता भेजेंगे।',
  'forgot.idLabel': 'यूज़रनेम, ईमेल या फ़ोन', 'forgot.idPlaceholder': 'यूज़रनेम, ईमेल या मोबाइल',
  'forgot.sendVia': 'भेजने का तरीक़ा', 'forgot.email': 'ईमेल', 'forgot.telegram': 'टेलीग्राम OTP',
  'forgot.telegramHelp': 'टेलीग्राम तभी काम करेगा जब आपका खाता हमारे बॉट से जुड़ा हो।',
  'forgot.cta': 'पुनर्प्राप्ति भेजें',
  'forgot.sentEmail': 'अगर खाता मौजूद है तो रीसेट लिंक ईमेल किया गया है।',
  'forgot.sentTelegram': 'अगर खाता मौजूद है तो टेलीग्राम पर 6-अंकों का कोड भेजा गया है।',
  'forgot.redirecting': 'रीसेट पेज पर भेजा जा रहा है…', 'forgot.back': 'लॉगिन पर वापस',
  'reset.title': 'नया पासवर्ड सेट करें',
  'reset.subOtp': 'टेलीग्राम पर भेजे कोड का उपयोग करें।',
  'reset.subLink': 'नया पासवर्ड चुनें और वापस खेलें।',
  'reset.otpLabel': '6-अंकों का कोड', 'reset.newPasswordLabel': 'नया पासवर्ड',
  'reset.cta': 'पासवर्ड अपडेट करें', 'reset.updating': 'अपडेट हो रहा है…',
  'reset.successTitle': 'पासवर्ड अपडेट हो गया', 'reset.successSub': 'सब तैयार। खेलने का समय।',
  'home.welcomeTo': 'स्वागत है', 'home.heroSub': 'कौशल-आधारित गेमिंग हब। खेलने के लिए साइन इन करें या खाता बनाएँ।',
  'home.skillBased': 'कौशल-आधारित', 'home.skillBasedSub': 'रणनीति जीतती है',
  'home.fairPlay': 'निष्पक्ष खेल', 'home.fairPlaySub': 'एंटी-चीट चालू',
  'home.instantPayout': 'UPI भुगतान', 'home.instantPayoutSub': '24 घंटे के अंदर',
  'home.featured': 'चुनिंदा खेल', 'home.tapToPlay': 'खेलने के लिए टैप करें',
  'home.ready': 'खेलने को तैयार?', 'home.dropIn': 'शामिल हों और असली इनाम जीतें।',
  'home.start': 'शुरू करें',
  'home.welcomeBack': 'वापस स्वागत है', 'home.balance': 'आपका बैलेंस', 'home.live': 'लाइव',
  'home.add': 'जोड़ें', 'home.manage': 'प्रबंधित करें', 'home.rewards': 'इनाम',
  'home.todayChallenge': 'आज की चुनौती', 'home.todayChallengeSub': 'मैच जीतें — बोनस पाएँ।',
  'home.view': 'देखें', 'home.yourGames': 'आपके खेल', 'home.topGames': 'टॉप गेम्स', 'home.resultOriented': 'रिज़ल्ट ओरिएंटेड', 'home.viewAll': 'सभी देखें', 'home.comingSoon': 'जल्द आ रहे हैं', 'home.noMatches': 'अभी कोई मैच नहीं', 'home.referEarn': 'रेफ़र करें, कमाएँ', 'home.refer': 'रेफ़र करें', 'home.perFriend': 'दोस्तों को बुलाएँ', 'home.cashback': 'कैशबैक', 'home.everyBet': 'हर बेट पर', 'home.offersLive': '{count} लाइव', 'home.noResultsYet': 'अभी कोई नतीजा नहीं',
  'profile.details': 'विवरण', 'profile.address': 'पता', 'profile.languages': 'भाषाएँ',
  'profile.account': 'खाता', 'profile.photo': 'फ़ोटो', 'profile.preferred': 'पसंदीदा भाषा',
  'profile.lang.primary': 'मुख्य भाषा', 'profile.lang.primarySub': 'पूरे ऐप का डिफ़ॉल्ट',
  'profile.lang.locked': 'बंद', 'profile.lang.second': 'दूसरी भाषा',
  'profile.lang.pickHelp': 'जो भाषा आप भी बोलते हैं उसे चुनें। कार्ड पर टैप करें।',
  'profile.lang.saved': 'दूसरी भाषा सहेज ली गई', 'profile.lang.cleared': 'दूसरी भाषा हटा दी गई',
  'lang.switcher.aria': 'भाषा चुनें', 'lang.switcher.title': 'अपनी भाषा चुनें',
  'lang.switcher.sub': 'पूरा ऐप तुरंत बदल जाएगा।', 'lang.switcher.current': 'वर्तमान',
  'lang.savePermanent': 'यही मेरी भाषा रखें', 'lang.savedPermanent': 'सेव हो गया। अब यही आपकी भाषा है।',
  'rewards.title': 'इनाम और ऑफ़र',
  'rewards.subtitle': 'अभी कमाने या क्लेम करने के लिए बोनस।',
  'rewards.tab.available': 'उपलब्ध', 'rewards.tab.inReview': 'समीक्षा में', 'rewards.tab.claimed': 'क्लेम किए',
  'rewards.empty.available.title': 'अभी कोई ऑफ़र नहीं',
  'rewards.empty.available.body': 'थोड़ी देर बाद देखें — एडमिन नियमित नए ऑफ़र पोस्ट करते हैं।',
  'rewards.empty.review.title': 'समीक्षा में कुछ नहीं',
  'rewards.empty.review.body': 'जिन क्लेम को एडमिन की मंज़ूरी चाहिए वे यहाँ दिखेंगे।',
  'rewards.empty.claimed.title': 'अभी तक कोई क्लेम नहीं',
  'rewards.empty.claimed.body': 'बोनस मिलते ही वह यहाँ दिखेगा।',
  'rewards.expires': '{date} को समाप्त', 'rewards.remaining': '{amount} बचा है', 'rewards.cap': 'सीमा',
  'rewards.claim.cta': 'क्लेम करें', 'rewards.claim.submit': 'भेजें',
  'rewards.claimed.credited': '{date} को {amount} क्लेम किया',
  'rewards.claimed.pending': 'समीक्षा बाक़ी', 'rewards.claimed.rejected': 'अस्वीकृत',
  'rewards.auto.willCredit': 'शर्तें पूरी होते ही अपने आप जुड़ेगा',
  'rewards.auto.willReview': 'एडमिन की समीक्षा क़तार में जाएगा',
  'rewards.row.creditedOn': '{date} को जोड़ा गया', 'rewards.row.submittedOn': '{date} को भेजा गया',
  'rewards.toast.credited': 'बोनस जुड़ गया — {amount} आपके वॉलेट में',
  'rewards.toast.submitted': 'समीक्षा के लिए भेजा गया',
  'rewards.toast.alreadyClaimed': 'पहले ही क्लेम किया जा चुका है',
  'rewards.toast.rejected': 'क्लेम अस्वीकार हुआ',

  'wallet.balance': 'बैलेंस',
  'wallet.balance.note': 'एंट्री तुरंत कटती है; जीत मैच के अंत में जुड़ती है।',
  'wallet.addMoney': 'पैसे जोड़ें', 'wallet.withdraw': 'निकालें',
  'wallet.withdraw.minBalance': 'निकालने के लिए कम से कम ₹200 चाहिए',
  'wallet.history.byGame': 'खेल के अनुसार आपका इतिहास',
  'wallet.history.empty': 'अभी तक कोई गतिविधि नहीं। आपका पहला मैच यहाँ दिखेगा।',
  'wallet.activity': 'हाल की गतिविधि', 'wallet.activity.empty': 'अभी तक कोई लेन-देन नहीं।',
  'wallet.net': 'नेट', 'wallet.newer': 'नया', 'wallet.older': 'पुराना', 'wallet.showOlder': 'पुराने लेन-देन देखें',
  'wallet.pending.title': 'लंबित अनुरोध',
  'wallet.pending.recharge': '{amount} का रिचार्ज — एडमिन की मंज़ूरी का इंतज़ार',
  'wallet.pending.withdrawal': '{amount} की निकासी — भुगतान का इंतज़ार',
  'wallet.state.pending': 'लंबित',
  'wallet.state.paid': 'भुगतान हो गया',
  'wallet.pending.txnPrefix': 'लेनदेन:',
  'wallet.pending.upload.label': 'तेज़ कार्रवाई के लिए भुगतान का स्क्रीनशॉट अपलोड करें।',
  'wallet.pending.upload.hint':  'अनुरोध करने के बाद आपके पास 24 घंटे हैं।',
  'wallet.pending.upload.failed': 'अपलोड विफल',
  'wallet.activity.range': '{total} में से {from}–{to}',
  'wallet.activity.balanceAfter': 'बैलेंस {balance}',
  'wallet.activity.window': 'आपकी पिछले {days} दिनों की गतिविधि दिखा रहे हैं',
  'wallet.history.summary.one':   '{matches} मैच · जीते {wins} · खर्च {bets}',
  'wallet.history.summary.other': '{matches} मैच · जीते {wins} · खर्च {bets}',
  'wallet.history.summary.refunded': ' · रिफंड {refunds}',
  'ledger.kind.bet':                'मैच एंट्री',
  'ledger.kind.win':                'मैच इनाम',
  'ledger.kind.refund':             'मैच रिफंड',
  'ledger.kind.forfeit':            'मैच ज़ब्ती',
  'ledger.kind.fee':                'प्लेटफ़ॉर्म फ़ीस',
  'ledger.kind.recharge':           'रिचार्ज',
  'ledger.kind.admin_credit':       'एडमिन क्रेडिट',
  'ledger.kind.admin_debit':        'एडमिन डेबिट',
  'ledger.kind.referral_bonus':     'रेफ़रल बोनस',
  'ledger.kind.signup_bonus':       'साइन-अप बोनस',
  'ledger.kind.offer_credit':       'ऑफ़र इनाम',
  'ledger.kind.withdrawal_hold':    'निकासी होल्ड',
  'ledger.kind.withdrawal_refund':  'निकासी रिफंड',
  'ledger.kind.agent_topup_in':     'एजेंट टॉप-अप',
  'ledger.kind.agent_co_fund':      'एजेंट योगदान',
  'ledger.kind.turnover_reward':    'कैशबैक',
  'ledger.withdrawalPaid':          'निकासी भुगतान हो गया',
  'ledger.withdrawalPaidPartial':   'निकासी भुगतान (आंशिक)',
  'ledger.withdrawalPaid.txn':      'भुगतान · लेनदेन {txnId}',
  'ledger.withdrawalRejected':      'निकासी अस्वीकृत',
  'ledger.withdrawalRejected.note': 'रोकी गई राशि वापस की गई',
  'ledger.withdrawalExpired':       'निकासी समाप्त',
  'ledger.awaitingProof':           'प्रमाण की प्रतीक्षा',
  'proofPicker.button.compact':     'स्क्रीनशॉट चुनें',
  'proofPicker.button.prominent':   'स्क्रीनशॉट जोड़ें',
  'proofPicker.processing':         'प्रोसेस हो रहा है…',
  'proofPicker.error.mime':         'केवल JPG, PNG या WebP छवियाँ अनुमत हैं।',
  'proofPicker.error.size':         'फ़ाइल बहुत बड़ी — अधिकतम 5 MB।',
  'proofPicker.error.processing':   'छवि प्रोसेस नहीं हो सकी',
  'proofPicker.previewAlt':         'चयनित स्क्रीनशॉट पूर्वावलोकन',
  'proofViewer.state.none':         'स्क्रीनशॉट की प्रतीक्षा',
  'proofViewer.state.available':    'स्क्रीनशॉट देखें',
  'proofViewer.state.expired':      'स्क्रीनशॉट अब उपलब्ध नहीं है',
  'proofViewer.state.viewLimit':    'देखने की सीमा पूरी',
  'proofViewer.loading':            'लोड हो रहा है…',
  'proofViewer.title.viewLimit':    'आपने यह स्क्रीनशॉट अधिकतम 2 बार देख लिया है',
  'proofViewer.error.load':         'स्क्रीनशॉट लोड नहीं हो सका',
  'proofViewer.lastView':           'अंतिम बार — छवि समाप्त हो जाएगी',
  'proofViewer.viewsRemaining.one':   '{n} बार और देख सकते हैं',
  'proofViewer.viewsRemaining.other': '{n} बार और देख सकते हैं',
  'proofViewer.imageAlt':           'भुगतान प्रमाण',
  'proofViewer.close':              'बंद करें',

  'recharge.title': 'पैसे जोड़ें',
  'recharge.step1.subtitle': 'कितने पैसे जोड़ना चाहते हैं?',
  'recharge.step1.amount': 'राशि', 'recharge.step1.range': 'न्यूनतम {min} · अधिकतम {max}',
  'recharge.step1.minHint': 'न्यूनतम रिचार्ज {min}', 'recharge.step1.maxHint': 'अधिकतम {max}',
  'recharge.step1.quickPick': 'त्वरित चुनाव',
  'recharge.step2.subtitle': 'UPI से भुगतान करें, फिर आगे बढ़ें',
  'recharge.step2.payAmount': 'इतना भुगतान करें', 'recharge.step2.iPaid': 'मैंने भुगतान किया',
  'recharge.step2.payFirst.title':   'पहले भुगतान करें, फिर वापस आएँ',
  'recharge.step2.payFirst.body':    'नीचे से कोई UPI ऐप चुनें और भुगतान भेजें। वापस आते ही पुष्टि बटन दिखेगा।',
  'recharge.step2.waiting.title':    'अपने UPI ऐप में भुगतान पूरा करें',
  'recharge.step2.waiting.body':     'पूरा करके वापस यहीं आएँ। पुष्टि बटन कुछ ही पल में दिखेगा।',
  'recharge.step2.didYouPay.title':  'भुगतान हो गया?',
  'recharge.step2.didYouPay.body':   'अगर भुगतान कर लिया है तो नीचे पुष्टि बटन दबाएँ।',
  'recharge.step2.confirmLocked':    'पहले UPI ऐप में भुगतान करें',
  'recharge.step2.alreadyPaid':      'मैंने पहले ही भुगतान कर दिया है',
  'recharge.step2.howto.1': 'अपना UPI ऐप खोलें (Google Pay / PhonePe / Paytm)।',
  'recharge.step2.howto.2': 'ऊपर दी गई राशि को UPI ID पर भेजें या QR स्कैन करें।',
  'recharge.step2.howto.3': 'UPI ऐप से ट्रांज़ैक्शन ID कॉपी करें — अगले स्टेप में चाहिए।',
  "recharge.locked.title": "रिचार्ज अस्थायी रूप से अनुपलब्ध",
  "recharge.locked.body": "हम इस रिचार्ज में आ रही समस्या को सुलझा रहे हैं। आपका वॉलेट और निकासी सामान्य रूप से काम कर रहे हैं। कृपया थोड़ी देर बाद कोशिश करें।",
  "recharge.locked.reassure.withdrawals": "निकासी सामान्य रूप से जारी है",
  "recharge.locked.reassure.existing": "जो भुगतान आपने पहले भेज दिया है, वह प्रोसेस हो रहा है",
  "recharge.cap.banner": "अभी अधिकतम रिचार्ज: {max}. प्राप्तकर्ता की क्षमता थोड़ी सीमित है।",
  "recharge.cap.tooHigh": "राशि सीमा से ज़्यादा है। अभी आप अधिकतम {max} ही रिचार्ज कर सकते हैं।",
  "recharge.notConfigured.title": "रिचार्ज जल्द ही उपलब्ध होगा",
  "recharge.notConfigured.body": "हम इस रिचार्ज सेवा के सेटअप को पूरा कर रहे हैं। आपका वॉलेट और निकासी सामान्य रूप से काम कर रहे हैं। कृपया थोड़ी देर बाद देखें।",
  'recharge.step3.subtitle': 'अपने भुगतान की पुष्टि करें',
  'recharge.step3.amountPaid': 'आपने जो राशि भेजी',
  'recharge.step3.txnId': 'UPI ट्रांज़ैक्शन ID',
  'recharge.step3.txnIdHelp': 'UPI ऐप की रसीद में मिलेगी — आम तौर पर 12 अंकों की।',
  'recharge.step3.note': 'एडमिन के लिए नोट',
  'recharge.step3.notePlaceholder': 'जैसे: पत्नी के नंबर से भेजा',
  'recharge.step3.submit': 'समीक्षा के लिए भेजें',
  'recharge.step3.disclaimer': 'एडमिन UPI ऐप में भुगतान सत्यापित करते हैं। मंज़ूरी के बाद 24 घंटे के अंदर वॉलेट में जुड़ जाएगा (अक्सर पहले)।',
  // Phase 21 (2026-05-21) — Step 3 स्क्रीनशॉट-first redesign.
  'recharge.step3.proof.firstStep':   'पहले यह करें',
  'recharge.step3.proof.done':        'हो गया',
  'recharge.step3.proof.title':       'भुगतान का स्क्रीनशॉट अपलोड करें',
  'recharge.step3.proof.subtitle':    'मंज़ूरी का सबसे तेज़ तरीक़ा — सभी के लिए सुझाव।',
  'recharge.step3.proof.pickerLabel': 'फ़ोन से स्क्रीनशॉट चुनने के लिए यहाँ टैप करें',
  'recharge.step3.proof.btn':         'स्क्रीनशॉट जोड़ें',
  'recharge.step3.proof.flow.upi':    'UPI ऐप खोलें',
  'recharge.step3.proof.flow.find':   'भुगतान ढूँढें',
  'recharge.step3.proof.flow.snap':   'स्क्रीनशॉट लें',
  'recharge.step3.proof.flow.upload': 'यहाँ अपलोड करें',
  'recharge.step3.or':                'या',
  'recharge.success.title': 'भेजा गया — समीक्षा प्रतीक्षित',
  'recharge.success.body': 'आपका {amount} का रिचार्ज एडमिन की क़तार में है। 24 घंटे के अंदर जुड़ जाएगा (अक्सर पहले)।',
  'recharge.success.refId': 'संदर्भ',
  'recharge.toast.submitted': 'रिचार्ज एडमिन की समीक्षा के लिए भेजा गया',

  'withdraw.title': 'निकालें',
  'withdraw.step1.subtitle': 'पैसे कहाँ भेजने हैं?',
  'withdraw.step2.subtitle': 'अंतिम जाँच',
  'withdraw.balance': 'उपलब्ध बैलेंस', 'withdraw.amount': 'राशि', 'withdraw.max': 'अधिकतम',
  'withdraw.upiId': 'आपकी UPI ID', 'withdraw.upiIdPh': 'aapkaname@upi',
  'withdraw.upiIdHelp': 'पैसे यहाँ भेजे जाएँगे। भेजने से पहले दो बार जाँच लें।',
  'withdraw.whatsapp': 'WhatsApp नंबर',
  'withdraw.errMin': 'न्यूनतम निकासी {min}', 'withdraw.errExceed': 'राशि बैलेंस से अधिक है',
  'withdraw.minHint': 'न्यूनतम निकासी {min}',
  'withdraw.step2.review': 'समीक्षा',
  'withdraw.step2.heldNotice': 'भेजते ही यह राशि रोक ली जाती है ताकि कहीं और खर्च न हो। एडमिन आपकी UPI ID पर भेजेगा और भुगतान के रूप में दर्ज करेगा।',
  'withdraw.heldAfter': 'रोकी गई', 'withdraw.spendableAfter': 'ख़र्च योग्य',
  'withdraw.step2.disclaimer': 'अगर एडमिन अस्वीकार करता है तो रोकी गई राशि वॉलेट में वापस आ जाएगी।',
  'withdraw.submit': 'भेजें',
  'withdraw.success.title': 'भेजा गया — भुगतान प्रतीक्षित',
  'withdraw.success.body': 'आपकी {amount} की निकासी क़तार में है। 24 घंटे के अंदर भुगतान (अक्सर पहले)। तब तक राशि रोकी रहेगी।',
  'withdraw.success.refId': 'संदर्भ',
  'withdraw.toast.submitted': 'निकासी क़तार में — आपके वॉलेट में रोकी गई',

  'payment.upi.label': 'इस UPI ID पर भेजें', 'payment.upi.copy': 'कॉपी',
  'payment.upiCopied': 'UPI ID कॉपी हुई',
  'payment.openUpiApp': 'अभी भुगतान करें', 'payment.openWhatsapp': 'WhatsApp',
  'payment.helpAt': 'मदद चाहिए? कॉल करें',
  'payment.qr.label': 'भुगतान के लिए QR स्कैन करें', 'payment.qr.tap': 'बड़ा करने के लिए टैप करें',
  'payment.qr.tapToClose': 'बंद करने के लिए कहीं भी टैप करें',
  'payment.notConfigured.title': 'अभी रिचार्ज उपलब्ध नहीं',
  'payment.notConfigured.contactBody': 'अभी कोई UPI सेट नहीं है। WhatsApp या फ़ोन पर संपर्क करें — हम मदद करेंगे।',
  'payment.notConfigured.unavailable': 'रिचार्ज अस्थायी रूप से उपलब्ध नहीं। कृपया बाद में फिर देखें।',
  'payment.chooser.title': 'अपना UPI ऐप चुनें',
  'payment.chooser.subtitle': 'आप भुगतान करेंगे',
  'payment.chooser.hint': 'राशि पहले से भरी स्थिति में ऐप खोलने के लिए टैप करें। ऐप पहले से फ़ोन में इंस्टॉल होनी चाहिए — यदि "address invalid" त्रुटि आये, तो पहले App Store / Play Store से ऐप इंस्टॉल करें।',

  'common.next': 'आगे', 'common.close': 'बंद करें',

  // Location capture (Phase R3)
  'geo.purpose': 'तेज़ और सहज भुगतान सेवा के लिए कृपया सही स्थान और संपर्क नंबर दें।',
  'geo.change': 'बदलें',
  'geo.gps.button': 'मेरा स्थान इस्तेमाल करें',
  'geo.gps.benefit': 'सबसे तेज़ — एक टैप, कुछ टाइप नहीं।',
  'geo.gps.denied': 'स्थान अनुमति अस्वीकृत। पिनकोड डालें या नीचे चुनें।',
  'geo.gps.failed': 'स्थान नहीं मिला। पिनकोड आज़माएँ।',
  'geo.gps.notResolved': 'आपका स्थान किसी क्षेत्र से नहीं जुड़ा। कृपया चुनें।',
  'geo.pincode.label': 'पिनकोड',
  'geo.pincode.notFound': 'पिनकोड नहीं मिला। कृपया अपना क्षेत्र चुनें।',
  'geo.pincode.lookupFailed': 'पिनकोड खोज विफल। कृपया चुनें।',
  'geo.multipleAreas.label': 'इस पिनकोड में कई क्षेत्र — अपना चुनें',
  'geo.multipleAreas.placeholder': 'अपना क्षेत्र चुनें',
  'geo.manual.title': 'मैन्युअल चुनें',
  'geo.manual.close': 'मैन्युअल छिपाएँ',
  'geo.state.label': 'राज्य', 'geo.state.placeholder': 'राज्य चुनें',
  'geo.district.label': 'ज़िला', 'geo.district.placeholder': 'ज़िला चुनें',
  'geo.area.label': 'क्षेत्र / शहर', 'geo.area.placeholder': 'क्षेत्र चुनें',
  'geo.privacy.note': 'केवल स्थानीय एजेंट देने के लिए। साझा नहीं किया जाता।',
  'geo.required.hint': 'रेफ़रल कोड न हो तो स्थान आवश्यक है।',

  // ════════════════════════════════════════════════════════════════════
  // AGENT APP — Hindi translations (Phase C Batch 1, Chunk 1 of 3).
  // Covers Phase B Groups 1-3: navigation, dashboard, emergency modals.

  // ── Navigation chrome (top + bottom) ──────────────────────────────
  'agent.nav.dashboard': 'डैशबोर्ड',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'हिसाब',
  'agent.nav.players':   'खिलाड़ी',
  'agent.nav.agents':    'एजेंट',
  'agent.nav.rewards':   'इनाम',
  'agent.nav.profile':   'प्रोफ़ाइल',
  'agent.nav.money':     'पैसे',
  'agent.bottomnav.home':    'होम',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'हिसाब',
  'agent.bottomnav.players': 'खिलाड़ी',
  'agent.bottomnav.agents':  'एजेंट',
  'agent.bottomnav.rewards': 'इनाम',
  'agent.bottomnav.profile': 'प्रोफ़ाइल',
  'agent.bottomnav.money':   'पैसे',

  // ── गेम्स हब — U3 (2026-07-09) ─────────────────────────────────────
  'agent.games.hub.title':        'हिसाब',
  'agent.games.hub.subtitle':     'आपका पूरा हिसाब — गेम से भी, लोगों से भी',
  // हिसाब व्यू सेलेक्टर — मालिक की माँग 2026-07-09.
  'agent.hisaab.tab.game':           'गेम हिसाब',
  'agent.hisaab.tab.people':         'लोगों का हिसाब',
  'agent.hisaab.people.playersCard': 'सीधे खिलाड़ियों से',
  'agent.hisaab.person.desc':        'किस गेम से, किस % पर, और आपका कट:',
  'agent.hisaab.person.myCut':       'मेरा कट',
  'agent.hisaab.person.viewProfile': 'पूरी प्रोफ़ाइल देखें',
  'agent.hisaab.rate.my':            'मेरा रेट',
  'agent.hisaab.rate.their':         'इसका रेट',
  'agent.hisaab.eq.lost':            'हारा',
  'agent.hisaab.eq.won':             'जीता',
  'agent.hisaab.eq.turnover':        'कारोबार',
  'agent.hisaab.pillar.short.win_loss':         'हार-जीत',
  'agent.hisaab.pillar.short.profit_share':     'P&L',
  'agent.hisaab.pillar.short.commission_share': 'एक्सचेंज',
  'agent.hisaab.pillar.short.adjust':           'एडज.',
  'agent.hisaab.pillar.short.turnover_share':   'रिटर्न',
  'agent.games.hero.kicker':      'सारे गेम मिलाकर',
  'agent.games.hero.sub':         'क्रिकेट और कैसीनो गेम, सब कुछ मिलाकर।',
  'agent.games.section.main':     'मुख्य गेम',
  'agent.games.section.casino':   'कैसीनो और छोटे गेम',
  'agent.games.cricket.name':     'क्रिकेट',
  'agent.games.cricket.balance':  'क्रिकेट वॉलेट',
  'agent.games.cricket.details':  'क्रिकेट की पूरी जानकारी देखें',
  'agent.games.row.nothing':      'अभी कोई कमाई नहीं',
  'agent.games.empty.casino':     'जैसे ही आपके खिलाड़ी खेलना शुरू करेंगे, हर गेम की कमाई यहाँ दिखने लगेगी।',
  'agent.games.error':            'गेम्स अभी लोड नहीं हो पाए। थोड़ी देर बाद फिर कोशिश करें।',
  'agent.games.range.today':      'आज',
  'agent.games.range.week':       'इस हफ़्ते',
  'agent.games.range.month':      'इस महीने',
  'agent.games.range.all':        'अब तक',
  // "किसकी वजह से" मोडल — मालिक की माँग 2026-07-09.
  'agent.games.detail.allGames':         'सारे गेम',
  'agent.games.detail.dateLine':         '{from} से {to} तक का हिसाब',
  'agent.games.detail.desc':             'इस दौरान किसने आपको कितना फ़ायदा या नुकसान कराया:',
  'agent.games.detail.net':              'इस दौरान कुल',
  'agent.games.detail.players.section':  'आपके खिलाड़ियों से',
  'agent.games.detail.downline.section': 'आपके {label} के ज़रिए',
  'agent.games.detail.other':            'अन्य / एडजस्टमेंट',
  'agent.games.detail.empty':            'इस दौरान कोई एंट्री नहीं।',
  'agent.games.detail.tapHint':          'टैप करें — देखें किसकी वजह से',

  // ── प्रोफ़ाइल — U4 (2026-07-09) कार्ड-मेन्यू ─────────────────────────
  'agent.profile.landing.wallet.title':   'मेरा वॉलेट',
  'agent.profile.landing.wallet.sub':     'रिचार्ज-निकासी अनुरोध और पूरा इतिहास',
  'agent.profile.landing.deposit.title':  'सुरक्षा जमा',
  'agent.profile.landing.deposit.sub':    'हाउस के पास रखी आपकी सुरक्षा राशि',
  'agent.profile.landing.deposit.safe':   'सुरक्षित',
  'agent.profile.landing.deposit.zero':   'खाली',
  'agent.profile.landing.rates.title':    'मेरे रेट',
  'agent.profile.landing.rates.sub':      'हर गेम से आपकी कमाई की दरें',
  'agent.profile.landing.rewards.title':  'इनाम और रेफ़रल',
  'agent.profile.landing.rewards.sub':    'ऑफ़र जॉइन करें, और अपनी नेटवर्क ग्रोथ देखें',
  'agent.profile.landing.account.title':  'खाता और सुरक्षा',
  'agent.profile.landing.account.sub':    'नाम, फ़ोन, पासवर्ड, साइन आउट',
  'agent.profile.landing.danger.wallet':  'आपका वॉलेट माइनस में है — कृपया जल्द रिचार्ज करें।',
  'agent.profile.landing.danger.deposit': 'आपकी सुरक्षा जमा खाली है — नए खिलाड़ी आपको नहीं मिलेंगे।',
  'agent.profile.rewards.offers.section':    'ऑफ़र',
  'agent.profile.rewards.cost.title':        'ऑफ़र से मेरा खर्च',
  'agent.profile.rewards.cost.line':         'ऑफ़र पर अब तक आपका खर्च {x}। ज़्यादा से ज़्यादा हो सकता है: {y}।',
  'agent.profile.rewards.referrals.section': 'रेफ़रल',

  // ── U5 string sweep (2026-07-09) — see en block for surface map. ──

  // Agent home dashboard.
  'agent.home.welcome':             'वापसी पर स्वागत है — आप हैं',
  'agent.home.error.load':          'डैशबोर्ड लोड नहीं हो पाया।',
  'agent.home.error.detail':        'लोड नहीं हो पाया।',
  'agent.home.hero.title':          'गेम से आपकी कमाई',
  'agent.home.hero.tap':            'हर गेम देखने के लिए टैप करें',
  'agent.home.tile.players':        'खिलाड़ी',
  'agent.home.tile.requests':       'अनुरोध',
  'agent.home.tile.myAccount':      'मेरा खाता',
  'agent.home.tile.totalBalance':   '{x} कुल बैलेंस',
  'agent.home.tile.inOut':          '+{in} आया · −{out} गया',
  'agent.home.tile.totalWorth':     '{x} कुल बैलेंस',
  'agent.home.tile.partnership':    '{x}% हार-जीत में हिस्सेदारी',
  'agent.home.modal.game':          'गेम की कमाई',
  'agent.home.modal.pendingNow':    'अभी लंबित',
  'agent.home.table.filter':        'खोजें…',
  'agent.home.table.clearFilter':   'फ़िल्टर हटाएँ',
  'agent.home.table.noMatches':     'कुछ नहीं मिला।',
  'agent.home.table.total':         'कुल',
  'agent.home.table.totalCount':    'कुल ({n})',
  'agent.home.game.col.game':       'गेम',
  'agent.home.game.col.bet':        'बेट',
  'agent.home.game.col.won':        'जीते',
  'agent.home.game.col.lost':       'हारे',
  'agent.home.game.col.myPct':      'आपका %',
  'agent.home.game.col.earned':     'आपकी कमाई',
  'agent.home.game.col.toUpline':   'अपलाइन को',
  'agent.home.game.empty':          'इस दौरान कोई गेम नहीं खेला गया।',
  'agent.home.game.filter':         'गेम खोजें…',
  'agent.home.downline.col.name':     'नाम',
  'agent.home.downline.col.theirPct': 'उनका %',
  'agent.home.downline.col.youEarn':  'आपकी कमाई',
  'agent.home.downline.col.theyEarn': 'उनकी कमाई',
  'agent.home.downline.col.balance':  'बैलेंस',
  'agent.home.downline.empty':      'अभी कोई {x} नहीं।',
  'agent.home.downline.filter':     '{x} खोजें…',
  'agent.home.users.col.player':    'खिलाड़ी',
  'agent.home.users.col.winLoss':   'जीत / हार',
  'agent.home.users.empty':         'अभी कोई खिलाड़ी नहीं।',
  'agent.home.users.filter':        'खिलाड़ी खोजें…',
  'agent.home.requests.col.from':   'किसने भेजा',
  'agent.home.requests.col.type':   'टाइप',
  'agent.home.requests.col.amount': 'रकम',
  'agent.home.requests.recharge':   'रिचार्ज',
  'agent.home.requests.withdrawal': 'निकासी',
  'agent.home.requests.net':        'नेट',
  'agent.home.requests.empty':      'कोई लंबित अनुरोध नहीं।',
  'agent.home.requests.filter':     'अनुरोध खोजें…',

  // Agent cricket earnings page.
  'agent.cricketPage.loading':          'Cricket की कमाई लोड हो रही है…',
  'agent.cricketPage.error.loadFailed': 'लोड नहीं हो पाया',
  'agent.cricketPage.title':            'Cricket से कमाई',
  'agent.cricketPage.subtitle':         'Cricket एक्सचेंज पर आपका कमीशन — लाइव, Cricket के अपने बहीखाते से।',
  'agent.cricketPage.upline':           'आपका अपलाइन:',
  'agent.cricketPage.uplineHouse':      'आप सीधे हाउस के नीचे हैं।',
  'agent.cricketPage.chip.pl':          'हार-जीत हिस्सा',
  'agent.cricketPage.chip.comm':        'कमीशन',
  'agent.cricketPage.stat.balance':     'Cricket बैलेंस',
  'agent.cricketPage.stat.balance.sub': 'अब तक जमा हुआ Cricket कमीशन वॉलेट',
  'agent.cricketPage.stat.week':        'इस हफ़्ते',
  'agent.cricketPage.stat.week.sub':    'सारी कमाई मिलाकर, पिछले 7 दिन',
  'agent.cricketPage.stat.allTime':     'अब तक',
  'agent.cricketPage.stat.allTime.sub': 'जब से आपका Cricket खाता खुला',
  'agent.cricketPage.pillars.title':    'कमाई कहाँ से आती है',
  'agent.cricketPage.pillar.profitShare': 'हार-जीत में हिस्सेदारी',
  'agent.cricketPage.pillar.commShare':   'एक्सचेंज कमीशन में हिस्सा',
  'agent.cricketPage.pillar.adjust':      'एडजस्टमेंट',
  'agent.cricketPage.hint.profitShare': 'आपके खिलाड़ियों की हार-जीत में आपकी हिस्सेदारी %।',
  'agent.cricketPage.hint.commShare':   'खिलाड़ी-बनाम-खिलाड़ी जीत पर एक्सचेंज कमीशन में आपका हिस्सा।',
  'agent.cricketPage.hint.turnoverReturn': 'आपके खिलाड़ियों के कारोबार पर घर से मिलने वाला रिटर्न — जीतें या हारें।',
  'agent.cricketPage.entries':          '{n} एंट्री',
  'agent.cricketPage.players.title':    'मेरे खिलाड़ी — Cricket का हिसाब',
  'agent.cricketPage.players.empty':    'अभी आपके नीचे कोई खिलाड़ी नहीं।',
  'agent.cricketPage.players.col.player':   'खिलाड़ी',
  'agent.cricketPage.players.col.turnover': 'हफ़्ते का कारोबार',
  'agent.cricketPage.players.col.atRisk':   'अभी दांव पर',
  'agent.cricketPage.players.col.balance':  'बैलेंस',
  'agent.cricketPage.players.col.earnedMe': 'मेरी कमाई',
  'agent.cricketPage.limits.open':       'लिमिट',
  'agent.cricketPage.limits.loading':    'लिमिट लोड हो रही है…',
  'agent.cricketPage.limits.saved':      'सेव हो गया।',
  'agent.cricketPage.limits.maxMO':      'मैक्स दांव · मैच ऑड्स',
  'agent.cricketPage.limits.maxBM':      'मैक्स दांव · बुकमेकर',
  'agent.cricketPage.limits.maxSession': 'मैक्स दांव · सेशन',
  'agent.cricketPage.limits.maxExposure':'मैक्स एक्सपोज़र / मैच',
  'agent.cricketPage.limits.noCap':      'कोई लिमिट नहीं',
  'agent.cricketPage.limits.save':       'लिमिट सेव करें',
  'agent.cricketPage.limits.perGame':    'हर गेम की मैक्स बेट',
  'agent.cricketPage.limits.casino':     'कैसीनो गेम',
  'agent.cricketPage.limits.board':      'बोर्ड गेम',
  'agent.cricketPage.limits.small':      'छोटे गेम',
  'agent.cricketPage.limits.cricket':    'Cricket',
  'agent.cricketPage.limits.hint':       'खाली = कोई लिमिट नहीं · 0 = ब्लॉक',
  'agent.cricketPage.subs.title':       'मेरे सब-एजेंट — रेट और Cricket बैलेंस',
  'agent.cricketPage.subs.col.agent':   'Agent',
  'agent.cricketPage.subs.col.match':   'मैच %',
  'agent.cricketPage.subs.col.session': 'सेशन %',
  'agent.cricketPage.subs.col.pl':      'हार-जीत %',
  'agent.cricketPage.subs.col.comm':    'कमीशन %',
  'agent.cricketPage.subs.col.players': 'खिलाड़ी',
  'agent.cricketPage.subs.col.theyEarned': 'उनकी कमाई',
  'agent.cricketPage.subs.col.residual':   'उनसे मेरी कमाई',
  'agent.cricketPage.subs.col.balance':    'उनका बैलेंस',
  'agent.cricketPage.subs.note':        'सब-एजेंट का रेट कभी आपके रेट से ज़्यादा नहीं हो सकता (Golden Limit)। "उनसे मेरी कमाई" वह है जो आपने उनके खिलाड़ियों से कमाया — आपके % और उन्हें दिए % का फ़र्क़।',
  'agent.cricketPage.weekly.title':     'हफ़्ते का हिसाब',
  'agent.cricketPage.weekly.empty':     'अभी तक कुछ सेटल नहीं हुआ।',
  'agent.cricketPage.weekly.weekOf':    '{date} का हफ़्ता',
  'agent.cricketPage.recent.title':     'ताज़ा एंट्री',
  'agent.cricketPage.recent.empty':     'अभी कोई एंट्री नहीं — आपके खिलाड़ियों की बेट सेटल होते ही यहाँ दिखेंगी।',
  'agent.cricketPage.recent.player':    'खिलाड़ी #{id}',

  // Agent referrals panel.
  'agent.referrals.loading':          'रेफ़रल लोड हो रहे हैं…',
  'agent.referrals.error.loadFailed': 'रेफ़रल लोड नहीं हो पाए।',
  'agent.referrals.hero.kicker':      'नेटवर्क की बढ़त',
  'agent.referrals.hero.paidLabel':   'एडमिन की तरफ़ से दिया बोनस',
  'agent.referrals.hero.explainer':   'रेफ़रल बोनस पूरा 100% प्लेटफ़ॉर्म देता है। इन भुगतानों का एक भी पैसा आपके वॉलेट से नहीं कटता — यह डैशबोर्ड बस दिखाता है कि आपके नेटवर्क में कितनी बढ़त हो रही है।',
  'agent.referrals.stat.total':       'कुल',
  'agent.referrals.status.pending':   'लंबित',
  'agent.referrals.status.credited':  'क्रेडिट हुआ',
  'agent.referrals.status.rejected':  'रद्द',
  'agent.referrals.filter.all':       'सभी ({n})',
  'agent.referrals.empty.all':        'आपके नेटवर्क में अभी किसी ने रेफ़र नहीं किया।',
  'agent.referrals.empty.filtered':   'कोई {status} रेफ़रल नहीं।',
  'agent.referrals.pagination':       '{total} में से {shown} दिखा रहे हैं — पेज-दर-पेज लोड जल्द आएगा।',
  'agent.referrals.card.code':        'कोड',
  'agent.referrals.card.referred':    'रेफ़र किया {date}',
  'agent.referrals.card.credited':    'क्रेडिट हुआ {date}',
  'agent.referrals.card.adminPaid':   'एडमिन ने दिया',
  'agent.referrals.card.recharge':    'रिचार्ज #{id}',

  // Agent browse-offers leftovers.
  'agent.offers.badge.new':     'नया',
  'agent.offers.badge.newAria': 'नया ऑफ़र — अभी चालू नहीं किया',

  // Agent my-rates panel.
  'agent.myRates.hero.subtitle': 'आप अपने खिलाड़ियों से क्या कमाते हैं — आपके अपलाइन ने तय किया है, यहाँ बदला नहीं जा सकता।',
  'agent.myRates.loading':       'आपके रेट लोड हो रहे हैं…',
  'agent.myRates.error':         'आपके रेट लोड नहीं हो पाए।',
  'agent.myRates.footer.zero':   '0% का मतलब बस इतना है कि वह कमाई अभी आपके लिए बंद है।',
  'agent.myRates.row.winLoss.label':     'हार-जीत में हिस्सेदारी',
  'agent.myRates.row.winLoss.plain':     'आपके खिलाड़ी जो हारते हैं उसमें आपका हिस्सा (वे जीतें तो उतना ही हिस्सा आप भी भरते हैं)।',
  'agent.myRates.row.profitShare.label': 'हार-जीत में हिस्सेदारी',
  'agent.myRates.row.profitShare.plain': 'Cricket में आपके खिलाड़ी जो हारते (और जीतते) हैं उसमें आपका हिस्सा।',
  'agent.myRates.row.commShare.label':   'एक्सचेंज कमीशन में हिस्सा',
  'agent.myRates.row.commShare.plain':   'खिलाड़ी-बनाम-खिलाड़ी जीत पर प्लेटफ़ॉर्म के छोटे कट में आपका हिस्सा।',

  // Agent account panel — U5 additions.
  'agent.account.personal.field.firstName': 'पहला नाम',
  'agent.account.personal.field.lastName':  'अंतिम नाम',
  'agent.account.personal.field.dob':       'जन्म तारीख़',
  'agent.account.personal.field.name.hint':     'आपके डाउनलाइन / अपलाइन को दिखता है',
  'agent.account.personal.field.dob.hint':      'निजी · किसी और को कभी नहीं दिखता',
  'agent.account.personal.field.username.hint': 'सिर्फ़ लॉगिन के लिए — किसी और को कभी नहीं दिखता',
  'agent.account.personal.error.firstNameShort': 'पहला नाम कम से कम 2 अक्षर का होना चाहिए',
  'agent.account.personal.error.lastNameShort':  'अंतिम नाम कम से कम 2 अक्षर का होना चाहिए',
  'agent.account.personal.error.dobFormat':      'जन्म तारीख़ YYYY-MM-DD फ़ॉर्मैट में होनी चाहिए',

  // ── Dashboard header + range toggle ───────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'फिर से स्वागत है, ',
  'agent.dashboard.header.welcomeFallback': 'दोस्त',
  'agent.dashboard.header.subtitle':        'आपका कारोबार एक नज़र में — पूल, जमा, खिलाड़ी, सब-एजेंट, लाभ-हानि, एक्सपोज़र',
  'agent.dashboard.range.today':            'आज',
  'agent.dashboard.range.yesterday':        'कल',
  'agent.dashboard.range.thisWeek':         'इस हफ़्ते',
  'agent.dashboard.range.lastWeek':         'पिछले हफ़्ते',
  'agent.dashboard.range.thisMonth':        'इस महीने',
  'agent.dashboard.range.lastMonth':        'पिछले महीने',
  'agent.dashboard.range.complete': 'कुल',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading':           'जमा कवरेज लोड हो रहा है…',
  'agent.dashboard.depositBar.label':             'जमा कवरेज',
  'agent.dashboard.depositBar.balanceOfTarget':   '· लक्ष्य {target} में से {balance}',
  'agent.dashboard.depositBar.tapToView':         'पूरा जमा पेज देखने के लिए टैप करें।',
  'agent.dashboard.depositBar.caption.suspended': 'खाता निलंबित। एडमिन से संपर्क करें।',
  'agent.dashboard.depositBar.caption.breach':    'आपके वॉलेट ने जमा सीमा का 100% इस्तेमाल कर लिया है। निकासी बंद कर दी गई है। तुरंत टॉप-अप करें।',
  'agent.dashboard.depositBar.caption.warning':   'आपका वॉलेट जमा सीमा के {pct}% पर है। चेतावनी 80% पर, लॉकडाउन 100% पर।',
  'agent.dashboard.depositBar.caption.caution':   'आपके वॉलेट ने जमा कवरेज का {pct}% इस्तेमाल कर लिया है। 80% से नीचे — अभी सुरक्षित।',
  'agent.dashboard.depositBar.caption.safe':      'जमा पूरी तरह सुरक्षित। कोई कवरेज इस्तेमाल नहीं हो रही।',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'चेतावनी · शून्य सुरक्षा जमा',
  'agent.dashboard.locatorSkip.body':  'आपका सुरक्षा जमा {amount} है। जब तक आप जमा नहीं भरते तब तक भौगोलिक लोकेटर से नए खिलाड़ी नहीं मिलेंगे। मौजूदा खिलाड़ी + सीधे रेफरल + मैन्युअल असाइनमेंट प्रभावित नहीं होंगे — केवल ऑर्गेनिक जियो-राउटेड साइनअप आपको छोड़ देंगे।',
  'agent.dashboard.locatorSkip.cta':   'जमा भरें',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title':                  'डाउनलाइन डिफ़ॉल्टर अलर्ट',
  'agent.dashboard.defaulter.summary.critical.one': 'आपके सीधे सब-एजेंट में से {n} डिफ़ॉल्ट में है — वह लॉक है या उसकी जमा सीमा टूट चुकी है।',
  'agent.dashboard.defaulter.summary.critical.other': 'आपके {n} सीधे सब-एजेंट डिफ़ॉल्ट में हैं — वे लॉक हैं या उनकी जमा सीमा टूट चुकी है।',
  'agent.dashboard.defaulter.summary.warning.one': 'आपके सीधे सब-एजेंट में से {n} जमा सीमा के 80%+ पर है — डिफ़ॉल्टर का जोखिम।',
  'agent.dashboard.defaulter.summary.warning.other':  'आपके {n} सीधे सब-एजेंट जमा सीमा के 80%+ पर हैं — डिफ़ॉल्टर का जोखिम।',
  'agent.dashboard.defaulter.worst':                  'सबसे बुरा: @{username} जमा सीमा के {pct}% पर।',
  'agent.dashboard.defaulter.tapHint':                'सीधे सब-एजेंट सूची पर जाने के लिए टैप करें। डिफ़ॉल्टर पंक्तियाँ हाइलाइट हैं।',

  // ── Margin-status pill labels ─────────────────────────────────────
  'agent.dashboard.marginPill.locked':  'लॉक',
  'agent.dashboard.marginPill.caution': 'सावधान',
  'agent.dashboard.marginPill.warning': 'चेतावनी',
  'agent.dashboard.marginPill.breach':  'सीमा भंग',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title':     'सुबह का सारांश',
  'agent.dashboard.sections.morning.subtitle':  'लाइव स्नैपशॉट — हर संख्या अपने आप ताज़ा होती है',
  'agent.dashboard.sections.activity.title':    'गतिविधि',
  'agent.dashboard.sections.activity.subtitle': 'सबसे ज़्यादा ख़र्च करने वाले और हाल के खिलाड़ी भुगतान',
  'agent.dashboard.sections.forensic.title':    'फ़ॉरेंसिक विवरण',
  'agent.dashboard.sections.forensic.subtitle': 'किसी भी सेक्शन को विस्तार से देखने के लिए टैप करें — पेज शांत रखने के लिए डिफ़ॉल्ट रूप से बंद',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title':           'लाभ-हानि का गहराई से विश्लेषण',
  'agent.dashboard.accordions.pnlDeep.helper':          'आपकी कमाई का प्रति-पिलर + प्रति-गेम विश्लेषण। अंतर्निहित मैच देखने के लिए गेम पंक्ति पर क्लिक करें।',
  'agent.dashboard.accordions.leaderboard.title':       'डाउनलाइन लीडरबोर्ड',
  'agent.dashboard.accordions.leaderboard.helper':      'आपके सब-एजेंट शुद्ध कमाई के आधार पर क्रम में। ऋणात्मक संख्याओं का मतलब है कि उस सब-एजेंट ने जितना लाया उससे ज़्यादा भुगतान किया।',
  'agent.dashboard.accordions.activity.title':          'डाउनलाइन गतिविधि फ़ीड',
  'agent.dashboard.accordions.activity.helper':         'आपके सब-एजेंट के हर मैच सेटलमेंट, रिचार्ज और रेट परिवर्तन की — फ़ॉरेंसिक टाइमलाइन।',
  'agent.dashboard.accordions.earnings.title':          'कमाई का विवरण (लाइफ़टाइम)',
  'agent.dashboard.accordions.earnings.helper':         'प्रकार के अनुसार लाइफ़टाइम कुल। ऊपर के समय फ़िल्टर को नज़रअंदाज़ करता है — ये कभी नहीं बदलते।',
  'agent.dashboard.accordions.directSubAgents.title':   'सीधे सब-एजेंट',
  'agent.dashboard.accordions.directSubAgents.helper':  'आपके सीधे प्रतिनिधि। हर एक अपने खिलाड़ी + अपने सब-एजेंट चलाता है। गुलाबी/एम्बर रंग की पंक्तियाँ डिफ़ॉल्टर क्षेत्र में हैं।',
  'agent.dashboard.accordions.chain.title':             'चेन दृश्यता',
  'agent.dashboard.accordions.chain.helper':            'आपकी पूरी डाउनलाइन — सीधी (आप संपादित करते हैं) और अप्रत्यक्ष (आपके सब-एजेंट प्रबंधित करते हैं) में अलग।',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'लाइव डेटा — फ़ोकस पर, हर 60 सेकंड में, और पैसे की हलचल पर ताज़ा होता है।',

  // ── Card: Pool balance ────────────────────────────────────────────
  'agent.dashboard.cards.poolBalance.label':       'मेरा पूल बैलेंस',
  'agent.dashboard.cards.poolBalance.helper':      'आपका ख़र्च योग्य बैलेंस। खिलाड़ियों को रिचार्ज करने या सब-एजेंट को भेजने के लिए उपलब्ध पैसा।',
  'agent.dashboard.cards.poolBalance.netInRange':  '{range} में नेट {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading':           'सुरक्षा जमा',
  'agent.dashboard.cards.depositStatus.titleLoaded':            'जमा स्थिति',
  'agent.dashboard.cards.depositStatus.helperLoading':          'जमा स्थिति लोड हो रही है…',
  'agent.dashboard.cards.depositStatus.helper':                 'आपके पूल के लिए ज़मानत। स्थिति लक्ष्य के 50% पर सावधान, 80% पर चेतावनी, 100% पर सीमा भंग में बदलती है।',
  'agent.dashboard.cards.depositStatus.suspendedSince':         '{when} से',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'चालू बैलेंस',
  'agent.dashboard.cards.depositStatus.field.depositBalance':   'जमा बैलेंस',
  'agent.dashboard.cards.depositStatus.field.depositTarget':    'जमा लक्ष्य',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ऋणात्मक एक्सपोज़र',
  'agent.dashboard.cards.depositStatus.field.coverageRatio':    'कवरेज अनुपात',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'जमा लक्ष्य का ऋणात्मक एक्सपोज़र से इस्तेमाल किया गया हिस्सा',
  'agent.dashboard.depositStatus.status.safe':      'सुरक्षित',
  'agent.dashboard.depositStatus.status.caution':   'सावधान',
  'agent.dashboard.depositStatus.status.warning':   'चेतावनी',
  'agent.dashboard.depositStatus.status.breach':    'सीमा भंग',
  'agent.dashboard.depositStatus.status.suspended': 'निलंबित',
  'agent.dashboard.depositStatus.status.healthy':   'स्वस्थ',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title':      'खिलाड़ियों का कुल वॉलेट',
  'agent.dashboard.cards.playersWallet.helper':     'अभी आपके हर खिलाड़ी के वॉलेट का संयुक्त लाइव बैलेंस। आपसे जुड़ी सभी wallet_points पंक्तियों का योग।',
  'agent.dashboard.cards.playersWallet.foot.one':   '{n} खिलाड़ी · {active} सक्रिय',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} खिलाड़ी · {active} सक्रिय',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'सब-एजेंट के वॉलेट + जमा',
  'agent.dashboard.cards.subAgents.helper':      'आपके सीधे सब-एजेंट का संयुक्त पूल बैलेंस और सुरक्षा जमा। हर सब-एजेंट अपने खिलाड़ी + अपनी डाउनलाइन चलाता है।',
  'agent.dashboard.cards.subAgents.field.pool':    'पूल',
  'agent.dashboard.cards.subAgents.field.deposit': 'जमा',
  'agent.dashboard.cards.subAgents.foot.one':      '{n} सीधा सब-एजेंट',
  'agent.dashboard.cards.subAgents.foot.other':    '{n} सीधे सब-एजेंट',

  // ── Card: My pending requests ─────────────────────────────────────
  'agent.dashboard.cards.myPending.title':            'मेरे लंबित अनुरोध',
  'agent.dashboard.cards.myPending.helper': 'प्राथमिकता 3. एडमिन को भेजे गए आपके अपने अनुरोध — इन पर आप कार्रवाई नहीं कर सकते, उनका इंतज़ार है।',
  'agent.dashboard.cards.myPending.recharge.one': '{n} रिचार्ज एडमिन की मंज़ूरी का इंतज़ार में है',
  'agent.dashboard.cards.myPending.recharge.other': '{n} रिचार्ज एडमिन की मंज़ूरी के इंतज़ार में हैं',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} निकासी एडमिन की मंज़ूरी का इंतज़ार में है',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} निकासियाँ एडमिन की मंज़ूरी के इंतज़ार में हैं',

  // ── Card: Players' pending requests ───────────────────────────────
  'agent.dashboard.cards.playersPending.title':            'खिलाड़ियों के लंबित अनुरोध',
  'agent.dashboard.cards.playersPending.helper':           'प्राथमिकता 1. आपके खिलाड़ी आपसे इन्हें मंज़ूर या भुगतान करने का इंतज़ार कर रहे हैं। कार्रवाई के लिए टैप करें।',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} रिचार्ज मंज़ूरी के लिए लंबित',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} रिचार्ज मंज़ूरी के लिए लंबित',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} निकासी भुगतान के लिए लंबित',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} निकासियाँ भुगतान के लिए लंबित',

  // ── Card: Sub-agents' pending requests ────────────────────────────
  'agent.dashboard.cards.subAgentsPending.title':  'सब-एजेंट के लंबित अनुरोध',
  'agent.dashboard.cards.subAgentsPending.helper': 'प्राथमिकता 2. आपके सब-एजेंट (पूरी डाउनलाइन) आपसे इन्हें मंज़ूर या भुगतान करने का इंतज़ार कर रहे हैं। कार्रवाई के लिए टैप करें।',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'वास्तविक नेट लाभ-हानि · {range}',
  'agent.dashboard.cards.trueNet.helper': 'इस अवधि में आपका असली लाभ/हानि। प्लेटफ़ॉर्म फ़ीस शेयर + विन/लॉस शेयर − ऑफ़र को-फ़ंडिंग। प्रति-गेम विवरण के लिए नीचे लाभ-हानि का गहरा विश्लेषण खोलें।',
  'agent.dashboard.pillarChip.platformFee': 'प्लेटफ़ॉर्म',
  'agent.dashboard.pillarChip.winLoss':     'विन/लॉस',
  'agent.dashboard.pillarChip.offer':       'ऑफ़र',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title':      'ऑफ़र उपयोग · {range}',
  'agent.dashboard.cards.rewardUtil.helper':     'इस अवधि में आपके खिलाड़ियों को ऑफ़र के ज़रिए दिए गए कुल इनाम ₹। उस लागत में अपना हिस्सा देखने के लिए अपने एक्सपोज़र पेज से तुलना करें।',
  'agent.dashboard.cards.rewardUtil.foot.one':   '{n} दावा जुड़ा',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} दावे जुड़े',

  // ── Activity row ──────────────────────────────────────────────────
  'agent.dashboard.activity.topSpenders.title':     'सबसे ज़्यादा ख़र्च करने वाले',
  'agent.dashboard.activity.topSpenders.helper':    '{range} में सबसे ज़्यादा ख़र्च करने वाले आपके खिलाड़ी।',
  'agent.dashboard.activity.topSpenders.empty':     'अभी इस अवधि में कोई ख़र्च नहीं।',
  'agent.dashboard.activity.recent.title':          'हाल की गतिविधि',
  'agent.dashboard.activity.recent.helper':         'आपके खिलाड़ियों के पाँच सबसे हाल के रिचार्ज ईवेंट।',
  'agent.dashboard.activity.recent.empty':          'कोई हाल की गतिविधि नहीं।',
  'agent.dashboard.activity.recent.state.approved': 'मंज़ूर',
  'agent.dashboard.activity.recent.state.rejected': 'अस्वीकृत',
  'agent.dashboard.activity.recent.state.pending':  'लंबित',

  // ── P&L deep-dive ─────────────────────────────────────────────────
  'agent.dashboard.pnl.perGameLabel':         'प्रति गेम · व्याख्या के लिए पंक्ति पर टैप करें',
  'agent.dashboard.pnl.noEarnings':           'इस अवधि में कोई कमाई नहीं।',
  'agent.dashboard.pnl.table.col.pillarGame': 'पिलर / गेम',
  'agent.dashboard.pnl.table.col.net':        'नेट',
  'agent.dashboard.pnl.table.col.matches':    'मैच',
  'agent.dashboard.pnl.ledgerRows.one':       '{n} लेजर पंक्ति',
  'agent.dashboard.pnl.ledgerRows.other':     '{n} लेजर पंक्तियाँ',
  'agent.dashboard.pnl.pillar.platformFee':   'प्लेटफ़ॉर्म फ़ीस',
  'agent.dashboard.pnl.pillar.winLoss':       'विन / लॉस',
  'agent.dashboard.pnl.pillar.offerReward':   'ऑफ़र इनाम',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel':      'अवधि: {range} — डैशबोर्ड की अवधि का सख़्त उपसमुच्चय',
  'agent.dashboard.pnlDrill.loading':          'मैच-स्तरीय विवरण लोड हो रहा है…',
  'agent.dashboard.pnlDrill.windowTotal':      'अवधि का कुल:',
  'agent.dashboard.pnlDrill.matchCount.one':   '{n} मैच में',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} मैचों में',
  'agent.dashboard.pnlDrill.noContribution':   'इस सेल में कोई मैच नहीं जुड़ा।',
  'agent.dashboard.pnlDrill.errorFallback':    'विवरण लोड नहीं हो सका',
  'agent.dashboard.pnlDrill.table.col.match':  'मैच',
  'agent.dashboard.pnlDrill.table.col.role':   'भूमिका',
  'agent.dashboard.pnlDrill.table.col.base':   'आधार',
  'agent.dashboard.pnlDrill.table.col.rate':   'दर',
  'agent.dashboard.pnlDrill.table.col.earned': 'कमाया',
  'agent.dashboard.pnlDrill.table.col.settled':'निपटाया',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading':            'लोड हो रहा है…',
  'agent.dashboard.directDownline.empty':              'अभी तक कोई सीधा सब-एजेंट नहीं।',
  'agent.dashboard.directDownline.row.player.one':     '{n} खिलाड़ी',
  'agent.dashboard.directDownline.row.player.other':   '{n} खिलाड़ी',
  'agent.dashboard.directDownline.row.subAgent.one':   '{n} सब-एजेंट',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} सब-एजेंट',
  'agent.dashboard.directDownline.row.walletDeposit':  'वॉलेट {wallet} · जमा {deposit}',
  'agent.dashboard.directDownline.footer':             '{range} अवधि के लिए शुद्ध नेट।',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading':                  'लोड हो रहा है…',
  'agent.dashboard.chain.directAgents.title':       'सीधे एजेंट',
  'agent.dashboard.chain.directAgents.subtitle':    'आपके सीधे सब-एजेंट — आप उनके अनुरोधों पर कार्रवाई कर सकते हैं।',
  'agent.dashboard.chain.indirectAgents.title':     'अप्रत्यक्ष एजेंट',
  'agent.dashboard.chain.indirectAgents.subtitle':  'आपके सब-एजेंट द्वारा प्रबंधित। आपके लिए केवल पढ़ने योग्य।',
  'agent.dashboard.chain.directPlayers.title':      'सीधे खिलाड़ी',
  'agent.dashboard.chain.directPlayers.subtitle': 'आपके खिलाड़ी — आप उनके रिचार्ज और निकासी पर कार्रवाई करते हैं।',
  'agent.dashboard.chain.indirectPlayers.title':    'अप्रत्यक्ष खिलाड़ी',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'आपके सब-एजेंट के खिलाड़ी। आपके लिए केवल पढ़ने योग्य।',
  'agent.dashboard.chain.field.walletTotal':        'कुल वॉलेट',
  'agent.dashboard.chain.field.depositTotal':       'कुल जमा',
  'agent.dashboard.chain.field.avgPerHead':         'प्रति व्यक्ति औसत',
  'agent.dashboard.chain.field.pendingRecharge':    'लंबित रिचार्ज',
  'agent.dashboard.chain.field.pendingWithdrawal':  'लंबित निकासी',
  'agent.dashboard.chain.pendingBadge':             '{n} लंबित',
  'agent.dashboard.chain.openInbox':                'इनबॉक्स खोलें',
  'agent.dashboard.chain.footer':                   'सीधा = वह संख्या जो आप ख़ुद प्रबंधित करते हैं। अप्रत्यक्ष = आपके सब-एजेंट द्वारा प्रबंधित (आप संख्याएँ देखते हैं पर उनके लंबित अनुरोधों पर कार्रवाई नहीं कर सकते — वे उनके पैरंट को जाते हैं)।',

  // ── ActivityPanel ─────────────────────────────────────────────────
  'agent.activity.error.loadFailed':                    'लोड करने में विफल',
  'agent.activity.filter.window':                       'अवधि',
  'agent.activity.filter.kind':                         'प्रकार',
  'agent.activity.window.all':                          'सभी',
  'agent.activity.kind.all':                            'सभी',
  'agent.activity.kind.matches':                        'मैच',
  'agent.activity.kind.recharges':                      'रिचार्ज',
  'agent.activity.kind.rateChanges':                    'रेट परिवर्तन',
  'agent.activity.loading':                             'लोड हो रहा है…',
  'agent.activity.empty.title':                         'शांत',
  'agent.activity.empty.subtitle':                      '{window} में कोई {kindWord}ईवेंट नहीं।',
  'agent.activity.empty.kindWord.all':                  '',
  'agent.activity.empty.kindWord.match':                'मैच ',
  'agent.activity.empty.kindWord.recharge':             'रिचार्ज ',
  'agent.activity.empty.kindWord.partnership_change':   'पार्टनरशिप परिवर्तन ',
  'agent.activity.row.settlementFallback':              'सेटलमेंट',
  'agent.activity.row.earned':                          'कमाया',
  'agent.activity.row.fromPillarOn': '{pillar} से, ',
  'agent.activity.row.pillar.platformFee':              'प्लेटफ़ॉर्म फ़ीस',
  'agent.activity.row.pillar.winLoss':                  'विन/लॉस',
  'agent.activity.row.pillar.offerReward':              'ऑफ़र इनाम',
  'agent.activity.row.recharge.prefix':                 'रिचार्ज',
  'agent.activity.row.recharge.of': '',
  'agent.activity.row.recharge.byUser':                 'यूज़र #{id} द्वारा',
  'agent.activity.row.recharge.via': ' — माध्यम: ',
  'agent.activity.row.state.approved':                  'मंज़ूर',
  'agent.activity.row.state.rejected':                  'अस्वीकृत',
  'agent.activity.row.state.pending':                   'लंबित',
  'agent.activity.row.rateChange.prefix':               'रेट परिवर्तन',
  'agent.activity.row.rateChange.dashOn':               '— {pillar} पर ',
  'agent.activity.row.rateChange.cascade':              'कैस्केड',

  // ── EarningsPanel ─────────────────────────────────────────────────
  'agent.earnings.notAgent':                            'यह व्यू केवल एजेंट के लिए है।',
  'agent.earnings.stat.poolBalance':                    'पूल बैलेंस',
  'agent.earnings.stat.totalIn':                        'कुल आय',
  'agent.earnings.stat.totalOut':                       'कुल व्यय',
  'agent.earnings.section.in':                          'कमाई (आय)',
  'agent.earnings.section.out':                         'खर्च (व्यय)',
  'agent.earnings.empty.in':                            'अभी तक कोई कमाई नहीं। जब आपके खिलाड़ी रिचार्ज करेंगे या खेलेंगे, तो हिस्सा यहाँ दिखेगा।',
  'agent.earnings.empty.out':                           'अभी तक कोई खर्च नहीं।',
  'agent.earnings.viewLedger':                          'पूरा लेजर देखें',
  'agent.earnings.kind.topupFromAdmin.label':           'एडमिन से टॉप-अप',
  'agent.earnings.kind.topupFromAdmin.hint':            'एडमिन ने आपके पूल को क्रेडिट किया',
  'agent.earnings.kind.topupToPlayer.label':            'खिलाड़ी को टॉप-अप',
  'agent.earnings.kind.topupToPlayer.hint':             'आपने एक खिलाड़ी को रिचार्ज किया',
  'agent.earnings.kind.topupFromPlayer.label':          'खिलाड़ी का रिचार्ज',
  'agent.earnings.kind.topupFromPlayer.hint':           'आपके खिलाड़ियों ने आपके पूल में भुगतान किया',
  'agent.earnings.kind.revenueShare.label':             'राजस्व हिस्सा',
  'agent.earnings.kind.revenueShare.hint':              'मैचों से प्लेटफ़ॉर्म फ़ीस का हिस्सा',
  'agent.earnings.kind.coFundOffer.label':              'सह-वित्तपोषित ऑफ़र',
  'agent.earnings.kind.coFundOffer.hint':               'ऑफ़र भुगतान में आपका हिस्सा',
  'agent.earnings.kind.adminAdjust.label':              'एडमिन समायोजन',
  'agent.earnings.kind.adminAdjust.hint':               'मैन्युअल सुधार',
  'agent.earnings.row.entry.one': 'एंट्री',
  'agent.earnings.row.entry.other': 'एंट्रियाँ',

  // ── LeaderboardPanel ──────────────────────────────────────────────
  'agent.leaderboard.filter.window':                'अवधि',
  'agent.leaderboard.filter.pillar':                'पिलर',
  'agent.leaderboard.window.all':                   'सभी',
  'agent.leaderboard.pillar.all':                   'सभी पिलर',
  'agent.leaderboard.pillar.platformFee':           'प्लेटफ़ॉर्म फ़ीस',
  'agent.leaderboard.pillar.winLoss':               'विन / लॉस',
  'agent.leaderboard.pillar.offerReward':           'ऑफ़र इनाम',
  'agent.leaderboard.loading':                      'लोड हो रहा है…',
  'agent.leaderboard.empty.title':                  'अभी तक कोई कमाई नहीं',
  'agent.leaderboard.empty.subtitle.admin':         '{window} में सभी एजेंटों के बीच कोई सेटलमेंट गतिविधि नहीं।',
  'agent.leaderboard.empty.subtitle.downline':      '{window} में आपकी डाउनलाइन से कोई कमाई नहीं। जैसे ही आपके सब-एजेंट मैच चलाएँगे, यह बोर्ड भर जाएगा।',
  'agent.leaderboard.row.matches.one':              '{n} मैच',
  'agent.leaderboard.row.matches.other':            '{n} मैच',
  'agent.leaderboard.footer':                       'कमाई match_settlement_snapshots से ली गई। ऋणात्मक संख्या का मतलब है उस एजेंट ने अवधि भर में जितना लाया उससे ज़्यादा भुगतान किया।',
  'agent.leaderboard.chip.platformFee': 'प्लेटफ़ॉर्म',
  'agent.leaderboard.chip.winLoss':                 'विन/लॉस',
  'agent.leaderboard.chip.offer':                   'ऑफ़र',

  // ── MarginWarningModal (80% threshold) ────────────────────────────
  'agent.warning.header.eyebrow':     'मार्जिन चेतावनी · 80% सीमा पहुँची',
  'agent.warning.header.title':       'एक बुरे सेटलमेंट की दूरी पर लॉकआउट।',
  'agent.warning.header.body':        'अभी आप लॉक नहीं हैं — लेकिन आपका जमा सुरक्षा कवर 80% से ज़्यादा खर्च हो चुका है। अभी टॉप-अप करें और नियंत्रण में बने रहें।',
  'agent.warning.header.dismissAria': 'बंद करें',
  'agent.warning.why.eyebrow':        'यह क्यों दिख रहा है',
  'agent.warning.why.body':           'हाल के गेम सेटलमेंट ने आपके वॉलेट को ऋणात्मक कर दिया। आपकी {deposit} की सुरक्षा जमा वह सुरक्षा-जाल है जो इसे सहती है — और अब वह 80% से ज़्यादा ख़र्च हो चुकी है। थोड़ा और ऋणात्मक आ गया तो आपका वॉलेट + जमा शून्य पर आ जाएगा और प्लेटफ़ॉर्म खाते को लॉक कर देगा।',
  'agent.warning.why.reassurance':    'यह अलर्ट जानबूझकर जल्दी आता है — जबकि आप अभी भी पूरा ऐप इस्तेमाल कर सकते हैं। लॉकआउट स्क्रीन तभी दिखेगी जब आप 100% पार करेंगे, और एक बार ऐसा हुआ तो ज़्यादातर सुविधाएँ बंद हो जाएँगी। अभी कार्रवाई करना बाद में करने से बहुत कम परेशानी देगा।',
  'agent.warning.gauge.label':        'लॉकआउट से पहले बाक़ी जगह',
  'agent.warning.gauge.suffix': 'जगह बची',
  'agent.warning.gauge.dangerBadge': 'ख़तरे के क्षेत्र में {pct}%',
  'agent.warning.gauge.field.wallet': 'आपका वॉलेट',
  'agent.warning.gauge.field.deposit':'आपका जमा',
  'agent.warning.ignore.eyebrow':       'अगर आप इसे नज़रअंदाज़ करते हैं और 100% पर पहुँचते हैं',
  'agent.warning.ignore.cardTitle':     'आप तुरंत खो देंगे',
  'agent.warning.ignore.item.dashboard':     'आपके डैशबोर्ड, लाभ-हानि और लीडरबोर्ड तक पहुँच',
  'agent.warning.ignore.item.pages':         'खिलाड़ी और एजेंट पेज',
  'agent.warning.ignore.item.manualTopup':   'अपने पूल से अपने खिलाड़ियों को मैन्युअल टॉप-अप',
  'agent.warning.ignore.item.newWithdrawal': 'अपने अपलाइन को नई निकासी भेजना',
  'agent.warning.ignore.item.subAgents':     'सब-एजेंट जोड़ना या प्रबंधित करना',
  'agent.warning.ignore.item.profile':       'अपनी प्रोफ़ाइल या भुगतान सेटिंग्स संपादित करना',
  'agent.warning.ignore.footer':             'आप अभी भी लंबित अनुरोध मंज़ूर कर सकेंगे + एक आपातकालीन टॉप-अप भेज सकेंगे — पर बाक़ी सब बंद रहेगा जब तक आपका वॉलेट + जमा शून्य से ऊपर नहीं चढ़ता।',
  'agent.warning.recoverEyebrow':            'पुनःप्राप्ति के लिए अभी अपने अपलाइन को भुगतान करें',
  'agent.warning.dismiss.cta':               'मैं इसे बाद में निपटाऊँगा',
  'agent.warning.dismiss.footnote':          'अगर आप चेतावनी क्षेत्र में रहे तो हम 10 मिनट में फिर याद दिलाएँगे। पुनःप्राप्ति होते ही अपने आप बंद हो जाएगा।',

  // ── EmergencyLockoutGate (100% lockout) ───────────────────────────
  'agent.lockout.header.eyebrow': 'खाता लॉक · राहत पोर्टल',
  'agent.lockout.header.title':   'आप पर अपनी जमा से ज़्यादा बक़ाया है।',
  'agent.lockout.header.body':    'चिंता न करें — यह ठीक हो सकता है। नीचे के दो स्टेप पूरे करें और आप अपने आप अनलॉक हो जाएँगे।',
  'agent.lockout.why.eyebrow':       'यह क्यों हुआ',
  'agent.lockout.why.body':          'गेम सेटलमेंट ने आपके वॉलेट को शून्य से नीचे ले जाया। आपकी सुरक्षा जमा वह सुरक्षा-जाल है जो इसे सहती है — पर आप उसका 100% इस्तेमाल कर चुके हैं। जब तक आपका वॉलेट और जमा वापस शून्य से ऊपर नहीं चढ़ता, आप सामान्य रूप से काम नहीं कर सकते।',
  'agent.lockout.why.reassurance':   'प्लेटफ़ॉर्म आपको इसलिए नहीं लॉक करता कि आपने कुछ ग़लत किया। यह इसलिए लॉक करता है ताकि आप स्थिर होते समय नेटवर्क और कर्ज़ इकट्ठा न कर सके।',
  'agent.lockout.gauge.label':         'सुरक्षित पुनःप्राप्ति लक्ष्य',
  'agent.lockout.gauge.suffix':        'सुरक्षित भुगतान के लिए',
  'agent.lockout.gauge.recoveredBadge': '{pct}% पुनःप्राप्त',
  'agent.lockout.gauge.field.wallet':  'आपका वॉलेट',
  'agent.lockout.gauge.field.deposit': 'आपका जमा',
  'agent.lockout.tier.justUnlock.label':   'सिर्फ़ अनलॉक',
  'agent.lockout.tier.justUnlock.tag':     'जोखिम भरा',
  'agent.lockout.tier.justUnlock.note':    'आपको ₹0 तक लाता है — कोई नया नुक़सान तुरंत फिर से लॉक कर देगा।',
  'agent.lockout.tier.safeMin.label':      'सुरक्षित न्यूनतम',
  'agent.lockout.tier.safeMin.tag':        'सिफ़ारिश',
  'agent.lockout.tier.safeMin.note':       'जमा ({deposit}) + आपके ऋणात्मक वॉलेट का आधा। एक असली कवर छोड़ता है।',
  'agent.lockout.tier.fullRecovery.label': 'पूर्ण पुनःप्राप्ति',
  'agent.lockout.tier.fullRecovery.tag':   'सर्वोत्तम',
  'agent.lockout.tier.fullRecovery.note':  'जमा पूरी तरह पुनःस्थापित और वॉलेट शून्य पर लाया गया।',
  'agent.lockout.waysOut.eyebrow':         'पुनःप्राप्ति के दो तरीक़े — जो भी (या दोनों) करें',
  'agent.lockout.waysOut.inbox.title':     'अपने इनबॉक्स पर कार्रवाई करें',
  'agent.lockout.waysOut.inbox.body':      'खिलाड़ी रिचार्ज मंज़ूर करें (पैसे पहले से आपके पास हैं) और खिलाड़ी निकासियों को भुगतान के रूप में दर्ज करें। हर मंज़ूरी आपके वॉलेट को क्रेडिट करती है।',
  'agent.lockout.waysOut.inbox.ctaOpen':   'इनबॉक्स खोलें',
  'agent.lockout.waysOut.inbox.ctaOpened': 'इनबॉक्स नीचे खुला है',
  'agent.lockout.waysOut.upline.title':    'अपने अपलाइन को भुगतान करें',
  'agent.lockout.waysOut.upline.body':     'अपने पैरंट एजेंट (या एडमिन) को UPI से पैसे भेजें, फिर ट्रांज़ैक्शन ID भेजें। मंज़ूरी मिलते ही आपका वॉलेट तुरंत क्रेडिट हो जाएगा।',
  'agent.lockout.waysOut.upline.ctaOpen':  'फ़ॉर्म खोलें',
  'agent.lockout.waysOut.upline.ctaOpened':'फ़ॉर्म नीचे खुला है',
  'agent.lockout.capabilities.eyebrow':    'आप क्या अभी भी कर सकते हैं (और क्या बंद है)',
  'agent.lockout.capabilities.canHeader':  'आप अभी भी कर सकते हैं',
  'agent.lockout.capabilities.cantHeader': 'आप (अस्थायी रूप से) नहीं कर सकते',
  'agent.lockout.can.approvePlayerRecharge':      'आने वाले खिलाड़ी रिचार्ज मंज़ूर करना',
  'agent.lockout.can.markPlayerWithdrawalPaid':   'खिलाड़ी निकासियों को भुगतान के रूप में दर्ज करना',
  'agent.lockout.can.approveSubAgentRecharge':    'सब-एजेंट रिचार्ज मंज़ूर करना',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'सब-एजेंट निकासियों को भुगतान के रूप में दर्ज करना',
  'agent.lockout.can.submitToUpline':             'अपने अपलाइन को रिचार्ज भेजना',
  'agent.lockout.can.receiveSseAndSignOut':       'SSE अलर्ट प्राप्त करना + साइन आउट करना',
  'agent.lockout.section.step':                   'स्टेप {n} · {title}',
  'agent.lockout.section.inbox.title':            'आपका इनबॉक्स — हर मंज़ूरी आपके वॉलेट को क्रेडिट करती है',
  'agent.lockout.section.inbox.subtitle':         'खिलाड़ी और सब-एजेंट से लंबित भुगतान अनुरोध जो आपकी कार्रवाई का इंतज़ार कर रहे हैं।',
  'agent.lockout.section.inbox.fromPlayers':      'आपके खिलाड़ियों से',
  'agent.lockout.section.inbox.fromSubAgents':    'आपके सब-एजेंट से',
  'agent.lockout.section.upline.title':           'अपने अपलाइन को भुगतान कर वॉलेट टॉप-अप करें',
  'agent.lockout.section.upline.subtitle':        'पैरंट का UPI सामने हो तो जल्दी होता है। मंज़ूरी आम तौर पर कुछ मिनटों में मिल जाती है।',
  'agent.lockout.queue.player.recharge.title':           'रिचार्ज · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle':        'Txn {txn} · {when} को भेजा गया',
  'agent.lockout.queue.player.recharge.subtitleNote':    'Txn {txn} · {when} को भेजा गया · "{note}"',
  'agent.lockout.queue.player.withdrawal.title':         'निकासी · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle':      '{upi} पर भुगतान करें · {when} को भेजा गया',
  'agent.lockout.queue.player.withdrawal.subtitleWa':    '{upi} पर भुगतान करें · {whatsapp} · {when} को भेजा गया',
  'agent.lockout.queue.subAgent.recharge.title':         'सब-एजेंट रिचार्ज · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle':      'Txn {txn} · {when} को भेजा गया',
  'agent.lockout.queue.subAgent.recharge.subtitleNote':  'Txn {txn} · {when} को भेजा गया · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title':       'सब-एजेंट निकासी · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle':    '{upi} पर भुगतान करें · {when} को भेजा गया',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote':'{upi} पर भुगतान करें · {when} को भेजा गया · "{note}"',
  'agent.lockout.queue.action.approve':                  'मंज़ूर करें',
  'agent.lockout.queue.action.reject':                   'अस्वीकार करें',
  'agent.lockout.queue.action.markPaid':                 'भुगतान दर्ज करें',
  'agent.lockout.queue.prompt.markPlayerPaid':           'खिलाड़ी को भुगतान के लिए जो UPI ट्रांज़ैक्शन ID इस्तेमाल की वह दर्ज करें:',
  'agent.lockout.queue.prompt.markSubAgentPaid':         'सब-एजेंट को भुगतान के लिए जो UPI ट्रांज़ैक्शन ID इस्तेमाल की वह दर्ज करें:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal':  'इस निकासी को अस्वीकार करें? रोकी गई राशि खिलाड़ी को वापस की जाएगी।',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal':'इस सब-एजेंट निकासी को अस्वीकार करें?',
  'agent.lockout.queue.toast.rechargeApproved': 'रिचार्ज मंज़ूर — खिलाड़ी के वॉलेट में क्रेडिट किया गया',
  'agent.lockout.queue.toast.rechargeRejected':          'रिचार्ज अस्वीकृत',
  'agent.lockout.queue.toast.subAgentRechargeApproved':  'सब-एजेंट रिचार्ज मंज़ूर',
  'agent.lockout.queue.toast.subAgentRechargeRejected':  'रिचार्ज अस्वीकृत',
  'agent.lockout.queue.toast.withdrawalPaid': 'निकासी का भुगतान दर्ज — आपके वॉलेट में क्रेडिट हुआ',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'सब-एजेंट निकासी का भुगतान दर्ज — वॉलेट में क्रेडिट हुआ',
  'agent.lockout.queue.toast.withdrawalRejected':        'निकासी अस्वीकृत (खिलाड़ी को वापस की गई)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected':'निकासी अस्वीकृत',
  'agent.lockout.queue.toast.actionFailed':              'कार्रवाई विफल',
  'agent.lockout.queue.empty.player':                    'अभी आपके खिलाड़ियों से कुछ नहीं।',
  'agent.lockout.queue.empty.subAgent':                  'अभी आपके सब-एजेंट से कुछ नहीं।',
  'agent.lockout.queue.loading':                         'क़तार लोड हो रही है…',
  'agent.lockout.refresh.cta':                           'ताज़ा करें — क्या मैं अभी अनलॉक हूँ?',
  'agent.lockout.refresh.checking':                      'जाँच रहे हैं…',
  'agent.lockout.refresh.toast':                         'स्थिति ताज़ा हुई',
  'agent.lockout.refresh.footnote':                      'यह पेज अपने आप बंद हो जाएगा जैसे ही आपका वॉलेट + जमा शून्य से ऊपर चढ़ता है।',

  // ── EmergencyRechargeForm ─────────────────────────────────────────
  'agent.emergencyForm.error.loadFailed':            'रिचार्ज विवरण लोड नहीं हो सका',
  'agent.emergencyForm.banner.locked.title':         'खाता अनलॉक करने के लिए अभी भुगतान करें',
  'agent.emergencyForm.banner.locked.body':          'आपके खिलाड़ियों की जीत आपकी जमा-ज़मानत से ज़्यादा हो गई है। आप अपना ऋणात्मक बैलेंस साफ़ किए बिना प्लेटफ़ॉर्म का इस्तेमाल नहीं कर सकते।',
  'agent.emergencyForm.banner.warning.title':        'जल्द टॉप-अप करें — आप सीमा के बहुत पास हैं',
  'agent.emergencyForm.banner.warning.body':         'आपका वॉलेट आपके जमा का 80% पार कर चुका है। 100% तक पहुँचने से पहले टॉप-अप करें — एक बार पहुँच गए तो खाता लॉक हो जाएगा।',
  'agent.emergencyForm.row.wallet':                  'आपका वॉलेट',
  'agent.emergencyForm.row.deposit':                 'आपका जमा',
  'agent.emergencyForm.tier.eyebrow.locked':         'आप क्यों फँसे हैं',
  'agent.emergencyForm.tier.eyebrow.warning':        'आप क्यों जोखिम में हैं',
  'agent.emergencyForm.tier.context.locked':         'आपका वॉलेट {wallet} पर पहुँच गया — यह आपकी {deposit} की जमा सुरक्षा कवर के परे है। प्लेटफ़ॉर्म आपको यहाँ लॉक करता है ताकि आपकी डाउनलाइन के नुक़सान खाली बफ़र पर और न बढ़ें।',
  'agent.emergencyForm.tier.context.warning':        'आपका वॉलेट {wallet} पर पहुँच गया — यह आपकी {deposit} की जमा सुरक्षा कवर के 80% से ज़्यादा है। एक और बुरा सेटलमेंट आपको 100% पार कर खाते को लॉक कर देगा।',
  'agent.emergencyForm.tier.pickHowMuch':            'चुनें कि कितना भुगतान करना है',
  'agent.emergencyForm.tier.bareMin.label.locked':   'सिर्फ़ अनलॉक',
  'agent.emergencyForm.tier.bareMin.label.warning':  'सिर्फ़ चेतावनी से बाहर',
  'agent.emergencyForm.tier.bareMin.tag':            'जोखिम भरा',
  'agent.emergencyForm.tier.bareMin.note.locked':    'केवल आपके वॉलेट + जमा को ₹0 तक लाता है। कोई भी नया नुक़सान आपको तुरंत फिर लॉक कर देगा।',
  'agent.emergencyForm.tier.bareMin.note.warning':   'केवल आपको 80% लाइन से थोड़ा ऊपर लाता है। कोई भी नया नुक़सान फिर चेतावनी देगा — या लॉकआउट में धकेलेगा।',
  'agent.emergencyForm.tier.safeMin.label':          'सुरक्षित न्यूनतम',
  'agent.emergencyForm.tier.safeMin.tag':            'सिफ़ारिश',
  'agent.emergencyForm.tier.safeMin.note':           'आपका जमा ({deposit}) + आपके ऋणात्मक वॉलेट का आधा ({half})। आपको एक असली कवर देता है।',
  'agent.emergencyForm.tier.full.label':             'पूर्ण पुनःप्राप्ति',
  'agent.emergencyForm.tier.full.tag':               'सर्वोत्तम',
  'agent.emergencyForm.tier.full.note':              'जमा + आपका पूरा ऋणात्मक वॉलेट ({neg})। जमा पूरी तरह पुनःस्थापित और वॉलेट शून्य पर।',
  'agent.emergencyForm.parent.label.admin':          'अपने एडमिन को भुगतान करें',
  'agent.emergencyForm.parent.label.parentAgent':    'अपने पैरंट एजेंट को भुगतान करें',
  'agent.emergencyForm.parent.role.admin':           'प्लेटफ़ॉर्म एडमिन',
  'agent.emergencyForm.parent.role.parentAgent':     'पैरंट एजेंट',
  'agent.emergencyForm.parent.upi':                  'UPI:',
  'agent.emergencyForm.parent.whatsapp':             'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile':            'आपके पैरंट का भुगतान विवरण अभी फ़ाइल में नहीं है। कृपया सीधे प्लेटफ़ॉर्म सपोर्ट से संपर्क करें।',
  'agent.emergencyForm.field.amount.label':          'भुगतान की गई राशि (₹)',
  'agent.emergencyForm.field.txn.label':             'UPI ट्रांज़ैक्शन ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'उदाहरण: 412312345678',
  'agent.emergencyForm.field.note.label':            'नोट (वैकल्पिक)',
  'agent.emergencyForm.field.note.placeholder': 'अगर पैरंट को कुछ बताना हो तो लिखें',
  'agent.emergencyForm.validation.amount':           'जो राशि भुगतान की वह दर्ज करें (न्यूनतम ₹10)',
  'agent.emergencyForm.validation.txn':              'UPI ट्रांज़ैक्शन ID / UTR दर्ज करें (कम से कम 6 अक्षर)',
  'agent.emergencyForm.cta':                         'रिचार्ज अनुरोध भेजें',
  'agent.emergencyForm.ctaSubmitting':               'भेज रहे हैं…',
  'agent.emergencyForm.success.title':               'रिचार्ज भेजा गया',
  'agent.emergencyForm.success.bodyAdmin':           'अनुरोध #{id} एडमिन की मंज़ूरी का इंतज़ार कर रहा है। मंज़ूरी मिलते ही आपका वॉलेट क्रेडिट होगा और खाता अपने आप अनलॉक हो जाएगा।',
  'agent.emergencyForm.success.bodyAgent':           'अनुरोध #{id} @{username} की मंज़ूरी का इंतज़ार कर रहा है। मंज़ूरी मिलते ही आपका वॉलेट क्रेडिट होगा और खाता अपने आप अनलॉक हो जाएगा।',
  'agent.emergencyForm.toast.submitted':             'रिचार्ज अनुरोध #{id} भेज दिया गया। मंज़ूरी का इंतज़ार है।',
  'agent.emergencyForm.toast.cantSave':              'भाषा सहेज नहीं सके',

  // ── GeoForcedModal ────────────────────────────────────────────────
  'agent.geo.title': 'एक बार का सेटअप: अपना स्थान पुष्टि करें',
  'agent.geo.subtitle': 'डैशबोर्ड का इस्तेमाल करने से पहले यह ज़रूरी है।',
  'agent.geo.body.primary': 'बाली गेम्स प्लेटफ़ॉर्म नए खिलाड़ियों को उनके सबसे नज़दीकी एजेंट के पास भेजता है। जब तक आप अपना सटीक GPS स्थान साझा नहीं करते, आप लोकेटर में नहीं दिखेंगे और ऑर्गेनिक साइनअप से एक भी नया खिलाड़ी नहीं मिलेगा — केवल वे ही खिलाड़ी मिलेंगे जो सीधे आपका रेफ़रल कोड टाइप करेंगे।',
  'agent.geo.body.secondary': 'एक बार "मेरा वर्तमान स्थान इस्तेमाल करें" पर टैप करें। हम केवल आपके निर्देशांक कैप्चर करते हैं — और कुछ भी नहीं, कभी नहीं।',
  'agent.geo.hint.disabled': 'ऊपर "मेरा वर्तमान स्थान इस्तेमाल करें" पर टैप करें। केवल पिनकोड या मैन्युअल रूप से क्षेत्र चुनना काफ़ी नहीं है — लोकेटर को सटीक GPS निर्देशांक चाहिए।',
  'agent.geo.error.fallback': 'स्थान सहेजा नहीं जा सका। कृपया फिर से कोशिश करें।',
  'agent.geo.cta.save':         'सहेजें और जारी रखें',
  'agent.geo.cta.saving': 'स्थान सहेजा जा रहा है…',
  'agent.geo.toast.success':    'स्थान सहेजा गया — अब आप खिलाड़ी प्राप्त कर सकते हैं।',

  // ════════════════════════════════════════════════════════════════════
  // AGENT APP — Hindi translations (Phase C Batch 1, Chunk 2 of 3).
  // Covers Phase B Groups 4-7: wallet/deposit, people hub, profile, rewards.

  // ── MyWalletPanel ─────────────────────────────────────────────────
  'agent.wallet.hero.label':       'पूल बैलेंस',
  'agent.wallet.hero.helper':      'खिलाड़ी रिचार्ज और सब-एजेंट टॉप-अप के लिए उपलब्ध। कमाई और एडमिन टॉप-अप यहाँ आते हैं।',
  'agent.wallet.action.recharge':  'रिचार्ज अनुरोध',
  'agent.wallet.action.withdraw':  'निकासी अनुरोध',
  'agent.wallet.tab.pending':      'लंबित',
  'agent.wallet.tab.history':      'इतिहास',
  'agent.wallet.pending.loading':  'लोड हो रहा है…',
  'agent.wallet.pending.empty':    'अभी कोई लंबित अनुरोध नहीं।',
  'agent.wallet.pending.recharge.title':   'रिचार्ज अनुरोध · +{amount}',
  'agent.wallet.pending.recharge.meta':    'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'निकासी अनुरोध · −{amount}',
  'agent.wallet.pending.withdrawal.meta':  '{upi} पर · {when}',
  'agent.wallet.pending.badge':            'लंबित',
  'agent.wallet.history.loading':                  'लोड हो रहा है…',
  'agent.wallet.history.empty':                    'अभी तक कोई वॉलेट गतिविधि नहीं।',
  'agent.wallet.history.recharge.titleAccepted':   'रिचार्ज मंज़ूर · {amount}',
  'agent.wallet.history.recharge.titleRejected':   'रिचार्ज अस्वीकृत · {amount}',
  'agent.wallet.history.recharge.meta':            'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid':     'निकासी भुगतान · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'निकासी अस्वीकृत · {amount}',
  'agent.wallet.history.withdrawal.meta':          '{upi} पर · {when}',
  'agent.wallet.history.withdrawal.paidTxn':       'भुगतान · {txn}',
  'agent.wallet.state.accepted': 'मंज़ूर',
  'agent.wallet.state.paid':     'भुगतान हुआ',
  'agent.wallet.state.rejected': 'अस्वीकृत',
  'agent.wallet.state.expired':  'समाप्त',
  'agent.wallet.ledger.kind.topupFromAdmin':   'एडमिन से टॉप-अप',
  'agent.wallet.ledger.kind.topupToPlayer':    'खिलाड़ी को टॉप-अप',
  'agent.wallet.ledger.kind.topupFromPlayer':  'खिलाड़ी का रिचार्ज',
  'agent.wallet.ledger.kind.revenueShare':     'राजस्व हिस्सा',
  'agent.wallet.ledger.kind.coFundOffer':      'सह-वित्तपोषित ऑफ़र',
  'agent.wallet.ledger.kind.adminAdjust':      'एडमिन समायोजन',
  'agent.wallet.ledger.kind.withdrawalHold':   'निकासी होल्ड',
  'agent.wallet.ledger.kind.withdrawalPaid':   'निकासी भुगतान',
  'agent.wallet.ledger.kind.withdrawalRefund': 'निकासी रिफ़ंड',
  'agent.wallet.ledger.balanceAfter':          'बैलेंस {amount}',

  // ── MyDepositPanel ────────────────────────────────────────────────
  'agent.deposit.hero.label':         'सुरक्षा जमा',
  'agent.deposit.hero.helper':        'आपके पूल के लिए ज़मानत। जब तक वॉलेट अच्छी स्थिति में है, यह अछूता रहता है।',
  'agent.deposit.margin.safe':        'सुरक्षित',
  'agent.deposit.margin.warning':     'चेतावनी',
  'agent.deposit.margin.locked':      'लॉक',
  'agent.deposit.action.recharge':    'जमा रिचार्ज',
  'agent.deposit.action.withdraw':    'जमा निकासी',
  'agent.deposit.gauge.label':        'कवरेज मीटर',
  'agent.deposit.gauge.thresholdWarn':'चेतावनी',
  'agent.deposit.gauge.thresholdLock':'लॉक',
  'agent.deposit.gauge.legend.warn':  '{pct} पर आपको चेतावनी मिलेगी',
  'agent.deposit.gauge.legend.lock':  '{pct} पर आपका खाता लॉक हो जाएगा',
  'agent.deposit.gauge.caption.locked':  'पूल पूरी तरह जमा से कवर — टॉप-अप होने तक निकासी बंद।',
  'agent.deposit.gauge.caption.warning': 'पूल ने आपके जमा का {pct}% इस्तेमाल कर लिया है। लॉकआउट से बचने के लिए 100% से पहले टॉप-अप करें।',
  'agent.deposit.gauge.caption.caution': 'पूल जमा कवरेज में {pct}% तक है। आप अभी भी 80% चेतावनी सीमा से सुरक्षित नीचे हैं।',
  'agent.deposit.gauge.caption.safe':    'आपका जमा पूरी तरह सुरक्षित है।',
  'agent.deposit.ledger.loading':         'लोड हो रहा है…',
  'agent.deposit.ledger.empty.title':     'अभी तक कोई जमा गतिविधि नहीं',
  'agent.deposit.ledger.empty.body':      'एडमिन के आपके जमा को क्रेडिट (या रिफ़ंड प्रोसेस) करते ही हर एंट्री यहाँ दिखेगी।',

  // ── RequestRechargeForm ───────────────────────────────────────────
  'agent.recharge.banner.eyebrow.wallet':  'अपना वॉलेट रिचार्ज करें',
  'agent.recharge.banner.eyebrow.deposit': 'अपनी सुरक्षा जमा रिचार्ज करें',
  'agent.recharge.banner.body.wallet': 'पहले अपने अपलाइन को भुगतान करें, फिर प्रमाण यहाँ भेजें ताकि वे आपका वॉलेट टॉप-अप कर सकें।',
  'agent.recharge.banner.body.deposit': 'पहले अपने अपलाइन को भुगतान करें, फिर प्रमाण यहाँ भेजें ताकि वे आपकी सुरक्षा जमा टॉप-अप कर सकें।',
  'agent.recharge.banner.closeAria':       'फ़ॉर्म बंद करें',
  'agent.recharge.parent.label.admin':       'अपने एडमिन को भुगतान करें',
  'agent.recharge.parent.label.parentAgent': 'अपने पैरंट एजेंट को भुगतान करें',
  'agent.recharge.parent.role.admin':        'प्लेटफ़ॉर्म एडमिन',
  'agent.recharge.parent.role.parentAgent':  'पैरंट एजेंट',
  'agent.recharge.parent.noQr':              'कोई QR सेट नहीं',
  'agent.recharge.parent.upi':               'UPI:',
  'agent.recharge.parent.loading':           'आपके अपलाइन के भुगतान विवरण लोड हो रहे हैं…',
  'agent.recharge.parent.loadError':         'आपके अपलाइन के भुगतान विवरण लोड नहीं हो सके',
  'agent.recharge.method.eyebrow':       'आपने कैसे भुगतान किया?',
  'agent.recharge.method.upi.label':     'UPI / QR',
  'agent.recharge.method.upi.note':      'ऑनलाइन डिजिटल भुगतान',
  'agent.recharge.method.cash.label':    'नक़द',
  'agent.recharge.method.cash.note':     'व्यक्तिगत रूप से हस्तांतरण',
  'agent.recharge.field.amount.label':       'राशि (₹) · न्यूनतम ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi':      'UPI ट्रांज़ैक्शन ID / UTR',
  'agent.recharge.field.txn.label.cash':     'नक़द रसीद मार्कर (कोई संक्षिप्त संदर्भ)',
  'agent.recharge.field.txn.placeholder.upi':  'जैसे 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'जैसे CASH-Sun-eve',
  'agent.recharge.field.note.label':         'नोट (वैकल्पिक, आपके अपलाइन को दिखेगा)',
  'agent.recharge.field.note.placeholder':   'अपलाइन के लिए कोई संदर्भ',
  'agent.recharge.error.amountMin': 'राशि पूर्णांक रुपये में होनी चाहिए और कम से कम ₹10',
  'agent.recharge.error.txnUpi':     'ट्रांज़ैक्शन ID बहुत छोटी लगती है — UPI संदर्भ पेस्ट करें',
  'agent.recharge.error.txnCash': 'एक छोटा मार्कर जोड़ें (जैसे "CASH-Sun-eve") ताकि आपका अपलाइन हस्तांतरण मिला सके',
  'agent.recharge.cta.submit': 'अनुरोध भेजें',
  'agent.recharge.cta.cancel': 'रद्द करें',

  // ── RequestWithdrawalForm ─────────────────────────────────────────
  'agent.withdrawal.banner.eyebrow.wallet':  'वॉलेट से निकालें',
  'agent.withdrawal.banner.eyebrow.deposit': 'जमा से निकालें',
  'agent.withdrawal.banner.body.wallet':     'राशि तुरंत रोक ली जाती है ताकि कहीं और ख़र्च न हो। आपका अपलाइन भुगतान करता है और भुगतान दर्ज करता है।',
  'agent.withdrawal.banner.body.deposit':    'राशि आपकी सुरक्षा जमा से तुरंत रोक ली जाती है। आपका अपलाइन भुगतान करता है और भुगतान दर्ज करता है।',
  'agent.withdrawal.banner.closeAria':       'फ़ॉर्म बंद करें',
  'agent.withdrawal.method.eyebrow':    'आप कैसे भुगतान पाना चाहते हैं?',
  'agent.withdrawal.method.upi.label':  'UPI / QR',
  'agent.withdrawal.method.upi.note':   'ऑनलाइन डिजिटल भुगतान',
  'agent.withdrawal.method.cash.label': 'नक़द',
  'agent.withdrawal.method.cash.note':  'व्यक्तिगत रूप से हस्तांतरण',
  'agent.withdrawal.field.amount.label':           'राशि (₹) · उपलब्ध {balance}',
  'agent.withdrawal.field.amount.placeholder':     '500',
  'agent.withdrawal.field.upi.label':              'आपकी UPI ID (जहाँ अपलाइन भुगतान करेगा)',
  'agent.withdrawal.field.upi.placeholder':        'you@bank',
  'agent.withdrawal.field.cashMarker.label':       'मिलने का मार्कर (जैसे "रविवार शाम नक़द")',
  'agent.withdrawal.field.cashMarker.placeholder': 'नक़द कब/कहाँ देना है',
  'agent.withdrawal.field.whatsapp.label':         'WhatsApp (वैकल्पिक, ताकि अपलाइन संपर्क कर सके)',
  'agent.withdrawal.field.whatsapp.placeholder':   '+919876543210',
  'agent.withdrawal.field.note.label':             'नोट (वैकल्पिक)',
  'agent.withdrawal.field.note.placeholder':       'अपलाइन के लिए कोई संदर्भ',
  'agent.withdrawal.error.amountMin': 'राशि पूर्णांक रुपये में होनी चाहिए और कम से कम ₹10',
  'agent.withdrawal.error.amountOverflow':  'आपके पास केवल {balance} उपलब्ध है',
  'agent.withdrawal.error.upi':             'मान्य UPI ID दर्ज करें',
  'agent.withdrawal.cta.submit': 'अनुरोध भेजें',
  'agent.withdrawal.cta.cancel': 'रद्द करें',

  // ── People Hub ────────────────────────────────────────────────────
  'agent.people.hub.players.title':           'खिलाड़ी',
  'agent.people.hub.players.subtitle':        'खिलाड़ी और उनके अनुरोध प्रबंधित करें',
  'agent.people.hub.players.subtab.requests': 'भुगतान',
  'agent.people.hub.players.subtab.list':     'मेरे खिलाड़ी',
  'agent.people.hub.agents.title':            'एजेंट',
  'agent.people.hub.agents.subtitle':         'सब-एजेंट और उनके अनुरोध प्रबंधित करें',
  'agent.people.hub.agents.subtab.requests':  'एजेंट अनुरोध',
  'agent.people.hub.agents.subtab.list':      'एजेंट',
  'agent.people.acceptingToggle.label': 'नए खिलाड़ी प्राप्त कर रहे हैं',
  'agent.people.acceptingToggle.bodyAccepting': 'आप अभी नए खिलाड़ी «स्वीकार कर रहे हैं» — भौगोलिक लोकेटर से। आपके क्षेत्र के कोल्ड साइनअप अपने आप आप तक पहुँचते हैं।',
  'agent.people.acceptingToggle.bodyOptedOut': 'आपने «ऑप्ट-आउट» कर दिया है। लोकेटर अब आपको छोड़कर आगे बढ़ रहा है — केवल सीधे रेफ़रल और मैन्युअल रूप से असाइन किए गए खिलाड़ी ही आप तक पहुँचेंगे। मौजूदा खिलाड़ियों पर कोई असर नहीं होगा।',
  'agent.people.acceptingToggle.error': 'अपडेट नहीं हो सका',
  'agent.people.acceptingToggle.aria.stop':       'नए खिलाड़ी लेना बंद करें',
  'agent.people.acceptingToggle.aria.start':      'नए खिलाड़ी लेना शुरू करें',

  // ── QRCodeModule ──────────────────────────────────────────────────
  'agent.qr.audience.player':     'खिलाड़ी',
  'agent.qr.audience.subAgent':   'एजेंट',
  'agent.qr.alt':                 'आपका भुगतान QR ({audience})',
  'agent.qr.cardLabel.player': 'मेरा QR · जो खिलाड़ियों को दिखता है',
  'agent.qr.cardLabel.subAgent': 'मेरा QR · जो एजेंटों को दिखता है',
  'agent.qr.upi.empty': 'अभी कोई UPI सेट नहीं है',
  'agent.qr.status.loading':      'लोड हो रहा है…',
  'agent.qr.status.live':         'लाइव',
  'agent.qr.status.notSet': 'सेट नहीं है',
  'agent.qr.status.paused':       'रुका हुआ',
  'agent.qr.cta.edit':            'QR और UPI संपादित करें',
  'agent.qr.cta.setup':           'QR सेट अप करें',
  'agent.qr.cta.viewOnly': 'केवल देखने योग्य — संपादन के लिए एडमिन से कहें',
  'agent.qr.modal.title':         'QR और UPI संपादित करें',
  'agent.qr.modal.closeAria':     'बंद करें',
  'agent.qr.modal.upload':        'QR अपलोड करें',
  'agent.qr.modal.replace':       'QR बदलें',
  'agent.qr.modal.remove':        'हटाएँ',
  'agent.qr.modal.confirmRemove.player': 'QR छवि हटाएँ? तब आपके खिलाड़ियों को केवल संपर्क विवरण (फ़ॉलबैक) दिखेगा।',
  'agent.qr.modal.confirmRemove.subAgent': 'QR छवि हटाएँ? तब आपके एजेंटों को केवल संपर्क विवरण (फ़ॉलबैक) दिखेगा।',
  'agent.qr.modal.field.upi.label':                       'UPI ID',
  'agent.qr.modal.field.upi.placeholder':                 'जैसे baaligames@upi',
  'agent.qr.modal.field.whatsapp.label.player':           'WhatsApp नंबर (फ़ॉलबैक)',
  'agent.qr.modal.field.whatsapp.label.subAgent':         'एजेंट के लिए WhatsApp नंबर',
  'agent.qr.modal.field.whatsapp.placeholder.default':    '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'खाली → तो {number} का उपयोग होगा',
  'agent.qr.modal.field.whatsapp.fallbackHint':           'सब-एजेंट {number} देखेंगे (आपका खिलाड़ी-मुखी नंबर)।',
  'agent.qr.modal.field.phone.label.player':              'सहायता फ़ोन (फ़ॉलबैक)',
  'agent.qr.modal.field.phone.label.subAgent':            'एजेंट के लिए सहायता फ़ोन',
  'agent.qr.modal.field.phone.placeholder.default':       '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'खाली → तो {number} का उपयोग होगा',
  'agent.qr.modal.field.phone.fallbackHint':              'सब-एजेंट {number} देखेंगे (आपका खिलाड़ी-मुखी नंबर)।',
  'agent.qr.modal.active.label':       'सक्रिय',
  'agent.qr.modal.active.description': 'खिलाड़ी रिचार्ज भेज सकते हैं',
  'agent.qr.modal.active.on':          'चालू',
  'agent.qr.modal.active.paused':      'रुका हुआ',
  'agent.qr.modal.cancel':             'रद्द करें',
  'agent.qr.modal.save':               'सहेजें',

  // ── ManualRechargeForm ────────────────────────────────────────────
  'agent.manualRecharge.banner.eyebrow':                  'मैन्युअल रिचार्ज',
  'agent.manualRecharge.banner.body.before':              'सीधे अपने पूल से ',
  'agent.manualRecharge.banner.body.target':              'खिलाड़ी',
  'agent.manualRecharge.banner.body.after':               ' को पैसे भेजें।',
  'agent.manualRecharge.pool.label':                      'आपका पूल',
  'agent.manualRecharge.pool.dailyCap':                   'सीमा · {amount}/दिन',
  'agent.manualRecharge.field.player.label':              'किस खिलाड़ी को',
  'agent.manualRecharge.field.player.placeholder':        'एक खिलाड़ी चुनें…',
  'agent.manualRecharge.field.player.option':             '@{username} · {contact} · बैल {balance}',
  'agent.manualRecharge.field.player.lockedLabel':        'किस खिलाड़ी को',
  'agent.manualRecharge.field.player.lockedMeta':         '{contact} · वर्तमान बैल {balance}',
  'agent.manualRecharge.field.player.notFound':           'आपकी सूची में खिलाड़ी नहीं मिला।',
  'agent.manualRecharge.field.amount.label':              'राशि (₹) — {min} से {max}',
  'agent.manualRecharge.field.amount.placeholder':        '0',
  'agent.manualRecharge.error.exceedsPool':               'आपके पूल बैलेंस से ज़्यादा है।',
  'agent.manualRecharge.error.belowMin':                  'न्यूनतम से कम ({min})।',
  'agent.manualRecharge.error.aboveMax':                  'अधिकतम से ज़्यादा ({max})।',
  'agent.manualRecharge.field.note.label':                'नोट (वैकल्पिक, खिलाड़ी लेजर पर दिखेगा)',
  'agent.manualRecharge.field.note.placeholder':          'जैसे दुकान पर नक़द लिया',
  'agent.manualRecharge.loading':                         'आपका पूल लोड हो रहा है…',
  'agent.manualRecharge.cta.cancel':                      'रद्द करें',
  'agent.manualRecharge.cta.send':                        '{amount} भेजें',
  'agent.manualRecharge.success.title':                   '@{username} को {amount} भेजा',
  'agent.manualRecharge.success.meta':                    'आपका पूल · {pool} · उनका वॉलेट · {playerBal}',
  'agent.manualRecharge.success.again':                   'और भेजें',

  // ── ManualSubAgentRechargeForm ────────────────────────────────────
  'agent.manualSubAgentRecharge.banner.eyebrow':              'मैन्युअल रिचार्ज',
  'agent.manualSubAgentRecharge.banner.body.before':          'सीधे अपने पूल से ',
  'agent.manualSubAgentRecharge.banner.body.target':          'सब-एजेंट',
  'agent.manualSubAgentRecharge.banner.body.after':           ' को पैसे भेजें।',
  'agent.manualSubAgentRecharge.pool.label':                  'आपका पूल',
  'agent.manualSubAgentRecharge.field.subAgent.label':        'किस सब-एजेंट को',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder':  'एक सब-एजेंट चुनें…',
  'agent.manualSubAgentRecharge.field.subAgent.option':       '@{username} · {email} · पूल {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption':  'कोई सक्रिय सीधा सब-एजेंट नहीं',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel':  'किस सब-एजेंट को',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta':   '{email} · वर्तमान पूल {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound':     'आपकी सीधी डाउनलाइन में सब-एजेंट नहीं मिला।',
  'agent.manualSubAgentRecharge.field.amount.label':          'राशि (₹) — {min} से {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder':    '0',
  'agent.manualSubAgentRecharge.error.exceedsPool':           'आपके पूल बैलेंस से ज़्यादा है।',
  'agent.manualSubAgentRecharge.error.belowMin':              'न्यूनतम से कम ({min})।',
  'agent.manualSubAgentRecharge.field.note.label':            'नोट (वैकल्पिक, दोनों लेजर पर दिखेगा)',
  'agent.manualSubAgentRecharge.field.note.placeholder':      'जैसे फ़्लोट एडवांस',
  'agent.manualSubAgentRecharge.loading':                     'आपका पूल लोड हो रहा है…',
  'agent.manualSubAgentRecharge.cta.cancel':                  'रद्द करें',
  'agent.manualSubAgentRecharge.cta.send':                    '{amount} भेजें',
  'agent.manualSubAgentRecharge.success.title':               '@{username} को {amount} भेजा',
  'agent.manualSubAgentRecharge.success.meta':                'आपका पूल · {parent} · उनका पूल · {child}',
  'agent.manualSubAgentRecharge.success.again':               'और भेजें',

  // ── PlayerRequestsPanel ───────────────────────────────────────────
  'agent.playerRequests.direction.recharge':         'रिचार्ज',
  'agent.playerRequests.direction.withdrawal':       'निकासी',
  'agent.playerRequests.tab.pending':                'लंबित',
  'agent.playerRequests.tab.history':                'इतिहास',
  'agent.playerRequests.loading':                    'लोड हो रहा है…',
  'agent.playerRequests.empty.pending.recharge':     'अभी कोई लंबित रिचार्ज नहीं।',
  'agent.playerRequests.empty.pending.withdrawal':   'अभी कोई लंबित निकासी नहीं।',
  'agent.playerRequests.empty.history':              'अभी तक कोई इतिहास नहीं।',
  'agent.playerRequests.prompt.rejectRecharge':      'अस्वीकार का कारण (वैकल्पिक):',
  'agent.playerRequests.prompt.rejectWithdrawal':    'अस्वीकार का कारण (खिलाड़ी की रोकी गई राशि वापस की जाएगी):',
  'agent.playerRequests.toast.rejected':             'अस्वीकृत',
  'agent.playerRequests.toast.rejectedRefunded':     'अस्वीकृत · रोकी गई राशि वापस की गई',
  'agent.playerRequests.toast.approved':             '{amount} मंज़ूर',
  'agent.playerRequests.toast.approvedWithBonus':    '{amount} मंज़ूर · मूल + ₹{bonus} बोनस',
  'agent.playerRequests.toast.markedPaid':           '{amount} का भुगतान दर्ज',
  'agent.playerRequests.card.upiTxnLabel':           'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel':         'भुगतान करें (UPI)',
  'agent.playerRequests.card.whatsapp':              'WhatsApp',
  'agent.playerRequests.card.viewQr':                'QR देखें',
  'agent.playerRequests.card.bonusChip':             'बोनस',
  'agent.playerRequests.card.partialApproved':       'अनुरोधित {requested} में से {approved} मंज़ूर',
  'agent.playerRequests.card.partialPaid':           'अनुरोधित {requested} में से {approved} भुगतान · वापस {refund}',
  'agent.playerRequests.card.paidTxnPrefix':         'भुगतान · {txn}',
  'agent.playerRequests.action.reject':              'अस्वीकार करें',
  'agent.playerRequests.action.approve':             'मंज़ूर करें',
  'agent.playerRequests.action.markPaid':            'भुगतान दर्ज',
  'agent.playerRequests.state.pending':              'लंबित',
  'agent.playerRequests.state.rejected':             'अस्वीकृत',
  'agent.playerRequests.state.expired':              'समाप्त',
  'agent.playerRequests.state.accepted':             'स्वीकृत',
  'agent.playerRequests.state.manuallyPaid':         'मैन्युअल भुगतान',
  'agent.peopleQueue.pagination': '{total} में से {from}–{to}',

  // ── AgentRequestsPanel ────────────────────────────────────────────
  'agent.agentRequests.direction.recharge':       'रिचार्ज',
  'agent.agentRequests.direction.withdrawal':     'निकासी',
  'agent.agentRequests.tab.pending':              'लंबित',
  'agent.agentRequests.tab.history':              'इतिहास',
  'agent.agentRequests.loading':                  'लोड हो रहा है…',
  'agent.agentRequests.empty.pending.recharge':   'अभी कोई लंबित रिचार्ज नहीं।',
  'agent.agentRequests.empty.pending.withdrawal': 'अभी कोई लंबित निकासी नहीं।',
  'agent.agentRequests.empty.history':            'अभी तक कोई इतिहास नहीं।',
  'agent.agentRequests.prompt.rejectRecharge':    'अस्वीकार का कारण (वैकल्पिक):',
  'agent.agentRequests.prompt.rejectWithdrawal':  'अस्वीकार का कारण (सब-एजेंट की रोकी गई राशि वापस की जाएगी):',
  'agent.agentRequests.toast.rejected':           'अस्वीकृत',
  'agent.agentRequests.toast.rejectedRefunded':   'अस्वीकृत · रोकी गई राशि वापस की गई',
  'agent.agentRequests.toast.approved':           '{amount} मंज़ूर · पूल को क्रेडिट किया',
  'agent.agentRequests.toast.markedPaid':         '{amount} का भुगतान दर्ज',
  'agent.agentRequests.card.agentPill':           'एजेंट',
  'agent.agentRequests.card.upiTxnLabel':         'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel':     'नक़द मार्कर',
  'agent.agentRequests.card.payToUpiLabel':       'भुगतान करें (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel':  'हस्तांतरण मार्कर',
  'agent.agentRequests.card.whatsapp':            'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved':     'अनुरोधित {requested} में से {approved} मंज़ूर',
  'agent.agentRequests.card.partialPaid':         'अनुरोधित {requested} में से {approved} भुगतान · वापस {refund}',
  'agent.agentRequests.card.paidTxnPrefix':       'भुगतान · {txn}',
  'agent.agentRequests.card.chip.deposit':        'जमा',
  'agent.agentRequests.card.chip.cash':           'नक़द',
  'agent.agentRequests.card.chip.upi':            'UPI',
  'agent.agentRequests.action.reject':            'अस्वीकार करें',
  'agent.agentRequests.action.approve':           'मंज़ूर करें',
  'agent.agentRequests.action.markPaid':          'भुगतान दर्ज',
  'agent.agentRequests.state.pending':            'लंबित',
  'agent.agentRequests.state.rejected':           'अस्वीकृत',
  'agent.agentRequests.state.expired':            'समाप्त',
  'agent.agentRequests.state.accepted':           'स्वीकृत',
  'agent.agentRequests.state.manuallyPaid':       'मैन्युअल भुगतान',

  // ── PlayersListPanel ──────────────────────────────────────────────
  'agent.playersList.add':                       'खिलाड़ी जोड़ें',
  'agent.playersList.loading':                   'लोड हो रहा है…',
  'agent.playersList.empty':                     'कोई खिलाड़ी नहीं मिला',
  'agent.playersList.results':                   '{total} में से {from}–{to} दिख रहे',
  'agent.playersList.searchPlaceholder':         'यूज़रनेम, ईमेल, फ़ोन खोजें…',
  'agent.playersList.filter.all':                'सभी',
  'agent.playersList.filter.active':             'सक्रिय',
  'agent.playersList.filter.pending':            'लंबित',
  'agent.playersList.filter.suspended':          'निलंबित',
  'agent.playersList.filter.banned':             'प्रतिबंधित',
  'agent.playersList.empty.title':               'कोई खिलाड़ी नहीं मिला',
  'agent.playersList.empty.body':                'दूसरा खोज या स्थिति फ़िल्टर आज़माएँ।',
  'agent.playersList.pagination':                'पेज {page} / {totalPages}',
  'agent.playersList.card.status.active':        'सक्रिय',
  'agent.playersList.card.status.pending':       'लंबित',
  'agent.playersList.card.status.suspended':     'निलंबित',
  'agent.playersList.card.status.banned':        'प्रतिबंधित',
  'agent.playersList.card.verifiedBadge':        'सत्यापित',
  'agent.playersList.column.wallet':             'वॉलेट',
  'agent.playersList.column.joined':             'शामिल हुए',

  // ── SubagentsListPanel ────────────────────────────────────────────
  'agent.subAgentsList.intro':                                'आप जो एजेंट बनाते हैं वे आपके नीचे काम करते हैं। वे हर पिलर पर 0% से शुरू होते हैं — उनके विवरण पेज से प्रति-गेम दरें दें।',
  'agent.subAgentsList.cta.new':                              'नया सब-एजेंट',
  'agent.subAgentsList.loading':                              'लोड हो रहा है…',
  'agent.subAgentsList.searchPlaceholder':                    'नाम, @यूज़रनेम, फ़ोन खोजें…',
  'agent.subAgentsList.noMatch':                              'कोई मेल नहीं। दूसरा नाम या नंबर आज़माएँ।',
  'agent.subAgentsList.empty.title':                          'अभी तक कोई सब-एजेंट नहीं',
  'agent.subAgentsList.empty.body.canCreate':                 '"+ नया सब-एजेंट" पर टैप कर एक बनाएँ। वे यहाँ अपने पूल, खिलाड़ी और कमाई के साथ दिखेंगे — आपके द्वारा प्रबंधित।',
  'agent.subAgentsList.empty.body.cantCreate':                'आपके एडमिन ने अभी आपको सब-एजेंट बनाने का अधिकार नहीं दिया। उनसे कहें कि वे आपकी अनुमतियों में create_subagent को "edit" पर सेट करें।',
  'agent.subAgentsList.card.suspendedBadge':                  'निलंबित',
  'agent.subAgentsList.column.agents':                        'एजेंट',
  'agent.subAgentsList.column.players':                       'खिलाड़ी',
  'agent.subAgentsList.column.pool':                          'पूल',
  'agent.subAgentsList.row.directLabel':                      'सीधे',
  'agent.subAgentsList.row.indirectLabel':                    'अप्रत्यक्ष',
  'agent.subAgentsList.modal.title':                          'नया सब-एजेंट',
  'agent.subAgentsList.modal.body':                           'वे हर पिलर पर 0% से शुरू होंगे। बनाने के बाद उनके विवरण पेज से प्रति-गेम दरें दें।',
  'agent.subAgentsList.modal.field.username.label':           'यूज़रनेम',
  'agent.subAgentsList.modal.field.username.placeholder':     'अक्षर-अंक, ._- अनुमत; न्यूनतम 3 अक्षर',
  'agent.subAgentsList.modal.field.email.label':              'ईमेल',
  'agent.subAgentsList.modal.field.email.placeholder':        'they@example.com',
  'agent.subAgentsList.modal.field.password.label':           'प्रारंभिक पासवर्ड',
  'agent.subAgentsList.modal.field.password.placeholder':     'न्यूनतम 8 अक्षर',
  'agent.subAgentsList.modal.field.password.hint':            'इसे नए एजेंट से साझा करें ताकि वे लॉगिन कर सकें। उन्हें पहले लॉगिन पर इसे बदलना चाहिए।',
  'agent.subAgentsList.modal.field.location.label':           'सब-एजेंट का स्थान',
  'agent.subAgentsList.modal.field.location.optional':        '(अत्यधिक अनुशंसित)',
  'agent.subAgentsList.modal.field.location.missing':         'स्थान के बिना भौगोलिक लोकेटर इस सब-एजेंट को कोई नया खिलाड़ी नहीं भेज पाएगा। उन्हें केवल सीधे रेफ़रल से ही खिलाड़ी मिलेंगे जब तक वे साइन इन कर अपना GPS पूरा नहीं करते।',
  'agent.subAgentsList.modal.cta.cancel':                     'रद्द करें',
  'agent.subAgentsList.modal.cta.create':                     'सब-एजेंट बनाएँ',
  'agent.subAgentsList.modal.cta.creating':                   'बना रहे हैं…',

  // ── ApprovalEditModal ─────────────────────────────────────────────
  'agent.approvalEdit.title.recharge':                  '{who} से रिचार्ज मंज़ूर करें',
  'agent.approvalEdit.title.withdrawal':                '{who} की निकासी का भुगतान दर्ज करें',
  'agent.approvalEdit.requestedLabel':                  'अनुरोधित:',
  'agent.approvalEdit.field.amount.label':              'मंज़ूर राशि (₹)',
  'agent.approvalEdit.field.amount.hint':               'अनुरोधित {amount} से अधिक नहीं हो सकता।',
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} अपने आप {who} के वॉलेट में वापस कर दी जाएगी।',
  'agent.approvalEdit.partial.recharge.bonusWarn':      'आंशिक मंज़ूरी। कोई संलग्न बोनस अपने आप अस्वीकार हो जाएगा।',
  'agent.approvalEdit.field.reason.label':              'कारण',
  'agent.approvalEdit.field.reason.requiredHint':       '(आवश्यक, ≥5 अक्षर)',
  'agent.approvalEdit.field.reason.optionalHint':       '(वैकल्पिक)',
  'agent.approvalEdit.field.reason.placeholder.partial':'जैसे खिलाड़ी ने केवल 30 रुपये भुगतान किया',
  'agent.approvalEdit.field.reason.placeholder.optional':'वैकल्पिक नोट',
  'agent.approvalEdit.field.txn.label':                 'UPI ट्रांज़ैक्शन ID',
  'agent.approvalEdit.field.txn.placeholder':           'जैसे 412312345678',
  'agent.approvalEdit.error.insufficient':              'आपका पूल ({balance}) इस मंज़ूरी को कवर नहीं कर सकता। पहले टॉप-अप करें।',
  'agent.approvalEdit.error.amountMin':                 'मंज़ूर राशि कम से कम ₹1 होनी चाहिए',
  'agent.approvalEdit.error.amountMax':                 'मंज़ूर राशि अनुरोधित {amount} से अधिक नहीं हो सकती',
  'agent.approvalEdit.error.reasonRequired':            'अलग राशि मंज़ूर करते समय कारण (≥5 अक्षर) आवश्यक है',
  'agent.approvalEdit.error.txnRequired':               'UPI ट्रांज़ैक्शन ID आवश्यक है (≥6 अक्षर)',
  'agent.approvalEdit.error.fallback':                  'विफल — फिर कोशिश करें',
  'agent.approvalEdit.cta.cancel':                      'रद्द करें',
  'agent.approvalEdit.cta.approve':                     'मंज़ूर करें',
  'agent.approvalEdit.cta.markPaid': 'भुगतान दर्ज करें',
  'agent.approvalEdit.cta.submitting': 'भेजा जा रहा है…',

  // ── ProfileHub + Sign-out modal ───────────────────────────────────
  'agent.profile.hub.title':                'पैसे',
  'agent.profile.hub.subtitle':             'वॉलेट, जमा, रेट और खाता एक जगह',
  'agent.profile.hub.tab.wallet':           'वॉलेट',
  'agent.profile.hub.tab.deposit':          'जमा',
  'agent.profile.hub.tab.account':          'खाता',
  'agent.profile.signOut.cta':              'साइन आउट',
  'agent.profile.signOut.modal.title': 'साइन आउट करें?',
  'agent.profile.signOut.modal.body':       'खाते तक पहुँचने के लिए आपको फिर से साइन इन करना होगा।',
  'agent.profile.signOut.modal.stay':       'साइन इन रहें',
  'agent.profile.signOut.modal.confirm': 'हाँ, साइन आउट करें',
  'agent.profile.signOut.modal.busy':       'साइन आउट हो रहा है…',
  'agent.profile.signOut.error': 'साइन आउट नहीं हो सका',

  // ── AccountPanel ──────────────────────────────────────────────────
  'agent.account.notSignedIn':              'साइन इन नहीं है।',
  'agent.account.hero.role.agent':          'एजेंट',
  'agent.account.hero.role.admin':          'प्लेटफ़ॉर्म एडमिन',
  'agent.account.hero.changePhoto':         'फ़ोटो बदलें',
  'agent.account.hero.removePhoto':         'फ़ोटो हटाएँ',
  'agent.account.avatar.cropTitle':         'अपनी फ़ोटो समायोजित करें',
  'agent.account.avatar.confirmRemove':     'अपनी प्रोफ़ाइल फ़ोटो हटाएँ?',
  'agent.account.toast.photoUpdated':       'फ़ोटो अपडेट हुई',
  'agent.account.toast.photoRemoved':       'फ़ोटो हटाई गई',
  'agent.account.toast.saved':              'सहेजा गया',
  'agent.account.toast.locationSaved':      'स्थान सहेजा गया',
  'agent.account.toast.passwordChanged': 'पासवर्ड बदल दिया गया — फिर से साइन इन करें',
  'agent.account.error.uploadFailed':       'अपलोड विफल',
  'agent.account.error.removeFailed':       'विफल',
  'agent.account.error.saveFailed':         'सहेजना विफल',
  'agent.account.error.changeFailed':       'बदलना विफल',
  'agent.account.info.title':               'खाता जानकारी',
  'agent.account.info.subtitle': 'केवल पढ़ने योग्य प्लेटफ़ॉर्म जानकारी',
  'agent.account.info.memberSince': 'सदस्य बने',
  'agent.account.info.lastLogin':           'अंतिम लॉगिन',
  'agent.account.info.lastLoginIp':         'अंतिम लॉगिन IP',
  'agent.account.info.reportsTo': 'रिपोर्ट करते हैं',
  'agent.account.info.reportsToValue':      'एडमिन #{id}',
  'agent.account.info.agentId':             'एजेंट ID',
  'agent.account.info.firstLogin':          'पहली बार',
  'agent.account.info.emptyDash':           '—',
  'agent.account.personal.title':                    'व्यक्तिगत विवरण',
  'agent.account.personal.subtitle.locked': 'एडमिन द्वारा पहचान लॉक · केवल देखने योग्य',
  'agent.account.personal.subtitle.agent':           'यूज़रनेम, ईमेल और संपर्क नंबर',
  'agent.account.personal.subtitle.admin':           'यूज़रनेम और ईमेल',
  'agent.account.personal.field.username':           'यूज़रनेम',
  'agent.account.personal.field.email':              'ईमेल',
  'agent.account.personal.field.phone':              'संपर्क नंबर',
  'agent.account.personal.field.phone.placeholder':  'जैसे +91 9876543210',
  'agent.account.personal.field.phone.hint': 'खिलाड़ी और एडमिन इस नंबर पर आपसे संपर्क करते हैं। मिटाने के लिए ख़ाली छोड़ें।',
  'agent.account.personal.fieldLockedNote':          'लॉक',
  'agent.account.personal.cta.save':                 'परिवर्तन सहेजें',
  'agent.account.personal.cta.saving':               'सहेज रहे हैं…',
  'agent.account.personal.cta.noChanges':            'कोई परिवर्तन नहीं',
  'agent.account.location.title':                    'आपका स्थान',
  'agent.account.location.subtitle.summary':        'लोकेटर नए खिलाड़ियों को आपके सबसे नज़दीकी क्षेत्र में भेजता है',
  'agent.account.location.subtitle.edit': 'अपना क्षेत्र अपडेट करें ताकि लोकेटर सही खिलाड़ियों को आपके पास भेजे',
  'agent.account.location.cta.edit':                'संपादित करें',
  'agent.account.location.cta.cancel':              'रद्द करें',
  'agent.account.location.cta.save':                'सहेजें',
  'agent.account.location.areaLabel':               'क्षेत्र',
  'agent.account.location.gpsLabel':                'GPS',
  'agent.account.location.setLabel':                'स्थान सेट है',
  'agent.account.location.setBody': 'GPS निर्देशांक रिकॉर्ड में हैं — क्षेत्र की जानकारी देखने के लिए «संपादित करें» पर टैप करें।',
  'agent.account.location.lastUpdated':             'अंतिम अपडेट · {date}',
  'agent.account.security.title':                   'सुरक्षा',
  'agent.account.security.subtitle.locked': 'एडमिन द्वारा लॉक · केवल देखने योग्य',
  'agent.account.security.subtitle.unlocked': 'अपना पासवर्ड बदलें · अन्य डिवाइसें साइन आउट हो जाएँगी',
  'agent.account.security.field.current':           'वर्तमान पासवर्ड',
  'agent.account.security.field.new':               'नया पासवर्ड',
  'agent.account.security.field.new.hint': 'कम से कम 8 वर्ण — कम से कम एक अक्षर और एक अंक शामिल हो',
  'agent.account.security.field.confirm': 'नया पासवर्ड पुष्टि करें',
  'agent.account.security.cta.update':              'पासवर्ड अपडेट करें',
  'agent.account.security.cta.updating':            'अपडेट हो रहा है…',
  'agent.account.security.error.mismatch': 'नए पासवर्ड आपस में मेल नहीं खा रहे',
  'agent.account.security.error.tooShort': 'पासवर्ड कम से कम 8 अक्षर का होना चाहिए',
  'agent.account.security.error.weak': 'पासवर्ड में कम से कम एक अक्षर और एक अंक होने चाहिए',

  // ── ShareableIdCard ───────────────────────────────────────────────
  'agent.shareableId.eyebrow':           'साझा करने योग्य एजेंट ID',
  'agent.shareableId.tooltip.copy':      'UAID कॉपी करें',
  'agent.shareableId.tooltip.copied':    'UAID कॉपी हुआ',
  'agent.shareableId.cta.copy':          'कॉपी',
  'agent.shareableId.cta.copied':        'कॉपी हुआ!',
  'agent.shareableId.body.before':       'इस कोड को किसी और एजेंट से साझा करें ताकि वे खिलाड़ी आपको ट्रांसफ़र कर सकें। चैट या WhatsApp से भेजना सुरक्षित है — इसे साझा करना आपके खाते तक कोई पहुँच ',
  'agent.shareableId.body.not':          'नहीं',
  'agent.shareableId.body.after':        ' देता।',

  // ── RewardsHub + sub-tabs ─────────────────────────────────────────
  'agent.rewards.hub.title':            'इनाम',
  'agent.rewards.hub.subtitle':         'ऑफ़र देखें और अपना एक्सपोज़र ट्रैक करें',
  'agent.rewards.hub.tab.browse':       'ऑफ़र देखें',
  'agent.rewards.hub.tab.exposure':     'मेरा एक्सपोज़र',
  'agent.rewards.status.optedIn':       'ऑप्ट इन किया',
  'agent.rewards.status.optedOut':      'ऑप्ट आउट किया',

  // ── BrowseOffersPanel ─────────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow':         'आपके खिलाड़ियों के लिए विशेष',
  'agent.rewards.browse.empty.title':          'अभी कोई सक्रिय इनाम नहीं',
  'agent.rewards.browse.empty.body':           'एडमिन नियमित नए ऑफ़र पोस्ट करते हैं। थोड़ी देर बाद देखें — या जो छूट गया उसमें आपको ऑप्ट इन करने को अपने अपलाइन से कहें।',
  'agent.rewards.browse.error.loadFailed':     'इनाम लोड नहीं हो सके',
  'agent.rewards.browse.card.aria.manage':     '{title} के लिए ऑप्ट-इन प्रबंधित करें',
  'agent.rewards.browse.chip.optedIn':         'ऑप्ट इन किया',
  'agent.rewards.browse.chip.optInPrompt':     'ऑप्ट इन के लिए टैप करें',
  'agent.rewards.browse.chip.shareSuffix':     '· {pct}%',

  // ── ExposurePanel ─────────────────────────────────────────────────
  'agent.rewards.exposure.loading': 'आपके एक्सपोज़र की गणना हो रही है…',
  'agent.rewards.exposure.error.loadFailed':   'एक्सपोज़र लोड नहीं हो सका',
  'agent.rewards.exposure.hero.eyebrow':                'ऑफ़र एक्सपोज़र',
  'agent.rewards.exposure.hero.activeOptIns.one':       '{count} सक्रिय ऑप्ट-इन',
  'agent.rewards.exposure.hero.activeOptIns.other':     '{count} सक्रिय ऑप्ट-इन',
  'agent.rewards.exposure.hero.actualSpend.label':      'वास्तविक ख़र्च',
  'agent.rewards.exposure.hero.actualSpend.helper':     'अब तक आपकी डाउनलाइन द्वारा दावा किया गया।',
  'agent.rewards.exposure.hero.maxExposure.label':      'अधिकतम एक्सपोज़र',
  'agent.rewards.exposure.hero.maxExposure.helper': 'सबसे ख़राब स्थिति की सीमा — मानो बजट का हर बचा हुआ रुपया दावा कर लिया गया हो।',
  'agent.rewards.exposure.hero.ceiling.label':          'सीमा खपत',
  'agent.rewards.exposure.gauge.label':                 'जोखिम मीटर',
  'agent.rewards.exposure.gauge.maxSuffix':             '/ {amount} अधिकतम',
  'agent.rewards.exposure.gauge.legend.amber.before': '',
  'agent.rewards.exposure.gauge.legend.amber.value':    '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': ' पर अपने ऑप्ट-इन पर ध्यान दें',
  'agent.rewards.exposure.gauge.legend.rose.before': '',
  'agent.rewards.exposure.gauge.legend.rose.value':     '80%',
  'agent.rewards.exposure.gauge.legend.rose.after':     ', आप अपनी सीमा के पास हैं',
  'agent.rewards.exposure.gauge.caption.noMax':         'आप अभी ऐसी किसी चीज़ में ऑप्ट इन नहीं हैं जिसकी अनुमानित सीमा हो।',
  'agent.rewards.exposure.gauge.caption.high':          'आपने अपने अधिकतम एक्सपोज़र का {pct}% खर्च कर दिया है। ज़्यादातर ऑप्ट-इन भारी रूप से दावा किए गए हैं।',
  'agent.rewards.exposure.gauge.caption.mid':           'आप अपनी अधिकतम सीमा के {pct}% पर हैं। प्रति-ऑफ़र विवरण पर ध्यान दें।',
  'agent.rewards.exposure.gauge.caption.low':           'आरामदायक — अब तक आपकी अधिकतम सीमा का केवल {pct}% खर्च हुआ है।',
  'agent.rewards.exposure.footnote.heading':            'अधिकतम एक्सपोज़र की गणना कैसे होती है:',
  'agent.rewards.exposure.footnote.prefix':             ' हर ऑप्ट-इन ऑफ़र के लिए: ',
  'agent.rewards.exposure.footnote.formula':            'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix':             ' — सबसे सावधान ऊपरी सीमा। आपकी वास्तविक देयता आम तौर पर ',
  'agent.rewards.exposure.footnote.emphasis':           'कम',
  'agent.rewards.exposure.footnote.suffix2': ' होती है जब आपके नीचे के सब-एजेंट भी ऑप्ट इन करते हैं (वे बड़ा हिस्सा भरते हैं; आप बाक़ी हिस्सा भरते हैं)।',
  'agent.rewards.exposure.empty.title':                 'कोई सक्रिय ऑप्ट-इन नहीं',
  'agent.rewards.exposure.empty.body':                  'जब आप ऑफ़र देखें टैब से किसी ऑफ़र में ऑप्ट इन करते हैं, तो यह यहाँ आपके ख़र्च और अनुमानित एक्सपोज़र के साथ दिखता है।',
  'agent.rewards.exposure.row.share':                   '{pct}% हिस्सा',
  'agent.rewards.exposure.row.claims.one':              '{count} दावा',
  'agent.rewards.exposure.row.claims.other':            '{count} दावे',
  'agent.rewards.exposure.row.overridden':              'एडमिन ने ओवरराइड किया',
  'agent.rewards.exposure.row.actualMaxLabel':          'वास्तविक / अधिकतम',
  'agent.rewards.exposure.row.noMaxDash':               '—',

  // ── OfferOptInModal ───────────────────────────────────────────────
  'agent.rewards.optInModal.eyebrow':                    'लागत-साझा ऑप्ट-इन',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'गेम लाभ',
  'agent.profile.rates.infoBanner.standardPctText': ' मानक प्रतिशत का उपयोग करते हैं। ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'प्रचार लागत',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ऑफ़र, कैशबैक) एक डायनामिक अपलाइन पूल का उपयोग करते हैं। दोनों को आपके स्तर (L{level}) के आधार पर प्लेटफ़ॉर्म एडमिन द्वारा वैश्विक रूप से प्रबंधित किया जाता है।',
  'agent.profile.rates.section.gameProfits': 'गेम लाभ — आप प्रत्येक मैच पर क्या कमाते हैं',
  'agent.profile.rates.section.promotionalCosts': 'प्रचार लागत — आपकी अपलाइन सब्सिडी योगदान',
  'agent.profile.rates.pillar.platformFee': 'प्लेटफ़ॉर्म शुल्क %',
  'agent.profile.rates.pillar.winLoss': 'जीत / हार लाभ %',
  'agent.profile.rates.pillar.promoWeight': 'प्रचार पूल वज़न',
  'agent.profile.rates.guarantee.title': 'डायरेक्ट रेफरल गारंटी',
  'agent.profile.rates.guarantee.body': 'यदि आपने सीधे उस खिलाड़ी को रेफर किया है जो ऑफ़र क्लेम कर रहा है, तो आप ₹0.00 का भुगतान करते हैं। आपकी अपलाइन्स आपके नेटवर्क को बढ़ाने में मदद करने के लिए 100% लागत सब्सिडी देती हैं।',
  'agent.profile.rates.poolExplainer.title': 'पूल कैसे काम करता है:',
  'agent.profile.rates.poolExplainer.body': 'जब एक सब-एजेंट का खिलाड़ी ऑफ़र क्लेम करता है, तो आपका वज़न शेष सक्रिय अपलाइन + प्लेटफ़ॉर्म के साथ एक पूल में जोड़ा जाता है। आप (आपका वज़न / कुल पूल) × ऑफ़र की लागत भुगतान करते हैं। अधिक वज़न = बड़ा हिस्सा।',
  'agent.profile.rates.weightLabel': 'वज़न:',
  'agent.profile.rates.updatedPrefix': 'अपडेट किया गया',
  'agent.profile.rates.notSetYet': 'अभी सेट नहीं किया गया',
  'agent.rewards.optInModal.guarantee.title': 'डायरेक्ट रेफरल गारंटी',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'यदि <strong>आपने सीधे रेफर किया</strong> उस खिलाड़ी को जो यह ऑफ़र क्लेम कर रहा है, तो आप <strong>₹0.00</strong> भुगतान करते हैं। आपकी अपलाइन्स आपके नेटवर्क को बढ़ाने में मदद करने के लिए 100% लागत सब्सिडी देती हैं।',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'जब <strong>आपके खिलाड़ी</strong> यह ऑफ़र क्लेम करते हैं, तो आप <strong>₹0.00</strong> भुगतान करते हैं। आपकी अपलाइन्स आपके नेटवर्क को बढ़ाने में मदद करने के लिए 100% लागत सब्सिडी देती हैं।',
  'agent.rewards.optInModal.uplineReality.title': 'अपलाइन रियलिटी चेक',
  'agent.rewards.optInModal.uplineReality.body': 'आपके पास <strong>{count}</strong> डायरेक्ट सब-एजेंट{plural} हैं। यदि कोई सब-एजेंट का खिलाड़ी यह ऑफ़र क्लेम करता है, तो आप अपने <strong>पूल वज़न ({weight})</strong> के आधार पर शेष सक्रिय अपलाइन और प्लेटफ़ॉर्म के मुकाबले एक आनुपातिक हिस्सा भुगतान करेंगे।',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'डायरेक्ट',
  'agent.rates.col.indirect': 'इनडायरेक्ट',
  'agent.rates.indirect.dynamic': 'डायनामिक',
  'agent.rates.indirect.notApplicable': 'लागू नहीं',
  'agent.rates.indirect.rangeTo': 'से',
  'agent.rewards.optInModal.close':                      'बंद करें',
  'agent.rewards.optInModal.cancel':                     'रद्द करें',
  'agent.rewards.optInModal.loading':                    'ऑफ़र विवरण लोड हो रहे हैं…',
  'agent.rewards.optInModal.error.loadFailed':           'ऑफ़र विवरण लोड नहीं हो सके',
  'agent.rewards.optInModal.error.optInFailed':          'ऑप्ट-इन विफल',
  'agent.rewards.optInModal.error.optOutFailed':         'ऑप्ट-आउट विफल',
  'agent.rewards.optInModal.error.saveFailed':           'सब-एजेंट दरें सहेज नहीं सके',
  'agent.rewards.optInModal.toast.optedIn':              'ऑप्ट इन किया। अब आपकी डाउनलाइन यह ऑफ़र देख सकती है।',
  'agent.rewards.optInModal.toast.optedOut': 'ऑप्ट आउट किया। आपकी डाउनलाइन को अब यह ऑफ़र नहीं दिखेगा।',
  'agent.rewards.optInModal.toast.saved.one':            '{count} सब-एजेंट दर परिवर्तन सहेजा।',
  'agent.rewards.optInModal.toast.saved.other':          '{count} सब-एजेंट दर परिवर्तन सहेजे।',
  'agent.rewards.optInModal.confirmOptOut': 'इस ऑफ़र से ऑप्ट आउट करें? आपकी डाउनलाइन को अब यह नहीं दिखेगा।',
  'agent.rewards.optInModal.auditNote': 'ऑफ़र #{id} के लिए ऑप्ट-इन डायलॉग से सेट',
  'agent.rewards.optInModal.stake.shareLabel':           'आपका हिस्सा',
  'agent.rewards.optInModal.stake.locked':               'ऑप्ट-इन पर लॉक',
  'agent.rewards.optInModal.stake.current':              'वर्तमान दर',
  'agent.rewards.optInModal.stake.noRate':               'एडमिन ने अभी आपको कोई दर नहीं दी',
  'agent.rewards.optInModal.stake.spentSoFar.label':     'अब तक ख़र्च',
  'agent.rewards.optInModal.stake.spentSoFar.helper':    'इस ऑफ़र पर डाउनलाइन के दावों के लिए आपके वॉलेट से कुल रुपये भुगतान।',
  'agent.rewards.optInModal.stake.maxPossible.label':    'अधिकतम संभव',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'सबसे ख़राब स्थिति में आप जो रुपये भुगतान कर सकते हैं अगर बजट का हर बचा हुआ रुपया दावा कर लिया जाए।',
  'agent.rewards.optInModal.stake.maxPossible.noCap':    '— कोई बजट सीमा नहीं —',
  'agent.rewards.optInModal.stake.worstCase': 'सबसे ख़राब स्थिति: अगर प्लेटफ़ॉर्म बजट के बचे हुए ₹{amount} आपकी डाउनलाइन पूरी तरह दावा कर लेती है, तो आप उसका {pct}% भुगतान करते हैं।',
  'agent.rewards.optInModal.toggle.title.optedIn':       'आप ऑप्ट इन हैं',
  'agent.rewards.optInModal.toggle.title.optedOut':      'अपनी डाउनलाइन के साथ साझा करने के लिए ऑप्ट इन करें',
  'agent.rewards.optInModal.toggle.body.optedIn': 'आपकी डाउनलाइन के खिलाड़ी यह ऑफ़र देखते हैं। आप उनके हर दावे में अपने लॉक हिस्से के मुताबिक़ साथ भुगतान करते हैं।',
  'agent.rewards.optInModal.toggle.body.optedOut': 'जब तक आप ऑप्ट इन नहीं करते, कोई खिलाड़ी यह ऑफ़र नहीं देखेगा। ऑप्ट इन करते ही आपके हिस्से की दर इस ऑफ़र के लिए लॉक हो जाती है।',
  'agent.rewards.optInModal.toggle.optOutLink':          'इस ऑफ़र से ऑप्ट आउट',
  'agent.rewards.optInModal.subAgents.title':            'सब-एजेंट दरें',
  'agent.rewards.optInModal.subAgents.ceilingHint.before':'हर एक अधिकतम ले सकता है: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} सहेजे नहीं गए',
  'agent.rewards.optInModal.subAgents.empty':            'कोई सीधा सब-एजेंट नहीं।',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'सहेजना कैस्केड होता है — अगर आप यहाँ किसी सब-एजेंट की दर बढ़ाते हैं जिसके नीचे पहले से और सब-एजेंट हैं, तो चेन को सुसंगत रखने के लिए उन निचली दरों को अपने आप क्लैम्प किया जा सकता है।',
  'agent.rewards.optInModal.subAgents.noChildren': 'आपका कोई सीधा सब-एजेंट नहीं है। ऑफ़र आपके अपने खिलाड़ियों को दिखता है; एडमिन पूरा बाक़ी हिस्सा भरते हैं।',
  'agent.rewards.optInModal.error.rateRequired':         'दर दर्ज करें (साफ़ करने के लिए 0)',
  'agent.rewards.optInModal.error.numbersOnly':          'केवल संख्याएँ',
  'agent.rewards.optInModal.error.negative':             'ऋणात्मक नहीं हो सकता',
  'agent.rewards.optInModal.error.max100':               'अधिकतम 100',
  'agent.rewards.optInModal.error.aboveCeiling':         'अधिकतम {ceiling}% (आपका हिस्सा)',
  'agent.rewards.optInModal.cta.saving':                 'सहेज रहे हैं…',
  'agent.rewards.optInModal.cta.done':                   'हो गया',
  'agent.rewards.optInModal.cta.save.one':               '{count} परिवर्तन सहेजें',
  'agent.rewards.optInModal.cta.save.other':             '{count} परिवर्तन सहेजें',

  // ════════════════════════════════════════════════════════════════════
  // AGENT APP — Hindi translations (Phase C Batch 1, Chunk 3 of 3).
  // Covers Phase B Groups 8-10: admin pages, login, long-tail sweep.

  // ── AdminRechargePlayer (route adapter) ───────────────────────────
  'agent.adminRechargePlayer.title':     'खिलाड़ी को रिचार्ज करें',
  'agent.adminRechargePlayer.subtitle':  'अपने पूल से खिलाड़ी के वॉलेट तक',
  'agent.adminRechargePlayer.backLink':  'खिलाड़ी पर वापस',

  // ── CreatePlayerModal ─────────────────────────────────────────────
  'agent.createPlayer.title':                          'नया खिलाड़ी जोड़ें',
  'agent.createPlayer.subtitle.agent':                 'वे अपने आप आपके अधीन होंगे।',
  'agent.createPlayer.subtitle.admin':                 'नीचे मालिक एजेंट चुनें, या प्लेटफ़ॉर्म-डायरेक्ट के लिए ख़ाली छोड़ें।',
  'agent.createPlayer.close':                          'बंद करें',
  'agent.createPlayer.section.identity.title':         'पहचान',
  'agent.createPlayer.section.identity.hint':          'खाता बनाने के लिए आवश्यक',
  'agent.createPlayer.field.username.label':           'यूज़रनेम',
  'agent.createPlayer.field.username.placeholder':     'kumar_42',
  'agent.createPlayer.field.email.label':              'ईमेल',
  'agent.createPlayer.field.email.placeholder':        'kumar@example.com',
  'agent.createPlayer.field.phone.label':              'मोबाइल',
  'agent.createPlayer.field.phone.placeholder':        '9876543210',
  'agent.createPlayer.field.phone.hint':               '10 अंकों का भारतीय मोबाइल, +91 अपने आप जुड़ जाएगा',
  'agent.createPlayer.field.password.label':           'प्रारंभिक पासवर्ड',
  'agent.createPlayer.field.password.placeholder':     'StrongP4ss',
  'agent.createPlayer.field.password.hint':            '8+ अक्षर, अक्षर + अंक',
  'agent.createPlayer.section.owner.title':            'मालिक एजेंट',
  'agent.createPlayer.section.owner.hint':             'वैकल्पिक — प्लेटफ़ॉर्म-डायरेक्ट खिलाड़ी के लिए ख़ाली छोड़ें',
  'agent.createPlayer.field.owner.label':              'एजेंट',
  'agent.createPlayer.field.owner.none':               '— कोई नहीं (प्लेटफ़ॉर्म-डायरेक्ट) —',
  'agent.createPlayer.section.profile.title':          'प्रोफ़ाइल',
  'agent.createPlayer.section.profile.hint':          'वैकल्पिक — जो अभी पता है वह भरें; खिलाड़ी पहले लॉगिन पर पूरा कर सकता है',
  'agent.createPlayer.field.firstName.label':          'पहला नाम',
  'agent.createPlayer.field.lastName.label':           'अंतिम नाम',
  'agent.createPlayer.field.dob.label':                'जन्म तारीख़',
  'agent.createPlayer.field.gender.label':             'लिंग',
  'agent.createPlayer.gender.unspecified':             '— निर्दिष्ट नहीं —',
  'agent.createPlayer.gender.male':                    'पुरुष',
  'agent.createPlayer.gender.female':                  'महिला',
  'agent.createPlayer.gender.other':                   'अन्य',
  'agent.createPlayer.gender.preferNot':               'बताना नहीं चाहते',
  'agent.createPlayer.field.language.label':           'पसंदीदा भाषा',
  'agent.createPlayer.section.location.title':         'स्थान',
  'agent.createPlayer.section.location.hint':          'वैकल्पिक — खिलाड़ी पहले लॉगिन पर पूरा कर सकता है',
  'agent.createPlayer.disclaimer':                     'खिलाड़ी को status=active और ईमेल ऑटो-सत्यापित के साथ बनाया जाता है — कोई लिंक नहीं भेजा जाता। सुनिश्चित करें कि आप संपर्क जानकारी पर भरोसा करते हैं।',
  'agent.createPlayer.cta.cancel':                     'रद्द करें',
  'agent.createPlayer.cta.creating':                   'बना रहे हैं…',
  'agent.createPlayer.cta.submit':                     'खिलाड़ी बनाएँ',
  'agent.createPlayer.error.missingAgentId':           'एजेंट ID नहीं है',
  'agent.createPlayer.error.createFailed':             'खिलाड़ी नहीं बना सके',

  // ── AdminHome dashboard ───────────────────────────────────────────
  'agent.adminHome.range.today':                       'आज',
  'agent.adminHome.range.yesterday':                   'कल',
  'agent.adminHome.range.week':                        'इस हफ़्ते',
  'agent.adminHome.range.lastWeek':                    'पिछले हफ़्ते',
  'agent.adminHome.range.month':                       'इस महीने',
  'agent.adminHome.range.lastMonth':                   'पिछले महीने',
  'agent.adminHome.range.all': 'सभी',
  'agent.adminHome.greeting.subtitle':                 'वापस स्वागत है',
  'agent.adminHome.greeting.fallback': '',
  'agent.adminHome.errorBanner.fallback':              'लोड करने में विफल',
  'agent.adminHome.admin.hero.label':                  'प्लेटफ़ॉर्म इनफ़्लो',
  'agent.adminHome.admin.action.moneyQueue': 'भुगतान क़तार',
  'agent.adminHome.admin.action.agents':               'एजेंट',
  'agent.adminHome.admin.snapshot.matches':            'मैच',
  'agent.adminHome.admin.snapshot.newSignups':         'नए साइनअप',
  'agent.adminHome.admin.snapshot.active':             'सक्रिय',
  'agent.adminHome.admin.snapshot.pending':            'लंबित',
  'agent.adminHome.section.games':                     'गेम',
  'agent.adminHome.section.games.subtitle':            'मैच और प्लेटफ़ॉर्म फ़ीस · {range}',
  'agent.adminHome.section.activity':                  'गतिविधि',
  'agent.adminHome.section.ops': 'संचालन और जानकारी',
  'agent.adminHome.section.ops.subtitle':              'लाइव स्नैपशॉट — पूरे पेज पर जाने के लिए किसी भी कार्ड पर क्लिक करें',
  'agent.adminHome.section.gameSettings':              'गेम सेटिंग्स',
  'agent.adminHome.section.gameSettings.subtitle':     'हर गेम कॉन्फ़िगर करें',
  'agent.adminHome.section.downline': 'मेरी डाउनलाइन की जानकारी',
  'agent.adminHome.section.downline.subtitle':         'पूरे पेज पर जाने के लिए किसी भी कार्ड पर क्लिक करें',
  'agent.adminHome.admin.card.players.title':              'खिलाड़ी वॉलेट',
  'agent.adminHome.admin.card.players.headlineLabel':      'अवधि में रिचार्ज',
  'agent.adminHome.admin.card.players.flow.recharges':     'रिचार्ज',
  'agent.adminHome.admin.card.players.flow.withdrawals':   'निकासियाँ',
  'agent.adminHome.admin.card.players.pending.recharges':  'लंबित रिचार्ज',
  'agent.adminHome.admin.card.players.pending.withdrawals':'लंबित निकासियाँ',
  'agent.adminHome.admin.card.players.stat.total':         'कुल खिलाड़ी',
  'agent.adminHome.admin.card.players.stat.active':        'सक्रिय',
  'agent.adminHome.admin.card.players.stat.suspended':     'निलंबित',
  'agent.adminHome.admin.card.agents.title':               'एजेंट वॉलेट',
  'agent.adminHome.admin.card.agents.headlineLabel':       'एजेंट पूल में',
  'agent.adminHome.admin.card.agents.flow.topups':         'टॉप-अप (एडमिन → एजेंट)',
  'agent.adminHome.admin.card.agents.flow.outflow':        'आउटफ़्लो (भुगतान / टॉप-अप)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'लंबित एजेंट निकासियाँ',
  'agent.adminHome.admin.card.agents.stat.total':          'कुल एजेंट',
  'agent.adminHome.admin.card.agents.stat.active':         'सक्रिय',
  'agent.adminHome.admin.card.agents.stat.suspended':      'निलंबित',
  'agent.adminHome.admin.card.bots.title':                 'बॉट वॉलेट',
  'agent.adminHome.admin.card.bots.headlineLabel':         'बॉट वॉलेट में',
  'agent.adminHome.admin.card.bots.stat.total':            'कुल बॉट',
  'agent.adminHome.admin.card.bots.stat.active':           'सक्रिय',
  'agent.adminHome.admin.card.bots.stat.matches':          'मैच',
  'agent.adminHome.admin.card.rewards.title':              'इनाम',
  'agent.adminHome.admin.card.rewards.headlineLabel':      'इस हफ़्ते भुगतान',
  'agent.adminHome.admin.card.rewards.pending.claims':     'लंबित दावे',
  'agent.adminHome.admin.card.rewards.stat.activeOffers':  'सक्रिय ऑफ़र',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs':      'बल्क जॉब',
  'agent.adminHome.gameTile.matches':                  'मैच',
  'agent.adminHome.game.ludo':                         'लूडो',
  'agent.adminHome.game.xo':                           'XO',
  'agent.adminHome.game.rummy':                        'रमी',
  'agent.adminHome.game.carrom':                       'कैरम',
  'agent.adminHome.game.chess':                        'शतरंज',
  'agent.adminHome.activity.topPlayers.title':         'टॉप खिलाड़ी',
  'agent.adminHome.activity.topPlayers.empty':         'इस अवधि में कोई मैच नहीं।',
  'agent.adminHome.activity.topPlayers.metric':        '{count} मैच',
  'agent.adminHome.activity.topAgents.title':          'टॉप एजेंट',
  'agent.adminHome.activity.topAgents.subtitle': 'स्वामित्व वाले खिलाड़ियों की संख्या के अनुसार',
  'agent.adminHome.activity.topAgents.empty':          'अभी तक कोई सक्रिय एजेंट नहीं।',
  'agent.adminHome.activity.topAgents.metric':         '{count} खिलाड़ी',
  'agent.adminHome.activity.recent.title':             'हाल के लेन-देन',
  'agent.adminHome.activity.recent.subtitle':          'पिछले 5',
  'agent.adminHome.activity.recent.empty':             'अभी तक कोई लेन-देन नहीं।',
  'agent.adminHome.ops.topEarners.title':              'टॉप कमाने वाले (7d)',
  'agent.adminHome.ops.topEarners.subtitle':           'शुद्ध नेट के अनुसार',
  'agent.adminHome.ops.topEarners.empty':              'पिछले 7 दिनों में कोई कमाई नहीं।',
  'agent.adminHome.ops.liveActivity.title':            'लाइव गतिविधि',
  'agent.adminHome.ops.liveActivity.subtitle':         'प्लेटफ़ॉर्म भर के पिछले 5 ईवेंट',
  'agent.adminHome.ops.liveActivity.empty':            'शांत — पिछले 24h में कोई ईवेंट नहीं।',
  'agent.adminHome.ops.botPerf.title':                 'बॉट प्रदर्शन (7d)',
  'agent.adminHome.ops.botPerf.subtitle':              'प्रति-श्रेणी लाभ-हानि',
  'agent.adminHome.ops.rateChanges.title':             'हाल के रेट परिवर्तन',
  'agent.adminHome.ops.rateChanges.subtitle':          'नवीनतम पार्टनरशिप संपादन',
  'agent.adminHome.ops.rateChanges.empty':             'अभी तक कोई रेट परिवर्तन ईवेंट नहीं।',
  'agent.adminHome.ops.opsHealth.title': 'संचालन की स्थिति',
  'agent.adminHome.ops.opsHealth.subtitle':            'डेड-लेटर क़तार · लंबित सेटलमेंट',
  'agent.adminHome.feed.match.earnedVerb':             'कमाया',
  'agent.adminHome.feed.match.onGameSuffix':           '{game} पर',
  'agent.adminHome.feed.recharge.label':               'रिचार्ज',
  'agent.adminHome.feed.recharge.amountInfix':         '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix':           'के माध्यम से',
  'agent.adminHome.feed.rateChange.label':             'रेट परिवर्तन',
  'agent.adminHome.feed.cascadeBadge':                 'कैस्केड',
  'agent.adminHome.feed.recharge.state.approved':      'मंज़ूर',
  'agent.adminHome.feed.recharge.state.rejected':      'अस्वीकृत',
  'agent.adminHome.feed.recharge.state.pending':       'लंबित',
  'agent.adminHome.feed.recharge.state.paid':          'भुगतान हुआ',
  'agent.adminHome.feed.recharge.state.expired':       'समाप्त',
  'agent.adminHome.leaderboard.matches.one':           '{count} मैच',
  'agent.adminHome.leaderboard.matches.other':         '{count} मैच',
  'agent.adminHome.leaderboard.levelPrefix':           'L{level}',
  'agent.adminHome.botMatrix.profitLabel':             'प्लेटफ़ॉर्म लाभ (7d)',
  'agent.adminHome.botMatrix.cat.easy':                'आसान',
  'agent.adminHome.botMatrix.cat.medium':              'मध्यम',
  'agent.adminHome.botMatrix.cat.hard':                'कठिन',
  'agent.adminHome.botMatrix.matchesShort':            '{matches}m',
  'agent.adminHome.botMatrix.activeSummary':           '{total} में से {active} बॉट सक्रिय',
  'agent.adminHome.botMatrix.loading':                 'लोड हो रहा है…',
  'agent.adminHome.opsHealth.dlqClean.title':          'DLQ साफ़ है',
  'agent.adminHome.opsHealth.dlqClean.body':           'कोई विफल सेटलमेंट जॉब नहीं।',
  'agent.adminHome.opsHealth.dlqDirty.title.one':      '{count} अनसुलझा DLQ जॉब',
  'agent.adminHome.opsHealth.dlqDirty.title.other':    '{count} अनसुलझे DLQ जॉब',
  'agent.adminHome.opsHealth.dlqDirty.body':           'फिर कोशिश / हल करने के लिए क्लिक करें।',
  'agent.adminHome.opsHealth.replay.eyebrow':          'फिर चलाएँ',
  'agent.adminHome.opsHealth.replay.body':             'सेटलमेंट फिर चलाएँ',
  'agent.adminHome.opsHealth.reassign.eyebrow':        'पुनः असाइन',
  'agent.adminHome.opsHealth.reassign.body':           'बल्क मूव',
  'agent.adminHome.agentDash.hero.label':              'मेरा पूल बैलेंस',
  'agent.adminHome.agentDash.hero.netPrefix':          'नेट',
  'agent.adminHome.agentDash.action.myWallet':         'मेरा वॉलेट',
  'agent.adminHome.agentDash.action.myPlayers':        'मेरे खिलाड़ी',
  'agent.adminHome.agentDash.snapshot.myMatches':      'मेरे मैच',
  'agent.adminHome.agentDash.snapshot.active':         'सक्रिय',
  'agent.adminHome.agentDash.snapshot.netRange':       'नेट (अवधि)',
  'agent.adminHome.agentDash.snapshot.pending':        'लंबित',
  'agent.adminHome.agentDash.card.players.title':              'मेरे खिलाड़ी वॉलेट',
  'agent.adminHome.agentDash.card.players.headlineLabel':      'अवधि में रिचार्ज',
  'agent.adminHome.agentDash.card.players.stat.total':         'मेरे खिलाड़ी',
  'agent.adminHome.agentDash.card.wallet.title':               'मेरा वॉलेट',
  'agent.adminHome.agentDash.card.wallet.headlineLabel':       'पूल बैलेंस',
  'agent.adminHome.agentDash.card.wallet.flow.earned':         'कमाया (कमीशन)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut':        'भुगतान किया',
  'agent.adminHome.agentDash.card.wallet.pending':             'मेरी लंबित निकासी',
  'agent.adminHome.agentDash.card.wallet.stat.topups':         'प्राप्त टॉप-अप',
  'agent.adminHome.agentDash.card.wallet.stat.netRange':       'नेट (अवधि)',
  'agent.adminHome.agentDash.card.activity.title':             'गतिविधि',
  'agent.adminHome.agentDash.card.activity.headlineLabel':     'अवधि में मेरे मैच',
  'agent.adminHome.agentDash.card.activity.stat.topSpender':   'टॉप ख़र्च करने वाला',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend':   'उनका ख़र्च',
  'agent.adminHome.agentDash.card.activity.stat.pending':      'लंबित आइटम',
  'agent.adminHome.agentDash.activity.topSpenders.title':      'टॉप ख़र्च करने वाले',
  'agent.adminHome.agentDash.activity.topSpenders.empty':      'इस अवधि में अभी तक कोई ख़र्च नहीं।',
  'agent.adminHome.agentDash.activity.recent.title':           'हाल की गतिविधि',
  'agent.adminHome.agentDash.activity.recent.subtitle':        'आपके खिलाड़ियों से पिछले 5',
  'agent.adminHome.agentDash.activity.recent.empty':           'अभी तक कोई लेन-देन नहीं।',
  'agent.adminHome.agentDash.insights.topEarners.title':       'टॉप कमाने वाले (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle':    'आपके सब-एजेंट क्रम में',
  'agent.adminHome.agentDash.insights.downline.title':         'डाउनलाइन गतिविधि',
  'agent.adminHome.agentDash.insights.downline.subtitle':      'आपकी डाउनलाइन से पिछले 5 ईवेंट',
  'agent.adminHome.agentDash.insights.deposit.title':          'जमा लेजर',
  'agent.adminHome.agentDash.insights.deposit.subtitle':       'हाल के जमा क्रेडिट / रिफ़ंड',
  'agent.adminHome.agentDash.insights.deposit.empty':          'अभी तक कोई जमा गतिविधि नहीं।',

  // ── AgentSubAgentDetail ───────────────────────────────────────────
  'agent.subAgentDetail.title':                                'सब-एजेंट',
  'agent.subAgentDetail.subtitle':                             'सीधा सब-एजेंट',
  'agent.subAgentDetail.error.loadFailed':                     'सब-एजेंट लोड करने में विफल',
  'agent.subAgentDetail.backToList':                           'सब-एजेंट सूची पर वापस',
  'agent.subAgentDetail.loading':                              'लोड हो रहा है…',
  'agent.subAgentDetail.tab.identity':                         'पहचान',
  'agent.subAgentDetail.tab.players':                          'खिलाड़ी',
  'agent.subAgentDetail.tab.wallet':                           'वॉलेट',
  'agent.subAgentDetail.tab.rates':                            'दरें',
  'agent.subAgentDetail.tab.ops':                              'संचालन',
  'agent.subAgentDetail.header.players.one':                   '{count} खिलाड़ी',
  'agent.subAgentDetail.header.players.other':                 '{count} खिलाड़ी',
  'agent.subAgentDetail.header.pool':                          'पूल: {amount}',
  'agent.subAgentDetail.header.lastLogin':                     'अंतिम लॉगिन {when}',
  'agent.subAgentDetail.status.active':                        'सक्रिय',
  'agent.subAgentDetail.status.suspended':                     'निलंबित',
  'agent.subAgentDetail.identity.row.uaid':                    'UAID',
  'agent.subAgentDetail.identity.row.username':                'यूज़रनेम',
  'agent.subAgentDetail.identity.row.email':                   'ईमेल',
  'agent.subAgentDetail.identity.row.status':                  'स्थिति',
  'agent.subAgentDetail.identity.row.parent':                  'पैरंट एजेंट',
  'agent.subAgentDetail.identity.parentValue':                 '@{username} (आप)',
  'agent.subAgentDetail.identity.parentEmpty':                 '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'स्वामित्व वाले खिलाड़ी',
  'agent.subAgentDetail.identity.row.wallet':                  'वॉलेट बैलेंस',
  'agent.subAgentDetail.identity.row.created':                 'बनाया',
  'agent.subAgentDetail.identity.row.lastLogin':               'अंतिम लॉगिन',
  'agent.subAgentDetail.identity.row.identityLocked':          'पहचान लॉक',
  'agent.subAgentDetail.identity.identityLocked.yes': 'हाँ (केवल एडमिन संपादित कर सकते हैं)',
  'agent.subAgentDetail.identity.identityLocked.no':           'नहीं (सब-एजेंट ख़ुद संपादित कर सकते हैं)',
  'agent.subAgentDetail.identity.row.receiving': 'खिलाड़ी स्वीकार स्थिति',
  'agent.subAgentDetail.optOut.accepting':                     'ले रहे हैं',
  'agent.subAgentDetail.optOut.optedOut':                      'ऑप्ट आउट',
  'agent.subAgentDetail.optOut.toast.accepting': 'सब-एजेंट अब खिलाड़ी ले रहा है',
  'agent.subAgentDetail.optOut.toast.optedOut':                'सब-एजेंट ने लोकेटर से ऑप्ट आउट किया',
  'agent.subAgentDetail.optOut.error.updateFailed':            'अपडेट नहीं कर सके',
  'agent.subAgentDetail.location.title':                       'स्थान',
  'agent.subAgentDetail.location.lastUpdated':                 '· अंतिम अपडेट {date}',
  'agent.subAgentDetail.location.cta.save':                    'स्थान सहेजें',
  'agent.subAgentDetail.location.nothingToSave':               'सहेजने के लिए कुछ नहीं',
  'agent.subAgentDetail.location.toast.saved.one':             'स्थान सहेजा गया ({count} फ़ील्ड)',
  'agent.subAgentDetail.location.toast.saved.other':           'स्थान सहेजा गया ({count} फ़ील्ड)',
  'agent.subAgentDetail.location.hint':                        'सहेजने को सक्षम करने के लिए कोई अलग राज्य / ज़िला / क्षेत्र चुनें। (सब-एजेंट ख़ुद पहले लॉगिन पर अनिवार्य पॉप-अप से सटीक GPS ओवरले करते हैं।)',
  'agent.subAgentDetail.players.empty':                        'इस सब-एजेंट के पास अभी कोई खिलाड़ी नहीं है।',
  'agent.subAgentDetail.players.count.one':                    '{count} खिलाड़ी',
  'agent.subAgentDetail.players.count.other':                  '{count} खिलाड़ी',
  'agent.subAgentDetail.wallet.balanceLabel':                  'वर्तमान सब-एजेंट बैलेंस',
  'agent.subAgentDetail.wallet.ledger.title':                  '@{username} का पूरा वॉलेट लेजर',
  'agent.subAgentDetail.wallet.ledger.subtitle':               'हर गतिविधि · सब-एजेंट अपने वॉलेट में वही देखता है',
  'agent.subAgentDetail.wallet.ledger.empty':                  'अभी तक कोई वॉलेट गतिविधि नहीं।',
  'agent.subAgentDetail.wallet.ledger.youThemBadge':           'आप ↔ वे',
  'agent.subAgentDetail.wallet.ledger.balanceAfter':           'बैलेंस ₹{amount}',
  'agent.subAgentDetail.wallet.kind.topupFromAdmin':           'एडमिन से टॉप-अप',
  'agent.subAgentDetail.wallet.kind.topupToPlayer':            'खिलाड़ी को भुगतान',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer':          'खिलाड़ी का रिचार्ज',
  'agent.subAgentDetail.wallet.kind.revenueShare':             'राजस्व हिस्सा',
  'agent.subAgentDetail.wallet.kind.rechargeCommission':       'रिचार्ज कमीशन',
  'agent.subAgentDetail.wallet.kind.coFundOffer':              'सह-वित्तपोषित ऑफ़र',
  'agent.subAgentDetail.wallet.kind.adminAdjust':              'एडमिन समायोजन',
  'agent.subAgentDetail.wallet.kind.withdrawalHold':           'निकासी होल्ड',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund':         'निकासी रिफ़ंड',
  'agent.subAgentDetail.wallet.kind.platformFeeShare':         'प्लेटफ़ॉर्म फ़ीस हिस्सा',
  'agent.subAgentDetail.wallet.kind.winLossShare':             'विन/लॉस हिस्सा',
  'agent.subAgentDetail.wallet.pullback.title':                'सिक्के वापस ले रहे हैं?',
  'agent.subAgentDetail.wallet.pullback.body.before': '',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' से कहें कि वे अपने पूल से निकासी अनुरोध भेजें। आप इसे (पूरा या आंशिक) भुगतान दर्ज कर सकते हैं ',
  'agent.subAgentDetail.wallet.pullback.financeLink':          'फ़ाइनेंस › सब-एजेंट',
  'agent.subAgentDetail.wallet.pullback.body.after':           ' से।',
  'agent.subAgentDetail.transfer.topup.heading':               '@{username} को ट्रांसफ़र',
  'agent.subAgentDetail.transfer.withdraw.heading':            '@{username} से निकालें',
  'agent.subAgentDetail.transfer.topup.body':                  'अविभाज्य ट्रांसफ़र: आपका वॉलेट कम, उनका वॉलेट अधिक। दोनों पूल ऋणात्मक हो सकते हैं (साइन्ड-पूल डिज़ाइन)।',
  'agent.subAgentDetail.transfer.withdraw.body':               'अविभाज्य उल्टा ट्रांसफ़र: उनका वॉलेट कम, आपका वॉलेट अधिक। सब-एजेंट के भुगतान अनुरोध को निपटाते समय यह इस्तेमाल करें।',
  'agent.subAgentDetail.transfer.amountLabel':                 'राशि (₹)',
  'agent.subAgentDetail.transfer.noteLabel':                   'नोट (वैकल्पिक)',
  'agent.subAgentDetail.transfer.note.placeholder.topup':      'जैसे साप्ताहिक टॉप-अप',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw':   'जैसे भुगतान निपटान',
  'agent.subAgentDetail.transfer.error.amountPositive':        'राशि एक धनात्मक पूर्णांक होनी चाहिए।',
  'agent.subAgentDetail.transfer.confirm.topup':               'अपने पूल से @{username} को {amount} ट्रांसफ़र? आपका बैलेंस इतना ही कम हो जाएगा।',
  'agent.subAgentDetail.transfer.confirm.withdraw':            '@{username} के पूल से {amount} अपने पास वापस? उनका बैलेंस इतना ही कम हो जाएगा।',
  'agent.subAgentDetail.transfer.toast.topup':                 '@{username} को {amount} भेजा। उनका बैलेंस: {childBalance}।',
  'agent.subAgentDetail.transfer.toast.withdraw':              '@{username} से {amount} वापस लिया। उनका बैलेंस: {childBalance}।',
  'agent.subAgentDetail.transfer.cta.working':                 'काम जारी है…',
  'agent.subAgentDetail.transfer.cta.suspended': 'सब-एजेंट निलंबित है',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} ट्रांसफ़र करें',
  'agent.subAgentDetail.transfer.cta.withdraw':                '{amount} निकालें',
  'agent.subAgentDetail.rates.loading':                        'दरें लोड हो रही हैं…',
  'agent.subAgentDetail.rates.intro.part1':                    'हर कार्ड एक कमीशन पिलर है; अंदर की हर पंक्ति एक (पिलर × गेम) हिस्सा है। ',
  'agent.subAgentDetail.rates.intro.ceiling':                  'सीमा',
  'agent.subAgentDetail.rates.intro.part2':                    ' जो हर इनपुट के बगल में दिखती है वह आपकी अपनी दर है — गोल्डन लिमिट के अनुसार आप उससे ज़्यादा बच्चे को नहीं दे सकते। आप जो भी बदलते हैं वह संपादन योग्य हो जाता है; क्लिक करें ',
  'agent.subAgentDetail.rates.intro.applyWord':                'लागू करें',
  'agent.subAgentDetail.rates.intro.part3':                    ' लागू करने के लिए। कैस्केड क्लैम्प सर्वर-साइड लागू होता है।',
  'agent.subAgentDetail.rates.toast.applied':                  'दरें लागू की गईं।',
  'agent.subAgentDetail.rates.confirm.one':                    '{count} दर परिवर्तन लागू करें? गोल्डन लिमिट + कैस्केड क्लैम्प सर्वर-साइड लागू होगा।',
  'agent.subAgentDetail.rates.confirm.other':                  '{count} दर परिवर्तन लागू करें? गोल्डन लिमिट + कैस्केड क्लैम्प सर्वर-साइड लागू होगा।',
  'agent.subAgentDetail.rates.error.blocked': 'ब्लॉक किया गया: {detail}',
  'agent.subAgentDetail.rates.pillar.platformFee':             'प्लेटफ़ॉर्म फ़ीस %',
  'agent.subAgentDetail.rates.pillar.winLoss':                 'विन/लॉस लाभ %',
  'agent.subAgentDetail.rates.pillar.offerReward':             'ऑफ़र इनाम %',
  'agent.subAgentDetail.rates.row.globalLabel': '— सभी गेम —',
  'agent.subAgentDetail.rates.row.ceilingSuffix':              '/ सीमा {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge':               'पैरंट से अधिक (गोल्डन लिमिट)',
  'agent.subAgentDetail.rates.row.editedBadge':                'संपादित',
  'agent.subAgentDetail.rates.cta.discard.one':                '{count} परिवर्तन छोड़ें',
  'agent.subAgentDetail.rates.cta.discard.other':              '{count} परिवर्तन छोड़ें',
  'agent.subAgentDetail.rates.cta.apply.one':                  '{count} परिवर्तन लागू करें',
  'agent.subAgentDetail.rates.cta.apply.other':                '{count} परिवर्तन लागू करें',
  'agent.subAgentDetail.rates.cta.applying':                   'लागू कर रहे हैं…',
  'agent.subAgentDetail.ops.status.title':                     'स्थिति',
  'agent.subAgentDetail.ops.status.body':                      'निलंबित करना सब-एजेंट के लॉगिन और डाउनलाइन संचालन को तब तक बंद करता है जब तक आप वापस सक्रिय नहीं करते। कारण वैकल्पिक है पर ऑडिट ट्रेल में आता है।',
  'agent.subAgentDetail.ops.status.reasonPlaceholder':         'कारण (वैकल्पिक)',
  'agent.subAgentDetail.ops.status.cta.suspend':               'सब-एजेंट निलंबित करें',
  'agent.subAgentDetail.ops.status.cta.unsuspend':             'सब-एजेंट का निलंबन हटाएँ',
  'agent.subAgentDetail.ops.status.cta.working':               'काम जारी है…',
  'agent.subAgentDetail.ops.status.confirm.suspend':           '@{username} को निलंबित करें? निलंबित रहते हुए वे लॉगिन या काम नहीं कर पाएँगे।',
  'agent.subAgentDetail.ops.status.confirm.unsuspend':         '@{username} का निलंबन हटाएँ? वे पूरी पहुँच फिर पाएँगे।',
  'agent.subAgentDetail.ops.status.toast.suspended':           '@{username} अब निलंबित है।',
  'agent.subAgentDetail.ops.status.toast.active':              '@{username} अब सक्रिय है।',
  'agent.subAgentDetail.ops.password.title':                   'पासवर्ड रीसेट करें',
  'agent.subAgentDetail.ops.password.body':                    'सब-एजेंट के लिए नया पासवर्ड सेट करता है और उनके मौजूदा रिफ़्रेश टोकन रद्द करता है। उन्हें नए पासवर्ड से फिर लॉगिन करना होगा। इसे सुरक्षित चैनल से साझा करें।',
  'agent.subAgentDetail.ops.password.placeholder':             'नया पासवर्ड (न्यूनतम 8 अक्षर)',
  'agent.subAgentDetail.ops.password.confirm':                 '@{username} का पासवर्ड रीसेट करें? उनके मौजूदा सत्र अमान्य हो जाएँगे और उन्हें नए पासवर्ड से लॉगिन करना होगा।',
  'agent.subAgentDetail.ops.password.toast.reset':             'पासवर्ड रीसेट। नया पासवर्ड @{username} के साथ साझा करें।',
  'agent.subAgentDetail.ops.password.error.tooShort':          'पासवर्ड कम से कम 8 अक्षर का होना चाहिए।',
  'agent.subAgentDetail.ops.password.cta.reset':               'पासवर्ड रीसेट करें',
  'agent.subAgentDetail.ops.password.cta.resetting':           'रीसेट हो रहा है…',

  // ── AdminUserDetail (player edit page) ────────────────────────────
  'agent.playerDetail.backToPlayers':                          'खिलाड़ियों पर वापस',
  'agent.playerDetail.loading':                                'लोड हो रहा है…',
  'agent.playerDetail.error.loadFailed':                       'यूज़र लोड करने में विफल',
  'agent.playerDetail.header.noName':                          '(कोई नाम सेट नहीं)',
  'agent.playerDetail.header.joined':                          '{date} को शामिल हुए',
  'agent.playerDetail.header.ownedBy.prefix':                  ' · मालिक ',
  'agent.playerDetail.header.walletLabel':                     'वॉलेट',
  'agent.playerDetail.status.pending':                         'लंबित',
  'agent.playerDetail.status.active':                          'सक्रिय',
  'agent.playerDetail.status.suspended':                       'निलंबित',
  'agent.playerDetail.status.banned':                          'प्रतिबंधित',
  'agent.playerDetail.tab.identity':                           'पहचान',
  'agent.playerDetail.tab.profile':                            'प्रोफ़ाइल',
  'agent.playerDetail.tab.wallet':                             'वॉलेट',
  'agent.playerDetail.tab.security':                           'सुरक्षा',
  'agent.playerDetail.tab.ownership':                          'स्वामित्व',
  'agent.playerDetail.tab.operations':                         'संचालन',
  'agent.playerDetail.identity.title':                         'पहचान',
  'agent.playerDetail.identity.row.username':                  'यूज़रनेम',
  'agent.playerDetail.identity.row.email':                     'ईमेल',
  'agent.playerDetail.identity.row.phone':                     'फ़ोन',
  'agent.playerDetail.identity.row.country':                   'देश',
  'agent.playerDetail.identity.row.joined':                    'शामिल हुए',
  'agent.playerDetail.identity.row.emailVerified':             'ईमेल सत्यापित',
  'agent.playerDetail.identity.row.telegram':                  'टेलीग्राम',
  'agent.playerDetail.identity.row.failedLogins':              'विफल लॉगिन',
  'agent.playerDetail.identity.row.lockedUntil':               'इस समय तक लॉक',
  'agent.playerDetail.identity.value.yes':                     'हाँ',
  'agent.playerDetail.identity.value.no':                      'नहीं',
  'agent.playerDetail.identity.value.dash':                    '—',
  'agent.playerDetail.identity.transfer.title':                'इस खिलाड़ी को ट्रांसफ़र करें',
  'agent.playerDetail.identity.transfer.body':                 'किसी अन्य एजेंट की Unique Agent ID से हस्तांतरण — पूरे नेटवर्क में काम करता है, केवल आपकी डाउनलाइन में नहीं।',
  'agent.playerDetail.identity.transfer.cta':                  'UAID से ट्रांसफ़र',
  'agent.playerDetail.avatar.changeTitle':                     'फ़ोटो बदलें',
  'agent.playerDetail.avatar.cropTitle':                       '@{username} की फ़ोटो समायोजित करें',
  'agent.playerDetail.avatar.toast.updated':                   'फ़ोटो अपडेट हुई',
  'agent.playerDetail.avatar.error.uploadFailed':              'अपलोड विफल',
  'agent.playerDetail.transfer.toast.admin':                   'खिलाड़ी UAID से ट्रांसफ़र हुआ',
  'agent.playerDetail.transfer.toast.agent':                   '@{username} सफलतापूर्वक ट्रांसफ़र हुआ',
  'agent.playerDetail.ops.accountStatus.title':                'खाता स्थिति',
  'agent.playerDetail.ops.accountStatus.body':                 'सक्रिय का मतलब खिलाड़ी लॉगिन और खेल सकता है। निलंबित आपके वापस करने तक लॉगिन बंद करता है। प्रतिबंधित मज़बूत लॉक है — नीति उल्लंघन के लिए रखें।',
  'agent.playerDetail.ops.statusBtn.activate':                 'सक्रिय करें',
  'agent.playerDetail.ops.statusBtn.suspend':                  'निलंबित करें',
  'agent.playerDetail.ops.statusBtn.ban':                      'प्रतिबंधित करें',
  'agent.playerDetail.ops.statusBtn.current':                  'वर्तमान',
  'agent.playerDetail.ops.statusBtn.toast':                    'यूज़र अब {status} है',
  'agent.playerDetail.ops.danger.title':                       'ख़तरा क्षेत्र',
  'agent.playerDetail.ops.danger.body':                        'हार्ड-डिलीट यूज़र रिकॉर्ड हटाता है। वॉलेट लेजर ऑडिट के लिए सुरक्षित रहता है, पर खिलाड़ी पूरी पहुँच खो देगा। यह क्रिया वापस नहीं हो सकती।',
  'agent.playerDetail.ops.delete.eyebrow':                     'ख़तरा क्षेत्र',
  'agent.playerDetail.ops.delete.confirm.before':              'इस खाते को स्थायी रूप से डिलीट करें। यह वापस नहीं हो सकता। पुष्टि के लिए ',
  'agent.playerDetail.ops.delete.confirm.after':               ' टाइप करें।',
  'agent.playerDetail.ops.delete.cta':                         'डिलीट करें',
  'agent.playerDetail.ops.delete.toast':                       'यूज़र डिलीट किया',
  'agent.playerDetail.walletAdjust.title':                     'वॉलेट समायोजित करें',
  'agent.playerDetail.walletAdjust.mode.credit':               '+ क्रेडिट',
  'agent.playerDetail.walletAdjust.mode.debit':                '− डेबिट',
  'agent.playerDetail.walletAdjust.field.amount':              'राशि (₹)',
  'agent.playerDetail.walletAdjust.field.reason':              'कारण · लेजर में दर्ज',
  'agent.playerDetail.walletAdjust.field.reason.placeholder':  'जैसे जमा वापसी, बोनस, रिफ़ंड',
  'agent.playerDetail.walletAdjust.markAsRecharge.label':      'रिचार्ज के रूप में दर्ज',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before':'इस क्रेडिट को लेजर में ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold':  'रिचार्ज',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' के रूप में दर्ज करें। अगर इस यूज़र को किसी ने रेफ़र किया था, तो रेफ़र करने वाले को अपने आप बोनस मिलता है।',
  'agent.playerDetail.walletAdjust.error.positiveAmount':      'धनात्मक राशि दर्ज करें',
  'agent.playerDetail.walletAdjust.error.reasonRequired':      'कारण आवश्यक है',
  'agent.playerDetail.walletAdjust.cta.applying':              'लागू कर रहे हैं…',
  'agent.playerDetail.walletAdjust.cta.applyCredit':           'क्रेडिट लागू करें',
  'agent.playerDetail.walletAdjust.cta.applyDebit':            'डेबिट लागू करें',
  'agent.playerDetail.walletAdjust.toast.credited':            '{amount} क्रेडिट किया',
  'agent.playerDetail.walletAdjust.toast.debited':             '{amount} डेबिट किया',
  'agent.playerDetail.walletAdjust.toast.referralSuffix':      ' · रेफ़र करने वाले ने {bonus} बोनस कमाया',
  'agent.playerDetail.rechargeCta.title':                      'इस खिलाड़ी को रिचार्ज करें',
  'agent.playerDetail.rechargeCta.body.before':                '@',
  'agent.playerDetail.rechargeCta.body.middle':                ' को टॉप-अप करने पर सिक्के ',
  'agent.playerDetail.rechargeCta.body.bold':                  'आपके वॉलेट',
  'agent.playerDetail.rechargeCta.body.after': ' से उनके वॉलेट में जाते हैं। आपका पूल पहले जाँचा जाता है — यह क्रिया अविभाज्य है और अगर बैलेंस अपर्याप्त है तो अस्वीकृत होती है।',
  'agent.playerDetail.rechargeCta.thisPlayer':                 'इस खिलाड़ी',
  'agent.playerDetail.rechargeCta.cta':                        'रिचार्ज फ़ॉर्म खोलें',
  'agent.playerDetail.security.title':                         'पासवर्ड रीसेट करें',
  'agent.playerDetail.security.subtitle':                      'नया पासवर्ड सेट करता है और यूज़र को हर डिवाइस से साइन आउट कर देता है।',
  'agent.playerDetail.security.field.new':                     'नया पासवर्ड',
  'agent.playerDetail.security.field.confirm':                 'पुष्टि करें',
  'agent.playerDetail.security.error.mismatch':                'पासवर्ड मेल नहीं खाते',
  'agent.playerDetail.security.error.tooShort':                'कम से कम 8 अक्षर, अक्षरों और अंकों के साथ',
  'agent.playerDetail.security.toast.reset':                   'पासवर्ड रीसेट और सभी सत्र रद्द',
  'agent.playerDetail.security.cta.reset':                     'पासवर्ड रीसेट करें',
  'agent.playerDetail.security.cta.resetting':                 'रीसेट हो रहा है…',
  'agent.playerDetail.ledger.title':                           'वॉलेट लेजर',
  'agent.playerDetail.ledger.entries.one':                     '{count} एंट्री',
  'agent.playerDetail.ledger.entries.other':                   '{count} एंट्रीज़',
  'agent.playerDetail.ledger.empty':                           'अभी तक कोई लेन-देन नहीं।',
  'agent.playerDetail.ledger.loading':                         'लोड हो रहा है…',
  'agent.playerDetail.ledger.actorSuffix':                     ' · एडमिन #{id} द्वारा',
  'agent.playerDetail.ledger.pagination.prev':                 'पिछला',
  'agent.playerDetail.ledger.pagination.next':                 'अगला',
  'agent.playerDetail.ledger.kind.bet':                        'मैच बेट',
  'agent.playerDetail.ledger.kind.win':                        'मैच इनाम',
  'agent.playerDetail.ledger.kind.refund':                     'रिफ़ंड',
  'agent.playerDetail.ledger.kind.fee':                        'प्लेटफ़ॉर्म फ़ीस',
  'agent.playerDetail.ledger.kind.adminCredit':                'एडमिन क्रेडिट',
  'agent.playerDetail.ledger.kind.adminDebit':                 'एडमिन डेबिट',
  'agent.playerDetail.ledger.kind.recharge':                   'रिचार्ज',
  'agent.playerDetail.ledger.kind.referralBonus':              'रेफ़रल बोनस',
  'agent.playerDetail.ledger.kind.signupBonus':                'साइनअप बोनस',
  'agent.playerDetail.ledger.kind.offerCredit':                'ऑफ़र क्रेडिट',
  'agent.playerDetail.ledger.kind.withdrawalHold':             'निकासी',
  'agent.playerDetail.ledger.kind.withdrawalRefund':           'रिफ़ंड',
  'agent.playerDetail.ledger.game.platform':                   'प्लेटफ़ॉर्म',
  'agent.playerDetail.profile.title':                          'प्रोफ़ाइल संपादित करें',
  'agent.playerDetail.profile.field.username':                 'यूज़रनेम',
  'agent.playerDetail.profile.field.email':                    'ईमेल',
  'agent.playerDetail.profile.field.phone':                    'फ़ोन',
  'agent.playerDetail.profile.field.firstName':                'पहला नाम',
  'agent.playerDetail.profile.field.lastName':                 'अंतिम नाम',
  'agent.playerDetail.profile.field.dob':                      'जन्म तारीख़',
  'agent.playerDetail.profile.field.gender':                   'लिंग',
  'agent.playerDetail.profile.gender.unspecified':             '—',
  'agent.playerDetail.profile.gender.male':                    'पुरुष',
  'agent.playerDetail.profile.gender.female':                  'महिला',
  'agent.playerDetail.profile.gender.other':                   'अन्य',
  'agent.playerDetail.profile.gender.preferNot':               'बताना नहीं चाहते',
  'agent.playerDetail.profile.location.title':                 'स्थान',
  'agent.playerDetail.profile.location.lastUpdated':           '· अंतिम अपडेट {date}',
  'agent.playerDetail.profile.location.legacy.label':          'फ़ाइल पर पुराना पता',
  'agent.playerDetail.profile.location.legacy.note':           ' (केवल पढ़ने योग्य — pre-R3, लोकेटर द्वारा इस्तेमाल नहीं):',
  'agent.playerDetail.profile.toast.nothingToSave':            'सहेजने के लिए कुछ नहीं',
  'agent.playerDetail.profile.toast.saved.one': '{count} फ़ील्ड सहेजा गया',
  'agent.playerDetail.profile.toast.saved.other': '{count} फ़ील्ड सहेजे गए',
  'agent.playerDetail.profile.cta.save':                       'परिवर्तन सहेजें',
  'agent.playerDetail.profile.cta.saving':                     'सहेज रहे हैं…',
  'agent.playerDetail.profile.cta.reset':                      'रीसेट करें',
  'agent.playerDetail.ownership.title':                        'मालिक एजेंट',
  'agent.playerDetail.ownership.row.eyebrow':                  'स्वामित्व',
  'agent.playerDetail.ownership.platform.title':               'प्लेटफ़ॉर्म-डायरेक्ट',
  'agent.playerDetail.ownership.platform.body':                'कोई एजेंट इस खिलाड़ी का मालिक नहीं। एडमिन सीधे प्रबंधित करते हैं।',
  'agent.playerDetail.ownership.owned.fallbackOwner':          'एजेंट #{id}',
  'agent.playerDetail.ownership.owned.body':                   'इस खिलाड़ी के रिचार्ज + मैचों पर कमीशन कमाता है।',
  'agent.playerDetail.ownership.cta.change':                   'बदलें',
  'agent.playerDetail.ownership.toast.changedTo':              'स्वामित्व → {label}',
  'agent.playerDetail.ownership.platformLabel':                'प्लेटफ़ॉर्म-डायरेक्ट',
  'agent.playerDetail.ownership.picker.title':                 '@{username} को फिर असाइन करें',
  'agent.playerDetail.ownership.picker.body':                  'नया एजेंट चुनें — या प्लेटफ़ॉर्म-डायरेक्ट पर अलग करें (कोई एजेंट मालिक नहीं)। भविष्य के कमीशन नए मालिक के पास जाएँगे; पहले से क्रेडिट पंक्तियाँ नहीं छुई जातीं।',
  'agent.playerDetail.ownership.picker.detach.title':          'प्लेटफ़ॉर्म पर अलग करें',
  'agent.playerDetail.ownership.picker.detach.body':           'एजेंट स्वामित्व हटाएँ। एडमिन सीधे प्रबंधित करते हैं।',
  'agent.playerDetail.ownership.picker.search.placeholder':    'एजेंट खोजें…',
  'agent.playerDetail.ownership.picker.loading':               'एजेंट लोड हो रहे हैं…',
  'agent.playerDetail.ownership.picker.empty.search':          'आपकी खोज से कोई एजेंट मेल नहीं खाता।',
  'agent.playerDetail.ownership.picker.empty.none':            'अभी तक कोई सक्रिय एजेंट नहीं।',
  'agent.playerDetail.ownership.picker.players.one':           '{count} खिलाड़ी',
  'agent.playerDetail.ownership.picker.players.other':         '{count} खिलाड़ी',
  'agent.playerDetail.ownership.picker.current':               'वर्तमान',
  'agent.playerDetail.ownership.picker.currentUpper':          'वर्तमान',
  'agent.playerDetail.ownership.picker.close':                 'बंद करें',

  // ── Agent login page (Group 9) ────────────────────────────────────
  'agent.login.brand':                    'बाली एजेंट',
  'agent.login.subtitle': 'अपने खिलाड़ियों को प्रबंधित करने के लिए साइन इन करें',
  'agent.login.field.identifier':         'यूज़रनेम या ईमेल',
  'agent.login.field.password':           'पासवर्ड',
  'agent.login.disclaimer.before': 'केवल एजेंट के लिए। अगर आप प्लेटफ़ॉर्म के स्वामी हैं, तो इस्तेमाल करें ',
  'agent.login.disclaimer.link':          '/admin/login',
  'agent.login.disclaimer.after':         '।',
  'agent.login.error.loginFailed':        'लॉगिन विफल',
  'agent.login.error.invalidCredentials': 'ग़लत क्रेडेंशियल',
  'agent.login.error.accountSuspended': 'खाता निलंबित — अपने एडमिन से संपर्क करें।',
  'agent.login.error.networkError': 'नेटवर्क त्रुटि — अपना कनेक्शन जाँचें और फिर से कोशिश करें।',

  // ── Long-tail sweep (Group 10) ────────────────────────────────────
  'agent.misc.gameTile.fallbackTag':    'गेम',
  'agent.misc.gameTile.actionSuffix':   '· कॉन्फ़िगर',
};

const bn: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "আমার তথ্য",
  'profile.group.security': "সাইন-ইন ও নিরাপত্তা",
  'profile.personal.title': "ব্যক্তিগত তথ্য",
  'profile.personal.editTitle': "ব্যক্তিগত তথ্য সম্পাদনা",
  'common.edit': "সম্পাদনা",
  'profile.field.name': "নাম",
  'profile.field.phone': "ফোন নম্বর",
  'profile.field.gender': "লিঙ্গ",
  'profile.field.location': "অবস্থান",
  'profile.gender.male': "পুরুষ",
  'profile.gender.female': "মহিলা",
  'profile.gender.other': "অন্যান্য",
  'profile.gender.preferNot': "বলতে চাই না",
  'profile.gender.select': "নির্বাচন করুন…",
  'profile.location.optionalHint': "ঐচ্ছিক — আপনার এলাকা বেছে নিন বা পিনকোড লিখুন (GPS আরও নির্ভুল করে তোলে)।",
  'profile.lang.title': "ভাষা",
  'profile.password.title': "পাসওয়ার্ড",
  'profile.password.desc': "আপনার অ্যাকাউন্টের পাসওয়ার্ড পরিবর্তন করুন। অন্য ডিভাইসগুলো সাইন-আউট হয়ে যাবে।",
  'profile.password.cta': "পরিবর্তন",
  'profile.password.editTitle': "পাসওয়ার্ড পরিবর্তন",
  'profile.password.current': "বর্তমান পাসওয়ার্ড",
  'profile.password.new': "নতুন পাসওয়ার্ড",
  'profile.password.confirm': "নতুন পাসওয়ার্ড নিশ্চিত করুন",
  'profile.password.currentPh': "আপনার বর্তমান পাসওয়ার্ড",
  'profile.password.newPh': "একটি শক্তিশালী পাসওয়ার্ড বেছে নিন",
  'profile.password.confirmPh': "নতুন পাসওয়ার্ডটি আবার লিখুন",
  'profile.password.rule': "কমপক্ষে ৮টি অক্ষর, একটি বর্ণ ও একটি সংখ্যা সহ।",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "লিঙ্ক করা হয়েছে",
  'profile.telegram.otpHere': "পাসওয়ার্ড রিসেট OTP এখানে আসবে।",
  'profile.telegram.unlinkChange': "আনলিঙ্ক ও পরিবর্তন",
  'profile.telegram.unlinkTitle': "Telegram আনলিঙ্ক করবেন?",
  'profile.telegram.unlink': "আনলিঙ্ক",
  'profile.telegram.keepLinked': "লিঙ্ক রাখুন",
  'profile.telegram.unlinkVerified': "পরে আপনি অন্য একটি Telegram অ্যাকাউন্ট আবার লিঙ্ক করতে পারবেন। ইমেল যাচাইকরণ অক্ষুণ্ণ থাকবে।",
  'profile.telegram.unlinkOnly': "Telegram আপনার একমাত্র যাচাই করা চ্যানেল — খেলা চালিয়ে যাওয়ার আগে আপনাকে অ্যাকাউন্ট আবার যাচাই করতে হতে পারে।",
  'profile.telegram.notLinkedDesc': "ব্যাকআপ হিসেবে পাসওয়ার্ড রিসেট OTP পেতে Telegram লিঙ্ক করুন।",
  'profile.telegram.openBot': "@BaaliGames_bot খুলুন",
  'profile.telegram.generate': "লিঙ্ক তৈরি করুন",
  'profile.telegram.generating': "তৈরি হচ্ছে…",
  'profile.avatar.cropTitle': "আপনার ছবি সমন্বয় করুন",
  'profile.toast.detailsSaved': "তথ্য সংরক্ষিত হয়েছে",
  'profile.toast.photoUpdated': "ছবি আপডেট হয়েছে",
  'profile.toast.pwChanged': "পাসওয়ার্ড পরিবর্তিত হয়েছে। অন্য ডিভাইসগুলো সাইন-আউট হয়ে গেছে।",
  'profile.toast.tgUnlinked': "Telegram আনলিঙ্ক হয়েছে। অন্য একটি চ্যাট সংযুক্ত করতে নতুন লিঙ্ক তৈরি করুন।",
  'profile.err.phone': "১০ সংখ্যার মোবাইল নম্বর লিখুন (অথবা অপরিবর্তিত রাখুন)",
  'profile.err.pwMismatch': "নতুন পাসওয়ার্ড মিলছে না",
  'profile.err.pwWeak': "পাসওয়ার্ডে কমপক্ষে ৮টি অক্ষর, একটি বর্ণ ও একটি সংখ্যা থাকতে হবে",
  'profile.err.pwSame': "নতুন পাসওয়ার্ড বর্তমানটির থেকে আলাদা হতে হবে",
  'wallet.payout.title': "সংরক্ষিত পেআউট তথ্য",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "এখনো কিছু সংরক্ষিত নেই",
  'wallet.payout.hint': "টাকা তোলার সময় আপনার UPI ID স্বয়ংক্রিয়ভাবে সংরক্ষিত হয়। পরবর্তী উত্তোলনের অনুরোধের সময় আপনি এটি আপডেট করতে পারবেন।",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ইনস্টল করুন",
  "pwa.install.subhint": "তাৎক্ষণিক, ফুল-স্ক্রিন অ্যাক্সেসের জন্য এটি আপনার হোম স্ক্রিনে যোগ করুন — কোনো অ্যাপ স্টোর লাগবে না।",
  "pwa.install.listen": "শুনুন",
  "pwa.install.listening": "থামুন",
  "pwa.install.installBtn": "ইনস্টল করুন",
  "pwa.install.notNow": "এখন নয়",
  "pwa.install.howToShow": "কীভাবে ইনস্টল করবেন",
  "pwa.install.howToHide": "ধাপগুলি লুকান",
  "pwa.install.guide.iosSafari": "Safari স্ক্রিনের নিচে, Share বোতামে ট্যাপ করুন — এটি উপরের দিকে তীরযুক্ত একটি বর্গক্ষেত্রের মতো দেখায়।\nযে মেনু খোলে তাতে একটু নিচে স্ক্রোল করুন।\n\"Add to Home Screen\" অপশনে ট্যাপ করুন।\nউপরের ডান কোণে Add-এ ট্যাপ করুন। এখন অ্যাপটি যেকোনো সাধারণ অ্যাপের মতো আপনার হোম স্ক্রিনে থাকবে।",
  "pwa.install.guide.iosOther": "iPhone বা iPad-এ, হোম স্ক্রিনে যোগ করা শুধুমাত্র Safari ব্রাউজারে কাজ করে।\nSafari খুলুন এবং এই একই ওয়েবসাইটে যান।\nSafari-এর নিচে Share বোতামে ট্যাপ করুন — উপরের দিকে তীরযুক্ত একটি বর্গক্ষেত্র।\nনিচে স্ক্রোল করুন, \"Add to Home Screen\"-এ ট্যাপ করুন, তারপর Add-এ ট্যাপ করুন।",
  "pwa.install.guide.android": "উপরের Install বোতামে ট্যাপ করুন।\nআপনার ব্রাউজার জিজ্ঞাসা করলে, নিশ্চিত করতে Install-এ ট্যাপ করুন।\nহয়ে গেছে — অ্যাপটি এখন আপনার হোম স্ক্রিনে। যেকোনো সাধারণ অ্যাপের মতো এটি যেকোনো সময় খুলুন।\nপপ-আপ দেখছেন না? আপনার ব্রাউজারের মেনু খুলুন — তিনটি বিন্দু, সাধারণত উপরের ডানদিকে — এবং \"Install app\"-এ ট্যাপ করুন।",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "সরাসরি রিচার্জ",
  "agent.directRecharge.subtitle.player": "একজন খেলোয়াড় বেছে নিন এবং আপনার পুল থেকে রিচার্জ করুন। কোনো অনুরোধ লাগবে না।",
  "agent.directRecharge.subtitle.subagent": "একজন সাব-এজেন্ট বেছে নিন এবং আপনার পুল থেকে তাদের পুলে রিচার্জ করুন।",
  "agent.directRecharge.search.player": "রিচার্জের জন্য খেলোয়াড় খুঁজুন (নাম · ফোন · ইমেল)…",
  "agent.directRecharge.search.subagent": "রিচার্জের জন্য সাব-এজেন্ট খুঁজুন (নাম · ইমেল · UAID)…",
  "agent.directRecharge.clearSearch": "অনুসন্ধান সাফ করুন",
  "agent.directRecharge.searching": "খোঁজা হচ্ছে…",
  "agent.directRecharge.noMatches": "কোনো মিল নেই।",
  "agent.directRecharge.empty.player": "আপনার তালিকায় এখনো কোনো খেলোয়াড় নেই।",
  "agent.directRecharge.empty.subagent": "এখনো কোনো সরাসরি সাব-এজেন্ট নেই।",
  "agent.directRecharge.searchFailed": "অনুসন্ধান ব্যর্থ",
  "agent.directRecharge.label.wallet": "ওয়ালেট",
  "agent.directRecharge.label.pool": "পুল",
  "agent.directRecharge.modal.title.player": "সরাসরি খেলোয়াড় রিচার্জ",
  "agent.directRecharge.modal.title.subagent": "সরাসরি সাব-এজেন্ট রিচার্জ",
  "agent.directRecharge.modal.close": "বন্ধ করুন",
  "agent.directRecharge.modal.targetPool": "লক্ষ্য পুল",
  "agent.directRecharge.modal.operatingWallet": "অপারেটিং ওয়ালেট",
  "agent.directRecharge.modal.dayToDay": "দৈনন্দিন ব্যালেন্স",
  "agent.directRecharge.modal.securityDeposit": "সিকিউরিটি ডিপোজিট",
  "agent.directRecharge.modal.collateral": "জামানত পুল",
  "agent.directRecharge.modal.amount": "পরিমাণ (₹)",
  "agent.directRecharge.modal.overdraw": "আপনি আপনার পুলের (₹{pool}) চেয়ে বেশি দিতে পারবেন না। পরিমাণ কমান।",
  "agent.directRecharge.modal.reason": "কারণ / নোট",
  "agent.directRecharge.modal.reasonPlaceholder": "নগদ সংগৃহীত · ref 4291",
  "agent.directRecharge.modal.reasonHint": "আপনার ওয়ালেট লেজার + অডিট লগে লেখা হয়।",
  "agent.directRecharge.modal.proofLabel": "পেমেন্টের স্ক্রিনশট আপলোড করুন (ঐচ্ছিক)",
  "agent.directRecharge.modal.proofHint": "ইতিহাসের মাধ্যমে প্রাপকের কাছে দৃশ্যমান।",
  "agent.directRecharge.modal.previewEmpty": "লেনদেন প্রিভিউ করতে একটি পরিমাণ লিখুন।",
  "agent.directRecharge.modal.previewBody": "আপনার ওয়ালেট থেকে {name}-এর {pool}-এ পাঠানো হয়েছে। নিরাপদ স্থানান্তর; লেজার + অডিটে রেকর্ড করা হয়েছে।",
  "agent.directRecharge.modal.pool.playerWallet": "ওয়ালেট",
  "agent.directRecharge.modal.pool.operatingWallet": "অপারেটিং ওয়ালেট",
  "agent.directRecharge.modal.pool.securityDeposit": "সিকিউরিটি ডিপোজিট",
  "agent.directRecharge.modal.cancel": "বাতিল",
  "agent.directRecharge.modal.send": "₹{amount} পাঠান",
  "agent.directRecharge.badge.player": "খেলোয়াড়",
  "agent.directRecharge.badge.subagent": "সাব-এজেন্ট",
  "agent.directRecharge.modal.yourDirectSubagent": "আপনার সরাসরি সাব-এজেন্ট",
  "agent.directRecharge.modal.fundingSource": "তহবিলের উৎস",
  "agent.directRecharge.modal.you": "(আপনি)",
  "agent.directRecharge.modal.yourPool": "আপনার পুল",
  "agent.directRecharge.modal.notSignedIn": "এজেন্ট হিসেবে সাইন ইন করা নেই",
  "agent.directRecharge.modal.failed": "ব্যর্থ",
  'common.signIn': 'সাইন ইন করুন', 'common.signUp': 'সাইন আপ করুন',
  'common.login': 'লগইন', 'common.logout': 'লগ আউট', 'common.continue': 'চালিয়ে যান',
  'common.cancel': 'বাতিল', 'common.save': 'সংরক্ষণ', 'common.update': 'আপডেট',
  'common.clear': 'মুছুন', 'common.back': 'ফিরে যান', 'common.loading': 'লোড হচ্ছে…',
  'common.sending': 'পাঠানো হচ্ছে…', 'common.saving': 'সংরক্ষণ হচ্ছে…',
  'common.optional': 'ঐচ্ছিক', 'common.show': 'দেখান', 'common.hide': 'লুকান',
  'common.profile': 'প্রোফাইল', 'common.settings': 'সেটিংস',
  'nav.brand': 'বালি গেমস', 'nav.home': 'হোম', 'nav.games': 'গেমস',
  'nav.wallet': 'ওয়ালেট', 'nav.rewards': 'পুরস্কার', 'nav.profile': 'প্রোফাইল',
  'login.title': 'আবার স্বাগতম', 'login.subtitle': 'খেলা চালিয়ে যেতে সাইন ইন করুন।',
  'login.idLabel': 'ইউজারনেম, ইমেল বা ফোন',
  'login.idPlaceholder': 'ইউজারনেম, ইমেল বা মোবাইল',
  'login.passwordLabel': 'পাসওয়ার্ড', 'login.passwordPlaceholder': 'আপনার পাসওয়ার্ড লিখুন',
  'login.forgot': 'ভুলে গেছেন?', 'login.cta': 'সাইন ইন',
  'login.signingIn': 'সাইন ইন হচ্ছে…',
  'login.newHere': 'নতুন এখানে?', 'login.createAccount': 'অ্যাকাউন্ট তৈরি করুন',
  'register.title': 'আপনার অ্যাকাউন্ট তৈরি করুন', 'register.subtitle': 'একটি নাম বেছে নিন, ব্যাস।',
  'register.usernameLabel': 'ইউজারনেম', 'register.usernamePlaceholder': 'একটি অনন্য নাম বাছুন',
  'register.usernameHelp': 'অক্ষর, সংখ্যা এবং আন্ডারস্কোর। ৩–২৪ অক্ষর।',
  'register.emailLabel': 'ইমেল', 'register.emailPlaceholder': 'আপনার ইমেল ঠিকানা',
  'register.mobileLabel': 'মোবাইল নম্বর', 'register.mobilePlaceholder': '১০-অঙ্কের মোবাইল',
  'register.mobileHelp': 'আমরা দেশের কোড স্বয়ংক্রিয়ভাবে যোগ করব।',
  'register.passwordLabel': 'পাসওয়ার্ড', 'register.passwordPlaceholder': 'একটি শক্তিশালী পাসওয়ার্ড বাছুন',
  'register.passwordHelp': 'কমপক্ষে ৮ অক্ষর — একটি অক্ষর ও একটি সংখ্যা সহ।',
  'register.referralLabel': 'রেফারেল কোড', 'register.referralPlaceholder': 'বন্ধুর ইনভাইট কোড',
  'register.cta': 'অ্যাকাউন্ট তৈরি করুন', 'register.creating': 'তৈরি হচ্ছে…',
  'register.alreadyPlayer': 'ইতিমধ্যে খেলোয়াড়?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'আপনার সম্পর্কে', 'register.step.signin': 'সাইন-ইন', 'register.step.finish': 'শেষ',
  'register.step.aboutSubtitle': 'নিজের সম্পর্কে একটু বলুন', 'register.step.signinSubtitle': 'কীভাবে সাইন-ইন করবেন তা সেট করুন', 'register.step.finishSubtitle': 'একটি অবস্থান বেছে নিন এবং শেষ করুন',
  'register.firstName': 'প্রথম নাম', 'register.firstNamePh': 'আরভ',
  'register.lastName': 'শেষ নাম', 'register.lastNamePh': 'শর্মা',
  'register.dob': 'জন্ম তারিখ', 'register.dobHelp': 'খেলতে আপনার বয়স 18 বা তার বেশি হতে হবে',
  'register.v.required': 'প্রয়োজনীয়', 'register.v.nameMin': 'কমপক্ষে 2 অক্ষর',
  'register.v.tooLong': 'অনেক বড়', 'register.v.nameChars': 'শুধু অক্ষর, স্পেস ও হাইফেন',
  'register.v.invalidDate': 'অবৈধ তারিখ', 'register.v.minAge': 'আপনার বয়স কমপক্ষে 18 হতে হবে',
  'register.v.tooOld': 'অনুগ্রহ করে বছর চেক করুন',
  'register.v.usernameMin': 'কমপক্ষে 3 অক্ষর', 'register.v.usernameMax': 'অনেক বড় (সর্বোচ্চ 24)',
  'register.v.usernameChars': 'শুধু অক্ষর, সংখ্যা ও আন্ডারস্কোর', 'register.v.emailShape': 'অবৈধ ইমেল মনে হচ্ছে',
  'register.v.phoneDigits': '10 অঙ্কের নম্বর হতে হবে', 'register.v.phonePrefix': 'ভারতীয় মোবাইল 6–9 দিয়ে শুরু হয়',
  'register.v.pwdMin': 'কমপক্ষে 8 অক্ষর', 'register.v.pwdLetter': 'কমপক্ষে একটি অক্ষর থাকতে হবে',
  'register.v.pwdNumber': 'কমপক্ষে একটি সংখ্যা থাকতে হবে',
  'register.v.referralShort': 'অনেক ছোট', 'register.v.referralChars': 'শুধু অক্ষর ও সংখ্যা',
  'register.v.usernameTaken': 'এই ইউজারনেম ইতিমধ্যে নেওয়া হয়েছে',
  'register.v.emailTaken': 'এই ইমেল ইতিমধ্যে নিবন্ধিত',
  'register.v.phoneTaken': 'এই নম্বর ইতিমধ্যে নিবন্ধিত',
  'register.v.noLocation': 'চালিয়ে যেতে কমপক্ষে একটি অবস্থান পদ্ধতি শেয়ার করুন।',
  'register.welcome.title': 'স্বাগতম', 'register.welcome.subtitle': 'আপনার অ্যাকাউন্ট প্রস্তুত। এখন সুরক্ষিত করুন বা সরাসরি ঢুকুন?',
  'register.welcome.verifyTelegram': 'টেলিগ্রাম দিয়ে যাচাই করুন',
  'register.welcome.verifyExplain': '⚠ গুরুত্বপূর্ণ: এখনই টেলিগ্রাম দিয়ে যাচাই করুন। যাচাই ছাড়া, পাসওয়ার্ড ভুলে গেলে আপনি নিজে রিসেট করতে পারবেন না — কেবল আপনার এজেন্ট বা অ্যাডমিন রিসেট করতে পারবেন। পরে প্রোফাইল থেকেও যাচাই করতে পারেন।',
  'register.welcome.verifyHeads': 'এটা পরে কাজে লাগবে',
  'register.welcome.verifyConsequence': 'ইমেইল যাচাই এখন উপলব্ধ নয়। পরে পাসওয়ার্ড রিসেটের জন্য টেলিগ্রাম দিয়ে যাচাই করুন। না করলে পাসওয়ার্ড ভুলে গেলে কেবল আপনার এজেন্ট বা অ্যাডমিন রিসেট করতে পারবেন।',
  'register.welcome.continue': 'অ্যাপে চালিয়ে যান', 'register.welcome.continueLogin': 'চালিয়ে যেতে সাইন ইন করুন', 'register.welcome.useDifferent': 'অন্য অ্যাকাউন্ট ব্যবহার করুন',
  'register.geo.title': 'আপনি কোথা থেকে খেলছেন?', 'register.geo.subtitle': 'আমরা আপনাকে নিকটতম এজেন্টের সাথে যুক্ত করতে আপনার অবস্থান ব্যবহার করি।',
  'register.geo.showAlt': 'অবস্থান শেয়ার করতে পারছেন না? অন্য পদ্ধতি ব্যবহার করুন',
  'register.geo.hideAlt': 'অন্য পদ্ধতি লুকান', 'register.geo.captured': 'অবস্থান ক্যাপচার করা হয়েছে',
  'register.geo.capturedHint': 'আপনি এটি যেকোনো সময় প্রোফাইল থেকে পরিবর্তন করতে পারেন।',
  'register.geo.verifyNote': 'সাইন-আপের পরে, প্রোফাইল থেকে টেলিগ্রাম (পাসওয়ার্ড রিসেটের জন্য) বা ইমেল দিয়ে যেকোনো সময় যাচাই করতে পারবেন। যাচাইকরণ ঐচ্ছিক।',
  'verify.title': 'আপনার অ্যাকাউন্ট যাচাই করুন', 'verify.subtitle': 'একটি বিকল্প বেছে নিন — যথেষ্ট।',
  'verify.tabTelegram': 'টেলিগ্রাম', 'verify.tabEmail': 'ইমেল',
  'verify.emailSent': 'আমরা {email}-এ যাচাই লিঙ্ক পাঠিয়েছি।',
  'verify.emailHint': 'এই ডিভাইসেই খুলুন। দেখা যাচ্ছে না? স্প্যাম/প্রোমোশন দেখুন।',
  'verify.telegramOpen': '@BaaliGames_bot খুলুন',
  'verify.telegramExpiry': 'লিঙ্ক ৩০ মিনিটে শেষ। টেলিগ্রামে একবার Start চাপতে হবে।',
  'verify.goToLogin': 'লগইনে যান',
  'verify.email.title': 'ইমেল যাচাই হয়েছে', 'verify.email.sub': 'আপনার অ্যাকাউন্ট সক্রিয়। খেলার সময়।',
  'verify.email.failed': 'যাচাই ব্যর্থ', 'verify.email.verifying': 'যাচাই হচ্ছে…',
  'verify.backRegister': 'রেজিস্টারে ফিরে যান',
  'forgot.title': 'অ্যাকাউন্ট পুনরুদ্ধার', 'forgot.subtitle': 'ফিরে আসার পথ পাঠাব।',
  'forgot.idLabel': 'ইউজারনেম, ইমেল বা ফোন', 'forgot.idPlaceholder': 'ইউজারনেম, ইমেল বা মোবাইল',
  'forgot.sendVia': 'কীভাবে পাঠাবো', 'forgot.email': 'ইমেল', 'forgot.telegram': 'টেলিগ্রাম OTP',
  'forgot.telegramHelp': 'টেলিগ্রাম তখনই কাজ করবে যখন আপনার অ্যাকাউন্ট আমাদের বটের সাথে লিঙ্ক করা।',
  'forgot.cta': 'পুনরুদ্ধার পাঠান',
  'forgot.sentEmail': 'অ্যাকাউন্ট থাকলে ইমেলে রিসেট লিঙ্ক পাঠানো হয়েছে।',
  'forgot.sentTelegram': 'অ্যাকাউন্ট থাকলে টেলিগ্রামে ৬-অঙ্কের কোড পাঠানো হয়েছে।',
  'forgot.redirecting': 'রিসেট পেজে নিয়ে যাচ্ছি…', 'forgot.back': 'লগইনে ফিরে যান',
  'reset.title': 'নতুন পাসওয়ার্ড সেট করুন',
  'reset.subOtp': 'টেলিগ্রামে পাঠানো কোডটি ব্যবহার করুন।',
  'reset.subLink': 'একটি নতুন পাসওয়ার্ড বেছে নিন এবং ফিরে আসুন।',
  'reset.otpLabel': '৬-অঙ্কের কোড', 'reset.newPasswordLabel': 'নতুন পাসওয়ার্ড',
  'reset.cta': 'পাসওয়ার্ড আপডেট', 'reset.updating': 'আপডেট হচ্ছে…',
  'reset.successTitle': 'পাসওয়ার্ড আপডেট হয়েছে', 'reset.successSub': 'প্রস্তুত। খেলার সময়।',
  'home.welcomeTo': 'স্বাগতম', 'home.heroSub': 'দক্ষতা-ভিত্তিক গেমিং হাব। খেলতে সাইন ইন করুন বা অ্যাকাউন্ট তৈরি করুন।',
  'home.skillBased': 'দক্ষতা-ভিত্তিক', 'home.skillBasedSub': 'কৌশল জেতে',
  'home.fairPlay': 'নিরপেক্ষ খেলা', 'home.fairPlaySub': 'অ্যান্টি-চিট চালু',
  'home.instantPayout': 'UPI পেআউট', 'home.instantPayoutSub': '২৪ ঘণ্টার মধ্যে',
  'home.featured': 'নির্বাচিত গেমস', 'home.tapToPlay': 'খেলতে ট্যাপ করুন',
  'home.ready': 'খেলার জন্য প্রস্তুত?', 'home.dropIn': 'অংশ নিন এবং সত্যিকারের পুরস্কার জিতুন।',
  'home.start': 'শুরু',
  'home.welcomeBack': 'আবার স্বাগতম', 'home.balance': 'আপনার ব্যালেন্স', 'home.live': 'লাইভ',
  'home.add': 'যোগ করুন', 'home.manage': 'পরিচালনা', 'home.rewards': 'পুরস্কার',
  'home.todayChallenge': 'আজকের চ্যালেঞ্জ', 'home.todayChallengeSub': 'একটি ম্যাচ জিতুন — স্ট্রিক বোনাস পান।',
  'home.view': 'দেখুন', 'home.yourGames': 'আপনার গেমস', 'home.viewAll': 'সব দেখুন',
  'profile.details': 'বিস্তারিত', 'profile.address': 'ঠিকানা', 'profile.languages': 'ভাষা',
  'profile.account': 'অ্যাকাউন্ট', 'profile.photo': 'ফটো', 'profile.preferred': 'পছন্দের ভাষা',
  'profile.lang.primary': 'প্রাথমিক ভাষা', 'profile.lang.primarySub': 'অ্যাপ-ব্যাপী ডিফল্ট',
  'profile.lang.locked': 'লক করা', 'profile.lang.second': 'দ্বিতীয় ভাষা',
  'profile.lang.pickHelp': 'আপনি যে ভাষাটি বলেন সেটি বাছুন। কার্ডে ট্যাপ করুন।',
  'profile.lang.saved': 'দ্বিতীয় ভাষা সংরক্ষিত', 'profile.lang.cleared': 'দ্বিতীয় ভাষা মুছে ফেলা হয়েছে',
  'lang.switcher.aria': 'ভাষা বাছুন', 'lang.switcher.title': 'আপনার ভাষা বেছে নিন',
  'lang.switcher.sub': 'পুরো অ্যাপ তৎক্ষণাৎ পরিবর্তন হবে।', 'lang.switcher.current': 'বর্তমান',
  'rewards.title': 'পুরস্কার ও অফার', 'rewards.subtitle': 'এখনই উপার্জন বা ক্লেমের জন্য বোনাস।',
  'rewards.tab.available': 'উপলব্ধ', 'rewards.tab.inReview': 'পর্যালোচনায়', 'rewards.tab.claimed': 'ক্লেম করা',
  'rewards.claim.cta': 'ক্লেম করুন', 'rewards.claimed.pending': 'পর্যালোচনায়',
  'rewards.empty.available.title': 'এখন কোনো অফার নেই',
  'rewards.toast.credited': 'বোনাস যুক্ত হয়েছে — {amount} আপনার ওয়ালেটে',
  'wallet.balance': 'ব্যালেন্স', 'wallet.addMoney': 'টাকা যোগ করুন', 'wallet.withdraw': 'উত্তোলন',
  'wallet.pending.title': 'মুলতুবি অনুরোধ', 'wallet.state.pending': 'মুলতুবি',
  'wallet.state.paid': 'প্রদান হয়েছে',
  'wallet.pending.txnPrefix': 'লেনদেন:',
  'wallet.pending.upload.label': 'দ্রুত পদক্ষেপের জন্য পেমেন্টের স্ক্রিনশট আপলোড করুন।',
  'wallet.pending.upload.hint':  'অনুরোধ তৈরির পর আপনার কাছে ২৪ ঘণ্টা আছে।',
  'wallet.pending.upload.failed': 'আপলোড ব্যর্থ',
  'wallet.activity.balanceAfter': 'ব্যালেন্স {balance}',
  'wallet.activity.window': 'গত {days} দিনের কার্যকলাপ দেখানো হচ্ছে',
  'wallet.activity.range': '{total}-এর মধ্যে {from}–{to}',
  'wallet.history.summary.one':   '{matches} ম্যাচ · জিতেছেন {wins} · খরচ {bets}',
  'wallet.history.summary.other': '{matches} ম্যাচ · জিতেছেন {wins} · খরচ {bets}',
  'wallet.history.summary.refunded': ' · ফেরত {refunds}',
  'ledger.kind.bet': 'ম্যাচ এন্ট্রি', 'ledger.kind.win': 'ম্যাচ পুরস্কার',
  'ledger.kind.refund': 'ম্যাচ ফেরত', 'ledger.kind.forfeit': 'ম্যাচ বাজেয়াপ্ত',
  'ledger.kind.fee': 'প্ল্যাটফর্ম ফি', 'ledger.kind.recharge': 'টপ-আপ',
  'ledger.kind.admin_credit': 'অ্যাডমিন ক্রেডিট', 'ledger.kind.admin_debit': 'অ্যাডমিন ডেবিট',
  'ledger.kind.referral_bonus': 'রেফারেল বোনাস', 'ledger.kind.signup_bonus': 'সাইন-আপ বোনাস',
  'ledger.kind.offer_credit': 'অফার পুরস্কার',
  'ledger.kind.withdrawal_hold': 'উত্তোলন হোল্ড', 'ledger.kind.withdrawal_refund': 'উত্তোলন ফেরত',
  'ledger.kind.agent_topup_in': 'এজেন্ট টপ-আপ', 'ledger.kind.agent_co_fund': 'এজেন্ট অবদান', 'ledger.kind.turnover_reward': 'ক্যাশব্যাক',
  'ledger.withdrawalPaid': 'উত্তোলন প্রদান হয়েছে', 'ledger.withdrawalPaidPartial': 'উত্তোলন প্রদান (আংশিক)',
  'ledger.withdrawalPaid.txn': 'প্রদান · লেনদেন {txnId}',
  'ledger.withdrawalRejected': 'উত্তোলন প্রত্যাখ্যাত', 'ledger.withdrawalRejected.note': 'হোল্ড করা অর্থ ফেরত',
  'ledger.withdrawalExpired': 'উত্তোলন মেয়াদোত্তীর্ণ', 'ledger.awaitingProof': 'প্রমাণের অপেক্ষায়',
  'proofPicker.button.compact': 'স্ক্রিনশট বেছে নিন', 'proofPicker.button.prominent': 'স্ক্রিনশট যোগ করুন',
  'proofPicker.processing': 'প্রক্রিয়াকরণ হচ্ছে…',
  'proofPicker.error.mime': 'শুধুমাত্র JPG, PNG বা WebP ছবি অনুমোদিত।',
  'proofPicker.error.size': 'ফাইল অনেক বড় — সর্বোচ্চ 5 MB।',
  'proofPicker.error.processing': 'ছবি প্রক্রিয়া করা যায়নি',
  'proofPicker.previewAlt': 'নির্বাচিত স্ক্রিনশট পূর্বরূপ',
  'proofViewer.state.none': 'স্ক্রিনশটের অপেক্ষায়', 'proofViewer.state.available': 'স্ক্রিনশট দেখুন',
  'proofViewer.state.expired': 'স্ক্রিনশট আর উপলব্ধ নয়', 'proofViewer.state.viewLimit': 'দেখার সীমা পূর্ণ',
  'proofViewer.loading': 'লোড হচ্ছে…',
  'proofViewer.title.viewLimit': 'আপনি এই স্ক্রিনশট সর্বোচ্চ ২ বার খুলেছেন',
  'proofViewer.error.load': 'স্ক্রিনশট লোড করা যায়নি',
  'proofViewer.lastView': 'শেষবার — ছবি শীঘ্রই মেয়াদোত্তীর্ণ হবে',
  'proofViewer.viewsRemaining.one': 'আর {n} বার দেখা যাবে',
  'proofViewer.viewsRemaining.other': 'আর {n} বার দেখা যাবে',
  'proofViewer.imageAlt': 'পেমেন্ট প্রমাণ', 'proofViewer.close': 'বন্ধ করুন',
  'recharge.title': 'টাকা যোগ করুন', 'recharge.step1.amount': 'পরিমাণ',
  'recharge.step3.txnId': 'UPI লেনদেন ID', 'recharge.step3.submit': 'পর্যালোচনার জন্য পাঠান',
  'recharge.success.title': 'পাঠানো হয়েছে — পর্যালোচনার অপেক্ষায়',
  'withdraw.title': 'উত্তোলন', 'withdraw.amount': 'পরিমাণ', 'withdraw.upiId': 'আপনার UPI ID',
  'withdraw.submit': 'পাঠান', 'withdraw.success.title': 'পাঠানো হয়েছে — অর্থ প্রদানের অপেক্ষায়',
  'payment.openUpiApp': 'এখনই পরিশোধ করুন', 'payment.upi.copy': 'কপি',
  'common.next': 'পরবর্তী', 'common.close': 'বন্ধ',

  // Location capture (Phase R3)
  'geo.purpose': 'দ্রুত ও মসৃণ পেমেন্ট সেবার জন্য সঠিক অবস্থান ও যোগাযোগ নম্বর দিন।',
  'geo.change': 'পরিবর্তন',
  'geo.gps.button': 'আমার বর্তমান অবস্থান ব্যবহার করুন',
  'geo.gps.benefit': 'দ্রুততম — এক ট্যাপ, টাইপ নেই।',
  'geo.gps.denied': 'অবস্থানের অনুমতি অস্বীকৃত। পিনকোড দিন বা নিচে বেছে নিন।',
  'geo.gps.failed': 'অবস্থান পাওয়া যায়নি। পিনকোড চেষ্টা করুন।',
  'geo.gps.notResolved': 'আপনার অবস্থান কোনও এলাকায় মেলেনি। অনুগ্রহ করে বেছে নিন।',
  'geo.pincode.label': 'পিনকোড',
  'geo.pincode.notFound': 'পিনকোড পাওয়া যায়নি। অনুগ্রহ করে এলাকা বেছে নিন।',
  'geo.pincode.lookupFailed': 'পিনকোড অনুসন্ধান ব্যর্থ। অনুগ্রহ করে বেছে নিন।',
  'geo.multipleAreas.label': 'একই পিনকোডে একাধিক এলাকা — আপনারটি বেছে নিন',
  'geo.multipleAreas.placeholder': 'আপনার এলাকা নির্বাচন করুন',
  'geo.manual.title': 'ম্যানুয়ালি বেছে নিন',
  'geo.manual.close': 'ম্যানুয়াল লুকান',
  'geo.state.label': 'রাজ্য', 'geo.state.placeholder': 'রাজ্য নির্বাচন করুন',
  'geo.district.label': 'জেলা', 'geo.district.placeholder': 'জেলা নির্বাচন করুন',
  'geo.area.label': 'এলাকা / শহর', 'geo.area.placeholder': 'এলাকা নির্বাচন করুন',
  'geo.privacy.note': 'শুধু স্থানীয় এজেন্ট দেওয়ার জন্য। শেয়ার হয় না।',
  'geo.required.hint': 'রেফারেল কোড না থাকলে অবস্থান প্রয়োজন।',

  // ════════════════════════════════════════════════════════════════════
  // AGENT APP — Bengali translations (Phase C Batch 2, Chunk 1 of 3).
  // Covers Phase B Groups 1-3: navigation, dashboard, emergency modals.

  // ── Navigation chrome ────────────────────────────────────────────
  'agent.nav.dashboard': 'ড্যাশবোর্ড',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'হিসাব',
  'agent.nav.players':   'খেলোয়াড়',
  'agent.nav.agents':    'এজেন্ট',
  'agent.nav.rewards':   'পুরস্কার',
  'agent.nav.profile':   'প্রোফাইল',
  'agent.bottomnav.home':    'হোম',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'হিসাব',
  'agent.bottomnav.players': 'খেলোয়াড়',
  'agent.bottomnav.agents':  'এজেন্ট',
  'agent.bottomnav.rewards': 'পুরস্কার',
  'agent.bottomnav.profile': 'প্রোফাইল',

  // ── Dashboard header + range toggle ──────────────────────────────
  'agent.dashboard.header.welcomePrefix':   'আবার স্বাগতম, ',
  'agent.dashboard.header.welcomeFallback': 'বন্ধু',
  'agent.dashboard.header.subtitle':        'আপনার ব্যবসা এক নজরে — পুল, ডিপোজিট, খেলোয়াড়, সাব-এজেন্ট, লাভ-ক্ষতি, এক্সপোজার',
  'agent.dashboard.range.today':            'আজ',
  'agent.dashboard.range.yesterday':        'গতকাল',
  'agent.dashboard.range.thisWeek':         'এই সপ্তাহে',
  'agent.dashboard.range.lastWeek':         'গত সপ্তাহে',
  'agent.dashboard.range.thisMonth':        'এই মাসে',
  'agent.dashboard.range.lastMonth':        'গত মাসে',
  'agent.dashboard.range.complete':         'সম্পূর্ণ',

  // ── Permanent deposit coverage bar ───────────────────────────────
  'agent.dashboard.depositBar.loading':           'ডিপোজিট কভারেজ লোড হচ্ছে…',
  'agent.dashboard.depositBar.label':             'ডিপোজিট কভারেজ',
  'agent.dashboard.depositBar.balanceOfTarget':   '· লক্ষ্য {target}-এর {balance}',
  'agent.dashboard.depositBar.tapToView':         'সম্পূর্ণ ডিপোজিট পেজ দেখতে ট্যাপ করুন।',
  'agent.dashboard.depositBar.caption.suspended': 'অ্যাকাউন্ট স্থগিত। এডমিনের সাথে যোগাযোগ করুন।',
  'agent.dashboard.depositBar.caption.breach':    'আপনার ওয়ালেট ডিপোজিট সীমার ১০০% খেয়েছে। উত্তোলন বন্ধ। অবিলম্বে টপ-আপ করুন।',
  'agent.dashboard.depositBar.caption.warning':   'আপনার ওয়ালেট ডিপোজিট সীমার {pct}%-এ। ৮০%-এ সতর্কবার্তা, ১০০%-এ লকডাউন।',
  'agent.dashboard.depositBar.caption.caution':   'আপনার ওয়ালেট ডিপোজিট কভারেজের {pct}% খেয়েছে। ৮০%-এর নিচে — এখনও নিরাপদ।',
  'agent.dashboard.depositBar.caption.safe':      'ডিপোজিট সম্পূর্ণ অক্ষত। কোনো কভারেজ ব্যবহৃত হচ্ছে না।',

  // ── Zero-deposit locator skip banner ─────────────────────────────
  'agent.dashboard.locatorSkip.title': 'সতর্কতা · শূন্য সিকিউরিটি ডিপোজিট',
  'agent.dashboard.locatorSkip.body':  'আপনার সিকিউরিটি ডিপোজিট {amount}। যতক্ষণ না আপনি এটি ফান্ড করেন ততক্ষণ ভৌগোলিক লোকেটর থেকে নতুন খেলোয়াড় পাবেন না। বিদ্যমান খেলোয়াড় + সরাসরি রেফারেল + ম্যানুয়াল অ্যাসাইনমেন্ট প্রভাবিত হবে না — শুধু অরগ্যানিক জিও-রাউটেড সাইনআপ আপনাকে এড়িয়ে যাবে।',
  'agent.dashboard.locatorSkip.cta':   'ডিপোজিট ফান্ড করুন',

  // ── Downline defaulter banner ────────────────────────────────────
  'agent.dashboard.defaulter.title':                  'ডাউনলাইন ডিফল্টার সতর্কতা',
  'agent.dashboard.defaulter.summary.critical.one':   'আপনার {n} সরাসরি সাব-এজেন্ট ডিফল্টে — তিনি লক বা ডিপোজিট সীমা ভেঙেছেন।',
  'agent.dashboard.defaulter.summary.critical.other': 'আপনার {n} সরাসরি সাব-এজেন্ট ডিফল্টে — তাঁরা লক বা ডিপোজিট সীমা ভেঙেছেন।',
  'agent.dashboard.defaulter.summary.warning.one':    'আপনার {n} সরাসরি সাব-এজেন্ট ডিপোজিট সীমার ৮০%+ — ডিফল্টার ঝুঁকি।',
  'agent.dashboard.defaulter.summary.warning.other':  'আপনার {n} সরাসরি সাব-এজেন্ট ডিপোজিট সীমার ৮০%+ — ডিফল্টার ঝুঁকি।',
  'agent.dashboard.defaulter.worst':                  'সবচেয়ে খারাপ: @{username} ডিপোজিট সীমার {pct}%-এ।',
  'agent.dashboard.defaulter.tapHint':                'সরাসরি সাব-এজেন্ট তালিকায় যেতে ট্যাপ করুন। ডিফল্টার সারিগুলি হাইলাইট করা।',

  // ── Margin-status pill labels ────────────────────────────────────
  'agent.dashboard.marginPill.locked':  'লক',
  'agent.dashboard.marginPill.caution': 'সাবধান',
  'agent.dashboard.marginPill.warning': 'সতর্কবার্তা',
  'agent.dashboard.marginPill.breach':  'সীমা ভঙ্গ',

  // ── Section titles + subtitles ───────────────────────────────────
  'agent.dashboard.sections.morning.title':     'সকালের সারসংক্ষেপ',
  'agent.dashboard.sections.morning.subtitle':  'লাইভ স্ন্যাপশট — প্রতিটি সংখ্যা স্বয়ংক্রিয়ভাবে রিফ্রেশ হয়',
  'agent.dashboard.sections.activity.title':    'কার্যকলাপ',
  'agent.dashboard.sections.activity.subtitle': 'সর্বাধিক খরচকারী এবং সাম্প্রতিক খেলোয়াড় পেমেন্ট',
  'agent.dashboard.sections.forensic.title':    'ফরেনসিক বিবরণ',
  'agent.dashboard.sections.forensic.subtitle': 'যেকোনো সেকশন বিস্তারিত দেখতে ট্যাপ করুন — পেজ শান্ত রাখতে ডিফল্টভাবে বন্ধ',

  // ── Accordion titles + helpers ───────────────────────────────────
  'agent.dashboard.accordions.pnlDeep.title':           'লাভ-ক্ষতির গভীর বিশ্লেষণ',
  'agent.dashboard.accordions.pnlDeep.helper':          'আপনার আয়ের প্রতি-পিলার + প্রতি-গেম বিশ্লেষণ। অন্তর্নিহিত ম্যাচ দেখতে গেম সারিতে ক্লিক করুন।',
  'agent.dashboard.accordions.leaderboard.title':       'ডাউনলাইন লিডারবোর্ড',
  'agent.dashboard.accordions.leaderboard.helper':      'আপনার সাব-এজেন্টরা নেট আয়ের ভিত্তিতে ক্রমে। ঋণাত্মক সংখ্যার মানে সেই সাব-এজেন্ট যা এনেছেন তার চেয়ে বেশি দিয়েছেন।',
  'agent.dashboard.accordions.activity.title':          'ডাউনলাইন কার্যকলাপ ফিড',
  'agent.dashboard.accordions.activity.helper':         'আপনার সাব-এজেন্টদের প্রতিটি ম্যাচ সেটলমেন্ট, রিচার্জ এবং রেট পরিবর্তন — ফরেনসিক টাইমলাইন।',
  'agent.dashboard.accordions.earnings.title':          'আয়ের বিবরণ (লাইফটাইম)',
  'agent.dashboard.accordions.earnings.helper':         'প্রকার অনুযায়ী লাইফটাইম মোট। উপরের সময় ফিল্টার উপেক্ষা করে — এগুলি কখনো পরিবর্তন হয় না।',
  'agent.dashboard.accordions.directSubAgents.title':   'সরাসরি সাব-এজেন্ট',
  'agent.dashboard.accordions.directSubAgents.helper':  'আপনার সরাসরি প্রতিনিধি। প্রত্যেকে নিজের খেলোয়াড় + নিজের সাব-এজেন্ট পরিচালনা করেন। গোলাপি/অ্যাম্বার রঙের সারিগুলি ডিফল্টার অঞ্চলে।',
  'agent.dashboard.accordions.chain.title':             'চেইন দৃশ্যমানতা',
  'agent.dashboard.accordions.chain.helper':            'আপনার সম্পূর্ণ ডাউনলাইন — সরাসরি (আপনি সম্পাদনা করেন) এবং পরোক্ষ (আপনার সাব-এজেন্টরা পরিচালনা করেন) ভাগে।',

  // ── Live-data footnote ───────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'লাইভ ডেটা — ফোকাসে, প্রতি ৬০ সেকেন্ডে, এবং টাকা চলাচলে রিফ্রেশ হয়।',

  // ── Card: Pool balance ───────────────────────────────────────────
  'agent.dashboard.cards.poolBalance.label':       'আমার পুল ব্যালেন্স',
  'agent.dashboard.cards.poolBalance.helper':      'আপনার ব্যয়যোগ্য ব্যালেন্স। খেলোয়াড়দের রিচার্জ বা সাব-এজেন্টকে পাঠানোর জন্য উপলব্ধ অর্থ।',
  'agent.dashboard.cards.poolBalance.netInRange':  '{range}-এ নেট {value}',

  // ── Card: Deposit status ─────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading':           'সিকিউরিটি ডিপোজিট',
  'agent.dashboard.cards.depositStatus.titleLoaded':            'ডিপোজিট স্থিতি',
  'agent.dashboard.cards.depositStatus.helperLoading':          'ডিপোজিট স্থিতি লোড হচ্ছে…',
  'agent.dashboard.cards.depositStatus.helper':                 'আপনার পুলের জন্য জামানত। লক্ষ্যের ৫০%-এ সাবধান, ৮০%-এ সতর্কবার্তা, ১০০%-এ সীমা ভঙ্গ-এ স্থিতি পরিবর্তিত হয়।',
  'agent.dashboard.cards.depositStatus.suspendedSince':         '{when} থেকে',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'চলতি ব্যালেন্স',
  'agent.dashboard.cards.depositStatus.field.depositBalance':   'ডিপোজিট ব্যালেন্স',
  'agent.dashboard.cards.depositStatus.field.depositTarget':    'ডিপোজিট লক্ষ্য',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ঋণাত্মক এক্সপোজার',
  'agent.dashboard.cards.depositStatus.field.coverageRatio':    'কভারেজ অনুপাত',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint':'ঋণাত্মক এক্সপোজার দ্বারা ডিপোজিট লক্ষ্যের এই অংশ ব্যবহৃত',
  'agent.dashboard.depositStatus.status.safe':      'নিরাপদ',
  'agent.dashboard.depositStatus.status.caution':   'সাবধান',
  'agent.dashboard.depositStatus.status.warning':   'সতর্কবার্তা',
  'agent.dashboard.depositStatus.status.breach':    'সীমা ভঙ্গ',
  'agent.dashboard.depositStatus.status.suspended': 'স্থগিত',
  'agent.dashboard.depositStatus.status.healthy':   'সুস্থ',

  // ── Card: Players' wallet total ──────────────────────────────────
  'agent.dashboard.cards.playersWallet.title':      'খেলোয়াড়দের মোট ওয়ালেট',
  'agent.dashboard.cards.playersWallet.helper':     'এই মুহূর্তে আপনার প্রতিটি খেলোয়াড়ের ওয়ালেটের সম্মিলিত লাইভ ব্যালেন্স। আপনার সাথে সংযুক্ত সব wallet_points সারির সমষ্টি।',
  'agent.dashboard.cards.playersWallet.foot.one':   '{n} খেলোয়াড় · {active} সক্রিয়',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} খেলোয়াড় · {active} সক্রিয়',

  // ── Card: Sub-agents wallet + deposit ────────────────────────────
  'agent.dashboard.cards.subAgents.title':       'সাব-এজেন্ট ওয়ালেট + ডিপোজিট',
  'agent.dashboard.cards.subAgents.helper':      'আপনার সরাসরি সাব-এজেন্টদের সম্মিলিত পুল ব্যালেন্স এবং সিকিউরিটি ডিপোজিট। প্রতিটি সাব-এজেন্ট নিজের খেলোয়াড় + নিজের ডাউনলাইন পরিচালনা করেন।',
  'agent.dashboard.cards.subAgents.field.pool':    'পুল',
  'agent.dashboard.cards.subAgents.field.deposit': 'ডিপোজিট',
  'agent.dashboard.cards.subAgents.foot.one':      '{n} সরাসরি সাব-এজেন্ট',
  'agent.dashboard.cards.subAgents.foot.other':    '{n} সরাসরি সাব-এজেন্ট',

  // ── Card: My pending requests ────────────────────────────────────
  'agent.dashboard.cards.myPending.title':            'আমার মুলতুবি অনুরোধ',
  'agent.dashboard.cards.myPending.helper':           'অগ্রাধিকার ৩। এডমিনে আপনার নিজের জমা — এতে আপনি কাজ করতে পারবেন না, তাঁদের অপেক্ষায়।',
  'agent.dashboard.cards.myPending.recharge.one':     '{n} রিচার্জ এডমিনের অনুমোদনের অপেক্ষায়',
  'agent.dashboard.cards.myPending.recharge.other':   '{n} রিচার্জ এডমিনের অনুমোদনের অপেক্ষায়',
  'agent.dashboard.cards.myPending.withdrawal.one':   '{n} উত্তোলন এডমিনের অনুমোদনের অপেক্ষায়',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} উত্তোলন এডমিনের অনুমোদনের অপেক্ষায়',

  // ── Card: Players' pending requests ──────────────────────────────
  'agent.dashboard.cards.playersPending.title':            'খেলোয়াড়দের মুলতুবি অনুরোধ',
  'agent.dashboard.cards.playersPending.helper':           'অগ্রাধিকার ১। আপনার খেলোয়াড়রা আপনার অনুমোদন বা পেমেন্টের অপেক্ষায়। ব্যবস্থা নিতে ট্যাপ করুন।',
  'agent.dashboard.cards.playersPending.recharge.one':     '{n} রিচার্জ অনুমোদনের জন্য',
  'agent.dashboard.cards.playersPending.recharge.other':   '{n} রিচার্জ অনুমোদনের জন্য',
  'agent.dashboard.cards.playersPending.withdrawal.one':   '{n} উত্তোলন পেমেন্টের জন্য',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} উত্তোলন পেমেন্টের জন্য',

  // ── Card: Sub-agents' pending requests ───────────────────────────
  'agent.dashboard.cards.subAgentsPending.title':  'সাব-এজেন্টদের মুলতুবি অনুরোধ',
  'agent.dashboard.cards.subAgentsPending.helper': 'অগ্রাধিকার ২। আপনার সাব-এজেন্টরা (সম্পূর্ণ ডাউনলাইন) আপনার অনুমোদন বা পেমেন্টের অপেক্ষায়। ব্যবস্থা নিতে ট্যাপ করুন।',

  // ── Card: True Net P&L ───────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title':  'প্রকৃত নেট লাভ-ক্ষতি · {range}',
  'agent.dashboard.cards.trueNet.helper': 'এই সময়সীমায় আপনার আসল লাভ/ক্ষতি। প্ল্যাটফর্ম ফি ভাগ + উইন/লস ভাগ − অফার সহ-ফান্ডিং। প্রতি-গেম বিবরণের জন্য নিচে লাভ-ক্ষতির গভীর বিশ্লেষণ খুলুন।',
  'agent.dashboard.pillarChip.platformFee': 'প্ল্যাট',
  'agent.dashboard.pillarChip.winLoss':     'উই/লস',
  'agent.dashboard.pillarChip.offer':       'অফার',

  // ── Card: Offer utilization ──────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title':      'অফার ব্যবহার · {range}',
  'agent.dashboard.cards.rewardUtil.helper':     'এই সময়সীমায় আপনার খেলোয়াড়দের অফারের মাধ্যমে দেওয়া মোট পুরস্কার ₹। সেই খরচে আপনার অংশ দেখতে আপনার এক্সপোজার পেজের সাথে মিলান।',
  'agent.dashboard.cards.rewardUtil.foot.one':   '{n} দাবি জমা',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} দাবি জমা',

  // ── Activity row ─────────────────────────────────────────────────
  'agent.dashboard.activity.topSpenders.title':     'সর্বাধিক খরচকারী',
  'agent.dashboard.activity.topSpenders.helper':    '{range}-এ আপনার সর্বাধিক খরচকারী খেলোয়াড়।',
  'agent.dashboard.activity.topSpenders.empty':     'এই সময়সীমায় এখনো কোনো খরচ নেই।',
  'agent.dashboard.activity.recent.title':          'সাম্প্রতিক কার্যকলাপ',
  'agent.dashboard.activity.recent.helper':         'আপনার খেলোয়াড়দের পাঁচটি সাম্প্রতিক রিচার্জ ইভেন্ট।',
  'agent.dashboard.activity.recent.empty':          'কোনো সাম্প্রতিক কার্যকলাপ নেই।',
  'agent.dashboard.activity.recent.state.approved': 'অনুমোদিত',
  'agent.dashboard.activity.recent.state.rejected': 'প্রত্যাখ্যাত',
  'agent.dashboard.activity.recent.state.pending':  'মুলতুবি',

  // ── P&L deep-dive ────────────────────────────────────────────────
  'agent.dashboard.pnl.perGameLabel':         'প্রতি গেম · ব্যাখ্যার জন্য সারিতে ট্যাপ করুন',
  'agent.dashboard.pnl.noEarnings':           'এই সময়সীমায় কোনো আয় নেই।',
  'agent.dashboard.pnl.table.col.pillarGame': 'পিলার / গেম',
  'agent.dashboard.pnl.table.col.net':        'নেট',
  'agent.dashboard.pnl.table.col.matches':    'ম্যাচ',
  'agent.dashboard.pnl.ledgerRows.one':       '{n} লেজার সারি',
  'agent.dashboard.pnl.ledgerRows.other':     '{n} লেজার সারি',
  'agent.dashboard.pnl.pillar.platformFee':   'প্ল্যাটফর্ম ফি',
  'agent.dashboard.pnl.pillar.winLoss':       'উইন / লস',
  'agent.dashboard.pnl.pillar.offerReward':   'অফার পুরস্কার',

  // ── P&L drilldown modal ──────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel':      'সময়সীমা: {range} — ড্যাশবোর্ডের সময়সীমার কঠোর উপসেট',
  'agent.dashboard.pnlDrill.loading':          'ম্যাচ-স্তরের বিবরণ লোড হচ্ছে…',
  'agent.dashboard.pnlDrill.windowTotal':      'সময়সীমার মোট:',
  'agent.dashboard.pnlDrill.matchCount.one':   '{n} ম্যাচে',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} ম্যাচে',
  'agent.dashboard.pnlDrill.noContribution':   'এই সেলে কোনো ম্যাচ অবদান রাখেনি।',
  'agent.dashboard.pnlDrill.errorFallback':    'বিবরণ লোড করা যায়নি',
  'agent.dashboard.pnlDrill.table.col.match':  'ম্যাচ',
  'agent.dashboard.pnlDrill.table.col.role':   'ভূমিকা',
  'agent.dashboard.pnlDrill.table.col.base':   'বেস',
  'agent.dashboard.pnlDrill.table.col.rate':   'রেট',
  'agent.dashboard.pnlDrill.table.col.earned': 'অর্জিত',
  'agent.dashboard.pnlDrill.table.col.settled':'সেটল্ড',

  // ── Direct sub-agents accordion ──────────────────────────────────
  'agent.dashboard.directDownline.loading':            'লোড হচ্ছে…',
  'agent.dashboard.directDownline.empty':              'এখনো কোনো সরাসরি সাব-এজেন্ট নেই।',
  'agent.dashboard.directDownline.row.player.one':     '{n} খেলোয়াড়',
  'agent.dashboard.directDownline.row.player.other':   '{n} খেলোয়াড়',
  'agent.dashboard.directDownline.row.subAgent.one':   '{n} সাব-এজেন্ট',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} সাব-এজেন্ট',
  'agent.dashboard.directDownline.row.walletDeposit':  'ওয়ালেট {wallet} · ডিপোজিট {deposit}',
  'agent.dashboard.directDownline.footer':             '{range} সময়সীমার জন্য প্রকৃত নেট।',

  // ── 4-quadrant chain visibility ──────────────────────────────────
  'agent.dashboard.chain.loading':                  'লোড হচ্ছে…',
  'agent.dashboard.chain.directAgents.title':       'সরাসরি এজেন্ট',
  'agent.dashboard.chain.directAgents.subtitle':    'আপনার সরাসরি সাব-এজেন্ট — আপনি তাঁদের অনুরোধে কাজ করতে পারেন।',
  'agent.dashboard.chain.indirectAgents.title':     'পরোক্ষ এজেন্ট',
  'agent.dashboard.chain.indirectAgents.subtitle':  'আপনার সাব-এজেন্টদের দ্বারা পরিচালিত। আপনার জন্য শুধুমাত্র পঠনযোগ্য।',
  'agent.dashboard.chain.directPlayers.title':      'সরাসরি খেলোয়াড়',
  'agent.dashboard.chain.directPlayers.subtitle':   'আপনার খেলোয়াড় — আপনি তাঁদের রিচার্জ এবং উত্তোলনে কাজ করেন।',
  'agent.dashboard.chain.indirectPlayers.title':    'পরোক্ষ খেলোয়াড়',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'আপনার সাব-এজেন্টদের খেলোয়াড়। আপনার জন্য শুধুমাত্র পঠনযোগ্য।',
  'agent.dashboard.chain.field.walletTotal':        'মোট ওয়ালেট',
  'agent.dashboard.chain.field.depositTotal':       'মোট ডিপোজিট',
  'agent.dashboard.chain.field.avgPerHead':         'মাথাপিছু গড়',
  'agent.dashboard.chain.field.pendingRecharge':    'মুলতুবি রিচার্জ',
  'agent.dashboard.chain.field.pendingWithdrawal':  'মুলতুবি উত্তোলন',
  'agent.dashboard.chain.pendingBadge':             '{n} মুলতুবি',
  'agent.dashboard.chain.openInbox':                'ইনবক্স খুলুন',
  'agent.dashboard.chain.footer':                   'সরাসরি = আপনি নিজে যাঁদের পরিচালনা করেন। পরোক্ষ = আপনার সাব-এজেন্টদের দ্বারা পরিচালিত (আপনি সংখ্যা দেখেন কিন্তু তাঁদের মুলতুবি অনুরোধে কাজ করতে পারেন না — তা তাঁদের পেরেন্টের কাছে যায়)।',

  // ── ActivityPanel ────────────────────────────────────────────────
  'agent.activity.error.loadFailed':                    'লোড করতে ব্যর্থ',
  'agent.activity.filter.window':                       'সময়সীমা',
  'agent.activity.filter.kind':                         'প্রকার',
  'agent.activity.window.all':                          'সব',
  'agent.activity.kind.all':                            'সব',
  'agent.activity.kind.matches':                        'ম্যাচ',
  'agent.activity.kind.recharges':                      'রিচার্জ',
  'agent.activity.kind.rateChanges':                    'রেট পরিবর্তন',
  'agent.activity.loading':                             'লোড হচ্ছে…',
  'agent.activity.empty.title':                         'শান্ত',
  'agent.activity.empty.subtitle':                      '{window}-এ কোনো {kindWord}ইভেন্ট নেই।',
  'agent.activity.empty.kindWord.all':                  '',
  'agent.activity.empty.kindWord.match':                'ম্যাচ ',
  'agent.activity.empty.kindWord.recharge':             'রিচার্জ ',
  'agent.activity.empty.kindWord.partnership_change':   'পার্টনারশিপ পরিবর্তন ',
  'agent.activity.row.settlementFallback':              'সেটলমেন্ট',
  'agent.activity.row.earned':                          'অর্জন করলেন',
  'agent.activity.row.fromPillarOn':                    '{pillar} থেকে ',
  'agent.activity.row.pillar.platformFee':              'প্ল্যাটফর্ম ফি',
  'agent.activity.row.pillar.winLoss':                  'উইন/লস',
  'agent.activity.row.pillar.offerReward':              'অফার পুরস্কার',
  'agent.activity.row.recharge.prefix':                 'রিচার্জ',
  'agent.activity.row.recharge.of':                     'এর',
  'agent.activity.row.recharge.byUser':                 'ইউজার #{id} দ্বারা',
  'agent.activity.row.recharge.via':                    ' মাধ্যমে ',
  'agent.activity.row.state.approved':                  'অনুমোদিত',
  'agent.activity.row.state.rejected':                  'প্রত্যাখ্যাত',
  'agent.activity.row.state.pending':                   'মুলতুবি',
  'agent.activity.row.rateChange.prefix':               'রেট পরিবর্তন',
  'agent.activity.row.rateChange.dashOn':               '— {pillar}-এ ',
  'agent.activity.row.rateChange.cascade':              'ক্যাসকেড',

  // ── EarningsPanel ────────────────────────────────────────────────
  'agent.earnings.notAgent':                            'এই ভিউ শুধু এজেন্টদের জন্য।',
  'agent.earnings.stat.poolBalance':                    'পুল ব্যালেন্স',
  'agent.earnings.stat.totalIn':                        'মোট আয়',
  'agent.earnings.stat.totalOut':                       'মোট ব্যয়',
  'agent.earnings.section.in':                          'আয় (আগত)',
  'agent.earnings.section.out':                         'ব্যয় (নির্গত)',
  'agent.earnings.empty.in':                            'এখনো কোনো আয় নেই। আপনার খেলোয়াড়রা রিচার্জ বা খেললে অংশ এখানে দেখাবে।',
  'agent.earnings.empty.out':                           'এখনো কোনো ব্যয় নেই।',
  'agent.earnings.viewLedger':                          'সম্পূর্ণ লেজার দেখুন',
  'agent.earnings.kind.topupFromAdmin.label':           'এডমিন থেকে টপ-আপ',
  'agent.earnings.kind.topupFromAdmin.hint':            'এডমিন আপনার পুলে ক্রেডিট করেছেন',
  'agent.earnings.kind.topupToPlayer.label':            'খেলোয়াড়কে টপ-আপ',
  'agent.earnings.kind.topupToPlayer.hint':             'আপনি একজন খেলোয়াড়কে রিচার্জ করেছেন',
  'agent.earnings.kind.topupFromPlayer.label':          'খেলোয়াড়ের রিচার্জ',
  'agent.earnings.kind.topupFromPlayer.hint':           'আপনার খেলোয়াড়রা আপনার পুলে পেমেন্ট করেছেন',
  'agent.earnings.kind.revenueShare.label':             'রাজস্ব ভাগ',
  'agent.earnings.kind.revenueShare.hint':              'ম্যাচ থেকে প্ল্যাটফর্ম ফির অংশ',
  'agent.earnings.kind.coFundOffer.label':              'সহ-ফান্ডেড অফার',
  'agent.earnings.kind.coFundOffer.hint':               'অফার পেআউটে আপনার অংশ',
  'agent.earnings.kind.adminAdjust.label':              'এডমিন সমন্বয়',
  'agent.earnings.kind.adminAdjust.hint':               'ম্যানুয়াল সংশোধন',
  'agent.earnings.row.entry.one':                       'এন্ট্রি',
  'agent.earnings.row.entry.other':                     'এন্ট্রি',

  // ── LeaderboardPanel ─────────────────────────────────────────────
  'agent.leaderboard.filter.window':                'সময়সীমা',
  'agent.leaderboard.filter.pillar':                'পিলার',
  'agent.leaderboard.window.all':                   'সব',
  'agent.leaderboard.pillar.all':                   'সব পিলার',
  'agent.leaderboard.pillar.platformFee':           'প্ল্যাটফর্ম ফি',
  'agent.leaderboard.pillar.winLoss':               'উইন / লস',
  'agent.leaderboard.pillar.offerReward':           'অফার পুরস্কার',
  'agent.leaderboard.loading':                      'লোড হচ্ছে…',
  'agent.leaderboard.empty.title':                  'এখনো কোনো আয় নেই',
  'agent.leaderboard.empty.subtitle.admin':         '{window}-এ সব এজেন্টের মধ্যে কোনো সেটলমেন্ট কার্যকলাপ নেই।',
  'agent.leaderboard.empty.subtitle.downline':      '{window}-এ আপনার ডাউনলাইন থেকে কোনো আয় নেই। আপনার সাব-এজেন্টরা ম্যাচ চালালে এই বোর্ড ভরে যাবে।',
  'agent.leaderboard.row.matches.one':              '{n} ম্যাচ',
  'agent.leaderboard.row.matches.other':            '{n} ম্যাচ',
  'agent.leaderboard.footer':                       'আয় match_settlement_snapshots থেকে নেওয়া। ঋণাত্মক সংখ্যার মানে সেই এজেন্ট সময়সীমার মধ্যে যা এনেছেন তার চেয়ে বেশি দিয়েছেন।',
  'agent.leaderboard.chip.platformFee':             'প্ল্যাট',
  'agent.leaderboard.chip.winLoss':                 'উই/লস',
  'agent.leaderboard.chip.offer':                   'অফার',

  // ── MarginWarningModal ───────────────────────────────────────────
  'agent.warning.header.eyebrow':     'মার্জিন সতর্কবার্তা · ৮০% সীমা পৌঁছেছে',
  'agent.warning.header.title':       'লকআউট থেকে এক খারাপ সেটলমেন্ট দূরে।',
  'agent.warning.header.body':        'এখনো লক নয় — কিন্তু আপনার ডিপোজিট সুরক্ষা কভার ৮০%-এর বেশি ব্যবহৃত। এখনই টপ-আপ করুন এবং নিয়ন্ত্রণে থাকুন।',
  'agent.warning.header.dismissAria': 'বন্ধ করুন',
  'agent.warning.why.eyebrow':        'এটি কেন দেখাচ্ছে',
  'agent.warning.why.body':           'সাম্প্রতিক গেম সেটলমেন্ট আপনার ওয়ালেটকে ঋণাত্মক করেছে। আপনার {deposit} সিকিউরিটি ডিপোজিট সেই সুরক্ষা-জাল যা এটি শোষণ করে — এবং এখন তা ৮০%-এর বেশি ব্যবহৃত। আর একটু ঋণাত্মক এলে আপনার ওয়ালেট + ডিপোজিট শূন্যে নেমে যাবে এবং প্ল্যাটফর্ম অ্যাকাউন্ট লক করবে।',
  'agent.warning.why.reassurance':    'এই সতর্কবার্তা ইচ্ছাকৃতভাবে তাড়াতাড়ি জ্বলে — যখন আপনি এখনো সম্পূর্ণ অ্যাপ ব্যবহার করতে পারেন। লকআউট স্ক্রিন তখনই আসবে যখন আপনি ১০০% অতিক্রম করবেন, এবং একবার তা হলে অধিকাংশ ফিচার বন্ধ হয়ে যাবে। এখন ব্যবস্থা নেওয়া পরে ব্যবস্থা নেওয়ার চেয়ে অনেক কম বিঘ্নকর।',
  'agent.warning.gauge.label':        'লকআউটের আগে অবশিষ্ট স্থান',
  'agent.warning.gauge.suffix':       'বাকি',
  'agent.warning.gauge.dangerBadge':  'বিপদ অঞ্চলে {pct}%',
  'agent.warning.gauge.field.wallet': 'আপনার ওয়ালেট',
  'agent.warning.gauge.field.deposit':'আপনার ডিপোজিট',
  'agent.warning.ignore.eyebrow':         'আপনি এটি উপেক্ষা করলে এবং ১০০%-এ পৌঁছালে',
  'agent.warning.ignore.cardTitle':       'আপনি তৎক্ষণাৎ হারাবেন',
  'agent.warning.ignore.item.dashboard':       'আপনার ড্যাশবোর্ড, লাভ-ক্ষতি এবং লিডারবোর্ডে প্রবেশ',
  'agent.warning.ignore.item.pages':           'খেলোয়াড় এবং এজেন্ট পেজ',
  'agent.warning.ignore.item.manualTopup':     'আপনার পুল থেকে খেলোয়াড়দের ম্যানুয়াল টপ-আপ',
  'agent.warning.ignore.item.newWithdrawal':   'আপনার আপলাইনে নতুন উত্তোলন পাঠানো',
  'agent.warning.ignore.item.subAgents':       'সাব-এজেন্ট যোগ বা পরিচালনা',
  'agent.warning.ignore.item.profile':         'আপনার প্রোফাইল বা পেমেন্ট সেটিংস সম্পাদনা',
  'agent.warning.ignore.footer':               'আপনি এখনো মুলতুবি অনুরোধ অনুমোদন করতে পারবেন + একটি জরুরি টপ-আপ পাঠাতে পারবেন — কিন্তু অন্য সব বন্ধ থাকবে যতক্ষণ না আপনার ওয়ালেট + ডিপোজিট শূন্যের উপরে ওঠে।',
  'agent.warning.recoverEyebrow':              'পুনরুদ্ধারের জন্য এখনই আপনার আপলাইনে পেমেন্ট করুন',
  'agent.warning.dismiss.cta':                 'আমি এটি পরে দেখব',
  'agent.warning.dismiss.footnote':            'আপনি সতর্কতা অঞ্চলে থাকলে ১০ মিনিটে আবার মনে করিয়ে দেব। পুনরুদ্ধার হওয়ার সাথে সাথে স্বয়ংক্রিয়ভাবে বন্ধ হয়।',

  // ── EmergencyLockoutGate ─────────────────────────────────────────
  'agent.lockout.header.eyebrow': 'অ্যাকাউন্ট লক · বেইলআউট পোর্টাল',
  'agent.lockout.header.title':   'আপনার ডিপোজিট যা কভার করে তার চেয়ে বেশি আপনার বকেয়া।',
  'agent.lockout.header.body':    'চিন্তা করবেন না — এটি পুনরুদ্ধারযোগ্য। নিচের দুটি ধাপ অনুসরণ করুন এবং আপনি স্বয়ংক্রিয়ভাবে আনলক হবেন।',
  'agent.lockout.why.eyebrow':       'এটি কেন হলো',
  'agent.lockout.why.body':          'গেম সেটলমেন্ট আপনার ওয়ালেটকে শূন্যের নিচে নামিয়েছে। আপনার সিকিউরিটি ডিপোজিট সেই সুরক্ষা-জাল যা এটি শোষণ করে — কিন্তু আপনি এর ১০০% ব্যবহার করে ফেলেছেন। যতক্ষণ না আপনার ওয়ালেট ও ডিপোজিট আবার শূন্যের উপরে ওঠে, আপনি স্বাভাবিকভাবে কাজ করতে পারবেন না।',
  'agent.lockout.why.reassurance':   'প্ল্যাটফর্ম আপনাকে লক করে এই কারণে নয় যে আপনি কিছু ভুল করেছেন। স্থিতিশীল হওয়ার সময় নেটওয়ার্ক যাতে আপনার ডিপোজিটের বিরুদ্ধে আরও ঋণ জমা না করে তার জন্য এটি লক করে।',
  'agent.lockout.gauge.label':         'নিরাপদ পুনরুদ্ধার লক্ষ্য',
  'agent.lockout.gauge.suffix':        'নিরাপদে পেমেন্ট করার জন্য',
  'agent.lockout.gauge.recoveredBadge': '{pct}% পুনরুদ্ধার হয়েছে',
  'agent.lockout.gauge.field.wallet':  'আপনার ওয়ালেট',
  'agent.lockout.gauge.field.deposit': 'আপনার ডিপোজিট',
  'agent.lockout.tier.justUnlock.label':   'শুধু আনলক',
  'agent.lockout.tier.justUnlock.tag':     'ঝুঁকিপূর্ণ',
  'agent.lockout.tier.justUnlock.note':    'আপনাকে ₹0-এ ফিরিয়ে আনে — যেকোনো নতুন ক্ষতি তৎক্ষণাৎ আবার লক করবে।',
  'agent.lockout.tier.safeMin.label':      'নিরাপদ ন্যূনতম',
  'agent.lockout.tier.safeMin.tag':        'সুপারিশকৃত',
  'agent.lockout.tier.safeMin.note':       'ডিপোজিট ({deposit}) + আপনার ঋণাত্মক ওয়ালেটের অর্ধেক। একটি প্রকৃত কুশন রাখে।',
  'agent.lockout.tier.fullRecovery.label': 'পূর্ণ পুনরুদ্ধার',
  'agent.lockout.tier.fullRecovery.tag':   'সেরা',
  'agent.lockout.tier.fullRecovery.note':  'ডিপোজিট সম্পূর্ণ পুনরুদ্ধার এবং ওয়ালেট শূন্যে আনা।',
  'agent.lockout.waysOut.eyebrow':         'পুনরুদ্ধারের দুটি উপায় — যেটিই করুন (বা দুটিই)',
  'agent.lockout.waysOut.inbox.title':     'আপনার ইনবক্সে ব্যবস্থা নিন',
  'agent.lockout.waysOut.inbox.body':      'খেলোয়াড় রিচার্জ অনুমোদন করুন (টাকা ইতিমধ্যে আপনার কাছে) এবং খেলোয়াড় উত্তোলনকে পরিশোধিত হিসেবে চিহ্নিত করুন। প্রতিটি অনুমোদন আপনার ওয়ালেটে ক্রেডিট করে।',
  'agent.lockout.waysOut.inbox.ctaOpen':   'ইনবক্স খুলুন',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ইনবক্স নিচে খোলা',
  'agent.lockout.waysOut.upline.title':    'আপনার আপলাইনে পেমেন্ট করুন',
  'agent.lockout.waysOut.upline.body':     'আপনার পেরেন্ট এজেন্ট (বা এডমিন)-কে UPI-এর মাধ্যমে টাকা পাঠান, তারপর টxn ID জমা দিন। অনুমোদন হলে আপনার ওয়ালেট তৎক্ষণাৎ ক্রেডিট হবে।',
  'agent.lockout.waysOut.upline.ctaOpen':  'ফর্ম খুলুন',
  'agent.lockout.waysOut.upline.ctaOpened':'ফর্ম নিচে খোলা',
  'agent.lockout.capabilities.eyebrow':    'আপনি এখনো কী করতে পারেন (এবং কী বন্ধ)',
  'agent.lockout.capabilities.canHeader':  'আপনি এখনো করতে পারেন',
  'agent.lockout.capabilities.cantHeader': 'আপনি (অস্থায়ীভাবে) পারেন না',
  'agent.lockout.can.approvePlayerRecharge':      'আগত খেলোয়াড় রিচার্জ অনুমোদন',
  'agent.lockout.can.markPlayerWithdrawalPaid':   'খেলোয়াড় উত্তোলনকে পরিশোধিত হিসেবে চিহ্নিত',
  'agent.lockout.can.approveSubAgentRecharge':    'সাব-এজেন্ট রিচার্জ অনুমোদন',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'সাব-এজেন্ট উত্তোলনকে পরিশোধিত হিসেবে চিহ্নিত',
  'agent.lockout.can.submitToUpline':             'আপনার আপলাইনে রিচার্জ পাঠানো',
  'agent.lockout.can.receiveSseAndSignOut':       'SSE সতর্কতা গ্রহণ + সাইন আউট',
  'agent.lockout.section.step':                   'ধাপ {n} · {title}',
  'agent.lockout.section.inbox.title':            'আপনার ইনবক্স — প্রতিটি অনুমোদন আপনার ওয়ালেটে ক্রেডিট করে',
  'agent.lockout.section.inbox.subtitle':         'খেলোয়াড় এবং সাব-এজেন্টদের কাছ থেকে মুলতুবি পেমেন্ট অনুরোধ যা আপনার ব্যবস্থার অপেক্ষায়।',
  'agent.lockout.section.inbox.fromPlayers':      'আপনার খেলোয়াড়দের থেকে',
  'agent.lockout.section.inbox.fromSubAgents':    'আপনার সাব-এজেন্টদের থেকে',
  'agent.lockout.section.upline.title':           'আপনার আপলাইনে পেমেন্ট করে ওয়ালেট টপ-আপ',
  'agent.lockout.section.upline.subtitle':        'পেরেন্টের UPI সামনে থাকলে দ্রুত হয়। অনুমোদন সাধারণত কয়েক মিনিটে।',
  'agent.lockout.queue.player.recharge.title':           'রিচার্জ · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle':        'টxn {txn} · {when}-এ জমা',
  'agent.lockout.queue.player.recharge.subtitleNote':    'টxn {txn} · {when}-এ জমা · "{note}"',
  'agent.lockout.queue.player.withdrawal.title':         'উত্তোলন · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle':      '{upi}-এ পেমেন্ট · {when}-এ জমা',
  'agent.lockout.queue.player.withdrawal.subtitleWa':    '{upi}-এ পেমেন্ট · {whatsapp} · {when}-এ জমা',
  'agent.lockout.queue.subAgent.recharge.title':         'সাব-এজেন্ট রিচার্জ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle':      'টxn {txn} · {when}-এ জমা',
  'agent.lockout.queue.subAgent.recharge.subtitleNote':  'টxn {txn} · {when}-এ জমা · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title':       'সাব-এজেন্ট উত্তোলন · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle':    '{upi}-এ পেমেন্ট · {when}-এ জমা',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote':'{upi}-এ পেমেন্ট · {when}-এ জমা · "{note}"',
  'agent.lockout.queue.action.approve':                  'অনুমোদন',
  'agent.lockout.queue.action.reject':                   'প্রত্যাখ্যান',
  'agent.lockout.queue.action.markPaid':                 'পরিশোধিত চিহ্নিত',
  'agent.lockout.queue.prompt.markPlayerPaid':           'খেলোয়াড়কে পেমেন্ট করার জন্য ব্যবহৃত UPI লেনদেন ID লিখুন:',
  'agent.lockout.queue.prompt.markSubAgentPaid':         'সাব-এজেন্টকে পেমেন্ট করার জন্য ব্যবহৃত UPI লেনদেন ID লিখুন:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal':  'এই উত্তোলন প্রত্যাখ্যান করবেন? আটকে রাখা পরিমাণ খেলোয়াড়ের কাছে ফেরত যাবে।',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal':'এই সাব-এজেন্ট উত্তোলন প্রত্যাখ্যান করবেন?',
  'agent.lockout.queue.toast.rechargeApproved':          'রিচার্জ অনুমোদিত — খেলোয়াড় ক্রেডিট হয়েছে',
  'agent.lockout.queue.toast.rechargeRejected':          'রিচার্জ প্রত্যাখ্যাত',
  'agent.lockout.queue.toast.subAgentRechargeApproved':  'সাব-এজেন্ট রিচার্জ অনুমোদিত',
  'agent.lockout.queue.toast.subAgentRechargeRejected':  'রিচার্জ প্রত্যাখ্যাত',
  'agent.lockout.queue.toast.withdrawalPaid':            'উত্তোলন পরিশোধিত চিহ্নিত — আপনার ওয়ালেট ক্রেডিট',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid':    'সাব-এজেন্ট উত্তোলন পরিশোধিত চিহ্নিত — ওয়ালেট ক্রেডিট',
  'agent.lockout.queue.toast.withdrawalRejected':        'উত্তোলন প্রত্যাখ্যাত (খেলোয়াড়কে ফেরত)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected':'উত্তোলন প্রত্যাখ্যাত',
  'agent.lockout.queue.toast.actionFailed':              'কাজ ব্যর্থ',
  'agent.lockout.queue.empty.player':                    'এই মুহূর্তে আপনার খেলোয়াড়দের থেকে কিছু নেই।',
  'agent.lockout.queue.empty.subAgent':                  'এই মুহূর্তে আপনার সাব-এজেন্টদের থেকে কিছু নেই।',
  'agent.lockout.queue.loading':                         'কিউ লোড হচ্ছে…',
  'agent.lockout.refresh.cta':                           'রিফ্রেশ — আমি কি এখন আনলক?',
  'agent.lockout.refresh.checking':                      'যাচাই হচ্ছে…',
  'agent.lockout.refresh.toast':                         'স্থিতি রিফ্রেশ হয়েছে',
  'agent.lockout.refresh.footnote':                      'আপনার ওয়ালেট + ডিপোজিট শূন্যের উপরে উঠলেই এই পেজ স্বয়ংক্রিয়ভাবে বন্ধ হয়।',

  // ── EmergencyRechargeForm ────────────────────────────────────────
  'agent.emergencyForm.error.loadFailed':            'রিচার্জ বিবরণ লোড করা যায়নি',
  'agent.emergencyForm.banner.locked.title':         'অ্যাকাউন্ট আনলক করতে এখনই পেমেন্ট করুন',
  'agent.emergencyForm.banner.locked.body':          'আপনার খেলোয়াড়রা আপনার ডিপোজিট জামানতের চেয়ে বেশি জিতেছেন। আপনার ঋণাত্মক ব্যালেন্স পরিষ্কার না করে আপনি প্ল্যাটফর্ম ব্যবহার করতে পারবেন না।',
  'agent.emergencyForm.banner.warning.title':        'শীঘ্রই টপ-আপ করুন — আপনি সীমার খুব কাছে',
  'agent.emergencyForm.banner.warning.body':         'আপনার ওয়ালেট ডিপোজিটের ৮০% অতিক্রম করেছে। ১০০%-এ পৌঁছানোর আগে টপ-আপ করুন — একবার পৌঁছালে আপনার অ্যাকাউন্ট লক হয়ে যাবে।',
  'agent.emergencyForm.row.wallet':                  'আপনার ওয়ালেট',
  'agent.emergencyForm.row.deposit':                 'আপনার ডিপোজিট',
  'agent.emergencyForm.tier.eyebrow.locked':         'আপনি কেন আটকা',
  'agent.emergencyForm.tier.eyebrow.warning':        'আপনি কেন ঝুঁকিতে',
  'agent.emergencyForm.tier.context.locked':         'আপনার ওয়ালেট {wallet}-এ গেছে — এটি আপনার {deposit} ডিপোজিট সুরক্ষা কভারের বাইরে। প্ল্যাটফর্ম আপনাকে এখানে লক করে যাতে আপনার ডাউনলাইনের ক্ষতি খালি বাফারে আরও না বাড়ে।',
  'agent.emergencyForm.tier.context.warning':        'আপনার ওয়ালেট {wallet}-এ গেছে — এটি আপনার {deposit} ডিপোজিট সুরক্ষা কভারের ৮০%-এর বেশি। আর একটি খারাপ সেটলমেন্ট আপনাকে ১০০% অতিক্রম করিয়ে অ্যাকাউন্ট লক করবে।',
  'agent.emergencyForm.tier.pickHowMuch':            'কত পেমেন্ট করতে চান বাছুন',
  'agent.emergencyForm.tier.bareMin.label.locked':   'শুধু আনলক',
  'agent.emergencyForm.tier.bareMin.label.warning':  'শুধু সতর্কতা থেকে বাইরে',
  'agent.emergencyForm.tier.bareMin.tag':            'ঝুঁকিপূর্ণ',
  'agent.emergencyForm.tier.bareMin.note.locked':    'শুধু আপনার ওয়ালেট + ডিপোজিটকে ₹0-এ আনে। যেকোনো নতুন ক্ষতি আপনাকে তৎক্ষণাৎ আবার লক করবে।',
  'agent.emergencyForm.tier.bareMin.note.warning':   'শুধু ৮০% লাইনের সামান্য উপরে আনে। যেকোনো নতুন ক্ষতি আবার সতর্কতা ট্রিগার করবে — বা লকআউটে ঠেলে দেবে।',
  'agent.emergencyForm.tier.safeMin.label':          'নিরাপদ ন্যূনতম',
  'agent.emergencyForm.tier.safeMin.tag':            'সুপারিশকৃত',
  'agent.emergencyForm.tier.safeMin.note':           'আপনার ডিপোজিট ({deposit}) + আপনার ঋণাত্মক ওয়ালেটের অর্ধেক ({half})। আপনাকে একটি প্রকৃত কুশন দেয়।',
  'agent.emergencyForm.tier.full.label':             'পূর্ণ পুনরুদ্ধার',
  'agent.emergencyForm.tier.full.tag':               'সেরা',
  'agent.emergencyForm.tier.full.note':              'ডিপোজিট + আপনার সম্পূর্ণ ঋণাত্মক ওয়ালেট ({neg})। ডিপোজিট সম্পূর্ণ পুনরুদ্ধার এবং ওয়ালেট শূন্যে।',
  'agent.emergencyForm.parent.label.admin':          'আপনার এডমিনকে পেমেন্ট করুন',
  'agent.emergencyForm.parent.label.parentAgent':    'আপনার পেরেন্ট এজেন্টকে পেমেন্ট করুন',
  'agent.emergencyForm.parent.role.admin':           'প্ল্যাটফর্ম এডমিন',
  'agent.emergencyForm.parent.role.parentAgent':     'পেরেন্ট এজেন্ট',
  'agent.emergencyForm.parent.upi':                  'UPI:',
  'agent.emergencyForm.parent.whatsapp':             'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile':            'আপনার পেরেন্টের পেমেন্ট বিবরণ এখনো ফাইলে নেই। অনুগ্রহ করে সরাসরি প্ল্যাটফর্ম সাপোর্টের সাথে যোগাযোগ করুন।',
  'agent.emergencyForm.field.amount.label':          'পরিশোধিত পরিমাণ (₹)',
  'agent.emergencyForm.field.txn.label':             'UPI লেনদেন ID / UTR',
  'agent.emergencyForm.field.txn.placeholder':       'যেমন 412312345678',
  'agent.emergencyForm.field.note.label':            'নোট (ঐচ্ছিক)',
  'agent.emergencyForm.field.note.placeholder':      'পেরেন্টকে কিছু জানাতে চান',
  'agent.emergencyForm.validation.amount':           'যা পেমেন্ট করেছেন তা লিখুন (ন্যূনতম ₹10)',
  'agent.emergencyForm.validation.txn':              'UPI লেনদেন ID / UTR লিখুন (কমপক্ষে 6 অক্ষর)',
  'agent.emergencyForm.cta':                         'রিচার্জ অনুরোধ পাঠান',
  'agent.emergencyForm.ctaSubmitting':               'পাঠানো হচ্ছে…',
  'agent.emergencyForm.success.title':               'রিচার্জ জমা হয়েছে',
  'agent.emergencyForm.success.bodyAdmin':           'অনুরোধ #{id} এডমিনের অনুমোদনের অপেক্ষায়। অনুমোদনের সাথে সাথে আপনার ওয়ালেট ক্রেডিট হবে এবং অ্যাকাউন্ট স্বয়ংক্রিয়ভাবে আনলক হবে।',
  'agent.emergencyForm.success.bodyAgent':           'অনুরোধ #{id} @{username}-এর অনুমোদনের অপেক্ষায়। অনুমোদনের সাথে সাথে আপনার ওয়ালেট ক্রেডিট হবে এবং অ্যাকাউন্ট স্বয়ংক্রিয়ভাবে আনলক হবে।',
  'agent.emergencyForm.toast.submitted':             'রিচার্জ অনুরোধ #{id} পাঠানো হয়েছে। অনুমোদনের অপেক্ষায়।',
  'agent.emergencyForm.toast.cantSave':              'ভাষা সংরক্ষণ করা যায়নি',

  // ── GeoForcedModal ───────────────────────────────────────────────
  'agent.geo.title':            'এক-বারের সেটআপ: আপনার অবস্থান নিশ্চিত করুন',
  'agent.geo.subtitle':         'ড্যাশবোর্ড ব্যবহারের আগে আবশ্যক।',
  'agent.geo.body.primary':     'বালি গেমস প্ল্যাটফর্ম নতুন খেলোয়াড়দের তাঁদের সবচেয়ে শারীরিকভাবে নিকটতম এজেন্টের কাছে পাঠায়। যতক্ষণ না আপনি আপনার সঠিক GPS অবস্থান শেয়ার করেন, আপনি লোকেটরে দেখা যাবেন না এবং অরগ্যানিক সাইনআপ থেকে শূন্য নতুন খেলোয়াড় পাবেন — শুধু যাঁরা সরাসরি আপনার রেফারেল কোড টাইপ করেন তাঁরাই আপনার কাছে আসবেন।',
  'agent.geo.body.secondary':   'একবার "আমার বর্তমান অবস্থান ব্যবহার করুন" ট্যাপ করুন। আমরা শুধু আপনার স্থানাঙ্ক ক্যাপচার করি, অন্য কিছু নয়।',
  'agent.geo.hint.disabled':    'উপরে "আমার বর্তমান অবস্থান ব্যবহার করুন" ট্যাপ করুন। শুধু পিনকোড বা ম্যানুয়াল এলাকা নির্বাচন যথেষ্ট নয় — লোকেটরের জন্য সঠিক GPS স্থানাঙ্ক দরকার।',
  'agent.geo.error.fallback':   'অবস্থান সংরক্ষণ করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
  'agent.geo.cta.save':         'সংরক্ষণ করে চালিয়ে যান',
  'agent.geo.cta.saving':       'অবস্থান সংরক্ষণ হচ্ছে…',
  'agent.geo.toast.success':    'অবস্থান সংরক্ষিত — এখন আপনি খেলোয়াড় পেতে পারেন।',

  // ════════════════════════════════════════════════════════════════════
  // AGENT APP — Bengali translations (Phase C Batch 2, Chunk 2 of 3).
  // Covers Phase B Groups 4-7: wallet/deposit, people hub, profile, rewards.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label':       'পুল ব্যালেন্স',
  'agent.wallet.hero.helper':      'খেলোয়াড় রিচার্জ এবং সাব-এজেন্ট টপ-আপের জন্য উপলব্ধ। আয় এবং এডমিন টপ-আপ এখানে আসে।',
  'agent.wallet.action.recharge':  'রিচার্জ অনুরোধ',
  'agent.wallet.action.withdraw':  'উত্তোলন অনুরোধ',
  'agent.wallet.tab.pending':      'মুলতুবি',
  'agent.wallet.tab.history':      'ইতিহাস',
  'agent.wallet.pending.loading':  'লোড হচ্ছে…',
  'agent.wallet.pending.empty':    'এই মুহূর্তে কোনো মুলতুবি অনুরোধ নেই।',
  'agent.wallet.pending.recharge.title':   'রিচার্জ অনুরোধ · +{amount}',
  'agent.wallet.pending.recharge.meta':    'টxn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'উত্তোলন অনুরোধ · −{amount}',
  'agent.wallet.pending.withdrawal.meta':  '{upi}-এ · {when}',
  'agent.wallet.pending.badge':            'মুলতুবি',
  'agent.wallet.history.loading':                  'লোড হচ্ছে…',
  'agent.wallet.history.empty':                    'এখনো কোনো ওয়ালেট কার্যকলাপ নেই।',
  'agent.wallet.history.recharge.titleAccepted':   'রিচার্জ অনুমোদিত · {amount}',
  'agent.wallet.history.recharge.titleRejected':   'রিচার্জ প্রত্যাখ্যাত · {amount}',
  'agent.wallet.history.recharge.meta':            'টxn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid':     'উত্তোলন পরিশোধিত · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'উত্তোলন প্রত্যাখ্যাত · {amount}',
  'agent.wallet.history.withdrawal.meta':          '{upi}-এ · {when}',
  'agent.wallet.history.withdrawal.paidTxn':       'পরিশোধিত · {txn}',
  'agent.wallet.state.accepted': 'অনুমোদিত',
  'agent.wallet.state.paid':     'পরিশোধিত',
  'agent.wallet.state.rejected': 'প্রত্যাখ্যাত',
  'agent.wallet.state.expired':  'মেয়াদোত্তীর্ণ',
  'agent.wallet.ledger.kind.topupFromAdmin':   'এডমিন থেকে টপ-আপ',
  'agent.wallet.ledger.kind.topupToPlayer':    'খেলোয়াড়কে টপ-আপ',
  'agent.wallet.ledger.kind.topupFromPlayer':  'খেলোয়াড়ের রিচার্জ',
  'agent.wallet.ledger.kind.revenueShare':     'রাজস্ব ভাগ',
  'agent.wallet.ledger.kind.coFundOffer':      'সহ-ফান্ডেড অফার',
  'agent.wallet.ledger.kind.adminAdjust':      'এডমিন সমন্বয়',
  'agent.wallet.ledger.kind.withdrawalHold':   'উত্তোলন হোল্ড',
  'agent.wallet.ledger.kind.withdrawalPaid':   'উত্তোলন পরিশোধিত',
  'agent.wallet.ledger.kind.withdrawalRefund': 'উত্তোলন ফেরত',
  'agent.wallet.ledger.balanceAfter':          'ব্যালেন্স {amount}',

  // ── MyDepositPanel ───────────────────────────────────────────────
  'agent.deposit.hero.label':         'সিকিউরিটি ডিপোজিট',
  'agent.deposit.hero.helper':        'আপনার পুলের জন্য জামানত। ওয়ালেট ভালো অবস্থায় থাকলে অস্পর্শিত থাকে।',
  'agent.deposit.margin.safe':        'নিরাপদ',
  'agent.deposit.margin.warning':     'সতর্কবার্তা',
  'agent.deposit.margin.locked':      'লক',
  'agent.deposit.action.recharge':    'ডিপোজিট রিচার্জ',
  'agent.deposit.action.withdraw':    'ডিপোজিট উত্তোলন',
  'agent.deposit.gauge.label':        'কভারেজ গেজ',
  'agent.deposit.gauge.thresholdWarn':'সতর্ক',
  'agent.deposit.gauge.thresholdLock':'লক',
  'agent.deposit.gauge.legend.warn':  '{pct}-এ আপনি সতর্কবার্তা পাবেন',
  'agent.deposit.gauge.legend.lock':  '{pct}-এ আপনার অ্যাকাউন্ট লক হবে',
  'agent.deposit.gauge.caption.locked':  'পুল সম্পূর্ণ ডিপোজিট দ্বারা কভার্ড — টপ-আপ না হওয়া পর্যন্ত উত্তোলন বন্ধ।',
  'agent.deposit.gauge.caption.warning': 'পুল আপনার ডিপোজিটের {pct}% খেয়েছে। লকআউট এড়াতে ১০০%-এর আগে টপ-আপ করুন।',
  'agent.deposit.gauge.caption.caution': 'পুল ডিপোজিট কভারেজের {pct}%-এ। আপনি এখনো ৮০% সতর্কতা সীমার নিচে নিরাপদে আছেন।',
  'agent.deposit.gauge.caption.safe':    'আপনার ডিপোজিট সম্পূর্ণ অক্ষত।',
  'agent.deposit.ledger.loading':         'লোড হচ্ছে…',
  'agent.deposit.ledger.empty.title':     'এখনো কোনো ডিপোজিট কার্যকলাপ নেই',
  'agent.deposit.ledger.empty.body':      'এডমিন আপনার ডিপোজিটে ক্রেডিট করলে (বা ফেরত প্রক্রিয়া করলে) প্রতিটি এন্ট্রি এখানে দেখাবে।',

  // ── RequestRechargeForm ──────────────────────────────────────────
  'agent.recharge.banner.eyebrow.wallet':  'আপনার ওয়ালেট রিচার্জ করুন',
  'agent.recharge.banner.eyebrow.deposit': 'আপনার ডিপোজিট রিচার্জ করুন',
  'agent.recharge.banner.body.wallet':     'প্রথমে আপনার আপলাইনে পেমেন্ট করুন, তারপর প্রমাণ এখানে জমা দিন যাতে তাঁরা আপনার চলতি ওয়ালেট টপ-আপ করতে পারেন।',
  'agent.recharge.banner.body.deposit':    'প্রথমে আপনার আপলাইনে পেমেন্ট করুন, তারপর প্রমাণ এখানে জমা দিন যাতে তাঁরা আপনার সিকিউরিটি ডিপোজিট টপ-আপ করতে পারেন।',
  'agent.recharge.banner.closeAria':       'ফর্ম বন্ধ করুন',
  'agent.recharge.parent.label.admin':       'আপনার এডমিনকে পেমেন্ট করুন',
  'agent.recharge.parent.label.parentAgent': 'আপনার পেরেন্ট এজেন্টকে পেমেন্ট করুন',
  'agent.recharge.parent.role.admin':        'প্ল্যাটফর্ম এডমিন',
  'agent.recharge.parent.role.parentAgent':  'পেরেন্ট এজেন্ট',
  'agent.recharge.parent.noQr':              'কোনো QR সেট নেই',
  'agent.recharge.parent.upi':               'UPI:',
  'agent.recharge.parent.loading':           'আপনার আপলাইনের পেমেন্ট বিবরণ লোড হচ্ছে…',
  'agent.recharge.parent.loadError':         'আপনার আপলাইনের পেমেন্ট বিবরণ লোড হয়নি',
  'agent.recharge.method.eyebrow':       'আপনি কীভাবে পেমেন্ট করেছেন?',
  'agent.recharge.method.upi.label':     'UPI / QR',
  'agent.recharge.method.upi.note':      'অনলাইন ডিজিটাল পেমেন্ট',
  'agent.recharge.method.cash.label':    'নগদ',
  'agent.recharge.method.cash.note':     'সাক্ষাতে হস্তান্তর',
  'agent.recharge.field.amount.label':       'পরিমাণ (₹) · ন্যূনতম ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi':      'UPI লেনদেন ID / UTR',
  'agent.recharge.field.txn.label.cash':     'নগদ রসিদ চিহ্ন (যেকোনো ছোট রেফারেন্স)',
  'agent.recharge.field.txn.placeholder.upi':  'যেমন 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'যেমন CASH-Sun-eve',
  'agent.recharge.field.note.label':         'নোট (ঐচ্ছিক, আপনার আপলাইনকে দেখানো হবে)',
  'agent.recharge.field.note.placeholder':   'আপলাইনের জন্য কোনো প্রসঙ্গ',
  'agent.recharge.error.amountMin':  'পরিমাণ অবশ্যই পূর্ণ রুপি ≥ 10 হতে হবে',
  'agent.recharge.error.txnUpi':     'লেনদেন ID খুব ছোট মনে হচ্ছে — UPI রেফারেন্স পেস্ট করুন',
  'agent.recharge.error.txnCash':    'একটি ছোট চিহ্ন যোগ করুন (যেমন "CASH-Sun-eve") যাতে আপলাইন হস্তান্তর মেলাতে পারেন',
  'agent.recharge.cta.submit': 'অনুরোধ পাঠান',
  'agent.recharge.cta.cancel': 'বাতিল',

  // ── RequestWithdrawalForm ────────────────────────────────────────
  'agent.withdrawal.banner.eyebrow.wallet':  'ওয়ালেট থেকে উত্তোলন',
  'agent.withdrawal.banner.eyebrow.deposit': 'ডিপোজিট থেকে উত্তোলন',
  'agent.withdrawal.banner.body.wallet':     'পরিমাণ তৎক্ষণাৎ আটকে রাখা হয় যাতে দ্বিগুণ খরচ না হয়। আপনার আপলাইন আপনাকে পেমেন্ট করে পরিশোধিত চিহ্নিত করেন।',
  'agent.withdrawal.banner.body.deposit':    'পরিমাণ আপনার সিকিউরিটি ডিপোজিট থেকে তৎক্ষণাৎ আটকে রাখা হয়। আপনার আপলাইন আপনাকে পেমেন্ট করে পরিশোধিত চিহ্নিত করেন।',
  'agent.withdrawal.banner.closeAria':       'ফর্ম বন্ধ করুন',
  'agent.withdrawal.method.eyebrow':    'আপনি কীভাবে পেমেন্ট পেতে চান?',
  'agent.withdrawal.method.upi.label':  'UPI / QR',
  'agent.withdrawal.method.upi.note':   'অনলাইন ডিজিটাল পেমেন্ট',
  'agent.withdrawal.method.cash.label': 'নগদ',
  'agent.withdrawal.method.cash.note':  'সাক্ষাতে হস্তান্তর',
  'agent.withdrawal.field.amount.label':           'পরিমাণ (₹) · উপলব্ধ {balance}',
  'agent.withdrawal.field.amount.placeholder':     '500',
  'agent.withdrawal.field.upi.label':              'আপনার UPI ID (যেখানে আপলাইন পেমেন্ট করবে)',
  'agent.withdrawal.field.upi.placeholder':        'you@bank',
  'agent.withdrawal.field.cashMarker.label':       'সাক্ষাৎ চিহ্ন (যেমন "রবিবার সন্ধ্যা নগদ")',
  'agent.withdrawal.field.cashMarker.placeholder': 'নগদ কোথায়/কখন দিতে হবে',
  'agent.withdrawal.field.whatsapp.label':         'WhatsApp (ঐচ্ছিক, যাতে আপলাইন যোগাযোগ করতে পারেন)',
  'agent.withdrawal.field.whatsapp.placeholder':   '+919876543210',
  'agent.withdrawal.field.note.label':             'নোট (ঐচ্ছিক)',
  'agent.withdrawal.field.note.placeholder':       'আপলাইনের জন্য কোনো প্রসঙ্গ',
  'agent.withdrawal.error.amountMin':       'পরিমাণ অবশ্যই পূর্ণ রুপি ≥ 10 হতে হবে',
  'agent.withdrawal.error.amountOverflow':  'আপনার কাছে শুধু {balance} উপলব্ধ',
  'agent.withdrawal.error.upi':             'বৈধ UPI ID লিখুন',
  'agent.withdrawal.cta.submit': 'অনুরোধ পাঠান',
  'agent.withdrawal.cta.cancel': 'বাতিল',

  // ── People Hub ───────────────────────────────────────────────────
  'agent.people.hub.players.title':           'খেলোয়াড়',
  'agent.people.hub.players.subtitle':        'খেলোয়াড় এবং তাঁদের অনুরোধ পরিচালনা',
  'agent.people.hub.players.subtab.requests': 'খেলোয়াড় অনুরোধ',
  'agent.people.hub.players.subtab.list':     'খেলোয়াড়',
  'agent.people.hub.agents.title':            'এজেন্ট',
  'agent.people.hub.agents.subtitle':         'সাব-এজেন্ট এবং তাঁদের অনুরোধ পরিচালনা',
  'agent.people.hub.agents.subtab.requests':  'এজেন্ট অনুরোধ',
  'agent.people.hub.agents.subtab.list':      'এজেন্ট',
  'agent.people.acceptingToggle.label':           'নতুন খেলোয়াড় গ্রহণ করছি',
  'agent.people.acceptingToggle.bodyAccepting':   'আপনি ভৌগোলিক লোকেটর থেকে নতুন খেলোয়াড় গ্রহণ করছেন। আপনার এলাকায় কোল্ড সাইনআপ স্বয়ংক্রিয়ভাবে আপনার কাছে আসে।',
  'agent.people.acceptingToggle.bodyOptedOut':    'আপনি অপ্ট-আউট করেছেন। লোকেটর আপনাকে এড়িয়ে যাচ্ছে — শুধু সরাসরি রেফারেল এবং ম্যানুয়াল অ্যাসাইন করা খেলোয়াড়রা আপনার কাছে আসবেন। বিদ্যমান খেলোয়াড়রা প্রভাবিত হবেন না।',
  'agent.people.acceptingToggle.error':           'আপডেট করা যায়নি',
  'agent.people.acceptingToggle.aria.stop':       'নতুন খেলোয়াড় গ্রহণ বন্ধ করুন',
  'agent.people.acceptingToggle.aria.start':      'নতুন খেলোয়াড় গ্রহণ শুরু করুন',

  // ── QRCodeModule ─────────────────────────────────────────────────
  'agent.qr.audience.player':     'খেলোয়াড়',
  'agent.qr.audience.subAgent':   'এজেন্ট',
  'agent.qr.alt':                 'আপনার পেমেন্ট QR ({audience})',
  'agent.qr.cardLabel.player':    'আমার QR · খেলোয়াড়রা যা দেখেন',
  'agent.qr.cardLabel.subAgent':  'আমার QR · এজেন্টরা যা দেখেন',
  'agent.qr.upi.empty':           'এখনো কোনো UPI সেট নেই',
  'agent.qr.status.loading':      'লোড হচ্ছে…',
  'agent.qr.status.live':         'লাইভ',
  'agent.qr.status.notSet':       'সেট নেই',
  'agent.qr.status.paused':       'বিরতি',
  'agent.qr.cta.edit':            'QR এবং UPI সম্পাদনা',
  'agent.qr.cta.setup':           'QR সেট আপ',
  'agent.qr.cta.viewOnly':        'শুধু দেখুন — সম্পাদনার জন্য এডমিনকে বলুন',
  'agent.qr.modal.title':         'QR এবং UPI সম্পাদনা',
  'agent.qr.modal.closeAria':     'বন্ধ',
  'agent.qr.modal.upload':        'QR আপলোড',
  'agent.qr.modal.replace':       'QR প্রতিস্থাপন',
  'agent.qr.modal.remove':        'মুছে ফেলুন',
  'agent.qr.modal.confirmRemove.player':   'QR ছবি মুছে ফেলবেন? আপনার খেলোয়াড়রা শুধু যোগাযোগ ফলব্যাক দেখবেন।',
  'agent.qr.modal.confirmRemove.subAgent': 'QR ছবি মুছে ফেলবেন? আপনার এজেন্টরা শুধু যোগাযোগ ফলব্যাক দেখবেন।',
  'agent.qr.modal.field.upi.label':                       'UPI ID',
  'agent.qr.modal.field.upi.placeholder':                 'যেমন baaligames@upi',
  'agent.qr.modal.field.whatsapp.label.player':           'WhatsApp নম্বর (ফলব্যাক)',
  'agent.qr.modal.field.whatsapp.label.subAgent':         'এজেন্টদের জন্য WhatsApp নম্বর',
  'agent.qr.modal.field.whatsapp.placeholder.default':    '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback':   'খালি → {number}-এ ফিরে যাবে',
  'agent.qr.modal.field.whatsapp.fallbackHint':           'সাব-এজেন্টরা {number} দেখবেন (আপনার খেলোয়াড়-মুখী নম্বর)।',
  'agent.qr.modal.field.phone.label.player':              'সহায়তা ফোন (ফলব্যাক)',
  'agent.qr.modal.field.phone.label.subAgent':            'এজেন্টদের জন্য সহায়তা ফোন',
  'agent.qr.modal.field.phone.placeholder.default':       '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback':      'খালি → {number}-এ ফিরে যাবে',
  'agent.qr.modal.field.phone.fallbackHint':              'সাব-এজেন্টরা {number} দেখবেন (আপনার খেলোয়াড়-মুখী নম্বর)।',
  'agent.qr.modal.active.label':       'সক্রিয়',
  'agent.qr.modal.active.description': 'খেলোয়াড়রা রিচার্জ জমা দিতে পারেন',
  'agent.qr.modal.active.on':          'চালু',
  'agent.qr.modal.active.paused':      'বিরতি',
  'agent.qr.modal.cancel':             'বাতিল',
  'agent.qr.modal.save':               'সংরক্ষণ',

  // ── ManualRechargeForm ───────────────────────────────────────────
  'agent.manualRecharge.banner.eyebrow':                  'ম্যানুয়াল রিচার্জ',
  'agent.manualRecharge.banner.body.before':              'সরাসরি আপনার পুল থেকে একজন ',
  'agent.manualRecharge.banner.body.target':              'খেলোয়াড়',
  'agent.manualRecharge.banner.body.after':               '-কে টাকা পাঠান।',
  'agent.manualRecharge.pool.label':                      'আপনার পুল',
  'agent.manualRecharge.pool.dailyCap':                   'সীমা · {amount}/দিন',
  'agent.manualRecharge.field.player.label':              'কোন খেলোয়াড়কে',
  'agent.manualRecharge.field.player.placeholder':        'একজন খেলোয়াড় বাছুন…',
  'agent.manualRecharge.field.player.option':             '@{username} · {contact} · ব্যাল {balance}',
  'agent.manualRecharge.field.player.lockedLabel':        'কোন খেলোয়াড়কে',
  'agent.manualRecharge.field.player.lockedMeta':         '{contact} · বর্তমান ব্যাল {balance}',
  'agent.manualRecharge.field.player.notFound':           'আপনার তালিকায় খেলোয়াড় পাওয়া যায়নি।',
  'agent.manualRecharge.field.amount.label':              'পরিমাণ (₹) — {min} থেকে {max}',
  'agent.manualRecharge.field.amount.placeholder':        '0',
  'agent.manualRecharge.error.exceedsPool':               'আপনার পুল ব্যালেন্সের চেয়ে বেশি।',
  'agent.manualRecharge.error.belowMin':                  'ন্যূনতমের কম ({min})।',
  'agent.manualRecharge.error.aboveMax':                  'সর্বাধিকের বেশি ({max})।',
  'agent.manualRecharge.field.note.label':                'নোট (ঐচ্ছিক, খেলোয়াড় লেজারে দেখাবে)',
  'agent.manualRecharge.field.note.placeholder':          'যেমন দোকানে নগদ নিয়েছি',
  'agent.manualRecharge.loading':                         'আপনার পুল লোড হচ্ছে…',
  'agent.manualRecharge.cta.cancel':                      'বাতিল',
  'agent.manualRecharge.cta.send':                        '{amount} পাঠান',
  'agent.manualRecharge.success.title':                   '@{username}-কে {amount} পাঠানো হয়েছে',
  'agent.manualRecharge.success.meta':                    'আপনার পুল · {pool} · তাঁদের ওয়ালেট · {playerBal}',
  'agent.manualRecharge.success.again':                   'আরও পাঠান',

  // ── ManualSubAgentRechargeForm ───────────────────────────────────
  'agent.manualSubAgentRecharge.banner.eyebrow':              'ম্যানুয়াল রিচার্জ',
  'agent.manualSubAgentRecharge.banner.body.before':          'সরাসরি আপনার পুল থেকে একজন ',
  'agent.manualSubAgentRecharge.banner.body.target':          'সাব-এজেন্ট',
  'agent.manualSubAgentRecharge.banner.body.after':           '-কে টাকা পাঠান।',
  'agent.manualSubAgentRecharge.pool.label':                  'আপনার পুল',
  'agent.manualSubAgentRecharge.field.subAgent.label':        'কোন সাব-এজেন্টকে',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder':  'একজন সাব-এজেন্ট বাছুন…',
  'agent.manualSubAgentRecharge.field.subAgent.option':       '@{username} · {email} · পুল {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption':  'কোনো সক্রিয় সরাসরি সাব-এজেন্ট নেই',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel':  'কোন সাব-এজেন্টকে',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta':   '{email} · বর্তমান পুল {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound':     'আপনার সরাসরি ডাউনলাইনে সাব-এজেন্ট পাওয়া যায়নি।',
  'agent.manualSubAgentRecharge.field.amount.label':          'পরিমাণ (₹) — {min} থেকে {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder':    '0',
  'agent.manualSubAgentRecharge.error.exceedsPool':           'আপনার পুল ব্যালেন্সের চেয়ে বেশি।',
  'agent.manualSubAgentRecharge.error.belowMin':              'ন্যূনতমের কম ({min})।',
  'agent.manualSubAgentRecharge.field.note.label':            'নোট (ঐচ্ছিক, উভয় লেজারে দেখাবে)',
  'agent.manualSubAgentRecharge.field.note.placeholder':      'যেমন ফ্লোট অগ্রিম',
  'agent.manualSubAgentRecharge.loading':                     'আপনার পুল লোড হচ্ছে…',
  'agent.manualSubAgentRecharge.cta.cancel':                  'বাতিল',
  'agent.manualSubAgentRecharge.cta.send':                    '{amount} পাঠান',
  'agent.manualSubAgentRecharge.success.title':               '@{username}-কে {amount} পাঠানো হয়েছে',
  'agent.manualSubAgentRecharge.success.meta':                'আপনার পুল · {parent} · তাঁদের পুল · {child}',
  'agent.manualSubAgentRecharge.success.again':               'আরও পাঠান',

  // ── PlayerRequestsPanel ──────────────────────────────────────────
  'agent.playerRequests.direction.recharge':         'রিচার্জ',
  'agent.playerRequests.direction.withdrawal':       'উত্তোলন',
  'agent.playerRequests.tab.pending':                'মুলতুবি',
  'agent.playerRequests.tab.history':                'ইতিহাস',
  'agent.playerRequests.loading':                    'লোড হচ্ছে…',
  'agent.playerRequests.empty.pending.recharge':     'এই মুহূর্তে কোনো মুলতুবি রিচার্জ নেই।',
  'agent.playerRequests.empty.pending.withdrawal':   'এই মুহূর্তে কোনো মুলতুবি উত্তোলন নেই।',
  'agent.playerRequests.empty.history':              'এখনো কোনো ইতিহাস নেই।',
  'agent.playerRequests.prompt.rejectRecharge':      'প্রত্যাখ্যানের কারণ (ঐচ্ছিক):',
  'agent.playerRequests.prompt.rejectWithdrawal':    'প্রত্যাখ্যানের কারণ (খেলোয়াড়ের আটকে রাখা ব্যালেন্স ফেরত দেওয়া হবে):',
  'agent.playerRequests.toast.rejected':             'প্রত্যাখ্যাত',
  'agent.playerRequests.toast.rejectedRefunded':     'প্রত্যাখ্যাত · আটকে রাখা ব্যালেন্স ফেরত',
  'agent.playerRequests.toast.approved':             '{amount} অনুমোদিত',
  'agent.playerRequests.toast.approvedWithBonus':    '{amount} অনুমোদিত · মূল + ₹{bonus} বোনাস',
  'agent.playerRequests.toast.markedPaid':           '{amount} পরিশোধিত চিহ্নিত',
  'agent.playerRequests.card.upiTxnLabel':           'UPI টxn ID',
  'agent.playerRequests.card.payToUpiLabel':         'পেমেন্ট করুন (UPI)',
  'agent.playerRequests.card.whatsapp':              'WhatsApp',
  'agent.playerRequests.card.viewQr':                'QR দেখুন',
  'agent.playerRequests.card.bonusChip':             'বোনাস',
  'agent.playerRequests.card.partialApproved':       'অনুরোধিত {requested}-এর মধ্যে {approved} অনুমোদিত',
  'agent.playerRequests.card.partialPaid':           'অনুরোধিত {requested}-এর মধ্যে {approved} পরিশোধিত · ফেরত {refund}',
  'agent.playerRequests.card.paidTxnPrefix':         'পরিশোধিত · {txn}',
  'agent.playerRequests.action.reject':              'প্রত্যাখ্যান',
  'agent.playerRequests.action.approve':             'অনুমোদন',
  'agent.playerRequests.action.markPaid':            'পরিশোধিত চিহ্নিত',
  'agent.playerRequests.state.pending':              'মুলতুবি',
  'agent.playerRequests.state.rejected':             'প্রত্যাখ্যাত',
  'agent.playerRequests.state.expired':              'মেয়াদোত্তীর্ণ',
  'agent.playerRequests.state.accepted':             'গৃহীত',
  'agent.playerRequests.state.manuallyPaid':         'ম্যানুয়াল পরিশোধিত',
  'agent.peopleQueue.pagination':                    '{from}–{to} এর {total}',

  // ── AgentRequestsPanel ───────────────────────────────────────────
  'agent.agentRequests.direction.recharge':       'রিচার্জ',
  'agent.agentRequests.direction.withdrawal':     'উত্তোলন',
  'agent.agentRequests.tab.pending':              'মুলতুবি',
  'agent.agentRequests.tab.history':              'ইতিহাস',
  'agent.agentRequests.loading':                  'লোড হচ্ছে…',
  'agent.agentRequests.empty.pending.recharge':   'এই মুহূর্তে কোনো মুলতুবি রিচার্জ নেই।',
  'agent.agentRequests.empty.pending.withdrawal': 'এই মুহূর্তে কোনো মুলতুবি উত্তোলন নেই।',
  'agent.agentRequests.empty.history':            'এখনো কোনো ইতিহাস নেই।',
  'agent.agentRequests.prompt.rejectRecharge':    'প্রত্যাখ্যানের কারণ (ঐচ্ছিক):',
  'agent.agentRequests.prompt.rejectWithdrawal':  'প্রত্যাখ্যানের কারণ (সাব-এজেন্টের আটকে রাখা ব্যালেন্স ফেরত দেওয়া হবে):',
  'agent.agentRequests.toast.rejected':           'প্রত্যাখ্যাত',
  'agent.agentRequests.toast.rejectedRefunded':   'প্রত্যাখ্যাত · আটকে রাখা ব্যালেন্স ফেরত',
  'agent.agentRequests.toast.approved':           '{amount} অনুমোদিত · পুল ক্রেডিট',
  'agent.agentRequests.toast.markedPaid':         '{amount} পরিশোধিত চিহ্নিত',
  'agent.agentRequests.card.agentPill':           'এজেন্ট',
  'agent.agentRequests.card.upiTxnLabel':         'UPI টxn ID',
  'agent.agentRequests.card.cashMarkerLabel':     'নগদ চিহ্ন',
  'agent.agentRequests.card.payToUpiLabel':       'পেমেন্ট করুন (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel':  'হস্তান্তর চিহ্ন',
  'agent.agentRequests.card.whatsapp':            'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved':     'অনুরোধিত {requested}-এর মধ্যে {approved} অনুমোদিত',
  'agent.agentRequests.card.partialPaid':         'অনুরোধিত {requested}-এর মধ্যে {approved} পরিশোধিত · ফেরত {refund}',
  'agent.agentRequests.card.paidTxnPrefix':       'পরিশোধিত · {txn}',
  'agent.agentRequests.card.chip.deposit':        'ডিপোজিট',
  'agent.agentRequests.card.chip.cash':           'নগদ',
  'agent.agentRequests.card.chip.upi':            'UPI',
  'agent.agentRequests.action.reject':            'প্রত্যাখ্যান',
  'agent.agentRequests.action.approve':           'অনুমোদন',
  'agent.agentRequests.action.markPaid':          'পরিশোধিত চিহ্নিত',
  'agent.agentRequests.state.pending':            'মুলতুবি',
  'agent.agentRequests.state.rejected':           'প্রত্যাখ্যাত',
  'agent.agentRequests.state.expired':            'মেয়াদোত্তীর্ণ',
  'agent.agentRequests.state.accepted':           'গৃহীত',
  'agent.agentRequests.state.manuallyPaid':       'ম্যানুয়াল পরিশোধিত',

  // ── PlayersListPanel ─────────────────────────────────────────────
  'agent.playersList.add':                       'খেলোয়াড় যোগ',
  'agent.playersList.loading':                   'লোড হচ্ছে…',
  'agent.playersList.empty':                     'কোনো খেলোয়াড় মেলেনি',
  'agent.playersList.results':                   '{total}-এর {from}–{to} দেখাচ্ছে',
  'agent.playersList.searchPlaceholder':         'ইউজারনেম, ইমেল, ফোন খুঁজুন…',
  'agent.playersList.filter.all':                'সব',
  'agent.playersList.filter.active':             'সক্রিয়',
  'agent.playersList.filter.pending':            'মুলতুবি',
  'agent.playersList.filter.suspended':          'স্থগিত',
  'agent.playersList.filter.banned':             'নিষিদ্ধ',
  'agent.playersList.empty.title':               'কোনো খেলোয়াড় পাওয়া যায়নি',
  'agent.playersList.empty.body':                'অন্য অনুসন্ধান বা স্থিতি ফিল্টার চেষ্টা করুন।',
  'agent.playersList.pagination':                'পেজ {page} / {totalPages}',
  'agent.playersList.card.status.active':        'সক্রিয়',
  'agent.playersList.card.status.pending':       'মুলতুবি',
  'agent.playersList.card.status.suspended':     'স্থগিত',
  'agent.playersList.card.status.banned':        'নিষিদ্ধ',
  'agent.playersList.card.verifiedBadge':        'যাচাইকৃত',
  'agent.playersList.column.wallet':             'ওয়ালেট',
  'agent.playersList.column.joined':             'যোগ দিয়েছেন',

  // ── SubagentsListPanel ───────────────────────────────────────────
  'agent.subAgentsList.intro':                                'আপনি যাঁদের তৈরি করেন তাঁরা আপনার অধীনে কাজ করেন। তাঁরা প্রতিটি পিলারে ০% থেকে শুরু করেন — তাঁদের বিবরণ পেজ থেকে প্রতি-গেম রেট দিন।',
  'agent.subAgentsList.cta.new':                              'নতুন সাব-এজেন্ট',
  'agent.subAgentsList.loading':                              'লোড হচ্ছে…',
  'agent.subAgentsList.empty.title':                          'এখনো কোনো সাব-এজেন্ট নেই',
  'agent.subAgentsList.empty.body.canCreate':                 '"+ নতুন সাব-এজেন্ট" ট্যাপ করে একজন তৈরি করুন। তাঁরা এখানে নিজের পুল, খেলোয়াড় এবং আয় সহ দেখাবেন — আপনি পরিচালনা করবেন।',
  'agent.subAgentsList.empty.body.cantCreate':                'আপনার এডমিন এখনো আপনাকে সাব-এজেন্ট তৈরির অধিকার দেননি। তাঁদের বলুন আপনার অনুমতিতে create_subagent-কে "edit"-এ পরিবর্তন করতে।',
  'agent.subAgentsList.card.suspendedBadge':                  'স্থগিত',
  'agent.subAgentsList.column.agents':                        'এজেন্ট',
  'agent.subAgentsList.column.players':                       'খেলোয়াড়',
  'agent.subAgentsList.column.pool':                          'পুল',
  'agent.subAgentsList.row.directLabel':                      'সরাসরি',
  'agent.subAgentsList.row.indirectLabel':                    'পরোক্ষ',
  'agent.subAgentsList.modal.title':                          'নতুন সাব-এজেন্ট',
  'agent.subAgentsList.modal.body':                           'তাঁরা প্রতিটি পিলারে ০% থেকে শুরু করবেন। তৈরির পর তাঁদের বিবরণ পেজ থেকে প্রতি-গেম রেট দিন।',
  'agent.subAgentsList.modal.field.username.label':           'ইউজারনেম',
  'agent.subAgentsList.modal.field.username.placeholder':     'অক্ষর-সংখ্যা, ._- অনুমোদিত; ন্যূনতম 3 অক্ষর',
  'agent.subAgentsList.modal.field.email.label':              'ইমেল',
  'agent.subAgentsList.modal.field.email.placeholder':        'they@example.com',
  'agent.subAgentsList.modal.field.password.label':           'প্রাথমিক পাসওয়ার্ড',
  'agent.subAgentsList.modal.field.password.placeholder':     'ন্যূনতম 8 অক্ষর',
  'agent.subAgentsList.modal.field.password.hint':            'নতুন এজেন্টের সাথে শেয়ার করুন যাতে তাঁরা লগইন করতে পারেন। প্রথম লগইনে তাঁদের এটি পরিবর্তন করা উচিত।',
  'agent.subAgentsList.modal.field.location.label':           'সাব-এজেন্টের অবস্থান',
  'agent.subAgentsList.modal.field.location.optional':        '(দৃঢ়ভাবে সুপারিশকৃত)',
  'agent.subAgentsList.modal.field.location.missing':         'অবস্থান ছাড়া ভৌগোলিক লোকেটর এই সাব-এজেন্টকে কোনো নতুন খেলোয়াড় পাঠাতে পারবে না। তাঁরা শুধু সরাসরি রেফারেলে খেলোয়াড় পাবেন যতক্ষণ না সাইন ইন করে নিজের GPS সম্পূর্ণ করেন।',
  'agent.subAgentsList.modal.cta.cancel':                     'বাতিল',
  'agent.subAgentsList.modal.cta.create':                     'সাব-এজেন্ট তৈরি',
  'agent.subAgentsList.modal.cta.creating':                   'তৈরি হচ্ছে…',

  // ── ApprovalEditModal ────────────────────────────────────────────
  'agent.approvalEdit.title.recharge':                  '{who} থেকে রিচার্জ অনুমোদন',
  'agent.approvalEdit.title.withdrawal':                '{who}-এর উত্তোলন পরিশোধিত চিহ্নিত',
  'agent.approvalEdit.requestedLabel':                  'অনুরোধিত:',
  'agent.approvalEdit.field.amount.label':              'অনুমোদিত পরিমাণ (₹)',
  'agent.approvalEdit.field.amount.hint':               'অনুরোধিত {amount}-এর বেশি হতে পারে না।',
  'agent.approvalEdit.partial.withdrawal.refund':       '{refund} স্বয়ংক্রিয়ভাবে {who}-এর ওয়ালেটে ফেরত দেওয়া হবে।',
  'agent.approvalEdit.partial.recharge.bonusWarn':      'আংশিক অনুমোদন। সংযুক্ত যেকোনো বোনাস স্বয়ংক্রিয়ভাবে প্রত্যাখ্যাত হবে।',
  'agent.approvalEdit.field.reason.label':              'কারণ',
  'agent.approvalEdit.field.reason.requiredHint':       '(আবশ্যক, ≥5 অক্ষর)',
  'agent.approvalEdit.field.reason.optionalHint':       '(ঐচ্ছিক)',
  'agent.approvalEdit.field.reason.placeholder.partial':'যেমন খেলোয়াড় শুধু 30 টাকা দিয়েছেন',
  'agent.approvalEdit.field.reason.placeholder.optional':'ঐচ্ছিক নোট',
  'agent.approvalEdit.field.txn.label':                 'UPI লেনদেন ID',
  'agent.approvalEdit.field.txn.placeholder':           'যেমন 412312345678',
  'agent.approvalEdit.error.insufficient':              'আপনার পুল ({balance}) এই অনুমোদন কভার করতে পারবে না। প্রথমে টপ-আপ করুন।',
  'agent.approvalEdit.error.amountMin':                 'অনুমোদিত পরিমাণ কমপক্ষে ₹1 হতে হবে',
  'agent.approvalEdit.error.amountMax':                 'অনুমোদিত পরিমাণ অনুরোধিত {amount}-এর বেশি হতে পারে না',
  'agent.approvalEdit.error.reasonRequired':            'ভিন্ন পরিমাণ অনুমোদন করার সময় কারণ (≥5 অক্ষর) আবশ্যক',
  'agent.approvalEdit.error.txnRequired':               'UPI লেনদেন ID আবশ্যক (≥6 অক্ষর)',
  'agent.approvalEdit.error.fallback':                  'ব্যর্থ — আবার চেষ্টা করুন',
  'agent.approvalEdit.cta.cancel':                      'বাতিল',
  'agent.approvalEdit.cta.approve':                     'অনুমোদন',
  'agent.approvalEdit.cta.markPaid':                    'পরিশোধিত চিহ্নিত',
  'agent.approvalEdit.cta.submitting':                  'পাঠানো হচ্ছে…',

  // ── ProfileHub + Sign-out modal ──────────────────────────────────
  'agent.profile.hub.title':                'আমার অ্যাকাউন্ট',
  'agent.profile.hub.subtitle':             'পরিচয়, ওয়ালেট এবং ডিপোজিট এক জায়গায়',
  'agent.profile.hub.tab.wallet':           'ওয়ালেট',
  'agent.profile.hub.tab.deposit':          'ডিপোজিট',
  'agent.profile.hub.tab.account':          'অ্যাকাউন্ট',
  'agent.profile.signOut.cta':              'সাইন আউট',
  'agent.profile.signOut.modal.title':      'সাইন আউট?',
  'agent.profile.signOut.modal.body':       'অ্যাকাউন্টে প্রবেশের জন্য আপনাকে আবার সাইন ইন করতে হবে।',
  'agent.profile.signOut.modal.stay':       'সাইন ইন থাকুন',
  'agent.profile.signOut.modal.confirm':    'হ্যাঁ, সাইন আউট',
  'agent.profile.signOut.modal.busy':       'সাইন আউট হচ্ছে…',
  'agent.profile.signOut.error':            'সাইন আউট করা যায়নি',

  // ── AccountPanel ─────────────────────────────────────────────────
  'agent.account.notSignedIn':              'সাইন ইন নেই।',
  'agent.account.hero.role.agent':          'এজেন্ট',
  'agent.account.hero.role.admin':          'প্ল্যাটফর্ম এডমিন',
  'agent.account.hero.changePhoto':         'ফটো পরিবর্তন',
  'agent.account.hero.removePhoto':         'ফটো সরান',
  'agent.account.avatar.cropTitle':         'আপনার ফটো সমন্বয় করুন',
  'agent.account.avatar.confirmRemove':     'আপনার প্রোফাইল ফটো সরাবেন?',
  'agent.account.toast.photoUpdated':       'ফটো আপডেট হয়েছে',
  'agent.account.toast.photoRemoved':       'ফটো সরানো হয়েছে',
  'agent.account.toast.saved':              'সংরক্ষিত',
  'agent.account.toast.locationSaved':      'অবস্থান সংরক্ষিত',
  'agent.account.toast.passwordChanged':    'পাসওয়ার্ড পরিবর্তিত — আবার সাইন ইন করুন',
  'agent.account.error.uploadFailed':       'আপলোড ব্যর্থ',
  'agent.account.error.removeFailed':       'ব্যর্থ',
  'agent.account.error.saveFailed':         'সংরক্ষণ ব্যর্থ',
  'agent.account.error.changeFailed':       'পরিবর্তন ব্যর্থ',
  'agent.account.info.title':               'অ্যাকাউন্ট তথ্য',
  'agent.account.info.subtitle':            'শুধু পঠনযোগ্য প্ল্যাটফর্ম মেটাডেটা',
  'agent.account.info.memberSince':         'সদস্য কখন থেকে',
  'agent.account.info.lastLogin':           'শেষ লগইন',
  'agent.account.info.lastLoginIp':         'শেষ লগইন IP',
  'agent.account.info.reportsTo':           'কাকে রিপোর্ট করেন',
  'agent.account.info.reportsToValue':      'এডমিন #{id}',
  'agent.account.info.agentId':             'এজেন্ট ID',
  'agent.account.info.firstLogin':          'প্রথমবার',
  'agent.account.info.emptyDash':           '—',
  'agent.account.personal.title':                    'ব্যক্তিগত বিবরণ',
  'agent.account.personal.subtitle.locked':          'এডমিন পরিচয় লক করেছেন · শুধু দেখুন',
  'agent.account.personal.subtitle.agent':           'ইউজারনেম, ইমেল এবং যোগাযোগ নম্বর',
  'agent.account.personal.subtitle.admin':           'ইউজারনেম এবং ইমেল',
  'agent.account.personal.field.username':           'ইউজারনেম',
  'agent.account.personal.field.email':              'ইমেল',
  'agent.account.personal.field.phone':              'যোগাযোগ নম্বর',
  'agent.account.personal.field.phone.placeholder':  'যেমন +91 9876543210',
  'agent.account.personal.field.phone.hint':         'খেলোয়াড় এবং এডমিন এই নম্বরে আপনার সাথে যোগাযোগ করেন। মুছতে খালি রাখুন।',
  'agent.account.personal.fieldLockedNote':          'লক',
  'agent.account.personal.cta.save':                 'পরিবর্তন সংরক্ষণ',
  'agent.account.personal.cta.saving':               'সংরক্ষণ হচ্ছে…',
  'agent.account.personal.cta.noChanges':            'কোনো পরিবর্তন নেই',
  'agent.account.location.title':                    'আপনার অবস্থান',
  'agent.account.location.subtitle.summary':        'লোকেটর নতুন খেলোয়াড়দের আপনার নিকটতম এলাকায় পাঠায়',
  'agent.account.location.subtitle.edit':           'আপনার এলাকা আপডেট করুন যাতে লোকেটর সঠিক খেলোয়াড় পাঠায়',
  'agent.account.location.cta.edit':                'সম্পাদনা',
  'agent.account.location.cta.cancel':              'বাতিল',
  'agent.account.location.cta.save':                'সংরক্ষণ',
  'agent.account.location.areaLabel':               'এলাকা',
  'agent.account.location.gpsLabel':                'GPS',
  'agent.account.location.setLabel':                'অবস্থান সেট',
  'agent.account.location.setBody':                 'ফাইলে GPS স্থানাঙ্ক — এলাকা বিবরণ দেখতে সম্পাদনা ট্যাপ করুন।',
  'agent.account.location.lastUpdated':             'শেষ আপডেট · {date}',
  'agent.account.security.title':                   'সিকিউরিটি',
  'agent.account.security.subtitle.locked':         'এডমিন লক করেছেন · শুধু দেখুন',
  'agent.account.security.subtitle.unlocked':       'আপনার পাসওয়ার্ড পরিবর্তন · অন্য ডিভাইস থেকে সাইন আউট হবে',
  'agent.account.security.field.current':           'বর্তমান পাসওয়ার্ড',
  'agent.account.security.field.new':               'নতুন পাসওয়ার্ড',
  'agent.account.security.field.new.hint':          'কমপক্ষে 8 অক্ষর, একটি অক্ষর এবং একটি অঙ্ক সহ',
  'agent.account.security.field.confirm':           'নতুন নিশ্চিত করুন',
  'agent.account.security.cta.update':              'পাসওয়ার্ড আপডেট',
  'agent.account.security.cta.updating':            'আপডেট হচ্ছে…',
  'agent.account.security.error.mismatch':          'নতুন পাসওয়ার্ড মিলছে না',
  'agent.account.security.error.tooShort':          'পাসওয়ার্ড ≥ 8 অক্ষরের হতে হবে',
  'agent.account.security.error.weak':              'পাসওয়ার্ডে কমপক্ষে একটি অক্ষর এবং একটি সংখ্যা থাকতে হবে',

  // ── ShareableIdCard ──────────────────────────────────────────────
  'agent.shareableId.eyebrow':           'শেয়ারযোগ্য এজেন্ট ID',
  'agent.shareableId.tooltip.copy':      'UAID কপি',
  'agent.shareableId.tooltip.copied':    'UAID কপি হয়েছে',
  'agent.shareableId.cta.copy':          'কপি',
  'agent.shareableId.cta.copied':        'কপি হয়েছে!',
  'agent.shareableId.body.before':       'এই কোডটি অন্য এজেন্টের সাথে শেয়ার করুন যাতে তাঁরা খেলোয়াড় আপনাকে স্থানান্তর করতে পারেন। চ্যাট বা WhatsApp-এর মাধ্যমে পাঠানো নিরাপদ — এটি শেয়ার করা আপনার অ্যাকাউন্টে কোনো প্রবেশাধিকার ',
  'agent.shareableId.body.not':          'দেয় না',
  'agent.shareableId.body.after':        '।',

  // ── RewardsHub + sub-tabs ────────────────────────────────────────
  'agent.rewards.hub.title':            'পুরস্কার',
  'agent.rewards.hub.subtitle':         'অফার দেখুন এবং আপনার এক্সপোজার ট্র্যাক করুন',
  'agent.rewards.hub.tab.browse':       'অফার দেখুন',
  'agent.rewards.hub.tab.exposure':     'আমার এক্সপোজার',
  'agent.rewards.status.optedIn':       'অপ্ট ইন',
  'agent.rewards.status.optedOut':      'অপ্ট আউট',

  // ── BrowseOffersPanel ────────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow':         'আপনার খেলোয়াড়দের জন্য বিশেষ',
  'agent.rewards.browse.empty.title':          'এই মুহূর্তে কোনো সক্রিয় পুরস্কার নেই',
  'agent.rewards.browse.empty.body':           'এডমিন নিয়মিত নতুন অফার পোস্ট করেন। শীঘ্রই দেখুন — বা যা মিস করেছেন তাতে অপ্ট ইন করার জন্য আপলাইনকে বলুন।',
  'agent.rewards.browse.error.loadFailed':     'পুরস্কার লোড করা যায়নি',
  'agent.rewards.browse.card.aria.manage':     '{title}-এর অপ্ট-ইন পরিচালনা',
  'agent.rewards.browse.chip.optedIn':         'অপ্ট ইন',
  'agent.rewards.browse.chip.optInPrompt':     'অপ্ট ইন করতে ট্যাপ করুন',
  'agent.rewards.browse.chip.shareSuffix':     '· {pct}%',

  // ── ExposurePanel ────────────────────────────────────────────────
  'agent.rewards.exposure.loading':            'আপনার এক্সপোজার গণনা হচ্ছে…',
  'agent.rewards.exposure.error.loadFailed':   'এক্সপোজার লোড করা যায়নি',
  'agent.rewards.exposure.hero.eyebrow':                'অফার এক্সপোজার',
  'agent.rewards.exposure.hero.activeOptIns.one':       '{count} সক্রিয় অপ্ট-ইন',
  'agent.rewards.exposure.hero.activeOptIns.other':     '{count} সক্রিয় অপ্ট-ইন',
  'agent.rewards.exposure.hero.actualSpend.label':      'প্রকৃত খরচ',
  'agent.rewards.exposure.hero.actualSpend.helper':     'এ পর্যন্ত আপনার ডাউনলাইন দ্বারা দাবিকৃত।',
  'agent.rewards.exposure.hero.maxExposure.label':      'সর্বাধিক এক্সপোজার',
  'agent.rewards.exposure.hero.maxExposure.helper':     'সবচেয়ে খারাপ পরিস্থিতির সীমা — প্রতিটি অবশিষ্ট বাজেট রুপি দাবিকৃত।',
  'agent.rewards.exposure.hero.ceiling.label':          'সীমা ব্যবহৃত',
  'agent.rewards.exposure.gauge.label':                 'ঝুঁকি গেজ',
  'agent.rewards.exposure.gauge.maxSuffix':             '/ {amount} সর্বাধিক',
  'agent.rewards.exposure.gauge.legend.amber.before':   '-এ ',
  'agent.rewards.exposure.gauge.legend.amber.value':    '৫০%',
  'agent.rewards.exposure.gauge.legend.amber.after':    ', আপনার অপ্ট-ইন লক্ষ্য রাখুন',
  'agent.rewards.exposure.gauge.legend.rose.before':    '-এ ',
  'agent.rewards.exposure.gauge.legend.rose.value':     '৮০%',
  'agent.rewards.exposure.gauge.legend.rose.after':     ', আপনি আপনার সীমার কাছে',
  'agent.rewards.exposure.gauge.caption.noMax':         'আপনি এখনো এমন কিছুতে অপ্ট ইন নন যার অনুমিত সীমা আছে।',
  'agent.rewards.exposure.gauge.caption.high':          'আপনি আপনার সর্বাধিক এক্সপোজারের {pct}% খরচ করেছেন। বেশিরভাগ অপ্ট-ইন ভারীভাবে দাবিকৃত।',
  'agent.rewards.exposure.gauge.caption.mid':           'আপনি আপনার সর্বাধিক সীমার {pct}%-এ। প্রতি-অফার বিবরণে নজর রাখুন।',
  'agent.rewards.exposure.gauge.caption.low':           'আরামদায়ক — এ পর্যন্ত আপনার সর্বাধিক সীমার মাত্র {pct}% ব্যবহৃত।',
  'agent.rewards.exposure.footnote.heading':            'সর্বাধিক এক্সপোজার কীভাবে গণনা হয়:',
  'agent.rewards.exposure.footnote.prefix':             ' প্রতিটি অপ্ট-ইন অফারের জন্য: ',
  'agent.rewards.exposure.footnote.formula':            'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix':             ' — রক্ষণশীল ঊর্ধ্ব সীমা। আপনার প্রকৃত দায় সাধারণত ',
  'agent.rewards.exposure.footnote.emphasis':           'কম',
  'agent.rewards.exposure.footnote.suffix2':            ' হয় যখন আপনার নিচের সাব-এজেন্টরাও অপ্ট ইন করেন (তাঁরা বড় অংশ দেন; আপনি অবশিষ্ট দেন)।',
  'agent.rewards.exposure.empty.title':                 'কোনো সক্রিয় অপ্ট-ইন নেই',
  'agent.rewards.exposure.empty.body':                  'আপনি অফার দেখুন ট্যাব থেকে কোনো অফারে অপ্ট ইন করলে এটি এখানে আপনার খরচ এবং অনুমিত এক্সপোজার সহ দেখাবে।',
  'agent.rewards.exposure.row.share':                   '{pct}% অংশ',
  'agent.rewards.exposure.row.claims.one':              '{count} দাবি',
  'agent.rewards.exposure.row.claims.other':            '{count} দাবি',
  'agent.rewards.exposure.row.overridden':              'এডমিন-অভারাইডেড',
  'agent.rewards.exposure.row.actualMaxLabel':          'প্রকৃত / সর্বাধিক',
  'agent.rewards.exposure.row.noMaxDash':               '—',

  // ── OfferOptInModal ──────────────────────────────────────────────
  'agent.rewards.optInModal.eyebrow':                    'খরচ-ভাগ অপ্ট-ইন',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'গেম মুনাফা',
  'agent.profile.rates.infoBanner.standardPctText': ' প্রমিত শতাংশ ব্যবহার করে। ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'প্রচারমূলক খরচ',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (অফার, ক্যাশব্যাক) একটি ডায়নামিক আপলাইন পুল ব্যবহার করে। উভয়ই আপনার স্তর (L{level}) অনুসারে প্ল্যাটফর্ম অ্যাডমিন দ্বারা গ্লোবালি পরিচালিত হয়।',
  'agent.profile.rates.section.gameProfits': 'গেম মুনাফা — প্রতি ম্যাচে আপনি যা আয় করেন',
  'agent.profile.rates.section.promotionalCosts': 'প্রচারমূলক খরচ — আপনার আপলাইন ভর্তুকি অবদান',
  'agent.profile.rates.pillar.platformFee': 'প্ল্যাটফর্ম ফি %',
  'agent.profile.rates.pillar.winLoss': 'জয় / পরাজয় মুনাফা %',
  'agent.profile.rates.pillar.promoWeight': 'প্রচারমূলক পুল ওজন',
  'agent.profile.rates.guarantee.title': 'ডাইরেক্ট রেফারেল গ্যারান্টি',
  'agent.profile.rates.guarantee.body': 'যদি আপনি সরাসরি অফার দাবি করা প্লেয়ারকে রেফার করেন, তবে আপনি ₹0.00 দেন। আপনার আপলাইন আপনার নেটওয়ার্ক বাড়াতে সহায়তা করার জন্য 100% খরচ ভর্তুকি দেয়।',
  'agent.profile.rates.poolExplainer.title': 'পুল কীভাবে কাজ করে:',
  'agent.profile.rates.poolExplainer.body': 'যখন একজন সাব-এজেন্টের প্লেয়ার অফার দাবি করে, আপনার ওজন বাকি সক্রিয় আপলাইন + প্ল্যাটফর্মের সাথে একটি পুলে যোগ হয়। আপনি (আপনার ওজন / মোট পুল) × অফার খরচ দেন। বেশি ওজন = বড় অংশ।',
  'agent.profile.rates.weightLabel': 'ওজন:',
  'agent.profile.rates.updatedPrefix': 'আপডেট হয়েছে',
  'agent.profile.rates.notSetYet': 'এখনও সেট করা হয়নি',
  'agent.rewards.optInModal.guarantee.title': 'ডাইরেক্ট রেফারেল গ্যারান্টি',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'যদি <strong>আপনি সরাসরি রেফার করেছেন</strong> এই অফার দাবি করা প্লেয়ারকে, তাহলে আপনি <strong>₹0.00</strong> দেন। আপনার আপলাইন আপনার নেটওয়ার্ক বাড়াতে সহায়তার জন্য 100% খরচ ভর্তুকি দেয়।',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'যখন <strong>আপনার প্লেয়াররা</strong> এই অফার দাবি করে, তখন আপনি <strong>₹0.00</strong> দেন। আপনার আপলাইন আপনার নেটওয়ার্ক বাড়াতে সহায়তার জন্য 100% খরচ ভর্তুকি দেয়।',
  'agent.rewards.optInModal.uplineReality.title': 'আপলাইন বাস্তবতা যাচাই',
  'agent.rewards.optInModal.uplineReality.body': 'আপনার <strong>{count}</strong> ডাইরেক্ট সাব-এজেন্ট{plural} আছে। যদি কোনো সাব-এজেন্টের প্লেয়ার এই অফার দাবি করে, আপনি আপনার <strong>পুল ওজন ({weight})</strong> অনুসারে বাকি সক্রিয় আপলাইন এবং প্ল্যাটফর্মের তুলনায় একটি আনুপাতিক অংশ দেবেন।',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ডায়রেক্ট',
  'agent.rates.col.indirect': 'ইনডায়রেক্ট',
  'agent.rates.indirect.dynamic': 'ডায়নামিক',
  'agent.rates.indirect.notApplicable': 'প্রযোজ্য নয়',
  'agent.rates.indirect.rangeTo': 'থেকে',
  'agent.rewards.optInModal.close':                      'বন্ধ',
  'agent.rewards.optInModal.cancel':                     'বাতিল',
  'agent.rewards.optInModal.loading':                    'অফার বিবরণ লোড হচ্ছে…',
  'agent.rewards.optInModal.error.loadFailed':           'অফার বিবরণ লোড করা যায়নি',
  'agent.rewards.optInModal.error.optInFailed':          'অপ্ট-ইন ব্যর্থ',
  'agent.rewards.optInModal.error.optOutFailed':         'অপ্ট-আউট ব্যর্থ',
  'agent.rewards.optInModal.error.saveFailed':           'সাব-এজেন্ট রেট সংরক্ষণ করা যায়নি',
  'agent.rewards.optInModal.toast.optedIn':              'অপ্ট ইন। এখন আপনার ডাউনলাইন এই অফার দেখতে পাবে।',
  'agent.rewards.optInModal.toast.optedOut':             'অপ্ট আউট। অফার আপনার ডাউনলাইন থেকে সরানো হয়েছে।',
  'agent.rewards.optInModal.toast.saved.one':            '{count} সাব-এজেন্ট রেট পরিবর্তন সংরক্ষিত।',
  'agent.rewards.optInModal.toast.saved.other':          '{count} সাব-এজেন্ট রেট পরিবর্তন সংরক্ষিত।',
  'agent.rewards.optInModal.confirmOptOut':              'এই অফার থেকে অপ্ট আউট? আপনার ডাউনলাইন আর এটি দেখবে না।',
  'agent.rewards.optInModal.auditNote':                  'অফার #{id}-এর জন্য অপ্ট-ইন মডেল থেকে সেট',
  'agent.rewards.optInModal.stake.shareLabel':           'আপনার অংশ',
  'agent.rewards.optInModal.stake.locked':               'অপ্ট-ইন-এ লক',
  'agent.rewards.optInModal.stake.current':              'বর্তমান রেট',
  'agent.rewards.optInModal.stake.noRate':               'এডমিন এখনো আপনাকে কোনো রেট দেননি',
  'agent.rewards.optInModal.stake.spentSoFar.label':     'এ পর্যন্ত খরচ',
  'agent.rewards.optInModal.stake.spentSoFar.helper':    'এই অফারে ডাউনলাইন দাবির জন্য আপনার ওয়ালেট থেকে পরিশোধিত মোট রুপি।',
  'agent.rewards.optInModal.stake.maxPossible.label':    'সর্বাধিক সম্ভব',
  'agent.rewards.optInModal.stake.maxPossible.helper':   'সবচেয়ে খারাপ পরিস্থিতিতে যদি প্রতিটি অবশিষ্ট বাজেট রুপি দাবিকৃত হয় তাহলে আপনি যত রুপি দিতে পারেন।',
  'agent.rewards.optInModal.stake.maxPossible.noCap':    '— কোনো বাজেট সীমা নেই —',
  'agent.rewards.optInModal.stake.worstCase':            'সবচেয়ে খারাপ পরিস্থিতি: প্ল্যাটফর্ম বাজেটের অবশিষ্ট প্রতিটি ₹{amount} যদি আপনার ডাউনলাইন দ্বারা দাবিকৃত হয়, তাহলে আপনি তার {pct}% পরিশোধ করেন।',
  'agent.rewards.optInModal.toggle.title.optedIn':       'আপনি অপ্ট ইন',
  'agent.rewards.optInModal.toggle.title.optedOut':      'আপনার ডাউনলাইনের সাথে শেয়ার করতে অপ্ট ইন করুন',
  'agent.rewards.optInModal.toggle.body.optedIn':        'আপনার ডাউনলাইনের খেলোয়াড়রা এই অফার দেখেন। আপনি তাঁদের প্রতিটি দাবিতে আপনার লক্ড অংশে সহ-ফান্ড করেন।',
  'agent.rewards.optInModal.toggle.body.optedOut':       'যতক্ষণ না আপনি অপ্ট ইন করেন, কোনো খেলোয়াড় এই অফার দেখবেন না। অপ্ট ইন করলে আপনার অংশের রেট এই অফারের জন্য লক হয়ে যায়।',
  'agent.rewards.optInModal.toggle.optOutLink':          'এই অফার থেকে অপ্ট আউট',
  'agent.rewards.optInModal.subAgents.title':            'সাব-এজেন্ট রেট',
  'agent.rewards.optInModal.subAgents.ceilingHint.before':'প্রত্যেকে সর্বাধিক নিতে পারেন: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved':          '{count} অসংরক্ষিত',
  'agent.rewards.optInModal.subAgents.empty':            'কোনো সরাসরি সাব-এজেন্ট নেই।',
  'agent.rewards.optInModal.subAgents.cascadeNote':      'সংরক্ষণ ক্যাসকেড হয় — আপনি এখানে কোনো শিশুকে বাড়ালে যাঁর ইতিমধ্যে গভীর সাব-এজেন্ট আছে, সেই রেটগুলি চেইন সঙ্গতিপূর্ণ রাখতে স্বয়ংক্রিয়ভাবে ক্ল্যাম্প হতে পারে।',
  'agent.rewards.optInModal.subAgents.noChildren':       'আপনার কোনো সরাসরি সাব-এজেন্ট নেই। অফার আপনার নিজের খেলোয়াড়দের দেখায়; এডমিন সম্পূর্ণ অবশিষ্ট পরিশোধ করেন।',
  'agent.rewards.optInModal.error.rateRequired':         'একটি রেট লিখুন (মুছতে 0)',
  'agent.rewards.optInModal.error.numbersOnly':          'শুধু সংখ্যা',
  'agent.rewards.optInModal.error.negative':             'ঋণাত্মক হতে পারে না',
  'agent.rewards.optInModal.error.max100':               'সর্বাধিক 100',
  'agent.rewards.optInModal.error.aboveCeiling':         'সর্বাধিক {ceiling}% (আপনার অংশ)',
  'agent.rewards.optInModal.cta.saving':                 'সংরক্ষণ হচ্ছে…',
  'agent.rewards.optInModal.cta.done':                   'হয়ে গেছে',
  'agent.rewards.optInModal.cta.save.one':               '{count} পরিবর্তন সংরক্ষণ',
  'agent.rewards.optInModal.cta.save.other':             '{count} পরিবর্তন সংরক্ষণ',

  // ════════════════════════════════════════════════════════════════════
  // AGENT APP — Bengali translations (Phase C Batch 2, Chunk 3 of 3).
  // Covers Phase B Groups 8-10: admin pages, login, long-tail sweep.

  // ── AdminRechargePlayer ──────────────────────────────────────────
  'agent.adminRechargePlayer.title':     'খেলোয়াড়কে রিচার্জ করুন',
  'agent.adminRechargePlayer.subtitle':  'আপনার পুল থেকে খেলোয়াড়ের ওয়ালেটে',
  'agent.adminRechargePlayer.backLink':  'খেলোয়াড়ে ফিরে যান',

  // ── CreatePlayerModal ────────────────────────────────────────────
  'agent.createPlayer.title':                          'নতুন খেলোয়াড় যোগ করুন',
  'agent.createPlayer.subtitle.agent':                 'তাঁরা স্বয়ংক্রিয়ভাবে আপনার অধীনে থাকবেন।',
  'agent.createPlayer.subtitle.admin':                 'নিচে মালিক এজেন্ট বাছুন, বা প্ল্যাটফর্ম-ডাইরেক্টের জন্য খালি রাখুন।',
  'agent.createPlayer.close':                          'বন্ধ',
  'agent.createPlayer.section.identity.title':         'পরিচয়',
  'agent.createPlayer.section.identity.hint':          'অ্যাকাউন্ট তৈরির জন্য আবশ্যক',
  'agent.createPlayer.field.username.label':           'ইউজারনেম',
  'agent.createPlayer.field.username.placeholder':     'kumar_42',
  'agent.createPlayer.field.email.label':              'ইমেল',
  'agent.createPlayer.field.email.placeholder':        'kumar@example.com',
  'agent.createPlayer.field.phone.label':              'মোবাইল',
  'agent.createPlayer.field.phone.placeholder':        '9876543210',
  'agent.createPlayer.field.phone.hint':               '10-অঙ্কের ভারতীয় মোবাইল, +91 স্বয়ংক্রিয়ভাবে যোগ হবে',
  'agent.createPlayer.field.password.label':           'প্রাথমিক পাসওয়ার্ড',
  'agent.createPlayer.field.password.placeholder':     'StrongP4ss',
  'agent.createPlayer.field.password.hint':            '8+ অক্ষর, অক্ষর + অঙ্ক',
  'agent.createPlayer.section.owner.title':            'মালিক এজেন্ট',
  'agent.createPlayer.section.owner.hint':             'ঐচ্ছিক — প্ল্যাটফর্ম-ডাইরেক্ট খেলোয়াড়ের জন্য খালি রাখুন',
  'agent.createPlayer.field.owner.label':              'এজেন্ট',
  'agent.createPlayer.field.owner.none':               '— কেউ নয় (প্ল্যাটফর্ম-ডাইরেক্ট) —',
  'agent.createPlayer.section.profile.title':          'প্রোফাইল',
  'agent.createPlayer.section.profile.hint':           'ঐচ্ছিক — যা এখন জানা সেটাই দিন; খেলোয়াড় প্রথম লগইনে সম্পূর্ণ করতে পারেন',
  'agent.createPlayer.field.firstName.label':          'প্রথম নাম',
  'agent.createPlayer.field.lastName.label':           'শেষ নাম',
  'agent.createPlayer.field.dob.label':                'জন্ম তারিখ',
  'agent.createPlayer.field.gender.label':             'লিঙ্গ',
  'agent.createPlayer.gender.unspecified':             '— উল্লেখ নেই —',
  'agent.createPlayer.gender.male':                    'পুরুষ',
  'agent.createPlayer.gender.female':                  'মহিলা',
  'agent.createPlayer.gender.other':                   'অন্য',
  'agent.createPlayer.gender.preferNot':              'বলতে চাই না',
  'agent.createPlayer.field.language.label':           'পছন্দের ভাষা',
  'agent.createPlayer.section.location.title':         'অবস্থান',
  'agent.createPlayer.section.location.hint':          'ঐচ্ছিক — খেলোয়াড় প্রথম লগইনে সম্পূর্ণ করতে পারেন',
  'agent.createPlayer.disclaimer':                     'খেলোয়াড়কে status=active এবং ইমেল অটো-যাচাইকৃত হিসেবে তৈরি করা হয় — কোনো লিঙ্ক পাঠানো হয় না। নিশ্চিত করুন যে আপনি যোগাযোগের তথ্যের উপর বিশ্বাস করেন।',
  'agent.createPlayer.cta.cancel':                     'বাতিল',
  'agent.createPlayer.cta.creating':                   'তৈরি হচ্ছে…',
  'agent.createPlayer.cta.submit':                     'খেলোয়াড় তৈরি',
  'agent.createPlayer.error.missingAgentId':           'এজেন্ট ID নেই',
  'agent.createPlayer.error.createFailed':             'খেলোয়াড় তৈরি করা যায়নি',

  // ── AdminHome dashboard ──────────────────────────────────────────
  'agent.adminHome.range.today':                       'আজ',
  'agent.adminHome.range.yesterday':                   'গতকাল',
  'agent.adminHome.range.week':                        'এই সপ্তাহে',
  'agent.adminHome.range.lastWeek':                    'গত সপ্তাহে',
  'agent.adminHome.range.month':                       'এই মাসে',
  'agent.adminHome.range.lastMonth':                   'গত মাসে',
  'agent.adminHome.range.all':                         'সম্পূর্ণ',
  'agent.adminHome.greeting.subtitle':                 'আবার স্বাগতম',
  'agent.adminHome.greeting.fallback':                 'বন্ধু',
  'agent.adminHome.errorBanner.fallback':              'লোড করতে ব্যর্থ',
  'agent.adminHome.admin.hero.label':                  'প্ল্যাটফর্ম ইনফ্লো',
  'agent.adminHome.admin.action.moneyQueue':           'মানি কিউ',
  'agent.adminHome.admin.action.agents':               'এজেন্ট',
  'agent.adminHome.admin.snapshot.matches':            'ম্যাচ',
  'agent.adminHome.admin.snapshot.newSignups':         'নতুন সাইনআপ',
  'agent.adminHome.admin.snapshot.active':             'সক্রিয়',
  'agent.adminHome.admin.snapshot.pending':            'মুলতুবি',
  'agent.adminHome.section.games':                     'গেমস',
  'agent.adminHome.section.games.subtitle':            'ম্যাচ এবং প্ল্যাটফর্ম ফি · {range}',
  'agent.adminHome.section.activity':                  'কার্যকলাপ',
  'agent.adminHome.section.ops':                       'অপারেশন ও অন্তর্দৃষ্টি',
  'agent.adminHome.section.ops.subtitle':              'লাইভ স্ন্যাপশট — সম্পূর্ণ পেজে যেতে যেকোনো কার্ডে ক্লিক করুন',
  'agent.adminHome.section.gameSettings':              'গেম সেটিংস',
  'agent.adminHome.section.gameSettings.subtitle':     'প্রতিটি গেম কনফিগার করুন',
  'agent.adminHome.section.downline':                  'আমার ডাউনলাইনের অন্তর্দৃষ্টি',
  'agent.adminHome.section.downline.subtitle':         'সম্পূর্ণ পেজে যেতে যেকোনো কার্ডে ক্লিক করুন',
  'agent.adminHome.admin.card.players.title':              'খেলোয়াড় ওয়ালেট',
  'agent.adminHome.admin.card.players.headlineLabel':      'সময়সীমায় রিচার্জ',
  'agent.adminHome.admin.card.players.flow.recharges':     'রিচার্জ',
  'agent.adminHome.admin.card.players.flow.withdrawals':   'উত্তোলন',
  'agent.adminHome.admin.card.players.pending.recharges':  'মুলতুবি রিচার্জ',
  'agent.adminHome.admin.card.players.pending.withdrawals':'মুলতুবি উত্তোলন',
  'agent.adminHome.admin.card.players.stat.total':         'মোট খেলোয়াড়',
  'agent.adminHome.admin.card.players.stat.active':        'সক্রিয়',
  'agent.adminHome.admin.card.players.stat.suspended':     'স্থগিত',
  'agent.adminHome.admin.card.agents.title':               'এজেন্ট ওয়ালেট',
  'agent.adminHome.admin.card.agents.headlineLabel':       'এজেন্ট পুলে',
  'agent.adminHome.admin.card.agents.flow.topups':         'টপ-আপ (এডমিন → এজেন্ট)',
  'agent.adminHome.admin.card.agents.flow.outflow':        'বহির্গামী (পরিশোধিত / টপ-আপ)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'মুলতুবি এজেন্ট উত্তোলন',
  'agent.adminHome.admin.card.agents.stat.total':          'মোট এজেন্ট',
  'agent.adminHome.admin.card.agents.stat.active':         'সক্রিয়',
  'agent.adminHome.admin.card.agents.stat.suspended':      'স্থগিত',
  'agent.adminHome.admin.card.bots.title':                 'বট ওয়ালেট',
  'agent.adminHome.admin.card.bots.headlineLabel':         'বট ওয়ালেটে',
  'agent.adminHome.admin.card.bots.stat.total':            'মোট বট',
  'agent.adminHome.admin.card.bots.stat.active':           'সক্রিয়',
  'agent.adminHome.admin.card.bots.stat.matches':          'ম্যাচ',
  'agent.adminHome.admin.card.rewards.title':              'পুরস্কার',
  'agent.adminHome.admin.card.rewards.headlineLabel':      'এই সপ্তাহে পরিশোধিত',
  'agent.adminHome.admin.card.rewards.pending.claims':     'মুলতুবি দাবি',
  'agent.adminHome.admin.card.rewards.stat.activeOffers':  'সক্রিয় অফার',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs':      'বাল্ক জব',
  'agent.adminHome.gameTile.matches':                  'ম্যাচ',
  'agent.adminHome.game.ludo':                         'লুডো',
  'agent.adminHome.game.xo':                           'XO',
  'agent.adminHome.game.rummy':                        'রামি',
  'agent.adminHome.game.carrom':                       'ক্যারম',
  'agent.adminHome.game.chess':                        'দাবা',
  'agent.adminHome.activity.topPlayers.title':         'শীর্ষ খেলোয়াড়',
  'agent.adminHome.activity.topPlayers.empty':         'এই সময়সীমায় কোনো ম্যাচ নেই।',
  'agent.adminHome.activity.topPlayers.metric':        '{count} ম্যাচ',
  'agent.adminHome.activity.topAgents.title':          'শীর্ষ এজেন্ট',
  'agent.adminHome.activity.topAgents.subtitle':       'মালিক খেলোয়াড়ের ভিত্তিতে',
  'agent.adminHome.activity.topAgents.empty':          'এখনো কোনো সক্রিয় এজেন্ট নেই।',
  'agent.adminHome.activity.topAgents.metric':         '{count} খেলোয়াড়',
  'agent.adminHome.activity.recent.title':             'সাম্প্রতিক লেনদেন',
  'agent.adminHome.activity.recent.subtitle':          'শেষ 5টি',
  'agent.adminHome.activity.recent.empty':             'এখনো কোনো লেনদেন নেই।',
  'agent.adminHome.ops.topEarners.title':              'শীর্ষ আয়কারী (7d)',
  'agent.adminHome.ops.topEarners.subtitle':           'প্রকৃত নেট-এর ভিত্তিতে',
  'agent.adminHome.ops.topEarners.empty':              'গত 7 দিনে কোনো আয় নেই।',
  'agent.adminHome.ops.liveActivity.title':            'লাইভ কার্যকলাপ',
  'agent.adminHome.ops.liveActivity.subtitle':         'প্ল্যাটফর্ম জুড়ে শেষ 5টি ইভেন্ট',
  'agent.adminHome.ops.liveActivity.empty':            'শান্ত — গত 24h-এ কোনো ইভেন্ট নেই।',
  'agent.adminHome.ops.botPerf.title':                 'বট পারফরম্যান্স (7d)',
  'agent.adminHome.ops.botPerf.subtitle':              'প্রতি-বিভাগ লাভ-ক্ষতি',
  'agent.adminHome.ops.rateChanges.title':             'সাম্প্রতিক রেট পরিবর্তন',
  'agent.adminHome.ops.rateChanges.subtitle':          'সর্বশেষ পার্টনারশিপ সম্পাদনা',
  'agent.adminHome.ops.rateChanges.empty':             'এখনো কোনো রেট-পরিবর্তন ইভেন্ট নেই।',
  'agent.adminHome.ops.opsHealth.title':               'অপারেশন স্বাস্থ্য',
  'agent.adminHome.ops.opsHealth.subtitle':            'ডেড-লেটার কিউ · মুলতুবি সেটলমেন্ট',
  'agent.adminHome.feed.match.earnedVerb':             'অর্জন করলেন',
  'agent.adminHome.feed.match.onGameSuffix':           '{game}-এ',
  'agent.adminHome.feed.recharge.label':               'রিচার্জ',
  'agent.adminHome.feed.recharge.amountInfix':         '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix':           'মাধ্যমে',
  'agent.adminHome.feed.rateChange.label':             'রেট পরিবর্তন',
  'agent.adminHome.feed.cascadeBadge':                 'ক্যাসকেড',
  'agent.adminHome.feed.recharge.state.approved':      'অনুমোদিত',
  'agent.adminHome.feed.recharge.state.rejected':      'প্রত্যাখ্যাত',
  'agent.adminHome.feed.recharge.state.pending':       'মুলতুবি',
  'agent.adminHome.feed.recharge.state.paid':          'পরিশোধিত',
  'agent.adminHome.feed.recharge.state.expired':       'মেয়াদোত্তীর্ণ',
  'agent.adminHome.leaderboard.matches.one':           '{count} ম্যাচ',
  'agent.adminHome.leaderboard.matches.other':         '{count} ম্যাচ',
  'agent.adminHome.leaderboard.levelPrefix':           'L{level}',
  'agent.adminHome.botMatrix.profitLabel':             'প্ল্যাটফর্ম লাভ (7d)',
  'agent.adminHome.botMatrix.cat.easy':                'সহজ',
  'agent.adminHome.botMatrix.cat.medium':              'মাঝারি',
  'agent.adminHome.botMatrix.cat.hard':                'কঠিন',
  'agent.adminHome.botMatrix.matchesShort':            '{matches}m',
  'agent.adminHome.botMatrix.activeSummary':           '{total}-এর মধ্যে {active} বট সক্রিয়',
  'agent.adminHome.botMatrix.loading':                 'লোড হচ্ছে…',
  'agent.adminHome.opsHealth.dlqClean.title':          'DLQ পরিষ্কার',
  'agent.adminHome.opsHealth.dlqClean.body':           'কোনো ব্যর্থ সেটলমেন্ট জব নেই।',
  'agent.adminHome.opsHealth.dlqDirty.title.one':      '{count} অমীমাংসিত DLQ জব',
  'agent.adminHome.opsHealth.dlqDirty.title.other':    '{count} অমীমাংসিত DLQ জব',
  'agent.adminHome.opsHealth.dlqDirty.body':           'আবার চেষ্টা / সমাধানের জন্য ক্লিক করুন।',
  'agent.adminHome.opsHealth.replay.eyebrow':          'আবার চালান',
  'agent.adminHome.opsHealth.replay.body':             'সেটলমেন্ট আবার চালান',
  'agent.adminHome.opsHealth.reassign.eyebrow':        'পুনরায় অ্যাসাইন',
  'agent.adminHome.opsHealth.reassign.body':           'বাল্ক মুভ',
  'agent.adminHome.agentDash.hero.label':              'আমার পুল ব্যালেন্স',
  'agent.adminHome.agentDash.hero.netPrefix':          'নেট',
  'agent.adminHome.agentDash.action.myWallet':         'আমার ওয়ালেট',
  'agent.adminHome.agentDash.action.myPlayers':        'আমার খেলোয়াড়',
  'agent.adminHome.agentDash.snapshot.myMatches':      'আমার ম্যাচ',
  'agent.adminHome.agentDash.snapshot.active':         'সক্রিয়',
  'agent.adminHome.agentDash.snapshot.netRange':       'নেট (সময়সীমা)',
  'agent.adminHome.agentDash.snapshot.pending':        'মুলতুবি',
  'agent.adminHome.agentDash.card.players.title':              'আমার খেলোয়াড় ওয়ালেট',
  'agent.adminHome.agentDash.card.players.headlineLabel':      'সময়সীমায় রিচার্জ',
  'agent.adminHome.agentDash.card.players.stat.total':         'আমার খেলোয়াড়',
  'agent.adminHome.agentDash.card.wallet.title':               'আমার ওয়ালেট',
  'agent.adminHome.agentDash.card.wallet.headlineLabel':       'পুল ব্যালেন্স',
  'agent.adminHome.agentDash.card.wallet.flow.earned':         'অর্জিত (কমিশন)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut':        'পরিশোধিত',
  'agent.adminHome.agentDash.card.wallet.pending':             'আমার মুলতুবি উত্তোলন',
  'agent.adminHome.agentDash.card.wallet.stat.topups':         'প্রাপ্ত টপ-আপ',
  'agent.adminHome.agentDash.card.wallet.stat.netRange':       'নেট (সময়সীমা)',
  'agent.adminHome.agentDash.card.activity.title':             'কার্যকলাপ',
  'agent.adminHome.agentDash.card.activity.headlineLabel':     'সময়সীমায় আমার ম্যাচ',
  'agent.adminHome.agentDash.card.activity.stat.topSpender':   'শীর্ষ ব্যয়কারী',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend':   'তাঁদের ব্যয়',
  'agent.adminHome.agentDash.card.activity.stat.pending':      'মুলতুবি আইটেম',
  'agent.adminHome.agentDash.activity.topSpenders.title':      'শীর্ষ ব্যয়কারী',
  'agent.adminHome.agentDash.activity.topSpenders.empty':      'এই সময়সীমায় এখনো কোনো ব্যয় নেই।',
  'agent.adminHome.agentDash.activity.recent.title':           'সাম্প্রতিক কার্যকলাপ',
  'agent.adminHome.agentDash.activity.recent.subtitle':        'আপনার খেলোয়াড়দের শেষ 5টি',
  'agent.adminHome.agentDash.activity.recent.empty':           'এখনো কোনো লেনদেন নেই।',
  'agent.adminHome.agentDash.insights.topEarners.title':       'শীর্ষ আয়কারী (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle':    'আপনার সাব-এজেন্ট ক্রমে',
  'agent.adminHome.agentDash.insights.downline.title':         'ডাউনলাইন কার্যকলাপ',
  'agent.adminHome.agentDash.insights.downline.subtitle':      'আপনার ডাউনলাইনের শেষ 5টি ইভেন্ট',
  'agent.adminHome.agentDash.insights.deposit.title':          'ডিপোজিট লেজার',
  'agent.adminHome.agentDash.insights.deposit.subtitle':       'সাম্প্রতিক ডিপোজিট ক্রেডিট / ফেরত',
  'agent.adminHome.agentDash.insights.deposit.empty':          'এখনো কোনো ডিপোজিট কার্যকলাপ নেই।',

  // ── AgentSubAgentDetail ──────────────────────────────────────────
  'agent.subAgentDetail.title':                                'সাব-এজেন্ট',
  'agent.subAgentDetail.subtitle':                             'সরাসরি সাব-এজেন্ট',
  'agent.subAgentDetail.error.loadFailed':                     'সাব-এজেন্ট লোড করতে ব্যর্থ',
  'agent.subAgentDetail.backToList':                           'সাব-এজেন্ট তালিকায় ফিরে যান',
  'agent.subAgentDetail.loading':                              'লোড হচ্ছে…',
  'agent.subAgentDetail.tab.identity':                         'পরিচয়',
  'agent.subAgentDetail.tab.players':                          'খেলোয়াড়',
  'agent.subAgentDetail.tab.wallet':                           'ওয়ালেট',
  'agent.subAgentDetail.tab.rates':                            'রেট',
  'agent.subAgentDetail.tab.ops':                              'অপারেশন',
  'agent.subAgentDetail.header.players.one':                   '{count} খেলোয়াড়',
  'agent.subAgentDetail.header.players.other':                 '{count} খেলোয়াড়',
  'agent.subAgentDetail.header.pool':                          'পুল: {amount}',
  'agent.subAgentDetail.header.lastLogin':                     'শেষ লগইন {when}',
  'agent.subAgentDetail.status.active':                        'সক্রিয়',
  'agent.subAgentDetail.status.suspended':                     'স্থগিত',
  'agent.subAgentDetail.identity.row.uaid':                    'UAID',
  'agent.subAgentDetail.identity.row.username':                'ইউজারনেম',
  'agent.subAgentDetail.identity.row.email':                   'ইমেল',
  'agent.subAgentDetail.identity.row.status':                  'স্থিতি',
  'agent.subAgentDetail.identity.row.parent':                  'পেরেন্ট এজেন্ট',
  'agent.subAgentDetail.identity.parentValue':                 '@{username} (আপনি)',
  'agent.subAgentDetail.identity.parentEmpty':                 '—',
  'agent.subAgentDetail.identity.row.ownedPlayers':            'মালিক খেলোয়াড়',
  'agent.subAgentDetail.identity.row.wallet':                  'ওয়ালেট ব্যালেন্স',
  'agent.subAgentDetail.identity.row.created':                 'তৈরি',
  'agent.subAgentDetail.identity.row.lastLogin':               'শেষ লগইন',
  'agent.subAgentDetail.identity.row.identityLocked':          'পরিচয় লক',
  'agent.subAgentDetail.identity.identityLocked.yes':          'হ্যাঁ (শুধু এডমিন সম্পাদনা)',
  'agent.subAgentDetail.identity.identityLocked.no':           'না (সাব-এজেন্ট নিজে সম্পাদনা করতে পারেন)',
  'agent.subAgentDetail.identity.row.receiving':               'খেলোয়াড় গ্রহণ',
  'agent.subAgentDetail.optOut.accepting':                     'গ্রহণ করছেন',
  'agent.subAgentDetail.optOut.optedOut':                      'অপ্ট আউট',
  'agent.subAgentDetail.optOut.toast.accepting':               'সাব-এজেন্ট এখন নতুন খেলোয়াড় গ্রহণ করছেন',
  'agent.subAgentDetail.optOut.toast.optedOut':                'সাব-এজেন্ট লোকেটর থেকে অপ্ট আউট করেছেন',
  'agent.subAgentDetail.optOut.error.updateFailed':            'আপডেট করা যায়নি',
  'agent.subAgentDetail.location.title':                       'অবস্থান',
  'agent.subAgentDetail.location.lastUpdated':                 '· শেষ আপডেট {date}',
  'agent.subAgentDetail.location.cta.save':                    'অবস্থান সংরক্ষণ',
  'agent.subAgentDetail.location.nothingToSave':               'সংরক্ষণের কিছু নেই',
  'agent.subAgentDetail.location.toast.saved.one':             'অবস্থান সংরক্ষিত ({count} ফিল্ড)',
  'agent.subAgentDetail.location.toast.saved.other':           'অবস্থান সংরক্ষিত ({count} ফিল্ড)',
  'agent.subAgentDetail.location.hint':                        'সংরক্ষণ সক্ষম করতে একটি ভিন্ন রাজ্য / জেলা / এলাকা বাছুন। (সাব-এজেন্ট নিজেই প্রথম লগইনে বাধ্যতামূলক পপ-আপ থেকে সঠিক GPS অভারলে করেন।)',
  'agent.subAgentDetail.players.empty':                        'এই সাব-এজেন্টের এখনো কোনো খেলোয়াড় নেই।',
  'agent.subAgentDetail.players.count.one':                    '{count} খেলোয়াড়',
  'agent.subAgentDetail.players.count.other':                  '{count} খেলোয়াড়',
  'agent.subAgentDetail.wallet.balanceLabel':                  'বর্তমান সাব-এজেন্ট ব্যালেন্স',
  'agent.subAgentDetail.wallet.ledger.title':                  '@{username}-এর সম্পূর্ণ ওয়ালেট লেজার',
  'agent.subAgentDetail.wallet.ledger.subtitle':               'প্রতিটি আন্দোলন · সাব-এজেন্ট নিজের ওয়ালেটে একই দেখেন',
  'agent.subAgentDetail.wallet.ledger.empty':                  'এখনো কোনো ওয়ালেট কার্যকলাপ নেই।',
  'agent.subAgentDetail.wallet.ledger.youThemBadge':           'আপনি ↔ তাঁরা',
  'agent.subAgentDetail.wallet.ledger.balanceAfter':           'ব্যাল ₹{amount}',
  'agent.subAgentDetail.wallet.kind.topupFromAdmin':           'এডমিন থেকে টপ-আপ',
  'agent.subAgentDetail.wallet.kind.topupToPlayer':            'খেলোয়াড়কে পেআউট',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer':          'খেলোয়াড়ের রিচার্জ',
  'agent.subAgentDetail.wallet.kind.revenueShare':             'রাজস্ব ভাগ',
  'agent.subAgentDetail.wallet.kind.rechargeCommission':       'রিচার্জ কমিশন',
  'agent.subAgentDetail.wallet.kind.coFundOffer':              'সহ-ফান্ডেড অফার',
  'agent.subAgentDetail.wallet.kind.adminAdjust':              'এডমিন সমন্বয়',
  'agent.subAgentDetail.wallet.kind.withdrawalHold':           'উত্তোলন হোল্ড',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund':         'উত্তোলন ফেরত',
  'agent.subAgentDetail.wallet.kind.platformFeeShare':         'প্ল্যাটফর্ম ফি ভাগ',
  'agent.subAgentDetail.wallet.kind.winLossShare':             'উইন/লস ভাগ',
  'agent.subAgentDetail.wallet.pullback.title':                'কয়েন ফিরিয়ে নিচ্ছেন?',
  'agent.subAgentDetail.wallet.pullback.body.before':          'বলুন ',
  'agent.subAgentDetail.wallet.pullback.body.middle':          '-কে তাঁর নিজের পুল থেকে উত্তোলন অনুরোধ পাঠাতে। আপনি (সম্পূর্ণ বা আংশিক) পরিশোধিত চিহ্নিত করতে পারেন ',
  'agent.subAgentDetail.wallet.pullback.financeLink':          'ফিন্যান্স › সাব-এজেন্ট',
  'agent.subAgentDetail.wallet.pullback.body.after':           ' থেকে।',
  'agent.subAgentDetail.transfer.topup.heading':               '@{username}-কে ট্রান্সফার',
  'agent.subAgentDetail.transfer.withdraw.heading':            '@{username} থেকে উত্তোলন',
  'agent.subAgentDetail.transfer.topup.body':                  'পরমাণু ট্রান্সফার: আপনার ওয়ালেট কম, তাঁদের ওয়ালেট বেশি। উভয় পুল ঋণাত্মক হতে পারে (চিহ্নিত-পুল ডিজাইন)।',
  'agent.subAgentDetail.transfer.withdraw.body':               'পরমাণু বিপরীত ট্রান্সফার: তাঁদের ওয়ালেট কম, আপনার ওয়ালেট বেশি। সাব-এজেন্টের পেআউট অনুরোধ মীমাংসার সময় ব্যবহার করুন।',
  'agent.subAgentDetail.transfer.amountLabel':                 'পরিমাণ (₹)',
  'agent.subAgentDetail.transfer.noteLabel':                   'নোট (ঐচ্ছিক)',
  'agent.subAgentDetail.transfer.note.placeholder.topup':      'যেমন সাপ্তাহিক টপ-আপ',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw':   'যেমন পেআউট মীমাংসা',
  'agent.subAgentDetail.transfer.error.amountPositive':        'পরিমাণ অবশ্যই একটি ধনাত্মক পূর্ণসংখ্যা হতে হবে।',
  'agent.subAgentDetail.transfer.confirm.topup':               'আপনার পুল থেকে @{username}-কে {amount} ট্রান্সফার? আপনার ব্যালেন্স ঠিক এই পরিমাণে কমবে।',
  'agent.subAgentDetail.transfer.confirm.withdraw':            '@{username}-এর পুল থেকে {amount} আপনার কাছে ফিরিয়ে নিন? তাঁদের ব্যালেন্স ঠিক এই পরিমাণে কমবে।',
  'agent.subAgentDetail.transfer.toast.topup':                 '@{username}-কে {amount} পাঠানো হয়েছে। তাঁদের ব্যালেন্স: {childBalance}।',
  'agent.subAgentDetail.transfer.toast.withdraw':              '@{username} থেকে {amount} ফিরিয়ে নেওয়া হয়েছে। তাঁদের ব্যালেন্স: {childBalance}।',
  'agent.subAgentDetail.transfer.cta.working':                 'কাজ চলছে…',
  'agent.subAgentDetail.transfer.cta.suspended':               'সাব-এজেন্ট স্থগিত',
  'agent.subAgentDetail.transfer.cta.topup':                   '{amount} ট্রান্সফার',
  'agent.subAgentDetail.transfer.cta.withdraw':                '{amount} উত্তোলন',
  'agent.subAgentDetail.rates.loading':                        'রেট লোড হচ্ছে…',
  'agent.subAgentDetail.rates.intro.part1':                    'প্রতিটি কার্ড একটি কমিশন পিলার; ভিতরের প্রতিটি সারি একটি (পিলার × গেম) অংশ। ',
  'agent.subAgentDetail.rates.intro.ceiling':                  'সীমা',
  'agent.subAgentDetail.rates.intro.part2':                    ' যা প্রতিটি ইনপুটের পাশে দেখায় তা আপনার নিজের রেট — গোল্ডেন লিমিট অনুসারে আপনি এর চেয়ে বেশি কোনো সন্তানকে দিতে পারবেন না। আপনি যা পরিবর্তন করেন তা সম্পাদনাযোগ্য হয়; ক্লিক করুন ',
  'agent.subAgentDetail.rates.intro.applyWord':                'প্রয়োগ',
  'agent.subAgentDetail.rates.intro.part3':                    ' কমিট করতে। ক্যাসকেড ক্ল্যাম্প সার্ভার-সাইডে প্রয়োগ হয়।',
  'agent.subAgentDetail.rates.toast.applied':                  'রেট প্রয়োগ করা হয়েছে।',
  'agent.subAgentDetail.rates.confirm.one':                    '{count} রেট পরিবর্তন প্রয়োগ? গোল্ডেন-লিমিট + ক্যাসকেড ক্ল্যাম্প সার্ভার-সাইডে প্রয়োগ হবে।',
  'agent.subAgentDetail.rates.confirm.other':                  '{count} রেট পরিবর্তন প্রয়োগ? গোল্ডেন-লিমিট + ক্যাসকেড ক্ল্যাম্প সার্ভার-সাইডে প্রয়োগ হবে।',
  'agent.subAgentDetail.rates.error.blocked':                  'অবরুদ্ধ: {detail}',
  'agent.subAgentDetail.rates.pillar.platformFee':             'প্ল্যাটফর্ম ফি %',
  'agent.subAgentDetail.rates.pillar.winLoss':                 'উইন/লস লাভ %',
  'agent.subAgentDetail.rates.pillar.offerReward':             'অফার পুরস্কার %',
  'agent.subAgentDetail.rates.row.globalLabel':                '— গ্লোবাল —',
  'agent.subAgentDetail.rates.row.ceilingSuffix':              '/ সীমা {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge':               'পেরেন্টের চেয়ে বেশি (গোল্ডেন লিমিট)',
  'agent.subAgentDetail.rates.row.editedBadge':                'সম্পাদিত',
  'agent.subAgentDetail.rates.cta.discard.one':                '{count} পরিবর্তন বাতিল',
  'agent.subAgentDetail.rates.cta.discard.other':              '{count} পরিবর্তন বাতিল',
  'agent.subAgentDetail.rates.cta.apply.one':                  '{count} পরিবর্তন প্রয়োগ',
  'agent.subAgentDetail.rates.cta.apply.other':                '{count} পরিবর্তন প্রয়োগ',
  'agent.subAgentDetail.rates.cta.applying':                   'প্রয়োগ হচ্ছে…',
  'agent.subAgentDetail.ops.status.title':                     'স্থিতি',
  'agent.subAgentDetail.ops.status.body':                      'স্থগিত করা সাব-এজেন্টের লগইন এবং ডাউনলাইন অপারেশন বন্ধ করে যতক্ষণ না আপনি আবার সক্রিয় করেন। কারণ ঐচ্ছিক কিন্তু অডিট ট্রেইলে আসে।',
  'agent.subAgentDetail.ops.status.reasonPlaceholder':         'কারণ (ঐচ্ছিক)',
  'agent.subAgentDetail.ops.status.cta.suspend':               'সাব-এজেন্ট স্থগিত',
  'agent.subAgentDetail.ops.status.cta.unsuspend':             'সাব-এজেন্টের স্থগিতাদেশ সরান',
  'agent.subAgentDetail.ops.status.cta.working':               'কাজ চলছে…',
  'agent.subAgentDetail.ops.status.confirm.suspend':           '@{username}-কে স্থগিত? স্থগিত থাকাকালীন তাঁরা লগইন বা কাজ করতে পারবেন না।',
  'agent.subAgentDetail.ops.status.confirm.unsuspend':         '@{username}-এর স্থগিতাদেশ সরান? তাঁরা পূর্ণ প্রবেশাধিকার ফিরে পাবেন।',
  'agent.subAgentDetail.ops.status.toast.suspended':           '@{username} এখন স্থগিত।',
  'agent.subAgentDetail.ops.status.toast.active':              '@{username} এখন সক্রিয়।',
  'agent.subAgentDetail.ops.password.title':                   'পাসওয়ার্ড রিসেট',
  'agent.subAgentDetail.ops.password.body':                    'সাব-এজেন্টের জন্য নতুন পাসওয়ার্ড সেট করে এবং তাঁদের বিদ্যমান রিফ্রেশ টোকেন বাতিল করে। তাঁরা নতুন পাসওয়ার্ড দিয়ে আবার লগইন করতে বাধ্য হবেন। নিরাপদ চ্যানেলের মাধ্যমে শেয়ার করুন।',
  'agent.subAgentDetail.ops.password.placeholder':             'নতুন পাসওয়ার্ড (ন্যূনতম 8 অক্ষর)',
  'agent.subAgentDetail.ops.password.confirm':                 '@{username}-এর পাসওয়ার্ড রিসেট? তাঁদের বিদ্যমান সেশন অবৈধ হবে এবং নতুন পাসওয়ার্ড দিয়ে লগইন করতে হবে।',
  'agent.subAgentDetail.ops.password.toast.reset':             'পাসওয়ার্ড রিসেট। নতুন পাসওয়ার্ড @{username}-এর সাথে শেয়ার করুন।',
  'agent.subAgentDetail.ops.password.error.tooShort':          'পাসওয়ার্ড কমপক্ষে 8 অক্ষরের হতে হবে।',
  'agent.subAgentDetail.ops.password.cta.reset':               'পাসওয়ার্ড রিসেট',
  'agent.subAgentDetail.ops.password.cta.resetting':           'রিসেট হচ্ছে…',

  // ── AdminUserDetail (player edit page) ───────────────────────────
  'agent.playerDetail.backToPlayers':                          'খেলোয়াড়ে ফিরে যান',
  'agent.playerDetail.loading':                                'লোড হচ্ছে…',
  'agent.playerDetail.error.loadFailed':                       'ইউজার লোড করতে ব্যর্থ',
  'agent.playerDetail.header.noName':                          '(কোনো নাম সেট নেই)',
  'agent.playerDetail.header.joined':                          '{date}-এ যোগ দিয়েছেন',
  'agent.playerDetail.header.ownedBy.prefix':                  ' · মালিক ',
  'agent.playerDetail.header.walletLabel':                     'ওয়ালেট',
  'agent.playerDetail.status.pending':                         'মুলতুবি',
  'agent.playerDetail.status.active':                          'সক্রিয়',
  'agent.playerDetail.status.suspended':                       'স্থগিত',
  'agent.playerDetail.status.banned':                          'নিষিদ্ধ',
  'agent.playerDetail.tab.identity':                           'পরিচয়',
  'agent.playerDetail.tab.profile':                            'প্রোফাইল',
  'agent.playerDetail.tab.wallet':                             'ওয়ালেট',
  'agent.playerDetail.tab.security':                           'সিকিউরিটি',
  'agent.playerDetail.tab.ownership':                          'মালিকানা',
  'agent.playerDetail.tab.operations':                         'অপারেশন',
  'agent.playerDetail.identity.title':                         'পরিচয়',
  'agent.playerDetail.identity.row.username':                  'ইউজারনেম',
  'agent.playerDetail.identity.row.email':                     'ইমেল',
  'agent.playerDetail.identity.row.phone':                     'ফোন',
  'agent.playerDetail.identity.row.country':                   'দেশ',
  'agent.playerDetail.identity.row.joined':                    'যোগ দিয়েছেন',
  'agent.playerDetail.identity.row.emailVerified':             'ইমেল যাচাইকৃত',
  'agent.playerDetail.identity.row.telegram':                  'টেলিগ্রাম',
  'agent.playerDetail.identity.row.failedLogins':              'ব্যর্থ লগইন',
  'agent.playerDetail.identity.row.lockedUntil':               'লক যতক্ষণ',
  'agent.playerDetail.identity.value.yes':                     'হ্যাঁ',
  'agent.playerDetail.identity.value.no':                      'না',
  'agent.playerDetail.identity.value.dash':                    '—',
  'agent.playerDetail.identity.transfer.title':                'এই খেলোয়াড়কে স্থানান্তর',
  'agent.playerDetail.identity.transfer.body':                 'অন্য এজেন্টের Unique Agent ID দ্বারা হস্তান্তর — সম্পূর্ণ নেটওয়ার্ক জুড়ে কাজ করে, শুধু আপনার ডাউনলাইনে নয়।',
  'agent.playerDetail.identity.transfer.cta':                  'UAID দ্বারা স্থানান্তর',
  'agent.playerDetail.avatar.changeTitle':                     'ফটো পরিবর্তন',
  'agent.playerDetail.avatar.cropTitle':                       '@{username}-এর জন্য ফটো সমন্বয়',
  'agent.playerDetail.avatar.toast.updated':                   'ফটো আপডেট হয়েছে',
  'agent.playerDetail.avatar.error.uploadFailed':              'আপলোড ব্যর্থ',
  'agent.playerDetail.transfer.toast.admin':                   'খেলোয়াড় UAID দ্বারা স্থানান্তরিত',
  'agent.playerDetail.transfer.toast.agent':                   '@{username} সফলভাবে স্থানান্তরিত',
  'agent.playerDetail.ops.accountStatus.title':                'অ্যাকাউন্ট স্থিতি',
  'agent.playerDetail.ops.accountStatus.body':                 'সক্রিয় মানে খেলোয়াড় লগইন এবং খেলতে পারেন। স্থগিত আপনার ফিরিয়ে আনা পর্যন্ত লগইন বন্ধ করে। নিষিদ্ধ একটি শক্তিশালী লক — নীতি লঙ্ঘনের জন্য রাখুন।',
  'agent.playerDetail.ops.statusBtn.activate':                 'সক্রিয়',
  'agent.playerDetail.ops.statusBtn.suspend':                  'স্থগিত',
  'agent.playerDetail.ops.statusBtn.ban':                      'নিষিদ্ধ',
  'agent.playerDetail.ops.statusBtn.current':                  'বর্তমান',
  'agent.playerDetail.ops.statusBtn.toast':                    'ইউজার এখন {status}',
  'agent.playerDetail.ops.danger.title':                       'বিপদ অঞ্চল',
  'agent.playerDetail.ops.danger.body':                        'হার্ড-ডিলিট ইউজার রেকর্ড সরায়। ওয়ালেট লেজার অডিটের জন্য সংরক্ষিত থাকে, কিন্তু খেলোয়াড় সব প্রবেশাধিকার হারাবেন। এই কাজ ফেরানো যাবে না।',
  'agent.playerDetail.ops.delete.eyebrow':                     'বিপদ অঞ্চল',
  'agent.playerDetail.ops.delete.confirm.before':              'এই অ্যাকাউন্টটি স্থায়ীভাবে মুছুন। এটি ফেরানো যাবে না। নিশ্চিত করতে টাইপ করুন ',
  'agent.playerDetail.ops.delete.confirm.after':               '।',
  'agent.playerDetail.ops.delete.cta':                         'মুছুন',
  'agent.playerDetail.ops.delete.toast':                       'ইউজার মুছে ফেলা হয়েছে',
  'agent.playerDetail.walletAdjust.title':                     'ওয়ালেট সমন্বয়',
  'agent.playerDetail.walletAdjust.mode.credit':               '+ ক্রেডিট',
  'agent.playerDetail.walletAdjust.mode.debit':                '− ডেবিট',
  'agent.playerDetail.walletAdjust.field.amount':              'পরিমাণ (₹)',
  'agent.playerDetail.walletAdjust.field.reason':              'কারণ · লেজারে সংরক্ষিত',
  'agent.playerDetail.walletAdjust.field.reason.placeholder':  'যেমন ডিপোজিট রিভার্সাল, বোনাস, ফেরত',
  'agent.playerDetail.walletAdjust.markAsRecharge.label':      'রিচার্জ হিসেবে চিহ্নিত',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before':'এই ক্রেডিটকে লেজারে ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold':  'রিচার্জ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' হিসেবে রেকর্ড করুন। যদি এই ইউজারকে কেউ রেফার করেছিলেন, রেফারকারী স্বয়ংক্রিয়ভাবে একটি বোনাস পান।',
  'agent.playerDetail.walletAdjust.error.positiveAmount':      'একটি ধনাত্মক পরিমাণ লিখুন',
  'agent.playerDetail.walletAdjust.error.reasonRequired':      'কারণ আবশ্যক',
  'agent.playerDetail.walletAdjust.cta.applying':              'প্রয়োগ হচ্ছে…',
  'agent.playerDetail.walletAdjust.cta.applyCredit':           'ক্রেডিট প্রয়োগ',
  'agent.playerDetail.walletAdjust.cta.applyDebit':            'ডেবিট প্রয়োগ',
  'agent.playerDetail.walletAdjust.toast.credited':            '{amount} ক্রেডিট',
  'agent.playerDetail.walletAdjust.toast.debited':             '{amount} ডেবিট',
  'agent.playerDetail.walletAdjust.toast.referralSuffix':      ' · রেফারকারী {bonus} বোনাস অর্জন করেছেন',
  'agent.playerDetail.rechargeCta.title':                      'এই খেলোয়াড়কে রিচার্জ',
  'agent.playerDetail.rechargeCta.body.before':                '@',
  'agent.playerDetail.rechargeCta.body.middle':                '-কে টপ-আপ করলে কয়েন ',
  'agent.playerDetail.rechargeCta.body.bold':                  'আপনার ওয়ালেট',
  'agent.playerDetail.rechargeCta.body.after':                 ' থেকে তাঁদের কাছে যায়। আপনার পুল প্রথমে যাচাই হয় — এই কাজ পরমাণু এবং অপর্যাপ্ত ব্যালেন্স থাকলে প্রত্যাখ্যাত হয়।',
  'agent.playerDetail.rechargeCta.thisPlayer':                 'এই খেলোয়াড়',
  'agent.playerDetail.rechargeCta.cta':                        'রিচার্জ ফর্ম খুলুন',
  'agent.playerDetail.security.title':                         'পাসওয়ার্ড রিসেট',
  'agent.playerDetail.security.subtitle':                      'নতুন পাসওয়ার্ড সেট করে এবং ইউজারকে প্রতিটি ডিভাইস থেকে সাইন আউট করে।',
  'agent.playerDetail.security.field.new':                     'নতুন পাসওয়ার্ড',
  'agent.playerDetail.security.field.confirm':                 'নিশ্চিত করুন',
  'agent.playerDetail.security.error.mismatch':                'পাসওয়ার্ড মিলছে না',
  'agent.playerDetail.security.error.tooShort':                'কমপক্ষে 8 অক্ষর, অক্ষর এবং অঙ্ক সহ',
  'agent.playerDetail.security.toast.reset':                   'পাসওয়ার্ড রিসেট এবং সব সেশন বাতিল',
  'agent.playerDetail.security.cta.reset':                     'পাসওয়ার্ড রিসেট',
  'agent.playerDetail.security.cta.resetting':                 'রিসেট হচ্ছে…',
  'agent.playerDetail.ledger.title':                           'ওয়ালেট লেজার',
  'agent.playerDetail.ledger.entries.one':                     '{count} এন্ট্রি',
  'agent.playerDetail.ledger.entries.other':                   '{count} এন্ট্রি',
  'agent.playerDetail.ledger.empty':                           'এখনো কোনো লেনদেন নেই।',
  'agent.playerDetail.ledger.loading':                         'লোড হচ্ছে…',
  'agent.playerDetail.ledger.actorSuffix':                     ' · এডমিন #{id} দ্বারা',
  'agent.playerDetail.ledger.pagination.prev':                 'পূর্ববর্তী',
  'agent.playerDetail.ledger.pagination.next':                 'পরবর্তী',
  'agent.playerDetail.ledger.kind.bet':                        'ম্যাচ বেট',
  'agent.playerDetail.ledger.kind.win':                        'ম্যাচ পুরস্কার',
  'agent.playerDetail.ledger.kind.refund':                     'ফেরত',
  'agent.playerDetail.ledger.kind.fee':                        'প্ল্যাটফর্ম ফি',
  'agent.playerDetail.ledger.kind.adminCredit':                'এডমিন ক্রেডিট',
  'agent.playerDetail.ledger.kind.adminDebit':                 'এডমিন ডেবিট',
  'agent.playerDetail.ledger.kind.recharge':                   'রিচার্জ',
  'agent.playerDetail.ledger.kind.referralBonus':              'রেফারেল বোনাস',
  'agent.playerDetail.ledger.kind.signupBonus':                'সাইনআপ বোনাস',
  'agent.playerDetail.ledger.kind.offerCredit':                'অফার ক্রেডিট',
  'agent.playerDetail.ledger.kind.withdrawalHold':             'উত্তোলন',
  'agent.playerDetail.ledger.kind.withdrawalRefund':           'ফেরত',
  'agent.playerDetail.ledger.game.platform':                   'প্ল্যাটফর্ম',
  'agent.playerDetail.profile.title':                          'প্রোফাইল সম্পাদনা',
  'agent.playerDetail.profile.field.username':                 'ইউজারনেম',
  'agent.playerDetail.profile.field.email':                    'ইমেল',
  'agent.playerDetail.profile.field.phone':                    'ফোন',
  'agent.playerDetail.profile.field.firstName':                'প্রথম নাম',
  'agent.playerDetail.profile.field.lastName':                 'শেষ নাম',
  'agent.playerDetail.profile.field.dob':                      'জন্ম তারিখ',
  'agent.playerDetail.profile.field.gender':                   'লিঙ্গ',
  'agent.playerDetail.profile.gender.unspecified':             '—',
  'agent.playerDetail.profile.gender.male':                    'পুরুষ',
  'agent.playerDetail.profile.gender.female':                  'মহিলা',
  'agent.playerDetail.profile.gender.other':                   'অন্য',
  'agent.playerDetail.profile.gender.preferNot':               'বলতে চাই না',
  'agent.playerDetail.profile.location.title':                 'অবস্থান',
  'agent.playerDetail.profile.location.lastUpdated':           '· শেষ আপডেট {date}',
  'agent.playerDetail.profile.location.legacy.label':          'ফাইলে পুরানো ঠিকানা',
  'agent.playerDetail.profile.location.legacy.note':           ' (শুধু পঠনযোগ্য — pre-R3, লোকেটর দ্বারা ব্যবহৃত নয়):',
  'agent.playerDetail.profile.toast.nothingToSave':            'সংরক্ষণের কিছু নেই',
  'agent.playerDetail.profile.toast.saved.one':                '{count} ফিল্ড সংরক্ষিত',
  'agent.playerDetail.profile.toast.saved.other':              '{count} ফিল্ড সংরক্ষিত',
  'agent.playerDetail.profile.cta.save':                       'পরিবর্তন সংরক্ষণ',
  'agent.playerDetail.profile.cta.saving':                     'সংরক্ষণ হচ্ছে…',
  'agent.playerDetail.profile.cta.reset':                      'রিসেট',
  'agent.playerDetail.ownership.title':                        'মালিক এজেন্ট',
  'agent.playerDetail.ownership.row.eyebrow':                  'মালিকানা',
  'agent.playerDetail.ownership.platform.title':               'প্ল্যাটফর্ম-ডাইরেক্ট',
  'agent.playerDetail.ownership.platform.body':                'কোনো এজেন্ট এই খেলোয়াড়ের মালিক নন। এডমিন সরাসরি পরিচালনা করেন।',
  'agent.playerDetail.ownership.owned.fallbackOwner':          'এজেন্ট #{id}',
  'agent.playerDetail.ownership.owned.body':                   'এই খেলোয়াড়ের রিচার্জ + ম্যাচে কমিশন অর্জন করেন।',
  'agent.playerDetail.ownership.cta.change':                   'পরিবর্তন',
  'agent.playerDetail.ownership.toast.changedTo':              'মালিকানা → {label}',
  'agent.playerDetail.ownership.platformLabel':                'প্ল্যাটফর্ম-ডাইরেক্ট',
  'agent.playerDetail.ownership.picker.title':                 '@{username}-কে পুনরায় অ্যাসাইন',
  'agent.playerDetail.ownership.picker.body':                  'একজন নতুন এজেন্ট বাছুন — বা প্ল্যাটফর্ম-ডাইরেক্টে আলাদা করুন (কোনো এজেন্ট মালিক নন)। ভবিষ্যৎ কমিশন নতুন মালিকের কাছে যাবে; ইতিমধ্যে ক্রেডিট সারিগুলি স্পর্শ করা হয় না।',
  'agent.playerDetail.ownership.picker.detach.title':          'প্ল্যাটফর্মে আলাদা',
  'agent.playerDetail.ownership.picker.detach.body':           'এজেন্ট মালিকানা সরান। এডমিন সরাসরি পরিচালনা করেন।',
  'agent.playerDetail.ownership.picker.search.placeholder':    'এজেন্ট খুঁজুন…',
  'agent.playerDetail.ownership.picker.loading':               'এজেন্ট লোড হচ্ছে…',
  'agent.playerDetail.ownership.picker.empty.search':          'আপনার অনুসন্ধানে কোনো এজেন্ট মেলেনি।',
  'agent.playerDetail.ownership.picker.empty.none':            'এখনো কোনো সক্রিয় এজেন্ট নেই।',
  'agent.playerDetail.ownership.picker.players.one':           '{count} খেলোয়াড়',
  'agent.playerDetail.ownership.picker.players.other':         '{count} খেলোয়াড়',
  'agent.playerDetail.ownership.picker.current':               'বর্তমান',
  'agent.playerDetail.ownership.picker.currentUpper':          'বর্তমান',
  'agent.playerDetail.ownership.picker.close':                 'বন্ধ',

  // ── Agent login page (Group 9) ───────────────────────────────────
  'agent.login.brand':                    'বালি এজেন্ট',
  'agent.login.subtitle':                 'আপনার খেলোয়াড় পরিচালনা করতে সাইন ইন করুন',
  'agent.login.field.identifier':         'ইউজারনেম বা ইমেল',
  'agent.login.field.password':           'পাসওয়ার্ড',
  'agent.login.disclaimer.before':        'শুধু এজেন্টদের জন্য। যদি আপনি প্ল্যাটফর্ম মালিক হন, ব্যবহার করুন ',
  'agent.login.disclaimer.link':          '/admin/login',
  'agent.login.disclaimer.after':         '।',
  'agent.login.error.loginFailed':        'লগইন ব্যর্থ',
  'agent.login.error.invalidCredentials': 'ভুল ক্রেডেনশিয়াল',
  'agent.login.error.accountSuspended':   'অ্যাকাউন্ট স্থগিত — আপনার অ্যাডমিনিস্ট্রেটরের সাথে যোগাযোগ করুন।',
  'agent.login.error.networkError':       'নেটওয়ার্ক ত্রুটি — আপনার সংযোগ পরীক্ষা করুন এবং আবার চেষ্টা করুন।',

  // ── Long-tail sweep (Group 10) ───────────────────────────────────
  'agent.misc.gameTile.fallbackTag':    'গেম',
  'agent.misc.gameTile.actionSuffix':   '· কনফিগার',
};

const mr: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "माझी माहिती",
  'profile.group.security': "साइन-इन आणि सुरक्षा",
  'profile.personal.title': "वैयक्तिक माहिती",
  'profile.personal.editTitle': "वैयक्तिक माहिती संपादित करा",
  'common.edit': "संपादित करा",
  'profile.field.name': "नाव",
  'profile.field.phone': "फोन नंबर",
  'profile.field.gender': "लिंग",
  'profile.field.location': "ठिकाण",
  'profile.gender.male': "पुरुष",
  'profile.gender.female': "स्त्री",
  'profile.gender.other': "इतर",
  'profile.gender.preferNot': "सांगू इच्छित नाही",
  'profile.gender.select': "निवडा…",
  'profile.location.optionalHint': "ऐच्छिक — तुमचा भाग निवडा किंवा पिनकोड टाका (GPS मुळे अधिक अचूक होते).",
  'profile.lang.title': "भाषा",
  'profile.password.title': "पासवर्ड",
  'profile.password.desc': "तुमचा खाते पासवर्ड बदला. इतर डिव्हाइसमधून साइन-आउट होतील.",
  'profile.password.cta': "बदला",
  'profile.password.editTitle': "पासवर्ड बदला",
  'profile.password.current': "सध्याचा पासवर्ड",
  'profile.password.new': "नवीन पासवर्ड",
  'profile.password.confirm': "नवीन पासवर्डची पुष्टी करा",
  'profile.password.currentPh': "तुमचा सध्याचा पासवर्ड",
  'profile.password.newPh': "मजबूत पासवर्ड निवडा",
  'profile.password.confirmPh': "नवीन पासवर्ड पुन्हा टाका",
  'profile.password.rule': "किमान ८ अक्षरे, एक अक्षर आणि एक अंक असावा.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "जोडलेले खाते",
  'profile.telegram.otpHere': "पासवर्ड-रिसेट OTP इथे येतात.",
  'profile.telegram.unlinkChange': "अनलिंक करा आणि बदला",
  'profile.telegram.unlinkTitle': "Telegram अनलिंक करायचे?",
  'profile.telegram.unlink': "अनलिंक करा",
  'profile.telegram.keepLinked': "जोडलेले ठेवा",
  'profile.telegram.unlinkVerified': "तुम्ही नंतर वेगळे Telegram खाते पुन्हा जोडू शकता. ईमेल पडताळणी तशीच राहते.",
  'profile.telegram.unlinkOnly': "Telegram हे तुमचे एकमेव पडताळलेले माध्यम आहे — खेळ सुरू ठेवण्यापूर्वी तुम्हाला खाते पुन्हा पडताळावे लागेल.",
  'profile.telegram.notLinkedDesc': "बॅकअप म्हणून पासवर्ड-रिसेट OTP मिळवण्यासाठी Telegram जोडा.",
  'profile.telegram.openBot': "@BaaliGames_bot उघडा",
  'profile.telegram.generate': "लिंक तयार करा",
  'profile.telegram.generating': "तयार होत आहे…",
  'profile.avatar.cropTitle': "तुमचा फोटो अ‍ॅडजस्ट करा",
  'profile.toast.detailsSaved': "माहिती जतन झाली",
  'profile.toast.photoUpdated': "फोटो अपडेट झाला",
  'profile.toast.pwChanged': "पासवर्ड बदलला. इतर डिव्हाइसमधून साइन-आउट झाले.",
  'profile.toast.tgUnlinked': "Telegram अनलिंक झाले. वेगळे चॅट जोडण्यासाठी नवीन लिंक तयार करा.",
  'profile.err.phone': "१०-अंकी मोबाइल नंबर टाका (किंवा तसाच राहू द्या)",
  'profile.err.pwMismatch': "नवीन पासवर्ड जुळत नाहीत",
  'profile.err.pwWeak': "पासवर्ड किमान ८ अक्षरांचा, एक अक्षर आणि एक अंक असलेला हवा",
  'profile.err.pwSame': "नवीन पासवर्ड सध्याच्यापेक्षा वेगळा हवा",
  'wallet.payout.title': "जतन केलेली पेआउट माहिती",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "अजून काही जतन केलेले नाही",
  'wallet.payout.hint': "तुम्ही पैसे काढता तेव्हा तुमचा UPI ID आपोआप जतन होतो. पुढील पैसे काढण्याच्या विनंतीच्या वेळी तुम्ही तो अपडेट करू शकता.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} इंस्टॉल करा",
  "pwa.install.subhint": "त्वरित, फुल-स्क्रीन प्रवेशासाठी ते तुमच्या होम स्क्रीनवर जोडा — कोणत्याही ॲप स्टोअरची गरज नाही.",
  "pwa.install.listen": "ऐका",
  "pwa.install.listening": "थांबा",
  "pwa.install.installBtn": "इंस्टॉल करा",
  "pwa.install.notNow": "आता नको",
  "pwa.install.howToShow": "कसे इंस्टॉल करावे",
  "pwa.install.howToHide": "पायऱ्या लपवा",
  "pwa.install.guide.iosSafari": "Safari स्क्रीनच्या तळाशी, Share बटणावर टॅप करा — ते वर बाण असलेल्या चौकोनासारखे दिसते.\nउघडणाऱ्या मेनूमध्ये थोडे खाली स्क्रोल करा.\n\"Add to Home Screen\" पर्यायावर टॅप करा.\nवरच्या उजव्या कोपऱ्यात Add वर टॅप करा. आता ॲप कोणत्याही सामान्य ॲपप्रमाणे तुमच्या होम स्क्रीनवर दिसेल.",
  "pwa.install.guide.iosOther": "iPhone किंवा iPad वर, होम स्क्रीनवर जोडणे फक्त Safari ब्राउझरमध्ये काम करते.\nSafari उघडा आणि याच वेबसाइटवर जा.\nSafari च्या तळाशी Share बटणावर टॅप करा — वर बाण असलेला चौकोन.\nखाली स्क्रोल करा, \"Add to Home Screen\" वर टॅप करा, मग Add वर टॅप करा.",
  "pwa.install.guide.android": "वरील Install बटणावर टॅप करा.\nतुमचा ब्राउझर विचारेल तेव्हा, पुष्टीसाठी Install वर टॅप करा.\nझाले — आता ॲप तुमच्या होम स्क्रीनवर आहे. कोणत्याही सामान्य ॲपप्रमाणे ते कधीही उघडा.\nपॉप-अप दिसत नाही? तुमच्या ब्राउझरचा मेनू उघडा — तीन ठिपके, सहसा वर उजवीकडे — आणि \"Install app\" वर टॅप करा.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "थेट रिचार्ज",
  "agent.directRecharge.subtitle.player": "एक खेळाडू निवडा आणि तुमच्या पूलमधून रिचार्ज करा. विनंतीची गरज नाही.",
  "agent.directRecharge.subtitle.subagent": "एक सब-एजंट निवडा आणि तुमच्या पूलमधून त्यांच्या पूलमध्ये रिचार्ज करा.",
  "agent.directRecharge.search.player": "रिचार्जसाठी खेळाडू शोधा (नाव · फोन · ईमेल)…",
  "agent.directRecharge.search.subagent": "रिचार्जसाठी सब-एजंट शोधा (नाव · ईमेल · UAID)…",
  "agent.directRecharge.clearSearch": "शोध साफ करा",
  "agent.directRecharge.searching": "शोधत आहे…",
  "agent.directRecharge.noMatches": "कोणतीही जुळणी नाही.",
  "agent.directRecharge.empty.player": "तुमच्या यादीत अद्याप कोणताही खेळाडू नाही.",
  "agent.directRecharge.empty.subagent": "अद्याप कोणताही थेट सब-एजंट नाही.",
  "agent.directRecharge.searchFailed": "शोध अयशस्वी",
  "agent.directRecharge.label.wallet": "वॉलेट",
  "agent.directRecharge.label.pool": "पूल",
  "agent.directRecharge.modal.title.player": "थेट खेळाडू रिचार्ज",
  "agent.directRecharge.modal.title.subagent": "थेट सब-एजंट रिचार्ज",
  "agent.directRecharge.modal.close": "बंद करा",
  "agent.directRecharge.modal.targetPool": "लक्ष्य पूल",
  "agent.directRecharge.modal.operatingWallet": "ऑपरेटिंग वॉलेट",
  "agent.directRecharge.modal.dayToDay": "दैनंदिन शिल्लक",
  "agent.directRecharge.modal.securityDeposit": "सुरक्षा ठेव",
  "agent.directRecharge.modal.collateral": "तारण पूल",
  "agent.directRecharge.modal.amount": "रक्कम (₹)",
  "agent.directRecharge.modal.overdraw": "तुम्ही तुमच्या पूलपेक्षा (₹{pool}) जास्त देऊ शकत नाही. रक्कम कमी करा.",
  "agent.directRecharge.modal.reason": "कारण / टीप",
  "agent.directRecharge.modal.reasonPlaceholder": "रोख जमा · ref 4291",
  "agent.directRecharge.modal.reasonHint": "तुमच्या वॉलेट लेजर + ऑडिट लॉगमध्ये नोंदवले जाते.",
  "agent.directRecharge.modal.proofLabel": "पेमेंटचा स्क्रीनशॉट अपलोड करा (ऐच्छिक)",
  "agent.directRecharge.modal.proofHint": "इतिहासाद्वारे प्राप्तकर्त्याला दिसते.",
  "agent.directRecharge.modal.previewEmpty": "व्यवहार पाहण्यासाठी रक्कम प्रविष्ट करा.",
  "agent.directRecharge.modal.previewBody": "तुमच्या वॉलेटमधून {name} च्या {pool} मध्ये पाठवले. सुरक्षित हस्तांतरण; लेजर + ऑडिटमध्ये नोंद.",
  "agent.directRecharge.modal.pool.playerWallet": "वॉलेट",
  "agent.directRecharge.modal.pool.operatingWallet": "ऑपरेटिंग वॉलेट",
  "agent.directRecharge.modal.pool.securityDeposit": "सुरक्षा ठेव",
  "agent.directRecharge.modal.cancel": "रद्द करा",
  "agent.directRecharge.modal.send": "₹{amount} पाठवा",
  "agent.directRecharge.badge.player": "खेळाडू",
  "agent.directRecharge.badge.subagent": "सब-एजंट",
  "agent.directRecharge.modal.yourDirectSubagent": "तुमचा थेट सब-एजंट",
  "agent.directRecharge.modal.fundingSource": "निधी स्रोत",
  "agent.directRecharge.modal.you": "(तुम्ही)",
  "agent.directRecharge.modal.yourPool": "तुमचा पूल",
  "agent.directRecharge.modal.notSignedIn": "एजंट म्हणून साइन इन नाही",
  "agent.directRecharge.modal.failed": "अयशस्वी",

  // Common controls
  'common.signIn': 'साइन इन करा',
  'common.signUp': 'साइन अप करा',
  'common.login': 'लॉगिन',
  'common.logout': 'लॉग आउट',
  'common.continue': 'पुढे चला',
  'common.cancel': 'रद्द करा',
  'common.save': 'जतन करा',
  'common.update': 'अपडेट करा',
  'common.clear': 'साफ करा',
  'common.back': 'मागे',
  'common.loading': 'लोड होत आहे…',
  'common.sending': 'पाठवत आहे…',
  'common.saving': 'जतन होत आहे…',
  'common.optional': 'पर्यायी',
  'common.show': 'दाखवा',
  'common.hide': 'लपवा',
  'common.profile': 'प्रोफाइल',
  'common.settings': 'सेटिंग्ज',

  // NavBar / BottomNav
  'nav.brand': 'बाली गेम्स',
  'nav.home': 'मुख्यपृष्ठ',
  'nav.games': 'गेम्स',
  'nav.wallet': 'वॉलेट',
  'nav.rewards': 'बक्षिसे',
  'nav.profile': 'प्रोफाइल',

  // Login page
  'login.title': 'पुन्हा स्वागत आहे',
  'login.subtitle': 'खेळ सुरू ठेवण्यासाठी साइन इन करा.',
  'login.idLabel': 'युजरनेम, ईमेल किंवा फोन',
  'login.idPlaceholder': 'युजरनेम, ईमेल किंवा मोबाइल',
  'login.passwordLabel': 'पासवर्ड',
  'login.passwordPlaceholder': 'तुमचा पासवर्ड टाका',
  'login.forgot': 'विसरलात?',
  'login.cta': 'साइन इन',
  'login.signingIn': 'साइन इन होत आहे…',
  'login.newHere': 'नवीन आहात?',
  'login.createAccount': 'खाते तयार करा',

  // Register page
  'register.title': 'तुमचे खाते तयार करा',
  'register.subtitle': 'एक नाव निवडा आणि सुरू करा.',
  'register.usernameLabel': 'युजरनेम',
  'register.usernamePlaceholder': 'अद्वितीय नाव निवडा',
  'register.usernameHelp': 'अक्षरे, अंक आणि अंडरस्कोर. ३–२४ अक्षरे.',
  'register.emailLabel': 'ईमेल',
  'register.emailPlaceholder': 'तुमचा ईमेल पत्ता',
  'register.mobileLabel': 'मोबाइल नंबर',
  'register.mobilePlaceholder': '१० अंकांचा मोबाइल',
  'register.mobileHelp': 'देशाचा कोड आम्ही आपोआप जोडू.',
  'register.passwordLabel': 'पासवर्ड',
  'register.passwordPlaceholder': 'मजबूत पासवर्ड निवडा',
  'register.passwordHelp': 'किमान ८ अक्षरे — एक अक्षर आणि एक अंक.',
  'register.referralLabel': 'रेफरल कोड',
  'register.referralPlaceholder': 'मित्राचा इनवाइट कोड',
  'register.cta': 'खाते तयार करा',
  'register.creating': 'तयार करत आहे…',
  'register.alreadyPlayer': 'आधीच खेळाडू आहात?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'तुमच्याबद्दल', 'register.step.signin': 'साइन-इन', 'register.step.finish': 'पूर्ण',
  'register.step.aboutSubtitle': 'स्वतःबद्दल थोडे सांगा', 'register.step.signinSubtitle': 'साइन-इन कसे करायचे ते सेट करा', 'register.step.finishSubtitle': 'स्थान निवडा आणि पूर्ण करा',
  'register.firstName': 'पहिले नाव', 'register.firstNamePh': 'आरव',
  'register.lastName': 'आडनाव', 'register.lastNamePh': 'शर्मा',
  'register.dob': 'जन्म तारीख', 'register.dobHelp': 'खेळण्यासाठी तुमचे वय 18 किंवा त्याहून जास्त असावे',
  'register.v.required': 'आवश्यक', 'register.v.nameMin': 'किमान 2 अक्षरे',
  'register.v.tooLong': 'खूप लांब', 'register.v.nameChars': 'फक्त अक्षरे, स्पेस आणि हायफन',
  'register.v.invalidDate': 'अवैध तारीख', 'register.v.minAge': 'तुमचे वय किमान 18 असावे',
  'register.v.tooOld': 'कृपया वर्ष तपासा',
  'register.v.usernameMin': 'किमान 3 अक्षरे', 'register.v.usernameMax': 'खूप लांब (कमाल 24)',
  'register.v.usernameChars': 'फक्त अक्षरे, अंक आणि अंडरस्कोर', 'register.v.emailShape': 'अवैध ईमेल वाटतो',
  'register.v.phoneDigits': '10 अंकी क्रमांक हवा', 'register.v.phonePrefix': 'भारतीय मोबाईल 6–9 ने सुरू होतो',
  'register.v.pwdMin': 'किमान 8 अक्षरे', 'register.v.pwdLetter': 'किमान एक अक्षर असावे',
  'register.v.pwdNumber': 'किमान एक अंक असावा',
  'register.v.referralShort': 'खूप लहान', 'register.v.referralChars': 'फक्त अक्षरे आणि अंक',
  'register.v.usernameTaken': 'हे यूज़रनेम आधीच घेतले आहे',
  'register.v.emailTaken': 'हा ईमेल आधीच नोंदणीकृत आहे',
  'register.v.phoneTaken': 'हा क्रमांक आधीच नोंदणीकृत आहे',
  'register.v.noLocation': 'पुढे जाण्यासाठी किमान एक स्थान पद्धत शेअर करा.',
  'register.welcome.title': 'स्वागत आहे', 'register.welcome.subtitle': 'तुमचे खाते तयार आहे. आता सुरक्षित करा किंवा थेट सुरू करा?',
  'register.welcome.verifyTelegram': 'टेलिग्राम द्वारे सत्यापित करा',
  'register.welcome.verifyExplain': '⚠ महत्त्वाचे: आत्ता टेलिग्रामवरून सत्यापित करा. सत्यापनाशिवाय, पासवर्ड विसरल्यास तुम्ही स्वतः रीसेट करू शकणार नाही — फक्त तुमचा एजंट किंवा अॅडमिन रीसेट करू शकेल. नंतर प्रोफाइलमधूनही सत्यापित करू शकता.',
  'register.welcome.verifyHeads': 'हे नंतरसाठी जतन करा',
  'register.welcome.verifyConsequence': 'ईमेल सत्यापन सध्या उपलब्ध नाही. नंतर पासवर्ड रीसेट करण्यासाठी टेलिग्रामवरून सत्यापित करा. न केल्यास पासवर्ड विसरल्यास फक्त तुमचा एजंट किंवा अॅडमिन रीसेट करू शकेल.',
  'register.welcome.continue': 'अॅपमध्ये जा', 'register.welcome.continueLogin': 'सुरू ठेवण्यासाठी साइन इन करा', 'register.welcome.useDifferent': 'वेगळे खाते वापरा',
  'register.geo.title': 'तुम्ही कुठून खेळत आहात?', 'register.geo.subtitle': 'तुम्हाला जवळच्या एजंटशी जोडण्यासाठी आम्ही तुमचे स्थान वापरतो.',
  'register.geo.showAlt': 'स्थान शेअर करता येत नाही? वेगळी पद्धत वापरा',
  'register.geo.hideAlt': 'इतर पद्धती लपवा', 'register.geo.captured': 'स्थान कॅप्चर केले',
  'register.geo.capturedHint': 'तुम्ही प्रोफाइलमधून हे कधीही बदलू शकता.',
  'register.geo.verifyNote': 'साइन-अप नंतर, प्रोफाइलमधून टेलिग्राम (पासवर्ड रीसेटसाठी) किंवा ईमेलने कधीही सत्यापित करू शकता. सत्यापन ऐच्छिक.',

  // Verify (post-signup)
  'verify.title': 'तुमचे खाते सत्यापित करा',
  'verify.subtitle': 'एक पर्याय निवडा — एवढेच पुरेसे.',
  'verify.tabTelegram': 'टेलिग्राम',
  'verify.tabEmail': 'ईमेल',
  'verify.emailSent': 'आम्ही {email} वर सत्यापन लिंक पाठवली आहे.',
  'verify.emailHint': 'ती याच डिव्हाइसवर उघडा. दिसत नाही? स्पॅम/प्रोमोशन तपासा.',
  'verify.telegramOpen': '@BaaliGames_bot उघडा',
  'verify.telegramExpiry': 'लिंक ३० मिनिटांत संपते. टेलिग्रामवर एकदा Start दाबा.',
  'verify.goToLogin': 'लॉगिनला जा',
  'verify.email.title': 'ईमेल सत्यापित',
  'verify.email.sub': 'खाते सक्रिय. खेळायची वेळ.',
  'verify.email.failed': 'सत्यापन अयशस्वी',
  'verify.email.verifying': 'सत्यापन होत आहे…',
  'verify.backRegister': 'रजिस्टरकडे परत',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ईमेल पुन्हा पाठवा',
  'verify.gate.resent': 'पाठवले!',
  'verify.gate.checking': 'काम सुरू आहे…',
  'verify.gate.tgFallback': 'लिंक मॅन्युअली उघडा',
  'verify.gate.waiting': 'आम्ही सत्यापन आपोआप ओळखू.',
  'verify.gate.signOut': 'साइन आउट',

  // Forgot password
  'forgot.title': 'खाते परत मिळवा',
  'forgot.subtitle': 'परत येण्याचा मार्ग पाठवू.',
  'forgot.idLabel': 'युजरनेम, ईमेल किंवा फोन',
  'forgot.idPlaceholder': 'युजरनेम, ईमेल किंवा मोबाइल',
  'forgot.sendVia': 'कसे पाठवायचे',
  'forgot.email': 'ईमेल',
  'forgot.telegram': 'टेलिग्राम OTP',
  'forgot.telegramHelp': 'टेलिग्राम तेव्हाच काम करेल जेव्हा तुमचे खाते आमच्या बॉटशी जोडलेले असेल.',
  'forgot.cta': 'पुनर्प्राप्ती पाठवा',
  'forgot.sentEmail': 'खाते अस्तित्वात असल्यास, रीसेट लिंक ईमेलने पाठवली आहे.',
  'forgot.sentTelegram': 'खाते अस्तित्वात असल्यास, टेलिग्रामद्वारे ६-अंकी कोड पाठवला आहे.',
  'forgot.redirecting': 'रीसेट पेजकडे नेत आहे…',
  'forgot.back': 'लॉगिनकडे परत',

  // Reset password
  'reset.title': 'नवीन पासवर्ड सेट करा',
  'reset.subOtp': 'टेलिग्रामवर पाठवलेला कोड वापरा.',
  'reset.subLink': 'नवीन पासवर्ड निवडा आणि परत खेळायला सुरुवात करा.',
  'reset.otpLabel': '६-अंकी कोड',
  'reset.newPasswordLabel': 'नवीन पासवर्ड',
  'reset.cta': 'पासवर्ड अपडेट करा',
  'reset.updating': 'अपडेट होत आहे…',
  'reset.successTitle': 'पासवर्ड अपडेट केला',
  'reset.successSub': 'तयार. खेळायची वेळ.',

  // Home (logged-out)
  'home.welcomeTo': 'स्वागत आहे',
  'home.heroSub': 'कौशल्य-आधारित गेमिंग हब. खेळण्यासाठी साइन इन करा किंवा खाते तयार करा.',
  'home.skillBased': 'कौशल्य-आधारित',
  'home.skillBasedSub': 'रणनीती जिंकते',
  'home.fairPlay': 'निष्पक्ष खेळ',
  'home.fairPlaySub': 'अँटी-चीट चालू',
  'home.instantPayout': 'UPI पेमेंट',
  'home.instantPayoutSub': '२४ तासांच्या आत',
  'home.featured': 'निवडक खेळ',
  'home.tapToPlay': 'खेळण्यासाठी टॅप करा',
  'home.ready': 'खेळायला तयार?',
  'home.dropIn': 'सामील व्हा आणि खरी बक्षिसे जिंका.',
  'home.start': 'सुरू करा',

  // Home (logged-in)
  'home.welcomeBack': 'पुन्हा स्वागत आहे',
  'home.balance': 'तुमचा बॅलन्स',
  'home.live': 'लाइव्ह',
  'home.add': 'जोडा',
  'home.manage': 'व्यवस्थापित करा',
  'home.rewards': 'बक्षिसे',
  'home.todayChallenge': 'आजचे आव्हान',
  'home.todayChallengeSub': 'सामना जिंका — स्ट्रिक बोनस मिळवा.',
  'home.view': 'पहा',
  'home.yourGames': 'तुमचे खेळ',
  'home.viewAll': 'सर्व पहा',

  // Profile tabs
  'profile.details': 'तपशील',
  'profile.address': 'पत्ता',
  'profile.languages': 'भाषा',
  'profile.account': 'खाते',
  'profile.photo': 'फोटो',
  'profile.preferred': 'पसंतीची भाषा',

  // Profile → Languages tab
  'profile.lang.primary': 'मुख्य भाषा',
  'profile.lang.primarySub': 'अॅप-व्यापी डीफॉल्ट',
  'profile.lang.locked': 'लॉक केलेले',
  'profile.lang.second': 'दुसरी भाषा',
  'profile.lang.pickHelp': 'तुम्ही बोलता ती भाषा निवडा. कार्डवर टॅप करा.',
  'profile.lang.saved': 'दुसरी भाषा जतन केली',
  'profile.lang.cleared': 'दुसरी भाषा काढून टाकली',

  // Language switcher
  'lang.switcher.aria': 'भाषा निवडा',
  'lang.switcher.title': 'तुमची भाषा निवडा',
  'lang.switcher.sub': 'संपूर्ण अॅप त्वरित बदलते.',
  'lang.switcher.current': 'सध्याची',

  // Rewards page
  'rewards.title': 'बक्षिसे आणि ऑफर',
  'rewards.subtitle': 'आत्ताच कमवण्यासाठी किंवा क्लेम करण्यासाठी बोनस.',
  'rewards.tab.available': 'उपलब्ध',
  'rewards.tab.inReview': 'पुनरावलोकनात',
  'rewards.tab.claimed': 'क्लेम केले',
  'rewards.empty.available.title': 'आत्ता कोणतेही ऑफर नाहीत',
  'rewards.empty.available.body': 'लवकरच पुन्हा तपासा — एडमिन नियमितपणे नवीन ऑफर पोस्ट करतो.',
  'rewards.empty.review.title': 'पुनरावलोकनात काहीही नाही',
  'rewards.empty.review.body': 'एडमिन मंजुरीची गरज असलेले क्लेम येथे दिसतात.',
  'rewards.empty.claimed.title': 'अद्याप कोणतेही क्लेम नाहीत',
  'rewards.empty.claimed.body': 'एकदा तुम्ही बोनस मिळवला की तो येथे क्लेम म्हणून दिसतो.',
  'rewards.expires': '{date} ला संपते',
  'rewards.remaining': '{amount} शिल्लक',
  'rewards.cap': 'मर्यादा',
  'rewards.claim.cta': 'क्लेम करा',
  'rewards.claim.submit': 'पाठवा',
  'rewards.claimed.credited': '{date} ला {amount} क्लेम केले',
  'rewards.claimed.pending': 'पुनरावलोकन प्रलंबित',
  'rewards.claimed.rejected': 'नाकारले',
  'rewards.auto.willCredit': 'अटी पूर्ण झाल्यावर ऑटो-क्रेडिट होते',
  'rewards.auto.willReview': 'एडमिन पुनरावलोकन रांगेत जाते',
  'rewards.row.creditedOn': '{date} ला क्रेडिट केले',
  'rewards.row.submittedOn': '{date} ला सबमिट केले',
  'rewards.toast.credited': 'बोनस जोडले — {amount} तुमच्या वॉलेटमध्ये',
  'rewards.toast.submitted': 'पुनरावलोकनासाठी सबमिट केले',
  'rewards.toast.alreadyClaimed': 'आधीच क्लेम केले',
  'rewards.toast.rejected': 'क्लेम नाकारला गेला',

  // Wallet page
  'wallet.balance': 'बॅलन्स',
  'wallet.balance.note': 'एंट्री लगेच डेबिट होतात; मॅच संपल्यावर बक्षिसे क्रेडिट होतात.',
  'wallet.addMoney': 'पैसे जोडा',
  'wallet.withdraw': 'काढा',
  'wallet.withdraw.minBalance': 'काढण्यासाठी किमान ₹200 हवेत',
  'wallet.history.byGame': 'गेमनुसार तुमचा इतिहास',
  'wallet.history.empty': 'अद्याप कोणतीही क्रियाकलाप नाही. तुमची पहिली मॅच येथे दिसेल.',
  'wallet.activity': 'अलीकडील क्रियाकलाप',
  'wallet.activity.empty': 'अद्याप कोणतेही व्यवहार नाहीत.',
  'wallet.net': 'नेट',
  'wallet.newer': 'नवीन',
  'wallet.older': 'जुने',
  'wallet.pending.title': 'प्रलंबित विनंत्या',
  'wallet.pending.recharge': '{amount} चे रिचार्ज — एडमिन मंजुरीची प्रतीक्षा',
  'wallet.pending.withdrawal': '{amount} चे पैसे काढणे — पेआउटची प्रतीक्षा',
  'wallet.state.pending': 'प्रलंबित',
  'wallet.state.paid': 'पैसे दिले',
  'wallet.pending.txnPrefix': 'व्यवहार:',
  'wallet.pending.upload.label': 'जलद कारवाईसाठी पेमेंटचा स्क्रीनशॉट अपलोड करा.',
  'wallet.pending.upload.hint':  'विनंती तयार केल्यानंतर तुमच्याकडे २४ तास आहेत.',
  'wallet.pending.upload.failed': 'अपलोड अयशस्वी',
  'wallet.activity.balanceAfter': 'बॅलन्स {balance}',
  'wallet.activity.window': 'तुमची मागील {days} दिवसांची क्रियाकलाप दाखवत आहोत',
  'wallet.activity.range': '{total} पैकी {from}–{to}',
  'wallet.history.summary.one':   '{matches} मॅच · जिंकले {wins} · खर्च {bets}',
  'wallet.history.summary.other': '{matches} मॅच · जिंकले {wins} · खर्च {bets}',
  'wallet.history.summary.refunded': ' · परतावा {refunds}',
  'ledger.kind.bet': 'मॅच एंट्री', 'ledger.kind.win': 'मॅच बक्षीस',
  'ledger.kind.refund': 'मॅच परतावा', 'ledger.kind.forfeit': 'मॅच जप्ती',
  'ledger.kind.fee': 'प्लॅटफॉर्म फी', 'ledger.kind.recharge': 'टॉप-अप',
  'ledger.kind.admin_credit': 'एडमिन क्रेडिट', 'ledger.kind.admin_debit': 'एडमिन डेबिट',
  'ledger.kind.referral_bonus': 'रेफरल बोनस', 'ledger.kind.signup_bonus': 'साइन-अप बोनस',
  'ledger.kind.offer_credit': 'ऑफर बक्षीस',
  'ledger.kind.withdrawal_hold': 'पैसे काढणे होल्ड', 'ledger.kind.withdrawal_refund': 'पैसे काढणे परतावा',
  'ledger.kind.agent_topup_in': 'एजंट टॉप-अप', 'ledger.kind.agent_co_fund': 'एजंट योगदान', 'ledger.kind.turnover_reward': 'कॅशबॅक',
  'ledger.withdrawalPaid': 'पैसे दिले', 'ledger.withdrawalPaidPartial': 'पैसे दिले (आंशिक)',
  'ledger.withdrawalPaid.txn': 'पैसे दिले · व्यवहार {txnId}',
  'ledger.withdrawalRejected': 'पैसे काढणे नाकारले', 'ledger.withdrawalRejected.note': 'होल्ड रक्कम परत केली',
  'ledger.withdrawalExpired': 'पैसे काढणे संपले', 'ledger.awaitingProof': 'पुराव्याची प्रतीक्षा',
  'proofPicker.button.compact': 'स्क्रीनशॉट निवडा', 'proofPicker.button.prominent': 'स्क्रीनशॉट जोडा',
  'proofPicker.processing': 'प्रक्रिया होत आहे…',
  'proofPicker.error.mime': 'फक्त JPG, PNG किंवा WebP प्रतिमा परवानगी आहेत.',
  'proofPicker.error.size': 'फाइल खूप मोठी — कमाल 5 MB.',
  'proofPicker.error.processing': 'प्रतिमेवर प्रक्रिया करता आली नाही',
  'proofPicker.previewAlt': 'निवडलेले स्क्रीनशॉट पूर्वावलोकन',
  'proofViewer.state.none': 'स्क्रीनशॉटची प्रतीक्षा', 'proofViewer.state.available': 'स्क्रीनशॉट पहा',
  'proofViewer.state.expired': 'स्क्रीनशॉट उपलब्ध नाही', 'proofViewer.state.viewLimit': 'पाहण्याची मर्यादा संपली',
  'proofViewer.loading': 'लोड होत आहे…',
  'proofViewer.title.viewLimit': 'तुम्ही हे स्क्रीनशॉट जास्तीत जास्त २ वेळा उघडले आहे',
  'proofViewer.error.load': 'स्क्रीनशॉट लोड होऊ शकला नाही',
  'proofViewer.lastView': 'शेवटची वेळ — प्रतिमा संपेल',
  'proofViewer.viewsRemaining.one': 'आणखी {n} वेळा पाहू शकता',
  'proofViewer.viewsRemaining.other': 'आणखी {n} वेळा पाहू शकता',
  'proofViewer.imageAlt': 'पेमेंट पुरावा', 'proofViewer.close': 'बंद करा',

  // Recharge flow
  'recharge.title': 'पैसे जोडा',
  'recharge.step1.subtitle': 'तुम्हाला किती जोडायचे आहेत?',
  'recharge.step1.amount': 'रक्कम',
  'recharge.step1.range': 'किमान {min} · कमाल {max}',
  'recharge.step1.minHint': 'किमान {min} रिचार्ज',
  'recharge.step1.maxHint': 'कमाल {max}',
  'recharge.step1.quickPick': 'क्विक पिक',
  'recharge.step2.subtitle': 'UPI ने पैसे द्या, नंतर पुढे जा',
  'recharge.step2.payAmount': 'ही रक्कम भरा',
  'recharge.step2.iPaid': 'मी पैसे दिले',
  'recharge.step2.howto.1': 'तुमचे UPI अ‍ॅप उघडा (Google Pay / PhonePe / Paytm).',
  'recharge.step2.howto.2': 'वरील रक्कम UPI ID वर भरा किंवा QR स्कॅन करा.',
  'recharge.step2.howto.3': 'तुमच्या UPI अ‍ॅपमधून ट्रान्झॅक्शन ID कॉपी करा — पुढे ती लागेल.',
  "recharge.locked.title": "रिचार्ज तात्पुरते उपलब्ध नाही",
  "recharge.locked.body": "आम्ही या रिचार्ज मार्गातील एक समस्या सोडवत आहोत. तुमचे वॉलेट आणि पैसे काढणे यावर परिणाम झालेला नाही. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा.",
  "recharge.locked.reassure.withdrawals": "पैसे काढणे नेहमीप्रमाणे चालू आहे",
  "recharge.locked.reassure.existing": "तुम्ही आधी पाठवलेले पैसे प्रोसेस होत आहेत",
  "recharge.cap.banner": "सध्या जास्तीत जास्त रिचार्ज: {max}. प्राप्तकर्त्याची क्षमता तात्पुरती मर्यादित आहे.",
  "recharge.cap.tooHigh": "रक्कम उपलब्ध मर्यादेपेक्षा जास्त आहे. सध्या आपण जास्तीत जास्त {max} रिचार्ज करू शकता.",
  "recharge.notConfigured.title": "रिचार्ज लवकरच उपलब्ध होईल",
  "recharge.notConfigured.body": "आम्ही या रिचार्ज सेवेचा सेटअप अंतिम करत आहोत. तुमचे वॉलेट आणि पैसे काढणे यावर परिणाम झालेला नाही. कृपया थोड्या वेळाने तपासा.",
  'recharge.step3.subtitle': 'तुमच्या पेमेंटची पुष्टी करा',
  'recharge.step3.amountPaid': 'तुम्ही भरलेली रक्कम',
  'recharge.step3.txnId': 'UPI व्यवहार ID',
  'recharge.step3.txnIdHelp': 'तुमच्या UPI अ‍ॅपमध्ये पेमेंट पावतीखाली ही मिळेल — सहसा 12 अंकी.',
  'recharge.step3.note': 'एडमिनसाठी नोट',
  'recharge.step3.notePlaceholder': 'उदा. जोडीदाराच्या नंबरवरून भरले',
  'recharge.step3.submit': 'पुनरावलोकनासाठी पाठवा',
  'recharge.step3.disclaimer': 'एडमिन UPI अ‍ॅपमध्ये पेमेंट तपासतो. मंजुरीनंतर २४ तासांच्या आत वॉलेटमध्ये क्रेडिट होईल (बहुधा लवकर).',
  'recharge.success.title': 'पाठवले — पुनरावलोकनाची प्रतीक्षा',
  'recharge.success.body': 'तुमचे {amount} चे रिचार्ज एडमिन रांगेत आहे. २४ तासांच्या आत क्रेडिट होईल (बहुधा लवकर).',
  'recharge.success.refId': 'संदर्भ',
  'recharge.toast.submitted': 'रिचार्ज एडमिन पुनरावलोकनासाठी सबमिट केले',

  // Withdraw flow
  'withdraw.title': 'काढा',
  'withdraw.step1.subtitle': 'आम्ही पैसे कुठे पाठवायचे?',
  'withdraw.step2.subtitle': 'शेवटची एक तपासणी',
  'withdraw.balance': 'उपलब्ध बॅलन्स',
  'withdraw.amount': 'रक्कम',
  'withdraw.max': 'कमाल',
  'withdraw.upiId': 'तुमची UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'आम्ही येथे पैसे पाठवू. सबमिट करण्यापूर्वी पुन्हा तपासा.',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'तुमच्या मागील पैसे काढण्यातून प्री-फिल केले',
  'withdraw.upiShapeErr': 'UPI ID yourname@psp सारखी असावी (उदा. lucky@oksbi)',
  'withdraw.phoneShapeErr': 'वैध 10-अंकी भारतीय मोबाइल टाका (6, 7, 8 किंवा 9 ने सुरू व्हावा)',
  'withdraw.whatsapp': 'WhatsApp नंबर',
  'withdraw.errMin': 'किमान पैसे काढणे {min} आहे',
  'withdraw.errExceed': 'रक्कम तुमच्या बॅलन्सपेक्षा जास्त आहे',
  'withdraw.minHint': 'किमान {min} पैसे काढणे',
  'withdraw.step2.review': 'पुनरावलोकन',
  'withdraw.step2.heldNotice': 'तुम्ही सबमिट करताच ही रक्कम लगेच होल्ड होते जेणेकरून ती इतरत्र खर्च होऊ शकत नाही. एडमिन ती तुमच्या UPI ID वर पेआउट करतो आणि पेड म्हणून चिन्हांकित करतो.',
  'withdraw.heldAfter': 'होल्ड',
  'withdraw.spendableAfter': 'खर्च करण्यायोग्य',
  'withdraw.step2.disclaimer': 'एडमिनने नाकारल्यास, होल्ड रक्कम तुमच्या वॉलेटमध्ये परत येते.',
  'withdraw.submit': 'पाठवा',
  'withdraw.success.title': 'पाठवले — पेमेंटची प्रतीक्षा',
  'withdraw.success.body': 'तुमचे {amount} चे पैसे काढणे रांगेत आहे. २४ तासांच्या आत पेआउट (बहुधा लवकर). तोपर्यंत होल्ड रक्कम राखीव राहते.',
  'withdraw.success.refId': 'संदर्भ',
  'withdraw.toast.submitted': 'पैसे काढणे रांगेत — तुमच्या वॉलेटमध्ये होल्ड',

  // Payment receiver card
  'payment.upi.label': 'या UPI ID वर पेमेंट करा',
  'payment.upi.copy': 'कॉपी',
  'payment.upiCopied': 'UPI ID कॉपी केली',
  'payment.openUpiApp': 'आता पैसे द्या',
  'payment.openWhatsapp': 'WhatsApp',
  'payment.helpAt': 'मदत हवी? कॉल करा',
  'payment.qr.label': 'पेमेंटसाठी QR स्कॅन करा',
  'payment.qr.tap': 'मोठे करण्यासाठी टॅप करा',
  'payment.qr.tapToClose': 'बंद करण्यासाठी कुठेही टॅप करा',
  'payment.notConfigured.title': 'रिचार्ज सध्या उपलब्ध नाही',
  'payment.notConfigured.contactBody': 'अद्याप कोणतीही UPI सेट केलेली नाही. WhatsApp किंवा फोनद्वारे आमच्याशी संपर्क साधा आणि आम्ही तुम्हाला रिचार्ज करण्यात मदत करू.',
  'payment.notConfigured.unavailable': 'रिचार्ज तात्पुरते उपलब्ध नाही. कृपया नंतर पुन्हा प्रयत्न करा.',
  'payment.chooser.title': 'तुमचे UPI अ‍ॅप निवडा',
  'payment.chooser.subtitle': 'तुम्ही भरणार',
  'payment.chooser.hint': 'रक्कम आधीच भरलेल्या स्थितीत अ‍ॅप उघडण्यासाठी अ‍ॅपवर टॅप करा. अ‍ॅप तुमच्या फोनवर आधीच इंस्टॉल असणे आवश्यक आहे — "address invalid" त्रुटी दिसल्यास, प्रथम App Store / Play Store वरून अ‍ॅप इंस्टॉल करा.',

  // Common
  'common.next': 'पुढे',
  'common.close': 'बंद करा',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'जलद व सुरळीत पेमेंट सेवेसाठी अचूक स्थान आणि संपर्क क्रमांक द्या.',
  'geo.change': 'बदला',
  'geo.gps.button': 'माझे सध्याचे स्थान वापरा',
  'geo.gps.benefit': 'सर्वात वेगवान — एक टॅप, टायपिंग नाही.',
  'geo.gps.denied': 'स्थान परवानगी नाकारली. पिनकोड वापरा किंवा खाली निवडा.',
  'geo.gps.failed': 'स्थान मिळाले नाही. पिनकोड वापरा.',
  'geo.gps.notResolved': 'तुमचे स्थान कोणत्याही क्षेत्राशी जुळले नाही. कृपया निवडा.',
  'geo.pincode.label': 'पिनकोड',
  'geo.pincode.notFound': 'पिनकोड सापडला नाही. कृपया तुमचे क्षेत्र निवडा.',
  'geo.pincode.lookupFailed': 'पिनकोड शोध अयशस्वी. कृपया निवडा.',
  'geo.multipleAreas.label': 'या पिनकोडमध्ये अनेक क्षेत्रे — तुमचे निवडा',
  'geo.multipleAreas.placeholder': 'तुमचे क्षेत्र निवडा',
  'geo.manual.title': 'मॅन्युअली निवडा',
  'geo.manual.close': 'मॅन्युअल लपवा',
  'geo.state.label': 'राज्य',
  'geo.state.placeholder': 'राज्य निवडा',
  'geo.district.label': 'जिल्हा',
  'geo.district.placeholder': 'जिल्हा निवडा',
  'geo.area.label': 'क्षेत्र / शहर',
  'geo.area.placeholder': 'क्षेत्र निवडा',
  'geo.privacy.note': 'फक्त स्थानिक एजंट देण्यासाठी. शेअर केले जात नाही.',
  'geo.required.hint': 'रेफरल कोड नसल्यास स्थान आवश्यक आहे.',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'डॅशबोर्ड',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'हिशेब',
  'agent.nav.players': 'खेळाडू',
  'agent.nav.agents': 'एजंट',
  'agent.nav.rewards': 'पुरस्कार',
  'agent.nav.profile':   'प्रोफाइल',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'होम',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'हिशेब',
  'agent.bottomnav.players': 'खेळाडू',
  'agent.bottomnav.agents': 'एजंट',
  'agent.bottomnav.rewards': 'पुरस्कार',
  'agent.bottomnav.profile': 'प्रोफाइल',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'पुन्हा स्वागत आहे, ',
  'agent.dashboard.header.welcomeFallback': 'मित्र',
  'agent.dashboard.header.subtitle': 'तुमचा व्यवसाय एका दृष्टीक्षेपात — पूल, डिपॉझिट, खेळाडू, सब-एजंट, नफा-तोटा, एक्सपोजर',
  'agent.dashboard.range.today': 'आज',
  'agent.dashboard.range.yesterday': 'काल',
  'agent.dashboard.range.thisWeek': 'या आठवड्यात',
  'agent.dashboard.range.lastWeek': 'मागील आठवड्यात',
  'agent.dashboard.range.thisMonth': 'या महिन्यात',
  'agent.dashboard.range.lastMonth': 'मागील महिन्यात',
  'agent.dashboard.range.complete': 'संपूर्ण',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'डिपॉझिट कव्हरेज लोड होत आहे…',
  'agent.dashboard.depositBar.label': 'डिपॉझिट कव्हरेज',
  'agent.dashboard.depositBar.balanceOfTarget': '· लक्ष्य {target} पैकी {balance}',
  'agent.dashboard.depositBar.tapToView': 'संपूर्ण डिपॉझिट पेज पाहण्यासाठी टॅप करा.',
  'agent.dashboard.depositBar.caption.suspended': 'खाते निलंबित. एडमिनशी संपर्क साधा.',
  'agent.dashboard.depositBar.caption.breach': 'तुमच्या वॉलेटने डिपॉझिट मर्यादेचा 100% वापरला आहे. पैसे काढणे थांबवले. ताबडतोब टॉप-अप करा.',
  'agent.dashboard.depositBar.caption.warning': 'तुमचे वॉलेट डिपॉझिट मर्यादेच्या {pct}% वर आहे. 80% वर इशारा, 100% वर लॉकडाउन.',
  'agent.dashboard.depositBar.caption.caution': 'तुमच्या वॉलेटने डिपॉझिट कव्हरेजचा {pct}% वापरला आहे. 80% पेक्षा कमी — अजूनही सुरक्षित.',
  'agent.dashboard.depositBar.caption.safe': 'डिपॉझिट पूर्णपणे सुरक्षित. कोणतेही कव्हरेज वापरले जात नाही.',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'इशारा · शून्य सुरक्षा डिपॉझिट',
  'agent.dashboard.locatorSkip.body': 'तुमचा सुरक्षा डिपॉझिट {amount} आहे. जोपर्यंत तुम्ही फंड करत नाही तोपर्यंत भौगोलिक लोकेटरकडून नवीन खेळाडू मिळणार नाहीत. विद्यमान खेळाडू + थेट रेफरल + मॅन्युअल असाइनमेंट प्रभावित होणार नाहीत — फक्त ऑर्गॅनिक जिओ-राउटेड साइनअप तुम्हाला सोडतील.',
  'agent.dashboard.locatorSkip.cta': 'डिपॉझिट फंड करा',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'डाउनलाइन डिफॉल्टर इशारा',
  'agent.dashboard.defaulter.summary.critical.one': 'तुमचा {n} थेट सब-एजंट डिफॉल्टमध्ये — तो लॉक आहे किंवा त्याची डिपॉझिट मर्यादा तुटली आहे.',
  'agent.dashboard.defaulter.summary.critical.other': 'तुमचे {n} थेट सब-एजंट डिफॉल्टमध्ये — ते लॉक आहेत किंवा त्यांची डिपॉझिट मर्यादा तुटली आहे.',
  'agent.dashboard.defaulter.summary.warning.one': 'तुमचा {n} थेट सब-एजंट डिपॉझिट मर्यादेच्या 80%+ वर — डिफॉल्टर धोका.',
  'agent.dashboard.defaulter.summary.warning.other': 'तुमचे {n} थेट सब-एजंट डिपॉझिट मर्यादेच्या 80%+ वर — डिफॉल्टर धोका.',
  'agent.dashboard.defaulter.worst': 'सर्वात वाईट: @{username} डिपॉझिट मर्यादेच्या {pct}% वर.',
  'agent.dashboard.defaulter.tapHint': 'थेट सब-एजंट यादीवर जाण्यासाठी टॅप करा. डिफॉल्टर रांगा हायलाइट केल्या आहेत.',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'लॉक',
  'agent.dashboard.marginPill.caution': 'सावध',
  'agent.dashboard.marginPill.warning': 'इशारा',
  'agent.dashboard.marginPill.breach': 'मर्यादा भंग',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'सकाळचा सारांश',
  'agent.dashboard.sections.morning.subtitle': 'लाइव्ह स्नॅपशॉट — प्रत्येक संख्या स्वयंचलितपणे रीफ्रेश होते',
  'agent.dashboard.sections.activity.title': 'क्रियाकलाप',
  'agent.dashboard.sections.activity.subtitle': 'सर्वाधिक खर्च करणारे आणि अलीकडील खेळाडू पेमेंट',
  'agent.dashboard.sections.forensic.title': 'फॉरेन्सिक तपशील',
  'agent.dashboard.sections.forensic.subtitle': 'विस्तारित करण्यासाठी कोणत्याही विभागावर टॅप करा — पेज शांत ठेवण्यासाठी डीफॉल्टनुसार बंद',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'नफा-तोटा सखोल विश्लेषण',
  'agent.dashboard.accordions.pnlDeep.helper': 'तुमच्या कमाईचे प्रति-पिलर + प्रति-गेम विश्लेषण. अंतर्निहित मॅच पाहण्यासाठी गेम रांगेवर क्लिक करा.',
  'agent.dashboard.accordions.leaderboard.title': 'डाउनलाइन लीडरबोर्ड',
  'agent.dashboard.accordions.leaderboard.helper': 'तुमचे सब-एजंट निव्वळ कमाईनुसार क्रमवारी. ऋणात्मक संख्यांचा अर्थ त्या सब-एजंटने आणलेल्यापेक्षा जास्त दिले.',
  'agent.dashboard.accordions.activity.title': 'डाउनलाइन क्रियाकलाप फीड',
  'agent.dashboard.accordions.activity.helper': 'तुमच्या सब-एजंट्सच्या प्रत्येक मॅच सेटलमेंट, रिचार्ज आणि रेट बदल — फॉरेन्सिक टाइमलाइन.',
  'agent.dashboard.accordions.earnings.title': 'कमाई तपशील (लाइफटाइम)',
  'agent.dashboard.accordions.earnings.helper': 'प्रकारानुसार लाइफटाइम एकूण. वरील वेळ फिल्टर दुर्लक्षित — हे कधीही बदलत नाहीत.',
  'agent.dashboard.accordions.directSubAgents.title': 'थेट सब-एजंट',
  'agent.dashboard.accordions.directSubAgents.helper': 'तुमचे थेट प्रतिनिधी. प्रत्येकजण स्वतःचे खेळाडू + स्वतःचे सब-एजंट चालवतो. गुलाबी/अंबर रांगा डिफॉल्टर क्षेत्रात आहेत.',
  'agent.dashboard.accordions.chain.title': 'साखळी दृश्यमानता',
  'agent.dashboard.accordions.chain.helper': 'तुमची संपूर्ण डाउनलाइन — थेट (तुम्ही संपादित करता) आणि अप्रत्यक्ष (तुमच्या सब-एजंट्सद्वारे व्यवस्थापित) मध्ये विभागलेली.',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'लाइव्ह डेटा — फोकसवर, दर 60 सेकंदाने आणि पैशांच्या हालचालीवर रीफ्रेश होते.',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'माझे पूल बॅलन्स',
  'agent.dashboard.cards.poolBalance.helper': 'तुमचे खर्च करण्यायोग्य बॅलन्स. खेळाडूंना रिचार्ज करण्यासाठी किंवा सब-एजंट्सना पाठवण्यासाठी उपलब्ध पैसे.',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} मध्ये नेट {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'सुरक्षा डिपॉझिट',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'डिपॉझिट स्थिती',
  'agent.dashboard.cards.depositStatus.helperLoading': 'डिपॉझिट स्थिती लोड होत आहे…',
  'agent.dashboard.cards.depositStatus.helper': 'तुमच्या पूलसाठी जामीन. लक्ष्याच्या 50% वर सावध, 80% वर इशारा, 100% वर मर्यादा भंग वर स्थिती बदलते.',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} पासून',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'चालू बॅलन्स',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'डिपॉझिट बॅलन्स',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'डिपॉझिट लक्ष्य',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ऋणात्मक एक्सपोजर',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'कव्हरेज प्रमाण',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'डिपॉझिट लक्ष्याचा हा भाग ऋणात्मक एक्सपोजरने वापरला',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'सुरक्षित',
  'agent.dashboard.depositStatus.status.caution': 'सावध',
  'agent.dashboard.depositStatus.status.warning': 'इशारा',
  'agent.dashboard.depositStatus.status.breach': 'मर्यादा भंग',
  'agent.dashboard.depositStatus.status.suspended': 'निलंबित',
  'agent.dashboard.depositStatus.status.healthy': 'निरोगी',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'खेळाडूंचे एकूण वॉलेट',
  'agent.dashboard.cards.playersWallet.helper': 'आत्ता तुमच्या प्रत्येक खेळाडूच्या वॉलेटचे एकत्रित लाइव्ह बॅलन्स. तुमच्याशी जोडलेल्या सर्व wallet_points पंक्तींची बेरीज.',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} खेळाडू · {active} सक्रिय',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} खेळाडू · {active} सक्रिय',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'सब-एजंट वॉलेट + डिपॉझिट',
  'agent.dashboard.cards.subAgents.helper': 'तुमच्या थेट सब-एजंट्सचे एकत्रित पूल बॅलन्स आणि सुरक्षा डिपॉझिट. प्रत्येक सब-एजंट स्वतःचे खेळाडू + स्वतःची डाउनलाइन चालवतो.',
  'agent.dashboard.cards.subAgents.field.pool': 'पूल',
  'agent.dashboard.cards.subAgents.field.deposit': 'डिपॉझिट',
  'agent.dashboard.cards.subAgents.foot.one': '{n} थेट सब-एजंट',
  'agent.dashboard.cards.subAgents.foot.other': '{n} थेट सब-एजंट',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'माझ्या प्रलंबित विनंत्या',
  'agent.dashboard.cards.myPending.helper': 'प्राधान्य 3. एडमिनला तुमचे स्वतःचे सबमिशन — यावर तुम्ही कारवाई करू शकत नाही, त्यांची प्रतीक्षा करत आहात.',
  'agent.dashboard.cards.myPending.recharge.one': '{n} रिचार्ज एडमिनच्या मंजुरीच्या प्रतीक्षेत',
  'agent.dashboard.cards.myPending.recharge.other': '{n} रिचार्ज एडमिनच्या मंजुरीच्या प्रतीक्षेत',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} पैसे काढणे एडमिनच्या मंजुरीच्या प्रतीक्षेत',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} पैसे काढणी एडमिनच्या मंजुरीच्या प्रतीक्षेत',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'खेळाडूंच्या प्रलंबित विनंत्या',
  'agent.dashboard.cards.playersPending.helper': 'प्राधान्य 1. तुमचे खेळाडू तुमच्या मंजुरी किंवा पेमेंटची प्रतीक्षा करत आहेत. कारवाईसाठी टॅप करा.',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} रिचार्ज मंजुरीसाठी',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} रिचार्ज मंजुरीसाठी',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} पैसे काढणे पेमेंटसाठी',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} पैसे काढणी पेमेंटसाठी',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'सब-एजंट्सच्या प्रलंबित विनंत्या',
  'agent.dashboard.cards.subAgentsPending.helper': 'प्राधान्य 2. तुमचे सब-एजंट (संपूर्ण डाउनलाइन) तुमच्या मंजुरी किंवा पेमेंटची प्रतीक्षा करत आहेत. कारवाईसाठी टॅप करा.',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'खरे नेट नफा-तोटा · {range}',
  'agent.dashboard.cards.trueNet.helper': 'या कालावधीतील तुमचा खरा नफा/तोटा. प्लॅटफॉर्म फी वाटा + विन/लॉस वाटा − ऑफर सह-निधी. प्रति-गेम तपशीलासाठी खाली नफा-तोटा सखोल विश्लेषण उघडा.',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'प्लॅट',
  'agent.dashboard.pillarChip.winLoss': 'व/ल',
  'agent.dashboard.pillarChip.offer': 'ऑफर',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'ऑफर वापर · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'या कालावधीत तुमच्या खेळाडूंना ऑफरद्वारे दिलेले एकूण पुरस्कार ₹. त्या खर्चातील तुमचा वाटा पाहण्यासाठी तुमच्या एक्सपोजर पेजशी तुलना करा.',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} दावा जमा',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} दावे जमा',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'सर्वाधिक खर्च करणारे',
  'agent.dashboard.activity.topSpenders.helper': '{range} मध्ये तुमचे सर्वाधिक खर्च करणारे खेळाडू.',
  'agent.dashboard.activity.topSpenders.empty': 'या कालावधीत अद्याप खर्च नाही.',
  'agent.dashboard.activity.recent.title': 'अलीकडील क्रियाकलाप',
  'agent.dashboard.activity.recent.helper': 'तुमच्या खेळाडूंचे पाच अलीकडील रिचार्ज इव्हेंट.',
  'agent.dashboard.activity.recent.empty': 'अलीकडील क्रियाकलाप नाही.',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'मंजूर',
  'agent.dashboard.activity.recent.state.rejected': 'नाकारले',
  'agent.dashboard.activity.recent.state.pending': 'प्रलंबित',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'प्रति गेम · स्पष्टीकरणासाठी रांगेवर टॅप करा',
  'agent.dashboard.pnl.noEarnings': 'या कालावधीत कमाई नाही.',
  'agent.dashboard.pnl.table.col.pillarGame': 'पिलर / गेम',
  'agent.dashboard.pnl.table.col.net': 'नेट',
  'agent.dashboard.pnl.table.col.matches': 'मॅच',
  'agent.dashboard.pnl.ledgerRows.one': '{n} लेजर पंक्ती',
  'agent.dashboard.pnl.ledgerRows.other': '{n} लेजर पंक्त्या',
  'agent.dashboard.pnl.pillar.platformFee': 'प्लॅटफॉर्म फी',
  'agent.dashboard.pnl.pillar.winLoss': 'विन / लॉस',
  'agent.dashboard.pnl.pillar.offerReward': 'ऑफर पुरस्कार',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'कालावधी: {range} — डॅशबोर्डच्या कालावधीचा कठोर उपसंच',
  'agent.dashboard.pnlDrill.loading': 'मॅच-स्तर तपशील लोड होत आहे…',
  'agent.dashboard.pnlDrill.windowTotal': 'कालावधी एकूण:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} मॅच मध्ये',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} मॅच मध्ये',
  'agent.dashboard.pnlDrill.noContribution': 'या सेलमध्ये कोणताही मॅच योगदान देत नाही.',
  'agent.dashboard.pnlDrill.errorFallback': 'तपशील लोड होऊ शकले नाहीत',
  'agent.dashboard.pnlDrill.table.col.match': 'मॅच',
  'agent.dashboard.pnlDrill.table.col.role': 'भूमिका',
  'agent.dashboard.pnlDrill.table.col.base': 'बेस',
  'agent.dashboard.pnlDrill.table.col.rate': 'रेट',
  'agent.dashboard.pnlDrill.table.col.earned': 'कमावले',
  'agent.dashboard.pnlDrill.table.col.settled': 'सेटल केले',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'लोड होत आहे…',
  'agent.dashboard.directDownline.empty': 'अद्याप थेट सब-एजंट नाहीत.',
  'agent.dashboard.directDownline.row.player.one': '{n} खेळाडू',
  'agent.dashboard.directDownline.row.player.other': '{n} खेळाडू',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} सब-एजंट',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} सब-एजंट',
  'agent.dashboard.directDownline.row.walletDeposit': 'वॉलेट {wallet} · डिपॉझिट {deposit}',
  'agent.dashboard.directDownline.footer': '{range} कालावधीसाठी खरे नेट.',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'लोड होत आहे…',
  'agent.dashboard.chain.directAgents.title': 'थेट एजंट',
  'agent.dashboard.chain.directAgents.subtitle': 'तुमचे थेट सब-एजंट — तुम्ही त्यांच्या विनंत्यांवर कारवाई करू शकता.',
  'agent.dashboard.chain.indirectAgents.title': 'अप्रत्यक्ष एजंट',
  'agent.dashboard.chain.indirectAgents.subtitle': 'तुमच्या सब-एजंट्सद्वारे व्यवस्थापित. तुमच्यासाठी फक्त वाचनीय.',
  'agent.dashboard.chain.directPlayers.title': 'थेट खेळाडू',
  'agent.dashboard.chain.directPlayers.subtitle': 'तुमचे खेळाडू — तुम्ही त्यांच्या रिचार्ज आणि पैसे काढण्यावर कारवाई करता.',
  'agent.dashboard.chain.indirectPlayers.title': 'अप्रत्यक्ष खेळाडू',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'तुमच्या सब-एजंट्सचे खेळाडू. तुमच्यासाठी फक्त वाचनीय.',
  'agent.dashboard.chain.field.walletTotal': 'एकूण वॉलेट',
  'agent.dashboard.chain.field.depositTotal': 'एकूण डिपॉझिट',
  'agent.dashboard.chain.field.avgPerHead': 'प्रति व्यक्ती सरासरी',
  'agent.dashboard.chain.field.pendingRecharge': 'प्रलंबित रिचार्ज',
  'agent.dashboard.chain.field.pendingWithdrawal': 'प्रलंबित पैसे काढणे',
  'agent.dashboard.chain.pendingBadge': '{n} प्रलंबित',
  'agent.dashboard.chain.openInbox': 'इनबॉक्स उघडा',
  'agent.dashboard.chain.footer': 'थेट = तुम्ही स्वतः व्यवस्थापित करता ती संख्या. अप्रत्यक्ष = तुमच्या सब-एजंट्सद्वारे व्यवस्थापित (तुम्ही संख्या पाहता पण त्यांच्या प्रलंबित विनंत्यांवर कारवाई करू शकत नाही — त्या त्यांच्या पॅरंटकडे जातात).',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'लोड करण्यात अयशस्वी',
  'agent.activity.filter.window': 'कालावधी',
  'agent.activity.filter.kind': 'प्रकार',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'सर्व',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'सर्व',
  'agent.activity.kind.matches': 'मॅच',
  'agent.activity.kind.recharges': 'रिचार्ज',
  'agent.activity.kind.rateChanges': 'रेट बदल',
  // Empty + loading states.
  'agent.activity.loading': 'लोड होत आहे…',
  'agent.activity.empty.title': 'शांत',
  'agent.activity.empty.subtitle': '{window} मध्ये {kindWord}इव्हेंट नाहीत.',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'मॅच ',
  'agent.activity.empty.kindWord.recharge': 'रिचार्ज ',
  'agent.activity.empty.kindWord.partnership_change': 'भागीदारी बदल ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'सेटलमेंट',
  'agent.activity.row.earned': 'कमावले',
  'agent.activity.row.fromPillarOn': '{pillar} वरून ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'प्लॅटफॉर्म फी',
  'agent.activity.row.pillar.winLoss': 'विन/लॉस',
  'agent.activity.row.pillar.offerReward': 'ऑफर पुरस्कार',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'रिचार्ज',
  'agent.activity.row.recharge.of': 'चा',
  'agent.activity.row.recharge.byUser': 'युजर #{id} द्वारे',
  'agent.activity.row.recharge.via': ' मार्गे ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'मंजूर',
  'agent.activity.row.state.rejected': 'नाकारले',
  'agent.activity.row.state.pending': 'प्रलंबित',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'रेट बदल',
  'agent.activity.row.rateChange.dashOn': '— {pillar} वर ',
  'agent.activity.row.rateChange.cascade': 'कॅस्केड',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'हे दृश्य फक्त एजंट्ससाठी आहे.',
  'agent.earnings.stat.poolBalance': 'पूल बॅलन्स',
  'agent.earnings.stat.totalIn': 'एकूण उत्पन्न',
  'agent.earnings.stat.totalOut': 'एकूण खर्च',
  'agent.earnings.section.in': 'कमाई (येणारी)',
  'agent.earnings.section.out': 'खर्च (जाणारा)',
  'agent.earnings.empty.in': 'अद्याप कमाई नाही. तुमचे खेळाडू रिचार्ज करतील किंवा खेळतील तेव्हा वाटा येथे दिसेल.',
  'agent.earnings.empty.out': 'अद्याप खर्च नाही.',
  'agent.earnings.viewLedger': 'संपूर्ण लेजर पहा',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'एडमिनकडून टॉप-अप',
  'agent.earnings.kind.topupFromAdmin.hint': 'एडमिनने तुमच्या पूलमध्ये क्रेडिट केले',
  'agent.earnings.kind.topupToPlayer.label': 'खेळाडूला टॉप-अप',
  'agent.earnings.kind.topupToPlayer.hint': 'तुम्ही एका खेळाडूला रिचार्ज केले',
  'agent.earnings.kind.topupFromPlayer.label': 'खेळाडूचा रिचार्ज',
  'agent.earnings.kind.topupFromPlayer.hint': 'तुमच्या खेळाडूंनी तुमच्या पूलमध्ये पेमेंट केले',
  'agent.earnings.kind.revenueShare.label': 'महसूल वाटा',
  'agent.earnings.kind.revenueShare.hint': 'मॅच पासून प्लॅटफॉर्म फीचा वाटा',
  'agent.earnings.kind.coFundOffer.label': 'सह-निधी ऑफर',
  'agent.earnings.kind.coFundOffer.hint': 'ऑफर पेआउटमधील तुमचा वाटा',
  'agent.earnings.kind.adminAdjust.label': 'एडमिन समायोजन',
  'agent.earnings.kind.adminAdjust.hint': 'मॅन्युअल सुधारणा',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'एंट्री',
  'agent.earnings.row.entry.other': 'एंट्रीज',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'कालावधी',
  'agent.leaderboard.filter.pillar': 'पिलर',
  'agent.leaderboard.window.all': 'सर्व',
  'agent.leaderboard.pillar.all': 'सर्व पिलर',
  'agent.leaderboard.pillar.platformFee': 'प्लॅटफॉर्म फी',
  'agent.leaderboard.pillar.winLoss': 'विन / लॉस',
  'agent.leaderboard.pillar.offerReward': 'ऑफर पुरस्कार',
  'agent.leaderboard.loading': 'लोड होत आहे…',
  'agent.leaderboard.empty.title': 'अद्याप कमाई नाही',
  'agent.leaderboard.empty.subtitle.admin': '{window} मध्ये सर्व एजंट्समध्ये सेटलमेंट क्रियाकलाप नाही.',
  'agent.leaderboard.empty.subtitle.downline': '{window} मध्ये तुमच्या डाउनलाइनकडून कमाई नाही. तुमचे सब-एजंट मॅच चालवतील तसा हा बोर्ड भरेल.',
  'agent.leaderboard.row.matches.one': '{n} मॅच',
  'agent.leaderboard.row.matches.other': '{n} मॅच',
  'agent.leaderboard.footer': 'कमाई match_settlement_snapshots मधून. ऋणात्मक संख्येचा अर्थ त्या एजंटने कालावधीत आणलेल्यापेक्षा जास्त दिले.',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'प्लॅट',
  'agent.leaderboard.chip.winLoss': 'व/ल',
  'agent.leaderboard.chip.offer': 'ऑफर',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'मार्जिन इशारा · 80% मर्यादा पोहोचली',
  'agent.warning.header.title': 'लॉकआउटपासून एक खराब सेटलमेंट दूर.',
  'agent.warning.header.body': 'अद्याप लॉक नाही — पण तुमचे डिपॉझिट सुरक्षा कव्हर 80% पेक्षा जास्त खर्च झाले आहे. आत्ता टॉप-अप करा आणि नियंत्रणात राहा.',
  'agent.warning.header.dismissAria': 'बंद करा',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'हे का दिसत आहे',
  'agent.warning.why.body': 'अलीकडील गेम सेटलमेंटमुळे तुमचे वॉलेट ऋणात्मक झाले. तुमचा {deposit} सुरक्षा डिपॉझिट हे सुरक्षा-जाळे जे हे शोषून घेते — आणि ते आता 80% पेक्षा जास्त खर्च झाले आहे. आणखी थोडे ऋणात्मक आले तर तुमचे वॉलेट + डिपॉझिट शून्यावर येईल आणि प्लॅटफॉर्म खाते लॉक करेल.',
  'agent.warning.why.reassurance': 'हा इशारा हेतुपूर्वक लवकर पेटतो — जेव्हा तुम्ही अद्याप संपूर्ण ऍप वापरू शकता. लॉकआउट स्क्रीन फक्त तेव्हाच दिसेल जेव्हा तुम्ही 100% पार कराल आणि एकदा झाले की बहुतेक वैशिष्ट्ये बंद होतील. आत्ता कारवाई नंतरच्या तुलनेत खूप कमी व्यत्ययकारक आहे.',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'लॉकआउटपूर्वी उरलेली जागा',
  'agent.warning.gauge.suffix': 'उरली',
  'agent.warning.gauge.dangerBadge': 'धोका क्षेत्रात {pct}%',
  'agent.warning.gauge.field.wallet': 'तुमचे वॉलेट',
  'agent.warning.gauge.field.deposit': 'तुमचा डिपॉझिट',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'तुम्ही याकडे दुर्लक्ष केले आणि 100% वर पोहोचलात',
  'agent.warning.ignore.cardTitle': 'तुम्ही ताबडतोब गमावाल',
  'agent.warning.ignore.item.dashboard': 'तुमचा डॅशबोर्ड, नफा-तोटा आणि लीडरबोर्डमध्ये प्रवेश',
  'agent.warning.ignore.item.pages': 'खेळाडू आणि एजंट पेजेस',
  'agent.warning.ignore.item.manualTopup': 'तुमच्या पूलमधून खेळाडूंना मॅन्युअल टॉप-अप',
  'agent.warning.ignore.item.newWithdrawal': 'तुमच्या अपलाइनला नवीन पैसे काढणे पाठवणे',
  'agent.warning.ignore.item.subAgents': 'सब-एजंट जोडणे किंवा व्यवस्थापित करणे',
  'agent.warning.ignore.item.profile': 'तुमचे प्रोफाइल किंवा पेमेंट सेटिंग्ज संपादित करणे',
  'agent.warning.ignore.footer': 'तुम्ही अद्याप प्रलंबित विनंत्या मंजूर करू शकाल + एक आणीबाणी टॉप-अप पाठवू शकाल — पण बाकी सर्व थांबेल जोपर्यंत तुमचे वॉलेट + डिपॉझिट शून्याच्या वर येत नाही.',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'पुनर्प्राप्तीसाठी आत्ता तुमच्या अपलाइनला पेमेंट करा',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'मी हे नंतर पाहीन',
  'agent.warning.dismiss.footnote': 'तुम्ही इशारा क्षेत्रात राहिल्यास 10 मिनिटांत पुन्हा आठवण करून देऊ. पुनर्प्राप्ती होताच आपोआप बंद होते.',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'खाते लॉक · बेलआउट पोर्टल',
  'agent.lockout.header.title': 'तुमचे डिपॉझिट कव्हर करण्यापेक्षा जास्त तुमच्यावर थकीत आहे.',
  'agent.lockout.header.body': 'काळजी करू नका — हे पुनर्प्राप्त करण्यायोग्य आहे. खाली दिलेल्या दोन पायऱ्या फॉलो करा आणि तुम्ही स्वयंचलितपणे अनलॉक व्हाल.',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'हे का झाले',
  'agent.lockout.why.body': 'गेम सेटलमेंटमुळे तुमचे वॉलेट शून्याच्या खाली गेले. तुमचा सुरक्षा डिपॉझिट हे सुरक्षा-जाळे जे हे शोषून घेते — पण तुम्ही त्याचा 100% वापरला आहे. जोपर्यंत तुमचे वॉलेट आणि डिपॉझिट पुन्हा शून्याच्या वर येत नाही, तुम्ही सामान्यपणे काम करू शकत नाही.',
  'agent.lockout.why.reassurance': 'प्लॅटफॉर्म तुम्हाला यासाठी लॉक करत नाही की तुम्ही काही चुकीचे केले. तो यासाठी लॉक करतो की तुम्ही स्थिर होईपर्यंत नेटवर्क तुमच्या डिपॉझिटवर आणखी कर्ज जमा करू शकत नाही.',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'सुरक्षित पुनर्प्राप्ती लक्ष्य',
  'agent.lockout.gauge.suffix': 'सुरक्षित पेमेंटसाठी',
  'agent.lockout.gauge.recoveredBadge': '{pct}% पुनर्प्राप्त',
  'agent.lockout.gauge.field.wallet': 'तुमचे वॉलेट',
  'agent.lockout.gauge.field.deposit': 'तुमचा डिपॉझिट',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'फक्त अनलॉक',
  'agent.lockout.tier.justUnlock.tag': 'धोकादायक',
  'agent.lockout.tier.justUnlock.note': 'तुम्हाला ₹0 वर परत आणते — कोणतेही नवीन नुकसान त्वरित पुन्हा लॉक करेल.',
  'agent.lockout.tier.safeMin.label': 'सुरक्षित किमान',
  'agent.lockout.tier.safeMin.tag': 'शिफारस',
  'agent.lockout.tier.safeMin.note': 'डिपॉझिट ({deposit}) + तुमच्या ऋणात्मक वॉलेटचा अर्धा. खरा कुशन सोडतो.',
  'agent.lockout.tier.fullRecovery.label': 'पूर्ण पुनर्प्राप्ती',
  'agent.lockout.tier.fullRecovery.tag': 'सर्वोत्तम',
  'agent.lockout.tier.fullRecovery.note': 'डिपॉझिट पूर्णपणे पुनर्संचयित आणि वॉलेट शून्यावर आणले.',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'पुनर्प्राप्तीचे दोन मार्ग — जे करा (किंवा दोन्ही)',
  'agent.lockout.waysOut.inbox.title': 'तुमच्या इनबॉक्सवर कारवाई करा',
  'agent.lockout.waysOut.inbox.body': 'खेळाडू रिचार्ज मंजूर करा (पैसे आधीच तुमच्याकडे आहेत) आणि खेळाडू पैसे काढणे पेड म्हणून चिन्हांकित करा. प्रत्येक मंजुरी तुमच्या वॉलेटला क्रेडिट करते.',
  'agent.lockout.waysOut.inbox.ctaOpen': 'इनबॉक्स उघडा',
  'agent.lockout.waysOut.inbox.ctaOpened': 'इनबॉक्स खाली उघडा आहे',
  'agent.lockout.waysOut.upline.title': 'तुमच्या अपलाइनला पेमेंट करा',
  'agent.lockout.waysOut.upline.body': 'तुमच्या पॅरंट एजंट (किंवा एडमिन)ला UPI द्वारे पैसे पाठवा, मग टxn आयडी सबमिट करा. एकदा मंजूर झाले की तुमचे वॉलेट लगेच क्रेडिट होते.',
  'agent.lockout.waysOut.upline.ctaOpen': 'फॉर्म उघडा',
  'agent.lockout.waysOut.upline.ctaOpened': 'फॉर्म खाली उघडा आहे',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'तुम्ही अद्याप काय करू शकता (आणि काय बंद आहे)',
  'agent.lockout.capabilities.canHeader': 'तुम्ही अद्याप करू शकता',
  'agent.lockout.capabilities.cantHeader': 'तुम्ही (तात्पुरते) करू शकत नाही',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'येणारे खेळाडू रिचार्ज मंजूर करणे',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'खेळाडू पैसे काढणे पेड म्हणून चिन्हांकित करणे',
  'agent.lockout.can.approveSubAgentRecharge': 'सब-एजंट रिचार्ज मंजूर करणे',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'सब-एजंट पैसे काढणे पेड म्हणून चिन्हांकित करणे',
  'agent.lockout.can.submitToUpline': 'तुमच्या अपलाइनला रिचार्ज सबमिट करणे',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE अलर्ट प्राप्त करणे + साइन आउट',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'पायरी {n} · {title}',
  'agent.lockout.section.inbox.title': 'तुमचा इनबॉक्स — प्रत्येक मंजुरी तुमच्या वॉलेटला क्रेडिट करते',
  'agent.lockout.section.inbox.subtitle': 'खेळाडू आणि सब-एजंट्सकडून प्रलंबित पेमेंट विनंत्या ज्या तुमच्या कारवाईची प्रतीक्षा करत आहेत.',
  'agent.lockout.section.inbox.fromPlayers': 'तुमच्या खेळाडूंकडून',
  'agent.lockout.section.inbox.fromSubAgents': 'तुमच्या सब-एजंट्सकडून',
  'agent.lockout.section.upline.title': 'अपलाइनला पेमेंट करून वॉलेट टॉप-अप करा',
  'agent.lockout.section.upline.subtitle': 'पॅरंटचा UPI समोर असेल तर लवकर होते. मंजुरी सहसा काही मिनिटांत मिळते.',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'रिचार्ज · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'टxn {txn} · {when} ला सबमिट',
  'agent.lockout.queue.player.recharge.subtitleNote': 'टxn {txn} · {when} ला सबमिट · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'पैसे काढणे · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi} वर पेमेंट · {when} ला सबमिट',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi} वर पेमेंट · {whatsapp} · {when} ला सबमिट',
  'agent.lockout.queue.subAgent.recharge.title': 'सब-एजंट रिचार्ज · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'टxn {txn} · {when} ला सबमिट',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'टxn {txn} · {when} ला सबमिट · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'सब-एजंट पैसे काढणे · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi} वर पेमेंट · {when} ला सबमिट',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi} वर पेमेंट · {when} ला सबमिट · "{note}"',

  'agent.lockout.queue.action.approve': 'मंजूर करा',
  'agent.lockout.queue.action.reject': 'नाकारा',
  'agent.lockout.queue.action.markPaid': 'पेड चिन्हांकित',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'खेळाडूला पेमेंट करण्यासाठी वापरलेला UPI व्यवहार ID एंटर करा:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'सब-एजंटला पेमेंट करण्यासाठी वापरलेला UPI व्यवहार ID एंटर करा:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'हे पैसे काढणे नाकारायचे? रोखलेली रक्कम खेळाडूला परत मिळेल.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'हे सब-एजंट पैसे काढणे नाकारायचे?',
  'agent.lockout.queue.toast.rechargeApproved': 'रिचार्ज मंजूर — खेळाडूला क्रेडिट',
  'agent.lockout.queue.toast.rechargeRejected': 'रिचार्ज नाकारले',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'सब-एजंट रिचार्ज मंजूर',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'रिचार्ज नाकारले',
  'agent.lockout.queue.toast.withdrawalPaid': 'पैसे काढणे पेड चिन्हांकित — तुमचे वॉलेट क्रेडिट झाले',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'सब-एजंट पैसे काढणे पेड चिन्हांकित — वॉलेट क्रेडिट',
  'agent.lockout.queue.toast.withdrawalRejected': 'पैसे काढणे नाकारले (खेळाडूला परत)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'पैसे काढणे नाकारले',
  'agent.lockout.queue.toast.actionFailed': 'कारवाई अयशस्वी',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'आत्ता तुमच्या खेळाडूंकडून काहीही नाही.',
  'agent.lockout.queue.empty.subAgent': 'आत्ता तुमच्या सब-एजंट्सकडून काहीही नाही.',
  'agent.lockout.queue.loading': 'रांग लोड होत आहे…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'रीफ्रेश — मी आता अनलॉक झालो का?',
  'agent.lockout.refresh.checking': 'तपासत आहे…',
  'agent.lockout.refresh.toast': 'स्थिती रीफ्रेश झाली',
  'agent.lockout.refresh.footnote': 'तुमचे वॉलेट + डिपॉझिट शून्याच्या वर येताच हा पेज आपोआप बंद होतो.',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'रिचार्ज तपशील लोड करता आला नाही',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'खाते अनलॉक करण्यासाठी आत्ताच पेमेंट करा',
  'agent.emergencyForm.banner.locked.body': 'तुमच्या खेळाडूंनी तुमच्या डिपॉझिट जामिनापेक्षा जास्त जिंकले आहे. ऋणात्मक बॅलन्स क्लिअर केल्याशिवाय तुम्ही प्लॅटफॉर्म वापरू शकत नाही.',
  'agent.emergencyForm.banner.warning.title': 'लवकर टॉप-अप करा — तुम्ही मर्यादेच्या जवळ आहात',
  'agent.emergencyForm.banner.warning.body': 'तुमचे वॉलेट तुमच्या डिपॉझिटच्या 80% पार करत आहे. 100% पर्यंत पोहोचण्यापूर्वी टॉप-अप करा — एकदा पोहोचले की खाते लॉक होईल.',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'तुमचे वॉलेट',
  'agent.emergencyForm.row.deposit': 'तुमचा डिपॉझिट',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'तुम्ही का अडकलात',
  'agent.emergencyForm.tier.eyebrow.warning': 'तुम्ही धोक्यात का आहात',
  'agent.emergencyForm.tier.context.locked': 'तुमचे वॉलेट {wallet} वर गेले — हे तुमच्या {deposit} डिपॉझिट सुरक्षा कव्हरच्या पलीकडे आहे. प्लॅटफॉर्म तुम्हाला येथे लॉक करते जेणेकरून तुमच्या डाउनलाइनचे नुकसान रिकाम्या बफरवर वाढत राहू नये.',
  'agent.emergencyForm.tier.context.warning': 'तुमचे वॉलेट {wallet} वर गेले — हे तुमच्या {deposit} डिपॉझिट सुरक्षा कव्हरच्या 80% पेक्षा जास्त आहे. आणखी एक खराब सेटलमेंट तुम्हाला 100% पार करून खाते लॉक करेल.',
  'agent.emergencyForm.tier.pickHowMuch': 'किती पेमेंट करायचे ते निवडा',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'फक्त अनलॉक',
  'agent.emergencyForm.tier.bareMin.label.warning': 'फक्त इशाऱ्यातून बाहेर',
  'agent.emergencyForm.tier.bareMin.tag': 'धोकादायक',
  'agent.emergencyForm.tier.bareMin.note.locked': 'फक्त तुमचे वॉलेट + डिपॉझिट ₹0 वर आणते. कोणतेही नवीन नुकसान तुम्हाला त्वरित पुन्हा लॉक करेल.',
  'agent.emergencyForm.tier.bareMin.note.warning': 'फक्त तुम्हाला 80% रेषेच्या किंचित वर आणते. कोणतेही नवीन नुकसान पुन्हा इशारा देईल — किंवा लॉकआउटमध्ये ढकलेल.',
  'agent.emergencyForm.tier.safeMin.label': 'सुरक्षित किमान',
  'agent.emergencyForm.tier.safeMin.tag': 'शिफारस',
  'agent.emergencyForm.tier.safeMin.note': 'तुमचा डिपॉझिट ({deposit}) + तुमच्या ऋणात्मक वॉलेटचा अर्धा ({half}). तुम्हाला खरा कुशन देते.',
  'agent.emergencyForm.tier.full.label': 'पूर्ण पुनर्प्राप्ती',
  'agent.emergencyForm.tier.full.tag': 'सर्वोत्तम',
  'agent.emergencyForm.tier.full.note': 'डिपॉझिट + तुमचे संपूर्ण ऋणात्मक वॉलेट ({neg}). डिपॉझिट पूर्णपणे पुनर्संचयित आणि वॉलेट शून्यावर.',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'तुमच्या एडमिनला पेमेंट करा',
  'agent.emergencyForm.parent.label.parentAgent': 'तुमच्या पॅरंट एजंटला पेमेंट करा',
  'agent.emergencyForm.parent.role.admin': 'प्लॅटफॉर्म एडमिन',
  'agent.emergencyForm.parent.role.parentAgent': 'पॅरंट एजंट',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile': 'तुमच्या पॅरंटचे पेमेंट तपशील अद्याप फाइलवर नाहीत. कृपया थेट प्लॅटफॉर्म सपोर्टशी संपर्क साधा.',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'भरलेली रक्कम (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI व्यवहार ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'उदा. 412312345678',
  'agent.emergencyForm.field.note.label': 'नोट (पर्यायी)',
  'agent.emergencyForm.field.note.placeholder': 'पॅरंटला काही सांगायचे',
  'agent.emergencyForm.validation.amount': 'तुम्ही जी रक्कम भरली ती एंटर करा (किमान ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI व्यवहार ID / UTR एंटर करा (किमान 6 अक्षर)',
  'agent.emergencyForm.cta': 'रिचार्ज विनंती सबमिट करा',
  'agent.emergencyForm.ctaSubmitting': 'सबमिट होत आहे…',
  // Success state.
  'agent.emergencyForm.success.title': 'रिचार्ज सबमिट केले',
  'agent.emergencyForm.success.bodyAdmin': 'विनंती #{id} एडमिनच्या मंजुरीची प्रतीक्षा करत आहे. एकदा मंजूर झाली की तुमचे वॉलेट क्रेडिट होईल आणि खाते आपोआप अनलॉक होईल.',
  'agent.emergencyForm.success.bodyAgent': 'विनंती #{id} @{username}च्या मंजुरीची प्रतीक्षा करत आहे. एकदा मंजूर झाली की तुमचे वॉलेट क्रेडिट होईल आणि खाते आपोआप अनलॉक होईल.',
  'agent.emergencyForm.toast.submitted': 'रिचार्ज विनंती #{id} सबमिट. मंजुरीची प्रतीक्षा.',
  'agent.emergencyForm.toast.cantSave': 'भाषा सेव्ह करता आली नाही',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'एकदा सेटअप: तुमचे स्थान निश्चित करा',
  'agent.geo.subtitle': 'डॅशबोर्ड वापरण्यापूर्वी आवश्यक.',
  'agent.geo.body.primary': 'बाली गेम्स प्लॅटफॉर्म नवीन खेळाडूंना त्यांच्या भौगोलिकदृष्ट्या जवळच्या एजंटकडे पाठवते. जोपर्यंत तुम्ही तुमचे अचूक GPS स्थान शेअर करत नाही, तुम्ही लोकेटरमध्ये दिसणार नाही आणि ऑर्गॅनिक साइनअपकडून शून्य नवीन खेळाडू मिळतील — फक्त जे थेट तुमचा रेफरल कोड टाइप करतात तेच तुमच्याकडे येतील.',
  'agent.geo.body.secondary': 'एकदा "माझे वर्तमान स्थान वापरा" वर टॅप करा. आम्ही फक्त तुमचे निर्देशांक कॅप्चर करतो, इतर काहीही नाही.',
  'agent.geo.hint.disabled': 'वरील "माझे वर्तमान स्थान वापरा" वर टॅप करा. फक्त पिनकोड किंवा मॅन्युअल क्षेत्र निवडणे पुरेसे नाही — लोकेटरला अचूक GPS निर्देशांकांची आवश्यकता आहे.',
  'agent.geo.error.fallback': 'स्थान सेव्ह करता आले नाही. कृपया पुन्हा प्रयत्न करा.',
  'agent.geo.cta.save': 'सेव्ह करा आणि सुरू ठेवा',
  'agent.geo.cta.saving': 'स्थान सेव्ह होत आहे…',
  'agent.geo.toast.success': 'स्थान सेव्ह झाले — आता तुम्ही खेळाडू मिळवू शकता.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'पूल बॅलन्स',
  'agent.wallet.hero.helper': 'खेळाडू रिचार्ज आणि सब-एजंट टॉप-अपसाठी उपलब्ध. कमाई आणि एडमिन टॉप-अप येथे येतात.',

  'agent.wallet.action.recharge': 'रिचार्ज विनंती',
  'agent.wallet.action.withdraw': 'पैसे काढण्याची विनंती',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'प्रलंबित',
  'agent.wallet.tab.history': 'इतिहास',

  // Pending feed.
  'agent.wallet.pending.loading': 'लोड होत आहे…',
  'agent.wallet.pending.empty': 'आत्ता कोणत्याही प्रलंबित विनंत्या नाहीत.',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'रिचार्ज विनंती · +{amount}',
  'agent.wallet.pending.recharge.meta': 'टxn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'पैसे काढण्याची विनंती · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi} वर · {when}',
  'agent.wallet.pending.badge': 'प्रलंबित',

  // History feed.
  'agent.wallet.history.loading': 'लोड होत आहे…',
  'agent.wallet.history.empty': 'अद्याप कोणतीही वॉलेट क्रियाकलाप नाही.',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'रिचार्ज मंजूर · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'रिचार्ज नाकारले · {amount}',
  'agent.wallet.history.recharge.meta': 'टxn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'पैसे काढणे पेड · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'पैसे काढणे नाकारले · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi} वर · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'पेड · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'मंजूर',
  'agent.wallet.state.paid': 'पेड',
  'agent.wallet.state.rejected': 'नाकारले',
  'agent.wallet.state.expired': 'कालबाह्य',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'एडमिनकडून टॉप-अप',
  'agent.wallet.ledger.kind.topupToPlayer': 'खेळाडूला टॉप-अप',
  'agent.wallet.ledger.kind.topupFromPlayer': 'खेळाडूचा रिचार्ज',
  'agent.wallet.ledger.kind.revenueShare': 'महसूल वाटा',
  'agent.wallet.ledger.kind.coFundOffer': 'सह-निधी ऑफर',
  'agent.wallet.ledger.kind.adminAdjust': 'एडमिन समायोजन',
  'agent.wallet.ledger.kind.withdrawalHold': 'पैसे काढणे होल्ड',
  'agent.wallet.ledger.kind.withdrawalPaid': 'पैसे काढणे पेड',
  'agent.wallet.ledger.kind.withdrawalRefund': 'पैसे काढणे परतावा',
  'agent.wallet.ledger.balanceAfter': 'बॅल {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'सुरक्षा डिपॉझिट',
  'agent.deposit.hero.helper': 'तुमच्या पूलसाठी जामीन. तुमचे वॉलेट चांगल्या स्थितीत असेपर्यंत अस्पर्शित राहते.',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'सुरक्षित',
  'agent.deposit.margin.warning': 'इशारा',
  'agent.deposit.margin.locked': 'लॉक',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'डिपॉझिट रिचार्ज',
  'agent.deposit.action.withdraw': 'डिपॉझिट पैसे काढणे',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'कव्हरेज गेज',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'इशारा',
  'agent.deposit.gauge.thresholdLock': 'लॉक',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct} वर तुम्हाला इशारा मिळेल',
  'agent.deposit.gauge.legend.lock': '{pct} वर तुमचे खाते लॉक होईल',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'पूल पूर्णपणे डिपॉझिटने कव्हर्ड — टॉप-अप होईपर्यंत पैसे काढणे थांबवले.',
  'agent.deposit.gauge.caption.warning': 'पूलने तुमच्या डिपॉझिटचा {pct}% वापरला आहे. लॉकआउट टाळण्यासाठी 100% पर्यंत पोहोचण्यापूर्वी टॉप-अप करा.',
  'agent.deposit.gauge.caption.caution': 'पूल डिपॉझिट कव्हरेजचा {pct}% वर आहे. तुम्ही अद्याप 80% इशारा सीमेच्या खाली सुरक्षित आहात.',
  'agent.deposit.gauge.caption.safe': 'तुमचे डिपॉझिट पूर्णपणे सुरक्षित.',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'लोड होत आहे…',
  'agent.deposit.ledger.empty.title': 'अद्याप कोणतीही डिपॉझिट क्रियाकलाप नाही',
  'agent.deposit.ledger.empty.body': 'एडमिनने तुमच्या डिपॉझिटला क्रेडिट केल्यावर (किंवा परतावा प्रक्रिया केल्यावर) प्रत्येक एंट्री येथे दिसेल.',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'तुमचे वॉलेट रिचार्ज करा',
  'agent.recharge.banner.eyebrow.deposit': 'तुमचा डिपॉझिट रिचार्ज करा',
  'agent.recharge.banner.body.wallet': 'आधी तुमच्या अपलाइनला पेमेंट करा, मग पुरावा येथे सबमिट करा जेणेकरून ते तुमच्या ऑपरेटिंग वॉलेटला टॉप-अप करू शकतील.',
  'agent.recharge.banner.body.deposit': 'आधी तुमच्या अपलाइनला पेमेंट करा, मग पुरावा येथे सबमिट करा जेणेकरून ते तुमच्या सुरक्षा डिपॉझिटला टॉप-अप करू शकतील.',
  'agent.recharge.banner.closeAria': 'फॉर्म बंद करा',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'तुमच्या एडमिनला पेमेंट करा',
  'agent.recharge.parent.label.parentAgent': 'तुमच्या पॅरंट एजंटला पेमेंट करा',
  'agent.recharge.parent.role.admin': 'प्लॅटफॉर्म एडमिन',
  'agent.recharge.parent.role.parentAgent': 'पॅरंट एजंट',
  'agent.recharge.parent.noQr': 'QR सेट नाही',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'तुमच्या अपलाइनचे पेमेंट तपशील लोड होत आहेत…',
  'agent.recharge.parent.loadError': 'तुमच्या अपलाइनचे पेमेंट तपशील लोड होऊ शकले नाहीत',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'तुम्ही कसे पेमेंट केले?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ऑनलाइन डिजिटल पेमेंट',
  'agent.recharge.method.cash.label': 'रोख',
  'agent.recharge.method.cash.note': 'समोरासमोर हस्तांतरण',

  // Form fields.
  'agent.recharge.field.amount.label': 'रक्कम (₹) · किमान ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI व्यवहार ID / UTR',
  'agent.recharge.field.txn.label.cash': 'रोख पावती चिन्ह (कोणताही लहान संदर्भ)',
  'agent.recharge.field.txn.placeholder.upi': 'उदा. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'उदा. CASH-Sun-eve',
  'agent.recharge.field.note.label': 'नोट (पर्यायी, तुमच्या अपलाइनला दिसेल)',
  'agent.recharge.field.note.placeholder': 'अपलाइनसाठी कोणताही संदर्भ',

  // Validation errors.
  'agent.recharge.error.amountMin': 'रक्कम पूर्ण रुपयांत ≥ 10 असली पाहिजे',
  'agent.recharge.error.txnUpi': 'व्यवहार ID खूप लहान वाटतो — UPI संदर्भ पेस्ट करा',
  'agent.recharge.error.txnCash': 'एक लहान चिन्ह जोडा (उदा. "CASH-Sun-eve") जेणेकरून अपलाइन हस्तांतरण जुळवू शकेल',

  // CTAs.
  'agent.recharge.cta.submit': 'विनंती सबमिट करा',
  'agent.recharge.cta.cancel': 'रद्द करा',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'वॉलेटमधून पैसे काढा',
  'agent.withdrawal.banner.eyebrow.deposit': 'डिपॉझिटमधून पैसे काढा',
  'agent.withdrawal.banner.body.wallet': 'रक्कम लगेच रोखली जाते जेणेकरून दुप्पट खर्च होऊ नये. तुमचा अपलाइन तुम्हाला पेमेंट करतो आणि पेड चिन्हांकित करतो.',
  'agent.withdrawal.banner.body.deposit': 'रक्कम तुमच्या सुरक्षा डिपॉझिटमधून लगेच रोखली जाते. तुमचा अपलाइन तुम्हाला पेमेंट करतो आणि पेड चिन्हांकित करतो.',
  'agent.withdrawal.banner.closeAria': 'फॉर्म बंद करा',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'तुम्हाला कसे पेमेंट हवे आहे?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ऑनलाइन डिजिटल पेमेंट',
  'agent.withdrawal.method.cash.label': 'रोख',
  'agent.withdrawal.method.cash.note': 'समोरासमोर हस्तांतरण',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'रक्कम (₹) · उपलब्ध {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'तुमचा UPI ID (जिथे अपलाइन पेमेंट करेल)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'भेटीचे चिन्ह (उदा. "रविवार संध्याकाळ रोख")',
  'agent.withdrawal.field.cashMarker.placeholder': 'रोख कधी/कुठे द्यायचे',
  'agent.withdrawal.field.whatsapp.label': 'WhatsApp (पर्यायी, अपलाइन संपर्कासाठी)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'नोट (पर्यायी)',
  'agent.withdrawal.field.note.placeholder': 'अपलाइनसाठी कोणताही संदर्भ',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'रक्कम पूर्ण रुपयांत ≥ 10 असली पाहिजे',
  'agent.withdrawal.error.amountOverflow': 'तुमच्याकडे फक्त {balance} उपलब्ध आहे',
  'agent.withdrawal.error.upi': 'वैध UPI ID एंटर करा',

  // CTAs.
  'agent.withdrawal.cta.submit': 'विनंती सबमिट करा',
  'agent.withdrawal.cta.cancel': 'रद्द करा',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'खेळाडू',
  'agent.people.hub.players.subtitle': 'खेळाडू आणि त्यांच्या विनंत्या व्यवस्थापित करा',
  'agent.people.hub.players.subtab.requests': 'खेळाडू विनंत्या',
  'agent.people.hub.players.subtab.list': 'खेळाडू',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'एजंट',
  'agent.people.hub.agents.subtitle': 'सब-एजंट आणि त्यांच्या विनंत्या व्यवस्थापित करा',
  'agent.people.hub.agents.subtab.requests': 'एजंट विनंत्या',
  'agent.people.hub.agents.subtab.list': 'एजंट',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'नवीन खेळाडू स्वीकारत आहे',
  'agent.people.acceptingToggle.bodyAccepting': 'तुम्ही भौगोलिक लोकेटरकडून नवीन खेळाडू स्वीकारत आहात. तुमच्या क्षेत्रातील कोल्ड साइनअप आपोआप तुमच्याकडे येतात.',
  'agent.people.acceptingToggle.bodyOptedOut': 'तुम्ही ऑप्ट-आउट केले आहे. लोकेटर तुम्हाला सोडत आहे — फक्त थेट रेफरल आणि मॅन्युअल असाइन केलेले खेळाडू तुमच्याकडे येतील. विद्यमान खेळाडू प्रभावित होणार नाहीत.',
  'agent.people.acceptingToggle.error': 'अपडेट करता आले नाही',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'नवीन खेळाडू स्वीकारणे थांबवा',
  'agent.people.acceptingToggle.aria.start': 'नवीन खेळाडू स्वीकारणे सुरू करा',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'खेळाडू',
  'agent.qr.audience.subAgent': 'एजंट',
  'agent.qr.alt': 'तुमचा पेमेंट QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'माझा QR · खेळाडू जे पाहतात',
  'agent.qr.cardLabel.subAgent': 'माझा QR · एजंट जे पाहतात',
  'agent.qr.upi.empty': 'अद्याप कोणताही UPI सेट नाही',
  // Status pill.
  'agent.qr.status.loading': 'लोड होत आहे…',
  'agent.qr.status.live': 'लाइव्ह',
  'agent.qr.status.notSet': 'सेट नाही',
  'agent.qr.status.paused': 'थांबवले',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR आणि UPI संपादित करा',
  'agent.qr.cta.setup': 'QR सेट करा',
  'agent.qr.cta.viewOnly': 'फक्त पहा — एडमिनला संपादनासाठी विचारा',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR आणि UPI संपादित करा',
  'agent.qr.modal.closeAria': 'बंद करा',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR अपलोड करा',
  'agent.qr.modal.replace': 'QR बदला',
  'agent.qr.modal.remove': 'काढून टाका',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR प्रतिमा काढून टाकायची? तुमचे खेळाडू फक्त संपर्क फॉलबॅक पाहतील.',
  'agent.qr.modal.confirmRemove.subAgent': 'QR प्रतिमा काढून टाकायची? तुमचे एजंट फक्त संपर्क फॉलबॅक पाहतील.',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'उदा. baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'WhatsApp नंबर (फॉलबॅक)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'एजंटसाठी WhatsApp नंबर',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'रिक्त → {number} वर परत येईल',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'सब-एजंट {number} पाहतील (तुमचा खेळाडू-समोरील नंबर).',
  'agent.qr.modal.field.phone.label.player': 'सपोर्ट फोन (फॉलबॅक)',
  'agent.qr.modal.field.phone.label.subAgent': 'एजंटसाठी सपोर्ट फोन',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'रिक्त → {number} वर परत येईल',
  'agent.qr.modal.field.phone.fallbackHint': 'सब-एजंट {number} पाहतील (तुमचा खेळाडू-समोरील नंबर).',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'सक्रिय',
  'agent.qr.modal.active.description': 'खेळाडू रिचार्ज सबमिट करू शकतात',
  'agent.qr.modal.active.on': 'चालू',
  'agent.qr.modal.active.paused': 'थांबवले',

  // Footer buttons.
  'agent.qr.modal.cancel': 'रद्द करा',
  'agent.qr.modal.save': 'सेव्ह करा',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'मॅन्युअल रिचार्ज',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'थेट तुमच्या पूलमधून एका ',
  'agent.manualRecharge.banner.body.target': 'खेळाडू',
  'agent.manualRecharge.banner.body.after': 'ला पैसे पाठवा.',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'तुमचा पूल',
  'agent.manualRecharge.pool.dailyCap': 'मर्यादा · {amount}/दिवस',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'खेळाडूला',
  'agent.manualRecharge.field.player.placeholder': 'खेळाडू निवडा…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · बॅल {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'खेळाडूला',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · सध्याचे बॅल {balance}',
  'agent.manualRecharge.field.player.notFound': 'तुमच्या यादीत खेळाडू सापडला नाही.',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'रक्कम (₹) — {min} ते {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'तुमच्या पूल बॅलन्सपेक्षा जास्त.',
  'agent.manualRecharge.error.belowMin': 'किमानपेक्षा कमी ({min}).',
  'agent.manualRecharge.error.aboveMax': 'कमालपेक्षा जास्त ({max}).',

  // Note field.
  'agent.manualRecharge.field.note.label': 'नोट (पर्यायी, खेळाडूच्या लेजरवर दिसते)',
  'agent.manualRecharge.field.note.placeholder': 'उदा. दुकानात रोख घेतली',

  // Loading + success states.
  'agent.manualRecharge.loading': 'तुमचा पूल लोड होत आहे…',
  'agent.manualRecharge.cta.cancel': 'रद्द करा',
  'agent.manualRecharge.cta.send': '{amount} पाठवा',
  'agent.manualRecharge.success.title': '@{username}ला {amount} पाठवले',
  'agent.manualRecharge.success.meta': 'तुमचा पूल · {pool} · त्यांचे वॉलेट · {playerBal}',
  'agent.manualRecharge.success.again': 'आणखी पाठवा',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'मॅन्युअल रिचार्ज',
  'agent.manualSubAgentRecharge.banner.body.before': 'थेट तुमच्या पूलमधून एका ',
  'agent.manualSubAgentRecharge.banner.body.target': 'सब-एजंट',
  'agent.manualSubAgentRecharge.banner.body.after': 'ला पैसे पाठवा.',

  'agent.manualSubAgentRecharge.pool.label': 'तुमचा पूल',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'सब-एजंटला',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'सब-एजंट निवडा…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · पूल {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'कोणतेही सक्रिय थेट सब-एजंट नाहीत',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'सब-एजंटला',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · सध्याचा पूल {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'तुमच्या थेट डाउनलाइनमध्ये सब-एजंट सापडला नाही.',

  'agent.manualSubAgentRecharge.field.amount.label': 'रक्कम (₹) — {min} ते {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'तुमच्या पूल बॅलन्सपेक्षा जास्त.',
  'agent.manualSubAgentRecharge.error.belowMin': 'किमानपेक्षा कमी ({min}).',

  'agent.manualSubAgentRecharge.field.note.label': 'नोट (पर्यायी, दोन्ही लेजरवर दिसते)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'उदा. फ्लोट आगाऊ',

  'agent.manualSubAgentRecharge.loading': 'तुमचा पूल लोड होत आहे…',
  'agent.manualSubAgentRecharge.cta.cancel': 'रद्द करा',
  'agent.manualSubAgentRecharge.cta.send': '{amount} पाठवा',
  'agent.manualSubAgentRecharge.success.title': '@{username}ला {amount} पाठवले',
  'agent.manualSubAgentRecharge.success.meta': 'तुमचा पूल · {parent} · त्यांचा पूल · {child}',
  'agent.manualSubAgentRecharge.success.again': 'आणखी पाठवा',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'रिचार्ज',
  'agent.playerRequests.direction.withdrawal': 'पैसे काढणे',
  'agent.playerRequests.tab.pending': 'प्रलंबित',
  'agent.playerRequests.tab.history': 'इतिहास',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'लोड होत आहे…',
  'agent.playerRequests.empty.pending.recharge': 'आत्ता कोणतेही प्रलंबित रिचार्ज नाहीत.',
  'agent.playerRequests.empty.pending.withdrawal': 'आत्ता कोणतेही प्रलंबित पैसे काढणे नाहीत.',
  'agent.playerRequests.empty.history': 'अद्याप इतिहास नाही.',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'नाकारण्याचे कारण (पर्यायी):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'नाकारण्याचे कारण (खेळाडूची रोखलेली रक्कम परत केली जाईल):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'नाकारले',
  'agent.playerRequests.toast.rejectedRefunded': 'नाकारले · रोखलेली रक्कम परत',
  'agent.playerRequests.toast.approved': '{amount} मंजूर',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} मंजूर · मूळ + ₹{bonus} बोनस',
  'agent.playerRequests.toast.markedPaid': '{amount} पेड चिन्हांकित',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI टxn ID',
  'agent.playerRequests.card.payToUpiLabel': 'पेमेंट करा (UPI)',
  'agent.playerRequests.card.whatsapp': 'WhatsApp',
  'agent.playerRequests.card.viewQr': 'QR पहा',
  'agent.playerRequests.card.bonusChip': 'बोनस',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'विनंती {requested} पैकी {approved} मंजूर',
  'agent.playerRequests.card.partialPaid': 'विनंती {requested} पैकी {approved} पेड · परत {refund}',
  'agent.playerRequests.card.paidTxnPrefix': 'पेड · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'नाकारा',
  'agent.playerRequests.action.approve': 'मंजूर करा',
  'agent.playerRequests.action.markPaid': 'पेड चिन्हांकित',
  'agent.playerRequests.state.pending': 'प्रलंबित',
  'agent.playerRequests.state.rejected': 'नाकारले',
  'agent.playerRequests.state.expired': 'कालबाह्य',
  'agent.playerRequests.state.accepted': 'स्वीकारले',
  'agent.playerRequests.state.manuallyPaid': 'मॅन्युअल पेड',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': '{from}–{to} पैकी {total}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'रिचार्ज',
  'agent.agentRequests.direction.withdrawal': 'पैसे काढणे',
  'agent.agentRequests.tab.pending': 'प्रलंबित',
  'agent.agentRequests.tab.history': 'इतिहास',
  'agent.agentRequests.loading': 'लोड होत आहे…',
  'agent.agentRequests.empty.pending.recharge': 'आत्ता कोणतेही प्रलंबित रिचार्ज नाहीत.',
  'agent.agentRequests.empty.pending.withdrawal': 'आत्ता कोणतेही प्रलंबित पैसे काढणे नाहीत.',
  'agent.agentRequests.empty.history': 'अद्याप इतिहास नाही.',

  'agent.agentRequests.prompt.rejectRecharge': 'नाकारण्याचे कारण (पर्यायी):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'नाकारण्याचे कारण (सब-एजंटची रोखलेली रक्कम परत केली जाईल):',

  'agent.agentRequests.toast.rejected': 'नाकारले',
  'agent.agentRequests.toast.rejectedRefunded': 'नाकारले · रोखलेली रक्कम परत',
  'agent.agentRequests.toast.approved': '{amount} मंजूर · पूलला क्रेडिट',
  'agent.agentRequests.toast.markedPaid': '{amount} पेड चिन्हांकित',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'एजंट',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI टxn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'रोख चिन्ह',
  'agent.agentRequests.card.payToUpiLabel': 'पेमेंट करा (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'हस्तांतरण चिन्ह',
  'agent.agentRequests.card.whatsapp': 'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved': 'विनंती {requested} पैकी {approved} मंजूर',
  'agent.agentRequests.card.partialPaid': 'विनंती {requested} पैकी {approved} पेड · परत {refund}',
  'agent.agentRequests.card.paidTxnPrefix': 'पेड · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'डिपॉझिट',
  'agent.agentRequests.card.chip.cash': 'रोख',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'नाकारा',
  'agent.agentRequests.action.approve': 'मंजूर करा',
  'agent.agentRequests.action.markPaid': 'पेड चिन्हांकित',
  'agent.agentRequests.state.pending': 'प्रलंबित',
  'agent.agentRequests.state.rejected': 'नाकारले',
  'agent.agentRequests.state.expired': 'कालबाह्य',
  'agent.agentRequests.state.accepted': 'स्वीकारले',
  'agent.agentRequests.state.manuallyPaid': 'मॅन्युअल पेड',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'खेळाडू जोडा',
  'agent.playersList.loading': 'लोड होत आहे…',
  'agent.playersList.empty': 'कोणताही खेळाडू जुळला नाही',
  'agent.playersList.results': '{total} पैकी {from}–{to} दाखवत आहे',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'युजरनेम, ईमेल, फोन शोधा…',

  // Status filter pills.
  'agent.playersList.filter.all': 'सर्व',
  'agent.playersList.filter.active': 'सक्रिय',
  'agent.playersList.filter.pending': 'प्रलंबित',
  'agent.playersList.filter.suspended': 'निलंबित',
  'agent.playersList.filter.banned': 'बंदी',

  // Empty-results panel.
  'agent.playersList.empty.title': 'कोणताही खेळाडू सापडला नाही',
  'agent.playersList.empty.body': 'वेगळा शोध किंवा स्थिती फिल्टर वापरून पहा.',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'पेज {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'सक्रिय',
  'agent.playersList.card.status.pending': 'प्रलंबित',
  'agent.playersList.card.status.suspended': 'निलंबित',
  'agent.playersList.card.status.banned': 'बंदी',
  'agent.playersList.card.verifiedBadge': 'सत्यापित',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'वॉलेट',
  'agent.playersList.column.joined': 'सामील झाले',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'तुम्ही तयार केलेले एजंट तुमच्या अंतर्गत काम करतात. ते प्रत्येक पिलरवर 0% पासून सुरू होतात — त्यांच्या तपशील पेजवरून प्रति-गेम दर द्या.',
  'agent.subAgentsList.cta.new': 'नवीन सब-एजंट',
  'agent.subAgentsList.loading': 'लोड होत आहे…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'अद्याप सब-एजंट नाहीत',
  'agent.subAgentsList.empty.body.canCreate': '"+ नवीन सब-एजंट" टॅप करून एक तयार करा. ते येथे त्यांच्या पूल, खेळाडू आणि कमाईसह दिसतील — तुम्ही व्यवस्थापित कराल.',
  'agent.subAgentsList.empty.body.cantCreate': 'तुमच्या एडमिनने अद्याप तुम्हाला सब-एजंट तयार करण्याचा अधिकार दिला नाही. त्यांना तुमच्या परवानग्यांमध्ये create_subagent ला "edit" वर सेट करायला सांगा.',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'निलंबित',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'एजंट',
  'agent.subAgentsList.column.players': 'खेळाडू',
  'agent.subAgentsList.column.pool': 'पूल',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'थेट',
  'agent.subAgentsList.row.indirectLabel': 'अप्रत्यक्ष',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'नवीन सब-एजंट',
  'agent.subAgentsList.modal.body': 'ते प्रत्येक पिलरवर 0% पासून सुरू होतील. तयार केल्यानंतर त्यांच्या तपशील पेजवरून प्रति-गेम दर द्या.',
  'agent.subAgentsList.modal.field.username.label': 'युजरनेम',
  'agent.subAgentsList.modal.field.username.placeholder': 'अक्षरांक, ._- अनुमत; किमान 3 अक्षर',
  'agent.subAgentsList.modal.field.email.label': 'ईमेल',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'प्रारंभिक पासवर्ड',
  'agent.subAgentsList.modal.field.password.placeholder': 'किमान 8 अक्षर',
  'agent.subAgentsList.modal.field.password.hint': 'हे नवीन एजंटसोबत शेअर करा जेणेकरून ते लॉगिन करू शकतील. पहिल्या लॉगिनवर त्यांनी बदलावे.',
  'agent.subAgentsList.modal.field.location.label': 'सब-एजंटचे स्थान',
  'agent.subAgentsList.modal.field.location.optional': '(अत्यंत शिफारस)',
  'agent.subAgentsList.modal.field.location.missing': 'स्थानाशिवाय भौगोलिक लोकेटर या सब-एजंटला कोणताही नवीन खेळाडू पाठवू शकणार नाही. ते फक्त थेट रेफरलद्वारेच खेळाडू मिळतील जोपर्यंत ते साइन इन करून त्यांचा GPS पूर्ण करत नाहीत.',
  'agent.subAgentsList.modal.cta.cancel': 'रद्द करा',
  'agent.subAgentsList.modal.cta.create': 'सब-एजंट तयार करा',
  'agent.subAgentsList.modal.cta.creating': 'तयार होत आहे…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who} कडून रिचार्ज मंजूर करा',
  'agent.approvalEdit.title.withdrawal': '{who} चे पैसे काढणे पेड चिन्हांकित करा',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'विनंती:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'मंजूर रक्कम (₹)',
  'agent.approvalEdit.field.amount.hint': 'विनंती {amount} पेक्षा जास्त असू शकत नाही.',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} आपोआप {who}च्या वॉलेटला परत केले जाईल.',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'आंशिक मंजुरी. कोणताही संलग्न बोनस आपोआप नाकारला जाईल.',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'कारण',
  'agent.approvalEdit.field.reason.requiredHint': '(आवश्यक, ≥5 अक्षर)',
  'agent.approvalEdit.field.reason.optionalHint': '(पर्यायी)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'उदा. खेळाडूने फक्त 30 रुपये भरले',
  'agent.approvalEdit.field.reason.placeholder.optional': 'पर्यायी नोट',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI व्यवहार ID',
  'agent.approvalEdit.field.txn.placeholder': 'उदा. 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'तुमचा पूल ({balance}) ही मंजुरी कव्हर करू शकत नाही. आधी टॉप-अप करा.',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'मंजूर रक्कम किमान ₹1 असली पाहिजे',
  'agent.approvalEdit.error.amountMax': 'मंजूर रक्कम विनंती {amount} पेक्षा जास्त असू शकत नाही',
  'agent.approvalEdit.error.reasonRequired': 'वेगळी रक्कम मंजूर करताना कारण (≥5 अक्षर) आवश्यक आहे',
  'agent.approvalEdit.error.txnRequired': 'UPI व्यवहार ID आवश्यक आहे (≥6 अक्षर)',
  'agent.approvalEdit.error.fallback': 'अयशस्वी — पुन्हा प्रयत्न करा',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'रद्द करा',
  'agent.approvalEdit.cta.approve': 'मंजूर करा',
  'agent.approvalEdit.cta.markPaid': 'पेड चिन्हांकित',
  'agent.approvalEdit.cta.submitting': 'सबमिट होत आहे…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'माझे खाते',
  'agent.profile.hub.subtitle': 'ओळख, वॉलेट आणि डिपॉझिट एका ठिकाणी',
  'agent.profile.hub.tab.wallet': 'वॉलेट',
  'agent.profile.hub.tab.deposit': 'डिपॉझिट',
  'agent.profile.hub.tab.account': 'खाते',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'साइन आउट',
  'agent.profile.signOut.modal.title': 'साइन आउट?',
  'agent.profile.signOut.modal.body': 'खात्यात प्रवेश करण्यासाठी तुम्हाला पुन्हा साइन इन करावे लागेल.',
  'agent.profile.signOut.modal.stay': 'साइन इन रहा',
  'agent.profile.signOut.modal.confirm': 'होय, साइन आउट',
  'agent.profile.signOut.modal.busy': 'साइन आउट होत आहे…',
  'agent.profile.signOut.error': 'साइन आउट करता आले नाही',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'साइन इन नाही.',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'एजंट',
  'agent.account.hero.role.admin': 'प्लॅटफॉर्म एडमिन',
  'agent.account.hero.changePhoto': 'फोटो बदला',
  'agent.account.hero.removePhoto': 'फोटो काढा',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'तुमचा फोटो समायोजित करा',
  'agent.account.avatar.confirmRemove': 'तुमचा प्रोफाइल फोटो काढायचा?',
  'agent.account.toast.photoUpdated': 'फोटो अपडेट झाला',
  'agent.account.toast.photoRemoved': 'फोटो काढला',
  'agent.account.toast.saved': 'सेव्ह झाले',
  'agent.account.toast.locationSaved': 'स्थान सेव्ह झाले',
  'agent.account.toast.passwordChanged': 'पासवर्ड बदलला — पुन्हा साइन इन करा',
  'agent.account.error.uploadFailed': 'अपलोड अयशस्वी',
  'agent.account.error.removeFailed': 'अयशस्वी',
  'agent.account.error.saveFailed': 'सेव्ह अयशस्वी',
  'agent.account.error.changeFailed': 'बदल अयशस्वी',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'खाते माहिती',
  'agent.account.info.subtitle': 'फक्त वाचनीय प्लॅटफॉर्म मेटाडेटा',
  'agent.account.info.memberSince': 'सदस्य कधीपासून',
  'agent.account.info.lastLogin': 'शेवटचा लॉगिन',
  'agent.account.info.lastLoginIp': 'शेवटचा लॉगिन IP',
  'agent.account.info.reportsTo': 'कोणाला रिपोर्ट',
  'agent.account.info.reportsToValue': 'एडमिन #{id}',
  'agent.account.info.agentId': 'एजंट ID',
  'agent.account.info.firstLogin': 'पहिल्यांदा',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'वैयक्तिक तपशील',
  'agent.account.personal.subtitle.locked': 'एडमिनने ओळख लॉक केली · फक्त पहा',
  'agent.account.personal.subtitle.agent': 'युजरनेम, ईमेल आणि संपर्क नंबर',
  'agent.account.personal.subtitle.admin': 'युजरनेम आणि ईमेल',
  'agent.account.personal.field.username': 'युजरनेम',
  'agent.account.personal.field.email': 'ईमेल',
  'agent.account.personal.field.phone': 'संपर्क नंबर',
  'agent.account.personal.field.phone.placeholder': 'उदा. +91 9876543210',
  'agent.account.personal.field.phone.hint': 'खेळाडू आणि एडमिन या नंबरवर तुमच्याशी संपर्क करतात. साफ करण्यासाठी रिक्त ठेवा.',
  'agent.account.personal.fieldLockedNote': 'लॉक',
  'agent.account.personal.cta.save': 'बदल सेव्ह करा',
  'agent.account.personal.cta.saving': 'सेव्ह होत आहे…',
  'agent.account.personal.cta.noChanges': 'कोणताही बदल नाही',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'तुमचे स्थान',
  'agent.account.location.subtitle.summary': 'लोकेटर नवीन खेळाडूंना तुमच्या जवळच्या क्षेत्रात पाठवते',
  'agent.account.location.subtitle.edit': 'तुमचे क्षेत्र अपडेट करा जेणेकरून लोकेटर योग्य खेळाडू पाठवेल',
  'agent.account.location.cta.edit': 'संपादित करा',
  'agent.account.location.cta.cancel': 'रद्द करा',
  'agent.account.location.cta.save': 'सेव्ह करा',
  'agent.account.location.areaLabel': 'क्षेत्र',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'स्थान सेट',
  'agent.account.location.setBody': 'फाइलवर GPS निर्देशांक — क्षेत्र तपशील पाहण्यासाठी संपादित करा टॅप करा.',
  'agent.account.location.lastUpdated': 'शेवटचा अपडेट · {date}',

  // Security card (password change).
  'agent.account.security.title': 'सुरक्षा',
  'agent.account.security.subtitle.locked': 'एडमिनने लॉक केले · फक्त पहा',
  'agent.account.security.subtitle.unlocked': 'तुमचा पासवर्ड बदला · इतर डिव्हाइसेसमधून साइन आउट होईल',
  'agent.account.security.field.current': 'सध्याचा पासवर्ड',
  'agent.account.security.field.new': 'नवीन पासवर्ड',
  'agent.account.security.field.new.hint': 'किमान 8 अक्षर, एक अक्षर आणि एक अंकासह',
  'agent.account.security.field.confirm': 'नवीन निश्चित करा',
  'agent.account.security.cta.update': 'पासवर्ड अपडेट करा',
  'agent.account.security.cta.updating': 'अपडेट होत आहे…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'नवीन पासवर्ड जुळत नाहीत',
  'agent.account.security.error.tooShort': 'पासवर्ड ≥ 8 अक्षरांचा असला पाहिजे',
  'agent.account.security.error.weak': 'पासवर्डमध्ये किमान एक अक्षर आणि एक अंक असला पाहिजे',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'शेअर करण्यायोग्य एजंट ID',
  'agent.shareableId.tooltip.copy': 'UAID कॉपी करा',
  'agent.shareableId.tooltip.copied': 'UAID कॉपी झाला',
  'agent.shareableId.cta.copy': 'कॉपी',
  'agent.shareableId.cta.copied': 'कॉपी झाले!',
  'agent.shareableId.body.before': 'हा कोड इतर एजंटसोबत शेअर करा जेणेकरून ते खेळाडू तुमच्याकडे हस्तांतरित करू शकतील. चॅट किंवा WhatsApp द्वारे पाठवणे सुरक्षित आहे — हे शेअर करणे तुमच्या खात्यात कोणताही प्रवेश ',
  'agent.shareableId.body.not': 'देत नाही',
  'agent.shareableId.body.after': '.',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'पुरस्कार',
  'agent.rewards.hub.subtitle': 'ऑफर पहा आणि तुमचा एक्सपोजर ट्रॅक करा',
  'agent.rewards.hub.tab.browse': 'ऑफर पहा',
  'agent.rewards.hub.tab.exposure': 'माझा एक्सपोजर',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'ऑप्ट इन',
  'agent.rewards.status.optedOut': 'ऑप्ट आउट',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'तुमच्या खेळाडूंसाठी विशेष',
  'agent.rewards.browse.empty.title': 'आत्ता कोणताही सक्रिय पुरस्कार नाही',
  'agent.rewards.browse.empty.body': 'एडमिन नियमित नवीन ऑफर पोस्ट करतात. लवकरच पहा — किंवा चुकलेल्या गोष्टींमध्ये ऑप्ट इन करण्यासाठी तुमच्या अपलाइनला सांगा.',
  'agent.rewards.browse.error.loadFailed': 'पुरस्कार लोड होऊ शकले नाहीत',
  'agent.rewards.browse.card.aria.manage': '{title}साठी ऑप्ट-इन व्यवस्थापित करा',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'ऑप्ट इन',
  'agent.rewards.browse.chip.optInPrompt': 'ऑप्ट इन करण्यासाठी टॅप करा',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'तुमचा एक्सपोजर मोजत आहोत…',
  'agent.rewards.exposure.error.loadFailed': 'एक्सपोजर लोड होऊ शकला नाही',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'ऑफर एक्सपोजर',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} सक्रिय ऑप्ट-इन',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} सक्रिय ऑप्ट-इन',

  'agent.rewards.exposure.hero.actualSpend.label': 'वास्तविक खर्च',
  'agent.rewards.exposure.hero.actualSpend.helper': 'आतापर्यंत तुमच्या डाउनलाइनद्वारे दावा.',

  'agent.rewards.exposure.hero.maxExposure.label': 'कमाल एक्सपोजर',
  'agent.rewards.exposure.hero.maxExposure.helper': 'सर्वात वाईट परिस्थितीची मर्यादा — प्रत्येक उरलेला बजेट रुपया दावा.',

  'agent.rewards.exposure.hero.ceiling.label': 'मर्यादा वापरली',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'जोखीम गेज',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} कमाल',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': 'वर ',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': ', तुमच्या ऑप्ट-इनवर लक्ष ठेवा',
  'agent.rewards.exposure.gauge.legend.rose.before': 'वर ',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': ', तुम्ही तुमच्या मर्यादेजवळ',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'तुम्ही अद्याप अंदाजित मर्यादा असलेल्या कशातही ऑप्ट इन नाहीत.',
  'agent.rewards.exposure.gauge.caption.high': 'तुम्ही तुमच्या कमाल एक्सपोजरचा {pct}% जाळला आहे. बहुतेक ऑप्ट-इन जोरदार दावा झाले.',
  'agent.rewards.exposure.gauge.caption.mid': 'तुम्ही तुमच्या कमाल मर्यादेच्या {pct}% वर आहात. प्रति-ऑफर तपशीलावर लक्ष ठेवा.',
  'agent.rewards.exposure.gauge.caption.low': 'आरामदायक — आतापर्यंत तुमच्या कमाल मर्यादेचा फक्त {pct}% वापरला.',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'कमाल एक्सपोजर कसे मोजले जाते:',
  'agent.rewards.exposure.footnote.prefix': ' प्रत्येक ऑप्ट-इन ऑफरसाठी: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — पुराणमतवादी वरची मर्यादा. तुमची वास्तविक जबाबदारी सहसा ',
  'agent.rewards.exposure.footnote.emphasis': 'कमी',
  'agent.rewards.exposure.footnote.suffix2': ' असते जेव्हा तुमच्या खालचे सब-एजंट देखील ऑप्ट इन करतात (ते मोठा वाटा देतात; तुम्ही उर्वरित देता).',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'कोणतेही सक्रिय ऑप्ट-इन नाहीत',
  'agent.rewards.exposure.empty.body': 'तुम्ही ऑफर पहा टॅबवरून कोणत्याही ऑफरमध्ये ऑप्ट इन केल्यावर, ते येथे तुमच्या खर्च आणि अंदाजित एक्सपोजरसह दिसते.',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% वाटा',
  'agent.rewards.exposure.row.claims.one': '{count} दावा',
  'agent.rewards.exposure.row.claims.other': '{count} दावे',
  'agent.rewards.exposure.row.overridden': 'एडमिन-ओव्हरराइड',
  'agent.rewards.exposure.row.actualMaxLabel': 'वास्तविक / कमाल',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'खर्च-वाटा ऑप्ट-इन',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'गेम नफा',
  'agent.profile.rates.infoBanner.standardPctText': ' मानक टक्केवारी वापरतात. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'प्रचारात्मक खर्च',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ऑफर, कॅशबॅक) डायनॅमिक अपलाइन पूल वापरतात. दोन्ही आपल्या स्तरावर (L{level}) आधारित प्लॅटफॉर्म ॲडमिनद्वारे ग्लोबली व्यवस्थापित केले जातात.',
  'agent.profile.rates.section.gameProfits': 'गेम नफा — आपण प्रत्येक सामन्यात काय कमवता',
  'agent.profile.rates.section.promotionalCosts': 'प्रचारात्मक खर्च — आपले अपलाइन सबसिडी योगदान',
  'agent.profile.rates.pillar.platformFee': 'प्लॅटफॉर्म फी %',
  'agent.profile.rates.pillar.winLoss': 'विजय / पराभव नफा %',
  'agent.profile.rates.pillar.promoWeight': 'प्रचारात्मक पूल वजन',
  'agent.profile.rates.guarantee.title': 'डायरेक्ट रेफरल गॅरंटी',
  'agent.profile.rates.guarantee.body': 'जर आपण ऑफर क्लेम करणाऱ्या प्लेयरला थेट रेफर केले असेल, तर आपण ₹0.00 भरता. आपल्या नेटवर्कची वाढ करण्यासाठी आपल्या अपलाइन्स 100% खर्चाला सबसिडी देतात.',
  'agent.profile.rates.poolExplainer.title': 'पूल कसे कार्य करते:',
  'agent.profile.rates.poolExplainer.body': 'जेव्हा सब-एजेंटचा प्लेयर ऑफर क्लेम करतो, तेव्हा आपले वजन उर्वरित सक्रिय अपलाइन + प्लॅटफॉर्मसह एका पूलमध्ये जोडले जाते. आपण (आपले वजन / एकूण पूल) × ऑफर खर्च भरता. जास्त वजन = मोठा वाटा.',
  'agent.profile.rates.weightLabel': 'वजन:',
  'agent.profile.rates.updatedPrefix': 'अद्ययावत',
  'agent.profile.rates.notSetYet': 'अद्याप सेट केले नाही',
  'agent.rewards.optInModal.guarantee.title': 'डायरेक्ट रेफरल गॅरंटी',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'जर <strong>आपण थेट रेफर केले</strong> या ऑफरला क्लेम करणाऱ्या प्लेयरला, तर आपण <strong>₹0.00</strong> भरता. आपल्या नेटवर्कची वाढ करण्यासाठी आपल्या अपलाइन्स 100% खर्चाला सबसिडी देतात.',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'जेव्हा <strong>आपले प्लेयर</strong> ही ऑफर क्लेम करतात, तेव्हा आपण <strong>₹0.00</strong> भरता. आपल्या नेटवर्कची वाढ करण्यासाठी आपल्या अपलाइन्स 100% खर्चाला सबसिडी देतात.',
  'agent.rewards.optInModal.uplineReality.title': 'अपलाइन वास्तविकता तपासणी',
  'agent.rewards.optInModal.uplineReality.body': 'आपल्याकडे <strong>{count}</strong> डायरेक्ट सब-एजेंट{plural} आहेत. जर सब-एजेंटचा प्लेयर ही ऑफर क्लेम करतो, तर आपण आपल्या <strong>पूल वजन ({weight})</strong> च्या आधारे उर्वरित सक्रिय अपलाइन आणि प्लॅटफॉर्मच्या तुलनेत आनुपातिक हिस्सा भरता.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'डायरेक्ट',
  'agent.rates.col.indirect': 'इनडायरेक्ट',
  'agent.rates.indirect.dynamic': 'डायनामिक',
  'agent.rates.indirect.notApplicable': 'लागू नाही',
  'agent.rates.indirect.rangeTo': 'पर्यंत',
  'agent.rewards.optInModal.close': 'बंद करा',
  'agent.rewards.optInModal.cancel': 'रद्द करा',
  'agent.rewards.optInModal.loading': 'ऑफर तपशील लोड होत आहेत…',
  'agent.rewards.optInModal.error.loadFailed': 'ऑफर तपशील लोड होऊ शकले नाहीत',
  'agent.rewards.optInModal.error.optInFailed': 'ऑप्ट-इन अयशस्वी',
  'agent.rewards.optInModal.error.optOutFailed': 'ऑप्ट-आउट अयशस्वी',
  'agent.rewards.optInModal.error.saveFailed': 'सब-एजंट दर सेव्ह होऊ शकले नाहीत',
  'agent.rewards.optInModal.toast.optedIn': 'ऑप्ट इन. आता तुमची डाउनलाइन ही ऑफर पाहू शकते.',
  'agent.rewards.optInModal.toast.optedOut': 'ऑप्ट आउट. ऑफर तुमच्या डाउनलाइनमधून काढली.',
  'agent.rewards.optInModal.toast.saved.one': '{count} सब-एजंट दर बदल सेव्ह.',
  'agent.rewards.optInModal.toast.saved.other': '{count} सब-एजंट दर बदल सेव्ह.',
  'agent.rewards.optInModal.confirmOptOut': 'या ऑफरमधून ऑप्ट आउट? तुमची डाउनलाइन यापुढे ती पाहणार नाही.',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'ऑफर #{id} साठी ऑप्ट-इन मॉडलवरून सेट',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'तुमचा वाटा',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'ऑप्ट-इनवर लॉक',
  'agent.rewards.optInModal.stake.current': 'सध्याचा दर',
  'agent.rewards.optInModal.stake.noRate': 'एडमिनने अद्याप तुम्हाला दर दिला नाही',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'आतापर्यंत खर्च',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'या ऑफरवर डाउनलाइन दाव्यांसाठी तुमच्या वॉलेटमधून दिलेले एकूण रुपये.',
  'agent.rewards.optInModal.stake.maxPossible.label': 'कमाल शक्य',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'सर्वात वाईट परिस्थितीत तुम्ही जे रुपये देऊ शकता जर प्रत्येक उरलेला बजेट रुपया दावा झाला असेल.',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— बजेट मर्यादा नाही —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'सर्वात वाईट परिस्थिती: जर प्लॅटफॉर्म बजेटचे उरलेले प्रत्येक ₹{amount} तुमच्या डाउनलाइनद्वारे दावा झाले, तर तुम्ही त्याचा {pct}% भरता.',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'तुम्ही ऑप्ट इन आहात',
  'agent.rewards.optInModal.toggle.title.optedOut': 'तुमच्या डाउनलाइनसोबत शेअर करण्यासाठी ऑप्ट इन करा',
  'agent.rewards.optInModal.toggle.body.optedIn': 'तुमच्या डाउनलाइनचे खेळाडू ही ऑफर पाहतात. तुम्ही त्यांच्या प्रत्येक दाव्यावर तुमच्या लॉक वाट्यावर सह-निधी देता.',
  'agent.rewards.optInModal.toggle.body.optedOut': 'जोपर्यंत तुम्ही ऑप्ट इन करत नाही, कोणताही खेळाडू ही ऑफर पाहणार नाही. एकदा ऑप्ट इन केले की तुमचा वाटा दर या ऑफरसाठी लॉक होतो.',
  'agent.rewards.optInModal.toggle.optOutLink': 'या ऑफरमधून ऑप्ट आउट',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'सब-एजंट दर',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'प्रत्येकजण कमाल घेऊ शकतो: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} असेव्ह',
  'agent.rewards.optInModal.subAgents.empty': 'थेट सब-एजंट नाहीत.',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'सेव्ह कॅस्केड होते — जर तुम्ही येथे अशा बालकाला वाढवले ज्याच्याकडे आधीच खोल सब-एजंट आहेत, ते दर साखळी सुसंगत ठेवण्यासाठी आपोआप क्लॅम्प होऊ शकतात.',
  'agent.rewards.optInModal.subAgents.noChildren': 'तुमच्याकडे थेट सब-एजंट नाहीत. ऑफर तुमच्या स्वतःच्या खेळाडूंना दिसते; एडमिन संपूर्ण उर्वरित भरतो.',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'दर एंटर करा (साफ करण्यासाठी 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'फक्त संख्या',
  'agent.rewards.optInModal.error.negative': 'ऋणात्मक असू शकत नाही',
  'agent.rewards.optInModal.error.max100': 'कमाल 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'कमाल {ceiling}% (तुमचा वाटा)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'सेव्ह होत आहे…',
  'agent.rewards.optInModal.cta.done': 'झाले',
  'agent.rewards.optInModal.cta.save.one': '{count} बदल सेव्ह',
  'agent.rewards.optInModal.cta.save.other': '{count} बदल सेव्ह',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'खेळाडूला रिचार्ज करा',
  'agent.adminRechargePlayer.subtitle': 'तुमच्या पूलमधून खेळाडूच्या वॉलेटला',
  'agent.adminRechargePlayer.backLink': 'खेळाडूकडे परत',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'नवीन खेळाडू जोडा',
  'agent.createPlayer.subtitle.agent': 'ते आपोआप तुमच्या अंतर्गत असतील.',
  'agent.createPlayer.subtitle.admin': 'खाली मालक एजंट निवडा, किंवा प्लॅटफॉर्म-डायरेक्टसाठी रिक्त ठेवा.',
  'agent.createPlayer.close': 'बंद करा',
  'agent.createPlayer.section.identity.title': 'ओळख',
  'agent.createPlayer.section.identity.hint': 'खाते तयार करण्यासाठी आवश्यक',
  'agent.createPlayer.field.username.label': 'युजरनेम',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ईमेल',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'मोबाइल',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-अंकी भारतीय मोबाइल, +91 आपोआप जोडले जाईल',
  'agent.createPlayer.field.password.label': 'प्रारंभिक पासवर्ड',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ अक्षर, अक्षर + अंक',
  'agent.createPlayer.section.owner.title': 'मालक एजंट',
  'agent.createPlayer.section.owner.hint': 'पर्यायी — प्लॅटफॉर्म-डायरेक्ट खेळाडूसाठी रिक्त ठेवा',
  'agent.createPlayer.field.owner.label': 'एजंट',
  'agent.createPlayer.field.owner.none': '— कोणीही नाही (प्लॅटफॉर्म-डायरेक्ट) —',
  'agent.createPlayer.section.profile.title': 'प्रोफाइल',
  'agent.createPlayer.section.profile.hint': 'पर्यायी — आत्ता माहित असलेले भरा; खेळाडू पहिल्या लॉगिनवर पूर्ण करू शकतो',
  'agent.createPlayer.field.firstName.label': 'पहिले नाव',
  'agent.createPlayer.field.lastName.label': 'आडनाव',
  'agent.createPlayer.field.dob.label': 'जन्म तारीख',
  'agent.createPlayer.field.gender.label': 'लिंग',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— उल्लेख नाही —',
  'agent.createPlayer.gender.male': 'पुरुष',
  'agent.createPlayer.gender.female': 'स्त्री',
  'agent.createPlayer.gender.other': 'इतर',
  'agent.createPlayer.gender.preferNot': 'सांगायचे नाही',
  'agent.createPlayer.field.language.label': 'पसंतीची भाषा',
  'agent.createPlayer.section.location.title': 'स्थान',
  'agent.createPlayer.section.location.hint': 'पर्यायी — खेळाडू पहिल्या लॉगिनवर पूर्ण करू शकतो',
  // Footer.
  'agent.createPlayer.disclaimer': 'खेळाडूला status=active आणि ईमेल ऑटो-सत्यापित म्हणून तयार केले जाते — कोणताही दुवा पाठवला जात नाही. संपर्क माहितीवर विश्वास ठेवा.',
  'agent.createPlayer.cta.cancel': 'रद्द करा',
  'agent.createPlayer.cta.creating': 'तयार होत आहे…',
  'agent.createPlayer.cta.submit': 'खेळाडू तयार करा',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'एजंट ID नाही',
  'agent.createPlayer.error.createFailed': 'खेळाडू तयार करू शकलो नाही',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'आज',
  'agent.adminHome.range.yesterday': 'काल',
  'agent.adminHome.range.week': 'या आठवड्यात',
  'agent.adminHome.range.lastWeek': 'मागील आठवड्यात',
  'agent.adminHome.range.month': 'या महिन्यात',
  'agent.adminHome.range.lastMonth': 'मागील महिन्यात',
  'agent.adminHome.range.all': 'संपूर्ण',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'पुन्हा स्वागत आहे',
  'agent.adminHome.greeting.fallback': 'मित्र',
  'agent.adminHome.errorBanner.fallback': 'लोड करण्यात अयशस्वी',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'प्लॅटफॉर्म इनफ्लो',
  'agent.adminHome.admin.action.moneyQueue': 'मनी रांग',
  'agent.adminHome.admin.action.agents': 'एजंट',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'मॅच',
  'agent.adminHome.admin.snapshot.newSignups': 'नवीन साइनअप',
  'agent.adminHome.admin.snapshot.active': 'सक्रिय',
  'agent.adminHome.admin.snapshot.pending': 'प्रलंबित',

  // Section titles between rows.
  'agent.adminHome.section.games': 'गेम्स',
  'agent.adminHome.section.games.subtitle': 'मॅच आणि प्लॅटफॉर्म फी · {range}',
  'agent.adminHome.section.activity': 'क्रियाकलाप',
  'agent.adminHome.section.ops': 'ऑपरेशन्स आणि अंतर्दृष्टी',
  'agent.adminHome.section.ops.subtitle': 'लाइव्ह स्नॅपशॉट — संपूर्ण पेजवर जाण्यासाठी कोणत्याही कार्डवर क्लिक करा',
  'agent.adminHome.section.gameSettings': 'गेम सेटिंग्ज',
  'agent.adminHome.section.gameSettings.subtitle': 'प्रत्येक गेम कॉन्फिगर करा',
  'agent.adminHome.section.downline': 'माझ्या डाउनलाइनची अंतर्दृष्टी',
  'agent.adminHome.section.downline.subtitle': 'संपूर्ण पेजवर जाण्यासाठी कोणत्याही कार्डवर क्लिक करा',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'खेळाडू वॉलेट',
  'agent.adminHome.admin.card.players.headlineLabel': 'कालावधीत रिचार्ज',
  'agent.adminHome.admin.card.players.flow.recharges': 'रिचार्ज',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'पैसे काढणे',
  'agent.adminHome.admin.card.players.pending.recharges': 'प्रलंबित रिचार्ज',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'प्रलंबित पैसे काढणे',
  'agent.adminHome.admin.card.players.stat.total': 'एकूण खेळाडू',
  'agent.adminHome.admin.card.players.stat.active': 'सक्रिय',
  'agent.adminHome.admin.card.players.stat.suspended': 'निलंबित',

  'agent.adminHome.admin.card.agents.title': 'एजंट वॉलेट',
  'agent.adminHome.admin.card.agents.headlineLabel': 'एजंट पूलमध्ये',
  'agent.adminHome.admin.card.agents.flow.topups': 'टॉप-अप (एडमिन → एजंट)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'आउटफ्लो (पेड / टॉप-अप)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'प्रलंबित एजंट पैसे काढणे',
  'agent.adminHome.admin.card.agents.stat.total': 'एकूण एजंट',
  'agent.adminHome.admin.card.agents.stat.active': 'सक्रिय',
  'agent.adminHome.admin.card.agents.stat.suspended': 'निलंबित',

  'agent.adminHome.admin.card.bots.title': 'बॉट्स वॉलेट',
  'agent.adminHome.admin.card.bots.headlineLabel': 'बॉट वॉलेटमध्ये',
  'agent.adminHome.admin.card.bots.stat.total': 'एकूण बॉट्स',
  'agent.adminHome.admin.card.bots.stat.active': 'सक्रिय',
  'agent.adminHome.admin.card.bots.stat.matches': 'मॅच',

  'agent.adminHome.admin.card.rewards.title': 'पुरस्कार',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'या आठवड्यात पेड',
  'agent.adminHome.admin.card.rewards.pending.claims': 'प्रलंबित दावे',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'सक्रिय ऑफर',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'बल्क जॉब्स',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'मॅच',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'लुडो',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'रमी',
  'agent.adminHome.game.carrom': 'कॅरम',
  'agent.adminHome.game.chess': 'बुद्धिबळ',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'टॉप खेळाडू',
  'agent.adminHome.activity.topPlayers.empty': 'या कालावधीत मॅच नाहीत.',
  'agent.adminHome.activity.topPlayers.metric': '{count} मॅच',
  'agent.adminHome.activity.topAgents.title': 'टॉप एजंट',
  'agent.adminHome.activity.topAgents.subtitle': 'मालक खेळाडूंनुसार',
  'agent.adminHome.activity.topAgents.empty': 'अद्याप सक्रिय एजंट नाहीत.',
  'agent.adminHome.activity.topAgents.metric': '{count} खेळाडू',
  'agent.adminHome.activity.recent.title': 'अलीकडील व्यवहार',
  'agent.adminHome.activity.recent.subtitle': 'शेवटचे 5',
  'agent.adminHome.activity.recent.empty': 'अद्याप व्यवहार नाहीत.',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'टॉप कमावणारे (7d)',
  'agent.adminHome.ops.topEarners.subtitle': 'खऱ्या नेटनुसार',
  'agent.adminHome.ops.topEarners.empty': 'मागील 7 दिवसांत कमाई नाही.',
  'agent.adminHome.ops.liveActivity.title': 'लाइव्ह क्रियाकलाप',
  'agent.adminHome.ops.liveActivity.subtitle': 'प्लॅटफॉर्म-व्यापी शेवटचे 5 इव्हेंट',
  'agent.adminHome.ops.liveActivity.empty': 'शांत — मागील 24h मध्ये इव्हेंट नाहीत.',
  'agent.adminHome.ops.botPerf.title': 'बॉट कामगिरी (7d)',
  'agent.adminHome.ops.botPerf.subtitle': 'प्रति-श्रेणी नफा-तोटा',
  'agent.adminHome.ops.rateChanges.title': 'अलीकडील रेट बदल',
  'agent.adminHome.ops.rateChanges.subtitle': 'नवीनतम भागीदारी संपादन',
  'agent.adminHome.ops.rateChanges.empty': 'अद्याप रेट-बदल इव्हेंट नाहीत.',
  'agent.adminHome.ops.opsHealth.title': 'ऑपरेशन आरोग्य',
  'agent.adminHome.ops.opsHealth.subtitle': 'डेड-लेटर रांग · प्रलंबित सेटलमेंट',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'कमावले',
  'agent.adminHome.feed.match.onGameSuffix': '{game} वर',
  'agent.adminHome.feed.recharge.label': 'रिचार्ज',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'द्वारे',
  'agent.adminHome.feed.rateChange.label': 'रेट बदल',
  'agent.adminHome.feed.cascadeBadge': 'कॅस्केड',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'मंजूर',
  'agent.adminHome.feed.recharge.state.rejected': 'नाकारले',
  'agent.adminHome.feed.recharge.state.pending': 'प्रलंबित',
  'agent.adminHome.feed.recharge.state.paid': 'पेड',
  'agent.adminHome.feed.recharge.state.expired': 'कालबाह्य',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} मॅच',
  'agent.adminHome.leaderboard.matches.other': '{count} मॅच',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'प्लॅटफॉर्म नफा (7d)',
  'agent.adminHome.botMatrix.cat.easy': 'सोपे',
  'agent.adminHome.botMatrix.cat.medium': 'मध्यम',
  'agent.adminHome.botMatrix.cat.hard': 'कठीण',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}m',
  'agent.adminHome.botMatrix.activeSummary': '{total} पैकी {active} बॉट्स सक्रिय',
  'agent.adminHome.botMatrix.loading': 'लोड होत आहे…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ स्वच्छ',
  'agent.adminHome.opsHealth.dlqClean.body': 'कोणतेही अयशस्वी सेटलमेंट जॉब नाहीत.',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} निराकरण न झालेला DLQ जॉब',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} निराकरण न झालेले DLQ जॉब',
  'agent.adminHome.opsHealth.dlqDirty.body': 'पुन्हा प्रयत्न / निराकरण करण्यासाठी क्लिक करा.',
  'agent.adminHome.opsHealth.replay.eyebrow': 'पुन्हा चालवा',
  'agent.adminHome.opsHealth.replay.body': 'सेटलमेंट पुन्हा चालवा',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'पुन्हा असाइन',
  'agent.adminHome.opsHealth.reassign.body': 'बल्क हलवा',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'माझे पूल बॅलन्स',
  'agent.adminHome.agentDash.hero.netPrefix': 'नेट',
  'agent.adminHome.agentDash.action.myWallet': 'माझे वॉलेट',
  'agent.adminHome.agentDash.action.myPlayers': 'माझे खेळाडू',
  'agent.adminHome.agentDash.snapshot.myMatches': 'माझे मॅच',
  'agent.adminHome.agentDash.snapshot.active': 'सक्रिय',
  'agent.adminHome.agentDash.snapshot.netRange': 'नेट (कालावधी)',
  'agent.adminHome.agentDash.snapshot.pending': 'प्रलंबित',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'माझे खेळाडू वॉलेट',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'कालावधीत रिचार्ज',
  'agent.adminHome.agentDash.card.players.stat.total': 'माझे खेळाडू',
  'agent.adminHome.agentDash.card.wallet.title': 'माझे वॉलेट',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'पूल बॅलन्स',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'कमावले (कमिशन)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'पेड आउट',
  'agent.adminHome.agentDash.card.wallet.pending': 'माझे प्रलंबित पैसे काढणे',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'मिळालेले टॉप-अप',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'नेट (कालावधी)',
  'agent.adminHome.agentDash.card.activity.title': 'क्रियाकलाप',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'कालावधीत माझे मॅच',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'टॉप खर्च करणारा',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'त्यांचा खर्च',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'प्रलंबित आयटम',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'टॉप खर्च करणारे',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'या कालावधीत अद्याप खर्च नाही.',
  'agent.adminHome.agentDash.activity.recent.title': 'अलीकडील क्रियाकलाप',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'तुमच्या खेळाडूंकडून शेवटचे 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'अद्याप व्यवहार नाहीत.',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'टॉप कमावणारे (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'तुमचे सब-एजंट क्रमवारीत',
  'agent.adminHome.agentDash.insights.downline.title': 'डाउनलाइन क्रियाकलाप',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'तुमच्या डाउनलाइनकडून शेवटचे 5 इव्हेंट',
  'agent.adminHome.agentDash.insights.deposit.title': 'डिपॉझिट लेजर',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'अलीकडील डिपॉझिट क्रेडिट / परतावे',
  'agent.adminHome.agentDash.insights.deposit.empty': 'अद्याप डिपॉझिट क्रियाकलाप नाही.',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'सब-एजंट',
  'agent.subAgentDetail.subtitle': 'थेट सब-एजंट',
  'agent.subAgentDetail.error.loadFailed': 'सब-एजंट लोड करण्यात अयशस्वी',
  'agent.subAgentDetail.backToList': 'सब-एजंट यादीकडे परत',
  'agent.subAgentDetail.loading': 'लोड होत आहे…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'ओळख',
  'agent.subAgentDetail.tab.players': 'खेळाडू',
  'agent.subAgentDetail.tab.wallet': 'वॉलेट',
  'agent.subAgentDetail.tab.rates': 'दर',
  'agent.subAgentDetail.tab.ops': 'ऑपरेशन्स',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} खेळाडू',
  'agent.subAgentDetail.header.players.other': '{count} खेळाडू',
  'agent.subAgentDetail.header.pool': 'पूल: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'शेवटचा लॉगिन {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'सक्रिय',
  'agent.subAgentDetail.status.suspended': 'निलंबित',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'युजरनेम',
  'agent.subAgentDetail.identity.row.email': 'ईमेल',
  'agent.subAgentDetail.identity.row.status': 'स्थिती',
  'agent.subAgentDetail.identity.row.parent': 'पॅरंट एजंट',
  'agent.subAgentDetail.identity.parentValue': '@{username} (तुम्ही)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'मालकीचे खेळाडू',
  'agent.subAgentDetail.identity.row.wallet': 'वॉलेट बॅलन्स',
  'agent.subAgentDetail.identity.row.created': 'तयार केले',
  'agent.subAgentDetail.identity.row.lastLogin': 'शेवटचा लॉगिन',
  'agent.subAgentDetail.identity.row.identityLocked': 'ओळख लॉक',
  'agent.subAgentDetail.identity.identityLocked.yes': 'होय (फक्त एडमिन संपादन)',
  'agent.subAgentDetail.identity.identityLocked.no': 'नाही (सब-एजंट स्वतः संपादित करू शकतो)',
  'agent.subAgentDetail.identity.row.receiving': 'खेळाडू स्वीकारत आहे',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'स्वीकारत आहे',
  'agent.subAgentDetail.optOut.optedOut': 'ऑप्ट आउट',
  'agent.subAgentDetail.optOut.toast.accepting': 'सब-एजंट आता खेळाडू स्वीकारत आहे',
  'agent.subAgentDetail.optOut.toast.optedOut': 'सब-एजंटने लोकेटरमधून ऑप्ट आउट केले',
  'agent.subAgentDetail.optOut.error.updateFailed': 'अपडेट करता आले नाही',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'स्थान',
  'agent.subAgentDetail.location.lastUpdated': '· शेवटचा अपडेट {date}',
  'agent.subAgentDetail.location.cta.save': 'स्थान सेव्ह करा',
  'agent.subAgentDetail.location.nothingToSave': 'सेव्ह करण्यासाठी काहीही नाही',
  'agent.subAgentDetail.location.toast.saved.one': 'स्थान सेव्ह झाले ({count} फील्ड)',
  'agent.subAgentDetail.location.toast.saved.other': 'स्थान सेव्ह झाले ({count} फील्ड)',
  'agent.subAgentDetail.location.hint': 'सेव्ह सक्षम करण्यासाठी वेगळे राज्य / जिल्हा / क्षेत्र निवडा. (सब-एजंट स्वतः पहिल्या लॉगिनवर सक्तीच्या पॉप-अपद्वारे अचूक GPS ओव्हरले करतात.)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'या सब-एजंटकडे अद्याप कोणतेही खेळाडू नाहीत.',
  'agent.subAgentDetail.players.count.one': '{count} खेळाडू',
  'agent.subAgentDetail.players.count.other': '{count} खेळाडू',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'सध्याचे सब-एजंट बॅलन्स',
  'agent.subAgentDetail.wallet.ledger.title': '@{username} चे संपूर्ण वॉलेट लेजर',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'प्रत्येक हालचाल · सब-एजंट त्याच्या वॉलेटमध्ये तेच पाहतो',
  'agent.subAgentDetail.wallet.ledger.empty': 'अद्याप वॉलेट क्रियाकलाप नाही.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'तुम्ही ↔ ते',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'बॅल ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'एडमिनकडून टॉप-अप',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'खेळाडूला पेआउट',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'खेळाडूचा रिचार्ज',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'महसूल वाटा',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'रिचार्ज कमिशन',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'सह-निधी ऑफर',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'एडमिन समायोजन',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'पैसे काढणे होल्ड',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'पैसे काढणे परतावा',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'प्लॅटफॉर्म फी वाटा',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'विन/लॉस वाटा',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'नाणी परत घेत आहात?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'विचारा ',
  'agent.subAgentDetail.wallet.pullback.body.middle': 'ला त्यांच्या स्वतःच्या पूलमधून पैसे काढण्याची विनंती सबमिट करण्यास. तुम्ही ती (पूर्ण किंवा आंशिक) पेड चिन्हांकित करू शकता ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'फायनान्स › सब-एजंट',
  'agent.subAgentDetail.wallet.pullback.body.after': ' वरून.',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username}ला ट्रान्सफर',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username}कडून पैसे काढा',
  'agent.subAgentDetail.transfer.topup.body': 'अणू ट्रान्सफर: तुमचे वॉलेट कमी, त्यांचे वॉलेट जास्त. दोन्ही पूल ऋणात्मक होऊ शकतात (साइन्ड-पूल डिझाइन).',
  'agent.subAgentDetail.transfer.withdraw.body': 'अणू उलट ट्रान्सफर: त्यांचे वॉलेट कमी, तुमचे वॉलेट जास्त. सब-एजंटची पेआउट विनंती निकाली काढताना वापरा.',
  'agent.subAgentDetail.transfer.amountLabel': 'रक्कम (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'नोट (पर्यायी)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'उदा. साप्ताहिक टॉप-अप',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'उदा. पेआउट सेटलमेंट',
  'agent.subAgentDetail.transfer.error.amountPositive': 'रक्कम धनात्मक पूर्णांक असली पाहिजे.',
  'agent.subAgentDetail.transfer.confirm.topup': 'तुमच्या पूलमधून @{username}ला {amount} ट्रान्सफर? तुमचे बॅलन्स याच रकमेने कमी होईल.',
  'agent.subAgentDetail.transfer.confirm.withdraw': '@{username}च्या पूलमधून {amount} तुमच्याकडे परत? त्यांचे बॅलन्स याच रकमेने कमी होईल.',
  'agent.subAgentDetail.transfer.toast.topup': '@{username}ला {amount} पाठवले. त्यांचे बॅलन्स: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username}कडून {amount} परत घेतले. त्यांचे बॅलन्स: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working': 'काम सुरू…',
  'agent.subAgentDetail.transfer.cta.suspended': 'सब-एजंट निलंबित',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} ट्रान्सफर',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} काढा',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'दर लोड होत आहेत…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'प्रत्येक कार्ड एक कमिशन पिलर आहे; आतील प्रत्येक रांग एक (पिलर × गेम) वाटा आहे. ',
  'agent.subAgentDetail.rates.intro.ceiling': 'मर्यादा',
  'agent.subAgentDetail.rates.intro.part2': ' जी प्रत्येक इनपुटच्या पुढे दिसते ती तुमचा स्वतःचा दर — गोल्डन लिमिटनुसार तुम्ही त्यापेक्षा जास्त बालकाला देऊ शकत नाही. तुम्ही जे बदलता ते संपादनयोग्य होते; क्लिक करा ',
  'agent.subAgentDetail.rates.intro.applyWord': 'लागू करा',
  'agent.subAgentDetail.rates.intro.part3': ' कमिट करण्यासाठी. कॅस्केड क्लॅम्प सर्व्हर-साइडवर लागू होते.',
  'agent.subAgentDetail.rates.toast.applied': 'दर लागू.',
  'agent.subAgentDetail.rates.confirm.one': '{count} दर बदल लागू करायचा? गोल्डन-लिमिट + कॅस्केड क्लॅम्प सर्व्हर-साइडवर लागू होईल.',
  'agent.subAgentDetail.rates.confirm.other': '{count} दर बदल लागू करायचे? गोल्डन-लिमिट + कॅस्केड क्लॅम्प सर्व्हर-साइडवर लागू होईल.',
  'agent.subAgentDetail.rates.error.blocked': 'अवरोधित: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'प्लॅटफॉर्म फी %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'विन/लॉस नफा %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ऑफर पुरस्कार %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— वैश्विक —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ मर्यादा {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'पॅरंटपेक्षा जास्त (गोल्डन लिमिट)',
  'agent.subAgentDetail.rates.row.editedBadge': 'संपादित',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} बदल टाका',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} बदल टाका',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} बदल लागू',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} बदल लागू',
  'agent.subAgentDetail.rates.cta.applying': 'लागू होत आहे…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'स्थिती',
  'agent.subAgentDetail.ops.status.body': 'निलंबित केल्याने सब-एजंटचे लॉगिन आणि डाउनलाइन ऑपरेशन्स तुम्ही पुन्हा सक्रिय करेपर्यंत थांबतात. कारण पर्यायी पण ऑडिट ट्रेलमध्ये येते.',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'कारण (पर्यायी)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'सब-एजंट निलंबित करा',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'सब-एजंटचे निलंबन काढा',
  'agent.subAgentDetail.ops.status.cta.working': 'काम सुरू…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username}ला निलंबित करायचे? निलंबित असताना ते लॉगिन किंवा काम करू शकणार नाहीत.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username}चे निलंबन काढायचे? त्यांना पूर्ण प्रवेश परत मिळेल.',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} आता निलंबित.',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} आता सक्रिय.',

  'agent.subAgentDetail.ops.password.title': 'पासवर्ड रीसेट',
  'agent.subAgentDetail.ops.password.body': 'सब-एजंटसाठी नवीन पासवर्ड सेट करते आणि त्यांचे विद्यमान रीफ्रेश टोकन रद्द करते. त्यांना नवीन पासवर्डने पुन्हा लॉगिन करावे लागेल. सुरक्षित मार्गाने शेअर करा.',
  'agent.subAgentDetail.ops.password.placeholder': 'नवीन पासवर्ड (किमान 8 अक्षर)',
  'agent.subAgentDetail.ops.password.confirm': '@{username}चा पासवर्ड रीसेट करायचा? त्यांचे विद्यमान सेशन अवैध होतील आणि त्यांना नवीन पासवर्डने लॉगिन करावे लागेल.',
  'agent.subAgentDetail.ops.password.toast.reset': 'पासवर्ड रीसेट. नवीन पासवर्ड @{username}सोबत शेअर करा.',
  'agent.subAgentDetail.ops.password.error.tooShort': 'पासवर्ड किमान 8 अक्षरांचा असला पाहिजे.',
  'agent.subAgentDetail.ops.password.cta.reset': 'पासवर्ड रीसेट',
  'agent.subAgentDetail.ops.password.cta.resetting': 'रीसेट होत आहे…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'खेळाडूंकडे परत',
  'agent.playerDetail.loading': 'लोड होत आहे…',
  'agent.playerDetail.error.loadFailed': 'युजर लोड करण्यात अयशस्वी',

  // Header card.
  'agent.playerDetail.header.noName': '(नाव सेट नाही)',
  'agent.playerDetail.header.joined': '{date}ला सामील झाले',
  'agent.playerDetail.header.ownedBy.prefix': ' · मालक ',
  'agent.playerDetail.header.walletLabel': 'वॉलेट',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'प्रलंबित',
  'agent.playerDetail.status.active': 'सक्रिय',
  'agent.playerDetail.status.suspended': 'निलंबित',
  'agent.playerDetail.status.banned': 'बंदी',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'ओळख',
  'agent.playerDetail.tab.profile': 'प्रोफाइल',
  'agent.playerDetail.tab.wallet': 'वॉलेट',
  'agent.playerDetail.tab.security': 'सुरक्षा',
  'agent.playerDetail.tab.ownership': 'मालकी',
  'agent.playerDetail.tab.operations': 'ऑपरेशन्स',

  // Identity tab.
  'agent.playerDetail.identity.title': 'ओळख',
  'agent.playerDetail.identity.row.username': 'युजरनेम',
  'agent.playerDetail.identity.row.email': 'ईमेल',
  'agent.playerDetail.identity.row.phone': 'फोन',
  'agent.playerDetail.identity.row.country': 'देश',
  'agent.playerDetail.identity.row.joined': 'सामील झाले',
  'agent.playerDetail.identity.row.emailVerified': 'ईमेल सत्यापित',
  'agent.playerDetail.identity.row.telegram': 'टेलिग्राम',
  'agent.playerDetail.identity.row.failedLogins': 'अयशस्वी लॉगिन',
  'agent.playerDetail.identity.row.lockedUntil': 'पर्यंत लॉक',
  'agent.playerDetail.identity.value.yes': 'होय',
  'agent.playerDetail.identity.value.no': 'नाही',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'या खेळाडूला ट्रान्सफर करा',
  'agent.playerDetail.identity.transfer.body': 'दुसऱ्या एजंटच्या Unique Agent ID द्वारे हस्तांतरण — संपूर्ण नेटवर्कमध्ये काम करते, फक्त तुमच्या डाउनलाइनमध्ये नाही.',
  'agent.playerDetail.identity.transfer.cta': 'UAID द्वारे ट्रान्सफर',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'फोटो बदला',
  'agent.playerDetail.avatar.cropTitle': '@{username} साठी फोटो समायोजित करा',
  'agent.playerDetail.avatar.toast.updated': 'फोटो अपडेट झाला',
  'agent.playerDetail.avatar.error.uploadFailed': 'अपलोड अयशस्वी',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'खेळाडू UAID द्वारे ट्रान्सफर झाला',
  'agent.playerDetail.transfer.toast.agent': '@{username} यशस्वीरित्या ट्रान्सफर झाला',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'खाते स्थिती',
  'agent.playerDetail.ops.accountStatus.body': 'सक्रिय म्हणजे खेळाडू लॉगिन आणि खेळू शकतो. निलंबित तुम्ही परत आणेपर्यंत लॉगिन थांबवते. बंदी एक मजबूत लॉक — धोरण उल्लंघनासाठी ठेवा.',
  'agent.playerDetail.ops.statusBtn.activate': 'सक्रिय करा',
  'agent.playerDetail.ops.statusBtn.suspend': 'निलंबित करा',
  'agent.playerDetail.ops.statusBtn.ban': 'बंदी',
  'agent.playerDetail.ops.statusBtn.current': 'सध्याचे',
  'agent.playerDetail.ops.statusBtn.toast': 'युजर आता {status}',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'धोका क्षेत्र',
  'agent.playerDetail.ops.danger.body': 'हार्ड-डिलीट युजर रेकॉर्ड काढते. वॉलेट लेजर ऑडिटसाठी जपले जाते, पण खेळाडू सर्व प्रवेश गमावेल. हा कारवाई रद्द होऊ शकत नाही.',
  'agent.playerDetail.ops.delete.eyebrow': 'धोका क्षेत्र',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'हे खाते कायमचे हटवा. हे परत येऊ शकत नाही. पुष्टीसाठी टाइप करा ',
  'agent.playerDetail.ops.delete.confirm.after': '.',
  'agent.playerDetail.ops.delete.cta': 'हटवा',
  'agent.playerDetail.ops.delete.toast': 'युजर हटवला',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'वॉलेट समायोजित करा',
  'agent.playerDetail.walletAdjust.mode.credit': '+ क्रेडिट',
  'agent.playerDetail.walletAdjust.mode.debit': '− डेबिट',
  'agent.playerDetail.walletAdjust.field.amount': 'रक्कम (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'कारण · लेजरमध्ये जपले',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'उदा. डिपॉझिट रिव्हर्सल, बोनस, परतावा',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'रिचार्ज म्हणून चिन्हांकित',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'हे क्रेडिट लेजरमध्ये ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'रिचार्ज',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' म्हणून रेकॉर्ड करा. जर हा युजर कोणी रेफर केला असेल, तर रेफर करणाऱ्याला आपोआप बोनस मिळतो.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'धनात्मक रक्कम एंटर करा',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'कारण आवश्यक आहे',
  'agent.playerDetail.walletAdjust.cta.applying': 'लागू होत आहे…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'क्रेडिट लागू',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'डेबिट लागू',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} क्रेडिट',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} डेबिट',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · रेफरकर्त्याने {bonus} बोनस कमावला',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'या खेळाडूला रिचार्ज करा',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': 'ला टॉप-अप करताना नाणी ',
  'agent.playerDetail.rechargeCta.body.bold': 'तुमच्या वॉलेट',
  'agent.playerDetail.rechargeCta.body.after': 'मधून त्यांच्याकडे जातात. तुमचा पूल आधी तपासला जातो — ही कारवाई अणू आहे आणि अपुरी शिल्लक असल्यास नाकारली जाते.',
  'agent.playerDetail.rechargeCta.thisPlayer': 'या खेळाडूला',
  'agent.playerDetail.rechargeCta.cta': 'रिचार्ज फॉर्म उघडा',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'पासवर्ड रीसेट',
  'agent.playerDetail.security.subtitle': 'नवीन पासवर्ड सेट करते आणि युजरला प्रत्येक डिव्हाइसवरून साइन आउट करते.',
  'agent.playerDetail.security.field.new': 'नवीन पासवर्ड',
  'agent.playerDetail.security.field.confirm': 'पुष्टी करा',
  'agent.playerDetail.security.error.mismatch': 'पासवर्ड जुळत नाहीत',
  'agent.playerDetail.security.error.tooShort': 'किमान 8 अक्षर, अक्षरे आणि अंकांसह',
  'agent.playerDetail.security.toast.reset': 'पासवर्ड रीसेट आणि सर्व सेशन रद्द',
  'agent.playerDetail.security.cta.reset': 'पासवर्ड रीसेट',
  'agent.playerDetail.security.cta.resetting': 'रीसेट होत आहे…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'वॉलेट लेजर',
  'agent.playerDetail.ledger.entries.one': '{count} एंट्री',
  'agent.playerDetail.ledger.entries.other': '{count} एंट्रीज',
  'agent.playerDetail.ledger.empty': 'अद्याप व्यवहार नाहीत.',
  'agent.playerDetail.ledger.loading': 'लोड होत आहे…',
  'agent.playerDetail.ledger.actorSuffix': ' · एडमिन #{id} द्वारे',
  'agent.playerDetail.ledger.pagination.prev': 'मागील',
  'agent.playerDetail.ledger.pagination.next': 'पुढील',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'मॅच बेट',
  'agent.playerDetail.ledger.kind.win': 'मॅच पुरस्कार',
  'agent.playerDetail.ledger.kind.refund': 'परतावा',
  'agent.playerDetail.ledger.kind.fee': 'प्लॅटफॉर्म फी',
  'agent.playerDetail.ledger.kind.adminCredit': 'एडमिन क्रेडिट',
  'agent.playerDetail.ledger.kind.adminDebit': 'एडमिन डेबिट',
  'agent.playerDetail.ledger.kind.recharge': 'रिचार्ज',
  'agent.playerDetail.ledger.kind.referralBonus': 'रेफरल बोनस',
  'agent.playerDetail.ledger.kind.signupBonus': 'साइनअप बोनस',
  'agent.playerDetail.ledger.kind.offerCredit': 'ऑफर क्रेडिट',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'पैसे काढणे',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'परतावा',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'प्लॅटफॉर्म',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'प्रोफाइल संपादित करा',
  'agent.playerDetail.profile.field.username': 'युजरनेम',
  'agent.playerDetail.profile.field.email': 'ईमेल',
  'agent.playerDetail.profile.field.phone': 'फोन',
  'agent.playerDetail.profile.field.firstName': 'पहिले नाव',
  'agent.playerDetail.profile.field.lastName': 'आडनाव',
  'agent.playerDetail.profile.field.dob': 'जन्म तारीख',
  'agent.playerDetail.profile.field.gender': 'लिंग',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'पुरुष',
  'agent.playerDetail.profile.gender.female': 'स्त्री',
  'agent.playerDetail.profile.gender.other': 'इतर',
  'agent.playerDetail.profile.gender.preferNot': 'सांगायचे नाही',
  // Location section.
  'agent.playerDetail.profile.location.title': 'स्थान',
  'agent.playerDetail.profile.location.lastUpdated': '· शेवटचा अपडेट {date}',
  'agent.playerDetail.profile.location.legacy.label': 'फाइलवर जुना पत्ता',
  'agent.playerDetail.profile.location.legacy.note': ' (फक्त वाचनीय — pre-R3, लोकेटरद्वारे वापरला जात नाही):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'सेव्ह करण्यासाठी काहीही नाही',
  'agent.playerDetail.profile.toast.saved.one': '{count} फील्ड सेव्ह',
  'agent.playerDetail.profile.toast.saved.other': '{count} फील्ड सेव्ह',
  'agent.playerDetail.profile.cta.save': 'बदल सेव्ह करा',
  'agent.playerDetail.profile.cta.saving': 'सेव्ह होत आहे…',
  'agent.playerDetail.profile.cta.reset': 'रीसेट',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'मालक एजंट',
  'agent.playerDetail.ownership.row.eyebrow': 'मालकी',
  'agent.playerDetail.ownership.platform.title': 'प्लॅटफॉर्म-डायरेक्ट',
  'agent.playerDetail.ownership.platform.body': 'कोणताही एजंट या खेळाडूचा मालक नाही. एडमिन थेट व्यवस्थापित करतात.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'एजंट #{id}',
  'agent.playerDetail.ownership.owned.body': 'या खेळाडूच्या रिचार्ज + मॅचवर कमिशन कमावतो.',
  'agent.playerDetail.ownership.cta.change': 'बदला',
  'agent.playerDetail.ownership.toast.changedTo': 'मालकी → {label}',
  'agent.playerDetail.ownership.platformLabel': 'प्लॅटफॉर्म-डायरेक्ट',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username}ला पुन्हा असाइन',
  'agent.playerDetail.ownership.picker.body': 'नवीन एजंट निवडा — किंवा प्लॅटफॉर्म-डायरेक्टवर वेगळे करा (कोणीही एजंट मालक नाही). भविष्यातील कमिशन नवीन मालकाकडे जातात; आधीच क्रेडिट केलेल्या पंक्ती स्पर्श होत नाहीत.',
  'agent.playerDetail.ownership.picker.detach.title': 'प्लॅटफॉर्मवर वेगळे करा',
  'agent.playerDetail.ownership.picker.detach.body': 'एजंट मालकी काढा. एडमिन थेट व्यवस्थापित करतात.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'एजंट शोधा…',
  'agent.playerDetail.ownership.picker.loading': 'एजंट लोड होत आहेत…',
  'agent.playerDetail.ownership.picker.empty.search': 'तुमच्या शोधाशी कोणताही एजंट जुळत नाही.',
  'agent.playerDetail.ownership.picker.empty.none': 'अद्याप सक्रिय एजंट नाहीत.',
  'agent.playerDetail.ownership.picker.players.one': '{count} खेळाडू',
  'agent.playerDetail.ownership.picker.players.other': '{count} खेळाडू',
  'agent.playerDetail.ownership.picker.current': 'सध्याचे',
  'agent.playerDetail.ownership.picker.currentUpper': 'सध्याचे',
  'agent.playerDetail.ownership.picker.close': 'बंद करा',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'बाली एजंट',
  'agent.login.subtitle': 'तुमच्या खेळाडूंचे व्यवस्थापन करण्यासाठी साइन इन करा',
  'agent.login.field.identifier': 'युजरनेम किंवा ईमेल',
  'agent.login.field.password': 'पासवर्ड',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'फक्त एजंटसाठी. जर तुम्ही प्लॅटफॉर्म मालक असाल, तर वापरा ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '.',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'लॉगिन अयशस्वी',
  'agent.login.error.invalidCredentials': 'चुकीचे क्रेडेन्शियल',
  'agent.login.error.accountSuspended': 'खाते निलंबित — तुमच्या एडमिनिस्ट्रेटरशी संपर्क साधा.',
  'agent.login.error.networkError': 'नेटवर्क त्रुटी — तुमचे कनेक्शन तपासा आणि पुन्हा प्रयत्न करा.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'गेम',
  'agent.misc.gameTile.actionSuffix': '· कॉन्फिगर',
};

const ta: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "என் விவரங்கள்",
  'profile.group.security': "உள்நுழைவு & பாதுகாப்பு",
  'profile.personal.title': "தனிப்பட்ட விவரங்கள்",
  'profile.personal.editTitle': "தனிப்பட்ட விவரங்களைத் திருத்து",
  'common.edit': "திருத்து",
  'profile.field.name': "பெயர்",
  'profile.field.phone': "மொபைல் எண்",
  'profile.field.gender': "பாலினம்",
  'profile.field.location': "இடம்",
  'profile.gender.male': "ஆண்",
  'profile.gender.female': "பெண்",
  'profile.gender.other': "மற்றவை",
  'profile.gender.preferNot': "சொல்ல விரும்பவில்லை",
  'profile.gender.select': "தேர்ந்தெடு…",
  'profile.location.optionalHint': "விருப்பம் — உங்கள் பகுதியைத் தேர்ந்தெடுக்கவும் அல்லது பின்கோடை உள்ளிடவும் (GPS இன்னும் துல்லியமாக்கும்).",
  'profile.lang.title': "மொழி",
  'profile.password.title': "கடவுச்சொல்",
  'profile.password.desc': "உங்கள் கணக்கு கடவுச்சொல்லை மாற்றவும். மற்ற சாதனங்கள் வெளியேற்றப்படும்.",
  'profile.password.cta': "மாற்று",
  'profile.password.editTitle': "கடவுச்சொல்லை மாற்று",
  'profile.password.current': "தற்போதைய கடவுச்சொல்",
  'profile.password.new': "புதிய கடவுச்சொல்",
  'profile.password.confirm': "புதிய கடவுச்சொல்லை உறுதிப்படுத்து",
  'profile.password.currentPh': "உங்கள் தற்போதைய கடவுச்சொல்",
  'profile.password.newPh': "வலிமையான கடவுச்சொல்லைத் தேர்ந்தெடுக்கவும்",
  'profile.password.confirmPh': "புதிய கடவுச்சொல்லை மீண்டும் உள்ளிடவும்",
  'profile.password.rule': "குறைந்தது 8 எழுத்துகள், ஒரு எழுத்தும் ஒரு எண்ணும் இருக்க வேண்டும்.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "இணைக்கப்பட்டது:",
  'profile.telegram.otpHere': "கடவுச்சொல் மீட்டமைப்பு OTP-கள் இங்கே வரும்.",
  'profile.telegram.unlinkChange': "இணைப்பை நீக்கி மாற்று",
  'profile.telegram.unlinkTitle': "Telegram இணைப்பை நீக்கவா?",
  'profile.telegram.unlink': "இணைப்பை நீக்கு",
  'profile.telegram.keepLinked': "இணைந்தே இருக்கட்டும்",
  'profile.telegram.unlinkVerified': "பின்னர் வேறு Telegram கணக்கை மீண்டும் இணைக்கலாம். மின்னஞ்சல் சரிபார்ப்பு அப்படியே இருக்கும்.",
  'profile.telegram.unlinkOnly': "Telegram தான் உங்கள் ஒரே சரிபார்க்கப்பட்ட வழி — தொடர்ந்து விளையாட உங்கள் கணக்கை மீண்டும் சரிபார்க்க வேண்டியிருக்கலாம்.",
  'profile.telegram.notLinkedDesc': "காப்புப் பிரதியாக கடவுச்சொல் மீட்டமைப்பு OTP-களைப் பெற Telegram-ஐ இணைக்கவும்.",
  'profile.telegram.openBot': "@BaaliGames_bot-ஐ திற",
  'profile.telegram.generate': "இணைப்பை உருவாக்கு",
  'profile.telegram.generating': "உருவாக்குகிறது…",
  'profile.avatar.cropTitle': "உங்கள் புகைப்படத்தை சரிசெய்யவும்",
  'profile.toast.detailsSaved': "விவரங்கள் சேமிக்கப்பட்டன",
  'profile.toast.photoUpdated': "புகைப்படம் புதுப்பிக்கப்பட்டது",
  'profile.toast.pwChanged': "கடவுச்சொல் மாற்றப்பட்டது. மற்ற சாதனங்கள் வெளியேற்றப்பட்டன.",
  'profile.toast.tgUnlinked': "Telegram இணைப்பு நீக்கப்பட்டது. வேறு அரட்டையை இணைக்க புதிய இணைப்பை உருவாக்கவும்.",
  'profile.err.phone': "10 இலக்க மொபைல் எண்ணை உள்ளிடவும் (அல்லது அப்படியே விடவும்)",
  'profile.err.pwMismatch': "புதிய கடவுச்சொற்கள் பொருந்தவில்லை",
  'profile.err.pwWeak': "கடவுச்சொல் 8+ எழுத்துகள், ஒரு எழுத்தும் ஒரு எண்ணும் இருக்க வேண்டும்",
  'profile.err.pwSame': "புதிய கடவுச்சொல் தற்போதையதிலிருந்து வேறுபட்டதாக இருக்க வேண்டும்",
  'wallet.payout.title': "சேமித்த பணம் பெறும் விவரங்கள்",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "இதுவரை எதுவும் சேமிக்கப்படவில்லை",
  'wallet.payout.hint': "நீங்கள் பணத்தை எடுக்கும்போது உங்கள் UPI ID தானாகவே சேமிக்கப்படும். அடுத்த முறை பணம் எடுக்கும் கோரிக்கையின்போது அதை புதுப்பிக்கலாம்.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ஐ நிறுவவும்",
  "pwa.install.subhint": "உடனடி, முழுத்திரை அணுகலுக்கு இதை உங்கள் முகப்புத் திரையில் சேர்க்கவும் — ஆப் ஸ்டோர் தேவையில்லை.",
  "pwa.install.listen": "கேளுங்கள்",
  "pwa.install.listening": "நிறுத்து",
  "pwa.install.installBtn": "நிறுவு",
  "pwa.install.notNow": "இப்போது வேண்டாம்",
  "pwa.install.howToShow": "எப்படி நிறுவுவது",
  "pwa.install.howToHide": "படிகளை மறை",
  "pwa.install.guide.iosSafari": "Safari திரையின் கீழே, Share பொத்தானைத் தட்டவும் — இது மேல்நோக்கி அம்புக்குறியுடன் கூடிய சதுரம் போல் தோன்றும்.\nதிறக்கும் மெனுவில் சிறிது கீழே ஸ்க்ரோல் செய்யவும்.\n\"Add to Home Screen\" விருப்பத்தைத் தட்டவும்.\nமேல் வலது மூலையில் Add ஐத் தட்டவும். இப்போது ஆப் வழக்கமான ஆப் போல் உங்கள் முகப்புத் திரையில் இருக்கும்.",
  "pwa.install.guide.iosOther": "iPhone அல்லது iPad இல், முகப்புத் திரையில் சேர்ப்பது Safari உலாவியில் மட்டுமே வேலை செய்யும்.\nSafari ஐத் திறந்து இதே வலைத்தளத்திற்குச் செல்லவும்.\nSafari இன் கீழே Share பொத்தானைத் தட்டவும் — மேல்நோக்கி அம்புக்குறியுடன் கூடிய சதுரம்.\nகீழே ஸ்க்ரோல் செய்து \"Add to Home Screen\" ஐத் தட்டி, பிறகு Add ஐத் தட்டவும்.",
  "pwa.install.guide.android": "மேலே உள்ள Install பொத்தானைத் தட்டவும்.\nஉங்கள் உலாவி கேட்கும்போது, உறுதிப்படுத்த Install ஐத் தட்டவும்.\nமுடிந்தது — இப்போது ஆப் உங்கள் முகப்புத் திரையில் உள்ளது. வழக்கமான ஆப் போல் எப்போது வேண்டுமானாலும் திறக்கவும்.\nபாப்-அப் தெரியவில்லையா? உங்கள் உலாவியின் மெனுவைத் திறக்கவும் — மூன்று புள்ளிகள், பொதுவாக மேல் வலதுபுறம் — பின்னர் \"Install app\" ஐத் தட்டவும்.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "நேரடி ரீசார்ஜ்",
  "agent.directRecharge.subtitle.player": "ஒரு வீரரைத் தேர்ந்தெடுத்து உங்கள் பூலில் இருந்து ரீசார்ஜ் செய்யுங்கள். கோரிக்கை தேவையில்லை.",
  "agent.directRecharge.subtitle.subagent": "ஒரு துணை-முகவரைத் தேர்ந்தெடுத்து உங்கள் பூலில் இருந்து அவர்களின் பூலுக்கு ரீசார்ஜ் செய்யுங்கள்.",
  "agent.directRecharge.search.player": "ரீசார்ஜ் செய்ய வீரரைத் தேடுங்கள் (பெயர் · தொலைபேசி · மின்னஞ்சல்)…",
  "agent.directRecharge.search.subagent": "ரீசார்ஜ் செய்ய துணை-முகவரைத் தேடுங்கள் (பெயர் · மின்னஞ்சல் · UAID)…",
  "agent.directRecharge.clearSearch": "தேடலை அழி",
  "agent.directRecharge.searching": "தேடுகிறது…",
  "agent.directRecharge.noMatches": "பொருத்தம் இல்லை.",
  "agent.directRecharge.empty.player": "உங்கள் பட்டியலில் இன்னும் வீரர்கள் இல்லை.",
  "agent.directRecharge.empty.subagent": "இன்னும் நேரடி துணை-முகவர் இல்லை.",
  "agent.directRecharge.searchFailed": "தேடல் தோல்வி",
  "agent.directRecharge.label.wallet": "வாலட்",
  "agent.directRecharge.label.pool": "பூல்",
  "agent.directRecharge.modal.title.player": "நேரடி வீரர் ரீசார்ஜ்",
  "agent.directRecharge.modal.title.subagent": "நேரடி துணை-முகவர் ரீசார்ஜ்",
  "agent.directRecharge.modal.close": "மூடு",
  "agent.directRecharge.modal.targetPool": "இலக்கு பூல்",
  "agent.directRecharge.modal.operatingWallet": "இயக்க வாலட்",
  "agent.directRecharge.modal.dayToDay": "அன்றாட இருப்பு",
  "agent.directRecharge.modal.securityDeposit": "பாதுகாப்பு வைப்பு",
  "agent.directRecharge.modal.collateral": "பிணைய பூல்",
  "agent.directRecharge.modal.amount": "தொகை (₹)",
  "agent.directRecharge.modal.overdraw": "உங்கள் பூலை (₹{pool}) விட அதிகமாக செலுத்த முடியாது. தொகையைக் குறைக்கவும்.",
  "agent.directRecharge.modal.reason": "காரணம் / குறிப்பு",
  "agent.directRecharge.modal.reasonPlaceholder": "பணம் பெறப்பட்டது · ref 4291",
  "agent.directRecharge.modal.reasonHint": "உங்கள் வாலட் லெட்ஜர் + தணிக்கை பதிவில் எழுதப்படும்.",
  "agent.directRecharge.modal.proofLabel": "பணம் செலுத்தியதன் ஸ்கிரீன்ஷாட்டைப் பதிவேற்றவும் (விருப்பத்தேர்வு)",
  "agent.directRecharge.modal.proofHint": "வரலாறு வழியாக பெறுநருக்குத் தெரியும்.",
  "agent.directRecharge.modal.previewEmpty": "பரிவர்த்தனையை முன்னோட்டம் காண தொகையை உள்ளிடவும்.",
  "agent.directRecharge.modal.previewBody": "உங்கள் வாலட்டில் இருந்து {name} இன் {pool} க்கு அனுப்பப்பட்டது. பாதுகாப்பான பரிமாற்றம்; லெட்ஜர் + தணிக்கையில் பதிவு.",
  "agent.directRecharge.modal.pool.playerWallet": "வாலட்",
  "agent.directRecharge.modal.pool.operatingWallet": "இயக்க வாலட்",
  "agent.directRecharge.modal.pool.securityDeposit": "பாதுகாப்பு வைப்பு",
  "agent.directRecharge.modal.cancel": "ரத்து",
  "agent.directRecharge.modal.send": "₹{amount} அனுப்பு",
  "agent.directRecharge.badge.player": "வீரர்",
  "agent.directRecharge.badge.subagent": "துணை-முகவர்",
  "agent.directRecharge.modal.yourDirectSubagent": "உங்கள் நேரடி துணை-முகவர்",
  "agent.directRecharge.modal.fundingSource": "நிதி ஆதாரம்",
  "agent.directRecharge.modal.you": "(நீங்கள்)",
  "agent.directRecharge.modal.yourPool": "உங்கள் பூல்",
  "agent.directRecharge.modal.notSignedIn": "முகவராக உள்நுழையவில்லை",
  "agent.directRecharge.modal.failed": "தோல்வி",
  'common.signIn': 'உள்நுழைக', 'common.signUp': 'பதிவுசெய்க', 'common.login': 'உள்நுழை',
  'common.logout': 'வெளியேறு', 'common.continue': 'தொடரவும்', 'common.cancel': 'ரத்து',
  'common.save': 'சேமி', 'common.update': 'புதுப்பி', 'common.clear': 'அழி',
  'common.back': 'பின்', 'common.loading': 'ஏற்றப்படுகிறது…', 'common.sending': 'அனுப்புகிறது…',
  'common.saving': 'சேமிக்கப்படுகிறது…', 'common.optional': 'விருப்பத்தேர்வு',
  'common.show': 'காட்டு', 'common.hide': 'மறை', 'common.profile': 'சுயவிவரம்', 'common.settings': 'அமைப்புகள்',
  'nav.brand': 'பாலி கேம்ஸ்', 'nav.home': 'முகப்பு', 'nav.games': 'கேம்கள்',
  'nav.wallet': 'பணப்பை', 'nav.rewards': 'பரிசுகள்', 'nav.profile': 'சுயவிவரம்',
  'login.title': 'மீண்டும் வரவேற்கிறோம்', 'login.subtitle': 'விளையாட்டைத் தொடர உள்நுழைக.',
  'login.idLabel': 'பயனர்பெயர், மின்னஞ்சல் அல்லது தொலைபேசி',
  'login.idPlaceholder': 'பயனர்பெயர், மின்னஞ்சல் அல்லது மொபைல்',
  'login.passwordLabel': 'கடவுச்சொல்', 'login.passwordPlaceholder': 'உங்கள் கடவுச்சொல்',
  'login.forgot': 'மறந்துவிட்டதா?', 'login.cta': 'உள்நுழை',
  'login.signingIn': 'உள்நுழைகிறது…',
  'login.newHere': 'புதியவரா?', 'login.createAccount': 'கணக்கு உருவாக்கு',
  'register.title': 'உங்கள் கணக்கை உருவாக்குங்கள்', 'register.subtitle': 'ஒரு பெயர் தேர்வு செய்யவும்.',
  'register.usernameLabel': 'பயனர்பெயர்', 'register.usernamePlaceholder': 'தனித்துவமான பெயரைத் தேர்வு செய்க',
  'register.usernameHelp': 'எழுத்துகள், இலக்கங்கள் மற்றும் அடிக்கோடு. ௩–௨௪ எழுத்துகள்.',
  'register.emailLabel': 'மின்னஞ்சல்', 'register.emailPlaceholder': 'உங்கள் மின்னஞ்சல் முகவரி',
  'register.mobileLabel': 'மொபைல் எண்', 'register.mobilePlaceholder': '௧௦-இலக்க மொபைல்',
  'register.mobileHelp': 'நாட்டு குறியீட்டை நாங்கள் தானாகவே சேர்ப்போம்.',
  'register.passwordLabel': 'கடவுச்சொல்', 'register.passwordPlaceholder': 'வலுவான கடவுச்சொல்லைத் தேர்வு செய்க',
  'register.passwordHelp': 'குறைந்தது ௮ எழுத்துகள் — ஒரு எழுத்து மற்றும் ஒரு இலக்கத்துடன்.',
  'register.referralLabel': 'பரிந்துரை குறியீடு', 'register.referralPlaceholder': 'நண்பர் அழைப்பு குறியீடு',
  'register.cta': 'கணக்கு உருவாக்கு', 'register.creating': 'உருவாக்குகிறது…',
  'register.alreadyPlayer': 'ஏற்கனவே வீரரா?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'உங்களைப் பற்றி', 'register.step.signin': 'உள்நுழைவு', 'register.step.finish': 'முடி',
  'register.step.aboutSubtitle': 'உங்களைப் பற்றி சிறிது சொல்லுங்கள்', 'register.step.signinSubtitle': 'எப்படி உள்நுழைய வேண்டும் என்று அமைக்கவும்', 'register.step.finishSubtitle': 'இடத்தைத் தேர்ந்தெடுத்து முடிக்கவும்',
  'register.firstName': 'முதல் பெயர்', 'register.firstNamePh': 'ஆரவ்',
  'register.lastName': 'கடைசி பெயர்', 'register.lastNamePh': 'ஷர்மா',
  'register.dob': 'பிறந்த தேதி', 'register.dobHelp': 'விளையாட உங்கள் வயது 18 அல்லது அதற்கு மேல் இருக்க வேண்டும்',
  'register.v.required': 'தேவை', 'register.v.nameMin': 'குறைந்தது 2 எழுத்துகள்',
  'register.v.tooLong': 'மிக நீளம்', 'register.v.nameChars': 'எழுத்துகள், இடைவெளி, ஹைஃபன் மட்டும்',
  'register.v.invalidDate': 'தவறான தேதி', 'register.v.minAge': 'உங்கள் வயது குறைந்தது 18 இருக்க வேண்டும்',
  'register.v.tooOld': 'வருடத்தை சரிபார்க்கவும்',
  'register.v.usernameMin': 'குறைந்தது 3 எழுத்துகள்', 'register.v.usernameMax': 'மிக நீளம் (அதிகபட்சம் 24)',
  'register.v.usernameChars': 'எழுத்துகள், எண்கள், அண்டர்ஸ்கோர் மட்டும்', 'register.v.emailShape': 'தவறான மின்னஞ்சல் போல உள்ளது',
  'register.v.phoneDigits': '10 இலக்க எண் இருக்க வேண்டும்', 'register.v.phonePrefix': 'இந்திய மொபைல் 6–9 இல் தொடங்கும்',
  'register.v.pwdMin': 'குறைந்தது 8 எழுத்துகள்', 'register.v.pwdLetter': 'குறைந்தது ஒரு எழுத்து இருக்க வேண்டும்',
  'register.v.pwdNumber': 'குறைந்தது ஒரு எண் இருக்க வேண்டும்',
  'register.v.referralShort': 'மிக சிறியதாக உள்ளது', 'register.v.referralChars': 'எழுத்துகள் மற்றும் எண்கள் மட்டும்',
  'register.v.usernameTaken': 'இந்த பயனர்பெயர் ஏற்கனவே எடுக்கப்பட்டுள்ளது',
  'register.v.emailTaken': 'இந்த மின்னஞ்சல் ஏற்கனவே பதிவு செய்யப்பட்டுள்ளது',
  'register.v.phoneTaken': 'இந்த எண் ஏற்கனவே பதிவு செய்யப்பட்டுள்ளது',
  'register.v.noLocation': 'தொடர குறைந்தது ஒரு இருப்பிட முறையை பகிரவும்.',
  'register.welcome.title': 'வரவேற்கிறோம்', 'register.welcome.subtitle': 'உங்கள் கணக்கு தயார். இப்போது பாதுகாக்கவா அல்லது நேரடியாக நுழையவா?',
  'register.welcome.verifyTelegram': 'டெலிகிராம் வழியாக சரிபார்க்கவும்',
  'register.welcome.verifyExplain': '⚠ முக்கியம்: இப்போதே டெலிகிராம் வழியாக சரிபார்க்கவும். சரிபார்ப்பு இல்லாமல், கடவுச்சொல்லை மறந்தால் நீங்களே மீட்டமைக்க முடியாது — உங்கள் ஏஜெண்ட் அல்லது நிர்வாகி மட்டுமே மீட்டமைக்க முடியும். பின்னர் சுயவிவரத்திலிருந்தும் சரிபார்க்கலாம்.',
  'register.welcome.verifyHeads': 'இதை பிற்காலத்திற்கு சேமிக்கவும்',
  'register.welcome.verifyConsequence': 'மின்னஞ்சல் சரிபார்ப்பு தற்போது இல்லை. பின்னர் கடவுச்சொல்லை மீட்டமைக்க டெலிகிராம் வழியாக சரிபார்க்கவும். தவிர்த்தால், கடவுச்சொல்லை மறந்தால் உங்கள் ஏஜெண்ட் அல்லது நிர்வாகி மட்டுமே மீட்டமைக்க முடியும்.',
  'register.welcome.continue': 'பயன்பாட்டிற்கு செல்லவும்', 'register.welcome.continueLogin': 'தொடர சைன் இன் செய்யவும்', 'register.welcome.useDifferent': 'வேறு கணக்கைப் பயன்படுத்தவும்',
  'register.geo.title': 'நீங்கள் எங்கிருந்து விளையாடுகிறீர்கள்?', 'register.geo.subtitle': 'அருகிலுள்ள முகவருடன் இணைக்க உங்கள் இருப்பிடத்தைப் பயன்படுத்துகிறோம்.',
  'register.geo.showAlt': 'இருப்பிடம் பகிர முடியவில்லையா? வேறு முறையைப் பயன்படுத்தவும்',
  'register.geo.hideAlt': 'மற்ற முறைகளை மறை', 'register.geo.captured': 'இருப்பிடம் பிடிக்கப்பட்டது',
  'register.geo.capturedHint': 'நீங்கள் இதை எப்போது வேண்டுமானாலும் சுயவிவரத்திலிருந்து மாற்றலாம்.',
  'register.geo.verifyNote': 'பதிவு செய்த பிறகு, டெலிகிராம் (கடவுச்சொல் மீட்டமைப்புக்கு) அல்லது மின்னஞ்சல் வழியாக சுயவிவரத்திலிருந்து எப்போது வேண்டுமானாலும் சரிபார்க்கலாம். சரிபார்ப்பு விருப்பமானது.',
  'verify.title': 'உங்கள் கணக்கை சரிபார்க்கவும்', 'verify.subtitle': 'ஒரு விருப்பத்தைத் தேர்வு செய்க — அதுவே போதும்.',
  'verify.tabTelegram': 'டெலிகிராம்', 'verify.tabEmail': 'மின்னஞ்சல்',
  'verify.emailSent': 'நாங்கள் {email} க்கு சரிபார்ப்பு இணைப்பை அனுப்பியுள்ளோம்.',
  'verify.emailHint': 'அதே சாதனத்தில் திறக்கவும். தெரியவில்லையா? ஸ்பாம்/புரோமோஷன் சரிபார்க்கவும்.',
  'verify.telegramOpen': '@BaaliGames_bot திற',
  'verify.telegramExpiry': 'இணைப்பு ௩௦ நிமிடங்களில் காலாவதி. டெலிகிராமில் Start ஒருமுறை அழுத்த வேண்டும்.',
  'verify.goToLogin': 'உள்நுழைவுக்குச் செல்',
  'verify.email.title': 'மின்னஞ்சல் சரிபார்க்கப்பட்டது', 'verify.email.sub': 'உங்கள் கணக்கு செயலில். விளையாட நேரம்.',
  'verify.email.failed': 'சரிபார்ப்பு தோல்வியடைந்தது', 'verify.email.verifying': 'சரிபார்க்கிறது…',
  'verify.backRegister': 'பதிவுக்குத் திரும்பு',
  'forgot.title': 'உங்கள் கணக்கை மீட்டெடு', 'forgot.subtitle': 'திரும்பி வரும் வழி அனுப்புவோம்.',
  'forgot.idLabel': 'பயனர்பெயர், மின்னஞ்சல் அல்லது தொலைபேசி', 'forgot.idPlaceholder': 'பயனர்பெயர், மின்னஞ்சல் அல்லது மொபைல்',
  'forgot.sendVia': 'அனுப்பும் வழி', 'forgot.email': 'மின்னஞ்சல்', 'forgot.telegram': 'டெலிகிராம் OTP',
  'forgot.telegramHelp': 'உங்கள் கணக்கு எங்கள் போட்டுடன் இணைக்கப்பட்டால் மட்டுமே டெலிகிராம் வேலை செய்யும்.',
  'forgot.cta': 'மீட்டமைப்பை அனுப்பு',
  'forgot.sentEmail': 'கணக்கு இருந்தால், மீட்டமைப்பு இணைப்பு மின்னஞ்சல் செய்யப்பட்டது.',
  'forgot.sentTelegram': 'கணக்கு இருந்தால், டெலிகிராம் வழியாக ௬-இலக்க குறியீடு அனுப்பப்பட்டது.',
  'forgot.redirecting': 'மீட்டமைப்பு பக்கத்திற்கு திருப்பி விடப்படுகிறது…', 'forgot.back': 'உள்நுழைவுக்குத் திரும்பு',
  'reset.title': 'புதிய கடவுச்சொல்லை அமைக்கவும்',
  'reset.subOtp': 'டெலிகிராமில் அனுப்பிய குறியீட்டை பயன்படுத்தவும்.',
  'reset.subLink': 'புதிய கடவுச்சொல்லைத் தேர்வு செய்து மீண்டும் விளையாடவும்.',
  'reset.otpLabel': '௬-இலக்க குறியீடு', 'reset.newPasswordLabel': 'புதிய கடவுச்சொல்',
  'reset.cta': 'கடவுச்சொல்லை புதுப்பி', 'reset.updating': 'புதுப்பிக்கப்படுகிறது…',
  'reset.successTitle': 'கடவுச்சொல் புதுப்பிக்கப்பட்டது', 'reset.successSub': 'தயார். விளையாட நேரம்.',
  'home.welcomeTo': 'வரவேற்கிறோம்', 'home.heroSub': 'திறன் சார்ந்த கேமிங் மையம். விளையாட உள்நுழையவும் அல்லது கணக்கை உருவாக்கவும்.',
  'home.skillBased': 'திறன் சார்ந்தது', 'home.skillBasedSub': 'உத்தி வெல்லும்',
  'home.fairPlay': 'நியாய விளையாட்டு', 'home.fairPlaySub': 'எதிர்ப்பு-மோசடி இயக்கம்',
  'home.instantPayout': 'UPI பணம்', 'home.instantPayoutSub': '24 மணி நேரத்திற்குள்',
  'home.featured': 'சிறப்பு கேம்கள்', 'home.tapToPlay': 'விளையாட தட்டவும்',
  'home.ready': 'விளையாட தயாரா?', 'home.dropIn': 'சேருங்கள் மற்றும் உண்மையான பரிசுகளை வெல்லுங்கள்.',
  'home.start': 'தொடங்கு',
  'home.welcomeBack': 'மீண்டும் வரவேற்கிறோம்', 'home.balance': 'உங்கள் இருப்பு', 'home.live': 'லைவ்',
  'home.add': 'சேர்', 'home.manage': 'நிர்வகி', 'home.rewards': 'பரிசுகள்',
  'home.todayChallenge': 'இன்றைய சவால்', 'home.todayChallengeSub': 'ஒரு போட்டியை வெல்லுங்கள் — பிளவ்வு போனஸ் பெறுங்கள்.',
  'home.view': 'பார்', 'home.yourGames': 'உங்கள் கேம்கள்', 'home.viewAll': 'அனைத்தையும் பார்',
  'profile.details': 'விவரங்கள்', 'profile.address': 'முகவரி', 'profile.languages': 'மொழிகள்',
  'profile.account': 'கணக்கு', 'profile.photo': 'புகைப்படம்', 'profile.preferred': 'விருப்ப மொழி',
  'profile.lang.primary': 'முதன்மை மொழி', 'profile.lang.primarySub': 'பயன்பாடு முழுவதும் இயல்புநிலை',
  'profile.lang.locked': 'பூட்டப்பட்டது', 'profile.lang.second': 'இரண்டாம் மொழி',
  'profile.lang.pickHelp': 'நீங்கள் பேசும் மற்றொரு மொழியைத் தேர்வுசெய்க. கார்டில் தட்டவும்.',
  'profile.lang.saved': 'இரண்டாம் மொழி சேமிக்கப்பட்டது', 'profile.lang.cleared': 'இரண்டாம் மொழி அழிக்கப்பட்டது',
  'lang.switcher.aria': 'மொழியைத் தேர்வுசெய்', 'lang.switcher.title': 'உங்கள் மொழியைத் தேர்வுசெய்க',
  'lang.switcher.sub': 'பயன்பாடு முழுவதும் உடனடியாக புதுப்பிக்கப்படும்.', 'lang.switcher.current': 'தற்போதைய',
  'rewards.title': 'பரிசுகள் & சலுகைகள்', 'rewards.subtitle': 'இப்போதே சம்பாதிக்க அல்லது பெற கூடுதல் பரிசுகள்.',
  'rewards.tab.available': 'கிடைக்கும்', 'rewards.tab.inReview': 'மதிப்பாய்வில்', 'rewards.tab.claimed': 'பெற்றவை',
  'rewards.claim.cta': 'பெறுக', 'rewards.claimed.pending': 'மதிப்பாய்வு நிலுவையில்',
  'rewards.empty.available.title': 'தற்போது சலுகைகள் இல்லை',
  'rewards.toast.credited': 'போனஸ் சேர்க்கப்பட்டது — {amount} உங்கள் வாலெட்டில்',
  'wallet.balance': 'இருப்பு', 'wallet.addMoney': 'பணம் சேர்', 'wallet.withdraw': 'எடு',
  'wallet.pending.title': 'நிலுவை கோரிக்கைகள்', 'wallet.state.pending': 'நிலுவையில்',
  'wallet.state.paid': 'பணம் வழங்கப்பட்டது',
  'wallet.pending.txnPrefix': 'பரிவர்த்தனை:',
  'wallet.pending.upload.label': 'விரைவான நடவடிக்கைக்கு பணம் செலுத்திய ஸ்க்ரீன்ஷாட்டை பதிவேற்றவும்.',
  'wallet.pending.upload.hint':  'கோரிக்கை உருவாக்கிய பிறகு 24 மணி நேரம் உள்ளது.',
  'wallet.pending.upload.failed': 'பதிவேற்றம் தோல்வி',
  'wallet.activity.balanceAfter': 'இருப்பு {balance}',
  'wallet.activity.range': '{total} இல் {from}–{to}',
  'wallet.activity.window': 'உங்கள் கடந்த {days} நாட்களின் செயல்பாடு',
  'wallet.history.summary.one':   '{matches} போட்டி · வென்றது {wins} · செலவு {bets}',
  'wallet.history.summary.other': '{matches} போட்டிகள் · வென்றது {wins} · செலவு {bets}',
  'wallet.history.summary.refunded': ' · திருப்பியளிப்பு {refunds}',
  'ledger.kind.bet': 'போட்டி நுழைவு', 'ledger.kind.win': 'போட்டி பரிசு',
  'ledger.kind.refund': 'போட்டி திருப்பியளிப்பு', 'ledger.kind.forfeit': 'போட்டி பறிப்பு',
  'ledger.kind.fee': 'தளக் கட்டணம்', 'ledger.kind.recharge': 'டாப்-அப்',
  'ledger.kind.admin_credit': 'நிர்வாகி கிரெடிட்', 'ledger.kind.admin_debit': 'நிர்வாகி டெபிட்',
  'ledger.kind.referral_bonus': 'பரிந்துரை போனஸ்', 'ledger.kind.signup_bonus': 'பதிவு போனஸ்',
  'ledger.kind.offer_credit': 'சலுகை பரிசு',
  'ledger.kind.withdrawal_hold': 'திரும்பப் பெறல் ஹோல்ட்', 'ledger.kind.withdrawal_refund': 'திரும்பப் பெறல் திருப்பியளிப்பு',
  'ledger.kind.agent_topup_in': 'ஏஜெண்ட் டாப்-அப்', 'ledger.kind.agent_co_fund': 'ஏஜெண்ட் பங்களிப்பு', 'ledger.kind.turnover_reward': 'கேஷ்பேக்',
  'ledger.withdrawalPaid': 'திரும்பப் பெறல் கொடுக்கப்பட்டது', 'ledger.withdrawalPaidPartial': 'திரும்பப் பெறல் கொடுக்கப்பட்டது (பகுதி)',
  'ledger.withdrawalPaid.txn': 'கொடுக்கப்பட்டது · பரிவர்த்தனை {txnId}',
  'ledger.withdrawalRejected': 'திரும்பப் பெறல் நிராகரிக்கப்பட்டது', 'ledger.withdrawalRejected.note': 'ஹோல்ட் தொகை திருப்பியளிக்கப்பட்டது',
  'ledger.withdrawalExpired': 'திரும்பப் பெறல் காலாவதி', 'ledger.awaitingProof': 'ஆதாரத்திற்காக காத்திருக்கிறது',
  'proofPicker.button.compact': 'ஸ்க்ரீன்ஷாட்டை தேர்வு செய்', 'proofPicker.button.prominent': 'ஸ்க்ரீன்ஷாட் சேர்',
  'proofPicker.processing': 'செயல்படுத்துகிறது…',
  'proofPicker.error.mime': 'JPG, PNG அல்லது WebP படங்கள் மட்டுமே அனுமதி.',
  'proofPicker.error.size': 'கோப்பு மிகப் பெரியது — அதிகபட்சம் 5 MB.',
  'proofPicker.error.processing': 'படத்தை செயல்படுத்த முடியவில்லை',
  'proofPicker.previewAlt': 'தேர்ந்தெடுக்கப்பட்ட ஸ்க்ரீன்ஷாட் முன்னோட்டம்',
  'proofViewer.state.none': 'ஸ்க்ரீன்ஷாட்டுக்காக காத்திருக்கிறது', 'proofViewer.state.available': 'ஸ்க்ரீன்ஷாட்டைப் பார்',
  'proofViewer.state.expired': 'ஸ்க்ரீன்ஷாட் இனி கிடைக்கவில்லை', 'proofViewer.state.viewLimit': 'பார்க்கும் வரம்பு முடிந்தது',
  'proofViewer.loading': 'ஏற்றப்படுகிறது…',
  'proofViewer.title.viewLimit': 'இந்த ஸ்க்ரீன்ஷாட்டை அதிகபட்சம் 2 முறை திறந்துள்ளீர்கள்',
  'proofViewer.error.load': 'ஸ்க்ரீன்ஷாட்டை ஏற்ற முடியவில்லை',
  'proofViewer.lastView': 'கடைசி பார்வை — படம் காலாவதியாகும்',
  'proofViewer.viewsRemaining.one': 'இன்னும் {n} முறை பார்க்கலாம்',
  'proofViewer.viewsRemaining.other': 'இன்னும் {n} முறை பார்க்கலாம்',
  'proofViewer.imageAlt': 'பணம் செலுத்திய ஆதாரம்', 'proofViewer.close': 'மூடு',
  'recharge.title': 'பணம் சேர்', 'recharge.step1.amount': 'தொகை',
  'recharge.step3.txnId': 'UPI பரிவர்த்தனை ID', 'recharge.step3.submit': 'மதிப்பாய்வுக்கு சமர்ப்பி',
  'recharge.success.title': 'சமர்ப்பிக்கப்பட்டது — மதிப்பாய்வுக்கு காத்திருக்கிறது',
  'withdraw.title': 'எடு', 'withdraw.amount': 'தொகை', 'withdraw.upiId': 'உங்கள் UPI ID',
  'withdraw.submit': 'சமர்ப்பி', 'withdraw.success.title': 'சமர்ப்பிக்கப்பட்டது — பணம் வழங்க காத்திருக்கிறது',
  'payment.openUpiApp': 'இப்போது செலுத்து', 'payment.upi.copy': 'நகலெடு',
  'common.next': 'அடுத்து', 'common.close': 'மூடு',

  // Location capture (Phase R3)
  'geo.purpose': 'வேகமான மற்றும் சீரான பணம் செலுத்தும் சேவைக்கு, துல்லியமான இடம் மற்றும் தொடர்பு எண் தரவும்.',
  'geo.change': 'மாற்று',
  'geo.gps.button': 'எனது தற்போதைய இடத்தைப் பயன்படுத்து',
  'geo.gps.benefit': 'வேகமானது — ஒரு தட்டு, தட்டச்சு இல்லை.',
  'geo.gps.denied': 'இடம் அனுமதி மறுக்கப்பட்டது. பின்கோடு பயன்படுத்து அல்லது கீழே தேர்வு செய்.',
  'geo.gps.failed': 'இடம் கிடைக்கவில்லை. பின்கோடு முயற்சி செய்.',
  'geo.gps.notResolved': 'உங்கள் இடத்தை பகுதியுடன் பொருத்த முடியவில்லை. தயவு செய்து தேர்வு செய்.',
  'geo.pincode.label': 'பின்கோடு',
  'geo.pincode.notFound': 'பின்கோடு கிடைக்கவில்லை. தயவு செய்து பகுதியைத் தேர்வு செய்.',
  'geo.pincode.lookupFailed': 'பின்கோடு தேடல் தோல்வி. தயவு செய்து தேர்வு செய்.',
  'geo.multipleAreas.label': 'இந்த பின்கோடில் பல பகுதிகள் — உங்களுடையதைத் தேர்வு செய்',
  'geo.multipleAreas.placeholder': 'உங்கள் பகுதியைத் தேர்வு செய்',
  'geo.manual.title': 'கைமுறையாகத் தேர்வு செய்',
  'geo.manual.close': 'கைமுறையை மறை',
  'geo.state.label': 'மாநிலம்', 'geo.state.placeholder': 'மாநிலம் தேர்வு செய்',
  'geo.district.label': 'மாவட்டம்', 'geo.district.placeholder': 'மாவட்டம் தேர்வு செய்',
  'geo.area.label': 'பகுதி / நகரம்', 'geo.area.placeholder': 'பகுதியைத் தேர்வு செய்',
  'geo.privacy.note': 'உள்ளூர் முகவரை வழங்கவே. பகிரப்படுவதில்லை.',
  'geo.required.hint': 'பரிந்துரை குறியீடு இல்லாதபோது இடம் அவசியம்.',
  'agent.nav.dashboard': 'டாஷ்போர்டு',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'கணக்கு',
  'agent.nav.players': 'பிளேயர்கள்',
  'agent.nav.agents': 'ஏஜெண்ட்கள்',
  'agent.nav.rewards': 'வெகுமதிகள்',
  'agent.nav.profile':   'சுயவிவரம்',
  'agent.bottomnav.home': 'முகப்பு',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'கணக்கு',
  'agent.bottomnav.players': 'பிளேயர்கள்',
  'agent.bottomnav.agents': 'ஏஜெண்ட்கள்',
  'agent.bottomnav.rewards': 'வெகுமதிகள்',
  'agent.bottomnav.profile': 'சுயவிவரம்',
  'agent.dashboard.header.welcomePrefix': 'மீண்டும் வரவேற்கிறோம், ',
  'agent.dashboard.header.welcomeFallback': 'நண்பரே',
  'agent.dashboard.header.subtitle': 'உங்கள் வணிகம் ஒரே பார்வையில் — பூல், டெபாசிட், பிளேயர்கள், சப்-ஏஜெண்ட்கள், P&L, எக்ஸ்போஷர்',
  'agent.dashboard.range.today': 'இன்று',
  'agent.dashboard.range.yesterday': 'நேற்று',
  'agent.dashboard.range.thisWeek': 'இந்த வாரம்',
  'agent.dashboard.range.lastWeek': 'கடந்த வாரம்',
  'agent.dashboard.range.thisMonth': 'இந்த மாதம்',
  'agent.dashboard.range.lastMonth': 'கடந்த மாதம்',
  'agent.dashboard.range.complete': 'முழுமை',
  'agent.dashboard.depositBar.loading': 'டெபாசிட் கவரேஜ் ஏற்றப்படுகிறது…',
  'agent.dashboard.depositBar.label': 'டெபாசிட் கவரேஜ்',
  'agent.dashboard.depositBar.balanceOfTarget': '· {target} இலக்கில் {balance}',
  'agent.dashboard.depositBar.tapToView': 'முழு டெபாசிட் பக்கத்தைப் பார்க்க தட்டவும்.',
  'agent.dashboard.depositBar.caption.suspended': 'கணக்கு நிறுத்தப்பட்டுள்ளது. அட்மினைத் தொடர்பு கொள்ளவும்.',
  'agent.dashboard.depositBar.caption.breach': 'உங்கள் வாலெட் டெபாசிட் உச்சவரம்பின் 100% பயன்படுத்திவிட்டது. பணம் எடுப்பு பூட்டப்பட்டுள்ளது. உடனே டாப்-அப் செய்யவும்.',
  'agent.dashboard.depositBar.caption.warning': 'உங்கள் வாலெட் டெபாசிட் உச்சவரம்பின் {pct}% என உள்ளது. எச்சரிக்கை 80%-இல், லாக்டவுன் 100%-இல்.',
  'agent.dashboard.depositBar.caption.caution': 'உங்கள் வாலெட் டெபாசிட் கவரேஜின் {pct}% சாப்பிட்டுவிட்டது. 80%-க்குக் கீழே — இன்னும் பாதுகாப்பானது.',
  'agent.dashboard.depositBar.caption.safe': 'டெபாசிட் முழுமையாக அப்படியே. கவரேஜ் எதுவும் பயன்படுத்தப்படவில்லை.',
  'agent.dashboard.locatorSkip.title': 'எச்சரிக்கை · பூஜ்ய பாதுகாப்பு டெபாசிட்',
  'agent.dashboard.locatorSkip.body': 'உங்கள் பாதுகாப்பு டெபாசிட் {amount}. நீங்கள் அதை நிதியளிக்கும் வரை, புவியியல் லொக்கேட்டரில் இருந்து புதிய பிளேயர்களை நீங்கள் பெறமாட்டீர்கள். ஏற்கனவே உள்ள பிளேயர்கள் + நேரடி ரெஃபெரல்கள் + மேனுவல் அசைன்மென்ட்களுக்கு பாதிப்பு இல்லை — ஆர்கானிக் ஜியோ-ரூட்டட் சைன்அப்கள் மட்டும் உங்களைத் தவிர்க்கும்.',
  'agent.dashboard.locatorSkip.cta': 'டெபாசிட்டை நிதியளி',
  'agent.dashboard.defaulter.title': 'டவுன்லைன் டிஃபால்ட்டர் எச்சரிக்கை',
  'agent.dashboard.defaulter.summary.critical.one': 'உங்கள் நேரடி சப்-ஏஜெண்ட்களில் {n} டிஃபால்ட்டில் உள்ளார் — அவர் பூட்டப்பட்டுள்ளார் அல்லது தனது டெபாசிட்டை மீறிவிட்டார்.',
  'agent.dashboard.defaulter.summary.critical.other': 'உங்கள் நேரடி சப்-ஏஜெண்ட்களில் {n} பேர் டிஃபால்ட்டில் உள்ளனர் — அவர்கள் பூட்டப்பட்டுள்ளனர் அல்லது தங்கள் டெபாசிட்டை மீறிவிட்டனர்.',
  'agent.dashboard.defaulter.summary.warning.one': 'உங்கள் நேரடி சப்-ஏஜெண்ட்களில் {n} தனது டெபாசிட் உச்சவரம்பின் 80%+ எத்திவிட்டார் — டிஃபால்ட்டர் ஆபத்து.',
  'agent.dashboard.defaulter.summary.warning.other': 'உங்கள் நேரடி சப்-ஏஜெண்ட்களில் {n} பேர் தங்கள் டெபாசிட் உச்சவரம்பின் 80%+ எத்திவிட்டனர் — டிஃபால்ட்டர் ஆபத்து.',
  'agent.dashboard.defaulter.worst': 'மிக மோசமானது: டெபாசிட் உச்சவரம்பின் {pct}%-இல் @{username}.',
  'agent.dashboard.defaulter.tapHint': 'நேரடி சப்-ஏஜெண்ட்கள் பட்டியலுக்கு செல்ல தட்டவும். டிஃபால்ட்டர் வரிசைகள் ஹைலைட் செய்யப்பட்டுள்ளன.',
  'agent.dashboard.marginPill.locked': 'பூட்டப்பட்டது',
  'agent.dashboard.marginPill.caution': 'எச்சரிக்கை',
  'agent.dashboard.marginPill.warning': 'வார்னிங்',
  'agent.dashboard.marginPill.breach': 'மீறல்',
  'agent.dashboard.sections.morning.title': 'காலை புரிஃபிங்',
  'agent.dashboard.sections.morning.subtitle': 'நேரடி ஸ்னாப்ஷாட் — ஒவ்வொரு எண்ணும் தானாகப் புதுப்பிக்கிறது',
  'agent.dashboard.sections.activity.title': 'செயல்பாடு',
  'agent.dashboard.sections.activity.subtitle': 'சிறந்த செலவாளிகள் மற்றும் சமீபத்திய பிளேயர் பேமெண்ட் நிகழ்வுகள்',
  'agent.dashboard.sections.forensic.title': 'ஃபோரென்சிக் விவரம்',
  'agent.dashboard.sections.forensic.subtitle': 'எந்த பகுதியையும் விரிவாக்க தட்டவும் — இந்தப் பக்கத்தைச் சாதாரணமாக வைத்திருக்க இயல்பாக மூடப்பட்டுள்ளது',
  'agent.dashboard.accordions.pnlDeep.title': 'லாபம் & நஷ்டம் ஆழமான பகுப்பாய்வு',
  'agent.dashboard.accordions.pnlDeep.helper': 'உங்கள் வருமானத்தின் பிலர் வாரியான + கேம் வாரியான பிரிவு. அடிப்படை மேட்ச்களுக்கு கேம் வரிசையைக் கிளிக் செய்யவும்.',
  'agent.dashboard.accordions.leaderboard.title': 'டவுன்லைன் லீடர்போர்டு',
  'agent.dashboard.accordions.leaderboard.helper': 'நிகர வருமானத்தின்படி உங்கள் சப்-ஏஜெண்ட்களின் தரவரிசை. எதிர்மறை எண்கள் என்றால் அந்த சப்-ஏஜெண்ட் கொண்டுவந்ததைவிட அதிகமாக பணம் கொடுத்துள்ளார்.',
  'agent.dashboard.accordions.activity.title': 'டவுன்லைன் செயல்பாட்டு ஃபீட்',
  'agent.dashboard.accordions.activity.helper': 'உங்கள் சப்-ஏஜெண்ட்கள் முழுவதும் ஒவ்வொரு மேட்ச் செட்டில்மென்ட், ரீசார்ஜ் மற்றும் ரேட் மாற்றம் — ஃபோரென்சிக் காலவரிசை.',
  'agent.dashboard.accordions.earnings.title': 'வருமான பிரிவு (வாழ்நாள் முழுவதும்)',
  'agent.dashboard.accordions.earnings.helper': 'வகை வாரியாக வாழ்நாள் மொத்தம். மேலே உள்ள ரேஞ்ச் டாகிளை புறக்கணிக்கிறது — இவை நேர வடிகட்டியுடன் மாறாது.',
  'agent.dashboard.accordions.directSubAgents.title': 'நேரடி சப்-ஏஜெண்ட்கள்',
  'agent.dashboard.accordions.directSubAgents.helper': 'உங்கள் நேரடி உதவியாளர்கள். ஒவ்வொருவரும் தங்கள் சொந்த பிளேயர்கள் + சொந்த சப்-ஏஜெண்ட்களை இயக்குகிறார்கள். ரோஸ்/அம்பர் ஹைலைட் செய்யப்பட்ட வரிசைகள் டிஃபால்ட் பகுதியில் உள்ளன.',
  'agent.dashboard.accordions.chain.title': 'செயின் தெரியும்தன்மை',
  'agent.dashboard.accordions.chain.helper': 'உங்கள் மொத்த டவுன்லைன் தடம், நேரடி (உங்களால் திருத்தப்பட்டது) மற்றும் மறைமுக (உங்கள் சப்-ஏஜெண்ட்களால் நிர்வகிக்கப்படுவது) என பிரிக்கப்பட்டுள்ளது.',
  'agent.dashboard.liveFootnote': 'நேரடி தரவு — ஃபோகஸில், ஒவ்வொரு 60 வினாடிக்கும், பணம் நகரும்போது புதுப்பிக்கிறது.',
  'agent.dashboard.cards.poolBalance.label': 'என் பூல் பேலன்ஸ்',
  'agent.dashboard.cards.poolBalance.helper': 'உங்கள் செலவழிக்கக்கூடிய பேலன்ஸ். பிளேயர்களை ரீசார்ஜ் செய்ய அல்லது சப்-ஏஜெண்ட்களுக்கு அனுப்ப பயன்படுத்தக்கூடிய பணம்.',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} இல் நிகர {value}',
  'agent.dashboard.cards.depositStatus.titleLoading': 'பாதுகாப்பு டெபாசிட்',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'டெபாசிட் நிலை',
  'agent.dashboard.cards.depositStatus.helperLoading': 'டெபாசிட் நிலை ஏற்றப்படுகிறது…',
  'agent.dashboard.cards.depositStatus.helper': 'உங்கள் பூலுக்கு எதிரான பிணையம். இலக்கின் 50%-இல் கொஷன், 80%-இல் வார்னிங், 100%-இல் ப்ரீச்-க்கு நிலை மாறுகிறது.',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} முதல்',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'ஆபரேட்டிங் பேலன்ஸ்',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'டெபாசிட் பேலன்ஸ்',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'டெபாசிட் இலக்கு',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'எதிர்மறை எக்ஸ்போஷர்',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'கவரேஜ் விகிதம்',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'எதிர்மறை எக்ஸ்போஷர் மூலம் பயன்படுத்தப்பட்ட டெபாசிட் இலக்கு',
  'agent.dashboard.depositStatus.status.safe': 'பாதுகாப்பான',
  'agent.dashboard.depositStatus.status.caution': 'எச்சரிக்கை',
  'agent.dashboard.depositStatus.status.warning': 'வார்னிங்',
  'agent.dashboard.depositStatus.status.breach': 'மீறல்',
  'agent.dashboard.depositStatus.status.suspended': 'நிறுத்தப்பட்டது',
  'agent.dashboard.depositStatus.status.healthy': 'ஆரோக்கியமான',
  'agent.dashboard.cards.playersWallet.title': 'பிளேயர்களின் வாலெட் மொத்தம்',
  'agent.dashboard.cards.playersWallet.helper': 'உங்கள் ஒவ்வொரு பிளேயரின் வாலெட்டிலும் இப்போது உள்ள ஒருங்கிணைந்த நேரடி பேலன்ஸ். உங்களை நோக்கி உள்ள ஒவ்வொரு wallet_points வரிசையின் கூட்டு.',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} பிளேயர் · {active} ஆக்டிவ்',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} பிளேயர்கள் · {active} ஆக்டிவ்',
  'agent.dashboard.cards.subAgents.title': 'சப்-ஏஜெண்ட்கள் வாலெட் + டெபாசிட்',
  'agent.dashboard.cards.subAgents.helper': 'உங்கள் நேரடி சப்-ஏஜெண்ட்களின் ஒருங்கிணைந்த பூல் பேலன்ஸ் மற்றும் பாதுகாப்பு டெபாசிட். ஒவ்வொரு சப்-ஏஜெண்ட்டும் தங்கள் சொந்த பிளேயர்கள் + சொந்த டவுன்லைனை இயக்குகிறார்கள்.',
  'agent.dashboard.cards.subAgents.field.pool': 'பூல்',
  'agent.dashboard.cards.subAgents.field.deposit': 'டெபாசிட்',
  'agent.dashboard.cards.subAgents.foot.one': '{n} நேரடி சப்-ஏஜெண்ட்',
  'agent.dashboard.cards.subAgents.foot.other': '{n} நேரடி சப்-ஏஜெண்ட்கள்',
  'agent.dashboard.cards.myPending.title': 'என் நிலுவையில் உள்ள கோரிக்கைகள்',
  'agent.dashboard.cards.myPending.helper': 'முன்னுரிமை 3. அட்மினுக்கு உங்கள் சொந்த சமர்ப்பிப்புகள் — இவற்றை நீங்களே நடவடிக்கை எடுக்க முடியாது, நீங்கள் அவர்களுக்காகக் காத்திருக்கிறீர்கள்.',
  'agent.dashboard.cards.myPending.recharge.one': '{n} ரீசார்ஜ் அட்மினுக்காகக் காத்திருக்கிறது',
  'agent.dashboard.cards.myPending.recharge.other': '{n} ரீசார்ஜ்கள் அட்மினுக்காகக் காத்திருக்கின்றன',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} பணம் எடுப்பு அட்மினுக்காகக் காத்திருக்கிறது',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} பணம் எடுப்புகள் அட்மினுக்காகக் காத்திருக்கின்றன',
  'agent.dashboard.cards.playersPending.title': 'பிளேயர்களின் நிலுவையில் உள்ள கோரிக்கைகள்',
  'agent.dashboard.cards.playersPending.helper': 'முன்னுரிமை 1. உங்கள் பிளேயர்கள் இவற்றை அங்கீகரிக்க அல்லது செலுத்த உங்களுக்காகக் காத்திருக்கிறார்கள். நடவடிக்கை எடுக்க தட்டவும்.',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} ரீசார்ஜ் அங்கீகரிக்க',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} ரீசார்ஜ்கள் அங்கீகரிக்க',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} பணம் எடுப்பு செலுத்த',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} பணம் எடுப்புகள் செலுத்த',
  'agent.dashboard.cards.subAgentsPending.title': 'சப்-ஏஜெண்ட்களின் நிலுவையில் உள்ள கோரிக்கைகள்',
  'agent.dashboard.cards.subAgentsPending.helper': 'முன்னுரிமை 2. உங்கள் சப்-ஏஜெண்ட்கள் (முழு டவுன்லைன்) இவற்றை அங்கீகரிக்க அல்லது செலுத்த உங்களுக்காகக் காத்திருக்கிறார்கள். நடவடிக்கைக்கு தட்டவும்.',
  'agent.dashboard.cards.trueNet.title': 'உண்மையான நிகர P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'இந்த ரேஞ்சில் உங்கள் உண்மையான லாபம்/நஷ்டம். ப்ளாட்ஃபார்ம் ஃபீ பங்கு + வின்/லாஸ் பங்கு − ஆஃபர் கோ-ஃபண்டிங். ஒரு கேம்-வாரியான விவரங்களுக்கு கீழே உள்ள P&L ஆழமான பகுப்பாய்வை விரிவாக்கவும்.',
  'agent.dashboard.pillarChip.platformFee': 'ப்ளாட்',
  'agent.dashboard.pillarChip.winLoss': 'வி/லா',
  'agent.dashboard.pillarChip.offer': 'ஆஃபர்',
  'agent.dashboard.cards.rewardUtil.title': 'ஆஃபர் பயன்பாடு · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'இந்த ரேஞ்சில் ஆஃபர்கள் மூலம் உங்கள் பிளேயர்களுக்கு வழங்கப்பட்ட மொத்த வெகுமதி ₹. அந்த விலையில் உங்கள் பங்கைப் பார்க்க உங்கள் எக்ஸ்போஷர் பக்கத்துடன் ஒப்பிடுங்கள்.',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} கிளெய்ம் வரவு வைக்கப்பட்டது',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} கிளெய்ம்கள் வரவு வைக்கப்பட்டன',
  'agent.dashboard.activity.topSpenders.title': 'அதிக செலவாளிகள்',
  'agent.dashboard.activity.topSpenders.helper': '{range} இல் உங்களின் அதிகம் செலவழிக்கும் பிளேயர்கள்.',
  'agent.dashboard.activity.topSpenders.empty': 'இந்த ரேஞ்சில் இன்னும் செலவு இல்லை.',
  'agent.dashboard.activity.recent.title': 'சமீபத்திய செயல்பாடு',
  'agent.dashboard.activity.recent.helper': 'உங்கள் பிளேயர்களிடமிருந்து சமீபத்திய ஐந்து ரீசார்ஜ் நிகழ்வுகள்.',
  'agent.dashboard.activity.recent.empty': 'சமீபத்திய செயல்பாடு இல்லை.',
  'agent.dashboard.activity.recent.state.approved': 'அங்கீகரிக்கப்பட்டது',
  'agent.dashboard.activity.recent.state.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.dashboard.activity.recent.state.pending': 'நிலுவையில்',
  'agent.dashboard.pnl.perGameLabel': 'ஒரு கேமுக்கு · விளக்க வரிசையைத் தட்டவும்',
  'agent.dashboard.pnl.noEarnings': 'இந்த ரேஞ்சில் வருமானம் இல்லை.',
  'agent.dashboard.pnl.table.col.pillarGame': 'பிலர் / கேம்',
  'agent.dashboard.pnl.table.col.net': 'நிகர',
  'agent.dashboard.pnl.table.col.matches': 'மேட்ச்கள்',
  'agent.dashboard.pnl.ledgerRows.one': '{n} லெட்ஜர் வரிசை',
  'agent.dashboard.pnl.ledgerRows.other': '{n} லெட்ஜர் வரிசைகள்',
  'agent.dashboard.pnl.pillar.platformFee': 'ப்ளாட்ஃபார்ம் ஃபீ',
  'agent.dashboard.pnl.pillar.winLoss': 'வின் / லாஸ்',
  'agent.dashboard.pnl.pillar.offerReward': 'ஆஃபர் வெகுமதி',
  'agent.dashboard.pnlDrill.windowLabel': 'வின்டோ: {range} — டாஷ்போர்டின் ரேஞ்சின் கடுமையான துணை தொகுப்பு',
  'agent.dashboard.pnlDrill.loading': 'மேட்ச்-நிலை விவரங்கள் ஏற்றப்படுகின்றன…',
  'agent.dashboard.pnlDrill.windowTotal': 'வின்டோ மொத்தம்:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} மேட்ச் முழுவதும்',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} மேட்ச்கள் முழுவதும்',
  'agent.dashboard.pnlDrill.noContribution': 'இந்த செல்லில் எந்த மேட்சும் பங்களிக்கவில்லை.',
  'agent.dashboard.pnlDrill.errorFallback': 'டிரில்டவுனை ஏற்ற முடியவில்லை',
  'agent.dashboard.pnlDrill.table.col.match': 'மேட்ச்',
  'agent.dashboard.pnlDrill.table.col.role': 'பங்கு',
  'agent.dashboard.pnlDrill.table.col.base': 'பேஸ்',
  'agent.dashboard.pnlDrill.table.col.rate': 'ரேட்',
  'agent.dashboard.pnlDrill.table.col.earned': 'சம்பாதித்தது',
  'agent.dashboard.pnlDrill.table.col.settled': 'செட்டில்',
  'agent.dashboard.directDownline.loading': 'ஏற்றப்படுகிறது…',
  'agent.dashboard.directDownline.empty': 'இன்னும் நேரடி சப்-ஏஜெண்ட்கள் இல்லை.',
  'agent.dashboard.directDownline.row.player.one': '{n} பிளேயர்',
  'agent.dashboard.directDownline.row.player.other': '{n} பிளேயர்கள்',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} சப்-ஏஜெண்ட்',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} சப்-ஏஜெண்ட்கள்',
  'agent.dashboard.directDownline.row.walletDeposit': 'வாலெட் {wallet} · டெபாசிட் {deposit}',
  'agent.dashboard.directDownline.footer': 'உண்மையான நிகர {range} வின்டோவுக்கானது.',
  'agent.dashboard.chain.loading': 'ஏற்றப்படுகிறது…',
  'agent.dashboard.chain.directAgents.title': 'நேரடி ஏஜெண்ட்கள்',
  'agent.dashboard.chain.directAgents.subtitle': 'உங்கள் நேரடி சப்-ஏஜெண்ட்கள் — நீங்கள் அவர்களின் கோரிக்கைகளில் நடவடிக்கை எடுக்கலாம்.',
  'agent.dashboard.chain.indirectAgents.title': 'மறைமுக ஏஜெண்ட்கள்',
  'agent.dashboard.chain.indirectAgents.subtitle': 'உங்கள் சப்-ஏஜெண்ட்களால் நிர்வகிக்கப்படுகிறது. உங்களுக்கு படிக்க மட்டுமே.',
  'agent.dashboard.chain.directPlayers.title': 'நேரடி பிளேயர்கள்',
  'agent.dashboard.chain.directPlayers.subtitle': 'உங்கள் பிளேயர்கள் — அவர்களின் ரீசார்ஜ்கள் மற்றும் பணம் எடுப்புகளில் நீங்கள் நடவடிக்கை எடுக்கிறீர்கள்.',
  'agent.dashboard.chain.indirectPlayers.title': 'மறைமுக பிளேயர்கள்',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'உங்கள் சப்-ஏஜெண்ட்களின் பிளேயர்கள். உங்களுக்கு படிக்க மட்டுமே.',
  'agent.dashboard.chain.field.walletTotal': 'வாலெட் மொத்தம்',
  'agent.dashboard.chain.field.depositTotal': 'டெபாசிட் மொத்தம்',
  'agent.dashboard.chain.field.avgPerHead': 'ஒரு நபருக்கான சராசரி',
  'agent.dashboard.chain.field.pendingRecharge': 'நிலுவையில் உள்ள ரீசார்ஜ்',
  'agent.dashboard.chain.field.pendingWithdrawal': 'நிலுவையில் உள்ள பணம் எடுப்பு',
  'agent.dashboard.chain.pendingBadge': '{n} நிலுவையில்',
  'agent.dashboard.chain.openInbox': 'இன்பாக்ஸ் திற',
  'agent.dashboard.chain.footer': 'நேரடி = நீங்களே நிர்வகிக்கும் எண்ணிக்கை. மறைமுக = உங்கள் சப்-ஏஜெண்ட்களால் நிர்வகிக்கப்படுவது (நீங்கள் எண்களைப் பார்க்கிறீர்கள் ஆனால் அவர்களது நிலுவை கோரிக்கைகளில் நடவடிக்கை எடுக்க முடியாது — அவை அவர்களின் பேரண்டுக்கு செல்கின்றன).',
  'agent.activity.error.loadFailed': 'ஏற்ற முடியவில்லை',
  'agent.activity.filter.window': 'வின்டோ',
  'agent.activity.filter.kind': 'வகை',
  'agent.activity.window.all': 'அனைத்தும்',
  'agent.activity.kind.all': 'அனைத்தும்',
  'agent.activity.kind.matches': 'மேட்ச்கள்',
  'agent.activity.kind.recharges': 'ரீசார்ஜ்கள்',
  'agent.activity.kind.rateChanges': 'ரேட் மாற்றங்கள்',
  'agent.activity.loading': 'ஏற்றப்படுகிறது…',
  'agent.activity.empty.title': 'அமைதி',
  'agent.activity.empty.subtitle': '{window} வின்டோவில் {kindWord}நிகழ்வுகள் இல்லை.',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'மேட்ச் ',
  'agent.activity.empty.kindWord.recharge': 'ரீசார்ஜ் ',
  'agent.activity.empty.kindWord.partnership_change': 'பார்ட்னர்ஷிப் மாற்றம் ',
  'agent.activity.row.settlementFallback': 'செட்டில்மென்ட்',
  'agent.activity.row.earned': 'சம்பாதித்தது',
  'agent.activity.row.fromPillarOn': '{pillar} இலிருந்து ',
  'agent.activity.row.pillar.platformFee': 'ப்ளாட்ஃபார்ம் ஃபீ',
  'agent.activity.row.pillar.winLoss': 'வின்/லாஸ்',
  'agent.activity.row.pillar.offerReward': 'ஆஃபர் வெகுமதி',
  'agent.activity.row.recharge.prefix': 'ரீசார்ஜ்',
  'agent.activity.row.recharge.of': 'இன்',
  'agent.activity.row.recharge.byUser': 'பயனர் #{id} மூலம்',
  'agent.activity.row.recharge.via': ' மூலம் ',
  'agent.activity.row.state.approved': 'அங்கீகரிக்கப்பட்டது',
  'agent.activity.row.state.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.activity.row.state.pending': 'நிலுவையில்',
  'agent.activity.row.rateChange.prefix': 'ரேட் மாற்றம்',
  'agent.activity.row.rateChange.dashOn': '— {pillar} இல் ',
  'agent.activity.row.rateChange.cascade': 'கேஸ்கேட்',
  'agent.earnings.notAgent': 'இந்த காட்சி ஏஜெண்ட்களுக்கு மட்டுமே.',
  'agent.earnings.stat.poolBalance': 'பூல் பேலன்ஸ்',
  'agent.earnings.stat.totalIn': 'மொத்த உள்வரவு',
  'agent.earnings.stat.totalOut': 'மொத்த வெளியேறல்',
  'agent.earnings.section.in': 'வருமானம் (உள்வரவு)',
  'agent.earnings.section.out': 'வெளியேறல் (வெளிவரவு)',
  'agent.earnings.empty.in': 'இன்னும் வருமானம் இல்லை. உங்கள் பிளேயர்கள் ரீசார்ஜ் செய்தாலோ அல்லது விளையாடினாலோ, பங்குகள் இங்கே தோன்றும்.',
  'agent.earnings.empty.out': 'இன்னும் வெளியேறல்கள் இல்லை.',
  'agent.earnings.viewLedger': 'முழு லெட்ஜரைப் பார்',
  'agent.earnings.kind.topupFromAdmin.label': 'அட்மினிடமிருந்து டாப்-அப்',
  'agent.earnings.kind.topupFromAdmin.hint': 'அட்மின் உங்கள் பூலுக்கு வரவு வைத்தார்',
  'agent.earnings.kind.topupToPlayer.label': 'பிளேயருக்கு டாப்-அப்',
  'agent.earnings.kind.topupToPlayer.hint': 'நீங்கள் ஒரு பிளேயரை ரீசார்ஜ் செய்தீர்கள்',
  'agent.earnings.kind.topupFromPlayer.label': 'பிளேயர் ரீசார்ஜ்',
  'agent.earnings.kind.topupFromPlayer.hint': 'உங்கள் பிளேயர்கள் உங்கள் பூலுக்கு செலுத்தினர்',
  'agent.earnings.kind.revenueShare.label': 'வருவாய் பங்கு',
  'agent.earnings.kind.revenueShare.hint': 'மேட்ச்களிலிருந்து ப்ளாட்ஃபார்ம் ஃபீ பங்கு',
  'agent.earnings.kind.coFundOffer.label': 'கோ-ஃபண்ட் ஆஃபர்',
  'agent.earnings.kind.coFundOffer.hint': 'ஆஃபர் பேஅவுட்களில் உங்கள் பங்கு',
  'agent.earnings.kind.adminAdjust.label': 'அட்மின் சரிசெய்தல்',
  'agent.earnings.kind.adminAdjust.hint': 'மேனுவல் சரிசெய்தல்',
  'agent.earnings.row.entry.one': 'பதிவு',
  'agent.earnings.row.entry.other': 'பதிவுகள்',
  'agent.leaderboard.filter.window': 'வின்டோ',
  'agent.leaderboard.filter.pillar': 'பிலர்',
  'agent.leaderboard.window.all': 'அனைத்தும்',
  'agent.leaderboard.pillar.all': 'அனைத்து பிலர்கள்',
  'agent.leaderboard.pillar.platformFee': 'ப்ளாட்ஃபார்ம் ஃபீ',
  'agent.leaderboard.pillar.winLoss': 'வின் / லாஸ்',
  'agent.leaderboard.pillar.offerReward': 'ஆஃபர் வெகுமதி',
  'agent.leaderboard.loading': 'ஏற்றப்படுகிறது…',
  'agent.leaderboard.empty.title': 'இன்னும் வருமானம் இல்லை',
  'agent.leaderboard.empty.subtitle.admin': '{window} வின்டோவில் அனைத்து ஏஜெண்ட்களிலும் செட்டில்மென்ட் செயல்பாடு இல்லை.',
  'agent.leaderboard.empty.subtitle.downline': '{window} வின்டோவில் உங்கள் டவுன்லைனிலிருந்து வருமானம் இல்லை. உங்கள் சப்-ஏஜெண்ட்கள் மேட்ச்களை இயக்கும்போது, இந்த போர்டு நிரப்பப்படும்.',
  'agent.leaderboard.row.matches.one': '{n} மேட்ச்',
  'agent.leaderboard.row.matches.other': '{n} மேட்ச்கள்',
  'agent.leaderboard.footer': 'வருமானம் match_settlement_snapshots இலிருந்து பெறப்பட்டது. எதிர்மறை எண் என்றால் அந்த ஏஜெண்ட் வின்டோவில் கொண்டுவந்ததைவிட அதிகமாக பணம் செலுத்தினார்.',
  'agent.leaderboard.chip.platformFee': 'ப்ளாட்',
  'agent.leaderboard.chip.winLoss': 'வி/லா',
  'agent.leaderboard.chip.offer': 'ஆஃபர்',
  'agent.warning.header.eyebrow': 'மார்ஜின் வார்னிங் · 80% வரம்பு எட்டப்பட்டது',
  'agent.warning.header.title': 'லாக்அவுட்டிலிருந்து ஒரு மோசமான செட்டில்மென்ட் தொலைவில்.',
  'agent.warning.header.body': 'நீங்கள் இன்னும் பூட்டப்படவில்லை — ஆனால் உங்கள் டெபாசிட் பாதுகாப்பு குஷன் 80%-க்கு மேல் செலவழிக்கப்பட்டுள்ளது. இப்போது டாப்-அப் செய்தால், நீங்கள் கட்டுப்பாட்டில் இருப்பீர்கள்.',
  'agent.warning.header.dismissAria': 'நிராகரிக்கவும்',
  'agent.warning.why.eyebrow': 'இது ஏன் காட்டப்படுகிறது',
  'agent.warning.why.body': 'சமீபத்திய கேம் செட்டில்மென்ட்கள் உங்கள் வாலெட்டை எதிர்மறையாக தள்ளியுள்ளன. உங்கள் {deposit} பாதுகாப்பு டெபாசிட் இதை உள்வாங்கும் பாதுகாப்பு வலை — அது இப்போது 80%-க்கு மேல் செலவழிக்கப்பட்டுள்ளது. கொஞ்சம் மேலும் எதிர்மறை வந்தால், உங்கள் வாலெட் + டெபாசிட் பூஜ்யத்திற்கு வந்து ப்ளாட்ஃபார்ம் கணக்கைப் பூட்டும்.',
  'agent.warning.why.reassurance': 'இந்த எச்சரிக்கை வேண்டுமென்றே சீக்கிரம் எரிகிறது — நீங்கள் இன்னும் முழு ஆப்பையும் பயன்படுத்த முடியும். லாக்அவுட் ஸ்கிரீன் நீங்கள் 100% கடந்தால் மட்டுமே தோன்றும், அது நடந்தவுடன், நீங்கள் மீட்கும் வரை பெரும்பாலான அம்சங்கள் தடுக்கப்படும். பின்னர் நடவடிக்கை எடுப்பதைவிட இப்போது நடவடிக்கை எடுப்பது மிகவும் குறைவான இடையூறு.',
  'agent.warning.gauge.label': 'லாக்அவுட்டிற்கு முன் இடம்',
  'agent.warning.gauge.suffix': 'இடம் மீதம்',
  'agent.warning.gauge.dangerBadge': 'ஆபத்து மண்டலத்தில் {pct}%',
  'agent.warning.gauge.field.wallet': 'உங்கள் வாலெட்',
  'agent.warning.gauge.field.deposit': 'உங்கள் டெபாசிட்',
  'agent.warning.ignore.eyebrow': 'இதைப் புறக்கணித்து 100% எட்டினால்',
  'agent.warning.ignore.cardTitle': 'நீங்கள் உடனடியாக இழக்கப் போவது',
  'agent.warning.ignore.item.dashboard': 'உங்கள் டாஷ்போர்டு, P&L மற்றும் லீடர்போர்டுகளுக்கான அணுகல்',
  'agent.warning.ignore.item.pages': 'பிளேயர்கள் மற்றும் ஏஜெண்ட்கள் பக்கங்கள்',
  'agent.warning.ignore.item.manualTopup': 'உங்கள் பூலிலிருந்து பிளேயர்களுக்கு மேனுவல் டாப்-அப்கள்',
  'agent.warning.ignore.item.newWithdrawal': 'உங்கள் அப்லைனுக்கு புதிய பணம் எடுப்புகளை சமர்ப்பித்தல்',
  'agent.warning.ignore.item.subAgents': 'சப்-ஏஜெண்ட்களைச் சேர்த்தல் அல்லது நிர்வகித்தல்',
  'agent.warning.ignore.item.profile': 'உங்கள் சுயவிவரம் அல்லது பேமெண்ட் அமைப்புகளைத் திருத்துதல்',
  'agent.warning.ignore.footer': 'நிலுவையில் உள்ள கோரிக்கைகளை அங்கீகரிக்கவும் + அவசர டாப்-அப் ஒன்றை சமர்ப்பிக்கவும் இன்னும் முடியும் — ஆனால் உங்கள் வாலெட் + டெபாசிட் பூஜ்யத்திற்கு மேல் வரும் வரை மற்ற அனைத்தும் நிற்கும்.',
  'agent.warning.recoverEyebrow': 'மீட்க இப்போது உங்கள் அப்லைனுக்கு செலுத்தவும்',
  'agent.warning.dismiss.cta': 'நான் இதை பின்னர் சமாளிக்கிறேன்',
  'agent.warning.dismiss.footnote': 'நீங்கள் எச்சரிக்கை மண்டலத்தில் இருந்தால் 10 நிமிடங்களில் மீண்டும் நினைவூட்டுவோம். நீங்கள் மீண்டவுடன் தானாக மூடப்படும்.',
  'agent.lockout.header.eyebrow': 'கணக்கு பூட்டப்பட்டது · பெயில்அவுட் போர்டல்',
  'agent.lockout.header.title': 'உங்கள் டெபாசிட் ஈடுகட்டுவதைவிட அதிகம் உங்களுக்குக் கடன்பட்டுள்ளது.',
  'agent.lockout.header.body': 'கவலைப்படாதீர்கள் — இது மீட்க முடியக்கூடியது. கீழே உள்ள இரண்டு படிகளைப் பின்பற்றவும், நீங்கள் தானாக அன்லாக் ஆவீர்கள்.',
  'agent.lockout.why.eyebrow': 'இது ஏன் நடந்தது',
  'agent.lockout.why.body': 'கேம் செட்டில்மென்ட்கள் உங்கள் வாலெட்டை பூஜ்யத்திற்குக் கீழே தள்ளியது. உங்கள் பாதுகாப்பு டெபாசிட் இதை உள்வாங்கும் பாதுகாப்பு வலை — ஆனால் நீங்கள் இப்போது அதை 100% பயன்படுத்திவிட்டீர்கள். உங்கள் வாலெட் மற்றும் டெபாசிட் மீண்டும் பூஜ்யத்திற்கு மேல் வரும் வரை, நீங்கள் சாதாரணமாக இயங்க முடியாது.',
  'agent.lockout.why.reassurance': 'நீங்கள் ஏதாவது தவறு செய்ததால் ப்ளாட்ஃபார்ம் உங்களைப் பூட்டவில்லை. நீங்கள் ஸ்திரப்படும்போது உங்கள் டெபாசிட்டுக்கு எதிராக நெட்வொர்க் மேலும் கடனைக் குவிக்காமல் இருக்க அது உங்களைப் பூட்டுகிறது.',
  'agent.lockout.gauge.label': 'பாதுகாப்பான மீட்பு இலக்கு',
  'agent.lockout.gauge.suffix': 'பாதுகாப்பாக செலுத்த',
  'agent.lockout.gauge.recoveredBadge': '{pct}% மீட்கப்பட்டது',
  'agent.lockout.gauge.field.wallet': 'உங்கள் வாலெட்',
  'agent.lockout.gauge.field.deposit': 'உங்கள் டெபாசிட்',
  'agent.lockout.tier.justUnlock.label': 'வெறும் அன்லாக்',
  'agent.lockout.tier.justUnlock.tag': 'ஆபத்தான',
  'agent.lockout.tier.justUnlock.note': 'உங்களை ₹0-க்கு திருப்பி கொண்டுவருகிறது — எந்த புதிய நஷ்டமும் உடனடியாக மீண்டும் பூட்டும்.',
  'agent.lockout.tier.safeMin.label': 'பாதுகாப்பான குறைந்தபட்சம்',
  'agent.lockout.tier.safeMin.tag': 'பரிந்துரைக்கப்படுகிறது',
  'agent.lockout.tier.safeMin.note': 'டெபாசிட் ({deposit}) + உங்கள் எதிர்மறை வாலெட்டின் பாதி. உண்மையான குஷனை விட்டுச் செல்கிறது.',
  'agent.lockout.tier.fullRecovery.label': 'முழு மீட்பு',
  'agent.lockout.tier.fullRecovery.tag': 'சிறந்த',
  'agent.lockout.tier.fullRecovery.note': 'டெபாசிட் முழுமையாக மீட்டமைக்கப்பட்டது மற்றும் வாலெட் பூஜ்யத்திற்குக் கொண்டுவரப்பட்டது.',
  'agent.lockout.waysOut.eyebrow': 'மீட்க இரண்டு வழிகள் — எதைச் செய்தாலும் (அல்லது இரண்டையும்) செய்யுங்கள்',
  'agent.lockout.waysOut.inbox.title': 'உங்கள் இன்பாக்ஸில் நடவடிக்கை எடுக்கவும்',
  'agent.lockout.waysOut.inbox.body': 'பிளேயர் ரீசார்ஜ்களை அங்கீகரிக்கவும் (பணம் ஏற்கனவே உங்களிடம் உள்ளது) மற்றும் பிளேயர் பணம் எடுப்புகளை செலுத்தப்பட்டதாகக் குறிக்கவும். ஒவ்வொரு ஒப்புதலும் உங்கள் வாலெட்டுக்கு வரவு வைக்கிறது.',
  'agent.lockout.waysOut.inbox.ctaOpen': 'இன்பாக்ஸ் திற',
  'agent.lockout.waysOut.inbox.ctaOpened': 'இன்பாக்ஸ் கீழே திறந்துள்ளது',
  'agent.lockout.waysOut.upline.title': 'உங்கள் அப்லைனுக்கு செலுத்தவும்',
  'agent.lockout.waysOut.upline.body': 'உங்கள் பெற்றோர் ஏஜெண்ட்டுக்கு (அல்லது அட்மினுக்கு) UPI மூலம் பணம் அனுப்பவும், பிறகு டிஎக்ஸ்என் ஐடியை சமர்ப்பிக்கவும். அவர்கள் அங்கீகரித்தவுடன், உங்கள் வாலெட் உடனடியாக வரவு வைக்கப்படும்.',
  'agent.lockout.waysOut.upline.ctaOpen': 'படிவம் திற',
  'agent.lockout.waysOut.upline.ctaOpened': 'படிவம் கீழே திறந்துள்ளது',
  'agent.lockout.capabilities.eyebrow': 'நீங்கள் இன்னும் என்ன செய்ய முடியும் (மற்றும் என்ன தடுக்கப்பட்டுள்ளது)',
  'agent.lockout.capabilities.canHeader': 'நீங்கள் இன்னும் செய்ய முடியும்',
  'agent.lockout.capabilities.cantHeader': 'நீங்கள் (தற்காலிகமாக) செய்ய முடியாது',
  'agent.lockout.can.approvePlayerRecharge': 'உள்வரும் பிளேயர் ரீசார்ஜ்களை அங்கீகரித்தல்',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'பிளேயர் பணம் எடுப்புகளை செலுத்தப்பட்டதாகக் குறித்தல்',
  'agent.lockout.can.approveSubAgentRecharge': 'சப்-ஏஜெண்ட் ரீசார்ஜ்களை அங்கீகரித்தல்',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'சப்-ஏஜெண்ட் பணம் எடுப்புகளை செலுத்தப்பட்டதாகக் குறித்தல்',
  'agent.lockout.can.submitToUpline': 'உங்கள் அப்லைனுக்கு ரீசார்ஜை சமர்ப்பித்தல்',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE எச்சரிக்கைகளைப் பெறுதல் + சைன் அவுட்',
  'agent.lockout.section.step': 'படி {n} · {title}',
  'agent.lockout.section.inbox.title': 'உங்கள் இன்பாக்ஸ் — ஒவ்வொரு ஒப்புதலும் உங்கள் வாலெட்டுக்கு வரவு வைக்கிறது',
  'agent.lockout.section.inbox.subtitle': 'பிளேயர்கள் மற்றும் சப்-ஏஜெண்ட்களிடமிருந்து உங்கள் நடவடிக்கைக்காகக் காத்திருக்கும் நிலுவை பேமெண்ட் கோரிக்கைகள்.',
  'agent.lockout.section.inbox.fromPlayers': 'உங்கள் பிளேயர்களிடமிருந்து',
  'agent.lockout.section.inbox.fromSubAgents': 'உங்கள் சப்-ஏஜெண்ட்களிடமிருந்து',
  'agent.lockout.section.upline.title': 'வாலெட்டை டாப்-அப் செய்ய அப்லைனுக்கு செலுத்தவும்',
  'agent.lockout.section.upline.subtitle': 'உங்கள் பெற்றோரின் UPI முன்னால் இருந்தால் விரைவாக நடக்கும். ஒப்புதல் வழக்கமாக சில நிமிடங்களில் கிடைக்கும்.',
  'agent.lockout.queue.player.recharge.title': 'ரீசார்ஜ் · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'டிஎக்ஸ்என் {txn} · {when} சமர்ப்பிக்கப்பட்டது',
  'agent.lockout.queue.player.recharge.subtitleNote': 'டிஎக்ஸ்என் {txn} · {when} சமர்ப்பிக்கப்பட்டது · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'பணம் எடுப்பு · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi}-க்கு செலுத்தவும் · {when} சமர்ப்பிக்கப்பட்டது',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi}-க்கு செலுத்தவும் · {whatsapp} · {when} சமர்ப்பிக்கப்பட்டது',
  'agent.lockout.queue.subAgent.recharge.title': 'சப்-ஏஜெண்ட் ரீசார்ஜ் · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'டிஎக்ஸ்என் {txn} · {when} சமர்ப்பிக்கப்பட்டது',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'டிஎக்ஸ்என் {txn} · {when} சமர்ப்பிக்கப்பட்டது · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'சப்-ஏஜெண்ட் பணம் எடுப்பு · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi}-க்கு செலுத்தவும் · {when} சமர்ப்பிக்கப்பட்டது',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi}-க்கு செலுத்தவும் · {when} சமர்ப்பிக்கப்பட்டது · "{note}"',
  'agent.lockout.queue.action.approve': 'அங்கீகரி',
  'agent.lockout.queue.action.reject': 'நிராகரி',
  'agent.lockout.queue.action.markPaid': 'செலுத்தப்பட்டதாக குறி',
  'agent.lockout.queue.prompt.markPlayerPaid': 'பிளேயருக்கு செலுத்த நீங்கள் பயன்படுத்திய UPI பரிவர்த்தனை ஐடியை உள்ளிடவும்:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'சப்-ஏஜெண்ட்டுக்கு செலுத்த நீங்கள் பயன்படுத்திய UPI பரிவர்த்தனை ஐடியை உள்ளிடவும்:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'இந்த பணம் எடுப்பை நிராகரிக்கவா? வைத்திருந்த தொகை பிளேயருக்கு திரும்பப் பெறப்படும்.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'இந்த சப்-ஏஜெண்ட் பணம் எடுப்பை நிராகரிக்கவா?',
  'agent.lockout.queue.toast.rechargeApproved': 'ரீசார்ஜ் அங்கீகரிக்கப்பட்டது — பிளேயர் வரவு வைக்கப்பட்டார்',
  'agent.lockout.queue.toast.rechargeRejected': 'ரீசார்ஜ் நிராகரிக்கப்பட்டது',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'சப்-ஏஜெண்ட் ரீசார்ஜ் அங்கீகரிக்கப்பட்டது',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'ரீசார்ஜ் நிராகரிக்கப்பட்டது',
  'agent.lockout.queue.toast.withdrawalPaid': 'பணம் எடுப்பு செலுத்தப்பட்டதாக குறிக்கப்பட்டது — உங்கள் வாலெட் வரவு வைக்கப்பட்டது',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'சப்-ஏஜெண்ட் பணம் எடுப்பு செலுத்தப்பட்டதாக குறிக்கப்பட்டது — வாலெட் வரவு வைக்கப்பட்டது',
  'agent.lockout.queue.toast.withdrawalRejected': 'பணம் எடுப்பு நிராகரிக்கப்பட்டது (பிளேயருக்கு திரும்பப் பெறப்பட்டது)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'பணம் எடுப்பு நிராகரிக்கப்பட்டது',
  'agent.lockout.queue.toast.actionFailed': 'நடவடிக்கை தோல்வியடைந்தது',
  'agent.lockout.queue.empty.player': 'இப்போது உங்கள் பிளேயர்களிடமிருந்து எதுவுமில்லை.',
  'agent.lockout.queue.empty.subAgent': 'இப்போது உங்கள் சப்-ஏஜெண்ட்களிடமிருந்து எதுவுமில்லை.',
  'agent.lockout.queue.loading': 'வரிசை ஏற்றப்படுகிறது…',
  'agent.lockout.refresh.cta': 'புதுப்பிக்கவும் — நான் அன்லாக் ஆகிவிட்டேனா?',
  'agent.lockout.refresh.checking': 'சரிபார்க்கிறது…',
  'agent.lockout.refresh.toast': 'நிலை புதுப்பிக்கப்பட்டது',
  'agent.lockout.refresh.footnote': 'உங்கள் வாலெட் + டெபாசிட் பூஜ்யத்திற்கு மேல் வந்த தருணத்தில் இந்தப் பக்கம் தானாக மூடப்படும்.',
  'agent.emergencyForm.error.loadFailed': 'ரீசார்ஜ் விவரங்களை ஏற்ற முடியவில்லை',
  'agent.emergencyForm.banner.locked.title': 'உங்கள் கணக்கை அன்லாக் செய்ய இப்போது செலுத்தவும்',
  'agent.emergencyForm.banner.locked.body': 'உங்கள் டெபாசிட் பிணையம் ஈடுகட்டக்கூடியதைவிட அதிகமாக உங்கள் பிளேயர்கள் வென்றுள்ளனர். உங்கள் எதிர்மறை பேலன்ஸை அழிக்கும் வரை நீங்கள் ப்ளாட்ஃபார்மை பயன்படுத்த முடியாது.',
  'agent.emergencyForm.banner.warning.title': 'விரைவில் டாப்-அப் செய்யுங்கள் — நீங்கள் வரம்புக்கு அருகில் உள்ளீர்கள்',
  'agent.emergencyForm.banner.warning.body': 'உங்கள் வாலெட் டெபாசிட்டின் 80% கடந்துவிட்டது. அது 100% எட்டுவதற்கு முன் டாப்-அப் செய்யவும் — அப்படி எட்டினால், உங்கள் கணக்கு பூட்டப்படும்.',
  'agent.emergencyForm.row.wallet': 'உங்கள் வாலெட்',
  'agent.emergencyForm.row.deposit': 'உங்கள் டெபாசிட்',
  'agent.emergencyForm.tier.eyebrow.locked': 'நீங்கள் ஏன் சிக்கியுள்ளீர்கள்',
  'agent.emergencyForm.tier.eyebrow.warning': 'நீங்கள் ஏன் ஆபத்தில் உள்ளீர்கள்',
  'agent.emergencyForm.tier.context.locked': 'உங்கள் வாலெட் {wallet} சென்றது — அது உங்கள் {deposit} டெபாசிட் பாதுகாப்பு குஷனைத் தாண்டியது. வெற்று பஃபருக்கு எதிராக உங்கள் டவுன்லைனின் நஷ்டங்கள் தொடர்ந்து குவியாமல் இருக்க ப்ளாட்ஃபார்ம் உங்களை இங்கே பூட்டுகிறது.',
  'agent.emergencyForm.tier.context.warning': 'உங்கள் வாலெட் {wallet} சென்றது — அது உங்கள் {deposit} டெபாசிட் பாதுகாப்பு குஷனின் 80%-க்கு மேல். மேலும் ஒரு மோசமான செட்டில்மென்ட் உங்களை 100% தாண்டித் தள்ளி கணக்கைப் பூட்டும்.',
  'agent.emergencyForm.tier.pickHowMuch': 'எவ்வளவு செலுத்த வேண்டும் என்பதைத் தேர்வுசெய்க',
  'agent.emergencyForm.tier.bareMin.label.locked': 'வெறும் அன்லாக்',
  'agent.emergencyForm.tier.bareMin.label.warning': 'எச்சரிக்கையிலிருந்து வெளியேற',
  'agent.emergencyForm.tier.bareMin.tag': 'ஆபத்தான',
  'agent.emergencyForm.tier.bareMin.note.locked': 'உங்கள் வாலெட் + டெபாசிட்டை வெறும் ₹0-க்கு திரும்பக் கொண்டுவருகிறது. எந்த புதிய நஷ்டமும் உடனடியாக மீண்டும் பூட்டும்.',
  'agent.emergencyForm.tier.bareMin.note.warning': 'உங்களை 80% கோட்டுக்கு வெறும் மேல் கொண்டுவருகிறது. எந்த புதிய நஷ்டமும் எச்சரிக்கையை மீண்டும் தூண்டும் — அல்லது உங்களை லாக்அவுட்டிற்குத் தள்ளும்.',
  'agent.emergencyForm.tier.safeMin.label': 'பாதுகாப்பான குறைந்தபட்சம்',
  'agent.emergencyForm.tier.safeMin.tag': 'பரிந்துரைக்கப்படுகிறது',
  'agent.emergencyForm.tier.safeMin.note': 'உங்கள் டெபாசிட் ({deposit}) + உங்கள் எதிர்மறை வாலெட்டின் பாதி ({half}). உங்களுக்கு உண்மையான குஷனை விட்டுச் செல்கிறது.',
  'agent.emergencyForm.tier.full.label': 'முழு மீட்பு',
  'agent.emergencyForm.tier.full.tag': 'சிறந்த',
  'agent.emergencyForm.tier.full.note': 'டெபாசிட் + உங்கள் முழு எதிர்மறை வாலெட் ({neg}). டெபாசிட் முழுமையாக மீட்டமைக்கப்பட்டது மற்றும் வாலெட் பூஜ்யத்திற்குத் திரும்புகிறது.',
  'agent.emergencyForm.parent.label.admin': 'உங்கள் அட்மினுக்கு செலுத்தவும்',
  'agent.emergencyForm.parent.label.parentAgent': 'உங்கள் பெற்றோர் ஏஜெண்ட்டுக்கு செலுத்தவும்',
  'agent.emergencyForm.parent.role.admin': 'ப்ளாட்ஃபார்ம் அட்மின்',
  'agent.emergencyForm.parent.role.parentAgent': 'பெற்றோர் ஏஜெண்ட்',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile': 'உங்கள் பெற்றோரின் பேமெண்ட் விவரங்கள் இன்னும் கோப்பில் இல்லை. தயவுசெய்து ப்ளாட்ஃபார்ம் ஆதரவை நேரடியாகத் தொடர்பு கொள்ளவும்.',
  'agent.emergencyForm.field.amount.label': 'செலுத்தப்பட்ட தொகை (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI பரிவர்த்தனை ஐடி / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'எ.கா. 412312345678',
  'agent.emergencyForm.field.note.label': 'குறிப்பு (விரும்பினால்)',
  'agent.emergencyForm.field.note.placeholder': 'உங்கள் பெற்றோர் தெரிந்து கொள்ள வேண்டிய எதுவும்',
  'agent.emergencyForm.validation.amount': 'நீங்கள் செலுத்திய தொகையை உள்ளிடவும் (குறைந்தபட்சம் ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI பரிவர்த்தனை ஐடி / UTR உள்ளிடவும் (குறைந்தது 6 எழுத்துகள்)',
  'agent.emergencyForm.cta': 'ரீசார்ஜ் கோரிக்கையை சமர்ப்பி',
  'agent.emergencyForm.ctaSubmitting': 'சமர்ப்பிக்கப்படுகிறது…',
  'agent.emergencyForm.success.title': 'ரீசார்ஜ் சமர்ப்பிக்கப்பட்டது',
  'agent.emergencyForm.success.bodyAdmin': 'கோரிக்கை #{id} அட்மின் அதை அங்கீகரிக்க காத்திருக்கிறது. அங்கீகரிக்கப்பட்டவுடன், உங்கள் வாலெட் வரவு வைக்கப்பட்டு உங்கள் கணக்கு தானாக அன்லாக் ஆகும்.',
  'agent.emergencyForm.success.bodyAgent': 'கோரிக்கை #{id} @{username} அதை அங்கீகரிக்க காத்திருக்கிறது. அங்கீகரிக்கப்பட்டவுடன், உங்கள் வாலெட் வரவு வைக்கப்பட்டு உங்கள் கணக்கு தானாக அன்லாக் ஆகும்.',
  'agent.emergencyForm.toast.submitted': 'ரீசார்ஜ் கோரிக்கை #{id} சமர்ப்பிக்கப்பட்டது. ஒப்புதலுக்காக காத்திருக்கிறது.',
  'agent.emergencyForm.toast.cantSave': 'மொழியை சேமிக்க முடியவில்லை',
  'agent.geo.title': 'ஒருமுறை அமைப்பு: உங்கள் இடத்தை உறுதிப்படுத்துங்கள்',
  'agent.geo.subtitle': 'டாஷ்போர்டை பயன்படுத்துவதற்கு முன் தேவையானது.',
  'agent.geo.body.primary': 'பாலி கேம்ஸ் ப்ளாட்ஃபார்ம் புதிய பிளேயர்களை உடல் ரீதியாக அவர்களுக்கு அருகில் உள்ள ஏஜெண்டுக்கு வழிநடத்துகிறது. நீங்கள் உங்கள் சரியான GPS இடத்தைப் பகிரும் வரை, நீங்கள் லொக்கேட்டரில் தோன்றமாட்டீர்கள் மற்றும் ஆர்கானிக் சைன்அப்களிலிருந்து புதிய பிளேயர்கள் பூஜ்யத்தைப் பெறுவீர்கள் — உங்கள் ரெஃபெரல் கோடை நேரடியாக டைப் செய்யும் பிளேயர்களை மட்டுமே நீங்கள் பெறுவீர்கள்.',
  'agent.geo.body.secondary': '"எனது தற்போதைய இடத்தைப் பயன்படுத்து" என்பதை ஒருமுறை தட்டவும். நாங்கள் உங்கள் ஆயத்தொலைவுகளை மட்டுமே பெறுகிறோம், வேறு எதையும் இல்லை.',
  'agent.geo.hint.disabled': 'மேலே "எனது தற்போதைய இடத்தைப் பயன்படுத்து" என்பதை தட்டவும். பின்கோடு அல்லது மேனுவல் ஏரியா தேர்வு மட்டும் போதாது — லொக்கேட்டருக்கு துல்லியமான GPS ஆயத்தொலைவுகள் தேவை.',
  'agent.geo.error.fallback': 'இடத்தை சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
  'agent.geo.cta.save': 'சேமித்து தொடரவும்',
  'agent.geo.cta.saving': 'இடம் சேமிக்கப்படுகிறது…',
  'agent.geo.toast.success': 'இடம் சேமிக்கப்பட்டது — நீங்கள் இப்போது பிளேயர்களைப் பெறலாம்.',
  'agent.wallet.hero.label': 'பூல் பேலன்ஸ்',
  'agent.wallet.hero.helper': 'பிளேயர் ரீசார்ஜ்கள் மற்றும் சப்-ஏஜெண்ட் டாப்-அப்களுக்கு செலவழிக்கக் கிடைக்கிறது. வருமானம் மற்றும் அட்மின் டாப்-அப்கள் இங்கே வந்து சேரும்.',
  'agent.wallet.action.recharge': 'ரீசார்ஜ் கோரு',
  'agent.wallet.action.withdraw': 'பணம் எடுப்பு கோரு',
  'agent.wallet.tab.pending': 'நிலுவையில்',
  'agent.wallet.tab.history': 'வரலாறு',
  'agent.wallet.pending.loading': 'ஏற்றப்படுகிறது…',
  'agent.wallet.pending.empty': 'இப்போது நிலுவையில் உள்ள கோரிக்கைகள் இல்லை.',
  'agent.wallet.pending.recharge.title': 'ரீசார்ஜ் கோரிக்கை · +{amount}',
  'agent.wallet.pending.recharge.meta': 'டிஎக்ஸ்என் {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'பணம் எடுப்பு கோரிக்கை · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi}-க்கு · {when}',
  'agent.wallet.pending.badge': 'நிலுவையில்',
  'agent.wallet.history.loading': 'ஏற்றப்படுகிறது…',
  'agent.wallet.history.empty': 'இன்னும் வாலெட் செயல்பாடு இல்லை.',
  'agent.wallet.history.recharge.titleAccepted': 'ரீசார்ஜ் ஏற்கப்பட்டது · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'ரீசார்ஜ் நிராகரிக்கப்பட்டது · {amount}',
  'agent.wallet.history.recharge.meta': 'டிஎக்ஸ்என் {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'பணம் எடுப்பு செலுத்தப்பட்டது · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'பணம் எடுப்பு நிராகரிக்கப்பட்டது · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi}-க்கு · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'செலுத்தப்பட்டது · {txn}',
  'agent.wallet.state.accepted': 'ஏற்கப்பட்டது',
  'agent.wallet.state.paid': 'செலுத்தப்பட்டது',
  'agent.wallet.state.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.wallet.state.expired': 'காலாவதியானது',
  'agent.wallet.ledger.kind.topupFromAdmin': 'அட்மினிடமிருந்து டாப்-அப்',
  'agent.wallet.ledger.kind.topupToPlayer': 'பிளேயருக்கு டாப்-அப்',
  'agent.wallet.ledger.kind.topupFromPlayer': 'பிளேயர் ரீசார்ஜ்',
  'agent.wallet.ledger.kind.revenueShare': 'வருவாய் பங்கு',
  'agent.wallet.ledger.kind.coFundOffer': 'கோ-ஃபண்ட் ஆஃபர்',
  'agent.wallet.ledger.kind.adminAdjust': 'அட்மின் சரிசெய்தல்',
  'agent.wallet.ledger.kind.withdrawalHold': 'பணம் எடுப்பு ஹோல்ட்',
  'agent.wallet.ledger.kind.withdrawalPaid': 'பணம் எடுப்பு செலுத்தப்பட்டது',
  'agent.wallet.ledger.kind.withdrawalRefund': 'பணம் எடுப்பு திருப்பி',
  'agent.wallet.ledger.balanceAfter': 'பேல் {amount}',
  'agent.deposit.hero.label': 'பாதுகாப்பு டெபாசிட்',
  'agent.deposit.hero.helper': 'உங்கள் பூலுக்கு எதிரான பிணையம். உங்கள் வாலெட் நல்ல நிலையில் உள்ள வரை தொடாமல் இருக்கும்.',
  'agent.deposit.margin.safe': 'பாதுகாப்பான',
  'agent.deposit.margin.warning': 'வார்னிங்',
  'agent.deposit.margin.locked': 'பூட்டப்பட்டது',
  'agent.deposit.action.recharge': 'டெபாசிட் ரீசார்ஜ்',
  'agent.deposit.action.withdraw': 'டெபாசிட் பணம் எடு',
  'agent.deposit.gauge.label': 'கவரேஜ் கேஜ்',
  'agent.deposit.gauge.thresholdWarn': 'எச்சரிக்கை',
  'agent.deposit.gauge.thresholdLock': 'பூட்டு',
  'agent.deposit.gauge.legend.warn': '{pct} இல், நீங்கள் ஒரு வார்னிங் பெறுவீர்கள்',
  'agent.deposit.gauge.legend.lock': '{pct} இல், உங்கள் கணக்கு பூட்டப்படும்',
  'agent.deposit.gauge.caption.locked': 'பூல் முழுமையாக டெபாசிட்டால் ஈடுசெய்யப்பட்டுள்ளது — நீங்கள் டாப்-அப் செய்யும் வரை பணம் எடுப்புகள் தடுக்கப்பட்டுள்ளன.',
  'agent.deposit.gauge.caption.warning': 'பூல் உங்கள் டெபாசிட்டின் {pct}% பயன்படுத்திவிட்டது. லாக்அவுட்டைத் தவிர்க்க 100% எட்டுவதற்கு முன் டாப்-அப் செய்யவும்.',
  'agent.deposit.gauge.caption.caution': 'பூல் டெபாசிட் கவரேஜின் {pct}%-இல் உள்ளது. நீங்கள் இன்னும் 80% எச்சரிக்கைக்குக் கீழே பாதுகாப்பாக உள்ளீர்கள்.',
  'agent.deposit.gauge.caption.safe': 'உங்கள் டெபாசிட் முழுமையாக அப்படியே உள்ளது.',
  'agent.deposit.ledger.loading': 'ஏற்றப்படுகிறது…',
  'agent.deposit.ledger.empty.title': 'இன்னும் டெபாசிட் செயல்பாடு இல்லை',
  'agent.deposit.ledger.empty.body': 'அட்மின் உங்கள் டெபாசிட்டை வரவு வைத்தவுடன் (அல்லது திரும்பப் பெறுதலை செயலாக்கினவுடன்), ஒவ்வொரு பதிவும் இங்கே தோன்றும்.',
  'agent.recharge.banner.eyebrow.wallet': 'உங்கள் வாலெட்டை ரீசார்ஜ் செய்யவும்',
  'agent.recharge.banner.eyebrow.deposit': 'உங்கள் டெபாசிட்டை ரீசார்ஜ் செய்யவும்',
  'agent.recharge.banner.body.wallet': 'முதலில் உங்கள் அப்லைனுக்கு செலுத்துங்கள், பின்னர் உங்கள் ஆபரேட்டிங் வாலெட்டை அவர்கள் டாப்-அப் செய்ய ஆதாரத்தை இங்கே சமர்ப்பிக்கவும்.',
  'agent.recharge.banner.body.deposit': 'முதலில் உங்கள் அப்லைனுக்கு செலுத்துங்கள், பின்னர் உங்கள் பாதுகாப்பு டெபாசிட்டை அவர்கள் டாப்-அப் செய்ய ஆதாரத்தை இங்கே சமர்ப்பிக்கவும்.',
  'agent.recharge.banner.closeAria': 'படிவத்தை மூடு',
  'agent.recharge.parent.label.admin': 'உங்கள் அட்மினுக்கு செலுத்தவும்',
  'agent.recharge.parent.label.parentAgent': 'உங்கள் பெற்றோர் ஏஜெண்ட்டுக்கு செலுத்தவும்',
  'agent.recharge.parent.role.admin': 'ப்ளாட்ஃபார்ம் அட்மின்',
  'agent.recharge.parent.role.parentAgent': 'பெற்றோர் ஏஜெண்ட்',
  'agent.recharge.parent.noQr': 'QR அமைக்கப்படவில்லை',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'உங்கள் அப்லைனின் பேமெண்ட் விவரங்கள் ஏற்றப்படுகின்றன…',
  'agent.recharge.parent.loadError': 'உங்கள் அப்லைன் பேமெண்ட் விவரங்களை ஏற்ற முடியவில்லை',
  'agent.recharge.method.eyebrow': 'எப்படி செலுத்தினீர்கள்?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ஆன்லைன் டிஜிட்டல் பேமெண்ட்',
  'agent.recharge.method.cash.label': 'பணம்',
  'agent.recharge.method.cash.note': 'நேருக்கு நேர் கையளிப்பு',
  'agent.recharge.field.amount.label': 'தொகை (₹) · குறைந்தபட்சம் ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI பரிவர்த்தனை ஐடி / UTR',
  'agent.recharge.field.txn.label.cash': 'பண ரசீது குறிப்பான் (ஏதேனும் குறுகிய குறிப்பு)',
  'agent.recharge.field.txn.placeholder.upi': 'எ.கா. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'எ.கா. CASH-Sun-eve',
  'agent.recharge.field.note.label': 'குறிப்பு (விரும்பினால், உங்கள் அப்லைனுக்கு காட்டப்படும்)',
  'agent.recharge.field.note.placeholder': 'உங்கள் அப்லைனுக்கான எந்த சூழலும்',
  'agent.recharge.error.amountMin': 'தொகை முழு ரூபாயாக ≥ 10 இருக்க வேண்டும்',
  'agent.recharge.error.txnUpi': 'பரிவர்த்தனை ஐடி மிகவும் சிறியதாகத் தோன்றுகிறது — UPI குறிப்பை ஒட்டவும்',
  'agent.recharge.error.txnCash': 'உங்கள் அப்லைன் கையளிப்பை பொருத்த ஒரு குறுகிய குறிப்பானைச் சேர்க்கவும் (எ.கா. "CASH-Sun-eve")',
  'agent.recharge.cta.submit': 'கோரிக்கையை சமர்ப்பி',
  'agent.recharge.cta.cancel': 'ரத்து செய்',
  'agent.withdrawal.banner.eyebrow.wallet': 'வாலெட்டிலிருந்து பணம் எடு',
  'agent.withdrawal.banner.eyebrow.deposit': 'டெபாசிட்டிலிருந்து பணம் எடு',
  'agent.withdrawal.banner.body.wallet': 'தொகை உடனடியாக வைத்திருக்கப்படுகிறது, அதனால் நீங்கள் இரட்டை-செலவு செய்ய முடியாது. உங்கள் அப்லைன் உங்களுக்கு செலுத்தி செலுத்தப்பட்டதாகக் குறிக்கிறார்.',
  'agent.withdrawal.banner.body.deposit': 'தொகை உங்கள் பாதுகாப்பு டெபாசிட்டிலிருந்து உடனடியாக வைத்திருக்கப்படுகிறது. உங்கள் அப்லைன் உங்களுக்கு செலுத்தி அதை செலுத்தப்பட்டதாகக் குறிக்கிறார்.',
  'agent.withdrawal.banner.closeAria': 'படிவத்தை மூடு',
  'agent.withdrawal.method.eyebrow': 'எப்படி பணம் பெற விரும்புகிறீர்கள்?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ஆன்லைன் டிஜிட்டல் பேமெண்ட்',
  'agent.withdrawal.method.cash.label': 'பணம்',
  'agent.withdrawal.method.cash.note': 'நேருக்கு நேர் கையளிப்பு',
  'agent.withdrawal.field.amount.label': 'தொகை (₹) · கிடைக்கும் {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'உங்கள் UPI ஐடி (உங்கள் அப்லைன் உங்களுக்கு செலுத்தும் இடம்)',
  'agent.withdrawal.field.upi.placeholder': 'நீங்கள்@bank',
  'agent.withdrawal.field.cashMarker.label': 'சந்திப்பு குறிப்பான் (எ.கா. "ஞாயிறு மாலை பணம்")',
  'agent.withdrawal.field.cashMarker.placeholder': 'பணத்தை எப்போது/எங்கே கையளிக்க வேண்டும்',
  'agent.withdrawal.field.whatsapp.label': 'WhatsApp (விரும்பினால், உங்கள் அப்லைன் உங்களைத் தொடர்பு கொள்ள)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'குறிப்பு (விரும்பினால்)',
  'agent.withdrawal.field.note.placeholder': 'உங்கள் அப்லைனுக்கான எந்த சூழலும்',
  'agent.withdrawal.error.amountMin': 'தொகை முழு ரூபாயாக ≥ 10 இருக்க வேண்டும்',
  'agent.withdrawal.error.amountOverflow': 'உங்களிடம் {balance} மட்டுமே உள்ளது',
  'agent.withdrawal.error.upi': 'சரியான UPI ஐடியை உள்ளிடவும்',
  'agent.withdrawal.cta.submit': 'கோரிக்கையை சமர்ப்பி',
  'agent.withdrawal.cta.cancel': 'ரத்து செய்',
  'agent.people.hub.players.title': 'பிளேயர்கள்',
  'agent.people.hub.players.subtitle': 'பிளேயர்கள் மற்றும் அவர்களின் கோரிக்கைகளை நிர்வகிக்கவும்',
  'agent.people.hub.players.subtab.requests': 'பிளேயர் கோரிக்கைகள்',
  'agent.people.hub.players.subtab.list': 'பிளேயர்கள்',
  'agent.people.hub.agents.title': 'ஏஜெண்ட்கள்',
  'agent.people.hub.agents.subtitle': 'சப்-ஏஜெண்ட்கள் மற்றும் அவர்களின் கோரிக்கைகளை நிர்வகிக்கவும்',
  'agent.people.hub.agents.subtab.requests': 'ஏஜெண்ட் கோரிக்கைகள்',
  'agent.people.hub.agents.subtab.list': 'ஏஜெண்ட்கள்',
  'agent.people.acceptingToggle.label': 'புதிய பிளேயர்களைப் பெறுதல்',
  'agent.people.acceptingToggle.bodyAccepting': 'நீங்கள் புவியியல் லொக்கேட்டரில் இருந்து புதிய பிளேயர்களை ஏற்றுக்கொள்கிறீர்கள். உங்கள் பகுதியில் கோல்ட் சைன்அப்கள் தானாக உங்களுக்கு வழிநடத்தப்படுகின்றன.',
  'agent.people.acceptingToggle.bodyOptedOut': 'நீங்கள் ஆப்ட் அவுட் செய்துள்ளீர்கள். லொக்கேட்டர் உங்களைத் தவிர்க்கிறது — நேரடி ரெஃபெரல்கள் + மேனுவலாக ஒதுக்கப்பட்ட பிளேயர்கள் மட்டுமே உங்களை அடைவார்கள். ஏற்கனவே உள்ள பிளேயர்களுக்கு பாதிப்பு இல்லை.',
  'agent.people.acceptingToggle.error': 'புதுப்பிக்க முடியவில்லை',
  'agent.people.acceptingToggle.aria.stop': 'புதிய பிளேயர்களை ஏற்க நிறுத்து',
  'agent.people.acceptingToggle.aria.start': 'புதிய பிளேயர்களை ஏற்கத் தொடங்கு',
  'agent.qr.audience.player': 'பிளேயர்கள்',
  'agent.qr.audience.subAgent': 'ஏஜெண்ட்கள்',
  'agent.qr.alt': 'உங்கள் பேமெண்ட் QR ({audience})',
  'agent.qr.cardLabel.player': 'என் QR · பிளேயர்கள் பார்ப்பது',
  'agent.qr.cardLabel.subAgent': 'என் QR · ஏஜெண்ட்கள் பார்ப்பது',
  'agent.qr.upi.empty': 'இன்னும் UPI அமைக்கப்படவில்லை',
  'agent.qr.status.loading': 'ஏற்றப்படுகிறது…',
  'agent.qr.status.live': 'நேரடி',
  'agent.qr.status.notSet': 'அமைக்கப்படவில்லை',
  'agent.qr.status.paused': 'இடைநிறுத்தப்பட்டது',
  'agent.qr.cta.edit': 'QR & UPI திருத்து',
  'agent.qr.cta.setup': 'QR அமை',
  'agent.qr.cta.viewOnly': 'பார்க்க மட்டும் — திருத்த அட்மினைக் கேளுங்கள்',
  'agent.qr.modal.title': 'QR & UPI திருத்து',
  'agent.qr.modal.closeAria': 'மூடு',
  'agent.qr.modal.upload': 'QR அப்லோடு செய்',
  'agent.qr.modal.replace': 'QR மாற்று',
  'agent.qr.modal.remove': 'அகற்று',
  'agent.qr.modal.confirmRemove.player': 'QR படத்தை அகற்றவா? உங்கள் பிளேயர்கள் கான்டாக்ட் ஃபால்பேக்கை மட்டுமே பார்ப்பார்கள்.',
  'agent.qr.modal.confirmRemove.subAgent': 'QR படத்தை அகற்றவா? உங்கள் ஏஜெண்ட்கள் கான்டாக்ட் ஃபால்பேக்கை மட்டுமே பார்ப்பார்கள்.',
  'agent.qr.modal.field.upi.label': 'UPI ஐடி',
  'agent.qr.modal.field.upi.placeholder': 'எ.கா. baaligames@upi',
  'agent.qr.modal.field.whatsapp.label.player': 'WhatsApp எண் (ஃபால்பேக்)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ஏஜெண்ட்களுக்கான WhatsApp எண்',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'காலி → {number}-க்கு ஃபால் பேக் செய்யும்',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'சப்-ஏஜெண்ட்கள் {number} ஐ பார்ப்பார்கள் (உங்கள் பிளேயர்-எதிர்கொள்ளும் எண்).',
  'agent.qr.modal.field.phone.label.player': 'ஆதரவு தொலைபேசி (ஃபால்பேக்)',
  'agent.qr.modal.field.phone.label.subAgent': 'ஏஜெண்ட்களுக்கான ஆதரவு தொலைபேசி',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'காலி → {number}-க்கு ஃபால் பேக் செய்யும்',
  'agent.qr.modal.field.phone.fallbackHint': 'சப்-ஏஜெண்ட்கள் {number} ஐ பார்ப்பார்கள் (உங்கள் பிளேயர்-எதிர்கொள்ளும் எண்).',
  'agent.qr.modal.active.label': 'செயலில்',
  'agent.qr.modal.active.description': 'பிளேயர்கள் ரீசார்ஜ்களை சமர்ப்பிக்கலாம்',
  'agent.qr.modal.active.on': 'ஆன்',
  'agent.qr.modal.active.paused': 'இடைநிறுத்தப்பட்டது',
  'agent.qr.modal.cancel': 'ரத்து செய்',
  'agent.qr.modal.save': 'சேமி',
  'agent.manualRecharge.banner.eyebrow': 'மேனுவல் ரீசார்ஜ்',
  'agent.manualRecharge.banner.body.before': 'உங்கள் பூலிலிருந்து நேரடியாக ஒரு ',
  'agent.manualRecharge.banner.body.target': 'பிளேயருக்கு',
  'agent.manualRecharge.banner.body.after': ' பணம் அனுப்பவும்.',
  'agent.manualRecharge.pool.label': 'உங்கள் பூல்',
  'agent.manualRecharge.pool.dailyCap': 'வரம்பு · {amount}/நாள்',
  'agent.manualRecharge.field.player.label': 'பிளேயருக்கு',
  'agent.manualRecharge.field.player.placeholder': 'ஒரு பிளேயரைத் தேர்வுசெய்க…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · பேல் {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'பிளேயருக்கு',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · தற்போதைய பேல் {balance}',
  'agent.manualRecharge.field.player.notFound': 'உங்கள் சொந்த பட்டியலில் பிளேயர் கண்டுபிடிக்கப்படவில்லை.',
  'agent.manualRecharge.field.amount.label': 'தொகை (₹) — {min} முதல் {max} வரை',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'உங்கள் பூல் பேலன்ஸை மீறுகிறது.',
  'agent.manualRecharge.error.belowMin': 'குறைந்தபட்சத்திற்கும் குறைவு ({min}).',
  'agent.manualRecharge.error.aboveMax': 'அதிகபட்சத்திற்கும் மேல் ({max}).',
  'agent.manualRecharge.field.note.label': 'குறிப்பு (விரும்பினால், பிளேயர் லெட்ஜரில் காட்டப்படும்)',
  'agent.manualRecharge.field.note.placeholder': 'எ.கா. கடையில் சேகரிக்கப்பட்ட பணம்',
  'agent.manualRecharge.loading': 'உங்கள் பூல் ஏற்றப்படுகிறது…',
  'agent.manualRecharge.cta.cancel': 'ரத்து செய்',
  'agent.manualRecharge.cta.send': '{amount} அனுப்பு',
  'agent.manualRecharge.success.title': '@{username}-க்கு {amount} அனுப்பப்பட்டது',
  'agent.manualRecharge.success.meta': 'உங்கள் பூல் · {pool} · அவர்களின் வாலெட் · {playerBal}',
  'agent.manualRecharge.success.again': 'மற்றொன்று அனுப்பு',
  'agent.manualSubAgentRecharge.banner.eyebrow': 'மேனுவல் ரீசார்ஜ்',
  'agent.manualSubAgentRecharge.banner.body.before': 'உங்கள் பூலிலிருந்து நேரடியாக ஒரு ',
  'agent.manualSubAgentRecharge.banner.body.target': 'சப்-ஏஜெண்ட்டுக்கு',
  'agent.manualSubAgentRecharge.banner.body.after': ' பணம் அனுப்பவும்.',
  'agent.manualSubAgentRecharge.pool.label': 'உங்கள் பூல்',
  'agent.manualSubAgentRecharge.field.subAgent.label': 'சப்-ஏஜெண்ட்டுக்கு',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'ஒரு சப்-ஏஜெண்ட்டைத் தேர்வுசெய்க…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · பூல் {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'செயலில் உள்ள நேரடி சப்-ஏஜெண்ட்கள் இல்லை',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'சப்-ஏஜெண்ட்டுக்கு',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · தற்போதைய பூல் {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'உங்கள் நேரடி டவுன்லைனில் சப்-ஏஜெண்ட் கண்டுபிடிக்கப்படவில்லை.',
  'agent.manualSubAgentRecharge.field.amount.label': 'தொகை (₹) — {min} முதல் {max} வரை',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'உங்கள் பூல் பேலன்ஸை மீறுகிறது.',
  'agent.manualSubAgentRecharge.error.belowMin': 'குறைந்தபட்சத்திற்கும் குறைவு ({min}).',
  'agent.manualSubAgentRecharge.field.note.label': 'குறிப்பு (விரும்பினால், இரு லெட்ஜர்களிலும் காட்டப்படும்)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'எ.கா. ஃப்ளோட் முன்பணம்',
  'agent.manualSubAgentRecharge.loading': 'உங்கள் பூல் ஏற்றப்படுகிறது…',
  'agent.manualSubAgentRecharge.cta.cancel': 'ரத்து செய்',
  'agent.manualSubAgentRecharge.cta.send': '{amount} அனுப்பு',
  'agent.manualSubAgentRecharge.success.title': '@{username}-க்கு {amount} அனுப்பப்பட்டது',
  'agent.manualSubAgentRecharge.success.meta': 'உங்கள் பூல் · {parent} · அவர்களின் பூல் · {child}',
  'agent.manualSubAgentRecharge.success.again': 'மற்றொன்று அனுப்பு',
  'agent.playerRequests.direction.recharge': 'ரீசார்ஜ்',
  'agent.playerRequests.direction.withdrawal': 'பணம் எடுப்பு',
  'agent.playerRequests.tab.pending': 'நிலுவையில்',
  'agent.playerRequests.tab.history': 'வரலாறு',
  'agent.playerRequests.loading': 'ஏற்றப்படுகிறது…',
  'agent.playerRequests.empty.pending.recharge': 'இப்போது நிலுவையில் ரீசார்ஜ்கள் இல்லை.',
  'agent.playerRequests.empty.pending.withdrawal': 'இப்போது நிலுவையில் பணம் எடுப்புகள் இல்லை.',
  'agent.playerRequests.empty.history': 'இன்னும் வரலாறு இல்லை.',
  'agent.playerRequests.prompt.rejectRecharge': 'நிராகரிப்பு காரணம் (விரும்பினால்):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'நிராகரிப்பு காரணம் (பிளேயரின் வைத்திருக்கப்பட்ட பேலன்ஸ் திருப்பித் தரப்படும்):',
  'agent.playerRequests.toast.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.playerRequests.toast.rejectedRefunded': 'நிராகரிக்கப்பட்டது · வைத்திருந்த பேலன்ஸ் திருப்பப்பட்டது',
  'agent.playerRequests.toast.approved': '{amount} அங்கீகரிக்கப்பட்டது',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} அங்கீகரிக்கப்பட்டது · அசல் + ₹{bonus} போனஸ்',
  'agent.playerRequests.toast.markedPaid': '{amount} செலுத்தப்பட்டதாக குறிக்கப்பட்டது',
  'agent.playerRequests.card.upiTxnLabel': 'UPI டிஎக்ஸ்என் ஐடி',
  'agent.playerRequests.card.payToUpiLabel': 'செலுத்த வேண்டிய இடம் (UPI)',
  'agent.playerRequests.card.whatsapp': 'WhatsApp',
  'agent.playerRequests.card.viewQr': 'QR பார்',
  'agent.playerRequests.card.bonusChip': 'போனஸ்',
  'agent.playerRequests.card.partialApproved': 'கோரப்பட்ட {requested}-இல் {approved} அங்கீகரிக்கப்பட்டது',
  'agent.playerRequests.card.partialPaid': 'கோரப்பட்ட {requested}-இல் {approved} செலுத்தப்பட்டது · {refund} திருப்பப்பட்டது',
  'agent.playerRequests.card.paidTxnPrefix': 'செலுத்தப்பட்டது · {txn}',
  'agent.playerRequests.action.reject': 'நிராகரி',
  'agent.playerRequests.action.approve': 'அங்கீகரி',
  'agent.playerRequests.action.markPaid': 'செலுத்தப்பட்டதாக குறி',
  'agent.playerRequests.state.pending': 'நிலுவையில்',
  'agent.playerRequests.state.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.playerRequests.state.expired': 'காலாவதியானது',
  'agent.playerRequests.state.accepted': 'ஏற்கப்பட்டது',
  'agent.playerRequests.state.manuallyPaid': 'மேனுவலாக செலுத்தப்பட்டது',
  'agent.peopleQueue.pagination': 'மொத்தம் {total}-இல் {from}–{to}',
  'agent.agentRequests.direction.recharge': 'ரீசார்ஜ்',
  'agent.agentRequests.direction.withdrawal': 'பணம் எடுப்பு',
  'agent.agentRequests.tab.pending': 'நிலுவையில்',
  'agent.agentRequests.tab.history': 'வரலாறு',
  'agent.agentRequests.loading': 'ஏற்றப்படுகிறது…',
  'agent.agentRequests.empty.pending.recharge': 'இப்போது நிலுவையில் ரீசார்ஜ்கள் இல்லை.',
  'agent.agentRequests.empty.pending.withdrawal': 'இப்போது நிலுவையில் பணம் எடுப்புகள் இல்லை.',
  'agent.agentRequests.empty.history': 'இன்னும் வரலாறு இல்லை.',
  'agent.agentRequests.prompt.rejectRecharge': 'நிராகரிப்பு காரணம் (விரும்பினால்):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'நிராகரிப்பு காரணம் (சப்-ஏஜெண்ட்டின் வைத்திருக்கப்பட்ட பேலன்ஸ் திருப்பித் தரப்படும்):',
  'agent.agentRequests.toast.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.agentRequests.toast.rejectedRefunded': 'நிராகரிக்கப்பட்டது · வைத்திருந்த பேலன்ஸ் திருப்பப்பட்டது',
  'agent.agentRequests.toast.approved': '{amount} அங்கீகரிக்கப்பட்டது · பூல் வரவு வைக்கப்பட்டது',
  'agent.agentRequests.toast.markedPaid': '{amount} செலுத்தப்பட்டதாக குறிக்கப்பட்டது',
  'agent.agentRequests.card.agentPill': 'ஏஜெண்ட்',
  'agent.agentRequests.card.upiTxnLabel': 'UPI டிஎக்ஸ்என் ஐடி',
  'agent.agentRequests.card.cashMarkerLabel': 'பண குறிப்பான்',
  'agent.agentRequests.card.payToUpiLabel': 'செலுத்த வேண்டிய இடம் (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'கையளிப்பு குறிப்பான்',
  'agent.agentRequests.card.whatsapp': 'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved': 'கோரப்பட்ட {requested}-இல் {approved} அங்கீகரிக்கப்பட்டது',
  'agent.agentRequests.card.partialPaid': 'கோரப்பட்ட {requested}-இல் {approved} செலுத்தப்பட்டது · {refund} திருப்பப்பட்டது',
  'agent.agentRequests.card.paidTxnPrefix': 'செலுத்தப்பட்டது · {txn}',
  'agent.agentRequests.card.chip.deposit': 'டெபாசிட்',
  'agent.agentRequests.card.chip.cash': 'பணம்',
  'agent.agentRequests.card.chip.upi': 'UPI',
  'agent.agentRequests.action.reject': 'நிராகரி',
  'agent.agentRequests.action.approve': 'அங்கீகரி',
  'agent.agentRequests.action.markPaid': 'செலுத்தப்பட்டதாக குறி',
  'agent.agentRequests.state.pending': 'நிலுவையில்',
  'agent.agentRequests.state.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.agentRequests.state.expired': 'காலாவதியானது',
  'agent.agentRequests.state.accepted': 'ஏற்கப்பட்டது',
  'agent.agentRequests.state.manuallyPaid': 'மேனுவலாக செலுத்தப்பட்டது',
  'agent.playersList.add': 'பிளேயர் சேர்',
  'agent.playersList.loading': 'ஏற்றப்படுகிறது…',
  'agent.playersList.empty': 'பிளேயர்கள் பொருந்தவில்லை',
  'agent.playersList.results': 'மொத்தம் {total}-இல் {from}–{to} காட்டப்படுகிறது',
  'agent.playersList.searchPlaceholder': 'பயனர்பெயர், மின்னஞ்சல், தொலைபேசி தேடு…',
  'agent.playersList.filter.all': 'அனைத்தும்',
  'agent.playersList.filter.active': 'செயலில்',
  'agent.playersList.filter.pending': 'நிலுவையில்',
  'agent.playersList.filter.suspended': 'நிறுத்தப்பட்டது',
  'agent.playersList.filter.banned': 'தடைசெய்யப்பட்டது',
  'agent.playersList.empty.title': 'பிளேயர்கள் கண்டுபிடிக்கப்படவில்லை',
  'agent.playersList.empty.body': 'வேறு தேடல் அல்லது நிலை வடிகட்டியை முயற்சிக்கவும்.',
  'agent.playersList.pagination': 'பக்கம் {page} / {totalPages}',
  'agent.playersList.card.status.active': 'செயலில்',
  'agent.playersList.card.status.pending': 'நிலுவையில்',
  'agent.playersList.card.status.suspended': 'நிறுத்தப்பட்டது',
  'agent.playersList.card.status.banned': 'தடைசெய்யப்பட்டது',
  'agent.playersList.card.verifiedBadge': 'சரிபார்க்கப்பட்டது',
  'agent.playersList.column.wallet': 'வாலெட்',
  'agent.playersList.column.joined': 'சேர்ந்தது',
  'agent.subAgentsList.intro': 'நீங்கள் உருவாக்கும் ஏஜெண்ட்கள் உங்கள் கீழ் வேலை செய்கிறார்கள். அவர்கள் ஒவ்வொரு பிலரிலும் 0% இல் தொடங்குகிறார்கள் — அவர்களின் விவர பக்கத்திலிருந்து ஒரு கேமுக்கு ரேட்களை வழங்குங்கள்.',
  'agent.subAgentsList.cta.new': 'புதிய சப்-ஏஜெண்ட்',
  'agent.subAgentsList.loading': 'ஏற்றப்படுகிறது…',
  'agent.subAgentsList.empty.title': 'இன்னும் சப்-ஏஜெண்ட்கள் இல்லை',
  'agent.subAgentsList.empty.body.canCreate': 'ஒன்றை உருவாக்க "+ புதிய சப்-ஏஜெண்ட்" தட்டவும். அவர்கள் தங்கள் சொந்த பூல், பிளேயர்கள் மற்றும் வருமானத்துடன் இங்கே தோன்றுவார்கள் — உங்களால் நிர்வகிக்கப்படுவார்கள்.',
  'agent.subAgentsList.empty.body.cantCreate': 'உங்கள் அட்மின் உங்களுக்கு சப்-ஏஜெண்ட் உருவாக்கும் உரிமைகளை இன்னும் வழங்கவில்லை. உங்கள் அனுமதிகளில் create_subagent-ஐ "edit" என மாற்றும்படி அவர்களைக் கேளுங்கள்.',
  'agent.subAgentsList.card.suspendedBadge': 'நிறுத்தப்பட்டது',
  'agent.subAgentsList.column.agents': 'ஏஜெண்ட்கள்',
  'agent.subAgentsList.column.players': 'பிளேயர்கள்',
  'agent.subAgentsList.column.pool': 'பூல்',
  'agent.subAgentsList.row.directLabel': 'நேரடி',
  'agent.subAgentsList.row.indirectLabel': 'மறைமுக',
  'agent.subAgentsList.modal.title': 'புதிய சப்-ஏஜெண்ட்',
  'agent.subAgentsList.modal.body': 'அவர்கள் ஒவ்வொரு பிலரிலும் 0%-இல் தொடங்குவார்கள். உருவாக்கிய பிறகு அவர்களின் விவர பக்கத்திலிருந்து ஒரு கேமுக்கு அவர்களின் ரேட்களை வழங்குங்கள்.',
  'agent.subAgentsList.modal.field.username.label': 'பயனர்பெயர்',
  'agent.subAgentsList.modal.field.username.placeholder': 'எழுத்து/எண், ._- அனுமதிக்கப்படுகிறது; குறைந்தபட்சம் 3 எழுத்துகள்',
  'agent.subAgentsList.modal.field.email.label': 'மின்னஞ்சல்',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'ஆரம்ப கடவுச்சொல்',
  'agent.subAgentsList.modal.field.password.placeholder': 'குறைந்தபட்சம் 8 எழுத்துகள்',
  'agent.subAgentsList.modal.field.password.hint': 'புதிய ஏஜெண்ட் உள்நுழைய இதை அவருடன் பகிரவும். முதல் உள்நுழைவில் அதை மாற்ற வேண்டும்.',
  'agent.subAgentsList.modal.field.location.label': 'சப்-ஏஜெண்ட்டின் இடம்',
  'agent.subAgentsList.modal.field.location.optional': '(வலுவாக பரிந்துரைக்கப்படுகிறது)',
  'agent.subAgentsList.modal.field.location.missing': 'இடம் இல்லாமல், புவியியல் லொக்கேட்டர் இந்த சப்-ஏஜெண்ட்டுக்கு எந்த புதிய பிளேயர்களையும் வழிநடத்த முடியாது. அவர்கள் உள்நுழைந்து தங்கள் சொந்த GPS-ஐ முடிக்கும் வரை நேரடி ரெஃபெரல் மூலம் மட்டுமே பிளேயர்களைப் பெறுவார்கள்.',
  'agent.subAgentsList.modal.cta.cancel': 'ரத்து செய்',
  'agent.subAgentsList.modal.cta.create': 'சப்-ஏஜெண்ட் உருவாக்கு',
  'agent.subAgentsList.modal.cta.creating': 'உருவாக்கப்படுகிறது…',
  'agent.approvalEdit.title.recharge': '{who}-இடமிருந்து ரீசார்ஜை அங்கீகரி',
  'agent.approvalEdit.title.withdrawal': '{who}-க்கான பணம் எடுப்பை செலுத்தப்பட்டதாக குறி',
  'agent.approvalEdit.requestedLabel': 'கோரப்பட்டது:',
  'agent.approvalEdit.field.amount.label': 'அங்கீகரிக்கும் தொகை (₹)',
  'agent.approvalEdit.field.amount.hint': 'கோரப்பட்ட {amount}-ஐ மீற முடியாது.',
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} {who}-இன் வாலெட்டுக்கு தானாக திருப்பப்படும்.',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'பகுதி ஒப்புதல். இணைக்கப்பட்ட எந்த போனஸும் தானாக நிராகரிக்கப்படும்.',
  'agent.approvalEdit.field.reason.label': 'காரணம்',
  'agent.approvalEdit.field.reason.requiredHint': '(தேவை, ≥5 எழுத்துகள்)',
  'agent.approvalEdit.field.reason.optionalHint': '(விரும்பினால்)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'எ.கா. பிளேயர் 30 ரூபாய் மட்டுமே செலுத்தினார்',
  'agent.approvalEdit.field.reason.placeholder.optional': 'விருப்ப குறிப்பு',
  'agent.approvalEdit.field.txn.label': 'UPI பரிவர்த்தனை ஐடி',
  'agent.approvalEdit.field.txn.placeholder': 'எ.கா. 412312345678',
  'agent.approvalEdit.error.insufficient': 'உங்கள் பூல் ({balance}) இந்த ஒப்புதலை ஈடுசெய்ய முடியாது. முதலில் டாப்-அப் செய்யவும்.',
  'agent.approvalEdit.error.amountMin': 'அங்கீகரிக்கப்பட்ட தொகை குறைந்தபட்சம் ₹1 இருக்க வேண்டும்',
  'agent.approvalEdit.error.amountMax': 'அங்கீகரிக்கப்பட்ட தொகை கோரப்பட்ட {amount}-ஐ மீற முடியாது',
  'agent.approvalEdit.error.reasonRequired': 'வேறு தொகையை அங்கீகரிக்கும்போது காரணம் (≥5 எழுத்துகள்) தேவை',
  'agent.approvalEdit.error.txnRequired': 'UPI பரிவர்த்தனை ஐடி தேவை (≥6 எழுத்துகள்)',
  'agent.approvalEdit.error.fallback': 'தோல்வியடைந்தது — மீண்டும் முயற்சிக்கவும்',
  'agent.approvalEdit.cta.cancel': 'ரத்து செய்',
  'agent.approvalEdit.cta.approve': 'அங்கீகரி',
  'agent.approvalEdit.cta.markPaid': 'செலுத்தப்பட்டதாக குறி',
  'agent.approvalEdit.cta.submitting': 'சமர்ப்பிக்கப்படுகிறது…',
  'agent.profile.hub.title': 'என் கணக்கு',
  'agent.profile.hub.subtitle': 'அடையாளம், வாலெட் மற்றும் டெபாசிட் ஒரே இடத்தில்',
  'agent.profile.hub.tab.wallet': 'வாலெட்',
  'agent.profile.hub.tab.deposit': 'டெபாசிட்',
  'agent.profile.hub.tab.account': 'கணக்கு',
  'agent.profile.signOut.cta': 'சைன் அவுட்',
  'agent.profile.signOut.modal.title': 'சைன் அவுட் செய்யவா?',
  'agent.profile.signOut.modal.body': 'உங்கள் கணக்கை அணுக நீங்கள் மீண்டும் உள்நுழைய வேண்டும்.',
  'agent.profile.signOut.modal.stay': 'உள்நுழைந்திருக்கவும்',
  'agent.profile.signOut.modal.confirm': 'ஆம், சைன் அவுட் செய்',
  'agent.profile.signOut.modal.busy': 'சைன் அவுட் செய்யப்படுகிறது…',
  'agent.profile.signOut.error': 'சைன் அவுட் செய்ய முடியவில்லை',
  'agent.account.notSignedIn': 'உள்நுழையவில்லை.',
  'agent.account.hero.role.agent': 'ஏஜெண்ட்',
  'agent.account.hero.role.admin': 'ப்ளாட்ஃபார்ம் அட்மின்',
  'agent.account.hero.changePhoto': 'புகைப்படத்தை மாற்று',
  'agent.account.hero.removePhoto': 'புகைப்படத்தை அகற்று',
  'agent.account.avatar.cropTitle': 'உங்கள் புகைப்படத்தை சரிசெய்யவும்',
  'agent.account.avatar.confirmRemove': 'உங்கள் சுயவிவர புகைப்படத்தை அகற்றவா?',
  'agent.account.toast.photoUpdated': 'புகைப்படம் புதுப்பிக்கப்பட்டது',
  'agent.account.toast.photoRemoved': 'புகைப்படம் அகற்றப்பட்டது',
  'agent.account.toast.saved': 'சேமிக்கப்பட்டது',
  'agent.account.toast.locationSaved': 'இடம் சேமிக்கப்பட்டது',
  'agent.account.toast.passwordChanged': 'கடவுச்சொல் மாற்றப்பட்டது — மீண்டும் உள்நுழைக',
  'agent.account.error.uploadFailed': 'அப்லோடு தோல்வியடைந்தது',
  'agent.account.error.removeFailed': 'தோல்வியடைந்தது',
  'agent.account.error.saveFailed': 'சேமிப்பு தோல்வியடைந்தது',
  'agent.account.error.changeFailed': 'மாற்றம் தோல்வியடைந்தது',
  'agent.account.info.title': 'கணக்கு தகவல்',
  'agent.account.info.subtitle': 'படிக்க மட்டுமே ப்ளாட்ஃபார்ம் மெட்டாடேட்டா',
  'agent.account.info.memberSince': 'உறுப்பினராக சேர்ந்தது',
  'agent.account.info.lastLogin': 'கடைசி உள்நுழைவு',
  'agent.account.info.lastLoginIp': 'கடைசி உள்நுழைவு IP',
  'agent.account.info.reportsTo': 'அறிக்கை செய்யப்படும் இடம்',
  'agent.account.info.reportsToValue': 'அட்மின் #{id}',
  'agent.account.info.agentId': 'ஏஜெண்ட் ஐடி',
  'agent.account.info.firstLogin': 'முதல் முறை',
  'agent.account.info.emptyDash': '—',
  'agent.account.personal.title': 'தனிப்பட்ட விவரங்கள்',
  'agent.account.personal.subtitle.locked': 'அட்மினால் அடையாளம் பூட்டப்பட்டது · பார்க்க மட்டுமே',
  'agent.account.personal.subtitle.agent': 'பயனர்பெயர், மின்னஞ்சல் மற்றும் தொடர்பு எண்',
  'agent.account.personal.subtitle.admin': 'பயனர்பெயர் மற்றும் மின்னஞ்சல்',
  'agent.account.personal.field.username': 'பயனர்பெயர்',
  'agent.account.personal.field.email': 'மின்னஞ்சல்',
  'agent.account.personal.field.phone': 'தொடர்பு எண்',
  'agent.account.personal.field.phone.placeholder': 'எ.கா. +91 9876543210',
  'agent.account.personal.field.phone.hint': 'பிளேயர்கள் மற்றும் அட்மின் இந்த எண்ணில் உங்களை அடைகிறார்கள். அழிக்க காலியாக விடவும்.',
  'agent.account.personal.fieldLockedNote': 'பூட்டப்பட்டது',
  'agent.account.personal.cta.save': 'மாற்றங்களை சேமி',
  'agent.account.personal.cta.saving': 'சேமிக்கப்படுகிறது…',
  'agent.account.personal.cta.noChanges': 'மாற்றங்கள் இல்லை',
  'agent.account.location.title': 'உங்கள் இடம்',
  'agent.account.location.subtitle.summary': 'லொக்கேட்டர் புதிய பிளேயர்களை உங்களுக்கு அருகிலுள்ள பகுதிக்கு வழிநடத்துகிறது',
  'agent.account.location.subtitle.edit': 'லொக்கேட்டர் சரியான பிளேயர்களை உங்களுக்கு வழிநடத்த உங்கள் பகுதியைப் புதுப்பிக்கவும்',
  'agent.account.location.cta.edit': 'திருத்து',
  'agent.account.location.cta.cancel': 'ரத்து செய்',
  'agent.account.location.cta.save': 'சேமி',
  'agent.account.location.areaLabel': 'பகுதி',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'இடம் அமைக்கப்பட்டது',
  'agent.account.location.setBody': 'GPS ஆயத்தொலைவுகள் கோப்பில் உள்ளன — பகுதி விவரங்களைப் பார்க்க திருத்தத் தட்டவும்.',
  'agent.account.location.lastUpdated': 'கடைசியாக புதுப்பிக்கப்பட்டது · {date}',
  'agent.account.security.title': 'பாதுகாப்பு',
  'agent.account.security.subtitle.locked': 'அட்மினால் பூட்டப்பட்டது · பார்க்க மட்டுமே',
  'agent.account.security.subtitle.unlocked': 'உங்கள் கடவுச்சொல்லை மாற்றவும் · மற்ற சாதனங்களை சைன் அவுட் செய்கிறது',
  'agent.account.security.field.current': 'தற்போதைய கடவுச்சொல்',
  'agent.account.security.field.new': 'புதிய கடவுச்சொல்',
  'agent.account.security.field.new.hint': 'குறைந்தபட்சம் 8 எழுத்துகள், ஒரு எழுத்து மற்றும் ஒரு இலக்கத்துடன்',
  'agent.account.security.field.confirm': 'புதியதை உறுதிப்படுத்தவும்',
  'agent.account.security.cta.update': 'கடவுச்சொல் புதுப்பி',
  'agent.account.security.cta.updating': 'புதுப்பிக்கப்படுகிறது…',
  'agent.account.security.error.mismatch': 'புதிய கடவுச்சொற்கள் பொருந்தவில்லை',
  'agent.account.security.error.tooShort': 'கடவுச்சொல் ≥ 8 எழுத்துகள் இருக்க வேண்டும்',
  'agent.account.security.error.weak': 'கடவுச்சொல் குறைந்தபட்சம் ஒரு எழுத்து மற்றும் ஒரு எண்ணைக் கொண்டிருக்க வேண்டும்',
  'agent.shareableId.eyebrow': 'பகிரக்கூடிய ஏஜெண்ட் ஐடி',
  'agent.shareableId.tooltip.copy': 'UAID-ஐ நகலெடு',
  'agent.shareableId.tooltip.copied': 'UAID நகலெடுக்கப்பட்டது',
  'agent.shareableId.cta.copy': 'நகலெடு',
  'agent.shareableId.cta.copied': 'நகலெடுக்கப்பட்டது!',
  'agent.shareableId.body.before': 'மற்றொரு ஏஜெண்ட் உங்களுக்கு பிளேயர்களை மாற்ற முடியும் என்பதற்காக இந்த குறியீட்டை அவருடன் பகிர்ந்து கொள்ளவும். அரட்டை அல்லது WhatsApp வழியாக அனுப்புவது பாதுகாப்பானது — அதைப் பகிர்வது உங்கள் கணக்குக்கு எந்த அணுகலையும் ',
  'agent.shareableId.body.not': 'வழங்காது',
  'agent.shareableId.body.after': '.',
  'agent.rewards.hub.title': 'வெகுமதிகள்',
  'agent.rewards.hub.subtitle': 'ஆஃபர்களை உலாவிப் பார்க்கவும் மற்றும் உங்கள் எக்ஸ்போஷரைக் கண்காணிக்கவும்',
  'agent.rewards.hub.tab.browse': 'ஆஃபர்களை உலாவு',
  'agent.rewards.hub.tab.exposure': 'என் எக்ஸ்போஷர்',
  'agent.rewards.status.optedIn': 'ஆப்ட் இன்',
  'agent.rewards.status.optedOut': 'ஆப்ட் அவுட்',
  'agent.rewards.browse.hero.eyebrow': 'உங்கள் பிளேயர்களுக்காக சிறப்பிக்கப்பட்டது',
  'agent.rewards.browse.empty.title': 'இப்போது செயலில் உள்ள வெகுமதிகள் இல்லை',
  'agent.rewards.browse.empty.body': 'அட்மின் தொடர்ந்து புதிய ஆஃபர்களை இடுகிறார். விரைவில் மீண்டும் சரிபார்க்கவும் — அல்லது நீங்கள் தவறவிட்ட எதிலும் உங்களை ஆப்ட் இன் செய்ய உங்கள் அப்லைனைக் கேட்கவும்.',
  'agent.rewards.browse.error.loadFailed': 'வெகுமதிகளை ஏற்ற முடியவில்லை',
  'agent.rewards.browse.card.aria.manage': '{title}-க்கான ஆப்ட்-இன்னை நிர்வகி',
  'agent.rewards.browse.chip.optedIn': 'ஆப்ட் இன்',
  'agent.rewards.browse.chip.optInPrompt': 'ஆப்ட் இன் செய்ய தட்டவும்',
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',
  'agent.rewards.exposure.loading': 'உங்கள் எக்ஸ்போஷர் கணக்கிடப்படுகிறது…',
  'agent.rewards.exposure.error.loadFailed': 'எக்ஸ்போஷரை ஏற்ற முடியவில்லை',
  'agent.rewards.exposure.hero.eyebrow': 'ஆஃபர் எக்ஸ்போஷர்',
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} செயலில் உள்ள ஆப்ட்-இன்',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} செயலில் உள்ள ஆப்ட்-இன்கள்',
  'agent.rewards.exposure.hero.actualSpend.label': 'உண்மையான செலவு',
  'agent.rewards.exposure.hero.actualSpend.helper': 'இதுவரை உங்கள் டவுன்லைனால் கிளெய்ம் செய்யப்பட்டது.',
  'agent.rewards.exposure.hero.maxExposure.label': 'அதிகபட்ச எக்ஸ்போஷர்',
  'agent.rewards.exposure.hero.maxExposure.helper': 'மோசமான-வழக்கு வரம்பு — மீதமுள்ள ஒவ்வொரு பட்ஜெட் ரூபாயும் கிளெய்ம் செய்யப்பட்டது.',
  'agent.rewards.exposure.hero.ceiling.label': 'பயன்படுத்தப்பட்ட வரம்பு',
  'agent.rewards.exposure.gauge.label': 'ஆபத்து கேஜ்',
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} அதிகபட்சம்',
  'agent.rewards.exposure.gauge.legend.amber.before': '',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': '-இல், உங்கள் ஆப்ட்-இன்களைக் கவனியுங்கள்',
  'agent.rewards.exposure.gauge.legend.rose.before': '',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': '-இல், நீங்கள் உங்கள் வரம்புக்கு அருகில்',
  'agent.rewards.exposure.gauge.caption.noMax': 'நீங்கள் இன்னும் முன்னிலை வரம்பு கொண்ட எதிலும் ஆப்ட் இன் செய்யவில்லை.',
  'agent.rewards.exposure.gauge.caption.high': 'நீங்கள் உங்கள் அதிகபட்ச எக்ஸ்போஷரின் {pct}% பயன்படுத்திவிட்டீர்கள். பெரும்பாலான ஆப்ட்-இன்கள் அதிகமாகக் கிளெய்ம் செய்யப்பட்டுள்ளன.',
  'agent.rewards.exposure.gauge.caption.mid': 'நீங்கள் உங்கள் அதிகபட்ச வரம்பின் {pct}%-இல் உள்ளீர்கள். ஒவ்வொரு ஆஃபருக்கான பிரிவைக் கண்காணியுங்கள்.',
  'agent.rewards.exposure.gauge.caption.low': 'வசதியான — உங்கள் அதிகபட்ச வரம்பின் {pct}% மட்டுமே இதுவரை பயன்படுத்தப்பட்டுள்ளது.',
  'agent.rewards.exposure.footnote.heading': 'அதிகபட்ச எக்ஸ்போஷர் எவ்வாறு கணக்கிடப்படுகிறது:',
  'agent.rewards.exposure.footnote.prefix': ' ஒவ்வொரு ஆப்ட்-இன் ஆஃபருக்கு: ',
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — பழமைவாத மேல் வரம்பு. உங்கள் உண்மையான பொறுப்பு பெரும்பாலும் ',
  'agent.rewards.exposure.footnote.emphasis': 'குறைவாக',
  'agent.rewards.exposure.footnote.suffix2': ' இருக்கும் — உங்களுக்குக் கீழே உள்ள சப்-ஏஜெண்ட்களும் ஆப்ட் இன் செய்யும்போது (அவர்கள் பெரிய பகுதியை செலுத்துகிறார்கள்; நீங்கள் மீதமுள்ள தொகையை செலுத்துகிறீர்கள்).',
  'agent.rewards.exposure.empty.title': 'செயலில் உள்ள ஆப்ட்-இன்கள் இல்லை',
  'agent.rewards.exposure.empty.body': 'ஆஃபர்களை உலாவு தாவலிலிருந்து நீங்கள் ஒரு ஆஃபரில் ஆப்ட் இன் செய்தவுடன், அது உங்கள் செலவு மற்றும் முன்னிலை எக்ஸ்போஷருடன் இங்கே தோன்றும்.',
  'agent.rewards.exposure.row.share': '{pct}% பங்கு',
  'agent.rewards.exposure.row.claims.one': '{count} கிளெய்ம்',
  'agent.rewards.exposure.row.claims.other': '{count} கிளெய்ம்கள்',
  'agent.rewards.exposure.row.overridden': 'அட்மினால் மீறப்பட்டது',
  'agent.rewards.exposure.row.actualMaxLabel': 'உண்மை / அதிகபட்சம்',
  'agent.rewards.exposure.row.noMaxDash': '—',
  'agent.rewards.optInModal.eyebrow': 'செலவு-பகிர்வு ஆப்ட்-இன்',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'விளையாட்டு லாபம்',
  'agent.profile.rates.infoBanner.standardPctText': ' நிலையான சதவீதங்களைப் பயன்படுத்துகிறது. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'விளம்பர செலவுகள்',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (சலுகைகள், கேஷ்பேக்) ஒரு டைனமிக் அப்லைன் பூலைப் பயன்படுத்துகின்றன. இரண்டும் உங்கள் நிலை (L{level}) அடிப்படையில் தளம் நிர்வாகியால் உலகளவில் நிர்வகிக்கப்படுகின்றன.',
  'agent.profile.rates.section.gameProfits': 'விளையாட்டு லாபம் — ஒவ்வொரு போட்டியிலும் நீங்கள் சம்பாதிப்பது',
  'agent.profile.rates.section.promotionalCosts': 'விளம்பர செலவுகள் — உங்கள் அப்லைன் சப்சிடி பங்களிப்பு',
  'agent.profile.rates.pillar.platformFee': 'தள கட்டணம் %',
  'agent.profile.rates.pillar.winLoss': 'வெற்றி / தோல்வி லாபம் %',
  'agent.profile.rates.pillar.promoWeight': 'விளம்பர பூல் எடை',
  'agent.profile.rates.guarantee.title': 'நேரடி பரிந்துரை உத்தரவாதம்',
  'agent.profile.rates.guarantee.body': 'சலுகையை உரிமை கோருகின்ற வீரரை நீங்கள் நேரடியாகப் பரிந்துரைத்திருந்தால், நீங்கள் ₹0.00 செலுத்துகிறீர்கள். உங்கள் நெட்வொர்க்கை வளர்க்க உங்கள் அப்லைன்கள் செலவின் 100% சப்சிடி வழங்குகின்றனர்.',
  'agent.profile.rates.poolExplainer.title': 'பூல் எப்படி வேலை செய்கிறது:',
  'agent.profile.rates.poolExplainer.body': 'ஒரு துணை-முகவரின் வீரர் ஒரு சலுகையை உரிமை கோரும்போது, ​​உங்கள் எடை மீதமுள்ள செயலில் உள்ள அப்லைன் + தளத்துடன் ஒரு பூலில் சேர்க்கப்படுகிறது. நீங்கள் (உங்கள் எடை / மொத்த பூல்) × சலுகை செலவு செலுத்துகிறீர்கள். அதிக எடை = பெரிய பங்கு.',
  'agent.profile.rates.weightLabel': 'எடை:',
  'agent.profile.rates.updatedPrefix': 'புதுப்பிக்கப்பட்டது',
  'agent.profile.rates.notSetYet': 'இன்னும் அமைக்கப்படவில்லை',
  'agent.rewards.optInModal.guarantee.title': 'நேரடி பரிந்துரை உத்தரவாதம்',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'இந்த சலுகையை உரிமை கோருகின்ற வீரரை <strong>நீங்கள் நேரடியாகப் பரிந்துரைத்திருந்தால்</strong>, நீங்கள் <strong>₹0.00</strong> செலுத்துகிறீர்கள். உங்கள் நெட்வொர்க்கை வளர்க்க உங்கள் அப்லைன்கள் செலவின் 100% சப்சிடி வழங்குகின்றனர்.',
  'agent.rewards.optInModal.guarantee.body.noDownline': '<strong>உங்கள் வீரர்கள்</strong> இந்த சலுகையை உரிமை கோரும்போது, ​​நீங்கள் <strong>₹0.00</strong> செலுத்துகிறீர்கள். உங்கள் நெட்வொர்க்கை வளர்க்க உங்கள் அப்லைன்கள் செலவின் 100% சப்சிடி வழங்குகின்றனர்.',
  'agent.rewards.optInModal.uplineReality.title': 'அப்லைன் உண்மை சரிபார்ப்பு',
  'agent.rewards.optInModal.uplineReality.body': 'உங்களிடம் <strong>{count}</strong> நேரடி துணை-முகவர்{plural} உள்ளனர். ஒரு துணை-முகவரின் வீரர் இந்த சலுகையை உரிமை கோரினால், நீங்கள் உங்கள் <strong>பூல் எடை ({weight})</strong> அடிப்படையில் மீதமுள்ள செயலில் உள்ள அப்லைன் மற்றும் தளத்துடன் ஒப்பிடும்போது விகிதாசார பங்கை செலுத்துவீர்கள்.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'நேரடி',
  'agent.rates.col.indirect': 'மறைமுக',
  'agent.rates.indirect.dynamic': 'டைனமிக்',
  'agent.rates.indirect.notApplicable': 'பொருந்தாது',
  'agent.rates.indirect.rangeTo': 'முதல்',
  'agent.rewards.optInModal.close': 'மூடு',
  'agent.rewards.optInModal.cancel': 'ரத்து செய்',
  'agent.rewards.optInModal.loading': 'ஆஃபர் விவரங்கள் ஏற்றப்படுகின்றன…',
  'agent.rewards.optInModal.error.loadFailed': 'ஆஃபர் விவரங்களை ஏற்ற முடியவில்லை',
  'agent.rewards.optInModal.error.optInFailed': 'ஆப்ட்-இன் தோல்வியடைந்தது',
  'agent.rewards.optInModal.error.optOutFailed': 'ஆப்ட்-அவுட் தோல்வியடைந்தது',
  'agent.rewards.optInModal.error.saveFailed': 'சப்-ஏஜெண்ட் ரேட்களை சேமிக்க முடியவில்லை',
  'agent.rewards.optInModal.toast.optedIn': 'ஆப்ட் இன் செய்யப்பட்டது. உங்கள் டவுன்லைன் இப்போது இந்த ஆஃபரைப் பார்க்க முடியும்.',
  'agent.rewards.optInModal.toast.optedOut': 'ஆப்ட் அவுட் செய்யப்பட்டது. ஆஃபர் உங்கள் டவுன்லைனில் இருந்து அகற்றப்பட்டது.',
  'agent.rewards.optInModal.toast.saved.one': '{count} சப்-ஏஜெண்ட் ரேட் மாற்றம் சேமிக்கப்பட்டது.',
  'agent.rewards.optInModal.toast.saved.other': '{count} சப்-ஏஜெண்ட் ரேட் மாற்றங்கள் சேமிக்கப்பட்டன.',
  'agent.rewards.optInModal.confirmOptOut': 'இந்த ஆஃபரிலிருந்து ஆப்ட் அவுட் செய்யவா? உங்கள் டவுன்லைன் இதை இனி பார்க்காது.',
  'agent.rewards.optInModal.auditNote': 'ஆஃபர் #{id}-க்கான ஆப்ட்-இன் மாடல் வழியாக அமைக்கப்பட்டது',
  'agent.rewards.optInModal.stake.shareLabel': 'உங்கள் பங்கு',
  'agent.rewards.optInModal.stake.locked': 'ஆப்ட்-இன்னில் பூட்டப்பட்டது',
  'agent.rewards.optInModal.stake.current': 'தற்போதைய ரேட்',
  'agent.rewards.optInModal.stake.noRate': 'அட்மின் உங்களுக்கு இன்னும் ரேட்டை வழங்கவில்லை',
  'agent.rewards.optInModal.stake.spentSoFar.label': 'இதுவரை செலவழித்தது',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'இந்த ஆஃபரின் டவுன்லைன் கிளெய்ம்களுக்கு உங்கள் வாலெட்டில் இருந்து ஏற்கனவே செலுத்தப்பட்ட திரட்டப்பட்ட ரூபாய்.',
  'agent.rewards.optInModal.stake.maxPossible.label': 'அதிகபட்ச சாத்தியம்',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'மீதமுள்ள ஒவ்வொரு பட்ஜெட் ரூபாயும் கிளெய்ம் செய்யப்பட்டால் நீங்கள் செலுத்த வேண்டிய மோசமான-வழக்கு ரூபாய்.',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— பட்ஜெட் வரம்பு இல்லை —',
  'agent.rewards.optInModal.stake.worstCase': 'மோசமான வழக்கு: ப்ளாட்ஃபார்ம் பட்ஜெட்டின் மீதமுள்ள ஒவ்வொரு ₹{amount}-ஐயும் உங்கள் டவுன்லைன் கிளெய்ம் செய்தால், நீங்கள் அதன் {pct}% செலுத்துகிறீர்கள்.',
  'agent.rewards.optInModal.toggle.title.optedIn': 'நீங்கள் ஆப்ட் இன் செய்துள்ளீர்கள்',
  'agent.rewards.optInModal.toggle.title.optedOut': 'உங்கள் டவுன்லைனுடன் பகிர ஆப்ட் இன் செய்யவும்',
  'agent.rewards.optInModal.toggle.body.optedIn': 'உங்கள் டவுன்லைன் பிளேயர்கள் இந்த ஆஃபரைப் பார்க்கிறார்கள். அவர்களிடமிருந்து ஒவ்வொரு கிளெய்மையும் உங்கள் பூட்டப்பட்ட பங்கில் கோ-ஃபண்ட் செய்கிறீர்கள்.',
  'agent.rewards.optInModal.toggle.body.optedOut': 'நீங்கள் ஆப்ட் இன் செய்யும் வரை, உங்கள் பிளேயர்களில் யாரும் இந்த ஆஃபரைப் பார்க்க மாட்டார்கள். நீங்கள் ஆப்ட் இன் செய்தவுடன், இந்த ஆஃபருக்கான உங்கள் பங்கு ரேட் பூட்டப்படும்.',
  'agent.rewards.optInModal.toggle.optOutLink': 'இந்த ஆஃபரிலிருந்து ஆப்ட் அவுட் செய்',
  'agent.rewards.optInModal.subAgents.title': 'சப்-ஏஜெண்ட் ரேட்கள்',
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ஒவ்வொருவரும் எடுக்கக்கூடிய அதிகபட்சம்: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} சேமிக்கப்படவில்லை',
  'agent.rewards.optInModal.subAgents.empty': 'நேரடி சப்-ஏஜெண்ட்கள் இல்லை.',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'சேமித்தல் கேஸ்கேட் ஆகும் — நீங்கள் இங்கே ஒரு குழந்தையை உயர்த்தினால், அதற்கு ஏற்கனவே ஆழமான சப்-ஏஜெண்ட்கள் இருந்தால், அந்த ரேட்கள் சங்கிலியை சீராக வைத்திருக்க தானாக கிளாம்ப் செய்யப்படலாம்.',
  'agent.rewards.optInModal.subAgents.noChildren': 'உங்களுக்கு நேரடி சப்-ஏஜெண்ட்கள் இல்லை. ஆஃபர் உங்கள் சொந்த பிளேயர்களுக்குத் தெரியும்; அட்மின் முழு மீதியையும் செலுத்துகிறார்.',
  'agent.rewards.optInModal.error.rateRequired': 'ஒரு ரேட்டை உள்ளிடவும் (அழிக்க 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'எண்கள் மட்டுமே',
  'agent.rewards.optInModal.error.negative': 'எதிர்மறையாக இருக்க முடியாது',
  'agent.rewards.optInModal.error.max100': 'அதிகபட்சம் 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'அதிகபட்சம் {ceiling}% (உங்கள் பங்கு)',
  'agent.rewards.optInModal.cta.saving': 'சேமிக்கப்படுகிறது…',
  'agent.rewards.optInModal.cta.done': 'முடிந்தது',
  'agent.rewards.optInModal.cta.save.one': '{count} மாற்றத்தை சேமி',
  'agent.rewards.optInModal.cta.save.other': '{count} மாற்றங்களை சேமி',
  'agent.adminRechargePlayer.title': 'ஒரு பிளேயரை ரீசார்ஜ் செய்',
  'agent.adminRechargePlayer.subtitle': 'உங்கள் பூலில் இருந்து ஒரு பிளேயரின் வாலெட்டுக்கு',
  'agent.adminRechargePlayer.backLink': 'பிளேயருக்குத் திரும்பு',
  'agent.createPlayer.title': 'ஒரு புதிய பிளேயரை சேர்',
  'agent.createPlayer.subtitle.agent': 'அவர்கள் தானாக உங்களுக்கு சொந்தமாகி விடுவார்கள்.',
  'agent.createPlayer.subtitle.admin': 'கீழே ஒரு உரிமை ஏஜெண்ட்டைத் தேர்ந்தெடுக்கவும், அல்லது ப்ளாட்ஃபார்ம்-நேரடிக்கு காலியாக விடவும்.',
  'agent.createPlayer.close': 'மூடு',
  'agent.createPlayer.section.identity.title': 'அடையாளம்',
  'agent.createPlayer.section.identity.hint': 'கணக்கை உருவாக்க தேவை',
  'agent.createPlayer.field.username.label': 'பயனர்பெயர்',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'மின்னஞ்சல்',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'மொபைல்',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-இலக்க இந்திய மொபைல், +91 தானாக சேர்க்கப்படும்',
  'agent.createPlayer.field.password.label': 'ஆரம்ப கடவுச்சொல்',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ எழுத்துகள், எழுத்து + இலக்கம்',
  'agent.createPlayer.section.owner.title': 'உரிமை ஏஜெண்ட்',
  'agent.createPlayer.section.owner.hint': 'விருப்பம் — ப்ளாட்ஃபார்ம்-நேரடி பிளேயருக்கு காலியாக விடவும்',
  'agent.createPlayer.field.owner.label': 'ஏஜெண்ட்',
  'agent.createPlayer.field.owner.none': '— எதுவும் இல்லை (ப்ளாட்ஃபார்ம்-நேரடி) —',
  'agent.createPlayer.section.profile.title': 'சுயவிவரம்',
  'agent.createPlayer.section.profile.hint': 'விருப்பம் — இப்போது தெரிந்ததை நிரப்பவும்; முதல் உள்நுழைவில் பிளேயர் முடிக்க முடியும்',
  'agent.createPlayer.field.firstName.label': 'முதல் பெயர்',
  'agent.createPlayer.field.lastName.label': 'கடைசி பெயர்',
  'agent.createPlayer.field.dob.label': 'பிறந்த தேதி',
  'agent.createPlayer.field.gender.label': 'பாலினம்',
  'agent.createPlayer.gender.unspecified': '— குறிப்பிடப்படவில்லை —',
  'agent.createPlayer.gender.male': 'ஆண்',
  'agent.createPlayer.gender.female': 'பெண்',
  'agent.createPlayer.gender.other': 'மற்றவை',
  'agent.createPlayer.gender.preferNot': 'சொல்ல விரும்பவில்லை',
  'agent.createPlayer.field.language.label': 'விருப்பமான மொழி',
  'agent.createPlayer.section.location.title': 'இடம்',
  'agent.createPlayer.section.location.hint': 'விருப்பம் — முதல் உள்நுழைவில் பிளேயர் முடிக்க முடியும்',
  'agent.createPlayer.disclaimer': 'பிளேயர் status=active மற்றும் மின்னஞ்சல் தானாக சரிபார்க்கப்பட்டதாக உருவாக்கப்படுகிறார் — எந்த இணைப்பும் அனுப்பப்படவில்லை. நீங்கள் தொடர்பு தகவலை நம்புகிறீர்கள் என்பதை உறுதிசெய்யவும்.',
  'agent.createPlayer.cta.cancel': 'ரத்து செய்',
  'agent.createPlayer.cta.creating': 'உருவாக்கப்படுகிறது…',
  'agent.createPlayer.cta.submit': 'பிளேயர் உருவாக்கு',
  'agent.createPlayer.error.missingAgentId': 'ஏஜெண்ட் ஐடி இல்லை',
  'agent.createPlayer.error.createFailed': 'பிளேயரை உருவாக்க முடியவில்லை',
  'agent.adminHome.range.today': 'இன்று',
  'agent.adminHome.range.yesterday': 'நேற்று',
  'agent.adminHome.range.week': 'இந்த வாரம்',
  'agent.adminHome.range.lastWeek': 'கடந்த வாரம்',
  'agent.adminHome.range.month': 'இந்த மாதம்',
  'agent.adminHome.range.lastMonth': 'கடந்த மாதம்',
  'agent.adminHome.range.all': 'முழுமை',
  'agent.adminHome.greeting.subtitle': 'மீண்டும் வரவேற்கிறோம்',
  'agent.adminHome.greeting.fallback': 'நண்பரே',
  'agent.adminHome.errorBanner.fallback': 'ஏற்ற முடியவில்லை',
  'agent.adminHome.admin.hero.label': 'ப்ளாட்ஃபார்ம் உள்வரவு',
  'agent.adminHome.admin.action.moneyQueue': 'பண வரிசை',
  'agent.adminHome.admin.action.agents': 'ஏஜெண்ட்கள்',
  'agent.adminHome.admin.snapshot.matches': 'மேட்ச்கள்',
  'agent.adminHome.admin.snapshot.newSignups': 'புதிய சைன்அப்கள்',
  'agent.adminHome.admin.snapshot.active': 'செயலில்',
  'agent.adminHome.admin.snapshot.pending': 'நிலுவையில்',
  'agent.adminHome.section.games': 'கேம்கள்',
  'agent.adminHome.section.games.subtitle': 'மேட்ச்கள் மற்றும் ப்ளாட்ஃபார்ம் ஃபீ · {range}',
  'agent.adminHome.section.activity': 'செயல்பாடு',
  'agent.adminHome.section.ops': 'செயல்பாடுகள் & நுண்ணறிவுகள்',
  'agent.adminHome.section.ops.subtitle': 'நேரடி ஸ்னாப்ஷாட்கள் — முழு பக்கத்திற்கு செல்ல எந்த அட்டையையும் கிளிக் செய்யவும்',
  'agent.adminHome.section.gameSettings': 'கேம் அமைப்புகள்',
  'agent.adminHome.section.gameSettings.subtitle': 'ஒவ்வொரு கேமையும் கட்டமைக்கவும்',
  'agent.adminHome.section.downline': 'என் டவுன்லைன் நுண்ணறிவுகள்',
  'agent.adminHome.section.downline.subtitle': 'முழு பக்கத்திற்கு செல்ல எந்த அட்டையையும் கிளிக் செய்யவும்',
  'agent.adminHome.admin.card.players.title': 'பிளேயர்கள் வாலெட்',
  'agent.adminHome.admin.card.players.headlineLabel': 'ரேஞ்சில் ரீசார்ஜ் செய்யப்பட்டது',
  'agent.adminHome.admin.card.players.flow.recharges': 'ரீசார்ஜ்கள்',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'பணம் எடுப்புகள்',
  'agent.adminHome.admin.card.players.pending.recharges': 'நிலுவையில் உள்ள ரீசார்ஜ்கள்',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'நிலுவையில் உள்ள பணம் எடுப்புகள்',
  'agent.adminHome.admin.card.players.stat.total': 'மொத்த பிளேயர்கள்',
  'agent.adminHome.admin.card.players.stat.active': 'செயலில்',
  'agent.adminHome.admin.card.players.stat.suspended': 'நிறுத்தப்பட்டது',
  'agent.adminHome.admin.card.agents.title': 'ஏஜெண்ட்கள் வாலெட்',
  'agent.adminHome.admin.card.agents.headlineLabel': 'ஏஜெண்ட் பூல்களில்',
  'agent.adminHome.admin.card.agents.flow.topups': 'டாப்அப்கள் (அட்மின் → ஏஜெண்ட்)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'வெளியேறல் (செலுத்தப்பட்டது / டாப்அப்)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'நிலுவையில் உள்ள ஏஜெண்ட் பணம் எடுப்புகள்',
  'agent.adminHome.admin.card.agents.stat.total': 'மொத்த ஏஜெண்ட்கள்',
  'agent.adminHome.admin.card.agents.stat.active': 'செயலில்',
  'agent.adminHome.admin.card.agents.stat.suspended': 'நிறுத்தப்பட்டது',
  'agent.adminHome.admin.card.bots.title': 'பாட்ஸ் வாலெட்',
  'agent.adminHome.admin.card.bots.headlineLabel': 'பாட் வாலெட்களில்',
  'agent.adminHome.admin.card.bots.stat.total': 'மொத்த பாட்கள்',
  'agent.adminHome.admin.card.bots.stat.active': 'செயலில்',
  'agent.adminHome.admin.card.bots.stat.matches': 'மேட்ச்கள்',
  'agent.adminHome.admin.card.rewards.title': 'வெகுமதிகள்',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'இந்த வாரம் செலுத்தப்பட்டது',
  'agent.adminHome.admin.card.rewards.pending.claims': 'நிலுவையில் உள்ள கிளெய்ம்கள்',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'செயலில் உள்ள ஆஃபர்கள்',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'பல்க் வேலைகள்',
  'agent.adminHome.gameTile.matches': 'மேட்ச்கள்',
  'agent.adminHome.game.ludo': 'லுடோ',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'ரம்மி',
  'agent.adminHome.game.carrom': 'கேரம்',
  'agent.adminHome.game.chess': 'சதுரங்கம்',
  'agent.adminHome.activity.topPlayers.title': 'சிறந்த பிளேயர்கள்',
  'agent.adminHome.activity.topPlayers.empty': 'இந்த ரேஞ்சில் மேட்ச்கள் இல்லை.',
  'agent.adminHome.activity.topPlayers.metric': '{count} மேட்ச்கள்',
  'agent.adminHome.activity.topAgents.title': 'சிறந்த ஏஜெண்ட்கள்',
  'agent.adminHome.activity.topAgents.subtitle': 'சொந்தமான பிளேயர்களின்படி',
  'agent.adminHome.activity.topAgents.empty': 'இன்னும் செயலில் உள்ள ஏஜெண்ட்கள் இல்லை.',
  'agent.adminHome.activity.topAgents.metric': '{count} பிளேயர்கள்',
  'agent.adminHome.activity.recent.title': 'சமீபத்திய பரிவர்த்தனைகள்',
  'agent.adminHome.activity.recent.subtitle': 'கடைசி 5',
  'agent.adminHome.activity.recent.empty': 'இன்னும் பரிவர்த்தனைகள் இல்லை.',
  'agent.adminHome.ops.topEarners.title': 'சிறந்த சம்பாதிப்பவர்கள் (7நா)',
  'agent.adminHome.ops.topEarners.subtitle': 'உண்மையான நிகர அளவில்',
  'agent.adminHome.ops.topEarners.empty': 'கடந்த 7 நாட்களில் வருமானம் இல்லை.',
  'agent.adminHome.ops.liveActivity.title': 'நேரடி செயல்பாடு',
  'agent.adminHome.ops.liveActivity.subtitle': 'ப்ளாட்ஃபார்ம் முழுவதிலும் கடைசி 5 நிகழ்வுகள்',
  'agent.adminHome.ops.liveActivity.empty': 'அமைதி — கடந்த 24 மணி நேரத்தில் நிகழ்வுகள் இல்லை.',
  'agent.adminHome.ops.botPerf.title': 'பாட் செயல்திறன் (7நா)',
  'agent.adminHome.ops.botPerf.subtitle': 'ஒரு வகை-வாரியான P&L',
  'agent.adminHome.ops.rateChanges.title': 'சமீபத்திய ரேட் மாற்றங்கள்',
  'agent.adminHome.ops.rateChanges.subtitle': 'சமீபத்திய பார்ட்னர்ஷிப் திருத்தங்கள்',
  'agent.adminHome.ops.rateChanges.empty': 'இன்னும் ரேட்-மாற்ற நிகழ்வுகள் இல்லை.',
  'agent.adminHome.ops.opsHealth.title': 'செயல்பாடுகள் ஆரோக்கியம்',
  'agent.adminHome.ops.opsHealth.subtitle': 'டெட்-லெட்டர் வரிசை · நிலுவையில் உள்ள செட்டில்மென்ட்கள்',
  'agent.adminHome.feed.match.earnedVerb': 'சம்பாதித்தது',
  'agent.adminHome.feed.match.onGameSuffix': '{game}-இல்',
  'agent.adminHome.feed.recharge.label': 'ரீசார்ஜ்',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'மூலம்',
  'agent.adminHome.feed.rateChange.label': 'ரேட் மாற்றம்',
  'agent.adminHome.feed.cascadeBadge': 'கேஸ்கேட்',
  'agent.adminHome.feed.recharge.state.approved': 'அங்கீகரிக்கப்பட்டது',
  'agent.adminHome.feed.recharge.state.rejected': 'நிராகரிக்கப்பட்டது',
  'agent.adminHome.feed.recharge.state.pending': 'நிலுவையில்',
  'agent.adminHome.feed.recharge.state.paid': 'செலுத்தப்பட்டது',
  'agent.adminHome.feed.recharge.state.expired': 'காலாவதியானது',
  'agent.adminHome.leaderboard.matches.one': '{count} மேட்ச்',
  'agent.adminHome.leaderboard.matches.other': '{count} மேட்ச்கள்',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',
  'agent.adminHome.botMatrix.profitLabel': 'ப்ளாட்ஃபார்ம் லாபம் (7நா)',
  'agent.adminHome.botMatrix.cat.easy': 'எளிய',
  'agent.adminHome.botMatrix.cat.medium': 'நடுத்தர',
  'agent.adminHome.botMatrix.cat.hard': 'கடினமான',
  'agent.adminHome.botMatrix.matchesShort': '{matches}மே',
  'agent.adminHome.botMatrix.activeSummary': '{total} பாட்களில் {active} செயலில்',
  'agent.adminHome.botMatrix.loading': 'ஏற்றப்படுகிறது…',
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ சுத்தமாக உள்ளது',
  'agent.adminHome.opsHealth.dlqClean.body': 'தோல்வியடைந்த செட்டில்மென்ட் வேலைகள் இல்லை.',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} தீர்க்கப்படாத DLQ வேலை',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} தீர்க்கப்படாத DLQ வேலைகள்',
  'agent.adminHome.opsHealth.dlqDirty.body': 'மீண்டும் முயற்சிக்க / தீர்க்க கிளிக் செய்யவும்.',
  'agent.adminHome.opsHealth.replay.eyebrow': 'மீண்டும் இயக்கு',
  'agent.adminHome.opsHealth.replay.body': 'செட்டில்மென்ட்டை மீண்டும் இயக்கு',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'மீண்டும் ஒதுக்கு',
  'agent.adminHome.opsHealth.reassign.body': 'பல்க் நகர்த்தல்',
  'agent.adminHome.agentDash.hero.label': 'என் பூல் பேலன்ஸ்',
  'agent.adminHome.agentDash.hero.netPrefix': 'நிகர',
  'agent.adminHome.agentDash.action.myWallet': 'என் வாலெட்',
  'agent.adminHome.agentDash.action.myPlayers': 'என் பிளேயர்கள்',
  'agent.adminHome.agentDash.snapshot.myMatches': 'என் மேட்ச்கள்',
  'agent.adminHome.agentDash.snapshot.active': 'செயலில்',
  'agent.adminHome.agentDash.snapshot.netRange': 'நிகர (ரேஞ்ச்)',
  'agent.adminHome.agentDash.snapshot.pending': 'நிலுவையில்',
  'agent.adminHome.agentDash.card.players.title': 'என் பிளேயர்கள் வாலெட்',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'ரேஞ்சில் ரீசார்ஜ் செய்யப்பட்டது',
  'agent.adminHome.agentDash.card.players.stat.total': 'என் பிளேயர்கள்',
  'agent.adminHome.agentDash.card.wallet.title': 'என் வாலெட்',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'பூல் பேலன்ஸ்',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'சம்பாதித்தது (கமிஷன்கள்)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'செலுத்தப்பட்டது',
  'agent.adminHome.agentDash.card.wallet.pending': 'என் நிலுவையில் உள்ள பணம் எடுப்பு',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'பெறப்பட்ட டாப்அப்கள்',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'நிகர (ரேஞ்ச்)',
  'agent.adminHome.agentDash.card.activity.title': 'செயல்பாடு',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'ரேஞ்சில் என் மேட்ச்கள்',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'சிறந்த செலவாளி',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'அவர்களின் செலவு',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'நிலுவையில் உள்ள உருப்படிகள்',
  'agent.adminHome.agentDash.activity.topSpenders.title': 'சிறந்த செலவாளிகள்',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'இந்த ரேஞ்சில் இன்னும் செலவு இல்லை.',
  'agent.adminHome.agentDash.activity.recent.title': 'சமீபத்திய செயல்பாடு',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'உங்கள் பிளேயர்களிடமிருந்து கடைசி 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'இன்னும் பரிவர்த்தனைகள் இல்லை.',
  'agent.adminHome.agentDash.insights.topEarners.title': 'சிறந்த சம்பாதிப்பவர்கள் (7நா)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'உங்கள் சப்-ஏஜெண்ட்கள் தரவரிசை',
  'agent.adminHome.agentDash.insights.downline.title': 'டவுன்லைன் செயல்பாடு',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'உங்கள் டவுன்லைனிலிருந்து கடைசி 5 நிகழ்வுகள்',
  'agent.adminHome.agentDash.insights.deposit.title': 'டெபாசிட் லெட்ஜர்',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'சமீபத்திய டெபாசிட் வரவுகள் / திருப்புதல்கள்',
  'agent.adminHome.agentDash.insights.deposit.empty': 'இன்னும் டெபாசிட் செயல்பாடு இல்லை.',
  'agent.subAgentDetail.title': 'சப்-ஏஜெண்ட்',
  'agent.subAgentDetail.subtitle': 'நேரடி சப்-ஏஜெண்ட்',
  'agent.subAgentDetail.error.loadFailed': 'சப்-ஏஜெண்ட்டை ஏற்ற முடியவில்லை',
  'agent.subAgentDetail.backToList': 'சப்-ஏஜெண்ட்கள் பட்டியலுக்குத் திரும்பு',
  'agent.subAgentDetail.loading': 'ஏற்றப்படுகிறது…',
  'agent.subAgentDetail.tab.identity': 'அடையாளம்',
  'agent.subAgentDetail.tab.players': 'பிளேயர்கள்',
  'agent.subAgentDetail.tab.wallet': 'வாலெட்',
  'agent.subAgentDetail.tab.rates': 'ரேட்கள்',
  'agent.subAgentDetail.tab.ops': 'செயல்பாடுகள்',
  'agent.subAgentDetail.header.players.one': '{count} பிளேயர்',
  'agent.subAgentDetail.header.players.other': '{count} பிளேயர்கள்',
  'agent.subAgentDetail.header.pool': 'பூல்: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'கடைசி உள்நுழைவு {when}',
  'agent.subAgentDetail.status.active': 'செயலில்',
  'agent.subAgentDetail.status.suspended': 'நிறுத்தப்பட்டது',
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'பயனர்பெயர்',
  'agent.subAgentDetail.identity.row.email': 'மின்னஞ்சல்',
  'agent.subAgentDetail.identity.row.status': 'நிலை',
  'agent.subAgentDetail.identity.row.parent': 'பெற்றோர் ஏஜெண்ட்',
  'agent.subAgentDetail.identity.parentValue': '@{username} (நீங்கள்)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'சொந்தமான பிளேயர்கள்',
  'agent.subAgentDetail.identity.row.wallet': 'வாலெட் பேலன்ஸ்',
  'agent.subAgentDetail.identity.row.created': 'உருவாக்கப்பட்டது',
  'agent.subAgentDetail.identity.row.lastLogin': 'கடைசி உள்நுழைவு',
  'agent.subAgentDetail.identity.row.identityLocked': 'அடையாளம் பூட்டப்பட்டது',
  'agent.subAgentDetail.identity.identityLocked.yes': 'ஆம் (அட்மின் மட்டுமே திருத்தங்கள்)',
  'agent.subAgentDetail.identity.identityLocked.no': 'இல்லை (சப்-ஏஜெண்ட் சுயமாகத் திருத்தலாம்)',
  'agent.subAgentDetail.identity.row.receiving': 'பிளேயர்களைப் பெறுதல்',
  'agent.subAgentDetail.optOut.accepting': 'ஏற்றுக்கொள்கிறார்',
  'agent.subAgentDetail.optOut.optedOut': 'ஆப்ட் அவுட்',
  'agent.subAgentDetail.optOut.toast.accepting': 'சப்-ஏஜெண்ட் இப்போது பிளேயர்களை ஏற்றுக்கொள்கிறார்',
  'agent.subAgentDetail.optOut.toast.optedOut': 'சப்-ஏஜெண்ட் லொக்கேட்டரிலிருந்து ஆப்ட் அவுட் செய்தார்',
  'agent.subAgentDetail.optOut.error.updateFailed': 'புதுப்பிக்க முடியவில்லை',
  'agent.subAgentDetail.location.title': 'இடம்',
  'agent.subAgentDetail.location.lastUpdated': '· கடைசியாக புதுப்பிக்கப்பட்டது {date}',
  'agent.subAgentDetail.location.cta.save': 'இடத்தை சேமி',
  'agent.subAgentDetail.location.nothingToSave': 'சேமிக்க எதுவும் இல்லை',
  'agent.subAgentDetail.location.toast.saved.one': 'இடம் சேமிக்கப்பட்டது ({count} புலம்)',
  'agent.subAgentDetail.location.toast.saved.other': 'இடம் சேமிக்கப்பட்டது ({count} புலங்கள்)',
  'agent.subAgentDetail.location.hint': 'சேமியை இயக்க வேறு மாநிலம் / மாவட்டம் / பகுதியைத் தேர்வுசெய்க. (சப்-ஏஜெண்ட் தாமே தங்கள் முதல் உள்நுழைவில் கட்டாய பாப்-அப் வழியாக துல்லியமான GPS-ஐ மேலெழுதுவார்கள்.)',
  'agent.subAgentDetail.players.empty': 'இந்த சப்-ஏஜெண்ட் இன்னும் எந்த பிளேயர்களையும் சொந்தமாக கொண்டிருக்கவில்லை.',
  'agent.subAgentDetail.players.count.one': '{count} பிளேயர்',
  'agent.subAgentDetail.players.count.other': '{count} பிளேயர்கள்',
  'agent.subAgentDetail.wallet.balanceLabel': 'தற்போதைய சப்-ஏஜெண்ட் பேலன்ஸ்',
  'agent.subAgentDetail.wallet.ledger.title': '@{username}-இன் முழு வாலெட் லெட்ஜர்',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'ஒவ்வொரு நகர்வும் · சப்-ஏஜெண்ட் தங்கள் வாலெட்டில் இதையே பார்க்கிறார்',
  'agent.subAgentDetail.wallet.ledger.empty': 'இன்னும் வாலெட் செயல்பாடு இல்லை.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'நீங்கள் ↔ அவர்கள்',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'பேல் ₹{amount}',
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'அட்மினிடமிருந்து டாப்-அப்',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'பிளேயருக்கு செலுத்தல்',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'பிளேயர் ரீசார்ஜ்',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'வருவாய் பங்கு',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'ரீசார்ஜ் கமிஷன்',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'கோ-ஃபண்ட் ஆஃபர்',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'அட்மின் சரிசெய்தல்',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'பணம் எடுப்பு ஹோல்ட்',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'பணம் எடுப்பு திருப்பி',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'ப்ளாட்ஃபார்ம் ஃபீ பங்கு',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'வின்/லாஸ் பங்கு',
  'agent.subAgentDetail.wallet.pullback.title': 'நாணயங்களை திரும்பப் பெறுகிறீர்களா?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'தங்கள் சொந்த பூலிலிருந்து பணம் எடுப்பு கோரிக்கையை சமர்ப்பிக்க ',
  'agent.subAgentDetail.wallet.pullback.body.middle': '-ஐ கேளுங்கள். நீங்கள் அதை செலுத்தப்பட்டதாக (முழு அல்லது பகுதி) ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'நிதி › சப்-ஏஜெண்ட்கள்',
  'agent.subAgentDetail.wallet.pullback.body.after': '-இல் இருந்து குறிக்க முடியும்.',
  'agent.subAgentDetail.transfer.topup.heading': '@{username}-க்கு பரிமாற்றம்',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username}-இலிருந்து பணம் எடுக்க',
  'agent.subAgentDetail.transfer.topup.body': 'அட்டாமிக் பரிமாற்றம்: உங்கள் வாலெட் கீழே, அவர்களின் வாலெட் மேலே. இரு பூல்களும் எதிர்மறையாக போகலாம் (கையெழுத்திட்ட பூல் வடிவமைப்பு).',
  'agent.subAgentDetail.transfer.withdraw.body': 'அட்டாமிக் தலைகீழ் பரிமாற்றம்: அவர்களின் வாலெட் கீழே, உங்கள் வாலெட் மேலே. சப்-ஏஜெண்ட்டிடமிருந்து செலுத்தும் கோரிக்கையை செட்டில் செய்யும்போது இதைப் பயன்படுத்தவும்.',
  'agent.subAgentDetail.transfer.amountLabel': 'தொகை (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'குறிப்பு (விரும்பினால்)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'எ.கா. வாரம் தோறும் டாப்-அப்',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'எ.கா. பேஅவுட் செட்டில்மென்ட்',
  'agent.subAgentDetail.transfer.error.amountPositive': 'தொகை நேர்மறை முழு எண்ணாக இருக்க வேண்டும்.',
  'agent.subAgentDetail.transfer.confirm.topup': '{amount}-ஐ உங்கள் பூலில் இருந்து @{username}-க்கு பரிமாற்றமா? உங்கள் பேலன்ஸ் சரியாக இந்த தொகை குறையும்.',
  'agent.subAgentDetail.transfer.confirm.withdraw': '{amount}-ஐ @{username}-இன் பூலில் இருந்து உங்களுக்குத் திருப்பி இழுக்கவா? அவர்களின் பேலன்ஸ் சரியாக இந்த தொகை குறையும்.',
  'agent.subAgentDetail.transfer.toast.topup': '@{username}-க்கு {amount} அனுப்பப்பட்டது. அவர்களின் பேலன்ஸ்: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username}-இடமிருந்து {amount} திரும்ப இழுக்கப்பட்டது. அவர்களின் பேலன்ஸ்: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working': 'வேலை செய்கிறது…',
  'agent.subAgentDetail.transfer.cta.suspended': 'சப்-ஏஜெண்ட் நிறுத்தப்பட்டது',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} பரிமாற்றம்',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} பணம் எடு',
  'agent.subAgentDetail.rates.loading': 'ரேட்கள் ஏற்றப்படுகின்றன…',
  'agent.subAgentDetail.rates.intro.part1': 'ஒவ்வொரு அட்டையும் ஒரு கமிஷன் பிலர்; ஒவ்வொரு வரிசையும் ஒரு (பிலர் × கேம்) பங்கு. ஒவ்வொரு உள்ளீட்டுக்கு அடுத்து காட்டப்படும் ',
  'agent.subAgentDetail.rates.intro.ceiling': 'வரம்பு',
  'agent.subAgentDetail.rates.intro.part2': ' உங்கள் சொந்த ரேட் — கோல்டன் வரம்பின்படி, ஒரு குழந்தைக்கு அதைவிட அதிகம் வழங்க முடியாது. நீங்கள் மாற்றும் எந்த மதிப்பும் திருத்தக்கூடியதாக மாறும்; கமிட் செய்ய ',
  'agent.subAgentDetail.rates.intro.applyWord': 'பயன்படுத்து',
  'agent.subAgentDetail.rates.intro.part3': '-ஐ கிளிக் செய்யவும். கேஸ்கேட் கிளாம்ப் சர்வர்-பக்க கட்டாயப்படுத்தப்படுகிறது.',
  'agent.subAgentDetail.rates.toast.applied': 'ரேட்கள் பயன்படுத்தப்பட்டன.',
  'agent.subAgentDetail.rates.confirm.one': '{count} ரேட் மாற்றத்தை பயன்படுத்தவா? கோல்டன்-வரம்பு + கேஸ்கேட் கிளாம்ப் சர்வர்-பக்க கட்டாயப்படுத்தப்படும்.',
  'agent.subAgentDetail.rates.confirm.other': '{count} ரேட் மாற்றங்களை பயன்படுத்தவா? கோல்டன்-வரம்பு + கேஸ்கேட் கிளாம்ப் சர்வர்-பக்க கட்டாயப்படுத்தப்படும்.',
  'agent.subAgentDetail.rates.error.blocked': 'தடுக்கப்பட்டது: {detail}',
  'agent.subAgentDetail.rates.pillar.platformFee': 'ப்ளாட்ஃபார்ம் ஃபீ %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'வின்/லாஸ் லாபம் %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ஆஃபர் வெகுமதி %',
  'agent.subAgentDetail.rates.row.globalLabel': '— குளோபல் —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ வரம்பு {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'பெற்றோரை மீறுகிறது (கோல்டன் வரம்பு)',
  'agent.subAgentDetail.rates.row.editedBadge': 'திருத்தப்பட்டது',
  'agent.subAgentDetail.rates.cta.discard.one': '{count} மாற்றத்தை நிராகரி',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} மாற்றங்களை நிராகரி',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} மாற்றத்தை பயன்படுத்து',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} மாற்றங்களை பயன்படுத்து',
  'agent.subAgentDetail.rates.cta.applying': 'பயன்படுத்தப்படுகிறது…',
  'agent.subAgentDetail.ops.status.title': 'நிலை',
  'agent.subAgentDetail.ops.status.body': 'நிறுத்துதல் சப்-ஏஜெண்ட்டின் உள்நுழைவுகள் மற்றும் டவுன்லைன் செயல்பாடுகளை நீங்கள் செயலிலுக்கு மாற்றும் வரை தடுக்கிறது. காரணம் விருப்பமானது ஆனால் ஆடிட் தடத்தில் வந்து சேருகிறது.',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'காரணம் (விரும்பினால்)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'சப்-ஏஜெண்ட்டை நிறுத்து',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'சப்-ஏஜெண்ட்டை மீட்டெடு',
  'agent.subAgentDetail.ops.status.cta.working': 'வேலை செய்கிறது…',
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username}-ஐ நிறுத்தவா? நிறுத்தப்பட்டிருக்கும் வரை உள்நுழைய அல்லது இயக்க முடியாது.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username}-ஐ மீட்டெடுக்கவா? அவர்கள் முழு அணுகலை மீண்டும் பெறுவார்கள்.',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} இப்போது நிறுத்தப்பட்டுள்ளார்.',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} இப்போது செயலில் உள்ளார்.',
  'agent.subAgentDetail.ops.password.title': 'கடவுச்சொல்லை மீட்டமை',
  'agent.subAgentDetail.ops.password.body': 'சப்-ஏஜெண்ட்டுக்கு புதிய கடவுச்சொல்லை அமைக்கிறது மற்றும் அவர்களின் ஏற்கனவே உள்ள புதுப்பிப்பு டோக்கன்களை ரத்து செய்கிறது. அவர்கள் புதிய கடவுச்சொல்லுடன் மீண்டும் உள்நுழைய வேண்டியிருக்கும். ஒரு பாதுகாப்பான வழியில் அவர்களுடன் பகிர்ந்து கொள்ளுங்கள்.',
  'agent.subAgentDetail.ops.password.placeholder': 'புதிய கடவுச்சொல் (குறைந்தபட்சம் 8 எழுத்துகள்)',
  'agent.subAgentDetail.ops.password.confirm': '@{username}-இன் கடவுச்சொல்லை மீட்டமைக்கவா? அவர்களின் ஏற்கனவே உள்ள அமர்வுகள் செல்லாததாக மாறும் மற்றும் புதிய கடவுச்சொல்லுடன் உள்நுழைய வேண்டும்.',
  'agent.subAgentDetail.ops.password.toast.reset': 'கடவுச்சொல் மீட்டமைக்கப்பட்டது. புதிய கடவுச்சொல்லை @{username}-உடன் பகிர்ந்து கொள்ளுங்கள்.',
  'agent.subAgentDetail.ops.password.error.tooShort': 'கடவுச்சொல் குறைந்தபட்சம் 8 எழுத்துகள் இருக்க வேண்டும்.',
  'agent.subAgentDetail.ops.password.cta.reset': 'கடவுச்சொல்லை மீட்டமை',
  'agent.subAgentDetail.ops.password.cta.resetting': 'மீட்டமைக்கப்படுகிறது…',
  'agent.playerDetail.backToPlayers': 'பிளேயர்களுக்குத் திரும்பு',
  'agent.playerDetail.loading': 'ஏற்றப்படுகிறது…',
  'agent.playerDetail.error.loadFailed': 'பயனரை ஏற்ற முடியவில்லை',
  'agent.playerDetail.header.noName': '(பெயர் அமைக்கப்படவில்லை)',
  'agent.playerDetail.header.joined': '{date} சேர்ந்தார்',
  'agent.playerDetail.header.ownedBy.prefix': ' · சொந்தம் ',
  'agent.playerDetail.header.walletLabel': 'வாலெட்',
  'agent.playerDetail.status.pending': 'நிலுவையில்',
  'agent.playerDetail.status.active': 'செயலில்',
  'agent.playerDetail.status.suspended': 'நிறுத்தப்பட்டது',
  'agent.playerDetail.status.banned': 'தடைசெய்யப்பட்டது',
  'agent.playerDetail.tab.identity': 'அடையாளம்',
  'agent.playerDetail.tab.profile': 'சுயவிவரம்',
  'agent.playerDetail.tab.wallet': 'வாலெட்',
  'agent.playerDetail.tab.security': 'பாதுகாப்பு',
  'agent.playerDetail.tab.ownership': 'உரிமை',
  'agent.playerDetail.tab.operations': 'செயல்பாடுகள்',
  'agent.playerDetail.identity.title': 'அடையாளம்',
  'agent.playerDetail.identity.row.username': 'பயனர்பெயர்',
  'agent.playerDetail.identity.row.email': 'மின்னஞ்சல்',
  'agent.playerDetail.identity.row.phone': 'தொலைபேசி',
  'agent.playerDetail.identity.row.country': 'நாடு',
  'agent.playerDetail.identity.row.joined': 'சேர்ந்தது',
  'agent.playerDetail.identity.row.emailVerified': 'மின்னஞ்சல் சரிபார்க்கப்பட்டது',
  'agent.playerDetail.identity.row.telegram': 'டெலிகிராம்',
  'agent.playerDetail.identity.row.failedLogins': 'தோல்வியடைந்த உள்நுழைவுகள்',
  'agent.playerDetail.identity.row.lockedUntil': 'பூட்டப்பட்டுள்ளது வரை',
  'agent.playerDetail.identity.value.yes': 'ஆம்',
  'agent.playerDetail.identity.value.no': 'இல்லை',
  'agent.playerDetail.identity.value.dash': '—',
  'agent.playerDetail.identity.transfer.title': 'இந்த பிளேயரை மாற்று',
  'agent.playerDetail.identity.transfer.body': 'அவர்களின் தனிப்பட்ட ஏஜெண்ட் ஐடி மூலம் மற்றொரு ஏஜெண்ட்டுக்கு கையளிக்கவும் — உங்கள் டவுன்லைன் மட்டுமல்ல, முழு நெட்வொர்க் முழுவதும் வேலை செய்கிறது.',
  'agent.playerDetail.identity.transfer.cta': 'UAID மூலம் மாற்று',
  'agent.playerDetail.avatar.changeTitle': 'புகைப்படத்தை மாற்று',
  'agent.playerDetail.avatar.cropTitle': '@{username}-க்கான புகைப்படத்தை சரிசெய்',
  'agent.playerDetail.avatar.toast.updated': 'புகைப்படம் புதுப்பிக்கப்பட்டது',
  'agent.playerDetail.avatar.error.uploadFailed': 'அப்லோடு தோல்வியடைந்தது',
  'agent.playerDetail.transfer.toast.admin': 'UAID வழியாக பிளேயர் மாற்றப்பட்டார்',
  'agent.playerDetail.transfer.toast.agent': '@{username} வெற்றிகரமாக மாற்றப்பட்டார்',
  'agent.playerDetail.ops.accountStatus.title': 'கணக்கு நிலை',
  'agent.playerDetail.ops.accountStatus.body': 'செயலில் என்றால் பிளேயர் உள்நுழைந்து விளையாட முடியும். நிறுத்து என்பது நீங்கள் மீண்டும் மாற்றும் வரை உள்நுழைவுகளைத் தடுக்கும். தடை என்பது வலுவான பூட்டு — கொள்கை மீறல்களுக்கு வைக்கவும்.',
  'agent.playerDetail.ops.statusBtn.activate': 'செயல்படுத்து',
  'agent.playerDetail.ops.statusBtn.suspend': 'நிறுத்து',
  'agent.playerDetail.ops.statusBtn.ban': 'தடை',
  'agent.playerDetail.ops.statusBtn.current': 'தற்போதைய',
  'agent.playerDetail.ops.statusBtn.toast': 'பயனர் இப்போது {status}',
  'agent.playerDetail.ops.danger.title': 'ஆபத்து மண்டலம்',
  'agent.playerDetail.ops.danger.body': 'கடினமாக-நீக்குதல் பயனர் பதிவை அகற்றுகிறது. வாலெட் லெட்ஜர் ஆடிட்டுக்காக பாதுகாக்கப்படுகிறது, ஆனால் பிளேயர் அனைத்து அணுகலையும் இழப்பார். இந்த நடவடிக்கையை மீட்டெடுக்க முடியாது.',
  'agent.playerDetail.ops.delete.eyebrow': 'ஆபத்து மண்டலம்',
  'agent.playerDetail.ops.delete.confirm.before': 'இந்த கணக்கை நிரந்தரமாக நீக்கு. இதை மீட்டெடுக்க முடியாது. உறுதிப்படுத்த ',
  'agent.playerDetail.ops.delete.confirm.after': '-ஐ தட்டச்சு செய்யவும்.',
  'agent.playerDetail.ops.delete.cta': 'நீக்கு',
  'agent.playerDetail.ops.delete.toast': 'பயனர் நீக்கப்பட்டார்',
  'agent.playerDetail.walletAdjust.title': 'வாலெட்டை சரிசெய்',
  'agent.playerDetail.walletAdjust.mode.credit': '+ வரவு',
  'agent.playerDetail.walletAdjust.mode.debit': '− பற்று',
  'agent.playerDetail.walletAdjust.field.amount': 'தொகை (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'காரணம் · லெட்ஜரில் சேமிக்கப்படுகிறது',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'எ.கா. டெபாசிட் திருப்புதல், போனஸ், திரும்பப் பெறுதல்',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'ரீசார்ஜாக குறி',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'இந்த வரவை லெட்ஜரில் ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'ரீசார்ஜ்',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' என பதிவு செய்யுங்கள். இந்த பயனர் யாரால் ரெஃபர் செய்யப்பட்டாரோ, அந்த ரெஃபரர் தானாக ஒரு போனஸ் பெறுவார்.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'நேர்மறை தொகையை உள்ளிடவும்',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'காரணம் தேவை',
  'agent.playerDetail.walletAdjust.cta.applying': 'பயன்படுத்தப்படுகிறது…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'வரவை பயன்படுத்து',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'பற்றை பயன்படுத்து',
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} வரவு வைக்கப்பட்டது',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} பற்று வைக்கப்பட்டது',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · ரெஃபரர் {bonus} போனஸ் சம்பாதித்தார்',
  'agent.playerDetail.rechargeCta.title': 'இந்த பிளேயரை ரீசார்ஜ் செய்',
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': '-ஐ டாப் அப் செய்வது ',
  'agent.playerDetail.rechargeCta.body.bold': 'உங்கள் வாலெட்',
  'agent.playerDetail.rechargeCta.body.after': '-இல் இருந்து அவர்களுக்கு நாணயங்களை மாற்றுகிறது. உங்கள் பூல் முதலில் சரிபார்க்கப்படுகிறது — நகர்வு அட்டாமிக் மற்றும் போதிய பேலன்ஸ் இல்லையெனில் நிராகரிக்கப்படுகிறது.',
  'agent.playerDetail.rechargeCta.thisPlayer': 'இந்த பிளேயர்',
  'agent.playerDetail.rechargeCta.cta': 'ரீசார்ஜ் படிவத்தை திற',
  'agent.playerDetail.security.title': 'கடவுச்சொல்லை மீட்டமை',
  'agent.playerDetail.security.subtitle': 'புதிய கடவுச்சொல்லை அமைக்கிறது மற்றும் ஒவ்வொரு சாதனத்திலிருந்தும் பயனரை சைன் அவுட் செய்கிறது.',
  'agent.playerDetail.security.field.new': 'புதிய கடவுச்சொல்',
  'agent.playerDetail.security.field.confirm': 'உறுதிப்படுத்தவும்',
  'agent.playerDetail.security.error.mismatch': 'கடவுச்சொற்கள் பொருந்தவில்லை',
  'agent.playerDetail.security.error.tooShort': 'குறைந்தபட்சம் 8 எழுத்துகள், எழுத்துகள் மற்றும் இலக்கங்களுடன்',
  'agent.playerDetail.security.toast.reset': 'கடவுச்சொல் மீட்டமைக்கப்பட்டது மற்றும் அனைத்து அமர்வுகளும் ரத்து செய்யப்பட்டன',
  'agent.playerDetail.security.cta.reset': 'கடவுச்சொல்லை மீட்டமை',
  'agent.playerDetail.security.cta.resetting': 'மீட்டமைக்கப்படுகிறது…',
  'agent.playerDetail.ledger.title': 'வாலெட் லெட்ஜர்',
  'agent.playerDetail.ledger.entries.one': '{count} பதிவு',
  'agent.playerDetail.ledger.entries.other': '{count} பதிவுகள்',
  'agent.playerDetail.ledger.empty': 'இன்னும் பரிவர்த்தனைகள் இல்லை.',
  'agent.playerDetail.ledger.loading': 'ஏற்றப்படுகிறது…',
  'agent.playerDetail.ledger.actorSuffix': ' · அட்மின் #{id} மூலம்',
  'agent.playerDetail.ledger.pagination.prev': 'முந்தைய',
  'agent.playerDetail.ledger.pagination.next': 'அடுத்த',
  'agent.playerDetail.ledger.kind.bet': 'மேட்ச் பெட்',
  'agent.playerDetail.ledger.kind.win': 'மேட்ச் பரிசு',
  'agent.playerDetail.ledger.kind.refund': 'திருப்புதல்',
  'agent.playerDetail.ledger.kind.fee': 'ப்ளாட்ஃபார்ம் ஃபீ',
  'agent.playerDetail.ledger.kind.adminCredit': 'அட்மின் வரவு',
  'agent.playerDetail.ledger.kind.adminDebit': 'அட்மின் பற்று',
  'agent.playerDetail.ledger.kind.recharge': 'ரீசார்ஜ்',
  'agent.playerDetail.ledger.kind.referralBonus': 'ரெஃபெரல் போனஸ்',
  'agent.playerDetail.ledger.kind.signupBonus': 'சைன்அப் போனஸ்',
  'agent.playerDetail.ledger.kind.offerCredit': 'ஆஃபர் வரவு',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'பணம் எடுப்பு',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'திருப்புதல்',
  'agent.playerDetail.ledger.game.platform': 'ப்ளாட்ஃபார்ம்',
  'agent.playerDetail.profile.title': 'சுயவிவரத்தைத் திருத்து',
  'agent.playerDetail.profile.field.username': 'பயனர்பெயர்',
  'agent.playerDetail.profile.field.email': 'மின்னஞ்சல்',
  'agent.playerDetail.profile.field.phone': 'தொலைபேசி',
  'agent.playerDetail.profile.field.firstName': 'முதல் பெயர்',
  'agent.playerDetail.profile.field.lastName': 'கடைசி பெயர்',
  'agent.playerDetail.profile.field.dob': 'பிறந்த தேதி',
  'agent.playerDetail.profile.field.gender': 'பாலினம்',
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'ஆண்',
  'agent.playerDetail.profile.gender.female': 'பெண்',
  'agent.playerDetail.profile.gender.other': 'மற்றவை',
  'agent.playerDetail.profile.gender.preferNot': 'சொல்ல விரும்பவில்லை',
  'agent.playerDetail.profile.location.title': 'இடம்',
  'agent.playerDetail.profile.location.lastUpdated': '· கடைசியாக புதுப்பிக்கப்பட்டது {date}',
  'agent.playerDetail.profile.location.legacy.label': 'கோப்பில் உள்ள பழைய முகவரி',
  'agent.playerDetail.profile.location.legacy.note': ' (படிக்க மட்டுமே — R3-க்கு முந்தையது, லொக்கேட்டரால் பயன்படுத்தப்படவில்லை):',
  'agent.playerDetail.profile.toast.nothingToSave': 'சேமிக்க எதுவும் இல்லை',
  'agent.playerDetail.profile.toast.saved.one': '{count} புலம் சேமிக்கப்பட்டது',
  'agent.playerDetail.profile.toast.saved.other': '{count} புலங்கள் சேமிக்கப்பட்டன',
  'agent.playerDetail.profile.cta.save': 'மாற்றங்களை சேமி',
  'agent.playerDetail.profile.cta.saving': 'சேமிக்கப்படுகிறது…',
  'agent.playerDetail.profile.cta.reset': 'மீட்டமை',
  'agent.playerDetail.ownership.title': 'உரிமை ஏஜெண்ட்',
  'agent.playerDetail.ownership.row.eyebrow': 'உரிமை',
  'agent.playerDetail.ownership.platform.title': 'ப்ளாட்ஃபார்ம்-நேரடி',
  'agent.playerDetail.ownership.platform.body': 'எந்த ஏஜெண்ட்டுக்கும் இந்த பிளேயர் சொந்தம் இல்லை. அட்மின் நேரடியாக நிர்வகிக்கிறார்.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ஏஜெண்ட் #{id}',
  'agent.playerDetail.ownership.owned.body': 'இந்த பிளேயரின் ரீசார்ஜ்கள் + மேட்ச்களில் கமிஷன்கள் சம்பாதிக்கிறார்.',
  'agent.playerDetail.ownership.cta.change': 'மாற்று',
  'agent.playerDetail.ownership.toast.changedTo': 'உரிமை → {label}',
  'agent.playerDetail.ownership.platformLabel': 'ப்ளாட்ஃபார்ம்-நேரடி',
  'agent.playerDetail.ownership.picker.title': '@{username}-ஐ மறு-ஒதுக்கு',
  'agent.playerDetail.ownership.picker.body': 'ஒரு புதிய ஏஜெண்ட்டைத் தேர்ந்தெடுக்கவும் — அல்லது ப்ளாட்ஃபார்ம்-நேரடிக்கு பிரிக்கவும் (எந்த ஏஜெண்ட்டுக்கும் இந்த பிளேயர் சொந்தம் இல்லை). எதிர்கால கமிஷன்கள் புதிய உரிமையாளரை பின்தொடர்கின்றன; ஏற்கனவே வரவு வைக்கப்பட்ட வரிசைகள் தொடப்படவில்லை.',
  'agent.playerDetail.ownership.picker.detach.title': 'ப்ளாட்ஃபார்மிற்கு பிரி',
  'agent.playerDetail.ownership.picker.detach.body': 'ஏஜெண்ட் உரிமையை அகற்று. அட்மின் நேரடியாக நிர்வகிக்கிறார்.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ஏஜெண்ட்களை தேடு…',
  'agent.playerDetail.ownership.picker.loading': 'ஏஜெண்ட்கள் ஏற்றப்படுகின்றனர்…',
  'agent.playerDetail.ownership.picker.empty.search': 'உங்கள் தேடலுக்கு ஏஜெண்ட்கள் பொருந்தவில்லை.',
  'agent.playerDetail.ownership.picker.empty.none': 'இன்னும் செயலில் உள்ள ஏஜெண்ட்கள் இல்லை.',
  'agent.playerDetail.ownership.picker.players.one': '{count} பிளேயர்',
  'agent.playerDetail.ownership.picker.players.other': '{count} பிளேயர்கள்',
  'agent.playerDetail.ownership.picker.current': 'தற்போதைய',
  'agent.playerDetail.ownership.picker.currentUpper': 'தற்போதைய',
  'agent.playerDetail.ownership.picker.close': 'மூடு',
  'agent.login.brand': 'பாலி ஏஜெண்ட்',
  'agent.login.subtitle': 'உங்கள் பிளேயர்களை நிர்வகிக்க உள்நுழைக',
  'agent.login.field.identifier': 'பயனர்பெயர் அல்லது மின்னஞ்சல்',
  'agent.login.field.password': 'கடவுச்சொல்',
  'agent.login.disclaimer.before': 'ஏஜெண்ட்கள் மட்டுமே. நீங்கள் ப்ளாட்ஃபார்ம் உரிமையாளராக இருந்தால், ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '-ஐ பயன்படுத்தவும்.',
  'agent.login.error.loginFailed': 'உள்நுழைவு தோல்வியடைந்தது',
  'agent.login.error.invalidCredentials': 'தவறான நற்சான்றுகள்',
  'agent.login.error.accountSuspended': 'கணக்கு நிறுத்தப்பட்டது — உங்கள் நிர்வாகியைத் தொடர்பு கொள்ளவும்.',
  'agent.login.error.networkError': 'நெட்வொர்க் பிழை — உங்கள் இணைப்பை சரிபார்த்து மீண்டும் முயற்சிக்கவும்.',
  'agent.misc.gameTile.fallbackTag': 'கேம்',
  'agent.misc.gameTile.actionSuffix': '· கட்டமைக்க',
};

const te: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "నా వివరాలు",
  'profile.group.security': "సైన్-ఇన్ & భద్రత",
  'profile.personal.title': "వ్యక్తిగత వివరాలు",
  'profile.personal.editTitle': "వ్యక్తిగత వివరాలను సవరించండి",
  'common.edit': "సవరించు",
  'profile.field.name': "పేరు",
  'profile.field.phone': "ఫోన్ నంబర్",
  'profile.field.gender': "లింగం",
  'profile.field.location': "ప్రాంతం",
  'profile.gender.male': "పురుషుడు",
  'profile.gender.female': "స్త్రీ",
  'profile.gender.other': "ఇతర",
  'profile.gender.preferNot': "చెప్పడానికి ఇష్టం లేదు",
  'profile.gender.select': "ఎంచుకోండి…",
  'profile.location.optionalHint': "ఐచ్ఛికం — మీ ప్రాంతాన్ని ఎంచుకోండి లేదా మీ పిన్‌కోడ్ టైప్ చేయండి (GPS దీన్ని మరింత ఖచ్చితంగా చేస్తుంది).",
  'profile.lang.title': "భాష",
  'profile.password.title': "పాస్‌వర్డ్",
  'profile.password.desc': "మీ ఖాతా పాస్‌వర్డ్ మార్చండి. ఇతర పరికరాలు సైన్ అవుట్ చేయబడతాయి.",
  'profile.password.cta': "మార్చు",
  'profile.password.editTitle': "పాస్‌వర్డ్ మార్చండి",
  'profile.password.current': "ప్రస్తుత పాస్‌వర్డ్",
  'profile.password.new': "కొత్త పాస్‌వర్డ్",
  'profile.password.confirm': "కొత్త పాస్‌వర్డ్‌ను నిర్ధారించండి",
  'profile.password.currentPh': "మీ ప్రస్తుత పాస్‌వర్డ్",
  'profile.password.newPh': "బలమైన పాస్‌వర్డ్ ఎంచుకోండి",
  'profile.password.confirmPh': "కొత్త పాస్‌వర్డ్‌ను మళ్లీ టైప్ చేయండి",
  'profile.password.rule': "కనీసం 8 అక్షరాలు, ఒక అక్షరం మరియు ఒక అంకెతో ఉండాలి.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "ఇలా లింక్ చేయబడింది",
  'profile.telegram.otpHere': "పాస్‌వర్డ్-రీసెట్ OTPలు ఇక్కడికి వస్తాయి.",
  'profile.telegram.unlinkChange': "అన్‌లింక్ చేసి మార్చండి",
  'profile.telegram.unlinkTitle': "Telegram అన్‌లింక్ చేయాలా?",
  'profile.telegram.unlink': "అన్‌లింక్",
  'profile.telegram.keepLinked': "లింక్‌గానే ఉంచండి",
  'profile.telegram.unlinkVerified': "మీరు తర్వాత వేరే Telegram ఖాతాను మళ్లీ లింక్ చేయవచ్చు. ఇమెయిల్ ధృవీకరణ యథావిధిగా ఉంటుంది.",
  'profile.telegram.unlinkOnly': "Telegram మీ ఏకైక ధృవీకరించిన మార్గం — ఆట కొనసాగించే ముందు మీరు మీ ఖాతాను మళ్లీ ధృవీకరించాల్సి రావచ్చు.",
  'profile.telegram.notLinkedDesc': "బ్యాకప్‌గా పాస్‌వర్డ్-రీసెట్ OTPలు అందుకోవడానికి Telegram లింక్ చేయండి.",
  'profile.telegram.openBot': "@BaaliGames_bot తెరవండి",
  'profile.telegram.generate': "లింక్ రూపొందించండి",
  'profile.telegram.generating': "రూపొందిస్తోంది…",
  'profile.avatar.cropTitle': "మీ ఫోటోను సర్దుబాటు చేయండి",
  'profile.toast.detailsSaved': "వివరాలు సేవ్ చేయబడ్డాయి",
  'profile.toast.photoUpdated': "ఫోటో అప్‌డేట్ చేయబడింది",
  'profile.toast.pwChanged': "పాస్‌వర్డ్ మార్చబడింది. ఇతర పరికరాలు సైన్ అవుట్ చేయబడ్డాయి.",
  'profile.toast.tgUnlinked': "Telegram అన్‌లింక్ చేయబడింది. వేరే చాట్‌ను కనెక్ట్ చేయడానికి కొత్త లింక్ రూపొందించండి.",
  'profile.err.phone': "10-అంకెల మొబైల్ నంబర్ నమోదు చేయండి (లేదా మార్చకుండా వదిలేయండి)",
  'profile.err.pwMismatch': "కొత్త పాస్‌వర్డ్‌లు సరిపోలడం లేదు",
  'profile.err.pwWeak': "పాస్‌వర్డ్ 8+ అక్షరాలు, ఒక అక్షరం మరియు ఒక అంకెతో ఉండాలి",
  'profile.err.pwSame': "కొత్త పాస్‌వర్డ్ ప్రస్తుత దాని కంటే వేరుగా ఉండాలి",
  'wallet.payout.title': "సేవ్ చేసిన చెల్లింపు వివరాలు",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "ఇంకా ఏదీ సేవ్ చేయలేదు",
  'wallet.payout.hint': "మీరు విత్‌డ్రా చేసినప్పుడు మీ UPI ID స్వయంచాలకంగా సేవ్ అవుతుంది. మీ తదుపరి విత్‌డ్రా అభ్యర్థన సమయంలో మీరు దీన్ని అప్‌డేట్ చేయవచ్చు.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ని ఇన్‌స్టాల్ చేయండి",
  "pwa.install.subhint": "తక్షణ, పూర్తి-స్క్రీన్ యాక్సెస్ కోసం దీన్ని మీ హోమ్ స్క్రీన్‌కు జోడించండి — యాప్ స్టోర్ అవసరం లేదు.",
  "pwa.install.listen": "వినండి",
  "pwa.install.listening": "ఆపండి",
  "pwa.install.installBtn": "ఇన్‌స్టాల్ చేయండి",
  "pwa.install.notNow": "ఇప్పుడు కాదు",
  "pwa.install.howToShow": "ఎలా ఇన్‌స్టాల్ చేయాలి",
  "pwa.install.howToHide": "దశలను దాచు",
  "pwa.install.guide.iosSafari": "Safari స్క్రీన్ దిగువన, Share బటన్‌ను నొక్కండి — ఇది పైకి బాణంతో ఉన్న చతురస్రంలా కనిపిస్తుంది.\nతెరుచుకునే మెనూలో కొంచెం కిందికి స్క్రోల్ చేయండి.\n\"Add to Home Screen\" ఎంపికను నొక్కండి.\nపై కుడి మూలలో Add నొక్కండి. ఇప్పుడు యాప్ సాధారణ యాప్‌లా మీ హోమ్ స్క్రీన్‌పై ఉంటుంది.",
  "pwa.install.guide.iosOther": "iPhone లేదా iPad లో, హోమ్ స్క్రీన్‌కు జోడించడం Safari బ్రౌజర్‌లో మాత్రమే పనిచేస్తుంది.\nSafari తెరిచి ఇదే వెబ్‌సైట్‌కు వెళ్లండి.\nSafari దిగువన Share బటన్‌ను నొక్కండి — పైకి బాణంతో ఉన్న చతురస్రం.\nకిందికి స్క్రోల్ చేసి \"Add to Home Screen\" నొక్కి, తర్వాత Add నొక్కండి.",
  "pwa.install.guide.android": "పైన ఉన్న Install బటన్‌ను నొక్కండి.\nమీ బ్రౌజర్ అడిగినప్పుడు, నిర్ధారించడానికి Install నొక్కండి.\nపూర్తయింది — ఇప్పుడు యాప్ మీ హోమ్ స్క్రీన్‌పై ఉంది. సాధారణ యాప్‌లా ఎప్పుడైనా తెరవండి.\nపాప్-అప్ కనిపించలేదా? మీ బ్రౌజర్ మెనూను తెరవండి — మూడు చుక్కలు, సాధారణంగా పై కుడివైపు — ఆపై \"Install app\" నొక్కండి.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "నేరుగా రీఛార్జ్",
  "agent.directRecharge.subtitle.player": "ఒక ఆటగాడిని ఎంచుకుని మీ పూల్ నుండి రీఛార్జ్ చేయండి. అభ్యర్థన అవసరం లేదు.",
  "agent.directRecharge.subtitle.subagent": "ఒక సబ్-ఏజెంట్‌ను ఎంచుకుని మీ పూల్ నుండి వారి పూల్‌కు రీఛార్జ్ చేయండి.",
  "agent.directRecharge.search.player": "రీఛార్జ్ చేయడానికి ఆటగాడిని వెతకండి (పేరు · ఫోన్ · ఇమెయిల్)…",
  "agent.directRecharge.search.subagent": "రీఛార్జ్ చేయడానికి సబ్-ఏజెంట్‌ను వెతకండి (పేరు · ఇమెయిల్ · UAID)…",
  "agent.directRecharge.clearSearch": "శోధనను తొలగించు",
  "agent.directRecharge.searching": "వెతుకుతోంది…",
  "agent.directRecharge.noMatches": "సరిపోలికలు లేవు.",
  "agent.directRecharge.empty.player": "మీ జాబితాలో ఇంకా ఆటగాళ్లు లేరు.",
  "agent.directRecharge.empty.subagent": "ఇంకా నేరుగా సబ్-ఏజెంట్లు లేరు.",
  "agent.directRecharge.searchFailed": "శోధన విఫలమైంది",
  "agent.directRecharge.label.wallet": "వాలెట్",
  "agent.directRecharge.label.pool": "పూల్",
  "agent.directRecharge.modal.title.player": "నేరుగా ఆటగాడి రీఛార్జ్",
  "agent.directRecharge.modal.title.subagent": "నేరుగా సబ్-ఏజెంట్ రీఛార్జ్",
  "agent.directRecharge.modal.close": "మూసివేయి",
  "agent.directRecharge.modal.targetPool": "లక్ష్య పూల్",
  "agent.directRecharge.modal.operatingWallet": "ఆపరేటింగ్ వాలెట్",
  "agent.directRecharge.modal.dayToDay": "రోజువారీ బ్యాలెన్స్",
  "agent.directRecharge.modal.securityDeposit": "సెక్యూరిటీ డిపాజిట్",
  "agent.directRecharge.modal.collateral": "తాకట్టు పూల్",
  "agent.directRecharge.modal.amount": "మొత్తం (₹)",
  "agent.directRecharge.modal.overdraw": "మీ పూల్ (₹{pool}) కంటే ఎక్కువ చెల్లించలేరు. మొత్తాన్ని తగ్గించండి.",
  "agent.directRecharge.modal.reason": "కారణం / గమనిక",
  "agent.directRecharge.modal.reasonPlaceholder": "నగదు సేకరించబడింది · ref 4291",
  "agent.directRecharge.modal.reasonHint": "మీ వాలెట్ లెడ్జర్ + ఆడిట్ లాగ్‌లో నమోదు చేయబడుతుంది.",
  "agent.directRecharge.modal.proofLabel": "చెల్లింపు స్క్రీన్‌షాట్‌ను అప్‌లోడ్ చేయండి (ఐచ్ఛికం)",
  "agent.directRecharge.modal.proofHint": "చరిత్ర ద్వారా స్వీకర్తకు కనిపిస్తుంది.",
  "agent.directRecharge.modal.previewEmpty": "లావాదేవీని ప్రివ్యూ చేయడానికి మొత్తాన్ని నమోదు చేయండి.",
  "agent.directRecharge.modal.previewBody": "మీ వాలెట్ నుండి {name} యొక్క {pool}కు పంపబడింది. సురక్షిత బదిలీ; లెడ్జర్ + ఆడిట్‌లో నమోదు.",
  "agent.directRecharge.modal.pool.playerWallet": "వాలెట్",
  "agent.directRecharge.modal.pool.operatingWallet": "ఆపరేటింగ్ వాలెట్",
  "agent.directRecharge.modal.pool.securityDeposit": "సెక్యూరిటీ డిపాజిట్",
  "agent.directRecharge.modal.cancel": "రద్దు",
  "agent.directRecharge.modal.send": "₹{amount} పంపండి",
  "agent.directRecharge.badge.player": "ఆటగాడు",
  "agent.directRecharge.badge.subagent": "సబ్-ఏజెంట్",
  "agent.directRecharge.modal.yourDirectSubagent": "మీ నేరుగా సబ్-ఏజెంట్",
  "agent.directRecharge.modal.fundingSource": "నిధి మూలం",
  "agent.directRecharge.modal.you": "(మీరు)",
  "agent.directRecharge.modal.yourPool": "మీ పూల్",
  "agent.directRecharge.modal.notSignedIn": "ఏజెంట్‌గా సైన్ ఇన్ కాలేదు",
  "agent.directRecharge.modal.failed": "విఫలమైంది",
  'common.signIn': 'సైన్ ఇన్ చేయండి', 'common.signUp': 'సైన్ అప్ చేయండి', 'common.login': 'లాగిన్',
  'common.logout': 'లాగౌట్', 'common.continue': 'కొనసాగించండి', 'common.cancel': 'రద్దు',
  'common.save': 'సేవ్', 'common.update': 'అప్‌డేట్', 'common.clear': 'క్లియర్',
  'common.back': 'వెనక్కి', 'common.loading': 'లోడ్ అవుతోంది…', 'common.sending': 'పంపుతోంది…',
  'common.saving': 'సేవ్ అవుతోంది…', 'common.optional': 'ఐచ్ఛికం',
  'common.show': 'చూపించు', 'common.hide': 'దాచు', 'common.profile': 'ప్రొఫైల్', 'common.settings': 'సెట్టింగ్‌లు',
  'nav.brand': 'బాలీ గేమ్స్', 'nav.home': 'హోమ్', 'nav.games': 'గేమ్స్',
  'nav.wallet': 'వాలెట్', 'nav.rewards': 'రివార్డ్‌లు', 'nav.profile': 'ప్రొఫైల్',
  'login.title': 'తిరిగి స్వాగతం', 'login.subtitle': 'ఆట కొనసాగించడానికి సైన్ ఇన్ చేయండి.',
  'login.idLabel': 'యూజర్‌నేమ్, ఇమెయిల్ లేదా ఫోన్',
  'login.idPlaceholder': 'యూజర్‌నేమ్, ఇమెయిల్ లేదా మొబైల్',
  'login.passwordLabel': 'పాస్‌వర్డ్', 'login.passwordPlaceholder': 'మీ పాస్‌వర్డ్ నమోదు చేయండి',
  'login.forgot': 'మర్చిపోయారా?', 'login.cta': 'సైన్ ఇన్',
  'login.signingIn': 'సైన్ ఇన్ అవుతోంది…',
  'login.newHere': 'క్రొత్తగా ఉన్నారా?', 'login.createAccount': 'ఖాతాను సృష్టించండి',
  'register.title': 'మీ ఖాతాను సృష్టించండి', 'register.subtitle': 'పేరును ఎంచుకోండి.',
  'register.usernameLabel': 'యూజర్‌నేమ్', 'register.usernamePlaceholder': 'ప్రత్యేకమైన పేరును ఎంచుకోండి',
  'register.usernameHelp': 'అక్షరాలు, అంకెలు, అండర్‌స్కోర్. ౩–౨౪ అక్షరాలు.',
  'register.emailLabel': 'ఇమెయిల్', 'register.emailPlaceholder': 'మీ ఇమెయిల్ చిరునామా',
  'register.mobileLabel': 'మొబైల్ నంబర్', 'register.mobilePlaceholder': '౧౦-అంకెల మొబైల్',
  'register.mobileHelp': 'దేశ కోడ్‌ను మేము స్వయంచాలకంగా జోడిస్తాము.',
  'register.passwordLabel': 'పాస్‌వర్డ్', 'register.passwordPlaceholder': 'బలమైన పాస్‌వర్డ్‌ను ఎంచుకోండి',
  'register.passwordHelp': 'కనీసం ౮ అక్షరాలు — ఒక అక్షరం మరియు ఒక అంకె.',
  'register.referralLabel': 'రిఫరల్ కోడ్', 'register.referralPlaceholder': 'మిత్రుని ఇన్‌వైట్ కోడ్',
  'register.cta': 'ఖాతాను సృష్టించండి', 'register.creating': 'సృష్టిస్తోంది…',
  'register.alreadyPlayer': 'ఇప్పటికే ఆటగాడా?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'మీ గురించి', 'register.step.signin': 'సైన్-ఇన్', 'register.step.finish': 'ముగించు',
  'register.step.aboutSubtitle': 'మీ గురించి కొద్దిగా చెప్పండి', 'register.step.signinSubtitle': 'మీరు ఎలా సైన్-ఇన్ చేస్తారో సెట్ చేయండి', 'register.step.finishSubtitle': 'స్థానం ఎంచుకుని పూర్తి చేయండి',
  'register.firstName': 'పేరు', 'register.firstNamePh': 'ఆరవ్',
  'register.lastName': 'ఇంటి పేరు', 'register.lastNamePh': 'శర్మ',
  'register.dob': 'పుట్టిన తేదీ', 'register.dobHelp': 'ఆడటానికి మీ వయస్సు 18 లేదా ఎక్కువ ఉండాలి',
  'register.v.required': 'అవసరం', 'register.v.nameMin': 'కనీసం 2 అక్షరాలు',
  'register.v.tooLong': 'చాలా పొడవు', 'register.v.nameChars': 'అక్షరాలు, స్పేస్, హైఫన్ మాత్రమే',
  'register.v.invalidDate': 'చెల్లని తేదీ', 'register.v.minAge': 'మీ వయస్సు కనీసం 18 ఉండాలి',
  'register.v.tooOld': 'దయచేసి సంవత్సరం తనిఖీ చేయండి',
  'register.v.usernameMin': 'కనీసం 3 అక్షరాలు', 'register.v.usernameMax': 'చాలా పొడవు (గరిష్టం 24)',
  'register.v.usernameChars': 'అక్షరాలు, సంఖ్యలు, అండర్‌స్కోర్ మాత్రమే', 'register.v.emailShape': 'చెల్లని ఇమెయిల్ లాగా ఉంది',
  'register.v.phoneDigits': '10 అంకెల సంఖ్య ఉండాలి', 'register.v.phonePrefix': 'భారతీయ మొబైల్ 6–9తో మొదలవుతుంది',
  'register.v.pwdMin': 'కనీసం 8 అక్షరాలు', 'register.v.pwdLetter': 'కనీసం ఒక అక్షరం ఉండాలి',
  'register.v.pwdNumber': 'కనీసం ఒక సంఖ్య ఉండాలి',
  'register.v.referralShort': 'చాలా చిన్నది', 'register.v.referralChars': 'అక్షరాలు మరియు సంఖ్యలు మాత్రమే',
  'register.v.usernameTaken': 'ఈ యూజర్‌నేమ్ ఇప్పటికే తీసుకోబడింది',
  'register.v.emailTaken': 'ఈ ఇమెయిల్ ఇప్పటికే నమోదు చేయబడింది',
  'register.v.phoneTaken': 'ఈ నంబర్ ఇప్పటికే నమోదు చేయబడింది',
  'register.v.noLocation': 'కొనసాగించడానికి కనీసం ఒక స్థాన పద్ధతిని పంచుకోండి.',
  'register.welcome.title': 'స్వాగతం', 'register.welcome.subtitle': 'మీ ఖాతా సిద్ధం. ఇప్పుడు భద్రపరచండి లేదా నేరుగా ప్రారంభించండి?',
  'register.welcome.verifyTelegram': 'టెలిగ్రామ్ ద్వారా ధృవీకరించండి',
  'register.welcome.verifyExplain': '⚠ ముఖ్యం: ఇప్పుడే టెలిగ్రామ్‌తో ధృవీకరించండి. ధృవీకరణ లేకుండా, పాస్‌వర్డ్ మర్చిపోతే మీరే రీసెట్ చేయలేరు — మీ ఏజెంట్ లేదా అడ్మిన్ మాత్రమే రీసెట్ చేయగలరు. తర్వాత ప్రొఫైల్ నుండి కూడా ధృవీకరించవచ్చు.',
  'register.welcome.verifyHeads': 'దీన్ని తర్వాతి కోసం సేవ్ చేయండి',
  'register.welcome.verifyConsequence': 'ఇమెయిల్ ధృవీకరణ ఇప్పుడు అందుబాటులో లేదు. తర్వాత పాస్‌వర్డ్ రీసెట్ చేయడానికి టెలిగ్రామ్‌తో ధృవీకరించండి. చేయకపోతే, పాస్‌వర్డ్ మర్చిపోతే మీ ఏజెంట్ లేదా అడ్మిన్ మాత్రమే రీసెట్ చేయగలరు.',
  'register.welcome.continue': 'యాప్‌కి కొనసాగండి', 'register.welcome.continueLogin': 'కొనసాగడానికి సైన్ ఇన్ చేయండి', 'register.welcome.useDifferent': 'వేరే ఖాతా ఉపయోగించండి',
  'register.geo.title': 'మీరు ఎక్కడ నుండి ఆడుతున్నారు?', 'register.geo.subtitle': 'మిమ్మల్ని సమీపంలోని ఏజెంట్‌తో జతచేయడానికి మీ స్థానాన్ని ఉపయోగిస్తాము.',
  'register.geo.showAlt': 'స్థానం పంచుకోలేరా? వేరే పద్ధతిని ఉపయోగించండి',
  'register.geo.hideAlt': 'ఇతర పద్ధతులను దాచండి', 'register.geo.captured': 'స్థానం క్యాప్చర్ చేయబడింది',
  'register.geo.capturedHint': 'మీరు ఎప్పుడైనా ప్రొఫైల్ నుండి దీన్ని మార్చవచ్చు.',
  'register.geo.verifyNote': 'సైన్-అప్ తర్వాత, ప్రొఫైల్ నుండి టెలిగ్రామ్ (పాస్‌వర్డ్ రీసెట్ కోసం) లేదా ఇమెయిల్‌తో ఎప్పుడైనా ధృవీకరించవచ్చు. ధృవీకరణ ఐచ్ఛికం.',
  'verify.title': 'మీ ఖాతాను ధృవీకరించండి', 'verify.subtitle': 'ఒక ఎంపికను ఎంచుకోండి — అంతే.',
  'verify.tabTelegram': 'టెలిగ్రామ్', 'verify.tabEmail': 'ఇమెయిల్',
  'verify.emailSent': '{email}కి ధృవీకరణ లింక్ పంపాము.',
  'verify.emailHint': 'ఇదే పరికరంలో తెరవండి. కనిపించలేదా? స్పామ్/ప్రొమోషన్‌లు చూడండి.',
  'verify.telegramOpen': '@BaaliGames_bot తెరవండి',
  'verify.telegramExpiry': 'లింక్ ౩౦ నిమిషాల్లో గడువు. టెలిగ్రామ్‌లో Start ఒకసారి నొక్కండి.',
  'verify.goToLogin': 'లాగిన్‌కు వెళ్ళు',
  'verify.email.title': 'ఇమెయిల్ ధృవీకరించబడింది', 'verify.email.sub': 'మీ ఖాతా క్రియాశీలంగా ఉంది. ఆడే సమయం.',
  'verify.email.failed': 'ధృవీకరణ విఫలమైంది', 'verify.email.verifying': 'ధృవీకరిస్తోంది…',
  'verify.backRegister': 'రిజిస్టర్‌కు తిరిగి',
  'forgot.title': 'మీ ఖాతాను తిరిగి పొందండి', 'forgot.subtitle': 'తిరిగి రాడానికి మార్గాన్ని పంపుతాము.',
  'forgot.idLabel': 'యూజర్‌నేమ్, ఇమెయిల్ లేదా ఫోన్', 'forgot.idPlaceholder': 'యూజర్‌నేమ్, ఇమెయిల్ లేదా మొబైల్',
  'forgot.sendVia': 'ద్వారా పంపండి', 'forgot.email': 'ఇమెయిల్', 'forgot.telegram': 'టెలిగ్రామ్ OTP',
  'forgot.telegramHelp': 'మీ ఖాతా మా బోట్‌కు లింక్ చేసి ఉంటేనే టెలిగ్రామ్ పనిచేస్తుంది.',
  'forgot.cta': 'రికవరీ పంపు',
  'forgot.sentEmail': 'ఖాతా ఉంటే, రీసెట్ లింక్ ఇమెయిల్ చేయబడింది.',
  'forgot.sentTelegram': 'ఖాతా ఉంటే, టెలిగ్రామ్ ద్వారా ౬-అంకెల కోడ్ పంపబడింది.',
  'forgot.redirecting': 'రీసెట్ పేజీకి దారి మారుస్తోంది…', 'forgot.back': 'లాగిన్‌కు తిరిగి',
  'reset.title': 'క్రొత్త పాస్‌వర్డ్ సెట్ చేయండి',
  'reset.subOtp': 'టెలిగ్రామ్‌లో పంపిన కోడ్‌ను ఉపయోగించండి.',
  'reset.subLink': 'క్రొత్త పాస్‌వర్డ్‌ను ఎంచుకోండి మరియు తిరిగి ఆడండి.',
  'reset.otpLabel': '౬-అంకెల కోడ్', 'reset.newPasswordLabel': 'క్రొత్త పాస్‌వర్డ్',
  'reset.cta': 'పాస్‌వర్డ్‌ను అప్‌డేట్ చేయండి', 'reset.updating': 'అప్‌డేట్ అవుతోంది…',
  'reset.successTitle': 'పాస్‌వర్డ్ అప్‌డేట్ చేయబడింది', 'reset.successSub': 'సిద్ధం. ఆడే సమయం.',
  'home.welcomeTo': 'స్వాగతం', 'home.heroSub': 'నైపుణ్య-ఆధారిత గేమింగ్ హబ్. ఆడడానికి సైన్ ఇన్ చేయండి లేదా ఖాతాను సృష్టించండి.',
  'home.skillBased': 'నైపుణ్య-ఆధారితం', 'home.skillBasedSub': 'వ్యూహం గెలుస్తుంది',
  'home.fairPlay': 'న్యాయమైన ఆట', 'home.fairPlaySub': 'యాంటీ-చీట్ ఆన్',
  'home.instantPayout': 'UPI చెల్లింపు', 'home.instantPayoutSub': '24 గంటలలోపు',
  'home.featured': 'ఎంపిక గేమ్స్', 'home.tapToPlay': 'ఆడడానికి ట్యాప్ చేయండి',
  'home.ready': 'ఆడడానికి సిద్ధంగా ఉన్నారా?', 'home.dropIn': 'చేరండి మరియు నిజమైన బహుమతులను గెలుచుకోండి.',
  'home.start': 'ప్రారంభించు',
  'home.welcomeBack': 'తిరిగి స్వాగతం', 'home.balance': 'మీ బ్యాలెన్స్', 'home.live': 'లైవ్',
  'home.add': 'జోడించు', 'home.manage': 'నిర్వహించు', 'home.rewards': 'రివార్డ్‌లు',
  'home.todayChallenge': 'నేటి సవాలు', 'home.todayChallengeSub': 'ఒక మ్యాచ్ గెలవండి — స్ట్రీక్ బోనస్ సంపాదించండి.',
  'home.view': 'చూడండి', 'home.yourGames': 'మీ గేమ్స్', 'home.viewAll': 'అన్నీ చూడండి',
  'profile.details': 'వివరాలు', 'profile.address': 'చిరునామా', 'profile.languages': 'భాషలు',
  'profile.account': 'ఖాతా', 'profile.photo': 'ఫోటో', 'profile.preferred': 'ప్రాధాన్య భాష',
  'profile.lang.primary': 'ప్రాథమిక భాష', 'profile.lang.primarySub': 'యాప్-వ్యాప్త డిఫాల్ట్',
  'profile.lang.locked': 'లాక్ చేయబడింది', 'profile.lang.second': 'రెండవ భాష',
  'profile.lang.pickHelp': 'మీరు మాట్లాడే మరో భాషను ఎంచుకోండి. కార్డుపై ట్యాప్ చేయండి.',
  'profile.lang.saved': 'రెండవ భాష సేవ్ చేయబడింది', 'profile.lang.cleared': 'రెండవ భాష తొలగించబడింది',
  'lang.switcher.aria': 'భాషను ఎంచుకోండి', 'lang.switcher.title': 'మీ భాషను ఎంచుకోండి',
  'lang.switcher.sub': 'మొత్తం యాప్ తక్షణం నవీకరించబడుతుంది.', 'lang.switcher.current': 'ప్రస్తుత',
  'rewards.title': 'రివార్డ్‌లు & ఆఫర్లు', 'rewards.subtitle': 'ఇప్పుడే సంపాదించడానికి లేదా క్లెయిమ్ చేయడానికి బోనస్‌లు.',
  'rewards.tab.available': 'అందుబాటులో', 'rewards.tab.inReview': 'సమీక్షలో', 'rewards.tab.claimed': 'క్లెయిమ్ చేసిన',
  'rewards.claim.cta': 'క్లెయిమ్', 'rewards.claimed.pending': 'సమీక్ష పెండింగ్',
  'rewards.empty.available.title': 'ప్రస్తుతం ఆఫర్లు లేవు',
  'rewards.toast.credited': 'బోనస్ జోడించబడింది — {amount} మీ వాలెట్‌లో',
  'wallet.balance': 'బ్యాలెన్స్', 'wallet.addMoney': 'డబ్బు జోడించు', 'wallet.withdraw': 'విత్‌డ్రా',
  'wallet.pending.title': 'పెండింగ్ అభ్యర్థనలు', 'wallet.state.pending': 'పెండింగ్',
  'wallet.state.paid': 'చెల్లించబడింది',
  'wallet.pending.txnPrefix': 'లావాదేవీ:',
  'wallet.pending.upload.label': 'వేగవంతమైన చర్యకు చెల్లింపు స్క్రీన్‌షాట్ అప్‌లోడ్ చేయండి.',
  'wallet.pending.upload.hint':  'అభ్యర్థన సృష్టించిన తర్వాత మీకు 24 గంటలు ఉన్నాయి.',
  'wallet.pending.upload.failed': 'అప్‌లోడ్ విఫలమైంది',
  'wallet.activity.balanceAfter': 'బ్యాలెన్స్ {balance}',
  'wallet.activity.range': '{total}లో {from}–{to}',
  'wallet.activity.window': 'మీ గత {days} రోజుల కార్యకలాపం',
  'wallet.history.summary.one':   '{matches} మ్యాచ్ · గెలిచినవి {wins} · ఖర్చు {bets}',
  'wallet.history.summary.other': '{matches} మ్యాచ్‌లు · గెలిచినవి {wins} · ఖర్చు {bets}',
  'wallet.history.summary.refunded': ' · తిరిగి ఇవ్వబడింది {refunds}',
  'ledger.kind.bet': 'మ్యాచ్ ఎంట్రీ', 'ledger.kind.win': 'మ్యాచ్ బహుమతి',
  'ledger.kind.refund': 'మ్యాచ్ రీఫండ్', 'ledger.kind.forfeit': 'మ్యాచ్ జప్తు',
  'ledger.kind.fee': 'ప్లాట్‌ఫామ్ ఫీజు', 'ledger.kind.recharge': 'టాప్-అప్',
  'ledger.kind.admin_credit': 'అడ్మిన్ క్రెడిట్', 'ledger.kind.admin_debit': 'అడ్మిన్ డెబిట్',
  'ledger.kind.referral_bonus': 'రెఫరల్ బోనస్', 'ledger.kind.signup_bonus': 'సైన్-అప్ బోనస్',
  'ledger.kind.offer_credit': 'ఆఫర్ బహుమతి',
  'ledger.kind.withdrawal_hold': 'ఉపసంహరణ హోల్డ్', 'ledger.kind.withdrawal_refund': 'ఉపసంహరణ రీఫండ్',
  'ledger.kind.agent_topup_in': 'ఏజెంట్ టాప్-అప్', 'ledger.kind.agent_co_fund': 'ఏజెంట్ సహకారం', 'ledger.kind.turnover_reward': 'క్యాష్‌బ్యాక్',
  'ledger.withdrawalPaid': 'ఉపసంహరణ చెల్లించబడింది', 'ledger.withdrawalPaidPartial': 'ఉపసంహరణ చెల్లించబడింది (పాక్షికం)',
  'ledger.withdrawalPaid.txn': 'చెల్లించబడింది · లావాదేవీ {txnId}',
  'ledger.withdrawalRejected': 'ఉపసంహరణ తిరస్కరించబడింది', 'ledger.withdrawalRejected.note': 'హోల్డ్ మొత్తం తిరిగి ఇవ్వబడింది',
  'ledger.withdrawalExpired': 'ఉపసంహరణ గడువు ముగిసింది', 'ledger.awaitingProof': 'రుజువు కోసం వేచి ఉంది',
  'proofPicker.button.compact': 'స్క్రీన్‌షాట్ ఎంచుకోండి', 'proofPicker.button.prominent': 'స్క్రీన్‌షాట్ జోడించండి',
  'proofPicker.processing': 'ప్రాసెస్ చేస్తోంది…',
  'proofPicker.error.mime': 'JPG, PNG లేదా WebP చిత్రాలు మాత్రమే అనుమతించబడతాయి.',
  'proofPicker.error.size': 'ఫైల్ చాలా పెద్దది — గరిష్టంగా 5 MB.',
  'proofPicker.error.processing': 'చిత్రాన్ని ప్రాసెస్ చేయలేకపోయాము',
  'proofPicker.previewAlt': 'ఎంచుకున్న స్క్రీన్‌షాట్ ప్రివ్యూ',
  'proofViewer.state.none': 'స్క్రీన్‌షాట్ కోసం వేచి ఉంది', 'proofViewer.state.available': 'స్క్రీన్‌షాట్ చూడండి',
  'proofViewer.state.expired': 'స్క్రీన్‌షాట్ అందుబాటులో లేదు', 'proofViewer.state.viewLimit': 'వీక్షణ పరిమితి దాటింది',
  'proofViewer.loading': 'లోడ్ అవుతోంది…',
  'proofViewer.title.viewLimit': 'మీరు ఈ స్క్రీన్‌షాట్‌ను గరిష్టంగా 2 సార్లు తెరిచారు',
  'proofViewer.error.load': 'స్క్రీన్‌షాట్ లోడ్ చేయలేకపోయాము',
  'proofViewer.lastView': 'చివరి వీక్షణ — చిత్రం గడువు ముగుస్తుంది',
  'proofViewer.viewsRemaining.one': 'మరో {n} సారి చూడవచ్చు',
  'proofViewer.viewsRemaining.other': 'మరో {n} సార్లు చూడవచ్చు',
  'proofViewer.imageAlt': 'చెల్లింపు రుజువు', 'proofViewer.close': 'మూసివేయండి',
  'recharge.title': 'డబ్బు జోడించు', 'recharge.step1.amount': 'మొత్తం',
  'recharge.step3.txnId': 'UPI లావాదేవీ ID', 'recharge.step3.submit': 'సమీక్షకు పంపండి',
  'recharge.success.title': 'పంపబడింది — సమీక్షకు ఎదురుచూస్తోంది',
  'withdraw.title': 'విత్‌డ్రా', 'withdraw.amount': 'మొత్తం', 'withdraw.upiId': 'మీ UPI ID',
  'withdraw.submit': 'సమర్పించు', 'withdraw.success.title': 'పంపబడింది — చెల్లింపు కోసం వేచి ఉంది',
  'payment.openUpiApp': 'ఇప్పుడు చెల్లించు', 'payment.upi.copy': 'కాపీ',
  'common.next': 'తదుపరి', 'common.close': 'మూయి',

  // Location capture (Phase R3)
  'geo.purpose': 'వేగవంతమైన మరియు సులభమైన చెల్లింపు సేవ కోసం, ఖచ్చితమైన స్థానం మరియు సంప్రదింపు నంబర్ ఇవ్వండి.',
  'geo.change': 'మార్చు',
  'geo.gps.button': 'నా ప్రస్తుత స్థానాన్ని ఉపయోగించు',
  'geo.gps.benefit': 'వేగవంతమైనది — ఒక ట్యాప్, టైపింగ్ లేదు.',
  'geo.gps.denied': 'స్థాన అనుమతి నిరాకరించబడింది. పిన్‌కోడ్ ఉపయోగించండి లేదా క్రింద ఎంచుకోండి.',
  'geo.gps.failed': 'స్థానం పొందలేకపోయాము. పిన్‌కోడ్ ప్రయత్నించండి.',
  'geo.gps.notResolved': 'మీ స్థానాన్ని ఏ ప్రాంతంతోనూ సరిపోల్చలేకపోయాము. దయచేసి ఎంచుకోండి.',
  'geo.pincode.label': 'పిన్‌కోడ్',
  'geo.pincode.notFound': 'పిన్‌కోడ్ దొరకలేదు. దయచేసి మీ ప్రాంతాన్ని ఎంచుకోండి.',
  'geo.pincode.lookupFailed': 'పిన్‌కోడ్ శోధన విఫలమైంది. దయచేసి ఎంచుకోండి.',
  'geo.multipleAreas.label': 'ఈ పిన్‌కోడ్‌లో అనేక ప్రాంతాలు — మీది ఎంచుకోండి',
  'geo.multipleAreas.placeholder': 'మీ ప్రాంతాన్ని ఎంచుకోండి',
  'geo.manual.title': 'మాన్యువల్‌గా ఎంచుకోండి',
  'geo.manual.close': 'మాన్యువల్ దాచు',
  'geo.state.label': 'రాష్ట్రం', 'geo.state.placeholder': 'రాష్ట్రాన్ని ఎంచుకోండి',
  'geo.district.label': 'జిల్లా', 'geo.district.placeholder': 'జిల్లా ఎంచుకోండి',
  'geo.area.label': 'ప్రాంతం / పట్టణం', 'geo.area.placeholder': 'ప్రాంతాన్ని ఎంచుకోండి',
  'geo.privacy.note': 'స్థానిక ఏజెంట్‌ను కేటాయించడానికి మాత్రమే. షేర్ చేయబడదు.',
  'geo.required.hint': 'రెఫరల్ కోడ్ లేకపోతే స్థానం అవసరం.',

  // Agent app strings (1,407 keys) — full Telugu coverage
  'agent.nav.dashboard': 'డాష్‌బోర్డ్',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'లెక్క',
  'agent.nav.players':   'ప్లేయర్లు',
  'agent.nav.agents':    'ఏజెంట్లు',
  'agent.nav.rewards':   'రివార్డ్‌లు',
  'agent.nav.profile':   'ప్రొఫైల్',
  'agent.bottomnav.home':    'హోమ్',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'లెక్క',
  'agent.bottomnav.players': 'ప్లేయర్లు',
  'agent.bottomnav.agents':  'ఏజెంట్లు',
  'agent.bottomnav.rewards': 'రివార్డ్‌లు',
  'agent.bottomnav.profile': 'ప్రొఫైల్',
  'agent.dashboard.header.welcomePrefix':  'తిరిగి స్వాగతం, ',
  'agent.dashboard.header.welcomeFallback': 'మిత్రమా',
  'agent.dashboard.header.subtitle':       'మీ వ్యాపారం ఒకే చూపులో — పూల్, డిపాజిట్, ప్లేయర్లు, సబ్-ఏజెంట్లు, P&L, ఎక్స్‌పోజర్',
  'agent.dashboard.range.today':           'ఈ రోజు',
  'agent.dashboard.range.yesterday':       'నిన్న',
  'agent.dashboard.range.thisWeek':        'ఈ వారం',
  'agent.dashboard.range.lastWeek':        'గత వారం',
  'agent.dashboard.range.thisMonth':       'ఈ నెల',
  'agent.dashboard.range.lastMonth':       'గత నెల',
  'agent.dashboard.range.complete':        'మొత్తం',
  'agent.dashboard.depositBar.loading':         'డిపాజిట్ కవరేజ్ లోడ్ అవుతోంది…',
  'agent.dashboard.depositBar.label':           'డిపాజిట్ కవరేజ్',
  'agent.dashboard.depositBar.balanceOfTarget': '· {target} టార్గెట్‌లో {balance}',
  'agent.dashboard.depositBar.tapToView':       'పూర్తి డిపాజిట్ పేజీని చూడడానికి ట్యాప్ చేయండి.',
  'agent.dashboard.depositBar.caption.suspended': 'ఖాతా నిలిపివేయబడింది. అడ్మిన్‌ను సంప్రదించండి.',
  'agent.dashboard.depositBar.caption.breach':  'మీ వాలెట్ డిపాజిట్ సీలింగ్‌లో 100% వాడేసింది. విత్‌డ్రాలు లాక్ అయ్యాయి. వెంటనే టాప్-అప్ చేయండి.',
  'agent.dashboard.depositBar.caption.warning': 'మీ వాలెట్ డిపాజిట్ సీలింగ్‌లో {pct}% వద్ద ఉంది. వార్నింగ్ 80%-వద్ద, లాక్‌డౌన్ 100%-వద్ద.',
  'agent.dashboard.depositBar.caption.caution': 'మీ వాలెట్ డిపాజిట్ కవరేజ్‌లో {pct}% తినేసింది. 80%-కి దిగువ — ఇంకా సురక్షితం.',
  'agent.dashboard.depositBar.caption.safe':    'డిపాజిట్ పూర్తిగా చెక్కుచెదరలేదు. కవరేజ్ ఏదీ వాడబడలేదు.',
  'agent.dashboard.locatorSkip.title': 'అలర్ట్ · జీరో సెక్యూరిటీ డిపాజిట్',
  'agent.dashboard.locatorSkip.body':  'మీ సెక్యూరిటీ డిపాజిట్ {amount}. మీరు దానిని ఫండ్ చేసే వరకు, జియోగ్రాఫిక్ లొకేటర్ నుండి కొత్త ప్లేయర్లు మీకు రారు. ఇప్పటికే ఉన్న ప్లేయర్లు + డైరెక్ట్ రెఫరల్‌లు + మాన్యువల్ అసైన్‌మెంట్‌లకు ఎటువంటి ప్రభావం లేదు — కేవలం ఆర్గానిక్ జియో-రూట్ సైన్‌అప్‌లు మాత్రమే మిమ్మల్ని దాటిపోతాయి.',
  'agent.dashboard.locatorSkip.cta':   'డిపాజిట్ ఫండ్ చేయండి',
  'agent.dashboard.defaulter.title':            'డౌన్‌లైన్ డిఫాల్టర్ అలర్ట్',
  'agent.dashboard.defaulter.summary.critical.one':   'మీ {n} డైరెక్ట్ సబ్-ఏజెంట్ డిఫాల్ట్‌లో ఉన్నారు — వారు లాక్ అయ్యారు లేదా వారి డిపాజిట్‌ను బ్రీచ్ చేశారు.',
  'agent.dashboard.defaulter.summary.critical.other': 'మీ {n} డైరెక్ట్ సబ్-ఏజెంట్లు డిఫాల్ట్‌లో ఉన్నారు — వారు లాక్ అయ్యారు లేదా వారి డిపాజిట్‌ను బ్రీచ్ చేశారు.',
  'agent.dashboard.defaulter.summary.warning.one':    'మీ {n} డైరెక్ట్ సబ్-ఏజెంట్ వారి డిపాజిట్ సీలింగ్‌లో 80%+ వద్ద ఉన్నారు — డిఫాల్టర్ రిస్క్.',
  'agent.dashboard.defaulter.summary.warning.other':  'మీ {n} డైరెక్ట్ సబ్-ఏజెంట్లు వారి డిపాజిట్ సీలింగ్‌లో 80%+ వద్ద ఉన్నారు — డిఫాల్టర్ రిస్క్.',
  'agent.dashboard.defaulter.worst':             'అత్యంత తీవ్రం: @{username} డిపాజిట్ సీలింగ్‌లో {pct}% వద్ద.',
  'agent.dashboard.defaulter.tapHint':           'డైరెక్ట్ సబ్-ఏజెంట్ల జాబితాకు దూకడానికి ట్యాప్ చేయండి. డిఫాల్టర్ రోలు హైలైట్ చేయబడ్డాయి.',
  'agent.dashboard.marginPill.locked':  'లాక్',
  'agent.dashboard.marginPill.caution': 'జాగ్రత్త',
  'agent.dashboard.marginPill.warning': 'వార్నింగ్',
  'agent.dashboard.marginPill.breach':  'బ్రీచ్',
  'agent.dashboard.sections.morning.title':    'మార్నింగ్ బ్రీఫింగ్',
  'agent.dashboard.sections.morning.subtitle': 'లైవ్ స్నాప్‌షాట్ — ప్రతి సంఖ్య స్వయంచాలకంగా రిఫ్రెష్ అవుతుంది',
  'agent.dashboard.sections.activity.title':    'కార్యకలాపం',
  'agent.dashboard.sections.activity.subtitle': 'టాప్ స్పెండర్లు మరియు అత్యంత ఇటీవలి ప్లేయర్ చెల్లింపు ఈవెంట్‌లు',
  'agent.dashboard.sections.forensic.title':    'ఫోరెన్సిక్ వివరం',
  'agent.dashboard.sections.forensic.subtitle': 'ఏ సెక్షన్‌ను అయినా విస్తరించడానికి ట్యాప్ చేయండి — ఈ పేజీని ప్రశాంతంగా ఉంచడానికి డిఫాల్ట్‌గా మూసివేయబడింది',
  'agent.dashboard.accordions.pnlDeep.title':      'లాభం & నష్టం డీప్-డైవ్',
  'agent.dashboard.accordions.pnlDeep.helper':     'మీ ఆదాయాల పిల్లర్-వారీ + గేమ్-వారీ విభజన. అంతర్లీన మ్యాచ్‌ల కోసం గేమ్ రోపై క్లిక్ చేయండి.',
  'agent.dashboard.accordions.leaderboard.title':  'డౌన్‌లైన్ లీడర్‌బోర్డ్',
  'agent.dashboard.accordions.leaderboard.helper': 'మీ సబ్-ఏజెంట్లు నికర ఆదాయాల ఆధారంగా ర్యాంక్ చేయబడ్డారు. ప్రతికూల సంఖ్యలు అంటే ఆ సబ్-ఏజెంట్ తీసుకొచ్చిన దానికంటే ఎక్కువ చెల్లించారని.',
  'agent.dashboard.accordions.activity.title':     'డౌన్‌లైన్ యాక్టివిటీ ఫీడ్',
  'agent.dashboard.accordions.activity.helper':    'మీ సబ్-ఏజెంట్ల అంతటా ప్రతి మ్యాచ్ సెటిల్‌మెంట్, రీఛార్జ్ మరియు రేట్ మార్పు — ఫోరెన్సిక్ టైమ్‌లైన్.',
  'agent.dashboard.accordions.earnings.title':     'ఆదాయాల విభజన (లైఫ్‌టైమ్)',
  'agent.dashboard.accordions.earnings.helper':    'రకం వారీగా లైఫ్‌టైమ్ మొత్తాలు. పైన ఉన్న రేంజ్ టోగుల్‌ను విస్మరిస్తుంది — ఇవి టైమ్ ఫిల్టర్‌తో ఎప్పటికీ మారవు.',
  'agent.dashboard.accordions.directSubAgents.title':  'డైరెక్ట్ సబ్-ఏజెంట్లు',
  'agent.dashboard.accordions.directSubAgents.helper': 'మీ డైరెక్ట్ లెఫ్టినెంట్‌లు. ప్రతి ఒక్కరు తమ సొంత ప్లేయర్లు + తమ సొంత సబ్-ఏజెంట్లను నడుపుతారు. రోజ్/అంబర్‌లో హైలైట్ చేసిన రోలు డిఫాల్ట్ టెరిటరీలో ఉన్నాయి.',
  'agent.dashboard.accordions.chain.title':        'చైన్ విజిబిలిటీ',
  'agent.dashboard.accordions.chain.helper':       'మీ మొత్తం డౌన్‌లైన్ ఫుట్‌ప్రింట్, డైరెక్ట్ (మీరు ఎడిట్ చేసేవి) మరియు ఇన్‌డైరెక్ట్ (మీ సబ్-ఏజెంట్లచే నిర్వహించబడేవి) గా వేరు చేయబడింది.',
  'agent.dashboard.liveFootnote': 'లైవ్ డేటా — ఫోకస్‌పై, ప్రతి 60 సెకన్లకు, మరియు డబ్బు కదిలినప్పుడు రిఫ్రెష్ అవుతుంది.',
  'agent.dashboard.cards.poolBalance.label':       'నా పూల్ బ్యాలెన్స్',
  'agent.dashboard.cards.poolBalance.helper':      'మీరు ఖర్చు చేయగల బ్యాలెన్స్. ప్లేయర్లను రీఛార్జ్ చేయడానికి లేదా సబ్-ఏజెంట్లకు పంపడానికి మీరు వాడగల డబ్బు.',
  'agent.dashboard.cards.poolBalance.netInRange':  '{range}లో నెట్ {value}',
  'agent.dashboard.cards.depositStatus.titleLoading':  'సెక్యూరిటీ డిపాజిట్',
  'agent.dashboard.cards.depositStatus.titleLoaded':   'డిపాజిట్ స్థితి',
  'agent.dashboard.cards.depositStatus.helperLoading': 'డిపాజిట్ స్థితి లోడ్ అవుతోంది…',
  'agent.dashboard.cards.depositStatus.helper':        'మీ పూల్‌కి వ్యతిరేకంగా ఉన్న కొలేటరల్. టార్గెట్‌లో 50% వద్ద కేషన్‌కి, 80%-వద్ద వార్నింగ్‌కి, 100%-వద్ద బ్రీచ్‌కి స్థితి మారుతుంది.',
  'agent.dashboard.cards.depositStatus.suspendedSince':           '{when} నుండి',
  'agent.dashboard.cards.depositStatus.field.operatingBalance':   'ఆపరేటింగ్ బ్యాలెన్స్',
  'agent.dashboard.cards.depositStatus.field.depositBalance':     'డిపాజిట్ బ్యాలెన్స్',
  'agent.dashboard.cards.depositStatus.field.depositTarget':      'డిపాజిట్ టార్గెట్',
  'agent.dashboard.cards.depositStatus.field.negativeExposure':   'నెగటివ్ ఎక్స్‌పోజర్',
  'agent.dashboard.cards.depositStatus.field.coverageRatio':      'కవరేజ్ నిష్పత్తి',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint':  'నెగటివ్ ఎక్స్‌పోజర్‌తో డిపాజిట్ టార్గెట్‌లో ఎంత వినియోగమైంది',
  'agent.dashboard.depositStatus.status.safe':      'సురక్షితం',
  'agent.dashboard.depositStatus.status.caution':   'జాగ్రత్త',
  'agent.dashboard.depositStatus.status.warning':   'వార్నింగ్',
  'agent.dashboard.depositStatus.status.breach':    'బ్రీచ్',
  'agent.dashboard.depositStatus.status.suspended': 'నిలిపివేయబడింది',
  'agent.dashboard.depositStatus.status.healthy':   'ఆరోగ్యకరంగా',
  'agent.dashboard.cards.playersWallet.title':     'ప్లేయర్ల వాలెట్ మొత్తం',
  'agent.dashboard.cards.playersWallet.helper':    'ఇప్పుడే మీ ప్రతి ఒక్క ప్లేయర్ వాలెట్ అంతటా కలిపిన లైవ్ బ్యాలెన్స్. మీకు పరిమితమైన ప్రతి wallet_points రో మొత్తం.',
  'agent.dashboard.cards.playersWallet.foot.one':  '{n} ప్లేయర్ · {active} యాక్టివ్',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} ప్లేయర్లు · {active} యాక్టివ్',
  'agent.dashboard.cards.subAgents.title':         'సబ్-ఏజెంట్ల వాలెట్ + డిపాజిట్',
  'agent.dashboard.cards.subAgents.helper':        'మీ డైరెక్ట్ సబ్-ఏజెంట్లు కలిగి ఉన్న కంబైన్డ్ పూల్ బ్యాలెన్స్ మరియు సెక్యూరిటీ డిపాజిట్. ప్రతి సబ్-ఏజెంట్ తమ సొంత ప్లేయర్లు + తమ సొంత డౌన్‌లైన్‌ను నడుపుతారు.',
  'agent.dashboard.cards.subAgents.field.pool':    'పూల్',
  'agent.dashboard.cards.subAgents.field.deposit': 'డిపాజిట్',
  'agent.dashboard.cards.subAgents.foot.one':      '{n} డైరెక్ట్ సబ్-ఏజెంట్',
  'agent.dashboard.cards.subAgents.foot.other':    '{n} డైరెక్ట్ సబ్-ఏజెంట్లు',
  'agent.dashboard.cards.myPending.title':            'నా పెండింగ్ అభ్యర్థనలు',
  'agent.dashboard.cards.myPending.helper':           'ప్రాధాన్యత 3. మీ సొంత సమర్పణలు అడ్మిన్‌కి — వీటిపై మీరే చర్య తీసుకోలేరు, మీరు వాటి కోసం వేచి ఉన్నారు.',
  'agent.dashboard.cards.myPending.recharge.one':     '{n} రీఛార్జ్ అడ్మిన్ కోసం వేచి ఉంది',
  'agent.dashboard.cards.myPending.recharge.other':   '{n} రీఛార్జ్‌లు అడ్మిన్ కోసం వేచి ఉన్నాయి',
  'agent.dashboard.cards.myPending.withdrawal.one':   '{n} విత్‌డ్రాల్ అడ్మిన్ కోసం వేచి ఉంది',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} విత్‌డ్రాల్‌లు అడ్మిన్ కోసం వేచి ఉన్నాయి',
  'agent.dashboard.cards.playersPending.title':            'ప్లేయర్ల పెండింగ్ అభ్యర్థనలు',
  'agent.dashboard.cards.playersPending.helper':           'ప్రాధాన్యత 1. మీ ప్లేయర్లు వీటిని ఆమోదించడానికి లేదా చెల్లించడానికి మీపై వేచి ఉన్నారు. చర్య కోసం ట్యాప్ చేయండి.',
  'agent.dashboard.cards.playersPending.recharge.one':     '{n} రీఛార్జ్ ఆమోదానికి',
  'agent.dashboard.cards.playersPending.recharge.other':   '{n} రీఛార్జ్‌లు ఆమోదానికి',
  'agent.dashboard.cards.playersPending.withdrawal.one':   '{n} విత్‌డ్రాల్ చెల్లించడానికి',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} విత్‌డ్రాల్‌లు చెల్లించడానికి',
  'agent.dashboard.cards.subAgentsPending.title':  'సబ్-ఏజెంట్ల పెండింగ్ అభ్యర్థనలు',
  'agent.dashboard.cards.subAgentsPending.helper': 'ప్రాధాన్యత 2. మీ సబ్-ఏజెంట్లు (పూర్తి డౌన్‌లైన్) వీటిని ఆమోదించడానికి లేదా చెల్లించడానికి మీపై వేచి ఉన్నారు. చర్య కోసం ట్యాప్ చేయండి.',
  'agent.dashboard.cards.trueNet.title':  'ట్రూ నెట్ P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'ఈ రేంజ్‌లో మీ నిజమైన లాభం/నష్టం. ప్లాట్‌ఫారమ్ ఫీ షేర్ + విన్/లాస్ షేర్ − ఆఫర్ కో-ఫండింగ్. గేమ్-వారీ వివరాల కోసం దిగువ P&L డీప్-డైవ్‌ను విస్తరించండి.',
  'agent.dashboard.pillarChip.platformFee': 'ప్లాట్',
  'agent.dashboard.pillarChip.winLoss':     'W/L',
  'agent.dashboard.pillarChip.offer':       'ఆఫర్',
  'agent.dashboard.cards.rewardUtil.title':     'ఆఫర్ వినియోగం · {range}',
  'agent.dashboard.cards.rewardUtil.helper':    'ఈ రేంజ్‌లో ఆఫర్ల ద్వారా మీ ప్లేయర్లకు చెల్లించిన మొత్తం రివార్డ్ ₹. ఆ ఖర్చులో మీ వాటాను చూడడానికి మీ ఎక్స్‌పోజర్ పేజీతో క్రాస్-రెఫరెన్స్ చేయండి.',
  'agent.dashboard.cards.rewardUtil.foot.one':  '{n} క్లెయిమ్ క్రెడిట్ చేయబడింది',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} క్లెయిమ్‌లు క్రెడిట్ చేయబడ్డాయి',
  'agent.dashboard.activity.topSpenders.title':   'టాప్ స్పెండర్లు',
  'agent.dashboard.activity.topSpenders.helper':  '{range}లో మీ టాప్-స్పెండింగ్ ప్లేయర్లు.',
  'agent.dashboard.activity.topSpenders.empty':   'ఈ రేంజ్‌లో ఇంకా ఖర్చు లేదు.',
  'agent.dashboard.activity.recent.title':        'ఇటీవలి కార్యకలాపం',
  'agent.dashboard.activity.recent.helper':       'మీ ప్లేయర్ల నుండి ఐదు అత్యంత ఇటీవలి రీఛార్జ్ ఈవెంట్‌లు.',
  'agent.dashboard.activity.recent.empty':        'ఇటీవలి కార్యకలాపం లేదు.',
  'agent.dashboard.activity.recent.state.approved': 'ఆమోదించబడింది',
  'agent.dashboard.activity.recent.state.rejected': 'తిరస్కరించబడింది',
  'agent.dashboard.activity.recent.state.pending':  'పెండింగ్',
  'agent.dashboard.pnl.perGameLabel':         'గేమ్ వారీగా · వివరణ కోసం రోపై ట్యాప్ చేయండి',
  'agent.dashboard.pnl.noEarnings':           'ఈ రేంజ్‌లో ఆదాయాలు లేవు.',
  'agent.dashboard.pnl.table.col.pillarGame': 'పిల్లర్ / గేమ్',
  'agent.dashboard.pnl.table.col.net':        'నెట్',
  'agent.dashboard.pnl.table.col.matches':    'మ్యాచ్‌లు',
  'agent.dashboard.pnl.ledgerRows.one':       '{n} లెడ్జర్ రో',
  'agent.dashboard.pnl.ledgerRows.other':     '{n} లెడ్జర్ రోలు',
  'agent.dashboard.pnl.pillar.platformFee':   'ప్లాట్‌ఫారమ్ ఫీ',
  'agent.dashboard.pnl.pillar.winLoss':       'విన్ / లాస్',
  'agent.dashboard.pnl.pillar.offerReward':   'ఆఫర్ రివార్డ్',
  'agent.dashboard.pnlDrill.windowLabel':       'విండో: {range} — డాష్‌బోర్డ్ రేంజ్‌లో కఠినమైన ఉప సమితి',
  'agent.dashboard.pnlDrill.loading':           'మ్యాచ్-స్థాయి వివరాలు లోడ్ అవుతున్నాయి…',
  'agent.dashboard.pnlDrill.windowTotal':       'విండో మొత్తం:',
  'agent.dashboard.pnlDrill.matchCount.one':    '{n} మ్యాచ్ అంతటా',
  'agent.dashboard.pnlDrill.matchCount.other':  '{n} మ్యాచ్‌ల అంతటా',
  'agent.dashboard.pnlDrill.noContribution':    'ఈ సెల్‌కి ఏ మ్యాచ్‌లు దోహదపడలేదు.',
  'agent.dashboard.pnlDrill.errorFallback':     'డ్రిల్‌డౌన్‌ను లోడ్ చేయలేకపోయాము',
  'agent.dashboard.pnlDrill.table.col.match':   'మ్యాచ్',
  'agent.dashboard.pnlDrill.table.col.role':    'రోల్',
  'agent.dashboard.pnlDrill.table.col.base':    'బేస్',
  'agent.dashboard.pnlDrill.table.col.rate':    'రేట్',
  'agent.dashboard.pnlDrill.table.col.earned':  'సంపాదించారు',
  'agent.dashboard.pnlDrill.table.col.settled': 'సెటిల్ అయింది',
  'agent.dashboard.directDownline.loading':       'లోడ్ అవుతోంది…',
  'agent.dashboard.directDownline.empty':         'ఇంకా డైరెక్ట్ సబ్-ఏజెంట్లు లేరు.',
  'agent.dashboard.directDownline.row.player.one':   '{n} ప్లేయర్',
  'agent.dashboard.directDownline.row.player.other': '{n} ప్లేయర్లు',
  'agent.dashboard.directDownline.row.subAgent.one':   '{n} సబ్-ఏజెంట్',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} సబ్-ఏజెంట్లు',
  'agent.dashboard.directDownline.row.walletDeposit':  'వాలెట్ {wallet} · డిపాజిట్ {deposit}',
  'agent.dashboard.directDownline.footer':        'ట్రూ నెట్ {range} విండో కోసం.',
  'agent.dashboard.chain.loading':                  'లోడ్ అవుతోంది…',
  'agent.dashboard.chain.directAgents.title':       'డైరెక్ట్ ఏజెంట్లు',
  'agent.dashboard.chain.directAgents.subtitle':    'మీ డైరెక్ట్ సబ్-ఏజెంట్లు — మీరు వారి అభ్యర్థనలపై చర్య తీసుకోగలరు.',
  'agent.dashboard.chain.indirectAgents.title':     'ఇన్‌డైరెక్ట్ ఏజెంట్లు',
  'agent.dashboard.chain.indirectAgents.subtitle':  'మీ సబ్-ఏజెంట్లచే నిర్వహించబడుతున్నారు. మీకు రీడ్-ఓన్లీ.',
  'agent.dashboard.chain.directPlayers.title':      'డైరెక్ట్ ప్లేయర్లు',
  'agent.dashboard.chain.directPlayers.subtitle':   'మీ ప్లేయర్లు — మీరు వారి రీఛార్జ్‌లు మరియు విత్‌డ్రాల్‌లపై చర్య తీసుకుంటారు.',
  'agent.dashboard.chain.indirectPlayers.title':    'ఇన్‌డైరెక్ట్ ప్లేయర్లు',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'మీ సబ్-ఏజెంట్ల ప్లేయర్లు. మీకు రీడ్-ఓన్లీ.',
  'agent.dashboard.chain.field.walletTotal':        'వాలెట్ మొత్తం',
  'agent.dashboard.chain.field.depositTotal':       'డిపాజిట్ మొత్తం',
  'agent.dashboard.chain.field.avgPerHead':         'తలవారీ సగటు',
  'agent.dashboard.chain.field.pendingRecharge':    'పెండింగ్ రీఛార్జ్',
  'agent.dashboard.chain.field.pendingWithdrawal':  'పెండింగ్ విత్‌డ్రాల్',
  'agent.dashboard.chain.pendingBadge':             '{n} పెండింగ్',
  'agent.dashboard.chain.openInbox':                'ఇన్‌బాక్స్ తెరవండి',
  'agent.dashboard.chain.footer':                   'డైరెక్ట్ = మీరే స్వయంగా నిర్వహించే హెడ్‌కౌంట్. ఇన్‌డైరెక్ట్ = మీ సబ్-ఏజెంట్లచే నిర్వహించబడుతుంది (మీరు సంఖ్యలు చూస్తారు కానీ వారి పెండింగ్ అభ్యర్థనలపై చర్య తీసుకోలేరు — అవి వారి పేరెంట్‌కు వెళతాయి).',
  'agent.activity.error.loadFailed':  'లోడ్ చేయడంలో విఫలమైంది',
  'agent.activity.filter.window':     'విండో',
  'agent.activity.filter.kind':       'రకం',
  'agent.activity.window.all':        'అన్నీ',
  'agent.activity.kind.all':              'అన్నీ',
  'agent.activity.kind.matches':          'మ్యాచ్‌లు',
  'agent.activity.kind.recharges':        'రీఛార్జ్‌లు',
  'agent.activity.kind.rateChanges':      'రేట్ మార్పులు',
  'agent.activity.loading':           'లోడ్ అవుతోంది…',
  'agent.activity.empty.title':       'నిశ్శబ్దం',
  'agent.activity.empty.subtitle':    '{window} విండోలో {kindWord}ఈవెంట్‌లు లేవు.',
  'agent.activity.empty.kindWord.all':                  '',
  'agent.activity.empty.kindWord.match':                'మ్యాచ్ ',
  'agent.activity.empty.kindWord.recharge':             'రీఛార్జ్ ',
  'agent.activity.empty.kindWord.partnership_change':   'భాగస్వామ్యం మార్పు ',
  'agent.activity.row.settlementFallback': 'సెటిల్‌మెంట్',
  'agent.activity.row.earned':             'సంపాదించారు',
  'agent.activity.row.fromPillarOn':       '{pillar} నుండి · ',
  'agent.activity.row.pillar.platformFee':  'ప్లాట్‌ఫారమ్ ఫీ',
  'agent.activity.row.pillar.winLoss':      'విన్/లాస్',
  'agent.activity.row.pillar.offerReward':  'ఆఫర్ రివార్డ్',
  'agent.activity.row.recharge.prefix': 'రీఛార్జ్',
  'agent.activity.row.recharge.of':     'యొక్క',
  'agent.activity.row.recharge.byUser': 'యూజర్ #{id} ద్వారా',
  'agent.activity.row.recharge.via':    ' ద్వారా ',
  'agent.activity.row.state.approved':  'ఆమోదించబడింది',
  'agent.activity.row.state.rejected':  'తిరస్కరించబడింది',
  'agent.activity.row.state.pending':   'పెండింగ్',
  'agent.activity.row.rateChange.prefix':  'రేట్ మార్పు',
  'agent.activity.row.rateChange.dashOn':  '— {pillar} పై ',
  'agent.activity.row.rateChange.cascade': 'క్యాస్కేడ్',
  'agent.earnings.notAgent': 'ఈ వీక్షణ ఏజెంట్ల కోసం మాత్రమే.',
  'agent.earnings.stat.poolBalance': 'పూల్ బ్యాలెన్స్',
  'agent.earnings.stat.totalIn':     'మొత్తం ఇన్',
  'agent.earnings.stat.totalOut':    'మొత్తం అవుట్',
  'agent.earnings.section.in':       'ఆదాయాలు (ఇన్)',
  'agent.earnings.section.out':      'అవుట్‌ఫ్లోలు (అవుట్)',
  'agent.earnings.empty.in':  'ఇంకా ఆదాయాలు లేవు. మీ ప్లేయర్లు రీఛార్జ్ చేసిన లేదా ఆడిన తర్వాత, కట్‌లు ఇక్కడ కనిపిస్తాయి.',
  'agent.earnings.empty.out': 'ఇంకా అవుట్‌ఫ్లోలు లేవు.',
  'agent.earnings.viewLedger': 'పూర్తి లెడ్జర్ చూడండి',
  'agent.earnings.kind.topupFromAdmin.label':  'అడ్మిన్ నుండి టాప్‌అప్',
  'agent.earnings.kind.topupFromAdmin.hint':   'అడ్మిన్ మీ పూల్‌ని క్రెడిట్ చేశారు',
  'agent.earnings.kind.topupToPlayer.label':   'ప్లేయర్‌కి టాప్‌అప్',
  'agent.earnings.kind.topupToPlayer.hint':    'మీరు ఒక ప్లేయర్‌ని రీఛార్జ్ చేశారు',
  'agent.earnings.kind.topupFromPlayer.label': 'ప్లేయర్ రీఛార్జ్',
  'agent.earnings.kind.topupFromPlayer.hint':  'మీ ప్లేయర్లు మీ పూల్‌లోకి చెల్లించారు',
  'agent.earnings.kind.revenueShare.label':    'రెవెన్యూ షేర్',
  'agent.earnings.kind.revenueShare.hint':     'మ్యాచ్‌ల నుండి ప్లాట్‌ఫారమ్ ఫీలో కట్',
  'agent.earnings.kind.coFundOffer.label':     'కో-ఫండ్ ఆఫర్',
  'agent.earnings.kind.coFundOffer.hint':      'ఆఫర్ చెల్లింపులలో మీ వాటా',
  'agent.earnings.kind.adminAdjust.label':     'అడ్మిన్ సర్దుబాటు',
  'agent.earnings.kind.adminAdjust.hint':      'మాన్యువల్ దిద్దుబాటు',
  'agent.earnings.row.entry.one':   'ఎంట్రీ',
  'agent.earnings.row.entry.other': 'ఎంట్రీలు',
  'agent.leaderboard.filter.window':   'విండో',
  'agent.leaderboard.filter.pillar':   'పిల్లర్',
  'agent.leaderboard.window.all':      'అన్నీ',
  'agent.leaderboard.pillar.all':         'అన్ని పిల్లర్‌లు',
  'agent.leaderboard.pillar.platformFee': 'ప్లాట్‌ఫారమ్ ఫీ',
  'agent.leaderboard.pillar.winLoss':     'విన్ / లాస్',
  'agent.leaderboard.pillar.offerReward': 'ఆఫర్ రివార్డ్',
  'agent.leaderboard.loading':         'లోడ్ అవుతోంది…',
  'agent.leaderboard.empty.title':         'ఇంకా ఆదాయాలు లేవు',
  'agent.leaderboard.empty.subtitle.admin':    '{window} విండోలో అన్ని ఏజెంట్ల అంతటా సెటిల్‌మెంట్ కార్యకలాపం లేదు.',
  'agent.leaderboard.empty.subtitle.downline': '{window} విండోలో మీ డౌన్‌లైన్ నుండి ఆదాయాలు లేవు. మీ సబ్-ఏజెంట్లు మ్యాచ్‌లను నడిపించినప్పుడు, ఈ బోర్డు నిండుతుంది.',
  'agent.leaderboard.row.matches.one':   '{n} మ్యాచ్',
  'agent.leaderboard.row.matches.other': '{n} మ్యాచ్‌లు',
  'agent.leaderboard.footer':          'ఆదాయాలు match_settlement_snapshots నుండి వస్తాయి. ప్రతికూల సంఖ్య అంటే ఆ ఏజెంట్ విండో అంతటా తీసుకున్న దానికంటే ఎక్కువ చెల్లించారని.',
  'agent.leaderboard.chip.platformFee': 'ప్లాట్',
  'agent.leaderboard.chip.winLoss':     'W/L',
  'agent.leaderboard.chip.offer':       'ఆఫర్',
  'agent.warning.header.eyebrow':   'మార్జిన్ వార్నింగ్ · 80% థ్రెషోల్డ్ చేరుకున్నాం',
  'agent.warning.header.title':     'లాక్‌అవుట్‌కి ఒక చెడ్డ సెటిల్‌మెంట్ దూరం.',
  'agent.warning.header.body':      'మీరు ఇంకా లాక్ కాలేదు — కానీ మీ డిపాజిట్ సేఫ్టీ క్యూషన్ 80%+ ఖర్చు అయింది. ఇప్పుడే టాప్ అప్ చేయండి మరియు మీరు నియంత్రణలోనే ఉంటారు.',
  'agent.warning.header.dismissAria': 'తీసివేయి',
  'agent.warning.why.eyebrow':      'ఇది ఎందుకు చూపిస్తోంది',
  'agent.warning.why.body':         'ఇటీవలి గేమ్ సెటిల్‌మెంట్‌లు మీ వాలెట్‌ని రెడ్‌లోకి నెట్టాయి. మీ సెక్యూరిటీ డిపాజిట్ {deposit} ఇది గ్రహించే సేఫ్టీ నెట్ — మరియు అది ఇప్పుడు 80%+ ఖర్చు అయింది. మరికొంత మాత్రమే నెగటివ్ వచ్చితే, మీ వాలెట్ + డిపాజిట్ సున్నాకి పడిపోతుంది మరియు ప్లాట్‌ఫారమ్ ఖాతాను లాక్ చేస్తుంది.',
  'agent.warning.why.reassurance':  'ఈ అలర్ట్ ముందుగానే ఉద్దేశపూర్వకంగా ఫైర్ అవుతుంది — మీరు ఇంకా మొత్తం యాప్‌ని వాడగలిగే సమయంలో. మీరు 100% క్రాస్ చేస్తేనే లాక్‌అవుట్ స్క్రీన్ కనిపిస్తుంది, మరియు అది అయిన తర్వాత, మీరు రికవర్ అయ్యే వరకు చాలా ఫీచర్లు బ్లాక్ అవుతాయి. తర్వాత చర్య తీసుకోవడం కంటే ఇప్పుడు చర్య తీసుకోవడం చాలా తక్కువ ఇబ్బందికరం.',
  'agent.warning.gauge.label':      'లాక్‌అవుట్‌కి ముందు హెడ్‌రూమ్',
  'agent.warning.gauge.suffix':     'రూమ్ మిగిలి ఉంది',
  'agent.warning.gauge.dangerBadge': 'డేంజర్ జోన్‌లోకి {pct}%',
  'agent.warning.gauge.field.wallet':  'మీ వాలెట్',
  'agent.warning.gauge.field.deposit': 'మీ డిపాజిట్',
  'agent.warning.ignore.eyebrow':       'మీరు దీనిని విస్మరించి 100% చేరితే',
  'agent.warning.ignore.cardTitle':     'మీరు వెంటనే కోల్పోతారు',
  'agent.warning.ignore.item.dashboard': 'మీ డాష్‌బోర్డ్, P&L, మరియు లీడర్‌బోర్డ్‌లకు యాక్సెస్',
  'agent.warning.ignore.item.pages':     'ప్లేయర్లు మరియు ఏజెంట్లు పేజీలు',
  'agent.warning.ignore.item.manualTopup': 'మీ పూల్ నుండి మీ ప్లేయర్లకు మాన్యువల్ టాప్-అప్‌లు',
  'agent.warning.ignore.item.newWithdrawal': 'మీ అప్‌లైన్‌కి కొత్త విత్‌డ్రాల్‌లు సమర్పించడం',
  'agent.warning.ignore.item.subAgents':  'సబ్-ఏజెంట్లను జోడించడం లేదా నిర్వహించడం',
  'agent.warning.ignore.item.profile':   'మీ ప్రొఫైల్ లేదా చెల్లింపు సెట్టింగ్‌లను సవరించడం',
  'agent.warning.ignore.footer':         'మీరు ఇంకా పెండింగ్ అభ్యర్థనలను ఆమోదించగలరు + ఎమర్జెన్సీ టాప్-అప్ సమర్పించగలరు — కానీ మీ వాలెట్ + డిపాజిట్ సున్నా పైకి తిరిగి వచ్చే వరకు మిగతావన్నీ ఆగిపోతాయి.',
  'agent.warning.recoverEyebrow':    'రికవర్ అవ్వడానికి ఇప్పుడే మీ అప్‌లైన్‌కి చెల్లించండి',
  'agent.warning.dismiss.cta':       'దీనిని తర్వాత చూసుకుంటాను',
  'agent.warning.dismiss.footnote':  'మీరు వార్నింగ్ జోన్‌లో ఉంటే 10 నిమిషాల్లో మళ్లీ గుర్తుచేస్తాము. మీరు రికవర్ అయిన తర్వాత స్వయంచాలకంగా మూసివేస్తుంది.',
  'agent.lockout.header.eyebrow': 'ఖాతా లాక్ · బెయిల్‌అవుట్ పోర్టల్',
  'agent.lockout.header.title':   'మీ డిపాజిట్ కవర్ చేయగలిగే దానికంటే ఎక్కువ మీరు ఋణపడి ఉన్నారు.',
  'agent.lockout.header.body':    'ఆందోళన పడవద్దు — ఇది రికవర్ చేయదగినది. క్రింది రెండు దశలను అనుసరించండి మరియు మీరు స్వయంచాలకంగా అన్‌లాక్ అవుతారు.',
  'agent.lockout.why.eyebrow':       'ఇది ఎందుకు జరిగింది',
  'agent.lockout.why.body':          'గేమ్ సెటిల్‌మెంట్‌లు మీ వాలెట్‌ని సున్నా క్రిందికి తీసుకెళ్లాయి. మీ సెక్యూరిటీ డిపాజిట్ ఇది గ్రహించే సేఫ్టీ నెట్ — కానీ మీరు ఇప్పుడు దానిలో 100% వాడేశారు. మీ వాలెట్ ప్లస్ డిపాజిట్ సున్నా పైకి తిరిగి వచ్చే వరకు, మీరు సాధారణంగా ఆపరేట్ చేయలేరు.',
  'agent.lockout.why.reassurance':   'మీరు ఏదైనా తప్పు చేశారని ప్లాట్‌ఫారమ్ మిమ్మల్ని లాక్ చేయదు. మీరు స్థిరపడుతున్నప్పుడు మీ డిపాజిట్‌కి వ్యతిరేకంగా నెట్‌వర్క్ మరింత ఋణం పేర్చుకోకుండా ఉండడానికి ఇది మిమ్మల్ని లాక్ చేస్తుంది.',
  'agent.lockout.gauge.label':         'సురక్షిత రికవరీ టార్గెట్',
  'agent.lockout.gauge.suffix':        'సురక్షితంగా చెల్లించడానికి',
  'agent.lockout.gauge.recoveredBadge': '{pct}% రికవర్ అయింది',
  'agent.lockout.gauge.field.wallet':  'మీ వాలెట్',
  'agent.lockout.gauge.field.deposit': 'మీ డిపాజిట్',
  'agent.lockout.tier.justUnlock.label': 'కేవలం అన్‌లాక్',
  'agent.lockout.tier.justUnlock.tag':   'రిస్కీ',
  'agent.lockout.tier.justUnlock.note':  'మిమ్మల్ని ₹0కి తిరిగి తీసుకొస్తుంది — ఏ కొత్త నష్టం అయినా మిమ్మల్ని వెంటనే మళ్లీ లాక్ చేస్తుంది.',
  'agent.lockout.tier.safeMin.label':    'సురక్షిత కనిష్ఠం',
  'agent.lockout.tier.safeMin.tag':      'సిఫార్సు',
  'agent.lockout.tier.safeMin.note':     'డిపాజిట్ ({deposit}) + మీ నెగటివ్ వాలెట్‌లో సగం. ఒక నిజమైన క్యూషన్ వదిలిపెడుతుంది.',
  'agent.lockout.tier.fullRecovery.label': 'పూర్తి రికవరీ',
  'agent.lockout.tier.fullRecovery.tag':  'ఉత్తమం',
  'agent.lockout.tier.fullRecovery.note': 'డిపాజిట్ పూర్తిగా పునరుద్ధరించబడింది మరియు వాలెట్ సున్నాకి తిరిగి తీసుకువచ్చింది.',
  'agent.lockout.waysOut.eyebrow': 'రికవర్ అవ్వడానికి రెండు మార్గాలు — ఏదైనా చేయండి (లేదా రెండూ)',
  'agent.lockout.waysOut.inbox.title':  'మీ ఇన్‌బాక్స్‌పై చర్య తీసుకోండి',
  'agent.lockout.waysOut.inbox.body':   'ప్లేయర్ రీఛార్జ్‌లను ఆమోదించండి (క్యాష్ ఇప్పటికే మీ వద్ద ఉంది) మరియు ప్లేయర్ విత్‌డ్రాల్‌లను చెల్లించినట్లు మార్క్ చేయండి. ప్రతి ఆమోదం మీ వాలెట్‌ని క్రెడిట్ చేస్తుంది.',
  'agent.lockout.waysOut.inbox.ctaOpen':  'ఇన్‌బాక్స్ తెరవండి',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ఇన్‌బాక్స్ క్రింద తెరిచి ఉంది',
  'agent.lockout.waysOut.upline.title': 'మీ అప్‌లైన్‌కి చెల్లించండి',
  'agent.lockout.waysOut.upline.body':  'మీ పేరెంట్ ఏజెంట్‌కి (లేదా అడ్మిన్‌కి) UPI ద్వారా క్యాష్ పంపండి, తర్వాత txn ఐడిని సమర్పించండి. వారు ఆమోదించిన తర్వాత, మీ వాలెట్ తక్షణం క్రెడిట్ అవుతుంది.',
  'agent.lockout.waysOut.upline.ctaOpen':  'ఫారం తెరవండి',
  'agent.lockout.waysOut.upline.ctaOpened': 'ఫారం క్రింద తెరిచి ఉంది',
  'agent.lockout.capabilities.eyebrow': 'మీరు ఇంకా ఏం చేయగలరు (మరియు ఏం బ్లాక్ చేయబడింది)',
  'agent.lockout.capabilities.canHeader':    'మీరు ఇంకా చేయగలరు',
  'agent.lockout.capabilities.cantHeader':   'మీరు చేయలేరు (తాత్కాలికంగా)',
  'agent.lockout.can.approvePlayerRecharge':    'వచ్చే ప్లేయర్ రీఛార్జ్‌లను ఆమోదించడం',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'ప్లేయర్ విత్‌డ్రాల్‌లను చెల్లించినట్లు మార్క్ చేయడం',
  'agent.lockout.can.approveSubAgentRecharge':  'సబ్-ఏజెంట్ రీఛార్జ్‌లను ఆమోదించడం',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'సబ్-ఏజెంట్ విత్‌డ్రాల్‌లను చెల్లించినట్లు మార్క్ చేయడం',
  'agent.lockout.can.submitToUpline':           'మీ అప్‌లైన్‌కి రీఛార్జ్ సమర్పించడం',
  'agent.lockout.can.receiveSseAndSignOut':     'SSE అలర్ట్‌లు పొందడం + సైన్ అవుట్',
  'agent.lockout.section.step':                 'దశ {n} · {title}',
  'agent.lockout.section.inbox.title':          'మీ ఇన్‌బాక్స్ — ప్రతి ఆమోదం మీ వాలెట్‌ని క్రెడిట్ చేస్తుంది',
  'agent.lockout.section.inbox.subtitle':       'ప్లేయర్లు మరియు సబ్-ఏజెంట్ల నుండి వేచి ఉన్న చెల్లింపు అభ్యర్థనలు మీ చర్య కోసం ఎదురుచూస్తున్నాయి.',
  'agent.lockout.section.inbox.fromPlayers':    'మీ ప్లేయర్ల నుండి',
  'agent.lockout.section.inbox.fromSubAgents':  'మీ సబ్-ఏజెంట్ల నుండి',
  'agent.lockout.section.upline.title':         'మీ వాలెట్‌ని టాప్ అప్ చేయడానికి మీ అప్‌లైన్‌కి చెల్లించండి',
  'agent.lockout.section.upline.subtitle':      'మీ పేరెంట్ UPI మీ ముందు ఉంటే వేగంగా. ఆమోదం సాధారణంగా నిమిషాల్లోనే.',
  'agent.lockout.queue.player.recharge.title':    'రీఛార్జ్ · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · {when} సమర్పించబడింది',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · {when} సమర్పించబడింది · "{note}"',
  'agent.lockout.queue.player.withdrawal.title':    'విత్‌డ్రాల్ · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi}కి చెల్లించండి · {when} సమర్పించబడింది',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi}కి చెల్లించండి · {whatsapp} · {when} సమర్పించబడింది',
  'agent.lockout.queue.subAgent.recharge.title':    'సబ్-ఏజెంట్ రీఛార్జ్ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · {when} సమర్పించబడింది',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · {when} సమర్పించబడింది · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title':    'సబ్-ఏజెంట్ విత్‌డ్రాల్ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi}కి చెల్లించండి · {when} సమర్పించబడింది',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi}కి చెల్లించండి · {when} సమర్పించబడింది · "{note}"',
  'agent.lockout.queue.action.approve':  'ఆమోదించు',
  'agent.lockout.queue.action.reject':   'తిరస్కరించు',
  'agent.lockout.queue.action.markPaid': 'చెల్లించినట్లు మార్క్ చేయి',
  'agent.lockout.queue.prompt.markPlayerPaid': 'ప్లేయర్‌కి చెల్లించడానికి మీరు ఉపయోగించిన UPI లావాదేవీ ఐడిని నమోదు చేయండి:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'సబ్-ఏజెంట్‌కి చెల్లించడానికి మీరు ఉపయోగించిన UPI లావాదేవీ ఐడిని నమోదు చేయండి:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'ఈ విత్‌డ్రాల్‌ని తిరస్కరించాలా? హోల్డ్ చేసిన మొత్తం ప్లేయర్‌కి రీఫండ్ అవుతుంది.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'ఈ సబ్-ఏజెంట్ విత్‌డ్రాల్‌ని తిరస్కరించాలా?',
  'agent.lockout.queue.toast.rechargeApproved': 'రీఛార్జ్ ఆమోదించబడింది — ప్లేయర్ క్రెడిట్ అయ్యారు',
  'agent.lockout.queue.toast.rechargeRejected': 'రీఛార్జ్ తిరస్కరించబడింది',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'సబ్-ఏజెంట్ రీఛార్జ్ ఆమోదించబడింది',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'రీఛార్జ్ తిరస్కరించబడింది',
  'agent.lockout.queue.toast.withdrawalPaid':   'విత్‌డ్రాల్ చెల్లించినట్లు మార్క్ చేయబడింది — మీ వాలెట్ క్రెడిట్ చేయబడింది',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'సబ్-ఏజెంట్ విత్‌డ్రాల్ చెల్లించినట్లు మార్క్ చేయబడింది — వాలెట్ క్రెడిట్ చేయబడింది',
  'agent.lockout.queue.toast.withdrawalRejected': 'విత్‌డ్రాల్ తిరస్కరించబడింది (ప్లేయర్‌కి రీఫండ్)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'విత్‌డ్రాల్ తిరస్కరించబడింది',
  'agent.lockout.queue.toast.actionFailed': 'చర్య విఫలమైంది',
  'agent.lockout.queue.empty.player':   'ఇప్పుడే మీ ప్లేయర్ల నుండి ఏమీ లేదు.',
  'agent.lockout.queue.empty.subAgent': 'ఇప్పుడే మీ సబ్-ఏజెంట్ల నుండి ఏమీ లేదు.',
  'agent.lockout.queue.loading':        'క్యూ లోడ్ అవుతోంది…',
  'agent.lockout.refresh.cta':       'రిఫ్రెష్ — నేను అన్‌లాక్ అయ్యానా?',
  'agent.lockout.refresh.checking':  'తనిఖీ చేస్తోంది…',
  'agent.lockout.refresh.toast':     'స్థితి రిఫ్రెష్ చేయబడింది',
  'agent.lockout.refresh.footnote':  'మీ వాలెట్ + డిపాజిట్ సున్నా పైకి తిరిగి వచ్చిన క్షణంలో ఈ పేజీ స్వయంచాలకంగా మూసివేస్తుంది.',
  'agent.emergencyForm.error.loadFailed':   'రీఛార్జ్ వివరాలను లోడ్ చేయలేకపోయాము',
  'agent.emergencyForm.banner.locked.title':  'మీ ఖాతాను అన్‌లాక్ చేయడానికి ఇప్పుడే చెల్లించండి',
  'agent.emergencyForm.banner.locked.body':   'మీ ప్లేయర్లు మీ డిపాజిట్ కొలేటరల్ కవర్ చేయగలిగే దానికంటే ఎక్కువ గెలిచారు. మీ నెగటివ్ బ్యాలెన్స్ క్లియర్ చేసే వరకు మీరు ప్లాట్‌ఫారమ్‌ను ఉపయోగించలేరు.',
  'agent.emergencyForm.banner.warning.title': 'త్వరలో టాప్ అప్ చేయండి — మీరు పరిమితికి దగ్గరగా ఉన్నారు',
  'agent.emergencyForm.banner.warning.body':  'మీ వాలెట్ మీ డిపాజిట్‌లో 80% క్రాస్ చేసింది. ఇది 100% చేరకముందే టాప్ అప్ చేయండి — చేరితే, మీ ఖాతా లాక్ అవుతుంది.',
  'agent.emergencyForm.row.wallet':  'మీ వాలెట్',
  'agent.emergencyForm.row.deposit': 'మీ డిపాజిట్',
  'agent.emergencyForm.tier.eyebrow.locked':  'మీరు ఎందుకు చిక్కుకుపోయారు',
  'agent.emergencyForm.tier.eyebrow.warning': 'మీరు ఎందుకు రిస్క్‌లో ఉన్నారు',
  'agent.emergencyForm.tier.context.locked':  'మీ వాలెట్ {wallet} వెళ్లింది — అది మీ {deposit} డిపాజిట్ సేఫ్టీ క్యూషన్‌ని దాటింది. ఖాళీ బఫర్‌కి వ్యతిరేకంగా మీ డౌన్‌లైన్ నష్టాలు పేరుకుపోకుండా ఉండడానికి ప్లాట్‌ఫారమ్ ఇక్కడ మిమ్మల్ని లాక్ చేస్తుంది.',
  'agent.emergencyForm.tier.context.warning': 'మీ వాలెట్ {wallet} వెళ్లింది — అది మీ {deposit} డిపాజిట్ సేఫ్టీ క్యూషన్‌లో 80% కంటే ఎక్కువ. మరో చెడ్డ సెటిల్‌మెంట్ మిమ్మల్ని 100%పైకి నెట్టి ఖాతాను లాక్ చేస్తుంది.',
  'agent.emergencyForm.tier.pickHowMuch':     'ఎంత చెల్లించాలో ఎంచుకోండి',
  'agent.emergencyForm.tier.bareMin.label.locked':  'కేవలం అన్‌లాక్',
  'agent.emergencyForm.tier.bareMin.label.warning': 'కేవలం వార్నింగ్ నుండి తప్పించుకోండి',
  'agent.emergencyForm.tier.bareMin.tag':           'రిస్కీ',
  'agent.emergencyForm.tier.bareMin.note.locked':   'మీ వాలెట్ + డిపాజిట్‌ని కేవలం ₹0కి తిరిగి తీసుకొస్తుంది. ఏ కొత్త నష్టం అయినా మిమ్మల్ని వెంటనే మళ్లీ లాక్ చేస్తుంది.',
  'agent.emergencyForm.tier.bareMin.note.warning':  'మిమ్మల్ని కేవలం 80% లైన్‌ని దాటి తీసుకొస్తుంది. ఏ కొత్త నష్టం అయినా వార్నింగ్‌ని తిరిగి ట్రిగ్గర్ చేస్తుంది — లేదా మిమ్మల్ని లాక్‌అవుట్‌లోకి నెట్టుతుంది.',
  'agent.emergencyForm.tier.safeMin.label':         'సురక్షిత కనిష్ఠం',
  'agent.emergencyForm.tier.safeMin.tag':           'సిఫార్సు',
  'agent.emergencyForm.tier.safeMin.note':          'మీ డిపాజిట్ ({deposit}) + మీ నెగటివ్ వాలెట్‌లో సగం ({half}). మీకు ఒక నిజమైన క్యూషన్‌తో మిగిలిపోతుంది.',
  'agent.emergencyForm.tier.full.label':            'పూర్తి రికవరీ',
  'agent.emergencyForm.tier.full.tag':              'ఉత్తమం',
  'agent.emergencyForm.tier.full.note':             'డిపాజిట్ + మీ పూర్తి నెగటివ్ వాలెట్ ({neg}). డిపాజిట్ పూర్తిగా పునరుద్ధరించబడింది మరియు వాలెట్ సున్నాకి తిరిగి.',
  'agent.emergencyForm.parent.label.admin':       'మీ అడ్మిన్‌కి చెల్లించండి',
  'agent.emergencyForm.parent.label.parentAgent': 'మీ పేరెంట్ ఏజెంట్‌కి చెల్లించండి',
  'agent.emergencyForm.parent.role.admin':        'ప్లాట్‌ఫారమ్ అడ్మిన్',
  'agent.emergencyForm.parent.role.parentAgent':  'పేరెంట్ ఏజెంట్',
  'agent.emergencyForm.parent.upi':               'UPI:',
  'agent.emergencyForm.parent.whatsapp':          'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile':         'మీ పేరెంట్ చెల్లింపు వివరాలు ఇంకా ఫైల్‌లో లేవు. దయచేసి ప్లాట్‌ఫారమ్ సపోర్ట్‌ను నేరుగా సంప్రదించండి.',
  'agent.emergencyForm.field.amount.label':   'చెల్లించిన మొత్తం (₹)',
  'agent.emergencyForm.field.txn.label':      'UPI లావాదేవీ ఐడి / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'ఉదా. 412312345678',
  'agent.emergencyForm.field.note.label':     'గమనిక (ఐచ్ఛికం)',
  'agent.emergencyForm.field.note.placeholder': 'మీ పేరెంట్ తెలుసుకోవలసిన ఏదైనా',
  'agent.emergencyForm.validation.amount':    'మీరు చెల్లించిన మొత్తాన్ని నమోదు చేయండి (కనిష్ఠం ₹10)',
  'agent.emergencyForm.validation.txn':       'UPI లావాదేవీ ఐడి / UTRని నమోదు చేయండి (కనీసం 6 అక్షరాలు)',
  'agent.emergencyForm.cta':                  'రీఛార్జ్ అభ్యర్థన సమర్పించండి',
  'agent.emergencyForm.ctaSubmitting':        'సమర్పిస్తోంది…',
  'agent.emergencyForm.success.title':        'రీఛార్జ్ సమర్పించబడింది',
  'agent.emergencyForm.success.bodyAdmin':    'అభ్యర్థన #{id} అడ్మిన్ ఆమోదం కోసం వేచి ఉంది. ఆమోదం పొందిన తర్వాత, మీ వాలెట్ క్రెడిట్ అవుతుంది మరియు మీ ఖాతా స్వయంచాలకంగా అన్‌లాక్ అవుతుంది.',
  'agent.emergencyForm.success.bodyAgent':    'అభ్యర్థన #{id} @{username} ఆమోదం కోసం వేచి ఉంది. ఆమోదం పొందిన తర్వాత, మీ వాలెట్ క్రెడిట్ అవుతుంది మరియు మీ ఖాతా స్వయంచాలకంగా అన్‌లాక్ అవుతుంది.',
  'agent.emergencyForm.toast.submitted':      'రీఛార్జ్ అభ్యర్థన #{id} సమర్పించబడింది. ఆమోదం కోసం వేచి ఉంది.',
  'agent.emergencyForm.toast.cantSave':       'భాషను సేవ్ చేయలేకపోయాము',
  'agent.geo.title':            'ఒక్కసారి సెటప్: మీ స్థానాన్ని ధృవీకరించండి',
  'agent.geo.subtitle':         'మీరు డాష్‌బోర్డ్‌ను ఉపయోగించడానికి ముందు అవసరం.',
  'agent.geo.body.primary':     'బాలీ గేమ్స్ ప్లాట్‌ఫారమ్ కొత్త ప్లేయర్లను వారికి భౌతికంగా దగ్గరగా ఉన్న ఏజెంట్‌కి రూట్ చేస్తుంది. మీరు మీ ఖచ్చితమైన GPS స్థానాన్ని పంచుకునే వరకు, మీరు లొకేటర్‌లో కనిపించరు మరియు ఆర్గానిక్ సైన్‌అప్‌ల నుండి జీరో కొత్త ప్లేయర్లు పొందుతారు — మీ రెఫరల్ కోడ్‌ను నేరుగా టైప్ చేసే ప్లేయర్లు మాత్రమే మీకు వస్తారు.',
  'agent.geo.body.secondary':   '"నా ప్రస్తుత స్థానాన్ని ఉపయోగించు"ని ఒకసారి ట్యాప్ చేయండి. మేము మీ కోఆర్డినేట్‌లను మాత్రమే క్యాప్చర్ చేస్తాము, మరేదైనా కాదు.',
  'agent.geo.hint.disabled':    'పైన ఉన్న "నా ప్రస్తుత స్థానాన్ని ఉపయోగించు"ని ట్యాప్ చేయండి. పిన్‌కోడ్ లేదా మాన్యువల్ ప్రాంత ఎంపిక మాత్రమే సరిపోదు — లొకేటర్‌కి ఖచ్చితమైన GPS కోఆర్డినేట్‌లు అవసరం.',
  'agent.geo.error.fallback':   'స్థానాన్ని సేవ్ చేయలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.',
  'agent.geo.cta.save':         'సేవ్ చేసి కొనసాగించండి',
  'agent.geo.cta.saving':       'స్థానం సేవ్ అవుతోంది…',
  'agent.geo.toast.success':    'స్థానం సేవ్ చేయబడింది — మీరు ఇప్పుడు ప్లేయర్లను స్వీకరించగలరు.',
  'agent.wallet.hero.label':       'పూల్ బ్యాలెన్స్',
  'agent.wallet.hero.helper':      'ప్లేయర్ రీఛార్జ్‌లు మరియు సబ్-ఏజెంట్ టాప్-అప్‌లపై ఖర్చు చేయడానికి అందుబాటులో. ఆదాయాలు మరియు అడ్మిన్ టాప్-అప్‌లు ఇక్కడ చేరుతాయి.',
  'agent.wallet.action.recharge':  'రీఛార్జ్ అభ్యర్థన',
  'agent.wallet.action.withdraw':  'విత్‌డ్రాల్ అభ్యర్థన',
  'agent.wallet.tab.pending':      'పెండింగ్',
  'agent.wallet.tab.history':      'చరిత్ర',
  'agent.wallet.pending.loading':  'లోడ్ అవుతోంది…',
  'agent.wallet.pending.empty':    'ఇప్పుడే పెండింగ్ అభ్యర్థనలు లేవు.',
  'agent.wallet.pending.recharge.title':   'రీఛార్జ్ అభ్యర్థన · +{amount}',
  'agent.wallet.pending.recharge.meta':    'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'విత్‌డ్రాల్ అభ్యర్థన · −{amount}',
  'agent.wallet.pending.withdrawal.meta':  '{upi}కి · {when}',
  'agent.wallet.pending.badge':            'పెండింగ్',
  'agent.wallet.history.loading':  'లోడ్ అవుతోంది…',
  'agent.wallet.history.empty':    'ఇంకా వాలెట్ కార్యకలాపం లేదు.',
  'agent.wallet.history.recharge.titleAccepted': 'రీఛార్జ్ ఆమోదించబడింది · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'రీఛార్జ్ తిరస్కరించబడింది · {amount}',
  'agent.wallet.history.recharge.meta':          'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid':     'విత్‌డ్రాల్ చెల్లించబడింది · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'విత్‌డ్రాల్ తిరస్కరించబడింది · {amount}',
  'agent.wallet.history.withdrawal.meta':          '{upi}కి · {when}',
  'agent.wallet.history.withdrawal.paidTxn':       'చెల్లించబడింది · {txn}',
  'agent.wallet.state.accepted': 'ఆమోదించబడింది',
  'agent.wallet.state.paid':     'చెల్లించబడింది',
  'agent.wallet.state.rejected': 'తిరస్కరించబడింది',
  'agent.wallet.state.expired':  'గడువు ముగిసింది',
  'agent.wallet.ledger.kind.topupFromAdmin':   'అడ్మిన్ నుండి టాప్‌అప్',
  'agent.wallet.ledger.kind.topupToPlayer':    'ప్లేయర్‌కి టాప్‌అప్',
  'agent.wallet.ledger.kind.topupFromPlayer':  'ప్లేయర్ రీఛార్జ్',
  'agent.wallet.ledger.kind.revenueShare':     'రెవెన్యూ షేర్',
  'agent.wallet.ledger.kind.coFundOffer':      'కో-ఫండ్ ఆఫర్',
  'agent.wallet.ledger.kind.adminAdjust':      'అడ్మిన్ సర్దుబాటు',
  'agent.wallet.ledger.kind.withdrawalHold':   'విత్‌డ్రాల్ హోల్డ్',
  'agent.wallet.ledger.kind.withdrawalPaid':   'విత్‌డ్రాల్ చెల్లించబడింది',
  'agent.wallet.ledger.kind.withdrawalRefund': 'విత్‌డ్రాల్ రీఫండ్',
  'agent.wallet.ledger.balanceAfter':          'bal {amount}',
  'agent.deposit.hero.label':   'సెక్యూరిటీ డిపాజిట్',
  'agent.deposit.hero.helper':  'మీ పూల్‌కి వ్యతిరేకంగా కొలేటరల్. మీ వాలెట్ మంచి స్థితిలో ఉన్నంత కాలం తాకబడదు.',
  'agent.deposit.margin.safe':    'సురక్షితం',
  'agent.deposit.margin.warning': 'వార్నింగ్',
  'agent.deposit.margin.locked':  'లాక్',
  'agent.deposit.action.recharge': 'డిపాజిట్ రీఛార్జ్',
  'agent.deposit.action.withdraw': 'డిపాజిట్ విత్‌డ్రా',
  'agent.deposit.gauge.label':   'కవరేజ్ గేజ్',
  'agent.deposit.gauge.thresholdWarn':  'వార్న్',
  'agent.deposit.gauge.thresholdLock':  'లాక్',
  'agent.deposit.gauge.legend.warn': '{pct} వద్ద, మీకు వార్నింగ్ వస్తుంది',
  'agent.deposit.gauge.legend.lock': '{pct} వద్ద, మీ ఖాతా లాక్ అవుతుంది',
  'agent.deposit.gauge.caption.locked':  'పూల్ డిపాజిట్‌తో పూర్తిగా కవర్ చేయబడింది — మీరు టాప్ అప్ చేసే వరకు విత్‌డ్రాల్‌లు బ్లాక్ చేయబడ్డాయి.',
  'agent.deposit.gauge.caption.warning': 'పూల్ మీ డిపాజిట్‌లో {pct}% వాడేసింది. లాక్‌అవుట్‌ని నివారించడానికి 100% చేరకముందే టాప్ అప్ చేయండి.',
  'agent.deposit.gauge.caption.caution': 'పూల్ డిపాజిట్ కవరేజ్‌లో {pct}% ఉంది. మీరు ఇంకా 80% వార్నింగ్‌కి దిగువ సురక్షితంగా ఉన్నారు.',
  'agent.deposit.gauge.caption.safe':    'మీ డిపాజిట్ పూర్తిగా చెక్కుచెదరలేదు.',
  'agent.deposit.ledger.loading':         'లోడ్ అవుతోంది…',
  'agent.deposit.ledger.empty.title':     'ఇంకా డిపాజిట్ కార్యకలాపం లేదు',
  'agent.deposit.ledger.empty.body':      'అడ్మిన్ మీ డిపాజిట్‌ని క్రెడిట్ చేసినప్పుడు (లేదా రీఫండ్ ప్రాసెస్ చేసినప్పుడు), ప్రతి ఎంట్రీ ఇక్కడ కనిపిస్తుంది.',
  'agent.recharge.banner.eyebrow.wallet':  'మీ వాలెట్‌ని రీఛార్జ్ చేయండి',
  'agent.recharge.banner.eyebrow.deposit': 'మీ డిపాజిట్‌ని రీఛార్జ్ చేయండి',
  'agent.recharge.banner.body.wallet':     'ముందుగా మీ అప్‌లైన్‌కి చెల్లించండి, తర్వాత ప్రూఫ్‌ని ఇక్కడ సమర్పించండి, తద్వారా వారు మీ ఆపరేటింగ్ వాలెట్‌ని టాప్ అప్ చేయగలరు.',
  'agent.recharge.banner.body.deposit':    'ముందుగా మీ అప్‌లైన్‌కి చెల్లించండి, తర్వాత ప్రూఫ్‌ని ఇక్కడ సమర్పించండి, తద్వారా వారు మీ సెక్యూరిటీ డిపాజిట్‌ని టాప్ అప్ చేయగలరు.',
  'agent.recharge.banner.closeAria':       'ఫారం మూసివేయి',
  'agent.recharge.parent.label.admin':       'మీ అడ్మిన్‌కి చెల్లించండి',
  'agent.recharge.parent.label.parentAgent': 'మీ పేరెంట్ ఏజెంట్‌కి చెల్లించండి',
  'agent.recharge.parent.role.admin':        'ప్లాట్‌ఫారమ్ అడ్మిన్',
  'agent.recharge.parent.role.parentAgent':  'పేరెంట్ ఏజెంట్',
  'agent.recharge.parent.noQr':              'QR సెట్ చేయలేదు',
  'agent.recharge.parent.upi':               'UPI:',
  'agent.recharge.parent.loading':           'మీ అప్‌లైన్ చెల్లింపు వివరాలు లోడ్ అవుతున్నాయి…',
  'agent.recharge.parent.loadError':         'మీ అప్‌లైన్ చెల్లింపు వివరాలను లోడ్ చేయలేకపోయాము',
  'agent.recharge.method.eyebrow':  'మీరు ఎలా చెల్లించారు?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note':  'ఆన్‌లైన్ డిజిటల్ చెల్లింపు',
  'agent.recharge.method.cash.label': 'క్యాష్',
  'agent.recharge.method.cash.note':  'ఇన్-పర్సన్ హ్యాండ్-ఆఫ్',
  'agent.recharge.field.amount.label':       'మొత్తం (₹) · కనిష్ఠం ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi':      'UPI లావాదేవీ ఐడి / UTR',
  'agent.recharge.field.txn.label.cash':     'క్యాష్ రసీదు మార్కర్ (ఏదైనా చిన్న రెఫరెన్స్)',
  'agent.recharge.field.txn.placeholder.upi':  'ఉదా. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'ఉదా. CASH-Sun-eve',
  'agent.recharge.field.note.label':         'గమనిక (ఐచ్ఛికం, మీ అప్‌లైన్‌కి చూపబడుతుంది)',
  'agent.recharge.field.note.placeholder':   'మీ అప్‌లైన్ కోసం ఏదైనా సందర్భం',
  'agent.recharge.error.amountMin':  'మొత్తం పూర్తి రూపాయి ≥ 10 ఉండాలి',
  'agent.recharge.error.txnUpi':     'లావాదేవీ ఐడి చాలా చిన్నదిగా కనిపిస్తోంది — UPI రెఫరెన్స్‌ని పేస్ట్ చేయండి',
  'agent.recharge.error.txnCash':    'మీ అప్‌లైన్ హ్యాండ్-ఆఫ్‌ని మ్యాచ్ చేయగలిగేలా ఒక చిన్న మార్కర్‌ని జోడించండి (ఉదా. "CASH-Sun-eve")',
  'agent.recharge.cta.submit': 'అభ్యర్థన సమర్పించండి',
  'agent.recharge.cta.cancel': 'రద్దు',
  'agent.withdrawal.banner.eyebrow.wallet':  'వాలెట్ నుండి విత్‌డ్రా',
  'agent.withdrawal.banner.eyebrow.deposit': 'డిపాజిట్ నుండి విత్‌డ్రా',
  'agent.withdrawal.banner.body.wallet':     'మొత్తం తక్షణం హోల్డ్ చేయబడుతుంది, తద్వారా మీరు డబుల్-స్పెండ్ చేయలేరు. మీ అప్‌లైన్ మీకు చెల్లించి చెల్లించినట్లు మార్క్ చేస్తారు.',
  'agent.withdrawal.banner.body.deposit':    'మొత్తం వెంటనే మీ సెక్యూరిటీ డిపాజిట్ నుండి హోల్డ్ చేయబడుతుంది. మీ అప్‌లైన్ మీకు చెల్లించి చెల్లించినట్లు మార్క్ చేస్తారు.',
  'agent.withdrawal.banner.closeAria':       'ఫారం మూసివేయి',
  'agent.withdrawal.method.eyebrow':    'మీరు ఎలా చెల్లించబడాలనుకుంటున్నారు?',
  'agent.withdrawal.method.upi.label':  'UPI / QR',
  'agent.withdrawal.method.upi.note':   'ఆన్‌లైన్ డిజిటల్ చెల్లింపు',
  'agent.withdrawal.method.cash.label': 'క్యాష్',
  'agent.withdrawal.method.cash.note':  'ఇన్-పర్సన్ హ్యాండ్-ఆఫ్',
  'agent.withdrawal.field.amount.label':        'మొత్తం (₹) · అందుబాటులో {balance}',
  'agent.withdrawal.field.amount.placeholder':  '500',
  'agent.withdrawal.field.upi.label':           'మీ UPI ఐడి (మీ అప్‌లైన్ ఇక్కడ మీకు చెల్లిస్తారు)',
  'agent.withdrawal.field.upi.placeholder':     'you@bank',
  'agent.withdrawal.field.cashMarker.label':    'మీటింగ్ మార్కర్ (ఉదా. "Sun evening cash")',
  'agent.withdrawal.field.cashMarker.placeholder': 'క్యాష్‌ని ఎప్పుడు/ఎక్కడ హ్యాండ్ ఓవర్ చేయాలి',
  'agent.withdrawal.field.whatsapp.label':      'WhatsApp (ఐచ్ఛికం, మీ అప్‌లైన్ మిమ్మల్ని చేరుకోడానికి)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label':          'గమనిక (ఐచ్ఛికం)',
  'agent.withdrawal.field.note.placeholder':    'మీ అప్‌లైన్ కోసం ఏదైనా సందర్భం',
  'agent.withdrawal.error.amountMin':       'మొత్తం పూర్తి రూపాయి ≥ 10 ఉండాలి',
  'agent.withdrawal.error.amountOverflow':  'మీ వద్ద కేవలం {balance} అందుబాటులో ఉంది',
  'agent.withdrawal.error.upi':             'చెల్లుబాటు అయ్యే UPI ఐడిని నమోదు చేయండి',
  'agent.withdrawal.cta.submit': 'అభ్యర్థన సమర్పించండి',
  'agent.withdrawal.cta.cancel': 'రద్దు',
  'agent.people.hub.players.title':     'ప్లేయర్లు',
  'agent.people.hub.players.subtitle':  'ప్లేయర్లను మరియు వారి అభ్యర్థనలను నిర్వహించండి',
  'agent.people.hub.players.subtab.requests': 'ప్లేయర్ అభ్యర్థనలు',
  'agent.people.hub.players.subtab.list':     'ప్లేయర్లు',
  'agent.people.hub.agents.title':      'ఏజెంట్లు',
  'agent.people.hub.agents.subtitle':   'సబ్-ఏజెంట్లను మరియు వారి అభ్యర్థనలను నిర్వహించండి',
  'agent.people.hub.agents.subtab.requests': 'ఏజెంట్ అభ్యర్థనలు',
  'agent.people.hub.agents.subtab.list':     'ఏజెంట్లు',
  'agent.people.acceptingToggle.label':       'కొత్త ప్లేయర్లను స్వీకరిస్తోంది',
  'agent.people.acceptingToggle.bodyAccepting':  'మీరు జియోగ్రాఫిక్ లొకేటర్ నుండి కొత్త ప్లేయర్లను స్వీకరిస్తున్నారు. మీ ప్రాంతంలోని కోల్డ్ సైన్‌అప్‌లు మీకు స్వయంచాలకంగా రూట్ అవుతాయి.',
  'agent.people.acceptingToggle.bodyOptedOut':   'మీరు ఆప్ట్ అవుట్ అయ్యారు. లొకేటర్ మిమ్మల్ని దాటిపోతోంది — కేవలం డైరెక్ట్ రెఫరల్‌లు + మాన్యువల్‌గా అసైన్ చేసిన ప్లేయర్లు మాత్రమే మిమ్మల్ని చేరుకుంటారు. ఇప్పటికే ఉన్న ప్లేయర్లకు ప్రభావం లేదు.',
  'agent.people.acceptingToggle.error':       'అప్‌డేట్ చేయలేకపోయాము',
  'agent.people.acceptingToggle.aria.stop':   'కొత్త ప్లేయర్లను స్వీకరించడం ఆపండి',
  'agent.people.acceptingToggle.aria.start':  'కొత్త ప్లేయర్లను స్వీకరించడం ప్రారంభించండి',
  'agent.qr.audience.player':     'ప్లేయర్లు',
  'agent.qr.audience.subAgent':   'ఏజెంట్లు',
  'agent.qr.alt':                 'మీ చెల్లింపు QR ({audience})',
  'agent.qr.cardLabel.player':    'నా QR · ప్లేయర్లు చూసేది',
  'agent.qr.cardLabel.subAgent':  'నా QR · ఏజెంట్లు చూసేది',
  'agent.qr.upi.empty':           'ఇంకా UPI సెట్ చేయలేదు',
  'agent.qr.status.loading':      'లోడ్ అవుతోంది…',
  'agent.qr.status.live':         'లైవ్',
  'agent.qr.status.notSet':       'సెట్ చేయలేదు',
  'agent.qr.status.paused':       'నిలిపివేయబడింది',
  'agent.qr.cta.edit':            'QR & UPI ఎడిట్',
  'agent.qr.cta.setup':           'QR సెటప్',
  'agent.qr.cta.viewOnly':        'వీక్షణ మాత్రమే — ఎడిట్ చేయడానికి అడ్మిన్‌ని అడగండి',
  'agent.qr.modal.title':         'QR & UPI ఎడిట్',
  'agent.qr.modal.closeAria':     'మూసివేయి',
  'agent.qr.modal.upload':        'QR అప్‌లోడ్',
  'agent.qr.modal.replace':       'QR రీప్లేస్',
  'agent.qr.modal.remove':        'తీసివేయి',
  'agent.qr.modal.confirmRemove.player':   'QR ఇమేజ్‌ని తీసివేయాలా? మీ ప్లేయర్లు కాంటాక్ట్ ఫాల్‌బ్యాక్‌ని మాత్రమే చూస్తారు.',
  'agent.qr.modal.confirmRemove.subAgent': 'QR ఇమేజ్‌ని తీసివేయాలా? మీ ఏజెంట్లు కాంటాక్ట్ ఫాల్‌బ్యాక్‌ని మాత్రమే చూస్తారు.',
  'agent.qr.modal.field.upi.label':         'UPI ఐడి',
  'agent.qr.modal.field.upi.placeholder':   'ఉదా. baaligames@upi',
  'agent.qr.modal.field.whatsapp.label.player':   'WhatsApp నంబర్ (ఫాల్‌బ్యాక్)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ఏజెంట్ల కోసం WhatsApp నంబర్',
  'agent.qr.modal.field.whatsapp.placeholder.default':  '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'ఖాళీ → {number}కి ఫాల్‌బ్యాక్',
  'agent.qr.modal.field.whatsapp.fallbackHint':         'సబ్-ఏజెంట్లు {number} చూస్తారు (మీ ప్లేయర్-ఫేసింగ్ నంబర్).',
  'agent.qr.modal.field.phone.label.player':   'సపోర్ట్ ఫోన్ (ఫాల్‌బ్యాక్)',
  'agent.qr.modal.field.phone.label.subAgent': 'ఏజెంట్ల కోసం సపోర్ట్ ఫోన్',
  'agent.qr.modal.field.phone.placeholder.default':  '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'ఖాళీ → {number}కి ఫాల్‌బ్యాక్',
  'agent.qr.modal.field.phone.fallbackHint':         'సబ్-ఏజెంట్లు {number} చూస్తారు (మీ ప్లేయర్-ఫేసింగ్ నంబర్).',
  'agent.qr.modal.active.label':        'యాక్టివ్',
  'agent.qr.modal.active.description':  'ప్లేయర్లు రీఛార్జ్‌లు సమర్పించగలరు',
  'agent.qr.modal.active.on':           'ఆన్',
  'agent.qr.modal.active.paused':       'నిలిపివేయబడింది',
  'agent.qr.modal.cancel': 'రద్దు',
  'agent.qr.modal.save':   'సేవ్',
  'agent.manualRecharge.banner.eyebrow':   'మాన్యువల్ రీఛార్జ్',
  'agent.manualRecharge.banner.body.before': 'ఒక ',
  'agent.manualRecharge.banner.body.target': 'ప్లేయర్',
  'agent.manualRecharge.banner.body.after':  'కి నేరుగా మీ పూల్ నుండి డబ్బు పంపండి.',
  'agent.manualRecharge.pool.label':    'మీ పూల్',
  'agent.manualRecharge.pool.dailyCap': 'cap · {amount}/రోజు',
  'agent.manualRecharge.field.player.label':       'ప్లేయర్‌కి',
  'agent.manualRecharge.field.player.placeholder': 'ఒక ప్లేయర్‌ని ఎంచుకోండి…',
  'agent.manualRecharge.field.player.option':      '@{username} · {contact} · bal {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'ప్లేయర్‌కి',
  'agent.manualRecharge.field.player.lockedMeta':  '{contact} · ప్రస్తుత bal {balance}',
  'agent.manualRecharge.field.player.notFound':    'మీ స్వంత జాబితాలో ప్లేయర్ కనుగొనబడలేదు.',
  'agent.manualRecharge.field.amount.label':       'మొత్తం (₹) — {min} నుండి {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool':        'మీ పూల్ బ్యాలెన్స్‌ని మించిపోతుంది.',
  'agent.manualRecharge.error.belowMin':           'కనిష్ఠం ({min}) దిగువ.',
  'agent.manualRecharge.error.aboveMax':           'గరిష్ఠం ({max}) పైన.',
  'agent.manualRecharge.field.note.label':       'గమనిక (ఐచ్ఛికం, ప్లేయర్ లెడ్జర్‌లో చూపబడుతుంది)',
  'agent.manualRecharge.field.note.placeholder': 'ఉదా. షాప్‌లో క్యాష్ సేకరించబడింది',
  'agent.manualRecharge.loading':       'మీ పూల్ లోడ్ అవుతోంది…',
  'agent.manualRecharge.cta.cancel':    'రద్దు',
  'agent.manualRecharge.cta.send':      '{amount} పంపండి',
  'agent.manualRecharge.success.title': '@{username}కి {amount} పంపబడింది',
  'agent.manualRecharge.success.meta':  'మీ పూల్ · {pool} · వారి వాలెట్ · {playerBal}',
  'agent.manualRecharge.success.again': 'మరొకటి పంపండి',
  'agent.manualSubAgentRecharge.banner.eyebrow':   'మాన్యువల్ రీఛార్జ్',
  'agent.manualSubAgentRecharge.banner.body.before': 'ఒక ',
  'agent.manualSubAgentRecharge.banner.body.target': 'సబ్-ఏజెంట్',
  'agent.manualSubAgentRecharge.banner.body.after':  'కి నేరుగా మీ పూల్ నుండి డబ్బు పంపండి.',
  'agent.manualSubAgentRecharge.pool.label':       'మీ పూల్',
  'agent.manualSubAgentRecharge.field.subAgent.label':       'సబ్-ఏజెంట్‌కి',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'ఒక సబ్-ఏజెంట్‌ని ఎంచుకోండి…',
  'agent.manualSubAgentRecharge.field.subAgent.option':      '@{username} · {email} · pool {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'యాక్టివ్ డైరెక్ట్ సబ్-ఏజెంట్లు లేరు',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'సబ్-ఏజెంట్‌కి',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta':  '{email} · ప్రస్తుత pool {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound':    'మీ డైరెక్ట్ డౌన్‌లైన్‌లో సబ్-ఏజెంట్ కనుగొనబడలేదు.',
  'agent.manualSubAgentRecharge.field.amount.label':       'మొత్తం (₹) — {min} నుండి {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool':        'మీ పూల్ బ్యాలెన్స్‌ని మించిపోతుంది.',
  'agent.manualSubAgentRecharge.error.belowMin':           'కనిష్ఠం ({min}) దిగువ.',
  'agent.manualSubAgentRecharge.field.note.label':       'గమనిక (ఐచ్ఛికం, రెండు లెడ్జర్‌లపై చూపబడుతుంది)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'ఉదా. ఫ్లోట్ ముందస్తు',
  'agent.manualSubAgentRecharge.loading':       'మీ పూల్ లోడ్ అవుతోంది…',
  'agent.manualSubAgentRecharge.cta.cancel':    'రద్దు',
  'agent.manualSubAgentRecharge.cta.send':      '{amount} పంపండి',
  'agent.manualSubAgentRecharge.success.title': '@{username}కి {amount} పంపబడింది',
  'agent.manualSubAgentRecharge.success.meta':  'మీ పూల్ · {parent} · వారి పూల్ · {child}',
  'agent.manualSubAgentRecharge.success.again': 'మరొకటి పంపండి',
  'agent.playerRequests.direction.recharge':   'రీఛార్జ్',
  'agent.playerRequests.direction.withdrawal': 'విత్‌డ్రాల్',
  'agent.playerRequests.tab.pending':          'పెండింగ్',
  'agent.playerRequests.tab.history':          'చరిత్ర',
  'agent.playerRequests.loading':         'లోడ్ అవుతోంది…',
  'agent.playerRequests.empty.pending.recharge':   'ఇప్పుడే పెండింగ్ రీఛార్జ్‌లు లేవు.',
  'agent.playerRequests.empty.pending.withdrawal': 'ఇప్పుడే పెండింగ్ విత్‌డ్రాల్‌లు లేవు.',
  'agent.playerRequests.empty.history':            'ఇంకా చరిత్ర లేదు.',
  'agent.playerRequests.prompt.rejectRecharge':   'తిరస్కరణ కారణం (ఐచ్ఛికం):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'తిరస్కరణ కారణం (ప్లేయర్ హోల్డ్ చేసిన బ్యాలెన్స్ రీఫండ్ అవుతుంది):',
  'agent.playerRequests.toast.rejected':              'తిరస్కరించబడింది',
  'agent.playerRequests.toast.rejectedRefunded':      'తిరస్కరించబడింది · హోల్డ్ చేసిన బ్యాలెన్స్ రీఫండ్',
  'agent.playerRequests.toast.approved':              '{amount} ఆమోదించబడింది',
  'agent.playerRequests.toast.approvedWithBonus':     '{amount} ఆమోదించబడింది · ప్రిన్సిపల్ + ₹{bonus} బోనస్',
  'agent.playerRequests.toast.markedPaid':            '{amount} చెల్లించినట్లు మార్క్',
  'agent.playerRequests.card.upiTxnLabel':            'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel':          'చెల్లించండి (UPI)',
  'agent.playerRequests.card.whatsapp':               'WhatsApp',
  'agent.playerRequests.card.viewQr':                 'QR చూడండి',
  'agent.playerRequests.card.bonusChip':              'బోనస్',
  'agent.playerRequests.card.partialApproved':        'అభ్యర్థించిన {requested}లో {approved} ఆమోదించబడింది',
  'agent.playerRequests.card.partialPaid':            'అభ్యర్థించిన {requested}లో {approved} చెల్లించబడింది · {refund} రీఫండ్',
  'agent.playerRequests.card.paidTxnPrefix':          'చెల్లించబడింది · {txn}',
  'agent.playerRequests.action.reject':     'తిరస్కరించు',
  'agent.playerRequests.action.approve':    'ఆమోదించు',
  'agent.playerRequests.action.markPaid':   'చెల్లించినట్లు మార్క్',
  'agent.playerRequests.state.pending':       'పెండింగ్',
  'agent.playerRequests.state.rejected':      'తిరస్కరించబడింది',
  'agent.playerRequests.state.expired':       'గడువు ముగిసింది',
  'agent.playerRequests.state.accepted':      'ఆమోదించబడింది',
  'agent.playerRequests.state.manuallyPaid':  'మాన్యువల్‌గా చెల్లించబడింది',
  'agent.peopleQueue.pagination':  '{total}లో {from}–{to}',
  'agent.agentRequests.direction.recharge':   'రీఛార్జ్',
  'agent.agentRequests.direction.withdrawal': 'విత్‌డ్రాల్',
  'agent.agentRequests.tab.pending':          'పెండింగ్',
  'agent.agentRequests.tab.history':          'చరిత్ర',
  'agent.agentRequests.loading':         'లోడ్ అవుతోంది…',
  'agent.agentRequests.empty.pending.recharge':   'ఇప్పుడే పెండింగ్ రీఛార్జ్‌లు లేవు.',
  'agent.agentRequests.empty.pending.withdrawal': 'ఇప్పుడే పెండింగ్ విత్‌డ్రాల్‌లు లేవు.',
  'agent.agentRequests.empty.history':            'ఇంకా చరిత్ర లేదు.',
  'agent.agentRequests.prompt.rejectRecharge':   'తిరస్కరణ కారణం (ఐచ్ఛికం):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'తిరస్కరణ కారణం (సబ్-ఏజెంట్ హోల్డ్ చేసిన బ్యాలెన్స్ రీఫండ్ అవుతుంది):',
  'agent.agentRequests.toast.rejected':         'తిరస్కరించబడింది',
  'agent.agentRequests.toast.rejectedRefunded': 'తిరస్కరించబడింది · హోల్డ్ చేసిన బ్యాలెన్స్ రీఫండ్',
  'agent.agentRequests.toast.approved':         '{amount} ఆమోదించబడింది · పూల్ క్రెడిట్ చేయబడింది',
  'agent.agentRequests.toast.markedPaid':       '{amount} చెల్లించినట్లు మార్క్',
  'agent.agentRequests.card.agentPill':       'ఏజెంట్',
  'agent.agentRequests.card.upiTxnLabel':         'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel':     'క్యాష్ మార్కర్',
  'agent.agentRequests.card.payToUpiLabel':       'చెల్లించండి (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel':  'హ్యాండ్-ఆఫ్ మార్కర్',
  'agent.agentRequests.card.whatsapp':            'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved':     'అభ్యర్థించిన {requested}లో {approved} ఆమోదించబడింది',
  'agent.agentRequests.card.partialPaid':         'అభ్యర్థించిన {requested}లో {approved} చెల్లించబడింది · {refund} రీఫండ్',
  'agent.agentRequests.card.paidTxnPrefix':       'చెల్లించబడింది · {txn}',
  'agent.agentRequests.card.chip.deposit':  'డిపాజిట్',
  'agent.agentRequests.card.chip.cash':     'క్యాష్',
  'agent.agentRequests.card.chip.upi':      'UPI',
  'agent.agentRequests.action.reject':    'తిరస్కరించు',
  'agent.agentRequests.action.approve':   'ఆమోదించు',
  'agent.agentRequests.action.markPaid':  'చెల్లించినట్లు మార్క్',
  'agent.agentRequests.state.pending':      'పెండింగ్',
  'agent.agentRequests.state.rejected':     'తిరస్కరించబడింది',
  'agent.agentRequests.state.expired':      'గడువు ముగిసింది',
  'agent.agentRequests.state.accepted':     'ఆమోదించబడింది',
  'agent.agentRequests.state.manuallyPaid': 'మాన్యువల్‌గా చెల్లించబడింది',
  'agent.playersList.add':         'ప్లేయర్‌ని జోడించు',
  'agent.playersList.loading':     'లోడ్ అవుతోంది…',
  'agent.playersList.empty':       'మ్యాచ్ అయ్యే ప్లేయర్లు లేరు',
  'agent.playersList.results':     '{total}లో {from}–{to} చూపిస్తోంది',
  'agent.playersList.searchPlaceholder': 'యూజర్‌నేమ్, ఇమెయిల్, ఫోన్ శోధించండి…',
  'agent.playersList.filter.all':       'అన్నీ',
  'agent.playersList.filter.active':    'యాక్టివ్',
  'agent.playersList.filter.pending':   'పెండింగ్',
  'agent.playersList.filter.suspended': 'నిలిపివేయబడింది',
  'agent.playersList.filter.banned':    'నిషేధించబడింది',
  'agent.playersList.empty.title': 'ప్లేయర్లు కనుగొనబడలేదు',
  'agent.playersList.empty.body':  'వేరే శోధన లేదా స్థితి ఫిల్టర్‌ని ప్రయత్నించండి.',
  'agent.playersList.pagination': 'పేజీ {page} / {totalPages}',
  'agent.playersList.card.status.active':    'యాక్టివ్',
  'agent.playersList.card.status.pending':   'పెండింగ్',
  'agent.playersList.card.status.suspended': 'నిలిపివేయబడింది',
  'agent.playersList.card.status.banned':    'నిషేధించబడింది',
  'agent.playersList.card.verifiedBadge':    'వెరిఫైడ్',
  'agent.playersList.column.wallet':  'వాలెట్',
  'agent.playersList.column.joined':  'చేరింది',
  'agent.subAgentsList.intro':     'మీరు సృష్టించే ఏజెంట్లు మీ క్రింద పనిచేస్తారు. వారు ప్రతి పిల్లర్‌పై 0%తో ప్రారంభమవుతారు — వారి వివరాల పేజీ నుండి గేమ్‌వారీ రేట్‌లను మంజూరు చేయండి.',
  'agent.subAgentsList.cta.new':   'కొత్త సబ్-ఏజెంట్',
  'agent.subAgentsList.loading':   'లోడ్ అవుతోంది…',
  'agent.subAgentsList.empty.title':            'ఇంకా సబ్-ఏజెంట్లు లేరు',
  'agent.subAgentsList.empty.body.canCreate':   'ఒకటి సృష్టించడానికి "+ కొత్త సబ్-ఏజెంట్" ట్యాప్ చేయండి. వారు తమ సొంత పూల్, ప్లేయర్లు మరియు ఆదాయాలతో ఇక్కడ కనిపిస్తారు — మీచే నిర్వహించబడతారు.',
  'agent.subAgentsList.empty.body.cantCreate':  'మీ అడ్మిన్ ఇంకా మీకు సబ్-ఏజెంట్ సృష్టి హక్కులను మంజూరు చేయలేదు. మీ అనుమతులపై create_subagentని "edit"గా మార్చమని వారిని అడగండి.',
  'agent.subAgentsList.card.suspendedBadge': 'నిలిపివేయబడింది',
  'agent.subAgentsList.column.agents':       'ఏజెంట్లు',
  'agent.subAgentsList.column.players':      'ప్లేయర్లు',
  'agent.subAgentsList.column.pool':         'పూల్',
  'agent.subAgentsList.row.directLabel':      'డైరెక్ట్',
  'agent.subAgentsList.row.indirectLabel':    'ఇన్‌డైరెక్ట్',
  'agent.subAgentsList.modal.title':  'కొత్త సబ్-ఏజెంట్',
  'agent.subAgentsList.modal.body':   'వారు ప్రతి పిల్లర్‌పై 0%తో ప్రారంభమవుతారు. వారిని సృష్టించిన తర్వాత వారి వివరాల పేజీ నుండి గేమ్‌వారీగా వారి రేట్లను మంజూరు చేయండి.',
  'agent.subAgentsList.modal.field.username.label':       'యూజర్‌నేమ్',
  'agent.subAgentsList.modal.field.username.placeholder': 'ఆల్ఫాన్యూమెరిక్, ._- అనుమతించబడింది; కనిష్ఠం 3 అక్షరాలు',
  'agent.subAgentsList.modal.field.email.label':          'ఇమెయిల్',
  'agent.subAgentsList.modal.field.email.placeholder':    'they@example.com',
  'agent.subAgentsList.modal.field.password.label':       'ఆరంభ పాస్‌వర్డ్',
  'agent.subAgentsList.modal.field.password.placeholder': 'కనిష్ఠం 8 అక్షరాలు',
  'agent.subAgentsList.modal.field.password.hint':        'కొత్త ఏజెంట్ లాగిన్ చేయగలిగేలా దీనిని వారితో పంచుకోండి. మొదటి లాగిన్‌లో దానిని మార్చాలి.',
  'agent.subAgentsList.modal.field.location.label':       'సబ్-ఏజెంట్ స్థానం',
  'agent.subAgentsList.modal.field.location.optional':    '(ఎక్కువగా సిఫార్సు చేయబడింది)',
  'agent.subAgentsList.modal.field.location.missing':     'స్థానం లేకుండా, జియోగ్రాఫిక్ లొకేటర్ ఈ సబ్-ఏజెంట్‌కి ఏ కొత్త ప్లేయర్‌ని రూట్ చేయలేదు. వారు సైన్ ఇన్ చేసి తమ సొంత GPSని పూర్తి చేసే వరకు డైరెక్ట్ రెఫరల్ ద్వారా మాత్రమే ప్లేయర్లను స్వీకరిస్తారు.',
  'agent.subAgentsList.modal.cta.cancel':   'రద్దు',
  'agent.subAgentsList.modal.cta.create':   'సబ్-ఏజెంట్‌ని సృష్టించు',
  'agent.subAgentsList.modal.cta.creating': 'సృష్టిస్తోంది…',
  'agent.approvalEdit.title.recharge':    '{who} నుండి రీఛార్జ్‌ను ఆమోదించండి',
  'agent.approvalEdit.title.withdrawal':  '{who} కోసం విత్‌డ్రాల్ చెల్లించినట్లు మార్క్ చేయండి',
  'agent.approvalEdit.requestedLabel':    'అభ్యర్థించిన:',
  'agent.approvalEdit.field.amount.label': 'మొత్తం ఆమోదించండి (₹)',
  'agent.approvalEdit.field.amount.hint':  'అభ్యర్థించిన {amount}ని మించలేదు.',
  'agent.approvalEdit.partial.withdrawal.refund':   '{refund} {who} వాలెట్‌కి స్వయంచాలకంగా రీఫండ్ చేయబడుతుంది.',
  'agent.approvalEdit.partial.recharge.bonusWarn':  'పాక్షిక ఆమోదం. అటాచ్ చేసిన ఏ బోనస్ అయినా స్వయంచాలకంగా తిరస్కరించబడుతుంది.',
  'agent.approvalEdit.field.reason.label':         'కారణం',
  'agent.approvalEdit.field.reason.requiredHint':  '(అవసరం, ≥5 అక్షరాలు)',
  'agent.approvalEdit.field.reason.optionalHint':  '(ఐచ్ఛికం)',
  'agent.approvalEdit.field.reason.placeholder.partial':  'ఉదా. ప్లేయర్ కేవలం 30 రూపాయలు మాత్రమే చెల్లించారు',
  'agent.approvalEdit.field.reason.placeholder.optional': 'ఐచ్ఛిక గమనిక',
  'agent.approvalEdit.field.txn.label':       'UPI లావాదేవీ ఐడి',
  'agent.approvalEdit.field.txn.placeholder': 'ఉదా. 412312345678',
  'agent.approvalEdit.error.insufficient': 'మీ పూల్ ({balance}) ఈ ఆమోదాన్ని కవర్ చేయలేదు. ముందుగా టాప్ అప్ చేయండి.',
  'agent.approvalEdit.error.amountMin':      'ఆమోదించిన మొత్తం కనీసం ₹1 ఉండాలి',
  'agent.approvalEdit.error.amountMax':      'ఆమోదించిన మొత్తం అభ్యర్థించిన {amount}ని మించలేదు',
  'agent.approvalEdit.error.reasonRequired': 'వేరే మొత్తాన్ని ఆమోదించేటప్పుడు కారణం (≥5 అక్షరాలు) అవసరం',
  'agent.approvalEdit.error.txnRequired':    'UPI లావాదేవీ ఐడి అవసరం (≥6 అక్షరాలు)',
  'agent.approvalEdit.error.fallback':       'విఫలమైంది — మళ్లీ ప్రయత్నించండి',
  'agent.approvalEdit.cta.cancel':       'రద్దు',
  'agent.approvalEdit.cta.approve':      'ఆమోదించు',
  'agent.approvalEdit.cta.markPaid':     'చెల్లించినట్లు మార్క్',
  'agent.approvalEdit.cta.submitting':   'సమర్పిస్తోంది…',
  'agent.profile.hub.title':         'నా ఖాతా',
  'agent.profile.hub.subtitle':      'ఐడెంటిటీ, వాలెట్ మరియు డిపాజిట్ ఒకే చోట',
  'agent.profile.hub.tab.wallet':    'వాలెట్',
  'agent.profile.hub.tab.deposit':   'డిపాజిట్',
  'agent.profile.hub.tab.account':   'ఖాతా',
  'agent.profile.signOut.cta':            'సైన్ అవుట్',
  'agent.profile.signOut.modal.title':    'సైన్ అవుట్?',
  'agent.profile.signOut.modal.body':     'మీ ఖాతాని యాక్సెస్ చేయడానికి మీరు మళ్లీ సైన్ ఇన్ చేయాలి.',
  'agent.profile.signOut.modal.stay':     'సైన్ ఇన్‌లో ఉండండి',
  'agent.profile.signOut.modal.confirm':  'అవును, సైన్ అవుట్',
  'agent.profile.signOut.modal.busy':     'సైన్ అవుట్ అవుతోంది…',
  'agent.profile.signOut.error':          'సైన్ అవుట్ చేయలేకపోయాము',
  'agent.account.notSignedIn':            'సైన్ ఇన్ చేయలేదు.',
  'agent.account.hero.role.agent':        'ఏజెంట్',
  'agent.account.hero.role.admin':        'ప్లాట్‌ఫారమ్ అడ్మిన్',
  'agent.account.hero.changePhoto':       'ఫోటో మార్చండి',
  'agent.account.hero.removePhoto':       'ఫోటో తీసివేయండి',
  'agent.account.avatar.cropTitle':       'మీ ఫోటోను సర్దుబాటు చేయండి',
  'agent.account.avatar.confirmRemove':   'మీ ప్రొఫైల్ ఫోటోను తీసివేయాలా?',
  'agent.account.toast.photoUpdated':     'ఫోటో అప్‌డేట్ చేయబడింది',
  'agent.account.toast.photoRemoved':     'ఫోటో తీసివేయబడింది',
  'agent.account.toast.saved':            'సేవ్ చేయబడింది',
  'agent.account.toast.locationSaved':    'స్థానం సేవ్ చేయబడింది',
  'agent.account.toast.passwordChanged':  'పాస్‌వర్డ్ మార్చబడింది — మళ్లీ సైన్ ఇన్ చేయండి',
  'agent.account.error.uploadFailed':     'అప్‌లోడ్ విఫలమైంది',
  'agent.account.error.removeFailed':     'విఫలమైంది',
  'agent.account.error.saveFailed':       'సేవ్ విఫలమైంది',
  'agent.account.error.changeFailed':     'మార్పు విఫలమైంది',
  'agent.account.info.title':             'ఖాతా సమాచారం',
  'agent.account.info.subtitle':          'రీడ్-ఓన్లీ ప్లాట్‌ఫారమ్ మెటాడేటా',
  'agent.account.info.memberSince':       'సభ్యత్వం',
  'agent.account.info.lastLogin':         'చివరి లాగిన్',
  'agent.account.info.lastLoginIp':       'చివరి లాగిన్ IP',
  'agent.account.info.reportsTo':         'రిపోర్ట్స్ టు',
  'agent.account.info.reportsToValue':    'అడ్మిన్ #{id}',
  'agent.account.info.agentId':           'ఏజెంట్ ID',
  'agent.account.info.firstLogin':        'మొదటి సారి',
  'agent.account.info.emptyDash':         '—',
  'agent.account.personal.title':                   'వ్యక్తిగత వివరాలు',
  'agent.account.personal.subtitle.locked':         'అడ్మిన్ చేత ఐడెంటిటీ లాక్ · వీక్షణ-మాత్రమే',
  'agent.account.personal.subtitle.agent':          'యూజర్‌నేమ్, ఇమెయిల్ మరియు సంప్రదింపు నంబర్',
  'agent.account.personal.subtitle.admin':          'యూజర్‌నేమ్ మరియు ఇమెయిల్',
  'agent.account.personal.field.username':          'యూజర్‌నేమ్',
  'agent.account.personal.field.email':             'ఇమెయిల్',
  'agent.account.personal.field.phone':             'సంప్రదింపు నంబర్',
  'agent.account.personal.field.phone.placeholder': 'ఉదా. +91 9876543210',
  'agent.account.personal.field.phone.hint':        'ప్లేయర్లు మరియు అడ్మిన్ ఈ నంబర్‌లో మిమ్మల్ని చేరుకుంటారు. క్లియర్ చేయడానికి ఖాళీగా ఉంచండి.',
  'agent.account.personal.fieldLockedNote':         'లాక్',
  'agent.account.personal.cta.save':                'మార్పులను సేవ్ చేయండి',
  'agent.account.personal.cta.saving':              'సేవ్ అవుతోంది…',
  'agent.account.personal.cta.noChanges':           'మార్పులు లేవు',
  'agent.account.location.title':              'మీ స్థానం',
  'agent.account.location.subtitle.summary':   'లొకేటర్ కొత్త ప్లేయర్లను మీ సమీప ప్రాంతానికి రూట్ చేస్తుంది',
  'agent.account.location.subtitle.edit':      'సరైన ప్లేయర్లను మీకు రూట్ చేయడానికి మీ ప్రాంతాన్ని అప్‌డేట్ చేయండి',
  'agent.account.location.cta.edit':           'ఎడిట్',
  'agent.account.location.cta.cancel':         'రద్దు',
  'agent.account.location.cta.save':           'సేవ్',
  'agent.account.location.areaLabel':          'ప్రాంతం',
  'agent.account.location.gpsLabel':           'GPS',
  'agent.account.location.setLabel':           'స్థానం సెట్',
  'agent.account.location.setBody':            'GPS కోఆర్డినేట్‌లు ఫైల్‌లో ఉన్నాయి — ప్రాంత వివరాలను చూడడానికి ఎడిట్ ట్యాప్ చేయండి.',
  'agent.account.location.lastUpdated':        'చివరిసారి అప్‌డేట్ · {date}',
  'agent.account.security.title':                   'భద్రత',
  'agent.account.security.subtitle.locked':         'అడ్మిన్ చేత లాక్ · వీక్షణ-మాత్రమే',
  'agent.account.security.subtitle.unlocked':       'మీ పాస్‌వర్డ్ మార్చండి · ఇతర పరికరాలను సైన్ అవుట్ చేస్తుంది',
  'agent.account.security.field.current':           'ప్రస్తుత పాస్‌వర్డ్',
  'agent.account.security.field.new':               'కొత్త పాస్‌వర్డ్',
  'agent.account.security.field.new.hint':          'కనీసం 8 అక్షరాలు, ఒక అక్షరం మరియు ఒక అంకెతో',
  'agent.account.security.field.confirm':           'కొత్తదాన్ని ధృవీకరించండి',
  'agent.account.security.cta.update':              'పాస్‌వర్డ్ అప్‌డేట్',
  'agent.account.security.cta.updating':            'అప్‌డేట్ అవుతోంది…',
  'agent.account.security.error.mismatch':          'కొత్త పాస్‌వర్డ్‌లు సరిపోలడం లేదు',
  'agent.account.security.error.tooShort':          'పాస్‌వర్డ్ ≥ 8 అక్షరాలు ఉండాలి',
  'agent.account.security.error.weak':              'పాస్‌వర్డ్‌లో కనీసం ఒక అక్షరం మరియు ఒక సంఖ్య ఉండాలి',
  'agent.shareableId.eyebrow':          'షేర్ చేయదగిన ఏజెంట్ ID',
  'agent.shareableId.tooltip.copy':     'UAIDని కాపీ చేయి',
  'agent.shareableId.tooltip.copied':   'UAID కాపీ చేయబడింది',
  'agent.shareableId.cta.copy':         'కాపీ',
  'agent.shareableId.cta.copied':       'కాపీ చేయబడింది!',
  'agent.shareableId.body.before':      'ఈ కోడ్‌ని మరో ఏజెంట్‌తో పంచుకోండి, తద్వారా వారు ప్లేయర్లను మీకు ట్రాన్స్‌ఫర్ చేయగలరు. చాట్ లేదా WhatsApp ద్వారా పంపడం సురక్షితం — దీనిని పంచుకోవడం మీ ఖాతాకి ఏ యాక్సెస్‌ని ',
  'agent.shareableId.body.not':         'మంజూరు చేయదు',
  'agent.shareableId.body.after':       '.',
  'agent.rewards.hub.title':            'రివార్డ్‌లు',
  'agent.rewards.hub.subtitle':         'ఆఫర్లను బ్రౌజ్ చేయండి మరియు మీ ఎక్స్‌పోజర్‌ని ట్రాక్ చేయండి',
  'agent.rewards.hub.tab.browse':       'ఆఫర్లు బ్రౌజ్ చేయండి',
  'agent.rewards.hub.tab.exposure':     'నా ఎక్స్‌పోజర్',
  'agent.rewards.status.optedIn':       'ఆప్ట్ ఇన్',
  'agent.rewards.status.optedOut':      'ఆప్ట్ అవుట్',
  'agent.rewards.browse.hero.eyebrow':         'మీ ప్లేయర్ల కోసం ఫీచర్డ్',
  'agent.rewards.browse.empty.title':          'ఇప్పుడే యాక్టివ్ రివార్డ్‌లు లేవు',
  'agent.rewards.browse.empty.body':           'అడ్మిన్ క్రమం తప్పకుండా కొత్త ఆఫర్లను పోస్ట్ చేస్తారు. త్వరలో మళ్లీ చూడండి — లేదా మీరు మిస్ అయిన ఏదైనా ఆఫర్‌లోకి మిమ్మల్ని ఆప్ట్ ఇన్ చేయమని మీ అప్‌లైన్‌ని అడగండి.',
  'agent.rewards.browse.error.loadFailed':     'రివార్డ్‌లను లోడ్ చేయలేకపోయాము',
  'agent.rewards.browse.card.aria.manage':     '{title} కోసం ఆప్ట్-ఇన్‌ని నిర్వహించండి',
  'agent.rewards.browse.chip.optedIn':         'ఆప్ట్ ఇన్',
  'agent.rewards.browse.chip.optInPrompt':     'ఆప్ట్ ఇన్ చేయడానికి ట్యాప్ చేయండి',
  'agent.rewards.browse.chip.shareSuffix':     '· {pct}%',
  'agent.rewards.exposure.loading':            'మీ ఎక్స్‌పోజర్‌ని లెక్కిస్తోంది…',
  'agent.rewards.exposure.error.loadFailed':   'ఎక్స్‌పోజర్‌ని లోడ్ చేయలేకపోయాము',
  'agent.rewards.exposure.hero.eyebrow':                 'ఆఫర్ ఎక్స్‌పోజర్',
  'agent.rewards.exposure.hero.activeOptIns.one':        '{count} యాక్టివ్ ఆప్ట్-ఇన్',
  'agent.rewards.exposure.hero.activeOptIns.other':      '{count} యాక్టివ్ ఆప్ట్-ఇన్‌లు',
  'agent.rewards.exposure.hero.actualSpend.label':       'వాస్తవ ఖర్చు',
  'agent.rewards.exposure.hero.actualSpend.helper':      'ఇప్పటివరకు మీ డౌన్‌లైన్ క్లెయిమ్ చేసింది.',
  'agent.rewards.exposure.hero.maxExposure.label':       'మ్యాక్స్ ఎక్స్‌పోజర్',
  'agent.rewards.exposure.hero.maxExposure.helper':      'వర్స్ట్-కేస్ సీలింగ్ — ప్రతి మిగిలి ఉన్న బడ్జెట్ రూపాయి క్లెయిమ్ చేయబడింది.',
  'agent.rewards.exposure.hero.ceiling.label':           'వాడుకున్న సీలింగ్',
  'agent.rewards.exposure.gauge.label':                  'రిస్క్ గేజ్',
  'agent.rewards.exposure.gauge.maxSuffix':              '/ {amount} మ్యాక్స్',
  'agent.rewards.exposure.gauge.legend.amber.before':    'ఈ ',
  'agent.rewards.exposure.gauge.legend.amber.value':     '50%',
  'agent.rewards.exposure.gauge.legend.amber.after':     '-వద్ద మీ ఆప్ట్-ఇన్‌లను గమనించండి',
  'agent.rewards.exposure.gauge.legend.rose.before':     'ఈ ',
  'agent.rewards.exposure.gauge.legend.rose.value':      '80%',
  'agent.rewards.exposure.gauge.legend.rose.after':      '-వద్ద, మీరు మీ సీలింగ్‌కి దగ్గర',
  'agent.rewards.exposure.gauge.caption.noMax':          'మీరు ఇంకా ప్రాజెక్టెడ్ సీలింగ్ కలిగిన దేనిలోనూ ఆప్ట్-ఇన్ చేయలేదు.',
  'agent.rewards.exposure.gauge.caption.high':           'మీరు మీ మ్యాక్స్ ఎక్స్‌పోజర్‌లో {pct}% కాల్చేశారు. చాలా ఆప్ట్-ఇన్‌లు ఎక్కువగా క్లెయిమ్ చేయబడ్డాయి.',
  'agent.rewards.exposure.gauge.caption.mid':            'మీరు మీ మ్యాక్స్ సీలింగ్‌లో {pct}% వద్ద ఉన్నారు. ఆఫర్-వారీ విభజనపై దృష్టి ఉంచండి.',
  'agent.rewards.exposure.gauge.caption.low':            'సౌకర్యం — మీ మ్యాక్స్ సీలింగ్‌లో ఇప్పటివరకు కేవలం {pct}% మాత్రమే వాడుకున్నారు.',
  'agent.rewards.exposure.footnote.heading':             'మ్యాక్స్ ఎక్స్‌పోజర్ ఎలా లెక్కించబడుతుంది:',
  'agent.rewards.exposure.footnote.prefix':              ' ప్రతి ఆప్ట్-ఇన్ ఆఫర్ కోసం: ',
  'agent.rewards.exposure.footnote.formula':             'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix':              ' — కన్సర్వేటివ్ అప్పర్ బౌండ్. మీ వాస్తవ బాధ్యత సాధారణంగా ',
  'agent.rewards.exposure.footnote.emphasis':            'తక్కువ',
  'agent.rewards.exposure.footnote.suffix2':             ' మీ క్రింద ఉన్న సబ్-ఏజెంట్లు కూడా ఆప్ట్ ఇన్ చేసినప్పుడు (వారు పెద్ద స్లైస్ చెల్లిస్తారు; మీరు మిగిలినది చెల్లిస్తారు).',
  'agent.rewards.exposure.empty.title':                  'యాక్టివ్ ఆప్ట్-ఇన్‌లు లేవు',
  'agent.rewards.exposure.empty.body':                   'మీరు బ్రౌజ్ ఆఫర్లు ట్యాబ్ నుండి ఒక ఆఫర్‌లోకి ఆప్ట్ ఇన్ అయిన తర్వాత, అది మీ ఖర్చు మరియు ప్రాజెక్టెడ్ ఎక్స్‌పోజర్‌తో ఇక్కడ కనిపిస్తుంది.',
  'agent.rewards.exposure.row.share':                    '{pct}% షేర్',
  'agent.rewards.exposure.row.claims.one':               '{count} క్లెయిమ్',
  'agent.rewards.exposure.row.claims.other':             '{count} క్లెయిమ్‌లు',
  'agent.rewards.exposure.row.overridden':               'అడ్మిన్-ఓవర్‌రైడ్',
  'agent.rewards.exposure.row.actualMaxLabel':           'వాస్తవ / మ్యాక్స్',
  'agent.rewards.exposure.row.noMaxDash':                '—',
  'agent.rewards.optInModal.eyebrow':                    'కాస్ట్-షేర్ ఆప్ట్-ఇన్',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'గేమ్ లాభాలు',
  'agent.profile.rates.infoBanner.standardPctText': ' ప్రామాణిక శాతాలను ఉపయోగిస్తాయి. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'ప్రమోషనల్ ఖర్చులు',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ఆఫర్‌లు, క్యాష్‌బ్యాక్‌లు) డైనమిక్ అప్‌లైన్ పూల్‌ను ఉపయోగిస్తాయి. రెండూ మీ స్థాయి (L{level}) ఆధారంగా ప్లాట్‌ఫారమ్ అడ్మిన్ ద్వారా గ్లోబల్‌గా నిర్వహించబడతాయి.',
  'agent.profile.rates.section.gameProfits': 'గేమ్ లాభాలు — ప్రతి మ్యాచ్‌లో మీరు సంపాదించేది',
  'agent.profile.rates.section.promotionalCosts': 'ప్రమోషనల్ ఖర్చులు — మీ అప్‌లైన్ సబ్సిడీ సహకారం',
  'agent.profile.rates.pillar.platformFee': 'ప్లాట్‌ఫారమ్ ఫీజు %',
  'agent.profile.rates.pillar.winLoss': 'గెలుపు / ఓటమి లాభం %',
  'agent.profile.rates.pillar.promoWeight': 'ప్రమోషనల్ పూల్ వెయిట్',
  'agent.profile.rates.guarantee.title': 'డైరెక్ట్ రెఫరల్ గ్యారెంటీ',
  'agent.profile.rates.guarantee.body': 'మీరు నేరుగా ఆఫర్‌ను క్లెయిమ్ చేస్తున్న ప్లేయర్‌ను రెఫర్ చేసి ఉంటే, మీరు ₹0.00 చెల్లిస్తారు. మీ నెట్‌వర్క్‌ను పెంచడంలో సహాయపడటానికి మీ అప్‌లైన్‌లు 100% ఖర్చుకు సబ్సిడీ ఇస్తాయి.',
  'agent.profile.rates.poolExplainer.title': 'పూల్ ఎలా పనిచేస్తుంది:',
  'agent.profile.rates.poolExplainer.body': 'సబ్-ఏజెంట్ ప్లేయర్ ఆఫర్‌ను క్లెయిమ్ చేసినప్పుడు, మీ వెయిట్ మిగిలిన యాక్టివ్ అప్‌లైన్ + ప్లాట్‌ఫారమ్‌తో పాటు ఒక పూల్‌లోకి జోడించబడుతుంది. మీరు (మీ వెయిట్ / మొత్తం పూల్) × ఆఫర్ ఖర్చు చెల్లిస్తారు. ఎక్కువ వెయిట్ = పెద్ద షేర్.',
  'agent.profile.rates.weightLabel': 'వెయిట్:',
  'agent.profile.rates.updatedPrefix': 'అప్‌డేట్ చేయబడింది',
  'agent.profile.rates.notSetYet': 'ఇంకా సెట్ చేయబడలేదు',
  'agent.rewards.optInModal.guarantee.title': 'డైరెక్ట్ రెఫరల్ గ్యారెంటీ',
  'agent.rewards.optInModal.guarantee.body.withDownline': '<strong>మీరు నేరుగా రెఫర్ చేసిన</strong> ఆటగాడు ఈ ఆఫర్‌ను క్లెయిమ్ చేస్తే, మీరు <strong>₹0.00</strong> చెల్లిస్తారు. మీ నెట్‌వర్క్ పెంచడంలో సహాయపడటానికి మీ అప్‌లైన్‌లు 100% ఖర్చుకు సబ్సిడీ ఇస్తాయి.',
  'agent.rewards.optInModal.guarantee.body.noDownline': '<strong>మీ ఆటగాళ్ళు</strong> ఈ ఆఫర్‌ను క్లెయిమ్ చేసినప్పుడు, మీరు <strong>₹0.00</strong> చెల్లిస్తారు. మీ నెట్‌వర్క్ పెంచడంలో సహాయపడటానికి మీ అప్‌లైన్‌లు 100% ఖర్చుకు సబ్సిడీ ఇస్తాయి.',
  'agent.rewards.optInModal.uplineReality.title': 'అప్‌లైన్ రియాలిటీ చెక్',
  'agent.rewards.optInModal.uplineReality.body': 'మీకు <strong>{count}</strong> డైరెక్ట్ సబ్-ఏజెంట్‌లు{plural} ఉన్నారు. ఒక సబ్-ఏజెంట్ ఆటగాడు ఈ ఆఫర్‌ను క్లెయిమ్ చేస్తే, మిగిలిన యాక్టివ్ అప్‌లైన్ మరియు ప్లాట్‌ఫారమ్‌తో పోలిస్తే మీ <strong>పూల్ వెయిట్ ({weight})</strong> ఆధారంగా అనుపాత షేర్ చెల్లిస్తారు.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'డైరెక్ట్',
  'agent.rates.col.indirect': 'ఇన్‌డైరెక్ట్',
  'agent.rates.indirect.dynamic': 'డైనమిక్',
  'agent.rates.indirect.notApplicable': 'వర్తించదు',
  'agent.rates.indirect.rangeTo': 'నుండి',
  'agent.rewards.optInModal.close':                      'మూసివేయి',
  'agent.rewards.optInModal.cancel':                     'రద్దు',
  'agent.rewards.optInModal.loading':                    'ఆఫర్ వివరాలు లోడ్ అవుతున్నాయి…',
  'agent.rewards.optInModal.error.loadFailed':           'ఆఫర్ వివరాలను లోడ్ చేయలేకపోయాము',
  'agent.rewards.optInModal.error.optInFailed':          'ఆప్ట్-ఇన్ విఫలమైంది',
  'agent.rewards.optInModal.error.optOutFailed':         'ఆప్ట్-అవుట్ విఫలమైంది',
  'agent.rewards.optInModal.error.saveFailed':           'సబ్-ఏజెంట్ రేట్‌లను సేవ్ చేయలేకపోయాము',
  'agent.rewards.optInModal.toast.optedIn':              'ఆప్ట్ ఇన్ అయ్యారు. మీ డౌన్‌లైన్ ఇప్పుడు ఈ ఆఫర్‌ని చూడగలదు.',
  'agent.rewards.optInModal.toast.optedOut':             'ఆప్ట్ అవుట్ అయ్యారు. మీ డౌన్‌లైన్ నుండి ఆఫర్ తీసివేయబడింది.',
  'agent.rewards.optInModal.toast.saved.one':            '{count} సబ్-ఏజెంట్ రేట్ మార్పు సేవ్ చేయబడింది.',
  'agent.rewards.optInModal.toast.saved.other':          '{count} సబ్-ఏజెంట్ రేట్ మార్పులు సేవ్ చేయబడ్డాయి.',
  'agent.rewards.optInModal.confirmOptOut':              'ఈ ఆఫర్ నుండి ఆప్ట్ అవుట్ అవ్వాలా? మీ డౌన్‌లైన్ ఇకపై దానిని చూడదు.',
  'agent.rewards.optInModal.auditNote':                  'ఆఫర్ #{id} కోసం ఆప్ట్-ఇన్ మోడల్ ద్వారా సెట్ చేయబడింది',
  'agent.rewards.optInModal.stake.shareLabel':           'మీ షేర్',
  'agent.rewards.optInModal.stake.locked':               'ఆప్ట్-ఇన్ వద్ద లాక్',
  'agent.rewards.optInModal.stake.current':              'ప్రస్తుత రేట్',
  'agent.rewards.optInModal.stake.noRate':               'అడ్మిన్ ఇంకా మీకు రేట్‌ని మంజూరు చేయలేదు',
  'agent.rewards.optInModal.stake.spentSoFar.label':     'ఇప్పటివరకు ఖర్చు చేసింది',
  'agent.rewards.optInModal.stake.spentSoFar.helper':    'ఈ ఆఫర్‌పై డౌన్‌లైన్ క్లెయిమ్‌ల కోసం ఇప్పటికే మీ వాలెట్ నుండి చెల్లించిన మొత్తం రూపాయలు.',
  'agent.rewards.optInModal.stake.maxPossible.label':    'మ్యాక్స్ సాధ్యం',
  'agent.rewards.optInModal.stake.maxPossible.helper':   'ప్రతి మిగిలి ఉన్న బడ్జెట్ రూపాయి క్లెయిమ్ చేయబడితే మీరు చెల్లించగల వర్స్ట్-కేస్ రూపాయలు.',
  'agent.rewards.optInModal.stake.maxPossible.noCap':    '— బడ్జెట్ క్యాప్ లేదు —',
  'agent.rewards.optInModal.stake.worstCase':            'వర్స్ట్ కేస్: ప్రతి మిగిలి ఉన్న ₹{amount} ప్లాట్‌ఫారమ్ బడ్జెట్‌ని మీ డౌన్‌లైన్ క్లెయిమ్ చేస్తే, మీరు దానిలో {pct}% చెల్లిస్తారు.',
  'agent.rewards.optInModal.toggle.title.optedIn':       'మీరు ఆప్ట్ ఇన్ అయ్యారు',
  'agent.rewards.optInModal.toggle.title.optedOut':      'మీ డౌన్‌లైన్‌తో పంచుకోవడానికి ఆప్ట్ ఇన్ చేయండి',
  'agent.rewards.optInModal.toggle.body.optedIn':        'మీ డౌన్‌లైన్ ప్లేయర్లు ఈ ఆఫర్‌ని చూస్తారు. మీ లాక్ చేసిన షేర్‌తో వారి ప్రతి క్లెయిమ్‌ని కో-ఫండ్ చేస్తారు.',
  'agent.rewards.optInModal.toggle.body.optedOut':       'మీరు ఆప్ట్ ఇన్ అయ్యే వరకు, మీ ప్లేయర్లలో ఎవరూ ఈ ఆఫర్‌ని చూడరు. మీరు ఆప్ట్ ఇన్ అయిన తర్వాత, ఈ ఆఫర్ కోసం మీ షేర్ రేట్ లాక్ అవుతుంది.',
  'agent.rewards.optInModal.toggle.optOutLink':          'ఈ ఆఫర్ నుండి ఆప్ట్ అవుట్',
  'agent.rewards.optInModal.subAgents.title':            'సబ్-ఏజెంట్ రేట్లు',
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ప్రతి ఒక్కరూ తీసుకోగల మ్యాక్స్: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value':  '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved':          '{count} సేవ్ చేయనివి',
  'agent.rewards.optInModal.subAgents.empty':            'డైరెక్ట్ సబ్-ఏజెంట్లు లేరు.',
  'agent.rewards.optInModal.subAgents.cascadeNote':      'సేవ్ క్యాస్కేడ్ అవుతుంది — మీరు ఇక్కడ ఒక చైల్డ్‌ని పెంచితే, దాని క్రింద ఇప్పటికే లోతైన సబ్-ఏజెంట్లు ఉంటే, చైన్ స్థిరంగా ఉంచడానికి ఆ రేట్లు స్వయంచాలకంగా క్లాంప్ చేయబడవచ్చు.',
  'agent.rewards.optInModal.subAgents.noChildren':       'మీకు డైరెక్ట్ సబ్-ఏజెంట్లు లేరు. ఆఫర్ మీ సొంత ప్లేయర్లకు కనిపిస్తుంది; అడ్మిన్ పూర్తి మిగతా చెల్లిస్తారు.',
  'agent.rewards.optInModal.error.rateRequired':         'రేట్‌ని నమోదు చేయండి (క్లియర్ చేయడానికి 0)',
  'agent.rewards.optInModal.error.numbersOnly':          'సంఖ్యలు మాత్రమే',
  'agent.rewards.optInModal.error.negative':             'ప్రతికూలంగా ఉండకూడదు',
  'agent.rewards.optInModal.error.max100':               'మ్యాక్స్ 100',
  'agent.rewards.optInModal.error.aboveCeiling':         'మ్యాక్స్ {ceiling}% (మీ షేర్)',
  'agent.rewards.optInModal.cta.saving':                 'సేవ్ అవుతోంది…',
  'agent.rewards.optInModal.cta.done':                   'పూర్తయింది',
  'agent.rewards.optInModal.cta.save.one':               '{count} మార్పు సేవ్ చేయండి',
  'agent.rewards.optInModal.cta.save.other':             '{count} మార్పులు సేవ్ చేయండి',
  'agent.adminRechargePlayer.title':     'ప్లేయర్‌ని రీఛార్జ్ చేయండి',
  'agent.adminRechargePlayer.subtitle':  'మీ పూల్ నుండి ప్లేయర్ వాలెట్‌కి',
  'agent.adminRechargePlayer.backLink':  'ప్లేయర్‌కి తిరిగి',
  'agent.createPlayer.title':                     'కొత్త ప్లేయర్‌ని ఆన్‌బోర్డ్ చేయండి',
  'agent.createPlayer.subtitle.agent':            'వారు మీచే స్వయంచాలకంగా స్వంతం చేయబడతారు.',
  'agent.createPlayer.subtitle.admin':            'క్రింద ఒక యజమాని ఏజెంట్‌ని ఎంచుకోండి, లేదా ప్లాట్‌ఫారమ్-డైరెక్ట్ కోసం ఖాళీగా ఉంచండి.',
  'agent.createPlayer.close':                     'మూసివేయి',
  'agent.createPlayer.section.identity.title':    'ఐడెంటిటీ',
  'agent.createPlayer.section.identity.hint':     'ఖాతాని సృష్టించడానికి అవసరం',
  'agent.createPlayer.field.username.label':      'యూజర్‌నేమ్',
  'agent.createPlayer.field.username.placeholder':'kumar_42',
  'agent.createPlayer.field.email.label':         'ఇమెయిల్',
  'agent.createPlayer.field.email.placeholder':   'kumar@example.com',
  'agent.createPlayer.field.phone.label':         'మొబైల్',
  'agent.createPlayer.field.phone.placeholder':   '9876543210',
  'agent.createPlayer.field.phone.hint':          '10-అంకెల ఇండియా మొబైల్, +91 స్వయంచాలకంగా జోడించబడుతుంది',
  'agent.createPlayer.field.password.label':      'ఆరంభ పాస్‌వర్డ్',
  'agent.createPlayer.field.password.placeholder':'StrongP4ss',
  'agent.createPlayer.field.password.hint':       '8+ అక్షరాలు, అక్షరం + అంకె',
  'agent.createPlayer.section.owner.title':       'యజమాని ఏజెంట్',
  'agent.createPlayer.section.owner.hint':        'ఐచ్ఛికం — ప్లాట్‌ఫారమ్-డైరెక్ట్ ప్లేయర్ కోసం ఖాళీగా ఉంచండి',
  'agent.createPlayer.field.owner.label':         'ఏజెంట్',
  'agent.createPlayer.field.owner.none':          '— ఏదీ లేదు (ప్లాట్‌ఫారమ్-డైరెక్ట్) —',
  'agent.createPlayer.section.profile.title':     'ప్రొఫైల్',
  'agent.createPlayer.section.profile.hint':      'ఐచ్ఛికం — ఇప్పుడు మీకు తెలిసినది నింపండి; ప్లేయర్ మొదటి లాగిన్‌లో పూర్తి చేయగలరు',
  'agent.createPlayer.field.firstName.label':     'మొదటి పేరు',
  'agent.createPlayer.field.lastName.label':      'చివరి పేరు',
  'agent.createPlayer.field.dob.label':           'పుట్టిన తేదీ',
  'agent.createPlayer.field.gender.label':        'లింగం',
  'agent.createPlayer.gender.unspecified':        '— పేర్కొనబడలేదు —',
  'agent.createPlayer.gender.male':               'పురుషుడు',
  'agent.createPlayer.gender.female':             'స్త్రీ',
  'agent.createPlayer.gender.other':              'ఇతర',
  'agent.createPlayer.gender.preferNot':          'చెప్పడం ఇష్టం లేదు',
  'agent.createPlayer.field.language.label':      'ప్రాధాన్య భాష',
  'agent.createPlayer.section.location.title':    'స్థానం',
  'agent.createPlayer.section.location.hint':     'ఐచ్ఛికం — ప్లేయర్ మొదటి లాగిన్‌లో పూర్తి చేయగలరు',
  'agent.createPlayer.disclaimer':                'ప్లేయర్ status=active మరియు ఇమెయిల్ స్వయంచాలకంగా ధృవీకరించబడి సృష్టించబడతారు — లింక్ ఏదీ పంపబడదు. మీరు సంప్రదింపు సమాచారాన్ని నమ్ముతున్నారని నిర్ధారించుకోండి.',
  'agent.createPlayer.cta.cancel':                'రద్దు',
  'agent.createPlayer.cta.creating':              'సృష్టిస్తోంది…',
  'agent.createPlayer.cta.submit':                'ప్లేయర్‌ని సృష్టించు',
  'agent.createPlayer.error.missingAgentId':      'ఏజెంట్ ఐడి తప్పిపోయింది',
  'agent.createPlayer.error.createFailed':        'ప్లేయర్‌ని సృష్టించలేకపోయాము',
  'agent.adminHome.range.today':        'ఈ రోజు',
  'agent.adminHome.range.yesterday':    'నిన్న',
  'agent.adminHome.range.week':         'ఈ వారం',
  'agent.adminHome.range.lastWeek':     'గత వారం',
  'agent.adminHome.range.month':        'ఈ నెల',
  'agent.adminHome.range.lastMonth':    'గత నెల',
  'agent.adminHome.range.all':          'మొత్తం',
  'agent.adminHome.greeting.subtitle':  'తిరిగి స్వాగతం',
  'agent.adminHome.greeting.fallback':  'మిత్రమా',
  'agent.adminHome.errorBanner.fallback': 'లోడ్ చేయడంలో విఫలమైంది',
  'agent.adminHome.admin.hero.label':                       'ప్లాట్‌ఫారమ్ ఇన్‌ఫ్లో',
  'agent.adminHome.admin.action.moneyQueue':                'మనీ క్యూ',
  'agent.adminHome.admin.action.agents':                    'ఏజెంట్లు',
  'agent.adminHome.admin.snapshot.matches':                 'మ్యాచ్‌లు',
  'agent.adminHome.admin.snapshot.newSignups':              'కొత్త సైన్‌అప్‌లు',
  'agent.adminHome.admin.snapshot.active':                  'యాక్టివ్',
  'agent.adminHome.admin.snapshot.pending':                 'పెండింగ్',
  'agent.adminHome.section.games':                          'గేమ్స్',
  'agent.adminHome.section.games.subtitle':                 'మ్యాచ్‌లు మరియు ప్లాట్‌ఫారమ్ ఫీ · {range}',
  'agent.adminHome.section.activity':                       'కార్యకలాపం',
  'agent.adminHome.section.ops':                            'ఆపరేషన్లు & అంతర్దృష్టులు',
  'agent.adminHome.section.ops.subtitle':                   'లైవ్ స్నాప్‌షాట్‌లు — పూర్తి పేజీలోకి డ్రిల్ చేయడానికి ఏ కార్డ్ అయినా క్లిక్ చేయండి',
  'agent.adminHome.section.gameSettings':                   'గేమ్ సెట్టింగ్‌లు',
  'agent.adminHome.section.gameSettings.subtitle':          'ప్రతి గేమ్‌ని కాన్ఫిగర్ చేయండి',
  'agent.adminHome.section.downline':                       'నా డౌన్‌లైన్ అంతర్దృష్టులు',
  'agent.adminHome.section.downline.subtitle':              'పూర్తి పేజీలోకి డ్రిల్ చేయడానికి ఏ కార్డ్ అయినా క్లిక్ చేయండి',
  'agent.adminHome.admin.card.players.title':               'ప్లేయర్ల వాలెట్',
  'agent.adminHome.admin.card.players.headlineLabel':       'రేంజ్‌లో రీఛార్జ్ చేసారు',
  'agent.adminHome.admin.card.players.flow.recharges':      'రీఛార్జ్‌లు',
  'agent.adminHome.admin.card.players.flow.withdrawals':    'విత్‌డ్రాల్‌లు',
  'agent.adminHome.admin.card.players.pending.recharges':   'పెండింగ్ రీఛార్జ్‌లు',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'పెండింగ్ విత్‌డ్రాల్‌లు',
  'agent.adminHome.admin.card.players.stat.total':          'మొత్తం ప్లేయర్లు',
  'agent.adminHome.admin.card.players.stat.active':         'యాక్టివ్',
  'agent.adminHome.admin.card.players.stat.suspended':      'నిలిపివేయబడింది',
  'agent.adminHome.admin.card.agents.title':                'ఏజెంట్ల వాలెట్',
  'agent.adminHome.admin.card.agents.headlineLabel':        'ఏజెంట్ పూల్స్‌లో',
  'agent.adminHome.admin.card.agents.flow.topups':          'టాప్‌అప్‌లు (అడ్మిన్ → ఏజెంట్)',
  'agent.adminHome.admin.card.agents.flow.outflow':         'అవుట్‌ఫ్లో (చెల్లించబడింది / టాప్‌అప్)',
  'agent.adminHome.admin.card.agents.pending.withdrawals':  'పెండింగ్ ఏజెంట్ విత్‌డ్రాల్‌లు',
  'agent.adminHome.admin.card.agents.stat.total':           'మొత్తం ఏజెంట్లు',
  'agent.adminHome.admin.card.agents.stat.active':          'యాక్టివ్',
  'agent.adminHome.admin.card.agents.stat.suspended':       'నిలిపివేయబడింది',
  'agent.adminHome.admin.card.bots.title':                  'బాట్ల వాలెట్',
  'agent.adminHome.admin.card.bots.headlineLabel':          'బాట్ వాలెట్లలో',
  'agent.adminHome.admin.card.bots.stat.total':             'మొత్తం బాట్లు',
  'agent.adminHome.admin.card.bots.stat.active':            'యాక్టివ్',
  'agent.adminHome.admin.card.bots.stat.matches':           'మ్యాచ్‌లు',
  'agent.adminHome.admin.card.rewards.title':               'రివార్డ్‌లు',
  'agent.adminHome.admin.card.rewards.headlineLabel':       'ఈ వారం చెల్లించబడింది',
  'agent.adminHome.admin.card.rewards.pending.claims':      'పెండింగ్ క్లెయిమ్‌లు',
  'agent.adminHome.admin.card.rewards.stat.activeOffers':   'యాక్టివ్ ఆఫర్లు',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs':       'బల్క్ జాబ్‌లు',
  'agent.adminHome.gameTile.matches':                       'మ్యాచ్‌లు',
  'agent.adminHome.game.ludo':                              'లుడో',
  'agent.adminHome.game.xo':                                'XO',
  'agent.adminHome.game.rummy':                             'రమ్మీ',
  'agent.adminHome.game.carrom':                            'కేరమ్',
  'agent.adminHome.game.chess':                             'చెస్',
  'agent.adminHome.activity.topPlayers.title':              'టాప్ ప్లేయర్లు',
  'agent.adminHome.activity.topPlayers.empty':              'ఈ రేంజ్‌లో మ్యాచ్‌లు లేవు.',
  'agent.adminHome.activity.topPlayers.metric':             '{count} మ్యాచ్‌లు',
  'agent.adminHome.activity.topAgents.title':               'టాప్ ఏజెంట్లు',
  'agent.adminHome.activity.topAgents.subtitle':            'స్వంత ప్లేయర్ల ప్రకారం',
  'agent.adminHome.activity.topAgents.empty':               'ఇంకా యాక్టివ్ ఏజెంట్లు లేరు.',
  'agent.adminHome.activity.topAgents.metric':              '{count} ప్లేయర్లు',
  'agent.adminHome.activity.recent.title':                  'ఇటీవలి లావాదేవీలు',
  'agent.adminHome.activity.recent.subtitle':               'చివరి 5',
  'agent.adminHome.activity.recent.empty':                  'ఇంకా లావాదేవీలు లేవు.',
  'agent.adminHome.ops.topEarners.title':                   'టాప్ సంపాదకులు (7d)',
  'agent.adminHome.ops.topEarners.subtitle':                'ట్రూ నెట్ ప్రకారం',
  'agent.adminHome.ops.topEarners.empty':                   'గత 7 రోజుల్లో ఆదాయాలు లేవు.',
  'agent.adminHome.ops.liveActivity.title':                 'లైవ్ కార్యకలాపం',
  'agent.adminHome.ops.liveActivity.subtitle':              'ప్లాట్‌ఫారమ్-వ్యాప్తంగా చివరి 5 ఈవెంట్‌లు',
  'agent.adminHome.ops.liveActivity.empty':                 'నిశ్శబ్దం — గత 24 గంటల్లో ఈవెంట్‌లు లేవు.',
  'agent.adminHome.ops.botPerf.title':                      'బాట్ ప్రదర్శన (7d)',
  'agent.adminHome.ops.botPerf.subtitle':                   'వర్గం-వారీ P&L',
  'agent.adminHome.ops.rateChanges.title':                  'ఇటీవలి రేట్ మార్పులు',
  'agent.adminHome.ops.rateChanges.subtitle':               'తాజా పార్ట్‌నర్‌షిప్ ఎడిట్‌లు',
  'agent.adminHome.ops.rateChanges.empty':                  'ఇంకా రేట్-మార్పు ఈవెంట్‌లు లేవు.',
  'agent.adminHome.ops.opsHealth.title':                    'ఆపరేషన్ల ఆరోగ్యం',
  'agent.adminHome.ops.opsHealth.subtitle':                 'డెడ్-లెటర్ క్యూ · పెండింగ్ సెటిల్‌మెంట్‌లు',
  'agent.adminHome.feed.match.earnedVerb':                  'సంపాదించారు',
  'agent.adminHome.feed.match.onGameSuffix':                '{game}పై',
  'agent.adminHome.feed.recharge.label':                    'రీఛార్జ్',
  'agent.adminHome.feed.recharge.amountInfix':              '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix':                'ద్వారా',
  'agent.adminHome.feed.rateChange.label':                  'రేట్ మార్పు',
  'agent.adminHome.feed.cascadeBadge':                      'క్యాస్కేడ్',
  'agent.adminHome.feed.recharge.state.approved':           'ఆమోదించబడింది',
  'agent.adminHome.feed.recharge.state.rejected':           'తిరస్కరించబడింది',
  'agent.adminHome.feed.recharge.state.pending':            'పెండింగ్',
  'agent.adminHome.feed.recharge.state.paid':               'చెల్లించబడింది',
  'agent.adminHome.feed.recharge.state.expired':            'గడువు ముగిసింది',
  'agent.adminHome.leaderboard.matches.one':                '{count} మ్యాచ్',
  'agent.adminHome.leaderboard.matches.other':              '{count} మ్యాచ్‌లు',
  'agent.adminHome.leaderboard.levelPrefix':                'L{level}',
  'agent.adminHome.botMatrix.profitLabel':                  'ప్లాట్‌ఫారమ్ లాభం (7d)',
  'agent.adminHome.botMatrix.cat.easy':                     'easy',
  'agent.adminHome.botMatrix.cat.medium':                   'medium',
  'agent.adminHome.botMatrix.cat.hard':                     'hard',
  'agent.adminHome.botMatrix.matchesShort':                 '{matches}m',
  'agent.adminHome.botMatrix.activeSummary':                '{total}లో {active} బాట్‌లు యాక్టివ్',
  'agent.adminHome.botMatrix.loading':                      'లోడ్ అవుతోంది…',
  'agent.adminHome.opsHealth.dlqClean.title':               'DLQ క్లీన్',
  'agent.adminHome.opsHealth.dlqClean.body':                'విఫలమైన సెటిల్‌మెంట్ జాబ్‌లు లేవు.',
  'agent.adminHome.opsHealth.dlqDirty.title.one':           '{count} పరిష్కరించని DLQ జాబ్',
  'agent.adminHome.opsHealth.dlqDirty.title.other':         '{count} పరిష్కరించని DLQ జాబ్‌లు',
  'agent.adminHome.opsHealth.dlqDirty.body':                'మళ్లీ ప్రయత్నించడానికి / పరిష్కరించడానికి క్లిక్ చేయండి.',
  'agent.adminHome.opsHealth.replay.eyebrow':               'రీప్లే',
  'agent.adminHome.opsHealth.replay.body':                  'సెటిల్‌మెంట్ మళ్లీ నడపండి',
  'agent.adminHome.opsHealth.reassign.eyebrow':             'మళ్లీ అసైన్',
  'agent.adminHome.opsHealth.reassign.body':                'బల్క్ తరలించండి',
  'agent.adminHome.agentDash.hero.label':                   'నా పూల్ బ్యాలెన్స్',
  'agent.adminHome.agentDash.hero.netPrefix':               'నెట్',
  'agent.adminHome.agentDash.action.myWallet':              'నా వాలెట్',
  'agent.adminHome.agentDash.action.myPlayers':             'నా ప్లేయర్లు',
  'agent.adminHome.agentDash.snapshot.myMatches':           'నా మ్యాచ్‌లు',
  'agent.adminHome.agentDash.snapshot.active':              'యాక్టివ్',
  'agent.adminHome.agentDash.snapshot.netRange':            'నెట్ (రేంజ్)',
  'agent.adminHome.agentDash.snapshot.pending':             'పెండింగ్',
  'agent.adminHome.agentDash.card.players.title':           'నా ప్లేయర్ల వాలెట్',
  'agent.adminHome.agentDash.card.players.headlineLabel':   'రేంజ్‌లో రీఛార్జ్ చేసారు',
  'agent.adminHome.agentDash.card.players.stat.total':      'నా ప్లేయర్లు',
  'agent.adminHome.agentDash.card.wallet.title':            'నా వాలెట్',
  'agent.adminHome.agentDash.card.wallet.headlineLabel':    'పూల్ బ్యాలెన్స్',
  'agent.adminHome.agentDash.card.wallet.flow.earned':      'సంపాదించారు (కమీషన్‌లు)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut':     'చెల్లించబడింది',
  'agent.adminHome.agentDash.card.wallet.pending':          'నా పెండింగ్ విత్‌డ్రాల్',
  'agent.adminHome.agentDash.card.wallet.stat.topups':      'టాప్‌అప్‌లు అందుకున్నాయి',
  'agent.adminHome.agentDash.card.wallet.stat.netRange':    'నెట్ (రేంజ్)',
  'agent.adminHome.agentDash.card.activity.title':          'కార్యకలాపం',
  'agent.adminHome.agentDash.card.activity.headlineLabel':  'రేంజ్‌లో నా మ్యాచ్‌లు',
  'agent.adminHome.agentDash.card.activity.stat.topSpender':'టాప్ స్పెండర్',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend':'వారి ఖర్చు',
  'agent.adminHome.agentDash.card.activity.stat.pending':   'పెండింగ్ ఐటెమ్‌లు',
  'agent.adminHome.agentDash.activity.topSpenders.title':   'టాప్ స్పెండర్లు',
  'agent.adminHome.agentDash.activity.topSpenders.empty':   'ఈ రేంజ్‌లో ఇంకా ఖర్చు లేదు.',
  'agent.adminHome.agentDash.activity.recent.title':        'ఇటీవలి కార్యకలాపం',
  'agent.adminHome.agentDash.activity.recent.subtitle':     'మీ ప్లేయర్ల నుండి చివరి 5',
  'agent.adminHome.agentDash.activity.recent.empty':        'ఇంకా లావాదేవీలు లేవు.',
  'agent.adminHome.agentDash.insights.topEarners.title':    'టాప్ సంపాదకులు (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'మీ సబ్-ఏజెంట్లు ర్యాంక్ చేయబడ్డారు',
  'agent.adminHome.agentDash.insights.downline.title':      'డౌన్‌లైన్ కార్యకలాపం',
  'agent.adminHome.agentDash.insights.downline.subtitle':   'మీ డౌన్‌లైన్ నుండి చివరి 5 ఈవెంట్‌లు',
  'agent.adminHome.agentDash.insights.deposit.title':       'డిపాజిట్ లెడ్జర్',
  'agent.adminHome.agentDash.insights.deposit.subtitle':    'ఇటీవలి డిపాజిట్ క్రెడిట్‌లు / రీఫండ్‌లు',
  'agent.adminHome.agentDash.insights.deposit.empty':       'ఇంకా డిపాజిట్ కార్యకలాపం లేదు.',
  'agent.subAgentDetail.title':                       'సబ్-ఏజెంట్',
  'agent.subAgentDetail.subtitle':                    'డైరెక్ట్ సబ్-ఏజెంట్',
  'agent.subAgentDetail.error.loadFailed':            'సబ్-ఏజెంట్‌ని లోడ్ చేయడంలో విఫలం',
  'agent.subAgentDetail.backToList':                  'సబ్-ఏజెంట్ల జాబితాకి తిరిగి',
  'agent.subAgentDetail.loading':                     'లోడ్ అవుతోంది…',
  'agent.subAgentDetail.tab.identity':                'ఐడెంటిటీ',
  'agent.subAgentDetail.tab.players':                 'ప్లేయర్లు',
  'agent.subAgentDetail.tab.wallet':                  'వాలెట్',
  'agent.subAgentDetail.tab.rates':                   'రేట్లు',
  'agent.subAgentDetail.tab.ops':                     'ఆపరేషన్లు',
  'agent.subAgentDetail.header.players.one':          '{count} ప్లేయర్',
  'agent.subAgentDetail.header.players.other':        '{count} ప్లేయర్లు',
  'agent.subAgentDetail.header.pool':                 'పూల్: {amount}',
  'agent.subAgentDetail.header.lastLogin':            'చివరి లాగిన్ {when}',
  'agent.subAgentDetail.status.active':               'యాక్టివ్',
  'agent.subAgentDetail.status.suspended':            'నిలిపివేయబడింది',
  'agent.subAgentDetail.identity.row.uaid':           'UAID',
  'agent.subAgentDetail.identity.row.username':       'యూజర్‌నేమ్',
  'agent.subAgentDetail.identity.row.email':          'ఇమెయిల్',
  'agent.subAgentDetail.identity.row.status':         'స్థితి',
  'agent.subAgentDetail.identity.row.parent':         'పేరెంట్ ఏజెంట్',
  'agent.subAgentDetail.identity.parentValue':        '@{username} (మీరు)',
  'agent.subAgentDetail.identity.parentEmpty':        '—',
  'agent.subAgentDetail.identity.row.ownedPlayers':   'స్వంత ప్లేయర్లు',
  'agent.subAgentDetail.identity.row.wallet':         'వాలెట్ బ్యాలెన్స్',
  'agent.subAgentDetail.identity.row.created':        'సృష్టించబడింది',
  'agent.subAgentDetail.identity.row.lastLogin':      'చివరి లాగిన్',
  'agent.subAgentDetail.identity.row.identityLocked': 'ఐడెంటిటీ లాక్',
  'agent.subAgentDetail.identity.identityLocked.yes': 'అవును (అడ్మిన్-మాత్రమే ఎడిట్‌లు)',
  'agent.subAgentDetail.identity.identityLocked.no':  'లేదు (సబ్-ఏజెంట్ స్వయంగా ఎడిట్ చేయగలరు)',
  'agent.subAgentDetail.identity.row.receiving':      'ప్లేయర్లను స్వీకరిస్తోంది',
  'agent.subAgentDetail.optOut.accepting':            'స్వీకరిస్తోంది',
  'agent.subAgentDetail.optOut.optedOut':             'ఆప్ట్ అవుట్',
  'agent.subAgentDetail.optOut.toast.accepting':      'సబ్-ఏజెంట్ ఇప్పుడు ప్లేయర్లను స్వీకరిస్తున్నారు',
  'agent.subAgentDetail.optOut.toast.optedOut':       'సబ్-ఏజెంట్ లొకేటర్ నుండి ఆప్ట్ అవుట్ అయ్యారు',
  'agent.subAgentDetail.optOut.error.updateFailed':   'అప్‌డేట్ చేయలేకపోయాము',
  'agent.subAgentDetail.location.title':              'స్థానం',
  'agent.subAgentDetail.location.lastUpdated':        '· చివరిసారి అప్‌డేట్ {date}',
  'agent.subAgentDetail.location.cta.save':           'స్థానం సేవ్',
  'agent.subAgentDetail.location.nothingToSave':      'సేవ్ చేయడానికి ఏమీ లేదు',
  'agent.subAgentDetail.location.toast.saved.one':    'స్థానం సేవ్ చేయబడింది ({count} ఫీల్డ్)',
  'agent.subAgentDetail.location.toast.saved.other':  'స్థానం సేవ్ చేయబడింది ({count} ఫీల్డ్‌లు)',
  'agent.subAgentDetail.location.hint':               'సేవ్‌ని ఎనేబుల్ చేయడానికి వేరే రాష్ట్రం / జిల్లా / ప్రాంతాన్ని ఎంచుకోండి. (సబ్-ఏజెంట్ స్వయంగా వారి మొదటి లాగిన్‌లో ఫోర్స్‌డ్ పాప్-అప్ ద్వారా ఖచ్చితమైన GPSని ఓవర్‌లే చేస్తారు.)',
  'agent.subAgentDetail.players.empty':               'ఈ సబ్-ఏజెంట్ ఇంకా ఏ ప్లేయర్లను కలిగి లేరు.',
  'agent.subAgentDetail.players.count.one':           '{count} ప్లేయర్',
  'agent.subAgentDetail.players.count.other':         '{count} ప్లేయర్లు',
  'agent.subAgentDetail.wallet.balanceLabel':         'ప్రస్తుత సబ్-ఏజెంట్ బ్యాలెన్స్',
  'agent.subAgentDetail.wallet.ledger.title':         '@{username} పూర్తి వాలెట్ లెడ్జర్',
  'agent.subAgentDetail.wallet.ledger.subtitle':      'ప్రతి కదలిక · సబ్-ఏజెంట్ తమ వాలెట్‌లో ఇదే చూస్తారు',
  'agent.subAgentDetail.wallet.ledger.empty':         'ఇంకా వాలెట్ కార్యకలాపం లేదు.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge':  'మీరు ↔ వారు',
  'agent.subAgentDetail.wallet.ledger.balanceAfter':  'bal ₹{amount}',
  'agent.subAgentDetail.wallet.kind.topupFromAdmin':      'అడ్మిన్ నుండి టాప్‌అప్',
  'agent.subAgentDetail.wallet.kind.topupToPlayer':       'ప్లేయర్‌కి చెల్లింపు',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer':     'ప్లేయర్ రీఛార్జ్',
  'agent.subAgentDetail.wallet.kind.revenueShare':        'రెవెన్యూ షేర్',
  'agent.subAgentDetail.wallet.kind.rechargeCommission':  'రీఛార్జ్ కమీషన్',
  'agent.subAgentDetail.wallet.kind.coFundOffer':         'కో-ఫండ్ ఆఫర్',
  'agent.subAgentDetail.wallet.kind.adminAdjust':         'అడ్మిన్ సర్దుబాటు',
  'agent.subAgentDetail.wallet.kind.withdrawalHold':      'విత్‌డ్రాల్ హోల్డ్',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund':    'విత్‌డ్రాల్ రీఫండ్',
  'agent.subAgentDetail.wallet.kind.platformFeeShare':    'ప్లాట్‌ఫారమ్ ఫీ షేర్',
  'agent.subAgentDetail.wallet.kind.winLossShare':        'విన్/లాస్ షేర్',
  'agent.subAgentDetail.wallet.pullback.title':           'కాయిన్‌లను తిరిగి తీసుకువస్తున్నారా?',
  'agent.subAgentDetail.wallet.pullback.body.before':     ' ',
  'agent.subAgentDetail.wallet.pullback.body.middle':     'ని వారి సొంత పూల్ నుండి విత్‌డ్రాల్ అభ్యర్థన సమర్పించమని అడగండి. మీరు దానిని (పూర్తి లేదా పాక్షిక) చెల్లించినట్లు మార్క్ చేయగలరు ',
  'agent.subAgentDetail.wallet.pullback.financeLink':     'Finance › Sub-agents',
  'agent.subAgentDetail.wallet.pullback.body.after':      ' నుండి.',
  'agent.subAgentDetail.transfer.topup.heading':            '@{username}కి ట్రాన్స్‌ఫర్',
  'agent.subAgentDetail.transfer.withdraw.heading':         '@{username} నుండి విత్‌డ్రా',
  'agent.subAgentDetail.transfer.topup.body':              'అటామిక్ ట్రాన్స్‌ఫర్: మీ వాలెట్ క్రింది, వారి వాలెట్ పైకి. రెండు పూల్‌లు నెగటివ్‌కి వెళ్లవచ్చు (సైన్డ్-పూల్ డిజైన్).',
  'agent.subAgentDetail.transfer.withdraw.body':           'అటామిక్ రివర్స్ ట్రాన్స్‌ఫర్: వారి వాలెట్ క్రింది, మీ వాలెట్ పైకి. సబ్-ఏజెంట్ నుండి చెల్లింపు అభ్యర్థనను సెటిల్ చేస్తున్నప్పుడు దీనిని ఉపయోగించండి.',
  'agent.subAgentDetail.transfer.amountLabel':             'మొత్తం (₹)',
  'agent.subAgentDetail.transfer.noteLabel':               'గమనిక (ఐచ్ఛికం)',
  'agent.subAgentDetail.transfer.note.placeholder.topup':  'ఉదా. వీక్లీ టాప్-అప్',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'ఉదా. చెల్లింపు సెటిల్‌మెంట్',
  'agent.subAgentDetail.transfer.error.amountPositive':    'మొత్తం పాజిటివ్ ఇంటీజర్ ఉండాలి.',
  'agent.subAgentDetail.transfer.confirm.topup':           'మీ పూల్ నుండి @{username}కి {amount} ట్రాన్స్‌ఫర్ చేయాలా? మీ బ్యాలెన్స్ సరిగ్గా ఈ మొత్తం తగ్గుతుంది.',
  'agent.subAgentDetail.transfer.confirm.withdraw':        '@{username} పూల్ నుండి {amount}ని తిరిగి మీ వద్దకు లాగాలా? వారి బ్యాలెన్స్ సరిగ్గా ఈ మొత్తం తగ్గుతుంది.',
  'agent.subAgentDetail.transfer.toast.topup':             '@{username}కి {amount} పంపబడింది. వారి బ్యాలెన్స్: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw':          '@{username} నుండి {amount} తిరిగి లాగబడింది. వారి బ్యాలెన్స్: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working':             'పనిచేస్తోంది…',
  'agent.subAgentDetail.transfer.cta.suspended':           'సబ్-ఏజెంట్ నిలిపివేయబడింది',
  'agent.subAgentDetail.transfer.cta.topup':               '{amount} ట్రాన్స్‌ఫర్',
  'agent.subAgentDetail.transfer.cta.withdraw':            '{amount} విత్‌డ్రా',
  'agent.subAgentDetail.rates.loading':                    'రేట్లు లోడ్ అవుతున్నాయి…',
  'agent.subAgentDetail.rates.intro.part1':                'ప్రతి కార్డ్ ఒక కమీషన్ పిల్లర్; లోపల ప్రతి రో ఒక (పిల్లర్ × గేమ్) షేర్. ప్రతి ఇన్‌పుట్ పక్కన చూపిన ',
  'agent.subAgentDetail.rates.intro.ceiling':              'సీలింగ్',
  'agent.subAgentDetail.rates.intro.part2':                ' మీ సొంత రేట్ — గోల్డెన్ లిమిట్ ద్వారా, మీరు చైల్డ్‌కి దాని కంటే ఎక్కువ మంజూరు చేయలేరు. మీరు మార్చిన ఏ విలువ అయినా ఎడిటబుల్ అవుతుంది; కమిట్ చేయడానికి ',
  'agent.subAgentDetail.rates.intro.applyWord':            'అప్లై',
  'agent.subAgentDetail.rates.intro.part3':                ' క్లిక్ చేయండి. క్యాస్కేడ్ క్లాంప్ సర్వర్-సైడ్‌లో అమలు చేయబడుతుంది.',
  'agent.subAgentDetail.rates.toast.applied':              'రేట్లు అప్లై చేయబడ్డాయి.',
  'agent.subAgentDetail.rates.confirm.one':                '{count} రేట్ మార్పుని అప్లై చేయాలా? గోల్డెన్-లిమిట్ + క్యాస్కేడ్ క్లాంప్ సర్వర్-సైడ్‌లో అమలు చేయబడతాయి.',
  'agent.subAgentDetail.rates.confirm.other':              '{count} రేట్ మార్పులని అప్లై చేయాలా? గోల్డెన్-లిమిట్ + క్యాస్కేడ్ క్లాంప్ సర్వర్-సైడ్‌లో అమలు చేయబడతాయి.',
  'agent.subAgentDetail.rates.error.blocked':              'బ్లాక్ చేయబడింది: {detail}',
  'agent.subAgentDetail.rates.pillar.platformFee':         'ప్లాట్‌ఫారమ్ ఫీ %',
  'agent.subAgentDetail.rates.pillar.winLoss':             'విన్/లాస్ లాభం %',
  'agent.subAgentDetail.rates.pillar.offerReward':         'ఆఫర్ రివార్డ్ %',
  'agent.subAgentDetail.rates.row.globalLabel':            '— గ్లోబల్ —',
  'agent.subAgentDetail.rates.row.ceilingSuffix':          '/ సీలింగ్ {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge':           'పేరెంట్‌ని మించింది (గోల్డెన్ లిమిట్)',
  'agent.subAgentDetail.rates.row.editedBadge':            'ఎడిట్ చేయబడింది',
  'agent.subAgentDetail.rates.cta.discard.one':            '{count} మార్పుని తొలగించు',
  'agent.subAgentDetail.rates.cta.discard.other':          '{count} మార్పులని తొలగించు',
  'agent.subAgentDetail.rates.cta.apply.one':              '{count} మార్పుని అప్లై',
  'agent.subAgentDetail.rates.cta.apply.other':            '{count} మార్పులని అప్లై',
  'agent.subAgentDetail.rates.cta.applying':               'అప్లై చేస్తోంది…',
  'agent.subAgentDetail.ops.status.title':                 'స్థితి',
  'agent.subAgentDetail.ops.status.body':                  'నిలిపివేయడం వలన మీరు యాక్టివ్‌కి తిరిగి మార్చే వరకు సబ్-ఏజెంట్ లాగిన్‌లు మరియు డౌన్‌లైన్ ఆపరేషన్‌లు బ్లాక్ అవుతాయి. కారణం ఐచ్ఛికం కానీ ఆడిట్ ట్రెయిల్‌లో పడుతుంది.',
  'agent.subAgentDetail.ops.status.reasonPlaceholder':     'కారణం (ఐచ్ఛికం)',
  'agent.subAgentDetail.ops.status.cta.suspend':           'సబ్-ఏజెంట్‌ని నిలిపివేయండి',
  'agent.subAgentDetail.ops.status.cta.unsuspend':         'సబ్-ఏజెంట్‌ని తిరిగి ఆన్',
  'agent.subAgentDetail.ops.status.cta.working':           'పనిచేస్తోంది…',
  'agent.subAgentDetail.ops.status.confirm.suspend':       '@{username}ని నిలిపివేయాలా? వారు నిలిపివేయబడినప్పుడు లాగిన్ లేదా ఆపరేట్ చేయలేరు.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend':     '@{username}ని తిరిగి ఆన్ చేయాలా? వారు పూర్తి యాక్సెస్‌ని తిరిగి పొందుతారు.',
  'agent.subAgentDetail.ops.status.toast.suspended':       '@{username} ఇప్పుడు నిలిపివేయబడ్డారు.',
  'agent.subAgentDetail.ops.status.toast.active':          '@{username} ఇప్పుడు యాక్టివ్.',
  'agent.subAgentDetail.ops.password.title':               'పాస్‌వర్డ్ రీసెట్',
  'agent.subAgentDetail.ops.password.body':                'సబ్-ఏజెంట్ కోసం కొత్త పాస్‌వర్డ్‌ని సెట్ చేస్తుంది మరియు వారి ఇప్పటికే ఉన్న రిఫ్రెష్ టోకెన్‌లను రద్దు చేస్తుంది. వారు కొత్త పాస్‌వర్డ్‌తో మళ్లీ లాగిన్ చేయవలసి ఉంటుంది. సురక్షిత ఛానెల్ ద్వారా దీనిని వారితో పంచుకోండి.',
  'agent.subAgentDetail.ops.password.placeholder':         'కొత్త పాస్‌వర్డ్ (కనిష్ఠం 8 అక్షరాలు)',
  'agent.subAgentDetail.ops.password.confirm':             '@{username} పాస్‌వర్డ్‌ని రీసెట్ చేయాలా? వారి ఇప్పటికే ఉన్న సెషన్‌లు రద్దు చేయబడతాయి మరియు వారు కొత్త పాస్‌వర్డ్‌తో లాగిన్ చేయవలసి ఉంటుంది.',
  'agent.subAgentDetail.ops.password.toast.reset':         'పాస్‌వర్డ్ రీసెట్ చేయబడింది. కొత్త పాస్‌వర్డ్‌ని @{username}తో పంచుకోండి.',
  'agent.subAgentDetail.ops.password.error.tooShort':      'పాస్‌వర్డ్ కనీసం 8 అక్షరాలు ఉండాలి.',
  'agent.subAgentDetail.ops.password.cta.reset':           'పాస్‌వర్డ్ రీసెట్',
  'agent.subAgentDetail.ops.password.cta.resetting':       'రీసెట్ అవుతోంది…',
  'agent.playerDetail.backToPlayers':                 'ప్లేయర్లకి తిరిగి',
  'agent.playerDetail.loading':                       'లోడ్ అవుతోంది…',
  'agent.playerDetail.error.loadFailed':              'యూజర్‌ని లోడ్ చేయడంలో విఫలం',
  'agent.playerDetail.header.noName':                 '(పేరు సెట్ చేయలేదు)',
  'agent.playerDetail.header.joined':                 '{date} చేరారు',
  'agent.playerDetail.header.ownedBy.prefix':         ' · స్వంతం: ',
  'agent.playerDetail.header.walletLabel':            'వాలెట్',
  'agent.playerDetail.status.pending':                'పెండింగ్',
  'agent.playerDetail.status.active':                 'యాక్టివ్',
  'agent.playerDetail.status.suspended':              'నిలిపివేయబడింది',
  'agent.playerDetail.status.banned':                 'నిషేధించబడింది',
  'agent.playerDetail.tab.identity':                  'ఐడెంటిటీ',
  'agent.playerDetail.tab.profile':                   'ప్రొఫైల్',
  'agent.playerDetail.tab.wallet':                    'వాలెట్',
  'agent.playerDetail.tab.security':                  'భద్రత',
  'agent.playerDetail.tab.ownership':                 'యాజమాన్యం',
  'agent.playerDetail.tab.operations':                'ఆపరేషన్లు',
  'agent.playerDetail.identity.title':                'ఐడెంటిటీ',
  'agent.playerDetail.identity.row.username':         'యూజర్‌నేమ్',
  'agent.playerDetail.identity.row.email':            'ఇమెయిల్',
  'agent.playerDetail.identity.row.phone':            'ఫోన్',
  'agent.playerDetail.identity.row.country':          'దేశం',
  'agent.playerDetail.identity.row.joined':           'చేరారు',
  'agent.playerDetail.identity.row.emailVerified':    'ఇమెయిల్ ధృవీకరించబడింది',
  'agent.playerDetail.identity.row.telegram':         'టెలిగ్రామ్',
  'agent.playerDetail.identity.row.failedLogins':     'విఫలమైన లాగిన్‌లు',
  'agent.playerDetail.identity.row.lockedUntil':      'వరకు లాక్',
  'agent.playerDetail.identity.value.yes':            'అవును',
  'agent.playerDetail.identity.value.no':             'లేదు',
  'agent.playerDetail.identity.value.dash':           '—',
  'agent.playerDetail.identity.transfer.title':       'ఈ ప్లేయర్‌ని ట్రాన్స్‌ఫర్ చేయండి',
  'agent.playerDetail.identity.transfer.body':        'వారి యూనిక్ ఏజెంట్ ID ద్వారా మరో ఏజెంట్‌కి హ్యాండ్ ఆఫ్ చేయండి — మీ డౌన్‌లైన్ మాత్రమే కాకుండా మొత్తం నెట్‌వర్క్ అంతటా పనిచేస్తుంది.',
  'agent.playerDetail.identity.transfer.cta':         'UAID ద్వారా ట్రాన్స్‌ఫర్',
  'agent.playerDetail.avatar.changeTitle':            'ఫోటో మార్చండి',
  'agent.playerDetail.avatar.cropTitle':              '@{username} కోసం ఫోటోను సర్దుబాటు చేయండి',
  'agent.playerDetail.avatar.toast.updated':          'ఫోటో అప్‌డేట్ చేయబడింది',
  'agent.playerDetail.avatar.error.uploadFailed':     'అప్‌లోడ్ విఫలమైంది',
  'agent.playerDetail.transfer.toast.admin':          'UAID ద్వారా ప్లేయర్ ట్రాన్స్‌ఫర్ చేయబడింది',
  'agent.playerDetail.transfer.toast.agent':          '@{username} విజయవంతంగా ట్రాన్స్‌ఫర్ చేయబడింది',
  'agent.playerDetail.ops.accountStatus.title':       'ఖాతా స్థితి',
  'agent.playerDetail.ops.accountStatus.body':        'యాక్టివ్ అంటే ప్లేయర్ లాగిన్ చేసి ఆడగలరు. మీరు తిరిగి మార్చే వరకు సస్పెండ్ లాగిన్‌లను బ్లాక్ చేస్తుంది. బాన్ ఒక బలమైన లాక్ — విధాన ఉల్లంఘనల కోసం ఉంచండి.',
  'agent.playerDetail.ops.statusBtn.activate':        'యాక్టివేట్',
  'agent.playerDetail.ops.statusBtn.suspend':         'నిలిపివేయి',
  'agent.playerDetail.ops.statusBtn.ban':             'నిషేధించు',
  'agent.playerDetail.ops.statusBtn.current':         'ప్రస్తుత',
  'agent.playerDetail.ops.statusBtn.toast':           'యూజర్ ఇప్పుడు {status}',
  'agent.playerDetail.ops.danger.title':              'డేంజర్ జోన్',
  'agent.playerDetail.ops.danger.body':               'హార్డ్-డిలీట్ యూజర్ రికార్డ్‌ని తీసివేస్తుంది. ఆడిట్ కోసం వాలెట్ లెడ్జర్ భద్రపరచబడుతుంది, కానీ ప్లేయర్ అన్ని యాక్సెస్‌ని కోల్పోతారు. ఈ చర్యని రద్దు చేయలేరు.',
  'agent.playerDetail.ops.delete.eyebrow':            'డేంజర్ జోన్',
  'agent.playerDetail.ops.delete.confirm.before':     'ఈ ఖాతాని శాశ్వతంగా తొలగించండి. దీనిని రద్దు చేయలేరు. ధృవీకరించడానికి ',
  'agent.playerDetail.ops.delete.confirm.after':      ' టైప్ చేయండి.',
  'agent.playerDetail.ops.delete.cta':                'తొలగించు',
  'agent.playerDetail.ops.delete.toast':              'యూజర్ తొలగించబడ్డారు',
  'agent.playerDetail.walletAdjust.title':            'వాలెట్ సర్దుబాటు',
  'agent.playerDetail.walletAdjust.mode.credit':      '+ క్రెడిట్',
  'agent.playerDetail.walletAdjust.mode.debit':       '− డెబిట్',
  'agent.playerDetail.walletAdjust.field.amount':     'మొత్తం (₹)',
  'agent.playerDetail.walletAdjust.field.reason':     'కారణం · లెడ్జర్‌లో నిల్వ చేయబడుతుంది',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'ఉదా. డిపాజిట్ రివర్సల్, బోనస్, రీఫండ్',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'రీఛార్జ్‌గా మార్క్ చేయండి',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before':  'ఈ క్రెడిట్‌ని లెడ్జర్‌లో ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold':    'రీఛార్జ్',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after':   'గా రికార్డ్ చేయండి. ఈ యూజర్‌ని ఎవరైనా రెఫర్ చేసినట్లయితే, రెఫరర్ స్వయంచాలకంగా బోనస్ పొందుతారు.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'పాజిటివ్ మొత్తాన్ని నమోదు చేయండి',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'కారణం అవసరం',
  'agent.playerDetail.walletAdjust.cta.applying':     'అప్లై చేస్తోంది…',
  'agent.playerDetail.walletAdjust.cta.applyCredit':  'క్రెడిట్ అప్లై',
  'agent.playerDetail.walletAdjust.cta.applyDebit':   'డెబిట్ అప్లై',
  'agent.playerDetail.walletAdjust.toast.credited':   '{amount} క్రెడిట్ చేయబడింది',
  'agent.playerDetail.walletAdjust.toast.debited':    '{amount} డెబిట్ చేయబడింది',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · రెఫరర్ {bonus} బోనస్ సంపాదించారు',
  'agent.playerDetail.rechargeCta.title':             'ఈ ప్లేయర్‌ని రీఛార్జ్ చేయండి',
  'agent.playerDetail.rechargeCta.body.before':       '@',
  'agent.playerDetail.rechargeCta.body.middle':       'ని టాప్ అప్ చేయడం ',
  'agent.playerDetail.rechargeCta.body.bold':         'మీ వాలెట్',
  'agent.playerDetail.rechargeCta.body.after':        ' నుండి వారికి కాయిన్‌లను ట్రాన్స్‌ఫర్ చేస్తుంది. మీ పూల్ ముందుగా తనిఖీ చేయబడుతుంది — కదలిక అటామిక్ మరియు మీ వద్ద తగినంత బ్యాలెన్స్ లేకపోతే తిరస్కరించబడుతుంది.',
  'agent.playerDetail.rechargeCta.thisPlayer':        'ఈ ప్లేయర్',
  'agent.playerDetail.rechargeCta.cta':               'రీఛార్జ్ ఫారం తెరవండి',
  'agent.playerDetail.security.title':                'పాస్‌వర్డ్ రీసెట్',
  'agent.playerDetail.security.subtitle':             'కొత్త పాస్‌వర్డ్‌ని సెట్ చేస్తుంది మరియు యూజర్‌ని ప్రతి పరికరం నుండి సైన్ అవుట్ చేస్తుంది.',
  'agent.playerDetail.security.field.new':            'కొత్త పాస్‌వర్డ్',
  'agent.playerDetail.security.field.confirm':        'ధృవీకరించండి',
  'agent.playerDetail.security.error.mismatch':       'పాస్‌వర్డ్‌లు సరిపోలడం లేదు',
  'agent.playerDetail.security.error.tooShort':       'కనీసం 8 అక్షరాలు, అక్షరాలు మరియు అంకెలతో',
  'agent.playerDetail.security.toast.reset':          'పాస్‌వర్డ్ రీసెట్ చేయబడింది మరియు అన్ని సెషన్‌లు రద్దు చేయబడ్డాయి',
  'agent.playerDetail.security.cta.reset':            'పాస్‌వర్డ్ రీసెట్',
  'agent.playerDetail.security.cta.resetting':        'రీసెట్ అవుతోంది…',
  'agent.playerDetail.ledger.title':                  'వాలెట్ లెడ్జర్',
  'agent.playerDetail.ledger.entries.one':            '{count} ఎంట్రీ',
  'agent.playerDetail.ledger.entries.other':          '{count} ఎంట్రీలు',
  'agent.playerDetail.ledger.empty':                  'ఇంకా లావాదేవీలు లేవు.',
  'agent.playerDetail.ledger.loading':                'లోడ్ అవుతోంది…',
  'agent.playerDetail.ledger.actorSuffix':            ' · అడ్మిన్ #{id} ద్వారా',
  'agent.playerDetail.ledger.pagination.prev':        'మునుపటి',
  'agent.playerDetail.ledger.pagination.next':        'తదుపరి',
  'agent.playerDetail.ledger.kind.bet':               'మ్యాచ్ బెట్',
  'agent.playerDetail.ledger.kind.win':               'మ్యాచ్ బహుమతి',
  'agent.playerDetail.ledger.kind.refund':            'రీఫండ్',
  'agent.playerDetail.ledger.kind.fee':               'ప్లాట్‌ఫారమ్ ఫీ',
  'agent.playerDetail.ledger.kind.adminCredit':       'అడ్మిన్ క్రెడిట్',
  'agent.playerDetail.ledger.kind.adminDebit':        'అడ్మిన్ డెబిట్',
  'agent.playerDetail.ledger.kind.recharge':          'రీఛార్జ్',
  'agent.playerDetail.ledger.kind.referralBonus':     'రెఫరల్ బోనస్',
  'agent.playerDetail.ledger.kind.signupBonus':       'సైన్‌అప్ బోనస్',
  'agent.playerDetail.ledger.kind.offerCredit':       'ఆఫర్ క్రెడిట్',
  'agent.playerDetail.ledger.kind.withdrawalHold':    'విత్‌డ్రాల్',
  'agent.playerDetail.ledger.kind.withdrawalRefund':  'రీఫండ్',
  'agent.playerDetail.ledger.game.platform':          'ప్లాట్‌ఫారమ్',
  'agent.playerDetail.profile.title':                 'ప్రొఫైల్ ఎడిట్',
  'agent.playerDetail.profile.field.username':        'యూజర్‌నేమ్',
  'agent.playerDetail.profile.field.email':           'ఇమెయిల్',
  'agent.playerDetail.profile.field.phone':           'ఫోన్',
  'agent.playerDetail.profile.field.firstName':       'మొదటి పేరు',
  'agent.playerDetail.profile.field.lastName':        'చివరి పేరు',
  'agent.playerDetail.profile.field.dob':             'పుట్టిన తేదీ',
  'agent.playerDetail.profile.field.gender':          'లింగం',
  'agent.playerDetail.profile.gender.unspecified':    '—',
  'agent.playerDetail.profile.gender.male':           'పురుషుడు',
  'agent.playerDetail.profile.gender.female':         'స్త్రీ',
  'agent.playerDetail.profile.gender.other':          'ఇతర',
  'agent.playerDetail.profile.gender.preferNot':      'చెప్పడం ఇష్టం లేదు',
  'agent.playerDetail.profile.location.title':        'స్థానం',
  'agent.playerDetail.profile.location.lastUpdated':  '· చివరిసారి అప్‌డేట్ {date}',
  'agent.playerDetail.profile.location.legacy.label': 'ఫైల్‌లో లెగసీ చిరునామా',
  'agent.playerDetail.profile.location.legacy.note':  ' (రీడ్-ఓన్లీ — pre-R3, లొకేటర్ ద్వారా వాడబడదు):',
  'agent.playerDetail.profile.toast.nothingToSave':   'సేవ్ చేయడానికి ఏమీ లేదు',
  'agent.playerDetail.profile.toast.saved.one':       '{count} ఫీల్డ్ సేవ్ చేయబడింది',
  'agent.playerDetail.profile.toast.saved.other':     '{count} ఫీల్డ్‌లు సేవ్ చేయబడ్డాయి',
  'agent.playerDetail.profile.cta.save':              'మార్పులు సేవ్',
  'agent.playerDetail.profile.cta.saving':            'సేవ్ అవుతోంది…',
  'agent.playerDetail.profile.cta.reset':             'రీసెట్',
  'agent.playerDetail.ownership.title':               'యజమాని ఏజెంట్',
  'agent.playerDetail.ownership.row.eyebrow':         'యాజమాన్యం',
  'agent.playerDetail.ownership.platform.title':      'ప్లాట్‌ఫారమ్-డైరెక్ట్',
  'agent.playerDetail.ownership.platform.body':       'ఈ ప్లేయర్‌ని ఏ ఏజెంట్ స్వంతం చేసుకోలేదు. అడ్మిన్ నేరుగా నిర్వహిస్తారు.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ఏజెంట్ #{id}',
  'agent.playerDetail.ownership.owned.body':          'ఈ ప్లేయర్ రీఛార్జ్‌లు + మ్యాచ్‌లపై కమీషన్‌లు సంపాదిస్తారు.',
  'agent.playerDetail.ownership.cta.change':          'మార్చండి',
  'agent.playerDetail.ownership.toast.changedTo':     'యాజమాన్యం → {label}',
  'agent.playerDetail.ownership.platformLabel':       'ప్లాట్‌ఫారమ్-డైరెక్ట్',
  'agent.playerDetail.ownership.picker.title':            '@{username}ని మళ్లీ అసైన్',
  'agent.playerDetail.ownership.picker.body':             'ఒక కొత్త ఏజెంట్‌ని ఎంచుకోండి — లేదా ప్లాట్‌ఫారమ్-డైరెక్ట్‌కి డిటాచ్ చేయండి (ఈ ప్లేయర్‌ని ఏ ఏజెంట్ స్వంతం చేసుకోరు). భవిష్యత్ కమీషన్‌లు కొత్త యజమానిని అనుసరిస్తాయి; ఇప్పటికే క్రెడిట్ చేసిన రోలు తాకబడవు.',
  'agent.playerDetail.ownership.picker.detach.title':     'ప్లాట్‌ఫారమ్‌కి డిటాచ్',
  'agent.playerDetail.ownership.picker.detach.body':      'ఏజెంట్ యాజమాన్యాన్ని తీసివేయండి. అడ్మిన్ నేరుగా నిర్వహిస్తారు.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ఏజెంట్లను శోధించండి…',
  'agent.playerDetail.ownership.picker.loading':          'ఏజెంట్లు లోడ్ అవుతున్నాయి…',
  'agent.playerDetail.ownership.picker.empty.search':     'మీ శోధనకి సరిపోలే ఏజెంట్లు లేరు.',
  'agent.playerDetail.ownership.picker.empty.none':       'ఇంకా యాక్టివ్ ఏజెంట్లు లేరు.',
  'agent.playerDetail.ownership.picker.players.one':      '{count} ప్లేయర్',
  'agent.playerDetail.ownership.picker.players.other':    '{count} ప్లేయర్లు',
  'agent.playerDetail.ownership.picker.current':          'ప్రస్తుత',
  'agent.playerDetail.ownership.picker.currentUpper':     'ప్రస్తుత',
  'agent.playerDetail.ownership.picker.close':            'మూసివేయి',
  'agent.login.brand':              'బాలీ ఏజెంట్',
  'agent.login.subtitle':           'మీ ప్లేయర్లను నిర్వహించడానికి సైన్ ఇన్ చేయండి',
  'agent.login.field.identifier':   'యూజర్‌నేమ్ లేదా ఇమెయిల్',
  'agent.login.field.password':     'పాస్‌వర్డ్',
  'agent.login.disclaimer.before':  'ఏజెంట్లు మాత్రమే. మీరు ప్లాట్‌ఫారమ్ యజమాని అయితే, ',
  'agent.login.disclaimer.link':    '/admin/login',
  'agent.login.disclaimer.after':   'ని ఉపయోగించండి.',
  'agent.login.error.loginFailed':     'లాగిన్ విఫలమైంది',
  'agent.login.error.invalidCredentials': 'తప్పు ఆధారాలు',
  'agent.login.error.accountSuspended':   'ఖాతా నిలిపివేయబడింది — మీ నిర్వాహకుని సంప్రదించండి.',
  'agent.login.error.networkError':       'నెట్‌వర్క్ లోపం — మీ కనెక్షన్ తనిఖీ చేసి మళ్లీ ప్రయత్నించండి.',
  'agent.misc.gameTile.fallbackTag':    'గేమ్',
  'agent.misc.gameTile.actionSuffix':   '· కాన్ఫిగర్ చేయండి',

};

// For the remaining 6 languages we ship the most-visible strings now;
// rarer keys gracefully fall back to English. Native speakers can fill
// any gap by editing this file — no code change needed elsewhere.

const gu: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "મારી વિગતો",
  'profile.group.security': "સાઇન-ઇન અને સુરક્ષા",
  'profile.personal.title': "વ્યક્તિગત વિગતો",
  'profile.personal.editTitle': "વ્યક્તિગત વિગતો સંપાદિત કરો",
  'common.edit': "ફેરફાર કરો",
  'profile.field.name': "નામ",
  'profile.field.phone': "ફોન નંબર",
  'profile.field.gender': "લિંગ",
  'profile.field.location': "સ્થાન",
  'profile.gender.male': "પુરુષ",
  'profile.gender.female': "સ્ત્રી",
  'profile.gender.other': "અન્ય",
  'profile.gender.preferNot': "કહેવા માંગતા નથી",
  'profile.gender.select': "પસંદ કરો…",
  'profile.location.optionalHint': "વૈકલ્પિક — તમારો વિસ્તાર પસંદ કરો અથવા પિનકોડ ટાઇપ કરો (GPS થી વધુ ચોકસાઈ મળે છે).",
  'profile.lang.title': "ભાષા",
  'profile.password.title': "પાસવર્ડ",
  'profile.password.desc': "તમારા એકાઉન્ટનો પાસવર્ડ બદલો. અન્ય ડિવાઇસ સાઇન-આઉટ થઈ જશે.",
  'profile.password.cta': "બદલો",
  'profile.password.editTitle': "પાસવર્ડ બદલો",
  'profile.password.current': "વર્તમાન પાસવર્ડ",
  'profile.password.new': "નવો પાસવર્ડ",
  'profile.password.confirm': "નવો પાસવર્ડ કન્ફર્મ કરો",
  'profile.password.currentPh': "તમારો વર્તમાન પાસવર્ડ",
  'profile.password.newPh': "મજબૂત પાસવર્ડ પસંદ કરો",
  'profile.password.confirmPh': "નવો પાસવર્ડ ફરી ટાઇપ કરો",
  'profile.password.rule': "ઓછામાં ઓછા 8 અક્ષર, જેમાં એક અક્ષર અને એક આંકડો હોય.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "લિંક કરેલ:",
  'profile.telegram.otpHere': "પાસવર્ડ-રીસેટ OTP અહીં આવે છે.",
  'profile.telegram.unlinkChange': "અનલિંક કરો અને બદલો",
  'profile.telegram.unlinkTitle': "Telegram અનલિંક કરવું છે?",
  'profile.telegram.unlink': "અનલિંક કરો",
  'profile.telegram.keepLinked': "લિંક રહેવા દો",
  'profile.telegram.unlinkVerified': "તમે પછીથી બીજું Telegram એકાઉન્ટ ફરી લિંક કરી શકો છો. ઇમેઇલ વેરિફિકેશન યથાવત્ રહેશે.",
  'profile.telegram.unlinkOnly': "Telegram તમારું એકમાત્ર વેરિફાઇડ ચેનલ છે — રમવાનું ચાલુ રાખતાં પહેલાં તમારે એકાઉન્ટ ફરી વેરિફાઇ કરવું પડી શકે છે.",
  'profile.telegram.notLinkedDesc': "બેકઅપ તરીકે પાસવર્ડ-રીસેટ OTP મેળવવા માટે Telegram લિંક કરો.",
  'profile.telegram.openBot': "@BaaliGames_bot ખોલો",
  'profile.telegram.generate': "લિંક જનરેટ કરો",
  'profile.telegram.generating': "જનરેટ થઈ રહ્યું છે…",
  'profile.avatar.cropTitle': "તમારો ફોટો એડજસ્ટ કરો",
  'profile.toast.detailsSaved': "વિગતો સેવ થઈ",
  'profile.toast.photoUpdated': "ફોટો અપડેટ થયો",
  'profile.toast.pwChanged': "પાસવર્ડ બદલાયો. અન્ય ડિવાઇસ સાઇન-આઉટ થઈ ગયા છે.",
  'profile.toast.tgUnlinked': "Telegram અનલિંક થયું. બીજી ચેટ કનેક્ટ કરવા નવી લિંક જનરેટ કરો.",
  'profile.err.phone': "10-આંકડાનો મોબાઇલ નંબર દાખલ કરો (અથવા બદલ્યા વગર રહેવા દો)",
  'profile.err.pwMismatch': "નવા પાસવર્ડ મેળ ખાતા નથી",
  'profile.err.pwWeak': "પાસવર્ડમાં 8+ અક્ષર હોવા જોઈએ, જેમાં એક અક્ષર અને એક આંકડો હોય",
  'profile.err.pwSame': "નવો પાસવર્ડ વર્તમાનથી અલગ હોવો જોઈએ",
  'wallet.payout.title': "સેવ કરેલી પેઆઉટ વિગતો",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "હજી કંઈ સેવ થયું નથી",
  'wallet.payout.hint': "તમે ઉપાડ કરો ત્યારે તમારું UPI ID આપોઆપ સેવ થાય છે. તમારી આગામી ઉપાડ વિનંતી દરમિયાન તમે તેને અપડેટ કરી શકો છો.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ઇન્સ્ટોલ કરો",
  "pwa.install.subhint": "ત્વરિત, ફુલ-સ્ક્રીન ઍક્સેસ માટે તેને તમારી હોમ સ્ક્રીન પર ઉમેરો — કોઈ ઍપ સ્ટોરની જરૂર નથી.",
  "pwa.install.listen": "સાંભળો",
  "pwa.install.listening": "રોકો",
  "pwa.install.installBtn": "ઇન્સ્ટોલ કરો",
  "pwa.install.notNow": "હમણાં નહીં",
  "pwa.install.howToShow": "કેવી રીતે ઇન્સ્ટોલ કરવું",
  "pwa.install.howToHide": "પગલાં છુપાવો",
  "pwa.install.guide.iosSafari": "Safari સ્ક્રીનની નીચે, Share બટન પર ટૅપ કરો — તે ઉપર તરફ તીર સાથેના ચોરસ જેવું દેખાય છે.\nખુલતા મેનૂમાં થોડું નીચે સ્ક્રોલ કરો.\n\"Add to Home Screen\" વિકલ્પ પર ટૅપ કરો.\nઉપર જમણા ખૂણે Add પર ટૅપ કરો. હવે ઍપ કોઈપણ સામાન્ય ઍપની જેમ તમારી હોમ સ્ક્રીન પર રહેશે.",
  "pwa.install.guide.iosOther": "iPhone કે iPad પર, હોમ સ્ક્રીન પર ઉમેરવું ફક્ત Safari બ્રાઉઝરમાં કામ કરે છે.\nSafari ખોલો અને આ જ વેબસાઇટ પર જાઓ.\nSafari ની નીચે Share બટન પર ટૅપ કરો — ઉપર તરફ તીર સાથેનો ચોરસ.\nનીચે સ્ક્રોલ કરો, \"Add to Home Screen\" પર ટૅપ કરો, પછી Add પર ટૅપ કરો.",
  "pwa.install.guide.android": "ઉપરના Install બટન પર ટૅપ કરો.\nતમારું બ્રાઉઝર પૂછે ત્યારે, ખાતરી માટે Install પર ટૅપ કરો.\nથઈ ગયું — હવે ઍપ તમારી હોમ સ્ક્રીન પર છે. કોઈપણ સામાન્ય ઍપની જેમ તેને કોઈપણ સમયે ખોલો.\nપૉપ-અપ દેખાતું નથી? તમારા બ્રાઉઝરનું મેનૂ ખોલો — ત્રણ ટપકાં, સામાન્ય રીતે ઉપર જમણે — અને \"Install app\" પર ટૅપ કરો.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "સીધો રિચાર્જ",
  "agent.directRecharge.subtitle.player": "એક ખેલાડી પસંદ કરો અને તમારા પૂલમાંથી રિચાર્જ કરો. કોઈ વિનંતીની જરૂર નથી.",
  "agent.directRecharge.subtitle.subagent": "એક સબ-એજન્ટ પસંદ કરો અને તમારા પૂલમાંથી તેમના પૂલમાં રિચાર્જ કરો.",
  "agent.directRecharge.search.player": "રિચાર્જ માટે ખેલાડી શોધો (નામ · ફોન · ઇમેઇલ)…",
  "agent.directRecharge.search.subagent": "રિચાર્જ માટે સબ-એજન્ટ શોધો (નામ · ઇમેઇલ · UAID)…",
  "agent.directRecharge.clearSearch": "શોધ સાફ કરો",
  "agent.directRecharge.searching": "શોધી રહ્યું છે…",
  "agent.directRecharge.noMatches": "કોઈ મેળ નથી.",
  "agent.directRecharge.empty.player": "તમારી યાદીમાં હજુ કોઈ ખેલાડી નથી.",
  "agent.directRecharge.empty.subagent": "હજુ કોઈ સીધો સબ-એજન્ટ નથી.",
  "agent.directRecharge.searchFailed": "શોધ નિષ્ફળ",
  "agent.directRecharge.label.wallet": "વોલેટ",
  "agent.directRecharge.label.pool": "પૂલ",
  "agent.directRecharge.modal.title.player": "સીધો ખેલાડી રિચાર્જ",
  "agent.directRecharge.modal.title.subagent": "સીધો સબ-એજન્ટ રિચાર્જ",
  "agent.directRecharge.modal.close": "બંધ કરો",
  "agent.directRecharge.modal.targetPool": "લક્ષ્ય પૂલ",
  "agent.directRecharge.modal.operatingWallet": "ઓપરેટિંગ વોલેટ",
  "agent.directRecharge.modal.dayToDay": "રોજિંદું બેલેન્સ",
  "agent.directRecharge.modal.securityDeposit": "સિક્યુરિટી ડિપોઝિટ",
  "agent.directRecharge.modal.collateral": "કોલેટરલ પૂલ",
  "agent.directRecharge.modal.amount": "રકમ (₹)",
  "agent.directRecharge.modal.overdraw": "તમે તમારા પૂલ (₹{pool}) કરતાં વધુ ચૂકવી શકતા નથી. રકમ ઘટાડો.",
  "agent.directRecharge.modal.reason": "કારણ / નોંધ",
  "agent.directRecharge.modal.reasonPlaceholder": "રોકડ એકત્રિત · ref 4291",
  "agent.directRecharge.modal.reasonHint": "તમારા વોલેટ લેજર + ઓડિટ લોગમાં લખાય છે.",
  "agent.directRecharge.modal.proofLabel": "ચુકવણીનો સ્ક્રીનશોટ અપલોડ કરો (વૈકલ્પિક)",
  "agent.directRecharge.modal.proofHint": "ઇતિહાસ દ્વારા પ્રાપ્તકર્તાને દેખાય છે.",
  "agent.directRecharge.modal.previewEmpty": "વ્યવહાર પૂર્વાવલોકન કરવા રકમ દાખલ કરો.",
  "agent.directRecharge.modal.previewBody": "તમારા વોલેટમાંથી {name} ના {pool} માં મોકલ્યું. સુરક્ષિત ટ્રાન્સફર; લેજર + ઓડિટમાં નોંધાયેલ.",
  "agent.directRecharge.modal.pool.playerWallet": "વોલેટ",
  "agent.directRecharge.modal.pool.operatingWallet": "ઓપરેટિંગ વોલેટ",
  "agent.directRecharge.modal.pool.securityDeposit": "સિક્યુરિટી ડિપોઝિટ",
  "agent.directRecharge.modal.cancel": "રદ કરો",
  "agent.directRecharge.modal.send": "₹{amount} મોકલો",
  "agent.directRecharge.badge.player": "ખેલાડી",
  "agent.directRecharge.badge.subagent": "સબ-એજન્ટ",
  "agent.directRecharge.modal.yourDirectSubagent": "તમારો સીધો સબ-એજન્ટ",
  "agent.directRecharge.modal.fundingSource": "ભંડોળ સ્ત્રોત",
  "agent.directRecharge.modal.you": "(તમે)",
  "agent.directRecharge.modal.yourPool": "તમારો પૂલ",
  "agent.directRecharge.modal.notSignedIn": "એજન્ટ તરીકે સાઇન ઇન નથી",
  "agent.directRecharge.modal.failed": "નિષ્ફળ",

  // Common controls
  'common.signIn': 'સાઇન ઇન',
  'common.signUp': 'સાઇન અપ',
  'common.login': 'લોગિન',
  'common.logout': 'લોગ આઉટ',
  'common.continue': 'ચાલુ રાખો',
  'common.cancel': 'રદ કરો',
  'common.save': 'સાચવો',
  'common.update': 'અપડેટ કરો',
  'common.clear': 'સાફ કરો',
  'common.back': 'પાછા',
  'common.loading': 'લોડ થઈ રહ્યું છે…',
  'common.sending': 'મોકલી રહ્યા છીએ…',
  'common.saving': 'સાચવી રહ્યા છીએ…',
  'common.optional': 'વૈકલ્પિક',
  'common.show': 'બતાવો',
  'common.hide': 'છુપાવો',
  'common.profile': 'પ્રોફાઇલ',
  'common.settings': 'સેટિંગ્સ',

  // NavBar / BottomNav
  'nav.brand': 'બાલી ગેમ્સ',
  'nav.home': 'હોમ',
  'nav.games': 'ગેમ્સ',
  'nav.wallet': 'વોલેટ',
  'nav.rewards': 'ઇનામો',
  'nav.profile': 'પ્રોફાઇલ',

  // Login page
  'login.title': 'ફરી સ્વાગત છે',
  'login.subtitle': 'તમારી રમત ચાલુ રાખવા માટે સાઇન ઇન કરો.',
  'login.idLabel': 'યુઝરનેમ, ઇમેઇલ અથવા ફોન',
  'login.idPlaceholder': 'યુઝરનેમ, ઇમેઇલ અથવા મોબાઇલ',
  'login.passwordLabel': 'પાસવર્ડ',
  'login.passwordPlaceholder': 'તમારો પાસવર્ડ દાખલ કરો',
  'login.forgot': 'ભૂલી ગયા?',
  'login.cta': 'સાઇન ઇન',
  'login.signingIn': 'સાઇન ઇન થઈ રહ્યું છે…',
  'login.newHere': 'નવા છો?',
  'login.createAccount': 'એકાઉન્ટ બનાવો',

  // Register page
  'register.title': 'તમારું એકાઉન્ટ બનાવો',
  'register.subtitle': 'એક ટેગ પસંદ કરો અને તમે અંદર છો.',
  'register.usernameLabel': 'યુઝરનેમ',
  'register.usernamePlaceholder': 'એક અનન્ય હેન્ડલ પસંદ કરો',
  'register.usernameHelp': 'અક્ષરો, સંખ્યાઓ અને અંડરસ્કોર. 3–24 અક્ષરો.',
  'register.emailLabel': 'ઇમેઇલ',
  'register.emailPlaceholder': 'તમારું ઇમેઇલ સરનામું',
  'register.mobileLabel': 'મોબાઇલ નંબર',
  'register.mobilePlaceholder': '10-અંકનો મોબાઇલ',
  'register.mobileHelp': 'અમે કન્ટ્રી કોડ આપમેળે ઉમેરી દઈશું.',
  'register.passwordLabel': 'પાસવર્ડ',
  'register.passwordPlaceholder': 'મજબૂત પાસવર્ડ પસંદ કરો',
  'register.passwordHelp': 'ઓછામાં ઓછા 8 અક્ષર, એક અક્ષર અને એક અંક સાથે.',
  'register.referralLabel': 'રેફરલ કોડ',
  'register.referralPlaceholder': 'મિત્રનો આમંત્રણ કોડ',
  'register.cta': 'એકાઉન્ટ બનાવો',
  'register.creating': 'બનાવી રહ્યા છીએ…',
  'register.alreadyPlayer': 'પહેલેથી જ ખેલાડી છો?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'તમારા વિશે', 'register.step.signin': 'સાઇન-ઇન', 'register.step.finish': 'સમાપ્ત',
  'register.step.aboutSubtitle': 'તમારા વિશે થોડું જણાવો', 'register.step.signinSubtitle': 'સાઇન-ઇન કેવી રીતે કરશો તે સેટ કરો', 'register.step.finishSubtitle': 'સ્થાન પસંદ કરો અને પૂર્ણ કરો',
  'register.firstName': 'પ્રથમ નામ', 'register.firstNamePh': 'આરવ',
  'register.lastName': 'છેલ્લું નામ', 'register.lastNamePh': 'શર્મા',
  'register.dob': 'જન્મ તારીખ', 'register.dobHelp': 'રમવા માટે તમારી ઉંમર 18 અથવા તેથી વધુ હોવી જોઈએ',
  'register.v.required': 'જરૂરી', 'register.v.nameMin': 'ઓછામાં ઓછા 2 અક્ષરો',
  'register.v.tooLong': 'ખૂબ લાંબું', 'register.v.nameChars': 'ફક્ત અક્ષરો, જગ્યા અને હાઇફન',
  'register.v.invalidDate': 'અમાન્ય તારીખ', 'register.v.minAge': 'તમારી ઉંમર ઓછામાં ઓછી 18 હોવી જોઈએ',
  'register.v.tooOld': 'કૃપા કરીને વર્ષ તપાસો',
  'register.v.usernameMin': 'ઓછામાં ઓછા 3 અક્ષરો', 'register.v.usernameMax': 'ખૂબ લાંબું (મહત્તમ 24)',
  'register.v.usernameChars': 'ફક્ત અક્ષરો, સંખ્યાઓ, અંડરસ્કોર', 'register.v.emailShape': 'અમાન્ય ઇમેઇલ લાગે છે',
  'register.v.phoneDigits': '10 અંકનો નંબર હોવો જોઈએ', 'register.v.phonePrefix': 'ભારતીય મોબાઇલ 6–9થી શરૂ થાય',
  'register.v.pwdMin': 'ઓછામાં ઓછા 8 અક્ષરો', 'register.v.pwdLetter': 'ઓછામાં ઓછો એક અક્ષર હોવો જોઈએ',
  'register.v.pwdNumber': 'ઓછામાં ઓછી એક સંખ્યા હોવી જોઈએ',
  'register.v.referralShort': 'ખૂબ ટૂંકું', 'register.v.referralChars': 'ફક્ત અક્ષરો અને સંખ્યાઓ',
  'register.v.usernameTaken': 'આ યુઝરનેમ પહેલેથી લેવાયું છે',
  'register.v.emailTaken': 'આ ઇમેઇલ પહેલેથી નોંધાયેલ છે',
  'register.v.phoneTaken': 'આ નંબર પહેલેથી નોંધાયેલ છે',
  'register.v.noLocation': 'આગળ વધવા માટે ઓછામાં ઓછી એક સ્થાન પદ્ધતિ શેર કરો.',
  'register.welcome.title': 'સ્વાગત છે', 'register.welcome.subtitle': 'તમારું ખાતું તૈયાર છે. હવે સુરક્ષિત કરો કે સીધા જ શરૂ કરો?',
  'register.welcome.verifyTelegram': 'ટેલિગ્રામ દ્વારા ચકાસો',
  'register.welcome.verifyExplain': '⚠ મહત્વનું: હમણાં ટેલિગ્રામ દ્વારા ચકાસો. ચકાસણી વગર, પાસવર્ડ ભૂલી જાવ તો તમે જાતે રીસેટ કરી શકશો નહીં — ફક્ત તમારો એજન્ટ અથવા એડમિન જ રીસેટ કરી શકશે. પછી પ્રોફાઇલમાંથી પણ ચકાસી શકો છો.',
  'register.welcome.verifyHeads': 'આને પછી માટે સાચવો',
  'register.welcome.verifyConsequence': 'ઈમેલ ચકાસણી હાલમાં ઉપલબ્ધ નથી. પછી પાસવર્ડ રીસેટ માટે ટેલિગ્રામ દ્વારા ચકાસો. જો ન કરો તો પાસવર્ડ ભૂલી જાવ ત્યારે ફક્ત એજન્ટ અથવા એડમિન જ રીસેટ કરી શકશે.',
  'register.welcome.continue': 'એપ્લિકેશનમાં ચાલુ રાખો', 'register.welcome.continueLogin': 'ચાલુ રાખવા માટે સાઇન ઇન કરો', 'register.welcome.useDifferent': 'અલગ ખાતું વાપરો',
  'register.geo.title': 'તમે ક્યાંથી રમો છો?', 'register.geo.subtitle': 'નજીકના એજન્ટ સાથે જોડવા તમારું સ્થાન વાપરીએ છીએ.',
  'register.geo.showAlt': 'સ્થાન શેર કરી શકતા નથી? અલગ પદ્ધતિ વાપરો',
  'register.geo.hideAlt': 'અન્ય પદ્ધતિઓ છુપાવો', 'register.geo.captured': 'સ્થાન મેળવ્યું',
  'register.geo.capturedHint': 'તમે પ્રોફાઇલમાંથી આને ગમે ત્યારે બદલી શકો છો.',
  'register.geo.verifyNote': 'સાઇન-અપ પછી, પ્રોફાઇલમાંથી ટેલિગ્રામ (પાસવર્ડ રીસેટ માટે) અથવા ઇમેઇલ દ્વારા ગમે ત્યારે ચકાસી શકો છો. ચકાસણી વૈકલ્પિક.',

  // Verify (post-signup)
  'verify.title': 'તમારું એકાઉન્ટ વેરિફાય કરો',
  'verify.subtitle': 'એક વિકલ્પ પસંદ કરો — બસ એટલું જ.',
  'verify.tabTelegram': 'ટેલિગ્રામ',
  'verify.tabEmail': 'ઇમેઇલ',
  'verify.emailSent': 'અમે {email} પર વેરિફિકેશન લિંક મોકલી છે.',
  'verify.emailHint': 'આ ડિવાઇસ પર ખોલો. દેખાતી નથી? સ્પામ / પ્રમોશન્સ તપાસો.',
  'verify.telegramOpen': '@BaaliGames_bot ખોલો',
  'verify.telegramExpiry': 'લિંક 30 મિનિટમાં સમાપ્ત થાય છે. ટેલિગ્રામમાં તમારે એક વાર Start દબાવવું જરૂરી છે.',
  'verify.goToLogin': 'લોગિન પર જાઓ',
  'verify.email.title': 'ઇમેઇલ વેરિફાય થયું',
  'verify.email.sub': 'તમારું એકાઉન્ટ સક્રિય છે. હવે રમવાનો સમય.',
  'verify.email.failed': 'વેરિફિકેશન નિષ્ફળ',
  'verify.email.verifying': 'વેરિફાય થઈ રહ્યું છે…',
  'verify.backRegister': 'રજિસ્ટર પર પાછા',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ઇમેઇલ ફરી મોકલો',
  'verify.gate.resent': 'મોકલાયું!',
  'verify.gate.checking': 'કામ ચાલુ છે…',
  'verify.gate.tgFallback': 'મેન્યુઅલી લિંક ખોલો',
  'verify.gate.waiting': 'અમે વેરિફિકેશન આપમેળે શોધી લઈશું.',
  'verify.gate.signOut': 'સાઇન આઉટ',

  // Forgot password
  'forgot.title': 'તમારું એકાઉન્ટ પાછું મેળવો',
  'forgot.subtitle': 'અમે તમને પાછા આવવાનો માર્ગ મોકલીશું.',
  'forgot.idLabel': 'યુઝરનેમ, ઇમેઇલ અથવા ફોન',
  'forgot.idPlaceholder': 'યુઝરનેમ, ઇમેઇલ અથવા મોબાઇલ',
  'forgot.sendVia': 'આના દ્વારા મોકલો',
  'forgot.email': 'ઇમેઇલ',
  'forgot.telegram': 'ટેલિગ્રામ OTP',
  'forgot.telegramHelp': 'ટેલિગ્રામ ત્યારે જ કામ કરે છે જ્યારે તમારું એકાઉન્ટ અમારા બોટ સાથે જોડાયેલ હોય.',
  'forgot.cta': 'રિકવરી મોકલો',
  'forgot.sentEmail': 'જો એકાઉન્ટ અસ્તિત્વમાં હોય, તો રીસેટ લિંક ઇમેઇલ કરવામાં આવી છે.',
  'forgot.sentTelegram': 'જો એકાઉન્ટ અસ્તિત્વમાં હોય, તો ટેલિગ્રામ દ્વારા 6-અંકનો કોડ મોકલવામાં આવ્યો છે.',
  'forgot.redirecting': 'રીસેટ પેજ પર રીડાયરેક્ટ થઈ રહ્યું છે…',
  'forgot.back': 'લોગિન પર પાછા',

  // Reset password
  'reset.title': 'નવો પાસવર્ડ સેટ કરો',
  'reset.subOtp': 'અમે ટેલિગ્રામ પર મોકલેલ કોડનો ઉપયોગ કરો.',
  'reset.subLink': 'નવો કોમ્બો પસંદ કરો અને સીધા પાછા ચાલુ રાખો.',
  'reset.otpLabel': '6-અંકનો કોડ',
  'reset.newPasswordLabel': 'નવો પાસવર્ડ',
  'reset.cta': 'પાસવર્ડ અપડેટ કરો',
  'reset.updating': 'અપડેટ થઈ રહ્યું છે…',
  'reset.successTitle': 'પાસવર્ડ અપડેટ થયો',
  'reset.successSub': 'તમે તૈયાર છો. રમવાનો સમય.',

  // Home (logged-out)
  'home.welcomeTo': 'આપનું સ્વાગત છે',
  'home.heroSub': 'કૌશલ્ય-આધારિત ગેમિંગ હબ. અંદર આવવા માટે સાઇન ઇન કરો અથવા તમારું ખેલાડી એકાઉન્ટ બનાવો.',
  'home.skillBased': 'કૌશલ્ય-આધારિત',
  'home.skillBasedSub': 'વ્યૂહરચના જીતે છે',
  'home.fairPlay': 'ફેર પ્લે',
  'home.fairPlaySub': 'એન્ટિ-ચીટ ON',
  'home.instantPayout': 'UPI પેઆઉટ',
  'home.instantPayoutSub': '૨૪ કલાકની અંદર',
  'home.featured': 'ફીચર્ડ ગેમ્સ',
  'home.tapToPlay': 'રમવા માટે ટેપ કરો',
  'home.ready': 'રમવા તૈયાર છો?',
  'home.dropIn': 'અંદર આવો અને વાસ્તવિક ઇનામો જીતો.',
  'home.start': 'શરૂ કરો',

  // Home (logged-in)
  'home.welcomeBack': 'ફરી સ્વાગત છે',
  'home.balance': 'તમારું બેલેન્સ',
  'home.live': 'લાઇવ',
  'home.add': 'ઉમેરો',
  'home.manage': 'મેનેજ કરો',
  'home.rewards': 'ઇનામો',
  'home.todayChallenge': 'આજનો પડકાર',
  'home.todayChallengeSub': 'મેચ જીતો — સ્ટ્રીક બોનસ કમાઓ.',
  'home.view': 'જુઓ',
  'home.yourGames': 'તમારી ગેમ્સ',
  'home.viewAll': 'બધી જુઓ',

  // Profile tabs
  'profile.details': 'વિગતો',
  'profile.address': 'સરનામું',
  'profile.languages': 'ભાષાઓ',
  'profile.account': 'એકાઉન્ટ',
  'profile.photo': 'ફોટો',
  'profile.preferred': 'પસંદગીની ભાષા',

  // Profile → Languages tab
  'profile.lang.primary': 'પ્રાથમિક ભાષા',
  'profile.lang.primarySub': 'આખા એપ માટે ડિફોલ્ટ',
  'profile.lang.locked': 'લોક',
  'profile.lang.second': 'બીજી ભાષા',
  'profile.lang.pickHelp': 'તમે જે ભાષા પણ બોલો છો તે પસંદ કરો. પસંદ કરવા માટે કાર્ડ પર ટેપ કરો.',
  'profile.lang.saved': 'ગૌણ ભાષા સાચવી',
  'profile.lang.cleared': 'ગૌણ ભાષા સાફ કરી',

  // Language switcher
  'lang.switcher.aria': 'ભાષા પસંદ કરો',
  'lang.switcher.title': 'તમારી ભાષા પસંદ કરો',
  'lang.switcher.sub': 'આખું એપ તરત જ અપડેટ થાય છે.',
  'lang.switcher.current': 'વર્તમાન',

  // Rewards page
  'rewards.title': 'ઇનામો અને ઓફર્સ',
  'rewards.subtitle': 'બોનસ જે તમે અત્યારે કમાઈ કે ક્લેમ કરી શકો છો.',
  'rewards.tab.available': 'ઉપલબ્ધ',
  'rewards.tab.inReview': 'સમીક્ષામાં',
  'rewards.tab.claimed': 'ક્લેમ કરેલ',
  'rewards.empty.available.title': 'અત્યારે કોઈ ઓફર નથી',
  'rewards.empty.available.body': 'ફરી તપાસો — એડમિન નિયમિતપણે નવી ઓફર્સ પોસ્ટ કરે છે.',
  'rewards.empty.review.title': 'સમીક્ષામાં કંઈ નથી',
  'rewards.empty.review.body': 'જે ક્લેમ્સને એડમિન મંજૂરીની જરૂર છે તે અહીં દેખાય છે.',
  'rewards.empty.claimed.title': 'હજુ સુધી કોઈ ક્લેમ નથી',
  'rewards.empty.claimed.body': 'જ્યારે તમે બોનસ કમાશો, તે અહીં ક્લેમ તરીકે દેખાશે.',
  'rewards.expires': '{date} ના રોજ સમાપ્ત',
  'rewards.remaining': '{amount} બાકી',
  'rewards.cap': 'મર્યાદા',
  'rewards.claim.cta': 'ક્લેમ કરો',
  'rewards.claim.submit': 'સબમિટ કરો',
  'rewards.claimed.credited': '{date} ના રોજ {amount} ક્લેમ કર્યું',
  'rewards.claimed.pending': 'સમીક્ષામાં બાકી',
  'rewards.claimed.rejected': 'નકાર',
  'rewards.auto.willCredit': 'શરતો પૂરી થયે આપમેળે જમા થશે',
  'rewards.auto.willReview': 'એડમિન સમીક્ષા કતારમાં જાય છે',
  'rewards.row.creditedOn': '{date} ના રોજ જમા થયું',
  'rewards.row.submittedOn': '{date} ના રોજ સબમિટ કર્યું',
  'rewards.toast.credited': 'બોનસ જમા થયું — તમારા વોલેટમાં {amount} ઉમેરાયા',
  'rewards.toast.submitted': 'સમીક્ષા માટે સબમિટ કરેલ',
  'rewards.toast.alreadyClaimed': 'પહેલેથી ક્લેમ કરેલ',
  'rewards.toast.rejected': 'ક્લેમ નકારવામાં આવ્યો',

  // Wallet page
  'wallet.balance': 'બેલેન્સ',
  'wallet.balance.note': 'એન્ટ્રીઓ તરત જ ડેબિટ થાય છે; મેચ સમાપ્ત થયે ઇનામો જમા થાય છે.',
  'wallet.addMoney': 'પૈસા ઉમેરો',
  'wallet.withdraw': 'ઉપાડ',
  'wallet.withdraw.minBalance': 'ઉપાડ માટે ઓછામાં ઓછા ₹200 જરૂરી છે',
  'wallet.history.byGame': 'ગેમ પ્રમાણે તમારો ઇતિહાસ',
  'wallet.history.empty': 'હજુ સુધી કોઈ પ્રવૃત્તિ નથી. તમારી પહેલી મેચ અહીં દેખાશે.',
  'wallet.activity': 'તાજેતરની પ્રવૃત્તિ',
  'wallet.activity.empty': 'હજુ સુધી કોઈ ટ્રાન્ઝેક્શન નથી.',
  'wallet.net': 'નેટ',
  'wallet.newer': 'નવા',
  'wallet.older': 'જૂના',
  'wallet.pending.title': 'બાકી વિનંતીઓ',
  'wallet.pending.recharge': '{amount} નો રિચાર્જ — એડમિન મંજૂરીની રાહમાં',
  'wallet.pending.withdrawal': '{amount} નો ઉપાડ — પેઆઉટની રાહમાં',
  'wallet.state.pending': 'બાકી',
  'wallet.state.paid': 'ચૂકવાયું',
  'wallet.pending.txnPrefix': 'વ્યવહાર:',
  'wallet.pending.upload.label': 'ઝડપી કાર્યવાહી માટે ચૂકવણીનો સ્ક્રીનશોટ અપલોડ કરો.',
  'wallet.pending.upload.hint':  'વિનંતી બનાવ્યા પછી તમારી પાસે 24 કલાક છે.',
  'wallet.pending.upload.failed': 'અપલોડ નિષ્ફળ',
  'wallet.activity.balanceAfter': 'બેલેન્સ {balance}',
  'wallet.activity.range': '{total} માંથી {from}–{to}',
  'wallet.activity.window': 'તમારી છેલ્લા {days} દિવસોની પ્રવૃત્તિ',
  'wallet.history.summary.one':   '{matches} મેચ · જીત્યા {wins} · ખર્ચ {bets}',
  'wallet.history.summary.other': '{matches} મેચ · જીત્યા {wins} · ખર્ચ {bets}',
  'wallet.history.summary.refunded': ' · રિફંડ {refunds}',
  'ledger.kind.bet': 'મેચ એન્ટ્રી', 'ledger.kind.win': 'મેચ ઇનામ',
  'ledger.kind.refund': 'મેચ રિફંડ', 'ledger.kind.forfeit': 'મેચ જપ્તી',
  'ledger.kind.fee': 'પ્લેટફોર્મ ફી', 'ledger.kind.recharge': 'ટોપ-અપ',
  'ledger.kind.admin_credit': 'એડમિન ક્રેડિટ', 'ledger.kind.admin_debit': 'એડમિન ડેબિટ',
  'ledger.kind.referral_bonus': 'રેફરલ બોનસ', 'ledger.kind.signup_bonus': 'સાઇન-અપ બોનસ',
  'ledger.kind.offer_credit': 'ઓફર ઇનામ',
  'ledger.kind.withdrawal_hold': 'ઉપાડ હોલ્ડ', 'ledger.kind.withdrawal_refund': 'ઉપાડ રિફંડ',
  'ledger.kind.agent_topup_in': 'એજન્ટ ટોપ-અપ', 'ledger.kind.agent_co_fund': 'એજન્ટ યોગદાન', 'ledger.kind.turnover_reward': 'કેશબેક',
  'ledger.withdrawalPaid': 'ઉપાડ ચૂકવાયું', 'ledger.withdrawalPaidPartial': 'ઉપાડ ચૂકવાયું (આંશિક)',
  'ledger.withdrawalPaid.txn': 'ચૂકવાયું · વ્યવહાર {txnId}',
  'ledger.withdrawalRejected': 'ઉપાડ નકારાયું', 'ledger.withdrawalRejected.note': 'હોલ્ડ રકમ પરત',
  'ledger.withdrawalExpired': 'ઉપાડ સમાપ્ત', 'ledger.awaitingProof': 'પુરાવાની રાહ',
  'proofPicker.button.compact': 'સ્ક્રીનશોટ પસંદ કરો', 'proofPicker.button.prominent': 'સ્ક્રીનશોટ ઉમેરો',
  'proofPicker.processing': 'પ્રક્રિયા થઈ રહી છે…',
  'proofPicker.error.mime': 'ફક્ત JPG, PNG અથવા WebP છબીઓ મંજૂર છે.',
  'proofPicker.error.size': 'ફાઇલ ખૂબ મોટી — મહત્તમ 5 MB.',
  'proofPicker.error.processing': 'છબી પર પ્રક્રિયા કરી શકાતી નથી',
  'proofPicker.previewAlt': 'પસંદ કરેલ સ્ક્રીનશોટ પૂર્વાવલોકન',
  'proofViewer.state.none': 'સ્ક્રીનશોટની રાહ', 'proofViewer.state.available': 'સ્ક્રીનશોટ જુઓ',
  'proofViewer.state.expired': 'સ્ક્રીનશોટ ઉપલબ્ધ નથી', 'proofViewer.state.viewLimit': 'જોવાની મર્યાદા પૂર્ણ',
  'proofViewer.loading': 'લોડ થઈ રહ્યું છે…',
  'proofViewer.title.viewLimit': 'તમે આ સ્ક્રીનશોટ મહત્તમ 2 વખત ખોલ્યો છે',
  'proofViewer.error.load': 'સ્ક્રીનશોટ લોડ થઈ શક્યો નથી',
  'proofViewer.lastView': 'છેલ્લી વખત — છબી સમાપ્ત થશે',
  'proofViewer.viewsRemaining.one': 'હજુ {n} વાર જોઈ શકો',
  'proofViewer.viewsRemaining.other': 'હજુ {n} વાર જોઈ શકો',
  'proofViewer.imageAlt': 'ચૂકવણી પુરાવો', 'proofViewer.close': 'બંધ કરો',

  // Recharge flow
  'recharge.title': 'પૈસા ઉમેરો',
  'recharge.step1.subtitle': 'તમે કેટલા ઉમેરવા માંગો છો?',
  'recharge.step1.amount': 'રકમ',
  'recharge.step1.range': 'ન્યૂનતમ {min} · મહત્તમ {max}',
  'recharge.step1.minHint': 'ન્યૂનતમ {min} રિચાર્જ',
  'recharge.step1.maxHint': 'મહત્તમ {max}',
  'recharge.step1.quickPick': 'ઝડપી પસંદ',
  'recharge.step2.subtitle': 'UPI દ્વારા ચૂકવો, પછી ચાલુ રાખો',
  'recharge.step2.payAmount': 'આ રકમ ચૂકવો',
  'recharge.step2.iPaid': 'મેં ચૂકવ્યું',
  'recharge.step2.howto.1': 'તમારી UPI એપ ખોલો (Google Pay / PhonePe / Paytm).',
  'recharge.step2.howto.2': 'ઉપરની રકમ UPI ID પર ચૂકવો અથવા QR સ્કેન કરો.',
  'recharge.step2.howto.3': 'તમારી UPI એપમાંથી ટ્રાન્ઝેક્શન ID કોપી કરો — તે પછીના સ્ટેપમાં જરૂરી છે.',
  "recharge.locked.title": "રિચાર્જ થોડા સમય માટે ઉપલબ્ધ નથી",
  "recharge.locked.body": "અમે આ રિચાર્જ રૂટની સમસ્યા ઉકેલવાનું કાર્ય કરી રહ્યા છીએ. તમારું વોલેટ અને ઉપાડ અસરગ્રસ્ત નથી. કૃપા કરી થોડી વાર પછી ફરી પ્રયાસ કરો.",
  "recharge.locked.reassure.withdrawals": "ઉપાડ સામાન્ય રીતે ચાલે છે",
  "recharge.locked.reassure.existing": "તમે પહેલેથી મોકલેલી ચૂકવણી પ્રક્રિયા હેઠળ છે",
  "recharge.cap.banner": "અત્યારે મહત્તમ રિચાર્જ: {max}. પ્રાપ્તકર્તાની ક્ષમતા થોડી મર્યાદિત છે.",
  "recharge.cap.tooHigh": "રકમ ઉપલબ્ધ મર્યાદાથી વધારે છે. અત્યારે તમે મહત્તમ {max} રિચાર્જ કરી શકો છો.",
  "recharge.notConfigured.title": "રિચાર્જ ટૂંક સમયમાં ઉપલબ્ધ થશે",
  "recharge.notConfigured.body": "અમે આ રિચાર્જ સેવાનું સેટઅપ પૂર્ણ કરી રહ્યા છીએ. તમારું વોલેટ અને ઉપાડ અસરગ્રસ્ત નથી. કૃપા કરી થોડી વાર પછી તપાસો.",
  'recharge.step3.subtitle': 'તમારી ચૂકવણીની પુષ્ટિ કરો',
  'recharge.step3.amountPaid': 'તમે જે રકમ ચૂકવી',
  'recharge.step3.txnId': 'UPI ટ્રાન્ઝેક્શન ID',
  'recharge.step3.txnIdHelp': 'આ તમારી UPI એપમાં ચૂકવણીની રસીદ હેઠળ મળશે — સામાન્ય રીતે 12 અંકોની હોય છે.',
  'recharge.step3.note': 'એડમિન માટે નોંધ',
  'recharge.step3.notePlaceholder': 'દા.ત. પત્નીના નંબરથી ચૂકવ્યું',
  'recharge.step3.submit': 'સમીક્ષા માટે સબમિટ કરો',
  'recharge.step3.disclaimer': 'એડમિન UPI એપમાં ચૂકવણી ચકાસે છે. મંજૂરી પછી ૨૪ કલાકની અંદર વોલેટમાં જમા થશે (ઘણીવાર પહેલા).',
  'recharge.success.title': 'સબમિટ કર્યું — સમીક્ષાની રાહમાં',
  'recharge.success.body': '{amount} નો તમારો રિચાર્જ એડમિન કતારમાં છે. ૨૪ કલાકની અંદર જમા થશે (ઘણીવાર પહેલા).',
  'recharge.success.refId': 'સંદર્ભ',
  'recharge.toast.submitted': 'રિચાર્જ એડમિન સમીક્ષા માટે સબમિટ કરેલ',

  // Withdraw flow
  'withdraw.title': 'ઉપાડ',
  'withdraw.step1.subtitle': 'અમે પૈસા ક્યાં મોકલીએ?',
  'withdraw.step2.subtitle': 'છેલ્લી તપાસ',
  'withdraw.balance': 'ઉપલબ્ધ બેલેન્સ',
  'withdraw.amount': 'રકમ',
  'withdraw.max': 'મહત્તમ',
  'withdraw.upiId': 'તમારી UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'અમે પૈસા અહીં મોકલીશું. સબમિટ કરતા પહેલા બે વાર તપાસો.',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'તમારા છેલ્લા ઉપાડથી પ્રી-ફિલ કરેલ',
  'withdraw.upiShapeErr': 'UPI ID yourname@psp જેવી હોવી જોઈએ (દા.ત. lucky@oksbi)',
  'withdraw.phoneShapeErr': 'માન્ય 10-અંકનો ભારતીય મોબાઇલ દાખલ કરો (6, 7, 8 અથવા 9 થી શરૂ થવો જોઈએ)',
  'withdraw.whatsapp': 'WhatsApp નંબર',
  'withdraw.errMin': 'ન્યૂનતમ ઉપાડ {min} છે',
  'withdraw.errExceed': 'રકમ તમારા બેલેન્સ કરતાં વધારે છે',
  'withdraw.minHint': 'ન્યૂનતમ {min} ઉપાડ',
  'withdraw.step2.review': 'સમીક્ષા',
  'withdraw.step2.heldNotice': 'જ્યારે તમે સબમિટ કરો, ત્યારે આ રકમ તરત જ હોલ્ડ થાય છે જેથી તેને બીજે ક્યાંય ખર્ચી ન શકાય. એડમિન તેને તમારી UPI ID પર ચૂકવે છે અને તેને ચૂકવાયેલ તરીકે ચિહ્નિત કરે છે.',
  'withdraw.heldAfter': 'હોલ્ડ',
  'withdraw.spendableAfter': 'ખર્ચ યોગ્ય',
  'withdraw.step2.disclaimer': 'જો એડમિન નકારે, તો હોલ્ડ કરેલ રકમ તમારા વોલેટમાં પાછી આવી જાય છે.',
  'withdraw.submit': 'સબમિટ કરો',
  'withdraw.success.title': 'સબમિટ કર્યું — પેઆઉટની રાહમાં',
  'withdraw.success.body': '{amount} નો તમારો ઉપાડ કતારમાં છે. ૨૪ કલાકની અંદર ચૂકવણી (ઘણીવાર પહેલા). ત્યાં સુધી હોલ્ડ કરેલી રકમ આરક્ષિત રહે છે.',
  'withdraw.success.refId': 'સંદર્ભ',
  'withdraw.toast.submitted': 'ઉપાડ કતારબદ્ધ — તમારા વોલેટમાં હોલ્ડ',

  // Payment receiver card
  'payment.upi.label': 'આ UPI ID પર ચૂકવો',
  'payment.upi.copy': 'કોપી',
  'payment.upiCopied': 'UPI ID કોપી કરી',
  'payment.openUpiApp': 'હવે ચૂકવો',
  'payment.openWhatsapp': 'WhatsApp',
  'payment.helpAt': 'મદદ જોઈએ? કૉલ કરો',
  'payment.qr.label': 'ચૂકવવા માટે QR સ્કેન કરો',
  'payment.qr.tap': 'મોટું કરવા માટે ટેપ કરો',
  'payment.qr.tapToClose': 'બંધ કરવા માટે ગમે ત્યાં ટેપ કરો',
  'payment.notConfigured.title': 'રિચાર્જ અત્યારે ઉપલબ્ધ નથી',
  'payment.notConfigured.contactBody': 'હજુ સુધી કોઈ UPI સેટઅપ નથી. WhatsApp અથવા ફોન દ્વારા સંપર્ક કરો, અમે તમને રિચાર્જ કરવામાં મદદ કરીશું.',
  'payment.notConfigured.unavailable': 'રિચાર્જ અસ્થાયી રૂપે ઉપલબ્ધ નથી. કૃપા કરીને પછી ફરી પ્રયાસ કરો.',
  'payment.chooser.title': 'તમારી UPI એપ પસંદ કરો',
  'payment.chooser.subtitle': 'તમે ચૂકવશો',
  'payment.chooser.hint': 'રકમ પ્રી-ફિલ સાથે એપ ખોલવા માટે તેના પર ટેપ કરો. એપ તમારા ફોન પર પહેલેથી જ ઇન્સ્ટોલ હોવી જરૂરી છે — જો "address invalid" એરર દેખાય, તો પહેલા App Store / Play Store માંથી એપ ઇન્સ્ટોલ કરો.',

  // Common
  'common.next': 'આગળ',
  'common.close': 'બંધ કરો',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'ઝડપી અને સરળ પેમેન્ટ સેવા સુનિશ્ચિત કરવા માટે, કૃપા કરીને તમારું ચોક્કસ સ્થાન અને સંપર્ક નંબર પ્રદાન કરો.',
  'geo.change': 'બદલો',
  'geo.gps.button': 'મારું વર્તમાન સ્થાન વાપરો',
  'geo.gps.benefit': 'સૌથી ઝડપી — એક ટેપ, કોઈ ટાઇપિંગ નહીં.',
  'geo.gps.denied': 'સ્થાન પરવાનગી નકારી. પિનકોડ વાપરો અથવા નીચે મેન્યુઅલી પસંદ કરો.',
  'geo.gps.failed': 'તમારું સ્થાન મેળવી શક્યા નહીં. તેના બદલે પિનકોડ અજમાવો.',
  'geo.gps.notResolved': 'અમે તમારા સ્થળને જાણીતા વિસ્તાર સાથે મેચ કરી શક્યા નહીં. કૃપા કરીને મેન્યુઅલી પસંદ કરો.',
  'geo.pincode.label': 'પિનકોડ',
  'geo.pincode.notFound': 'પિનકોડ મળ્યો નહીં. કૃપા કરીને તમારો વિસ્તાર મેન્યુઅલી પસંદ કરો.',
  'geo.pincode.lookupFailed': 'પિનકોડ લુકઅપ નિષ્ફળ. કૃપા કરીને મેન્યુઅલી પસંદ કરો.',
  'geo.multipleAreas.label': 'આ પિનકોડ ઘણા વિસ્તારોમાં વહેંચાયેલ છે — તમારો પસંદ કરો',
  'geo.multipleAreas.placeholder': 'તમારો વિસ્તાર પસંદ કરો',
  'geo.manual.title': 'મેન્યુઅલી પસંદ કરો',
  'geo.manual.close': 'મેન્યુઅલ પિકર છુપાવો',
  'geo.state.label': 'રાજ્ય',
  'geo.state.placeholder': 'રાજ્ય પસંદ કરો',
  'geo.district.label': 'જિલ્લો',
  'geo.district.placeholder': 'જિલ્લો પસંદ કરો',
  'geo.area.label': 'વિસ્તાર / શહેર',
  'geo.area.placeholder': 'વિસ્તાર પસંદ કરો',
  'geo.privacy.note': 'ફક્ત તમારા સ્થાનિક એજન્ટને સોંપવા માટે વપરાય છે. ક્યારેય શેર થતું નથી.',
  'geo.required.hint': 'જ્યારે તમારી પાસે રેફરલ કોડ ન હોય ત્યારે સ્થાન જરૂરી છે.',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'ડેશબોર્ડ',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'હિસાબ',
  'agent.nav.players': 'ખેલાડીઓ',
  'agent.nav.agents': 'એજન્ટો',
  'agent.nav.rewards': 'ઇનામો',
  'agent.nav.profile':   'પ્રોફાઇલ',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'હોમ',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'હિસાબ',
  'agent.bottomnav.players': 'ખેલાડીઓ',
  'agent.bottomnav.agents': 'એજન્ટો',
  'agent.bottomnav.rewards': 'ઇનામો',
  'agent.bottomnav.profile': 'પ્રોફાઇલ',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'ફરી સ્વાગત છે, ',
  'agent.dashboard.header.welcomeFallback': 'મિત્ર',
  'agent.dashboard.header.subtitle': 'તમારો બિઝનેસ એક નજરમાં — પૂલ, ડિપોઝિટ, ખેલાડીઓ, સબ-એજન્ટો, P&L, એક્સપોઝર',
  'agent.dashboard.range.today': 'આજે',
  'agent.dashboard.range.yesterday': 'ગઈકાલે',
  'agent.dashboard.range.thisWeek': 'આ અઠવાડિયે',
  'agent.dashboard.range.lastWeek': 'પાછલા અઠવાડિયે',
  'agent.dashboard.range.thisMonth': 'આ મહિને',
  'agent.dashboard.range.lastMonth': 'પાછલા મહિને',
  'agent.dashboard.range.complete': 'સંપૂર્ણ',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'ડિપોઝિટ કવરેજ લોડ થઈ રહ્યું છે…',
  'agent.dashboard.depositBar.label': 'ડિપોઝિટ કવરેજ',
  'agent.dashboard.depositBar.balanceOfTarget': '· લક્ષ્ય {target} માંથી {balance}',
  'agent.dashboard.depositBar.tapToView': 'સંપૂર્ણ ડિપોઝિટ પેજ જોવા માટે ટેપ કરો.',
  'agent.dashboard.depositBar.caption.suspended': 'ખાતું સસ્પેન્ડ. એડમિનનો સંપર્ક કરો.',
  'agent.dashboard.depositBar.caption.breach': 'તમારા વોલેટે તમારી ડિપોઝિટ સીમાનો 100% વાપરી લીધો છે. ઉપાડ લોક છે. તરત જ ટોપ-અપ કરો.',
  'agent.dashboard.depositBar.caption.warning': 'તમારું વોલેટ તમારી ડિપોઝિટ સીમાના {pct}% પર છે. ચેતવણી 80% પર, લોકડાઉન 100% પર.',
  'agent.dashboard.depositBar.caption.caution': 'તમારા વોલેટે તમારા ડિપોઝિટ કવરેજનો {pct}% વાપરી લીધો છે. 80% થી નીચે — હજુ સુરક્ષિત.',
  'agent.dashboard.depositBar.caption.safe': 'ડિપોઝિટ સંપૂર્ણ સુરક્ષિત. કોઈ કવરેજ વપરાતું નથી.',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'અલર્ટ · શૂન્ય સિક્યુરિટી ડિપોઝિટ',
  'agent.dashboard.locatorSkip.body': 'તમારી સિક્યુરિટી ડિપોઝિટ {amount} છે. જ્યાં સુધી તમે તેને ભરો નહીં ત્યાં સુધી તમને ભૌગોલિક લોકેટરથી નવા ખેલાડીઓ નહીં મળે. હાલના ખેલાડીઓ + સીધા રેફરલ + મેન્યુઅલ અસાઇનમેન્ટ પર અસર નહીં થાય — ફક્ત ઓર્ગેનિક જિયો-રાઉટેડ સાઇનઅપ તમને છોડી દેશે.',
  'agent.dashboard.locatorSkip.cta': 'ડિપોઝિટ ભરો',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'ડાઉનલાઇન ડિફોલ્ટર અલર્ટ',
  'agent.dashboard.defaulter.summary.critical.one': 'તમારો {n} સીધો સબ-એજન્ટ ડિફોલ્ટમાં છે — તે લોક છે અથવા તેણે પોતાની ડિપોઝિટ બ્રીચ કરી છે.',
  'agent.dashboard.defaulter.summary.critical.other': 'તમારા {n} સીધા સબ-એજન્ટો ડિફોલ્ટમાં છે — તેઓ લોક છે અથવા તેમણે પોતાની ડિપોઝિટ બ્રીચ કરી છે.',
  'agent.dashboard.defaulter.summary.warning.one': 'તમારો {n} સીધો સબ-એજન્ટ પોતાની ડિપોઝિટ સીમાના 80%+ પર છે — ડિફોલ્ટરનું જોખમ.',
  'agent.dashboard.defaulter.summary.warning.other': 'તમારા {n} સીધા સબ-એજન્ટો પોતાની ડિપોઝિટ સીમાના 80%+ પર છે — ડિફોલ્ટરનું જોખમ.',
  'agent.dashboard.defaulter.worst': 'સૌથી ખરાબ: @{username} ડિપોઝિટ સીમાના {pct}% પર.',
  'agent.dashboard.defaulter.tapHint': 'સીધા સબ-એજન્ટોની યાદી પર જવા માટે ટેપ કરો. ડિફોલ્ટર પંક્તિઓ હાઇલાઇટ છે.',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'લોક',
  'agent.dashboard.marginPill.caution': 'સાવધાન',
  'agent.dashboard.marginPill.warning': 'ચેતવણી',
  'agent.dashboard.marginPill.breach': 'બ્રીચ',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'સવારનું બ્રીફિંગ',
  'agent.dashboard.sections.morning.subtitle': 'લાઇવ સ્નેપશોટ — દરેક સંખ્યા આપમેળે રિફ્રેશ થાય છે',
  'agent.dashboard.sections.activity.title': 'પ્રવૃત્તિ',
  'agent.dashboard.sections.activity.subtitle': 'સૌથી વધુ ખર્ચ કરનારા અને તાજેતરના ખેલાડી ચુકવણી પ્રસંગો',
  'agent.dashboard.sections.forensic.title': 'ફોરેન્સિક વિગત',
  'agent.dashboard.sections.forensic.subtitle': 'કોઈપણ સેક્શન વિસ્તારવા માટે ટેપ કરો — પેજને શાંત રાખવા માટે ડિફોલ્ટ રૂપે બંધ',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'પ્રોફિટ અને લોસનું ઊંડાણભર્યું વિશ્લેષણ',
  'agent.dashboard.accordions.pnlDeep.helper': 'તમારી કમાણીનું પ્રતિ-પિલર + પ્રતિ-ગેમ વિભાજન. અંતર્ગત મેચ જોવા માટે ગેમ પંક્તિ પર ક્લિક કરો.',
  'agent.dashboard.accordions.leaderboard.title': 'ડાઉનલાઇન લીડરબોર્ડ',
  'agent.dashboard.accordions.leaderboard.helper': 'તમારા સબ-એજન્ટો ચોખ્ખી કમાણી પ્રમાણે ક્રમમાં. ઋણ સંખ્યાઓનો અર્થ છે કે તે સબ-એજન્ટે જેટલું લાવ્યું તેનાથી વધુ ચૂકવ્યું.',
  'agent.dashboard.accordions.activity.title': 'ડાઉનલાઇન પ્રવૃત્તિ ફીડ',
  'agent.dashboard.accordions.activity.helper': 'તમારા સબ-એજન્ટોના દરેક મેચ સેટલમેન્ટ, રિચાર્જ અને રેટ ફેરફારની — ફોરેન્સિક ટાઇમલાઇન.',
  'agent.dashboard.accordions.earnings.title': 'કમાણીનું વિભાજન (લાઇફટાઇમ)',
  'agent.dashboard.accordions.earnings.helper': 'પ્રકાર પ્રમાણે લાઇફટાઇમ કુલ. ઉપરના સમય ફિલ્ટરને નજરઅંદાજ કરે છે — આ ક્યારેય બદલાતા નથી.',
  'agent.dashboard.accordions.directSubAgents.title': 'સીધા સબ-એજન્ટો',
  'agent.dashboard.accordions.directSubAgents.helper': 'તમારા સીધા પ્રતિનિધિઓ. દરેક પોતાના ખેલાડીઓ + પોતાના સબ-એજન્ટો ચલાવે છે. ગુલાબી/એમ્બર રંગની હાઇલાઇટ પંક્તિઓ ડિફોલ્ટર ક્ષેત્રમાં છે.',
  'agent.dashboard.accordions.chain.title': 'ચેઇન વિઝિબિલિટી',
  'agent.dashboard.accordions.chain.helper': 'તમારી કુલ ડાઉનલાઇન ફૂટપ્રિન્ટ, સીધી (તમે સંપાદિત કરો છો) અને પરોક્ષ (તમારા સબ-એજન્ટો દ્વારા સંચાલિત) માં અલગ.',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'લાઇવ ડેટા — ફોકસ પર, દર 60 સેકન્ડે, અને જ્યારે પૈસા હલે ત્યારે રિફ્રેશ થાય છે.',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'મારો પૂલ બેલેન્સ',
  'agent.dashboard.cards.poolBalance.helper': 'તમારો ખર્ચ કરી શકાય તેવો બેલેન્સ. ખેલાડીઓને રિચાર્જ કરવા અથવા સબ-એજન્ટોને મોકલવા માટે વાપરી શકાય તેવા પૈસા.',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} માં નેટ {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'સિક્યુરિટી ડિપોઝિટ',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'ડિપોઝિટ સ્થિતિ',
  'agent.dashboard.cards.depositStatus.helperLoading': 'ડિપોઝિટ સ્થિતિ લોડ થઈ રહી છે…',
  'agent.dashboard.cards.depositStatus.helper': 'તમારા પૂલ સામેની જામીનગીરી. સ્થિતિ લક્ષ્યના 50% પર સાવધાન, 80% પર ચેતવણી, 100% પર બ્રીચમાં બદલાય છે.',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} થી',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'ઓપરેટિંગ બેલેન્સ',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'ડિપોઝિટ બેલેન્સ',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'ડિપોઝિટ લક્ષ્ય',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ઋણ એક્સપોઝર',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'કવરેજ ગુણોત્તર',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'ડિપોઝિટ લક્ષ્યનો આટલો ઋણ એક્સપોઝર દ્વારા વપરાયો',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'સુરક્ષિત',
  'agent.dashboard.depositStatus.status.caution': 'સાવધાન',
  'agent.dashboard.depositStatus.status.warning': 'ચેતવણી',
  'agent.dashboard.depositStatus.status.breach': 'બ્રીચ',
  'agent.dashboard.depositStatus.status.suspended': 'સસ્પેન્ડ',
  'agent.dashboard.depositStatus.status.healthy': 'સ્વસ્થ',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'ખેલાડીઓનું કુલ વોલેટ',
  'agent.dashboard.cards.playersWallet.helper': 'અત્યારે તમારા દરેક ખેલાડીના વોલેટનો સંયુક્ત લાઇવ બેલેન્સ. તમારી સાથે જોડાયેલી તમામ wallet_points પંક્તિઓનો સરવાળો.',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} ખેલાડી · {active} સક્રિય',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} ખેલાડીઓ · {active} સક્રિય',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'સબ-એજન્ટો વોલેટ + ડિપોઝિટ',
  'agent.dashboard.cards.subAgents.helper': 'તમારા સીધા સબ-એજન્ટો દ્વારા રાખવામાં આવેલ સંયુક્ત પૂલ બેલેન્સ અને સિક્યુરિટી ડિપોઝિટ. દરેક સબ-એજન્ટ પોતાના ખેલાડીઓ + પોતાની ડાઉનલાઇન ચલાવે છે.',
  'agent.dashboard.cards.subAgents.field.pool': 'પૂલ',
  'agent.dashboard.cards.subAgents.field.deposit': 'ડિપોઝિટ',
  'agent.dashboard.cards.subAgents.foot.one': '{n} સીધો સબ-એજન્ટ',
  'agent.dashboard.cards.subAgents.foot.other': '{n} સીધા સબ-એજન્ટો',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'મારી બાકી વિનંતીઓ',
  'agent.dashboard.cards.myPending.helper': 'પ્રાથમિકતા 3. એડમિનને તમારી પોતાની રજૂઆતો — આના પર તમે કાર્યવાહી કરી શકતા નથી, તેઓ રાહ જોઈ રહ્યા છે.',
  'agent.dashboard.cards.myPending.recharge.one': '{n} રિચાર્જ એડમિનની મંજૂરીની રાહમાં',
  'agent.dashboard.cards.myPending.recharge.other': '{n} રિચાર્જ એડમિનની મંજૂરીની રાહમાં',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} ઉપાડ એડમિનની મંજૂરીની રાહમાં',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} ઉપાડ એડમિનની મંજૂરીની રાહમાં',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'ખેલાડીઓની બાકી વિનંતીઓ',
  'agent.dashboard.cards.playersPending.helper': 'પ્રાથમિકતા 1. તમારા ખેલાડીઓ આને મંજૂર કરવા અથવા ચૂકવવા માટે તમારી રાહ જોઈ રહ્યા છે. કાર્યવાહી માટે ટેપ કરો.',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} રિચાર્જ મંજૂરી માટે',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} રિચાર્જ મંજૂર કરવાના છે',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} ઉપાડ ચૂકવવાનો છે',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} ઉપાડ ચૂકવવાના છે',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'સબ-એજન્ટના બાકી અનુરોધો',
  'agent.dashboard.cards.subAgentsPending.helper': 'પ્રાથમિકતા 2. તમારા સબ-એજન્ટ્સ (સંપૂર્ણ ડાઉનલાઇન) આ મંજૂર કરવા અથવા ચૂકવવા માટે તમારી રાહ જોઈ રહ્યા છે. કાર્યવાહી માટે ટેપ કરો.',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'True Net P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'આ સમયગાળામાં તમારો વાસ્તવિક નફો/નુકસાન. પ્લેટફોર્મ ફી શેર + વિન/લોસ શેર − ઓફર કો-ફંડિંગ. પ્રતિ-ગેમ વિગતો માટે નીચે P&L ડીપ-ડાઇવ વિસ્તૃત કરો.',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'પ્લેટ',
  'agent.dashboard.pillarChip.winLoss': 'વિન/લોસ',
  'agent.dashboard.pillarChip.offer': 'ઓફર',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'ઓફર ઉપયોગ · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'આ સમયગાળામાં ઓફર દ્વારા તમારા ખેલાડીઓને ચૂકવાયેલ કુલ ઇનામ ₹. તે ખર્ચમાં તમારો હિસ્સો જોવા માટે તમારા એક્સપોઝર પેજ સાથે સરખામણી કરો.',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} ક્લેમ જમા થયો',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} ક્લેમ જમા થયા',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'ટોચના ખર્ચ કરનારા',
  'agent.dashboard.activity.topSpenders.helper': '{range} માં તમારા સૌથી વધુ ખર્ચ કરનારા ખેલાડીઓ.',
  'agent.dashboard.activity.topSpenders.empty': 'આ સમયગાળામાં હજુ સુધી કોઈ ખર્ચ નથી.',
  'agent.dashboard.activity.recent.title': 'તાજેતરની પ્રવૃત્તિ',
  'agent.dashboard.activity.recent.helper': 'તમારા ખેલાડીઓની પાંચ સૌથી તાજેતરની રિચાર્જ ઘટનાઓ.',
  'agent.dashboard.activity.recent.empty': 'કોઈ તાજેતરની પ્રવૃત્તિ નથી.',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'મંજૂર',
  'agent.dashboard.activity.recent.state.rejected': 'નકારેલ',
  'agent.dashboard.activity.recent.state.pending': 'બાકી',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'પ્રતિ ગેમ · સમજૂતી માટે પંક્તિ પર ટેપ કરો',
  'agent.dashboard.pnl.noEarnings': 'આ સમયગાળામાં કોઈ કમાણી નથી.',
  'agent.dashboard.pnl.table.col.pillarGame': 'પિલર / ગેમ',
  'agent.dashboard.pnl.table.col.net': 'નેટ',
  'agent.dashboard.pnl.table.col.matches': 'મેચ',
  'agent.dashboard.pnl.ledgerRows.one': '{n} લેજર પંક્તિ',
  'agent.dashboard.pnl.ledgerRows.other': '{n} લેજર પંક્તિઓ',
  'agent.dashboard.pnl.pillar.platformFee': 'પ્લેટફોર્મ ફી',
  'agent.dashboard.pnl.pillar.winLoss': 'વિન / લોસ',
  'agent.dashboard.pnl.pillar.offerReward': 'ઓફર ઇનામ',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'સમયગાળો: {range} — ડેશબોર્ડના સમયગાળાનો કડક ઉપસમૂહ',
  'agent.dashboard.pnlDrill.loading': 'મેચ-સ્તરની વિગતો લોડ થઈ રહી છે…',
  'agent.dashboard.pnlDrill.windowTotal': 'સમયગાળાનો કુલ:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} મેચમાં',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} મેચોમાં',
  'agent.dashboard.pnlDrill.noContribution': 'આ સેલમાં કોઈ મેચનું યોગદાન નથી.',
  'agent.dashboard.pnlDrill.errorFallback': 'ડ્રિલડાઉન લોડ કરી શકાયું નથી',
  'agent.dashboard.pnlDrill.table.col.match': 'મેચ',
  'agent.dashboard.pnlDrill.table.col.role': 'ભૂમિકા',
  'agent.dashboard.pnlDrill.table.col.base': 'આધાર',
  'agent.dashboard.pnlDrill.table.col.rate': 'દર',
  'agent.dashboard.pnlDrill.table.col.earned': 'કમાયા',
  'agent.dashboard.pnlDrill.table.col.settled': 'પતાવેલ',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.dashboard.directDownline.empty': 'હજુ સુધી કોઈ સીધા સબ-એજન્ટ નથી.',
  'agent.dashboard.directDownline.row.player.one': '{n} ખેલાડી',
  'agent.dashboard.directDownline.row.player.other': '{n} ખેલાડીઓ',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} સબ-એજન્ટ',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} સબ-એજન્ટ્સ',
  'agent.dashboard.directDownline.row.walletDeposit': 'વોલેટ {wallet} · ડિપોઝિટ {deposit}',
  'agent.dashboard.directDownline.footer': 'True Net એ {range} સમયગાળા માટે છે.',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.dashboard.chain.directAgents.title': 'સીધા એજન્ટ્સ',
  'agent.dashboard.chain.directAgents.subtitle': 'તમારા સીધા સબ-એજન્ટ્સ — તમે તેમના અનુરોધો પર કાર્યવાહી કરી શકો છો.',
  'agent.dashboard.chain.indirectAgents.title': 'પરોક્ષ એજન્ટ્સ',
  'agent.dashboard.chain.indirectAgents.subtitle': 'તમારા સબ-એજન્ટ્સ દ્વારા સંચાલિત. તમારા માટે માત્ર-વાંચન.',
  'agent.dashboard.chain.directPlayers.title': 'સીધા ખેલાડીઓ',
  'agent.dashboard.chain.directPlayers.subtitle': 'તમારા ખેલાડીઓ — તમે તેમના રિચાર્જ અને ઉપાડ પર કાર્યવાહી કરો છો.',
  'agent.dashboard.chain.indirectPlayers.title': 'પરોક્ષ ખેલાડીઓ',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'તમારા સબ-એજન્ટ્સના ખેલાડીઓ. તમારા માટે માત્ર-વાંચન.',
  'agent.dashboard.chain.field.walletTotal': 'કુલ વોલેટ',
  'agent.dashboard.chain.field.depositTotal': 'કુલ ડિપોઝિટ',
  'agent.dashboard.chain.field.avgPerHead': 'પ્રતિ વ્યક્તિ સરેરાશ',
  'agent.dashboard.chain.field.pendingRecharge': 'બાકી રિચાર્જ',
  'agent.dashboard.chain.field.pendingWithdrawal': 'બાકી ઉપાડ',
  'agent.dashboard.chain.pendingBadge': '{n} બાકી',
  'agent.dashboard.chain.openInbox': 'ઇનબોક્સ ખોલો',
  'agent.dashboard.chain.footer': 'સીધા = તે સંખ્યા જે તમે જાતે સંચાલિત કરો છો. પરોક્ષ = તમારા સબ-એજન્ટ્સ દ્વારા સંચાલિત (તમે સંખ્યાઓ જુઓ છો પણ તેમના બાકી અનુરોધો પર કાર્યવાહી કરી શકતા નથી — તે તેમના પેરન્ટને જાય છે).',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'લોડ કરવામાં નિષ્ફળ',
  'agent.activity.filter.window': 'સમયગાળો',
  'agent.activity.filter.kind': 'પ્રકાર',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'બધા',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'બધા',
  'agent.activity.kind.matches': 'મેચ',
  'agent.activity.kind.recharges': 'રિચાર્જ',
  'agent.activity.kind.rateChanges': 'દર ફેરફારો',
  // Empty + loading states.
  'agent.activity.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.activity.empty.title': 'શાંત',
  'agent.activity.empty.subtitle': '{window} સમયગાળામાં કોઈ {kindWord}ઘટનાઓ નથી.',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'મેચ ',
  'agent.activity.empty.kindWord.recharge': 'રિચાર્જ ',
  'agent.activity.empty.kindWord.partnership_change': 'પાર્ટનરશિપ ફેરફાર ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'સેટલમેન્ટ',
  'agent.activity.row.earned': 'કમાયા',
  'agent.activity.row.fromPillarOn': '{pillar} થી ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'પ્લેટફોર્મ ફી',
  'agent.activity.row.pillar.winLoss': 'વિન/લોસ',
  'agent.activity.row.pillar.offerReward': 'ઓફર ઇનામ',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'રિચાર્જ',
  'agent.activity.row.recharge.of': 'નું',
  'agent.activity.row.recharge.byUser': 'યુઝર #{id} દ્વારા',
  'agent.activity.row.recharge.via': ' દ્વારા ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'મંજૂર',
  'agent.activity.row.state.rejected': 'નકારેલ',
  'agent.activity.row.state.pending': 'બાકી',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'દર ફેરફાર',
  'agent.activity.row.rateChange.dashOn': '— {pillar} પર ',
  'agent.activity.row.rateChange.cascade': 'કાસ્કેડ',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'આ વ્યૂ ફક્ત એજન્ટ માટે છે.',
  'agent.earnings.stat.poolBalance': 'પૂલ બેલેન્સ',
  'agent.earnings.stat.totalIn': 'કુલ આવક',
  'agent.earnings.stat.totalOut': 'કુલ ખર્ચ',
  'agent.earnings.section.in': 'કમાણી (આવક)',
  'agent.earnings.section.out': 'બહાર જતું (ખર્ચ)',
  'agent.earnings.empty.in': 'હજુ સુધી કોઈ કમાણી નથી. જ્યારે તમારા ખેલાડીઓ રિચાર્જ કરશે અથવા રમશે, ત્યારે હિસ્સો અહીં દેખાશે.',
  'agent.earnings.empty.out': 'હજુ સુધી કોઈ ખર્ચ નથી.',
  'agent.earnings.viewLedger': 'સંપૂર્ણ લેજર જુઓ',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'એડમિન તરફથી ટોપ-અપ',
  'agent.earnings.kind.topupFromAdmin.hint': 'એડમિને તમારા પૂલને ક્રેડિટ કર્યું',
  'agent.earnings.kind.topupToPlayer.label': 'ખેલાડીને ટોપ-અપ',
  'agent.earnings.kind.topupToPlayer.hint': 'તમે એક ખેલાડીને રિચાર્જ કર્યો',
  'agent.earnings.kind.topupFromPlayer.label': 'ખેલાડીનો રિચાર્જ',
  'agent.earnings.kind.topupFromPlayer.hint': 'તમારા ખેલાડીઓએ તમારા પૂલમાં ચૂકવણી કરી',
  'agent.earnings.kind.revenueShare.label': 'આવક હિસ્સો',
  'agent.earnings.kind.revenueShare.hint': 'મેચોમાંથી પ્લેટફોર્મ ફીનો હિસ્સો',
  'agent.earnings.kind.coFundOffer.label': 'સહ-ભંડોળિત ઓફર',
  'agent.earnings.kind.coFundOffer.hint': 'ઓફર ચૂકવણીમાં તમારો હિસ્સો',
  'agent.earnings.kind.adminAdjust.label': 'એડમિન સમાયોજન',
  'agent.earnings.kind.adminAdjust.hint': 'મેન્યુઅલ સુધારો',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'એન્ટ્રી',
  'agent.earnings.row.entry.other': 'એન્ટ્રીઝ',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'અવધિ',
  'agent.leaderboard.filter.pillar': 'પિલર',
  'agent.leaderboard.window.all': 'બધા',
  'agent.leaderboard.pillar.all': 'બધા પિલર',
  'agent.leaderboard.pillar.platformFee': 'પ્લેટફોર્મ ફી',
  'agent.leaderboard.pillar.winLoss': 'વિન / લોસ',
  'agent.leaderboard.pillar.offerReward': 'ઓફર ઇનામ',
  'agent.leaderboard.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.leaderboard.empty.title': 'હજુ સુધી કોઈ કમાણી નથી',
  'agent.leaderboard.empty.subtitle.admin': '{window} અવધિમાં બધા એજન્ટોમાં કોઈ સેટલમેન્ટ પ્રવૃત્તિ નથી.',
  'agent.leaderboard.empty.subtitle.downline': '{window} અવધિમાં તમારી ડાઉનલાઇનમાંથી કોઈ કમાણી નથી. જેમ તમારા સબ-એજન્ટો મેચ ચલાવશે, આ બોર્ડ ભરાશે.',
  'agent.leaderboard.row.matches.one': '{n} મેચ',
  'agent.leaderboard.row.matches.other': '{n} મેચ',
  'agent.leaderboard.footer': 'કમાણી match_settlement_snapshots માંથી લેવામાં આવી છે. ઋણાત્મક સંખ્યાનો અર્થ છે કે તે એજન્ટે અવધિ દરમિયાન જેટલું મેળવ્યું તેના કરતાં વધુ ચૂકવણી કરી.',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'પ્લેટ',
  'agent.leaderboard.chip.winLoss': 'વિન/લોસ',
  'agent.leaderboard.chip.offer': 'ઓફર',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'માર્જિન ચેતવણી · 80% સીમા પહોંચી',
  'agent.warning.header.title': 'એક ખરાબ સેટલમેન્ટ દૂર લોકઆઉટથી.',
  'agent.warning.header.body': 'હજુ લોક નથી — પરંતુ તમારી સિક્યુરિટી ડિપોઝિટનું કવર 80% થી વધુ વપરાઈ ગયું છે. હમણાં ટોપ-અપ કરો અને નિયંત્રણમાં રહો.',
  'agent.warning.header.dismissAria': 'બંધ કરો',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'આ કેમ દેખાય છે',
  'agent.warning.why.body': 'તાજેતરના ગેમ સેટલમેન્ટોએ તમારા વોલેટને ઋણાત્મક કરી દીધું. તમારી {deposit} ની સિક્યુરિટી ડિપોઝિટ એ સુરક્ષા-જાળ છે જે તેને સહન કરે છે — અને હવે તે 80% થી વધુ વપરાઈ ગઈ છે. થોડું વધુ ઋણાત્મક આવ્યું તો તમારું વોલેટ + ડિપોઝિટ શૂન્ય પર આવી જશે અને પ્લેટફોર્મ ખાતાને લોક કરી દેશે.',
  'agent.warning.why.reassurance': 'આ ચેતવણી જાણીજોઈને વહેલી આવે છે — જ્યારે તમે હજુ સંપૂર્ણ એપનો ઉપયોગ કરી શકો છો. લોકઆઉટ સ્ક્રીન ત્યારે જ દેખાશે જ્યારે તમે 100% પાર કરશો, અને એક વાર એવું થયું તો મોટાભાગની સુવિધાઓ બંધ થઈ જશે જ્યાં સુધી તમે રિકવર ન થાઓ. હમણાં પગલાં લેવા પછીથી લેવા કરતાં ઘણું ઓછું વિક્ષેપકારક છે.',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'લોકઆઉટ પહેલાંની હેડરૂમ',
  'agent.warning.gauge.suffix': 'જગ્યા બાકી',
  'agent.warning.gauge.dangerBadge': 'ખતરા ઝોનમાં {pct}%',
  'agent.warning.gauge.field.wallet': 'તમારું વોલેટ',
  'agent.warning.gauge.field.deposit': 'તમારી ડિપોઝિટ',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'જો તમે આને અવગણો અને 100% પર પહોંચો',
  'agent.warning.ignore.cardTitle': 'તમે તરત જ ગુમાવશો',
  'agent.warning.ignore.item.dashboard': 'તમારા ડેશબોર્ડ, P&L અને લીડરબોર્ડ સુધી પહોંચ',
  'agent.warning.ignore.item.pages': 'ખેલાડીઓ અને એજન્ટો પેજ',
  'agent.warning.ignore.item.manualTopup': 'તમારા પૂલમાંથી તમારા ખેલાડીઓને મેન્યુઅલ ટોપ-અપ',
  'agent.warning.ignore.item.newWithdrawal': 'તમારા અપલાઇનને નવા ઉપાડ સબમિટ કરવા',
  'agent.warning.ignore.item.subAgents': 'સબ-એજન્ટ ઉમેરવા અથવા મેનેજ કરવા',
  'agent.warning.ignore.item.profile': 'તમારી પ્રોફાઇલ અથવા ચૂકવણી સેટિંગ્સ સંપાદિત કરવી',
  'agent.warning.ignore.footer': 'તમે હજુ પણ બાકી વિનંતીઓ મંજૂર કરી શકશો + એક કટોકટી ટોપ-અપ મોકલી શકશો — પરંતુ બાકી બધું બંધ રહેશે જ્યાં સુધી તમારું વોલેટ + ડિપોઝિટ શૂન્યથી ઉપર ન ચઢે.',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'રિકવરી માટે હમણાં તમારા અપલાઇનને ચૂકવણી કરો',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'હું આને પછી નિપટાવીશ',
  'agent.warning.dismiss.footnote': 'જો તમે ચેતવણી ઝોનમાં રહેશો તો અમે 10 મિનિટમાં ફરી યાદ કરાવીશું. રિકવરી થતાં જ આપોઆપ બંધ થઈ જશે.',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'ખાતું લોક · બેલ-આઉટ પોર્ટલ',
  'agent.lockout.header.title': 'તમે તમારી ડિપોઝિટ કવર કરી શકે તેના કરતાં વધુ બાકી છો.',
  'agent.lockout.header.body': 'ચિંતા ન કરો — આ ઠીક થઈ શકે છે. નીચેના બે પગલાં પૂર્ણ કરો અને તમે આપોઆપ અનલોક થઈ જશો.',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'આ કેમ થયું',
  'agent.lockout.why.body': 'ગેમ સેટલમેન્ટોએ તમારા વોલેટને શૂન્યથી નીચે લઈ ગયું. તમારી સિક્યુરિટી ડિપોઝિટ એ સુરક્ષા-જાળ છે જે તેને સહન કરે છે — પરંતુ તમે તેનો 100% વાપરી ચૂક્યા છો. જ્યાં સુધી તમારું વોલેટ અને ડિપોઝિટ ફરી શૂન્યથી ઉપર ન ચઢે, તમે સામાન્ય રીતે કામ કરી શકતા નથી.',
  'agent.lockout.why.reassurance': 'પ્લેટફોર્મ તમને એટલા માટે લોક નથી કરતું કે તમે કંઈ ખોટું કર્યું છે. તે એટલા માટે લોક કરે છે જેથી તમે સ્થિર થતી વખતે નેટવર્ક અને દેવું એકઠું ન કરી શકો.',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'સુરક્ષિત રિકવરી લક્ષ્ય',
  'agent.lockout.gauge.suffix': 'સુરક્ષિત ચૂકવણી માટે',
  'agent.lockout.gauge.recoveredBadge': '{pct}% રિકવર થયું',
  'agent.lockout.gauge.field.wallet': 'તમારું વોલેટ',
  'agent.lockout.gauge.field.deposit': 'તમારી ડિપોઝિટ',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'ફક્ત અનલોક',
  'agent.lockout.tier.justUnlock.tag': 'જોખમી',
  'agent.lockout.tier.justUnlock.note': 'તમને ₹0 પર પાછા લાવે છે — કોઈપણ નવો લોસ તરત જ ફરી લોક કરી દેશે.',
  'agent.lockout.tier.safeMin.label': 'સુરક્ષિત ન્યૂનતમ',
  'agent.lockout.tier.safeMin.tag': 'ભલામણ કરેલ',
  'agent.lockout.tier.safeMin.note': 'ડિપોઝિટ ({deposit}) + તમારા ઋણાત્મક વોલેટનો અડધો. એક વાસ્તવિક કવરેજ છોડે છે.',
  'agent.lockout.tier.fullRecovery.label': 'સંપૂર્ણ રિકવરી',
  'agent.lockout.tier.fullRecovery.tag': 'શ્રેષ્ઠ',
  'agent.lockout.tier.fullRecovery.note': 'ડિપોઝિટ સંપૂર્ણપણે પુનઃસ્થાપિત અને વોલેટ શૂન્ય પર પાછું લાવ્યું.',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'રિકવર થવાના બે રસ્તા — જે પણ (અથવા બંને) કરો',
  'agent.lockout.waysOut.inbox.title': 'તમારા ઇનબોક્સ પર પગલાં લો',
  'agent.lockout.waysOut.inbox.body': 'ખેલાડી રિચાર્જ મંજૂર કરો (પૈસા પહેલેથી તમારી પાસે છે) અને ખેલાડી ઉપાડને ચૂકવેલ તરીકે ચિહ્નિત કરો. દરેક મંજૂરી તમારા વોલેટને ક્રેડિટ કરે છે.',
  'agent.lockout.waysOut.inbox.ctaOpen': 'ઇનબોક્સ ખોલો',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ઇનબોક્સ નીચે ખુલ્લું છે',
  'agent.lockout.waysOut.upline.title': 'તમારા અપલાઇનને ચૂકવણી કરો',
  'agent.lockout.waysOut.upline.body': 'તમારા પેરન્ટ એજન્ટ (અથવા એડમિન) ને UPI દ્વારા પૈસા મોકલો, પછી ટ્રાન્ઝેક્શન ID સબમિટ કરો. એકવાર તેઓ મંજૂર કરે, તમારું વોલેટ તરત જ ક્રેડિટ થઈ જાય છે.',
  'agent.lockout.waysOut.upline.ctaOpen': 'ફોર્મ ખોલો',
  'agent.lockout.waysOut.upline.ctaOpened': 'ફોર્મ નીચે ખુલ્લું છે',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'તમે હજુ શું કરી શકો છો (અને શું બંધ છે)',
  'agent.lockout.capabilities.canHeader': 'તમે હજુ કરી શકો છો',
  'agent.lockout.capabilities.cantHeader': 'તમે (અસ્થાયી રૂપે) કરી શકતા નથી',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'આવનારા ખેલાડી રિચાર્જ મંજૂર કરવા',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'ખેલાડી ઉપાડને ચૂકવેલ તરીકે ચિહ્નિત કરવા',
  'agent.lockout.can.approveSubAgentRecharge': 'સબ-એજન્ટ રિચાર્જ મંજૂર કરો',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'સબ-એજન્ટ ઉપાડને ચૂકવેલ તરીકે ચિહ્નિત કરો',
  'agent.lockout.can.submitToUpline': 'તમારા અપલાઇનને રિચાર્જ સબમિટ કરો',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE એલર્ટ મેળવો + સાઇન આઉટ કરો',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'સ્ટેપ {n} · {title}',
  'agent.lockout.section.inbox.title': 'તમારું ઇનબોક્સ — દરેક મંજૂરી તમારા વોલેટમાં ક્રેડિટ કરે છે',
  'agent.lockout.section.inbox.subtitle': 'ખેલાડીઓ અને સબ-એજન્ટ તરફથી બાકી ચુકવણી વિનંતીઓ જે તમારી કાર્યવાહીની રાહ જોઈ રહી છે.',
  'agent.lockout.section.inbox.fromPlayers': 'તમારા ખેલાડીઓ તરફથી',
  'agent.lockout.section.inbox.fromSubAgents': 'તમારા સબ-એજન્ટ તરફથી',
  'agent.lockout.section.upline.title': 'વોલેટ ટોપ-અપ કરવા તમારા અપલાઇનને ચૂકવણી કરો',
  'agent.lockout.section.upline.subtitle': 'પેરન્ટનું UPI સામે હોય તો ઝડપથી થાય છે. મંજૂરી સામાન્ય રીતે થોડી મિનિટોમાં મળી જાય છે.',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'રિચાર્જ · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'ટ્રાન્ઝેક્શન {txn} · {when} સબમિટ કર્યું',
  'agent.lockout.queue.player.recharge.subtitleNote': 'ટ્રાન્ઝેક્શન {txn} · {when} સબમિટ કર્યું · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'ઉપાડ · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi} પર ચૂકવણી કરો · {when} સબમિટ કર્યું',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi} પર ચૂકવણી કરો · {whatsapp} · {when} સબમિટ કર્યું',
  'agent.lockout.queue.subAgent.recharge.title': 'સબ-એજન્ટ રિચાર્જ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'ટ્રાન્ઝેક્શન {txn} · {when} સબમિટ કર્યું',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'ટ્રાન્ઝેક્શન {txn} · {when} સબમિટ કર્યું · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'સબ-એજન્ટ ઉપાડ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi} પર ચૂકવણી કરો · {when} સબમિટ કર્યું',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi} પર ચૂકવણી કરો · {when} સબમિટ કર્યું · "{note}"',

  'agent.lockout.queue.action.approve': 'મંજૂર કરો',
  'agent.lockout.queue.action.reject': 'નકારો',
  'agent.lockout.queue.action.markPaid': 'ચૂકવેલ ચિહ્નિત કરો',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'ખેલાડીને ચૂકવણી માટે વાપરેલ UPI ટ્રાન્ઝેક્શન ID દાખલ કરો:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'સબ-એજન્ટને ચૂકવણી માટે વાપરેલ UPI ટ્રાન્ઝેક્શન ID દાખલ કરો:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'આ ઉપાડ નકારો? રોકાયેલ રકમ ખેલાડીને પરત આપવામાં આવશે.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'આ સબ-એજન્ટ ઉપાડ નકારો?',
  'agent.lockout.queue.toast.rechargeApproved': 'રિચાર્જ મંજૂર — ખેલાડીને ક્રેડિટ કર્યું',
  'agent.lockout.queue.toast.rechargeRejected': 'રિચાર્જ નકારાયું',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'સબ-એજન્ટ રિચાર્જ મંજૂર',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'રિચાર્જ નકારાયું',
  'agent.lockout.queue.toast.withdrawalPaid': 'ઉપાડ ચૂકવેલ ચિહ્નિત — તમારા વોલેટમાં ક્રેડિટ કરાયું',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'સબ-એજન્ટ ઉપાડ ચૂકવેલ ચિહ્નિત — વોલેટમાં ક્રેડિટ કરાયું',
  'agent.lockout.queue.toast.withdrawalRejected': 'ઉપાડ નકારાયું (ખેલાડીને પરત આપ્યું)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'ઉપાડ નકારાયું',
  'agent.lockout.queue.toast.actionFailed': 'કાર્યવાહી નિષ્ફળ',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'અત્યારે તમારા ખેલાડીઓ તરફથી કંઈ નથી.',
  'agent.lockout.queue.empty.subAgent': 'અત્યારે તમારા સબ-એજન્ટ તરફથી કંઈ નથી.',
  'agent.lockout.queue.loading': 'કતાર લોડ થઈ રહી છે…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'રિફ્રેશ કરો — શું હું હવે અનલોક છું?',
  'agent.lockout.refresh.checking': 'તપાસી રહ્યા છીએ…',
  'agent.lockout.refresh.toast': 'સ્થિતિ રિફ્રેશ થઈ',
  'agent.lockout.refresh.footnote': 'આ પેજ આપોઆપ બંધ થઈ જશે જે ક્ષણે તમારું વોલેટ + ડિપોઝિટ શૂન્યથી ઉપર પાછું ચઢે છે.',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'રિચાર્જ વિગતો લોડ થઈ શકી નથી',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'તમારું એકાઉન્ટ અનલોક કરવા હમણાં ચૂકવણી કરો',
  'agent.emergencyForm.banner.locked.body': 'તમારા ખેલાડીઓએ તમારી ડિપોઝિટ કોલેટરલ કવર કરી શકે તેનાથી વધુ જીત્યું છે. તમે તમારું ઋણાત્મક બેલેન્સ સાફ ન કરો ત્યાં સુધી પ્લેટફોર્મ વાપરી શકતા નથી.',
  'agent.emergencyForm.banner.warning.title': 'જલ્દી ટોપ-અપ કરો — તમે મર્યાદાની ખૂબ નજીક છો',
  'agent.emergencyForm.banner.warning.body': 'તમારું વોલેટ તમારી ડિપોઝિટના 80% ને પાર કરી ગયું છે. 100% સુધી પહોંચે તે પહેલાં ટોપ-અપ કરો — એક વાર પહોંચી જશે, તો તમારું એકાઉન્ટ લોક થઈ જશે.',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'તમારું વોલેટ',
  'agent.emergencyForm.row.deposit': 'તમારી ડિપોઝિટ',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'તમે કેમ ફસાયા છો',
  'agent.emergencyForm.tier.eyebrow.warning': 'તમે કેમ જોખમમાં છો',
  'agent.emergencyForm.tier.context.locked': 'તમારું વોલેટ {wallet} પર પહોંચ્યું — તે તમારી {deposit} ની સિક્યુરિટી ડિપોઝિટ કવરની બહાર છે. પ્લેટફોર્મ તમને અહીં લોક કરે છે જેથી તમારી ડાઉનલાઇનના નુકસાન ખાલી બફર પર વધુ ન વધે.',
  'agent.emergencyForm.tier.context.warning': 'તમારું વોલેટ {wallet} પર પહોંચ્યું — તે તમારી {deposit} ની સિક્યુરિટી ડિપોઝિટ કવરના 80% થી વધુ છે. એક વધુ ખરાબ સેટલમેન્ટ તમને 100% પાર કરીને એકાઉન્ટને લોક કરી દેશે.',
  'agent.emergencyForm.tier.pickHowMuch': 'પસંદ કરો કે કેટલું ચૂકવવું',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'ફક્ત અનલોક',
  'agent.emergencyForm.tier.bareMin.label.warning': 'ફક્ત ચેતવણીમાંથી બહાર',
  'agent.emergencyForm.tier.bareMin.tag': 'જોખમી',
  'agent.emergencyForm.tier.bareMin.note.locked': 'ફક્ત તમારા વોલેટ + ડિપોઝિટને ₹0 સુધી લાવે છે. કોઈપણ નવું નુકસાન તમને તરત જ ફરી લોક કરી દેશે.',
  'agent.emergencyForm.tier.bareMin.note.warning': 'ફક્ત તમને 80% લાઇનથી થોડું ઉપર લાવે છે. કોઈપણ નવું નુકસાન ફરી ચેતવણી આપશે — અથવા તમને લોકઆઉટમાં ધકેલશે.',
  'agent.emergencyForm.tier.safeMin.label': 'સુરક્ષિત ન્યૂનતમ',
  'agent.emergencyForm.tier.safeMin.tag': 'ભલામણ કરેલ',
  'agent.emergencyForm.tier.safeMin.note': 'તમારી ડિપોઝિટ ({deposit}) + તમારા ઋણાત્મક વોલેટનો અડધો ({half}). તમને એક વાસ્તવિક કુશન આપે છે.',
  'agent.emergencyForm.tier.full.label': 'સંપૂર્ણ રિકવરી',
  'agent.emergencyForm.tier.full.tag': 'શ્રેષ્ઠ',
  'agent.emergencyForm.tier.full.note': 'ડિપોઝિટ + તમારું પૂરું ઋણાત્મક વોલેટ ({neg}). ડિપોઝિટ સંપૂર્ણ પુનઃસ્થાપિત અને વોલેટ શૂન્ય પર.',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'તમારા એડમિનને ચૂકવણી કરો',
  'agent.emergencyForm.parent.label.parentAgent': 'તમારા પેરન્ટ એજન્ટને ચૂકવણી કરો',
  'agent.emergencyForm.parent.role.admin': 'પ્લેટફોર્મ એડમિન',
  'agent.emergencyForm.parent.role.parentAgent': 'પેરન્ટ એજન્ટ',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile': 'તમારા પેરન્ટની ચુકવણી વિગતો હાલ ફાઇલમાં નથી. કૃપા કરીને સીધા પ્લેટફોર્મ સપોર્ટનો સંપર્ક કરો.',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'ચૂકવેલ રકમ (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI ટ્રાન્ઝેક્શન ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'જેમ કે 412312345678',
  'agent.emergencyForm.field.note.label': 'નોંધ (વૈકલ્પિક)',
  'agent.emergencyForm.field.note.placeholder': 'પેરન્ટને કંઈક જણાવવું છે',
  'agent.emergencyForm.validation.amount': 'જે રકમ ચૂકવી છે તે દાખલ કરો (ન્યૂનતમ ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI ટ્રાન્ઝેક્શન ID / UTR દાખલ કરો (ઓછામાં ઓછા 6 અક્ષરો)',
  'agent.emergencyForm.cta': 'રિચાર્જ વિનંતી સબમિટ કરો',
  'agent.emergencyForm.ctaSubmitting': 'સબમિટ કરી રહ્યા છીએ…',
  // Success state.
  'agent.emergencyForm.success.title': 'રિચાર્જ સબમિટ કર્યું',
  'agent.emergencyForm.success.bodyAdmin': 'વિનંતી #{id} એડમિન દ્વારા મંજૂરીની રાહ જોઈ રહી છે. મંજૂર થયા પછી, તમારા વોલેટમાં ક્રેડિટ થશે અને તમારું એકાઉન્ટ આપોઆપ અનલોક થશે.',
  'agent.emergencyForm.success.bodyAgent': 'વિનંતી #{id} @{username} ની મંજૂરીની રાહ જોઈ રહી છે. મંજૂર થયા પછી, તમારા વોલેટમાં ક્રેડિટ થશે અને તમારું એકાઉન્ટ આપોઆપ અનલોક થશે.',
  'agent.emergencyForm.toast.submitted': 'રિચાર્જ વિનંતી #{id} સબમિટ થઈ. મંજૂરીની રાહ જોવાઈ રહી છે.',
  'agent.emergencyForm.toast.cantSave': 'ભાષા સાચવી શકાઈ નથી',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'એક-વારનું સેટઅપ: તમારું સ્થાન ખાતરી કરો',
  'agent.geo.subtitle': 'ડેશબોર્ડ વાપરતા પહેલાં જરૂરી.',
  'agent.geo.body.primary': 'બાલી ગેમ્સ પ્લેટફોર્મ નવા ખેલાડીઓને તેમની શારીરિક રીતે સૌથી નજીકના એજન્ટ પાસે મોકલે છે. જ્યાં સુધી તમે તમારું ચોક્કસ GPS સ્થાન શેર નહીં કરો, ત્યાં સુધી તમે લોકેટરમાં દેખાશો નહીં અને ઓર્ગેનિક સાઇનઅપ્સમાંથી શૂન્ય નવા ખેલાડીઓ મળશે — તમને માત્ર એવા ખેલાડીઓ મળશે જે સીધો તમારો રેફરલ કોડ ટાઇપ કરે.',
  'agent.geo.body.secondary': 'એક વાર "મારું વર્તમાન સ્થાન વાપરો" ટૅપ કરો. અમે ફક્ત તમારા કોઓર્ડિનેટ્સ કેપ્ચર કરીએ છીએ, બીજું કંઈ નહીં.',
  'agent.geo.hint.disabled': 'ઉપર "મારું વર્તમાન સ્થાન વાપરો" ટૅપ કરો. ફક્ત પિનકોડ અથવા મેન્યુઅલ વિસ્તાર પસંદગી પૂરતી નથી — લોકેટરને ચોક્કસ GPS કોઓર્ડિનેટ્સ જોઈએ છે.',
  'agent.geo.error.fallback': 'સ્થાન સાચવી શકાયું નથી. કૃપા કરીને ફરી પ્રયાસ કરો.',
  'agent.geo.cta.save': 'સાચવો અને ચાલુ રાખો',
  'agent.geo.cta.saving': 'સ્થાન સાચવી રહ્યા છીએ…',
  'agent.geo.toast.success': 'સ્થાન સાચવ્યું — હવે તમે ખેલાડીઓ મેળવી શકો છો.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'પૂલ બેલેન્સ',
  'agent.wallet.hero.helper': 'ખેલાડી રિચાર્જ અને સબ-એજન્ટ ટોપ-અપ માટે ઉપલબ્ધ. કમાણી અને એડમિન ટોપ-અપ અહીં આવે છે.',

  'agent.wallet.action.recharge': 'રિચાર્જ વિનંતી',
  'agent.wallet.action.withdraw': 'ઉપાડ વિનંતી',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'બાકી',
  'agent.wallet.tab.history': 'ઇતિહાસ',

  // Pending feed.
  'agent.wallet.pending.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.wallet.pending.empty': 'અત્યારે કોઈ બાકી વિનંતી નથી.',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'રિચાર્જ વિનંતી · +{amount}',
  'agent.wallet.pending.recharge.meta': 'ટ્રાન્ઝેક્શન {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'ઉપાડ વિનંતી · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi} પર · {when}',
  'agent.wallet.pending.badge': 'બાકી',

  // History feed.
  'agent.wallet.history.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.wallet.history.empty': 'હજુ સુધી કોઈ વોલેટ પ્રવૃત્તિ નથી.',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'રિચાર્જ મંજૂર · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'રિચાર્જ નકાર · {amount}',
  'agent.wallet.history.recharge.meta': 'ટ્રાન્ઝેક્શન {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'ઉપાડ ચૂકવાયો · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'ઉપાડ નકાર · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi} પર · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'ચૂકવાયું · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'મંજૂર',
  'agent.wallet.state.paid': 'ચૂકવાયું',
  'agent.wallet.state.rejected': 'નકાર',
  'agent.wallet.state.expired': 'સમાપ્ત',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'એડમિન તરફથી ટોપ-અપ',
  'agent.wallet.ledger.kind.topupToPlayer': 'ખેલાડીને ટોપ-અપ',
  'agent.wallet.ledger.kind.topupFromPlayer': 'ખેલાડીનો રિચાર્જ',
  'agent.wallet.ledger.kind.revenueShare': 'આવક હિસ્સો',
  'agent.wallet.ledger.kind.coFundOffer': 'સહ-ભંડોળ ઓફર',
  'agent.wallet.ledger.kind.adminAdjust': 'એડમિન સમાયોજન',
  'agent.wallet.ledger.kind.withdrawalHold': 'ઉપાડ હોલ્ડ',
  'agent.wallet.ledger.kind.withdrawalPaid': 'ઉપાડ ચૂકવાયો',
  'agent.wallet.ledger.kind.withdrawalRefund': 'ઉપાડ રિફંડ',
  'agent.wallet.ledger.balanceAfter': 'બેલેન્સ {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'સિક્યુરિટી ડિપોઝિટ',
  'agent.deposit.hero.helper': 'તમારા પૂલ સામે જામીન. જ્યાં સુધી તમારું વોલેટ સારી સ્થિતિમાં છે ત્યાં સુધી તે અકબંધ રહે છે.',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'સુરક્ષિત',
  'agent.deposit.margin.warning': 'ચેતવણી',
  'agent.deposit.margin.locked': 'લોક',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'ડિપોઝિટ રિચાર્જ',
  'agent.deposit.action.withdraw': 'ડિપોઝિટ ઉપાડ',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'કવરેજ ગેજ',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'ચેતવણી',
  'agent.deposit.gauge.thresholdLock': 'લોક',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct} પર તમને ચેતવણી મળશે',
  'agent.deposit.gauge.legend.lock': '{pct} પર, તમારું એકાઉન્ટ લોક થાય છે',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'પૂલ ડિપોઝિટ દ્વારા સંપૂર્ણપણે કવર — જ્યાં સુધી તમે ટોપ-અપ ન કરો ત્યાં સુધી ઉપાડ બ્લોક છે.',
  'agent.deposit.gauge.caption.warning': 'પૂલે તમારી ડિપોઝિટના {pct}% વાપરી લીધા છે. લોકઆઉટ ટાળવા માટે 100% પહોંચે તે પહેલાં ટોપ-અપ કરો.',
  'agent.deposit.gauge.caption.caution': 'પૂલ ડિપોઝિટ કવરેજમાં {pct}% સુધી છે. તમે હજુ પણ 80% ચેતવણી મર્યાદાથી સુરક્ષિત રીતે નીચે છો.',
  'agent.deposit.gauge.caption.safe': 'તમારી ડિપોઝિટ સંપૂર્ણપણે અકબંધ છે.',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.deposit.ledger.empty.title': 'હજુ સુધી કોઈ ડિપોઝિટ પ્રવૃત્તિ નથી',
  'agent.deposit.ledger.empty.body': 'જ્યારે એડમિન તમારી ડિપોઝિટ ક્રેડિટ કરે (અથવા રિફંડ પ્રોસેસ કરે) ત્યારે દરેક એન્ટ્રી અહીં દેખાશે.',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'તમારું વોલેટ રિચાર્જ કરો',
  'agent.recharge.banner.eyebrow.deposit': 'તમારી ડિપોઝિટ રિચાર્જ કરો',
  'agent.recharge.banner.body.wallet': 'પહેલા તમારા અપલાઇનને ચૂકવણી કરો, પછી પુરાવો અહીં સબમિટ કરો જેથી તેઓ તમારા ઓપરેટિંગ વોલેટને ટોપ-અપ કરી શકે.',
  'agent.recharge.banner.body.deposit': 'પહેલા તમારા અપલાઇનને ચૂકવણી કરો, પછી પુરાવો અહીં સબમિટ કરો જેથી તેઓ તમારી સિક્યુરિટી ડિપોઝિટને ટોપ-અપ કરી શકે.',
  'agent.recharge.banner.closeAria': 'ફોર્મ બંધ કરો',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'તમારા એડમિનને ચૂકવણી કરો',
  'agent.recharge.parent.label.parentAgent': 'તમારા પેરન્ટ એજન્ટને ચૂકવણી કરો',
  'agent.recharge.parent.role.admin': 'પ્લેટફોર્મ એડમિન',
  'agent.recharge.parent.role.parentAgent': 'પેરન્ટ એજન્ટ',
  'agent.recharge.parent.noQr': 'કોઈ QR સેટ નથી',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'તમારા અપલાઇનની ચૂકવણી વિગતો લોડ થઈ રહી છે…',
  'agent.recharge.parent.loadError': 'તમારા અપલાઇનની ચૂકવણી વિગતો લોડ થઈ શકી નથી',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'તમે કેવી રીતે ચૂકવણી કરી?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ઓનલાઇન ડિજિટલ ચૂકવણી',
  'agent.recharge.method.cash.label': 'રોકડ',
  'agent.recharge.method.cash.note': 'રૂબરૂ હસ્તાંતરણ',

  // Form fields.
  'agent.recharge.field.amount.label': 'રકમ (₹) · ન્યૂનતમ ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI ટ્રાન્ઝેક્શન ID / UTR',
  'agent.recharge.field.txn.label.cash': 'રોકડ રસીદ માર્કર (કોઈ ટૂંકો સંદર્ભ)',
  'agent.recharge.field.txn.placeholder.upi': 'દા.ત. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'દા.ત. CASH-Sun-eve',
  'agent.recharge.field.note.label': 'નોંધ (વૈકલ્પિક, તમારા અપલાઇનને દેખાશે)',
  'agent.recharge.field.note.placeholder': 'તમારા અપલાઇન માટે કોઈ સંદર્ભ',

  // Validation errors.
  'agent.recharge.error.amountMin': 'રકમ આખા રૂપિયામાં ≥ 10 હોવી જોઈએ',
  'agent.recharge.error.txnUpi': 'ટ્રાન્ઝેક્શન ID ખૂબ ટૂંકી લાગે છે — UPI સંદર્ભ પેસ્ટ કરો',
  'agent.recharge.error.txnCash': 'એક ટૂંકો માર્કર ઉમેરો (દા.ત. "CASH-Sun-eve") જેથી તમારો અપલાઇન હસ્તાંતરણ મેળવી શકે',

  // CTAs.
  'agent.recharge.cta.submit': 'વિનંતી સબમિટ કરો',
  'agent.recharge.cta.cancel': 'રદ કરો',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'વોલેટમાંથી ઉપાડો',
  'agent.withdrawal.banner.eyebrow.deposit': 'ડિપોઝિટમાંથી ઉપાડો',
  'agent.withdrawal.banner.body.wallet': 'રકમ તરત જ રોકી લેવામાં આવે છે જેથી તે બીજે ક્યાંય ખર્ચ ન થાય. તમારો અપલાઇન ચૂકવણી કરે છે અને ચૂકવણી નોંધે છે.',
  'agent.withdrawal.banner.body.deposit': 'રકમ તમારી સિક્યુરિટી ડિપોઝિટમાંથી તરત જ રોકી લેવામાં આવે છે. તમારો અપલાઇન તમને ચૂકવણી કરે છે અને તેને ચૂકવાયેલ તરીકે ચિહ્નિત કરે છે.',
  'agent.withdrawal.banner.closeAria': 'ફોર્મ બંધ કરો',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'તમે કેવી રીતે ચૂકવણી મેળવવા માંગો છો?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ઓનલાઇન ડિજિટલ ચૂકવણી',
  'agent.withdrawal.method.cash.label': 'રોકડ',
  'agent.withdrawal.method.cash.note': 'રૂબરૂ હસ્તાંતરણ',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'રકમ (₹) · ઉપલબ્ધ {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'તમારી UPI ID (જ્યાં તમારો અપલાઇન તમને ચૂકવણી કરશે)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'મીટિંગ માર્કર (દા.ત. "રવિવાર સાંજે રોકડ")',
  'agent.withdrawal.field.cashMarker.placeholder': 'રોકડ ક્યારે/ક્યાં આપવાની છે',
  'agent.withdrawal.field.whatsapp.label': 'WhatsApp (વૈકલ્પિક, જેથી તમારી અપલાઇન તમારો સંપર્ક કરી શકે)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'નોંધ (વૈકલ્પિક)',
  'agent.withdrawal.field.note.placeholder': 'તમારી અપલાઇન માટે કોઈ સંદર્ભ',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'રકમ આખા રૂપિયામાં ≥ 10 હોવી જોઈએ',
  'agent.withdrawal.error.amountOverflow': 'તમારી પાસે ફક્ત {balance} ઉપલબ્ધ છે',
  'agent.withdrawal.error.upi': 'માન્ય UPI ID દાખલ કરો',

  // CTAs.
  'agent.withdrawal.cta.submit': 'વિનંતી મોકલો',
  'agent.withdrawal.cta.cancel': 'રદ કરો',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'ખેલાડીઓ',
  'agent.people.hub.players.subtitle': 'ખેલાડીઓ અને તેમની વિનંતીઓનું સંચાલન કરો',
  'agent.people.hub.players.subtab.requests': 'ખેલાડી વિનંતીઓ',
  'agent.people.hub.players.subtab.list': 'ખેલાડીઓ',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'એજન્ટો',
  'agent.people.hub.agents.subtitle': 'સબ-એજન્ટો અને તેમની વિનંતીઓનું સંચાલન કરો',
  'agent.people.hub.agents.subtab.requests': 'એજન્ટ વિનંતીઓ',
  'agent.people.hub.agents.subtab.list': 'એજન્ટો',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'નવા ખેલાડીઓ મેળવી રહ્યા છો',
  'agent.people.acceptingToggle.bodyAccepting': 'તમે ભૌગોલિક લોકેટરથી નવા ખેલાડીઓ સ્વીકારી રહ્યા છો. તમારા વિસ્તારમાં કોલ્ડ સાઇનઅપ આપમેળે તમારી પાસે આવે છે.',
  'agent.people.acceptingToggle.bodyOptedOut': 'તમે ઓપ્ટ-આઉટ કરી દીધું છે. લોકેટર તમને છોડી રહ્યું છે — ફક્ત સીધા રેફરલ અને મેન્યુઅલ રીતે અસાઇન કરેલા ખેલાડીઓ જ તમારા સુધી પહોંચશે. હાલના ખેલાડીઓ પર અસર નહીં થાય.',
  'agent.people.acceptingToggle.error': 'અપડેટ થઈ શક્યું નહીં',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'નવા ખેલાડીઓ લેવાનું બંધ કરો',
  'agent.people.acceptingToggle.aria.start': 'નવા ખેલાડીઓ લેવાનું શરૂ કરો',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'ખેલાડીઓ',
  'agent.qr.audience.subAgent': 'એજન્ટો',
  'agent.qr.alt': 'તમારો ચુકવણી QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'મારો QR · ખેલાડીઓ જે જુએ છે',
  'agent.qr.cardLabel.subAgent': 'મારો QR · એજન્ટો જે જુએ છે',
  'agent.qr.upi.empty': 'હજુ સુધી કોઈ UPI સેટ નથી',
  // Status pill.
  'agent.qr.status.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.qr.status.live': 'લાઇવ',
  'agent.qr.status.notSet': 'સેટ નથી',
  'agent.qr.status.paused': 'થોભાવેલું',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR અને UPI સંપાદિત કરો',
  'agent.qr.cta.setup': 'QR સેટ અપ કરો',
  'agent.qr.cta.viewOnly': 'ફક્ત જુઓ — સંપાદન માટે એડમિનને કહો',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR અને UPI સંપાદિત કરો',
  'agent.qr.modal.closeAria': 'બંધ કરો',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR અપલોડ કરો',
  'agent.qr.modal.replace': 'QR બદલો',
  'agent.qr.modal.remove': 'દૂર કરો',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR છબી દૂર કરવી છે? તમારા ખેલાડીઓ ફક્ત સંપર્ક ફોલબેક જ જોશે.',
  'agent.qr.modal.confirmRemove.subAgent': 'QR છબી દૂર કરવી છે? તમારા એજન્ટો ફક્ત સંપર્ક ફોલબેક જ જોશે.',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'દા.ત. baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'WhatsApp નંબર (ફોલબેક)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'એજન્ટો માટે WhatsApp નંબર',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'ખાલી → {number} પર પાછું આવશે',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'સબ-એજન્ટો {number} જોશે (તમારો ખેલાડી-મુખી નંબર).',
  'agent.qr.modal.field.phone.label.player': 'સહાય ફોન (ફોલબેક)',
  'agent.qr.modal.field.phone.label.subAgent': 'એજન્ટો માટે સહાય ફોન',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'ખાલી → {number} પર પાછું આવશે',
  'agent.qr.modal.field.phone.fallbackHint': 'સબ-એજન્ટો {number} જોશે (તમારો ખેલાડી-મુખી નંબર).',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'સક્રિય',
  'agent.qr.modal.active.description': 'ખેલાડીઓ રિચાર્જ સબમિટ કરી શકે છે',
  'agent.qr.modal.active.on': 'ચાલુ',
  'agent.qr.modal.active.paused': 'થોભાવેલું',

  // Footer buttons.
  'agent.qr.modal.cancel': 'રદ કરો',
  'agent.qr.modal.save': 'સાચવો',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'મેન્યુઅલ રિચાર્જ',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'સીધા તમારા પૂલમાંથી ',
  'agent.manualRecharge.banner.body.target': 'ખેલાડી',
  'agent.manualRecharge.banner.body.after': 'ને પૈસા મોકલો.',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'તમારો પૂલ',
  'agent.manualRecharge.pool.dailyCap': 'મર્યાદા · {amount}/દિવસ',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'કયા ખેલાડીને',
  'agent.manualRecharge.field.player.placeholder': 'એક ખેલાડી પસંદ કરો…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · બેલ {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'કયા ખેલાડીને',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · વર્તમાન બેલ {balance}',
  'agent.manualRecharge.field.player.notFound': 'તમારી માલિકીની યાદીમાં ખેલાડી મળ્યો નથી.',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'રકમ (₹) — {min} થી {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'તમારા પૂલ બેલેન્સ કરતાં વધારે છે.',
  'agent.manualRecharge.error.belowMin': 'ન્યૂનતમ કરતાં ઓછું ({min}).',
  'agent.manualRecharge.error.aboveMax': 'મહત્તમ કરતાં વધારે ({max}).',

  // Note field.
  'agent.manualRecharge.field.note.label': 'નોંધ (વૈકલ્પિક, ખેલાડી લેજર પર દેખાશે)',
  'agent.manualRecharge.field.note.placeholder': 'દા.ત. દુકાન પર રોકડ લીધી',

  // Loading + success states.
  'agent.manualRecharge.loading': 'તમારો પૂલ લોડ થઈ રહ્યો છે…',
  'agent.manualRecharge.cta.cancel': 'રદ કરો',
  'agent.manualRecharge.cta.send': '{amount} મોકલો',
  'agent.manualRecharge.success.title': '@{username} ને {amount} મોકલ્યા',
  'agent.manualRecharge.success.meta': 'તમારો પૂલ · {pool} · તેમનો વોલેટ · {playerBal}',
  'agent.manualRecharge.success.again': 'બીજો મોકલો',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'મેન્યુઅલ રિચાર્જ',
  'agent.manualSubAgentRecharge.banner.body.before': 'સીધા તમારા પૂલમાંથી ',
  'agent.manualSubAgentRecharge.banner.body.target': 'સબ-એજન્ટ',
  'agent.manualSubAgentRecharge.banner.body.after': 'ને પૈસા મોકલો.',

  'agent.manualSubAgentRecharge.pool.label': 'તમારો પૂલ',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'કયા સબ-એજન્ટને',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'એક સબ-એજન્ટ પસંદ કરો…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · પૂલ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'કોઈ સક્રિય સીધો સબ-એજન્ટ નથી',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'કયા સબ-એજન્ટને',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · વર્તમાન પૂલ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'તમારી સીધી ડાઉનલાઇનમાં સબ-એજન્ટ મળ્યો નથી.',

  'agent.manualSubAgentRecharge.field.amount.label': 'રકમ (₹) — {min} થી {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'તમારા પૂલ બેલેન્સ કરતાં વધારે છે.',
  'agent.manualSubAgentRecharge.error.belowMin': 'ન્યૂનતમ કરતાં ઓછું ({min}).',

  'agent.manualSubAgentRecharge.field.note.label': 'નોંધ (વૈકલ્પિક, બંને લેજર પર બતાવાશે)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'દા.ત. ફ્લોટ એડવાન્સ',

  'agent.manualSubAgentRecharge.loading': 'તમારો પૂલ લોડ થઈ રહ્યો છે…',
  'agent.manualSubAgentRecharge.cta.cancel': 'રદ કરો',
  'agent.manualSubAgentRecharge.cta.send': '{amount} મોકલો',
  'agent.manualSubAgentRecharge.success.title': '@{username} ને {amount} મોકલ્યા',
  'agent.manualSubAgentRecharge.success.meta': 'તમારો પૂલ · {parent} · તેમનો પૂલ · {child}',
  'agent.manualSubAgentRecharge.success.again': 'બીજું મોકલો',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'રિચાર્જ',
  'agent.playerRequests.direction.withdrawal': 'ઉપાડ',
  'agent.playerRequests.tab.pending': 'બાકી',
  'agent.playerRequests.tab.history': 'ઇતિહાસ',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.playerRequests.empty.pending.recharge': 'અત્યારે કોઈ બાકી રિચાર્જ નથી.',
  'agent.playerRequests.empty.pending.withdrawal': 'અત્યારે કોઈ બાકી ઉપાડ નથી.',
  'agent.playerRequests.empty.history': 'હજુ સુધી કોઈ ઇતિહાસ નથી.',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'નકારવાનું કારણ (વૈકલ્પિક):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'નકારવાનું કારણ (ખેલાડીની રોકેલી રકમ પરત કરવામાં આવશે):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'નકારેલ',
  'agent.playerRequests.toast.rejectedRefunded': 'નકારેલ · રોકેલું બેલેન્સ પરત કરવામાં આવ્યું',
  'agent.playerRequests.toast.approved': '{amount} મંજૂર',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} મંજૂર · મૂળ + ₹{bonus} બોનસ',
  'agent.playerRequests.toast.markedPaid': '{amount} ચૂકવેલ તરીકે નોંધાયું',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel': 'ને ચૂકવો (UPI)',
  'agent.playerRequests.card.whatsapp': 'WhatsApp',
  'agent.playerRequests.card.viewQr': 'QR જુઓ',
  'agent.playerRequests.card.bonusChip': 'બોનસ',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'વિનંતી કરેલ {requested} માંથી {approved} મંજૂર',
  'agent.playerRequests.card.partialPaid': 'વિનંતી કરેલ {requested} માંથી {approved} ચૂકવેલ · પરત {refund}',
  'agent.playerRequests.card.paidTxnPrefix': 'ચૂકવેલ · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'નકારો',
  'agent.playerRequests.action.approve': 'મંજૂર કરો',
  'agent.playerRequests.action.markPaid': 'ચૂકવેલ તરીકે ચિહ્નિત કરો',
  'agent.playerRequests.state.pending': 'બાકી',
  'agent.playerRequests.state.rejected': 'નકારેલ',
  'agent.playerRequests.state.expired': 'સમાપ્ત',
  'agent.playerRequests.state.accepted': 'સ્વીકારેલ',
  'agent.playerRequests.state.manuallyPaid': 'મેન્યુઅલી ચૂકવેલ',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': '{total} માંથી {from}–{to}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'રિચાર્જ',
  'agent.agentRequests.direction.withdrawal': 'ઉપાડ',
  'agent.agentRequests.tab.pending': 'બાકી',
  'agent.agentRequests.tab.history': 'ઇતિહાસ',
  'agent.agentRequests.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.agentRequests.empty.pending.recharge': 'અત્યારે કોઈ બાકી રિચાર્જ નથી.',
  'agent.agentRequests.empty.pending.withdrawal': 'અત્યારે કોઈ બાકી ઉપાડ નથી.',
  'agent.agentRequests.empty.history': 'હજુ સુધી કોઈ ઇતિહાસ નથી.',

  'agent.agentRequests.prompt.rejectRecharge': 'નકારવાનું કારણ (વૈકલ્પિક):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'નકારવાનું કારણ (સબ-એજન્ટની રોકેલી રકમ પરત કરવામાં આવશે):',

  'agent.agentRequests.toast.rejected': 'નકારેલ',
  'agent.agentRequests.toast.rejectedRefunded': 'નકારેલ · રોકેલું બેલેન્સ પરત કરવામાં આવ્યું',
  'agent.agentRequests.toast.approved': '{amount} મંજૂર · પૂલમાં ક્રેડિટ થયું',
  'agent.agentRequests.toast.markedPaid': '{amount} ચૂકવેલ તરીકે નોંધાયું',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'એજન્ટ',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'રોકડ માર્કર',
  'agent.agentRequests.card.payToUpiLabel': 'ને ચૂકવો (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'હેન્ડ-ઑફ માર્કર',
  'agent.agentRequests.card.whatsapp': 'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved': 'વિનંતી કરેલ {requested} માંથી {approved} મંજૂર',
  'agent.agentRequests.card.partialPaid': 'વિનંતી કરેલ {requested} માંથી {approved} ચૂકવેલ · પરત {refund}',
  'agent.agentRequests.card.paidTxnPrefix': 'ચૂકવેલ · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'ડિપોઝિટ',
  'agent.agentRequests.card.chip.cash': 'રોકડ',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'નકારો',
  'agent.agentRequests.action.approve': 'મંજૂર કરો',
  'agent.agentRequests.action.markPaid': 'ચૂકવેલ તરીકે ચિહ્નિત કરો',
  'agent.agentRequests.state.pending': 'બાકી',
  'agent.agentRequests.state.rejected': 'નકારેલ',
  'agent.agentRequests.state.expired': 'સમાપ્ત',
  'agent.agentRequests.state.accepted': 'સ્વીકારેલ',
  'agent.agentRequests.state.manuallyPaid': 'મેન્યુઅલી ચૂકવેલ',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'ખેલાડી ઉમેરો',
  'agent.playersList.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.playersList.empty': 'કોઈ ખેલાડી મળતો નથી',
  'agent.playersList.results': '{total} માંથી {from}–{to} બતાવી રહ્યા છીએ',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'યુઝરનેમ, ઈમેલ, ફોન શોધો…',

  // Status filter pills.
  'agent.playersList.filter.all': 'બધા',
  'agent.playersList.filter.active': 'સક્રિય',
  'agent.playersList.filter.pending': 'બાકી',
  'agent.playersList.filter.suspended': 'સસ્પેન્ડ',
  'agent.playersList.filter.banned': 'પ્રતિબંધિત',

  // Empty-results panel.
  'agent.playersList.empty.title': 'કોઈ ખેલાડી મળ્યો નથી',
  'agent.playersList.empty.body': 'બીજી શોધ અથવા સ્ટેટસ ફિલ્ટર અજમાવો.',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'પેજ {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'સક્રિય',
  'agent.playersList.card.status.pending': 'બાકી',
  'agent.playersList.card.status.suspended': 'સસ્પેન્ડ',
  'agent.playersList.card.status.banned': 'પ્રતિબંધિત',
  'agent.playersList.card.verifiedBadge': 'ચકાસાયેલ',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'વોલેટ',
  'agent.playersList.column.joined': 'જોડાયા',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'તમે જે એજન્ટ બનાવો છો તેઓ તમારી નીચે કામ કરે છે. તેઓ દરેક પિલર પર 0% થી શરૂ થાય છે — તેમના વિગત પેજ પરથી પ્રતિ-ગેમ દરો આપો.',
  'agent.subAgentsList.cta.new': 'નવો સબ-એજન્ટ',
  'agent.subAgentsList.loading': 'લોડ થઈ રહ્યું છે…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'હજુ સુધી કોઈ સબ-એજન્ટ નથી',
  'agent.subAgentsList.empty.body.canCreate': 'એક બનાવવા માટે "+ નવો સબ-એજન્ટ" પર ટેપ કરો. તેઓ અહીં તેમના પોતાના પૂલ, ખેલાડીઓ અને કમાણી સાથે દેખાશે — તમારા દ્વારા સંચાલિત.',
  'agent.subAgentsList.empty.body.cantCreate': 'તમારા એડમિને હજુ સુધી તમને સબ-એજન્ટ બનાવવાનો અધિકાર આપ્યો નથી. તેમને કહો કે તેઓ તમારી પરવાનગીઓમાં create_subagent ને "edit" પર સેટ કરે.',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'સસ્પેન્ડ',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'એજન્ટ્સ',
  'agent.subAgentsList.column.players': 'ખેલાડીઓ',
  'agent.subAgentsList.column.pool': 'પૂલ',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'સીધો',
  'agent.subAgentsList.row.indirectLabel': 'પરોક્ષ',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'નવો સબ-એજન્ટ',
  'agent.subAgentsList.modal.body': 'તેઓ દરેક પિલર પર 0% થી શરૂ થશે. બનાવ્યા પછી તેમના વિગતો પેજ પરથી દરેક ગેમ માટે દરો આપો.',
  'agent.subAgentsList.modal.field.username.label': 'યુઝરનેમ',
  'agent.subAgentsList.modal.field.username.placeholder': 'અક્ષર-અંક, ._- મંજૂર; ઓછામાં ઓછા 3 અક્ષર',
  'agent.subAgentsList.modal.field.email.label': 'ઇમેઇલ',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'પ્રારંભિક પાસવર્ડ',
  'agent.subAgentsList.modal.field.password.placeholder': 'ઓછામાં ઓછા 8 અક્ષર',
  'agent.subAgentsList.modal.field.password.hint': 'આ નવા એજન્ટ સાથે શેર કરો જેથી તેઓ લોગિન કરી શકે. તેમણે પહેલા લોગિન પર આ બદલવો જોઈએ.',
  'agent.subAgentsList.modal.field.location.label': 'સબ-એજન્ટનું સ્થાન',
  'agent.subAgentsList.modal.field.location.optional': '(ખૂબ ભલામણ કરેલ)',
  'agent.subAgentsList.modal.field.location.missing': 'સ્થાન વગર ભૌગોલિક લોકેટર આ સબ-એજન્ટને કોઈ નવો ખેલાડી મોકલી શકશે નહીં. તેમને માત્ર સીધા રેફરલથી જ ખેલાડીઓ મળશે જ્યાં સુધી તેઓ સાઇન ઇન કરીને પોતાનું GPS પૂરું ન કરે.',
  'agent.subAgentsList.modal.cta.cancel': 'રદ કરો',
  'agent.subAgentsList.modal.cta.create': 'સબ-એજન્ટ બનાવો',
  'agent.subAgentsList.modal.cta.creating': 'બનાવી રહ્યા છીએ…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who} તરફથી રિચાર્જ મંજૂર કરો',
  'agent.approvalEdit.title.withdrawal': '{who} ની ઉપાડ ચૂકવેલ માર્ક કરો',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'વિનંતી કરેલ:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'મંજૂર રકમ (₹)',
  'agent.approvalEdit.field.amount.hint': 'વિનંતી કરેલ {amount} થી વધુ ન હોઈ શકે.',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} આપમેળે {who} ના વોલેટમાં પાછા જમા થશે.',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'આંશિક મંજૂરી. કોઈ પણ જોડાયેલ બોનસ આપમેળે નકારવામાં આવશે.',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'કારણ',
  'agent.approvalEdit.field.reason.requiredHint': '(આવશ્યક, ≥5 અક્ષર)',
  'agent.approvalEdit.field.reason.optionalHint': '(વૈકલ્પિક)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'દા.ત. ખેલાડીએ માત્ર 30 રૂપિયા ચૂકવ્યા',
  'agent.approvalEdit.field.reason.placeholder.optional': 'વૈકલ્પિક નોંધ',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI ટ્રાન્ઝેક્શન ID',
  'agent.approvalEdit.field.txn.placeholder': 'દા.ત. 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'તમારો પૂલ ({balance}) આ મંજૂરી કવર કરી શકતો નથી. પહેલા ટોપ-અપ કરો.',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'મંજૂર રકમ ઓછામાં ઓછી ₹1 હોવી જોઈએ',
  'agent.approvalEdit.error.amountMax': 'મંજૂર રકમ વિનંતી કરેલ {amount} થી વધુ ન હોઈ શકે',
  'agent.approvalEdit.error.reasonRequired': 'અલગ રકમ મંજૂર કરતી વખતે કારણ (≥5 અક્ષર) આવશ્યક છે',
  'agent.approvalEdit.error.txnRequired': 'UPI ટ્રાન્ઝેક્શન ID આવશ્યક છે (≥6 અક્ષર)',
  'agent.approvalEdit.error.fallback': 'નિષ્ફળ — ફરી પ્રયત્ન કરો',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'રદ કરો',
  'agent.approvalEdit.cta.approve': 'મંજૂર કરો',
  'agent.approvalEdit.cta.markPaid': 'ચૂકવેલ માર્ક કરો',
  'agent.approvalEdit.cta.submitting': 'મોકલી રહ્યા છીએ…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'મારું એકાઉન્ટ',
  'agent.profile.hub.subtitle': 'ઓળખ, વોલેટ અને ડિપોઝિટ એક જ જગ્યાએ',
  'agent.profile.hub.tab.wallet': 'વોલેટ',
  'agent.profile.hub.tab.deposit': 'ડિપોઝિટ',
  'agent.profile.hub.tab.account': 'એકાઉન્ટ',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'સાઇન આઉટ',
  'agent.profile.signOut.modal.title': 'સાઇન આઉટ?',
  'agent.profile.signOut.modal.body': 'એકાઉન્ટ ઍક્સેસ કરવા માટે તમારે ફરી સાઇન ઇન કરવું પડશે.',
  'agent.profile.signOut.modal.stay': 'સાઇન ઇન રહો',
  'agent.profile.signOut.modal.confirm': 'હા, સાઇન આઉટ',
  'agent.profile.signOut.modal.busy': 'સાઇન આઉટ થઈ રહ્યું છે…',
  'agent.profile.signOut.error': 'સાઇન આઉટ થઈ શક્યું નહીં',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'સાઇન ઇન નથી.',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'એજન્ટ',
  'agent.account.hero.role.admin': 'પ્લેટફોર્મ એડમિન',
  'agent.account.hero.changePhoto': 'ફોટો બદલો',
  'agent.account.hero.removePhoto': 'ફોટો હટાવો',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'તમારો ફોટો ગોઠવો',
  'agent.account.avatar.confirmRemove': 'તમારો પ્રોફાઇલ ફોટો હટાવવો છે?',
  'agent.account.toast.photoUpdated': 'ફોટો અપડેટ થયો',
  'agent.account.toast.photoRemoved': 'ફોટો હટાવ્યો',
  'agent.account.toast.saved': 'સેવ થયું',
  'agent.account.toast.locationSaved': 'સ્થાન સેવ થયું',
  'agent.account.toast.passwordChanged': 'પાસવર્ડ બદલાયો — ફરી સાઇન ઇન કરો',
  'agent.account.error.uploadFailed': 'અપલોડ નિષ્ફળ',
  'agent.account.error.removeFailed': 'નિષ્ફળ',
  'agent.account.error.saveFailed': 'સેવ નિષ્ફળ',
  'agent.account.error.changeFailed': 'બદલવું નિષ્ફળ',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'એકાઉન્ટ માહિતી',
  'agent.account.info.subtitle': 'માત્ર વાંચવા યોગ્ય પ્લેટફોર્મ મેટાડેટા',
  'agent.account.info.memberSince': 'સભ્ય બન્યા ત્યારથી',
  'agent.account.info.lastLogin': 'છેલ્લો લોગિન',
  'agent.account.info.lastLoginIp': 'છેલ્લો લોગિન IP',
  'agent.account.info.reportsTo': 'કોને રિપોર્ટ કરો છો',
  'agent.account.info.reportsToValue': 'એડમિન #{id}',
  'agent.account.info.agentId': 'એજન્ટ ID',
  'agent.account.info.firstLogin': 'પહેલી વાર',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'વ્યક્તિગત વિગતો',
  'agent.account.personal.subtitle.locked': 'એડમિન દ્વારા ઓળખ લોક · માત્ર જુઓ',
  'agent.account.personal.subtitle.agent': 'યુઝરનેમ, ઇમેઇલ અને સંપર્ક નંબર',
  'agent.account.personal.subtitle.admin': 'યુઝરનેમ અને ઇમેઇલ',
  'agent.account.personal.field.username': 'યુઝરનેમ',
  'agent.account.personal.field.email': 'ઇમેઇલ',
  'agent.account.personal.field.phone': 'સંપર્ક નંબર',
  'agent.account.personal.field.phone.placeholder': 'દા.ત. +91 9876543210',
  'agent.account.personal.field.phone.hint': 'ખેલાડીઓ અને એડમિન આ નંબર પર તમારો સંપર્ક કરે છે. ખાલી કરવા માટે ખાલી છોડો.',
  'agent.account.personal.fieldLockedNote': 'લોક',
  'agent.account.personal.cta.save': 'ફેરફારો સેવ કરો',
  'agent.account.personal.cta.saving': 'સેવ કરી રહ્યા છીએ…',
  'agent.account.personal.cta.noChanges': 'કોઈ ફેરફાર નહીં',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'તમારું સ્થાન',
  'agent.account.location.subtitle.summary': 'લોકેટર નવા ખેલાડીઓને તમારા સૌથી નજીકના વિસ્તાર પર મોકલે છે',
  'agent.account.location.subtitle.edit': 'તમારો વિસ્તાર અપડેટ કરો જેથી લોકેટર સાચા ખેલાડીઓ મોકલે',
  'agent.account.location.cta.edit': 'સંપાદિત કરો',
  'agent.account.location.cta.cancel': 'રદ કરો',
  'agent.account.location.cta.save': 'સેવ કરો',
  'agent.account.location.areaLabel': 'વિસ્તાર',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'સ્થાન સેટ છે',
  'agent.account.location.setBody': 'ફાઇલ પર GPS કોઓર્ડિનેટ્સ — વિસ્તાર વિગતો જોવા સંપાદિત કરો ટૅપ કરો.',
  'agent.account.location.lastUpdated': 'છેલ્લે અપડેટ · {date}',

  // Security card (password change).
  'agent.account.security.title': 'સુરક્ષા',
  'agent.account.security.subtitle.locked': 'એડમિન દ્વારા લોક · માત્ર જુઓ',
  'agent.account.security.subtitle.unlocked': 'તમારો પાસવર્ડ બદલો · અન્ય ડિવાઇસથી સાઇન આઉટ થઈ જશે',
  'agent.account.security.field.current': 'વર્તમાન પાસવર્ડ',
  'agent.account.security.field.new': 'નવો પાસવર્ડ',
  'agent.account.security.field.new.hint': 'ઓછામાં ઓછા 8 અક્ષર, એક અક્ષર અને એક અંક સાથે',
  'agent.account.security.field.confirm': 'નવાની પુષ્ટિ કરો',
  'agent.account.security.cta.update': 'પાસવર્ડ અપડેટ કરો',
  'agent.account.security.cta.updating': 'અપડેટ થઈ રહ્યું છે…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'નવા પાસવર્ડ મેળ ખાતા નથી',
  'agent.account.security.error.tooShort': 'પાસવર્ડ ≥ 8 અક્ષરનો હોવો જોઈએ',
  'agent.account.security.error.weak': 'પાસવર્ડમાં ઓછામાં ઓછો એક અક્ષર અને એક અંક હોવો જોઈએ',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'શેર કરી શકાય તેવી એજન્ટ ID',
  'agent.shareableId.tooltip.copy': 'UAID કોપી કરો',
  'agent.shareableId.tooltip.copied': 'UAID કોપી થયું',
  'agent.shareableId.cta.copy': 'કોપી',
  'agent.shareableId.cta.copied': 'કોપી થયું!',
  'agent.shareableId.body.before': 'આ કોડ બીજા એજન્ટ સાથે શેર કરો જેથી તેઓ ખેલાડીઓ તમને ટ્રાન્સફર કરી શકે. ચેટ કે WhatsApp થી મોકલવો સુરક્ષિત છે — તેને શેર કરવાથી તમારા એકાઉન્ટ પર કોઈ ઍક્સેસ ',
  'agent.shareableId.body.not': 'નહીં',
  'agent.shareableId.body.after': ' મળે છે.',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'ઇનામ',
  'agent.rewards.hub.subtitle': 'ઓફર્સ જુઓ અને તમારું એક્સપોઝર ટ્રૅક કરો',
  'agent.rewards.hub.tab.browse': 'ઓફર્સ જુઓ',
  'agent.rewards.hub.tab.exposure': 'મારું એક્સપોઝર',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'ઓપ્ટ ઇન કર્યું',
  'agent.rewards.status.optedOut': 'ઓપ્ટ આઉટ કર્યું',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'તમારા ખેલાડીઓ માટે ખાસ',
  'agent.rewards.browse.empty.title': 'અત્યારે કોઈ સક્રિય ઇનામ નથી',
  'agent.rewards.browse.empty.body': 'એડમિન નિયમિત નવી ઓફર્સ પોસ્ટ કરે છે. થોડી વારે પાછા આવો — અથવા જે ચૂકી ગયા તેમાં તમને ઓપ્ટ ઇન કરવા તમારા અપલાઇનને કહો.',
  'agent.rewards.browse.error.loadFailed': 'ઇનામ લોડ થઈ શક્યા નહીં',
  'agent.rewards.browse.card.aria.manage': '{title} માટે ઓપ્ટ-ઇન મેનેજ કરો',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'ઓપ્ટ ઇન કર્યું',
  'agent.rewards.browse.chip.optInPrompt': 'ઓપ્ટ ઇન માટે ટૅપ કરો',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'તમારું એક્સપોઝર ગણી રહ્યા છીએ…',
  'agent.rewards.exposure.error.loadFailed': 'એક્સપોઝર લોડ થઈ શક્યું નહીં',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'ઓફર એક્સપોઝર',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} સક્રિય ઓપ્ટ-ઇન',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} સક્રિય ઓપ્ટ-ઇન્સ',

  'agent.rewards.exposure.hero.actualSpend.label': 'વાસ્તવિક ખર્ચ',
  'agent.rewards.exposure.hero.actualSpend.helper': 'અત્યાર સુધી તમારી ડાઉનલાઇન દ્વારા ક્લેઇમ કરેલ.',

  'agent.rewards.exposure.hero.maxExposure.label': 'મહત્તમ એક્સપોઝર',
  'agent.rewards.exposure.hero.maxExposure.helper': 'વર્સ્ટ-કેસ સીલિંગ — દરેક બાકી બજેટ રૂપિયો ક્લેઇમ થયો.',

  'agent.rewards.exposure.hero.ceiling.label': 'સીલિંગ વપરાયું',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'જોખમ ગેજ',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} મહત્તમ',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': 'પર ',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': ', તમારા ઓપ્ટ-ઇન્સ પર ધ્યાન રાખો',
  'agent.rewards.exposure.gauge.legend.rose.before': 'પર ',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': ', તમે તમારી સીમાની નજીક છો',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'તમે અત્યારે એવી કોઈ વસ્તુમાં ઓપ્ટ ઇન નથી જેની અંદાજિત સીમા હોય.',
  'agent.rewards.exposure.gauge.caption.high': 'તમે તમારા મહત્તમ એક્સપોઝરનો {pct}% વાપરી નાખ્યો છે. મોટા ભાગના ઓપ્ટ-ઇન્સ ભારે રીતે ક્લેઇમ થયા છે.',
  'agent.rewards.exposure.gauge.caption.mid': 'તમે તમારી મહત્તમ સીમાના {pct}% પર છો. દરેક ઓફરની વિગતો પર ધ્યાન રાખો.',
  'agent.rewards.exposure.gauge.caption.low': 'આરામદાયક — અત્યાર સુધી તમારી મહત્તમ સીલિંગનો માત્ર {pct}% વપરાયો છે.',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'મહત્તમ એક્સપોઝર કેવી રીતે ગણાય છે:',
  'agent.rewards.exposure.footnote.prefix': ' દરેક ઓપ્ટ-ઇન કરેલ ઓફર માટે: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — સંયમિત ઉપલી મર્યાદા. તમારી વાસ્તવિક જવાબદારી સામાન્ય રીતે ',
  'agent.rewards.exposure.footnote.emphasis': 'ઓછી',
  'agent.rewards.exposure.footnote.suffix2': ' હોય છે જ્યારે તમારી નીચેના સબ-એજન્ટ્સ પણ ઓપ્ટ ઇન કરે છે (તેઓ મોટો હિસ્સો ભરે છે; તમે બાકીનો ભરો છો).',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'કોઈ સક્રિય ઓપ્ટ-ઇન્સ નથી',
  'agent.rewards.exposure.empty.body': 'જ્યારે તમે ઓફર્સ જુઓ ટૅબમાંથી કોઈ ઓફરમાં ઓપ્ટ ઇન કરો, ત્યારે તે અહીં તમારા ખર્ચ અને અંદાજિત એક્સપોઝર સાથે દેખાય છે.',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% હિસ્સો',
  'agent.rewards.exposure.row.claims.one': '{count} ક્લેઇમ',
  'agent.rewards.exposure.row.claims.other': '{count} ક્લેઇમ્સ',
  'agent.rewards.exposure.row.overridden': 'એડમિન-ઓવરરાઇડ કરેલ',
  'agent.rewards.exposure.row.actualMaxLabel': 'વાસ્તવિક / મહત્તમ',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'કોસ્ટ-શેર ઓપ્ટ-ઇન',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'ગેમ નફો',
  'agent.profile.rates.infoBanner.standardPctText': ' પ્રમાણભૂત ટકાવારી વાપરે છે. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'પ્રચારાત્મક ખર્ચ',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ઓફર્સ, કેશબેક) ડાયનામિક અપલાઇન પૂલનો ઉપયોગ કરે છે. બંને તમારા સ્તર (L{level}) આધારે પ્લેટફોર્મ એડમિન દ્વારા વૈશ્વિક રીતે સંચાલિત છે.',
  'agent.profile.rates.section.gameProfits': 'ગેમ નફો — તમે દરેક મેચ પર શું કમાવો છો',
  'agent.profile.rates.section.promotionalCosts': 'પ્રચારાત્મક ખર્ચ — તમારું અપલાઇન સબસિડી યોગદાન',
  'agent.profile.rates.pillar.platformFee': 'પ્લેટફોર્મ ફી %',
  'agent.profile.rates.pillar.winLoss': 'જીત / હાર નફો %',
  'agent.profile.rates.pillar.promoWeight': 'પ્રચારાત્મક પૂલ વજન',
  'agent.profile.rates.guarantee.title': 'ડાયરેક્ટ રેફરલ ગેરંટી',
  'agent.profile.rates.guarantee.body': 'જો તમે સીધે ઓફર ક્લેમ કરનાર પ્લેયરને રેફર કર્યો છે, તો તમે ₹0.00 ચૂકવો છો. તમારા નેટવર્કને વધારવા માટે તમારી અપલાઇન્સ ખર્ચના 100% સબસિડી આપે છે.',
  'agent.profile.rates.poolExplainer.title': 'પૂલ કેવી રીતે કાર્ય કરે છે:',
  'agent.profile.rates.poolExplainer.body': 'જ્યારે સબ-એજન્ટનો પ્લેયર ઓફર ક્લેમ કરે છે, ત્યારે તમારું વજન બાકીના સક્રિય અપલાઇન + પ્લેટફોર્મ સાથે એક પૂલમાં ઉમેરવામાં આવે છે. તમે (તમારું વજન / કુલ પૂલ) × ઓફર ખર્ચ ચૂકવો છો. વધુ વજન = મોટો ભાગ.',
  'agent.profile.rates.weightLabel': 'વજન:',
  'agent.profile.rates.updatedPrefix': 'અપડેટ થયું',
  'agent.profile.rates.notSetYet': 'હજુ સેટ કર્યું નથી',
  'agent.rewards.optInModal.guarantee.title': 'ડાયરેક્ટ રેફરલ ગેરંટી',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'જો <strong>તમે સીધે રેફર કર્યો</strong> આ ઓફર ક્લેમ કરનાર પ્લેયરને, તો તમે <strong>₹0.00</strong> ચૂકવો છો. તમારા નેટવર્કને વધારવા માટે તમારી અપલાઇન્સ ખર્ચના 100% સબસિડી આપે છે.',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'જ્યારે <strong>તમારા પ્લેયર્સ</strong> આ ઓફર ક્લેમ કરે છે, ત્યારે તમે <strong>₹0.00</strong> ચૂકવો છો. તમારા નેટવર્કને વધારવા માટે તમારી અપલાઇન્સ ખર્ચના 100% સબસિડી આપે છે.',
  'agent.rewards.optInModal.uplineReality.title': 'અપલાઇન રિયાલિટી ચેક',
  'agent.rewards.optInModal.uplineReality.body': 'તમારી પાસે <strong>{count}</strong> ડાયરેક્ટ સબ-એજન્ટ{plural} છે. જો કોઈ સબ-એજન્ટનો પ્લેયર આ ઓફર ક્લેમ કરે, તો તમે તમારા <strong>પૂલ વજન ({weight})</strong> આધારે બાકીના સક્રિય અપલાઇન અને પ્લેટફોર્મ સામે પ્રમાણસર હિસ્સો ચૂકવશો.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ડાયરેક્ટ',
  'agent.rates.col.indirect': 'ઇનડાયરેક્ટ',
  'agent.rates.indirect.dynamic': 'ડાયનામિક',
  'agent.rates.indirect.notApplicable': 'લાગુ નથી',
  'agent.rates.indirect.rangeTo': 'થી',
  'agent.rewards.optInModal.close': 'બંધ કરો',
  'agent.rewards.optInModal.cancel': 'રદ કરો',
  'agent.rewards.optInModal.loading': 'ઓફર વિગતો લોડ થઈ રહી છે…',
  'agent.rewards.optInModal.error.loadFailed': 'ઓફર વિગતો લોડ થઈ શકી નથી',
  'agent.rewards.optInModal.error.optInFailed': 'ઓપ્ટ-ઇન નિષ્ફળ',
  'agent.rewards.optInModal.error.optOutFailed': 'ઓપ્ટ-આઉટ નિષ્ફળ',
  'agent.rewards.optInModal.error.saveFailed': 'સબ-એજન્ટ દરો સેવ થઈ શકી નહીં',
  'agent.rewards.optInModal.toast.optedIn': 'ઓપ્ટ ઇન કર્યું. હવે તમારી ડાઉનલાઇન આ ઓફર જોઈ શકે છે.',
  'agent.rewards.optInModal.toast.optedOut': 'ઓપ્ટ આઉટ કર્યું. ઓફર તમારી ડાઉનલાઇનમાંથી હટાવાઈ.',
  'agent.rewards.optInModal.toast.saved.one': '{count} સબ-એજન્ટ દર ફેરફાર સેવ થયો.',
  'agent.rewards.optInModal.toast.saved.other': '{count} સબ-એજન્ટ દર ફેરફારો સેવ થયા.',
  'agent.rewards.optInModal.confirmOptOut': 'આ ઓફરમાંથી ઓપ્ટ આઉટ કરવું છે? તમારી ડાઉનલાઇન હવે તે જોઈ શકશે નહીં.',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'ઓફર #{id} માટે ઓપ્ટ-ઇન મોડલથી સેટ',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'તમારો હિસ્સો',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'ઓપ્ટ-ઇન પર લોક',
  'agent.rewards.optInModal.stake.current': 'વર્તમાન દર',
  'agent.rewards.optInModal.stake.noRate': 'એડમિને હજુ તમને કોઈ દર આપ્યો નથી',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'અત્યાર સુધી ખર્ચ',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'આ ઓફર પર ડાઉનલાઇન ક્લેઇમ માટે તમારા વોલેટમાંથી ચૂકવેલા કુલ રૂપિયા.',
  'agent.rewards.optInModal.stake.maxPossible.label': 'મહત્તમ શક્ય',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'વર્સ્ટ-કેસમાં તમે જે રૂપિયા ચૂકવી શકો, જો દરેક બાકી બજેટ રૂપિયો ક્લેઇમ થાય.',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— કોઈ બજેટ કેપ નહીં —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'વર્સ્ટ કેસ: જો પ્લેટફોર્મ બજેટના બાકી દરેક ₹{amount} તમારી ડાઉનલાઇન દ્વારા ક્લેઇમ થાય, તો તમે તેનો {pct}% ચૂકવો છો.',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'તમે ઓપ્ટ ઇન છો',
  'agent.rewards.optInModal.toggle.title.optedOut': 'તમારી ડાઉનલાઇન સાથે શેર કરવા ઓપ્ટ ઇન કરો',
  'agent.rewards.optInModal.toggle.body.optedIn': 'તમારી ડાઉનલાઇનના ખેલાડીઓ આ ઓફર જુએ છે. તમે તેમના દરેક ક્લેઇમને તમારા લોક હિસ્સા પર સહ-ભંડોળ આપો છો.',
  'agent.rewards.optInModal.toggle.body.optedOut': 'જ્યાં સુધી તમે ઓપ્ટ ઇન ન કરો, કોઈ ખેલાડી આ ઓફર નહીં જુએ. ઓપ્ટ ઇન કરતા જ તમારો હિસ્સા દર આ ઓફર માટે LOCK થઈ જાય છે.',
  'agent.rewards.optInModal.toggle.optOutLink': 'આ ઓફરમાંથી ઓપ્ટ આઉટ',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'સબ-એજન્ટ દરો',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'દરેક વધુમાં વધુ લઈ શકે: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} સેવ ન કરેલા',
  'agent.rewards.optInModal.subAgents.empty': 'કોઈ સીધો સબ-એજન્ટ નથી.',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'સેવ કરવાથી કૅસ્કેડ થાય છે — જો તમે અહીં કોઈ બાળકને વધારો જેને પહેલેથી ઊંડા સબ-એજન્ટ્સ છે, તો ચેન સુસંગત રાખવા તે દરો આપમેળે ક્લેમ્પ થઈ શકે છે.',
  'agent.rewards.optInModal.subAgents.noChildren': 'તમારી પાસે કોઈ સીધો સબ-એજન્ટ નથી. ઓફર તમારા પોતાના ખેલાડીઓને દેખાય છે; એડમિન બધો બાકીનો ભાગ ભરે છે.',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'દર દાખલ કરો (સાફ કરવા 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'માત્ર સંખ્યાઓ',
  'agent.rewards.optInModal.error.negative': 'ઋણાત્મક ન હોઈ શકે',
  'agent.rewards.optInModal.error.max100': 'મહત્તમ 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'મહત્તમ {ceiling}% (તમારો હિસ્સો)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'સેવ કરી રહ્યા છીએ…',
  'agent.rewards.optInModal.cta.done': 'થઈ ગયું',
  'agent.rewards.optInModal.cta.save.one': '{count} ફેરફાર સેવ કરો',
  'agent.rewards.optInModal.cta.save.other': '{count} ફેરફારો સેવ કરો',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'ખેલાડીને રિચાર્જ કરો',
  'agent.adminRechargePlayer.subtitle': 'તમારા પૂલમાંથી ખેલાડીના વોલેટ સુધી',
  'agent.adminRechargePlayer.backLink': 'ખેલાડી પર પાછા',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'નવો ખેલાડી ઓનબોર્ડ કરો',
  'agent.createPlayer.subtitle.agent': 'તેઓ આપમેળે તમારા હેઠળ આવશે.',
  'agent.createPlayer.subtitle.admin': 'નીચે મલિક એજન્ટ પસંદ કરો, અથવા પ્લેટફોર્મ-ડાયરેક્ટ માટે ખાલી છોડો.',
  'agent.createPlayer.close': 'બંધ કરો',
  'agent.createPlayer.section.identity.title': 'ઓળખ',
  'agent.createPlayer.section.identity.hint': 'એકાઉન્ટ બનાવવા માટે આવશ્યક',
  'agent.createPlayer.field.username.label': 'યુઝરનેમ',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ઇમેઇલ',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'મોબાઇલ',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10 અંકનો ભારતીય મોબાઇલ, +91 આપમેળે ઉમેરાશે',
  'agent.createPlayer.field.password.label': 'પ્રારંભિક પાસવર્ડ',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ અક્ષર, અક્ષર + અંક',
  'agent.createPlayer.section.owner.title': 'મલિક એજન્ટ',
  'agent.createPlayer.section.owner.hint': 'વૈકલ્પિક — પ્લેટફોર્મ-ડાયરેક્ટ ખેલાડી માટે ખાલી છોડો',
  'agent.createPlayer.field.owner.label': 'એજન્ટ',
  'agent.createPlayer.field.owner.none': '— કોઈ નહીં (પ્લેટફોર્મ-ડાયરેક્ટ) —',
  'agent.createPlayer.section.profile.title': 'પ્રોફાઇલ',
  'agent.createPlayer.section.profile.hint': 'વૈકલ્પિક — જે અત્યારે ખબર છે તે ભરો; ખેલાડી પહેલા લોગિન પર પૂરું કરી શકે',
  'agent.createPlayer.field.firstName.label': 'પ્રથમ નામ',
  'agent.createPlayer.field.lastName.label': 'છેલ્લું નામ',
  'agent.createPlayer.field.dob.label': 'જન્મ તારીખ',
  'agent.createPlayer.field.gender.label': 'લિંગ',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— ઉલ્લેખ નથી —',
  'agent.createPlayer.gender.male': 'પુરુષ',
  'agent.createPlayer.gender.female': 'સ્ત્રી',
  'agent.createPlayer.gender.other': 'અન્ય',
  'agent.createPlayer.gender.preferNot': 'કહેવા માગતા નથી',
  'agent.createPlayer.field.language.label': 'પસંદગીની ભાષા',
  'agent.createPlayer.section.location.title': 'સ્થાન',
  'agent.createPlayer.section.location.hint': 'વૈકલ્પિક — ખેલાડી પહેલા લોગિન પર પૂરું કરી શકે',
  // Footer.
  'agent.createPlayer.disclaimer': 'ખેલાડી status=active અને ઇમેઇલ ઓટો-વેરિફાય સાથે બને છે — કોઈ લિંક મોકલાતી નથી. ખાતરી કરો કે તમે સંપર્ક માહિતી પર વિશ્વાસ કરો છો.',
  'agent.createPlayer.cta.cancel': 'રદ કરો',
  'agent.createPlayer.cta.creating': 'બનાવી રહ્યા છીએ…',
  'agent.createPlayer.cta.submit': 'ખેલાડી બનાવો',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'એજન્ટ ID નથી',
  'agent.createPlayer.error.createFailed': 'ખેલાડી બનાવી શકાયો નહીં',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'આજે',
  'agent.adminHome.range.yesterday': 'ગઈકાલે',
  'agent.adminHome.range.week': 'આ અઠવાડિયે',
  'agent.adminHome.range.lastWeek': 'ગયા અઠવાડિયે',
  'agent.adminHome.range.month': 'આ મહિને',
  'agent.adminHome.range.lastMonth': 'ગયા મહિને',
  'agent.adminHome.range.all': 'પૂર્ણ',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'ફરી સ્વાગત છે',
  'agent.adminHome.greeting.fallback': 'મિત્ર',
  'agent.adminHome.errorBanner.fallback': 'લોડ કરવામાં નિષ્ફળ',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'પ્લેટફોર્મ ઇનફ્લો',
  'agent.adminHome.admin.action.moneyQueue': 'મની ક્યૂ',
  'agent.adminHome.admin.action.agents': 'એજન્ટ્સ',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'મૅચ',
  'agent.adminHome.admin.snapshot.newSignups': 'નવા સાઇનઅપ',
  'agent.adminHome.admin.snapshot.active': 'સક્રિય',
  'agent.adminHome.admin.snapshot.pending': 'બાકી',

  // Section titles between rows.
  'agent.adminHome.section.games': 'ગેમ્સ',
  'agent.adminHome.section.games.subtitle': 'મૅચ અને પ્લેટફોર્મ ફી · {range}',
  'agent.adminHome.section.activity': 'ગતિવિધિ',
  'agent.adminHome.section.ops': 'ઓપરેશન્સ અને આંતરદૃષ્ટિ',
  'agent.adminHome.section.ops.subtitle': 'લાઇવ સ્નૅપશોટ — પૂરા પેજ પર જવા માટે કોઈ પણ કાર્ડ પર ક્લિક કરો',
  'agent.adminHome.section.gameSettings': 'ગેમ સેટિંગ્સ',
  'agent.adminHome.section.gameSettings.subtitle': 'દરેક ગેમ કૉન્ફિગર કરો',
  'agent.adminHome.section.downline': 'મારી ડાઉનલાઇન આંતરદૃષ્ટિ',
  'agent.adminHome.section.downline.subtitle': 'પૂરા પેજ પર જવા માટે કોઈ પણ કાર્ડ પર ક્લિક કરો',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'ખેલાડીઓનું વોલેટ',
  'agent.adminHome.admin.card.players.headlineLabel': 'અવધિમાં રિચાર્જ',
  'agent.adminHome.admin.card.players.flow.recharges': 'રિચાર્જ',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'ઉપાડ',
  'agent.adminHome.admin.card.players.pending.recharges': 'બાકી રિચાર્જ',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'બાકી ઉપાડ',
  'agent.adminHome.admin.card.players.stat.total': 'કુલ ખેલાડીઓ',
  'agent.adminHome.admin.card.players.stat.active': 'સક્રિય',
  'agent.adminHome.admin.card.players.stat.suspended': 'સસ્પેન્ડેડ',

  'agent.adminHome.admin.card.agents.title': 'એજન્ટ્સનું વોલેટ',
  'agent.adminHome.admin.card.agents.headlineLabel': 'એજન્ટ પૂલમાં',
  'agent.adminHome.admin.card.agents.flow.topups': 'ટોપ-અપ (એડમિન → એજન્ટ)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'આઉટફ્લો (ચૂકવેલ / ટોપ-અપ)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'બાકી એજન્ટ ઉપાડ',
  'agent.adminHome.admin.card.agents.stat.total': 'કુલ એજન્ટ્સ',
  'agent.adminHome.admin.card.agents.stat.active': 'સક્રિય',
  'agent.adminHome.admin.card.agents.stat.suspended': 'સસ્પેન્ડેડ',

  'agent.adminHome.admin.card.bots.title': 'બોટ્સનું વોલેટ',
  'agent.adminHome.admin.card.bots.headlineLabel': 'બોટ વોલેટ્સમાં',
  'agent.adminHome.admin.card.bots.stat.total': 'કુલ બોટ્સ',
  'agent.adminHome.admin.card.bots.stat.active': 'સક્રિય',
  'agent.adminHome.admin.card.bots.stat.matches': 'મૅચ',

  'agent.adminHome.admin.card.rewards.title': 'ઇનામ',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'આ અઠવાડિયે ચૂકવ્યું',
  'agent.adminHome.admin.card.rewards.pending.claims': 'બાકી ક્લેઇમ્સ',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'સક્રિય ઓફર્સ',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'બલ્ક જોબ્સ',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'મૅચ',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'લૂડો',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'રમી',
  'agent.adminHome.game.carrom': 'કૅરમ',
  'agent.adminHome.game.chess': 'ચેસ',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'ટોપ ખેલાડીઓ',
  'agent.adminHome.activity.topPlayers.empty': 'આ અવધિમાં કોઈ મૅચ નથી.',
  'agent.adminHome.activity.topPlayers.metric': '{count} મૅચ',
  'agent.adminHome.activity.topAgents.title': 'ટોપ એજન્ટ્સ',
  'agent.adminHome.activity.topAgents.subtitle': 'મલિક ખેલાડીઓ પ્રમાણે',
  'agent.adminHome.activity.topAgents.empty': 'હજુ સુધી કોઈ સક્રિય એજન્ટ નથી.',
  'agent.adminHome.activity.topAgents.metric': '{count} ખેલાડીઓ',
  'agent.adminHome.activity.recent.title': 'તાજેતરના ટ્રાન્ઝેક્શન',
  'agent.adminHome.activity.recent.subtitle': 'છેલ્લા 5',
  'agent.adminHome.activity.recent.empty': 'હજુ કોઈ ટ્રાન્ઝેક્શન નથી.',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'ટોપ કમાનારા (7d)',
  'agent.adminHome.ops.topEarners.subtitle': 'True Net પ્રમાણે',
  'agent.adminHome.ops.topEarners.empty': 'છેલ્લા 7 દિવસમાં કોઈ કમાણી નથી.',
  'agent.adminHome.ops.liveActivity.title': 'લાઇવ ગતિવિધિ',
  'agent.adminHome.ops.liveActivity.subtitle': 'પ્લેટફોર્મ ભરના છેલ્લા 5 ઇવેન્ટ',
  'agent.adminHome.ops.liveActivity.empty': 'શાંત — છેલ્લા 24h માં કોઈ ઇવેન્ટ નથી.',
  'agent.adminHome.ops.botPerf.title': 'બોટ પ્રદર્શન (7d)',
  'agent.adminHome.ops.botPerf.subtitle': 'દરેક કેટેગરી મુજબ P&L',
  'agent.adminHome.ops.rateChanges.title': 'તાજેતરના રેટ ફેરફારો',
  'agent.adminHome.ops.rateChanges.subtitle': 'નવીનતમ પાર્ટનરશિપ સંપાદનો',
  'agent.adminHome.ops.rateChanges.empty': 'હજુ સુધી કોઈ રેટ-ફેરફાર ઇવેન્ટ નથી.',
  'agent.adminHome.ops.opsHealth.title': 'ઓપરેશન્સ આરોગ્ય',
  'agent.adminHome.ops.opsHealth.subtitle': 'ડેડ-લેટર ક્યૂ · બાકી સેટલમેન્ટ',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'કમાયું',
  'agent.adminHome.feed.match.onGameSuffix': '{game} પર',
  'agent.adminHome.feed.recharge.label': 'રિચાર્જ',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'દ્વારા',
  'agent.adminHome.feed.rateChange.label': 'રેટ ફેરફાર',
  'agent.adminHome.feed.cascadeBadge': 'કૅસ્કેડ',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'મંજૂર',
  'agent.adminHome.feed.recharge.state.rejected': 'નકારેલ',
  'agent.adminHome.feed.recharge.state.pending': 'બાકી',
  'agent.adminHome.feed.recharge.state.paid': 'ચૂકવેલ',
  'agent.adminHome.feed.recharge.state.expired': 'મુદ્દત પૂર્ણ',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} મૅચ',
  'agent.adminHome.leaderboard.matches.other': '{count} મૅચ',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'પ્લેટફોર્મ નફો (7d)',
  'agent.adminHome.botMatrix.cat.easy': 'સરળ',
  'agent.adminHome.botMatrix.cat.medium': 'મધ્યમ',
  'agent.adminHome.botMatrix.cat.hard': 'કઠિન',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}m',
  'agent.adminHome.botMatrix.activeSummary': '{total} માંથી {active} બોટ સક્રિય',
  'agent.adminHome.botMatrix.loading': 'લોડ થઈ રહ્યું છે…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ સ્વચ્છ છે',
  'agent.adminHome.opsHealth.dlqClean.body': 'કોઈ નિષ્ફળ સેટલમેન્ટ જોબ નથી.',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} અણઉકેલ DLQ જોબ',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} અણઉકેલ DLQ જોબ્સ',
  'agent.adminHome.opsHealth.dlqDirty.body': 'ફરી પ્રયત્ન / ઉકેલવા ક્લિક કરો.',
  'agent.adminHome.opsHealth.replay.eyebrow': 'ફરી ચલાવો',
  'agent.adminHome.opsHealth.replay.body': 'સેટલમેન્ટ ફરી ચલાવો',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'ફરી અસાઇન કરો',
  'agent.adminHome.opsHealth.reassign.body': 'બલ્ક મૂવ',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'મારું પૂલ બેલેન્સ',
  'agent.adminHome.agentDash.hero.netPrefix': 'નેટ',
  'agent.adminHome.agentDash.action.myWallet': 'મારું વોલેટ',
  'agent.adminHome.agentDash.action.myPlayers': 'મારા ખેલાડીઓ',
  'agent.adminHome.agentDash.snapshot.myMatches': 'મારા મૅચ',
  'agent.adminHome.agentDash.snapshot.active': 'સક્રિય',
  'agent.adminHome.agentDash.snapshot.netRange': 'નેટ (અવધિ)',
  'agent.adminHome.agentDash.snapshot.pending': 'બાકી',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'મારા ખેલાડીઓનું વોલેટ',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'અવધિમાં રિચાર્જ',
  'agent.adminHome.agentDash.card.players.stat.total': 'મારા ખેલાડીઓ',
  'agent.adminHome.agentDash.card.wallet.title': 'મારું વોલેટ',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'પૂલ બેલેન્સ',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'કમાયું (કમિશન)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'ચૂકવ્યું',
  'agent.adminHome.agentDash.card.wallet.pending': 'મારી બાકી ઉપાડ',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'મળેલ ટોપ-અપ',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'નેટ (અવધિ)',
  'agent.adminHome.agentDash.card.activity.title': 'ગતિવિધિ',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'અવધિમાં મારા મૅચ',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'ટોપ ખર્ચ કરનાર',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'તેમનો ખર્ચ',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'બાકી આઇટમ્સ',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'ટોપ ખર્ચ કરનારા',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'આ અવધિમાં હજુ કોઈ ખર્ચ નથી.',
  'agent.adminHome.agentDash.activity.recent.title': 'તાજેતરની ગતિવિધિ',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'તમારા ખેલાડીઓ તરફથી છેલ્લા 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'હજુ કોઈ ટ્રાન્ઝેક્શન નથી.',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'ટોપ કમાનારા (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'તમારા સબ-એજન્ટ્સ ક્રમમાં',
  'agent.adminHome.agentDash.insights.downline.title': 'ડાઉનલાઇન ગતિવિધિ',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'તમારી ડાઉનલાઇન તરફથી છેલ્લા 5 ઇવેન્ટ',
  'agent.adminHome.agentDash.insights.deposit.title': 'ડિપોઝિટ લેજર',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'તાજેતરના ડિપોઝિટ ક્રેડિટ / રિફંડ',
  'agent.adminHome.agentDash.insights.deposit.empty': 'હજુ કોઈ ડિપોઝિટ ગતિવિધિ નથી.',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'સબ-એજન્ટ',
  'agent.subAgentDetail.subtitle': 'સીધો સબ-એજન્ટ',
  'agent.subAgentDetail.error.loadFailed': 'સબ-એજન્ટ લોડ કરવામાં નિષ્ફળ',
  'agent.subAgentDetail.backToList': 'સબ-એજન્ટ યાદી પર પાછા',
  'agent.subAgentDetail.loading': 'લોડ થઈ રહ્યું છે…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'ઓળખ',
  'agent.subAgentDetail.tab.players': 'ખેલાડીઓ',
  'agent.subAgentDetail.tab.wallet': 'વોલેટ',
  'agent.subAgentDetail.tab.rates': 'દરો',
  'agent.subAgentDetail.tab.ops': 'ઓપરેશન્સ',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} ખેલાડી',
  'agent.subAgentDetail.header.players.other': '{count} ખેલાડીઓ',
  'agent.subAgentDetail.header.pool': 'પૂલ: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'છેલ્લો લોગિન {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'સક્રિય',
  'agent.subAgentDetail.status.suspended': 'સસ્પેન્ડેડ',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'યુઝરનેમ',
  'agent.subAgentDetail.identity.row.email': 'ઇમેઇલ',
  'agent.subAgentDetail.identity.row.status': 'સ્થિતિ',
  'agent.subAgentDetail.identity.row.parent': 'પેરન્ટ એજન્ટ',
  'agent.subAgentDetail.identity.parentValue': '@{username} (તમે)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'મલિક ખેલાડીઓ',
  'agent.subAgentDetail.identity.row.wallet': 'વોલેટ બેલેન્સ',
  'agent.subAgentDetail.identity.row.created': 'બનાવ્યું',
  'agent.subAgentDetail.identity.row.lastLogin': 'છેલ્લો લોગિન',
  'agent.subAgentDetail.identity.row.identityLocked': 'ઓળખ લોક',
  'agent.subAgentDetail.identity.identityLocked.yes': 'હા (માત્ર એડમિન સંપાદન કરે)',
  'agent.subAgentDetail.identity.identityLocked.no': 'ના (સબ-એજન્ટ પોતે સંપાદન કરી શકે)',
  'agent.subAgentDetail.identity.row.receiving': 'ખેલાડીઓ સ્વીકારે છે',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'સ્વીકારે છે',
  'agent.subAgentDetail.optOut.optedOut': 'ઓપ્ટ આઉટ',
  'agent.subAgentDetail.optOut.toast.accepting': 'સબ-એજન્ટ હવે નવા ખેલાડીઓ સ્વીકારે છે',
  'agent.subAgentDetail.optOut.toast.optedOut': 'સબ-એજન્ટે લોકેટરમાંથી ઓપ્ટ આઉટ કર્યું',
  'agent.subAgentDetail.optOut.error.updateFailed': 'અપડેટ થઈ શક્યું નહીં',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'સ્થાન',
  'agent.subAgentDetail.location.lastUpdated': '· છેલ્લે અપડેટ {date}',
  'agent.subAgentDetail.location.cta.save': 'સ્થાન સેવ કરો',
  'agent.subAgentDetail.location.nothingToSave': 'સેવ કરવા માટે કંઈ નથી',
  'agent.subAgentDetail.location.toast.saved.one': 'સ્થાન સેવ થયું ({count} ફીલ્ડ)',
  'agent.subAgentDetail.location.toast.saved.other': 'સ્થાન સેવ થયું ({count} ફીલ્ડ્સ)',
  'agent.subAgentDetail.location.hint': 'સેવ સક્ષમ કરવા કોઈ અલગ રાજ્ય / જિલ્લો / વિસ્તાર પસંદ કરો. (સબ-એજન્ટ પોતે પહેલા લોગિન પર ફરજિયાત પોપ-અપથી ચોક્કસ GPS ઓવરલે કરે છે.)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'આ સબ-એજન્ટ પાસે અત્યારે કોઈ ખેલાડી નથી.',
  'agent.subAgentDetail.players.count.one': '{count} ખેલાડી',
  'agent.subAgentDetail.players.count.other': '{count} ખેલાડીઓ',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'વર્તમાન સબ-એજન્ટ બેલેન્સ',
  'agent.subAgentDetail.wallet.ledger.title': '@{username} નું પૂરું વોલેટ લેજર',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'દરેક હલચલ · સબ-એજન્ટ પોતાના વોલેટમાં તે જ જુએ છે',
  'agent.subAgentDetail.wallet.ledger.empty': 'હજુ કોઈ વોલેટ ગતિવિધિ નથી.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'તમે ↔ તેઓ',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'બેલ ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'એડમિન તરફથી ટોપ-અપ',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'ખેલાડીને ચૂકવણી',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'ખેલાડીનો રિચાર્જ',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'આવક હિસ્સો',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'રિચાર્જ કમિશન',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'સહ-ભંડોળ ઓફર',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'એડમિન સમાયોજન',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'ઉપાડ હોલ્ડ',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'ઉપાડ રિફંડ',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'પ્લેટફોર્મ ફી હિસ્સો',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'વિન/લોસ હિસ્સો',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'સિક્કા પાછા લઈ રહ્યા છો?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'કહો ',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' ને કે તેઓ પોતાના પૂલમાંથી ઉપાડ વિનંતી મોકલે. તમે તેને (પૂર્ણ કે આંશિક) ચૂકવેલ માર્ક કરી શકો છો — ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'ફાઇનાન્સ › સબ-એજન્ટ્સ',
  'agent.subAgentDetail.wallet.pullback.body.after': '.',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username} ને ટ્રાન્સફર',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username} માંથી ઉપાડો',
  'agent.subAgentDetail.transfer.topup.body': 'એટોમિક ટ્રાન્સફર: તમારું વોલેટ ઘટે, તેમનું વોલેટ વધે. બંને પૂલ ઋણાત્મક જઈ શકે છે (સાઇન્ડ-પૂલ ડિઝાઇન).',
  'agent.subAgentDetail.transfer.withdraw.body': 'એટોમિક ઉલટું ટ્રાન્સફર: તેમનું વોલેટ ઘટે, તમારું વોલેટ વધે. સબ-એજન્ટની ચૂકવણી વિનંતી પતાવતી વખતે આનો ઉપયોગ કરો.',
  'agent.subAgentDetail.transfer.amountLabel': 'રકમ (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'નોંધ (વૈકલ્પિક)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'દા.ત. સાપ્તાહિક ટોપ-અપ',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'દા.ત. ચૂકવણી પતાવટ',
  'agent.subAgentDetail.transfer.error.amountPositive': 'રકમ ધનાત્મક પૂર્ણાંક હોવી જોઈએ.',
  'agent.subAgentDetail.transfer.confirm.topup': 'તમારા પૂલમાંથી @{username} ને {amount} ટ્રાન્સફર કરવું છે? તમારું બેલેન્સ બરાબર એટલું જ ઘટશે.',
  'agent.subAgentDetail.transfer.confirm.withdraw': '@{username} ના પૂલમાંથી {amount} તમારી પાસે પાછા? તેમનું બેલેન્સ બરાબર એટલું જ ઘટશે.',
  'agent.subAgentDetail.transfer.toast.topup': '@{username} ને {amount} મોકલ્યું. તેમનું બેલેન્સ: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username} માંથી {amount} પાછું લીધું. તેમનું બેલેન્સ: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working': 'કામ ચાલુ છે…',
  'agent.subAgentDetail.transfer.cta.suspended': 'સબ-એજન્ટ સસ્પેન્ડેડ',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} ટ્રાન્સફર',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} ઉપાડો',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'દરો લોડ થઈ રહ્યા છે…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'દરેક કાર્ડ એક કમિશન પિલર છે; અંદરની દરેક પંક્તિ એક (પિલર × ગેમ) હિસ્સો છે. ',
  'agent.subAgentDetail.rates.intro.ceiling': 'સીલિંગ',
  'agent.subAgentDetail.rates.intro.part2': ' જે દરેક ઇનપુટની બાજુમાં દેખાય છે તે તમારો પોતાનો દર છે — ગોલ્ડન લિમિટ મુજબ તમે તેનાથી વધારે બાળકને આપી શકતા નથી. તમે જે પણ બદલો તે સંપાદન યોગ્ય બની જાય છે; ક્લિક કરો ',
  'agent.subAgentDetail.rates.intro.applyWord': 'લાગુ કરો',
  'agent.subAgentDetail.rates.intro.part3': ' કમિટ કરવા. કૅસ્કેડ ક્લેમ્પ સર્વર-સાઇડ લાગુ થાય છે.',
  'agent.subAgentDetail.rates.toast.applied': 'દરો લાગુ થયા.',
  'agent.subAgentDetail.rates.confirm.one': '{count} દર ફેરફાર લાગુ કરવો છે? ગોલ્ડન-લિમિટ + કૅસ્કેડ ક્લેમ્પ સર્વર-સાઇડ લાગુ થશે.',
  'agent.subAgentDetail.rates.confirm.other': '{count} દર ફેરફારો લાગુ કરવા છે? ગોલ્ડન-લિમિટ + કૅસ્કેડ ક્લેમ્પ સર્વર-સાઇડ લાગુ થશે.',
  'agent.subAgentDetail.rates.error.blocked': 'બ્લોક: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'પ્લેટફોર્મ ફી %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'વિન/લોસ નફો %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ઓફર ઇનામ %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— ગ્લોબલ —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ સીલિંગ {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'પેરન્ટથી વધારે (ગોલ્ડન લિમિટ)',
  'agent.subAgentDetail.rates.row.editedBadge': 'સંપાદિત',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} ફેરફાર છોડો',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} ફેરફારો છોડો',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} ફેરફાર લાગુ કરો',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} ફેરફારો લાગુ કરો',
  'agent.subAgentDetail.rates.cta.applying': 'લાગુ કરી રહ્યા છીએ…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'સ્થિતિ',
  'agent.subAgentDetail.ops.status.body': 'સસ્પેન્ડ કરવાથી સબ-એજન્ટના લોગિન અને ડાઉનલાઇન કામગીરી તમે ફરી સક્રિય ન કરો ત્યાં સુધી બંધ થાય છે. કારણ વૈકલ્પિક છે પણ ઓડિટ ટ્રેઇલમાં આવે છે.',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'કારણ (વૈકલ્પિક)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'સબ-એજન્ટ સસ્પેન્ડ કરો',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'સબ-એજન્ટનું સસ્પેન્શન હટાવો',
  'agent.subAgentDetail.ops.status.cta.working': 'કામ ચાલુ છે…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username} ને સસ્પેન્ડ કરવા છે? સસ્પેન્ડ થયા હોય ત્યારે તેઓ લોગિન કે કામ કરી શકશે નહીં.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username} નું સસ્પેન્શન હટાવવું છે? તેમને પૂરી ઍક્સેસ પાછી મળશે.',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} હવે સસ્પેન્ડેડ છે.',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} હવે સક્રિય છે.',

  'agent.subAgentDetail.ops.password.title': 'પાસવર્ડ રીસેટ કરો',
  'agent.subAgentDetail.ops.password.body': 'સબ-એજન્ટ માટે નવો પાસવર્ડ સેટ કરે છે અને તેમના હાલના રિફ્રેશ ટોકન રદ કરે છે. તેમને નવા પાસવર્ડથી ફરી લોગિન કરવું પડશે. તેને સુરક્ષિત ચેનલથી શેર કરો.',
  'agent.subAgentDetail.ops.password.placeholder': 'નવો પાસવર્ડ (ઓછામાં ઓછા 8 અક્ષર)',
  'agent.subAgentDetail.ops.password.confirm': '@{username} નો પાસવર્ડ રીસેટ કરવો છે? તેમના હાલના સત્રો અમાન્ય થઈ જશે અને તેમને નવા પાસવર્ડથી લોગિન કરવું પડશે.',
  'agent.subAgentDetail.ops.password.toast.reset': 'પાસવર્ડ રીસેટ. નવો પાસવર્ડ @{username} સાથે શેર કરો.',
  'agent.subAgentDetail.ops.password.error.tooShort': 'પાસવર્ડ ઓછામાં ઓછા 8 અક્ષરનો હોવો જોઈએ.',
  'agent.subAgentDetail.ops.password.cta.reset': 'પાસવર્ડ રીસેટ કરો',
  'agent.subAgentDetail.ops.password.cta.resetting': 'રીસેટ થઈ રહ્યું છે…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'ખેલાડીઓ પર પાછા',
  'agent.playerDetail.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.playerDetail.error.loadFailed': 'યુઝર લોડ કરવામાં નિષ્ફળ',

  // Header card.
  'agent.playerDetail.header.noName': '(કોઈ નામ સેટ નથી)',
  'agent.playerDetail.header.joined': '{date} ના જોડાયા',
  'agent.playerDetail.header.ownedBy.prefix': ' · મલિક ',
  'agent.playerDetail.header.walletLabel': 'વોલેટ',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'બાકી',
  'agent.playerDetail.status.active': 'સક્રિય',
  'agent.playerDetail.status.suspended': 'સસ્પેન્ડેડ',
  'agent.playerDetail.status.banned': 'પ્રતિબંધિત',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'ઓળખ',
  'agent.playerDetail.tab.profile': 'પ્રોફાઇલ',
  'agent.playerDetail.tab.wallet': 'વોલેટ',
  'agent.playerDetail.tab.security': 'સુરક્ષા',
  'agent.playerDetail.tab.ownership': 'માલિકી',
  'agent.playerDetail.tab.operations': 'ઓપરેશન્સ',

  // Identity tab.
  'agent.playerDetail.identity.title': 'ઓળખ',
  'agent.playerDetail.identity.row.username': 'યુઝરનેમ',
  'agent.playerDetail.identity.row.email': 'ઇમેઇલ',
  'agent.playerDetail.identity.row.phone': 'ફોન',
  'agent.playerDetail.identity.row.country': 'દેશ',
  'agent.playerDetail.identity.row.joined': 'જોડાયા',
  'agent.playerDetail.identity.row.emailVerified': 'ઇમેઇલ વેરિફાય',
  'agent.playerDetail.identity.row.telegram': 'Telegram',
  'agent.playerDetail.identity.row.failedLogins': 'નિષ્ફળ લોગિન',
  'agent.playerDetail.identity.row.lockedUntil': 'આ સમય સુધી લોક',
  'agent.playerDetail.identity.value.yes': 'હા',
  'agent.playerDetail.identity.value.no': 'ના',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'આ ખેલાડીને ટ્રાન્સફર કરો',
  'agent.playerDetail.identity.transfer.body': 'બીજા એજન્ટને તેમની Unique Agent ID થી હેન્ડ ઓફ કરો — પૂરા નેટવર્કમાં કામ કરે છે, માત્ર તમારી ડાઉનલાઇનમાં નહીં.',
  'agent.playerDetail.identity.transfer.cta': 'UAID થી ટ્રાન્સફર',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'ફોટો બદલો',
  'agent.playerDetail.avatar.cropTitle': '@{username} નો ફોટો ગોઠવો',
  'agent.playerDetail.avatar.toast.updated': 'ફોટો અપડેટ થયો',
  'agent.playerDetail.avatar.error.uploadFailed': 'અપલોડ નિષ્ફળ',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'ખેલાડી UAID થી ટ્રાન્સફર થયો',
  'agent.playerDetail.transfer.toast.agent': '@{username} સફળતાપૂર્વક ટ્રાન્સફર થયો',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'એકાઉન્ટ સ્થિતિ',
  'agent.playerDetail.ops.accountStatus.body': 'સક્રિયનો અર્થ છે ખેલાડી લોગિન કરી અને રમી શકે છે. સસ્પેન્ડ તમે પાછા ફેરવો ત્યાં સુધી લોગિન બંધ કરે છે. પ્રતિબંધ મજબૂત લોક છે — નીતિ ઉલ્લંઘન માટે રાખો.',
  'agent.playerDetail.ops.statusBtn.activate': 'સક્રિય કરો',
  'agent.playerDetail.ops.statusBtn.suspend': 'સસ્પેન્ડ કરો',
  'agent.playerDetail.ops.statusBtn.ban': 'પ્રતિબંધ',
  'agent.playerDetail.ops.statusBtn.current': 'વર્તમાન',
  'agent.playerDetail.ops.statusBtn.toast': 'યુઝર હવે {status} છે',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'ખતરો ઝોન',
  'agent.playerDetail.ops.danger.body': 'હાર્ડ-ડિલીટ યુઝર રેકોર્ડ હટાવે છે. વોલેટ લેજર ઓડિટ માટે સચવાય છે, પણ ખેલાડી પૂરી ઍક્સેસ ગુમાવશે. આ ક્રિયા પાછી ન કરી શકાય.',
  'agent.playerDetail.ops.delete.eyebrow': 'ખતરો ઝોન',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'આ એકાઉન્ટ કાયમ માટે ડિલીટ કરો. આ પાછું ન થઈ શકે. પુષ્ટિ માટે ',
  'agent.playerDetail.ops.delete.confirm.after': ' ટાઇપ કરો.',
  'agent.playerDetail.ops.delete.cta': 'ડિલીટ કરો',
  'agent.playerDetail.ops.delete.toast': 'યુઝર ડિલીટ થયો',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'વોલેટ સમાયોજિત કરો',
  'agent.playerDetail.walletAdjust.mode.credit': '+ ક્રેડિટ',
  'agent.playerDetail.walletAdjust.mode.debit': '− ડેબિટ',
  'agent.playerDetail.walletAdjust.field.amount': 'રકમ (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'કારણ · લેજરમાં સંગ્રહિત',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'દા.ત. ડિપોઝિટ રિવર્સલ, બોનસ, રિફંડ',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'રિચાર્જ તરીકે માર્ક કરો',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'આ ક્રેડિટને લેજરમાં ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'રિચાર્જ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' તરીકે રેકોર્ડ કરો. જો આ યુઝરને કોઈએ રેફર કર્યો હતો, તો રેફર કરનારને આપમેળે બોનસ મળે છે.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'ધનાત્મક રકમ દાખલ કરો',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'કારણ આવશ્યક છે',
  'agent.playerDetail.walletAdjust.cta.applying': 'લાગુ કરી રહ્યા છીએ…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'ક્રેડિટ લાગુ કરો',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'ડેબિટ લાગુ કરો',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} ક્રેડિટ થયું',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} ડેબિટ થયું',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · રેફર કરનારે {bonus} બોનસ કમાયો',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'આ ખેલાડીને રિચાર્જ કરો',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': ' ને ટોપ-અપ કરવાથી સિક્કા ',
  'agent.playerDetail.rechargeCta.body.bold': 'તમારા વોલેટ',
  'agent.playerDetail.rechargeCta.body.after': ' માંથી તેમના સુધી જાય છે. તમારો પૂલ પહેલા ચકાસાય છે — આ ક્રિયા એટોમિક છે અને જો બેલેન્સ અપૂરતું હોય તો નકારાય છે.',
  'agent.playerDetail.rechargeCta.thisPlayer': 'આ ખેલાડી',
  'agent.playerDetail.rechargeCta.cta': 'રિચાર્જ ફોર્મ ખોલો',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'પાસવર્ડ રીસેટ કરો',
  'agent.playerDetail.security.subtitle': 'નવો પાસવર્ડ સેટ કરે છે અને યુઝરને દરેક ડિવાઇસથી સાઇન આઉટ કરે છે.',
  'agent.playerDetail.security.field.new': 'નવો પાસવર્ડ',
  'agent.playerDetail.security.field.confirm': 'પુષ્ટિ કરો',
  'agent.playerDetail.security.error.mismatch': 'પાસવર્ડ મેળ ખાતા નથી',
  'agent.playerDetail.security.error.tooShort': 'ઓછામાં ઓછા 8 અક્ષર, અક્ષરો અને અંકો સાથે',
  'agent.playerDetail.security.toast.reset': 'પાસવર્ડ રીસેટ અને બધા સત્ર રદ થયા',
  'agent.playerDetail.security.cta.reset': 'પાસવર્ડ રીસેટ કરો',
  'agent.playerDetail.security.cta.resetting': 'રીસેટ થઈ રહ્યું છે…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'વોલેટ લેજર',
  'agent.playerDetail.ledger.entries.one': '{count} એન્ટ્રી',
  'agent.playerDetail.ledger.entries.other': '{count} એન્ટ્રીઝ',
  'agent.playerDetail.ledger.empty': 'હજુ કોઈ ટ્રાન્ઝેક્શન નથી.',
  'agent.playerDetail.ledger.loading': 'લોડ થઈ રહ્યું છે…',
  'agent.playerDetail.ledger.actorSuffix': ' · એડમિન #{id} દ્વારા',
  'agent.playerDetail.ledger.pagination.prev': 'પાછળ',
  'agent.playerDetail.ledger.pagination.next': 'આગળ',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'મૅચ બેટ',
  'agent.playerDetail.ledger.kind.win': 'મૅચ ઇનામ',
  'agent.playerDetail.ledger.kind.refund': 'રિફંડ',
  'agent.playerDetail.ledger.kind.fee': 'પ્લેટફોર્મ ફી',
  'agent.playerDetail.ledger.kind.adminCredit': 'એડમિન ક્રેડિટ',
  'agent.playerDetail.ledger.kind.adminDebit': 'એડમિન ડેબિટ',
  'agent.playerDetail.ledger.kind.recharge': 'રિચાર્જ',
  'agent.playerDetail.ledger.kind.referralBonus': 'રેફરલ બોનસ',
  'agent.playerDetail.ledger.kind.signupBonus': 'સાઇનઅપ બોનસ',
  'agent.playerDetail.ledger.kind.offerCredit': 'ઓફર ક્રેડિટ',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'ઉપાડ',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'રિફંડ',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'પ્લેટફોર્મ',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'પ્રોફાઇલ સંપાદિત કરો',
  'agent.playerDetail.profile.field.username': 'યુઝરનેમ',
  'agent.playerDetail.profile.field.email': 'ઇમેઇલ',
  'agent.playerDetail.profile.field.phone': 'ફોન',
  'agent.playerDetail.profile.field.firstName': 'પ્રથમ નામ',
  'agent.playerDetail.profile.field.lastName': 'છેલ્લું નામ',
  'agent.playerDetail.profile.field.dob': 'જન્મ તારીખ',
  'agent.playerDetail.profile.field.gender': 'લિંગ',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'પુરુષ',
  'agent.playerDetail.profile.gender.female': 'સ્ત્રી',
  'agent.playerDetail.profile.gender.other': 'અન્ય',
  'agent.playerDetail.profile.gender.preferNot': 'કહેવા માગતા નથી',
  // Location section.
  'agent.playerDetail.profile.location.title': 'સ્થાન',
  'agent.playerDetail.profile.location.lastUpdated': '· છેલ્લે અપડેટ {date}',
  'agent.playerDetail.profile.location.legacy.label': 'ફાઇલ પર જૂનું સરનામું',
  'agent.playerDetail.profile.location.legacy.note': ' (માત્ર વાંચવા યોગ્ય — pre-R3, લોકેટર દ્વારા ઉપયોગમાં નથી):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'સેવ કરવા માટે કંઈ નથી',
  'agent.playerDetail.profile.toast.saved.one': '{count} ફીલ્ડ સેવ થયું',
  'agent.playerDetail.profile.toast.saved.other': '{count} ફીલ્ડ્સ સેવ થયા',
  'agent.playerDetail.profile.cta.save': 'ફેરફારો સેવ કરો',
  'agent.playerDetail.profile.cta.saving': 'સેવ કરી રહ્યા છીએ…',
  'agent.playerDetail.profile.cta.reset': 'રીસેટ કરો',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'મલિક એજન્ટ',
  'agent.playerDetail.ownership.row.eyebrow': 'માલિકી',
  'agent.playerDetail.ownership.platform.title': 'પ્લેટફોર્મ-ડાયરેક્ટ',
  'agent.playerDetail.ownership.platform.body': 'આ ખેલાડીનો કોઈ એજન્ટ મલિક નથી. એડમિન સીધો સંભાળે છે.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'એજન્ટ #{id}',
  'agent.playerDetail.ownership.owned.body': 'આ ખેલાડીના રિચાર્જ + મૅચ પર કમિશન કમાય છે.',
  'agent.playerDetail.ownership.cta.change': 'બદલો',
  'agent.playerDetail.ownership.toast.changedTo': 'માલિકી → {label}',
  'agent.playerDetail.ownership.platformLabel': 'પ્લેટફોર્મ-ડાયરેક્ટ',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username} ને ફરી અસાઇન કરો',
  'agent.playerDetail.ownership.picker.body': 'નવો એજન્ટ પસંદ કરો — અથવા પ્લેટફોર્મ-ડાયરેક્ટ પર અલગ કરો (કોઈ એજન્ટ મલિક નહીં). ભવિષ્યના કમિશન નવા મલિકને જશે; પહેલેથી ક્રેડિટ થયેલી પંક્તિઓને છેડવામાં આવતી નથી.',
  'agent.playerDetail.ownership.picker.detach.title': 'પ્લેટફોર્મ પર અલગ કરો',
  'agent.playerDetail.ownership.picker.detach.body': 'એજન્ટ માલિકી હટાવો. એડમિન સીધો સંભાળે છે.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'એજન્ટ્સ શોધો…',
  'agent.playerDetail.ownership.picker.loading': 'એજન્ટ્સ લોડ થઈ રહ્યા છે…',
  'agent.playerDetail.ownership.picker.empty.search': 'તમારી શોધ સાથે કોઈ એજન્ટ મેળ ખાતો નથી.',
  'agent.playerDetail.ownership.picker.empty.none': 'હજુ સુધી કોઈ સક્રિય એજન્ટ નથી.',
  'agent.playerDetail.ownership.picker.players.one': '{count} ખેલાડી',
  'agent.playerDetail.ownership.picker.players.other': '{count} ખેલાડીઓ',
  'agent.playerDetail.ownership.picker.current': 'વર્તમાન',
  'agent.playerDetail.ownership.picker.currentUpper': 'વર્તમાન',
  'agent.playerDetail.ownership.picker.close': 'બંધ કરો',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'બાલી એજન્ટ',
  'agent.login.subtitle': 'તમારા ખેલાડીઓ મેનેજ કરવા સાઇન ઇન કરો',
  'agent.login.field.identifier': 'યુઝરનેમ અથવા ઇમેઇલ',
  'agent.login.field.password': 'પાસવર્ડ',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'માત્ર એજન્ટ માટે. જો તમે પ્લેટફોર્મ મલિક છો, તો વાપરો ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '.',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'લોગિન નિષ્ફળ',
  'agent.login.error.invalidCredentials': 'ખોટા ક્રેડેન્શિયલ',
  'agent.login.error.accountSuspended': 'એકાઉન્ટ સસ્પેન્ડેડ — તમારા એડમિનિસ્ટ્રેટરનો સંપર્ક કરો.',
  'agent.login.error.networkError': 'નેટવર્ક ભૂલ — તમારું કનેક્શન તપાસો અને ફરી પ્રયત્ન કરો.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'ગેમ',
  'agent.misc.gameTile.actionSuffix': '· કૉન્ફિગર',
};

const ur: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "میری تفصیلات",
  'profile.group.security': "سائن اِن اور سیکیورٹی",
  'profile.personal.title': "ذاتی تفصیلات",
  'profile.personal.editTitle': "ذاتی تفصیلات میں ترمیم کریں",
  'common.edit': "ترمیم",
  'profile.field.name': "نام",
  'profile.field.phone': "فون نمبر",
  'profile.field.gender': "جنس",
  'profile.field.location': "مقام",
  'profile.gender.male': "مرد",
  'profile.gender.female': "عورت",
  'profile.gender.other': "دیگر",
  'profile.gender.preferNot': "بتانا نہیں چاہتا",
  'profile.gender.select': "منتخب کریں…",
  'profile.location.optionalHint': "اختیاری — اپنا علاقہ منتخب کریں یا اپنا پن کوڈ لکھیں (GPS سے یہ زیادہ درست ہو جاتا ہے)۔",
  'profile.lang.title': "زبان",
  'profile.password.title': "پاس ورڈ",
  'profile.password.desc': "اپنے اکاؤنٹ کا پاس ورڈ تبدیل کریں۔ دیگر ڈیوائسز سائن آؤٹ ہو جائیں گی۔",
  'profile.password.cta': "تبدیل کریں",
  'profile.password.editTitle': "پاس ورڈ تبدیل کریں",
  'profile.password.current': "موجودہ پاس ورڈ",
  'profile.password.new': "نیا پاس ورڈ",
  'profile.password.confirm': "نئے پاس ورڈ کی تصدیق کریں",
  'profile.password.currentPh': "آپ کا موجودہ پاس ورڈ",
  'profile.password.newPh': "ایک مضبوط پاس ورڈ منتخب کریں",
  'profile.password.confirmPh': "نیا پاس ورڈ دوبارہ لکھیں",
  'profile.password.rule': "کم از کم 8 حروف، ایک حرف اور ایک عدد کے ساتھ۔",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "اس نام سے منسلک",
  'profile.telegram.otpHere': "پاس ورڈ ری سیٹ کے OTP یہاں آتے ہیں۔",
  'profile.telegram.unlinkChange': "منقطع کریں اور تبدیل کریں",
  'profile.telegram.unlinkTitle': "Telegram منقطع کریں؟",
  'profile.telegram.unlink': "منقطع کریں",
  'profile.telegram.keepLinked': "منسلک رکھیں",
  'profile.telegram.unlinkVerified': "آپ بعد میں کوئی اور Telegram اکاؤنٹ دوبارہ منسلک کر سکتے ہیں۔ ای میل کی تصدیق برقرار رہے گی۔",
  'profile.telegram.unlinkOnly': "Telegram آپ کا واحد تصدیق شدہ ذریعہ ہے — کھیل جاری رکھنے سے پہلے آپ کو اپنے اکاؤنٹ کی دوبارہ تصدیق کرنی پڑ سکتی ہے۔",
  'profile.telegram.notLinkedDesc': "بیک اپ کے طور پر پاس ورڈ ری سیٹ کے OTP حاصل کرنے کے لیے Telegram منسلک کریں۔",
  'profile.telegram.openBot': "@BaaliGames_bot کھولیں",
  'profile.telegram.generate': "لنک بنائیں",
  'profile.telegram.generating': "بنایا جا رہا ہے…",
  'profile.avatar.cropTitle': "اپنی تصویر ایڈجسٹ کریں",
  'profile.toast.detailsSaved': "تفصیلات محفوظ ہو گئیں",
  'profile.toast.photoUpdated': "تصویر اپ ڈیٹ ہو گئی",
  'profile.toast.pwChanged': "پاس ورڈ تبدیل ہو گیا۔ دیگر ڈیوائسز سائن آؤٹ ہو گئی ہیں۔",
  'profile.toast.tgUnlinked': "Telegram منقطع ہو گیا۔ کوئی اور چیٹ جوڑنے کے لیے نیا لنک بنائیں۔",
  'profile.err.phone': "10 ہندسوں کا موبائل نمبر درج کریں (یا اسے ویسے ہی رہنے دیں)",
  'profile.err.pwMismatch': "نئے پاس ورڈ آپس میں مماثل نہیں ہیں",
  'profile.err.pwWeak': "پاس ورڈ 8 یا زیادہ حروف کا ہو، ایک حرف اور ایک عدد کے ساتھ",
  'profile.err.pwSame': "نیا پاس ورڈ موجودہ سے مختلف ہونا چاہیے",
  'wallet.payout.title': "محفوظ شدہ ادائیگی کی تفصیلات",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "ابھی کچھ محفوظ نہیں",
  'wallet.payout.hint': "جب آپ رقم نکالتے ہیں تو آپ کی UPI ID خود بخود محفوظ ہو جاتی ہے۔ آپ اسے اپنی اگلی نکاسی کی درخواست کے دوران اپ ڈیٹ کر سکتے ہیں۔",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} انسٹال کریں",
  "pwa.install.subhint": "فوری، فل اسکرین رسائی کے لیے اسے اپنی ہوم اسکرین پر شامل کریں — کسی ایپ اسٹور کی ضرورت نہیں۔",
  "pwa.install.listen": "سنیں",
  "pwa.install.listening": "روکیں",
  "pwa.install.installBtn": "انسٹال کریں",
  "pwa.install.notNow": "ابھی نہیں",
  "pwa.install.howToShow": "انسٹال کیسے کریں",
  "pwa.install.howToHide": "مراحل چھپائیں",
  "pwa.install.guide.iosSafari": "Safari اسکرین کے نیچے، Share بٹن پر ٹیپ کریں — یہ اوپر کی طرف تیر والے مربع جیسا دکھتا ہے۔\nکھلنے والے مینو میں تھوڑا نیچے اسکرول کریں۔\n\"Add to Home Screen\" کے آپشن پر ٹیپ کریں۔\nاوپر دائیں کونے میں Add پر ٹیپ کریں۔ اب ایپ کسی عام ایپ کی طرح آپ کی ہوم اسکرین پر ہوگی۔",
  "pwa.install.guide.iosOther": "iPhone یا iPad پر، ہوم اسکرین پر شامل کرنا صرف Safari براؤزر میں کام کرتا ہے۔\nSafari کھولیں اور اسی ویب سائٹ پر جائیں۔\nSafari کے نیچے Share بٹن پر ٹیپ کریں — اوپر کی طرف تیر والا مربع۔\nنیچے اسکرول کریں، \"Add to Home Screen\" پر ٹیپ کریں، پھر Add پر ٹیپ کریں۔",
  "pwa.install.guide.android": "اوپر دیے گئے Install بٹن پر ٹیپ کریں۔\nجب آپ کا براؤزر پوچھے، تصدیق کے لیے Install پر ٹیپ کریں۔\nہو گیا — اب ایپ آپ کی ہوم اسکرین پر ہے۔ کسی عام ایپ کی طرح اسے کسی بھی وقت کھولیں۔\nپاپ اپ نظر نہیں آ رہا؟ اپنے براؤزر کا مینو کھولیں — تین نقطے، عام طور پر اوپر دائیں — اور \"Install app\" پر ٹیپ کریں۔",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "براہ راست ری چارج",
  "agent.directRecharge.subtitle.player": "ایک کھلاڑی منتخب کریں اور اپنے پول سے ری چارج کریں۔ کسی درخواست کی ضرورت نہیں۔",
  "agent.directRecharge.subtitle.subagent": "ایک ذیلی ایجنٹ منتخب کریں اور اپنے پول سے اُن کے پول میں ری چارج کریں۔",
  "agent.directRecharge.search.player": "ری چارج کے لیے کھلاڑی تلاش کریں (نام · فون · ای میل)…",
  "agent.directRecharge.search.subagent": "ری چارج کے لیے ذیلی ایجنٹ تلاش کریں (نام · ای میل · UAID)…",
  "agent.directRecharge.clearSearch": "تلاش صاف کریں",
  "agent.directRecharge.searching": "تلاش جاری ہے…",
  "agent.directRecharge.noMatches": "کوئی مماثلت نہیں۔",
  "agent.directRecharge.empty.player": "آپ کی فہرست میں ابھی کوئی کھلاڑی نہیں۔",
  "agent.directRecharge.empty.subagent": "ابھی کوئی براہ راست ذیلی ایجنٹ نہیں۔",
  "agent.directRecharge.searchFailed": "تلاش ناکام",
  "agent.directRecharge.label.wallet": "والیٹ",
  "agent.directRecharge.label.pool": "پول",
  "agent.directRecharge.modal.title.player": "براہ راست کھلاڑی ری چارج",
  "agent.directRecharge.modal.title.subagent": "براہ راست ذیلی ایجنٹ ری چارج",
  "agent.directRecharge.modal.close": "بند کریں",
  "agent.directRecharge.modal.targetPool": "ہدف پول",
  "agent.directRecharge.modal.operatingWallet": "آپریٹنگ والیٹ",
  "agent.directRecharge.modal.dayToDay": "روزمرہ بیلنس",
  "agent.directRecharge.modal.securityDeposit": "سیکیورٹی ڈپازٹ",
  "agent.directRecharge.modal.collateral": "ضمانتی پول",
  "agent.directRecharge.modal.amount": "رقم (₹)",
  "agent.directRecharge.modal.overdraw": "آپ اپنے پول (₹{pool}) سے زیادہ ادا نہیں کر سکتے۔ رقم کم کریں۔",
  "agent.directRecharge.modal.reason": "وجہ / نوٹ",
  "agent.directRecharge.modal.reasonPlaceholder": "نقد وصول · ref 4291",
  "agent.directRecharge.modal.reasonHint": "آپ کے والیٹ لیجر + آڈٹ لاگ میں درج ہوتا ہے۔",
  "agent.directRecharge.modal.proofLabel": "ادائیگی کا اسکرین شاٹ اپ لوڈ کریں (اختیاری)",
  "agent.directRecharge.modal.proofHint": "تاریخ کے ذریعے وصول کنندہ کو نظر آتا ہے۔",
  "agent.directRecharge.modal.previewEmpty": "لین دین دیکھنے کے لیے رقم درج کریں۔",
  "agent.directRecharge.modal.previewBody": "آپ کے والیٹ سے {name} کے {pool} میں بھیجا گیا۔ محفوظ منتقلی؛ لیجر + آڈٹ میں ریکارڈ۔",
  "agent.directRecharge.modal.pool.playerWallet": "والیٹ",
  "agent.directRecharge.modal.pool.operatingWallet": "آپریٹنگ والیٹ",
  "agent.directRecharge.modal.pool.securityDeposit": "سیکیورٹی ڈپازٹ",
  "agent.directRecharge.modal.cancel": "منسوخ",
  "agent.directRecharge.modal.send": "₹{amount} بھیجیں",
  "agent.directRecharge.badge.player": "کھلاڑی",
  "agent.directRecharge.badge.subagent": "ذیلی ایجنٹ",
  "agent.directRecharge.modal.yourDirectSubagent": "آپ کا براہ راست ذیلی ایجنٹ",
  "agent.directRecharge.modal.fundingSource": "فنڈنگ ذریعہ",
  "agent.directRecharge.modal.you": "(آپ)",
  "agent.directRecharge.modal.yourPool": "آپ کا پول",
  "agent.directRecharge.modal.notSignedIn": "ایجنٹ کے طور پر سائن ان نہیں",
  "agent.directRecharge.modal.failed": "ناکام",

  // Common controls
  'common.signIn': 'سائن ان کریں',
  'common.signUp': 'سائن اپ کریں',
  'common.login': 'لاگ ان',
  'common.logout': 'لاگ آؤٹ',
  'common.continue': 'جاری رکھیں',
  'common.cancel': 'منسوخ',
  'common.save': 'محفوظ کریں',
  'common.update': 'اپ ڈیٹ',
  'common.clear': 'صاف کریں',
  'common.back': 'واپس',
  'common.loading': 'لوڈ ہو رہا ہے…',
  'common.sending': 'بھیجا جا رہا ہے…',
  'common.saving': 'محفوظ ہو رہا ہے…',
  'common.optional': 'اختیاری',
  'common.show': 'دکھائیں',
  'common.hide': 'چھپائیں',
  'common.profile': 'پروفائل',
  'common.settings': 'ترتیبات',

  // NavBar / BottomNav
  'nav.brand': 'بالی گیمز',
  'nav.home': 'ہوم',
  'nav.games': 'گیمز',
  'nav.wallet': 'والیٹ',
  'nav.rewards': 'انعامات',
  'nav.profile': 'پروفائل',

  // Login page
  'login.title': 'دوبارہ خوش آمدید',
  'login.subtitle': 'کھیل جاری رکھنے کے لیے سائن ان کریں۔',
  'login.idLabel': 'یوزرنیم، ای میل یا فون',
  'login.idPlaceholder': 'یوزرنیم، ای میل یا موبائل',
  'login.passwordLabel': 'پاس ورڈ',
  'login.passwordPlaceholder': 'اپنا پاس ورڈ درج کریں',
  'login.forgot': 'بھول گئے؟',
  'login.cta': 'سائن ان',
  'login.signingIn': 'سائن ان ہو رہا ہے…',
  'login.newHere': 'یہاں نئے ہیں؟',
  'login.createAccount': 'اکاؤنٹ بنائیں',

  // Register page
  'register.title': 'اپنا اکاؤنٹ بنائیں',
  'register.subtitle': 'ایک ٹیگ منتخب کریں اور آپ اندر ہیں۔',
  'register.usernameLabel': 'یوزرنیم',
  'register.usernamePlaceholder': 'منفرد نام منتخب کریں',
  'register.usernameHelp': 'حروف، نمبر، اور انڈرسکور۔ 3–24 حروف۔',
  'register.emailLabel': 'ای میل',
  'register.emailPlaceholder': 'آپ کا ای میل پتہ',
  'register.mobileLabel': 'موبائل نمبر',
  'register.mobilePlaceholder': '10 ہندسوں کا موبائل',
  'register.mobileHelp': 'ملک کا کوڈ ہم خودکار طور پر شامل کریں گے۔',
  'register.passwordLabel': 'پاس ورڈ',
  'register.passwordPlaceholder': 'ایک مضبوط پاس ورڈ منتخب کریں',
  'register.passwordHelp': 'کم از کم 8 حروف، ایک حرف اور ایک ہندسے کے ساتھ۔',
  'register.referralLabel': 'ریفرل کوڈ',
  'register.referralPlaceholder': 'دوست کا دعوتی کوڈ',
  'register.cta': 'اکاؤنٹ بنائیں',
  'register.creating': 'بنایا جا رہا ہے…',
  'register.alreadyPlayer': 'پہلے سے کھلاڑی ہیں؟',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'آپ کے بارے میں', 'register.step.signin': 'سائن ان', 'register.step.finish': 'مکمل',
  'register.step.aboutSubtitle': 'اپنے بارے میں تھوڑا بتائیں', 'register.step.signinSubtitle': 'سائن ان کا طریقہ سیٹ کریں', 'register.step.finishSubtitle': 'مقام منتخب کریں اور مکمل کریں',
  'register.firstName': 'پہلا نام', 'register.firstNamePh': 'آرو',
  'register.lastName': 'آخری نام', 'register.lastNamePh': 'شرما',
  'register.dob': 'تاریخِ پیدائش', 'register.dobHelp': 'کھیلنے کے لیے آپ کی عمر 18 یا اس سے زیادہ ہونی چاہیے',
  'register.v.required': 'ضروری', 'register.v.nameMin': 'کم از کم 2 حروف',
  'register.v.tooLong': 'بہت لمبا', 'register.v.nameChars': 'صرف حروف، اسپیس اور ہائفن',
  'register.v.invalidDate': 'غلط تاریخ', 'register.v.minAge': 'آپ کی عمر کم از کم 18 ہونی چاہیے',
  'register.v.tooOld': 'براہ کرم سال چیک کریں',
  'register.v.usernameMin': 'کم از کم 3 حروف', 'register.v.usernameMax': 'بہت لمبا (زیادہ سے زیادہ 24)',
  'register.v.usernameChars': 'صرف حروف، اعداد اور انڈر اسکور', 'register.v.emailShape': 'غلط ای میل لگتا ہے',
  'register.v.phoneDigits': '10 ہندسوں کا نمبر ہونا چاہیے', 'register.v.phonePrefix': 'ہندوستانی موبائل 6–9 سے شروع ہوتا ہے',
  'register.v.pwdMin': 'کم از کم 8 حروف', 'register.v.pwdLetter': 'کم از کم ایک حرف ہونا چاہیے',
  'register.v.pwdNumber': 'کم از کم ایک عدد ہونا چاہیے',
  'register.v.referralShort': 'بہت چھوٹا', 'register.v.referralChars': 'صرف حروف اور اعداد',
  'register.v.usernameTaken': 'یہ یوزر نیم پہلے سے لیا جا چکا ہے',
  'register.v.emailTaken': 'یہ ای میل پہلے سے رجسٹرڈ ہے',
  'register.v.phoneTaken': 'یہ نمبر پہلے سے رجسٹرڈ ہے',
  'register.v.noLocation': 'جاری رکھنے کے لیے کم از کم ایک مقام طریقہ شیئر کریں۔',
  'register.welcome.title': 'خوش آمدید', 'register.welcome.subtitle': 'آپ کا اکاؤنٹ تیار ہے۔ ابھی محفوظ کریں یا براہ راست شروع کریں؟',
  'register.welcome.verifyTelegram': 'ٹیلیگرام کے ذریعے تصدیق کریں',
  'register.welcome.verifyExplain': '⚠ ضروری: ابھی ٹیلی گرام سے تصدیق کریں۔ بغیر تصدیق کے، اگر پاس ورڈ بھول گئے تو آپ خود اسے ری سیٹ نہیں کر سکیں گے — صرف آپ کا ایجنٹ یا ایڈمن ہی ری سیٹ کر سکے گا۔ آپ بعد میں پروفائل سے بھی تصدیق کر سکتے ہیں۔',
  'register.welcome.verifyHeads': 'اسے بعد کے لیے محفوظ رکھیں',
  'register.welcome.verifyConsequence': 'ای میل کی تصدیق ابھی دستیاب نہیں ہے۔ بعد میں پاس ورڈ ری سیٹ کرنے کے لیے ٹیلی گرام سے تصدیق کریں۔ اگر آپ ایسا نہیں کرتے تو پاس ورڈ بھول جانے پر صرف ایجنٹ یا ایڈمن ہی ری سیٹ کر سکیں گے۔',
  'register.welcome.continue': 'ایپ میں جاری رکھیں', 'register.welcome.continueLogin': 'جاری رکھنے کے لیے سائن ان کریں', 'register.welcome.useDifferent': 'مختلف اکاؤنٹ استعمال کریں',
  'register.geo.title': 'آپ کہاں سے کھیل رہے ہیں؟', 'register.geo.subtitle': 'قریب ترین ایجنٹ سے جوڑنے کے لیے ہم آپ کا مقام استعمال کرتے ہیں۔',
  'register.geo.showAlt': 'مقام شیئر نہیں کر سکتے؟ مختلف طریقہ استعمال کریں',
  'register.geo.hideAlt': 'دوسرے طریقے چھپائیں', 'register.geo.captured': 'مقام لیا گیا',
  'register.geo.capturedHint': 'آپ پروفائل سے کسی بھی وقت تبدیل کر سکتے ہیں۔',
  'register.geo.verifyNote': 'سائن اپ کے بعد، پروفائل سے ٹیلیگرام (پاس ورڈ ری سیٹ کے لیے) یا ای میل سے کسی بھی وقت تصدیق کر سکتے ہیں۔ تصدیق اختیاری ہے۔',

  // Verify (post-signup)
  'verify.title': 'اپنا اکاؤنٹ تصدیق کریں',
  'verify.subtitle': 'ایک آپشن منتخب کریں — بس اتنا ہی کافی ہے۔',
  'verify.tabTelegram': 'ٹیلیگرام',
  'verify.tabEmail': 'ای میل',
  'verify.emailSent': 'ہم نے {email} پر تصدیقی لنک بھیج دیا ہے۔',
  'verify.emailHint': 'اسے اسی ڈیوائس پر کھولیں۔ نظر نہیں آ رہا؟ سپیم / پروموشنز چیک کریں۔',
  'verify.telegramOpen': '@BaaliGames_bot کھولیں',
  'verify.telegramExpiry': 'لنک 30 منٹ میں ختم ہو جائے گا۔ ٹیلیگرام پر ایک بار Start دبانا ضروری ہے۔',
  'verify.goToLogin': 'لاگ ان پر جائیں',
  'verify.email.title': 'ای میل تصدیق ہو گئی',
  'verify.email.sub': 'آپ کا اکاؤنٹ فعال ہے۔ اب کھیلنے کا وقت ہے۔',
  'verify.email.failed': 'تصدیق ناکام',
  'verify.email.verifying': 'تصدیق ہو رہی ہے…',
  'verify.backRegister': 'رجسٹر پر واپس',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ای میل دوبارہ بھیجیں',
  'verify.gate.resent': 'بھیج دیا!',
  'verify.gate.checking': 'کام ہو رہا ہے…',
  'verify.gate.tgFallback': 'لنک دستی طور پر کھولیں',
  'verify.gate.waiting': 'ہم تصدیق خودکار طور پر معلوم کر لیں گے۔',
  'verify.gate.signOut': 'سائن آؤٹ',

  // Forgot password
  'forgot.title': 'اپنا اکاؤنٹ بحال کریں',
  'forgot.subtitle': 'ہم آپ کو واپس آنے کا راستہ بھیجیں گے۔',
  'forgot.idLabel': 'یوزرنیم، ای میل یا فون',
  'forgot.idPlaceholder': 'یوزرنیم، ای میل یا موبائل',
  'forgot.sendVia': 'بذریعہ بھیجیں',
  'forgot.email': 'ای میل',
  'forgot.telegram': 'ٹیلیگرام OTP',
  'forgot.telegramHelp': 'ٹیلیگرام صرف اس وقت کام کرتا ہے جب آپ کا اکاؤنٹ ہمارے بوٹ سے منسلک ہو۔',
  'forgot.cta': 'بحالی بھیجیں',
  'forgot.sentEmail': 'اگر اکاؤنٹ موجود ہے، تو ری سیٹ لنک ای میل کر دیا گیا ہے۔',
  'forgot.sentTelegram': 'اگر اکاؤنٹ موجود ہے، تو 6 ہندسوں کا کوڈ ٹیلیگرام کے ذریعے بھیج دیا گیا ہے۔',
  'forgot.redirecting': 'ری سیٹ پیج پر بھیجا جا رہا ہے…',
  'forgot.back': 'لاگ ان پر واپس',

  // Reset password
  'reset.title': 'نیا پاس ورڈ سیٹ کریں',
  'reset.subOtp': 'وہ کوڈ استعمال کریں جو ہم نے آپ کو ٹیلیگرام پر بھیجا ہے۔',
  'reset.subLink': 'ایک نیا کومبو منتخب کریں اور سیدھے واپس آ جائیں۔',
  'reset.otpLabel': '6 ہندسوں کا کوڈ',
  'reset.newPasswordLabel': 'نیا پاس ورڈ',
  'reset.cta': 'پاس ورڈ اپ ڈیٹ کریں',
  'reset.updating': 'اپ ڈیٹ ہو رہا ہے…',
  'reset.successTitle': 'پاس ورڈ اپ ڈیٹ ہو گیا',
  'reset.successSub': 'آپ تیار ہیں۔ کھیلنے کا وقت ہے۔',

  // Home (logged-out)
  'home.welcomeTo': 'خوش آمدید',
  'home.heroSub': 'ایک مہارت پر مبنی گیمنگ ہب۔ شامل ہونے کے لیے سائن ان کریں یا اپنا کھلاڑی اکاؤنٹ بنائیں۔',
  'home.skillBased': 'مہارت پر مبنی',
  'home.skillBasedSub': 'حکمت عملی جیتتی ہے',
  'home.fairPlay': 'منصفانہ کھیل',
  'home.fairPlaySub': 'اینٹی چیٹ آن',
  'home.instantPayout': 'UPI ادائیگی',
  'home.instantPayoutSub': '24 گھنٹے کے اندر',
  'home.featured': 'منتخب گیمز',
  'home.tapToPlay': 'کھیلنے کے لیے ٹیپ کریں',
  'home.ready': 'کھیلنے کے لیے تیار؟',
  'home.dropIn': 'شامل ہوں اور حقیقی انعام جیتیں۔',
  'home.start': 'شروع کریں',

  // Home (logged-in)
  'home.welcomeBack': 'دوبارہ خوش آمدید',
  'home.balance': 'آپ کا بیلنس',
  'home.live': 'لائیو',
  'home.add': 'شامل کریں',
  'home.manage': 'انتظام',
  'home.rewards': 'انعامات',
  'home.todayChallenge': 'آج کا چیلنج',
  'home.todayChallengeSub': 'ایک میچ جیتیں — اسٹریک بونس کمائیں۔',
  'home.view': 'دیکھیں',
  'home.yourGames': 'آپ کے گیمز',
  'home.viewAll': 'سب دیکھیں',

  // Profile tabs
  'profile.details': 'تفصیلات',
  'profile.address': 'پتہ',
  'profile.languages': 'زبانیں',
  'profile.account': 'اکاؤنٹ',
  'profile.photo': 'تصویر',
  'profile.preferred': 'پسندیدہ زبان',

  // Profile → Languages tab
  'profile.lang.primary': 'بنیادی زبان',
  'profile.lang.primarySub': 'پوری ایپ کا ڈیفالٹ',
  'profile.lang.locked': 'مقفل',
  'profile.lang.second': 'دوسری زبان',
  'profile.lang.pickHelp': 'وہ زبان منتخب کریں جو آپ بولتے ہیں۔',
  'profile.lang.saved': 'دوسری زبان محفوظ ہو گئی',
  'profile.lang.cleared': 'دوسری زبان صاف ہو گئی',

  // Language switcher
  'lang.switcher.aria': 'زبان منتخب کریں',
  'lang.switcher.title': 'اپنی زبان منتخب کریں',
  'lang.switcher.sub': 'پوری ایپ فوراً تبدیل ہو جائے گی۔',
  'lang.switcher.current': 'موجودہ',

  // Rewards page
  'rewards.title': 'انعامات اور آفرز',
  'rewards.subtitle': 'ابھی کمانے یا کلیم کرنے کے لیے بونس۔',
  'rewards.tab.available': 'دستیاب',
  'rewards.tab.inReview': 'جائزے میں',
  'rewards.tab.claimed': 'کلیم کیے',
  'rewards.empty.available.title': 'اس وقت کوئی آفر نہیں',
  'rewards.empty.available.body': 'جلد دوبارہ چیک کریں — ایڈمن باقاعدگی سے نئی آفرز پوسٹ کرتا ہے۔',
  'rewards.empty.review.title': 'جائزے میں کچھ نہیں',
  'rewards.empty.review.body': 'جن کلیمز کو ایڈمن کی منظوری درکار ہے وہ یہاں ظاہر ہوتے ہیں۔',
  'rewards.empty.claimed.title': 'ابھی کوئی کلیم نہیں',
  'rewards.empty.claimed.body': 'جیسے ہی آپ بونس کماتے ہیں، وہ کلیم کے طور پر یہاں ظاہر ہو جاتا ہے۔',
  'rewards.expires': '{date} کو ختم',
  'rewards.remaining': '{amount} باقی',
  'rewards.cap': 'حد',
  'rewards.claim.cta': 'کلیم کریں',
  'rewards.claim.submit': 'بھیجیں',
  'rewards.claimed.credited': '{date} کو {amount} کلیم کیا گیا',
  'rewards.claimed.pending': 'جائزہ زیر التواء',
  'rewards.claimed.rejected': 'مسترد',
  'rewards.auto.willCredit': 'شرائط پوری ہونے پر خودکار کریڈٹ',
  'rewards.auto.willReview': 'ایڈمن جائزہ قطار میں جاتا ہے',
  'rewards.row.creditedOn': '{date} کو کریڈٹ ہوا',
  'rewards.row.submittedOn': '{date} کو بھیجا گیا',
  'rewards.toast.credited': 'بونس شامل ہو گیا — {amount} آپ کے والیٹ میں',
  'rewards.toast.submitted': 'جائزے کے لیے بھیج دیا گیا',
  'rewards.toast.alreadyClaimed': 'پہلے سے کلیم کیا جا چکا',
  'rewards.toast.rejected': 'کلیم مسترد کر دیا گیا',

  // Wallet page
  'wallet.balance': 'بیلنس',
  'wallet.balance.note': 'اندراج فوراً منہا ہوتے ہیں؛ انعام میچ ختم ہونے پر کریڈٹ ہوتے ہیں۔',
  'wallet.addMoney': 'پیسے شامل کریں',
  'wallet.withdraw': 'نکالیں',
  'wallet.withdraw.minBalance': 'نکالنے کے لیے کم از کم ₹200 درکار ہیں',
  'wallet.history.byGame': 'گیم کے لحاظ سے آپ کی تاریخ',
  'wallet.history.empty': 'ابھی کوئی سرگرمی نہیں۔ آپ کا پہلا میچ یہاں ظاہر ہوگا۔',
  'wallet.activity': 'حالیہ سرگرمی',
  'wallet.activity.empty': 'ابھی کوئی لین دین نہیں۔',
  'wallet.net': 'نیٹ',
  'wallet.newer': 'نیا',
  'wallet.older': 'پرانا',
  'wallet.pending.title': 'زیر التواء درخواستیں',
  'wallet.pending.recharge': '{amount} کا ریچارج — ایڈمن کی منظوری کا انتظار',
  'wallet.pending.withdrawal': '{amount} کی نکاسی — ادائیگی کا انتظار',
  'wallet.state.pending': 'زیر التواء',
  'wallet.state.paid': 'ادا کر دیا',
  'wallet.pending.txnPrefix': 'لین دین:',
  'wallet.pending.upload.label': 'تیز کارروائی کے لیے ادائیگی کا اسکرین شاٹ اپ لوڈ کریں۔',
  'wallet.pending.upload.hint':  'درخواست بنانے کے بعد آپ کے پاس 24 گھنٹے ہیں۔',
  'wallet.pending.upload.failed': 'اپ لوڈ ناکام',
  'wallet.activity.balanceAfter': 'بیلنس {balance}',
  'wallet.activity.range': '{total} میں سے {from}–{to}',
  'wallet.activity.window': 'آپ کی پچھلے {days} دنوں کی سرگرمی',
  'wallet.history.summary.one':   '{matches} میچ · جیتے {wins} · خرچ {bets}',
  'wallet.history.summary.other': '{matches} میچ · جیتے {wins} · خرچ {bets}',
  'wallet.history.summary.refunded': ' · رقم واپس {refunds}',
  'ledger.kind.bet': 'میچ انٹری', 'ledger.kind.win': 'میچ انعام',
  'ledger.kind.refund': 'میچ ریفنڈ', 'ledger.kind.forfeit': 'میچ ضبطی',
  'ledger.kind.fee': 'پلیٹ فارم فیس', 'ledger.kind.recharge': 'ٹاپ اپ',
  'ledger.kind.admin_credit': 'ایڈمن کریڈٹ', 'ledger.kind.admin_debit': 'ایڈمن ڈیبٹ',
  'ledger.kind.referral_bonus': 'ریفرل بونس', 'ledger.kind.signup_bonus': 'سائن اپ بونس',
  'ledger.kind.offer_credit': 'آفر انعام',
  'ledger.kind.withdrawal_hold': 'نکاسی ہولڈ', 'ledger.kind.withdrawal_refund': 'نکاسی ریفنڈ',
  'ledger.kind.agent_topup_in': 'ایجنٹ ٹاپ اپ', 'ledger.kind.agent_co_fund': 'ایجنٹ تعاون', 'ledger.kind.turnover_reward': 'کیش بیک',
  'ledger.withdrawalPaid': 'نکاسی ادا کر دی', 'ledger.withdrawalPaidPartial': 'نکاسی ادا کر دی (جزوی)',
  'ledger.withdrawalPaid.txn': 'ادا کر دی · لین دین {txnId}',
  'ledger.withdrawalRejected': 'نکاسی مسترد', 'ledger.withdrawalRejected.note': 'روکی گئی رقم واپس',
  'ledger.withdrawalExpired': 'نکاسی ختم', 'ledger.awaitingProof': 'ثبوت کا انتظار',
  'proofPicker.button.compact': 'اسکرین شاٹ منتخب کریں', 'proofPicker.button.prominent': 'اسکرین شاٹ شامل کریں',
  'proofPicker.processing': 'پروسیس ہو رہا ہے…',
  'proofPicker.error.mime': 'صرف JPG، PNG یا WebP تصاویر کی اجازت ہے۔',
  'proofPicker.error.size': 'فائل بہت بڑی — زیادہ سے زیادہ 5 MB۔',
  'proofPicker.error.processing': 'تصویر پروسیس نہیں ہو سکی',
  'proofPicker.previewAlt': 'منتخب اسکرین شاٹ کا پیش منظر',
  'proofViewer.state.none': 'اسکرین شاٹ کا انتظار', 'proofViewer.state.available': 'اسکرین شاٹ دیکھیں',
  'proofViewer.state.expired': 'اسکرین شاٹ اب دستیاب نہیں', 'proofViewer.state.viewLimit': 'دیکھنے کی حد پوری',
  'proofViewer.loading': 'لوڈ ہو رہا ہے…',
  'proofViewer.title.viewLimit': 'آپ نے یہ اسکرین شاٹ زیادہ سے زیادہ 2 بار کھولا ہے',
  'proofViewer.error.load': 'اسکرین شاٹ لوڈ نہیں ہو سکا',
  'proofViewer.lastView': 'آخری بار — تصویر ختم ہو جائے گی',
  'proofViewer.viewsRemaining.one': '{n} بار اور دیکھ سکتے ہیں',
  'proofViewer.viewsRemaining.other': '{n} بار اور دیکھ سکتے ہیں',
  'proofViewer.imageAlt': 'ادائیگی کا ثبوت', 'proofViewer.close': 'بند کریں',

  // Recharge flow
  'recharge.title': 'پیسے شامل کریں',
  'recharge.step1.subtitle': 'آپ کتنا شامل کرنا چاہتے ہیں؟',
  'recharge.step1.amount': 'رقم',
  'recharge.step1.range': 'کم از کم {min} · زیادہ سے زیادہ {max}',
  'recharge.step1.minHint': 'کم از کم {min} ریچارج',
  'recharge.step1.maxHint': 'زیادہ سے زیادہ {max}',
  'recharge.step1.quickPick': 'فوری انتخاب',
  'recharge.step2.subtitle': 'UPI سے ادائیگی کریں، پھر جاری رکھیں',
  'recharge.step2.payAmount': 'یہ رقم ادا کریں',
  'recharge.step2.iPaid': 'میں نے ادا کر دیا',
  'recharge.step2.howto.1': 'اپنی UPI ایپ کھولیں (Google Pay / PhonePe / Paytm)۔',
  'recharge.step2.howto.2': 'اوپر دی گئی رقم UPI ID پر ادا کریں یا QR اسکین کریں۔',
  'recharge.step2.howto.3': 'اپنی UPI ایپ سے ٹرانزیکشن ID کاپی کریں — اگلے مرحلے میں آپ کو اس کی ضرورت ہوگی۔',
  "recharge.locked.title": "ریچارج عارضی طور پر دستیاب نہیں",
  "recharge.locked.body": "ہم اس ریچارج راستے میں ایک مسئلہ حل کر رہے ہیں۔ آپ کا والٹ اور رقم نکالنا متاثر نہیں ہوا۔ براہ کرم تھوڑی دیر بعد دوبارہ کوشش کریں۔",
  "recharge.locked.reassure.withdrawals": "رقم نکالنا معمول کے مطابق چل رہا ہے",
  "recharge.locked.reassure.existing": "آپ کی پہلے بھیجی گئی ادائیگی پر کارروائی جاری ہے",
  "recharge.cap.banner": "ابھی زیادہ سے زیادہ ریچارج: {max}. وصول کنندہ کی گنجائش عارضی طور پر محدود ہے۔",
  "recharge.cap.tooHigh": "رقم دستیاب حد سے زیادہ ہے۔ ابھی آپ زیادہ سے زیادہ {max} ریچارج کر سکتے ہیں۔",
  "recharge.notConfigured.title": "ریچارج جلد ہی دستیاب ہوگا",
  "recharge.notConfigured.body": "ہم اس ریچارج کا سیٹ اپ مکمل کر رہے ہیں۔ آپ کا والٹ اور رقم نکالنا متاثر نہیں ہوا۔ براہ کرم تھوڑی دیر بعد دیکھیں۔",
  'recharge.step3.subtitle': 'اپنی ادائیگی کی تصدیق کریں',
  'recharge.step3.amountPaid': 'آپ نے جو رقم ادا کی',
  'recharge.step3.txnId': 'UPI لین دین ID',
  'recharge.step3.txnIdHelp': 'یہ آپ کو UPI ایپ میں ادائیگی کی رسید کے تحت ملے گا — عام طور پر 12 ہندسے۔',
  'recharge.step3.note': 'ایڈمن کے لیے نوٹ',
  'recharge.step3.notePlaceholder': 'مثلاً شریک حیات کے نمبر سے ادا کیا',
  'recharge.step3.submit': 'جائزے کے لیے بھیجیں',
  'recharge.step3.disclaimer': 'ایڈمن UPI ایپ میں ادائیگی کی تصدیق کرتا ہے۔ منظوری کے بعد 24 گھنٹے کے اندر والیٹ میں کریڈٹ ہو جائے گا (اکثر جلدی)۔',
  'recharge.success.title': 'بھیج دیا گیا — جائزے کا انتظار',
  'recharge.success.body': 'آپ کا {amount} کا ریچارج ایڈمن قطار میں ہے۔ 24 گھنٹے کے اندر کریڈٹ ہو جائے گا (اکثر جلدی)۔',
  'recharge.success.refId': 'ریفرنس',
  'recharge.toast.submitted': 'ریچارج ایڈمن جائزے کے لیے بھیج دیا گیا',

  // Withdraw flow
  'withdraw.title': 'نکالیں',
  'withdraw.step1.subtitle': 'ہمیں پیسے کہاں بھیجنے چاہئیں؟',
  'withdraw.step2.subtitle': 'ایک آخری جائزہ',
  'withdraw.balance': 'دستیاب بیلنس',
  'withdraw.amount': 'رقم',
  'withdraw.max': 'زیادہ سے زیادہ',
  'withdraw.upiId': 'آپ کی UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'ہم پیسے یہاں بھیجیں گے۔ بھیجنے سے پہلے دوبارہ چیک کر لیں۔',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'آپ کی پچھلی نکاسی سے پہلے سے بھرا ہوا',
  'withdraw.upiShapeErr': 'UPI ID yourname@psp کی شکل میں ہونی چاہیے (مثلاً lucky@oksbi)',
  'withdraw.phoneShapeErr': 'ایک درست 10 ہندسوں کا انڈین موبائل درج کریں (6، 7، 8 یا 9 سے شروع ہونا چاہیے)',
  'withdraw.whatsapp': 'واٹس ایپ نمبر',
  'withdraw.errMin': 'کم از کم نکاسی {min} ہے',
  'withdraw.errExceed': 'رقم آپ کے بیلنس سے زیادہ ہے',
  'withdraw.minHint': 'کم از کم {min} نکاسی',
  'withdraw.step2.review': 'جائزہ',
  'withdraw.step2.heldNotice': 'جب آپ بھیجتے ہیں، یہ رقم فوراً روک لی جاتی ہے تاکہ کہیں اور خرچ نہ ہو۔ ایڈمن اسے آپ کی UPI ID پر ادا کر کے ادا شدہ نشان زد کرتا ہے۔',
  'withdraw.heldAfter': 'روکی گئی',
  'withdraw.spendableAfter': 'قابلِ خرچ',
  'withdraw.step2.disclaimer': 'اگر ایڈمن مسترد کرے، تو روکی گئی رقم آپ کے والیٹ میں واپس آ جاتی ہے۔',
  'withdraw.submit': 'بھیجیں',
  'withdraw.success.title': 'بھیج دیا گیا — ادائیگی کا انتظار',
  'withdraw.success.body': 'آپ کی {amount} کی نکاسی قطار میں ہے۔ 24 گھنٹے کے اندر ادائیگی (اکثر جلدی)۔ تب تک روکی گئی رقم محفوظ رہے گی۔',
  'withdraw.success.refId': 'ریفرنس',
  'withdraw.toast.submitted': 'نکاسی قطار میں — آپ کے والیٹ میں روکی گئی',

  // Payment receiver card
  'payment.upi.label': 'اس UPI ID پر ادا کریں',
  'payment.upi.copy': 'کاپی',
  'payment.upiCopied': 'UPI ID کاپی ہو گئی',
  'payment.openUpiApp': 'ابھی ادا کریں',
  'payment.openWhatsapp': 'واٹس ایپ',
  'payment.helpAt': 'مدد چاہیے؟ کال کریں',
  'payment.qr.label': 'ادائیگی کے لیے QR اسکین کریں',
  'payment.qr.tap': 'بڑا کرنے کے لیے ٹیپ کریں',
  'payment.qr.tapToClose': 'بند کرنے کے لیے کہیں بھی ٹیپ کریں',
  'payment.notConfigured.title': 'ابھی ریچارج دستیاب نہیں',
  'payment.notConfigured.contactBody': 'ابھی کوئی UPI سیٹ نہیں ہے۔ واٹس ایپ یا فون کے ذریعے ہم سے رابطہ کریں اور ہم آپ کو ریچارج میں مدد دیں گے۔',
  'payment.notConfigured.unavailable': 'ریچارج عارضی طور پر دستیاب نہیں۔ براہ کرم بعد میں دوبارہ کوشش کریں۔',
  'payment.chooser.title': 'اپنی UPI ایپ منتخب کریں',
  'payment.chooser.subtitle': 'آپ ادا کریں گے',
  'payment.chooser.hint': 'ایپ کو رقم پہلے سے بھرے ہوئے کھولنے کے لیے ٹیپ کریں۔ ایپ آپ کے فون پر پہلے سے انسٹال ہونی چاہیے — اگر آپ کو "address invalid" کی خرابی نظر آئے، تو پہلے App Store / Play Store سے ایپ انسٹال کریں۔',

  // Common
  'common.next': 'اگلا',
  'common.close': 'بند کریں',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'تیز اور ہموار ادائیگی کی سروس کے لیے درست مقام اور رابطہ نمبر فراہم کریں۔',
  'geo.change': 'تبدیل کریں',
  'geo.gps.button': 'میرا موجودہ مقام استعمال کریں',
  'geo.gps.benefit': 'تیز ترین — ایک ٹیپ، کوئی ٹائپنگ نہیں۔',
  'geo.gps.denied': 'مقام کی اجازت مسترد۔ پن کوڈ استعمال کریں یا نیچے منتخب کریں۔',
  'geo.gps.failed': 'مقام نہیں ملا۔ پن کوڈ آزمائیں۔',
  'geo.gps.notResolved': 'آپ کا مقام کسی علاقے سے میچ نہیں ہوا۔ براہ کرم منتخب کریں۔',
  'geo.pincode.label': 'پن کوڈ',
  'geo.pincode.notFound': 'پن کوڈ نہیں ملا۔ براہ کرم اپنا علاقہ منتخب کریں۔',
  'geo.pincode.lookupFailed': 'پن کوڈ تلاش ناکام۔ براہ کرم منتخب کریں۔',
  'geo.multipleAreas.label': 'اس پن کوڈ میں کئی علاقے — اپنا منتخب کریں',
  'geo.multipleAreas.placeholder': 'اپنا علاقہ منتخب کریں',
  'geo.manual.title': 'دستی طور پر منتخب کریں',
  'geo.manual.close': 'دستی چھپائیں',
  'geo.state.label': 'ریاست',
  'geo.state.placeholder': 'ریاست منتخب کریں',
  'geo.district.label': 'ضلع',
  'geo.district.placeholder': 'ضلع منتخب کریں',
  'geo.area.label': 'علاقہ / شہر',
  'geo.area.placeholder': 'علاقہ منتخب کریں',
  'geo.privacy.note': 'صرف مقامی ایجنٹ تفویض کرنے کے لیے۔ شیئر نہیں ہوتا۔',
  'geo.required.hint': 'ریفرل کوڈ نہ ہونے پر مقام لازمی ہے۔',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'ڈیش بورڈ',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'حساب',
  'agent.nav.players': 'کھلاڑی',
  'agent.nav.agents': 'ایجنٹس',
  'agent.nav.rewards': 'انعامات',
  'agent.nav.profile':   'پروفائل',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'ہوم',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'حساب',
  'agent.bottomnav.players': 'کھلاڑی',
  'agent.bottomnav.agents': 'ایجنٹس',
  'agent.bottomnav.rewards': 'انعامات',
  'agent.bottomnav.profile': 'پروفائل',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'دوبارہ خوش آمدید، ',
  'agent.dashboard.header.welcomeFallback': 'دوست',
  'agent.dashboard.header.subtitle': 'آپ کا کاروبار ایک نظر میں — پول، ڈپازٹ، کھلاڑی، سب-ایجنٹس، P&L، ایکسپوژر',
  'agent.dashboard.range.today': 'آج',
  'agent.dashboard.range.yesterday': 'کل',
  'agent.dashboard.range.thisWeek': 'اس ہفتے',
  'agent.dashboard.range.lastWeek': 'پچھلے ہفتے',
  'agent.dashboard.range.thisMonth': 'اس مہینے',
  'agent.dashboard.range.lastMonth': 'پچھلے مہینے',
  'agent.dashboard.range.complete': 'مکمل',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'ڈپازٹ کوریج لوڈ ہو رہا ہے…',
  'agent.dashboard.depositBar.label': 'ڈپازٹ کوریج',
  'agent.dashboard.depositBar.balanceOfTarget': '· ہدف {target} میں سے {balance}',
  'agent.dashboard.depositBar.tapToView': 'مکمل ڈپازٹ پیج دیکھنے کے لیے ٹیپ کریں۔',
  'agent.dashboard.depositBar.caption.suspended': 'اکاؤنٹ معطل۔ ایڈمن سے رابطہ کریں۔',
  'agent.dashboard.depositBar.caption.breach': 'آپ کے والیٹ نے ڈپازٹ کی حد کا 100% کھپا لیا ہے۔ نکاسی بند ہے۔ فوراً ٹاپ-اپ کریں۔',
  'agent.dashboard.depositBar.caption.warning': 'آپ کا والیٹ ڈپازٹ کی حد کے {pct}% پر ہے۔ انتباہ 80% پر، لاک ڈاؤن 100% پر۔',
  'agent.dashboard.depositBar.caption.caution': 'آپ کے والیٹ نے ڈپازٹ کوریج کا {pct}% کھپا لیا ہے۔ 80% سے کم — اب بھی محفوظ۔',
  'agent.dashboard.depositBar.caption.safe': 'ڈپازٹ مکمل طور پر محفوظ۔ کوئی کوریج استعمال نہیں ہو رہی۔',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'انتباہ · سکیورٹی ڈپازٹ صفر',
  'agent.dashboard.locatorSkip.body': 'آپ کا سکیورٹی ڈپازٹ {amount} ہے۔ جب تک آپ اسے فنڈ نہیں کرتے، جغرافیائی لوکیٹر سے نئے کھلاڑی نہیں ملیں گے۔ موجودہ کھلاڑی + براہِ راست ریفرل + دستی اسائنمنٹ متاثر نہیں ہوں گے — صرف آرگینک جیو-روٹڈ سائن اپ آپ کو چھوڑ دیں گے۔',
  'agent.dashboard.locatorSkip.cta': 'ڈپازٹ فنڈ کریں',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'ڈاؤن لائن ڈیفالٹر انتباہ',
  'agent.dashboard.defaulter.summary.critical.one': 'آپ کا {n} براہِ راست سب-ایجنٹ ڈیفالٹ میں ہے — وہ لاک ہے یا اپنا ڈپازٹ توڑ چکا ہے۔',
  'agent.dashboard.defaulter.summary.critical.other': 'آپ کے {n} براہِ راست سب-ایجنٹس ڈیفالٹ میں ہیں — وہ لاک ہیں یا ڈپازٹ توڑ چکے ہیں۔',
  'agent.dashboard.defaulter.summary.warning.one': 'آپ کا {n} براہِ راست سب-ایجنٹ اپنی ڈپازٹ حد کے 80%+ پر ہے — ڈیفالٹر کا خطرہ۔',
  'agent.dashboard.defaulter.summary.warning.other': 'آپ کے {n} براہِ راست سب-ایجنٹس اپنی ڈپازٹ حد کے 80%+ پر ہیں — ڈیفالٹر کا خطرہ۔',
  'agent.dashboard.defaulter.worst': 'سب سے خراب: @{username} ڈپازٹ حد کے {pct}% پر۔',
  'agent.dashboard.defaulter.tapHint': 'براہِ راست سب-ایجنٹس کی فہرست پر جانے کے لیے ٹیپ کریں۔ ڈیفالٹر قطاریں نمایاں ہیں۔',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'لاک',
  'agent.dashboard.marginPill.caution': 'احتیاط',
  'agent.dashboard.marginPill.warning': 'انتباہ',
  'agent.dashboard.marginPill.breach': 'بریچ',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'صبح کا بریفنگ',
  'agent.dashboard.sections.morning.subtitle': 'لائیو سنیپ شاٹ — ہر نمبر خودکار طور پر ریفریش ہوتا ہے',
  'agent.dashboard.sections.activity.title': 'سرگرمی',
  'agent.dashboard.sections.activity.subtitle': 'سب سے زیادہ خرچ کرنے والے اور حالیہ کھلاڑی ادائیگی ایونٹس',
  'agent.dashboard.sections.forensic.title': 'تفصیلی فرانزک',
  'agent.dashboard.sections.forensic.subtitle': 'کسی بھی سیکشن کو پھیلانے کے لیے ٹیپ کریں — یہ پیج پرسکون رکھنے کے لیے بائے ڈیفالٹ بند ہیں',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'منافع و نقصان کی گہری تفصیل',
  'agent.dashboard.accordions.pnlDeep.helper': 'آپ کی کمائی کی فی-پلر + فی-گیم تفصیل۔ بنیادی میچز کے لیے گیم قطار پر کلک کریں۔',
  'agent.dashboard.accordions.leaderboard.title': 'ڈاؤن لائن لیڈربورڈ',
  'agent.dashboard.accordions.leaderboard.helper': 'آپ کے سب-ایجنٹس نیٹ کمائی کی بنیاد پر درجہ بند۔ منفی نمبر کا مطلب ہے کہ اس سب-ایجنٹ نے لائے سے زیادہ ادا کیا۔',
  'agent.dashboard.accordions.activity.title': 'ڈاؤن لائن سرگرمی فیڈ',
  'agent.dashboard.accordions.activity.helper': 'آپ کے سب-ایجنٹس میں ہر میچ سیٹلمنٹ، ریچارج اور ریٹ تبدیلی — فرانزک ٹائم لائن۔',
  'agent.dashboard.accordions.earnings.title': 'کمائی کی تفصیل (تاحیات)',
  'agent.dashboard.accordions.earnings.helper': 'قسم کے لحاظ سے تاحیات ٹوٹل۔ اوپر کی رینج ٹوگل کو نظر انداز کرتا ہے — یہ وقت کے فلٹر سے کبھی نہیں بدلتے۔',
  'agent.dashboard.accordions.directSubAgents.title': 'براہِ راست سب-ایجنٹس',
  'agent.dashboard.accordions.directSubAgents.helper': 'آپ کے براہِ راست لیفٹیننٹس۔ ہر ایک اپنے کھلاڑی + اپنے سب-ایجنٹس چلاتا ہے۔ گلابی/کہربائی قطاریں ڈیفالٹ زون میں ہیں۔',
  'agent.dashboard.accordions.chain.title': 'چین کی نمائش',
  'agent.dashboard.accordions.chain.helper': 'آپ کا کل ڈاؤن لائن نقشہ، براہِ راست (آپ کے ذریعہ ایڈٹ) اور بالواسطہ (آپ کے سب-ایجنٹس کے ذریعہ منظم) میں الگ۔',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'لائیو ڈیٹا — فوکس پر، ہر 60 سیکنڈ، اور جب پیسہ منتقل ہو تب ریفریش۔',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'میرا پول بیلنس',
  'agent.dashboard.cards.poolBalance.helper': 'آپ کا قابلِ خرچ بیلنس۔ وہ پیسہ جو آپ کھلاڑیوں کو ریچارج یا سب-ایجنٹس کو بھیجنے کے لیے استعمال کر سکتے ہیں۔',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} میں نیٹ {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'سکیورٹی ڈپازٹ',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'ڈپازٹ کی حالت',
  'agent.dashboard.cards.depositStatus.helperLoading': 'ڈپازٹ کی حالت لوڈ ہو رہی ہے…',
  'agent.dashboard.cards.depositStatus.helper': 'آپ کے پول کے خلاف کولیٹرل۔ حالت ہدف کے 50% پر احتیاط، 80% پر انتباہ، 100% پر بریچ ہو جاتی ہے۔',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} سے',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'آپریٹنگ بیلنس',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'ڈپازٹ بیلنس',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'ڈپازٹ ہدف',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'منفی ایکسپوژر',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'کوریج ریشو',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'ڈپازٹ ہدف کا حصہ جو منفی ایکسپوژر نے استعمال کیا',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'محفوظ',
  'agent.dashboard.depositStatus.status.caution': 'احتیاط',
  'agent.dashboard.depositStatus.status.warning': 'انتباہ',
  'agent.dashboard.depositStatus.status.breach': 'بریچ',
  'agent.dashboard.depositStatus.status.suspended': 'معطل',
  'agent.dashboard.depositStatus.status.healthy': 'صحت مند',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'کھلاڑیوں کا والیٹ کل',
  'agent.dashboard.cards.playersWallet.helper': 'ابھی آپ کے ہر کھلاڑی کے والیٹ کا مشترکہ لائیو بیلنس۔ آپ سے منسلک ہر wallet_points قطار کا مجموعہ۔',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} کھلاڑی · {active} فعال',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} کھلاڑی · {active} فعال',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'سب-ایجنٹس والیٹ + ڈپازٹ',
  'agent.dashboard.cards.subAgents.helper': 'آپ کے براہِ راست سب-ایجنٹس کے مجموعی پول بیلنس اور سکیورٹی ڈپازٹ۔ ہر سب-ایجنٹ اپنے کھلاڑی + اپنی ڈاؤن لائن چلاتا ہے۔',
  'agent.dashboard.cards.subAgents.field.pool': 'پول',
  'agent.dashboard.cards.subAgents.field.deposit': 'ڈپازٹ',
  'agent.dashboard.cards.subAgents.foot.one': '{n} براہِ راست سب-ایجنٹ',
  'agent.dashboard.cards.subAgents.foot.other': '{n} براہِ راست سب-ایجنٹس',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'میری زیر التواء درخواستیں',
  'agent.dashboard.cards.myPending.helper': 'پرائیورٹی 3۔ آپ کی اپنی ایڈمن کو بھیجی گئی درخواستیں — ان پر آپ خود ایکشن نہیں لے سکتے، انتظار کرنا ہے۔',
  'agent.dashboard.cards.myPending.recharge.one': '{n} ریچارج ایڈمن کے انتظار میں',
  'agent.dashboard.cards.myPending.recharge.other': '{n} ریچارجز ایڈمن کے انتظار میں',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} نکاسی ایڈمن کے انتظار میں',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} نکاسیاں ایڈمن کے انتظار میں',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'کھلاڑیوں کی زیر التواء درخواستیں',
  'agent.dashboard.cards.playersPending.helper': 'پرائیورٹی 1۔ آپ کے کھلاڑی آپ کے انتظار میں ہیں کہ آپ ان کی منظوری دیں یا ادائیگی کریں۔ ٹیپ کرکے ایکشن لیں۔',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} ریچارج منظور کرنے کے لیے',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} ریچارجز منظور کرنے کے لیے',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} نکاسی ادا کرنے کے لیے',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} نکاسیاں ادا کرنے کے لیے',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'سب-ایجنٹس کی زیر التواء درخواستیں',
  'agent.dashboard.cards.subAgentsPending.helper': 'پرائیورٹی 2۔ آپ کے سب-ایجنٹس (مکمل ڈاؤن لائن) آپ کے انتظار میں ہیں کہ آپ ان کی منظوری دیں یا ادائیگی کریں۔ ٹیپ کرکے ایکشن لیں۔',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'True Net P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'اس رینج میں آپ کا اصل منافع/نقصان۔ پلیٹ فارم فیس حصہ + ون/لاس حصہ − آفر کو-فنڈنگ۔ فی-گیم تفصیل کے لیے نیچے P&L تفصیل پھیلائیں۔',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'Plat',
  'agent.dashboard.pillarChip.winLoss': 'W/L',
  'agent.dashboard.pillarChip.offer': 'آفر',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'آفر استعمال · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'اس رینج میں آفرز کے ذریعے آپ کے کھلاڑیوں کو ادا کل انعام ₹۔ اس لاگت میں اپنا حصہ دیکھنے کے لیے اپنے ایکسپوژر پیج سے موازنہ کریں۔',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} کلیم کریڈٹ',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} کلیمز کریڈٹ',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'سب سے زیادہ خرچ کرنے والے',
  'agent.dashboard.activity.topSpenders.helper': '{range} میں آپ کے سب سے زیادہ خرچ کرنے والے کھلاڑی۔',
  'agent.dashboard.activity.topSpenders.empty': 'اس رینج میں ابھی کوئی خرچ نہیں۔',
  'agent.dashboard.activity.recent.title': 'حالیہ سرگرمی',
  'agent.dashboard.activity.recent.helper': 'آپ کے کھلاڑیوں سے پانچ سب سے حالیہ ریچارج ایونٹس۔',
  'agent.dashboard.activity.recent.empty': 'کوئی حالیہ سرگرمی نہیں۔',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'منظور',
  'agent.dashboard.activity.recent.state.rejected': 'مسترد',
  'agent.dashboard.activity.recent.state.pending': 'زیر التواء',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'فی گیم · تشریح کے لیے قطار پر ٹیپ کریں',
  'agent.dashboard.pnl.noEarnings': 'اس رینج میں کوئی کمائی نہیں۔',
  'agent.dashboard.pnl.table.col.pillarGame': 'پلر / گیم',
  'agent.dashboard.pnl.table.col.net': 'نیٹ',
  'agent.dashboard.pnl.table.col.matches': 'میچز',
  'agent.dashboard.pnl.ledgerRows.one': '{n} لیجر قطار',
  'agent.dashboard.pnl.ledgerRows.other': '{n} لیجر قطاریں',
  'agent.dashboard.pnl.pillar.platformFee': 'پلیٹ فارم فیس',
  'agent.dashboard.pnl.pillar.winLoss': 'ون / لاس',
  'agent.dashboard.pnl.pillar.offerReward': 'آفر انعام',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'ونڈو: {range} — ڈیش بورڈ کی رینج کا سخت ذیلی سیٹ',
  'agent.dashboard.pnlDrill.loading': 'میچ کی تفصیلات لوڈ ہو رہی ہیں…',
  'agent.dashboard.pnlDrill.windowTotal': 'ونڈو کل:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} میچ میں',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} میچز میں',
  'agent.dashboard.pnlDrill.noContribution': 'اس سیل میں کوئی میچ شامل نہیں۔',
  'agent.dashboard.pnlDrill.errorFallback': 'ڈرل ڈاؤن لوڈ نہیں ہو سکا',
  'agent.dashboard.pnlDrill.table.col.match': 'میچ',
  'agent.dashboard.pnlDrill.table.col.role': 'کردار',
  'agent.dashboard.pnlDrill.table.col.base': 'بیس',
  'agent.dashboard.pnlDrill.table.col.rate': 'ریٹ',
  'agent.dashboard.pnlDrill.table.col.earned': 'کمایا',
  'agent.dashboard.pnlDrill.table.col.settled': 'طے شدہ',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'لوڈ ہو رہا ہے…',
  'agent.dashboard.directDownline.empty': 'ابھی کوئی براہِ راست سب-ایجنٹس نہیں۔',
  'agent.dashboard.directDownline.row.player.one': '{n} کھلاڑی',
  'agent.dashboard.directDownline.row.player.other': '{n} کھلاڑی',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} سب-ایجنٹ',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} سب-ایجنٹس',
  'agent.dashboard.directDownline.row.walletDeposit': 'والیٹ {wallet} · ڈپازٹ {deposit}',
  'agent.dashboard.directDownline.footer': 'True Net {range} ونڈو کے لیے ہے۔',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'لوڈ ہو رہا ہے…',
  'agent.dashboard.chain.directAgents.title': 'براہِ راست ایجنٹس',
  'agent.dashboard.chain.directAgents.subtitle': 'آپ کے براہِ راست سب-ایجنٹس — آپ ان کی درخواستوں پر ایکشن لے سکتے ہیں۔',
  'agent.dashboard.chain.indirectAgents.title': 'بالواسطہ ایجنٹس',
  'agent.dashboard.chain.indirectAgents.subtitle': 'آپ کے سب-ایجنٹس کے ذریعہ منظم۔ آپ کے لیے صرف-پڑھنے کی۔',
  'agent.dashboard.chain.directPlayers.title': 'براہِ راست کھلاڑی',
  'agent.dashboard.chain.directPlayers.subtitle': 'آپ کے کھلاڑی — آپ ان کے ریچارج اور نکاسی پر ایکشن لیتے ہیں۔',
  'agent.dashboard.chain.indirectPlayers.title': 'بالواسطہ کھلاڑی',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'آپ کے سب-ایجنٹس کے کھلاڑی۔ آپ کے لیے صرف-پڑھنے کی۔',
  'agent.dashboard.chain.field.walletTotal': 'والیٹ کل',
  'agent.dashboard.chain.field.depositTotal': 'ڈپازٹ کل',
  'agent.dashboard.chain.field.avgPerHead': 'اوسط فی شخص',
  'agent.dashboard.chain.field.pendingRecharge': 'زیر التواء ریچارج',
  'agent.dashboard.chain.field.pendingWithdrawal': 'زیر التواء نکاسی',
  'agent.dashboard.chain.pendingBadge': '{n} زیر التواء',
  'agent.dashboard.chain.openInbox': 'ان باکس کھولیں',
  'agent.dashboard.chain.footer': 'براہِ راست = جنہیں آپ خود سنبھالتے ہیں۔ بالواسطہ = آپ کے سب-ایجنٹس سنبھالتے ہیں (آپ نمبرز دیکھتے ہیں مگر ان کی زیر التواء درخواستوں پر ایکشن نہیں لے سکتے — وہ ان کے والدین کو جاتی ہیں)۔',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'لوڈ ناکام',
  'agent.activity.filter.window': 'ونڈو',
  'agent.activity.filter.kind': 'قسم',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'تمام',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'تمام',
  'agent.activity.kind.matches': 'میچز',
  'agent.activity.kind.recharges': 'ریچارجز',
  'agent.activity.kind.rateChanges': 'ریٹ تبدیلیاں',
  // Empty + loading states.
  'agent.activity.loading': 'لوڈ ہو رہا ہے…',
  'agent.activity.empty.title': 'خاموش',
  'agent.activity.empty.subtitle': '{window} ونڈو میں کوئی {kindWord} ایونٹ نہیں۔',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'میچ ',
  'agent.activity.empty.kindWord.recharge': 'ریچارج ',
  'agent.activity.empty.kindWord.partnership_change': 'پارٹنرشپ تبدیلی ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'سیٹلمنٹ',
  'agent.activity.row.earned': 'کمایا',
  'agent.activity.row.fromPillarOn': '{pillar} سے ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'پلیٹ فارم فیس',
  'agent.activity.row.pillar.winLoss': 'ون/لاس',
  'agent.activity.row.pillar.offerReward': 'آفر انعام',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'ریچارج',
  'agent.activity.row.recharge.of': 'کا',
  'agent.activity.row.recharge.byUser': 'صارف #{id} کے ذریعہ',
  'agent.activity.row.recharge.via': ' بذریعہ ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'منظور',
  'agent.activity.row.state.rejected': 'مسترد',
  'agent.activity.row.state.pending': 'زیر التواء',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'ریٹ تبدیلی',
  'agent.activity.row.rateChange.dashOn': '— {pillar} پر ',
  'agent.activity.row.rateChange.cascade': 'کیسکیڈ',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'یہ ویو صرف ایجنٹس کے لیے ہے۔',
  'agent.earnings.stat.poolBalance': 'پول بیلنس',
  'agent.earnings.stat.totalIn': 'کل اِن',
  'agent.earnings.stat.totalOut': 'کل آؤٹ',
  'agent.earnings.section.in': 'کمائی (اِن)',
  'agent.earnings.section.out': 'اخراجات (آؤٹ)',
  'agent.earnings.empty.in': 'ابھی کوئی کمائی نہیں۔ جب آپ کے کھلاڑی ریچارج یا کھیلیں گے، یہاں کٹ نظر آئیں گی۔',
  'agent.earnings.empty.out': 'ابھی کوئی اخراجات نہیں۔',
  'agent.earnings.viewLedger': 'مکمل لیجر دیکھیں',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'ایڈمن سے ٹاپ-اپ',
  'agent.earnings.kind.topupFromAdmin.hint': 'ایڈمن نے آپ کا پول کریڈٹ کیا',
  'agent.earnings.kind.topupToPlayer.label': 'کھلاڑی کو ٹاپ-اپ',
  'agent.earnings.kind.topupToPlayer.hint': 'آپ نے ایک کھلاڑی کو ریچارج کیا',
  'agent.earnings.kind.topupFromPlayer.label': 'کھلاڑی ریچارج',
  'agent.earnings.kind.topupFromPlayer.hint': 'آپ کے کھلاڑیوں نے آپ کے پول میں ادا کیا',
  'agent.earnings.kind.revenueShare.label': 'آمدنی حصہ',
  'agent.earnings.kind.revenueShare.hint': 'میچز سے پلیٹ فارم فیس کا حصہ',
  'agent.earnings.kind.coFundOffer.label': 'کو-فنڈ آفر',
  'agent.earnings.kind.coFundOffer.hint': 'آفر ادائیگیوں میں آپ کا حصہ',
  'agent.earnings.kind.adminAdjust.label': 'ایڈمن ایڈجسٹمنٹ',
  'agent.earnings.kind.adminAdjust.hint': 'دستی اصلاح',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'اندراج',
  'agent.earnings.row.entry.other': 'اندراجات',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'ونڈو',
  'agent.leaderboard.filter.pillar': 'پلر',
  'agent.leaderboard.window.all': 'تمام',
  'agent.leaderboard.pillar.all': 'تمام پلرز',
  'agent.leaderboard.pillar.platformFee': 'پلیٹ فارم فیس',
  'agent.leaderboard.pillar.winLoss': 'ون / لاس',
  'agent.leaderboard.pillar.offerReward': 'آفر انعام',
  'agent.leaderboard.loading': 'لوڈ ہو رہا ہے…',
  'agent.leaderboard.empty.title': 'ابھی کوئی کمائی نہیں',
  'agent.leaderboard.empty.subtitle.admin': 'تمام ایجنٹس میں {window} ونڈو میں کوئی سیٹلمنٹ سرگرمی نہیں۔',
  'agent.leaderboard.empty.subtitle.downline': '{window} ونڈو میں آپ کی ڈاؤن لائن سے کوئی کمائی نہیں۔ جیسے ہی آپ کے سب-ایجنٹس میچ چلائیں گے، یہ بورڈ بھر جائے گا۔',
  'agent.leaderboard.row.matches.one': '{n} میچ',
  'agent.leaderboard.row.matches.other': '{n} میچز',
  'agent.leaderboard.footer': 'کمائی match_settlement_snapshots سے۔ منفی نمبر کا مطلب ہے کہ اس ایجنٹ نے ونڈو میں لینے سے زیادہ ادا کیا۔',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'Plat',
  'agent.leaderboard.chip.winLoss': 'W/L',
  'agent.leaderboard.chip.offer': 'آفر',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'مارجن انتباہ · 80% حد تک پہنچ گیا',
  'agent.warning.header.title': 'لاک آؤٹ سے ایک خراب سیٹلمنٹ دور۔',
  'agent.warning.header.body': 'آپ ابھی لاک نہیں ہیں — مگر آپ کا ڈپازٹ سیفٹی کشن 80% سے زیادہ خرچ ہو چکا ہے۔ ابھی ٹاپ-اپ کریں اور کنٹرول میں رہیں۔',
  'agent.warning.header.dismissAria': 'بند کریں',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'یہ کیوں دکھ رہا ہے',
  'agent.warning.why.body': 'حالیہ گیم سیٹلمنٹس نے آپ کے والیٹ کو منفی میں دھکیل دیا۔ آپ کا {deposit} سکیورٹی ڈپازٹ یہ نقصان جذب کرنے والا حفاظتی جال ہے — اور اب 80% سے زیادہ خرچ ہو چکا ہے۔ اگر تھوڑا اور منفی آیا، تو والیٹ + ڈپازٹ صفر ہو جائے گا اور پلیٹ فارم اکاؤنٹ لاک کر دے گا۔',
  'agent.warning.why.reassurance': 'یہ انتباہ جان بوجھ کر جلدی آتا ہے — جب آپ پوری ایپ استعمال کر سکتے ہیں۔ لاک آؤٹ سکرین صرف اس وقت آتی ہے جب 100% پار ہوتا ہے، اور اس کے بعد زیادہ تر فیچرز بحال ہونے تک بند رہتے ہیں۔ ابھی عمل کرنا بعد میں عمل کرنے سے بہت کم خلل ڈالنے والا ہے۔',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'لاک آؤٹ سے پہلے گنجائش',
  'agent.warning.gauge.suffix': 'گنجائش باقی',
  'agent.warning.gauge.dangerBadge': 'خطرے کے زون میں {pct}%',
  'agent.warning.gauge.field.wallet': 'آپ کا والیٹ',
  'agent.warning.gauge.field.deposit': 'آپ کا ڈپازٹ',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'اگر اسے نظر انداز کریں اور 100% تک پہنچ جائیں',
  'agent.warning.ignore.cardTitle': 'آپ فوری کھو دیں گے',
  'agent.warning.ignore.item.dashboard': 'اپنے ڈیش بورڈ، P&L اور لیڈربورڈز تک رسائی',
  'agent.warning.ignore.item.pages': 'کھلاڑی اور ایجنٹس پیجز',
  'agent.warning.ignore.item.manualTopup': 'اپنے پول سے کھلاڑیوں کو دستی ٹاپ-اپ',
  'agent.warning.ignore.item.newWithdrawal': 'اپنے اپ لائن کو نئی نکاسی بھیجنا',
  'agent.warning.ignore.item.subAgents': 'سب-ایجنٹس شامل یا منظم کرنا',
  'agent.warning.ignore.item.profile': 'اپنا پروفائل یا ادائیگی سیٹنگز ایڈٹ کرنا',
  'agent.warning.ignore.footer': 'آپ پھر بھی زیر التواء درخواستیں منظور + ایمرجنسی ٹاپ-اپ بھیج سکیں گے — مگر باقی سب کچھ رک جائے گا جب تک والیٹ + ڈپازٹ صفر سے اوپر نہ آ جائے۔',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'بحالی کے لیے ابھی اپنے اپ لائن کو ادا کریں',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'میں اس سے بعد میں نمٹوں گا',
  'agent.warning.dismiss.footnote': 'اگر آپ انتباہ زون میں رہے تو 10 منٹ میں ہم آپ کو دوبارہ یاد دلائیں گے۔ بحال ہوتے ہی خودکار بند ہو جاتا ہے۔',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'اکاؤنٹ لاک · بیل آؤٹ پورٹل',
  'agent.lockout.header.title': 'آپ پر ڈپازٹ سے زیادہ واجب الادا ہے۔',
  'agent.lockout.header.body': 'پریشان نہ ہوں — یہ بحال ہو سکتا ہے۔ نیچے دیے گئے دو مراحل پر عمل کریں اور آپ خودکار طور پر ان لاک ہو جائیں گے۔',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'یہ کیوں ہوا',
  'agent.lockout.why.body': 'گیم سیٹلمنٹس نے آپ کے والیٹ کو صفر سے نیچے دھکیل دیا۔ آپ کا سکیورٹی ڈپازٹ یہ جذب کرنے والا حفاظتی جال ہے — مگر آپ اس کا 100% استعمال کر چکے ہیں۔ جب تک والیٹ + ڈپازٹ صفر سے اوپر نہ آ جائے، آپ معمول کے مطابق کام نہیں کر سکتے۔',
  'agent.lockout.why.reassurance': 'پلیٹ فارم آپ کو اس لیے لاک نہیں کرتا کہ آپ نے کچھ غلط کیا۔ یہ آپ کو اس لیے لاک کرتا ہے تاکہ نیٹ ورک آپ کے ڈپازٹ کے خلاف مزید قرض جمع نہ کرے جب آپ مستحکم ہو رہے ہوں۔',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'محفوظ بحالی ہدف',
  'agent.lockout.gauge.suffix': 'محفوظ ادائیگی کے لیے',
  'agent.lockout.gauge.recoveredBadge': '{pct}% بحال',
  'agent.lockout.gauge.field.wallet': 'آپ کا والیٹ',
  'agent.lockout.gauge.field.deposit': 'آپ کا ڈپازٹ',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'صرف ان لاک',
  'agent.lockout.tier.justUnlock.tag': 'خطرناک',
  'agent.lockout.tier.justUnlock.note': 'آپ کو ₹0 پر واپس لاتا ہے — کوئی بھی نیا نقصان فوراً دوبارہ لاک کر دیتا ہے۔',
  'agent.lockout.tier.safeMin.label': 'محفوظ کم سے کم',
  'agent.lockout.tier.safeMin.tag': 'سفارش کردہ',
  'agent.lockout.tier.safeMin.note': 'ڈپازٹ ({deposit}) + آپ کے منفی والیٹ کا نصف۔ ایک حقیقی کشن چھوڑتا ہے۔',
  'agent.lockout.tier.fullRecovery.label': 'مکمل بحالی',
  'agent.lockout.tier.fullRecovery.tag': 'بہترین',
  'agent.lockout.tier.fullRecovery.note': 'ڈپازٹ مکمل بحال اور والیٹ صفر پر واپس۔',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'بحالی کے دو طریقے — جو بھی کریں (یا دونوں)',
  'agent.lockout.waysOut.inbox.title': 'اپنے ان باکس پر ایکشن لیں',
  'agent.lockout.waysOut.inbox.body': 'کھلاڑی ریچارج منظور کریں (نقدی پہلے سے آپ کے پاس ہے) اور کھلاڑی نکاسی کو ادا شدہ نشان زد کریں۔ ہر منظوری آپ کے والیٹ کو کریڈٹ کرتی ہے۔',
  'agent.lockout.waysOut.inbox.ctaOpen': 'ان باکس کھولیں',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ان باکس نیچے کھلا ہے',
  'agent.lockout.waysOut.upline.title': 'اپنے اپ لائن کو ادا کریں',
  'agent.lockout.waysOut.upline.body': 'UPI کے ذریعے اپنے والدین ایجنٹ (یا ایڈمن) کو نقدی بھیجیں، پھر txn id جمع کرائیں۔ ان کی منظوری کے بعد، آپ کا والیٹ فوراً کریڈٹ ہو جاتا ہے۔',
  'agent.lockout.waysOut.upline.ctaOpen': 'فارم کھولیں',
  'agent.lockout.waysOut.upline.ctaOpened': 'فارم نیچے کھلا ہے',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'جو آپ اب بھی کر سکتے ہیں (اور جو بند ہے)',
  'agent.lockout.capabilities.canHeader': 'آپ اب بھی کر سکتے ہیں',
  'agent.lockout.capabilities.cantHeader': 'آپ نہیں کر سکتے (عارضی طور پر)',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'آنے والی کھلاڑی ریچارج منظور کرنا',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'کھلاڑی نکاسی کو ادا شدہ نشان زد کرنا',
  'agent.lockout.can.approveSubAgentRecharge': 'سب-ایجنٹ ریچارج منظور کرنا',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'سب-ایجنٹ نکاسی کو ادا شدہ نشان زد کرنا',
  'agent.lockout.can.submitToUpline': 'اپنے اپ لائن کو ریچارج بھیجنا',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE الرٹ وصول کرنا + سائن آؤٹ',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'مرحلہ {n} · {title}',
  'agent.lockout.section.inbox.title': 'آپ کا ان باکس — ہر منظوری آپ کے والیٹ کو کریڈٹ کرتی ہے',
  'agent.lockout.section.inbox.subtitle': 'کھلاڑیوں اور سب-ایجنٹس کی زیر التواء ادائیگی درخواستیں جو آپ کے ایکشن کا انتظار کر رہی ہیں۔',
  'agent.lockout.section.inbox.fromPlayers': 'آپ کے کھلاڑیوں سے',
  'agent.lockout.section.inbox.fromSubAgents': 'آپ کے سب-ایجنٹس سے',
  'agent.lockout.section.upline.title': 'اپنا والیٹ ٹاپ-اپ کرنے کے لیے اپنے اپ لائن کو ادا کریں',
  'agent.lockout.section.upline.subtitle': 'آپ کے سامنے والدین کا UPI ہو تو تیز ہے۔ منظوری عام طور پر منٹوں میں ہوتی ہے۔',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'ریچارج · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · جمع کرایا {when}',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · جمع کرایا {when} · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'نکاسی · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi} کو ادا کریں · جمع کرایا {when}',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi} کو ادا کریں · {whatsapp} · جمع کرایا {when}',
  'agent.lockout.queue.subAgent.recharge.title': 'سب-ایجنٹ ریچارج · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · جمع کرایا {when}',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · جمع کرایا {when} · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'سب-ایجنٹ نکاسی · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi} کو ادا کریں · جمع کرایا {when}',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi} کو ادا کریں · جمع کرایا {when} · "{note}"',

  'agent.lockout.queue.action.approve': 'منظور کریں',
  'agent.lockout.queue.action.reject': 'مسترد کریں',
  'agent.lockout.queue.action.markPaid': 'ادا شدہ نشان زد کریں',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'وہ UPI ٹرانزیکشن ID درج کریں جس سے آپ نے کھلاڑی کو ادائیگی کی:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'وہ UPI ٹرانزیکشن ID درج کریں جس سے آپ نے سب-ایجنٹ کو ادائیگی کی:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'اس نکاسی کو مسترد کریں؟ روکی گئی رقم کھلاڑی کو واپس مل جائے گی۔',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'اس سب-ایجنٹ نکاسی کو مسترد کریں؟',
  'agent.lockout.queue.toast.rechargeApproved': 'ریچارج منظور — کھلاڑی کریڈٹ',
  'agent.lockout.queue.toast.rechargeRejected': 'ریچارج مسترد',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'سب-ایجنٹ ریچارج منظور',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'ریچارج مسترد',
  'agent.lockout.queue.toast.withdrawalPaid': 'نکاسی ادا شدہ نشان زد — آپ کا والیٹ کریڈٹ',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'سب-ایجنٹ نکاسی ادا شدہ نشان زد — والیٹ کریڈٹ',
  'agent.lockout.queue.toast.withdrawalRejected': 'نکاسی مسترد (کھلاڑی کو واپس)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'نکاسی مسترد',
  'agent.lockout.queue.toast.actionFailed': 'ایکشن ناکام',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'ابھی آپ کے کھلاڑیوں سے کچھ نہیں۔',
  'agent.lockout.queue.empty.subAgent': 'ابھی آپ کے سب-ایجنٹس سے کچھ نہیں۔',
  'agent.lockout.queue.loading': 'قطار لوڈ ہو رہی ہے…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'ریفریش — کیا میں ابھی ان لاک ہوں؟',
  'agent.lockout.refresh.checking': 'چیک ہو رہا ہے…',
  'agent.lockout.refresh.toast': 'حالت ریفریش',
  'agent.lockout.refresh.footnote': 'یہ پیج خودکار طور پر اس لمحے بند ہو جاتا ہے جب آپ کا والیٹ + ڈپازٹ صفر سے اوپر آ جاتا ہے۔',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'ریچارج تفصیلات لوڈ نہیں ہو سکیں',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'اپنا اکاؤنٹ ان لاک کرنے کے لیے ابھی ادا کریں',
  'agent.emergencyForm.banner.locked.body': 'آپ کے کھلاڑی آپ کے ڈپازٹ کولیٹرل سے زیادہ جیت چکے ہیں۔ منفی بیلنس صاف کیے بغیر آپ پلیٹ فارم استعمال نہیں کر سکتے۔',
  'agent.emergencyForm.banner.warning.title': 'جلد ٹاپ-اپ کریں — آپ حد کے قریب ہیں',
  'agent.emergencyForm.banner.warning.body': 'آپ کا والیٹ ڈپازٹ کے 80% کو پار کر چکا ہے۔ 100% تک پہنچنے سے پہلے ٹاپ-اپ کریں — ایک بار ہو گیا تو اکاؤنٹ لاک ہو جائے گا۔',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'آپ کا والیٹ',
  'agent.emergencyForm.row.deposit': 'آپ کا ڈپازٹ',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'آپ کیوں پھنسے ہیں',
  'agent.emergencyForm.tier.eyebrow.warning': 'آپ کیوں خطرے میں ہیں',
  'agent.emergencyForm.tier.context.locked': 'آپ کا والیٹ {wallet} ہو گیا — یہ آپ کے {deposit} ڈپازٹ سیفٹی کشن سے باہر ہے۔ پلیٹ فارم آپ کو یہاں لاک کرتا ہے تاکہ آپ کی ڈاؤن لائن کے نقصانات خالی بفر کے خلاف بڑھتے نہ رہیں۔',
  'agent.emergencyForm.tier.context.warning': 'آپ کا والیٹ {wallet} ہو گیا — یہ آپ کے {deposit} ڈپازٹ سیفٹی کشن کے 80% سے زیادہ ہے۔ ایک اور خراب سیٹلمنٹ آپ کو 100% سے اوپر دھکیلے گا اور اکاؤنٹ لاک ہو جائے گا۔',
  'agent.emergencyForm.tier.pickHowMuch': 'کتنا ادا کرنا ہے، منتخب کریں',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'صرف ان لاک',
  'agent.emergencyForm.tier.bareMin.label.warning': 'صرف انتباہ سے بچیں',
  'agent.emergencyForm.tier.bareMin.tag': 'خطرناک',
  'agent.emergencyForm.tier.bareMin.note.locked': 'صرف آپ کا والیٹ + ڈپازٹ ₹0 پر واپس لاتا ہے۔ کوئی بھی نیا نقصان آپ کو فوراً دوبارہ لاک کر دیتا ہے۔',
  'agent.emergencyForm.tier.bareMin.note.warning': 'صرف آپ کو 80% لائن سے ذرا اوپر لاتا ہے۔ کوئی بھی نیا نقصان انتباہ دوبارہ ٹرگر کرے گا — یا آپ کو لاک آؤٹ میں دھکیل دے گا۔',
  'agent.emergencyForm.tier.safeMin.label': 'محفوظ کم سے کم',
  'agent.emergencyForm.tier.safeMin.tag': 'سفارش کردہ',
  'agent.emergencyForm.tier.safeMin.note': 'آپ کا ڈپازٹ ({deposit}) + آپ کے منفی والیٹ کا نصف ({half})۔ آپ کو حقیقی کشن دیتا ہے۔',
  'agent.emergencyForm.tier.full.label': 'مکمل بحالی',
  'agent.emergencyForm.tier.full.tag': 'بہترین',
  'agent.emergencyForm.tier.full.note': 'ڈپازٹ + آپ کا پورا منفی والیٹ ({neg})۔ ڈپازٹ مکمل بحال اور والیٹ صفر پر۔',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'اپنے ایڈمن کو ادا کریں',
  'agent.emergencyForm.parent.label.parentAgent': 'اپنے والدین ایجنٹ کو ادا کریں',
  'agent.emergencyForm.parent.role.admin': 'پلیٹ فارم ایڈمن',
  'agent.emergencyForm.parent.role.parentAgent': 'والدین ایجنٹ',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile': 'آپ کے والدین کی ادائیگی تفصیلات ابھی فائل پر نہیں ہیں۔ براہ کرم پلیٹ فارم سپورٹ سے براہِ راست رابطہ کریں۔',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'ادا کردہ رقم (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI ٹرانزیکشن ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'مثلاً 412312345678',
  'agent.emergencyForm.field.note.label': 'نوٹ (اختیاری)',
  'agent.emergencyForm.field.note.placeholder': 'کوئی بات جو آپ کے والدین کو معلوم ہونی چاہیے',
  'agent.emergencyForm.validation.amount': 'ادا کی گئی رقم درج کریں (کم از کم ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI ٹرانزیکشن ID / UTR درج کریں (کم از کم 6 حروف)',
  'agent.emergencyForm.cta': 'ریچارج درخواست جمع کرائیں',
  'agent.emergencyForm.ctaSubmitting': 'جمع ہو رہا ہے…',
  // Success state.
  'agent.emergencyForm.success.title': 'ریچارج جمع کرا دیا گیا',
  'agent.emergencyForm.success.bodyAdmin': 'درخواست #{id} ایڈمن کی منظوری کے انتظار میں ہے۔ منظوری کے بعد، آپ کا والیٹ کریڈٹ ہو جائے گا اور اکاؤنٹ خودکار ان لاک ہو جائے گا۔',
  'agent.emergencyForm.success.bodyAgent': 'درخواست #{id} @{username} کی منظوری کے انتظار میں ہے۔ منظوری کے بعد، آپ کا والیٹ کریڈٹ ہو جائے گا اور اکاؤنٹ خودکار ان لاک ہو جائے گا۔',
  'agent.emergencyForm.toast.submitted': 'ریچارج درخواست #{id} جمع کرا دی گئی۔ منظوری کا انتظار۔',
  'agent.emergencyForm.toast.cantSave': 'زبان محفوظ نہیں ہو سکی',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'ایک بار کا سیٹ اپ: اپنا مقام تصدیق کریں',
  'agent.geo.subtitle': 'ڈیش بورڈ استعمال کرنے سے پہلے ضروری۔',
  'agent.geo.body.primary': 'بالی گیمز پلیٹ فارم نئے کھلاڑیوں کو ان کے قریب ترین ایجنٹ کو روٹ کرتا ہے۔ جب تک آپ اپنا درست GPS مقام شیئر نہیں کرتے، آپ لوکیٹر میں نظر نہیں آئیں گے اور آرگینک سائن اپ سے صفر نئے کھلاڑی ملیں گے — صرف وہ کھلاڑی ملیں گے جو آپ کا ریفرل کوڈ براہِ راست ٹائپ کریں۔',
  'agent.geo.body.secondary': '"میرا موجودہ مقام استعمال کریں" پر ایک بار ٹیپ کریں۔ ہم صرف آپ کے کوآرڈینیٹس لیتے ہیں، کبھی کچھ اور نہیں۔',
  'agent.geo.hint.disabled': 'اوپر "میرا موجودہ مقام استعمال کریں" پر ٹیپ کریں۔ صرف پن کوڈ یا دستی علاقہ منتخب کرنا کافی نہیں — لوکیٹر کو درست GPS کوآرڈینیٹس چاہیے۔',
  'agent.geo.error.fallback': 'مقام محفوظ نہیں ہو سکا۔ براہ کرم دوبارہ کوشش کریں۔',
  'agent.geo.cta.save': 'محفوظ کریں اور جاری رکھیں',
  'agent.geo.cta.saving': 'مقام محفوظ ہو رہا ہے…',
  'agent.geo.toast.success': 'مقام محفوظ — اب آپ کھلاڑی وصول کر سکتے ہیں۔',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'پول بیلنس',
  'agent.wallet.hero.helper': 'کھلاڑی ریچارج اور سب-ایجنٹ ٹاپ-اپ پر خرچ کرنے کے لیے دستیاب۔ کمائی اور ایڈمن ٹاپ-اپ یہاں آتے ہیں۔',

  'agent.wallet.action.recharge': 'ریچارج کی درخواست',
  'agent.wallet.action.withdraw': 'نکاسی کی درخواست',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'زیر التواء',
  'agent.wallet.tab.history': 'تاریخ',

  // Pending feed.
  'agent.wallet.pending.loading': 'لوڈ ہو رہا ہے…',
  'agent.wallet.pending.empty': 'ابھی کوئی زیر التواء درخواست نہیں۔',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'ریچارج درخواست · +{amount}',
  'agent.wallet.pending.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'نکاسی درخواست · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi} کو · {when}',
  'agent.wallet.pending.badge': 'زیر التواء',

  // History feed.
  'agent.wallet.history.loading': 'لوڈ ہو رہا ہے…',
  'agent.wallet.history.empty': 'ابھی والیٹ کی کوئی سرگرمی نہیں۔',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'ریچارج قبول · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'ریچارج مسترد · {amount}',
  'agent.wallet.history.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'نکاسی ادا · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'نکاسی مسترد · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi} کو · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'ادا · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'قبول',
  'agent.wallet.state.paid': 'ادا',
  'agent.wallet.state.rejected': 'مسترد',
  'agent.wallet.state.expired': 'ختم',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'ایڈمن سے ٹاپ-اپ',
  'agent.wallet.ledger.kind.topupToPlayer': 'کھلاڑی کو ٹاپ-اپ',
  'agent.wallet.ledger.kind.topupFromPlayer': 'کھلاڑی ریچارج',
  'agent.wallet.ledger.kind.revenueShare': 'آمدنی حصہ',
  'agent.wallet.ledger.kind.coFundOffer': 'کو-فنڈ آفر',
  'agent.wallet.ledger.kind.adminAdjust': 'ایڈمن ایڈجسٹمنٹ',
  'agent.wallet.ledger.kind.withdrawalHold': 'نکاسی ہولڈ',
  'agent.wallet.ledger.kind.withdrawalPaid': 'نکاسی ادا',
  'agent.wallet.ledger.kind.withdrawalRefund': 'نکاسی واپسی',
  'agent.wallet.ledger.balanceAfter': 'بیل {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'سکیورٹی ڈپازٹ',
  'agent.deposit.hero.helper': 'آپ کے پول کے خلاف کولیٹرل۔ جب تک آپ کا والیٹ اچھی حالت میں ہے، چھوا نہیں جاتا۔',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'محفوظ',
  'agent.deposit.margin.warning': 'انتباہ',
  'agent.deposit.margin.locked': 'لاک',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'ڈپازٹ ریچارج',
  'agent.deposit.action.withdraw': 'ڈپازٹ واپس لیں',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'کوریج گیج',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'انتباہ',
  'agent.deposit.gauge.thresholdLock': 'لاک',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct} پر، آپ کو انتباہ ملے گا',
  'agent.deposit.gauge.legend.lock': '{pct} پر، آپ کا اکاؤنٹ لاک ہو جاتا ہے',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'پول مکمل طور پر ڈپازٹ سے ڈھکا ہوا — ٹاپ-اپ تک نکاسی بند۔',
  'agent.deposit.gauge.caption.warning': 'پول نے آپ کے ڈپازٹ کا {pct}% کھپا لیا ہے۔ لاک آؤٹ سے بچنے کے لیے 100% سے پہلے ٹاپ-اپ کریں۔',
  'agent.deposit.gauge.caption.caution': 'پول ڈپازٹ کوریج میں {pct}% ہے۔ آپ ابھی 80% انتباہ سے محفوظ نیچے ہیں۔',
  'agent.deposit.gauge.caption.safe': 'آپ کا ڈپازٹ مکمل محفوظ ہے۔',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'لوڈ ہو رہا ہے…',
  'agent.deposit.ledger.empty.title': 'ابھی ڈپازٹ کی کوئی سرگرمی نہیں',
  'agent.deposit.ledger.empty.body': 'جب ایڈمن آپ کا ڈپازٹ کریڈٹ کرے گا (یا واپسی پروسیس کرے گا)، ہر اندراج یہاں آئے گا۔',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'اپنا والیٹ ریچارج کریں',
  'agent.recharge.banner.eyebrow.deposit': 'اپنا ڈپازٹ ریچارج کریں',
  'agent.recharge.banner.body.wallet': 'پہلے اپنے اپ لائن کو ادا کریں، پھر یہاں ثبوت جمع کرائیں تاکہ وہ آپ کا آپریٹنگ والیٹ ٹاپ-اپ کر سکیں۔',
  'agent.recharge.banner.body.deposit': 'پہلے اپنے اپ لائن کو ادا کریں، پھر یہاں ثبوت جمع کرائیں تاکہ وہ آپ کا سکیورٹی ڈپازٹ ٹاپ-اپ کر سکیں۔',
  'agent.recharge.banner.closeAria': 'فارم بند کریں',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'اپنے ایڈمن کو ادا کریں',
  'agent.recharge.parent.label.parentAgent': 'اپنے والدین ایجنٹ کو ادا کریں',
  'agent.recharge.parent.role.admin': 'پلیٹ فارم ایڈمن',
  'agent.recharge.parent.role.parentAgent': 'والدین ایجنٹ',
  'agent.recharge.parent.noQr': 'کوئی QR سیٹ نہیں',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'آپ کے اپ لائن کی ادائیگی تفصیلات لوڈ ہو رہی ہیں…',
  'agent.recharge.parent.loadError': 'اپ لائن ادائیگی تفصیلات لوڈ نہیں ہو سکیں',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'آپ نے کیسے ادائیگی کی؟',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'آن لائن ڈیجیٹل ادائیگی',
  'agent.recharge.method.cash.label': 'نقد',
  'agent.recharge.method.cash.note': 'بالمشافہ حوالگی',

  // Form fields.
  'agent.recharge.field.amount.label': 'رقم (₹) · کم از کم ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI ٹرانزیکشن ID / UTR',
  'agent.recharge.field.txn.label.cash': 'نقد رسید مارکر (کوئی مختصر حوالہ)',
  'agent.recharge.field.txn.placeholder.upi': 'مثلاً 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'مثلاً CASH-Sun-eve',
  'agent.recharge.field.note.label': 'نوٹ (اختیاری، آپ کے اپ لائن کو دکھایا جائے گا)',
  'agent.recharge.field.note.placeholder': 'اپنے اپ لائن کے لیے کوئی سیاق و سباق',

  // Validation errors.
  'agent.recharge.error.amountMin': 'رقم ≥ 10 پورا روپیہ ہونی چاہیے',
  'agent.recharge.error.txnUpi': 'ٹرانزیکشن ID بہت چھوٹا لگتا ہے — UPI حوالہ پیسٹ کریں',
  'agent.recharge.error.txnCash': 'مختصر مارکر شامل کریں (مثلاً "CASH-Sun-eve") تاکہ اپ لائن حوالگی کو میچ کر سکے',

  // CTAs.
  'agent.recharge.cta.submit': 'درخواست جمع کرائیں',
  'agent.recharge.cta.cancel': 'منسوخ',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'والیٹ سے نکالیں',
  'agent.withdrawal.banner.eyebrow.deposit': 'ڈپازٹ سے نکالیں',
  'agent.withdrawal.banner.body.wallet': 'رقم فوراً روک لی جاتی ہے تاکہ آپ ڈبل خرچ نہ کریں۔ آپ کا اپ لائن آپ کو ادا کر کے ادا شدہ نشان زد کرتا ہے۔',
  'agent.withdrawal.banner.body.deposit': 'رقم فوراً آپ کے سکیورٹی ڈپازٹ سے روک لی جاتی ہے۔ آپ کا اپ لائن آپ کو ادا کر کے ادا شدہ نشان زد کرتا ہے۔',
  'agent.withdrawal.banner.closeAria': 'فارم بند کریں',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'آپ کیسے ادائیگی چاہتے ہیں؟',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'آن لائن ڈیجیٹل ادائیگی',
  'agent.withdrawal.method.cash.label': 'نقد',
  'agent.withdrawal.method.cash.note': 'بالمشافہ حوالگی',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'رقم (₹) · دستیاب {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'آپ کی UPI ID (جہاں آپ کا اپ لائن ادائیگی کرے گا)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'ملاقات مارکر (مثلاً "اتوار شام نقد")',
  'agent.withdrawal.field.cashMarker.placeholder': 'نقدی کب/کہاں دینا ہے',
  'agent.withdrawal.field.whatsapp.label': 'WhatsApp (اختیاری، آپ کے اپ لائن کے رابطے کے لیے)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'نوٹ (اختیاری)',
  'agent.withdrawal.field.note.placeholder': 'اپنے اپ لائن کے لیے کوئی سیاق و سباق',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'رقم ≥ 10 پورا روپیہ ہونی چاہیے',
  'agent.withdrawal.error.amountOverflow': 'آپ کے پاس صرف {balance} دستیاب ہے',
  'agent.withdrawal.error.upi': 'درست UPI ID درج کریں',

  // CTAs.
  'agent.withdrawal.cta.submit': 'درخواست جمع کرائیں',
  'agent.withdrawal.cta.cancel': 'منسوخ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'کھلاڑی',
  'agent.people.hub.players.subtitle': 'کھلاڑیوں اور ان کی درخواستوں کو منظم کریں',
  'agent.people.hub.players.subtab.requests': 'کھلاڑی درخواستیں',
  'agent.people.hub.players.subtab.list': 'کھلاڑی',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'ایجنٹس',
  'agent.people.hub.agents.subtitle': 'سب-ایجنٹس اور ان کی درخواستوں کو منظم کریں',
  'agent.people.hub.agents.subtab.requests': 'ایجنٹ درخواستیں',
  'agent.people.hub.agents.subtab.list': 'ایجنٹس',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'نئے کھلاڑی وصول کر رہے ہیں',
  'agent.people.acceptingToggle.bodyAccepting': 'آپ جغرافیائی لوکیٹر سے نئے کھلاڑی قبول کر رہے ہیں۔ آپ کے علاقے میں سرد سائن اپ خودکار آپ کو روٹ ہوتے ہیں۔',
  'agent.people.acceptingToggle.bodyOptedOut': 'آپ نے آپٹ آؤٹ کیا ہے۔ لوکیٹر آپ کو چھوڑ رہا ہے — صرف براہِ راست ریفرل + دستی اسائن کھلاڑی آپ تک پہنچیں گے۔ موجودہ کھلاڑی متاثر نہیں۔',
  'agent.people.acceptingToggle.error': 'اپ ڈیٹ نہیں ہو سکا',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'نئے کھلاڑی قبول کرنا بند کریں',
  'agent.people.acceptingToggle.aria.start': 'نئے کھلاڑی قبول کرنا شروع کریں',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'کھلاڑی',
  'agent.qr.audience.subAgent': 'ایجنٹس',
  'agent.qr.alt': 'آپ کا ادائیگی QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'میرا QR · جو کھلاڑی دیکھتے ہیں',
  'agent.qr.cardLabel.subAgent': 'میرا QR · جو ایجنٹس دیکھتے ہیں',
  'agent.qr.upi.empty': 'ابھی کوئی UPI سیٹ نہیں',
  // Status pill.
  'agent.qr.status.loading': 'لوڈ ہو رہا ہے…',
  'agent.qr.status.live': 'لائیو',
  'agent.qr.status.notSet': 'سیٹ نہیں',
  'agent.qr.status.paused': 'روکا گیا',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR اور UPI ایڈٹ کریں',
  'agent.qr.cta.setup': 'QR سیٹ اپ کریں',
  'agent.qr.cta.viewOnly': 'صرف دیکھنا — ایڈٹ کے لیے ایڈمن سے کہیں',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR اور UPI ایڈٹ کریں',
  'agent.qr.modal.closeAria': 'بند',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR اپ لوڈ کریں',
  'agent.qr.modal.replace': 'QR بدلیں',
  'agent.qr.modal.remove': 'ہٹائیں',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR تصویر ہٹائیں؟ آپ کے کھلاڑی صرف رابطہ فال بیک دیکھیں گے۔',
  'agent.qr.modal.confirmRemove.subAgent': 'QR تصویر ہٹائیں؟ آپ کے ایجنٹس صرف رابطہ فال بیک دیکھیں گے۔',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'مثلاً baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'WhatsApp نمبر (فال بیک)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ایجنٹس کے لیے WhatsApp نمبر',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'خالی → {number} پر فال بیک',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'سب-ایجنٹس {number} دیکھیں گے (آپ کا کھلاڑی-فیسنگ نمبر)۔',
  'agent.qr.modal.field.phone.label.player': 'سپورٹ فون (فال بیک)',
  'agent.qr.modal.field.phone.label.subAgent': 'ایجنٹس کے لیے سپورٹ فون',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'خالی → {number} پر فال بیک',
  'agent.qr.modal.field.phone.fallbackHint': 'سب-ایجنٹس {number} دیکھیں گے (آپ کا کھلاڑی-فیسنگ نمبر)۔',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'فعال',
  'agent.qr.modal.active.description': 'کھلاڑی ریچارج جمع کرا سکتے ہیں',
  'agent.qr.modal.active.on': 'آن',
  'agent.qr.modal.active.paused': 'روکا گیا',

  // Footer buttons.
  'agent.qr.modal.cancel': 'منسوخ',
  'agent.qr.modal.save': 'محفوظ کریں',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'دستی ریچارج',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'پیسے بھیجیں ایک ',
  'agent.manualRecharge.banner.body.target': 'کھلاڑی',
  'agent.manualRecharge.banner.body.after': ' کو سیدھے اپنے پول سے۔',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'آپ کا پول',
  'agent.manualRecharge.pool.dailyCap': 'حد · {amount}/دن',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'کھلاڑی کو',
  'agent.manualRecharge.field.player.placeholder': 'کھلاڑی منتخب کریں…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · بیل {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'کھلاڑی کو',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · موجودہ بیل {balance}',
  'agent.manualRecharge.field.player.notFound': 'کھلاڑی آپ کی فہرست میں نہیں ملا۔',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'رقم (₹) — {min} سے {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'آپ کے پول بیلنس سے زیادہ۔',
  'agent.manualRecharge.error.belowMin': 'کم از کم ({min}) سے کم۔',
  'agent.manualRecharge.error.aboveMax': 'زیادہ سے زیادہ ({max}) سے زیادہ۔',

  // Note field.
  'agent.manualRecharge.field.note.label': 'نوٹ (اختیاری، کھلاڑی لیجر پر دکھایا جائے گا)',
  'agent.manualRecharge.field.note.placeholder': 'مثلاً دکان پر نقدی وصول',

  // Loading + success states.
  'agent.manualRecharge.loading': 'آپ کا پول لوڈ ہو رہا ہے…',
  'agent.manualRecharge.cta.cancel': 'منسوخ',
  'agent.manualRecharge.cta.send': '{amount} بھیجیں',
  'agent.manualRecharge.success.title': '@{username} کو {amount} بھیجا',
  'agent.manualRecharge.success.meta': 'آپ کا پول · {pool} · ان کا والیٹ · {playerBal}',
  'agent.manualRecharge.success.again': 'ایک اور بھیجیں',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'دستی ریچارج',
  'agent.manualSubAgentRecharge.banner.body.before': 'پیسے بھیجیں ایک ',
  'agent.manualSubAgentRecharge.banner.body.target': 'سب-ایجنٹ',
  'agent.manualSubAgentRecharge.banner.body.after': ' کو سیدھے اپنے پول سے۔',

  'agent.manualSubAgentRecharge.pool.label': 'آپ کا پول',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'سب-ایجنٹ کو',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'سب-ایجنٹ منتخب کریں…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · پول {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'کوئی فعال براہِ راست سب-ایجنٹس نہیں',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'سب-ایجنٹ کو',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · موجودہ پول {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'سب-ایجنٹ آپ کی براہِ راست ڈاؤن لائن میں نہیں ملا۔',

  'agent.manualSubAgentRecharge.field.amount.label': 'رقم (₹) — {min} سے {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'آپ کے پول بیلنس سے زیادہ۔',
  'agent.manualSubAgentRecharge.error.belowMin': 'کم از کم ({min}) سے کم۔',

  'agent.manualSubAgentRecharge.field.note.label': 'نوٹ (اختیاری، دونوں لیجرز پر دکھایا جائے گا)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'مثلاً فلوٹ ایڈوانس',

  'agent.manualSubAgentRecharge.loading': 'آپ کا پول لوڈ ہو رہا ہے…',
  'agent.manualSubAgentRecharge.cta.cancel': 'منسوخ',
  'agent.manualSubAgentRecharge.cta.send': '{amount} بھیجیں',
  'agent.manualSubAgentRecharge.success.title': '@{username} کو {amount} بھیجا',
  'agent.manualSubAgentRecharge.success.meta': 'آپ کا پول · {parent} · ان کا پول · {child}',
  'agent.manualSubAgentRecharge.success.again': 'ایک اور بھیجیں',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'ریچارج',
  'agent.playerRequests.direction.withdrawal': 'نکاسی',
  'agent.playerRequests.tab.pending': 'زیر التواء',
  'agent.playerRequests.tab.history': 'تاریخ',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'لوڈ ہو رہا ہے…',
  'agent.playerRequests.empty.pending.recharge': 'ابھی کوئی زیر التواء ریچارج نہیں۔',
  'agent.playerRequests.empty.pending.withdrawal': 'ابھی کوئی زیر التواء نکاسی نہیں۔',
  'agent.playerRequests.empty.history': 'ابھی کوئی تاریخ نہیں۔',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'رد کرنے کی وجہ (اختیاری):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'رد کرنے کی وجہ (کھلاڑی کا روکا بیلنس واپس ہو جائے گا):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'مسترد',
  'agent.playerRequests.toast.rejectedRefunded': 'مسترد · روکا بیلنس واپس',
  'agent.playerRequests.toast.approved': '{amount} منظور',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} منظور · اصل + ₹{bonus} بونس',
  'agent.playerRequests.toast.markedPaid': 'ادا شدہ نشان زد {amount}',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel': 'ادا کریں (UPI)',
  'agent.playerRequests.card.whatsapp': 'WhatsApp',
  'agent.playerRequests.card.viewQr': 'QR دیکھیں',
  'agent.playerRequests.card.bonusChip': 'بونس',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'درخواست شدہ {requested} میں سے {approved} منظور',
  'agent.playerRequests.card.partialPaid': 'درخواست شدہ {requested} میں سے {approved} ادا · {refund} واپس',
  'agent.playerRequests.card.paidTxnPrefix': 'ادا · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'مسترد کریں',
  'agent.playerRequests.action.approve': 'منظور کریں',
  'agent.playerRequests.action.markPaid': 'ادا شدہ نشان زد کریں',
  'agent.playerRequests.state.pending': 'زیر التواء',
  'agent.playerRequests.state.rejected': 'مسترد',
  'agent.playerRequests.state.expired': 'ختم',
  'agent.playerRequests.state.accepted': 'قبول',
  'agent.playerRequests.state.manuallyPaid': 'دستی ادا',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': 'کل {total} میں سے {from}–{to}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'ریچارج',
  'agent.agentRequests.direction.withdrawal': 'نکاسی',
  'agent.agentRequests.tab.pending': 'زیر التواء',
  'agent.agentRequests.tab.history': 'تاریخ',
  'agent.agentRequests.loading': 'لوڈ ہو رہا ہے…',
  'agent.agentRequests.empty.pending.recharge': 'ابھی کوئی زیر التواء ریچارج نہیں۔',
  'agent.agentRequests.empty.pending.withdrawal': 'ابھی کوئی زیر التواء نکاسی نہیں۔',
  'agent.agentRequests.empty.history': 'ابھی کوئی تاریخ نہیں۔',

  'agent.agentRequests.prompt.rejectRecharge': 'رد کرنے کی وجہ (اختیاری):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'رد کرنے کی وجہ (سب-ایجنٹ کا روکا بیلنس واپس ہو جائے گا):',

  'agent.agentRequests.toast.rejected': 'مسترد',
  'agent.agentRequests.toast.rejectedRefunded': 'مسترد · روکا بیلنس واپس',
  'agent.agentRequests.toast.approved': '{amount} منظور · پول کریڈٹ',
  'agent.agentRequests.toast.markedPaid': 'ادا شدہ نشان زد {amount}',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'ایجنٹ',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'نقد مارکر',
  'agent.agentRequests.card.payToUpiLabel': 'ادا کریں (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'حوالگی مارکر',
  'agent.agentRequests.card.whatsapp': 'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved': 'درخواست شدہ {requested} میں سے {approved} منظور',
  'agent.agentRequests.card.partialPaid': 'درخواست شدہ {requested} میں سے {approved} ادا · {refund} واپس',
  'agent.agentRequests.card.paidTxnPrefix': 'ادا · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'ڈپازٹ',
  'agent.agentRequests.card.chip.cash': 'نقد',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'مسترد کریں',
  'agent.agentRequests.action.approve': 'منظور کریں',
  'agent.agentRequests.action.markPaid': 'ادا شدہ نشان زد کریں',
  'agent.agentRequests.state.pending': 'زیر التواء',
  'agent.agentRequests.state.rejected': 'مسترد',
  'agent.agentRequests.state.expired': 'ختم',
  'agent.agentRequests.state.accepted': 'قبول',
  'agent.agentRequests.state.manuallyPaid': 'دستی ادا',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'کھلاڑی شامل کریں',
  'agent.playersList.loading': 'لوڈ ہو رہا ہے…',
  'agent.playersList.empty': 'کوئی کھلاڑی میچ نہیں',
  'agent.playersList.results': 'کل {total} میں سے {from}–{to} دکھا رہا ہے',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'یوزرنیم، ای میل، فون تلاش کریں…',

  // Status filter pills.
  'agent.playersList.filter.all': 'تمام',
  'agent.playersList.filter.active': 'فعال',
  'agent.playersList.filter.pending': 'زیر التواء',
  'agent.playersList.filter.suspended': 'معطل',
  'agent.playersList.filter.banned': 'پابندی',

  // Empty-results panel.
  'agent.playersList.empty.title': 'کوئی کھلاڑی نہیں ملا',
  'agent.playersList.empty.body': 'مختلف تلاش یا حالت فلٹر آزمائیں۔',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'صفحہ {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'فعال',
  'agent.playersList.card.status.pending': 'زیر التواء',
  'agent.playersList.card.status.suspended': 'معطل',
  'agent.playersList.card.status.banned': 'پابندی',
  'agent.playersList.card.verifiedBadge': 'تصدیق شدہ',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'والیٹ',
  'agent.playersList.column.joined': 'شامل ہوا',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'آپ کے بنائے گئے ایجنٹس آپ کے تحت کام کرتے ہیں۔ وہ ہر پلر پر 0% سے شروع ہوتے ہیں — ان کے تفصیل پیج سے فی گیم ریٹ دیں۔',
  'agent.subAgentsList.cta.new': 'نیا سب-ایجنٹ',
  'agent.subAgentsList.loading': 'لوڈ ہو رہا ہے…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'ابھی کوئی سب-ایجنٹس نہیں',
  'agent.subAgentsList.empty.body.canCreate': '"+ نیا سب-ایجنٹ" پر ٹیپ کر کے بنائیں۔ وہ یہاں اپنے پول، کھلاڑیوں اور کمائی کے ساتھ ظاہر ہوں گے — آپ کے ذریعے منظم۔',
  'agent.subAgentsList.empty.body.cantCreate': 'آپ کے ایڈمن نے ابھی سب-ایجنٹ بنانے کے حقوق نہیں دیے۔ ان سے کہیں کہ آپ کی اجازتوں میں create_subagent کو "edit" پر کر دیں۔',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'معطل',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'ایجنٹس',
  'agent.subAgentsList.column.players': 'کھلاڑی',
  'agent.subAgentsList.column.pool': 'پول',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'براہِ راست',
  'agent.subAgentsList.row.indirectLabel': 'بالواسطہ',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'نیا سب-ایجنٹ',
  'agent.subAgentsList.modal.body': 'وہ ہر پلر پر 0% سے شروع ہوں گے۔ بنانے کے بعد ان کے تفصیل پیج سے فی گیم ریٹ دیں۔',
  'agent.subAgentsList.modal.field.username.label': 'یوزرنیم',
  'agent.subAgentsList.modal.field.username.placeholder': 'حروف اور ہندسے، ._- اجازت؛ کم از کم 3 حروف',
  'agent.subAgentsList.modal.field.email.label': 'ای میل',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'ابتدائی پاس ورڈ',
  'agent.subAgentsList.modal.field.password.placeholder': 'کم از کم 8 حروف',
  'agent.subAgentsList.modal.field.password.hint': 'نئے ایجنٹ کے ساتھ شیئر کریں تاکہ وہ لاگ ان کر سکیں۔ انہیں پہلے لاگ ان پر اسے بدلنا چاہیے۔',
  'agent.subAgentsList.modal.field.location.label': 'سب-ایجنٹ کا مقام',
  'agent.subAgentsList.modal.field.location.optional': '(زبردست سفارش)',
  'agent.subAgentsList.modal.field.location.missing': 'مقام کے بغیر، جغرافیائی لوکیٹر اس سب-ایجنٹ کو کوئی نئے کھلاڑی روٹ نہیں کر سکتا۔ وہ صرف براہِ راست ریفرل سے کھلاڑی حاصل کریں گے، جب تک کہ خود سائن ان کر کے اپنا GPS مکمل نہ کریں۔',
  'agent.subAgentsList.modal.cta.cancel': 'منسوخ',
  'agent.subAgentsList.modal.cta.create': 'سب-ایجنٹ بنائیں',
  'agent.subAgentsList.modal.cta.creating': 'بن رہا ہے…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who} سے ریچارج منظور کریں',
  'agent.approvalEdit.title.withdrawal': '{who} کی نکاسی ادا شدہ نشان زد کریں',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'درخواست کی گئی:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'منظور رقم (₹)',
  'agent.approvalEdit.field.amount.hint': 'درخواست شدہ {amount} سے زیادہ نہیں ہو سکتا۔',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} خودکار {who} کے والیٹ کو واپس ہو جائے گا۔',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'جزوی منظوری۔ کوئی منسلک بونس خود رد ہو جائے گا۔',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'وجہ',
  'agent.approvalEdit.field.reason.requiredHint': '(لازمی، ≥5 حروف)',
  'agent.approvalEdit.field.reason.optionalHint': '(اختیاری)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'مثلاً کھلاڑی نے صرف 30 روپے دیے',
  'agent.approvalEdit.field.reason.placeholder.optional': 'اختیاری نوٹ',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI ٹرانزیکشن ID',
  'agent.approvalEdit.field.txn.placeholder': 'مثلاً 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'آپ کا پول ({balance}) یہ منظوری کور نہیں کر سکتا۔ پہلے ٹاپ-اپ کریں۔',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'منظور رقم کم از کم ₹1 ہونی چاہیے',
  'agent.approvalEdit.error.amountMax': 'منظور رقم درخواست شدہ {amount} سے زیادہ نہیں ہو سکتی',
  'agent.approvalEdit.error.reasonRequired': 'مختلف رقم منظور کرنے پر وجہ (≥5 حروف) لازمی ہے',
  'agent.approvalEdit.error.txnRequired': 'UPI ٹرانزیکشن ID لازمی ہے (≥6 حروف)',
  'agent.approvalEdit.error.fallback': 'ناکام — دوبارہ کوشش کریں',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'منسوخ',
  'agent.approvalEdit.cta.approve': 'منظور کریں',
  'agent.approvalEdit.cta.markPaid': 'ادا شدہ نشان زد کریں',
  'agent.approvalEdit.cta.submitting': 'جمع ہو رہا ہے…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'میرا اکاؤنٹ',
  'agent.profile.hub.subtitle': 'شناخت، والیٹ اور ڈپازٹ ایک جگہ',
  'agent.profile.hub.tab.wallet': 'والیٹ',
  'agent.profile.hub.tab.deposit': 'ڈپازٹ',
  'agent.profile.hub.tab.account': 'اکاؤنٹ',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'سائن آؤٹ',
  'agent.profile.signOut.modal.title': 'سائن آؤٹ کریں؟',
  'agent.profile.signOut.modal.body': 'اپنے اکاؤنٹ تک رسائی کے لیے دوبارہ سائن ان کرنا ہوگا۔',
  'agent.profile.signOut.modal.stay': 'سائن ان رہیں',
  'agent.profile.signOut.modal.confirm': 'ہاں، سائن آؤٹ',
  'agent.profile.signOut.modal.busy': 'سائن آؤٹ ہو رہا ہے…',
  'agent.profile.signOut.error': 'سائن آؤٹ نہیں ہو سکا',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'سائن ان نہیں ہے۔',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'ایجنٹ',
  'agent.account.hero.role.admin': 'پلیٹ فارم ایڈمن',
  'agent.account.hero.changePhoto': 'تصویر بدلیں',
  'agent.account.hero.removePhoto': 'تصویر ہٹائیں',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'اپنی تصویر ایڈجسٹ کریں',
  'agent.account.avatar.confirmRemove': 'اپنی پروفائل تصویر ہٹائیں؟',
  'agent.account.toast.photoUpdated': 'تصویر اپ ڈیٹ',
  'agent.account.toast.photoRemoved': 'تصویر ہٹا دی گئی',
  'agent.account.toast.saved': 'محفوظ',
  'agent.account.toast.locationSaved': 'مقام محفوظ',
  'agent.account.toast.passwordChanged': 'پاس ورڈ بدل دیا گیا — دوبارہ سائن ان کریں',
  'agent.account.error.uploadFailed': 'اپ لوڈ ناکام',
  'agent.account.error.removeFailed': 'ناکام',
  'agent.account.error.saveFailed': 'محفوظ ناکام',
  'agent.account.error.changeFailed': 'تبدیلی ناکام',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'اکاؤنٹ کی معلومات',
  'agent.account.info.subtitle': 'صرف-پڑھنے کا پلیٹ فارم میٹا ڈیٹا',
  'agent.account.info.memberSince': 'ممبر بنا',
  'agent.account.info.lastLogin': 'آخری لاگ ان',
  'agent.account.info.lastLoginIp': 'آخری لاگ ان IP',
  'agent.account.info.reportsTo': 'رپورٹس کو',
  'agent.account.info.reportsToValue': 'ایڈمن #{id}',
  'agent.account.info.agentId': 'ایجنٹ ID',
  'agent.account.info.firstLogin': 'پہلی بار',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'ذاتی تفصیلات',
  'agent.account.personal.subtitle.locked': 'شناخت ایڈمن نے لاک · صرف دیکھنا',
  'agent.account.personal.subtitle.agent': 'یوزرنیم، ای میل اور رابطہ نمبر',
  'agent.account.personal.subtitle.admin': 'یوزرنیم اور ای میل',
  'agent.account.personal.field.username': 'یوزرنیم',
  'agent.account.personal.field.email': 'ای میل',
  'agent.account.personal.field.phone': 'رابطہ نمبر',
  'agent.account.personal.field.phone.placeholder': 'مثلاً +91 9876543210',
  'agent.account.personal.field.phone.hint': 'کھلاڑی اور ایڈمن اس نمبر پر آپ تک پہنچتے ہیں۔ صاف کرنے کے لیے خالی چھوڑیں۔',
  'agent.account.personal.fieldLockedNote': 'لاک',
  'agent.account.personal.cta.save': 'تبدیلیاں محفوظ کریں',
  'agent.account.personal.cta.saving': 'محفوظ ہو رہا ہے…',
  'agent.account.personal.cta.noChanges': 'کوئی تبدیلی نہیں',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'آپ کا مقام',
  'agent.account.location.subtitle.summary': 'لوکیٹر نئے کھلاڑیوں کو آپ کے قریب ترین علاقے میں روٹ کرتا ہے',
  'agent.account.location.subtitle.edit': 'اپنا علاقہ اپ ڈیٹ کریں تاکہ لوکیٹر صحیح کھلاڑی آپ کو روٹ کرے',
  'agent.account.location.cta.edit': 'ایڈٹ',
  'agent.account.location.cta.cancel': 'منسوخ',
  'agent.account.location.cta.save': 'محفوظ کریں',
  'agent.account.location.areaLabel': 'علاقہ',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'مقام سیٹ',
  'agent.account.location.setBody': 'GPS کوآرڈینیٹس فائل پر — علاقے کی تفصیلات دیکھنے کے لیے ایڈٹ پر ٹیپ کریں۔',
  'agent.account.location.lastUpdated': 'آخری اپ ڈیٹ · {date}',

  // Security card (password change).
  'agent.account.security.title': 'سکیورٹی',
  'agent.account.security.subtitle.locked': 'ایڈمن نے لاک · صرف دیکھنا',
  'agent.account.security.subtitle.unlocked': 'اپنا پاس ورڈ بدلیں · دیگر ڈیوائسز سائن آؤٹ ہو جائیں گے',
  'agent.account.security.field.current': 'موجودہ پاس ورڈ',
  'agent.account.security.field.new': 'نیا پاس ورڈ',
  'agent.account.security.field.new.hint': 'کم از کم 8 حروف، ایک حرف اور ایک ہندسے کے ساتھ',
  'agent.account.security.field.confirm': 'نئے کی تصدیق',
  'agent.account.security.cta.update': 'پاس ورڈ اپ ڈیٹ کریں',
  'agent.account.security.cta.updating': 'اپ ڈیٹ ہو رہا ہے…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'نئے پاس ورڈ میل نہیں کھاتے',
  'agent.account.security.error.tooShort': 'پاس ورڈ ≥ 8 حروف ہونا چاہیے',
  'agent.account.security.error.weak': 'پاس ورڈ میں کم از کم ایک حرف اور ایک نمبر ہونا چاہیے',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'قابل اشتراک ایجنٹ ID',
  'agent.shareableId.tooltip.copy': 'UAID کاپی کریں',
  'agent.shareableId.tooltip.copied': 'UAID کاپی کر دیا',
  'agent.shareableId.cta.copy': 'کاپی',
  'agent.shareableId.cta.copied': 'کاپی!',
  'agent.shareableId.body.before': 'یہ کوڈ دوسرے ایجنٹ کے ساتھ شیئر کریں تاکہ وہ آپ کو کھلاڑی منتقل کر سکیں۔ چیٹ یا WhatsApp سے بھیجنا محفوظ ہے — اسے شیئر کرنا ',
  'agent.shareableId.body.not': 'نہیں',
  'agent.shareableId.body.after': ' آپ کے اکاؤنٹ تک کوئی رسائی دیتا۔',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'انعامات',
  'agent.rewards.hub.subtitle': 'آفرز دیکھیں اور اپنا ایکسپوژر ٹریک کریں',
  'agent.rewards.hub.tab.browse': 'آفرز دیکھیں',
  'agent.rewards.hub.tab.exposure': 'میرا ایکسپوژر',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'آپٹڈ ان',
  'agent.rewards.status.optedOut': 'آپٹڈ آؤٹ',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'آپ کے کھلاڑیوں کے لیے نمایاں',
  'agent.rewards.browse.empty.title': 'ابھی کوئی فعال انعامات نہیں',
  'agent.rewards.browse.empty.body': 'ایڈمن باقاعدہ نئے آفرز پوسٹ کرتا ہے۔ جلد دوبارہ چیک کریں — یا کسی چھوٹے ہوئے کے لیے اپ لائن سے آپٹ ان کرنے کا کہیں۔',
  'agent.rewards.browse.error.loadFailed': 'انعامات لوڈ نہیں ہو سکے',
  'agent.rewards.browse.card.aria.manage': '{title} کے لیے آپٹ ان منظم کریں',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'آپٹڈ ان',
  'agent.rewards.browse.chip.optInPrompt': 'آپٹ ان کے لیے ٹیپ کریں',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'آپ کا ایکسپوژر گن رہا ہے…',
  'agent.rewards.exposure.error.loadFailed': 'ایکسپوژر لوڈ نہیں ہو سکا',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'آفر ایکسپوژر',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} فعال آپٹ-ان',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} فعال آپٹ-انز',

  'agent.rewards.exposure.hero.actualSpend.label': 'اصل خرچ',
  'agent.rewards.exposure.hero.actualSpend.helper': 'اب تک آپ کی ڈاؤن لائن نے کلیم کیا۔',

  'agent.rewards.exposure.hero.maxExposure.label': 'زیادہ سے زیادہ ایکسپوژر',
  'agent.rewards.exposure.hero.maxExposure.helper': 'بدترین صورت کی حد — ہر باقی بجٹ روپیہ کلیم۔',

  'agent.rewards.exposure.hero.ceiling.label': 'حد استعمال',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'خطرہ گیج',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} زیادہ سے زیادہ',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': 'اس پر ',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': '، اپنے آپٹ-انز پر نظر رکھیں',
  'agent.rewards.exposure.gauge.legend.rose.before': 'اس پر ',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': '، آپ اپنی حد کے قریب ہیں',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'آپ نے ابھی کسی پروجیکٹڈ حد والے آفر میں آپٹ ان نہیں کیا۔',
  'agent.rewards.exposure.gauge.caption.high': 'آپ نے اپنے زیادہ سے زیادہ ایکسپوژر کا {pct}% خرچ کیا۔ زیادہ تر آپٹ-انز بھرپور کلیم۔',
  'agent.rewards.exposure.gauge.caption.mid': 'آپ اپنی زیادہ سے زیادہ حد کے {pct}% پر ہیں۔ فی-آفر تفصیل پر نظر رکھیں۔',
  'agent.rewards.exposure.gauge.caption.low': 'آرام دہ — ابھی صرف {pct}% زیادہ سے زیادہ حد استعمال۔',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'زیادہ سے زیادہ ایکسپوژر کیسے گنا جاتا ہے:',
  'agent.rewards.exposure.footnote.prefix': ' ہر آپٹڈ-ان آفر کے لیے: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — قدامت پسند بالائی حد۔ آپ کی اصل ذمہ داری عام طور پر ',
  'agent.rewards.exposure.footnote.emphasis': 'کم',
  'agent.rewards.exposure.footnote.suffix2': ' ہوتی ہے جب نیچے کے سب-ایجنٹس بھی آپٹ ان کریں (وہ بڑا حصہ دیتے ہیں؛ آپ بقیہ ادا کرتے ہیں)۔',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'کوئی فعال آپٹ-ان نہیں',
  'agent.rewards.exposure.empty.body': 'جب آپ Browse offers ٹیب سے کسی آفر میں آپٹ ان کریں گے، یہ یہاں آپ کے خرچ اور پروجیکٹڈ ایکسپوژر کے ساتھ ظاہر ہوگا۔',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% حصہ',
  'agent.rewards.exposure.row.claims.one': '{count} کلیم',
  'agent.rewards.exposure.row.claims.other': '{count} کلیمز',
  'agent.rewards.exposure.row.overridden': 'ایڈمن-اوور رائیڈ',
  'agent.rewards.exposure.row.actualMaxLabel': 'اصل / زیادہ سے زیادہ',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'لاگت-اشتراک آپٹ-ان',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'گیم منافع',
  'agent.profile.rates.infoBanner.standardPctText': ' معیاری فیصد استعمال کرتا ہے۔ ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'پروموشنل اخراجات',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (آفرز، کیش بیک) ایک ڈائنامک اپ لائن پول استعمال کرتے ہیں۔ دونوں آپ کے درجے (L{level}) کی بنیاد پر پلیٹ فارم ایڈمن کے ذریعے گلوبل طور پر منظم ہوتے ہیں۔',
  'agent.profile.rates.section.gameProfits': 'گیم منافع — آپ ہر میچ پر کیا کماتے ہیں',
  'agent.profile.rates.section.promotionalCosts': 'پروموشنل اخراجات — آپ کی اپ لائن سبسڈی شراکت',
  'agent.profile.rates.pillar.platformFee': 'پلیٹ فارم فیس %',
  'agent.profile.rates.pillar.winLoss': 'جیت / ہار منافع %',
  'agent.profile.rates.pillar.promoWeight': 'پروموشنل پول وزن',
  'agent.profile.rates.guarantee.title': 'ڈائریکٹ ریفرل گارنٹی',
  'agent.profile.rates.guarantee.body': 'اگر آپ نے براہ راست آفر کلیم کرنے والے کھلاڑی کا حوالہ دیا ہے، تو آپ ₹0.00 ادا کرتے ہیں۔ آپ کی اپ لائنیں آپ کے نیٹ ورک کو بڑھانے میں مدد کے لیے 100% لاگت کی سبسڈی دیتی ہیں۔',
  'agent.profile.rates.poolExplainer.title': 'پول کیسے کام کرتا ہے:',
  'agent.profile.rates.poolExplainer.body': 'جب کسی سب ایجنٹ کا کھلاڑی آفر کلیم کرتا ہے، تو آپ کا وزن بقیہ فعال اپ لائن + پلیٹ فارم کے ساتھ ایک پول میں شامل کیا جاتا ہے۔ آپ (آپ کا وزن / کل پول) × آفر لاگت ادا کرتے ہیں۔ زیادہ وزن = بڑا حصہ۔',
  'agent.profile.rates.weightLabel': 'وزن:',
  'agent.profile.rates.updatedPrefix': 'اپ ڈیٹ ہوا',
  'agent.profile.rates.notSetYet': 'ابھی سیٹ نہیں ہوا',
  'agent.rewards.optInModal.guarantee.title': 'ڈائریکٹ ریفرل گارنٹی',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'اگر <strong>آپ نے براہ راست حوالہ دیا</strong> اس آفر کلیم کرنے والے کھلاڑی کا، تو آپ <strong>₹0.00</strong> ادا کرتے ہیں۔ آپ کی اپ لائنیں آپ کے نیٹ ورک کو بڑھانے میں مدد کے لیے 100% لاگت کی سبسڈی دیتی ہیں۔',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'جب <strong>آپ کے کھلاڑی</strong> یہ آفر کلیم کرتے ہیں، تو آپ <strong>₹0.00</strong> ادا کرتے ہیں۔ آپ کی اپ لائنیں آپ کے نیٹ ورک کو بڑھانے میں مدد کے لیے 100% لاگت کی سبسڈی دیتی ہیں۔',
  'agent.rewards.optInModal.uplineReality.title': 'اپ لائن حقیقت کا جائزہ',
  'agent.rewards.optInModal.uplineReality.body': 'آپ کے پاس <strong>{count}</strong> براہ راست سب ایجنٹ{plural} ہیں۔ اگر کسی سب ایجنٹ کا کھلاڑی یہ آفر کلیم کرتا ہے، تو آپ اپنے <strong>پول وزن ({weight})</strong> کی بنیاد پر بقیہ فعال اپ لائن اور پلیٹ فارم کے مقابلے میں متناسب حصہ ادا کریں گے۔',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ڈائریکٹ',
  'agent.rates.col.indirect': 'ان ڈائریکٹ',
  'agent.rates.indirect.dynamic': 'ڈائنامک',
  'agent.rates.indirect.notApplicable': 'لاگو نہیں',
  'agent.rates.indirect.rangeTo': 'سے',
  'agent.rewards.optInModal.close': 'بند',
  'agent.rewards.optInModal.cancel': 'منسوخ',
  'agent.rewards.optInModal.loading': 'آفر تفصیلات لوڈ ہو رہی ہیں…',
  'agent.rewards.optInModal.error.loadFailed': 'آفر تفصیلات لوڈ نہیں ہو سکیں',
  'agent.rewards.optInModal.error.optInFailed': 'آپٹ-ان ناکام',
  'agent.rewards.optInModal.error.optOutFailed': 'آپٹ-آؤٹ ناکام',
  'agent.rewards.optInModal.error.saveFailed': 'سب-ایجنٹ ریٹس محفوظ نہیں ہو سکے',
  'agent.rewards.optInModal.toast.optedIn': 'آپٹ ان کر دیا۔ اب آپ کی ڈاؤن لائن یہ آفر دیکھ سکتی ہے۔',
  'agent.rewards.optInModal.toast.optedOut': 'آپٹ آؤٹ کر دیا۔ آفر آپ کی ڈاؤن لائن سے ہٹا دیا گیا۔',
  'agent.rewards.optInModal.toast.saved.one': '{count} سب-ایجنٹ ریٹ تبدیلی محفوظ۔',
  'agent.rewards.optInModal.toast.saved.other': '{count} سب-ایجنٹ ریٹ تبدیلیاں محفوظ۔',
  'agent.rewards.optInModal.confirmOptOut': 'اس آفر سے آپٹ آؤٹ کریں؟ آپ کی ڈاؤن لائن اسے نہیں دیکھے گی۔',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'آفر #{id} کے لیے آپٹ-ان موڈل سے سیٹ',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'آپ کا حصہ',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'آپٹ-ان پر لاک',
  'agent.rewards.optInModal.stake.current': 'موجودہ ریٹ',
  'agent.rewards.optInModal.stake.noRate': 'ایڈمن نے ابھی آپ کو ریٹ نہیں دیا',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'اب تک خرچ',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'اس آفر پر ڈاؤن لائن کلیمز کے لیے آپ کے والیٹ سے پہلے ہی ادا شدہ کل روپے۔',
  'agent.rewards.optInModal.stake.maxPossible.label': 'زیادہ سے زیادہ ممکنہ',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'بدترین صورت میں روپے جو آپ ادا کر سکتے ہیں اگر ہر باقی بجٹ روپیہ کلیم ہو جائے۔',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— کوئی بجٹ حد نہیں —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'بدترین صورت: اگر پلیٹ فارم بجٹ کے ہر باقی ₹{amount} کو آپ کی ڈاؤن لائن کلیم کرے، تو آپ اس کا {pct}% ادا کریں گے۔',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'آپ آپٹڈ ان ہیں',
  'agent.rewards.optInModal.toggle.title.optedOut': 'اپنی ڈاؤن لائن کے ساتھ شیئر کرنے کے لیے آپٹ ان کریں',
  'agent.rewards.optInModal.toggle.body.optedIn': 'آپ کی ڈاؤن لائن کے کھلاڑی یہ آفر دیکھتے ہیں۔ آپ ان کے ہر کلیم کو اپنے لاک شدہ حصے سے کو-فنڈ کرتے ہیں۔',
  'agent.rewards.optInModal.toggle.body.optedOut': 'جب تک آپ آپٹ ان نہیں کرتے، آپ کے کسی کھلاڑی کو یہ آفر نظر نہیں آتا۔ آپٹ ان کرنے پر آپ کا حصہ ریٹ اس آفر کے لیے LOCKED ہو جاتا ہے۔',
  'agent.rewards.optInModal.toggle.optOutLink': 'اس آفر سے آپٹ آؤٹ کریں',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'سب-ایجنٹ ریٹس',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ہر ایک کی زیادہ سے زیادہ: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} غیر محفوظ',
  'agent.rewards.optInModal.subAgents.empty': 'کوئی براہِ راست سب-ایجنٹس نہیں۔',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'محفوظ کرنا کیسکیڈ ہوتا ہے — اگر آپ کسی ایسے بچے کا ریٹ بڑھائیں جس کے پہلے سے گہرے سب-ایجنٹس ہیں، تو ان ریٹس کو چین مستقل رکھنے کے لیے خود-کلیمپ کیا جا سکتا ہے۔',
  'agent.rewards.optInModal.subAgents.noChildren': 'آپ کے کوئی براہِ راست سب-ایجنٹس نہیں۔ آفر آپ کے اپنے کھلاڑیوں کو نظر آئے گا؛ ایڈمن پورا بقیہ ادا کرتا ہے۔',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'ریٹ درج کریں (صاف کرنے کے لیے 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'صرف نمبر',
  'agent.rewards.optInModal.error.negative': 'منفی نہیں ہو سکتا',
  'agent.rewards.optInModal.error.max100': 'زیادہ سے زیادہ 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'زیادہ سے زیادہ {ceiling}% (آپ کا حصہ)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'محفوظ ہو رہا ہے…',
  'agent.rewards.optInModal.cta.done': 'ہو گیا',
  'agent.rewards.optInModal.cta.save.one': '{count} تبدیلی محفوظ کریں',
  'agent.rewards.optInModal.cta.save.other': '{count} تبدیلیاں محفوظ کریں',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'ایک کھلاڑی کو ریچارج کریں',
  'agent.adminRechargePlayer.subtitle': 'آپ کے پول سے کھلاڑی کے والیٹ میں',
  'agent.adminRechargePlayer.backLink': 'کھلاڑی پر واپس',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'نیا کھلاڑی شامل کریں',
  'agent.createPlayer.subtitle.agent': 'وہ خودکار آپ کے ملکیت میں ہوں گے۔',
  'agent.createPlayer.subtitle.admin': 'نیچے مالک ایجنٹ منتخب کریں، یا پلیٹ فارم-ڈائریکٹ کے لیے خالی چھوڑیں۔',
  'agent.createPlayer.close': 'بند',
  'agent.createPlayer.section.identity.title': 'شناخت',
  'agent.createPlayer.section.identity.hint': 'اکاؤنٹ بنانے کے لیے لازمی',
  'agent.createPlayer.field.username.label': 'یوزرنیم',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ای میل',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'موبائل',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-ہندسوں کا ہندوستان موبائل، +91 خودکار شامل',
  'agent.createPlayer.field.password.label': 'ابتدائی پاس ورڈ',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ حروف، حرف + ہندسہ',
  'agent.createPlayer.section.owner.title': 'مالک ایجنٹ',
  'agent.createPlayer.section.owner.hint': 'اختیاری — پلیٹ فارم-ڈائریکٹ کھلاڑی کے لیے خالی چھوڑیں',
  'agent.createPlayer.field.owner.label': 'ایجنٹ',
  'agent.createPlayer.field.owner.none': '— کوئی نہیں (پلیٹ فارم-ڈائریکٹ) —',
  'agent.createPlayer.section.profile.title': 'پروفائل',
  'agent.createPlayer.section.profile.hint': 'اختیاری — جو معلوم ہے بھریں؛ کھلاڑی پہلے لاگ ان پر مکمل کر سکتا ہے',
  'agent.createPlayer.field.firstName.label': 'پہلا نام',
  'agent.createPlayer.field.lastName.label': 'آخری نام',
  'agent.createPlayer.field.dob.label': 'تاریخ پیدائش',
  'agent.createPlayer.field.gender.label': 'جنس',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— غیر متعین —',
  'agent.createPlayer.gender.male': 'مرد',
  'agent.createPlayer.gender.female': 'عورت',
  'agent.createPlayer.gender.other': 'دیگر',
  'agent.createPlayer.gender.preferNot': 'نہیں کہنا چاہتے',
  'agent.createPlayer.field.language.label': 'پسندیدہ زبان',
  'agent.createPlayer.section.location.title': 'مقام',
  'agent.createPlayer.section.location.hint': 'اختیاری — کھلاڑی پہلے لاگ ان پر مکمل کر سکتا ہے',
  // Footer.
  'agent.createPlayer.disclaimer': 'کھلاڑی status=active اور ای میل خودکار تصدیق کے ساتھ بنایا جاتا ہے — کوئی لنک نہیں بھیجا جاتا۔ یقین کریں کہ رابطہ معلومات پر بھروسہ ہے۔',
  'agent.createPlayer.cta.cancel': 'منسوخ',
  'agent.createPlayer.cta.creating': 'بن رہا ہے…',
  'agent.createPlayer.cta.submit': 'کھلاڑی بنائیں',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'ایجنٹ id غائب',
  'agent.createPlayer.error.createFailed': 'کھلاڑی نہیں بنایا جا سکا',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'آج',
  'agent.adminHome.range.yesterday': 'کل',
  'agent.adminHome.range.week': 'اس ہفتے',
  'agent.adminHome.range.lastWeek': 'پچھلے ہفتے',
  'agent.adminHome.range.month': 'اس مہینے',
  'agent.adminHome.range.lastMonth': 'پچھلے مہینے',
  'agent.adminHome.range.all': 'مکمل',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'دوبارہ خوش آمدید',
  'agent.adminHome.greeting.fallback': 'دوست',
  'agent.adminHome.errorBanner.fallback': 'لوڈ ناکام',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'پلیٹ فارم اِن فلو',
  'agent.adminHome.admin.action.moneyQueue': 'پیسہ قطار',
  'agent.adminHome.admin.action.agents': 'ایجنٹس',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'میچز',
  'agent.adminHome.admin.snapshot.newSignups': 'نئے سائن اپ',
  'agent.adminHome.admin.snapshot.active': 'فعال',
  'agent.adminHome.admin.snapshot.pending': 'زیر التواء',

  // Section titles between rows.
  'agent.adminHome.section.games': 'گیمز',
  'agent.adminHome.section.games.subtitle': 'میچز اور پلیٹ فارم فیس · {range}',
  'agent.adminHome.section.activity': 'سرگرمی',
  'agent.adminHome.section.ops': 'آپریشنز اور بصیرت',
  'agent.adminHome.section.ops.subtitle': 'لائیو سنیپ شاٹس — مکمل پیج میں جانے کے لیے کسی کارڈ پر کلک کریں',
  'agent.adminHome.section.gameSettings': 'گیم سیٹنگز',
  'agent.adminHome.section.gameSettings.subtitle': 'ہر گیم کنفگر کریں',
  'agent.adminHome.section.downline': 'میری ڈاؤن لائن بصیرت',
  'agent.adminHome.section.downline.subtitle': 'مکمل پیج میں جانے کے لیے کسی کارڈ پر کلک کریں',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'کھلاڑی والیٹ',
  'agent.adminHome.admin.card.players.headlineLabel': 'رینج میں ریچارج',
  'agent.adminHome.admin.card.players.flow.recharges': 'ریچارجز',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'نکاسیاں',
  'agent.adminHome.admin.card.players.pending.recharges': 'زیر التواء ریچارجز',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'زیر التواء نکاسیاں',
  'agent.adminHome.admin.card.players.stat.total': 'کل کھلاڑی',
  'agent.adminHome.admin.card.players.stat.active': 'فعال',
  'agent.adminHome.admin.card.players.stat.suspended': 'معطل',

  'agent.adminHome.admin.card.agents.title': 'ایجنٹس والیٹ',
  'agent.adminHome.admin.card.agents.headlineLabel': 'ایجنٹ پولز میں',
  'agent.adminHome.admin.card.agents.flow.topups': 'ٹاپ-اپس (ایڈمن → ایجنٹ)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'آؤٹ فلو (ادا/ٹاپ-اپ)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'زیر التواء ایجنٹ نکاسیاں',
  'agent.adminHome.admin.card.agents.stat.total': 'کل ایجنٹس',
  'agent.adminHome.admin.card.agents.stat.active': 'فعال',
  'agent.adminHome.admin.card.agents.stat.suspended': 'معطل',

  'agent.adminHome.admin.card.bots.title': 'بوٹس والیٹ',
  'agent.adminHome.admin.card.bots.headlineLabel': 'بوٹ والیٹس میں',
  'agent.adminHome.admin.card.bots.stat.total': 'کل بوٹس',
  'agent.adminHome.admin.card.bots.stat.active': 'فعال',
  'agent.adminHome.admin.card.bots.stat.matches': 'میچز',

  'agent.adminHome.admin.card.rewards.title': 'انعامات',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'اس ہفتے ادا',
  'agent.adminHome.admin.card.rewards.pending.claims': 'زیر التواء کلیمز',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'فعال آفرز',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'بلک جابز',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'میچز',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'لوڈو',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'رمی',
  'agent.adminHome.game.carrom': 'کیرم',
  'agent.adminHome.game.chess': 'شطرنج',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'ٹاپ کھلاڑی',
  'agent.adminHome.activity.topPlayers.empty': 'اس رینج میں کوئی میچ نہیں۔',
  'agent.adminHome.activity.topPlayers.metric': '{count} میچز',
  'agent.adminHome.activity.topAgents.title': 'ٹاپ ایجنٹس',
  'agent.adminHome.activity.topAgents.subtitle': 'ملکیت کھلاڑیوں کے حساب سے',
  'agent.adminHome.activity.topAgents.empty': 'ابھی کوئی فعال ایجنٹس نہیں۔',
  'agent.adminHome.activity.topAgents.metric': '{count} کھلاڑی',
  'agent.adminHome.activity.recent.title': 'حالیہ لین دین',
  'agent.adminHome.activity.recent.subtitle': 'آخری 5',
  'agent.adminHome.activity.recent.empty': 'ابھی کوئی لین دین نہیں۔',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'ٹاپ کمائی کرنے والے (7d)',
  'agent.adminHome.ops.topEarners.subtitle': 'True Net کے حساب سے',
  'agent.adminHome.ops.topEarners.empty': 'پچھلے 7 دنوں میں کوئی کمائی نہیں۔',
  'agent.adminHome.ops.liveActivity.title': 'لائیو سرگرمی',
  'agent.adminHome.ops.liveActivity.subtitle': 'پلیٹ فارم بھر کے آخری 5 ایونٹس',
  'agent.adminHome.ops.liveActivity.empty': 'خاموش — پچھلے 24h میں کوئی ایونٹ نہیں۔',
  'agent.adminHome.ops.botPerf.title': 'بوٹ کارکردگی (7d)',
  'agent.adminHome.ops.botPerf.subtitle': 'فی-قسم P&L',
  'agent.adminHome.ops.rateChanges.title': 'حالیہ ریٹ تبدیلیاں',
  'agent.adminHome.ops.rateChanges.subtitle': 'تازہ ترین پارٹنرشپ ایڈٹ',
  'agent.adminHome.ops.rateChanges.empty': 'ابھی کوئی ریٹ-تبدیلی ایونٹ نہیں۔',
  'agent.adminHome.ops.opsHealth.title': 'آپریشنز ہیلتھ',
  'agent.adminHome.ops.opsHealth.subtitle': 'ڈیڈ-لیٹر قطار · زیر التواء سیٹلمنٹس',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'کمایا',
  'agent.adminHome.feed.match.onGameSuffix': '{game} پر',
  'agent.adminHome.feed.recharge.label': 'ریچارج',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'بذریعہ',
  'agent.adminHome.feed.rateChange.label': 'ریٹ تبدیلی',
  'agent.adminHome.feed.cascadeBadge': 'کیسکیڈ',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'منظور',
  'agent.adminHome.feed.recharge.state.rejected': 'مسترد',
  'agent.adminHome.feed.recharge.state.pending': 'زیر التواء',
  'agent.adminHome.feed.recharge.state.paid': 'ادا',
  'agent.adminHome.feed.recharge.state.expired': 'ختم',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} میچ',
  'agent.adminHome.leaderboard.matches.other': '{count} میچز',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'پلیٹ فارم منافع (7d)',
  'agent.adminHome.botMatrix.cat.easy': 'آسان',
  'agent.adminHome.botMatrix.cat.medium': 'درمیانہ',
  'agent.adminHome.botMatrix.cat.hard': 'مشکل',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}m',
  'agent.adminHome.botMatrix.activeSummary': '{total} میں سے {active} بوٹس فعال',
  'agent.adminHome.botMatrix.loading': 'لوڈ ہو رہا ہے…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ صاف ہے',
  'agent.adminHome.opsHealth.dlqClean.body': 'کوئی ناکام سیٹلمنٹ جاب نہیں۔',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} غیر حل شدہ DLQ جاب',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} غیر حل شدہ DLQ جابز',
  'agent.adminHome.opsHealth.dlqDirty.body': 'دوبارہ کوشش / حل کے لیے کلک کریں۔',
  'agent.adminHome.opsHealth.replay.eyebrow': 'دوبارہ چلائیں',
  'agent.adminHome.opsHealth.replay.body': 'سیٹلمنٹ دوبارہ چلائیں',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'دوبارہ مقرر کریں',
  'agent.adminHome.opsHealth.reassign.body': 'بلک منتقلی',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'میرا پول بیلنس',
  'agent.adminHome.agentDash.hero.netPrefix': 'نیٹ',
  'agent.adminHome.agentDash.action.myWallet': 'میرا والیٹ',
  'agent.adminHome.agentDash.action.myPlayers': 'میرے کھلاڑی',
  'agent.adminHome.agentDash.snapshot.myMatches': 'میرے میچز',
  'agent.adminHome.agentDash.snapshot.active': 'فعال',
  'agent.adminHome.agentDash.snapshot.netRange': 'نیٹ (رینج)',
  'agent.adminHome.agentDash.snapshot.pending': 'زیر التواء',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'میرا کھلاڑی والیٹ',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'رینج میں ریچارج',
  'agent.adminHome.agentDash.card.players.stat.total': 'میرے کھلاڑی',
  'agent.adminHome.agentDash.card.wallet.title': 'میرا والیٹ',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'پول بیلنس',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'کمایا (کمیشن)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'ادا شدہ',
  'agent.adminHome.agentDash.card.wallet.pending': 'میری زیر التواء نکاسی',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'ٹاپ-اپس وصول',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'نیٹ (رینج)',
  'agent.adminHome.agentDash.card.activity.title': 'سرگرمی',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'رینج میں میرے میچز',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'ٹاپ خرچ کنندہ',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'ان کا خرچ',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'زیر التواء آئٹمز',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'سب سے زیادہ خرچ کرنے والے',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'اس رینج میں ابھی کوئی خرچ نہیں۔',
  'agent.adminHome.agentDash.activity.recent.title': 'حالیہ سرگرمی',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'آپ کے کھلاڑیوں سے آخری 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'ابھی کوئی لین دین نہیں۔',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'ٹاپ کمائی کرنے والے (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'آپ کے سب-ایجنٹس درجہ بند',
  'agent.adminHome.agentDash.insights.downline.title': 'ڈاؤن لائن سرگرمی',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'آپ کی ڈاؤن لائن کے آخری 5 ایونٹس',
  'agent.adminHome.agentDash.insights.deposit.title': 'ڈپازٹ لیجر',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'حالیہ ڈپازٹ کریڈٹس / واپسی',
  'agent.adminHome.agentDash.insights.deposit.empty': 'ابھی ڈپازٹ سرگرمی نہیں۔',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'سب-ایجنٹ',
  'agent.subAgentDetail.subtitle': 'براہِ راست سب-ایجنٹ',
  'agent.subAgentDetail.error.loadFailed': 'سب-ایجنٹ لوڈ ناکام',
  'agent.subAgentDetail.backToList': 'سب-ایجنٹس فہرست پر واپس',
  'agent.subAgentDetail.loading': 'لوڈ ہو رہا ہے…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'شناخت',
  'agent.subAgentDetail.tab.players': 'کھلاڑی',
  'agent.subAgentDetail.tab.wallet': 'والیٹ',
  'agent.subAgentDetail.tab.rates': 'ریٹس',
  'agent.subAgentDetail.tab.ops': 'آپریشنز',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} کھلاڑی',
  'agent.subAgentDetail.header.players.other': '{count} کھلاڑی',
  'agent.subAgentDetail.header.pool': 'پول: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'آخری لاگ ان {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'فعال',
  'agent.subAgentDetail.status.suspended': 'معطل',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'یوزرنیم',
  'agent.subAgentDetail.identity.row.email': 'ای میل',
  'agent.subAgentDetail.identity.row.status': 'حالت',
  'agent.subAgentDetail.identity.row.parent': 'والدین ایجنٹ',
  'agent.subAgentDetail.identity.parentValue': '@{username} (آپ)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'ملکیت کھلاڑی',
  'agent.subAgentDetail.identity.row.wallet': 'والیٹ بیلنس',
  'agent.subAgentDetail.identity.row.created': 'بنایا',
  'agent.subAgentDetail.identity.row.lastLogin': 'آخری لاگ ان',
  'agent.subAgentDetail.identity.row.identityLocked': 'شناخت لاک',
  'agent.subAgentDetail.identity.identityLocked.yes': 'ہاں (صرف ایڈمن ایڈٹ)',
  'agent.subAgentDetail.identity.identityLocked.no': 'نہیں (سب-ایجنٹ خود ایڈٹ کر سکتا ہے)',
  'agent.subAgentDetail.identity.row.receiving': 'کھلاڑی وصول کر رہا ہے',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'قبول کر رہا ہے',
  'agent.subAgentDetail.optOut.optedOut': 'آپٹ آؤٹ',
  'agent.subAgentDetail.optOut.toast.accepting': 'سب-ایجنٹ اب کھلاڑی قبول کر رہا ہے',
  'agent.subAgentDetail.optOut.toast.optedOut': 'سب-ایجنٹ نے لوکیٹر سے آپٹ آؤٹ کر دیا',
  'agent.subAgentDetail.optOut.error.updateFailed': 'اپ ڈیٹ نہیں ہو سکا',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'مقام',
  'agent.subAgentDetail.location.lastUpdated': '· آخری اپ ڈیٹ {date}',
  'agent.subAgentDetail.location.cta.save': 'مقام محفوظ کریں',
  'agent.subAgentDetail.location.nothingToSave': 'محفوظ کرنے کے لیے کچھ نہیں',
  'agent.subAgentDetail.location.toast.saved.one': 'مقام محفوظ ({count} فیلڈ)',
  'agent.subAgentDetail.location.toast.saved.other': 'مقام محفوظ ({count} فیلڈز)',
  'agent.subAgentDetail.location.hint': 'مختلف ریاست / ضلع / علاقہ منتخب کریں تاکہ محفوظ فعال ہو۔ (سب-ایجنٹ خود اپنے پہلے لاگ ان پر زبردستی پاپ-اپ کے ذریعے درست GPS اوور لے کرتا ہے۔)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'اس سب-ایجنٹ کے پاس ابھی کوئی کھلاڑی نہیں۔',
  'agent.subAgentDetail.players.count.one': '{count} کھلاڑی',
  'agent.subAgentDetail.players.count.other': '{count} کھلاڑی',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'موجودہ سب-ایجنٹ بیلنس',
  'agent.subAgentDetail.wallet.ledger.title': '@{username} کا مکمل والیٹ لیجر',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'ہر حرکت · سب-ایجنٹ بھی اپنے والیٹ میں یہی دیکھتا ہے',
  'agent.subAgentDetail.wallet.ledger.empty': 'ابھی والیٹ کی کوئی سرگرمی نہیں۔',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'آپ ↔ وہ',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'بیل ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'ایڈمن سے ٹاپ-اپ',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'کھلاڑی کو ادائیگی',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'کھلاڑی ریچارج',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'آمدنی حصہ',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'ریچارج کمیشن',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'کو-فنڈ آفر',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'ایڈمن ایڈجسٹمنٹ',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'نکاسی ہولڈ',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'نکاسی واپسی',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'پلیٹ فارم فیس حصہ',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'ون/لاس حصہ',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'سکے واپس لینا ہیں؟',
  'agent.subAgentDetail.wallet.pullback.body.before': 'پوچھیں ',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' کہ وہ اپنے پول سے نکاسی کی درخواست بھیجیں۔ آپ اسے ادا شدہ (مکمل یا جزوی) نشان زد کر سکتے ہیں ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'فنانس › سب-ایجنٹس',
  'agent.subAgentDetail.wallet.pullback.body.after': '۔',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username} کو منتقلی',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username} سے نکاسی',
  'agent.subAgentDetail.transfer.topup.body': 'اٹامک منتقلی: آپ کا والیٹ کم، ان کا والیٹ زیادہ۔ دونوں پول منفی ہو سکتے ہیں (سائنڈ-پول ڈیزائن)۔',
  'agent.subAgentDetail.transfer.withdraw.body': 'اٹامک ریورس منتقلی: ان کا والیٹ کم، آپ کا والیٹ زیادہ۔ یہ سب-ایجنٹ کی ادائیگی درخواست طے کرتے وقت استعمال کریں۔',
  'agent.subAgentDetail.transfer.amountLabel': 'رقم (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'نوٹ (اختیاری)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'مثلاً ہفتہ وار ٹاپ-اپ',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'مثلاً ادائیگی سیٹلمنٹ',
  'agent.subAgentDetail.transfer.error.amountPositive': 'رقم مثبت عدد صحیح ہونی چاہیے۔',
  'agent.subAgentDetail.transfer.confirm.topup': '{amount} اپنے پول سے @{username} کو منتقل کریں؟ آپ کا بیلنس بالکل اتنا کم ہو جائے گا۔',
  'agent.subAgentDetail.transfer.confirm.withdraw': '{amount} @{username} کے پول سے اپنے پول میں واپس لیں؟ ان کا بیلنس بالکل اتنا کم ہو جائے گا۔',
  'agent.subAgentDetail.transfer.toast.topup': '@{username} کو {amount} بھیجا۔ ان کا بیلنس: {childBalance}۔',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username} سے {amount} واپس لیا۔ ان کا بیلنس: {childBalance}۔',
  'agent.subAgentDetail.transfer.cta.working': 'کام جاری…',
  'agent.subAgentDetail.transfer.cta.suspended': 'سب-ایجنٹ معطل',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} منتقل کریں',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} نکالیں',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'ریٹس لوڈ ہو رہے ہیں…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'ہر کارڈ ایک کمیشن پلر ہے؛ اندر کی ہر قطار ایک (پلر × گیم) حصہ ہے۔ ',
  'agent.subAgentDetail.rates.intro.ceiling': 'حد',
  'agent.subAgentDetail.rates.intro.part2': ' جو ہر ان پٹ کے ساتھ دکھائی گئی ہے وہ آپ کا اپنا ریٹ ہے — گولڈن لمٹ کے مطابق، آپ بچے کو اس سے زیادہ نہیں دے سکتے۔ آپ کی تبدیل کی ہوئی قدر ایڈٹ ہو سکتی ہے؛ کلک کریں ',
  'agent.subAgentDetail.rates.intro.applyWord': 'اپلائی',
  'agent.subAgentDetail.rates.intro.part3': ' کرنے کے لیے۔ کیسکیڈ کلیمپ سرور کی طرف سے نافذ ہے۔',
  'agent.subAgentDetail.rates.toast.applied': 'ریٹس اپلائی۔',
  'agent.subAgentDetail.rates.confirm.one': '{count} ریٹ تبدیلی اپلائی کریں؟ گولڈن-لمٹ + کیسکیڈ کلیمپ سرور کی طرف سے نافذ ہوگا۔',
  'agent.subAgentDetail.rates.confirm.other': '{count} ریٹ تبدیلیاں اپلائی کریں؟ گولڈن-لمٹ + کیسکیڈ کلیمپ سرور کی طرف سے نافذ ہوگا۔',
  'agent.subAgentDetail.rates.error.blocked': 'بلاک: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'پلیٹ فارم فیس %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'ون/لاس منافع %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'آفر انعام %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— عالمی —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ حد {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'والدین سے زیادہ (گولڈن لمٹ)',
  'agent.subAgentDetail.rates.row.editedBadge': 'ایڈٹ',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} تبدیلی ضائع کریں',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} تبدیلیاں ضائع کریں',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} تبدیلی اپلائی کریں',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} تبدیلیاں اپلائی کریں',
  'agent.subAgentDetail.rates.cta.applying': 'اپلائی ہو رہا…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'حالت',
  'agent.subAgentDetail.ops.status.body': 'معطل کرنا سب-ایجنٹ کے لاگ ان اور ڈاؤن لائن آپریشنز کو بلاک کرتا ہے جب تک آپ واپس فعال نہ کریں۔ وجہ اختیاری ہے مگر آڈٹ ٹریل میں جاتی ہے۔',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'وجہ (اختیاری)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'سب-ایجنٹ معطل کریں',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'سب-ایجنٹ کی معطلی ختم کریں',
  'agent.subAgentDetail.ops.status.cta.working': 'کام جاری…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username} کو معطل کریں؟ معطل ہونے پر وہ لاگ ان یا کام نہیں کر سکیں گے۔',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username} کی معطلی ختم کریں؟ وہ مکمل رسائی دوبارہ حاصل کریں گے۔',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} اب معطل ہے۔',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} اب فعال ہے۔',

  'agent.subAgentDetail.ops.password.title': 'پاس ورڈ ری سیٹ',
  'agent.subAgentDetail.ops.password.body': 'سب-ایجنٹ کے لیے نیا پاس ورڈ سیٹ کرتا ہے اور موجودہ ریفریش ٹوکن منسوخ کرتا ہے۔ انہیں نئے پاس ورڈ سے دوبارہ لاگ ان کرنا پڑے گا۔ محفوظ چینل سے ان کے ساتھ شیئر کریں۔',
  'agent.subAgentDetail.ops.password.placeholder': 'نیا پاس ورڈ (کم از کم 8 حروف)',
  'agent.subAgentDetail.ops.password.confirm': '@{username} کا پاس ورڈ ری سیٹ کریں؟ ان کے موجودہ سیشنز غیر فعال ہوں گے اور انہیں نئے پاس ورڈ سے لاگ ان کرنا ہوگا۔',
  'agent.subAgentDetail.ops.password.toast.reset': 'پاس ورڈ ری سیٹ۔ نیا پاس ورڈ @{username} کے ساتھ شیئر کریں۔',
  'agent.subAgentDetail.ops.password.error.tooShort': 'پاس ورڈ کم از کم 8 حروف کا ہونا چاہیے۔',
  'agent.subAgentDetail.ops.password.cta.reset': 'پاس ورڈ ری سیٹ',
  'agent.subAgentDetail.ops.password.cta.resetting': 'ری سیٹ ہو رہا…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'کھلاڑیوں پر واپس',
  'agent.playerDetail.loading': 'لوڈ ہو رہا ہے…',
  'agent.playerDetail.error.loadFailed': 'صارف لوڈ ناکام',

  // Header card.
  'agent.playerDetail.header.noName': '(کوئی نام سیٹ نہیں)',
  'agent.playerDetail.header.joined': 'شامل ہوا {date}',
  'agent.playerDetail.header.ownedBy.prefix': ' · ملکیت ',
  'agent.playerDetail.header.walletLabel': 'والیٹ',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'زیر التواء',
  'agent.playerDetail.status.active': 'فعال',
  'agent.playerDetail.status.suspended': 'معطل',
  'agent.playerDetail.status.banned': 'پابندی',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'شناخت',
  'agent.playerDetail.tab.profile': 'پروفائل',
  'agent.playerDetail.tab.wallet': 'والیٹ',
  'agent.playerDetail.tab.security': 'سکیورٹی',
  'agent.playerDetail.tab.ownership': 'ملکیت',
  'agent.playerDetail.tab.operations': 'آپریشنز',

  // Identity tab.
  'agent.playerDetail.identity.title': 'شناخت',
  'agent.playerDetail.identity.row.username': 'یوزرنیم',
  'agent.playerDetail.identity.row.email': 'ای میل',
  'agent.playerDetail.identity.row.phone': 'فون',
  'agent.playerDetail.identity.row.country': 'ملک',
  'agent.playerDetail.identity.row.joined': 'شامل ہوا',
  'agent.playerDetail.identity.row.emailVerified': 'ای میل تصدیق شدہ',
  'agent.playerDetail.identity.row.telegram': 'ٹیلیگرام',
  'agent.playerDetail.identity.row.failedLogins': 'ناکام لاگ انز',
  'agent.playerDetail.identity.row.lockedUntil': 'تک لاک',
  'agent.playerDetail.identity.value.yes': 'ہاں',
  'agent.playerDetail.identity.value.no': 'نہیں',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'اس کھلاڑی کو منتقل کریں',
  'agent.playerDetail.identity.transfer.body': 'ان کے Unique Agent ID سے دوسرے ایجنٹ کو حوالے کریں — پورے نیٹ ورک میں کام کرتا ہے، نہ کہ صرف آپ کی ڈاؤن لائن۔',
  'agent.playerDetail.identity.transfer.cta': 'UAID سے منتقل کریں',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'تصویر بدلیں',
  'agent.playerDetail.avatar.cropTitle': '@{username} کے لیے تصویر ایڈجسٹ کریں',
  'agent.playerDetail.avatar.toast.updated': 'تصویر اپ ڈیٹ',
  'agent.playerDetail.avatar.error.uploadFailed': 'اپ لوڈ ناکام',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'کھلاڑی UAID سے منتقل ہو گیا',
  'agent.playerDetail.transfer.toast.agent': '@{username} کامیابی سے منتقل',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'اکاؤنٹ حالت',
  'agent.playerDetail.ops.accountStatus.body': 'فعال کا مطلب کھلاڑی لاگ ان اور کھیل سکتا ہے۔ معطل لاگ ان کو بلاک کرتا ہے۔ پابندی مضبوط لاک ہے — پالیسی خلاف ورزیوں کے لیے رکھیں۔',
  'agent.playerDetail.ops.statusBtn.activate': 'فعال کریں',
  'agent.playerDetail.ops.statusBtn.suspend': 'معطل کریں',
  'agent.playerDetail.ops.statusBtn.ban': 'پابندی',
  'agent.playerDetail.ops.statusBtn.current': 'موجودہ',
  'agent.playerDetail.ops.statusBtn.toast': 'صارف اب {status} ہے',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'خطرے کا زون',
  'agent.playerDetail.ops.danger.body': 'ہارڈ-ڈیلیٹ صارف ریکارڈ ہٹاتا ہے۔ والیٹ لیجر آڈٹ کے لیے محفوظ رہتا ہے، مگر کھلاڑی تمام رسائی کھو دے گا۔ یہ کارروائی واپس نہیں ہو سکتی۔',
  'agent.playerDetail.ops.delete.eyebrow': 'خطرے کا زون',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'اس اکاؤنٹ کو مستقل طور پر ڈیلیٹ کریں۔ یہ واپس نہیں ہو سکتا۔ تصدیق کے لیے ',
  'agent.playerDetail.ops.delete.confirm.after': ' ٹائپ کریں۔',
  'agent.playerDetail.ops.delete.cta': 'ڈیلیٹ',
  'agent.playerDetail.ops.delete.toast': 'صارف ڈیلیٹ',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'والیٹ ایڈجسٹ کریں',
  'agent.playerDetail.walletAdjust.mode.credit': '+ کریڈٹ',
  'agent.playerDetail.walletAdjust.mode.debit': '− ڈیبٹ',
  'agent.playerDetail.walletAdjust.field.amount': 'رقم (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'وجہ · لیجر میں محفوظ',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'مثلاً ڈپازٹ ریورسل، بونس، واپسی',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'ریچارج نشان زد کریں',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'اس کریڈٹ کو ریکارڈ کریں ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'ریچارج',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' لیجر میں۔ اگر کسی نے اس صارف کو ریفر کیا ہے، تو ریفر کرنے والے کو خود بونس ملتا ہے۔',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'مثبت رقم درج کریں',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'وجہ لازمی ہے',
  'agent.playerDetail.walletAdjust.cta.applying': 'اپلائی ہو رہا…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'کریڈٹ اپلائی کریں',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'ڈیبٹ اپلائی کریں',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} کریڈٹ',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} ڈیبٹ',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · ریفر کرنے والے نے {bonus} بونس کمایا',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'اس کھلاڑی کو ریچارج کریں',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': 'ٹاپ-اپ کر رہے ہیں @',
  'agent.playerDetail.rechargeCta.body.middle': ' سکے منتقل کرتا ہے ',
  'agent.playerDetail.rechargeCta.body.bold': 'آپ کا والیٹ',
  'agent.playerDetail.rechargeCta.body.after': ' ان کو۔ پہلے آپ کا پول چیک ہوتا ہے — حرکت اٹامک ہے اور ناکافی بیلنس پر مسترد ہو جاتی ہے۔',
  'agent.playerDetail.rechargeCta.thisPlayer': 'یہ کھلاڑی',
  'agent.playerDetail.rechargeCta.cta': 'ریچارج فارم کھولیں',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'پاس ورڈ ری سیٹ',
  'agent.playerDetail.security.subtitle': 'نیا پاس ورڈ سیٹ کرتا ہے اور صارف کو ہر ڈیوائس سے سائن آؤٹ کرتا ہے۔',
  'agent.playerDetail.security.field.new': 'نیا پاس ورڈ',
  'agent.playerDetail.security.field.confirm': 'تصدیق',
  'agent.playerDetail.security.error.mismatch': 'پاس ورڈ میل نہیں کھاتے',
  'agent.playerDetail.security.error.tooShort': 'کم از کم 8 حروف، حروف اور ہندسوں کے ساتھ',
  'agent.playerDetail.security.toast.reset': 'پاس ورڈ ری سیٹ اور تمام سیشنز منسوخ',
  'agent.playerDetail.security.cta.reset': 'پاس ورڈ ری سیٹ',
  'agent.playerDetail.security.cta.resetting': 'ری سیٹ ہو رہا…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'والیٹ لیجر',
  'agent.playerDetail.ledger.entries.one': '{count} اندراج',
  'agent.playerDetail.ledger.entries.other': '{count} اندراجات',
  'agent.playerDetail.ledger.empty': 'ابھی کوئی لین دین نہیں۔',
  'agent.playerDetail.ledger.loading': 'لوڈ ہو رہا ہے…',
  'agent.playerDetail.ledger.actorSuffix': ' · ایڈمن #{id} کے ذریعہ',
  'agent.playerDetail.ledger.pagination.prev': 'پچھلا',
  'agent.playerDetail.ledger.pagination.next': 'اگلا',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'میچ شرط',
  'agent.playerDetail.ledger.kind.win': 'میچ انعام',
  'agent.playerDetail.ledger.kind.refund': 'واپسی',
  'agent.playerDetail.ledger.kind.fee': 'پلیٹ فارم فیس',
  'agent.playerDetail.ledger.kind.adminCredit': 'ایڈمن کریڈٹ',
  'agent.playerDetail.ledger.kind.adminDebit': 'ایڈمن ڈیبٹ',
  'agent.playerDetail.ledger.kind.recharge': 'ریچارج',
  'agent.playerDetail.ledger.kind.referralBonus': 'ریفرل بونس',
  'agent.playerDetail.ledger.kind.signupBonus': 'سائن اپ بونس',
  'agent.playerDetail.ledger.kind.offerCredit': 'آفر کریڈٹ',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'نکاسی',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'واپسی',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'پلیٹ فارم',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'پروفائل ایڈٹ کریں',
  'agent.playerDetail.profile.field.username': 'یوزرنیم',
  'agent.playerDetail.profile.field.email': 'ای میل',
  'agent.playerDetail.profile.field.phone': 'فون',
  'agent.playerDetail.profile.field.firstName': 'پہلا نام',
  'agent.playerDetail.profile.field.lastName': 'آخری نام',
  'agent.playerDetail.profile.field.dob': 'تاریخ پیدائش',
  'agent.playerDetail.profile.field.gender': 'جنس',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'مرد',
  'agent.playerDetail.profile.gender.female': 'عورت',
  'agent.playerDetail.profile.gender.other': 'دیگر',
  'agent.playerDetail.profile.gender.preferNot': 'نہیں کہنا چاہتے',
  // Location section.
  'agent.playerDetail.profile.location.title': 'مقام',
  'agent.playerDetail.profile.location.lastUpdated': '· آخری اپ ڈیٹ {date}',
  'agent.playerDetail.profile.location.legacy.label': 'فائل پر پرانا پتہ',
  'agent.playerDetail.profile.location.legacy.note': ' (صرف-پڑھنے — R3 سے پہلے، لوکیٹر استعمال نہیں کرتا):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'محفوظ کرنے کے لیے کچھ نہیں',
  'agent.playerDetail.profile.toast.saved.one': '{count} فیلڈ محفوظ',
  'agent.playerDetail.profile.toast.saved.other': '{count} فیلڈز محفوظ',
  'agent.playerDetail.profile.cta.save': 'تبدیلیاں محفوظ کریں',
  'agent.playerDetail.profile.cta.saving': 'محفوظ ہو رہا ہے…',
  'agent.playerDetail.profile.cta.reset': 'ری سیٹ',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'مالک ایجنٹ',
  'agent.playerDetail.ownership.row.eyebrow': 'ملکیت',
  'agent.playerDetail.ownership.platform.title': 'پلیٹ فارم-ڈائریکٹ',
  'agent.playerDetail.ownership.platform.body': 'کوئی ایجنٹ اس کھلاڑی کا مالک نہیں۔ ایڈمن براہِ راست منظم کرتا ہے۔',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ایجنٹ #{id}',
  'agent.playerDetail.ownership.owned.body': 'اس کھلاڑی کے ریچارج + میچز پر کمیشن کماتا ہے۔',
  'agent.playerDetail.ownership.cta.change': 'بدلیں',
  'agent.playerDetail.ownership.toast.changedTo': 'ملکیت → {label}',
  'agent.playerDetail.ownership.platformLabel': 'پلیٹ فارم-ڈائریکٹ',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username} کو دوبارہ مقرر کریں',
  'agent.playerDetail.ownership.picker.body': 'نیا ایجنٹ منتخب کریں — یا پلیٹ فارم-ڈائریکٹ پر علیحدہ کریں (کوئی ایجنٹ اس کھلاڑی کا مالک نہیں)۔ مستقبل کے کمیشن نئے مالک کے پاس جائیں گے؛ پہلے سے کریڈٹ شدہ قطاریں نہیں چھیڑی جائیں گی۔',
  'agent.playerDetail.ownership.picker.detach.title': 'پلیٹ فارم پر علیحدہ کریں',
  'agent.playerDetail.ownership.picker.detach.body': 'ایجنٹ ملکیت ہٹائیں۔ ایڈمن براہِ راست منظم کرتا ہے۔',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ایجنٹس تلاش کریں…',
  'agent.playerDetail.ownership.picker.loading': 'ایجنٹس لوڈ ہو رہے ہیں…',
  'agent.playerDetail.ownership.picker.empty.search': 'آپ کی تلاش سے کوئی ایجنٹ میچ نہیں۔',
  'agent.playerDetail.ownership.picker.empty.none': 'ابھی کوئی فعال ایجنٹس نہیں۔',
  'agent.playerDetail.ownership.picker.players.one': '{count} کھلاڑی',
  'agent.playerDetail.ownership.picker.players.other': '{count} کھلاڑی',
  'agent.playerDetail.ownership.picker.current': 'موجودہ',
  'agent.playerDetail.ownership.picker.currentUpper': 'موجودہ',
  'agent.playerDetail.ownership.picker.close': 'بند',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'بالی ایجنٹ',
  'agent.login.subtitle': 'اپنے کھلاڑیوں کو منظم کرنے کے لیے سائن ان کریں',
  'agent.login.field.identifier': 'یوزرنیم یا ای میل',
  'agent.login.field.password': 'پاس ورڈ',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'صرف ایجنٹس۔ اگر آپ پلیٹ فارم مالک ہیں، استعمال کریں ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '۔',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'لاگ ان ناکام',
  'agent.login.error.invalidCredentials': 'غلط اسناد',
  'agent.login.error.accountSuspended': 'اکاؤنٹ معطل — اپنے ایڈمنسٹریٹر سے رابطہ کریں۔',
  'agent.login.error.networkError': 'نیٹ ورک خرابی — اپنا کنکشن چیک کر کے دوبارہ کوشش کریں۔',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'گیم',
  'agent.misc.gameTile.actionSuffix': '· کنفگر',
};

const kn: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "ನನ್ನ ವಿವರಗಳು",
  'profile.group.security': "ಸೈನ್-ಇನ್ ಮತ್ತು ಭದ್ರತೆ",
  'profile.personal.title': "ವೈಯಕ್ತಿಕ ವಿವರಗಳು",
  'profile.personal.editTitle': "ವೈಯಕ್ತಿಕ ವಿವರಗಳನ್ನು ಸಂಪಾದಿಸಿ",
  'common.edit': "ಸಂಪಾದಿಸಿ",
  'profile.field.name': "ಹೆಸರು",
  'profile.field.phone': "ಫೋನ್ ಸಂಖ್ಯೆ",
  'profile.field.gender': "ಲಿಂಗ",
  'profile.field.location': "ಸ್ಥಳ",
  'profile.gender.male': "ಪುರುಷ",
  'profile.gender.female': "ಮಹಿಳೆ",
  'profile.gender.other': "ಇತರೆ",
  'profile.gender.preferNot': "ಹೇಳಲು ಬಯಸುವುದಿಲ್ಲ",
  'profile.gender.select': "ಆಯ್ಕೆಮಾಡಿ…",
  'profile.location.optionalHint': "ಐಚ್ಛಿಕ — ನಿಮ್ಮ ಪ್ರದೇಶವನ್ನು ಆಯ್ಕೆಮಾಡಿ ಅಥವಾ ಪಿನ್‌ಕೋಡ್ ಟೈಪ್ ಮಾಡಿ (GPS ಇದನ್ನು ಹೆಚ್ಚು ನಿಖರವಾಗಿಸುತ್ತದೆ).",
  'profile.lang.title': "ಭಾಷೆ",
  'profile.password.title': "ಪಾಸ್‌ವರ್ಡ್",
  'profile.password.desc': "ನಿಮ್ಮ ಖಾತೆಯ ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಿ. ಇತರ ಸಾಧನಗಳಿಂದ ಸೈನ್ ಔಟ್ ಆಗುತ್ತವೆ.",
  'profile.password.cta': "ಬದಲಾಯಿಸಿ",
  'profile.password.editTitle': "ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಿ",
  'profile.password.current': "ಪ್ರಸ್ತುತ ಪಾಸ್‌ವರ್ಡ್",
  'profile.password.new': "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್",
  'profile.password.confirm': "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ",
  'profile.password.currentPh': "ನಿಮ್ಮ ಪ್ರಸ್ತುತ ಪಾಸ್‌ವರ್ಡ್",
  'profile.password.newPh': "ಬಲವಾದ ಪಾಸ್‌ವರ್ಡ್ ಆಯ್ಕೆಮಾಡಿ",
  'profile.password.confirmPh': "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ಮತ್ತೆ ಟೈಪ್ ಮಾಡಿ",
  'profile.password.rule': "ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು, ಒಂದು ಅಕ್ಷರ ಮತ್ತು ಒಂದು ಅಂಕಿ ಇರಬೇಕು.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "ಹೀಗೆ ಲಿಂಕ್ ಆಗಿದೆ",
  'profile.telegram.otpHere': "ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವ OTP ಗಳು ಇಲ್ಲಿಗೆ ಬರುತ್ತವೆ.",
  'profile.telegram.unlinkChange': "ಅನ್‌ಲಿಂಕ್ ಮಾಡಿ ಮತ್ತು ಬದಲಾಯಿಸಿ",
  'profile.telegram.unlinkTitle': "Telegram ಅನ್‌ಲಿಂಕ್ ಮಾಡಬೇಕೆ?",
  'profile.telegram.unlink': "ಅನ್‌ಲಿಂಕ್ ಮಾಡಿ",
  'profile.telegram.keepLinked': "ಲಿಂಕ್ ಆಗಿಯೇ ಇರಲಿ",
  'profile.telegram.unlinkVerified': "ನಂತರ ನೀವು ಬೇರೆ Telegram ಖಾತೆಯನ್ನು ಮತ್ತೆ ಲಿಂಕ್ ಮಾಡಬಹುದು. ಇಮೇಲ್ ಪರಿಶೀಲನೆ ಹಾಗೆಯೇ ಉಳಿಯುತ್ತದೆ.",
  'profile.telegram.unlinkOnly': "Telegram ನಿಮ್ಮ ಏಕೈಕ ಪರಿಶೀಲಿತ ಮಾಧ್ಯಮ — ಆಟ ಮುಂದುವರಿಸುವ ಮೊದಲು ನಿಮ್ಮ ಖಾತೆಯನ್ನು ಮತ್ತೆ ಪರಿಶೀಲಿಸಬೇಕಾಗಬಹುದು.",
  'profile.telegram.notLinkedDesc': "ಬ್ಯಾಕ್‌ಅಪ್ ಆಗಿ ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವ OTP ಪಡೆಯಲು Telegram ಲಿಂಕ್ ಮಾಡಿ.",
  'profile.telegram.openBot': "@BaaliGames_bot ತೆರೆಯಿರಿ",
  'profile.telegram.generate': "ಲಿಂಕ್ ರಚಿಸಿ",
  'profile.telegram.generating': "ರಚಿಸಲಾಗುತ್ತಿದೆ…",
  'profile.avatar.cropTitle': "ನಿಮ್ಮ ಫೋಟೋ ಹೊಂದಿಸಿ",
  'profile.toast.detailsSaved': "ವಿವರಗಳು ಉಳಿಸಲಾಗಿದೆ",
  'profile.toast.photoUpdated': "ಫೋಟೋ ನವೀಕರಿಸಲಾಗಿದೆ",
  'profile.toast.pwChanged': "ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಲಾಗಿದೆ. ಇತರ ಸಾಧನಗಳಿಂದ ಸೈನ್ ಔಟ್ ಆಗಿದೆ.",
  'profile.toast.tgUnlinked': "Telegram unlinked. Generate a new link to connect a different chat.",
  'profile.err.phone': "10-ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ನಮೂದಿಸಿ (ಅಥವಾ ಬದಲಾಯಿಸದೆ ಬಿಡಿ)",
  'profile.err.pwMismatch': "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ",
  'profile.err.pwWeak': "ಪಾಸ್‌ವರ್ಡ್ 8+ ಅಕ್ಷರಗಳು, ಒಂದು ಅಕ್ಷರ ಮತ್ತು ಒಂದು ಅಂಕಿ ಹೊಂದಿರಬೇಕು",
  'profile.err.pwSame': "ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ಪ್ರಸ್ತುತದ್ದಕ್ಕಿಂತ ಭಿನ್ನವಾಗಿರಬೇಕು",
  'wallet.payout.title': "ಉಳಿಸಿದ ಪಾವತಿ ವಿವರಗಳು",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "ಇನ್ನೂ ಏನೂ ಉಳಿಸಿಲ್ಲ",
  'wallet.payout.hint': "ನೀವು ಹಣ ಹಿಂಪಡೆದಾಗ ನಿಮ್ಮ UPI ID ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಉಳಿಸಲಾಗುತ್ತದೆ. ಮುಂದಿನ ಹಿಂಪಡೆಯುವ ವಿನಂತಿಯ ಸಮಯದಲ್ಲಿ ನೀವು ಅದನ್ನು ನವೀಕರಿಸಬಹುದು.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ಅನ್ನು ಇನ್‌ಸ್ಟಾಲ್ ಮಾಡಿ",
  "pwa.install.subhint": "ತಕ್ಷಣದ, ಪೂರ್ಣ-ಪರದೆ ಪ್ರವೇಶಕ್ಕಾಗಿ ಇದನ್ನು ನಿಮ್ಮ ಹೋಮ್ ಸ್ಕ್ರೀನ್‌ಗೆ ಸೇರಿಸಿ — ಯಾವುದೇ ಆ್ಯಪ್ ಸ್ಟೋರ್ ಅಗತ್ಯವಿಲ್ಲ.",
  "pwa.install.listen": "ಕೇಳಿ",
  "pwa.install.listening": "ನಿಲ್ಲಿಸಿ",
  "pwa.install.installBtn": "ಇನ್‌ಸ್ಟಾಲ್ ಮಾಡಿ",
  "pwa.install.notNow": "ಈಗ ಬೇಡ",
  "pwa.install.howToShow": "ಹೇಗೆ ಇನ್‌ಸ್ಟಾಲ್ ಮಾಡುವುದು",
  "pwa.install.howToHide": "ಹಂತಗಳನ್ನು ಮರೆಮಾಡಿ",
  "pwa.install.guide.iosSafari": "Safari ಪರದೆಯ ಕೆಳಭಾಗದಲ್ಲಿ, Share ಬಟನ್ ಅನ್ನು ಟ್ಯಾಪ್ ಮಾಡಿ — ಇದು ಮೇಲ್ಮುಖ ಬಾಣವಿರುವ ಚೌಕದಂತೆ ಕಾಣುತ್ತದೆ.\nತೆರೆಯುವ ಮೆನುವಿನಲ್ಲಿ ಸ್ವಲ್ಪ ಕೆಳಗೆ ಸ್ಕ್ರೋಲ್ ಮಾಡಿ.\n\"Add to Home Screen\" ಆಯ್ಕೆಯನ್ನು ಟ್ಯಾಪ್ ಮಾಡಿ.\nಮೇಲಿನ ಬಲ ಮೂಲೆಯಲ್ಲಿ Add ಟ್ಯಾಪ್ ಮಾಡಿ. ಈಗ ಆ್ಯಪ್ ಸಾಮಾನ್ಯ ಆ್ಯಪ್‌ನಂತೆ ನಿಮ್ಮ ಹೋಮ್ ಸ್ಕ್ರೀನ್‌ನಲ್ಲಿ ಇರುತ್ತದೆ.",
  "pwa.install.guide.iosOther": "iPhone ಅಥವಾ iPad ನಲ್ಲಿ, ಹೋಮ್ ಸ್ಕ್ರೀನ್‌ಗೆ ಸೇರಿಸುವುದು Safari ಬ್ರೌಸರ್‌ನಲ್ಲಿ ಮಾತ್ರ ಕೆಲಸ ಮಾಡುತ್ತದೆ.\nSafari ತೆರೆದು ಇದೇ ವೆಬ್‌ಸೈಟ್‌ಗೆ ಹೋಗಿ.\nSafari ಕೆಳಭಾಗದಲ್ಲಿ Share ಬಟನ್ ಟ್ಯಾಪ್ ಮಾಡಿ — ಮೇಲ್ಮುಖ ಬಾಣವಿರುವ ಚೌಕ.\nಕೆಳಗೆ ಸ್ಕ್ರೋಲ್ ಮಾಡಿ, \"Add to Home Screen\" ಟ್ಯಾಪ್ ಮಾಡಿ, ನಂತರ Add ಟ್ಯಾಪ್ ಮಾಡಿ.",
  "pwa.install.guide.android": "ಮೇಲಿನ Install ಬಟನ್ ಅನ್ನು ಟ್ಯಾಪ್ ಮಾಡಿ.\nನಿಮ್ಮ ಬ್ರೌಸರ್ ಕೇಳಿದಾಗ, ದೃಢೀಕರಿಸಲು Install ಟ್ಯಾಪ್ ಮಾಡಿ.\nಆಯಿತು — ಈಗ ಆ್ಯಪ್ ನಿಮ್ಮ ಹೋಮ್ ಸ್ಕ್ರೀನ್‌ನಲ್ಲಿದೆ. ಸಾಮಾನ್ಯ ಆ್ಯಪ್‌ನಂತೆ ಯಾವಾಗ ಬೇಕಾದರೂ ತೆರೆಯಿರಿ.\nಪಾಪ್-ಅಪ್ ಕಾಣುತ್ತಿಲ್ಲವೇ? ನಿಮ್ಮ ಬ್ರೌಸರ್ ಮೆನು ತೆರೆಯಿರಿ — ಮೂರು ಚುಕ್ಕೆಗಳು, ಸಾಮಾನ್ಯವಾಗಿ ಮೇಲಿನ ಬಲಭಾಗದಲ್ಲಿ — ಮತ್ತು \"Install app\" ಟ್ಯಾಪ್ ಮಾಡಿ.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "ನೇರ ರೀಚಾರ್ಜ್",
  "agent.directRecharge.subtitle.player": "ಒಬ್ಬ ಆಟಗಾರನನ್ನು ಆಯ್ಕೆಮಾಡಿ ಮತ್ತು ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ ರೀಚಾರ್ಜ್ ಮಾಡಿ. ವಿನಂತಿ ಅಗತ್ಯವಿಲ್ಲ.",
  "agent.directRecharge.subtitle.subagent": "ಒಬ್ಬ ಉಪ-ಏಜೆಂಟ್‌ ಆಯ್ಕೆಮಾಡಿ ಮತ್ತು ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ ಅವರ ಪೂಲ್‌ಗೆ ರೀಚಾರ್ಜ್ ಮಾಡಿ.",
  "agent.directRecharge.search.player": "ರೀಚಾರ್ಜ್ ಮಾಡಲು ಆಟಗಾರನನ್ನು ಹುಡುಕಿ (ಹೆಸರು · ಫೋನ್ · ಇಮೇಲ್)…",
  "agent.directRecharge.search.subagent": "ರೀಚಾರ್ಜ್ ಮಾಡಲು ಉಪ-ಏಜೆಂಟ್‌ ಹುಡುಕಿ (ಹೆಸರು · ಇಮೇಲ್ · UAID)…",
  "agent.directRecharge.clearSearch": "ಹುಡುಕಾಟ ತೆರವುಗೊಳಿಸಿ",
  "agent.directRecharge.searching": "ಹುಡುಕುತ್ತಿದೆ…",
  "agent.directRecharge.noMatches": "ಯಾವುದೇ ಹೊಂದಾಣಿಕೆ ಇಲ್ಲ.",
  "agent.directRecharge.empty.player": "ನಿಮ್ಮ ಪಟ್ಟಿಯಲ್ಲಿ ಇನ್ನೂ ಆಟಗಾರರಿಲ್ಲ.",
  "agent.directRecharge.empty.subagent": "ಇನ್ನೂ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ ಇಲ್ಲ.",
  "agent.directRecharge.searchFailed": "ಹುಡುಕಾಟ ವಿಫಲವಾಗಿದೆ",
  "agent.directRecharge.label.wallet": "ವಾಲೆಟ್",
  "agent.directRecharge.label.pool": "ಪೂಲ್",
  "agent.directRecharge.modal.title.player": "ನೇರ ಆಟಗಾರ ರೀಚಾರ್ಜ್",
  "agent.directRecharge.modal.title.subagent": "ನೇರ ಉಪ-ಏಜೆಂಟ್ ರೀಚಾರ್ಜ್",
  "agent.directRecharge.modal.close": "ಮುಚ್ಚಿ",
  "agent.directRecharge.modal.targetPool": "ಗುರಿ ಪೂಲ್",
  "agent.directRecharge.modal.operatingWallet": "ಆಪರೇಟಿಂಗ್ ವಾಲೆಟ್",
  "agent.directRecharge.modal.dayToDay": "ದೈನಂದಿನ ಬ್ಯಾಲೆನ್ಸ್",
  "agent.directRecharge.modal.securityDeposit": "ಭದ್ರತಾ ಠೇವಣಿ",
  "agent.directRecharge.modal.collateral": "ಮೇಲಾಧಾರ ಪೂಲ್",
  "agent.directRecharge.modal.amount": "ಮೊತ್ತ (₹)",
  "agent.directRecharge.modal.overdraw": "ನಿಮ್ಮ ಪೂಲ್‌ (₹{pool}) ಗಿಂತ ಹೆಚ್ಚು ಪಾವತಿಸಲಾಗದು. ಮೊತ್ತವನ್ನು ಕಡಿಮೆ ಮಾಡಿ.",
  "agent.directRecharge.modal.reason": "ಕಾರಣ / ಟಿಪ್ಪಣಿ",
  "agent.directRecharge.modal.reasonPlaceholder": "ನಗದು ಸಂಗ್ರಹಿಸಲಾಗಿದೆ · ref 4291",
  "agent.directRecharge.modal.reasonHint": "ನಿಮ್ಮ ವಾಲೆಟ್ ಲೆಡ್ಜರ್ + ಆಡಿಟ್ ಲಾಗ್‌ನಲ್ಲಿ ಬರೆಯಲಾಗುತ್ತದೆ.",
  "agent.directRecharge.modal.proofLabel": "ಪಾವತಿಯ ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ (ಐಚ್ಛಿಕ)",
  "agent.directRecharge.modal.proofHint": "ಇತಿಹಾಸದ ಮೂಲಕ ಸ್ವೀಕರಿಸುವವರಿಗೆ ಗೋಚರಿಸುತ್ತದೆ.",
  "agent.directRecharge.modal.previewEmpty": "ವಹಿವಾಟು ಮುನ್ನೋಟ ನೋಡಲು ಮೊತ್ತ ನಮೂದಿಸಿ.",
  "agent.directRecharge.modal.previewBody": "ನಿಮ್ಮ ವಾಲೆಟ್‌ನಿಂದ {name} ಅವರ {pool}ಗೆ ಕಳುಹಿಸಲಾಗಿದೆ. ಸುರಕ್ಷಿತ ವರ್ಗಾವಣೆ; ಲೆಡ್ಜರ್ + ಆಡಿಟ್‌ನಲ್ಲಿ ದಾಖಲು.",
  "agent.directRecharge.modal.pool.playerWallet": "ವಾಲೆಟ್",
  "agent.directRecharge.modal.pool.operatingWallet": "ಆಪರೇಟಿಂಗ್ ವಾಲೆಟ್",
  "agent.directRecharge.modal.pool.securityDeposit": "ಭದ್ರತಾ ಠೇವಣಿ",
  "agent.directRecharge.modal.cancel": "ರದ್ದು",
  "agent.directRecharge.modal.send": "₹{amount} ಕಳುಹಿಸಿ",
  "agent.directRecharge.badge.player": "ಆಟಗಾರ",
  "agent.directRecharge.badge.subagent": "ಉಪ-ಏಜೆಂಟ್",
  "agent.directRecharge.modal.yourDirectSubagent": "ನಿಮ್ಮ ನೇರ ಉಪ-ಏಜೆಂಟ್",
  "agent.directRecharge.modal.fundingSource": "ನಿಧಿ ಮೂಲ",
  "agent.directRecharge.modal.you": "(ನೀವು)",
  "agent.directRecharge.modal.yourPool": "ನಿಮ್ಮ ಪೂಲ್",
  "agent.directRecharge.modal.notSignedIn": "ಏಜೆಂಟ್ ಆಗಿ ಸೈನ್ ಇನ್ ಆಗಿಲ್ಲ",
  "agent.directRecharge.modal.failed": "ವಿಫಲವಾಗಿದೆ",

  // Common controls
  'common.signIn': 'ಸೈನ್ ಇನ್',
  'common.signUp': 'ಸೈನ್ ಅಪ್',
  'common.login': 'ಲಾಗಿನ್',
  'common.logout': 'ಲಾಗ್ ಔಟ್',
  'common.continue': 'ಮುಂದುವರಿಸಿ',
  'common.cancel': 'ರದ್ದುಮಾಡಿ',
  'common.save': 'ಉಳಿಸಿ',
  'common.update': 'ನವೀಕರಿಸಿ',
  'common.clear': 'ಅಳಿಸಿ',
  'common.back': 'ಹಿಂದೆ',
  'common.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'common.sending': 'ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…',
  'common.saving': 'ಉಳಿಸಲಾಗುತ್ತಿದೆ…',
  'common.optional': 'ಐಚ್ಛಿಕ',
  'common.show': 'ತೋರಿಸಿ',
  'common.hide': 'ಮರೆಮಾಡಿ',
  'common.profile': 'ಪ್ರೊಫೈಲ್',
  'common.settings': 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',

  // NavBar / BottomNav
  'nav.brand': 'ಬಾಲಿ ಗೇಮ್ಸ್',
  'nav.home': 'ಮುಖಪುಟ',
  'nav.games': 'ಗೇಮ್‌ಗಳು',
  'nav.wallet': 'ವಾಲೆಟ್',
  'nav.rewards': 'ಬಹುಮಾನಗಳು',
  'nav.profile': 'ಪ್ರೊಫೈಲ್',

  // Login page
  'login.title': 'ಮರಳಿ ಸ್ವಾಗತ',
  'login.subtitle': 'ನಿಮ್ಮ ಆಟವನ್ನು ಮುಂದುವರಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ.',
  'login.idLabel': 'ಬಳಕೆದಾರ ಹೆಸರು, ಇಮೇಲ್ ಅಥವಾ ಫೋನ್',
  'login.idPlaceholder': 'ಬಳಕೆದಾರ ಹೆಸರು, ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್',
  'login.passwordLabel': 'ಪಾಸ್‌ವರ್ಡ್',
  'login.passwordPlaceholder': 'ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ನಮೂದಿಸಿ',
  'login.forgot': 'ಮರೆತಿರಾ?',
  'login.cta': 'ಸೈನ್ ಇನ್',
  'login.signingIn': 'ಸೈನ್ ಇನ್ ಆಗುತ್ತಿದೆ…',
  'login.newHere': 'ಹೊಸಬರೇ?',
  'login.createAccount': 'ಖಾತೆ ರಚಿಸಿ',

  // Register page
  'register.title': 'ನಿಮ್ಮ ಖಾತೆಯನ್ನು ರಚಿಸಿ',
  'register.subtitle': 'ಒಂದು ಟ್ಯಾಗ್ ಆರಿಸಿ ಮತ್ತು ನೀವು ಒಳಗೆ.',
  'register.usernameLabel': 'ಬಳಕೆದಾರ ಹೆಸರು',
  'register.usernamePlaceholder': 'ಒಂದು ವಿಶಿಷ್ಟ ಹ್ಯಾಂಡಲ್ ಆರಿಸಿ',
  'register.usernameHelp': 'ಅಕ್ಷರಗಳು, ಸಂಖ್ಯೆಗಳು ಮತ್ತು ಅಂಡರ್‌ಸ್ಕೋರ್‌ಗಳು. 3–24 ಅಕ್ಷರಗಳು.',
  'register.emailLabel': 'ಇಮೇಲ್',
  'register.emailPlaceholder': 'ನಿಮ್ಮ ಇಮೇಲ್ ವಿಳಾಸ',
  'register.mobileLabel': 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  'register.mobilePlaceholder': '10-ಅಂಕಿಯ ಮೊಬೈಲ್',
  'register.mobileHelp': 'ನಾವು ದೇಶದ ಕೋಡ್ ಅನ್ನು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಸೇರಿಸುತ್ತೇವೆ.',
  'register.passwordLabel': 'ಪಾಸ್‌ವರ್ಡ್',
  'register.passwordPlaceholder': 'ಒಂದು ಬಲವಾದ ಪಾಸ್‌ವರ್ಡ್ ಆರಿಸಿ',
  'register.passwordHelp': 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು, ಒಂದು ಅಕ್ಷರ ಮತ್ತು ಒಂದು ಅಂಕಿಯೊಂದಿಗೆ.',
  'register.referralLabel': 'ರೆಫರಲ್ ಕೋಡ್',
  'register.referralPlaceholder': 'ಸ್ನೇಹಿತನ ಆಹ್ವಾನ ಕೋಡ್',
  'register.cta': 'ಖಾತೆ ರಚಿಸಿ',
  'register.creating': 'ರಚಿಸಲಾಗುತ್ತಿದೆ…',
  'register.alreadyPlayer': 'ಈಗಾಗಲೇ ಆಟಗಾರರೇ?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'ನಿಮ್ಮ ಬಗ್ಗೆ', 'register.step.signin': 'ಸೈನ್-ಇನ್', 'register.step.finish': 'ಮುಗಿಸಿ',
  'register.step.aboutSubtitle': 'ನಿಮ್ಮ ಬಗ್ಗೆ ಸ್ವಲ್ಪ ಹೇಳಿ', 'register.step.signinSubtitle': 'ಹೇಗೆ ಸೈನ್-ಇನ್ ಮಾಡುತ್ತೀರಿ ಎಂದು ಹೊಂದಿಸಿ', 'register.step.finishSubtitle': 'ಸ್ಥಳವನ್ನು ಆಯ್ಕೆಮಾಡಿ ಮತ್ತು ಮುಗಿಸಿ',
  'register.firstName': 'ಮೊದಲ ಹೆಸರು', 'register.firstNamePh': 'ಆರವ್',
  'register.lastName': 'ಕೊನೆಯ ಹೆಸರು', 'register.lastNamePh': 'ಶರ್ಮಾ',
  'register.dob': 'ಜನ್ಮ ದಿನಾಂಕ', 'register.dobHelp': 'ಆಡಲು ನಿಮ್ಮ ವಯಸ್ಸು 18 ಅಥವಾ ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿರಬೇಕು',
  'register.v.required': 'ಅಗತ್ಯ', 'register.v.nameMin': 'ಕನಿಷ್ಠ 2 ಅಕ್ಷರಗಳು',
  'register.v.tooLong': 'ತುಂಬಾ ಉದ್ದ', 'register.v.nameChars': 'ಅಕ್ಷರಗಳು, ಸ್ಪೇಸ್, ಹೈಫನ್ ಮಾತ್ರ',
  'register.v.invalidDate': 'ಅಮಾನ್ಯ ದಿನಾಂಕ', 'register.v.minAge': 'ನಿಮ್ಮ ವಯಸ್ಸು ಕನಿಷ್ಠ 18 ಇರಬೇಕು',
  'register.v.tooOld': 'ದಯವಿಟ್ಟು ವರ್ಷ ಪರಿಶೀಲಿಸಿ',
  'register.v.usernameMin': 'ಕನಿಷ್ಠ 3 ಅಕ್ಷರಗಳು', 'register.v.usernameMax': 'ತುಂಬಾ ಉದ್ದ (ಗರಿಷ್ಠ 24)',
  'register.v.usernameChars': 'ಅಕ್ಷರಗಳು, ಸಂಖ್ಯೆಗಳು, ಅಂಡರ್‌ಸ್ಕೋರ್ ಮಾತ್ರ', 'register.v.emailShape': 'ಅಮಾನ್ಯ ಇಮೇಲ್ ಎಂದು ತೋರುತ್ತದೆ',
  'register.v.phoneDigits': '10 ಅಂಕಿಗಳ ಸಂಖ್ಯೆ ಇರಬೇಕು', 'register.v.phonePrefix': 'ಭಾರತೀಯ ಮೊಬೈಲ್ 6–9 ರಿಂದ ಆರಂಭ',
  'register.v.pwdMin': 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು', 'register.v.pwdLetter': 'ಕನಿಷ್ಠ ಒಂದು ಅಕ್ಷರ ಇರಬೇಕು',
  'register.v.pwdNumber': 'ಕನಿಷ್ಠ ಒಂದು ಸಂಖ್ಯೆ ಇರಬೇಕು',
  'register.v.referralShort': 'ತುಂಬಾ ಚಿಕ್ಕದು', 'register.v.referralChars': 'ಅಕ್ಷರಗಳು ಮತ್ತು ಸಂಖ್ಯೆಗಳು ಮಾತ್ರ',
  'register.v.usernameTaken': 'ಈ ಬಳಕೆದಾರಹೆಸರು ಈಗಾಗಲೇ ತೆಗೆದುಕೊಳ್ಳಲಾಗಿದೆ',
  'register.v.emailTaken': 'ಈ ಇಮೇಲ್ ಈಗಾಗಲೇ ನೋಂದಾಯಿಸಲಾಗಿದೆ',
  'register.v.phoneTaken': 'ಈ ಸಂಖ್ಯೆ ಈಗಾಗಲೇ ನೋಂದಾಯಿಸಲಾಗಿದೆ',
  'register.v.noLocation': 'ಮುಂದುವರಿಯಲು ಕನಿಷ್ಠ ಒಂದು ಸ್ಥಳ ವಿಧಾನವನ್ನು ಹಂಚಿಕೊಳ್ಳಿ.',
  'register.welcome.title': 'ಸ್ವಾಗತ', 'register.welcome.subtitle': 'ನಿಮ್ಮ ಖಾತೆ ಸಿದ್ಧ. ಈಗ ಸುರಕ್ಷಿತಗೊಳಿಸಿ ಅಥವಾ ನೇರವಾಗಿ ಪ್ರವೇಶಿಸಿ?',
  'register.welcome.verifyTelegram': 'ಟೆಲಿಗ್ರಾಮ್ ಮೂಲಕ ಪರಿಶೀಲಿಸಿ',
  'register.welcome.verifyExplain': '⚠ ಮುಖ್ಯ: ಈಗಲೇ ಟೆಲಿಗ್ರಾಮ್ ಮೂಲಕ ಪರಿಶೀಲಿಸಿ. ಪರಿಶೀಲನೆ ಇಲ್ಲದೆ, ಪಾಸ್‌ವರ್ಡ್ ಮರೆತರೆ ನೀವೇ ರೀಸೆಟ್ ಮಾಡಲು ಸಾಧ್ಯವಿಲ್ಲ — ನಿಮ್ಮ ಏಜೆಂಟ್ ಅಥವಾ ಅಡ್ಮಿನ್ ಮಾತ್ರ ರೀಸೆಟ್ ಮಾಡಬಹುದು. ನಂತರ ಪ್ರೊಫೈಲ್‌ನಿಂದಲೂ ಪರಿಶೀಲಿಸಬಹುದು.',
  'register.welcome.verifyHeads': 'ಇದನ್ನು ನಂತರಕ್ಕಾಗಿ ಉಳಿಸಿ',
  'register.welcome.verifyConsequence': 'ಇಮೇಲ್ ಪರಿಶೀಲನೆ ಈಗ ಲಭ್ಯವಿಲ್ಲ. ನಂತರ ಪಾಸ್‌ವರ್ಡ್ ರೀಸೆಟ್ ಮಾಡಲು ಟೆಲಿಗ್ರಾಮ್ ಮೂಲಕ ಪರಿಶೀಲಿಸಿ. ಮಾಡದಿದ್ದರೆ, ಪಾಸ್‌ವರ್ಡ್ ಮರೆತರೆ ನಿಮ್ಮ ಏಜೆಂಟ್ ಅಥವಾ ಅಡ್ಮಿನ್ ಮಾತ್ರ ರೀಸೆಟ್ ಮಾಡಬಹುದು.',
  'register.welcome.continue': 'ಅಪ್ಲಿಕೇಶನ್‌ಗೆ ಮುಂದುವರಿಯಿರಿ', 'register.welcome.continueLogin': 'ಮುಂದುವರಿಯಲು ಸೈನ್ ಇನ್ ಮಾಡಿ', 'register.welcome.useDifferent': 'ಬೇರೆ ಖಾತೆಯನ್ನು ಬಳಸಿ',
  'register.geo.title': 'ನೀವು ಎಲ್ಲಿಂದ ಆಡುತ್ತಿದ್ದೀರಿ?', 'register.geo.subtitle': 'ಹತ್ತಿರದ ಏಜೆಂಟ್‌ನೊಂದಿಗೆ ಜೋಡಿಸಲು ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ಬಳಸುತ್ತೇವೆ.',
  'register.geo.showAlt': 'ಸ್ಥಳ ಹಂಚಿಕೊಳ್ಳಲಾಗದೇ? ಬೇರೆ ವಿಧಾನವನ್ನು ಬಳಸಿ',
  'register.geo.hideAlt': 'ಇತರ ವಿಧಾನಗಳನ್ನು ಮರೆಮಾಡಿ', 'register.geo.captured': 'ಸ್ಥಳವನ್ನು ಸೆರೆಹಿಡಿಯಲಾಗಿದೆ',
  'register.geo.capturedHint': 'ನೀವು ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ಪ್ರೊಫೈಲ್‌ನಿಂದ ಇದನ್ನು ಬದಲಾಯಿಸಬಹುದು.',
  'register.geo.verifyNote': 'ಸೈನ್-ಅಪ್ ನಂತರ, ಪ್ರೊಫೈಲ್‌ನಿಂದ ಟೆಲಿಗ್ರಾಮ್ (ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಲು) ಅಥವಾ ಇಮೇಲ್‌ನಿಂದ ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ಪರಿಶೀಲಿಸಬಹುದು. ಪರಿಶೀಲನೆ ಐಚ್ಛಿಕ.',

  // Verify (post-signup)
  'verify.title': 'ನಿಮ್ಮ ಖಾತೆಯನ್ನು ಪರಿಶೀಲಿಸಿ',
  'verify.subtitle': 'ಒಂದು ಆಯ್ಕೆಯನ್ನು ಆರಿಸಿ — ಅಷ್ಟೇ ಸಾಕು.',
  'verify.tabTelegram': 'ಟೆಲಿಗ್ರಾಂ',
  'verify.tabEmail': 'ಇಮೇಲ್',
  'verify.emailSent': 'ನಾವು {email} ಗೆ ಪರಿಶೀಲನಾ ಲಿಂಕ್ ಕಳುಹಿಸಿದ್ದೇವೆ.',
  'verify.emailHint': 'ಈ ಸಾಧನದಲ್ಲಿ ಅದನ್ನು ತೆರೆಯಿರಿ. ಕಾಣಿಸುತ್ತಿಲ್ಲವೇ? ಸ್ಪ್ಯಾಮ್ / ಪ್ರಚಾರಗಳನ್ನು ಪರಿಶೀಲಿಸಿ.',
  'verify.telegramOpen': '@BaaliGames_bot ತೆರೆಯಿರಿ',
  'verify.telegramExpiry': 'ಲಿಂಕ್ 30 ನಿಮಿಷಗಳಲ್ಲಿ ಮುಗಿಯುತ್ತದೆ. ಟೆಲಿಗ್ರಾಂ ನೀವು ಒಮ್ಮೆ Start ಒತ್ತಬೇಕು.',
  'verify.goToLogin': 'ಲಾಗಿನ್‌ಗೆ ಹೋಗಿ',
  'verify.email.title': 'ಇಮೇಲ್ ಪರಿಶೀಲಿಸಲಾಗಿದೆ',
  'verify.email.sub': 'ನಿಮ್ಮ ಖಾತೆ ಸಕ್ರಿಯವಾಗಿದೆ. ಡ್ರಾಪ್ ಇನ್ ಮಾಡುವ ಸಮಯ.',
  'verify.email.failed': 'ಪರಿಶೀಲನೆ ವಿಫಲವಾಗಿದೆ',
  'verify.email.verifying': 'ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ…',
  'verify.backRegister': 'ನೋಂದಣಿಗೆ ಹಿಂದೆ',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ಇಮೇಲ್ ಮರು ಕಳುಹಿಸಿ',
  'verify.gate.resent': 'ಕಳುಹಿಸಲಾಗಿದೆ!',
  'verify.gate.checking': 'ಕೆಲಸ ಮಾಡಲಾಗುತ್ತಿದೆ…',
  'verify.gate.tgFallback': 'ಲಿಂಕ್ ಅನ್ನು ಕೈಯಾರೆ ತೆರೆಯಿರಿ',
  'verify.gate.waiting': 'ನಾವು ಪರಿಶೀಲನೆಯನ್ನು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಪತ್ತೆಹಚ್ಚುತ್ತೇವೆ.',
  'verify.gate.signOut': 'ಸೈನ್ ಔಟ್',

  // Forgot password
  'forgot.title': 'ನಿಮ್ಮ ಖಾತೆಯನ್ನು ಮರುಪಡೆಯಿರಿ',
  'forgot.subtitle': 'ನಾವು ನಿಮಗೆ ಮರಳಿ ಪ್ರವೇಶಿಸುವ ಮಾರ್ಗವನ್ನು ಕಳುಹಿಸುತ್ತೇವೆ.',
  'forgot.idLabel': 'ಬಳಕೆದಾರ ಹೆಸರು, ಇಮೇಲ್ ಅಥವಾ ಫೋನ್',
  'forgot.idPlaceholder': 'ಬಳಕೆದಾರ ಹೆಸರು, ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್',
  'forgot.sendVia': 'ಮೂಲಕ ಕಳುಹಿಸಿ',
  'forgot.email': 'ಇಮೇಲ್',
  'forgot.telegram': 'ಟೆಲಿಗ್ರಾಂ OTP',
  'forgot.telegramHelp': 'ನಿಮ್ಮ ಖಾತೆ ನಮ್ಮ ಬಾಟ್‌ಗೆ ಲಿಂಕ್ ಆಗಿದ್ದರೆ ಮಾತ್ರ ಟೆಲಿಗ್ರಾಂ ಕೆಲಸ ಮಾಡುತ್ತದೆ.',
  'forgot.cta': 'ಮರುಪಡೆಯುವಿಕೆ ಕಳುಹಿಸಿ',
  'forgot.sentEmail': 'ಖಾತೆ ಅಸ್ತಿತ್ವದಲ್ಲಿದ್ದರೆ, ಮರುಹೊಂದಿಕೆ ಲಿಂಕ್ ಅನ್ನು ಇಮೇಲ್ ಮಾಡಲಾಗಿದೆ.',
  'forgot.sentTelegram': 'ಖಾತೆ ಅಸ್ತಿತ್ವದಲ್ಲಿದ್ದರೆ, ಟೆಲಿಗ್ರಾಂ ಮೂಲಕ 6-ಅಂಕಿಯ ಕೋಡ್ ಕಳುಹಿಸಲಾಗಿದೆ.',
  'forgot.redirecting': 'ಮರುಹೊಂದಿಕೆ ಪುಟಕ್ಕೆ ಮರುನಿರ್ದೇಶಿಸಲಾಗುತ್ತಿದೆ…',
  'forgot.back': 'ಲಾಗಿನ್‌ಗೆ ಹಿಂದೆ',

  // Reset password
  'reset.title': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ಹೊಂದಿಸಿ',
  'reset.subOtp': 'ನಾವು ಟೆಲಿಗ್ರಾಂನಲ್ಲಿ ಕಳುಹಿಸಿದ ಕೋಡ್ ಬಳಸಿ.',
  'reset.subLink': 'ತಾಜಾ ಸಂಯೋಜನೆಯನ್ನು ಆರಿಸಿ ಮತ್ತು ಮರಳಿ ಪ್ರವೇಶಿಸಿ.',
  'reset.otpLabel': '6-ಅಂಕಿಯ ಕೋಡ್',
  'reset.newPasswordLabel': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್',
  'reset.cta': 'ಪಾಸ್‌ವರ್ಡ್ ನವೀಕರಿಸಿ',
  'reset.updating': 'ನವೀಕರಿಸಲಾಗುತ್ತಿದೆ…',
  'reset.successTitle': 'ಪಾಸ್‌ವರ್ಡ್ ನವೀಕರಿಸಲಾಗಿದೆ',
  'reset.successSub': 'ನೀವು ಸಿದ್ಧ. ಆಡುವ ಸಮಯ.',

  // Home (logged-out)
  'home.welcomeTo': 'ಸ್ವಾಗತ',
  'home.heroSub': 'ಕೌಶಲ್ಯ ಆಧಾರಿತ ಗೇಮಿಂಗ್ ಕೇಂದ್ರ. ಡ್ರಾಪ್ ಇನ್ ಮಾಡಲು ಸೈನ್ ಇನ್ ಮಾಡಿ ಅಥವಾ ನಿಮ್ಮ ಆಟಗಾರ ಖಾತೆ ರಚಿಸಿ.',
  'home.skillBased': 'ಕೌಶಲ್ಯ ಆಧಾರಿತ',
  'home.skillBasedSub': 'ತಂತ್ರಗಾರಿಕೆ ಗೆಲ್ಲುತ್ತದೆ',
  'home.fairPlay': 'ನ್ಯಾಯಯುತ ಆಟ',
  'home.fairPlaySub': 'ಆಂಟಿ-ಚೀಟ್ ಆನ್',
  'home.instantPayout': 'UPI ಪಾವತಿ',
  'home.instantPayoutSub': '24 ಗಂಟೆಗಳ ಒಳಗೆ',
  'home.featured': 'ವಿಶೇಷ ಗೇಮ್‌ಗಳು',
  'home.tapToPlay': 'ಆಡಲು ಟ್ಯಾಪ್ ಮಾಡಿ',
  'home.ready': 'ಆಡಲು ಸಿದ್ಧವೇ?',
  'home.dropIn': 'ಡ್ರಾಪ್ ಇನ್ ಮಾಡಿ ಮತ್ತು ನಿಜವಾದ ಬಹುಮಾನಗಳನ್ನು ಗೆಲ್ಲಿ.',
  'home.start': 'ಪ್ರಾರಂಭಿಸಿ',

  // Home (logged-in)
  'home.welcomeBack': 'ಮರಳಿ ಸ್ವಾಗತ',
  'home.balance': 'ನಿಮ್ಮ ಬ್ಯಾಲೆನ್ಸ್',
  'home.live': 'ಲೈವ್',
  'home.add': 'ಸೇರಿಸಿ',
  'home.manage': 'ನಿರ್ವಹಿಸಿ',
  'home.rewards': 'ಬಹುಮಾನಗಳು',
  'home.todayChallenge': 'ಇಂದಿನ ಸವಾಲು',
  'home.todayChallengeSub': 'ಮ್ಯಾಚ್ ಗೆಲ್ಲಿ — ಸ್ಟ್ರೀಕ್ ಬೋನಸ್ ಗಳಿಸಿ.',
  'home.view': 'ನೋಡಿ',
  'home.yourGames': 'ನಿಮ್ಮ ಗೇಮ್‌ಗಳು',
  'home.viewAll': 'ಎಲ್ಲವನ್ನು ನೋಡಿ',

  // Profile tabs
  'profile.details': 'ವಿವರಗಳು',
  'profile.address': 'ವಿಳಾಸ',
  'profile.languages': 'ಭಾಷೆಗಳು',
  'profile.account': 'ಖಾತೆ',
  'profile.photo': 'ಫೋಟೋ',
  'profile.preferred': 'ಆದ್ಯತೆಯ ಭಾಷೆ',

  // Profile → Languages tab
  'profile.lang.primary': 'ಪ್ರಾಥಮಿಕ ಭಾಷೆ',
  'profile.lang.primarySub': 'ಆ್ಯಪ್-ವ್ಯಾಪಕ ಡೀಫಾಲ್ಟ್',
  'profile.lang.locked': 'ಲಾಕ್ ಆಗಿದೆ',
  'profile.lang.second': 'ಎರಡನೇ ಭಾಷೆ',
  'profile.lang.pickHelp': 'ನೀವು ಮಾತನಾಡುವ ಇನ್ನೊಂದು ಭಾಷೆಯನ್ನು ಆರಿಸಿ. ಆಯ್ಕೆ ಮಾಡಲು ಕಾರ್ಡ್ ಅನ್ನು ಟ್ಯಾಪ್ ಮಾಡಿ.',
  'profile.lang.saved': 'ಎರಡನೇ ಭಾಷೆ ಉಳಿಸಲಾಗಿದೆ',
  'profile.lang.cleared': 'ಎರಡನೇ ಭಾಷೆ ಅಳಿಸಲಾಗಿದೆ',

  // Language switcher
  'lang.switcher.aria': 'ಭಾಷೆ ಆರಿಸಿ',
  'lang.switcher.title': 'ನಿಮ್ಮ ಭಾಷೆಯನ್ನು ಆರಿಸಿ',
  'lang.switcher.sub': 'ಸಂಪೂರ್ಣ ಆ್ಯಪ್ ತಕ್ಷಣ ನವೀಕರಿಸುತ್ತದೆ.',
  'lang.switcher.current': 'ಪ್ರಸ್ತುತ',

  // Rewards page
  'rewards.title': 'ಬಹುಮಾನಗಳು ಮತ್ತು ಆಫರ್‌ಗಳು',
  'rewards.subtitle': 'ನೀವು ಈಗಲೇ ಗಳಿಸಬಹುದಾದ ಅಥವಾ ಕ್ಲೈಮ್ ಮಾಡಬಹುದಾದ ಬೋನಸ್‌ಗಳು.',
  'rewards.tab.available': 'ಲಭ್ಯ',
  'rewards.tab.inReview': 'ಪರಿಶೀಲನೆಯಲ್ಲಿ',
  'rewards.tab.claimed': 'ಕ್ಲೈಮ್ ಮಾಡಲಾಗಿದೆ',
  'rewards.empty.available.title': 'ಈಗ ಯಾವುದೇ ಆಫರ್‌ಗಳಿಲ್ಲ',
  'rewards.empty.available.body': 'ಶೀಘ್ರದಲ್ಲೇ ಮತ್ತೆ ಪರಿಶೀಲಿಸಿ — ಅಡ್ಮಿನ್ ನಿಯಮಿತವಾಗಿ ಹೊಸ ಆಫರ್‌ಗಳನ್ನು ಪೋಸ್ಟ್ ಮಾಡುತ್ತಾರೆ.',
  'rewards.empty.review.title': 'ಪರಿಶೀಲನೆಯಲ್ಲಿ ಏನೂ ಇಲ್ಲ',
  'rewards.empty.review.body': 'ಅಡ್ಮಿನ್ ಅನುಮೋದನೆ ಬೇಕಾದ ಕ್ಲೈಮ್‌ಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ.',
  'rewards.empty.claimed.title': 'ಇನ್ನೂ ಯಾವುದೇ ಕ್ಲೈಮ್‌ಗಳಿಲ್ಲ',
  'rewards.empty.claimed.body': 'ನೀವು ಬೋನಸ್ ಗಳಿಸಿದ ನಂತರ, ಅದು ಇಲ್ಲಿ ಕ್ಲೈಮ್ ಆಗಿ ಕಾಣಿಸುತ್ತದೆ.',
  'rewards.expires': '{date} ರಂದು ಮುಗಿಯುತ್ತದೆ',
  'rewards.remaining': '{amount} ಉಳಿದಿದೆ',
  'rewards.cap': 'ಮಿತಿ',
  'rewards.claim.cta': 'ಕ್ಲೈಮ್ ಮಾಡಿ',
  'rewards.claim.submit': 'ಸಲ್ಲಿಸಿ',
  'rewards.claimed.credited': '{date} ರಂದು {amount} ಕ್ಲೈಮ್ ಮಾಡಲಾಗಿದೆ',
  'rewards.claimed.pending': 'ಪರಿಶೀಲನೆಗೆ ಬಾಕಿಯಿರುವ',
  'rewards.claimed.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'rewards.auto.willCredit': 'ಷರತ್ತುಗಳು ಪೂರೈಸಿದಾಗ ಸ್ವಯಂ-ಜಮಾ ಆಗುತ್ತದೆ',
  'rewards.auto.willReview': 'ಅಡ್ಮಿನ್ ಪರಿಶೀಲನಾ ಸಾಲಿನಲ್ಲಿ ಬರುತ್ತದೆ',
  'rewards.row.creditedOn': '{date} ರಂದು ಜಮಾ ಆಗಿದೆ',
  'rewards.row.submittedOn': '{date} ರಂದು ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'rewards.toast.credited': 'ಬೋನಸ್ ಜಮಾ ಆಗಿದೆ — ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ {amount} ಸೇರಿಸಲಾಗಿದೆ',
  'rewards.toast.submitted': 'ಪರಿಶೀಲನೆಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'rewards.toast.alreadyClaimed': 'ಈಗಾಗಲೇ ಕ್ಲೈಮ್ ಮಾಡಲಾಗಿದೆ',
  'rewards.toast.rejected': 'ಕ್ಲೈಮ್ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',

  // Wallet page
  'wallet.balance': 'ಬ್ಯಾಲೆನ್ಸ್',
  'wallet.balance.note': 'ಪ್ರವೇಶಗಳು ತಕ್ಷಣವೇ ಡೆಬಿಟ್ ಆಗುತ್ತವೆ; ಮ್ಯಾಚ್ ಕೊನೆಯಲ್ಲಿ ಬಹುಮಾನಗಳು ಜಮಾ ಆಗುತ್ತವೆ.',
  'wallet.addMoney': 'ಹಣ ಸೇರಿಸಿ',
  'wallet.withdraw': 'ಹಿಂಪಡೆಯಿರಿ',
  'wallet.withdraw.minBalance': 'ಹಿಂಪಡೆಯಲು ಕನಿಷ್ಠ ₹200 ಬೇಕು',
  'wallet.history.byGame': 'ಗೇಮ್ ಪ್ರಕಾರ ನಿಮ್ಮ ಇತಿಹಾಸ',
  'wallet.history.empty': 'ಇನ್ನೂ ಯಾವುದೇ ಚಟುವಟಿಕೆ ಇಲ್ಲ. ನಿಮ್ಮ ಮೊದಲ ಮ್ಯಾಚ್ ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತದೆ.',
  'wallet.activity': 'ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ',
  'wallet.activity.empty': 'ಇನ್ನೂ ಯಾವುದೇ ವ್ಯವಹಾರಗಳಿಲ್ಲ.',
  'wallet.net': 'ನಿವ್ವಳ',
  'wallet.newer': 'ಹೊಸತು',
  'wallet.older': 'ಹಳೆಯದು',
  'wallet.pending.title': 'ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳು',
  'wallet.pending.recharge': '{amount} ರೀಚಾರ್ಜ್ — ಅಡ್ಮಿನ್ ಅನುಮೋದನೆಗೆ ಕಾಯುತ್ತಿದೆ',
  'wallet.pending.withdrawal': '{amount} ಹಿಂಪಡೆಯುವಿಕೆ — ಪಾವತಿಗೆ ಕಾಯುತ್ತಿದೆ',
  'wallet.state.pending': 'ಬಾಕಿಯಿರುವ',
  'wallet.state.paid': 'ಪಾವತಿಯಾಗಿದೆ',
  'wallet.pending.txnPrefix': 'ವಹಿವಾಟು:',
  'wallet.pending.upload.label': 'ತ್ವರಿತ ಕ್ರಮಕ್ಕಾಗಿ ಪಾವತಿಯ ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ.',
  'wallet.pending.upload.hint':  'ವಿನಂತಿ ರಚಿಸಿದ ನಂತರ ನಿಮಗೆ 24 ಗಂಟೆಗಳ ಸಮಯವಿದೆ.',
  'wallet.pending.upload.failed': 'ಅಪ್‌ಲೋಡ್ ವಿಫಲವಾಗಿದೆ',
  'wallet.activity.balanceAfter': 'ಬ್ಯಾಲೆನ್ಸ್ {balance}',
  'wallet.activity.range': '{total}ರಲ್ಲಿ {from}–{to}',
  'wallet.activity.window': 'ನಿಮ್ಮ ಕಳೆದ {days} ದಿನಗಳ ಚಟುವಟಿಕೆ',
  'wallet.history.summary.one':   '{matches} ಪಂದ್ಯ · ಗೆದ್ದದ್ದು {wins} · ಖರ್ಚು {bets}',
  'wallet.history.summary.other': '{matches} ಪಂದ್ಯಗಳು · ಗೆದ್ದದ್ದು {wins} · ಖರ್ಚು {bets}',
  'wallet.history.summary.refunded': ' · ಹಿಂಪಾವತಿ {refunds}',
  'ledger.kind.bet': 'ಪಂದ್ಯ ಎಂಟ್ರಿ', 'ledger.kind.win': 'ಪಂದ್ಯ ಬಹುಮಾನ',
  'ledger.kind.refund': 'ಪಂದ್ಯ ಮರುಪಾವತಿ', 'ledger.kind.forfeit': 'ಪಂದ್ಯ ಜಪ್ತಿ',
  'ledger.kind.fee': 'ವೇದಿಕೆ ಶುಲ್ಕ', 'ledger.kind.recharge': 'ಟಾಪ್-ಅಪ್',
  'ledger.kind.admin_credit': 'ಅಡ್ಮಿನ್ ಕ್ರೆಡಿಟ್', 'ledger.kind.admin_debit': 'ಅಡ್ಮಿನ್ ಡೆಬಿಟ್',
  'ledger.kind.referral_bonus': 'ರೆಫರಲ್ ಬೋನಸ್', 'ledger.kind.signup_bonus': 'ಸೈನ್-ಅಪ್ ಬೋನಸ್',
  'ledger.kind.offer_credit': 'ಆಫರ್ ಬಹುಮಾನ',
  'ledger.kind.withdrawal_hold': 'ಹಿಂಪಡೆಯುವಿಕೆ ಹೋಲ್ಡ್', 'ledger.kind.withdrawal_refund': 'ಹಿಂಪಡೆಯುವಿಕೆ ಮರುಪಾವತಿ',
  'ledger.kind.agent_topup_in': 'ಏಜೆಂಟ್ ಟಾಪ್-ಅಪ್', 'ledger.kind.agent_co_fund': 'ಏಜೆಂಟ್ ಕೊಡುಗೆ', 'ledger.kind.turnover_reward': 'ಕ್ಯಾಶ್‌ಬ್ಯಾಕ್',
  'ledger.withdrawalPaid': 'ಹಿಂಪಡೆಯುವಿಕೆ ಪಾವತಿಯಾಗಿದೆ', 'ledger.withdrawalPaidPartial': 'ಹಿಂಪಡೆಯುವಿಕೆ ಪಾವತಿ (ಭಾಗಶಃ)',
  'ledger.withdrawalPaid.txn': 'ಪಾವತಿಯಾಗಿದೆ · ವಹಿವಾಟು {txnId}',
  'ledger.withdrawalRejected': 'ಹಿಂಪಡೆಯುವಿಕೆ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ', 'ledger.withdrawalRejected.note': 'ಹಿಡಿದಿಟ್ಟ ಮೊತ್ತ ಮರಳಿದೆ',
  'ledger.withdrawalExpired': 'ಹಿಂಪಡೆಯುವಿಕೆ ಮುಗಿದಿದೆ', 'ledger.awaitingProof': 'ಪುರಾವೆಗಾಗಿ ಕಾಯುತ್ತಿದೆ',
  'proofPicker.button.compact': 'ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಆಯ್ಕೆಮಾಡಿ', 'proofPicker.button.prominent': 'ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಸೇರಿಸಿ',
  'proofPicker.processing': 'ಪ್ರಕ್ರಿಯೆಗೊಳ್ಳುತ್ತಿದೆ…',
  'proofPicker.error.mime': 'ಕೇವಲ JPG, PNG ಅಥವಾ WebP ಚಿತ್ರಗಳಿಗೆ ಮಾತ್ರ ಅವಕಾಶ.',
  'proofPicker.error.size': 'ಫೈಲ್ ತುಂಬಾ ದೊಡ್ಡದು — ಗರಿಷ್ಠ 5 MB.',
  'proofPicker.error.processing': 'ಚಿತ್ರವನ್ನು ಪ್ರಕ್ರಿಯೆಗೊಳಿಸಲಾಗಿಲ್ಲ',
  'proofPicker.previewAlt': 'ಆಯ್ಕೆ ಮಾಡಿದ ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಪೂರ್ವವೀಕ್ಷಣೆ',
  'proofViewer.state.none': 'ಸ್ಕ್ರೀನ್‌ಶಾಟ್‌ಗಾಗಿ ಕಾಯುತ್ತಿದೆ', 'proofViewer.state.available': 'ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ನೋಡಿ',
  'proofViewer.state.expired': 'ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಲಭ್ಯವಿಲ್ಲ', 'proofViewer.state.viewLimit': 'ವೀಕ್ಷಣೆ ಮಿತಿ ಮುಗಿದಿದೆ',
  'proofViewer.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'proofViewer.title.viewLimit': 'ನೀವು ಈ ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಗರಿಷ್ಠ 2 ಬಾರಿ ತೆರೆದಿದ್ದೀರಿ',
  'proofViewer.error.load': 'ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಲೋಡ್ ಆಗಲಿಲ್ಲ',
  'proofViewer.lastView': 'ಕೊನೆಯ ವೀಕ್ಷಣೆ — ಚಿತ್ರ ಮುಕ್ತಾಯಗೊಳ್ಳುತ್ತದೆ',
  'proofViewer.viewsRemaining.one': 'ಇನ್ನೂ {n} ಬಾರಿ ನೋಡಬಹುದು',
  'proofViewer.viewsRemaining.other': 'ಇನ್ನೂ {n} ಬಾರಿ ನೋಡಬಹುದು',
  'proofViewer.imageAlt': 'ಪಾವತಿ ಪುರಾವೆ', 'proofViewer.close': 'ಮುಚ್ಚಿ',

  // Recharge flow
  'recharge.title': 'ಹಣ ಸೇರಿಸಿ',
  'recharge.step1.subtitle': 'ಎಷ್ಟು ಸೇರಿಸಲು ಬಯಸುತ್ತೀರಿ?',
  'recharge.step1.amount': 'ಮೊತ್ತ',
  'recharge.step1.range': 'ಕನಿಷ್ಠ {min} · ಗರಿಷ್ಠ {max}',
  'recharge.step1.minHint': 'ಕನಿಷ್ಠ {min} ರೀಚಾರ್ಜ್',
  'recharge.step1.maxHint': 'ಗರಿಷ್ಠ {max}',
  'recharge.step1.quickPick': 'ತ್ವರಿತ ಆಯ್ಕೆ',
  'recharge.step2.subtitle': 'UPI ಬಳಸಿ ಪಾವತಿಸಿ, ನಂತರ ಮುಂದುವರಿಸಿ',
  'recharge.step2.payAmount': 'ಈ ಮೊತ್ತವನ್ನು ಪಾವತಿಸಿ',
  'recharge.step2.iPaid': 'ನಾನು ಪಾವತಿಸಿದ್ದೇನೆ',
  'recharge.step2.howto.1': 'ನಿಮ್ಮ UPI ಆ್ಯಪ್ ತೆರೆಯಿರಿ (Google Pay / PhonePe / Paytm).',
  'recharge.step2.howto.2': 'ಮೇಲಿನ ಮೊತ್ತವನ್ನು UPI ID ಗೆ ಪಾವತಿಸಿ ಅಥವಾ QR ಸ್ಕ್ಯಾನ್ ಮಾಡಿ.',
  'recharge.step2.howto.3': 'ನಿಮ್ಮ UPI ಆ್ಯಪ್‌ನಿಂದ ವ್ಯವಹಾರ ID ಯನ್ನು ನಕಲಿಸಿ — ಮುಂದೆ ಅದು ಬೇಕಾಗುತ್ತದೆ.',
  "recharge.locked.title": "ರೀಚಾರ್ಜ್ ತಾತ್ಕಾಲಿಕವಾಗಿ ಲಭ್ಯವಿಲ್ಲ",
  "recharge.locked.body": "ಈ ರೀಚಾರ್ಜ್ ಮಾರ್ಗದ ಸಮಸ್ಯೆಯನ್ನು ನಾವು ಪರಿಹರಿಸುತ್ತಿದ್ದೇವೆ. ನಿಮ್ಮ ವಾಲೆಟ್ ಮತ್ತು ಹಿಂಪಡೆಯುವಿಕೆ ಪರಿಣಾಮ ಬೀರಿಲ್ಲ. ದಯವಿಟ್ಟು ಸ್ವಲ್ಪ ಸಮಯದ ನಂತರ ಪುನಃ ಪ್ರಯತ್ನಿಸಿ.",
  "recharge.locked.reassure.withdrawals": "ಹಿಂಪಡೆಯುವಿಕೆ ಸಾಮಾನ್ಯವಾಗಿ ಕಾರ್ಯನಿರ್ವಹಿಸುತ್ತಿದೆ",
  "recharge.locked.reassure.existing": "ನೀವು ಈಗಾಗಲೇ ಕಳುಹಿಸಿರುವ ಪಾವತಿ ಪ್ರಕ್ರಿಯೆಯಲ್ಲಿದೆ",
  "recharge.cap.banner": "ಈಗ ಗರಿಷ್ಠ ರೀಚಾರ್ಜ್: {max}. ಸ್ವೀಕರಿಸುವವರ ಸಾಮರ್ಥ್ಯ ತಾತ್ಕಾಲಿಕವಾಗಿ ಸೀಮಿತವಾಗಿದೆ.",
  "recharge.cap.tooHigh": "ಮೊತ್ತವು ಲಭ್ಯವಿರುವ ಮಿತಿಯನ್ನು ಮೀರಿದೆ. ಈಗ ನೀವು ಗರಿಷ್ಠ {max} ರೀಚಾರ್ಜ್ ಮಾಡಬಹುದು.",
  "recharge.notConfigured.title": "ರೀಚಾರ್ಜ್ ಶೀಘ್ರವೇ ಲಭ್ಯವಾಗಲಿದೆ",
  "recharge.notConfigured.body": "ನಾವು ಈ ರೀಚಾರ್ಜ್ ಮಾರ್ಗದ ಸೆಟಪ್ ಪೂರ್ಣಗೊಳಿಸುತ್ತಿದ್ದೇವೆ. ನಿಮ್ಮ ವಾಲೆಟ್ ಮತ್ತು ಹಿಂಪಡೆಯುವಿಕೆ ಪರಿಣಾಮ ಬೀರಿಲ್ಲ. ದಯವಿಟ್ಟು ಸ್ವಲ್ಪ ಸಮಯದ ನಂತರ ಪರಿಶೀಲಿಸಿ.",
  'recharge.step3.subtitle': 'ನಿಮ್ಮ ಪಾವತಿಯನ್ನು ದೃಢೀಕರಿಸಿ',
  'recharge.step3.amountPaid': 'ನೀವು ಪಾವತಿಸಿದ ಮೊತ್ತ',
  'recharge.step3.txnId': 'UPI ವ್ಯವಹಾರ ID',
  'recharge.step3.txnIdHelp': 'ಇದನ್ನು ನಿಮ್ಮ UPI ಆ್ಯಪ್‌ನಲ್ಲಿ ಪಾವತಿ ರಸೀದಿಯ ಅಡಿಯಲ್ಲಿ ಹುಡುಕಿ — ಸಾಮಾನ್ಯವಾಗಿ 12 ಅಂಕಿಗಳು.',
  'recharge.step3.note': 'ಅಡ್ಮಿನ್‌ಗೆ ಟಿಪ್ಪಣಿ',
  'recharge.step3.notePlaceholder': 'ಉದಾ. ಸಂಗಾತಿಯ ಸಂಖ್ಯೆಯಿಂದ ಪಾವತಿಸಲಾಗಿದೆ',
  'recharge.step3.submit': 'ಪರಿಶೀಲನೆಗೆ ಸಲ್ಲಿಸಿ',
  'recharge.step3.disclaimer': 'ಅಡ್ಮಿನ್ UPI ಆ್ಯಪ್‌ನಲ್ಲಿ ಪಾವತಿಯನ್ನು ಪರಿಶೀಲಿಸುತ್ತಾರೆ. ಅನುಮೋದನೆ ನಂತರ 24 ಗಂಟೆಗಳ ಒಳಗೆ ವಾಲೆಟ್ ಜಮಾ ಆಗುತ್ತದೆ (ಸಾಮಾನ್ಯವಾಗಿ ಬೇಗ).',
  'recharge.success.title': 'ಸಲ್ಲಿಸಲಾಗಿದೆ — ಪರಿಶೀಲನೆಗೆ ಕಾಯುತ್ತಿದೆ',
  'recharge.success.body': 'ನಿಮ್ಮ {amount} ರೀಚಾರ್ಜ್ ಅಡ್ಮಿನ್ ಸಾಲಿನಲ್ಲಿದೆ. 24 ಗಂಟೆಗಳ ಒಳಗೆ ಜಮಾ ಆಗುತ್ತದೆ (ಸಾಮಾನ್ಯವಾಗಿ ಬೇಗ).',
  'recharge.success.refId': 'ಉಲ್ಲೇಖ',
  'recharge.toast.submitted': 'ಅಡ್ಮಿನ್ ಪರಿಶೀಲನೆಗೆ ರೀಚಾರ್ಜ್ ಸಲ್ಲಿಸಲಾಗಿದೆ',

  // Withdraw flow
  'withdraw.title': 'ಹಿಂಪಡೆಯಿರಿ',
  'withdraw.step1.subtitle': 'ಹಣವನ್ನು ಎಲ್ಲಿಗೆ ಕಳುಹಿಸಬೇಕು?',
  'withdraw.step2.subtitle': 'ಒಂದು ಕೊನೆಯ ಪರಿಶೀಲನೆ',
  'withdraw.balance': 'ಲಭ್ಯ ಬ್ಯಾಲೆನ್ಸ್',
  'withdraw.amount': 'ಮೊತ್ತ',
  'withdraw.max': 'ಗರಿಷ್ಠ',
  'withdraw.upiId': 'ನಿಮ್ಮ UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'ನಾವು ಹಣವನ್ನು ಇಲ್ಲಿಗೆ ಕಳುಹಿಸುತ್ತೇವೆ. ಸಲ್ಲಿಸುವ ಮೊದಲು ಎರಡು ಬಾರಿ ಪರಿಶೀಲಿಸಿ.',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'ನಿಮ್ಮ ಕೊನೆಯ ಹಿಂಪಡೆಯುವಿಕೆಯಿಂದ ಮೊದಲೇ ತುಂಬಲಾಗಿದೆ',
  'withdraw.upiShapeErr': 'UPI ID yourname@psp ರೀತಿ ಕಾಣಬೇಕು (ಉದಾ. lucky@oksbi)',
  'withdraw.phoneShapeErr': 'ಮಾನ್ಯ 10-ಅಂಕಿಯ ಭಾರತೀಯ ಮೊಬೈಲ್ ನಮೂದಿಸಿ (6, 7, 8 ಅಥವಾ 9 ರಿಂದ ಪ್ರಾರಂಭವಾಗಬೇಕು)',
  'withdraw.whatsapp': 'WhatsApp ಸಂಖ್ಯೆ',
  'withdraw.errMin': 'ಕನಿಷ್ಠ ಹಿಂಪಡೆಯುವಿಕೆ {min}',
  'withdraw.errExceed': 'ಮೊತ್ತ ನಿಮ್ಮ ಬ್ಯಾಲೆನ್ಸ್ ಮೀರಿದೆ',
  'withdraw.minHint': 'ಕನಿಷ್ಠ {min} ಹಿಂಪಡೆಯುವಿಕೆ',
  'withdraw.step2.review': 'ಪರಿಶೀಲನೆ',
  'withdraw.step2.heldNotice': 'ನೀವು ಸಲ್ಲಿಸಿದಾಗ, ಈ ಮೊತ್ತವನ್ನು ತಕ್ಷಣವೇ ಹಿಡಿದಿಡಲಾಗುತ್ತದೆ, ಆದ್ದರಿಂದ ಇದನ್ನು ಬೇರೆಡೆ ಖರ್ಚು ಮಾಡಲಾಗದು. ಅಡ್ಮಿನ್ ಇದನ್ನು ನಿಮ್ಮ UPI ID ಗೆ ಪಾವತಿಸಿ ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸುತ್ತಾರೆ.',
  'withdraw.heldAfter': 'ಹಿಡಿದಿಟ್ಟಿದೆ',
  'withdraw.spendableAfter': 'ಖರ್ಚು ಮಾಡಬಹುದಾದ',
  'withdraw.step2.disclaimer': 'ಅಡ್ಮಿನ್ ತಿರಸ್ಕರಿಸಿದರೆ, ಹಿಡಿದಿಟ್ಟ ಮೊತ್ತ ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ ಮರಳುತ್ತದೆ.',
  'withdraw.submit': 'ಸಲ್ಲಿಸಿ',
  'withdraw.success.title': 'ಸಲ್ಲಿಸಲಾಗಿದೆ — ಪಾವತಿಗೆ ಕಾಯುತ್ತಿದೆ',
  'withdraw.success.body': 'ನಿಮ್ಮ {amount} ಹಿಂಪಡೆಯುವಿಕೆ ಸಾಲಿನಲ್ಲಿದೆ. 24 ಗಂಟೆಗಳ ಒಳಗೆ ಪಾವತಿ (ಸಾಮಾನ್ಯವಾಗಿ ಬೇಗ). ಆ ತನಕ ಹಿಡಿದಿಟ್ಟ ಮೊತ್ತ ಮೀಸಲಿರುತ್ತದೆ.',
  'withdraw.success.refId': 'ಉಲ್ಲೇಖ',
  'withdraw.toast.submitted': 'ಹಿಂಪಡೆಯುವಿಕೆ ಸಾಲಿನಲ್ಲಿ — ನಿಮ್ಮ ವಾಲೆಟ್‌ನಲ್ಲಿ ಹಿಡಿದಿಡಲಾಗಿದೆ',

  // Payment receiver card
  'payment.upi.label': 'ಈ UPI ID ಗೆ ಪಾವತಿಸಿ',
  'payment.upi.copy': 'ನಕಲಿಸಿ',
  'payment.upiCopied': 'UPI ID ನಕಲಿಸಲಾಗಿದೆ',
  'payment.openUpiApp': 'ಈಗ ಪಾವತಿಸಿ',
  'payment.openWhatsapp': 'WhatsApp',
  'payment.helpAt': 'ಸಹಾಯ ಬೇಕೇ? ಕರೆ ಮಾಡಿ',
  'payment.qr.label': 'ಪಾವತಿಸಲು QR ಸ್ಕ್ಯಾನ್ ಮಾಡಿ',
  'payment.qr.tap': 'ವಿಸ್ತರಿಸಲು ಟ್ಯಾಪ್ ಮಾಡಿ',
  'payment.qr.tapToClose': 'ಮುಚ್ಚಲು ಎಲ್ಲಿಯಾದರೂ ಟ್ಯಾಪ್ ಮಾಡಿ',
  'payment.notConfigured.title': 'ಈಗ ರೀಚಾರ್ಜ್ ಲಭ್ಯವಿಲ್ಲ',
  'payment.notConfigured.contactBody': 'ಇನ್ನೂ UPI ಹೊಂದಿಸಲಾಗಿಲ್ಲ. WhatsApp ಅಥವಾ ಫೋನ್ ಮೂಲಕ ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ ಮತ್ತು ನಾವು ರೀಚಾರ್ಜ್ ಮಾಡಲು ಸಹಾಯ ಮಾಡುತ್ತೇವೆ.',
  'payment.notConfigured.unavailable': 'ರೀಚಾರ್ಜ್ ತಾತ್ಕಾಲಿಕವಾಗಿ ಲಭ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಪುನಃ ಪ್ರಯತ್ನಿಸಿ.',
  'payment.chooser.title': 'ನಿಮ್ಮ UPI ಆ್ಯಪ್ ಆರಿಸಿ',
  'payment.chooser.subtitle': 'ನೀವು ಪಾವತಿಸುತ್ತೀರಿ',
  'payment.chooser.hint': 'ಮೊತ್ತವನ್ನು ಮೊದಲೇ ತುಂಬಿಸಿ ಆ್ಯಪ್ ತೆರೆಯಲು ಟ್ಯಾಪ್ ಮಾಡಿ. ಆ್ಯಪ್ ಈಗಾಗಲೇ ನಿಮ್ಮ ಫೋನ್‌ನಲ್ಲಿ ಸ್ಥಾಪಿಸಲ್ಪಟ್ಟಿರಬೇಕು — ನೀವು "address invalid" ದೋಷ ನೋಡಿದರೆ, ಮೊದಲು App Store / Play Store ನಿಂದ ಆ್ಯಪ್ ಸ್ಥಾಪಿಸಿ.',

  // Common
  'common.next': 'ಮುಂದೆ',
  'common.close': 'ಮುಚ್ಚಿ',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'ವೇಗದ ಮತ್ತು ಸುಗಮ ಪಾವತಿ ಸೇವೆ ಖಚಿತಪಡಿಸಲು, ದಯವಿಟ್ಟು ನಿಮ್ಮ ನಿಖರ ಸ್ಥಳ ಮತ್ತು ಸಂಪರ್ಕ ಸಂಖ್ಯೆಯನ್ನು ಒದಗಿಸಿ.',
  'geo.change': 'ಬದಲಾಯಿಸಿ',
  'geo.gps.button': 'ನನ್ನ ಪ್ರಸ್ತುತ ಸ್ಥಳ ಬಳಸಿ',
  'geo.gps.benefit': 'ವೇಗದ — ಒಂದು ಟ್ಯಾಪ್, ಟೈಪಿಂಗ್ ಇಲ್ಲ.',
  'geo.gps.denied': 'ಸ್ಥಳ ಅನುಮತಿ ನಿರಾಕರಿಸಲಾಗಿದೆ. ಪಿನ್‌ಕೋಡ್ ಬಳಸಿ ಅಥವಾ ಕೆಳಗೆ ಕೈಯಾರೆ ಆರಿಸಿ.',
  'geo.gps.failed': 'ನಿಮ್ಮ ಸ್ಥಳ ಪಡೆಯಲು ಆಗಲಿಲ್ಲ. ಬದಲಿಗೆ ಪಿನ್‌ಕೋಡ್ ಪ್ರಯತ್ನಿಸಿ.',
  'geo.gps.notResolved': 'ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ತಿಳಿದಿರುವ ಪ್ರದೇಶಕ್ಕೆ ಮ್ಯಾಪ್ ಮಾಡಲು ಆಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಕೈಯಾರೆ ಆರಿಸಿ.',
  'geo.pincode.label': 'ಪಿನ್‌ಕೋಡ್',
  'geo.pincode.notFound': 'ಪಿನ್‌ಕೋಡ್ ಸಿಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಪ್ರದೇಶವನ್ನು ಕೈಯಾರೆ ಆರಿಸಿ.',
  'geo.pincode.lookupFailed': 'ಪಿನ್‌ಕೋಡ್ ಲುಕಪ್ ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಕೈಯಾರೆ ಆರಿಸಿ.',
  'geo.multipleAreas.label': 'ಬಹು ಪ್ರದೇಶಗಳು ಈ ಪಿನ್‌ಕೋಡ್ ಹಂಚಿಕೊಳ್ಳುತ್ತವೆ — ನಿಮ್ಮದನ್ನು ಆರಿಸಿ',
  'geo.multipleAreas.placeholder': 'ನಿಮ್ಮ ಪ್ರದೇಶ ಆಯ್ಕೆಮಾಡಿ',
  'geo.manual.title': 'ಕೈಯಾರೆ ಆರಿಸಿ',
  'geo.manual.close': 'ಮ್ಯಾನ್ಯುಯಲ್ ಪಿಕರ್ ಮರೆಮಾಡಿ',
  'geo.state.label': 'ರಾಜ್ಯ',
  'geo.state.placeholder': 'ರಾಜ್ಯ ಆಯ್ಕೆಮಾಡಿ',
  'geo.district.label': 'ಜಿಲ್ಲೆ',
  'geo.district.placeholder': 'ಜಿಲ್ಲೆ ಆಯ್ಕೆಮಾಡಿ',
  'geo.area.label': 'ಪ್ರದೇಶ / ಪಟ್ಟಣ',
  'geo.area.placeholder': 'ಪ್ರದೇಶ ಆಯ್ಕೆಮಾಡಿ',
  'geo.privacy.note': 'ಕೇವಲ ನಿಮ್ಮ ಸ್ಥಳೀಯ ಏಜೆಂಟ್ ನಿಯೋಜಿಸಲು ಬಳಸಲಾಗುತ್ತದೆ. ಎಂದಿಗೂ ಹಂಚಿಕೊಳ್ಳಲಾಗದು.',
  'geo.required.hint': 'ರೆಫರಲ್ ಕೋಡ್ ಇಲ್ಲದಿದ್ದಾಗ ಸ್ಥಳ ಅಗತ್ಯವಿದೆ.',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'ಲೆಕ್ಕ',
  'agent.nav.players': 'ಆಟಗಾರರು',
  'agent.nav.agents': 'ಏಜೆಂಟ್‌ಗಳು',
  'agent.nav.rewards': 'ಬಹುಮಾನಗಳು',
  'agent.nav.profile':   'ಪ್ರೊಫೈಲ್',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'ಮುಖಪುಟ',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'ಲೆಕ್ಕ',
  'agent.bottomnav.players': 'ಆಟಗಾರರು',
  'agent.bottomnav.agents': 'ಏಜೆಂಟ್‌ಗಳು',
  'agent.bottomnav.rewards': 'ಬಹುಮಾನಗಳು',
  'agent.bottomnav.profile': 'ಪ್ರೊಫೈಲ್',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'ಮರಳಿ ಸ್ವಾಗತ, ',
  'agent.dashboard.header.welcomeFallback': 'ಸ್ನೇಹಿತ',
  'agent.dashboard.header.subtitle': 'ನಿಮ್ಮ ವ್ಯವಹಾರ ಒಂದು ನೋಟದಲ್ಲಿ — ಪೂಲ್, ಠೇವಣಿ, ಆಟಗಾರರು, ಉಪ-ಏಜೆಂಟ್‌ಗಳು, P&L, ಎಕ್ಸ್‌ಪೋಸರ್',
  'agent.dashboard.range.today': 'ಇಂದು',
  'agent.dashboard.range.yesterday': 'ನಿನ್ನೆ',
  'agent.dashboard.range.thisWeek': 'ಈ ವಾರ',
  'agent.dashboard.range.lastWeek': 'ಕಳೆದ ವಾರ',
  'agent.dashboard.range.thisMonth': 'ಈ ತಿಂಗಳು',
  'agent.dashboard.range.lastMonth': 'ಕಳೆದ ತಿಂಗಳು',
  'agent.dashboard.range.complete': 'ಸಂಪೂರ್ಣ',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'ಠೇವಣಿ ಕವರೇಜ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.dashboard.depositBar.label': 'ಠೇವಣಿ ಕವರೇಜ್',
  'agent.dashboard.depositBar.balanceOfTarget': '· {target} ಗುರಿಯಲ್ಲಿ {balance}',
  'agent.dashboard.depositBar.tapToView': 'ಪೂರ್ಣ ಠೇವಣಿ ಪುಟ ನೋಡಲು ಟ್ಯಾಪ್ ಮಾಡಿ.',
  'agent.dashboard.depositBar.caption.suspended': 'ಖಾತೆ ಅಮಾನತುಗೊಂಡಿದೆ. ಅಡ್ಮಿನ್ ಸಂಪರ್ಕಿಸಿ.',
  'agent.dashboard.depositBar.caption.breach': 'ನಿಮ್ಮ ವಾಲೆಟ್ ಠೇವಣಿ ಮಿತಿಯ 100% ಬಳಸಿಕೊಂಡಿದೆ. ಹಿಂಪಡೆಯುವಿಕೆಗಳು ಲಾಕ್ ಆಗಿವೆ. ತಕ್ಷಣ ಟಾಪ್-ಅಪ್ ಮಾಡಿ.',
  'agent.dashboard.depositBar.caption.warning': 'ನಿಮ್ಮ ವಾಲೆಟ್ ಠೇವಣಿ ಮಿತಿಯ {pct}% ನಲ್ಲಿದೆ. ಎಚ್ಚರಿಕೆ 80% ನಲ್ಲಿ, ಲಾಕ್‌ಡೌನ್ 100% ನಲ್ಲಿ ಬರುತ್ತದೆ.',
  'agent.dashboard.depositBar.caption.caution': 'ನಿಮ್ಮ ವಾಲೆಟ್ ಠೇವಣಿ ಕವರೇಜಿನ {pct}% ಬಳಸಿಕೊಂಡಿದೆ. 80% ಗಿಂತ ಕೆಳಗೆ — ಇನ್ನೂ ಸುರಕ್ಷಿತ.',
  'agent.dashboard.depositBar.caption.safe': 'ಠೇವಣಿ ಸಂಪೂರ್ಣ ಸುರಕ್ಷಿತ. ಯಾವುದೇ ಕವರೇಜ್ ಬಳಕೆಯಾಗಿಲ್ಲ.',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'ಎಚ್ಚರಿಕೆ · ಶೂನ್ಯ ಭದ್ರತಾ ಠೇವಣಿ',
  'agent.dashboard.locatorSkip.body': 'ನಿಮ್ಮ ಭದ್ರತಾ ಠೇವಣಿ {amount} ಆಗಿದೆ. ನೀವು ಅದನ್ನು ಭರ್ತಿ ಮಾಡುವವರೆಗೆ ಭೌಗೋಳಿಕ ಲೊಕೇಟರ್‌ನಿಂದ ಹೊಸ ಆಟಗಾರರು ಸಿಗುವುದಿಲ್ಲ. ಪ್ರಸ್ತುತ ಆಟಗಾರರು + ನೇರ ರೆಫರಲ್‌ಗಳು + ಮ್ಯಾನ್ಯುಯಲ್ ನಿಯೋಜನೆಗಳು ಪರಿಣಾಮ ಬೀರುವುದಿಲ್ಲ — ಕೇವಲ ಆರ್ಗ್ಯಾನಿಕ್ ಜಿಯೋ-ರೂಟೆಡ್ ಸೈನ್‌ಅಪ್‌ಗಳು ನಿಮ್ಮನ್ನು ಬಿಟ್ಟುಬಿಡುತ್ತವೆ.',
  'agent.dashboard.locatorSkip.cta': 'ಠೇವಣಿ ಭರ್ತಿ ಮಾಡಿ',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'ಡೌನ್-ಲೈನ್ ಡಿಫಾಲ್ಟರ್ ಎಚ್ಚರಿಕೆ',
  'agent.dashboard.defaulter.summary.critical.one': 'ನಿಮ್ಮ {n} ನೇರ ಉಪ-ಏಜೆಂಟ್ ಡಿಫಾಲ್ಟ್‌ನಲ್ಲಿದ್ದಾರೆ — ಅವರು ಲಾಕ್ ಆಗಿದ್ದಾರೆ ಅಥವಾ ಠೇವಣಿ ಮಿತಿ ಉಲ್ಲಂಘಿಸಿದ್ದಾರೆ.',
  'agent.dashboard.defaulter.summary.critical.other': 'ನಿಮ್ಮ {n} ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಡಿಫಾಲ್ಟ್‌ನಲ್ಲಿದ್ದಾರೆ — ಅವರು ಲಾಕ್ ಆಗಿದ್ದಾರೆ ಅಥವಾ ಠೇವಣಿ ಮಿತಿ ಉಲ್ಲಂಘಿಸಿದ್ದಾರೆ.',
  'agent.dashboard.defaulter.summary.warning.one': 'ನಿಮ್ಮ {n} ನೇರ ಉಪ-ಏಜೆಂಟ್ ಠೇವಣಿ ಮಿತಿಯ 80%+ ನಲ್ಲಿದ್ದಾರೆ — ಡಿಫಾಲ್ಟರ್ ಅಪಾಯ.',
  'agent.dashboard.defaulter.summary.warning.other': 'ನಿಮ್ಮ {n} ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಠೇವಣಿ ಮಿತಿಯ 80%+ ನಲ್ಲಿದ್ದಾರೆ — ಡಿಫಾಲ್ಟರ್ ಅಪಾಯ.',
  'agent.dashboard.defaulter.worst': 'ಅತ್ಯಂತ ಕೆಟ್ಟದು: @{username} ಠೇವಣಿ ಮಿತಿಯ {pct}% ನಲ್ಲಿ.',
  'agent.dashboard.defaulter.tapHint': 'ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳ ಪಟ್ಟಿಗೆ ಹೋಗಲು ಟ್ಯಾಪ್ ಮಾಡಿ. ಡಿಫಾಲ್ಟರ್ ಸಾಲುಗಳು ಹೈಲೈಟ್ ಆಗಿವೆ.',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'ಲಾಕ್',
  'agent.dashboard.marginPill.caution': 'ಎಚ್ಚರ',
  'agent.dashboard.marginPill.warning': 'ಎಚ್ಚರಿಕೆ',
  'agent.dashboard.marginPill.breach': 'ಉಲ್ಲಂಘನೆ',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'ಬೆಳಗಿನ ಸಾರಾಂಶ',
  'agent.dashboard.sections.morning.subtitle': 'ಲೈವ್ ಸ್ನ್ಯಾಪ್‌ಶಾಟ್ — ಪ್ರತಿ ಸಂಖ್ಯೆ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ರಿಫ್ರೆಶ್ ಆಗುತ್ತದೆ',
  'agent.dashboard.sections.activity.title': 'ಚಟುವಟಿಕೆ',
  'agent.dashboard.sections.activity.subtitle': 'ಅತಿ ಹೆಚ್ಚು ಖರ್ಚು ಮಾಡುವವರು ಮತ್ತು ಇತ್ತೀಚಿನ ಆಟಗಾರ ಪಾವತಿ ಘಟನೆಗಳು',
  'agent.dashboard.sections.forensic.title': 'ಫೊರೆನ್ಸಿಕ್ ವಿವರ',
  'agent.dashboard.sections.forensic.subtitle': 'ಯಾವುದೇ ವಿಭಾಗವನ್ನು ವಿಸ್ತರಿಸಲು ಟ್ಯಾಪ್ ಮಾಡಿ — ಈ ಪುಟವನ್ನು ಶಾಂತವಾಗಿಡಲು ಡೀಫಾಲ್ಟ್ ಆಗಿ ಮುಚ್ಚಲಾಗಿದೆ',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'ಲಾಭ-ನಷ್ಟ ಆಳವಾದ ವಿಶ್ಲೇಷಣೆ',
  'agent.dashboard.accordions.pnlDeep.helper': 'ನಿಮ್ಮ ಗಳಿಕೆಯ ಪಿಲ್ಲರ್-ವಾರು + ಗೇಮ್-ವಾರು ವಿವರಣೆ. ಆಧಾರವಾಗಿರುವ ಪಂದ್ಯಗಳಿಗಾಗಿ ಗೇಮ್ ಸಾಲಿನ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ.',
  'agent.dashboard.accordions.leaderboard.title': 'ಡೌನ್-ಲೈನ್ ಲೀಡರ್‌ಬೋರ್ಡ್',
  'agent.dashboard.accordions.leaderboard.helper': 'ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ನಿವ್ವಳ ಗಳಿಕೆಯ ಆಧಾರದ ಮೇಲೆ ಕ್ರಮಾಂಕಿತ. ಋಣಾತ್ಮಕ ಸಂಖ್ಯೆಗಳು ಆ ಉಪ-ಏಜೆಂಟ್ ತಂದದ್ದಕ್ಕಿಂತ ಹೆಚ್ಚು ಪಾವತಿಸಿದ್ದಾರೆ ಎಂದರ್ಥ.',
  'agent.dashboard.accordions.activity.title': 'ಡೌನ್-ಲೈನ್ ಚಟುವಟಿಕೆ ಫೀಡ್',
  'agent.dashboard.accordions.activity.helper': 'ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳ ಪ್ರತಿ ಪಂದ್ಯ ಸೆಟ್ಲ್‌ಮೆಂಟ್, ರೀಚಾರ್ಜ್ ಮತ್ತು ದರ ಬದಲಾವಣೆಯ — ಫೊರೆನ್ಸಿಕ್ ಟೈಮ್‌ಲೈನ್.',
  'agent.dashboard.accordions.earnings.title': 'ಗಳಿಕೆ ವಿವರಣೆ (ಲೈಫ್‌ಟೈಮ್)',
  'agent.dashboard.accordions.earnings.helper': 'ಪ್ರಕಾರದ ಪ್ರಕಾರ ಲೈಫ್‌ಟೈಮ್ ಒಟ್ಟು. ಮೇಲಿನ ಶ್ರೇಣಿ ಟಾಗಲ್ ಅನ್ನು ನಿರ್ಲಕ್ಷಿಸುತ್ತದೆ — ಇವು ಸಮಯ ಫಿಲ್ಟರ್‌ನೊಂದಿಗೆ ಎಂದಿಗೂ ಬದಲಾಗುವುದಿಲ್ಲ.',
  'agent.dashboard.accordions.directSubAgents.title': 'ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳು',
  'agent.dashboard.accordions.directSubAgents.helper': 'ನಿಮ್ಮ ನೇರ ಪ್ರತಿನಿಧಿಗಳು. ಪ್ರತಿಯೊಬ್ಬರೂ ತಮ್ಮದೇ ಆಟಗಾರರು + ತಮ್ಮದೇ ಉಪ-ಏಜೆಂಟ್‌ಗಳನ್ನು ನಡೆಸುತ್ತಾರೆ. ಗುಲಾಬಿ/ಅಂಬರ್ ಬಣ್ಣದಿಂದ ಹೈಲೈಟ್ ಆದ ಸಾಲುಗಳು ಡಿಫಾಲ್ಟ್ ಪ್ರದೇಶದಲ್ಲಿವೆ.',
  'agent.dashboard.accordions.chain.title': 'ಚೈನ್ ಗೋಚರತೆ',
  'agent.dashboard.accordions.chain.helper': 'ನಿಮ್ಮ ಒಟ್ಟು ಡೌನ್-ಲೈನ್ ಹೆಜ್ಜೆಗುರುತು, ನೇರ (ನಿಮ್ಮಿಂದ ಸಂಪಾದಿಸಲಾದ) ಮತ್ತು ಪರೋಕ್ಷ (ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಂದ ನಿರ್ವಹಿಸಲ್ಪಡುತ್ತದೆ) ಎಂದು ಬೇರ್ಪಡಿಸಲಾಗಿದೆ.',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'ಲೈವ್ ಡೇಟಾ — ಫೋಕಸ್‌ನಲ್ಲಿ, ಪ್ರತಿ 60 ಸೆಕೆಂಡಿಗೊಮ್ಮೆ, ಮತ್ತು ಹಣ ಚಲಿಸಿದಾಗ ರಿಫ್ರೆಶ್ ಆಗುತ್ತದೆ.',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'ನನ್ನ ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.dashboard.cards.poolBalance.helper': 'ನಿಮ್ಮ ಖರ್ಚು ಮಾಡಬಹುದಾದ ಬ್ಯಾಲೆನ್ಸ್. ಆಟಗಾರರಿಗೆ ರೀಚಾರ್ಜ್ ಮಾಡಲು ಅಥವಾ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಗೆ ಕಳುಹಿಸಲು ನೀವು ಬಳಸಬಹುದಾದ ಹಣ.',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} ನಲ್ಲಿ ನಿವ್ವಳ {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'ಭದ್ರತಾ ಠೇವಣಿ',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'ಠೇವಣಿ ಸ್ಥಿತಿ',
  'agent.dashboard.cards.depositStatus.helperLoading': 'ಠೇವಣಿ ಸ್ಥಿತಿ ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.dashboard.cards.depositStatus.helper': 'ನಿಮ್ಮ ಪೂಲ್‌ಗಾಗಿ ಮೇಲಾಧಾರ. ಸ್ಥಿತಿ ಗುರಿಯ 50% ನಲ್ಲಿ ಎಚ್ಚರ, 80% ನಲ್ಲಿ ಎಚ್ಚರಿಕೆ, 100% ನಲ್ಲಿ ಉಲ್ಲಂಘನೆಗೆ ಬದಲಾಗುತ್ತದೆ.',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} ರಿಂದ',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'ಚಾಲನಾ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'ಠೇವಣಿ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'ಠೇವಣಿ ಗುರಿ',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ಋಣಾತ್ಮಕ ಎಕ್ಸ್‌ಪೋಸರ್',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'ಕವರೇಜ್ ಅನುಪಾತ',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'ಠೇವಣಿ ಗುರಿಯಲ್ಲಿ ಋಣಾತ್ಮಕ ಎಕ್ಸ್‌ಪೋಸರ್‌ನಿಂದ ಬಳಸಿಕೊಂಡದ್ದು',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'ಸುರಕ್ಷಿತ',
  'agent.dashboard.depositStatus.status.caution': 'ಎಚ್ಚರ',
  'agent.dashboard.depositStatus.status.warning': 'ಎಚ್ಚರಿಕೆ',
  'agent.dashboard.depositStatus.status.breach': 'ಉಲ್ಲಂಘನೆ',
  'agent.dashboard.depositStatus.status.suspended': 'ಅಮಾನತು',
  'agent.dashboard.depositStatus.status.healthy': 'ಆರೋಗ್ಯಕರ',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'ಆಟಗಾರರ ಒಟ್ಟು ವಾಲೆಟ್',
  'agent.dashboard.cards.playersWallet.helper': 'ಈಗ ನಿಮ್ಮ ಪ್ರತಿ ಆಟಗಾರನ ವಾಲೆಟ್‌ನ ಸಂಯೋಜಿತ ಲೈವ್ ಬ್ಯಾಲೆನ್ಸ್. ನಿಮಗೆ ಲಿಂಕ್ ಆಗಿರುವ ಎಲ್ಲಾ wallet_points ಸಾಲುಗಳ ಮೊತ್ತ.',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} ಆಟಗಾರ · {active} ಸಕ್ರಿಯ',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} ಆಟಗಾರರು · {active} ಸಕ್ರಿಯ',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'ಉಪ-ಏಜೆಂಟ್‌ಗಳ ವಾಲೆಟ್ + ಠೇವಣಿ',
  'agent.dashboard.cards.subAgents.helper': 'ನಿಮ್ಮ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಹೊಂದಿರುವ ಸಂಯೋಜಿತ ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್ ಮತ್ತು ಭದ್ರತಾ ಠೇವಣಿ. ಪ್ರತಿ ಉಪ-ಏಜೆಂಟ್ ತಮ್ಮದೇ ಆಟಗಾರರು + ತಮ್ಮದೇ ಡೌನ್-ಲೈನ್ ನಡೆಸುತ್ತಾರೆ.',
  'agent.dashboard.cards.subAgents.field.pool': 'ಪೂಲ್',
  'agent.dashboard.cards.subAgents.field.deposit': 'ಠೇವಣಿ',
  'agent.dashboard.cards.subAgents.foot.one': '{n} ನೇರ ಉಪ-ಏಜೆಂಟ್',
  'agent.dashboard.cards.subAgents.foot.other': '{n} ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳು',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'ನನ್ನ ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳು',
  'agent.dashboard.cards.myPending.helper': 'ಆದ್ಯತೆ 3. ಅಡ್ಮಿನ್‌ಗೆ ನಿಮ್ಮ ಸ್ವಂತ ಸಲ್ಲಿಕೆಗಳು — ಇವುಗಳ ಮೇಲೆ ನೀವು ಕ್ರಮ ಕೈಗೊಳ್ಳಲು ಸಾಧ್ಯವಿಲ್ಲ, ಅವರ ಪ್ರತಿಕ್ರಿಯೆಗಾಗಿ ಕಾಯುತ್ತಿವೆ.',
  'agent.dashboard.cards.myPending.recharge.one': '{n} ರೀಚಾರ್ಜ್ ಅಡ್ಮಿನ್‌ಗಾಗಿ ಕಾಯುತ್ತಿದೆ',
  'agent.dashboard.cards.myPending.recharge.other': '{n} ರೀಚಾರ್ಜ್‌ಗಳು ಅಡ್ಮಿನ್‌ಗಾಗಿ ಕಾಯುತ್ತಿವೆ',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} ಹಿಂಪಡೆಯುವಿಕೆ ಅಡ್ಮಿನ್‌ಗಾಗಿ ಕಾಯುತ್ತಿದೆ',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} ಹಿಂಪಡೆಯುವಿಕೆಗಳು ಅಡ್ಮಿನ್‌ಗಾಗಿ ಕಾಯುತ್ತಿವೆ',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'ಆಟಗಾರರ ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳು',
  'agent.dashboard.cards.playersPending.helper': 'ಆದ್ಯತೆ 1. ನಿಮ್ಮ ಆಟಗಾರರು ಇವುಗಳನ್ನು ಅನುಮೋದನೆ ಅಥವಾ ಪಾವತಿಸಲು ನಿಮಗಾಗಿ ಕಾಯುತ್ತಿದ್ದಾರೆ. ಕ್ರಮ ಕೈಗೊಳ್ಳಲು ಟ್ಯಾಪ್ ಮಾಡಿ.',
  'agent.dashboard.cards.playersPending.recharge.one': 'ಅನುಮೋದನೆಗಾಗಿ {n} ರೀಚಾರ್ಜ್',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} ರೀಚಾರ್ಜ್‌ಗಳು ಅನುಮೋದನೆಗೆ',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} ಹಿಂಪಡೆಯುವಿಕೆ ಪಾವತಿಗೆ',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} ಹಿಂಪಡೆಯುವಿಕೆಗಳು ಪಾವತಿಗೆ',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'ಉಪ-ಏಜೆಂಟ್ ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳು',
  'agent.dashboard.cards.subAgentsPending.helper': 'ಆದ್ಯತೆ 2. ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳು (ಸಂಪೂರ್ಣ ಡೌನ್-ಲೈನ್) ಇವುಗಳನ್ನು ಅನುಮೋದನೆ ಅಥವಾ ಪಾವತಿಸಲು ನಿಮಗಾಗಿ ಕಾಯುತ್ತಿದ್ದಾರೆ. ಕ್ರಮ ಕೈಗೊಳ್ಳಲು ಟ್ಯಾಪ್ ಮಾಡಿ.',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'ನಿಜವಾದ ನೆಟ್ ಲಾಭ-ನಷ್ಟ · {range}',
  'agent.dashboard.cards.trueNet.helper': 'ಈ ಅವಧಿಯಲ್ಲಿ ನಿಮ್ಮ ನಿಜವಾದ ಲಾಭ/ನಷ್ಟ. ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ ಪಾಲು + ವಿನ್/ಲಾಸ್ ಪಾಲು − ಆಫರ್ ಸಹ-ಧನಸಹಾಯ. ಪ್ರತಿ-ಗೇಮ್ ವಿವರಗಳಿಗಾಗಿ ಕೆಳಗಿನ ಲಾಭ-ನಷ್ಟ ಡೀಪ್-ಡೈವ್ ಅನ್ನು ವಿಸ್ತರಿಸಿ.',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'ಪ್ಲಾಟ್',
  'agent.dashboard.pillarChip.winLoss': 'ವಿನ್/ಲಾಸ್',
  'agent.dashboard.pillarChip.offer': 'ಆಫರ್',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'ಆಫರ್ ಬಳಕೆ · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'ಈ ಅವಧಿಯಲ್ಲಿ ಆಫರ್‌ಗಳ ಮೂಲಕ ನಿಮ್ಮ ಆಟಗಾರರಿಗೆ ಪಾವತಿಸಲಾದ ಒಟ್ಟು ಬಹುಮಾನ ₹. ಆ ವೆಚ್ಚದಲ್ಲಿ ನಿಮ್ಮ ಪಾಲನ್ನು ನೋಡಲು ನಿಮ್ಮ ಎಕ್ಸ್‌ಪೋಸರ್ ಪುಟದೊಂದಿಗೆ ಹೋಲಿಸಿ.',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} ಕ್ಲೈಮ್ ಜಮಾ ಆಯಿತು',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} ಕ್ಲೈಮ್‌ಗಳು ಜಮಾ ಆದವು',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'ಟಾಪ್ ಖರ್ಚು ಮಾಡುವವರು',
  'agent.dashboard.activity.topSpenders.helper': '{range} ಅವಧಿಯಲ್ಲಿ ಅತಿ ಹೆಚ್ಚು ಖರ್ಚು ಮಾಡುವ ನಿಮ್ಮ ಆಟಗಾರರು.',
  'agent.dashboard.activity.topSpenders.empty': 'ಈ ಅವಧಿಯಲ್ಲಿ ಇನ್ನೂ ಯಾವುದೇ ಖರ್ಚು ಇಲ್ಲ.',
  'agent.dashboard.activity.recent.title': 'ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ',
  'agent.dashboard.activity.recent.helper': 'ನಿಮ್ಮ ಆಟಗಾರರಿಂದ ಐದು ಇತ್ತೀಚಿನ ರೀಚಾರ್ಜ್ ಈವೆಂಟ್‌ಗಳು.',
  'agent.dashboard.activity.recent.empty': 'ಯಾವುದೇ ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ ಇಲ್ಲ.',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.dashboard.activity.recent.state.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.dashboard.activity.recent.state.pending': 'ಬಾಕಿಯಿರುವ',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'ಪ್ರತಿ ಗೇಮ್ · ವಿವರಣೆಗಾಗಿ ಸಾಲನ್ನು ಟ್ಯಾಪ್ ಮಾಡಿ',
  'agent.dashboard.pnl.noEarnings': 'ಈ ಅವಧಿಯಲ್ಲಿ ಯಾವುದೇ ಗಳಿಕೆ ಇಲ್ಲ.',
  'agent.dashboard.pnl.table.col.pillarGame': 'ಪಿಲ್ಲರ್ / ಗೇಮ್',
  'agent.dashboard.pnl.table.col.net': 'ನೆಟ್',
  'agent.dashboard.pnl.table.col.matches': 'ಮ್ಯಾಚ್‌ಗಳು',
  'agent.dashboard.pnl.ledgerRows.one': '{n} ಲೆಜರ್ ಸಾಲು',
  'agent.dashboard.pnl.ledgerRows.other': '{n} ಲೆಜರ್ ಸಾಲುಗಳು',
  'agent.dashboard.pnl.pillar.platformFee': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ',
  'agent.dashboard.pnl.pillar.winLoss': 'ವಿನ್ / ಲಾಸ್',
  'agent.dashboard.pnl.pillar.offerReward': 'ಆಫರ್ ಬಹುಮಾನ',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'ಅವಧಿ: {range} — ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ ಅವಧಿಯ ಕಟ್ಟುನಿಟ್ಟಾದ ಉಪಸೆಟ್',
  'agent.dashboard.pnlDrill.loading': 'ಮ್ಯಾಚ್-ಮಟ್ಟದ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ…',
  'agent.dashboard.pnlDrill.windowTotal': 'ಅವಧಿಯ ಒಟ್ಟು:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} ಮ್ಯಾಚ್‌ನಲ್ಲಿ',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} ಮ್ಯಾಚ್‌ಗಳಲ್ಲಿ',
  'agent.dashboard.pnlDrill.noContribution': 'ಈ ಸೆಲ್‌ಗೆ ಯಾವುದೇ ಮ್ಯಾಚ್ ಕೊಡುಗೆ ನೀಡಿಲ್ಲ.',
  'agent.dashboard.pnlDrill.errorFallback': 'ಡ್ರಿಲ್‌ಡೌನ್ ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ',
  'agent.dashboard.pnlDrill.table.col.match': 'ಮ್ಯಾಚ್',
  'agent.dashboard.pnlDrill.table.col.role': 'ಪಾತ್ರ',
  'agent.dashboard.pnlDrill.table.col.base': 'ಬೇಸ್',
  'agent.dashboard.pnlDrill.table.col.rate': 'ದರ',
  'agent.dashboard.pnlDrill.table.col.earned': 'ಗಳಿಸಿದ್ದು',
  'agent.dashboard.pnlDrill.table.col.settled': 'ಇತ್ಯರ್ಥ',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.dashboard.directDownline.empty': 'ಇನ್ನೂ ಯಾವುದೇ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.dashboard.directDownline.row.player.one': '{n} ಆಟಗಾರ',
  'agent.dashboard.directDownline.row.player.other': '{n} ಆಟಗಾರರು',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} ಉಪ-ಏಜೆಂಟ್',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} ಉಪ-ಏಜೆಂಟ್‌ಗಳು',
  'agent.dashboard.directDownline.row.walletDeposit': 'ವಾಲೆಟ್ {wallet} · ಠೇವಣಿ {deposit}',
  'agent.dashboard.directDownline.footer': 'ನಿಜವಾದ ನೆಟ್ {range} ಅವಧಿಗಾಗಿ.',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.dashboard.chain.directAgents.title': 'ನೇರ ಏಜೆಂಟ್‌ಗಳು',
  'agent.dashboard.chain.directAgents.subtitle': 'ನಿಮ್ಮ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳು — ನೀವು ಅವರ ವಿನಂತಿಗಳ ಮೇಲೆ ಕ್ರಮ ಕೈಗೊಳ್ಳಬಹುದು.',
  'agent.dashboard.chain.indirectAgents.title': 'ಪರೋಕ್ಷ ಏಜೆಂಟ್‌ಗಳು',
  'agent.dashboard.chain.indirectAgents.subtitle': 'ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಂದ ನಿರ್ವಹಿಸಲ್ಪಡುತ್ತದೆ. ನಿಮಗೆ ಓದಲು-ಮಾತ್ರ.',
  'agent.dashboard.chain.directPlayers.title': 'ನೇರ ಆಟಗಾರರು',
  'agent.dashboard.chain.directPlayers.subtitle': 'ನಿಮ್ಮ ಆಟಗಾರರು — ನೀವು ಅವರ ರೀಚಾರ್ಜ್ ಮತ್ತು ಹಿಂಪಡೆಯುವಿಕೆಗಳ ಮೇಲೆ ಕ್ರಮ ಕೈಗೊಳ್ಳುತ್ತೀರಿ.',
  'agent.dashboard.chain.indirectPlayers.title': 'ಪರೋಕ್ಷ ಆಟಗಾರರು',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳ ಆಟಗಾರರು. ನಿಮಗೆ ಓದಲು-ಮಾತ್ರ.',
  'agent.dashboard.chain.field.walletTotal': 'ವಾಲೆಟ್ ಒಟ್ಟು',
  'agent.dashboard.chain.field.depositTotal': 'ಠೇವಣಿ ಒಟ್ಟು',
  'agent.dashboard.chain.field.avgPerHead': 'ಪ್ರತಿ ತಲೆಗೆ ಸರಾಸರಿ',
  'agent.dashboard.chain.field.pendingRecharge': 'ಬಾಕಿಯಿರುವ ರೀಚಾರ್ಜ್',
  'agent.dashboard.chain.field.pendingWithdrawal': 'ಬಾಕಿಯಿರುವ ಹಿಂಪಡೆಯುವಿಕೆ',
  'agent.dashboard.chain.pendingBadge': '{n} ಬಾಕಿಯಿರುವ',
  'agent.dashboard.chain.openInbox': 'ಇನ್‌ಬಾಕ್ಸ್ ತೆರೆಯಿರಿ',
  'agent.dashboard.chain.footer': 'ನೇರ = ನೀವೇ ನಿರ್ವಹಿಸುವ ಸಂಖ್ಯೆ. ಪರೋಕ್ಷ = ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಂದ ನಿರ್ವಹಿಸಲ್ಪಡುತ್ತದೆ (ನೀವು ಸಂಖ್ಯೆಗಳನ್ನು ನೋಡುತ್ತೀರಿ ಆದರೆ ಅವರ ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳ ಮೇಲೆ ಕ್ರಮ ಕೈಗೊಳ್ಳಲು ಸಾಧ್ಯವಿಲ್ಲ — ಅವು ಅವರ ಪೇರೆಂಟ್‌ಗೆ ಹೋಗುತ್ತವೆ).',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
  'agent.activity.filter.window': 'ಅವಧಿ',
  'agent.activity.filter.kind': 'ಪ್ರಕಾರ',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'ಎಲ್ಲಾ',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'ಎಲ್ಲಾ',
  'agent.activity.kind.matches': 'ಮ್ಯಾಚ್‌ಗಳು',
  'agent.activity.kind.recharges': 'ರೀಚಾರ್ಜ್‌ಗಳು',
  'agent.activity.kind.rateChanges': 'ದರ ಬದಲಾವಣೆಗಳು',
  // Empty + loading states.
  'agent.activity.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.activity.empty.title': 'ಶಾಂತ',
  'agent.activity.empty.subtitle': '{window} ಅವಧಿಯಲ್ಲಿ ಯಾವುದೇ {kindWord}ಈವೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'ಮ್ಯಾಚ್ ',
  'agent.activity.empty.kindWord.recharge': 'ರೀಚಾರ್ಜ್ ',
  'agent.activity.empty.kindWord.partnership_change': 'ಪಾಲುದಾರಿಕೆ ಬದಲಾವಣೆ ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'ಇತ್ಯರ್ಥ',
  'agent.activity.row.earned': 'ಗಳಿಸಿದ್ದು',
  'agent.activity.row.fromPillarOn': '{pillar} ಮೇಲೆ ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ',
  'agent.activity.row.pillar.winLoss': 'ವಿನ್/ಲಾಸ್',
  'agent.activity.row.pillar.offerReward': 'ಆಫರ್ ಬಹುಮಾನ',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'ರೀಚಾರ್ಜ್',
  'agent.activity.row.recharge.of': ' ರ ',
  'agent.activity.row.recharge.byUser': 'ಬಳಕೆದಾರ #{id} ಮೂಲಕ',
  'agent.activity.row.recharge.via': ' ಮೂಲಕ ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.activity.row.state.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.activity.row.state.pending': 'ಬಾಕಿಯಿರುವ',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'ದರ ಬದಲಾವಣೆ',
  'agent.activity.row.rateChange.dashOn': '— {pillar} ಮೇಲೆ ',
  'agent.activity.row.rateChange.cascade': 'ಕ್ಯಾಸ್ಕೇಡ್',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'ಈ ವ್ಯೂ ಕೇವಲ ಏಜೆಂಟ್‌ಗಳಿಗೆ ಮಾತ್ರ.',
  'agent.earnings.stat.poolBalance': 'ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.earnings.stat.totalIn': 'ಒಟ್ಟು ಆದಾಯ',
  'agent.earnings.stat.totalOut': 'ಒಟ್ಟು ವೆಚ್ಚ',
  'agent.earnings.section.in': 'ಗಳಿಕೆ (ಆದಾಯ)',
  'agent.earnings.section.out': 'ಹೊರಹರಿವು (ವೆಚ್ಚ)',
  'agent.earnings.empty.in': 'ಇನ್ನೂ ಯಾವುದೇ ಗಳಿಕೆ ಇಲ್ಲ. ನಿಮ್ಮ ಆಟಗಾರರು ರೀಚಾರ್ಜ್ ಮಾಡಿದಾಗ ಅಥವಾ ಆಡಿದಾಗ, ಪಾಲು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತದೆ.',
  'agent.earnings.empty.out': 'ಇನ್ನೂ ಯಾವುದೇ ಹೊರಹರಿವು ಇಲ್ಲ.',
  'agent.earnings.viewLedger': 'ಪೂರ್ಣ ಲೆಜರ್ ನೋಡಿ',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'ಅಡ್ಮಿನ್‌ನಿಂದ ಟಾಪ್-ಅಪ್',
  'agent.earnings.kind.topupFromAdmin.hint': 'ಅಡ್ಮಿನ್ ನಿಮ್ಮ ಪೂಲ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡಿದರು',
  'agent.earnings.kind.topupToPlayer.label': 'ಆಟಗಾರನಿಗೆ ಟಾಪ್-ಅಪ್',
  'agent.earnings.kind.topupToPlayer.hint': 'ನೀವು ಆಟಗಾರನಿಗೆ ರೀಚಾರ್ಜ್ ಮಾಡಿದ್ದೀರಿ',
  'agent.earnings.kind.topupFromPlayer.label': 'ಆಟಗಾರನ ರೀಚಾರ್ಜ್',
  'agent.earnings.kind.topupFromPlayer.hint': 'ನಿಮ್ಮ ಆಟಗಾರರು ನಿಮ್ಮ ಪೂಲ್‌ಗೆ ಪಾವತಿಸಿದ್ದಾರೆ',
  'agent.earnings.kind.revenueShare.label': 'ಆದಾಯ ಹಂಚಿಕೆ',
  'agent.earnings.kind.revenueShare.hint': 'ಪಂದ್ಯಗಳಿಂದ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕದ ಪಾಲು',
  'agent.earnings.kind.coFundOffer.label': 'ಸಹ-ನಿಧಿ ಆಫರ್',
  'agent.earnings.kind.coFundOffer.hint': 'ಆಫರ್ ಪಾವತಿಗಳಲ್ಲಿ ನಿಮ್ಮ ಪಾಲು',
  'agent.earnings.kind.adminAdjust.label': 'ಅಡ್ಮಿನ್ ಹೊಂದಾಣಿಕೆ',
  'agent.earnings.kind.adminAdjust.hint': 'ಕೈಯಿಂದ ತಿದ್ದುಪಡಿ',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'ನಮೂದು',
  'agent.earnings.row.entry.other': 'ನಮೂದುಗಳು',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'ಅವಧಿ',
  'agent.leaderboard.filter.pillar': 'ಪಿಲ್ಲರ್',
  'agent.leaderboard.window.all': 'ಎಲ್ಲಾ',
  'agent.leaderboard.pillar.all': 'ಎಲ್ಲಾ ಪಿಲ್ಲರ್‌ಗಳು',
  'agent.leaderboard.pillar.platformFee': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ',
  'agent.leaderboard.pillar.winLoss': 'ವಿನ್ / ಲಾಸ್',
  'agent.leaderboard.pillar.offerReward': 'ಆಫರ್ ಬಹುಮಾನ',
  'agent.leaderboard.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.leaderboard.empty.title': 'ಇನ್ನೂ ಯಾವುದೇ ಗಳಿಕೆ ಇಲ್ಲ',
  'agent.leaderboard.empty.subtitle.admin': '{window} ಅವಧಿಯಲ್ಲಿ ಎಲ್ಲಾ ಏಜೆಂಟ್‌ಗಳ ನಡುವೆ ಯಾವುದೇ ಸೆಟಲ್‌ಮೆಂಟ್ ಚಟುವಟಿಕೆ ಇಲ್ಲ.',
  'agent.leaderboard.empty.subtitle.downline': '{window} ಅವಧಿಯಲ್ಲಿ ನಿಮ್ಮ ಡೌನ್-ಲೈನ್‌ನಿಂದ ಯಾವುದೇ ಗಳಿಕೆ ಇಲ್ಲ. ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಪಂದ್ಯಗಳನ್ನು ನಡೆಸಿದಂತೆ, ಈ ಬೋರ್ಡ್ ತುಂಬಿಕೊಳ್ಳುತ್ತದೆ.',
  'agent.leaderboard.row.matches.one': '{n} ಪಂದ್ಯ',
  'agent.leaderboard.row.matches.other': '{n} ಪಂದ್ಯಗಳು',
  'agent.leaderboard.footer': 'ಗಳಿಕೆಯನ್ನು match_settlement_snapshots ನಿಂದ ತೆಗೆದುಕೊಳ್ಳಲಾಗಿದೆ. ಋಣಾತ್ಮಕ ಸಂಖ್ಯೆ ಎಂದರೆ ಆ ಏಜೆಂಟ್ ಅವಧಿಯಾದ್ಯಂತ ಒಳಬಂದದ್ದಕ್ಕಿಂತ ಹೆಚ್ಚು ಹೊರಗೆ ಪಾವತಿಸಿದ್ದಾರೆ.',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'ಪ್ಲಾಟ್',
  'agent.leaderboard.chip.winLoss': 'ವಿ/ಲಾ',
  'agent.leaderboard.chip.offer': 'ಆಫರ್',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'ಮಾರ್ಜಿನ್ ಎಚ್ಚರಿಕೆ · 80% ಮಿತಿ ತಲುಪಿದೆ',
  'agent.warning.header.title': 'ಲಾಕ್-ಔಟ್‌ನಿಂದ ಒಂದು ಕೆಟ್ಟ ಸೆಟಲ್‌ಮೆಂಟ್ ದೂರ.',
  'agent.warning.header.body': 'ಈಗ ಲಾಕ್ ಆಗಿಲ್ಲ — ಆದರೆ ನಿಮ್ಮ ಠೇವಣಿ ಸುರಕ್ಷಾ ಕವರೇಜ್ 80%ಕ್ಕಿಂತ ಹೆಚ್ಚು ಬಳಸಲ್ಪಟ್ಟಿದೆ. ಈಗಲೇ ಟಾಪ್-ಅಪ್ ಮಾಡಿ ಮತ್ತು ನಿಯಂತ್ರಣದಲ್ಲಿರಿ.',
  'agent.warning.header.dismissAria': 'ಮುಚ್ಚಿ',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'ಇದು ಏಕೆ ಕಾಣಿಸುತ್ತಿದೆ',
  'agent.warning.why.body': 'ಇತ್ತೀಚಿನ ಆಟದ ಸೆಟಲ್‌ಮೆಂಟ್‌ಗಳು ನಿಮ್ಮ ವಾಲೆಟ್ ಅನ್ನು ಋಣಾತ್ಮಕವಾಗಿಸಿವೆ. ನಿಮ್ಮ {deposit} ಭದ್ರತಾ ಠೇವಣಿ ಇದನ್ನು ಸಹಿಸುವ ಸುರಕ್ಷಾ-ಬಲೆ — ಮತ್ತು ಈಗ ಅದರ 80%ಕ್ಕಿಂತ ಹೆಚ್ಚು ಖರ್ಚಾಗಿದೆ. ಸ್ವಲ್ಪ ಹೆಚ್ಚು ಋಣಾತ್ಮಕವಾದರೆ ನಿಮ್ಮ ವಾಲೆಟ್ + ಠೇವಣಿ ಶೂನ್ಯಕ್ಕೆ ಬರುತ್ತದೆ ಮತ್ತು ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಖಾತೆಯನ್ನು ಲಾಕ್ ಮಾಡುತ್ತದೆ.',
  'agent.warning.why.reassurance': 'ಈ ಎಚ್ಚರಿಕೆಯು ಉದ್ದೇಶಪೂರ್ವಕವಾಗಿ ಬೇಗನೆ ಸಕ್ರಿಯವಾಗುತ್ತದೆ — ನೀವು ಇನ್ನೂ ಸಂಪೂರ್ಣ ಆಪ್ ಬಳಸಬಹುದಾಗಿರುವಾಗ. ಲಾಕ್-ಔಟ್ ಪರದೆಯು ನೀವು 100% ದಾಟಿದಾಗ ಮಾತ್ರ ಕಾಣಿಸುತ್ತದೆ, ಮತ್ತು ಒಮ್ಮೆ ಆದರೆ ನೀವು ಚೇತರಿಸಿಕೊಳ್ಳುವವರೆಗೆ ಹೆಚ್ಚಿನ ವೈಶಿಷ್ಟ್ಯಗಳು ನಿರ್ಬಂಧಿಸಲ್ಪಡುತ್ತವೆ. ಈಗಲೇ ಕಾರ್ಯನಿರ್ವಹಿಸುವುದು ನಂತರ ಮಾಡುವುದಕ್ಕಿಂತ ಬಹಳ ಕಡಿಮೆ ಅಡಚಣೆಯಾಗಿರುತ್ತದೆ.',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'ಲಾಕ್-ಔಟ್‌ಗೆ ಮುಂಚೆ ಹೆಡ್‌ರೂಮ್',
  'agent.warning.gauge.suffix': 'ಜಾಗ ಉಳಿದಿದೆ',
  'agent.warning.gauge.dangerBadge': 'ಅಪಾಯ ವಲಯದಲ್ಲಿ {pct}%',
  'agent.warning.gauge.field.wallet': 'ನಿಮ್ಮ ವಾಲೆಟ್',
  'agent.warning.gauge.field.deposit': 'ನಿಮ್ಮ ಠೇವಣಿ',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'ನೀವು ಇದನ್ನು ನಿರ್ಲಕ್ಷಿಸಿ 100%ಗೆ ತಲುಪಿದರೆ',
  'agent.warning.ignore.cardTitle': 'ನೀವು ತಕ್ಷಣ ಕಳೆದುಕೊಳ್ಳುತ್ತೀರಿ',
  'agent.warning.ignore.item.dashboard': 'ನಿಮ್ಮ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್, ಲಾಭ-ನಷ್ಟ ಮತ್ತು ಲೀಡರ್‌ಬೋರ್ಡ್‌ಗಳಿಗೆ ಪ್ರವೇಶ',
  'agent.warning.ignore.item.pages': 'ಆಟಗಾರರು ಮತ್ತು ಏಜೆಂಟ್‌ಗಳ ಪುಟಗಳು',
  'agent.warning.ignore.item.manualTopup': 'ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ ನಿಮ್ಮ ಆಟಗಾರರಿಗೆ ಕೈಯಿಂದ ಟಾಪ್-ಅಪ್‌ಗಳು',
  'agent.warning.ignore.item.newWithdrawal': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಹೊಸ ಹಿಂಪಡೆಯುವಿಕೆಗಳನ್ನು ಸಲ್ಲಿಸುವುದು',
  'agent.warning.ignore.item.subAgents': 'ಉಪ-ಏಜೆಂಟ್‌ಗಳನ್ನು ಸೇರಿಸುವುದು ಅಥವಾ ನಿರ್ವಹಿಸುವುದು',
  'agent.warning.ignore.item.profile': 'ನಿಮ್ಮ ಪ್ರೊಫೈಲ್ ಅಥವಾ ಪಾವತಿ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ಸಂಪಾದಿಸುವುದು',
  'agent.warning.ignore.footer': 'ನೀವು ಇನ್ನೂ ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳನ್ನು ಅನುಮೋದನೆ ಮಾಡಬಹುದು + ಒಂದು ತುರ್ತು ಟಾಪ್-ಅಪ್ ಕಳುಹಿಸಬಹುದು — ಆದರೆ ನಿಮ್ಮ ವಾಲೆಟ್ + ಠೇವಣಿ ಶೂನ್ಯಕ್ಕಿಂತ ಮೇಲೇರುವವರೆಗೆ ಉಳಿದೆಲ್ಲವೂ ಬಂದ್ ಆಗಿರುತ್ತದೆ.',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'ಚೇತರಿಸಿಕೊಳ್ಳಲು ಈಗಲೇ ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಪಾವತಿಸಿ',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'ನಾನು ಇದನ್ನು ನಂತರ ನಿಭಾಯಿಸುತ್ತೇನೆ',
  'agent.warning.dismiss.footnote': 'ನೀವು ಎಚ್ಚರಿಕೆ ವಲಯದಲ್ಲಿಯೇ ಇದ್ದರೆ ನಾವು 10 ನಿಮಿಷಗಳಲ್ಲಿ ಮತ್ತೆ ನೆನಪಿಸುತ್ತೇವೆ. ಚೇತರಿಕೆಯಾದ ತಕ್ಷಣ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಮುಚ್ಚುತ್ತದೆ.',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'ಖಾತೆ ಲಾಕ್ · ಬೈಲ್-ಔಟ್ ಪೋರ್ಟಲ್',
  'agent.lockout.header.title': 'ನಿಮ್ಮ ಠೇವಣಿ ಕವರ್ ಮಾಡುವುದಕ್ಕಿಂತ ಹೆಚ್ಚು ನೀವು ಬಾಕಿ ಇದ್ದೀರಿ.',
  'agent.lockout.header.body': 'ಚಿಂತಿಸಬೇಡಿ — ಇದು ಸರಿಪಡಿಸಬಹುದಾದ್ದು. ಕೆಳಗಿನ ಎರಡು ಹಂತಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸಿ ಮತ್ತು ನೀವು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಅನ್‌ಲಾಕ್ ಆಗುತ್ತೀರಿ.',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'ಇದು ಏಕೆ ಸಂಭವಿಸಿತು',
  'agent.lockout.why.body': 'ಆಟದ ಸೆಟಲ್‌ಮೆಂಟ್‌ಗಳು ನಿಮ್ಮ ವಾಲೆಟ್ ಅನ್ನು ಶೂನ್ಯಕ್ಕಿಂತ ಕೆಳಗೆ ಒಯ್ದಿವೆ. ನಿಮ್ಮ ಭದ್ರತಾ ಠೇವಣಿ ಇದನ್ನು ಸಹಿಸುವ ಸುರಕ್ಷಾ-ಬಲೆ — ಆದರೆ ನೀವು ಅದರ 100% ಬಳಸಿಯಾಗಿದೆ. ನಿಮ್ಮ ವಾಲೆಟ್ ಮತ್ತು ಠೇವಣಿ ಮತ್ತೆ ಶೂನ್ಯಕ್ಕಿಂತ ಮೇಲೇರುವವರೆಗೆ, ನೀವು ಸಾಮಾನ್ಯವಾಗಿ ಕೆಲಸ ಮಾಡಲು ಸಾಧ್ಯವಿಲ್ಲ.',
  'agent.lockout.why.reassurance': 'ನೀವು ಸ್ಥಿರವಾಗುತ್ತಿರುವಾಗ ನಿಮ್ಮ ಠೇವಣಿ ವಿರುದ್ಧ ನೆಟ್‌ವರ್ಕ್ ಇನ್ನಷ್ಟು ಸಾಲ ಸಂಗ್ರಹಿಸದಂತೆ ಇದು ಲಾಕ್ ಮಾಡುತ್ತದೆ.',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'ಸುರಕ್ಷಿತ ಚೇತರಿಕೆ ಗುರಿ',
  'agent.lockout.gauge.suffix': 'ಸುರಕ್ಷಿತವಾಗಿ ಪಾವತಿಸಲು',
  'agent.lockout.gauge.recoveredBadge': '{pct}% ಚೇತರಿಸಲಾಗಿದೆ',
  'agent.lockout.gauge.field.wallet': 'ನಿಮ್ಮ ವಾಲೆಟ್',
  'agent.lockout.gauge.field.deposit': 'ನಿಮ್ಮ ಠೇವಣಿ',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'ಬರೀ ಅನ್‌ಲಾಕ್',
  'agent.lockout.tier.justUnlock.tag': 'ಅಪಾಯಕಾರಿ',
  'agent.lockout.tier.justUnlock.note': 'ನಿಮ್ಮನ್ನು ₹0ಗೆ ಮರಳಿ ತರುತ್ತದೆ — ಯಾವುದೇ ಹೊಸ ನಷ್ಟ ತಕ್ಷಣ ಮತ್ತೆ ಲಾಕ್ ಮಾಡುತ್ತದೆ.',
  'agent.lockout.tier.safeMin.label': 'ಸುರಕ್ಷಿತ ಕನಿಷ್ಠ',
  'agent.lockout.tier.safeMin.tag': 'ಶಿಫಾರಸು',
  'agent.lockout.tier.safeMin.note': 'ಠೇವಣಿ ({deposit}) + ನಿಮ್ಮ ಋಣಾತ್ಮಕ ವಾಲೆಟ್‌ನ ಅರ್ಧ. ನಿಜವಾದ ಕುಶನ್ ಬಿಡುತ್ತದೆ.',
  'agent.lockout.tier.fullRecovery.label': 'ಪೂರ್ಣ ಚೇತರಿಕೆ',
  'agent.lockout.tier.fullRecovery.tag': 'ಅತ್ಯುತ್ತಮ',
  'agent.lockout.tier.fullRecovery.note': 'ಠೇವಣಿ ಸಂಪೂರ್ಣವಾಗಿ ಪುನಃಸ್ಥಾಪಿಸಲಾಗಿದೆ ಮತ್ತು ವಾಲೆಟ್ ಶೂನ್ಯಕ್ಕೆ ಮರಳಿ ತರಲಾಗಿದೆ.',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'ಚೇತರಿಸಿಕೊಳ್ಳಲು ಎರಡು ಮಾರ್ಗಗಳು — ಯಾವುದನ್ನಾದರೂ (ಅಥವಾ ಎರಡನ್ನೂ) ಮಾಡಿ',
  'agent.lockout.waysOut.inbox.title': 'ನಿಮ್ಮ ಇನ್‌ಬಾಕ್ಸ್‌ನ ಮೇಲೆ ಕ್ರಮ ತೆಗೆದುಕೊಳ್ಳಿ',
  'agent.lockout.waysOut.inbox.body': 'ಆಟಗಾರನ ರೀಚಾರ್ಜ್‌ಗಳನ್ನು ಅನುಮೋದನೆ ಮಾಡಿ (ಹಣ ಈಗಾಗಲೇ ನಿಮ್ಮ ಬಳಿ ಇದೆ) ಮತ್ತು ಆಟಗಾರನ ಹಿಂಪಡೆಯುವಿಕೆಗಳನ್ನು ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸಿ. ಪ್ರತಿ ಅನುಮೋದನೆಯು ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡುತ್ತದೆ.',
  'agent.lockout.waysOut.inbox.ctaOpen': 'ಇನ್‌ಬಾಕ್ಸ್ ತೆರೆಯಿರಿ',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ಇನ್‌ಬಾಕ್ಸ್ ಕೆಳಗೆ ತೆರೆದಿದೆ',
  'agent.lockout.waysOut.upline.title': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಪಾವತಿಸಿ',
  'agent.lockout.waysOut.upline.body': 'UPI ಮೂಲಕ ನಿಮ್ಮ ಪೋಷಕ ಏಜೆಂಟ್ (ಅಥವಾ ಅಡ್ಮಿನ್) ಗೆ ನಗದು ಕಳುಹಿಸಿ, ನಂತರ txn id ಸಲ್ಲಿಸಿ. ಅವರು ಅನುಮೋದನೆ ಮಾಡಿದ ತಕ್ಷಣ, ನಿಮ್ಮ ವಾಲೆಟ್ ತಕ್ಷಣ ಕ್ರೆಡಿಟ್ ಆಗುತ್ತದೆ.',
  'agent.lockout.waysOut.upline.ctaOpen': 'ಫಾರ್ಮ್ ತೆರೆಯಿರಿ',
  'agent.lockout.waysOut.upline.ctaOpened': 'ಫಾರ್ಮ್ ಕೆಳಗೆ ತೆರೆದಿದೆ',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'ನೀವು ಇನ್ನೂ ಏನು ಮಾಡಬಹುದು (ಮತ್ತು ಏನು ಬಂದ್ ಆಗಿದೆ)',
  'agent.lockout.capabilities.canHeader': 'ನೀವು ಇನ್ನೂ ಮಾಡಬಹುದು',
  'agent.lockout.capabilities.cantHeader': 'ನೀವು (ತಾತ್ಕಾಲಿಕವಾಗಿ) ಮಾಡಲು ಸಾಧ್ಯವಿಲ್ಲ',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'ಬರುತ್ತಿರುವ ಆಟಗಾರನ ರೀಚಾರ್ಜ್‌ಗಳನ್ನು ಅನುಮೋದನೆ ಮಾಡುವುದು',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'ಆಟಗಾರನ ಹಿಂಪಡೆಯುವಿಕೆಗಳನ್ನು ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸುವುದು',
  'agent.lockout.can.approveSubAgentRecharge': 'ಉಪ-ಏಜೆಂಟ್ ರೀಚಾರ್ಜ್‌ಗಳನ್ನು ಅನುಮೋದಿಸಿ',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'ಉಪ-ಏಜೆಂಟ್ ಹಿಂಪಡೆಯುವಿಕೆಗಳನ್ನು ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಿ',
  'agent.lockout.can.submitToUpline': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ರೀಚಾರ್ಜ್ ಸಲ್ಲಿಸಿ',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE ಎಚ್ಚರಿಕೆಗಳನ್ನು ಸ್ವೀಕರಿಸಿ + ಸೈನ್ ಔಟ್ ಮಾಡಿ',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'ಹಂತ {n} · {title}',
  'agent.lockout.section.inbox.title': 'ನಿಮ್ಮ ಇನ್‌ಬಾಕ್ಸ್ — ಪ್ರತಿ ಅನುಮೋದನೆಯು ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡುತ್ತದೆ',
  'agent.lockout.section.inbox.subtitle': 'ಆಟಗಾರರು ಮತ್ತು ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಂದ ನಿಮ್ಮ ಕ್ರಿಯೆಗಾಗಿ ಕಾಯುತ್ತಿರುವ ಬಾಕಿಯಿರುವ ಪಾವತಿ ವಿನಂತಿಗಳು.',
  'agent.lockout.section.inbox.fromPlayers': 'ನಿಮ್ಮ ಆಟಗಾರರಿಂದ',
  'agent.lockout.section.inbox.fromSubAgents': 'ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಂದ',
  'agent.lockout.section.upline.title': 'ನಿಮ್ಮ ವಾಲೆಟ್ ಟಾಪ್-ಅಪ್ ಮಾಡಲು ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಪಾವತಿಸಿ',
  'agent.lockout.section.upline.subtitle': 'ಪೋಷಕರ UPI ಮುಂದೆ ಇದ್ದರೆ ವೇಗವಾಗಿರುತ್ತದೆ. ಅನುಮೋದನೆ ಸಾಮಾನ್ಯವಾಗಿ ಕೆಲವು ನಿಮಿಷಗಳಲ್ಲಿ ಸಿಗುತ್ತದೆ.',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'ರೀಚಾರ್ಜ್ · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'ಹಿಂಪಡೆಯುವಿಕೆ · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi} ಗೆ ಪಾವತಿಸಿ · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi} ಗೆ ಪಾವತಿಸಿ · {whatsapp} · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.subAgent.recharge.title': 'ಉಪ-ಏಜೆಂಟ್ ರೀಚಾರ್ಜ್ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'ಉಪ-ಏಜೆಂಟ್ ಹಿಂಪಡೆಯುವಿಕೆ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi} ಗೆ ಪಾವತಿಸಿ · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi} ಗೆ ಪಾವತಿಸಿ · {when} ಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ · "{note}"',

  'agent.lockout.queue.action.approve': 'ಅನುಮೋದಿಸಿ',
  'agent.lockout.queue.action.reject': 'ತಿರಸ್ಕರಿಸಿ',
  'agent.lockout.queue.action.markPaid': 'ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಿ',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'ಆಟಗಾರನಿಗೆ ಪಾವತಿಸಲು ನೀವು ಬಳಸಿದ UPI ವಹಿವಾಟು ID ಅನ್ನು ನಮೂದಿಸಿ:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'ಉಪ-ಏಜೆಂಟ್‌ಗೆ ಪಾವತಿಸಲು ನೀವು ಬಳಸಿದ UPI ವಹಿವಾಟು ID ಅನ್ನು ನಮೂದಿಸಿ:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'ಈ ಹಿಂಪಡೆಯುವಿಕೆಯನ್ನು ತಿರಸ್ಕರಿಸುವುದೇ? ಹಿಡಿದಿಟ್ಟ ಮೊತ್ತವನ್ನು ಆಟಗಾರನಿಗೆ ಮರುಪಾವತಿ ಮಾಡಲಾಗುತ್ತದೆ.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'ಈ ಉಪ-ಏಜೆಂಟ್ ಹಿಂಪಡೆಯುವಿಕೆಯನ್ನು ತಿರಸ್ಕರಿಸುವುದೇ?',
  'agent.lockout.queue.toast.rechargeApproved': 'ರೀಚಾರ್ಜ್ ಅನುಮೋದಿಸಲಾಗಿದೆ — ಆಟಗಾರನಿಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡಲಾಗಿದೆ',
  'agent.lockout.queue.toast.rechargeRejected': 'ರೀಚಾರ್ಜ್ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'ಉಪ-ಏಜೆಂಟ್ ರೀಚಾರ್ಜ್ ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'ರೀಚಾರ್ಜ್ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.toast.withdrawalPaid': 'ಹಿಂಪಡೆಯುವಿಕೆಯನ್ನು ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಲಾಗಿದೆ — ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡಲಾಗಿದೆ',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'ಉಪ-ಏಜೆಂಟ್ ಹಿಂಪಡೆಯುವಿಕೆಯನ್ನು ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಲಾಗಿದೆ — ವಾಲೆಟ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡಲಾಗಿದೆ',
  'agent.lockout.queue.toast.withdrawalRejected': 'ಹಿಂಪಡೆಯುವಿಕೆ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ (ಆಟಗಾರನಿಗೆ ಮರುಪಾವತಿ ಮಾಡಲಾಗಿದೆ)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'ಹಿಂಪಡೆಯುವಿಕೆ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.lockout.queue.toast.actionFailed': 'ಕ್ರಿಯೆ ವಿಫಲವಾಗಿದೆ',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'ಇದೀಗ ನಿಮ್ಮ ಆಟಗಾರರಿಂದ ಏನೂ ಇಲ್ಲ.',
  'agent.lockout.queue.empty.subAgent': 'ಇದೀಗ ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಂದ ಏನೂ ಇಲ್ಲ.',
  'agent.lockout.queue.loading': 'ಕ್ಯೂ ಲೋಡ್ ಆಗುತ್ತಿದೆ…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'ರಿಫ್ರೆಶ್ — ನಾನು ಈಗ ಅನ್‌ಲಾಕ್ ಆಗಿದ್ದೇನೆಯೇ?',
  'agent.lockout.refresh.checking': 'ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ…',
  'agent.lockout.refresh.toast': 'ಸ್ಥಿತಿ ರಿಫ್ರೆಶ್ ಆಗಿದೆ',
  'agent.lockout.refresh.footnote': 'ನಿಮ್ಮ ವಾಲೆಟ್ + ಠೇವಣಿ ಶೂನ್ಯಕ್ಕಿಂತ ಮೇಲೇರಿದ ಕ್ಷಣ ಈ ಪುಟ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಮುಚ್ಚುತ್ತದೆ.',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'ರೀಚಾರ್ಜ್ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'ನಿಮ್ಮ ಖಾತೆ ಅನ್‌ಲಾಕ್ ಮಾಡಲು ಈಗಲೇ ಪಾವತಿಸಿ',
  'agent.emergencyForm.banner.locked.body': 'ನಿಮ್ಮ ಆಟಗಾರರು ನಿಮ್ಮ ಠೇವಣಿ ಜಾಮೀನು ಕವರ್ ಮಾಡಬಹುದಾದ್ದಕ್ಕಿಂತ ಹೆಚ್ಚು ಗೆದ್ದಿದ್ದಾರೆ. ನಿಮ್ಮ ಋಣಾತ್ಮಕ ಬ್ಯಾಲೆನ್ಸ್ ಅನ್ನು ತೆರವುಗೊಳಿಸುವವರೆಗೆ ನೀವು ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಬಳಸಲು ಸಾಧ್ಯವಿಲ್ಲ.',
  'agent.emergencyForm.banner.warning.title': 'ಶೀಘ್ರದಲ್ಲೇ ಟಾಪ್-ಅಪ್ ಮಾಡಿ — ನೀವು ಮಿತಿಗೆ ಬಹಳ ಹತ್ತಿರವಿದ್ದೀರಿ',
  'agent.emergencyForm.banner.warning.body': 'ನಿಮ್ಮ ವಾಲೆಟ್ ನಿಮ್ಮ ಠೇವಣಿಯ 80% ದಾಟಿದೆ. 100% ತಲುಪುವ ಮೊದಲು ಟಾಪ್-ಅಪ್ ಮಾಡಿ — ಒಮ್ಮೆ ತಲುಪಿದರೆ, ನಿಮ್ಮ ಖಾತೆ ಲಾಕ್ ಆಗುತ್ತದೆ.',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'ನಿಮ್ಮ ವಾಲೆಟ್',
  'agent.emergencyForm.row.deposit': 'ನಿಮ್ಮ ಠೇವಣಿ',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'ನೀವು ಏಕೆ ಸಿಲುಕಿದ್ದೀರಿ',
  'agent.emergencyForm.tier.eyebrow.warning': 'ನೀವು ಏಕೆ ಅಪಾಯದಲ್ಲಿದ್ದೀರಿ',
  'agent.emergencyForm.tier.context.locked': 'ನಿಮ್ಮ ವಾಲೆಟ್ {wallet} ತಲುಪಿದೆ — ಇದು ನಿಮ್ಮ {deposit} ಠೇವಣಿ ಭದ್ರತಾ ಕವರ್ ಮೀರಿದೆ. ನಿಮ್ಮ ಡೌನ್-ಲೈನ್‌ನ ನಷ್ಟಗಳು ಖಾಲಿ ಬಫರ್‌ನಲ್ಲಿ ಮತ್ತಷ್ಟು ಬೆಳೆಯದಂತೆ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ನಿಮ್ಮನ್ನು ಇಲ್ಲಿ ಲಾಕ್ ಮಾಡುತ್ತದೆ.',
  'agent.emergencyForm.tier.context.warning': 'ನಿಮ್ಮ ವಾಲೆಟ್ {wallet} ತಲುಪಿದೆ — ಇದು ನಿಮ್ಮ {deposit} ಠೇವಣಿ ಭದ್ರತಾ ಕವರ್‌ನ 80% ಗಿಂತ ಹೆಚ್ಚು. ಇನ್ನೊಂದು ಕೆಟ್ಟ ಸೆಟಲ್‌ಮೆಂಟ್ ನಿಮ್ಮನ್ನು 100% ದಾಟಿ ಖಾತೆಯನ್ನು ಲಾಕ್ ಮಾಡುತ್ತದೆ.',
  'agent.emergencyForm.tier.pickHowMuch': 'ಎಷ್ಟು ಪಾವತಿಸಬೇಕು ಎಂಬುದನ್ನು ಆಯ್ಕೆಮಾಡಿ',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'ಕೇವಲ ಅನ್‌ಲಾಕ್',
  'agent.emergencyForm.tier.bareMin.label.warning': 'ಕೇವಲ ಎಚ್ಚರಿಕೆಯಿಂದ ಹೊರಬರಿ',
  'agent.emergencyForm.tier.bareMin.tag': 'ಅಪಾಯಕಾರಿ',
  'agent.emergencyForm.tier.bareMin.note.locked': 'ನಿಮ್ಮ ವಾಲೆಟ್ + ಠೇವಣಿಯನ್ನು ₹0 ಗೆ ಮಾತ್ರ ಮರಳಿ ತರುತ್ತದೆ. ಯಾವುದೇ ಹೊಸ ನಷ್ಟ ನಿಮ್ಮನ್ನು ತಕ್ಷಣ ಮತ್ತೆ ಲಾಕ್ ಮಾಡುತ್ತದೆ.',
  'agent.emergencyForm.tier.bareMin.note.warning': 'ನಿಮ್ಮನ್ನು 80% ಗೆರೆಯಿಂದ ಸ್ವಲ್ಪ ಮೇಲೆ ಮಾತ್ರ ತರುತ್ತದೆ. ಯಾವುದೇ ಹೊಸ ನಷ್ಟ ಮತ್ತೆ ಎಚ್ಚರಿಕೆಯನ್ನು ಪ್ರಚೋದಿಸುತ್ತದೆ — ಅಥವಾ ನಿಮ್ಮನ್ನು ಲಾಕ್-ಔಟ್‌ಗೆ ತಳ್ಳುತ್ತದೆ.',
  'agent.emergencyForm.tier.safeMin.label': 'ಸುರಕ್ಷಿತ ಕನಿಷ್ಠ',
  'agent.emergencyForm.tier.safeMin.tag': 'ಶಿಫಾರಸು',
  'agent.emergencyForm.tier.safeMin.note': 'ನಿಮ್ಮ ಠೇವಣಿ ({deposit}) + ನಿಮ್ಮ ಋಣಾತ್ಮಕ ವಾಲೆಟ್‌ನ ಅರ್ಧ ({half}). ನಿಮಗೆ ನಿಜವಾದ ಕುಶನ್ ನೀಡುತ್ತದೆ.',
  'agent.emergencyForm.tier.full.label': 'ಪೂರ್ಣ ಚೇತರಿಕೆ',
  'agent.emergencyForm.tier.full.tag': 'ಅತ್ಯುತ್ತಮ',
  'agent.emergencyForm.tier.full.note': 'ಠೇವಣಿ + ನಿಮ್ಮ ಸಂಪೂರ್ಣ ಋಣಾತ್ಮಕ ವಾಲೆಟ್ ({neg}). ಠೇವಣಿ ಸಂಪೂರ್ಣವಾಗಿ ಮರುಸ್ಥಾಪಿಸಲ್ಪಟ್ಟಿದೆ ಮತ್ತು ವಾಲೆಟ್ ಶೂನ್ಯಕ್ಕೆ ಮರಳಿದೆ.',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'ನಿಮ್ಮ ಅಡ್ಮಿನ್‌ಗೆ ಪಾವತಿಸಿ',
  'agent.emergencyForm.parent.label.parentAgent': 'ನಿಮ್ಮ ಪೋಷಕ ಏಜೆಂಟ್‌ಗೆ ಪಾವತಿಸಿ',
  'agent.emergencyForm.parent.role.admin': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಅಡ್ಮಿನ್',
  'agent.emergencyForm.parent.role.parentAgent': 'ಪೋಷಕ ಏಜೆಂಟ್',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile': 'ನಿಮ್ಮ ಪೋಷಕರ ಪಾವತಿ ವಿವರಗಳು ಇನ್ನೂ ಫೈಲ್‌ನಲ್ಲಿ ಇಲ್ಲ. ದಯವಿಟ್ಟು ನೇರವಾಗಿ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಬೆಂಬಲವನ್ನು ಸಂಪರ್ಕಿಸಿ.',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'ಪಾವತಿಸಿದ ಮೊತ್ತ (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI ವಹಿವಾಟು ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'ಉದಾ. 412312345678',
  'agent.emergencyForm.field.note.label': 'ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ)',
  'agent.emergencyForm.field.note.placeholder': 'ಪೋಷಕರಿಗೆ ಏನಾದರೂ ಹೇಳಬೇಕೇ',
  'agent.emergencyForm.validation.amount': 'ನೀವು ಪಾವತಿಸಿದ ಮೊತ್ತವನ್ನು ನಮೂದಿಸಿ (ಕನಿಷ್ಠ ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI ವಹಿವಾಟು ID / UTR ಅನ್ನು ನಮೂದಿಸಿ (ಕನಿಷ್ಠ 6 ಅಕ್ಷರಗಳು)',
  'agent.emergencyForm.cta': 'ರೀಚಾರ್ಜ್ ವಿನಂತಿಯನ್ನು ಸಲ್ಲಿಸಿ',
  'agent.emergencyForm.ctaSubmitting': 'ಸಲ್ಲಿಸಲಾಗುತ್ತಿದೆ…',
  // Success state.
  'agent.emergencyForm.success.title': 'ರೀಚಾರ್ಜ್ ಸಲ್ಲಿಸಲಾಗಿದೆ',
  'agent.emergencyForm.success.bodyAdmin': 'ವಿನಂತಿ #{id} ಅನುಮೋದನೆಗಾಗಿ ಅಡ್ಮಿನ್ ಅನ್ನು ಕಾಯುತ್ತಿದೆ. ಅನುಮೋದನೆ ಸಿಕ್ಕ ತಕ್ಷಣ, ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಆಗುತ್ತದೆ ಮತ್ತು ನಿಮ್ಮ ಖಾತೆ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಅನ್‌ಲಾಕ್ ಆಗುತ್ತದೆ.',
  'agent.emergencyForm.success.bodyAgent': 'ವಿನಂತಿ #{id} ಅನುಮೋದನೆಗಾಗಿ @{username} ಅನ್ನು ಕಾಯುತ್ತಿದೆ. ಅನುಮೋದನೆ ಸಿಕ್ಕ ತಕ್ಷಣ, ನಿಮ್ಮ ವಾಲೆಟ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಆಗುತ್ತದೆ ಮತ್ತು ನಿಮ್ಮ ಖಾತೆ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಅನ್‌ಲಾಕ್ ಆಗುತ್ತದೆ.',
  'agent.emergencyForm.toast.submitted': 'ರೀಚಾರ್ಜ್ ವಿನಂತಿ #{id} ಸಲ್ಲಿಸಲಾಗಿದೆ. ಅನುಮೋದನೆಗಾಗಿ ಕಾಯಲಾಗುತ್ತಿದೆ.',
  'agent.emergencyForm.toast.cantSave': 'ಭಾಷೆಯನ್ನು ಉಳಿಸಲಾಗಲಿಲ್ಲ',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'ಒಂದು-ಬಾರಿಯ ಸೆಟಪ್: ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ಖಚಿತಪಡಿಸಿ',
  'agent.geo.subtitle': 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ ಬಳಸುವ ಮೊದಲು ಅಗತ್ಯ.',
  'agent.geo.body.primary': 'ಬಾಲಿ ಗೇಮ್ಸ್ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಹೊಸ ಆಟಗಾರರನ್ನು ಅವರಿಗೆ ಭೌತಿಕವಾಗಿ ಹತ್ತಿರವಿರುವ ಏಜೆಂಟ್‌ಗೆ ರೂಟ್ ಮಾಡುತ್ತದೆ. ನೀವು ನಿಮ್ಮ ನಿಖರವಾದ GPS ಸ್ಥಳವನ್ನು ಹಂಚಿಕೊಳ್ಳುವವರೆಗೆ, ನೀವು ಲೊಕೇಟರ್‌ನಲ್ಲಿ ಕಾಣಿಸುವುದಿಲ್ಲ ಮತ್ತು ಆರ್ಗ್ಯಾನಿಕ್ ಸೈನಪ್‌ಗಳಿಂದ ಶೂನ್ಯ ಹೊಸ ಆಟಗಾರರನ್ನು ಪಡೆಯುತ್ತೀರಿ — ನಿಮ್ಮ ರೆಫರಲ್ ಕೋಡ್ ಅನ್ನು ನೇರವಾಗಿ ಟೈಪ್ ಮಾಡುವ ಆಟಗಾರರನ್ನು ಮಾತ್ರ ನೀವು ಪಡೆಯುತ್ತೀರಿ.',
  'agent.geo.body.secondary': '"ನನ್ನ ಪ್ರಸ್ತುತ ಸ್ಥಳವನ್ನು ಬಳಸಿ" ಒಮ್ಮೆ ಟ್ಯಾಪ್ ಮಾಡಿ. ನಾವು ನಿಮ್ಮ ನಿರ್ದೇಶಾಂಕಗಳನ್ನು ಮಾತ್ರ ಸೆರೆಹಿಡಿಯುತ್ತೇವೆ, ಬೇರೆ ಏನನ್ನೂ ಅಲ್ಲ.',
  'agent.geo.hint.disabled': 'ಮೇಲೆ "ನನ್ನ ಪ್ರಸ್ತುತ ಸ್ಥಳವನ್ನು ಬಳಸಿ" ಟ್ಯಾಪ್ ಮಾಡಿ. ಪಿನ್‌ಕೋಡ್ ಅಥವಾ ಕೈಯಾರೆ ಪ್ರದೇಶ ಆಯ್ಕೆ ಮಾತ್ರ ಸಾಕಾಗುವುದಿಲ್ಲ — ಲೊಕೇಟರ್‌ಗೆ ನಿಖರವಾದ GPS ನಿರ್ದೇಶಾಂಕಗಳು ಬೇಕು.',
  'agent.geo.error.fallback': 'ಸ್ಥಳವನ್ನು ಉಳಿಸಲಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
  'agent.geo.cta.save': 'ಉಳಿಸಿ ಮತ್ತು ಮುಂದುವರಿಸಿ',
  'agent.geo.cta.saving': 'ಸ್ಥಳವನ್ನು ಉಳಿಸಲಾಗುತ್ತಿದೆ…',
  'agent.geo.toast.success': 'ಸ್ಥಳ ಉಳಿಸಲಾಗಿದೆ — ಈಗ ನೀವು ಆಟಗಾರರನ್ನು ಪಡೆಯಬಹುದು.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.wallet.hero.helper': 'ಆಟಗಾರರ ರೀಚಾರ್ಜ್ ಮತ್ತು ಉಪ-ಏಜೆಂಟ್ ಟಾಪ್-ಅಪ್‌ಗಳಿಗೆ ಖರ್ಚು ಮಾಡಲು ಲಭ್ಯ. ಗಳಿಕೆ ಮತ್ತು ಅಡ್ಮಿನ್ ಟಾಪ್-ಅಪ್‌ಗಳು ಇಲ್ಲಿಗೆ ಬರುತ್ತವೆ.',

  'agent.wallet.action.recharge': 'ರೀಚಾರ್ಜ್ ವಿನಂತಿ',
  'agent.wallet.action.withdraw': 'ಹಿಂಪಡೆಯುವಿಕೆ ವಿನಂತಿ',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.wallet.tab.history': 'ಇತಿಹಾಸ',

  // Pending feed.
  'agent.wallet.pending.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.wallet.pending.empty': 'ಸದ್ಯ ಯಾವುದೇ ಬಾಕಿಯಿರುವ ವಿನಂತಿಗಳಿಲ್ಲ.',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'ರೀಚಾರ್ಜ್ ವಿನಂತಿ · +{amount}',
  'agent.wallet.pending.recharge.meta': 'ವಹಿವಾಟು {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'ಹಿಂಪಡೆಯುವಿಕೆ ವಿನಂತಿ · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi} ಗೆ · {when}',
  'agent.wallet.pending.badge': 'ಬಾಕಿಯಿರುವ',

  // History feed.
  'agent.wallet.history.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.wallet.history.empty': 'ಇನ್ನೂ ಯಾವುದೇ ವಾಲೆಟ್ ಚಟುವಟಿಕೆಯಿಲ್ಲ.',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'ರೀಚಾರ್ಜ್ ಸ್ವೀಕರಿಸಲಾಗಿದೆ · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'ರೀಚಾರ್ಜ್ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ · {amount}',
  'agent.wallet.history.recharge.meta': 'ವಹಿವಾಟು {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'ಹಿಂಪಡೆಯುವಿಕೆ ಪಾವತಿಸಲಾಗಿದೆ · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'ಹಿಂಪಡೆಯುವಿಕೆ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi} ಗೆ · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'ಪಾವತಿಸಲಾಗಿದೆ · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'ಸ್ವೀಕರಿಸಲಾಗಿದೆ',
  'agent.wallet.state.paid': 'ಪಾವತಿಸಲಾಗಿದೆ',
  'agent.wallet.state.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.wallet.state.expired': 'ಅವಧಿ ಮುಗಿದಿದೆ',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'ಅಡ್ಮಿನ್‌ನಿಂದ ಟಾಪ್-ಅಪ್',
  'agent.wallet.ledger.kind.topupToPlayer': 'ಆಟಗಾರನಿಗೆ ಟಾಪ್-ಅಪ್',
  'agent.wallet.ledger.kind.topupFromPlayer': 'ಆಟಗಾರನ ರೀಚಾರ್ಜ್',
  'agent.wallet.ledger.kind.revenueShare': 'ಆದಾಯ ಪಾಲು',
  'agent.wallet.ledger.kind.coFundOffer': 'ಸಹ-ಧನಸಹಾಯ ಆಫರ್',
  'agent.wallet.ledger.kind.adminAdjust': 'ಅಡ್ಮಿನ್ ಹೊಂದಾಣಿಕೆ',
  'agent.wallet.ledger.kind.withdrawalHold': 'ಹಿಂಪಡೆಯುವಿಕೆ ಹೋಲ್ಡ್',
  'agent.wallet.ledger.kind.withdrawalPaid': 'ಹಿಂಪಡೆಯುವಿಕೆ ಪಾವತಿಸಲಾಗಿದೆ',
  'agent.wallet.ledger.kind.withdrawalRefund': 'ಹಿಂಪಡೆಯುವಿಕೆ ಮರುಪಾವತಿ',
  'agent.wallet.ledger.balanceAfter': 'ಬ್ಯಾಲೆನ್ಸ್ {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'ಭದ್ರತಾ ಠೇವಣಿ',
  'agent.deposit.hero.helper': 'ನಿಮ್ಮ ಪೂಲ್‌ಗೆ ಎದುರಾಗಿ ಜಾಮೀನು. ನಿಮ್ಮ ವಾಲೆಟ್ ಉತ್ತಮ ಸ್ಥಿತಿಯಲ್ಲಿರುವವರೆಗೆ ಇದು ಮುಟ್ಟದೆ ಉಳಿಯುತ್ತದೆ.',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'ಸುರಕ್ಷಿತ',
  'agent.deposit.margin.warning': 'ಎಚ್ಚರಿಕೆ',
  'agent.deposit.margin.locked': 'ಲಾಕ್',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'ಠೇವಣಿ ರೀಚಾರ್ಜ್',
  'agent.deposit.action.withdraw': 'ಠೇವಣಿ ಹಿಂಪಡೆಯಿರಿ',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'ಕವರೇಜ್ ಗೇಜ್',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'ಎಚ್ಚರಿಕೆ',
  'agent.deposit.gauge.thresholdLock': 'ಲಾಕ್',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct} ನಲ್ಲಿ ನಿಮಗೆ ಎಚ್ಚರಿಕೆ ಸಿಗುತ್ತದೆ',
  'agent.deposit.gauge.legend.lock': '{pct} ನಲ್ಲಿ, ನಿಮ್ಮ ಖಾತೆ ಲಾಕ್ ಆಗುತ್ತದೆ',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'ಪೂಲ್ ಸಂಪೂರ್ಣವಾಗಿ ಠೇವಣಿಯಿಂದ ಕವರ್ ಆಗಿದೆ — ನೀವು ಟಾಪ್ ಅಪ್ ಮಾಡುವವರೆಗೆ ಹಿಂಪಡೆಯುವಿಕೆಗಳನ್ನು ತಡೆಯಲಾಗಿದೆ.',
  'agent.deposit.gauge.caption.warning': 'ಪೂಲ್ ನಿಮ್ಮ ಠೇವಣಿಯ {pct}% ಸೇವಿಸಿದೆ. ಲಾಕ್-ಔಟ್ ತಪ್ಪಿಸಲು 100% ತಲುಪುವ ಮೊದಲು ಟಾಪ್ ಅಪ್ ಮಾಡಿ.',
  'agent.deposit.gauge.caption.caution': 'ಪೂಲ್ ಠೇವಣಿ ಕವರೇಜ್‌ನಲ್ಲಿ {pct}% ವರೆಗೆ ಇದೆ. ನೀವು ಇನ್ನೂ 80% ಎಚ್ಚರಿಕೆ ಮಿತಿಯಿಂದ ಸುರಕ್ಷಿತವಾಗಿ ಕೆಳಗಿದ್ದೀರಿ.',
  'agent.deposit.gauge.caption.safe': 'ನಿಮ್ಮ ಠೇವಣಿ ಸಂಪೂರ್ಣವಾಗಿ ಸುರಕ್ಷಿತವಾಗಿದೆ.',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.deposit.ledger.empty.title': 'ಇನ್ನೂ ಯಾವುದೇ ಠೇವಣಿ ಚಟುವಟಿಕೆಯಿಲ್ಲ',
  'agent.deposit.ledger.empty.body': 'ಅಡ್ಮಿನ್ ನಿಮ್ಮ ಠೇವಣಿಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡಿದ ತಕ್ಷಣ (ಅಥವಾ ಮರುಪಾವತಿ ಪ್ರಕ್ರಿಯೆಗೊಳಿಸಿದರೆ), ಪ್ರತಿ ನಮೂದು ಇಲ್ಲಿ ಕಾಣಿಸಿಕೊಳ್ಳುತ್ತದೆ.',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'ನಿಮ್ಮ ವಾಲೆಟ್ ರೀಚಾರ್ಜ್ ಮಾಡಿ',
  'agent.recharge.banner.eyebrow.deposit': 'ನಿಮ್ಮ ಠೇವಣಿ ರೀಚಾರ್ಜ್ ಮಾಡಿ',
  'agent.recharge.banner.body.wallet': 'ಮೊದಲು ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಪಾವತಿಸಿ, ನಂತರ ಪುರಾವೆಯನ್ನು ಇಲ್ಲಿ ಸಲ್ಲಿಸಿ ಇದರಿಂದ ಅವರು ನಿಮ್ಮ ಕಾರ್ಯಾಚರಣೆಯ ವಾಲೆಟ್ ಅನ್ನು ಟಾಪ್ ಅಪ್ ಮಾಡಬಹುದು.',
  'agent.recharge.banner.body.deposit': 'ಮೊದಲು ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಪಾವತಿಸಿ, ನಂತರ ಪುರಾವೆಯನ್ನು ಇಲ್ಲಿ ಸಲ್ಲಿಸಿ ಇದರಿಂದ ಅವರು ನಿಮ್ಮ ಭದ್ರತಾ ಠೇವಣಿಯನ್ನು ಟಾಪ್ ಅಪ್ ಮಾಡಬಹುದು.',
  'agent.recharge.banner.closeAria': 'ಫಾರ್ಮ್ ಮುಚ್ಚಿ',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'ನಿಮ್ಮ ಅಡ್ಮಿನ್‌ಗೆ ಪಾವತಿಸಿ',
  'agent.recharge.parent.label.parentAgent': 'ನಿಮ್ಮ ಪೋಷಕ ಏಜೆಂಟ್‌ಗೆ ಪಾವತಿಸಿ',
  'agent.recharge.parent.role.admin': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಅಡ್ಮಿನ್',
  'agent.recharge.parent.role.parentAgent': 'ಪೋಷಕ ಏಜೆಂಟ್',
  'agent.recharge.parent.noQr': 'ಯಾವುದೇ QR ಸೆಟ್ ಆಗಿಲ್ಲ',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ಪಾವತಿ ವಿವರಗಳು ಲೋಡ್ ಆಗುತ್ತಿವೆ…',
  'agent.recharge.parent.loadError': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ಪಾವತಿ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'ನೀವು ಹೇಗೆ ಪಾವತಿಸಿದ್ದೀರಿ?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ಆನ್‌ಲೈನ್ ಡಿಜಿಟಲ್ ಪಾವತಿ',
  'agent.recharge.method.cash.label': 'ನಗದು',
  'agent.recharge.method.cash.note': 'ವ್ಯಕ್ತಿಗತ ಹಸ್ತಾಂತರ',

  // Form fields.
  'agent.recharge.field.amount.label': 'ಮೊತ್ತ (₹) · ಕನಿಷ್ಠ ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI ವಹಿವಾಟು ID / UTR',
  'agent.recharge.field.txn.label.cash': 'ನಗದು ರಸೀದಿ ಮಾರ್ಕರ್ (ಯಾವುದೇ ಸಂಕ್ಷಿಪ್ತ ಉಲ್ಲೇಖ)',
  'agent.recharge.field.txn.placeholder.upi': 'ಉದಾ. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'ಉದಾ. CASH-Sun-eve',
  'agent.recharge.field.note.label': 'ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ, ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ತೋರಿಸಲಾಗುತ್ತದೆ)',
  'agent.recharge.field.note.placeholder': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗಾಗಿ ಯಾವುದೇ ಸಂದರ್ಭ',

  // Validation errors.
  'agent.recharge.error.amountMin': 'ಮೊತ್ತ ಪೂರ್ಣ ರೂಪಾಯಿಯಲ್ಲಿ ≥ 10 ಆಗಿರಬೇಕು',
  'agent.recharge.error.txnUpi': 'ವಹಿವಾಟು ID ತುಂಬಾ ಚಿಕ್ಕದಾಗಿ ಕಾಣುತ್ತದೆ — UPI ಉಲ್ಲೇಖವನ್ನು ಅಂಟಿಸಿ',
  'agent.recharge.error.txnCash': 'ಒಂದು ಚಿಕ್ಕ ಮಾರ್ಕರ್ ಸೇರಿಸಿ (ಉದಾ. "CASH-Sun-eve") ಇದರಿಂದ ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ಹಸ್ತಾಂತರವನ್ನು ಹೊಂದಿಸಬಹುದು',

  // CTAs.
  'agent.recharge.cta.submit': 'ವಿನಂತಿ ಸಲ್ಲಿಸಿ',
  'agent.recharge.cta.cancel': 'ರದ್ದು',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'ವಾಲೆಟ್‌ನಿಂದ ಹಿಂಪಡೆಯಿರಿ',
  'agent.withdrawal.banner.eyebrow.deposit': 'ಠೇವಣಿಯಿಂದ ಹಿಂಪಡೆಯಿರಿ',
  'agent.withdrawal.banner.body.wallet': 'ಮೊತ್ತವನ್ನು ತಕ್ಷಣವೇ ತಡೆಹಿಡಿಯಲಾಗುತ್ತದೆ ಇದರಿಂದ ಬೇರೆಡೆ ಖರ್ಚಾಗುವುದಿಲ್ಲ. ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ಪಾವತಿಸುತ್ತಾರೆ ಮತ್ತು ಪಾವತಿಯನ್ನು ದಾಖಲಿಸುತ್ತಾರೆ.',
  'agent.withdrawal.banner.body.deposit': 'ಮೊತ್ತವನ್ನು ನಿಮ್ಮ ಭದ್ರತಾ ಠೇವಣಿಯಿಂದ ತಕ್ಷಣವೇ ತಡೆಹಿಡಿಯಲಾಗುತ್ತದೆ. ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ನಿಮಗೆ ಪಾವತಿಸುತ್ತಾರೆ ಮತ್ತು ಅದನ್ನು ಪಾವತಿಸಿದೆ ಎಂದು ಗುರುತಿಸುತ್ತಾರೆ.',
  'agent.withdrawal.banner.closeAria': 'ಫಾರ್ಮ್ ಮುಚ್ಚಿ',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'ನೀವು ಹೇಗೆ ಪಾವತಿ ಪಡೆಯಲು ಬಯಸುತ್ತೀರಿ?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ಆನ್‌ಲೈನ್ ಡಿಜಿಟಲ್ ಪಾವತಿ',
  'agent.withdrawal.method.cash.label': 'ನಗದು',
  'agent.withdrawal.method.cash.note': 'ವ್ಯಕ್ತಿಗತ ಹಸ್ತಾಂತರ',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'ಮೊತ್ತ (₹) · ಲಭ್ಯವಿರುವ {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'ನಿಮ್ಮ UPI ID (ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ನಿಮಗೆ ಎಲ್ಲಿ ಪಾವತಿಸುತ್ತಾರೆ)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'ಸಭೆಯ ಮಾರ್ಕರ್ (ಉದಾ. "Sun evening cash")',
  'agent.withdrawal.field.cashMarker.placeholder': 'ನಗದು ಯಾವಾಗ/ಎಲ್ಲಿ ಹಸ್ತಾಂತರಿಸಬೇಕು',
  'agent.withdrawal.field.whatsapp.label': 'WhatsApp (ಐಚ್ಛಿಕ, ನಿಮ್ಮ ಅಪ್-ಲೈನ್ ನಿಮ್ಮನ್ನು ತಲುಪಲು)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ)',
  'agent.withdrawal.field.note.placeholder': 'ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗಾಗಿ ಯಾವುದೇ ಸಂದರ್ಭ',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'ಮೊತ್ತವು ಪೂರ್ಣ ರೂಪಾಯಿ ≥ 10 ಆಗಿರಬೇಕು',
  'agent.withdrawal.error.amountOverflow': 'ನಿಮ್ಮಲ್ಲಿ ಕೇವಲ {balance} ಲಭ್ಯವಿದೆ',
  'agent.withdrawal.error.upi': 'ಮಾನ್ಯವಾದ UPI ID ನಮೂದಿಸಿ',

  // CTAs.
  'agent.withdrawal.cta.submit': 'ವಿನಂತಿಯನ್ನು ಸಲ್ಲಿಸಿ',
  'agent.withdrawal.cta.cancel': 'ರದ್ದುಗೊಳಿಸಿ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'ಆಟಗಾರರು',
  'agent.people.hub.players.subtitle': 'ಆಟಗಾರರು ಮತ್ತು ಅವರ ವಿನಂತಿಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
  'agent.people.hub.players.subtab.requests': 'ಆಟಗಾರ ವಿನಂತಿಗಳು',
  'agent.people.hub.players.subtab.list': 'ಆಟಗಾರರು',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'ಏಜೆಂಟ್‌ಗಳು',
  'agent.people.hub.agents.subtitle': 'ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಮತ್ತು ಅವರ ವಿನಂತಿಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
  'agent.people.hub.agents.subtab.requests': 'ಏಜೆಂಟ್ ವಿನಂತಿಗಳು',
  'agent.people.hub.agents.subtab.list': 'ಏಜೆಂಟ್‌ಗಳು',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'ಹೊಸ ಆಟಗಾರರನ್ನು ಸ್ವೀಕರಿಸಲಾಗುತ್ತಿದೆ',
  'agent.people.acceptingToggle.bodyAccepting': 'ನೀವು ಭೌಗೋಳಿಕ ಲೊಕೇಟರ್ ಮೂಲಕ ಹೊಸ ಆಟಗಾರರನ್ನು ಸ್ವೀಕರಿಸುತ್ತಿದ್ದೀರಿ. ನಿಮ್ಮ ಪ್ರದೇಶದಲ್ಲಿ ಕೋಲ್ಡ್ ಸೈನ್‌ಅಪ್‌ಗಳು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ನಿಮ್ಮ ಬಳಿಗೆ ಬರುತ್ತವೆ.',
  'agent.people.acceptingToggle.bodyOptedOut': 'ನೀವು ಆಪ್ಟ್-ಔಟ್ ಮಾಡಿದ್ದೀರಿ. ಲೊಕೇಟರ್ ನಿಮ್ಮನ್ನು ಬಿಟ್ಟುಬಿಡುತ್ತಿದೆ — ಕೇವಲ ನೇರ ರೆಫರಲ್‌ಗಳು ಮತ್ತು ಕೈಯಿಂದ ನಿಯೋಜಿಸಲಾದ ಆಟಗಾರರು ಮಾತ್ರ ನಿಮ್ಮನ್ನು ತಲುಪುತ್ತಾರೆ. ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ಆಟಗಾರರು ಪ್ರಭಾವಿತರಾಗುವುದಿಲ್ಲ.',
  'agent.people.acceptingToggle.error': 'ಅಪ್‌ಡೇಟ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'ಹೊಸ ಆಟಗಾರರನ್ನು ಸ್ವೀಕರಿಸುವುದನ್ನು ನಿಲ್ಲಿಸಿ',
  'agent.people.acceptingToggle.aria.start': 'ಹೊಸ ಆಟಗಾರರನ್ನು ಸ್ವೀಕರಿಸಲು ಪ್ರಾರಂಭಿಸಿ',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'ಆಟಗಾರರು',
  'agent.qr.audience.subAgent': 'ಏಜೆಂಟ್‌ಗಳು',
  'agent.qr.alt': 'ನಿಮ್ಮ ಪಾವತಿ QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'ನನ್ನ QR · ಆಟಗಾರರು ಏನು ನೋಡುತ್ತಾರೆ',
  'agent.qr.cardLabel.subAgent': 'ನನ್ನ QR · ಏಜೆಂಟ್‌ಗಳು ಏನು ನೋಡುತ್ತಾರೆ',
  'agent.qr.upi.empty': 'ಇನ್ನೂ UPI ಹೊಂದಿಸಲಾಗಿಲ್ಲ',
  // Status pill.
  'agent.qr.status.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.qr.status.live': 'ಲೈವ್',
  'agent.qr.status.notSet': 'ಹೊಂದಿಸಿಲ್ಲ',
  'agent.qr.status.paused': 'ವಿರಾಮಗೊಳಿಸಲಾಗಿದೆ',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR ಮತ್ತು UPI ಸಂಪಾದಿಸಿ',
  'agent.qr.cta.setup': 'QR ಹೊಂದಿಸಿ',
  'agent.qr.cta.viewOnly': 'ಕೇವಲ ವೀಕ್ಷಿಸಿ — ಸಂಪಾದಿಸಲು ಅಡ್ಮಿನ್‌ಗೆ ಕೇಳಿ',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR ಮತ್ತು UPI ಸಂಪಾದಿಸಿ',
  'agent.qr.modal.closeAria': 'ಮುಚ್ಚಿ',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR ಅಪ್‌ಲೋಡ್ ಮಾಡಿ',
  'agent.qr.modal.replace': 'QR ಬದಲಾಯಿಸಿ',
  'agent.qr.modal.remove': 'ತೆಗೆದುಹಾಕಿ',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR ಚಿತ್ರವನ್ನು ತೆಗೆದುಹಾಕಬೇಕೆ? ನಿಮ್ಮ ಆಟಗಾರರು ಸಂಪರ್ಕ ಫಾಲ್‌ಬ್ಯಾಕ್ ಅನ್ನು ಮಾತ್ರ ನೋಡುತ್ತಾರೆ.',
  'agent.qr.modal.confirmRemove.subAgent': 'QR ಚಿತ್ರವನ್ನು ತೆಗೆದುಹಾಕಬೇಕೆ? ನಿಮ್ಮ ಏಜೆಂಟ್‌ಗಳು ಸಂಪರ್ಕ ಫಾಲ್‌ಬ್ಯಾಕ್ ಅನ್ನು ಮಾತ್ರ ನೋಡುತ್ತಾರೆ.',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'ಉದಾ. baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'WhatsApp ಸಂಖ್ಯೆ (ಫಾಲ್‌ಬ್ಯಾಕ್)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ಏಜೆಂಟ್‌ಗಳಿಗಾಗಿ WhatsApp ಸಂಖ್ಯೆ',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'ಖಾಲಿ → {number} ಗೆ ಹಿಂತಿರುಗುತ್ತದೆ',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'ಉಪ-ಏಜೆಂಟ್‌ಗಳು {number} ಅನ್ನು ನೋಡುತ್ತಾರೆ (ನಿಮ್ಮ ಆಟಗಾರ-ಮುಖಿ ಸಂಖ್ಯೆ).',
  'agent.qr.modal.field.phone.label.player': 'ಬೆಂಬಲ ಫೋನ್ (ಫಾಲ್‌ಬ್ಯಾಕ್)',
  'agent.qr.modal.field.phone.label.subAgent': 'ಏಜೆಂಟ್‌ಗಳಿಗಾಗಿ ಬೆಂಬಲ ಫೋನ್',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'ಖಾಲಿ → {number} ಗೆ ಹಿಂತಿರುಗುತ್ತದೆ',
  'agent.qr.modal.field.phone.fallbackHint': 'ಉಪ-ಏಜೆಂಟ್‌ಗಳು {number} ಅನ್ನು ನೋಡುತ್ತಾರೆ (ನಿಮ್ಮ ಆಟಗಾರ-ಮುಖಿ ಸಂಖ್ಯೆ).',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'ಸಕ್ರಿಯ',
  'agent.qr.modal.active.description': 'ಆಟಗಾರರು ರೀಚಾರ್ಜ್‌ಗಳನ್ನು ಸಲ್ಲಿಸಬಹುದು',
  'agent.qr.modal.active.on': 'ಆನ್',
  'agent.qr.modal.active.paused': 'ವಿರಾಮಗೊಳಿಸಲಾಗಿದೆ',

  // Footer buttons.
  'agent.qr.modal.cancel': 'ರದ್ದುಗೊಳಿಸಿ',
  'agent.qr.modal.save': 'ಉಳಿಸಿ',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'ಕೈಯಾರೆ ರೀಚಾರ್ಜ್',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'ಹಣವನ್ನು ಕಳುಹಿಸಿ ',
  'agent.manualRecharge.banner.body.target': 'ಆಟಗಾರ',
  'agent.manualRecharge.banner.body.after': ' ಗೆ ನೇರವಾಗಿ ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ.',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'ನಿಮ್ಮ ಪೂಲ್',
  'agent.manualRecharge.pool.dailyCap': 'ಮಿತಿ · {amount}/ದಿನ',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'ಯಾವ ಆಟಗಾರನಿಗೆ',
  'agent.manualRecharge.field.player.placeholder': 'ಒಬ್ಬ ಆಟಗಾರನನ್ನು ಆಯ್ಕೆಮಾಡಿ…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · ಬ್ಯಾಲೆನ್ಸ್ {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'ಯಾವ ಆಟಗಾರನಿಗೆ',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · ಪ್ರಸ್ತುತ ಬ್ಯಾಲೆನ್ಸ್ {balance}',
  'agent.manualRecharge.field.player.notFound': 'ನಿಮ್ಮ ಸ್ವಂತ ಪಟ್ಟಿಯಲ್ಲಿ ಆಟಗಾರ ಸಿಗಲಿಲ್ಲ.',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'ಮೊತ್ತ (₹) — {min} ರಿಂದ {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'ನಿಮ್ಮ ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್‌ಗಿಂತ ಹೆಚ್ಚು.',
  'agent.manualRecharge.error.belowMin': 'ಕನಿಷ್ಠಕ್ಕಿಂತ ಕಡಿಮೆ ({min}).',
  'agent.manualRecharge.error.aboveMax': 'ಗರಿಷ್ಠಕ್ಕಿಂತ ಹೆಚ್ಚು ({max}).',

  // Note field.
  'agent.manualRecharge.field.note.label': 'ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ, ಆಟಗಾರ ಲೆಜರ್‌ನಲ್ಲಿ ತೋರಿಸಲಾಗುತ್ತದೆ)',
  'agent.manualRecharge.field.note.placeholder': 'ಉದಾ. ಅಂಗಡಿಯಲ್ಲಿ ನಗದು ಸಂಗ್ರಹಿಸಲಾಗಿದೆ',

  // Loading + success states.
  'agent.manualRecharge.loading': 'ನಿಮ್ಮ ಪೂಲ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.manualRecharge.cta.cancel': 'ರದ್ದುಗೊಳಿಸಿ',
  'agent.manualRecharge.cta.send': '{amount} ಕಳುಹಿಸಿ',
  'agent.manualRecharge.success.title': '@{username} ಗೆ {amount} ಕಳುಹಿಸಲಾಗಿದೆ',
  'agent.manualRecharge.success.meta': 'ನಿಮ್ಮ ಪೂಲ್ · {pool} · ಅವರ ವಾಲೆಟ್ · {playerBal}',
  'agent.manualRecharge.success.again': 'ಇನ್ನೊಂದು ಕಳುಹಿಸಿ',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'ಕೈಯಾರೆ ರೀಚಾರ್ಜ್',
  'agent.manualSubAgentRecharge.banner.body.before': 'ಹಣವನ್ನು ಕಳುಹಿಸಿ ',
  'agent.manualSubAgentRecharge.banner.body.target': 'ಉಪ-ಏಜೆಂಟ್',
  'agent.manualSubAgentRecharge.banner.body.after': ' ಗೆ ನೇರವಾಗಿ ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ.',

  'agent.manualSubAgentRecharge.pool.label': 'ನಿಮ್ಮ ಪೂಲ್',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'ಯಾವ ಉಪ-ಏಜೆಂಟ್‌ಗೆ',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'ಒಬ್ಬ ಉಪ-ಏಜೆಂಟ್‌ನನ್ನು ಆಯ್ಕೆಮಾಡಿ…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · ಪೂಲ್ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'ಯಾವುದೇ ಸಕ್ರಿಯ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'ಯಾವ ಉಪ-ಏಜೆಂಟ್‌ಗೆ',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · ಪ್ರಸ್ತುತ ಪೂಲ್ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'ನಿಮ್ಮ ನೇರ ಡೌನ್-ಲೈನ್‌ನಲ್ಲಿ ಉಪ-ಏಜೆಂಟ್ ಸಿಗಲಿಲ್ಲ.',

  'agent.manualSubAgentRecharge.field.amount.label': 'ಮೊತ್ತ (₹) — {min} ರಿಂದ {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'ನಿಮ್ಮ ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್‌ಗಿಂತ ಹೆಚ್ಚಾಗಿದೆ.',
  'agent.manualSubAgentRecharge.error.belowMin': 'ಕನಿಷ್ಠಕ್ಕಿಂತ ಕಡಿಮೆ ({min}).',

  'agent.manualSubAgentRecharge.field.note.label': 'ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ, ಎರಡೂ ಲೆಜರ್‌ಗಳಲ್ಲಿ ತೋರಿಸಲಾಗುತ್ತದೆ)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'ಉದಾ. ಫ್ಲೋಟ್ ಮುಂಗಡ',

  'agent.manualSubAgentRecharge.loading': 'ನಿಮ್ಮ ಪೂಲ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.manualSubAgentRecharge.cta.cancel': 'ರದ್ದುಮಾಡು',
  'agent.manualSubAgentRecharge.cta.send': '{amount} ಕಳುಹಿಸಿ',
  'agent.manualSubAgentRecharge.success.title': '@{username} ಗೆ {amount} ಕಳುಹಿಸಲಾಗಿದೆ',
  'agent.manualSubAgentRecharge.success.meta': 'ನಿಮ್ಮ ಪೂಲ್ · {parent} · ಅವರ ಪೂಲ್ · {child}',
  'agent.manualSubAgentRecharge.success.again': 'ಇನ್ನೊಂದು ಕಳುಹಿಸಿ',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'ರೀಚಾರ್ಜ್',
  'agent.playerRequests.direction.withdrawal': 'ಹಿಂಪಡೆಯುವಿಕೆ',
  'agent.playerRequests.tab.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.playerRequests.tab.history': 'ಇತಿಹಾಸ',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.playerRequests.empty.pending.recharge': 'ಈಗ ಯಾವುದೇ ಬಾಕಿಯಿರುವ ರೀಚಾರ್ಜ್‌ಗಳಿಲ್ಲ.',
  'agent.playerRequests.empty.pending.withdrawal': 'ಈಗ ಯಾವುದೇ ಬಾಕಿಯಿರುವ ಹಿಂಪಡೆಯುವಿಕೆಗಳಿಲ್ಲ.',
  'agent.playerRequests.empty.history': 'ಇನ್ನೂ ಯಾವುದೇ ಇತಿಹಾಸವಿಲ್ಲ.',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'ತಿರಸ್ಕಾರದ ಕಾರಣ (ಐಚ್ಛಿಕ):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'ತಿರಸ್ಕಾರದ ಕಾರಣ (ಆಟಗಾರನ ತಡೆಹಿಡಿದ ಮೊತ್ತವನ್ನು ಮರಳಿಸಲಾಗುತ್ತದೆ):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.playerRequests.toast.rejectedRefunded': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ · ತಡೆಹಿಡಿದ ಬ್ಯಾಲೆನ್ಸ್ ಮರಳಿಸಲಾಗಿದೆ',
  'agent.playerRequests.toast.approved': '{amount} ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} ಅನುಮೋದಿಸಲಾಗಿದೆ · ಮೂಲ + ₹{bonus} ಬೋನಸ್',
  'agent.playerRequests.toast.markedPaid': '{amount} ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸಲಾಗಿದೆ',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel': 'ಇವರಿಗೆ ಪಾವತಿಸಿ (UPI)',
  'agent.playerRequests.card.whatsapp': 'WhatsApp',
  'agent.playerRequests.card.viewQr': 'QR ನೋಡಿ',
  'agent.playerRequests.card.bonusChip': 'ಬೋನಸ್',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'ವಿನಂತಿಸಿದ {requested} ರಲ್ಲಿ {approved} ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.playerRequests.card.partialPaid': 'ವಿನಂತಿಸಿದ {requested} ರಲ್ಲಿ {approved} ಪಾವತಿಸಲಾಗಿದೆ · {refund} ಮರಳಿಸಲಾಗಿದೆ',
  'agent.playerRequests.card.paidTxnPrefix': 'ಪಾವತಿಸಲಾಗಿದೆ · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'ತಿರಸ್ಕರಿಸು',
  'agent.playerRequests.action.approve': 'ಅನುಮೋದನೆ',
  'agent.playerRequests.action.markPaid': 'ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸು',
  'agent.playerRequests.state.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.playerRequests.state.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.playerRequests.state.expired': 'ಅವಧಿ ಮೀರಿದೆ',
  'agent.playerRequests.state.accepted': 'ಸ್ವೀಕರಿಸಲಾಗಿದೆ',
  'agent.playerRequests.state.manuallyPaid': 'ಕೈಯಾರೆ ಪಾವತಿಸಲಾಗಿದೆ',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': '{total} ರಲ್ಲಿ {from}–{to}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'ರೀಚಾರ್ಜ್',
  'agent.agentRequests.direction.withdrawal': 'ಹಿಂಪಡೆಯುವಿಕೆ',
  'agent.agentRequests.tab.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.agentRequests.tab.history': 'ಇತಿಹಾಸ',
  'agent.agentRequests.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.agentRequests.empty.pending.recharge': 'ಈಗ ಯಾವುದೇ ಬಾಕಿಯಿರುವ ರೀಚಾರ್ಜ್‌ಗಳಿಲ್ಲ.',
  'agent.agentRequests.empty.pending.withdrawal': 'ಈಗ ಯಾವುದೇ ಬಾಕಿಯಿರುವ ಹಿಂಪಡೆಯುವಿಕೆಗಳಿಲ್ಲ.',
  'agent.agentRequests.empty.history': 'ಇನ್ನೂ ಯಾವುದೇ ಇತಿಹಾಸವಿಲ್ಲ.',

  'agent.agentRequests.prompt.rejectRecharge': 'ತಿರಸ್ಕಾರದ ಕಾರಣ (ಐಚ್ಛಿಕ):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'ತಿರಸ್ಕಾರದ ಕಾರಣ (ಉಪ-ಏಜೆಂಟ್‌ನ ತಡೆಹಿಡಿದ ಮೊತ್ತವನ್ನು ಮರಳಿಸಲಾಗುತ್ತದೆ):',

  'agent.agentRequests.toast.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.agentRequests.toast.rejectedRefunded': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ · ತಡೆಹಿಡಿದ ಬ್ಯಾಲೆನ್ಸ್ ಮರಳಿಸಲಾಗಿದೆ',
  'agent.agentRequests.toast.approved': '{amount} ಅನುಮೋದಿಸಲಾಗಿದೆ · ಪೂಲ್‌ಗೆ ಕ್ರೆಡಿಟ್ ಮಾಡಲಾಗಿದೆ',
  'agent.agentRequests.toast.markedPaid': '{amount} ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸಲಾಗಿದೆ',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'ಏಜೆಂಟ್',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'ನಗದು ಮಾರ್ಕರ್',
  'agent.agentRequests.card.payToUpiLabel': 'ಇವರಿಗೆ ಪಾವತಿಸಿ (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'ಹಸ್ತಾಂತರ ಮಾರ್ಕರ್',
  'agent.agentRequests.card.whatsapp': 'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved': 'ವಿನಂತಿಸಿದ {requested} ರಲ್ಲಿ {approved} ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.agentRequests.card.partialPaid': 'ವಿನಂತಿಸಿದ {requested} ರಲ್ಲಿ {approved} ಪಾವತಿಸಲಾಗಿದೆ · {refund} ಮರಳಿಸಲಾಗಿದೆ',
  'agent.agentRequests.card.paidTxnPrefix': 'ಪಾವತಿಸಲಾಗಿದೆ · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'ಠೇವಣಿ',
  'agent.agentRequests.card.chip.cash': 'ನಗದು',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'ತಿರಸ್ಕರಿಸು',
  'agent.agentRequests.action.approve': 'ಅನುಮೋದನೆ',
  'agent.agentRequests.action.markPaid': 'ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸು',
  'agent.agentRequests.state.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.agentRequests.state.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.agentRequests.state.expired': 'ಅವಧಿ ಮೀರಿದೆ',
  'agent.agentRequests.state.accepted': 'ಸ್ವೀಕರಿಸಲಾಗಿದೆ',
  'agent.agentRequests.state.manuallyPaid': 'ಕೈಯಾರೆ ಪಾವತಿಸಲಾಗಿದೆ',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'ಆಟಗಾರನನ್ನು ಸೇರಿಸಿ',
  'agent.playersList.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.playersList.empty': 'ಯಾವುದೇ ಆಟಗಾರರು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ',
  'agent.playersList.results': '{total} ರಲ್ಲಿ {from}–{to} ತೋರಿಸಲಾಗುತ್ತಿದೆ',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'ಬಳಕೆದಾರಹೆಸರು, ಇಮೇಲ್, ಫೋನ್ ಹುಡುಕಿ…',

  // Status filter pills.
  'agent.playersList.filter.all': 'ಎಲ್ಲಾ',
  'agent.playersList.filter.active': 'ಸಕ್ರಿಯ',
  'agent.playersList.filter.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.playersList.filter.suspended': 'ಅಮಾನತುಗೊಳಿಸಲಾಗಿದೆ',
  'agent.playersList.filter.banned': 'ನಿಷೇಧಿಸಲಾಗಿದೆ',

  // Empty-results panel.
  'agent.playersList.empty.title': 'ಯಾವುದೇ ಆಟಗಾರರು ಸಿಗಲಿಲ್ಲ',
  'agent.playersList.empty.body': 'ಬೇರೆ ಹುಡುಕಾಟ ಅಥವಾ ಸ್ಥಿತಿ ಫಿಲ್ಟರ್ ಪ್ರಯತ್ನಿಸಿ.',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'ಪುಟ {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'ಸಕ್ರಿಯ',
  'agent.playersList.card.status.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.playersList.card.status.suspended': 'ಅಮಾನತುಗೊಳಿಸಲಾಗಿದೆ',
  'agent.playersList.card.status.banned': 'ನಿಷೇಧಿಸಲಾಗಿದೆ',
  'agent.playersList.card.verifiedBadge': 'ಪರಿಶೀಲಿಸಲಾಗಿದೆ',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'ವಾಲೆಟ್',
  'agent.playersList.column.joined': 'ಸೇರಿದ ದಿನಾಂಕ',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'ನೀವು ರಚಿಸುವ ಏಜೆಂಟ್‌ಗಳು ನಿಮ್ಮ ಅಡಿಯಲ್ಲಿ ಕೆಲಸ ಮಾಡುತ್ತಾರೆ. ಅವರು ಪ್ರತಿ ಪಿಲ್ಲರ್‌ನಲ್ಲಿ 0% ನಿಂದ ಪ್ರಾರಂಭಿಸುತ್ತಾರೆ — ಅವರ ವಿವರ ಪುಟದಿಂದ ಪ್ರತಿ ಗೇಮ್‌ಗೆ ದರಗಳನ್ನು ನೀಡಿ.',
  'agent.subAgentsList.cta.new': 'ಹೊಸ ಉಪ-ಏಜೆಂಟ್',
  'agent.subAgentsList.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'ಇನ್ನೂ ಯಾವುದೇ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ',
  'agent.subAgentsList.empty.body.canCreate': 'ಒಂದನ್ನು ರಚಿಸಲು "+ ಹೊಸ ಉಪ-ಏಜೆಂಟ್" ಮೇಲೆ ಟ್ಯಾಪ್ ಮಾಡಿ. ಅವರು ತಮ್ಮ ಸ್ವಂತ ಪೂಲ್, ಆಟಗಾರರು ಮತ್ತು ಗಳಿಕೆಯೊಂದಿಗೆ ಇಲ್ಲಿ ಕಾಣಿಸಿಕೊಳ್ಳುತ್ತಾರೆ — ನಿಮ್ಮಿಂದ ನಿರ್ವಹಿಸಲ್ಪಡುತ್ತಾರೆ.',
  'agent.subAgentsList.empty.body.cantCreate': 'ನಿಮ್ಮ ಅಡ್ಮಿನ್ ನಿಮಗೆ ಇನ್ನೂ ಉಪ-ಏಜೆಂಟ್‌ಗಳನ್ನು ರಚಿಸುವ ಹಕ್ಕನ್ನು ನೀಡಿಲ್ಲ. ನಿಮ್ಮ ಅನುಮತಿಗಳಲ್ಲಿ create_subagent ಅನ್ನು "edit" ಗೆ ಹೊಂದಿಸಲು ಅವರನ್ನು ಕೇಳಿ.',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'ಅಮಾನತುಗೊಂಡಿದೆ',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'ಏಜೆಂಟ್‌ಗಳು',
  'agent.subAgentsList.column.players': 'ಆಟಗಾರರು',
  'agent.subAgentsList.column.pool': 'ಪೂಲ್',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'ನೇರ',
  'agent.subAgentsList.row.indirectLabel': 'ಪರೋಕ್ಷ',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'ಹೊಸ ಉಪ-ಏಜೆಂಟ್',
  'agent.subAgentsList.modal.body': 'ಅವರು ಪ್ರತಿ ಪಿಲ್ಲರ್‌ನಲ್ಲಿ 0% ನಿಂದ ಪ್ರಾರಂಭಿಸುತ್ತಾರೆ. ರಚಿಸಿದ ನಂತರ ಅವರ ವಿವರಗಳ ಪುಟದಿಂದ ಪ್ರತಿ-ಗೇಮ್ ದರಗಳನ್ನು ನೀಡಿ.',
  'agent.subAgentsList.modal.field.username.label': 'ಯೂಸರ್‌ನೇಮ್',
  'agent.subAgentsList.modal.field.username.placeholder': 'ಅಕ್ಷರ-ಅಂಕಿ, ._- ಅನುಮತಿಸಲಾಗಿದೆ; ಕನಿಷ್ಠ 3 ಅಕ್ಷರಗಳು',
  'agent.subAgentsList.modal.field.email.label': 'ಇಮೇಲ್',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'ಆರಂಭಿಕ ಪಾಸ್‌ವರ್ಡ್',
  'agent.subAgentsList.modal.field.password.placeholder': 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು',
  'agent.subAgentsList.modal.field.password.hint': 'ಹೊಸ ಏಜೆಂಟ್ ಲಾಗಿನ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗುವಂತೆ ಇದನ್ನು ಅವರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಿ. ಅವರು ಮೊದಲ ಲಾಗಿನ್‌ನಲ್ಲಿ ಇದನ್ನು ಬದಲಾಯಿಸಬೇಕು.',
  'agent.subAgentsList.modal.field.location.label': 'ಉಪ-ಏಜೆಂಟ್‌ನ ಸ್ಥಳ',
  'agent.subAgentsList.modal.field.location.optional': '(ಬಲವಾಗಿ ಶಿಫಾರಸು ಮಾಡಲಾಗಿದೆ)',
  'agent.subAgentsList.modal.field.location.missing': 'ಸ್ಥಳವಿಲ್ಲದೆ ಭೌಗೋಳಿಕ ಲೊಕೇಟರ್ ಈ ಉಪ-ಏಜೆಂಟ್‌ಗೆ ಯಾವುದೇ ಹೊಸ ಆಟಗಾರರನ್ನು ಕಳುಹಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ. ಅವರು ಸೈನ್ ಇನ್ ಆಗಿ ತಮ್ಮ GPS ಪೂರ್ಣಗೊಳಿಸುವವರೆಗೆ ಕೇವಲ ನೇರ ರೆಫರಲ್‌ಗಳಿಂದ ಮಾತ್ರ ಆಟಗಾರರನ್ನು ಪಡೆಯುತ್ತಾರೆ.',
  'agent.subAgentsList.modal.cta.cancel': 'ರದ್ದುಮಾಡಿ',
  'agent.subAgentsList.modal.cta.create': 'ಉಪ-ಏಜೆಂಟ್ ರಚಿಸಿ',
  'agent.subAgentsList.modal.cta.creating': 'ರಚಿಸಲಾಗುತ್ತಿದೆ…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who} ರಿಂದ ರೀಚಾರ್ಜ್ ಅನುಮೋದನೆ ಮಾಡಿ',
  'agent.approvalEdit.title.withdrawal': '{who} ರ ಹಿಂಪಡೆಯುವಿಕೆಗೆ ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಿ',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'ವಿನಂತಿಸಲಾಗಿದೆ:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'ಅನುಮೋದಿಸುವ ಮೊತ್ತ (₹)',
  'agent.approvalEdit.field.amount.hint': 'ವಿನಂತಿಸಿದ {amount} ಮೀರಲು ಸಾಧ್ಯವಿಲ್ಲ.',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} ಅನ್ನು ಸ್ವಯಂಚಾಲಿತವಾಗಿ {who} ರ ವಾಲೆಟ್‌ಗೆ ಮರುಪಾವತಿ ಮಾಡಲಾಗುತ್ತದೆ.',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'ಭಾಗಶಃ ಅನುಮೋದನೆ. ಲಗತ್ತಿಸಿದ ಯಾವುದೇ ಬೋನಸ್ ಸ್ವಯಂ-ತಿರಸ್ಕರಿಸಲ್ಪಡುತ್ತದೆ.',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'ಕಾರಣ',
  'agent.approvalEdit.field.reason.requiredHint': '(ಅಗತ್ಯವಿದೆ, ≥5 ಅಕ್ಷರಗಳು)',
  'agent.approvalEdit.field.reason.optionalHint': '(ಐಚ್ಛಿಕ)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'ಉದಾ. ಆಟಗಾರ ಕೇವಲ 30 ರೂಪಾಯಿ ಮಾತ್ರ ಪಾವತಿಸಿದ್ದಾರೆ',
  'agent.approvalEdit.field.reason.placeholder.optional': 'ಐಚ್ಛಿಕ ಟಿಪ್ಪಣಿ',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI ವಹಿವಾಟು ID',
  'agent.approvalEdit.field.txn.placeholder': 'ಉದಾ. 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'ನಿಮ್ಮ ಪೂಲ್ ({balance}) ಈ ಅನುಮೋದನೆಯನ್ನು ಕವರ್ ಮಾಡಲು ಸಾಧ್ಯವಿಲ್ಲ. ಮೊದಲು ಟಾಪ್ ಅಪ್ ಮಾಡಿ.',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'ಅನುಮೋದಿಸಿದ ಮೊತ್ತ ಕನಿಷ್ಠ ₹1 ಆಗಿರಬೇಕು',
  'agent.approvalEdit.error.amountMax': 'ಅನುಮೋದಿಸಿದ ಮೊತ್ತ ವಿನಂತಿಸಿದ {amount} ಮೀರಲು ಸಾಧ್ಯವಿಲ್ಲ',
  'agent.approvalEdit.error.reasonRequired': 'ಬೇರೆ ಮೊತ್ತವನ್ನು ಅನುಮೋದಿಸುವಾಗ ಕಾರಣ (≥5 ಅಕ್ಷರಗಳು) ಅಗತ್ಯವಿದೆ',
  'agent.approvalEdit.error.txnRequired': 'UPI ವಹಿವಾಟು ID ಅಗತ್ಯವಿದೆ (≥6 ಅಕ್ಷರಗಳು)',
  'agent.approvalEdit.error.fallback': 'ವಿಫಲವಾಗಿದೆ — ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'ರದ್ದುಮಾಡಿ',
  'agent.approvalEdit.cta.approve': 'ಅನುಮೋದಿಸಿ',
  'agent.approvalEdit.cta.markPaid': 'ಪಾವತಿಸಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಿ',
  'agent.approvalEdit.cta.submitting': 'ಸಲ್ಲಿಸಲಾಗುತ್ತಿದೆ…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'ನನ್ನ ಖಾತೆ',
  'agent.profile.hub.subtitle': 'ಗುರುತು, ವಾಲೆಟ್ ಮತ್ತು ಠೇವಣಿ ಒಂದೇ ಸ್ಥಳದಲ್ಲಿ',
  'agent.profile.hub.tab.wallet': 'ವಾಲೆಟ್',
  'agent.profile.hub.tab.deposit': 'ಠೇವಣಿ',
  'agent.profile.hub.tab.account': 'ಖಾತೆ',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'ಸೈನ್ ಔಟ್',
  'agent.profile.signOut.modal.title': 'ಸೈನ್ ಔಟ್?',
  'agent.profile.signOut.modal.body': 'ನಿಮ್ಮ ಖಾತೆಯನ್ನು ಪ್ರವೇಶಿಸಲು ನೀವು ಮತ್ತೆ ಸೈನ್ ಇನ್ ಮಾಡಬೇಕಾಗುತ್ತದೆ.',
  'agent.profile.signOut.modal.stay': 'ಸೈನ್ ಇನ್ ಆಗಿರಿ',
  'agent.profile.signOut.modal.confirm': 'ಹೌದು, ಸೈನ್ ಔಟ್',
  'agent.profile.signOut.modal.busy': 'ಸೈನ್ ಔಟ್ ಆಗುತ್ತಿದೆ…',
  'agent.profile.signOut.error': 'ಸೈನ್ ಔಟ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'ಸೈನ್ ಇನ್ ಆಗಿಲ್ಲ.',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'ಏಜೆಂಟ್',
  'agent.account.hero.role.admin': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಅಡ್ಮಿನ್',
  'agent.account.hero.changePhoto': 'ಫೋಟೋ ಬದಲಾಯಿಸಿ',
  'agent.account.hero.removePhoto': 'ಫೋಟೋ ತೆಗೆದುಹಾಕಿ',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'ನಿಮ್ಮ ಫೋಟೋವನ್ನು ಹೊಂದಿಸಿ',
  'agent.account.avatar.confirmRemove': 'ನಿಮ್ಮ ಪ್ರೊಫೈಲ್ ಫೋಟೋವನ್ನು ತೆಗೆದುಹಾಕಬೇಕೆ?',
  'agent.account.toast.photoUpdated': 'ಫೋಟೋ ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ',
  'agent.account.toast.photoRemoved': 'ಫೋಟೋ ತೆಗೆದುಹಾಕಲಾಗಿದೆ',
  'agent.account.toast.saved': 'ಉಳಿಸಲಾಗಿದೆ',
  'agent.account.toast.locationSaved': 'ಸ್ಥಳ ಉಳಿಸಲಾಗಿದೆ',
  'agent.account.toast.passwordChanged': 'ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಲಾಗಿದೆ — ಮತ್ತೆ ಸೈನ್ ಇನ್ ಮಾಡಿ',
  'agent.account.error.uploadFailed': 'ಅಪ್‌ಲೋಡ್ ವಿಫಲವಾಗಿದೆ',
  'agent.account.error.removeFailed': 'ವಿಫಲವಾಗಿದೆ',
  'agent.account.error.saveFailed': 'ಉಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
  'agent.account.error.changeFailed': 'ಬದಲಾಯಿಸಲು ವಿಫಲವಾಗಿದೆ',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'ಖಾತೆ ಮಾಹಿತಿ',
  'agent.account.info.subtitle': 'ಕೇವಲ-ಓದಲು ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಮೆಟಾಡೇಟಾ',
  'agent.account.info.memberSince': 'ಸದಸ್ಯರಾಗಿರುವ ದಿನಾಂಕ',
  'agent.account.info.lastLogin': 'ಕೊನೆಯ ಲಾಗಿನ್',
  'agent.account.info.lastLoginIp': 'ಕೊನೆಯ ಲಾಗಿನ್ IP',
  'agent.account.info.reportsTo': 'ಯಾರಿಗೆ ವರದಿ ಮಾಡುತ್ತಾರೆ',
  'agent.account.info.reportsToValue': 'ಅಡ್ಮಿನ್ #{id}',
  'agent.account.info.agentId': 'ಏಜೆಂಟ್ ID',
  'agent.account.info.firstLogin': 'ಮೊದಲ ಬಾರಿ',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'ವೈಯಕ್ತಿಕ ವಿವರಗಳು',
  'agent.account.personal.subtitle.locked': 'ಅಡ್ಮಿನ್ ಗುರುತನ್ನು ಲಾಕ್ ಮಾಡಿದ್ದಾರೆ · ಕೇವಲ-ವೀಕ್ಷಣೆ',
  'agent.account.personal.subtitle.agent': 'ಯೂಸರ್‌ನೇಮ್, ಇಮೇಲ್ ಮತ್ತು ಸಂಪರ್ಕ ಸಂಖ್ಯೆ',
  'agent.account.personal.subtitle.admin': 'ಯೂಸರ್‌ನೇಮ್ ಮತ್ತು ಇಮೇಲ್',
  'agent.account.personal.field.username': 'ಯೂಸರ್‌ನೇಮ್',
  'agent.account.personal.field.email': 'ಇಮೇಲ್',
  'agent.account.personal.field.phone': 'ಸಂಪರ್ಕ ಸಂಖ್ಯೆ',
  'agent.account.personal.field.phone.placeholder': 'ಉದಾ. +91 9876543210',
  'agent.account.personal.field.phone.hint': 'ಆಟಗಾರರು ಮತ್ತು ಅಡ್ಮಿನ್ ಈ ಸಂಖ್ಯೆಯಲ್ಲಿ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತಾರೆ. ಖಾಲಿ ಮಾಡಲು ಬಿಟ್ಟುಬಿಡಿ.',
  'agent.account.personal.fieldLockedNote': 'ಲಾಕ್',
  'agent.account.personal.cta.save': 'ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ',
  'agent.account.personal.cta.saving': 'ಉಳಿಸಲಾಗುತ್ತಿದೆ…',
  'agent.account.personal.cta.noChanges': 'ಯಾವುದೇ ಬದಲಾವಣೆಗಳಿಲ್ಲ',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'ನಿಮ್ಮ ಸ್ಥಳ',
  'agent.account.location.subtitle.summary': 'ಲೊಕೇಟರ್ ಹೊಸ ಆಟಗಾರರನ್ನು ನಿಮ್ಮ ಸಮೀಪದ ಪ್ರದೇಶಕ್ಕೆ ಕಳುಹಿಸುತ್ತದೆ',
  'agent.account.location.subtitle.edit': 'ನಿಮ್ಮ ಪ್ರದೇಶವನ್ನು ಅಪ್‌ಡೇಟ್ ಮಾಡಿ ಇದರಿಂದ ಲೊಕೇಟರ್ ಸರಿಯಾದ ಆಟಗಾರರನ್ನು ನಿಮಗೆ ಕಳುಹಿಸುತ್ತದೆ',
  'agent.account.location.cta.edit': 'ಸಂಪಾದಿಸಿ',
  'agent.account.location.cta.cancel': 'ರದ್ದುಮಾಡಿ',
  'agent.account.location.cta.save': 'ಉಳಿಸಿ',
  'agent.account.location.areaLabel': 'ಪ್ರದೇಶ',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'ಸ್ಥಳ ಸೆಟ್ ಆಗಿದೆ',
  'agent.account.location.setBody': 'ಫೈಲ್‌ನಲ್ಲಿ GPS ನಿರ್ದೇಶಾಂಕಗಳಿವೆ — ಪ್ರದೇಶದ ವಿವರಗಳನ್ನು ನೋಡಲು ಎಡಿಟ್ ಟ್ಯಾಪ್ ಮಾಡಿ.',
  'agent.account.location.lastUpdated': 'ಕೊನೆಯ ಅಪ್‌ಡೇಟ್ · {date}',

  // Security card (password change).
  'agent.account.security.title': 'ಭದ್ರತೆ',
  'agent.account.security.subtitle.locked': 'ಅಡ್ಮಿನ್ ಲಾಕ್ ಮಾಡಿದ್ದಾರೆ · ನೋಡಲು ಮಾತ್ರ',
  'agent.account.security.subtitle.unlocked': 'ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಿ · ಇತರ ಡಿವೈಸ್‌ಗಳಿಂದ ಸೈನ್ ಔಟ್ ಆಗುತ್ತದೆ',
  'agent.account.security.field.current': 'ಪ್ರಸ್ತುತ ಪಾಸ್‌ವರ್ಡ್',
  'agent.account.security.field.new': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್',
  'agent.account.security.field.new.hint': 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು, ಒಂದು ಅಕ್ಷರ ಮತ್ತು ಒಂದು ಅಂಕಿಯೊಂದಿಗೆ',
  'agent.account.security.field.confirm': 'ಹೊಸದನ್ನು ಖಚಿತಪಡಿಸಿ',
  'agent.account.security.cta.update': 'ಪಾಸ್‌ವರ್ಡ್ ಅಪ್‌ಡೇಟ್ ಮಾಡಿ',
  'agent.account.security.cta.updating': 'ಅಪ್‌ಡೇಟ್ ಆಗುತ್ತಿದೆ…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಾಣಿಕೆಯಾಗುತ್ತಿಲ್ಲ',
  'agent.account.security.error.tooShort': 'ಪಾಸ್‌ವರ್ಡ್ ≥ 8 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು',
  'agent.account.security.error.weak': 'ಪಾಸ್‌ವರ್ಡ್‌ನಲ್ಲಿ ಕನಿಷ್ಠ ಒಂದು ಅಕ್ಷರ ಮತ್ತು ಒಂದು ಸಂಖ್ಯೆ ಇರಬೇಕು',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'ಹಂಚಿಕೊಳ್ಳಬಹುದಾದ ಏಜೆಂಟ್ ID',
  'agent.shareableId.tooltip.copy': 'UAID ನಕಲಿಸಿ',
  'agent.shareableId.tooltip.copied': 'UAID ನಕಲಿಸಲಾಗಿದೆ',
  'agent.shareableId.cta.copy': 'ನಕಲಿಸಿ',
  'agent.shareableId.cta.copied': 'ನಕಲಿಸಲಾಗಿದೆ!',
  'agent.shareableId.body.before': 'ಬೇರೆ ಏಜೆಂಟ್‌ಗೆ ಈ ಕೋಡ್ ಹಂಚಿಕೊಳ್ಳಿ ಇದರಿಂದ ಅವರು ಆಟಗಾರರನ್ನು ನಿಮಗೆ ವರ್ಗಾಯಿಸಬಹುದು. ಚಾಟ್ ಅಥವಾ WhatsApp ಮೂಲಕ ಕಳುಹಿಸುವುದು ಸುರಕ್ಷಿತ — ಇದನ್ನು ಹಂಚಿಕೊಳ್ಳುವುದರಿಂದ ನಿಮ್ಮ ಖಾತೆಗೆ ಯಾವುದೇ ಪ್ರವೇಶವನ್ನು ',
  'agent.shareableId.body.not': 'ಇಲ್ಲ',
  'agent.shareableId.body.after': ' ನೀಡುವುದಿಲ್ಲ.',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'ಬಹುಮಾನಗಳು',
  'agent.rewards.hub.subtitle': 'ಆಫರ್‌ಗಳನ್ನು ಬ್ರೌಸ್ ಮಾಡಿ ಮತ್ತು ನಿಮ್ಮ ಎಕ್ಸ್‌ಪೋಷರ್ ಟ್ರ್ಯಾಕ್ ಮಾಡಿ',
  'agent.rewards.hub.tab.browse': 'ಆಫರ್‌ಗಳನ್ನು ಬ್ರೌಸ್ ಮಾಡಿ',
  'agent.rewards.hub.tab.exposure': 'ನನ್ನ ಎಕ್ಸ್‌ಪೋಷರ್',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'ಆಪ್ಟ್ ಇನ್ ಆಗಿದೆ',
  'agent.rewards.status.optedOut': 'ಆಪ್ಟ್ ಔಟ್ ಆಗಿದೆ',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'ನಿಮ್ಮ ಆಟಗಾರರಿಗೆ ವಿಶೇಷ',
  'agent.rewards.browse.empty.title': 'ಸದ್ಯ ಯಾವುದೇ ಸಕ್ರಿಯ ಬಹುಮಾನಗಳಿಲ್ಲ',
  'agent.rewards.browse.empty.body': 'ಅಡ್ಮಿನ್‌ಗಳು ನಿಯಮಿತವಾಗಿ ಹೊಸ ಆಫರ್‌ಗಳನ್ನು ಪೋಸ್ಟ್ ಮಾಡುತ್ತಾರೆ. ಸ್ವಲ್ಪ ಸಮಯದ ನಂತರ ಮತ್ತೆ ಪರಿಶೀಲಿಸಿ — ಅಥವಾ ತಪ್ಪಿಸಿಕೊಂಡಿರುವದರಲ್ಲಿ ನಿಮ್ಮನ್ನು ಆಪ್ಟ್ ಇನ್ ಮಾಡಲು ನಿಮ್ಮ ಅಪ್-ಲೈನ್‌ಗೆ ಕೇಳಿ.',
  'agent.rewards.browse.error.loadFailed': 'ಬಹುಮಾನಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ',
  'agent.rewards.browse.card.aria.manage': '{title} ಗಾಗಿ ಆಪ್ಟ್-ಇನ್ ನಿರ್ವಹಿಸಿ',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'ಆಪ್ಟ್ ಇನ್ ಆಗಿದೆ',
  'agent.rewards.browse.chip.optInPrompt': 'ಆಪ್ಟ್ ಇನ್ ಮಾಡಲು ಟ್ಯಾಪ್ ಮಾಡಿ',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'ನಿಮ್ಮ ಎಕ್ಸ್‌ಪೋಷರ್ ಲೆಕ್ಕ ಹಾಕಲಾಗುತ್ತಿದೆ…',
  'agent.rewards.exposure.error.loadFailed': 'ಎಕ್ಸ್‌ಪೋಷರ್ ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'ಆಫರ್ ಎಕ್ಸ್‌ಪೋಷರ್',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} ಸಕ್ರಿಯ ಆಪ್ಟ್-ಇನ್',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} ಸಕ್ರಿಯ ಆಪ್ಟ್-ಇನ್‌ಗಳು',

  'agent.rewards.exposure.hero.actualSpend.label': 'ವಾಸ್ತವಿಕ ಖರ್ಚು',
  'agent.rewards.exposure.hero.actualSpend.helper': 'ಇಲ್ಲಿಯವರೆಗೆ ನಿಮ್ಮ ಡೌನ್-ಲೈನ್ ಮೂಲಕ ಕ್ಲೇಮ್ ಮಾಡಲಾಗಿದೆ.',

  'agent.rewards.exposure.hero.maxExposure.label': 'ಗರಿಷ್ಠ ಎಕ್ಸ್‌ಪೋಷರ್',
  'agent.rewards.exposure.hero.maxExposure.helper': 'ಕೆಟ್ಟ ಪ್ರಕರಣದ ಮಿತಿ — ಉಳಿದ ಪ್ರತಿ ಬಜೆಟ್ ರೂಪಾಯಿಯನ್ನು ಕ್ಲೇಮ್ ಮಾಡಲಾಗಿದೆ.',

  'agent.rewards.exposure.hero.ceiling.label': 'ಬಳಸಿದ ಮಿತಿ',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'ರಿಸ್ಕ್ ಗೇಜ್',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} ಗರಿಷ್ಠ',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': 'ನಲ್ಲಿ ',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': ', ನಿಮ್ಮ ಆಪ್ಟ್-ಇನ್‌ಗಳನ್ನು ಗಮನಿಸಿ',
  'agent.rewards.exposure.gauge.legend.rose.before': 'ನಲ್ಲಿ ',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': ', ನೀವು ನಿಮ್ಮ ಮಿತಿಗೆ ಸಮೀಪದಲ್ಲಿದ್ದೀರಿ',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'ನಿಮ್ಮ ಬಳಿ ಯಾವುದೇ ಅಂದಾಜು ಮಿತಿ ಹೊಂದಿರುವ ಯಾವುದರಲ್ಲಿಯೂ ಸದ್ಯ ಆಪ್ಟ್ ಇನ್ ಆಗಿಲ್ಲ.',
  'agent.rewards.exposure.gauge.caption.high': 'ನೀವು ನಿಮ್ಮ ಗರಿಷ್ಠ ಎಕ್ಸ್‌ಪೋಷರ್‌ನ {pct}% ಖರ್ಚು ಮಾಡಿದ್ದೀರಿ. ಹೆಚ್ಚಿನ ಆಪ್ಟ್-ಇನ್‌ಗಳನ್ನು ಭಾರೀ ಪ್ರಮಾಣದಲ್ಲಿ ಕ್ಲೇಮ್ ಮಾಡಲಾಗಿದೆ.',
  'agent.rewards.exposure.gauge.caption.mid': 'ನೀವು ನಿಮ್ಮ ಗರಿಷ್ಠ ಮಿತಿಯ {pct}% ನಲ್ಲಿದ್ದೀರಿ. ಪ್ರತಿ-ಆಫರ್ ವಿವರಗಳ ಮೇಲೆ ಗಮನವಿಡಿ.',
  'agent.rewards.exposure.gauge.caption.low': 'ಆರಾಮದಾಯಕ — ಇಲ್ಲಿಯವರೆಗೆ ನಿಮ್ಮ ಗರಿಷ್ಠ ಮಿತಿಯ ಕೇವಲ {pct}% ಬಳಸಲಾಗಿದೆ.',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'ಗರಿಷ್ಠ ಎಕ್ಸ್‌ಪೋಷರ್ ಅನ್ನು ಹೇಗೆ ಲೆಕ್ಕ ಹಾಕಲಾಗುತ್ತದೆ:',
  'agent.rewards.exposure.footnote.prefix': ' ಪ್ರತಿ ಆಪ್ಟ್-ಇನ್ ಆಫರ್‌ಗಾಗಿ: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — ಸಂರಕ್ಷಣಾತ್ಮಕ ಮೇಲಿನ ಮಿತಿ. ನಿಮ್ಮ ವಾಸ್ತವಿಕ ಹೊಣೆಗಾರಿಕೆ ಸಾಮಾನ್ಯವಾಗಿ ',
  'agent.rewards.exposure.footnote.emphasis': 'ಕಡಿಮೆ',
  'agent.rewards.exposure.footnote.suffix2': ' ಇರುತ್ತದೆ ನಿಮ್ಮ ಕೆಳಗಿನ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಸಹ ಆಪ್ಟ್ ಇನ್ ಮಾಡಿದಾಗ (ಅವರು ದೊಡ್ಡ ಪಾಲನ್ನು ಪಾವತಿಸುತ್ತಾರೆ; ನೀವು ಬಾಕಿಯನ್ನು ಪಾವತಿಸುತ್ತೀರಿ).',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'ಯಾವುದೇ ಸಕ್ರಿಯ ಆಪ್ಟ್-ಇನ್‌ಗಳಿಲ್ಲ',
  'agent.rewards.exposure.empty.body': 'ನೀವು ಬ್ರೌಸ್ ಆಫರ್‌ಗಳ ಟ್ಯಾಬ್‌ನಿಂದ ಒಂದು ಆಫರ್‌ಗೆ ಆಪ್ಟ್ ಇನ್ ಮಾಡಿದ ನಂತರ, ಅದು ನಿಮ್ಮ ಖರ್ಚು ಮತ್ತು ಅಂದಾಜು ಎಕ್ಸ್‌ಪೋಷರ್‌ನೊಂದಿಗೆ ಇಲ್ಲಿ ಕಾಣಿಸಿಕೊಳ್ಳುತ್ತದೆ.',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% ಪಾಲು',
  'agent.rewards.exposure.row.claims.one': '{count} ಕ್ಲೇಮ್',
  'agent.rewards.exposure.row.claims.other': '{count} ಕ್ಲೇಮ್‌ಗಳು',
  'agent.rewards.exposure.row.overridden': 'ಅಡ್ಮಿನ್ ಓವರ್‌ರೈಡ್ ಮಾಡಿದ್ದಾರೆ',
  'agent.rewards.exposure.row.actualMaxLabel': 'ವಾಸ್ತವಿಕ / ಗರಿಷ್ಠ',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'ವೆಚ್ಚ-ಹಂಚಿಕೆ ಆಪ್ಟ್-ಇನ್',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'ಗೇಮ್ ಲಾಭ',
  'agent.profile.rates.infoBanner.standardPctText': ' ಪ್ರಮಾಣಿತ ಶೇಕಡಾವಾರುಗಳನ್ನು ಬಳಸುತ್ತದೆ. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'ಪ್ರಚಾರ ವೆಚ್ಚಗಳು',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ಆಫರ್‌ಗಳು, ಕ್ಯಾಶ್‌ಬ್ಯಾಕ್‌ಗಳು) ಡೈನಾಮಿಕ್ ಅಪ್‌ಲೈನ್ ಪೂಲ್ ಬಳಸುತ್ತವೆ. ಎರಡೂ ನಿಮ್ಮ ಮಟ್ಟ (L{level}) ಆಧಾರದ ಮೇಲೆ ಪ್ಲ್ಯಾಟ್‌ಫಾರ್ಮ್ ಅಡ್ಮಿನ್‌ನಿಂದ ಗ್ಲೋಬಲ್ ಆಗಿ ನಿರ್ವಹಿಸಲ್ಪಡುತ್ತವೆ.',
  'agent.profile.rates.section.gameProfits': 'ಗೇಮ್ ಲಾಭ — ಪ್ರತಿ ಪಂದ್ಯಕ್ಕೆ ನೀವು ಗಳಿಸುವುದು',
  'agent.profile.rates.section.promotionalCosts': 'ಪ್ರಚಾರ ವೆಚ್ಚಗಳು — ನಿಮ್ಮ ಅಪ್‌ಲೈನ್ ಸಬ್ಸಿಡಿ ಕೊಡುಗೆ',
  'agent.profile.rates.pillar.platformFee': 'ಪ್ಲ್ಯಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ %',
  'agent.profile.rates.pillar.winLoss': 'ಗೆಲುವು / ಸೋಲು ಲಾಭ %',
  'agent.profile.rates.pillar.promoWeight': 'ಪ್ರಚಾರ ಪೂಲ್ ತೂಕ',
  'agent.profile.rates.guarantee.title': 'ಡೈರೆಕ್ಟ್ ರೆಫರಲ್ ಗ್ಯಾರಂಟಿ',
  'agent.profile.rates.guarantee.body': 'ನೀವು ಆಫರ್‌ ಕ್ಲೈಮ್ ಮಾಡುತ್ತಿರುವ ಪ್ಲೇಯರ್‌ನನ್ನು ನೇರವಾಗಿ ರೆಫರ್ ಮಾಡಿದ್ದರೆ, ನೀವು ₹0.00 ಪಾವತಿಸುತ್ತೀರಿ. ನಿಮ್ಮ ನೆಟ್‌ವರ್ಕ್ ಬೆಳೆಯಲು ಸಹಾಯ ಮಾಡಲು ನಿಮ್ಮ ಅಪ್‌ಲೈನ್‌ಗಳು ವೆಚ್ಚದ 100% ಸಬ್ಸಿಡಿ ನೀಡುತ್ತವೆ.',
  'agent.profile.rates.poolExplainer.title': 'ಪೂಲ್ ಹೇಗೆ ಕಾರ್ಯ ನಿರ್ವಹಿಸುತ್ತದೆ:',
  'agent.profile.rates.poolExplainer.body': 'ಸಬ್-ಏಜೆಂಟ್‌ನ ಪ್ಲೇಯರ್ ಆಫರ್ ಕ್ಲೈಮ್ ಮಾಡಿದಾಗ, ನಿಮ್ಮ ತೂಕವು ಉಳಿದ ಸಕ್ರಿಯ ಅಪ್‌ಲೈನ್ + ಪ್ಲ್ಯಾಟ್‌ಫಾರ್ಮ್‌ನೊಂದಿಗೆ ಒಂದು ಪೂಲ್‌ಗೆ ಸೇರಿಸಲ್ಪಡುತ್ತದೆ. ನೀವು (ನಿಮ್ಮ ತೂಕ / ಒಟ್ಟು ಪೂಲ್) × ಆಫರ್ ವೆಚ್ಚ ಪಾವತಿಸುತ್ತೀರಿ. ಹೆಚ್ಚಿನ ತೂಕ = ದೊಡ್ಡ ಪಾಲು.',
  'agent.profile.rates.weightLabel': 'ತೂಕ:',
  'agent.profile.rates.updatedPrefix': 'ನವೀಕರಿಸಲಾಗಿದೆ',
  'agent.profile.rates.notSetYet': 'ಇನ್ನೂ ಹೊಂದಿಸಲಾಗಿಲ್ಲ',
  'agent.rewards.optInModal.guarantee.title': 'ಡೈರೆಕ್ಟ್ ರೆಫರಲ್ ಗ್ಯಾರಂಟಿ',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'ಈ ಆಫರ್ ಕ್ಲೈಮ್ ಮಾಡುತ್ತಿರುವ ಪ್ಲೇಯರ್‌ನನ್ನು <strong>ನೀವು ನೇರವಾಗಿ ರೆಫರ್ ಮಾಡಿದ್ದರೆ</strong>, ನೀವು <strong>₹0.00</strong> ಪಾವತಿಸುತ್ತೀರಿ. ನಿಮ್ಮ ನೆಟ್‌ವರ್ಕ್ ಬೆಳೆಯಲು ಸಹಾಯ ಮಾಡಲು ನಿಮ್ಮ ಅಪ್‌ಲೈನ್‌ಗಳು ವೆಚ್ಚದ 100% ಸಬ್ಸಿಡಿ ನೀಡುತ್ತವೆ.',
  'agent.rewards.optInModal.guarantee.body.noDownline': '<strong>ನಿಮ್ಮ ಪ್ಲೇಯರ್‌ಗಳು</strong> ಈ ಆಫರ್ ಕ್ಲೈಮ್ ಮಾಡಿದಾಗ, ನೀವು <strong>₹0.00</strong> ಪಾವತಿಸುತ್ತೀರಿ. ನಿಮ್ಮ ನೆಟ್‌ವರ್ಕ್ ಬೆಳೆಯಲು ಸಹಾಯ ಮಾಡಲು ನಿಮ್ಮ ಅಪ್‌ಲೈನ್‌ಗಳು ವೆಚ್ಚದ 100% ಸಬ್ಸಿಡಿ ನೀಡುತ್ತವೆ.',
  'agent.rewards.optInModal.uplineReality.title': 'ಅಪ್‌ಲೈನ್ ರಿಯಾಲಿಟಿ ಚೆಕ್',
  'agent.rewards.optInModal.uplineReality.body': 'ನಿಮ್ಮಲ್ಲಿ <strong>{count}</strong> ಡೈರೆಕ್ಟ್ ಸಬ್-ಏಜೆಂಟ್{plural} ಇದ್ದಾರೆ. ಸಬ್-ಏಜೆಂಟ್‌ನ ಪ್ಲೇಯರ್ ಈ ಆಫರ್ ಕ್ಲೈಮ್ ಮಾಡಿದರೆ, ನೀವು ನಿಮ್ಮ <strong>ಪೂಲ್ ತೂಕ ({weight})</strong> ಆಧಾರದ ಮೇಲೆ ಉಳಿದ ಸಕ್ರಿಯ ಅಪ್‌ಲೈನ್ ಮತ್ತು ಪ್ಲ್ಯಾಟ್‌ಫಾರ್ಮ್‌ಗೆ ಹೋಲಿಸಿದರೆ ಅನುಪಾತಿಕ ಪಾಲನ್ನು ಪಾವತಿಸುತ್ತೀರಿ.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ಡೈರೆಕ್ಟ್',
  'agent.rates.col.indirect': 'ಇನ್‌ಡೈರೆಕ್ಟ್',
  'agent.rates.indirect.dynamic': 'ಡೈನಾಮಿಕ್',
  'agent.rates.indirect.notApplicable': 'ಅನ್ವಯಿಸುವುದಿಲ್ಲ',
  'agent.rates.indirect.rangeTo': 'ರಿಂದ',
  'agent.rewards.optInModal.close': 'ಮುಚ್ಚಿ',
  'agent.rewards.optInModal.cancel': 'ರದ್ದುಮಾಡಿ',
  'agent.rewards.optInModal.loading': 'ಆಫರ್ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ…',
  'agent.rewards.optInModal.error.loadFailed': 'ಆಫರ್ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ',
  'agent.rewards.optInModal.error.optInFailed': 'ಆಪ್ಟ್-ಇನ್ ವಿಫಲವಾಯಿತು',
  'agent.rewards.optInModal.error.optOutFailed': 'ಆಪ್ಟ್-ಔಟ್ ವಿಫಲವಾಯಿತು',
  'agent.rewards.optInModal.error.saveFailed': 'ಉಪ-ಏಜೆಂಟ್ ದರಗಳನ್ನು ಉಳಿಸಲಾಗಲಿಲ್ಲ',
  'agent.rewards.optInModal.toast.optedIn': 'ಆಪ್ಟ್ ಇನ್ ಆಗಿದೆ. ಈಗ ನಿಮ್ಮ ಡೌನ್-ಲೈನ್ ಈ ಆಫರ್ ನೋಡಬಹುದು.',
  'agent.rewards.optInModal.toast.optedOut': 'ಆಪ್ಟ್ ಔಟ್ ಆಗಿದೆ. ಆಫರ್ ಅನ್ನು ನಿಮ್ಮ ಡೌನ್-ಲೈನ್‌ನಿಂದ ತೆಗೆದುಹಾಕಲಾಗಿದೆ.',
  'agent.rewards.optInModal.toast.saved.one': '{count} ಉಪ-ಏಜೆಂಟ್ ದರ ಬದಲಾವಣೆಯನ್ನು ಉಳಿಸಲಾಗಿದೆ.',
  'agent.rewards.optInModal.toast.saved.other': '{count} ಉಪ-ಏಜೆಂಟ್ ದರ ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಲಾಗಿದೆ.',
  'agent.rewards.optInModal.confirmOptOut': 'ಈ ಆಫರ್‌ನಿಂದ ಆಪ್ಟ್ ಔಟ್ ಮಾಡಬೇಕೆ? ನಿಮ್ಮ ಡೌನ್-ಲೈನ್ ಇದನ್ನು ಇನ್ನು ನೋಡುವುದಿಲ್ಲ.',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'ಆಫರ್ #{id} ಗಾಗಿ ಆಪ್ಟ್-ಇನ್ ಮೋಡಲ್ ಮೂಲಕ ಸೆಟ್ ಮಾಡಲಾಗಿದೆ',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'ನಿಮ್ಮ ಪಾಲು',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'ಆಪ್ಟ್-ಇನ್‌ನಲ್ಲಿ ಲಾಕ್ ಆಗಿದೆ',
  'agent.rewards.optInModal.stake.current': 'ಪ್ರಸ್ತುತ ದರ',
  'agent.rewards.optInModal.stake.noRate': 'ಅಡ್ಮಿನ್ ಇನ್ನೂ ನಿಮಗೆ ಯಾವುದೇ ದರ ನೀಡಿಲ್ಲ',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'ಇಲ್ಲಿಯವರೆಗೆ ಖರ್ಚು',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'ಈ ಆಫರ್‌ನಲ್ಲಿ ಡೌನ್-ಲೈನ್ ಕ್ಲೇಮ್‌ಗಳಿಗಾಗಿ ನಿಮ್ಮ ವಾಲೆಟ್‌ನಿಂದ ಒಟ್ಟು ಪಾವತಿಸಿದ ರೂಪಾಯಿಗಳು.',
  'agent.rewards.optInModal.stake.maxPossible.label': 'ಗರಿಷ್ಠ ಸಾಧ್ಯ',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'ಉಳಿದ ಪ್ರತಿ ಬಜೆಟ್ ರೂಪಾಯಿಯನ್ನು ಕ್ಲೇಮ್ ಮಾಡಿದರೆ ಕೆಟ್ಟ ಪ್ರಕರಣದಲ್ಲಿ ನೀವು ಪಾವತಿಸಬಹುದಾದ ರೂಪಾಯಿಗಳು.',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— ಯಾವುದೇ ಬಜೆಟ್ ಮಿತಿ ಇಲ್ಲ —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'ಕೆಟ್ಟ ಪ್ರಕರಣ: ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಬಜೆಟ್‌ನ ಉಳಿದ ಪ್ರತಿ ₹{amount} ಅನ್ನು ನಿಮ್ಮ ಡೌನ್-ಲೈನ್ ಮೂಲಕ ಕ್ಲೇಮ್ ಮಾಡಿದರೆ, ನೀವು ಅದರ {pct}% ಪಾವತಿಸುತ್ತೀರಿ.',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'ನೀವು ಆಪ್ಟ್ ಇನ್ ಆಗಿದ್ದೀರಿ',
  'agent.rewards.optInModal.toggle.title.optedOut': 'ನಿಮ್ಮ ಡೌನ್-ಲೈನ್‌ನೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಲು ಆಪ್ಟ್ ಇನ್ ಮಾಡಿ',
  'agent.rewards.optInModal.toggle.body.optedIn': 'ನಿಮ್ಮ ಡೌನ್-ಲೈನ್ ಆಟಗಾರರು ಈ ಆಫರ್ ಅನ್ನು ನೋಡುತ್ತಾರೆ. ಅವರಿಂದ ಬರುವ ಪ್ರತಿ ಕ್ಲೈಮ್‌ಗೆ ನಿಮ್ಮ ಲಾಕ್ ಆಗಿರುವ ಪಾಲಿನಲ್ಲಿ ನೀವು ಸಹ-ಧನಸಹಾಯ ನೀಡುತ್ತೀರಿ.',
  'agent.rewards.optInModal.toggle.body.optedOut': 'ನೀವು ಆಪ್ಟ್ ಇನ್ ಆಗುವವರೆಗೆ, ನಿಮ್ಮ ಯಾವುದೇ ಆಟಗಾರರು ಈ ಆಫರ್ ಅನ್ನು ನೋಡುವುದಿಲ್ಲ. ನೀವು ಆಪ್ಟ್ ಇನ್ ಆದ ತಕ್ಷಣ, ಈ ಆಫರ್‌ಗಾಗಿ ನಿಮ್ಮ ಪಾಲಿನ ದರ ಲಾಕ್ ಆಗುತ್ತದೆ.',
  'agent.rewards.optInModal.toggle.optOutLink': 'ಈ ಆಫರ್‌ನಿಂದ ಆಪ್ಟ್ ಔಟ್',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'ಉಪ-ಏಜೆಂಟ್ ದರಗಳು',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ಪ್ರತಿಯೊಬ್ಬರು ಗರಿಷ್ಠ ಪಡೆಯಬಹುದು: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} ಉಳಿಸದ',
  'agent.rewards.optInModal.subAgents.empty': 'ಯಾವುದೇ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'ಉಳಿಸುವಿಕೆ ಕ್ಯಾಸ್ಕೇಡ್ ಆಗುತ್ತದೆ — ಈಗಾಗಲೇ ಆಳವಾದ ಉಪ-ಏಜೆಂಟ್‌ಗಳನ್ನು ಹೊಂದಿರುವ ಮಗುವಿನ ದರವನ್ನು ಇಲ್ಲಿ ನೀವು ಹೆಚ್ಚಿಸಿದರೆ, ಚೈನ್ ಸ್ಥಿರವಾಗಿರಿಸಲು ಆ ದರಗಳನ್ನು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಕ್ಲಾಂಪ್ ಮಾಡಬಹುದು.',
  'agent.rewards.optInModal.subAgents.noChildren': 'ನಿಮಗೆ ಯಾವುದೇ ನೇರ ಉಪ-ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ. ಆಫರ್ ನಿಮ್ಮ ಸ್ವಂತ ಆಟಗಾರರಿಗೆ ಗೋಚರಿಸುತ್ತದೆ; ಅಡ್ಮಿನ್ ಸಂಪೂರ್ಣ ಬಾಕಿಯನ್ನು ಪಾವತಿಸುತ್ತಾರೆ.',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'ದರ ನಮೂದಿಸಿ (ತೆರವುಗೊಳಿಸಲು 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'ಸಂಖ್ಯೆಗಳು ಮಾತ್ರ',
  'agent.rewards.optInModal.error.negative': 'ಋಣಾತ್ಮಕವಾಗಿರಲು ಸಾಧ್ಯವಿಲ್ಲ',
  'agent.rewards.optInModal.error.max100': 'ಗರಿಷ್ಠ 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'ಗರಿಷ್ಠ {ceiling}% (ನಿಮ್ಮ ಪಾಲು)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'ಉಳಿಸಲಾಗುತ್ತಿದೆ…',
  'agent.rewards.optInModal.cta.done': 'ಮುಗಿಯಿತು',
  'agent.rewards.optInModal.cta.save.one': '{count} ಬದಲಾವಣೆ ಉಳಿಸಿ',
  'agent.rewards.optInModal.cta.save.other': '{count} ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'ಆಟಗಾರನಿಗೆ ರೀಚಾರ್ಜ್ ಮಾಡಿ',
  'agent.adminRechargePlayer.subtitle': 'ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ ಆಟಗಾರನ ವಾಲೆಟ್‌ಗೆ',
  'agent.adminRechargePlayer.backLink': 'ಆಟಗಾರನಿಗೆ ಹಿಂತಿರುಗಿ',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'ಹೊಸ ಆಟಗಾರನನ್ನು ಸೇರಿಸಿ',
  'agent.createPlayer.subtitle.agent': 'ಅವರು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ನಿಮ್ಮ ಅಡಿಯಲ್ಲಿರುತ್ತಾರೆ.',
  'agent.createPlayer.subtitle.admin': 'ಕೆಳಗೆ ಮಾಲೀಕ ಏಜೆಂಟ್ ಆಯ್ಕೆಮಾಡಿ, ಅಥವಾ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ನೇರಕ್ಕಾಗಿ ಖಾಲಿ ಬಿಡಿ.',
  'agent.createPlayer.close': 'ಮುಚ್ಚಿ',
  'agent.createPlayer.section.identity.title': 'ಗುರುತು',
  'agent.createPlayer.section.identity.hint': 'ಖಾತೆ ರಚಿಸಲು ಅಗತ್ಯವಿದೆ',
  'agent.createPlayer.field.username.label': 'ಯೂಸರ್‌ನೇಮ್',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ಇಮೇಲ್',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'ಮೊಬೈಲ್',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-ಅಂಕಿಯ ಭಾರತ ಮೊಬೈಲ್, +91 ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಸೇರ್ಪಡೆಯಾಗುತ್ತದೆ',
  'agent.createPlayer.field.password.label': 'ಆರಂಭಿಕ ಪಾಸ್‌ವರ್ಡ್',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ ಅಕ್ಷರಗಳು, ಅಕ್ಷರ + ಅಂಕಿ',
  'agent.createPlayer.section.owner.title': 'ಮಾಲೀಕ ಏಜೆಂಟ್',
  'agent.createPlayer.section.owner.hint': 'ಐಚ್ಛಿಕ — ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ನೇರ ಆಟಗಾರನಿಗಾಗಿ ಖಾಲಿ ಬಿಡಿ',
  'agent.createPlayer.field.owner.label': 'ಏಜೆಂಟ್',
  'agent.createPlayer.field.owner.none': '— ಯಾವುದೂ ಇಲ್ಲ (ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ನೇರ) —',
  'agent.createPlayer.section.profile.title': 'ಪ್ರೊಫೈಲ್',
  'agent.createPlayer.section.profile.hint': 'ಐಚ್ಛಿಕ — ಈಗ ತಿಳಿದಿರುವದನ್ನು ಭರ್ತಿ ಮಾಡಿ; ಆಟಗಾರ ಮೊದಲ ಲಾಗಿನ್‌ನಲ್ಲಿ ಪೂರ್ಣಗೊಳಿಸಬಹುದು',
  'agent.createPlayer.field.firstName.label': 'ಮೊದಲ ಹೆಸರು',
  'agent.createPlayer.field.lastName.label': 'ಕೊನೆಯ ಹೆಸರು',
  'agent.createPlayer.field.dob.label': 'ಜನ್ಮ ದಿನಾಂಕ',
  'agent.createPlayer.field.gender.label': 'ಲಿಂಗ',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— ಸೂಚಿಸಲಾಗಿಲ್ಲ —',
  'agent.createPlayer.gender.male': 'ಪುರುಷ',
  'agent.createPlayer.gender.female': 'ಮಹಿಳೆ',
  'agent.createPlayer.gender.other': 'ಇತರೆ',
  'agent.createPlayer.gender.preferNot': 'ಹೇಳಲು ಇಷ್ಟಪಡುವುದಿಲ್ಲ',
  'agent.createPlayer.field.language.label': 'ಆದ್ಯತೆಯ ಭಾಷೆ',
  'agent.createPlayer.section.location.title': 'ಸ್ಥಳ',
  'agent.createPlayer.section.location.hint': 'ಐಚ್ಛಿಕ — ಆಟಗಾರ ಮೊದಲ ಲಾಗಿನ್‌ನಲ್ಲಿ ಪೂರ್ಣಗೊಳಿಸಬಹುದು',
  // Footer.
  'agent.createPlayer.disclaimer': 'ಆಟಗಾರನನ್ನು status=active ಮತ್ತು ಇಮೇಲ್ ಸ್ವಯಂ-ಪರಿಶೀಲನೆಯೊಂದಿಗೆ ರಚಿಸಲಾಗುತ್ತದೆ — ಯಾವುದೇ ಲಿಂಕ್ ಕಳುಹಿಸಲಾಗುವುದಿಲ್ಲ. ಸಂಪರ್ಕ ಮಾಹಿತಿಯನ್ನು ನೀವು ನಂಬುತ್ತೀರಿ ಎಂದು ಖಚಿತಪಡಿಸಿಕೊಳ್ಳಿ.',
  'agent.createPlayer.cta.cancel': 'ರದ್ದುಗೊಳಿಸಿ',
  'agent.createPlayer.cta.creating': 'ರಚಿಸಲಾಗುತ್ತಿದೆ…',
  'agent.createPlayer.cta.submit': 'ಆಟಗಾರನನ್ನು ರಚಿಸಿ',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'ಏಜೆಂಟ್ ID ಕಾಣೆಯಾಗಿದೆ',
  'agent.createPlayer.error.createFailed': 'ಆಟಗಾರನನ್ನು ರಚಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'ಇಂದು',
  'agent.adminHome.range.yesterday': 'ನಿನ್ನೆ',
  'agent.adminHome.range.week': 'ಈ ವಾರ',
  'agent.adminHome.range.lastWeek': 'ಕಳೆದ ವಾರ',
  'agent.adminHome.range.month': 'ಈ ತಿಂಗಳು',
  'agent.adminHome.range.lastMonth': 'ಕಳೆದ ತಿಂಗಳು',
  'agent.adminHome.range.all': 'ಸಂಪೂರ್ಣ',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'ಮತ್ತೆ ಸ್ವಾಗತ',
  'agent.adminHome.greeting.fallback': 'ಸ್ನೇಹಿತ',
  'agent.adminHome.errorBanner.fallback': 'ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಒಳಹರಿವು',
  'agent.adminHome.admin.action.moneyQueue': 'ಹಣದ ಸರತಿ',
  'agent.adminHome.admin.action.agents': 'ಏಜೆಂಟ್‌ಗಳು',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'ಪಂದ್ಯಗಳು',
  'agent.adminHome.admin.snapshot.newSignups': 'ಹೊಸ ಸೈನ್‌ಅಪ್‌ಗಳು',
  'agent.adminHome.admin.snapshot.active': 'ಸಕ್ರಿಯ',
  'agent.adminHome.admin.snapshot.pending': 'ಬಾಕಿಯಿರುವ',

  // Section titles between rows.
  'agent.adminHome.section.games': 'ಆಟಗಳು',
  'agent.adminHome.section.games.subtitle': 'ಪಂದ್ಯಗಳು ಮತ್ತು ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ · {range}',
  'agent.adminHome.section.activity': 'ಚಟುವಟಿಕೆ',
  'agent.adminHome.section.ops': 'ಕಾರ್ಯಾಚರಣೆಗಳು ಮತ್ತು ಒಳನೋಟಗಳು',
  'agent.adminHome.section.ops.subtitle': 'ಲೈವ್ ಸ್ನ್ಯಾಪ್‌ಶಾಟ್‌ಗಳು — ಪೂರ್ಣ ಪುಟಕ್ಕೆ ಹೋಗಲು ಯಾವುದೇ ಕಾರ್ಡ್ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ',
  'agent.adminHome.section.gameSettings': 'ಆಟದ ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
  'agent.adminHome.section.gameSettings.subtitle': 'ಪ್ರತಿ ಆಟವನ್ನು ಕಾನ್ಫಿಗರ್ ಮಾಡಿ',
  'agent.adminHome.section.downline': 'ನನ್ನ ಡೌನ್-ಲೈನ್ ಒಳನೋಟಗಳು',
  'agent.adminHome.section.downline.subtitle': 'ಪೂರ್ಣ ಪುಟಕ್ಕೆ ಹೋಗಲು ಯಾವುದೇ ಕಾರ್ಡ್ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'ಆಟಗಾರರ ವಾಲೆಟ್',
  'agent.adminHome.admin.card.players.headlineLabel': 'ಅವಧಿಯಲ್ಲಿ ರೀಚಾರ್ಜ್ ಆದದ್ದು',
  'agent.adminHome.admin.card.players.flow.recharges': 'ರೀಚಾರ್ಜ್‌ಗಳು',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'ಹಿಂಪಡೆಯುವಿಕೆಗಳು',
  'agent.adminHome.admin.card.players.pending.recharges': 'ಬಾಕಿಯಿರುವ ರೀಚಾರ್ಜ್‌ಗಳು',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'ಬಾಕಿಯಿರುವ ಹಿಂಪಡೆಯುವಿಕೆಗಳು',
  'agent.adminHome.admin.card.players.stat.total': 'ಒಟ್ಟು ಆಟಗಾರರು',
  'agent.adminHome.admin.card.players.stat.active': 'ಸಕ್ರಿಯ',
  'agent.adminHome.admin.card.players.stat.suspended': 'ಅಮಾನತುಗೊಂಡ',

  'agent.adminHome.admin.card.agents.title': 'ಏಜೆಂಟ್‌ಗಳ ವಾಲೆಟ್',
  'agent.adminHome.admin.card.agents.headlineLabel': 'ಏಜೆಂಟ್ ಪೂಲ್‌ಗಳಲ್ಲಿ',
  'agent.adminHome.admin.card.agents.flow.topups': 'ಟಾಪ್-ಅಪ್‌ಗಳು (ಅಡ್ಮಿನ್ → ಏಜೆಂಟ್)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'ಔಟ್‌ಫ್ಲೋ (ಪಾವತಿಸಲಾಗಿದೆ / ಟಾಪ್-ಅಪ್)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'ಬಾಕಿಯಿರುವ ಏಜೆಂಟ್ ಹಿಂಪಡೆಯುವಿಕೆಗಳು',
  'agent.adminHome.admin.card.agents.stat.total': 'ಒಟ್ಟು ಏಜೆಂಟ್‌ಗಳು',
  'agent.adminHome.admin.card.agents.stat.active': 'ಸಕ್ರಿಯ',
  'agent.adminHome.admin.card.agents.stat.suspended': 'ಅಮಾನತುಗೊಂಡಿದೆ',

  'agent.adminHome.admin.card.bots.title': 'ಬಾಟ್‌ಗಳ ವಾಲೆಟ್',
  'agent.adminHome.admin.card.bots.headlineLabel': 'ಬಾಟ್ ವಾಲೆಟ್‌ಗಳಲ್ಲಿ',
  'agent.adminHome.admin.card.bots.stat.total': 'ಒಟ್ಟು ಬಾಟ್‌ಗಳು',
  'agent.adminHome.admin.card.bots.stat.active': 'ಸಕ್ರಿಯ',
  'agent.adminHome.admin.card.bots.stat.matches': 'ಪಂದ್ಯಗಳು',

  'agent.adminHome.admin.card.rewards.title': 'ಬಹುಮಾನಗಳು',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'ಈ ವಾರ ಪಾವತಿಸಲಾಗಿದೆ',
  'agent.adminHome.admin.card.rewards.pending.claims': 'ಬಾಕಿಯಿರುವ ಕ್ಲೈಮ್‌ಗಳು',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'ಸಕ್ರಿಯ ಆಫರ್‌ಗಳು',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'ಬಲ್ಕ್ ಜಾಬ್‌ಗಳು',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'ಪಂದ್ಯಗಳು',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'ಲೂಡೋ',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'ರಮ್ಮಿ',
  'agent.adminHome.game.carrom': 'ಕ್ಯಾರಂ',
  'agent.adminHome.game.chess': 'ಚದುರಂಗ',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'ಟಾಪ್ ಆಟಗಾರರು',
  'agent.adminHome.activity.topPlayers.empty': 'ಈ ಅವಧಿಯಲ್ಲಿ ಯಾವುದೇ ಪಂದ್ಯಗಳಿಲ್ಲ.',
  'agent.adminHome.activity.topPlayers.metric': '{count} ಪಂದ್ಯಗಳು',
  'agent.adminHome.activity.topAgents.title': 'ಟಾಪ್ ಏಜೆಂಟ್‌ಗಳು',
  'agent.adminHome.activity.topAgents.subtitle': 'ಮಾಲೀಕತ್ವದ ಆಟಗಾರರ ಪ್ರಕಾರ',
  'agent.adminHome.activity.topAgents.empty': 'ಇನ್ನೂ ಯಾವುದೇ ಸಕ್ರಿಯ ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.adminHome.activity.topAgents.metric': '{count} ಆಟಗಾರರು',
  'agent.adminHome.activity.recent.title': 'ಇತ್ತೀಚಿನ ವಹಿವಾಟುಗಳು',
  'agent.adminHome.activity.recent.subtitle': 'ಕೊನೆಯ 5',
  'agent.adminHome.activity.recent.empty': 'ಇನ್ನೂ ಯಾವುದೇ ವಹಿವಾಟುಗಳಿಲ್ಲ.',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'ಟಾಪ್ ಗಳಿಕೆದಾರರು (7ದಿ)',
  'agent.adminHome.ops.topEarners.subtitle': 'ಟ್ರೂ ನೆಟ್ ಪ್ರಕಾರ',
  'agent.adminHome.ops.topEarners.empty': 'ಕಳೆದ 7 ದಿನಗಳಲ್ಲಿ ಯಾವುದೇ ಗಳಿಕೆ ಇಲ್ಲ.',
  'agent.adminHome.ops.liveActivity.title': 'ಲೈವ್ ಚಟುವಟಿಕೆ',
  'agent.adminHome.ops.liveActivity.subtitle': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್‌ನಾದ್ಯಂತ ಕೊನೆಯ 5 ಈವೆಂಟ್‌ಗಳು',
  'agent.adminHome.ops.liveActivity.empty': 'ಶಾಂತ — ಕಳೆದ 24ಗಂ ನಲ್ಲಿ ಯಾವುದೇ ಈವೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.adminHome.ops.botPerf.title': 'ಬಾಟ್ ಕಾರ್ಯಕ್ಷಮತೆ (7ದಿ)',
  'agent.adminHome.ops.botPerf.subtitle': 'ಪ್ರತಿ-ವರ್ಗ ಲಾಭ-ನಷ್ಟ',
  'agent.adminHome.ops.rateChanges.title': 'ಇತ್ತೀಚಿನ ದರ ಬದಲಾವಣೆಗಳು',
  'agent.adminHome.ops.rateChanges.subtitle': 'ಇತ್ತೀಚಿನ ಪಾಲುದಾರಿಕೆ ಸಂಪಾದನೆಗಳು',
  'agent.adminHome.ops.rateChanges.empty': 'ಇನ್ನೂ ಯಾವುದೇ ದರ-ಬದಲಾವಣೆ ಈವೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.adminHome.ops.opsHealth.title': 'ಕಾರ್ಯಾಚರಣೆಗಳ ಆರೋಗ್ಯ',
  'agent.adminHome.ops.opsHealth.subtitle': 'ಡೆಡ್-ಲೆಟರ್ ಕ್ಯೂ · ಬಾಕಿಯಿರುವ ಸೆಟಲ್‌ಮೆಂಟ್‌ಗಳು',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'ಗಳಿಸಿದೆ',
  'agent.adminHome.feed.match.onGameSuffix': '{game} ನಲ್ಲಿ',
  'agent.adminHome.feed.recharge.label': 'ರೀಚಾರ್ಜ್',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'ಮೂಲಕ',
  'agent.adminHome.feed.rateChange.label': 'ದರ ಬದಲಾವಣೆ',
  'agent.adminHome.feed.cascadeBadge': 'ಕ್ಯಾಸ್ಕೇಡ್',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'ಅನುಮೋದಿಸಲಾಗಿದೆ',
  'agent.adminHome.feed.recharge.state.rejected': 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
  'agent.adminHome.feed.recharge.state.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.adminHome.feed.recharge.state.paid': 'ಪಾವತಿಸಲಾಗಿದೆ',
  'agent.adminHome.feed.recharge.state.expired': 'ಅವಧಿ ಮೀರಿದೆ',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} ಪಂದ್ಯ',
  'agent.adminHome.leaderboard.matches.other': '{count} ಪಂದ್ಯಗಳು',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಲಾಭ (7ದಿ)',
  'agent.adminHome.botMatrix.cat.easy': 'ಸುಲಭ',
  'agent.adminHome.botMatrix.cat.medium': 'ಮಧ್ಯಮ',
  'agent.adminHome.botMatrix.cat.hard': 'ಕಠಿಣ',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}ಪಂ',
  'agent.adminHome.botMatrix.activeSummary': '{total} ಬಾಟ್‌ಗಳಲ್ಲಿ {active} ಸಕ್ರಿಯ',
  'agent.adminHome.botMatrix.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ ಸ್ವಚ್ಛವಾಗಿದೆ',
  'agent.adminHome.opsHealth.dlqClean.body': 'ಯಾವುದೇ ವಿಫಲ ಸೆಟಲ್‌ಮೆಂಟ್ ಜಾಬ್‌ಗಳಿಲ್ಲ.',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} ಬಗೆಹರಿಯದ DLQ ಜಾಬ್',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} ಬಗೆಹರಿಯದ DLQ ಜಾಬ್‌ಗಳು',
  'agent.adminHome.opsHealth.dlqDirty.body': 'ಮರುಪ್ರಯತ್ನಿಸಲು / ಪರಿಹರಿಸಲು ಕ್ಲಿಕ್ ಮಾಡಿ.',
  'agent.adminHome.opsHealth.replay.eyebrow': 'ಮರು-ಪ್ಲೇ',
  'agent.adminHome.opsHealth.replay.body': 'ಸೆಟಲ್‌ಮೆಂಟ್ ಮರು-ಚಲಾಯಿಸಿ',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'ಮರು-ನಿಯೋಜಿಸಿ',
  'agent.adminHome.opsHealth.reassign.body': 'ಬಲ್ಕ್ ಮೂವ್',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'ನನ್ನ ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.adminHome.agentDash.hero.netPrefix': 'ನೆಟ್',
  'agent.adminHome.agentDash.action.myWallet': 'ನನ್ನ ವಾಲೆಟ್',
  'agent.adminHome.agentDash.action.myPlayers': 'ನನ್ನ ಆಟಗಾರರು',
  'agent.adminHome.agentDash.snapshot.myMatches': 'ನನ್ನ ಪಂದ್ಯಗಳು',
  'agent.adminHome.agentDash.snapshot.active': 'ಸಕ್ರಿಯ',
  'agent.adminHome.agentDash.snapshot.netRange': 'ನೆಟ್ (ಅವಧಿ)',
  'agent.adminHome.agentDash.snapshot.pending': 'ಬಾಕಿಯಿರುವ',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'ನನ್ನ ಆಟಗಾರರ ವಾಲೆಟ್',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'ಅವಧಿಯಲ್ಲಿ ರೀಚಾರ್ಜ್ ಆಗಿದೆ',
  'agent.adminHome.agentDash.card.players.stat.total': 'ನನ್ನ ಆಟಗಾರರು',
  'agent.adminHome.agentDash.card.wallet.title': 'ನನ್ನ ವಾಲೆಟ್',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'ಪೂಲ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'ಗಳಿಸಿದೆ (ಕಮಿಷನ್‌ಗಳು)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'ಪಾವತಿಸಲಾಗಿದೆ',
  'agent.adminHome.agentDash.card.wallet.pending': 'ನನ್ನ ಬಾಕಿಯಿರುವ ಹಿಂಪಡೆಯುವಿಕೆ',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'ಸ್ವೀಕರಿಸಿದ ಟಾಪ್-ಅಪ್‌ಗಳು',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'ನೆಟ್ (ಅವಧಿ)',
  'agent.adminHome.agentDash.card.activity.title': 'ಚಟುವಟಿಕೆ',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'ಅವಧಿಯಲ್ಲಿ ನನ್ನ ಪಂದ್ಯಗಳು',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'ಟಾಪ್ ಖರ್ಚು ಮಾಡುವವರು',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'ಅವರ ಖರ್ಚು',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'ಬಾಕಿಯಿರುವ ಐಟಂಗಳು',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'ಟಾಪ್ ಖರ್ಚು ಮಾಡುವವರು',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'ಈ ಅವಧಿಯಲ್ಲಿ ಇನ್ನೂ ಯಾವುದೇ ಖರ್ಚು ಇಲ್ಲ.',
  'agent.adminHome.agentDash.activity.recent.title': 'ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'ನಿಮ್ಮ ಆಟಗಾರರಿಂದ ಕೊನೆಯ 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'ಇನ್ನೂ ಯಾವುದೇ ವಹಿವಾಟುಗಳಿಲ್ಲ.',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'ಟಾಪ್ ಗಳಿಕೆದಾರರು (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'ನಿಮ್ಮ ಉಪ-ಏಜೆಂಟ್‌ಗಳು ಕ್ರಮದಲ್ಲಿ',
  'agent.adminHome.agentDash.insights.downline.title': 'ಡೌನ್-ಲೈನ್ ಚಟುವಟಿಕೆ',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'ನಿಮ್ಮ ಡೌನ್-ಲೈನ್‌ನಿಂದ ಕೊನೆಯ 5 ಈವೆಂಟ್‌ಗಳು',
  'agent.adminHome.agentDash.insights.deposit.title': 'ಠೇವಣಿ ಲೆಜರ್',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'ಇತ್ತೀಚಿನ ಠೇವಣಿ ಕ್ರೆಡಿಟ್‌ಗಳು / ರಿಫಂಡ್‌ಗಳು',
  'agent.adminHome.agentDash.insights.deposit.empty': 'ಇನ್ನೂ ಯಾವುದೇ ಠೇವಣಿ ಚಟುವಟಿಕೆ ಇಲ್ಲ.',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'ಉಪ-ಏಜೆಂಟ್',
  'agent.subAgentDetail.subtitle': 'ನೇರ ಉಪ-ಏಜೆಂಟ್',
  'agent.subAgentDetail.error.loadFailed': 'ಉಪ-ಏಜೆಂಟ್ ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
  'agent.subAgentDetail.backToList': 'ಉಪ-ಏಜೆಂಟ್‌ಗಳ ಪಟ್ಟಿಗೆ ಹಿಂತಿರುಗಿ',
  'agent.subAgentDetail.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'ಗುರುತು',
  'agent.subAgentDetail.tab.players': 'ಆಟಗಾರರು',
  'agent.subAgentDetail.tab.wallet': 'ವಾಲೆಟ್',
  'agent.subAgentDetail.tab.rates': 'ದರಗಳು',
  'agent.subAgentDetail.tab.ops': 'ಕಾರ್ಯಾಚರಣೆಗಳು',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} ಆಟಗಾರ',
  'agent.subAgentDetail.header.players.other': '{count} ಆಟಗಾರರು',
  'agent.subAgentDetail.header.pool': 'ಪೂಲ್: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'ಕೊನೆಯ ಲಾಗಿನ್ {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'ಸಕ್ರಿಯ',
  'agent.subAgentDetail.status.suspended': 'ಅಮಾನತುಗೊಂಡಿದೆ',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'ಯೂಸರ್‌ನೇಮ್',
  'agent.subAgentDetail.identity.row.email': 'ಇಮೇಲ್',
  'agent.subAgentDetail.identity.row.status': 'ಸ್ಥಿತಿ',
  'agent.subAgentDetail.identity.row.parent': 'ಪೋಷಕ ಏಜೆಂಟ್',
  'agent.subAgentDetail.identity.parentValue': '@{username} (ನೀವು)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'ಮಾಲೀಕತ್ವದ ಆಟಗಾರರು',
  'agent.subAgentDetail.identity.row.wallet': 'ವಾಲೆಟ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.subAgentDetail.identity.row.created': 'ರಚಿಸಲಾಗಿದೆ',
  'agent.subAgentDetail.identity.row.lastLogin': 'ಕೊನೆಯ ಲಾಗಿನ್',
  'agent.subAgentDetail.identity.row.identityLocked': 'ಗುರುತು ಲಾಕ್ ಆಗಿದೆ',
  'agent.subAgentDetail.identity.identityLocked.yes': 'ಹೌದು (ಕೇವಲ ಅಡ್ಮಿನ್ ಸಂಪಾದನೆಗಳು)',
  'agent.subAgentDetail.identity.identityLocked.no': 'ಇಲ್ಲ (ಉಪ-ಏಜೆಂಟ್ ಸ್ವತಃ ಸಂಪಾದಿಸಬಹುದು)',
  'agent.subAgentDetail.identity.row.receiving': 'ಸ್ವೀಕರಿಸುತ್ತಿರುವ ಆಟಗಾರರು',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'ಸ್ವೀಕರಿಸುತ್ತಿದೆ',
  'agent.subAgentDetail.optOut.optedOut': 'ಆಪ್ಟ್ ಔಟ್ ಮಾಡಿದೆ',
  'agent.subAgentDetail.optOut.toast.accepting': 'ಉಪ-ಏಜೆಂಟ್ ಈಗ ಆಟಗಾರರನ್ನು ಸ್ವೀಕರಿಸುತ್ತಿದ್ದಾರೆ',
  'agent.subAgentDetail.optOut.toast.optedOut': 'ಉಪ-ಏಜೆಂಟ್ ಲೊಕೇಟರ್‌ನಿಂದ ಆಪ್ಟ್ ಔಟ್ ಮಾಡಿದರು',
  'agent.subAgentDetail.optOut.error.updateFailed': 'ಅಪ್‌ಡೇಟ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'ಸ್ಥಳ',
  'agent.subAgentDetail.location.lastUpdated': '· ಕೊನೆಯ ಅಪ್‌ಡೇಟ್ {date}',
  'agent.subAgentDetail.location.cta.save': 'ಸ್ಥಳ ಉಳಿಸಿ',
  'agent.subAgentDetail.location.nothingToSave': 'ಉಳಿಸಲು ಏನೂ ಇಲ್ಲ',
  'agent.subAgentDetail.location.toast.saved.one': 'ಸ್ಥಳ ಉಳಿಸಲಾಗಿದೆ ({count} ಫೀಲ್ಡ್)',
  'agent.subAgentDetail.location.toast.saved.other': 'ಸ್ಥಳ ಉಳಿಸಲಾಗಿದೆ ({count} ಫೀಲ್ಡ್‌ಗಳು)',
  'agent.subAgentDetail.location.hint': 'ಉಳಿಸುವಿಕೆಯನ್ನು ಸಕ್ರಿಯಗೊಳಿಸಲು ಬೇರೆ ರಾಜ್ಯ / ಜಿಲ್ಲೆ / ಪ್ರದೇಶವನ್ನು ಆಯ್ಕೆಮಾಡಿ. (ಉಪ-ಏಜೆಂಟ್‌ಗಳು ತಮ್ಮ ಮೊದಲ ಲಾಗಿನ್‌ನಲ್ಲಿ ಬಲವಂತದ ಪಾಪ್-ಅಪ್ ಮೂಲಕ ನಿಖರವಾದ GPS ಅನ್ನು ಸ್ವತಃ ಓವರ್‌ಲೇ ಮಾಡುತ್ತಾರೆ.)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'ಈ ಉಪ-ಏಜೆಂಟ್‌ಗೆ ಇನ್ನೂ ಯಾವುದೇ ಆಟಗಾರರಿಲ್ಲ.',
  'agent.subAgentDetail.players.count.one': '{count} ಆಟಗಾರ',
  'agent.subAgentDetail.players.count.other': '{count} ಆಟಗಾರರು',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'ಪ್ರಸ್ತುತ ಉಪ-ಏಜೆಂಟ್ ಬ್ಯಾಲೆನ್ಸ್',
  'agent.subAgentDetail.wallet.ledger.title': '@{username} ರ ಪೂರ್ಣ ವಾಲೆಟ್ ಲೆಜರ್',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'ಪ್ರತಿ ಚಲನೆ · ಉಪ-ಏಜೆಂಟ್ ತಮ್ಮ ವಾಲೆಟ್‌ನಲ್ಲಿ ಇದನ್ನೇ ನೋಡುತ್ತಾರೆ',
  'agent.subAgentDetail.wallet.ledger.empty': 'ಇನ್ನೂ ಯಾವುದೇ ವಾಲೆಟ್ ಚಟುವಟಿಕೆ ಇಲ್ಲ.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'ನೀವು ↔ ಅವರು',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'ಬ್ಯಾಲೆನ್ಸ್ ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'ಅಡ್ಮಿನ್‌ನಿಂದ ಟಾಪ್-ಅಪ್',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'ಆಟಗಾರನಿಗೆ ಪಾವತಿ',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'ಆಟಗಾರ ರೀಚಾರ್ಜ್',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'ಆದಾಯ ಹಂಚಿಕೆ',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'ರೀಚಾರ್ಜ್ ಕಮಿಷನ್',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'ಸಹ-ನಿಧಿ ಆಫರ್',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'ಅಡ್ಮಿನ್ ಹೊಂದಾಣಿಕೆ',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'ಹಿಂಪಡೆಯುವಿಕೆ ತಡೆಹಿಡಿತ',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'ಹಿಂಪಡೆಯುವಿಕೆ ರಿಫಂಡ್',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ ಹಂಚಿಕೆ',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'ವಿನ್/ಲಾಸ್ ಹಂಚಿಕೆ',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'ಕಾಯಿನ್‌ಗಳನ್ನು ಹಿಂದಕ್ಕೆ ಎಳೆಯುತ್ತಿದ್ದೀರಾ?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'ಕೇಳಿ ',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' ತಮ್ಮ ಸ್ವಂತ ಪೂಲ್‌ನಿಂದ ಹಿಂಪಡೆಯುವಿಕೆ ವಿನಂತಿಯನ್ನು ಸಲ್ಲಿಸಲು. ನೀವು ಅದನ್ನು (ಪೂರ್ಣ ಅಥವಾ ಭಾಗಶಃ) ಪಾವತಿಸಿದಂತೆ ಗುರುತಿಸಬಹುದು ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'ಫೈನಾನ್ಸ್ › ಉಪ-ಏಜೆಂಟ್‌ಗಳು',
  'agent.subAgentDetail.wallet.pullback.body.after': '.',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username} ಗೆ ವರ್ಗಾವಣೆ',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username} ರಿಂದ ಹಿಂಪಡೆಯಿರಿ',
  'agent.subAgentDetail.transfer.topup.body': 'ಪರಮಾಣು ವರ್ಗಾವಣೆ: ನಿಮ್ಮ ವಾಲೆಟ್ ಕಡಿಮೆ, ಅವರ ವಾಲೆಟ್ ಹೆಚ್ಚು. ಎರಡೂ ಪೂಲ್‌ಗಳು ಋಣಾತ್ಮಕವಾಗಬಹುದು (ಸಹಿ-ಮಾಡಲಾದ-ಪೂಲ್ ವಿನ್ಯಾಸ).',
  'agent.subAgentDetail.transfer.withdraw.body': 'ಪರಮಾಣು ರಿವರ್ಸ್ ವರ್ಗಾವಣೆ: ಅವರ ವಾಲೆಟ್ ಕಡಿಮೆ, ನಿಮ್ಮ ವಾಲೆಟ್ ಹೆಚ್ಚು. ಉಪ-ಏಜೆಂಟ್‌ನಿಂದ ಪಾವತಿ ವಿನಂತಿಯನ್ನು ಇತ್ಯರ್ಥಗೊಳಿಸುವಾಗ ಇದನ್ನು ಬಳಸಿ.',
  'agent.subAgentDetail.transfer.amountLabel': 'ಮೊತ್ತ (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'ಉದಾ. ವಾರದ ಟಾಪ್-ಅಪ್',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'ಉದಾ. ಪಾವತಿ ಇತ್ಯರ್ಥ',
  'agent.subAgentDetail.transfer.error.amountPositive': 'ಮೊತ್ತವು ಧನಾತ್ಮಕ ಪೂರ್ಣಾಂಕವಾಗಿರಬೇಕು.',
  'agent.subAgentDetail.transfer.confirm.topup': 'ನಿಮ್ಮ ಪೂಲ್‌ನಿಂದ @{username} ಗೆ {amount} ವರ್ಗಾಯಿಸುವುದೇ? ನಿಮ್ಮ ಬ್ಯಾಲೆನ್ಸ್ ಸರಿಯಾಗಿ ಈ ಮೊತ್ತದಷ್ಟು ಕಡಿಮೆಯಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.transfer.confirm.withdraw': '@{username} ರ ಪೂಲ್‌ನಿಂದ {amount} ಅನ್ನು ನಿಮಗೆ ಹಿಂದಕ್ಕೆ? ಅವರ ಬ್ಯಾಲೆನ್ಸ್ ಸರಿಯಾಗಿ ಇಷ್ಟೇ ಕಡಿಮೆಯಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.transfer.toast.topup': '@{username} ಗೆ {amount} ಕಳುಹಿಸಲಾಗಿದೆ. ಅವರ ಬ್ಯಾಲೆನ್ಸ್: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username} ರಿಂದ {amount} ಹಿಂದಕ್ಕೆ ಎಳೆಯಲಾಗಿದೆ. ಅವರ ಬ್ಯಾಲೆನ್ಸ್: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working': 'ಕೆಲಸ ನಡೆಯುತ್ತಿದೆ…',
  'agent.subAgentDetail.transfer.cta.suspended': 'ಉಪ-ಏಜೆಂಟ್ ಅಮಾನತುಗೊಂಡಿದೆ',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} ವರ್ಗಾವಣೆ',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} ಹಿಂಪಡೆಯಿರಿ',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'ದರಗಳು ಲೋಡ್ ಆಗುತ್ತಿವೆ…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'ಪ್ರತಿ ಕಾರ್ಡ್ ಒಂದು ಕಮಿಷನ್ ಪಿಲ್ಲರ್; ಒಳಗಿನ ಪ್ರತಿ ಸಾಲು ಒಂದು (ಪಿಲ್ಲರ್ × ಗೇಮ್) ಹಂಚಿಕೆ. ',
  'agent.subAgentDetail.rates.intro.ceiling': 'ಸೀಮೆ',
  'agent.subAgentDetail.rates.intro.part2': ' ಪ್ರತಿ ಇನ್‌ಪುಟ್‌ನ ಪಕ್ಕದಲ್ಲಿ ತೋರಿಸಿರುವುದು ನಿಮ್ಮ ಸ್ವಂತ ದರ — ಗೋಲ್ಡನ್ ಲಿಮಿಟ್ ಪ್ರಕಾರ, ನೀವು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನದನ್ನು ಮಗುವಿಗೆ ನೀಡಲು ಸಾಧ್ಯವಿಲ್ಲ. ನೀವು ಬದಲಾಯಿಸಿದ ಯಾವುದೇ ಮೌಲ್ಯವು ಸಂಪಾದನೆ ಯೋಗ್ಯವಾಗುತ್ತದೆ; ಕ್ಲಿಕ್ ಮಾಡಿ ',
  'agent.subAgentDetail.rates.intro.applyWord': 'ಅನ್ವಯಿಸಿ',
  'agent.subAgentDetail.rates.intro.part3': ' ಬದ್ಧಗೊಳಿಸಲು. ಕ್ಯಾಸ್ಕೇಡ್ ಕ್ಲ್ಯಾಂಪ್ ಸರ್ವರ್-ಸೈಡ್‌ನಲ್ಲಿ ಜಾರಿಗೊಳಿಸಲಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.rates.toast.applied': 'ದರಗಳು ಅನ್ವಯಿಸಲಾಯಿತು.',
  'agent.subAgentDetail.rates.confirm.one': '{count} ದರ ಬದಲಾವಣೆಯನ್ನು ಅನ್ವಯಿಸಬೇಕೆ? ಗೋಲ್ಡನ್-ಲಿಮಿಟ್ + ಕ್ಯಾಸ್ಕೇಡ್ ಕ್ಲ್ಯಾಂಪ್ ಸರ್ವರ್-ಸೈಡ್‌ನಲ್ಲಿ ಜಾರಿಯಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.rates.confirm.other': '{count} ದರ ಬದಲಾವಣೆಗಳನ್ನು ಅನ್ವಯಿಸಬೇಕೆ? ಗೋಲ್ಡನ್-ಲಿಮಿಟ್ + ಕ್ಯಾಸ್ಕೇಡ್ ಕ್ಲ್ಯಾಂಪ್ ಸರ್ವರ್-ಸೈಡ್‌ನಲ್ಲಿ ಜಾರಿಯಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.rates.error.blocked': 'ತಡೆಯಲಾಗಿದೆ: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'ವಿನ್/ಲಾಸ್ ಲಾಭ %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ಆಫರ್ ಬಹುಮಾನ %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— ಜಾಗತಿಕ —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ ಸೀಲಿಂಗ್ {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'ಪೋಷಕರಿಗಿಂತ ಹೆಚ್ಚು (ಗೋಲ್ಡನ್ ಲಿಮಿಟ್)',
  'agent.subAgentDetail.rates.row.editedBadge': 'ಸಂಪಾದಿತ',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} ಬದಲಾವಣೆಯನ್ನು ತ್ಯಜಿಸಿ',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} ಬದಲಾವಣೆಗಳನ್ನು ತ್ಯಜಿಸಿ',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} ಬದಲಾವಣೆಯನ್ನು ಅನ್ವಯಿಸಿ',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} ಬದಲಾವಣೆಗಳನ್ನು ಅನ್ವಯಿಸಿ',
  'agent.subAgentDetail.rates.cta.applying': 'ಅನ್ವಯಿಸುತ್ತಿದೆ…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'ಸ್ಥಿತಿ',
  'agent.subAgentDetail.ops.status.body': 'ಅಮಾನತುಗೊಳಿಸುವಿಕೆಯು ಉಪ-ಏಜೆಂಟ್‌ನ ಲಾಗಿನ್ ಮತ್ತು ಡೌನ್-ಲೈನ್ ಕಾರ್ಯಾಚರಣೆಗಳನ್ನು ನೀವು ಮತ್ತೆ ಸಕ್ರಿಯಗೊಳಿಸುವವರೆಗೆ ಸ್ಥಗಿತಗೊಳಿಸುತ್ತದೆ. ಕಾರಣವು ಐಚ್ಛಿಕವಾಗಿದೆ ಆದರೆ ಆಡಿಟ್ ಟ್ರೇಲ್‌ನಲ್ಲಿ ದಾಖಲಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'ಕಾರಣ (ಐಚ್ಛಿಕ)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'ಉಪ-ಏಜೆಂಟ್ ಅಮಾನತುಗೊಳಿಸಿ',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'ಉಪ-ಏಜೆಂಟ್ ಅಮಾನತು ತೆಗೆದುಹಾಕಿ',
  'agent.subAgentDetail.ops.status.cta.working': 'ಕೆಲಸ ನಡೆಯುತ್ತಿದೆ…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username} ಅನ್ನು ಅಮಾನತುಗೊಳಿಸಬೇಕೆ? ಅಮಾನತಿನಲ್ಲಿರುವಾಗ ಅವರು ಲಾಗಿನ್ ಆಗಲು ಅಥವಾ ಕಾರ್ಯನಿರ್ವಹಿಸಲು ಸಾಧ್ಯವಾಗುವುದಿಲ್ಲ.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username} ಅಮಾನತು ತೆಗೆದುಹಾಕಬೇಕೆ? ಅವರು ಪೂರ್ಣ ಪ್ರವೇಶವನ್ನು ಮರಳಿ ಪಡೆಯುತ್ತಾರೆ.',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} ಈಗ ಅಮಾನತುಗೊಳಿಸಲಾಗಿದೆ.',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} ಈಗ ಸಕ್ರಿಯವಾಗಿದೆ.',

  'agent.subAgentDetail.ops.password.title': 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ',
  'agent.subAgentDetail.ops.password.body': 'ಉಪ-ಏಜೆಂಟ್‌ಗೆ ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ಹೊಂದಿಸುತ್ತದೆ ಮತ್ತು ಅವರ ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ರಿಫ್ರೆಶ್ ಟೋಕನ್‌ಗಳನ್ನು ರದ್ದುಗೊಳಿಸುತ್ತದೆ. ಅವರು ಹೊಸ ಪಾಸ್‌ವರ್ಡ್‌ನೊಂದಿಗೆ ಮತ್ತೆ ಲಾಗಿನ್ ಆಗುವಂತೆ ಒತ್ತಾಯಿಸಲಾಗುತ್ತದೆ. ಅದನ್ನು ಸುರಕ್ಷಿತ ಚಾನಲ್ ಮೂಲಕ ಅವರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಿ.',
  'agent.subAgentDetail.ops.password.placeholder': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ (ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು)',
  'agent.subAgentDetail.ops.password.confirm': '@{username} ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಬೇಕೆ? ಅವರ ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ಸೆಷನ್‌ಗಳು ಅಮಾನ್ಯವಾಗುತ್ತವೆ ಮತ್ತು ಅವರು ಹೊಸ ಪಾಸ್‌ವರ್ಡ್‌ನೊಂದಿಗೆ ಲಾಗಿನ್ ಆಗಬೇಕಾಗುತ್ತದೆ.',
  'agent.subAgentDetail.ops.password.toast.reset': 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಲಾಗಿದೆ. ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ಅನ್ನು @{username} ಜೊತೆಗೆ ಹಂಚಿಕೊಳ್ಳಿ.',
  'agent.subAgentDetail.ops.password.error.tooShort': 'ಪಾಸ್‌ವರ್ಡ್ ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು.',
  'agent.subAgentDetail.ops.password.cta.reset': 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ',
  'agent.subAgentDetail.ops.password.cta.resetting': 'ಮರುಹೊಂದಿಸಲಾಗುತ್ತಿದೆ…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'ಆಟಗಾರರಿಗೆ ಹಿಂತಿರುಗಿ',
  'agent.playerDetail.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.playerDetail.error.loadFailed': 'ಬಳಕೆದಾರರನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',

  // Header card.
  'agent.playerDetail.header.noName': '(ಯಾವುದೇ ಹೆಸರು ಸೆಟ್ ಆಗಿಲ್ಲ)',
  'agent.playerDetail.header.joined': '{date} ರಂದು ಸೇರಿದರು',
  'agent.playerDetail.header.ownedBy.prefix': ' · ಒಡೆಯ ',
  'agent.playerDetail.header.walletLabel': 'ವಾಲೆಟ್',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'ಬಾಕಿಯಿರುವ',
  'agent.playerDetail.status.active': 'ಸಕ್ರಿಯ',
  'agent.playerDetail.status.suspended': 'ಅಮಾನತುಗೊಳಿಸಲಾಗಿದೆ',
  'agent.playerDetail.status.banned': 'ನಿಷೇಧಿಸಲಾಗಿದೆ',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'ಗುರುತು',
  'agent.playerDetail.tab.profile': 'ಪ್ರೊಫೈಲ್',
  'agent.playerDetail.tab.wallet': 'ವಾಲೆಟ್',
  'agent.playerDetail.tab.security': 'ಭದ್ರತೆ',
  'agent.playerDetail.tab.ownership': 'ಮಾಲೀಕತ್ವ',
  'agent.playerDetail.tab.operations': 'ಕಾರ್ಯಾಚರಣೆಗಳು',

  // Identity tab.
  'agent.playerDetail.identity.title': 'ಗುರುತು',
  'agent.playerDetail.identity.row.username': 'ಯೂಸರ್‌ನೇಮ್',
  'agent.playerDetail.identity.row.email': 'ಇಮೇಲ್',
  'agent.playerDetail.identity.row.phone': 'ಫೋನ್',
  'agent.playerDetail.identity.row.country': 'ದೇಶ',
  'agent.playerDetail.identity.row.joined': 'ಸೇರಿದರು',
  'agent.playerDetail.identity.row.emailVerified': 'ಇಮೇಲ್ ಪರಿಶೀಲಿಸಲಾಗಿದೆ',
  'agent.playerDetail.identity.row.telegram': 'ಟೆಲಿಗ್ರಾಮ್',
  'agent.playerDetail.identity.row.failedLogins': 'ವಿಫಲ ಲಾಗಿನ್‌ಗಳು',
  'agent.playerDetail.identity.row.lockedUntil': 'ಈ ಸಮಯದವರೆಗೆ ಲಾಕ್',
  'agent.playerDetail.identity.value.yes': 'ಹೌದು',
  'agent.playerDetail.identity.value.no': 'ಇಲ್ಲ',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'ಈ ಆಟಗಾರನನ್ನು ವರ್ಗಾಯಿಸಿ',
  'agent.playerDetail.identity.transfer.body': 'ಮತ್ತೊಬ್ಬ ಏಜೆಂಟ್‌ಗೆ ಅವರ Unique Agent ID ಮೂಲಕ ಹಸ್ತಾಂತರಿಸಿ — ಇಡೀ ನೆಟ್‌ವರ್ಕ್‌ನಲ್ಲಿ ಕೆಲಸ ಮಾಡುತ್ತದೆ, ಕೇವಲ ನಿಮ್ಮ ಡೌನ್-ಲೈನ್‌ನಲ್ಲಿ ಮಾತ್ರವಲ್ಲ.',
  'agent.playerDetail.identity.transfer.cta': 'UAID ಮೂಲಕ ವರ್ಗಾಯಿಸಿ',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'ಫೋಟೋ ಬದಲಾಯಿಸಿ',
  'agent.playerDetail.avatar.cropTitle': '@{username} ಗಾಗಿ ಫೋಟೋ ಸರಿಹೊಂದಿಸಿ',
  'agent.playerDetail.avatar.toast.updated': 'ಫೋಟೋ ನವೀಕರಿಸಲಾಗಿದೆ',
  'agent.playerDetail.avatar.error.uploadFailed': 'ಅಪ್‌ಲೋಡ್ ವಿಫಲವಾಗಿದೆ',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'ಆಟಗಾರನನ್ನು UAID ಮೂಲಕ ವರ್ಗಾಯಿಸಲಾಗಿದೆ',
  'agent.playerDetail.transfer.toast.agent': '@{username} ಯಶಸ್ವಿಯಾಗಿ ವರ್ಗಾಯಿಸಲಾಗಿದೆ',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'ಖಾತೆಯ ಸ್ಥಿತಿ',
  'agent.playerDetail.ops.accountStatus.body': 'ಸಕ್ರಿಯ ಎಂದರೆ ಆಟಗಾರ ಲಾಗಿನ್ ಆಗಿ ಆಡಬಹುದು. ಅಮಾನತು ನೀವು ಮತ್ತೆ ಸಕ್ರಿಯಗೊಳಿಸುವವರೆಗೆ ಲಾಗಿನ್‌ಗಳನ್ನು ತಡೆಯುತ್ತದೆ. ನಿಷೇಧವು ಬಲವಾದ ಲಾಕ್ — ನೀತಿ ಉಲ್ಲಂಘನೆಗಳಿಗೆ ಇದನ್ನು ಬಳಸಿ.',
  'agent.playerDetail.ops.statusBtn.activate': 'ಸಕ್ರಿಯಗೊಳಿಸಿ',
  'agent.playerDetail.ops.statusBtn.suspend': 'ಅಮಾನತುಗೊಳಿಸಿ',
  'agent.playerDetail.ops.statusBtn.ban': 'ನಿಷೇಧಿಸಿ',
  'agent.playerDetail.ops.statusBtn.current': 'ಪ್ರಸ್ತುತ',
  'agent.playerDetail.ops.statusBtn.toast': 'ಬಳಕೆದಾರ ಈಗ {status} ಆಗಿದ್ದಾರೆ',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'ಅಪಾಯದ ವಲಯ',
  'agent.playerDetail.ops.danger.body': 'ಹಾರ್ಡ್-ಡಿಲೀಟ್ ಬಳಕೆದಾರ ದಾಖಲೆಯನ್ನು ತೆಗೆದುಹಾಕುತ್ತದೆ. ವಾಲೆಟ್ ಲೆಜರ್ ಆಡಿಟ್‌ಗಾಗಿ ಸಂರಕ್ಷಿಸಲಾಗಿದೆ, ಆದರೆ ಆಟಗಾರ ಎಲ್ಲಾ ಪ್ರವೇಶವನ್ನು ಕಳೆದುಕೊಳ್ಳುತ್ತಾರೆ. ಈ ಕ್ರಿಯೆಯನ್ನು ಹಿಂತೆಗೆಯಲು ಸಾಧ್ಯವಿಲ್ಲ.',
  'agent.playerDetail.ops.delete.eyebrow': 'ಅಪಾಯದ ವಲಯ',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'ಈ ಖಾತೆಯನ್ನು ಶಾಶ್ವತವಾಗಿ ಅಳಿಸಿ. ಇದನ್ನು ಹಿಂತೆಗೆಯಲು ಸಾಧ್ಯವಿಲ್ಲ. ಖಚಿತಪಡಿಸಲು ಟೈಪ್ ಮಾಡಿ ',
  'agent.playerDetail.ops.delete.confirm.after': ' ಖಚಿತಪಡಿಸಲು.',
  'agent.playerDetail.ops.delete.cta': 'ಅಳಿಸಿ',
  'agent.playerDetail.ops.delete.toast': 'ಬಳಕೆದಾರನನ್ನು ಅಳಿಸಲಾಗಿದೆ',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'ವಾಲೆಟ್ ಸರಿಹೊಂದಿಸಿ',
  'agent.playerDetail.walletAdjust.mode.credit': '+ ಕ್ರೆಡಿಟ್',
  'agent.playerDetail.walletAdjust.mode.debit': '− ಡೆಬಿಟ್',
  'agent.playerDetail.walletAdjust.field.amount': 'ಮೊತ್ತ (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'ಕಾರಣ · ಲೆಜರ್‌ನಲ್ಲಿ ಸಂಗ್ರಹಿಸಲಾಗಿದೆ',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'ಉದಾ. ಠೇವಣಿ ಹಿಂದಿರುಗಿಸುವಿಕೆ, ಬೋನಸ್, ರಿಫಂಡ್',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'ರೀಚಾರ್ಜ್ ಎಂದು ಗುರುತಿಸಿ',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'ಈ ಕ್ರೆಡಿಟ್ ಅನ್ನು ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'ರೀಚಾರ್ಜ್',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' ಎಂದು ಲೆಜರ್‌ನಲ್ಲಿ ದಾಖಲಿಸಿ. ಈ ಬಳಕೆದಾರನನ್ನು ಯಾರಾದರೂ ರೆಫರ್ ಮಾಡಿದ್ದರೆ, ರೆಫರ್ ಮಾಡಿದವರಿಗೆ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಬೋನಸ್ ಸಿಗುತ್ತದೆ.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'ಧನಾತ್ಮಕ ಮೊತ್ತವನ್ನು ನಮೂದಿಸಿ',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'ಕಾರಣ ಅಗತ್ಯವಿದೆ',
  'agent.playerDetail.walletAdjust.cta.applying': 'ಅನ್ವಯಿಸುತ್ತಿದೆ…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'ಕ್ರೆಡಿಟ್ ಅನ್ವಯಿಸಿ',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'ಡೆಬಿಟ್ ಅನ್ವಯಿಸಿ',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} ಕ್ರೆಡಿಟ್ ಮಾಡಲಾಗಿದೆ',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} ಡೆಬಿಟ್ ಮಾಡಲಾಗಿದೆ',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · ರೆಫರ್ ಮಾಡಿದವರು {bonus} ಬೋನಸ್ ಗಳಿಸಿದರು',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'ಈ ಆಟಗಾರನಿಗೆ ರೀಚಾರ್ಜ್ ಮಾಡಿ',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': ' ಗೆ ಟಾಪ್-ಅಪ್ ಮಾಡಿದಾಗ ಕಾಯಿನ್‌ಗಳು ',
  'agent.playerDetail.rechargeCta.body.bold': 'ನಿಮ್ಮ ವಾಲೆಟ್',
  'agent.playerDetail.rechargeCta.body.after': ' ನಿಂದ ಅವರಿಗೆ ವರ್ಗಾವಣೆಯಾಗುತ್ತವೆ. ನಿಮ್ಮ ಪೂಲ್ ಅನ್ನು ಮೊದಲು ಪರಿಶೀಲಿಸಲಾಗುತ್ತದೆ — ಈ ಕ್ರಿಯೆ ಪರಮಾಣ್ವಿಕವಾಗಿದೆ ಮತ್ತು ಬ್ಯಾಲೆನ್ಸ್ ಅಪರ್ಯಾಪ್ತವಾಗಿದ್ದರೆ ತಿರಸ್ಕರಿಸಲಾಗುತ್ತದೆ.',
  'agent.playerDetail.rechargeCta.thisPlayer': 'ಈ ಆಟಗಾರ',
  'agent.playerDetail.rechargeCta.cta': 'ರೀಚಾರ್ಜ್ ಫಾರ್ಮ್ ತೆರೆಯಿರಿ',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ',
  'agent.playerDetail.security.subtitle': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ಹೊಂದಿಸುತ್ತದೆ ಮತ್ತು ಪ್ರತಿ ಸಾಧನದಿಂದ ಬಳಕೆದಾರನನ್ನು ಸೈನ್ ಔಟ್ ಮಾಡುತ್ತದೆ.',
  'agent.playerDetail.security.field.new': 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್',
  'agent.playerDetail.security.field.confirm': 'ದೃಢೀಕರಿಸಿ',
  'agent.playerDetail.security.error.mismatch': 'ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಾಣಿಕೆಯಾಗುತ್ತಿಲ್ಲ',
  'agent.playerDetail.security.error.tooShort': 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು, ಅಕ್ಷರಗಳು ಮತ್ತು ಅಂಕಿಗಳೊಂದಿಗೆ',
  'agent.playerDetail.security.toast.reset': 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಲಾಗಿದೆ ಮತ್ತು ಎಲ್ಲಾ ಸೆಷನ್‌ಗಳನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗಿದೆ',
  'agent.playerDetail.security.cta.reset': 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ',
  'agent.playerDetail.security.cta.resetting': 'ಮರುಹೊಂದಿಸುತ್ತಿದೆ…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'ವಾಲೆಟ್ ಲೆಜರ್',
  'agent.playerDetail.ledger.entries.one': '{count} ನಮೂದು',
  'agent.playerDetail.ledger.entries.other': '{count} ನಮೂದುಗಳು',
  'agent.playerDetail.ledger.empty': 'ಇನ್ನೂ ಯಾವುದೇ ವಹಿವಾಟುಗಳಿಲ್ಲ.',
  'agent.playerDetail.ledger.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  'agent.playerDetail.ledger.actorSuffix': ' · ಅಡ್ಮಿನ್ #{id} ಮೂಲಕ',
  'agent.playerDetail.ledger.pagination.prev': 'ಹಿಂದಿನ',
  'agent.playerDetail.ledger.pagination.next': 'ಮುಂದಿನ',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'ಮ್ಯಾಚ್ ಬೆಟ್',
  'agent.playerDetail.ledger.kind.win': 'ಮ್ಯಾಚ್ ಬಹುಮಾನ',
  'agent.playerDetail.ledger.kind.refund': 'ರಿಫಂಡ್',
  'agent.playerDetail.ledger.kind.fee': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ',
  'agent.playerDetail.ledger.kind.adminCredit': 'ಅಡ್ಮಿನ್ ಕ್ರೆಡಿಟ್',
  'agent.playerDetail.ledger.kind.adminDebit': 'ಅಡ್ಮಿನ್ ಡೆಬಿಟ್',
  'agent.playerDetail.ledger.kind.recharge': 'ರೀಚಾರ್ಜ್',
  'agent.playerDetail.ledger.kind.referralBonus': 'ರೆಫರಲ್ ಬೋನಸ್',
  'agent.playerDetail.ledger.kind.signupBonus': 'ಸೈನಪ್ ಬೋನಸ್',
  'agent.playerDetail.ledger.kind.offerCredit': 'ಆಫರ್ ಕ್ರೆಡಿಟ್',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'ಹಿಂಪಡೆಯುವಿಕೆ',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'ರಿಫಂಡ್',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'ಪ್ರೊಫೈಲ್ ಸಂಪಾದಿಸಿ',
  'agent.playerDetail.profile.field.username': 'ಬಳಕೆದಾರಹೆಸರು',
  'agent.playerDetail.profile.field.email': 'ಇಮೇಲ್',
  'agent.playerDetail.profile.field.phone': 'ಫೋನ್',
  'agent.playerDetail.profile.field.firstName': 'ಮೊದಲ ಹೆಸರು',
  'agent.playerDetail.profile.field.lastName': 'ಕೊನೆಯ ಹೆಸರು',
  'agent.playerDetail.profile.field.dob': 'ಜನ್ಮ ದಿನಾಂಕ',
  'agent.playerDetail.profile.field.gender': 'ಲಿಂಗ',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'ಪುರುಷ',
  'agent.playerDetail.profile.gender.female': 'ಮಹಿಳೆ',
  'agent.playerDetail.profile.gender.other': 'ಇತರೆ',
  'agent.playerDetail.profile.gender.preferNot': 'ಹೇಳಲು ಇಷ್ಟವಿಲ್ಲ',
  // Location section.
  'agent.playerDetail.profile.location.title': 'ಸ್ಥಳ',
  'agent.playerDetail.profile.location.lastUpdated': '· ಕೊನೆಯ ಅಪ್‌ಡೇಟ್ {date}',
  'agent.playerDetail.profile.location.legacy.label': 'ಫೈಲ್‌ನಲ್ಲಿರುವ ಹಳೆಯ ವಿಳಾಸ',
  'agent.playerDetail.profile.location.legacy.note': ' (ಓದಲು-ಮಾತ್ರ — pre-R3, ಲೊಕೇಟರ್‌ನಿಂದ ಬಳಸಲಾಗಿಲ್ಲ):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'ಉಳಿಸಲು ಏನೂ ಇಲ್ಲ',
  'agent.playerDetail.profile.toast.saved.one': '{count} ಫೀಲ್ಡ್ ಉಳಿಸಲಾಗಿದೆ',
  'agent.playerDetail.profile.toast.saved.other': '{count} ಫೀಲ್ಡ್‌ಗಳು ಉಳಿಸಲಾಗಿದೆ',
  'agent.playerDetail.profile.cta.save': 'ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ',
  'agent.playerDetail.profile.cta.saving': 'ಉಳಿಸುತ್ತಿದೆ…',
  'agent.playerDetail.profile.cta.reset': 'ಮರುಹೊಂದಿಸಿ',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'ಮಾಲೀಕ ಏಜೆಂಟ್',
  'agent.playerDetail.ownership.row.eyebrow': 'ಮಾಲೀಕತ್ವ',
  'agent.playerDetail.ownership.platform.title': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ನೇರ',
  'agent.playerDetail.ownership.platform.body': 'ಯಾವುದೇ ಏಜೆಂಟ್ ಈ ಆಟಗಾರನ ಮಾಲೀಕರಲ್ಲ. ಅಡ್ಮಿನ್ ನೇರವಾಗಿ ನಿರ್ವಹಿಸುತ್ತಾರೆ.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ಏಜೆಂಟ್ #{id}',
  'agent.playerDetail.ownership.owned.body': 'ಈ ಆಟಗಾರನ ರೀಚಾರ್ಜ್‌ಗಳು + ಮ್ಯಾಚ್‌ಗಳ ಮೇಲೆ ಕಮಿಷನ್ ಗಳಿಸುತ್ತಾರೆ.',
  'agent.playerDetail.ownership.cta.change': 'ಬದಲಾಯಿಸಿ',
  'agent.playerDetail.ownership.toast.changedTo': 'ಮಾಲೀಕತ್ವ → {label}',
  'agent.playerDetail.ownership.platformLabel': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ನೇರ',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username} ಅನ್ನು ಮರು-ನಿಯೋಜಿಸಿ',
  'agent.playerDetail.ownership.picker.body': 'ಹೊಸ ಏಜೆಂಟ್ ಆಯ್ಕೆಮಾಡಿ — ಅಥವಾ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ನೇರಕ್ಕೆ ಬೇರ್ಪಡಿಸಿ (ಯಾವುದೇ ಏಜೆಂಟ್ ಮಾಲೀಕರಲ್ಲ). ಭವಿಷ್ಯದ ಕಮಿಷನ್‌ಗಳು ಹೊಸ ಮಾಲೀಕರಿಗೆ ಹೋಗುತ್ತವೆ; ಈಗಾಗಲೇ ಕ್ರೆಡಿಟ್ ಆದ ಸಾಲುಗಳನ್ನು ಮುಟ್ಟಲಾಗುವುದಿಲ್ಲ.',
  'agent.playerDetail.ownership.picker.detach.title': 'ಪ್ಲಾಟ್‌ಫಾರ್ಮ್‌ಗೆ ಬೇರ್ಪಡಿಸಿ',
  'agent.playerDetail.ownership.picker.detach.body': 'ಏಜೆಂಟ್ ಮಾಲೀಕತ್ವವನ್ನು ತೆಗೆದುಹಾಕಿ. ಅಡ್ಮಿನ್ ನೇರವಾಗಿ ನಿರ್ವಹಿಸುತ್ತಾರೆ.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ಏಜೆಂಟ್‌ಗಳನ್ನು ಹುಡುಕಿ…',
  'agent.playerDetail.ownership.picker.loading': 'ಏಜೆಂಟ್‌ಗಳು ಲೋಡ್ ಆಗುತ್ತಿವೆ…',
  'agent.playerDetail.ownership.picker.empty.search': 'ನಿಮ್ಮ ಹುಡುಕಾಟಕ್ಕೆ ಯಾವುದೇ ಏಜೆಂಟ್ ಹೊಂದಾಣಿಕೆಯಾಗುತ್ತಿಲ್ಲ.',
  'agent.playerDetail.ownership.picker.empty.none': 'ಇನ್ನೂ ಯಾವುದೇ ಸಕ್ರಿಯ ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ.',
  'agent.playerDetail.ownership.picker.players.one': '{count} ಆಟಗಾರ',
  'agent.playerDetail.ownership.picker.players.other': '{count} ಆಟಗಾರರು',
  'agent.playerDetail.ownership.picker.current': 'ಪ್ರಸ್ತುತ',
  'agent.playerDetail.ownership.picker.currentUpper': 'ಪ್ರಸ್ತುತ',
  'agent.playerDetail.ownership.picker.close': 'ಮುಚ್ಚಿ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'ಬಾಲಿ ಏಜೆಂಟ್',
  'agent.login.subtitle': 'ನಿಮ್ಮ ಆಟಗಾರರನ್ನು ನಿರ್ವಹಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ',
  'agent.login.field.identifier': 'ಬಳಕೆದಾರಹೆಸರು ಅಥವಾ ಇಮೇಲ್',
  'agent.login.field.password': 'ಪಾಸ್‌ವರ್ಡ್',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'ಏಜೆಂಟ್‌ಗಳಿಗೆ ಮಾತ್ರ. ನೀವು ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಮಾಲೀಕರಾಗಿದ್ದರೆ, ಬಳಸಿ ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '.',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'ಲಾಗಿನ್ ವಿಫಲವಾಗಿದೆ',
  'agent.login.error.invalidCredentials': 'ಅಮಾನ್ಯ ಕ್ರೆಡೆನ್ಷಿಯಲ್‌ಗಳು',
  'agent.login.error.accountSuspended': 'ಖಾತೆಯನ್ನು ಅಮಾನತು ಮಾಡಲಾಗಿದೆ — ನಿಮ್ಮ ಅಡ್ಮಿನಿಸ್ಟ್ರೇಟರ್ ಅನ್ನು ಸಂಪರ್ಕಿಸಿ.',
  'agent.login.error.networkError': 'ನೆಟ್‌ವರ್ಕ್ ದೋಷ — ನಿಮ್ಮ ಸಂಪರ್ಕವನ್ನು ಪರಿಶೀಲಿಸಿ ಮತ್ತು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'ಆಟ',
  'agent.misc.gameTile.actionSuffix': '· ಕಾನ್ಫಿಗರ್ ಮಾಡಿ',
};

const or: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "ମୋ ବିବରଣୀ",
  'profile.group.security': "ସାଇନ୍-ଇନ୍ ଓ ସୁରକ୍ଷା",
  'profile.personal.title': "ବ୍ୟକ୍ତିଗତ ବିବରଣୀ",
  'profile.personal.editTitle': "ବ୍ୟକ୍ତିଗତ ବିବରଣୀ ସମ୍ପାଦନ କରନ୍ତୁ",
  'common.edit': "ସମ୍ପାଦନ",
  'profile.field.name': "ନାମ",
  'profile.field.phone': "ଫୋନ୍ ନମ୍ବର",
  'profile.field.gender': "ଲିଙ୍ଗ",
  'profile.field.location': "ସ୍ଥାନ",
  'profile.gender.male': "ପୁରୁଷ",
  'profile.gender.female': "ମହିଳା",
  'profile.gender.other': "ଅନ୍ୟ",
  'profile.gender.preferNot': "କହିବାକୁ ଚାହୁଁ ନାହିଁ",
  'profile.gender.select': "ବାଛନ୍ତୁ…",
  'profile.location.optionalHint': "ବୈକଳ୍ପିକ — ଆପଣଙ୍କ ଅଞ୍ଚଳ ବାଛନ୍ତୁ କିମ୍ବା ପିନ୍‌କୋଡ୍ ଟାଇପ୍ କରନ୍ତୁ (GPS ଏହାକୁ ଅଧିକ ସଠିକ୍ କରେ)।",
  'profile.lang.title': "ଭାଷା",
  'profile.password.title': "ପାସୱାର୍ଡ",
  'profile.password.desc': "ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ପାସୱାର୍ଡ ବଦଳାନ୍ତୁ। ଅନ୍ୟ ଡିଭାଇସ୍‌ଗୁଡ଼ିକ ସାଇନ୍ ଆଉଟ୍ ହୋଇଯିବ।",
  'profile.password.cta': "ବଦଳାନ୍ତୁ",
  'profile.password.editTitle': "ପାସୱାର୍ଡ ବଦଳାନ୍ତୁ",
  'profile.password.current': "ବର୍ତ୍ତମାନ ପାସୱାର୍ଡ",
  'profile.password.new': "ନୂଆ ପାସୱାର୍ଡ",
  'profile.password.confirm': "ନୂଆ ପାସୱାର୍ଡ ନିଶ୍ଚିତ କରନ୍ତୁ",
  'profile.password.currentPh': "ଆପଣଙ୍କ ବର୍ତ୍ତମାନ ପାସୱାର୍ଡ",
  'profile.password.newPh': "ଏକ ଶକ୍ତିଶାଳୀ ପାସୱାର୍ଡ ବାଛନ୍ତୁ",
  'profile.password.confirmPh': "ନୂଆ ପାସୱାର୍ଡ ପୁଣି ଟାଇପ୍ କରନ୍ତୁ",
  'profile.password.rule': "ଅତି କମ୍‌ରେ ୮ ଅକ୍ଷର, ଗୋଟିଏ ଅକ୍ଷର ଓ ଗୋଟିଏ ସଂଖ୍ୟା ସହିତ।",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "ଏହିପରି ଲିଙ୍କ୍ ହୋଇଛି",
  'profile.telegram.otpHere': "ପାସୱାର୍ଡ ରିସେଟ୍ OTP ଏଠାରେ ଆସେ।",
  'profile.telegram.unlinkChange': "ଅନ୍‌ଲିଙ୍କ୍ ଓ ବଦଳାନ୍ତୁ",
  'profile.telegram.unlinkTitle': "Telegram ଅନ୍‌ଲିଙ୍କ୍ କରିବେ?",
  'profile.telegram.unlink': "ଅନ୍‌ଲିଙ୍କ୍",
  'profile.telegram.keepLinked': "ଲିଙ୍କ୍ ରଖନ୍ତୁ",
  'profile.telegram.unlinkVerified': "ଆପଣ ପରେ ଏକ ଭିନ୍ନ Telegram ଆକାଉଣ୍ଟ ପୁଣି ଲିଙ୍କ୍ କରିପାରିବେ। ଇମେଲ୍ ଯାଞ୍ଚ ଅକ୍ଷୁର୍ଣ୍ଣ ରହିବ।",
  'profile.telegram.unlinkOnly': "Telegram ହେଉଛି ଆପଣଙ୍କ ଏକମାତ୍ର ଯାଞ୍ଚ ହୋଇଥିବା ମାଧ୍ୟମ — ଖେଳ ଜାରି ରଖିବା ପୂର୍ବରୁ ଆପଣଙ୍କୁ ଆକାଉଣ୍ଟ ପୁଣି ଯାଞ୍ଚ କରିବାକୁ ପଡ଼ିପାରେ।",
  'profile.telegram.notLinkedDesc': "ବ୍ୟାକଅପ୍ ଭାବରେ ପାସୱାର୍ଡ ରିସେଟ୍ OTP ପାଇବାକୁ Telegram ଲିଙ୍କ୍ କରନ୍ତୁ।",
  'profile.telegram.openBot': "@BaaliGames_bot ଖୋଲନ୍ତୁ",
  'profile.telegram.generate': "ଲିଙ୍କ୍ ତିଆରି କରନ୍ତୁ",
  'profile.telegram.generating': "ତିଆରି ହେଉଛି…",
  'profile.avatar.cropTitle': "ଆପଣଙ୍କ ଫଟୋ ସଜାଡ଼ନ୍ତୁ",
  'profile.toast.detailsSaved': "ବିବରଣୀ ସେଭ୍ ହେଲା",
  'profile.toast.photoUpdated': "ଫଟୋ ଅପଡେଟ୍ ହେଲା",
  'profile.toast.pwChanged': "ପାସୱାର୍ଡ ବଦଳିଗଲା। ଅନ୍ୟ ଡିଭାଇସ୍‌ଗୁଡ଼ିକ ସାଇନ୍ ଆଉଟ୍ ହୋଇଯାଇଛି।",
  'profile.toast.tgUnlinked': "Telegram ଅନ୍‌ଲିଙ୍କ୍ ହେଲା। ଏକ ଭିନ୍ନ ଚାଟ୍ ସଂଯୋଗ କରିବାକୁ ନୂଆ ଲିଙ୍କ୍ ତିଆରି କରନ୍ତୁ।",
  'profile.err.phone': "ଏକ ୧୦-ଅଙ୍କ ମୋବାଇଲ୍ ନମ୍ବର ଦିଅନ୍ତୁ (କିମ୍ବା ଏହାକୁ ଅପରିବର୍ତ୍ତିତ ରଖନ୍ତୁ)",
  'profile.err.pwMismatch': "ନୂଆ ପାସୱାର୍ଡ ମେଳ ଖାଉ ନାହିଁ",
  'profile.err.pwWeak': "ପାସୱାର୍ଡରେ ୮+ ଅକ୍ଷର, ଗୋଟିଏ ଅକ୍ଷର ଓ ଗୋଟିଏ ସଂଖ୍ୟା ରହିବା ଆବଶ୍ୟକ",
  'profile.err.pwSame': "ନୂଆ ପାସୱାର୍ଡ ବର୍ତ୍ତମାନଠାରୁ ଭିନ୍ନ ହେବା ଆବଶ୍ୟକ",
  'wallet.payout.title': "ସେଭ୍ ହୋଇଥିବା ପେଆଉଟ୍ ବିବରଣୀ",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "ଏପର୍ଯ୍ୟନ୍ତ କିଛି ସେଭ୍ ହୋଇନାହିଁ",
  'wallet.payout.hint': "ଆପଣ ଉଠାଣ କଲାବେଳେ ଆପଣଙ୍କ UPI ID ସ୍ୱୟଂଚାଳିତ ଭାବରେ ସେଭ୍ ହୁଏ। ପରବର୍ତ୍ତୀ ଉଠାଣ ଅନୁରୋଧ ସମୟରେ ଆପଣ ଏହାକୁ ଅପଡେଟ୍ କରିପାରିବେ।",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ଇନ୍ସ୍ଟଲ୍ କରନ୍ତୁ",
  "pwa.install.subhint": "ତୁରନ୍ତ, ଫୁଲ୍-ସ୍କ୍ରିନ୍ ଆକ୍ସେସ୍ ପାଇଁ ଏହାକୁ ଆପଣଙ୍କ ହୋମ୍ ସ୍କ୍ରିନ୍‌ରେ ଯୋଡ଼ନ୍ତୁ — କୌଣସି ଆପ୍ ଷ୍ଟୋର୍ ଆବଶ୍ୟକ ନାହିଁ।",
  "pwa.install.listen": "ଶୁଣନ୍ତୁ",
  "pwa.install.listening": "ବନ୍ଦ କରନ୍ତୁ",
  "pwa.install.installBtn": "ଇନ୍ସ୍ଟଲ୍ କରନ୍ତୁ",
  "pwa.install.notNow": "ବର୍ତ୍ତମାନ ନୁହେଁ",
  "pwa.install.howToShow": "କିପରି ଇନ୍ସ୍ଟଲ୍ କରିବେ",
  "pwa.install.howToHide": "ପଦକ୍ଷେପ ଲୁଚାନ୍ତୁ",
  "pwa.install.guide.iosSafari": "Safari ସ୍କ୍ରିନ୍‌ର ତଳେ, Share ବଟନ୍‌ରେ ଟ୍ୟାପ୍ କରନ୍ତୁ — ଏହା ଉପର ଆଡ଼କୁ ତୀର ଥିବା ଚୌକ ପରି ଦେଖାଯାଏ।\nଖୁଲୁଥିବା ମେନୁରେ ଟିକିଏ ତଳକୁ ସ୍କ୍ରୋଲ୍ କରନ୍ତୁ।\n\"Add to Home Screen\" ବିକଳ୍ପରେ ଟ୍ୟାପ୍ କରନ୍ତୁ।\nଉପର ଡାହାଣ କୋଣରେ Add ଟ୍ୟାପ୍ କରନ୍ତୁ। ଏବେ ଆପ୍ ଯେକୌଣସି ସାଧାରଣ ଆପ୍ ପରି ଆପଣଙ୍କ ହୋମ୍ ସ୍କ୍ରିନ୍‌ରେ ରହିବ।",
  "pwa.install.guide.iosOther": "iPhone କିମ୍ବା iPad ରେ, ହୋମ୍ ସ୍କ୍ରିନ୍‌ରେ ଯୋଡ଼ିବା କେବଳ Safari ବ୍ରାଉଜର୍‌ରେ କାମ କରେ।\nSafari ଖୋଲନ୍ତୁ ଏବଂ ଏହି ସମାନ ୱେବସାଇଟ୍‌କୁ ଯାଆନ୍ତୁ।\nSafari ତଳେ Share ବଟନ୍ ଟ୍ୟାପ୍ କରନ୍ତୁ — ଉପର ଆଡ଼କୁ ତୀର ଥିବା ଚୌକ।\nତଳକୁ ସ୍କ୍ରୋଲ୍ କରନ୍ତୁ, \"Add to Home Screen\" ଟ୍ୟାପ୍ କରନ୍ତୁ, ତାପରେ Add ଟ୍ୟାପ୍ କରନ୍ତୁ।",
  "pwa.install.guide.android": "ଉପରେ ଥିବା Install ବଟନ୍‌ରେ ଟ୍ୟାପ୍ କରନ୍ତୁ।\nଆପଣଙ୍କ ବ୍ରାଉଜର୍ ପଚାରିଲେ, ନିଶ୍ଚିତ କରିବାକୁ Install ଟ୍ୟାପ୍ କରନ୍ତୁ।\nହୋଇଗଲା — ଏବେ ଆପ୍ ଆପଣଙ୍କ ହୋମ୍ ସ୍କ୍ରିନ୍‌ରେ ଅଛି। ଯେକୌଣସି ସାଧାରଣ ଆପ୍ ପରି ଏହାକୁ ଯେକୌଣସି ସମୟରେ ଖୋଲନ୍ତୁ।\nପପ୍-ଅପ୍ ଦେଖାଯାଉନାହିଁ? ଆପଣଙ୍କ ବ୍ରାଉଜର୍ ମେନୁ ଖୋଲନ୍ତୁ — ତିନୋଟି ବିନ୍ଦୁ, ସାଧାରଣତଃ ଉପର ଡାହାଣରେ — ଏବଂ \"Install app\" ଟ୍ୟାପ୍ କରନ୍ତୁ।",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "ସିଧାସଳଖ ରିଚାର୍ଜ",
  "agent.directRecharge.subtitle.player": "ଜଣେ ଖେଳାଳିଙ୍କୁ ବାଛନ୍ତୁ ଏବଂ ଆପଣଙ୍କ ପୁଲ୍‌ରୁ ରିଚାର୍ଜ କରନ୍ତୁ। କୌଣସି ଅନୁରୋଧ ଆବଶ୍ୟକ ନାହିଁ।",
  "agent.directRecharge.subtitle.subagent": "ଜଣେ ସବ୍-ଏଜେଣ୍ଟଙ୍କୁ ବାଛନ୍ତୁ ଏବଂ ଆପଣଙ୍କ ପୁଲ୍‌ରୁ ସେମାନଙ୍କ ପୁଲ୍‌କୁ ରିଚାର୍ଜ କରନ୍ତୁ।",
  "agent.directRecharge.search.player": "ରିଚାର୍ଜ ପାଇଁ ଖେଳାଳି ଖୋଜନ୍ତୁ (ନାମ · ଫୋନ୍ · ଇମେଲ୍)…",
  "agent.directRecharge.search.subagent": "ରିଚାର୍ଜ ପାଇଁ ସବ୍-ଏଜେଣ୍ଟ ଖୋଜନ୍ତୁ (ନାମ · ଇମେଲ୍ · UAID)…",
  "agent.directRecharge.clearSearch": "ସନ୍ଧାନ ସଫା କରନ୍ତୁ",
  "agent.directRecharge.searching": "ଖୋଜୁଛି…",
  "agent.directRecharge.noMatches": "କୌଣସି ମେଳ ନାହିଁ।",
  "agent.directRecharge.empty.player": "ଆପଣଙ୍କ ତାଲିକାରେ ଏବେ କୌଣସି ଖେଳାଳି ନାହାନ୍ତି।",
  "agent.directRecharge.empty.subagent": "ଏବେ କୌଣସି ସିଧାସଳଖ ସବ୍-ଏଜେଣ୍ଟ ନାହାନ୍ତି।",
  "agent.directRecharge.searchFailed": "ସନ୍ଧାନ ବିଫଳ",
  "agent.directRecharge.label.wallet": "ୱାଲେଟ୍",
  "agent.directRecharge.label.pool": "ପୁଲ୍",
  "agent.directRecharge.modal.title.player": "ସିଧାସଳଖ ଖେଳାଳି ରିଚାର୍ଜ",
  "agent.directRecharge.modal.title.subagent": "ସିଧାସଳଖ ସବ୍-ଏଜେଣ୍ଟ ରିଚାର୍ଜ",
  "agent.directRecharge.modal.close": "ବନ୍ଦ କରନ୍ତୁ",
  "agent.directRecharge.modal.targetPool": "ଲକ୍ଷ୍ୟ ପୁଲ୍",
  "agent.directRecharge.modal.operatingWallet": "ଅପରେଟିଂ ୱାଲେଟ୍",
  "agent.directRecharge.modal.dayToDay": "ଦୈନନ୍ଦିନ ବାଲାନ୍ସ",
  "agent.directRecharge.modal.securityDeposit": "ସୁରକ୍ଷା ଜମା",
  "agent.directRecharge.modal.collateral": "ବନ୍ଧକ ପୁଲ୍",
  "agent.directRecharge.modal.amount": "ରାଶି (₹)",
  "agent.directRecharge.modal.overdraw": "ଆପଣ ଆପଣଙ୍କ ପୁଲ୍ (₹{pool}) ଠାରୁ ଅଧିକ ଦେଇ ପାରିବେ ନାହିଁ। ରାଶି କମ୍ କରନ୍ତୁ।",
  "agent.directRecharge.modal.reason": "କାରଣ / ଟିପ୍ପଣୀ",
  "agent.directRecharge.modal.reasonPlaceholder": "ନଗଦ ସଂଗୃହିତ · ref 4291",
  "agent.directRecharge.modal.reasonHint": "ଆପଣଙ୍କ ୱାଲେଟ୍ ଲେଜର୍ + ଅଡିଟ୍ ଲଗ୍‌ରେ ଲେଖାଯାଏ।",
  "agent.directRecharge.modal.proofLabel": "ପେମେଣ୍ଟର ସ୍କ୍ରିନସଟ୍ ଅପଲୋଡ୍ କରନ୍ତୁ (ଐଚ୍ଛିକ)",
  "agent.directRecharge.modal.proofHint": "ଇତିହାସ ମାଧ୍ୟମରେ ପ୍ରାପ୍ତକର୍ତ୍ତାଙ୍କୁ ଦେଖାଯାଏ।",
  "agent.directRecharge.modal.previewEmpty": "କାରବାର ପୂର୍ବାବଲୋକନ ପାଇଁ ରାଶି ଲେଖନ୍ତୁ।",
  "agent.directRecharge.modal.previewBody": "ଆପଣଙ୍କ ୱାଲେଟ୍‌ରୁ {name}ଙ୍କ {pool}କୁ ପଠାଯାଇଛି। ସୁରକ୍ଷିତ ସ୍ଥାନାନ୍ତର; ଲେଜର୍ + ଅଡିଟ୍‌ରେ ରେକର୍ଡ।",
  "agent.directRecharge.modal.pool.playerWallet": "ୱାଲେଟ୍",
  "agent.directRecharge.modal.pool.operatingWallet": "ଅପରେଟିଂ ୱାଲେଟ୍",
  "agent.directRecharge.modal.pool.securityDeposit": "ସୁରକ୍ଷା ଜମା",
  "agent.directRecharge.modal.cancel": "ବାତିଲ୍",
  "agent.directRecharge.modal.send": "₹{amount} ପଠାନ୍ତୁ",
  "agent.directRecharge.badge.player": "ଖେଳାଳି",
  "agent.directRecharge.badge.subagent": "ସବ୍-ଏଜେଣ୍ଟ",
  "agent.directRecharge.modal.yourDirectSubagent": "ଆପଣଙ୍କ ସିଧାସଳଖ ସବ୍-ଏଜେଣ୍ଟ",
  "agent.directRecharge.modal.fundingSource": "ପାଣ୍ଠି ଉତ୍ସ",
  "agent.directRecharge.modal.you": "(ଆପଣ)",
  "agent.directRecharge.modal.yourPool": "ଆପଣଙ୍କ ପୁଲ୍",
  "agent.directRecharge.modal.notSignedIn": "ଏଜେଣ୍ଟ ଭାବେ ସାଇନ୍ ଇନ୍ ହୋଇନାହାନ୍ତି",
  "agent.directRecharge.modal.failed": "ବିଫଳ",

  // Common controls
  'common.signIn': 'ସାଇନ୍ ଇନ୍',
  'common.signUp': 'ସାଇନ୍ ଅପ୍',
  'common.login': 'ଲଗ୍ ଇନ୍',
  'common.logout': 'ଲଗ୍ ଆଉଟ୍',
  'common.continue': 'ଜାରି ରଖନ୍ତୁ',
  'common.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'common.save': 'ସେଭ୍ କରନ୍ତୁ',
  'common.update': 'ଅଦ୍ୟତନ କରନ୍ତୁ',
  'common.clear': 'ସଫା କରନ୍ତୁ',
  'common.back': 'ପଛକୁ',
  'common.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'common.sending': 'ପଠାଉଛି…',
  'common.saving': 'ସେଭ୍ କରୁଛି…',
  'common.optional': 'ବୈକଳ୍ପିକ',
  'common.show': 'ଦେଖାନ୍ତୁ',
  'common.hide': 'ଲୁଚାନ୍ତୁ',
  'common.profile': 'ପ୍ରୋଫାଇଲ୍',
  'common.settings': 'ସେଟିଂସ୍',

  // NavBar / BottomNav
  'nav.brand': 'ବାଲି ଗେମ୍ସ',
  'nav.home': 'ହୋମ୍',
  'nav.games': 'ଗେମ୍ସ',
  'nav.wallet': 'ୱାଲେଟ୍',
  'nav.rewards': 'ପୁରସ୍କାର',
  'nav.profile': 'ପ୍ରୋଫାଇଲ୍',

  // Login page
  'login.title': 'ସ୍ୱାଗତ',
  'login.subtitle': 'ଆପଣଙ୍କ ଖେଳ ଜାରି ରଖିବାକୁ ସାଇନ୍ ଇନ୍ କରନ୍ତୁ।',
  'login.idLabel': 'ୟୁଜରନେମ୍, ଇମେଲ୍ କିମ୍ବା ଫୋନ୍',
  'login.idPlaceholder': 'ୟୁଜରନେମ୍, ଇମେଲ୍ କିମ୍ବା ମୋବାଇଲ୍',
  'login.passwordLabel': 'ପାସ୍‌ୱାର୍ଡ',
  'login.passwordPlaceholder': 'ଆପଣଙ୍କ ପାସ୍‌ୱାର୍ଡ ଦର୍ଜ କରନ୍ତୁ',
  'login.forgot': 'ଭୁଲିଗଲେ?',
  'login.cta': 'ସାଇନ୍ ଇନ୍',
  'login.signingIn': 'ସାଇନ୍ ଇନ୍ ହେଉଛି…',
  'login.newHere': 'ଏଠାରେ ନୂଆ?',
  'login.createAccount': 'ଆକାଉଣ୍ଟ ତିଆରି କରନ୍ତୁ',

  // Register page
  'register.title': 'ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ତିଆରି କରନ୍ତୁ',
  'register.subtitle': 'ଏକ ଟ୍ୟାଗ୍ ବାଛନ୍ତୁ, ଆପଣ ପ୍ରବେଶ କରିଗଲେ।',
  'register.usernameLabel': 'ୟୁଜରନେମ୍',
  'register.usernamePlaceholder': 'ଏକ ଅଦ୍ୱିତୀୟ ହ୍ୟାଣ୍ଡଲ୍ ବାଛନ୍ତୁ',
  'register.usernameHelp': 'ଅକ୍ଷର, ସଂଖ୍ୟା ଓ ଅଣ୍ଡରସ୍କୋର୍। 3–24 ଅକ୍ଷର।',
  'register.emailLabel': 'ଇମେଲ୍',
  'register.emailPlaceholder': 'ଆପଣଙ୍କ ଇମେଲ୍ ଠିକଣା',
  'register.mobileLabel': 'ମୋବାଇଲ୍ ନମ୍ବର୍',
  'register.mobilePlaceholder': '10-ଅଙ୍କର ମୋବାଇଲ୍',
  'register.mobileHelp': 'ଆମେ ସ୍ୱୟଂଚାଳିତ ଭାବେ ଦେଶ କୋଡ୍ ଯୋଗ କରିବୁ।',
  'register.passwordLabel': 'ପାସ୍‌ୱାର୍ଡ',
  'register.passwordPlaceholder': 'ଏକ ଶକ୍ତ ପାସ୍‌ୱାର୍ଡ ବାଛନ୍ତୁ',
  'register.passwordHelp': 'ଅତିକମରେ 8 ଅକ୍ଷର, ଗୋଟିଏ ଅକ୍ଷର ଓ ଗୋଟିଏ ଅଙ୍କ ସହ।',
  'register.referralLabel': 'ରେଫରାଲ୍ କୋଡ୍',
  'register.referralPlaceholder': 'ସାଥୀଙ୍କ ଆମନ୍ତ୍ରଣ କୋଡ୍',
  'register.cta': 'ଆକାଉଣ୍ଟ ତିଆରି କରନ୍ତୁ',
  'register.creating': 'ତିଆରି କରୁଛୁ…',
  'register.alreadyPlayer': 'ପୂର୍ବରୁ ଖେଳାଳି?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'ଆପଣଙ୍କ ବିଷୟରେ', 'register.step.signin': 'ସାଇନ୍-ଇନ୍', 'register.step.finish': 'ଶେଷ',
  'register.step.aboutSubtitle': 'ନିଜ ବିଷୟରେ ଟିକେ କୁହନ୍ତୁ', 'register.step.signinSubtitle': 'କିପରି ସାଇନ୍-ଇନ୍ କରିବେ ସେଟ୍ କରନ୍ତୁ', 'register.step.finishSubtitle': 'ସ୍ଥାନ ବାଛନ୍ତୁ ଏବଂ ଶେଷ କରନ୍ତୁ',
  'register.firstName': 'ପ୍ରଥମ ନାମ', 'register.firstNamePh': 'ଆରଭ୍',
  'register.lastName': 'ଶେଷ ନାମ', 'register.lastNamePh': 'ଶର୍ମା',
  'register.dob': 'ଜନ୍ମ ତାରିଖ', 'register.dobHelp': 'ଖେଳିବାକୁ ଆପଣଙ୍କର ବୟସ 18 ବା ତାହାଠାରୁ ଅଧିକ ହେବା ଉଚିତ',
  'register.v.required': 'ଆବଶ୍ୟକ', 'register.v.nameMin': 'ଅନ୍ତତଃ 2 ଅକ୍ଷର',
  'register.v.tooLong': 'ଅତି ଲମ୍ବା', 'register.v.nameChars': 'କେବଳ ଅକ୍ଷର, ସ୍ପେସ୍ ଓ ହାଇଫେନ୍',
  'register.v.invalidDate': 'ଅବୈଧ ତାରିଖ', 'register.v.minAge': 'ଆପଣଙ୍କର ବୟସ ଅନ୍ତତଃ 18 ହେବା ଆବଶ୍ୟକ',
  'register.v.tooOld': 'ଦୟାକରି ବର୍ଷ ଯାଞ୍ଚ କରନ୍ତୁ',
  'register.v.usernameMin': 'ଅନ୍ତତଃ 3 ଅକ୍ଷର', 'register.v.usernameMax': 'ଅତି ଲମ୍ବା (ସର୍ବାଧିକ 24)',
  'register.v.usernameChars': 'କେବଳ ଅକ୍ଷର, ସଂଖ୍ୟା, ଅଣ୍ଡରସ୍କୋର୍', 'register.v.emailShape': 'ଅବୈଧ ଇମେଲ୍ ପରି ଲାଗୁଛି',
  'register.v.phoneDigits': '10 ଅଙ୍କର ସଂଖ୍ୟା ହେବା ଉଚିତ', 'register.v.phonePrefix': 'ଭାରତୀୟ ମୋବାଇଲ୍ 6–9ରେ ଆରମ୍ଭ ହୁଏ',
  'register.v.pwdMin': 'ଅନ୍ତତଃ 8 ଅକ୍ଷର', 'register.v.pwdLetter': 'ଅନ୍ତତଃ ଗୋଟିଏ ଅକ୍ଷର ଥିବା ଉଚିତ',
  'register.v.pwdNumber': 'ଅନ୍ତତଃ ଗୋଟିଏ ସଂଖ୍ୟା ଥିବା ଉଚିତ',
  'register.v.referralShort': 'ବହୁତ ଛୋଟ', 'register.v.referralChars': 'କେବଳ ଅକ୍ଷର ଓ ସଂଖ୍ୟା',
  'register.v.usernameTaken': 'ଏହି ୟୁଜରନେମ୍ ପୂର୍ବରୁ ନିଆଯାଇଛି',
  'register.v.emailTaken': 'ଏହି ଇମେଲ୍ ପୂର୍ବରୁ ପଞ୍ଜିକୃତ',
  'register.v.phoneTaken': 'ଏହି ନମ୍ବର ପୂର୍ବରୁ ପଞ୍ଜିକୃତ',
  'register.v.noLocation': 'ଜାରି ରଖିବାକୁ ଅନ୍ତତଃ ଗୋଟିଏ ସ୍ଥାନ ପଦ୍ଧତି ସେୟାର କରନ୍ତୁ।',
  'register.welcome.title': 'ସ୍ୱାଗତ', 'register.welcome.subtitle': 'ଆପଣଙ୍କ ଖାତା ପ୍ରସ୍ତୁତ। ବର୍ତ୍ତମାନ ସୁରକ୍ଷିତ କରନ୍ତୁ କିମ୍ବା ସିଧାସଳଖ ଆରମ୍ଭ କରନ୍ତୁ?',
  'register.welcome.verifyTelegram': 'ଟେଲିଗ୍ରାମ୍ ମାଧ୍ୟମରେ ଯାଞ୍ଚ କରନ୍ତୁ',
  'register.welcome.verifyExplain': '⚠ ଗୁରୁତ୍ୱପୂର୍ଣ: ବର୍ତ୍ତମାନ ଟେଲିଗ୍ରାମ୍ ମାଧ୍ୟମରେ ଯାଞ୍ଚ କରନ୍ତୁ। ଯାଞ୍ଚ ବିନା, ପାସୱର୍ଡ ଭୁଲିଗଲେ ଆପଣ ନିଜେ ରିସେଟ୍ କରିପାରିବେ ନାହିଁ — କେବଳ ଆପଣଙ୍କ ଏଜେଣ୍ଟ କିମ୍ବା ଆଡମିନ୍ ରିସେଟ୍ କରିପାରିବେ। ପରେ ପ୍ରୋଫାଇଲ୍‌ରୁ ମଧ୍ୟ ଯାଞ୍ଚ କରିପାରିବେ।',
  'register.welcome.verifyHeads': 'ଏହାକୁ ପରେ ପାଇଁ ସଂରକ୍ଷଣ କରନ୍ତୁ',
  'register.welcome.verifyConsequence': 'ଇମେଲ୍ ଯାଞ୍ଚ ବର୍ତ୍ତମାନ ଉପଲବ୍ଧ ନୁହେଁ। ପରେ ପାସୱର୍ଡ ରିସେଟ୍ କରିବାକୁ ଟେଲିଗ୍ରାମ୍ ମାଧ୍ୟମରେ ଯାଞ୍ଚ କରନ୍ତୁ। ନ କଲେ, ପାସୱର୍ଡ ଭୁଲିଗଲେ କେବଳ ଆପଣଙ୍କ ଏଜେଣ୍ଟ କିମ୍ବା ଆଡମିନ୍ ରିସେଟ୍ କରିପାରିବେ।',
  'register.welcome.continue': 'ଆପ୍‌କୁ ଯାଆନ୍ତୁ', 'register.welcome.continueLogin': 'ଜାରି ରଖିବାକୁ ସାଇନ୍ ଇନ୍ କରନ୍ତୁ', 'register.welcome.useDifferent': 'ଭିନ୍ନ ଖାତା ବ୍ୟବହାର କରନ୍ତୁ',
  'register.geo.title': 'ଆପଣ କେଉଁଠାରୁ ଖେଳୁଛନ୍ତି?', 'register.geo.subtitle': 'ନିକଟବର୍ତ୍ତୀ ଏଜେଣ୍ଟଙ୍କ ସହ ଯୋଡ଼ିବାକୁ ଆମେ ଆପଣଙ୍କ ସ୍ଥାନ ବ୍ୟବହାର କରୁଛୁ।',
  'register.geo.showAlt': 'ସ୍ଥାନ ସେୟାର କରିପାରୁ ନାହାନ୍ତି? ଭିନ୍ନ ପଦ୍ଧତି ବ୍ୟବହାର କରନ୍ତୁ',
  'register.geo.hideAlt': 'ଅନ୍ୟ ପଦ୍ଧତି ଲୁଚାନ୍ତୁ', 'register.geo.captured': 'ସ୍ଥାନ ଗ୍ରହଣ କରାଗଲା',
  'register.geo.capturedHint': 'ଆପଣ ପ୍ରୋଫାଇଲ୍‌ରୁ ଯେକୌଣସି ସମୟରେ ଏହାକୁ ବଦଳାଇ ପାରିବେ।',
  'register.geo.verifyNote': 'ସାଇନ୍-ଅପ୍ ପରେ, ପ୍ରୋଫାଇଲ୍‌ରୁ ଟେଲିଗ୍ରାମ୍ (ପାସୱର୍ଡ ରିସେଟ୍ ପାଇଁ) କିମ୍ବା ଇମେଲ୍ ମାଧ୍ୟମରେ ଯେକୌଣସି ସମୟରେ ଯାଞ୍ଚ କରିପାରିବେ। ଯାଞ୍ଚ ବୈକଳ୍ପିକ।',

  // Verify (post-signup)
  'verify.title': 'ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ଯାଞ୍ଚ କରନ୍ତୁ',
  'verify.subtitle': 'ଗୋଟିଏ ଅପ୍‌ସନ୍ ବାଛନ୍ତୁ — ଏତିକି ଯଥେଷ୍ଟ।',
  'verify.tabTelegram': 'ଟେଲିଗ୍ରାମ୍',
  'verify.tabEmail': 'ଇମେଲ୍',
  'verify.emailSent': 'ଆମେ {email} କୁ ଏକ ଯାଞ୍ଚ ଲିଙ୍କ୍ ପଠାଇଛୁ।',
  'verify.emailHint': 'ଏହି ଡିଭାଇସ୍‌ରେ ଖୋଲନ୍ତୁ। ଦେଖିପାରୁନାହାନ୍ତି? ସ୍ପାମ୍ / ପ୍ରମୋସନ୍ସ ଯାଞ୍ଚ କରନ୍ତୁ।',
  'verify.telegramOpen': '@BaaliGames_bot ଖୋଲନ୍ତୁ',
  'verify.telegramExpiry': 'ଲିଙ୍କ୍ 30 ମିନିଟ୍ ମଧ୍ୟରେ ସମାପ୍ତ ହୋଇଯିବ। ଟେଲିଗ୍ରାମ୍ ଆପଣଙ୍କୁ ଥରେ Start ଦବାଇବାକୁ କୁହେ।',
  'verify.goToLogin': 'ଲଗ୍ ଇନ୍‌କୁ ଯାଆନ୍ତୁ',
  'verify.email.title': 'ଇମେଲ୍ ଯାଞ୍ଚ ହୋଇଗଲା',
  'verify.email.sub': 'ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ସକ୍ରିୟ। ଖେଳିବାର ସମୟ।',
  'verify.email.failed': 'ଯାଞ୍ଚ ବିଫଳ',
  'verify.email.verifying': 'ଯାଞ୍ଚ କରୁଛୁ…',
  'verify.backRegister': 'ରେଜିଷ୍ଟର୍‌କୁ ଫେରନ୍ତୁ',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ଇମେଲ୍ ପୁଣି ପଠାନ୍ତୁ',
  'verify.gate.resent': 'ପଠାଗଲା!',
  'verify.gate.checking': 'କାମ କରୁଛୁ…',
  'verify.gate.tgFallback': 'ଲିଙ୍କ୍ ମାନୁଆଲ୍ ଭାବେ ଖୋଲନ୍ତୁ',
  'verify.gate.waiting': 'ଆମେ ସ୍ୱୟଂଚାଳିତ ଭାବେ ଯାଞ୍ଚ ସନ୍ଧାନ କରିବୁ।',
  'verify.gate.signOut': 'ସାଇନ୍ ଆଉଟ୍',

  // Forgot password
  'forgot.title': 'ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ପୁନରୁଦ୍ଧାର କରନ୍ତୁ',
  'forgot.subtitle': 'ଆମେ ଆପଣଙ୍କୁ ଫେରିବାର ଏକ ଉପାୟ ପଠାଇବୁ।',
  'forgot.idLabel': 'ୟୁଜରନେମ୍, ଇମେଲ୍ କିମ୍ବା ଫୋନ୍',
  'forgot.idPlaceholder': 'ୟୁଜରନେମ୍, ଇମେଲ୍ କିମ୍ବା ମୋବାଇଲ୍',
  'forgot.sendVia': 'ମାଧ୍ୟମରେ ପଠାନ୍ତୁ',
  'forgot.email': 'ଇମେଲ୍',
  'forgot.telegram': 'ଟେଲିଗ୍ରାମ୍ OTP',
  'forgot.telegramHelp': 'ଟେଲିଗ୍ରାମ୍ କେବଳ ତେବେ କାମ କରେ ଯଦି ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ଆମ ବଟ୍ ସହ ସଂଯୁକ୍ତ।',
  'forgot.cta': 'ପୁନରୁଦ୍ଧାର ପଠାନ୍ତୁ',
  'forgot.sentEmail': 'ଯଦି ଆକାଉଣ୍ଟ ଅଛି, ଏକ ରିସେଟ୍ ଲିଙ୍କ୍ ଇମେଲ୍ କରାଯାଇଛି।',
  'forgot.sentTelegram': 'ଯଦି ଆକାଉଣ୍ଟ ଅଛି, ଏକ 6-ଅଙ୍କ କୋଡ୍ ଟେଲିଗ୍ରାମ୍ ମାଧ୍ୟମରେ ପଠାଯାଇଛି।',
  'forgot.redirecting': 'ରିସେଟ୍ ପୃଷ୍ଠାକୁ ରିଡାଇରେକ୍ଟ କରୁଛୁ…',
  'forgot.back': 'ଲଗ୍ ଇନ୍‌କୁ ଫେରନ୍ତୁ',

  // Reset password
  'reset.title': 'ଏକ ନୂତନ ପାସ୍‌ୱାର୍ଡ ସେଟ୍ କରନ୍ତୁ',
  'reset.subOtp': 'ଆମେ ଆପଣଙ୍କୁ ଟେଲିଗ୍ରାମ୍‌ରେ ପଠାଇଥିବା କୋଡ୍ ବ୍ୟବହାର କରନ୍ତୁ।',
  'reset.subLink': 'ଏକ ତାଜା କମ୍ବୋ ବାଛନ୍ତୁ ଏବଂ ସିଧାସଳଖ ଫେରନ୍ତୁ।',
  'reset.otpLabel': '6-ଅଙ୍କ କୋଡ୍',
  'reset.newPasswordLabel': 'ନୂତନ ପାସ୍‌ୱାର୍ଡ',
  'reset.cta': 'ପାସ୍‌ୱାର୍ଡ ଅଦ୍ୟତନ କରନ୍ତୁ',
  'reset.updating': 'ଅଦ୍ୟତନ କରୁଛୁ…',
  'reset.successTitle': 'ପାସ୍‌ୱାର୍ଡ ଅଦ୍ୟତନ ହୋଇଗଲା',
  'reset.successSub': 'ଆପଣ ପ୍ରସ୍ତୁତ। ଖେଳିବାର ସମୟ।',

  // Home (logged-out)
  'home.welcomeTo': 'ସ୍ୱାଗତ',
  'home.heroSub': 'ଏକ ଦକ୍ଷତା-ଭିତ୍ତିକ ଗେମିଂ ହବ୍। ସାଇନ୍ ଇନ୍ କରନ୍ତୁ କିମ୍ବା ପ୍ରବେଶ କରିବାକୁ ଆପଣଙ୍କ ଖେଳାଳି ଆକାଉଣ୍ଟ ତିଆରି କରନ୍ତୁ।',
  'home.skillBased': 'ଦକ୍ଷତା-ଭିତ୍ତିକ',
  'home.skillBasedSub': 'ରଣନୀତି ଜିତେ',
  'home.fairPlay': 'ସତ ଖେଳ',
  'home.fairPlaySub': 'ଆଣ୍ଟି-ଚିଟ୍ ଅନ୍',
  'home.instantPayout': 'UPI ପେଆଉଟ୍',
  'home.instantPayoutSub': '୨୪ ଘଣ୍ଟା ମଧ୍ୟରେ',
  'home.featured': 'ଫିଚର୍ଡ ଗେମ୍ସ',
  'home.tapToPlay': 'ଖେଳିବାକୁ ଟ୍ୟାପ୍ କରନ୍ତୁ',
  'home.ready': 'ଖେଳିବାକୁ ପ୍ରସ୍ତୁତ?',
  'home.dropIn': 'ପ୍ରବେଶ କରନ୍ତୁ ଏବଂ ପ୍ରକୃତ ପୁରସ୍କାର ଜିତନ୍ତୁ।',
  'home.start': 'ଆରମ୍ଭ କରନ୍ତୁ',

  // Home (logged-in)
  'home.welcomeBack': 'ସ୍ୱାଗତ',
  'home.balance': 'ଆପଣଙ୍କ ବାଲାନ୍ସ',
  'home.live': 'ଲାଇଭ୍',
  'home.add': 'ଯୋଗ କରନ୍ତୁ',
  'home.manage': 'ପରିଚାଳନା କରନ୍ତୁ',
  'home.rewards': 'ପୁରସ୍କାର',
  'home.todayChallenge': 'ଆଜିର ଚ୍ୟାଲେଞ୍ଜ୍',
  'home.todayChallengeSub': 'ଏକ ମ୍ୟାଚ୍ ଜିତନ୍ତୁ — ଷ୍ଟ୍ରିକ୍ ବୋନସ୍ ଅର୍ଜନ କରନ୍ତୁ।',
  'home.view': 'ଦେଖନ୍ତୁ',
  'home.yourGames': 'ଆପଣଙ୍କ ଗେମ୍ସ',
  'home.viewAll': 'ସବୁ ଦେଖନ୍ତୁ',

  // Profile tabs
  'profile.details': 'ବିବରଣୀ',
  'profile.address': 'ଠିକଣା',
  'profile.languages': 'ଭାଷା',
  'profile.account': 'ଆକାଉଣ୍ଟ',
  'profile.photo': 'ଫଟୋ',
  'profile.preferred': 'ପସନ୍ଦ ଭାଷା',

  // Profile → Languages tab
  'profile.lang.primary': 'ପ୍ରାଥମିକ ଭାଷା',
  'profile.lang.primarySub': 'ଆପ୍-ବ୍ୟାପୀ ଡିଫଲ୍ଟ',
  'profile.lang.locked': 'ଲକ୍',
  'profile.lang.second': 'ଦ୍ୱିତୀୟ ଭାଷା',
  'profile.lang.pickHelp': 'ଆପଣ ମଧ୍ୟ କୁହନ୍ତି ଯେଉଁ ଭାଷା ତାହା ବାଛନ୍ତୁ। ବାଛିବାକୁ ଏକ କାର୍ଡ ଟ୍ୟାପ୍ କରନ୍ତୁ।',
  'profile.lang.saved': 'ଦ୍ୱିତୀୟକ ଭାଷା ସେଭ୍ ହୋଇଗଲା',
  'profile.lang.cleared': 'ଦ୍ୱିତୀୟକ ଭାଷା ସଫା ହୋଇଗଲା',

  // Language switcher
  'lang.switcher.aria': 'ଭାଷା ବାଛନ୍ତୁ',
  'lang.switcher.title': 'ଆପଣଙ୍କ ଭାଷା ବାଛନ୍ତୁ',
  'lang.switcher.sub': 'ସମଗ୍ର ଆପ୍ ତୁରନ୍ତ ଅଦ୍ୟତନ ହୁଏ।',
  'lang.switcher.current': 'ବର୍ତ୍ତମାନ',

  // Rewards page
  'rewards.title': 'ପୁରସ୍କାର ଓ ଅଫର୍',
  'rewards.subtitle': 'ଆପଣ ବର୍ତ୍ତମାନ ଅର୍ଜନ କିମ୍ବା କ୍ଲେମ୍ କରିପାରୁଥିବା ବୋନସ୍।',
  'rewards.tab.available': 'ଉପଲବ୍ଧ',
  'rewards.tab.inReview': 'ସମୀକ୍ଷା ଅଧୀନରେ',
  'rewards.tab.claimed': 'କ୍ଲେମ୍ କରାଯାଇଛି',
  'rewards.empty.available.title': 'ବର୍ତ୍ତମାନ କୌଣସି ଅଫର୍ ନାହିଁ',
  'rewards.empty.available.body': 'ଶୀଘ୍ର ପୁଣି ଯାଞ୍ଚ କରନ୍ତୁ — ଆଡମିନ୍ ନିୟମିତ ଭାବରେ ନୂତନ ଅଫର୍ ପୋଷ୍ଟ କରନ୍ତି।',
  'rewards.empty.review.title': 'ସମୀକ୍ଷାରେ କିଛି ନାହିଁ',
  'rewards.empty.review.body': 'ଯେଉଁ କ୍ଲେମ୍‌ଗୁଡ଼ିକୁ ଆଡମିନ୍ ଅନୁମୋଦନ ଆବଶ୍ୟକ, ସେଗୁଡ଼ିକ ଏଠାରେ ଦେଖାଯିବ।',
  'rewards.empty.claimed.title': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି କ୍ଲେମ୍ ନାହିଁ',
  'rewards.empty.claimed.body': 'ଥରେ ଆପଣ ଏକ ବୋନସ୍ ଅର୍ଜନ କଲେ, ଏହା ଏକ କ୍ଲେମ୍ ଭାବେ ଏଠାରେ ଦେଖାଯିବ।',
  'rewards.expires': '{date}ରେ ସମାପ୍ତ',
  'rewards.remaining': '{amount} ବାକି',
  'rewards.cap': 'ଲିମିଟ୍',
  'rewards.claim.cta': 'କ୍ଲେମ୍ କରନ୍ତୁ',
  'rewards.claim.submit': 'ଦାଖଲ କରନ୍ତୁ',
  'rewards.claimed.credited': '{date}ରେ {amount} କ୍ଲେମ୍ ହୋଇଗଲା',
  'rewards.claimed.pending': 'ସମୀକ୍ଷା ଅପେକ୍ଷାରେ',
  'rewards.claimed.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'rewards.auto.willCredit': 'ସର୍ତ୍ତ ପୂରଣ ହେଲେ ସ୍ୱୟଂଚାଳିତ କ୍ରେଡିଟ୍ ହୁଏ',
  'rewards.auto.willReview': 'ଆଡମିନ୍ ସମୀକ୍ଷା କ୍ୟୁରେ ପଡ଼ିବ',
  'rewards.row.creditedOn': '{date}ରେ କ୍ରେଡିଟ୍ ହୋଇଗଲା',
  'rewards.row.submittedOn': '{date}ରେ ଦାଖଲ ହୋଇଛି',
  'rewards.toast.credited': 'ବୋନସ୍ କ୍ରେଡିଟ୍ ହୋଇଗଲା — ଆପଣଙ୍କ ୱାଲେଟ୍‌ରେ {amount} ଯୋଗ ହୋଇଗଲା',
  'rewards.toast.submitted': 'ସମୀକ୍ଷା ପାଇଁ ଦାଖଲ ହୋଇଛି',
  'rewards.toast.alreadyClaimed': 'ପୂର୍ବରୁ କ୍ଲେମ୍ ହୋଇସାରିଛି',
  'rewards.toast.rejected': 'କ୍ଲେମ୍ ପ୍ରତ୍ୟାଖ୍ୟାନ ହୋଇଗଲା',

  // Wallet page
  'wallet.balance': 'ବାଲାନ୍ସ',
  'wallet.balance.note': 'ଏଣ୍ଟ୍ରି ତୁରନ୍ତ ଡେବିଟ୍ ହୁଏ; ମ୍ୟାଚ୍ ଶେଷରେ ପୁରସ୍କାର କ୍ରେଡିଟ୍ ହୁଏ।',
  'wallet.addMoney': 'ଟଙ୍କା ଯୋଗ କରନ୍ତୁ',
  'wallet.withdraw': 'ଉଠାଣ',
  'wallet.withdraw.minBalance': 'ଉଠାଇବାକୁ ଅତିକମରେ ₹200 ଆବଶ୍ୟକ',
  'wallet.history.byGame': 'ଗେମ୍ ଅନୁସାରେ ଆପଣଙ୍କ ଇତିହାସ',
  'wallet.history.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଗତିବିଧି ନାହିଁ। ଆପଣଙ୍କ ପ୍ରଥମ ମ୍ୟାଚ୍ ଏଠାରେ ଦେଖାଯିବ।',
  'wallet.activity': 'ସମ୍ପ୍ରତି ଗତିବିଧି',
  'wallet.activity.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଟ୍ରାଞ୍ଜାକ୍ସନ୍ ନାହିଁ।',
  'wallet.net': 'ନେଟ୍',
  'wallet.newer': 'ନୂତନ',
  'wallet.older': 'ପୁରୁଣା',
  'wallet.pending.title': 'ଅପେକ୍ଷାରତ ଅନୁରୋଧ',
  'wallet.pending.recharge': '{amount}ର ରିଚାର୍ଜ୍ — ଆଡମିନ୍ ଅନୁମୋଦନ ଅପେକ୍ଷାରେ',
  'wallet.pending.withdrawal': '{amount}ର ଉଠାଣ — ପେଆଉଟ୍ ଅପେକ୍ଷାରେ',
  'wallet.state.pending': 'ଅପେକ୍ଷାରତ',
  'wallet.state.paid': 'ପ୍ରଦାନ କରାଯାଇଛି',
  'wallet.pending.txnPrefix': 'ଲେନଦେନ:',
  'wallet.pending.upload.label': 'ଶୀଘ୍ର କାର୍ଯ୍ୟ ପାଇଁ ଭୁଗତାନର ସ୍କ୍ରୀନଶଟ୍ ଅପଲୋଡ୍ କରନ୍ତୁ।',
  'wallet.pending.upload.hint':  'ଅନୁରୋଧ ସୃଷ୍ଟି ପରେ ଆପଣଙ୍କ ପାଖରେ ୨୪ ଘଣ୍ଟା ଅଛି।',
  'wallet.pending.upload.failed': 'ଅପଲୋଡ୍ ବିଫଳ',
  'wallet.activity.balanceAfter': 'ବାଲାନ୍ସ {balance}',
  'wallet.activity.range': '{total} ମଧ୍ୟରୁ {from}–{to}',
  'wallet.activity.window': 'ଆପଣଙ୍କ ଗତ {days} ଦିନର କାର୍ଯ୍ୟକଳାପ',
  'wallet.history.summary.one':   '{matches} ମ୍ୟାଚ୍ · ଜିତିଲେ {wins} · ଖର୍ଚ୍ଚ {bets}',
  'wallet.history.summary.other': '{matches} ମ୍ୟାଚ୍ · ଜିତିଲେ {wins} · ଖର୍ଚ୍ଚ {bets}',
  'wallet.history.summary.refunded': ' · ଫେରସ୍ତ {refunds}',
  'ledger.kind.bet': 'ମ୍ୟାଚ୍ ଏଣ୍ଟ୍ରି', 'ledger.kind.win': 'ମ୍ୟାଚ୍ ପୁରସ୍କାର',
  'ledger.kind.refund': 'ମ୍ୟାଚ୍ ଫେରସ୍ତ', 'ledger.kind.forfeit': 'ମ୍ୟାଚ୍ ଜବତ',
  'ledger.kind.fee': 'ପ୍ଲାଟଫର୍ମ୍ ଫିସ୍', 'ledger.kind.recharge': 'ଟପ୍-ଅପ୍',
  'ledger.kind.admin_credit': 'ଆଡମିନ୍ କ୍ରେଡିଟ୍', 'ledger.kind.admin_debit': 'ଆଡମିନ୍ ଡେବିଟ୍',
  'ledger.kind.referral_bonus': 'ରେଫରାଲ୍ ବୋନସ୍', 'ledger.kind.signup_bonus': 'ସାଇନ୍-ଅପ୍ ବୋନସ୍',
  'ledger.kind.offer_credit': 'ଅଫର୍ ପୁରସ୍କାର',
  'ledger.kind.withdrawal_hold': 'ଉଠାଣ ହୋଲ୍ଡ୍', 'ledger.kind.withdrawal_refund': 'ଉଠାଣ ଫେରସ୍ତ',
  'ledger.kind.agent_topup_in': 'ଏଜେଣ୍ଟ ଟପ୍-ଅପ୍', 'ledger.kind.agent_co_fund': 'ଏଜେଣ୍ଟ ଅବଦାନ', 'ledger.kind.turnover_reward': 'କ୍ୟାସବ୍ୟାକ୍',
  'ledger.withdrawalPaid': 'ଉଠାଣ ପ୍ରଦାନ କରାଯାଇଛି', 'ledger.withdrawalPaidPartial': 'ଉଠାଣ ପ୍ରଦାନ (ଆଂଶିକ)',
  'ledger.withdrawalPaid.txn': 'ପ୍ରଦାନ · ଲେନଦେନ {txnId}',
  'ledger.withdrawalRejected': 'ଉଠାଣ ଅସ୍ୱୀକୃତ', 'ledger.withdrawalRejected.note': 'ଧାରଣ କରାଯାଇଥିବା ରାଶି ଫେରସ୍ତ',
  'ledger.withdrawalExpired': 'ଉଠାଣ ସମାପ୍ତ', 'ledger.awaitingProof': 'ପ୍ରମାଣ ଅପେକ୍ଷାରେ',
  'proofPicker.button.compact': 'ସ୍କ୍ରୀନଶଟ୍ ବାଛନ୍ତୁ', 'proofPicker.button.prominent': 'ସ୍କ୍ରୀନଶଟ୍ ଯୋଗ କରନ୍ତୁ',
  'proofPicker.processing': 'ପ୍ରକ୍ରିୟାକରଣ ହେଉଛି…',
  'proofPicker.error.mime': 'କେବଳ JPG, PNG କିମ୍ବା WebP ଚିତ୍ର ଅନୁମତି।',
  'proofPicker.error.size': 'ଫାଇଲ୍ ବହୁତ ବଡ — ସର୍ବାଧିକ 5 MB।',
  'proofPicker.error.processing': 'ଚିତ୍ର ପ୍ରକ୍ରିୟାକରଣ ହୋଇପାରିଲା ନାହିଁ',
  'proofPicker.previewAlt': 'ବାଛିଥିବା ସ୍କ୍ରୀନଶଟ୍ ପୂର୍ବାଭାସ',
  'proofViewer.state.none': 'ସ୍କ୍ରୀନଶଟ୍ ଅପେକ୍ଷାରେ', 'proofViewer.state.available': 'ସ୍କ୍ରୀନଶଟ୍ ଦେଖନ୍ତୁ',
  'proofViewer.state.expired': 'ସ୍କ୍ରୀନଶଟ୍ ଆଉ ଉପଲବ୍ଧ ନାହିଁ', 'proofViewer.state.viewLimit': 'ଦେଖିବା ସୀମା ସମାପ୍ତ',
  'proofViewer.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'proofViewer.title.viewLimit': 'ଆପଣ ଏହି ସ୍କ୍ରୀନଶଟ୍ ସର୍ବାଧିକ ୨ ଥର ଖୋଲିଛନ୍ତି',
  'proofViewer.error.load': 'ସ୍କ୍ରୀନଶଟ୍ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ',
  'proofViewer.lastView': 'ଶେଷ ଦର୍ଶନ — ଚିତ୍ର ସମାପ୍ତ ହେବ',
  'proofViewer.viewsRemaining.one': 'ଆଉ {n} ଥର ଦେଖାଯାଇପାରେ',
  'proofViewer.viewsRemaining.other': 'ଆଉ {n} ଥର ଦେଖାଯାଇପାରେ',
  'proofViewer.imageAlt': 'ଭୁଗତାନ ପ୍ରମାଣ', 'proofViewer.close': 'ବନ୍ଦ କରନ୍ତୁ',

  // Recharge flow
  'recharge.title': 'ଟଙ୍କା ଯୋଗ କରନ୍ତୁ',
  'recharge.step1.subtitle': 'ଆପଣ କେତେ ଯୋଗ କରିବାକୁ ଚାହାଁନ୍ତି?',
  'recharge.step1.amount': 'ରାଶି',
  'recharge.step1.range': 'ସର୍ବନିମ୍ନ {min} · ସର୍ବାଧିକ {max}',
  'recharge.step1.minHint': 'ସର୍ବନିମ୍ନ {min} ରିଚାର୍ଜ୍',
  'recharge.step1.maxHint': 'ସର୍ବାଧିକ {max}',
  'recharge.step1.quickPick': 'ଶୀଘ୍ର ବାଛନ୍ତୁ',
  'recharge.step2.subtitle': "UPI ବ୍ୟବହାର କରି ଭୁଗତାନ କରନ୍ତୁ, ତା'ପରେ ଜାରି ରଖନ୍ତୁ",
  'recharge.step2.payAmount': 'ଏହି ରାଶି ଭୁଗତାନ କରନ୍ତୁ',
  'recharge.step2.iPaid': 'ମୁଁ ଭୁଗତାନ କରିଛି',
  'recharge.step2.howto.1': 'ଆପଣଙ୍କ UPI ଆପ୍ ଖୋଲନ୍ତୁ (Google Pay / PhonePe / Paytm)।',
  'recharge.step2.howto.2': 'ଉପର ରାଶି UPI ID କୁ ଭୁଗତାନ କରନ୍ତୁ କିମ୍ବା QR ସ୍କାନ୍ କରନ୍ତୁ।',
  'recharge.step2.howto.3': 'ଆପଣଙ୍କ UPI ଆପ୍‌ରୁ ଟ୍ରାଞ୍ଜାକ୍ସନ୍ ID କପି କରନ୍ତୁ — ଆପଣଙ୍କୁ ପରବର୍ତ୍ତୀ ଆବଶ୍ୟକ ହେବ।',
  "recharge.locked.title": "ରିଚାର୍ଜ ସାମୟିକ ଭାବେ ଉପଲବ୍ଧ ନୁହେଁ",
  "recharge.locked.body": "ଆମେ ଏହି ରିଚାର୍ଜ ମାର୍ଗର ସମସ୍ୟା ସମାଧାନ କରୁଛୁ। ଆପଣଙ୍କ ୱାଲେଟ୍ ଏବଂ ଉଠାଣ ପ୍ରଭାବିତ ହୋଇନାହିଁ। ଦୟାକରି କିଛି ସମୟ ପରେ ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।",
  "recharge.locked.reassure.withdrawals": "ଉଠାଣ ସ୍ୱାଭାବିକ ଭାବେ କାର୍ଯ୍ୟ କରୁଛି",
  "recharge.locked.reassure.existing": "ଆପଣ ପୂର୍ବରୁ ପଠାଇଥିବା ଭୁଗତାନ ପ୍ରକ୍ରିୟାଧୀନ ଅଛି",
  "recharge.cap.banner": "ବର୍ତ୍ତମାନ ସର୍ବାଧିକ ରିଚାର୍ଜ: {max}. ଗ୍ରହଣକାରୀଙ୍କ କ୍ଷମତା ସାମୟିକ ଭାବେ ସୀମିତ ଅଛି।",
  "recharge.cap.tooHigh": "ରାଶି ଉପଲବ୍ଧ ସୀମା ଠାରୁ ଅଧିକ। ବର୍ତ୍ତମାନ ଆପଣ ସର୍ବାଧିକ {max} ରିଚାର୍ଜ କରିପାରିବେ।",
  "recharge.notConfigured.title": "ରିଚାର୍ଜ ଶୀଘ୍ର ଉପଲବ୍ଧ ହେବ",
  "recharge.notConfigured.body": "ଆମେ ଏହି ରିଚାର୍ଜ ମାର୍ଗର ସେଟଅପ୍ ଶେଷ କରୁଛୁ। ଆପଣଙ୍କ ୱାଲେଟ୍ ଏବଂ ଉଠାଣ ପ୍ରଭାବିତ ହୋଇନାହିଁ। ଦୟାକରି କିଛି ସମୟ ପରେ ପୁଣି ଯାଞ୍ଚ କରନ୍ତୁ।",
  'recharge.step3.subtitle': 'ଆପଣଙ୍କ ଭୁଗତାନ ନିଶ୍ଚିତ କରନ୍ତୁ',
  'recharge.step3.amountPaid': 'ଆପଣ ଭୁଗତାନ କରିଥିବା ରାଶି',
  'recharge.step3.txnId': 'UPI ଟ୍ରାଞ୍ଜାକ୍ସନ୍ ID',
  'recharge.step3.txnIdHelp': 'ଏହାକୁ ଆପଣଙ୍କ UPI ଆପ୍‌ର ଭୁଗତାନ ରସିଦରେ ପାଆନ୍ତୁ — ସାଧାରଣତଃ 12 ଅଙ୍କ।',
  'recharge.step3.note': 'ଆଡମିନ୍ ପାଇଁ ନୋଟ୍',
  'recharge.step3.notePlaceholder': 'ଯଥା: ସ୍ତ୍ରୀଙ୍କ ନମ୍ବର୍‌ରୁ ଭୁଗତାନ କରାଯାଇଛି',
  'recharge.step3.submit': 'ସମୀକ୍ଷା ପାଇଁ ଦାଖଲ କରନ୍ତୁ',
  'recharge.step3.disclaimer': 'ଆଡମିନ୍ UPI ଆପ୍‌ରେ ଭୁଗତାନ ଯାଞ୍ଚ କରନ୍ତି। ଅନୁମୋଦନ ପରେ ୨୪ ଘଣ୍ଟା ମଧ୍ୟରେ ୱାଲେଟ୍ କ୍ରେଡିଟ୍ ହେବ (ପ୍ରାୟତଃ ଶୀଘ୍ର)।',
  'recharge.success.title': 'ଦାଖଲ ହୋଇଛି — ସମୀକ୍ଷା ଅପେକ୍ଷାରେ',
  'recharge.success.body': 'ଆପଣଙ୍କର {amount}ର ରିଚାର୍ଜ୍ ଆଡମିନ୍ କ୍ୟୁରେ ଅଛି। ୨୪ ଘଣ୍ଟା ମଧ୍ୟରେ କ୍ରେଡିଟ୍ ହେବ (ପ୍ରାୟତଃ ଶୀଘ୍ର)।',
  'recharge.success.refId': 'ରେଫରେନ୍ସ',
  'recharge.toast.submitted': 'ରିଚାର୍ଜ୍ ଆଡମିନ୍ ସମୀକ୍ଷା ପାଇଁ ଦାଖଲ ହୋଇଛି',

  // Withdraw flow
  'withdraw.title': 'ଉଠାଣ',
  'withdraw.step1.subtitle': 'ଆମେ ଟଙ୍କା କେଉଁଠିକୁ ପଠାଇବୁ?',
  'withdraw.step2.subtitle': 'ଶେଷ ଯାଞ୍ଚ',
  'withdraw.balance': 'ଉପଲବ୍ଧ ବାଲାନ୍ସ',
  'withdraw.amount': 'ରାଶି',
  'withdraw.max': 'ସର୍ବାଧିକ',
  'withdraw.upiId': 'ଆପଣଙ୍କ UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'ଆମେ ଏଠାକୁ ଟଙ୍କା ପଠାଇବୁ। ଦାଖଲ ପୂର୍ବରୁ ଦୁଇଥର ଯାଞ୍ଚ କରନ୍ତୁ।',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'ଆପଣଙ୍କ ଶେଷ ଉଠାଣରୁ ପୂର୍ବ-ପୂରଣ କରାଯାଇଛି',
  'withdraw.upiShapeErr': 'UPI ID ର ଆକାର yourname@psp ପରି ହେବା ଆବଶ୍ୟକ (ଯଥା lucky@oksbi)',
  'withdraw.phoneShapeErr': 'ଏକ ବୈଧ 10-ଅଙ୍କ ଭାରତୀୟ ମୋବାଇଲ୍ ଦର୍ଜ କରନ୍ତୁ (6, 7, 8 କିମ୍ବା 9 ରୁ ଆରମ୍ଭ ହେବା ଆବଶ୍ୟକ)',
  'withdraw.whatsapp': 'WhatsApp ନମ୍ବର୍',
  'withdraw.errMin': 'ସର୍ବନିମ୍ନ ଉଠାଣ {min}',
  'withdraw.errExceed': 'ରାଶି ଆପଣଙ୍କ ବାଲାନ୍ସଠାରୁ ଅଧିକ',
  'withdraw.minHint': 'ସର୍ବନିମ୍ନ {min} ଉଠାଣ',
  'withdraw.step2.review': 'ସମୀକ୍ଷା',
  'withdraw.step2.heldNotice': 'ଯେତେବେଳେ ଆପଣ ଦାଖଲ କରନ୍ତି, ଏହି ରାଶି ତୁରନ୍ତ ଧାରଣ କରାଯାଏ ତେଣୁ ଅନ୍ୟ କେଉଁଠି ଖର୍ଚ୍ଚ ହୋଇପାରିବ ନାହିଁ। ଆଡମିନ୍ ଏହାକୁ ଆପଣଙ୍କ UPI ID କୁ ପେଆଉଟ୍ କରନ୍ତି ଏବଂ ଭୁଗତାନ ଭାବେ ଚିହ୍ନଟ କରନ୍ତି।',
  'withdraw.heldAfter': 'ଧାରଣ',
  'withdraw.spendableAfter': 'ଖର୍ଚ୍ଚଯୋଗ୍ୟ',
  'withdraw.step2.disclaimer': 'ଯଦି ଆଡମିନ୍ ପ୍ରତ୍ୟାଖ୍ୟାନ କରନ୍ତି, ତେବେ ଧାରଣ କରାଯାଇଥିବା ରାଶି ଆପଣଙ୍କ ୱାଲେଟ୍‌କୁ ଫେରିଯିବ।',
  'withdraw.submit': 'ଦାଖଲ କରନ୍ତୁ',
  'withdraw.success.title': 'ଦାଖଲ ହୋଇଛି — ପେଆଉଟ୍ ଅପେକ୍ଷାରେ',
  'withdraw.success.body': 'ଆପଣଙ୍କର {amount}ର ଉଠାଣ କ୍ୟୁରେ ଅଛି। ୨୪ ଘଣ୍ଟା ମଧ୍ୟରେ ପେଆଉଟ୍ (ପ୍ରାୟତଃ ଶୀଘ୍ର)। ତାହା ପର୍ଯ୍ୟନ୍ତ ଧାରଣ କରାଯାଇଥିବା ରାଶି ସଂରକ୍ଷିତ ରହିବ।',
  'withdraw.success.refId': 'ରେଫରେନ୍ସ',
  'withdraw.toast.submitted': 'ଉଠାଣ କ୍ୟୁ ହୋଇଛି — ଆପଣଙ୍କ ୱାଲେଟ୍‌ରେ ଧାରଣ',

  // Payment receiver card
  'payment.upi.label': 'ଏହି UPI ID କୁ ଭୁଗତାନ କରନ୍ତୁ',
  'payment.upi.copy': 'କପି କରନ୍ତୁ',
  'payment.upiCopied': 'UPI ID କପି ହୋଇଗଲା',
  'payment.openUpiApp': 'ବର୍ତ୍ତମାନ ଭୁଗତାନ କରନ୍ତୁ',
  'payment.openWhatsapp': 'WhatsApp',
  'payment.helpAt': 'ସାହାଯ୍ୟ ଆବଶ୍ୟକ? କଲ୍ କରନ୍ତୁ',
  'payment.qr.label': 'ଭୁଗତାନ ପାଇଁ QR ସ୍କାନ୍ କରନ୍ତୁ',
  'payment.qr.tap': 'ବଡ଼ କରିବାକୁ ଟ୍ୟାପ୍ କରନ୍ତୁ',
  'payment.qr.tapToClose': 'ବନ୍ଦ କରିବାକୁ କୌଣସିଠାରେ ଟ୍ୟାପ୍ କରନ୍ତୁ',
  'payment.notConfigured.title': 'ବର୍ତ୍ତମାନ ରିଚାର୍ଜ୍ ଉପଲବ୍ଧ ନୁହେଁ',
  'payment.notConfigured.contactBody': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି UPI ସେଟ୍ ଅପ୍ ହୋଇନାହିଁ। ଆମକୁ WhatsApp କିମ୍ବା ଫୋନ୍ ମାଧ୍ୟମରେ ଯୋଗାଯୋଗ କରନ୍ତୁ, ଆମେ ଆପଣଙ୍କୁ ରିଚାର୍ଜ୍ କରିବାରେ ସାହାଯ୍ୟ କରିବୁ।',
  'payment.notConfigured.unavailable': 'ରିଚାର୍ଜ୍ ଅସ୍ଥାୟୀ ଭାବେ ଅନୁପଲବ୍ଧ। ଅନୁଗ୍ରହ କରି ପରେ ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।',
  'payment.chooser.title': 'ଆପଣଙ୍କ UPI ଆପ୍ ବାଛନ୍ତୁ',
  'payment.chooser.subtitle': 'ଆପଣ ଭୁଗତାନ କରିବେ',
  'payment.chooser.hint': 'ରାଶି ପୂର୍ବ-ପୂରଣ ସହ ଆପ୍ ଖୋଲିବାକୁ ଆପ୍ ଟ୍ୟାପ୍ କରନ୍ତୁ। ଆପ୍ ପୂର୍ବରୁ ଆପଣଙ୍କ ଫୋନ୍‌ରେ ଇନଷ୍ଟଲ୍ ହୋଇଥିବା ଆବଶ୍ୟକ — ଯଦି "address invalid" ତ୍ରୁଟି ଦେଖନ୍ତି, ପ୍ରଥମେ App Store / Play Store ରୁ ଆପ୍ ଇନଷ୍ଟଲ୍ କରନ୍ତୁ।',

  // Common
  'common.next': 'ପରବର୍ତ୍ତୀ',
  'common.close': 'ବନ୍ଦ କରନ୍ତୁ',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'ଶୀଘ୍ର ଓ ସୁମସୃଣ ଭୁଗତାନ ସେବା ସୁନିଶ୍ଚିତ କରିବାକୁ, ଅନୁଗ୍ରହ କରି ଆପଣଙ୍କ ସଠିକ୍ ଅବସ୍ଥାନ ଓ ଯୋଗାଯୋଗ ନମ୍ବର୍ ପ୍ରଦାନ କରନ୍ତୁ।',
  'geo.change': 'ବଦଳାନ୍ତୁ',
  'geo.gps.button': 'ମୋର ବର୍ତ୍ତମାନ ସ୍ଥାନ ବ୍ୟବହାର କରନ୍ତୁ',
  'geo.gps.benefit': 'ସର୍ବାଧିକ ଶୀଘ୍ର — ଗୋଟିଏ ଟ୍ୟାପ୍, କୌଣସି ଟାଇପିଂ ନାହିଁ।',
  'geo.gps.denied': 'ଲୋକେସନ୍ ଅନୁମତି ପ୍ରତ୍ୟାଖ୍ୟାନ ହୋଇଛି। ପିନକୋଡ୍ ବ୍ୟବହାର କରନ୍ତୁ କିମ୍ବା ତଳେ ମାନୁଆଲ୍ ଭାବେ ବାଛନ୍ତୁ।',
  'geo.gps.failed': 'ଆପଣଙ୍କ ସ୍ଥାନ ପାଇପାରିଲୁ ନାହିଁ। ଏହା ବଦଳରେ ପିନକୋଡ୍ ବ୍ୟବହାର କରନ୍ତୁ।',
  'geo.gps.notResolved': 'ଆମେ ଆପଣଙ୍କ ସ୍ଥାନକୁ ଏକ ଜଣାଶୁଣା ଅଞ୍ଚଳ ସହ ମ୍ୟାପ୍ କରିପାରିଲୁ ନାହିଁ। ଅନୁଗ୍ରହ କରି ମାନୁଆଲ୍ ଭାବେ ବାଛନ୍ତୁ।',
  'geo.pincode.label': 'ପିନକୋଡ୍',
  'geo.pincode.notFound': 'ପିନକୋଡ୍ ମିଳିଲା ନାହିଁ। ଅନୁଗ୍ରହ କରି ଆପଣଙ୍କ ଅଞ୍ଚଳ ମାନୁଆଲ୍ ଭାବେ ବାଛନ୍ତୁ।',
  'geo.pincode.lookupFailed': 'ପିନକୋଡ୍ ଅନୁସନ୍ଧାନ ବିଫଳ। ଅନୁଗ୍ରହ କରି ମାନୁଆଲ୍ ଭାବେ ବାଛନ୍ତୁ।',
  'geo.multipleAreas.label': 'ଏକାଧିକ ଅଞ୍ଚଳ ଏହି ପିନକୋଡ୍ ଅଂଶୀଦାର କରନ୍ତି — ଆପଣଙ୍କଟି ବାଛନ୍ତୁ',
  'geo.multipleAreas.placeholder': 'ଆପଣଙ୍କ ଅଞ୍ଚଳ ବାଛନ୍ତୁ',
  'geo.manual.title': 'ମାନୁଆଲ୍ ଭାବେ ବାଛନ୍ତୁ',
  'geo.manual.close': 'ମାନୁଆଲ୍ ପିକର୍ ଲୁଚାନ୍ତୁ',
  'geo.state.label': 'ରାଜ୍ୟ',
  'geo.state.placeholder': 'ରାଜ୍ୟ ବାଛନ୍ତୁ',
  'geo.district.label': 'ଜିଲ୍ଲା',
  'geo.district.placeholder': 'ଜିଲ୍ଲା ବାଛନ୍ତୁ',
  'geo.area.label': 'ଅଞ୍ଚଳ / ସହର',
  'geo.area.placeholder': 'ଅଞ୍ଚଳ ବାଛନ୍ତୁ',
  'geo.privacy.note': 'କେବଳ ଆପଣଙ୍କ ସ୍ଥାନୀୟ ଏଜେଣ୍ଟଙ୍କୁ ନ୍ୟସ୍ତ କରିବାକୁ ବ୍ୟବହୃତ। କଦାପି ସେୟାର୍ କରାଯାଏ ନାହିଁ।',
  'geo.required.hint': 'ଯଦି ଆପଣଙ୍କ ପାଖରେ ରେଫରାଲ୍ କୋଡ୍ ନାହିଁ, ତେବେ ସ୍ଥାନ ଆବଶ୍ୟକ।',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'ଡ୍ୟାସବୋର୍ଡ',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'ହିସାବ',
  'agent.nav.players': 'ଖେଳାଳି',
  'agent.nav.agents': 'ଏଜେଣ୍ଟ',
  'agent.nav.rewards': 'ପୁରସ୍କାର',
  'agent.nav.profile':   'ପ୍ରୋଫାଇଲ୍',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'ହୋମ୍',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'ହିସାବ',
  'agent.bottomnav.players': 'ଖେଳାଳି',
  'agent.bottomnav.agents': 'ଏଜେଣ୍ଟ',
  'agent.bottomnav.rewards': 'ପୁରସ୍କାର',
  'agent.bottomnav.profile': 'ପ୍ରୋଫାଇଲ୍',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'ପୁଣି ସ୍ୱାଗତ, ',
  'agent.dashboard.header.welcomeFallback': 'ସାଥୀ',
  'agent.dashboard.header.subtitle': 'ଆପଣଙ୍କ ବ୍ୟବସାୟ ଏକ ନଜରରେ — ପୁଲ୍, ଡିପୋଜିଟ୍, ଖେଳାଳି, ଉପ-ଏଜେଣ୍ଟ, ଲାଭ-କ୍ଷତି, ଏକ୍ସପୋଜର୍',
  'agent.dashboard.range.today': 'ଆଜି',
  'agent.dashboard.range.yesterday': 'ଗତକାଲି',
  'agent.dashboard.range.thisWeek': 'ଏହି ସପ୍ତାହ',
  'agent.dashboard.range.lastWeek': 'ଗତ ସପ୍ତାହ',
  'agent.dashboard.range.thisMonth': 'ଏହି ମାସ',
  'agent.dashboard.range.lastMonth': 'ଗତ ମାସ',
  'agent.dashboard.range.complete': 'ସମ୍ପୂର୍ଣ୍ଣ',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'ଡିପୋଜିଟ୍ କଭରେଜ୍ ଲୋଡ୍ ହେଉଛି…',
  'agent.dashboard.depositBar.label': 'ଡିପୋଜିଟ୍ କଭରେଜ୍',
  'agent.dashboard.depositBar.balanceOfTarget': '· ଲକ୍ଷ୍ୟ {target} ମଧ୍ୟରୁ {balance}',
  'agent.dashboard.depositBar.tapToView': 'ସମ୍ପୂର୍ଣ୍ଣ ଡିପୋଜିଟ୍ ପୃଷ୍ଠା ଦେଖିବାକୁ ଟ୍ୟାପ୍ କରନ୍ତୁ।',
  'agent.dashboard.depositBar.caption.suspended': 'ଆକାଉଣ୍ଟ ସ୍ଥଗିତ। ଆଡମିନ୍ଙ୍କୁ ଯୋଗାଯୋଗ କରନ୍ତୁ।',
  'agent.dashboard.depositBar.caption.breach': 'ଆପଣଙ୍କ ୱାଲେଟ୍ ଡିପୋଜିଟ୍ ସିଲିଂର 100% ଖାଇଯାଇଛି। ଉଠାଣ ଲକ୍ ହୋଇଯାଇଛି। ତୁରନ୍ତ ଟପ୍-ଅପ୍ କରନ୍ତୁ।',
  'agent.dashboard.depositBar.caption.warning': 'ଆପଣଙ୍କ ୱାଲେଟ୍ ଡିପୋଜିଟ୍ ସିଲିଂର {pct}% ରେ ଅଛି। ଚେତାବନୀ 80% ରେ, ଲକ୍-ଡାଉନ୍ 100% ରେ।',
  'agent.dashboard.depositBar.caption.caution': 'ଆପଣଙ୍କ ୱାଲେଟ୍ ଡିପୋଜିଟ୍ କଭରେଜ୍ର {pct}% ଖାଇଯାଇଛି। 80% ତଳେ — ଏବେ ସୁରକ୍ଷିତ।',
  'agent.dashboard.depositBar.caption.safe': 'ଡିପୋଜିଟ୍ ସମ୍ପୂର୍ଣ୍ଣ ସୁରକ୍ଷିତ। କୌଣସି କଭରେଜ୍ ଖାଇଯାଉ ନାହିଁ।',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'ଚେତାବନୀ · ଶୂନ୍ୟ ସୁରକ୍ଷା ଜମା',
  'agent.dashboard.locatorSkip.body': 'ଆପଣଙ୍କ ସୁରକ୍ଷା ଜମା {amount}। ଯେପର୍ଯ୍ୟନ୍ତ ଆପଣ ଏହାକୁ ଭରୁନାହାଁନ୍ତି ସେପର୍ଯ୍ୟନ୍ତ ଭୌଗୋଳିକ ଲୋକେଟର୍ରୁ ନୂଆ ଖେଳାଳି ମିଳିବେ ନାହିଁ। ବର୍ତ୍ତମାନର ଖେଳାଳି + ସିଧାସଳଖ ରେଫରାଲ୍ + ମାନୁଆଲ୍ ଆସାଇନମେଣ୍ଟ ପ୍ରଭାବିତ ହେବେ ନାହିଁ — କେବଳ ଅର୍ଗାନିକ୍ ଜିଓ-ରାଉଟେଡ୍ ସାଇନ୍-ଅପ୍ ଆପଣଙ୍କୁ ଛାଡ଼ିଦେବେ।',
  'agent.dashboard.locatorSkip.cta': 'ଜମା ଭରନ୍ତୁ',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'ଡାଉନ୍-ଲାଇନ୍ ଡିଫଲ୍ଟର୍ ଆଲର୍ଟ',
  'agent.dashboard.defaulter.summary.critical.one': 'ଆପଣଙ୍କ {n} ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ଡିଫଲ୍ଟରେ ଅଛନ୍ତି — ସେମାନେ ଲକ୍ ଅଛନ୍ତି କିମ୍ବା ସେମାନଙ୍କ ଡିପୋଜିଟ୍ ଲଙ୍ଘନ କରିଛନ୍ତି।',
  'agent.dashboard.defaulter.summary.critical.other': 'ଆପଣଙ୍କ {n} ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ଡିଫଲ୍ଟରେ ଅଛନ୍ତି — ସେମାନେ ଲକ୍ ଅଛନ୍ତି କିମ୍ବା ସେମାନଙ୍କ ଡିପୋଜିଟ୍ ଲଙ୍ଘନ କରିଛନ୍ତି।',
  'agent.dashboard.defaulter.summary.warning.one': 'ଆପଣଙ୍କ {n} ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ତାଙ୍କ ଡିପୋଜିଟ୍ ସିଲିଂର 80%+ ରେ ଅଛନ୍ତି — ଡିଫଲ୍ଟର୍ ବିପଦ।',
  'agent.dashboard.defaulter.summary.warning.other': 'ଆପଣଙ୍କ {n} ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ସେମାନଙ୍କ ଡିପୋଜିଟ୍ ସିଲିଂର 80%+ ରେ ଅଛନ୍ତି — ଡିଫଲ୍ଟର୍ ବିପଦ।',
  'agent.dashboard.defaulter.worst': 'ସବୁଠାରୁ ଖରାପ: @{username} ଡିପୋଜିଟ୍ ସିଲିଂର {pct}% ରେ।',
  'agent.dashboard.defaulter.tapHint': 'ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ତାଲିକାକୁ ଯିବାକୁ ଟ୍ୟାପ୍ କରନ୍ତୁ। ଡିଫଲ୍ଟର୍ ଧାଡ଼ିଗୁଡ଼ିକ ହାଇଲାଇଟ୍ ହୋଇଛି।',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'ଲକ୍',
  'agent.dashboard.marginPill.caution': 'ସାବଧାନ',
  'agent.dashboard.marginPill.warning': 'ଚେତାବନୀ',
  'agent.dashboard.marginPill.breach': 'ଲଙ୍ଘନ',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'ସକାଳର ବ୍ରିଫିଂ',
  'agent.dashboard.sections.morning.subtitle': 'ଲାଇଭ୍ ସ୍ନାପ୍‌ସଟ୍ — ପ୍ରତ୍ୟେକ ସଂଖ୍ୟା ସ୍ୱୟଂଚାଳିତ ଭାବେ ସତେଜ ହୁଏ',
  'agent.dashboard.sections.activity.title': 'କାର୍ଯ୍ୟକଳାପ',
  'agent.dashboard.sections.activity.subtitle': 'ସବୁଠାରୁ ବେଶି ଖର୍ଚ୍ଚକାରୀ ଏବଂ ସାମ୍ପ୍ରତିକ ଖେଳାଳି ଭୁଗତାନ ଘଟଣା',
  'agent.dashboard.sections.forensic.title': 'ଫୋରେନ୍‌ସିକ୍ ବିବରଣୀ',
  'agent.dashboard.sections.forensic.subtitle': 'କୌଣସି ବିଭାଗକୁ ବିସ୍ତାର କରିବାକୁ ଟ୍ୟାପ୍ କରନ୍ତୁ — ଏହି ପୃଷ୍ଠାକୁ ଶାନ୍ତ ରଖିବାକୁ ଡିଫଲ୍ଟ ଭାବେ ବନ୍ଦ',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'ଲାଭ ଓ କ୍ଷତିର ଗଭୀର ବିଶ୍ଳେଷଣ',
  'agent.dashboard.accordions.pnlDeep.helper': 'ଆପଣଙ୍କ ଆୟର ପ୍ରତି-ସ୍ତମ୍ଭ + ପ୍ରତି-ଗେମ୍ ବିଭାଜନ। ଅନ୍ତର୍ନିହିତ ମ୍ୟାଚ୍ ପାଇଁ ଗେମ୍ ଧାଡ଼ିରେ କ୍ଲିକ୍ କରନ୍ତୁ।',
  'agent.dashboard.accordions.leaderboard.title': 'ଡାଉନ୍-ଲାଇନ୍ ଲିଡରବୋର୍ଡ',
  'agent.dashboard.accordions.leaderboard.helper': 'ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟ ନେଟ୍ ଆୟ ଅନୁଯାୟୀ ଲିଷ୍ଟ। ଋଣାତ୍ମକ ସଂଖ୍ୟାର ଅର୍ଥ ସେହି ଉପ-ଏଜେଣ୍ଟ ଯାହା ଆଣିଥିଲେ ତାହାଠାରୁ ଅଧିକ ଦେଇଛନ୍ତି।',
  'agent.dashboard.accordions.activity.title': 'ଡାଉନ୍-ଲାଇନ୍ କାର୍ଯ୍ୟକଳାପ ଫିଡ୍',
  'agent.dashboard.accordions.activity.helper': 'ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟଙ୍କ ପ୍ରତ୍ୟେକ ମ୍ୟାଚ୍ ସେଟଲ୍‌ମେଣ୍ଟ, ରିଚାର୍ଜ୍ ଓ ରେଟ୍ ପରିବର୍ତ୍ତନ — ଫୋରେନ୍‌ସିକ୍ ଟାଇମ୍‌ଲାଇନ୍।',
  'agent.dashboard.accordions.earnings.title': 'ଆୟ ବିଭାଜନ (ଲାଇଫ୍‌ଟାଇମ୍)',
  'agent.dashboard.accordions.earnings.helper': 'ପ୍ରକାର ଅନୁଯାୟୀ ଲାଇଫ୍‌ଟାଇମ୍ ମୋଟ। ଉପର ସମୟ ଫିଲ୍ଟର୍ ଉପେକ୍ଷା କରେ — ଏଗୁଡ଼ିକ କେବେ ବି ବଦଳନ୍ତି ନାହିଁ।',
  'agent.dashboard.accordions.directSubAgents.title': 'ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ',
  'agent.dashboard.accordions.directSubAgents.helper': 'ଆପଣଙ୍କ ସିଧାସଳଖ ପ୍ରତିନିଧି। ପ୍ରତ୍ୟେକ ନିଜ ଖେଳାଳି + ନିଜ ଉପ-ଏଜେଣ୍ଟ ଚଳାନ୍ତି। ଗୋଲାପୀ/ଅମ୍ବର ରଙ୍ଗର ଧାଡ଼ିଗୁଡ଼ିକ ଡିଫଲ୍ଟର୍ ଅଞ୍ଚଳରେ ଅଛନ୍ତି।',
  'agent.dashboard.accordions.chain.title': 'ଚେନ୍ ଦୃଶ୍ୟମାନତା',
  'agent.dashboard.accordions.chain.helper': 'ଆପଣଙ୍କ ସମଗ୍ର ଡାଉନ୍-ଲାଇନ୍ ପଦଚିହ୍ନ, ସିଧାସଳଖ (ଆପଣ ସମ୍ପାଦନା କରନ୍ତି) ଏବଂ ପରୋକ୍ଷ (ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟ ପରିଚାଳନା କରନ୍ତି) ଭାବେ ଅଲଗା।',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'ଲାଇଭ୍ ଡାଟା — ଫୋକସ୍ ଉପରେ, ପ୍ରତି 60s ରେ, ଏବଂ ଟଙ୍କା ଘୁରିଲେ ସତେଜ ହୁଏ।',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'ମୋ ପୁଲ୍ ବାଲାନ୍ସ',
  'agent.dashboard.cards.poolBalance.helper': 'ଆପଣଙ୍କ ଖର୍ଚ୍ଚଯୋଗ୍ୟ ବାଲାନ୍ସ। ଖେଳାଳିଙ୍କୁ ରିଚାର୍ଜ୍ କରିବାକୁ କିମ୍ବା ଉପ-ଏଜେଣ୍ଟଙ୍କୁ ପଠାଇବାକୁ ଆପଣ ବ୍ୟବହାର କରିପାରିବେ ସେହି ଟଙ୍କା।',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} ରେ ନେଟ୍ {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'ସୁରକ୍ଷା ଜମା',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'ଡିପୋଜିଟ୍ ସ୍ଥିତି',
  'agent.dashboard.cards.depositStatus.helperLoading': 'ଡିପୋଜିଟ୍ ସ୍ଥିତି ଲୋଡ୍ ହେଉଛି…',
  'agent.dashboard.cards.depositStatus.helper': 'ଆପଣଙ୍କ ପୁଲ୍ ବିରୁଦ୍ଧରେ ଜାମିନ। ସ୍ଥିତି ଲକ୍ଷ୍ୟର 50% ରେ ସାବଧାନ, 80% ରେ ଚେତାବନୀ, 100% ରେ ଲଙ୍ଘନକୁ ବଦଳେ।',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} ଠାରୁ',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'ସଞ୍ଚାଳନ ବାଲାନ୍ସ',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'ଡିପୋଜିଟ୍ ବାଲାନ୍ସ',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'ଡିପୋଜିଟ୍ ଲକ୍ଷ୍ୟ',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ଋଣାତ୍ମକ ଏକ୍ସପୋଜର୍',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'କଭରେଜ୍ ଅନୁପାତ',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'ଡିପୋଜିଟ୍ ଲକ୍ଷ୍ୟର ଏତିକି ଋଣାତ୍ମକ ଏକ୍ସପୋଜର୍ ଦ୍ୱାରା ଖାଇଯାଇଛି',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'ସୁରକ୍ଷିତ',
  'agent.dashboard.depositStatus.status.caution': 'ସାବଧାନ',
  'agent.dashboard.depositStatus.status.warning': 'ଚେତାବନୀ',
  'agent.dashboard.depositStatus.status.breach': 'ଲଙ୍ଘନ',
  'agent.dashboard.depositStatus.status.suspended': 'ସ୍ଥଗିତ',
  'agent.dashboard.depositStatus.status.healthy': 'ସୁସ୍ଥ',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'ଖେଳାଳିଙ୍କ ସମୁଦାୟ ୱାଲେଟ୍',
  'agent.dashboard.cards.playersWallet.helper': 'ବର୍ତ୍ତମାନ ଆପଣଙ୍କ ପ୍ରତ୍ୟେକ ଖେଳାଳିଙ୍କ ୱାଲେଟ୍ର ସଂଯୁକ୍ତ ଲାଇଭ୍ ବାଲାନ୍ସ। ଆପଣଙ୍କ ସହ ସଂଯୁକ୍ତ ସମସ୍ତ wallet_points ଧାଡ଼ିର ଯୋଗଫଳ।',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} ଖେଳାଳି · {active} ସକ୍ରିୟ',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} ଖେଳାଳି · {active} ସକ୍ରିୟ',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'ଉପ-ଏଜେଣ୍ଟ ୱାଲେଟ୍ + ଡିପୋଜିଟ୍',
  'agent.dashboard.cards.subAgents.helper': 'ଆପଣଙ୍କ ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟଙ୍କ ଦ୍ୱାରା ଧାରଣ କରାଯାଇଥିବା ସଂଯୁକ୍ତ ପୁଲ୍ ବାଲାନ୍ସ ଏବଂ ସୁରକ୍ଷା ଜମା। ପ୍ରତ୍ୟେକ ଉପ-ଏଜେଣ୍ଟ ନିଜ ଖେଳାଳି + ନିଜ ଡାଉନ୍-ଲାଇନ୍ ଚଳାନ୍ତି।',
  'agent.dashboard.cards.subAgents.field.pool': 'ପୁଲ୍',
  'agent.dashboard.cards.subAgents.field.deposit': 'ଡିପୋଜିଟ୍',
  'agent.dashboard.cards.subAgents.foot.one': '{n} ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ',
  'agent.dashboard.cards.subAgents.foot.other': '{n} ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'ମୋର ଅପେକ୍ଷାରତ ଅନୁରୋଧ',
  'agent.dashboard.cards.myPending.helper': 'ପ୍ରାଥମିକତା 3। ଆଡମିନ୍ଙ୍କୁ ଆପଣଙ୍କ ନିଜର ଦାଖଲ — ଏଗୁଡ଼ିକରେ ଆପଣ କାର୍ଯ୍ୟ କରିପାରିବେ ନାହିଁ, ସେମାନଙ୍କ ଅପେକ୍ଷା।',
  'agent.dashboard.cards.myPending.recharge.one': '{n} ରିଚାର୍ଜ୍ ଆଡମିନ୍ଙ୍କ ଅନୁମୋଦନ ଅପେକ୍ଷାରେ',
  'agent.dashboard.cards.myPending.recharge.other': '{n} ରିଚାର୍ଜ୍ ଆଡମିନ୍ଙ୍କ ଅନୁମୋଦନ ଅପେକ୍ଷାରେ',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} ଉଠାଣ ଆଡମିନ୍ଙ୍କ ଅନୁମୋଦନ ଅପେକ୍ଷାରେ',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} ଉଠାଣ ଆଡମିନ୍ଙ୍କ ଅନୁମୋଦନ ଅପେକ୍ଷାରେ',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'ଖେଳାଳିଙ୍କ ଅପେକ୍ଷାରତ ଅନୁରୋଧ',
  'agent.dashboard.cards.playersPending.helper': 'ପ୍ରାଥମିକତା 1। ଆପଣଙ୍କ ଖେଳାଳି ଏଗୁଡ଼ିକୁ ଅନୁମୋଦନ କିମ୍ବା ଭୁଗତାନ କରିବାକୁ ଆପଣଙ୍କୁ ଅପେକ୍ଷା କରୁଛନ୍ତି। କାର୍ଯ୍ୟ କରିବାକୁ ଟ୍ୟାପ୍ କରନ୍ତୁ।',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} ରିଚାର୍ଜ୍ ଅନୁମୋଦନ ପାଇଁ',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} ରିଚାର୍ଜ୍ ଅନୁମୋଦନ ପାଇଁ',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} ଉଠାଣ ଭୁଗତାନ ପାଇଁ',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} ଉଠାଣ ଭୁଗତାନ ପାଇଁ',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'ଉପ-ଏଜେଣ୍ଟଙ୍କ ଅପେକ୍ଷାରତ ଅନୁରୋଧ',
  'agent.dashboard.cards.subAgentsPending.helper': 'ପ୍ରାଥମିକତା 2। ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟ (ସମ୍ପୂର୍ଣ୍ଣ ଡାଉନ୍-ଲାଇନ୍) ଆପଣଙ୍କଠାରୁ ଏଗୁଡ଼ିକୁ ଅନୁମୋଦନ କିମ୍ବା ଭୁଗତାନ କରିବାକୁ ଅପେକ୍ଷା କରୁଛନ୍ତି। କାର୍ଯ୍ୟାନୁଷ୍ଠାନ ପାଇଁ ଟ୍ୟାପ୍ କରନ୍ତୁ।',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'True Net P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'ଏହି ସମୟସୀମାରେ ଆପଣଙ୍କର ପ୍ରକୃତ ଲାଭ/କ୍ଷତି। ପ୍ଲାଟଫର୍ମ ଫିସ୍ ଶେୟାର୍ + ୱିନ୍/ଲସ୍ ଶେୟାର୍ − ଅଫର୍ କୋ-ଫଣ୍ଡିଂ। ପ୍ରତି-ଗେମ୍ ବିବରଣୀ ପାଇଁ ତଳେ P&L ଗଭୀର-ଅନୁସନ୍ଧାନ ବିସ୍ତାର କରନ୍ତୁ।',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'Plat',
  'agent.dashboard.pillarChip.winLoss': 'W/L',
  'agent.dashboard.pillarChip.offer': 'Offer',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'ଅଫର୍ ବ୍ୟବହାର · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'ଏହି ସମୟସୀମାରେ ଅଫର୍ ମାଧ୍ୟମରେ ଆପଣଙ୍କ ଖେଳାଳିଙ୍କୁ ଦିଆଯାଇଥିବା ମୋଟ ପୁରସ୍କାର ₹। ସେହି ଖର୍ଚ୍ଚରେ ଆପଣଙ୍କ ଅଂଶ ଦେଖିବାକୁ ଆପଣଙ୍କ ଏକ୍ସପୋଜର ପୃଷ୍ଠା ସହ ତୁଳନା କରନ୍ତୁ।',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} ଦାବି ଯୋଡ଼ାଗଲା',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} ଦାବି ଯୋଡ଼ାଗଲା',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'ସର୍ବାଧିକ ଖର୍ଚ୍ଚକାରୀ',
  'agent.dashboard.activity.topSpenders.helper': '{range}ରେ ଆପଣଙ୍କର ସର୍ବାଧିକ ଖର୍ଚ୍ଚକାରୀ ଖେଳାଳି।',
  'agent.dashboard.activity.topSpenders.empty': 'ଏହି ସମୟସୀମାରେ ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଖର୍ଚ୍ଚ ନାହିଁ।',
  'agent.dashboard.activity.recent.title': 'ସାମ୍ପ୍ରତିକ କାର୍ଯ୍ୟକଳାପ',
  'agent.dashboard.activity.recent.helper': 'ଆପଣଙ୍କ ଖେଳାଳିଙ୍କଠାରୁ ପାଞ୍ଚଟି ସବୁଠାରୁ ସାମ୍ପ୍ରତିକ ରିଚାର୍ଜ୍ ଇଭେଣ୍ଟ।',
  'agent.dashboard.activity.recent.empty': 'କୌଣସି ସାମ୍ପ୍ରତିକ କାର୍ଯ୍ୟକଳାପ ନାହିଁ।',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'ଅନୁମୋଦିତ',
  'agent.dashboard.activity.recent.state.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'agent.dashboard.activity.recent.state.pending': 'ଅପେକ୍ଷାରତ',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'ପ୍ରତି ଗେମ୍ · ବ୍ୟାଖ୍ୟା ପାଇଁ ଏକ ଧାଡ଼ିରେ ଟ୍ୟାପ୍ କରନ୍ତୁ',
  'agent.dashboard.pnl.noEarnings': 'ଏହି ସମୟସୀମାରେ କୌଣସି ଆୟ ନାହିଁ।',
  'agent.dashboard.pnl.table.col.pillarGame': 'ପିଲାର୍ / ଗେମ୍',
  'agent.dashboard.pnl.table.col.net': 'ନେଟ୍',
  'agent.dashboard.pnl.table.col.matches': 'ମ୍ୟାଚ୍',
  'agent.dashboard.pnl.ledgerRows.one': '{n} ଲେଜର୍ ଧାଡ଼ି',
  'agent.dashboard.pnl.ledgerRows.other': '{n} ଲେଜର୍ ଧାଡ଼ି',
  'agent.dashboard.pnl.pillar.platformFee': 'ପ୍ଲାଟଫର୍ମ ଫିସ୍',
  'agent.dashboard.pnl.pillar.winLoss': 'ୱିନ୍ / ଲସ୍',
  'agent.dashboard.pnl.pillar.offerReward': 'ଅଫର୍ ପୁରସ୍କାର',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'ସମୟସୀମା: {range} — ଡ୍ୟାସବୋର୍ଡର ସମୟସୀମାର ଏକ କଠୋର ଉପସେଟ୍',
  'agent.dashboard.pnlDrill.loading': 'ମ୍ୟାଚ୍-ସ୍ତରୀୟ ବିବରଣୀ ଲୋଡ୍ ହେଉଛି…',
  'agent.dashboard.pnlDrill.windowTotal': 'ସମୟସୀମାର ମୋଟ:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} ମ୍ୟାଚ୍ ମଧ୍ୟରେ',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} ମ୍ୟାଚ୍ ମଧ୍ୟରେ',
  'agent.dashboard.pnlDrill.noContribution': 'ଏହି ସେଲରେ କୌଣସି ମ୍ୟାଚ୍ ଯୋଗ ହୋଇନାହିଁ।',
  'agent.dashboard.pnlDrill.errorFallback': 'ଗଭୀର-ଅନୁସନ୍ଧାନ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ',
  'agent.dashboard.pnlDrill.table.col.match': 'ମ୍ୟାଚ୍',
  'agent.dashboard.pnlDrill.table.col.role': 'ଭୂମିକା',
  'agent.dashboard.pnlDrill.table.col.base': 'ଆଧାର',
  'agent.dashboard.pnlDrill.table.col.rate': 'ଦର',
  'agent.dashboard.pnlDrill.table.col.earned': 'ଅର୍ଜିତ',
  'agent.dashboard.pnlDrill.table.col.settled': 'ସେଟ୍ଲ ହୋଇଛି',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.dashboard.directDownline.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ନାହିଁ।',
  'agent.dashboard.directDownline.row.player.one': '{n} ଖେଳାଳି',
  'agent.dashboard.directDownline.row.player.other': '{n} ଖେଳାଳି',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} ଉପ-ଏଜେଣ୍ଟ',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} ଉପ-ଏଜେଣ୍ଟ',
  'agent.dashboard.directDownline.row.walletDeposit': 'ୱାଲେଟ୍ {wallet} · ଡିପୋଜିଟ୍ {deposit}',
  'agent.dashboard.directDownline.footer': 'True Net {range} ସମୟସୀମା ପାଇଁ।',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.dashboard.chain.directAgents.title': 'ସିଧାସଳଖ ଏଜେଣ୍ଟ',
  'agent.dashboard.chain.directAgents.subtitle': 'ଆପଣଙ୍କର ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ — ଆପଣ ସେମାନଙ୍କ ଅନୁରୋଧ ଉପରେ କାର୍ଯ୍ୟାନୁଷ୍ଠାନ କରିପାରିବେ।',
  'agent.dashboard.chain.indirectAgents.title': 'ପରୋକ୍ଷ ଏଜେଣ୍ଟ',
  'agent.dashboard.chain.indirectAgents.subtitle': 'ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟଙ୍କ ଦ୍ୱାରା ପରିଚାଳିତ। ଆପଣଙ୍କ ପାଇଁ କେବଳ ପଠନଯୋଗ୍ୟ।',
  'agent.dashboard.chain.directPlayers.title': 'ସିଧାସଳଖ ଖେଳାଳି',
  'agent.dashboard.chain.directPlayers.subtitle': 'ଆପଣଙ୍କ ଖେଳାଳି — ଆପଣ ସେମାନଙ୍କ ରିଚାର୍ଜ୍ ଓ ଉଠାଣ ଉପରେ କାର୍ଯ୍ୟାନୁଷ୍ଠାନ କରନ୍ତି।',
  'agent.dashboard.chain.indirectPlayers.title': 'ପରୋକ୍ଷ ଖେଳାଳି',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟଙ୍କ ଖେଳାଳି। ଆପଣଙ୍କ ପାଇଁ କେବଳ ପଠନଯୋଗ୍ୟ।',
  'agent.dashboard.chain.field.walletTotal': 'ୱାଲେଟ୍ ମୋଟ',
  'agent.dashboard.chain.field.depositTotal': 'ଡିପୋଜିଟ୍ ମୋଟ',
  'agent.dashboard.chain.field.avgPerHead': 'ପ୍ରତି ବ୍ୟକ୍ତି ହାରାହାରି',
  'agent.dashboard.chain.field.pendingRecharge': 'ଅପେକ୍ଷାରତ ରିଚାର୍ଜ୍',
  'agent.dashboard.chain.field.pendingWithdrawal': 'ଅପେକ୍ଷାରତ ଉଠାଣ',
  'agent.dashboard.chain.pendingBadge': '{n} ଅପେକ୍ଷାରତ',
  'agent.dashboard.chain.openInbox': 'ଇନ୍‌ବକ୍ସ ଖୋଲନ୍ତୁ',
  'agent.dashboard.chain.footer': 'ସିଧାସଳଖ = ଆପଣ ନିଜେ ପରିଚାଳନା କରୁଥିବା ସଂଖ୍ୟା। ପରୋକ୍ଷ = ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟଙ୍କ ଦ୍ୱାରା ପରିଚାଳିତ (ଆପଣ ସଂଖ୍ୟା ଦେଖନ୍ତି କିନ୍ତୁ ସେମାନଙ୍କ ଅପେକ୍ଷାରତ ଅନୁରୋଧ ଉପରେ କାର୍ଯ୍ୟାନୁଷ୍ଠାନ କରିପାରିବେ ନାହିଁ — ସେଗୁଡ଼ିକ ସେମାନଙ୍କ ପ୍ୟାରେଣ୍ଟଙ୍କ ପାଖକୁ ଯାଆନ୍ତି)।',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'ଲୋଡ୍ କରିବାରେ ବିଫଳ',
  'agent.activity.filter.window': 'ସମୟସୀମା',
  'agent.activity.filter.kind': 'ପ୍ରକାର',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'ସମସ୍ତ',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'ସମସ୍ତ',
  'agent.activity.kind.matches': 'ମ୍ୟାଚ୍',
  'agent.activity.kind.recharges': 'ରିଚାର୍ଜ୍',
  'agent.activity.kind.rateChanges': 'ଦର ପରିବର୍ତ୍ତନ',
  // Empty + loading states.
  'agent.activity.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.activity.empty.title': 'ଶାନ୍ତ',
  'agent.activity.empty.subtitle': '{window} ସମୟସୀମାରେ କୌଣସି {kindWord}ଇଭେଣ୍ଟ ନାହିଁ।',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'ମ୍ୟାଚ୍ ',
  'agent.activity.empty.kindWord.recharge': 'ରିଚାର୍ଜ୍ ',
  'agent.activity.empty.kindWord.partnership_change': 'ପାର୍ଟନରଶିପ୍ ପରିବର୍ତ୍ତନ ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'ସେଟଲମେଣ୍ଟ',
  'agent.activity.row.earned': 'ଅର୍ଜିତ',
  'agent.activity.row.fromPillarOn': '{pillar}ରୁ ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'ପ୍ଲାଟଫର୍ମ ଫିସ୍',
  'agent.activity.row.pillar.winLoss': 'ୱିନ୍/ଲସ୍',
  'agent.activity.row.pillar.offerReward': 'ଅଫର୍ ପୁରସ୍କାର',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'ରିଚାର୍ଜ୍',
  'agent.activity.row.recharge.of': 'ର',
  'agent.activity.row.recharge.byUser': 'ୟୁଜର୍ #{id} ଦ୍ୱାରା',
  'agent.activity.row.recharge.via': ' ମାଧ୍ୟମରେ ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'ଅନୁମୋଦିତ',
  'agent.activity.row.state.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'agent.activity.row.state.pending': 'ଅପେକ୍ଷାରତ',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'ଦର ପରିବର୍ତ୍ତନ',
  'agent.activity.row.rateChange.dashOn': '— {pillar} ଉପରେ ',
  'agent.activity.row.rateChange.cascade': 'କ୍ୟାସ୍କେଡ୍',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'ଏହି ଭ୍ୟୁ କେବଳ ଏଜେଣ୍ଟମାନଙ୍କ ପାଇଁ।',
  'agent.earnings.stat.poolBalance': 'ପୁଲ୍ ବାଲାନ୍ସ',
  'agent.earnings.stat.totalIn': 'ମୋଟ ଆୟ',
  'agent.earnings.stat.totalOut': 'ମୋଟ ଖର୍ଚ୍ଚ',
  'agent.earnings.section.in': 'ଆୟ (ଇନ୍)',
  'agent.earnings.section.out': 'ଖର୍ଚ୍ଚ (ଆଉଟ୍)',
  'agent.earnings.empty.in': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଆୟ ନାହିଁ। ଯେତେବେଳେ ଆପଣଙ୍କ ଖେଳାଳିମାନେ ରିଚାର୍ଜ୍ କରିବେ କିମ୍ବା ଖେଳିବେ, ତୁମର ଅଂଶ ଏଠାରେ ଦେଖାଯିବ।',
  'agent.earnings.empty.out': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଖର୍ଚ୍ଚ ନାହିଁ।',
  'agent.earnings.viewLedger': 'ସମ୍ପୂର୍ଣ୍ଣ ଲେଜର ଦେଖନ୍ତୁ',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'ଆଡମିନଠାରୁ ଟପ୍-ଅପ୍',
  'agent.earnings.kind.topupFromAdmin.hint': 'ଆଡମିନ ଆପଣଙ୍କ ପୁଲ୍‌କୁ କ୍ରେଡିଟ୍ କଲେ',
  'agent.earnings.kind.topupToPlayer.label': 'ଖେଳାଳିକୁ ଟପ୍-ଅପ୍',
  'agent.earnings.kind.topupToPlayer.hint': 'ଆପଣ ଜଣେ ଖେଳାଳିଙ୍କୁ ରିଚାର୍ଜ୍ କଲେ',
  'agent.earnings.kind.topupFromPlayer.label': 'ଖେଳାଳିଙ୍କ ରିଚାର୍ଜ୍',
  'agent.earnings.kind.topupFromPlayer.hint': 'ଆପଣଙ୍କ ଖେଳାଳିମାନେ ଆପଣଙ୍କ ପୁଲ୍‌କୁ ଭୁଗତାନ କଲେ',
  'agent.earnings.kind.revenueShare.label': 'ରାଜସ୍ୱ ଅଂଶ',
  'agent.earnings.kind.revenueShare.hint': 'ମ୍ୟାଚରୁ ପ୍ଲାଟଫର୍ମ ଫିସ୍‌ର ଅଂଶ',
  'agent.earnings.kind.coFundOffer.label': 'ସହ-ପୋଷିତ ଅଫର୍',
  'agent.earnings.kind.coFundOffer.hint': 'ଅଫର୍ ପେଆଉଟରେ ଆପଣଙ୍କ ଅଂଶ',
  'agent.earnings.kind.adminAdjust.label': 'ଆଡମିନ ସମନ୍ୱୟ',
  'agent.earnings.kind.adminAdjust.hint': 'ମାନୁଆଲ ସୁଧାର',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'ଏଣ୍ଟ୍ରି',
  'agent.earnings.row.entry.other': 'ଏଣ୍ଟ୍ରିଗୁଡ଼ିକ',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'ଅବଧି',
  'agent.leaderboard.filter.pillar': 'ପିଲାର୍',
  'agent.leaderboard.window.all': 'ସବୁ',
  'agent.leaderboard.pillar.all': 'ସବୁ ପିଲାର୍',
  'agent.leaderboard.pillar.platformFee': 'ପ୍ଲାଟଫର୍ମ ଫିସ୍',
  'agent.leaderboard.pillar.winLoss': 'ୱିନ୍ / ଲସ୍',
  'agent.leaderboard.pillar.offerReward': 'ଅଫର୍ ପୁରସ୍କାର',
  'agent.leaderboard.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.leaderboard.empty.title': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଆୟ ନାହିଁ',
  'agent.leaderboard.empty.subtitle.admin': '{window} ୱିଣ୍ଡୋରେ ସବୁ ଏଜେଣ୍ଟଙ୍କ ମଧ୍ୟରେ କୌଣସି ସେଟଲମେଣ୍ଟ କାର୍ଯ୍ୟକଳାପ ନାହିଁ।',
  'agent.leaderboard.empty.subtitle.downline': '{window} ୱିଣ୍ଡୋରେ ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନରୁ କୌଣସି ଆୟ ନାହିଁ। ଯେତେବେଳେ ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟମାନେ ମ୍ୟାଚ୍ ଚଲାଇବେ, ଏହି ବୋର୍ଡ ଭର୍ତ୍ତି ହେବ।',
  'agent.leaderboard.row.matches.one': '{n} ମ୍ୟାଚ୍',
  'agent.leaderboard.row.matches.other': '{n} ମ୍ୟାଚ୍',
  'agent.leaderboard.footer': 'ଆୟ match_settlement_snapshots ରୁ ଆସିଛି। ଗୋଟିଏ ଋଣାତ୍ମକ ସଂଖ୍ୟାର ଅର୍ଥ ସେହି ଏଜେଣ୍ଟ ୱିଣ୍ଡୋ ସାରା ଯେତିକି ଆଣିଥିଲେ ତାହାଠାରୁ ଅଧିକ ଭୁଗତାନ କଲେ।',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'ପ୍ଲାଟ୍',
  'agent.leaderboard.chip.winLoss': 'ୱି/ଲ',
  'agent.leaderboard.chip.offer': 'ଅଫର୍',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'ମାର୍ଜିନ୍ ଚେତାବନୀ · 80% ସୀମା ପହଞ୍ଚିଲା',
  'agent.warning.header.title': 'ଗୋଟିଏ ଖରାପ ସେଟଲମେଣ୍ଟ ଲକ୍-ଆଉଟଠାରୁ ଦୂରତାରେ।',
  'agent.warning.header.body': 'ଏବେ ଲକ୍ ନୁହଁନ୍ତି — କିନ୍ତୁ ଆପଣଙ୍କ ସୁରକ୍ଷା ଜମା କଭରେଜ୍‌ର 80%ରୁ ଅଧିକ ବ୍ୟବହାର ହୋଇଯାଇଛି। ଏବେ ଟପ୍-ଅପ୍ କରନ୍ତୁ ଏବଂ ନିୟନ୍ତ୍ରଣରେ ରୁହନ୍ତୁ।',
  'agent.warning.header.dismissAria': 'ବନ୍ଦ କରନ୍ତୁ',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'ଏହା କାହିଁକି ଦେଖାଯାଉଛି',
  'agent.warning.why.body': 'ସମ୍ପ୍ରତି ଗେମ୍ ସେଟଲମେଣ୍ଟଗୁଡ଼ିକ ଆପଣଙ୍କ ୱାଲେଟ୍‌କୁ ଋଣାତ୍ମକ କରିଦେଲା। ଆପଣଙ୍କ {deposit}ର ସୁରକ୍ଷା ଜମା ସେହି ସୁରକ୍ଷା-ଜାଲ ଯାହାକି ଏହାକୁ ସହ୍ୟ କରେ — ଏବଂ ବର୍ତ୍ତମାନ ତାହାର 80%ରୁ ଅଧିକ ଖର୍ଚ୍ଚ ହୋଇଯାଇଛି। ଆଉ ଟିକେ ଋଣାତ୍ମକ ଆସିଲେ ଆପଣଙ୍କ ୱାଲେଟ୍ + ଜମା ଶୂନକୁ ପହଞ୍ଚିଯିବ ଏବଂ ପ୍ଲାଟଫର୍ମ ଆକାଉଣ୍ଟକୁ ଲକ୍ କରିଦେବ।',
  'agent.warning.why.reassurance': 'ଏହି ଆଲର୍ଟ ଜାଣିଶୁଣି ଶୀଘ୍ର ଦେଖାଯାଏ — ଯେତେବେଳେ ଆପଣ ଏବେ ବି ସମ୍ପୂର୍ଣ୍ଣ ଆପ୍ ବ୍ୟବହାର କରିପାରୁଛନ୍ତି। ଲକ୍-ଆଉଟ୍ ସ୍କ୍ରିନ୍ କେବଳ ସେତେବେଳେ ଦେଖାଯିବ ଯେତେବେଳେ ଆପଣ 100% ପାର କରିବେ, ଏବଂ ଥରେ ତାହା ହୋଇଗଲେ, ଅଧିକାଂଶ ସୁବିଧା ବନ୍ଦ ହୋଇଯିବ ଯେପର୍ଯ୍ୟନ୍ତ ଆପଣ ପୁନରୁଦ୍ଧାର ନ କରନ୍ତି। ଏବେ କାର୍ଯ୍ୟ କରିବା ପରେ କରିବାଠାରୁ ବହୁତ କମ୍ ବ୍ୟାଘାତ ସୃଷ୍ଟି କରେ।',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'ଲକ୍-ଆଉଟ୍ ପୂର୍ବର ବଳକା ସ୍ଥାନ',
  'agent.warning.gauge.suffix': 'ସ୍ଥାନ ବଳକା',
  'agent.warning.gauge.dangerBadge': 'ବିପଦ କ୍ଷେତ୍ରରେ {pct}%',
  'agent.warning.gauge.field.wallet': 'ଆପଣଙ୍କ ୱାଲେଟ୍',
  'agent.warning.gauge.field.deposit': 'ଆପଣଙ୍କ ଡିପୋଜିଟ୍',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'ଯଦି ଆପଣ ଏହାକୁ ଅଣଦେଖା କରନ୍ତି ଏବଂ 100%କୁ ପହଞ୍ଚନ୍ତି',
  'agent.warning.ignore.cardTitle': 'ଆପଣ ତୁରନ୍ତ ହରାଇବେ',
  'agent.warning.ignore.item.dashboard': 'ଆପଣଙ୍କ ଡ୍ୟାସବୋର୍ଡ, P&L ଏବଂ ଲିଡରବୋର୍ଡକୁ ପ୍ରବେଶ',
  'agent.warning.ignore.item.pages': 'ଖେଳାଳି ଏବଂ ଏଜେଣ୍ଟ ପୃଷ୍ଠାଗୁଡ଼ିକ',
  'agent.warning.ignore.item.manualTopup': 'ଆପଣଙ୍କ ପୁଲ୍‌ରୁ ଆପଣଙ୍କ ଖେଳାଳିମାନଙ୍କୁ ମାନୁଆଲ ଟପ୍-ଅପ୍',
  'agent.warning.ignore.item.newWithdrawal': 'ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ନୂଆ ଉଠାଣ ପଠାଇବା',
  'agent.warning.ignore.item.subAgents': 'ଉପ-ଏଜେଣ୍ଟ ଯୋଗ କରିବା କିମ୍ବା ପରିଚାଳନା କରିବା',
  'agent.warning.ignore.item.profile': 'ଆପଣଙ୍କ ପ୍ରୋଫାଇଲ୍ କିମ୍ବା ଭୁଗତାନ ସେଟିଂସ୍ ସମ୍ପାଦନା କରିବା',
  'agent.warning.ignore.footer': 'ଆପଣ ଏବେ ବି ଅପେକ୍ଷାରତ ଅନୁରୋଧଗୁଡ଼ିକୁ ଅନୁମୋଦନ କରିପାରିବେ + ଗୋଟିଏ ଜରୁରୀ ଟପ୍-ଅପ୍ ପଠାଇପାରିବେ — କିନ୍ତୁ ବାକି ସବୁକିଛି ବନ୍ଦ ରହିବ ଯେପର୍ଯ୍ୟନ୍ତ ଆପଣଙ୍କ ୱାଲେଟ୍ + ଡିପୋଜିଟ୍ ଶୂନରୁ ଉପରକୁ ନ ଆସନ୍ତି।',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'ପୁନରୁଦ୍ଧାର ପାଇଁ ଏବେ ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଭୁଗତାନ କରନ୍ତୁ',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'ମୁଁ ଏହାକୁ ପରେ ଦେଖିବି',
  'agent.warning.dismiss.footnote': 'ଯଦି ଆପଣ ଚେତାବନୀ କ୍ଷେତ୍ରରେ ରୁହନ୍ତି ତେବେ ଆମେ 10 ମିନିଟରେ ପୁଣି ମନେ ପକାଇଦେବୁ। ପୁନରୁଦ୍ଧାର ହେଲେ ଅଟୋମାଟିକ୍ ବନ୍ଦ ହୋଇଯିବ।',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'ଆକାଉଣ୍ଟ ଲକ୍ · ବେଲ୍-ଆଉଟ୍ ପୋର୍ଟାଲ୍',
  'agent.lockout.header.title': 'ଆପଣଙ୍କ ଉପରେ ଡିପୋଜିଟ୍ କଭର କରୁଥିବାଠାରୁ ଅଧିକ ବକାୟା ଅଛି।',
  'agent.lockout.header.body': 'ଚିନ୍ତା କରନ୍ତୁ ନାହିଁ — ଏହା ଠିକ୍ ହୋଇପାରେ। ତଳର ଦୁଇଟି ପଦକ୍ଷେପ ସମ୍ପୂର୍ଣ୍ଣ କରନ୍ତୁ ଏବଂ ଆପଣ ଅଟୋମାଟିକ୍ ଅନଲକ୍ ହୋଇଯିବେ।',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'ଏହା କାହିଁକି ହେଲା',
  'agent.lockout.why.body': 'ଗେମ୍ ସେଟଲମେଣ୍ଟ ଆପଣଙ୍କ ୱାଲେଟ୍‌କୁ ଶୂନରୁ ତଳକୁ ନେଇଗଲା। ଆପଣଙ୍କ ସୁରକ୍ଷା ଜମା ସେହି ସୁରକ୍ଷା-ଜାଲ ଯାହାକି ଏହାକୁ ସହ୍ୟ କରେ — କିନ୍ତୁ ଆପଣ ତାହାର 100% ବ୍ୟବହାର କରିସାରିଛନ୍ତି। ଯେପର୍ଯ୍ୟନ୍ତ ଆପଣଙ୍କ ୱାଲେଟ୍ ଏବଂ ଡିପୋଜିଟ୍ ଶୂନରୁ ଉପରକୁ ନ ଆସେ, ଆପଣ ସାଧାରଣ ଭାବରେ କାର୍ଯ୍ୟ କରିପାରିବେ ନାହିଁ।',
  'agent.lockout.why.reassurance': 'ଏହା ଏଥିପାଇଁ ଲକ୍ କରେ ଯେ ଆପଣ ସ୍ଥିର ହେବା ସମୟରେ ନେଟୱାର୍କ ଆପଣଙ୍କ ଡିପୋଜିଟ୍ ବିରୁଦ୍ଧରେ ଅଧିକ ଋଣ ଠୁଳ କରିପାରିବ ନାହିଁ।',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'ସୁରକ୍ଷିତ ପୁନରୁଦ୍ଧାର ଲକ୍ଷ୍ୟ',
  'agent.lockout.gauge.suffix': 'ସୁରକ୍ଷିତ ଭୁଗତାନ ପାଇଁ',
  'agent.lockout.gauge.recoveredBadge': '{pct}% ପୁନରୁଦ୍ଧାର',
  'agent.lockout.gauge.field.wallet': 'ଆପଣଙ୍କ ୱାଲେଟ୍',
  'agent.lockout.gauge.field.deposit': 'ଆପଣଙ୍କ ଡିପୋଜିଟ୍',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'କେବଳ ଅନଲକ୍',
  'agent.lockout.tier.justUnlock.tag': 'ବିପଜ୍ଜନକ',
  'agent.lockout.tier.justUnlock.note': 'ଆପଣଙ୍କୁ ₹0କୁ ଆଣେ — କୌଣସି ନୂଆ କ୍ଷତି ତୁରନ୍ତ ପୁଣି ଲକ୍ କରିଦେବ।',
  'agent.lockout.tier.safeMin.label': 'ସୁରକ୍ଷିତ ସର୍ବନିମ୍ନ',
  'agent.lockout.tier.safeMin.tag': 'ସୁପାରିଶ',
  'agent.lockout.tier.safeMin.note': 'ଡିପୋଜିଟ୍ ({deposit}) + ଆପଣଙ୍କ ଋଣାତ୍ମକ ୱାଲେଟ୍‌ର ଅଧା। ଗୋଟିଏ ଅସଲ କଭର୍ ଛାଡ଼ିଥାଏ।',
  'agent.lockout.tier.fullRecovery.label': 'ସମ୍ପୂର୍ଣ୍ଣ ପୁନରୁଦ୍ଧାର',
  'agent.lockout.tier.fullRecovery.tag': 'ସର୍ବୋତ୍ତମ',
  'agent.lockout.tier.fullRecovery.note': 'ଡିପୋଜିଟ୍ ସମ୍ପୂର୍ଣ୍ଣ ପୁନଃସ୍ଥାପିତ ଏବଂ ୱାଲେଟ୍ ଶୂନକୁ ଫେରାଇ ଅଣାଗଲା।',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'ପୁନରୁଦ୍ଧାରର ଦୁଇଟି ଉପାୟ — ଯାହା ବି (କିମ୍ବା ଉଭୟ) କରନ୍ତୁ',
  'agent.lockout.waysOut.inbox.title': 'ଆପଣଙ୍କ ଇନ୍‌ବକ୍ସରେ କାର୍ଯ୍ୟ କରନ୍ତୁ',
  'agent.lockout.waysOut.inbox.body': 'ଖେଳାଳି ରିଚାର୍ଜ୍ ଅନୁମୋଦନ କରନ୍ତୁ (ନଗଦ ଆଗରୁ ଆପଣଙ୍କ ପାଖରେ ଅଛି) ଏବଂ ଖେଳାଳି ଉଠାଣଗୁଡ଼ିକୁ ଭୁଗତାନ ହୋଇଥିବା ଭାବରେ ଚିହ୍ନଟ କରନ୍ତୁ। ପ୍ରତିଟି ଅନୁମୋଦନ ଆପଣଙ୍କ ୱାଲେଟ୍‌କୁ କ୍ରେଡିଟ୍ କରେ।',
  'agent.lockout.waysOut.inbox.ctaOpen': 'ଇନ୍‌ବକ୍ସ ଖୋଲନ୍ତୁ',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ଇନ୍‌ବକ୍ସ ତଳେ ଖୋଲା ଅଛି',
  'agent.lockout.waysOut.upline.title': 'ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.lockout.waysOut.upline.body': 'ଆପଣଙ୍କ ପ୍ୟାରେଣ୍ଟ ଏଜେଣ୍ଟ (କିମ୍ବା ଆଡମିନ)କୁ UPI ମାଧ୍ୟମରେ ନଗଦ ପଠାନ୍ତୁ, ତା’ପରେ txn id ଦାଖଲ କରନ୍ତୁ। ଥରେ ସେମାନେ ଅନୁମୋଦନ କଲେ, ଆପଣଙ୍କ ୱାଲେଟ୍ ତୁରନ୍ତ କ୍ରେଡିଟ୍ ହୋଇଯିବ।',
  'agent.lockout.waysOut.upline.ctaOpen': 'ଫର୍ମ ଖୋଲନ୍ତୁ',
  'agent.lockout.waysOut.upline.ctaOpened': 'ଫର୍ମ ତଳେ ଖୋଲା ଅଛି',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'ଆପଣ ଏବେ ବି କଣ କରିପାରିବେ (ଏବଂ କଣ ବନ୍ଦ ଅଛି)',
  'agent.lockout.capabilities.canHeader': 'ଆପଣ ଏବେ ବି କରିପାରିବେ',
  'agent.lockout.capabilities.cantHeader': 'ଆପଣ (ଅସ୍ଥାୟୀ ଭାବେ) କରିପାରିବେ ନାହିଁ',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'ଆସୁଥିବା ଖେଳାଳି ରିଚାର୍ଜ୍ ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'ଖେଳାଳି ଉଠାଣଗୁଡ଼ିକୁ ଭୁଗତାନ ହୋଇଥିବା ଭାବେ ଚିହ୍ନଟ କରନ୍ତୁ',
  'agent.lockout.can.approveSubAgentRecharge': 'ଉପ-ଏଜେଣ୍ଟ ରିଚାର୍ଜ୍ ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'ଉପ-ଏଜେଣ୍ଟ ଉଠାଣକୁ ପ୍ରଦତ୍ତ ଭାବେ ଚିହ୍ନିତ କରନ୍ତୁ',
  'agent.lockout.can.submitToUpline': 'ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଏକ ରିଚାର୍ଜ୍ ଦାଖଲ କରନ୍ତୁ',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE ଆଲର୍ଟ ପ୍ରାପ୍ତ କରନ୍ତୁ + ସାଇନ୍ ଆଉଟ୍ କରନ୍ତୁ',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'ପଦକ୍ଷେପ {n} · {title}',
  'agent.lockout.section.inbox.title': 'ଆପଣଙ୍କର ଇନ୍‌ବକ୍ସ — ପ୍ରତ୍ୟେକ ଅନୁମୋଦନ ଆପଣଙ୍କ ୱାଲେଟ୍‌କୁ କ୍ରେଡିଟ୍ କରେ',
  'agent.lockout.section.inbox.subtitle': 'ଖେଳାଳି ଓ ଉପ-ଏଜେଣ୍ଟମାନଙ୍କଠାରୁ ଅପେକ୍ଷାରତ ଭୁଗତାନ ଅନୁରୋଧ ଯାହା ଆପଣଙ୍କ କାର୍ଯ୍ୟାନୁଷ୍ଠାନକୁ ଅପେକ୍ଷା କରୁଛନ୍ତି।',
  'agent.lockout.section.inbox.fromPlayers': 'ଆପଣଙ୍କ ଖେଳାଳିମାନଙ୍କଠାରୁ',
  'agent.lockout.section.inbox.fromSubAgents': 'ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟମାନଙ୍କଠାରୁ',
  'agent.lockout.section.upline.title': 'ୱାଲେଟ୍ ଟପ୍-ଅପ୍ କରିବାକୁ ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.lockout.section.upline.subtitle': 'ପ୍ୟାରେଣ୍ଟଙ୍କ UPI ସାମ୍ନାରେ ଥିଲେ ଶୀଘ୍ର ହୁଏ। ସାଧାରଣତଃ କିଛି ମିନିଟ୍ ମଧ୍ୟରେ ଅନୁମୋଦନ ମିଳିଯାଏ।',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'ରିଚାର୍ଜ୍ · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · {when} ରେ ଦାଖଲ',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · {when} ରେ ଦାଖଲ · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'ଉଠାଣ · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi} କୁ ଭୁଗତାନ କରନ୍ତୁ · {when} ରେ ଦାଖଲ',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi} କୁ ଭୁଗତାନ କରନ୍ତୁ · {whatsapp} · {when} ରେ ଦାଖଲ',
  'agent.lockout.queue.subAgent.recharge.title': 'ଉପ-ଏଜେଣ୍ଟ ରିଚାର୍ଜ୍ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · {when} ରେ ଦାଖଲ',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · {when} ରେ ଦାଖଲ · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'ଉପ-ଏଜେଣ୍ଟ ଉଠାଣ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi} କୁ ଭୁଗତାନ କରନ୍ତୁ · {when} ରେ ଦାଖଲ',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi} କୁ ଭୁଗତାନ କରନ୍ତୁ · {when} ରେ ଦାଖଲ · "{note}"',

  'agent.lockout.queue.action.approve': 'ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.lockout.queue.action.reject': 'ପ୍ରତ୍ୟାଖ୍ୟାନ କରନ୍ତୁ',
  'agent.lockout.queue.action.markPaid': 'ପ୍ରଦତ୍ତ ଭାବେ ଚିହ୍ନିତ କରନ୍ତୁ',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'ଖେଳାଳିଙ୍କୁ ଭୁଗତାନ କରିବା ପାଇଁ ବ୍ୟବହାର କରିଥିବା UPI ଲେଣଦେଣ ID ଦର୍ଜ କରନ୍ତୁ:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'ଉପ-ଏଜେଣ୍ଟଙ୍କୁ ଭୁଗତାନ କରିବା ପାଇଁ ବ୍ୟବହାର କରିଥିବା UPI ଲେଣଦେଣ ID ଦର୍ଜ କରନ୍ତୁ:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'ଏହି ଉଠାଣକୁ ପ୍ରତ୍ୟାଖ୍ୟାନ କରିବେ? ଅଟକାଯାଇଥିବା ରାଶି ଖେଳାଳିଙ୍କୁ ଫେରସ୍ତ ହେବ।',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'ଏହି ଉପ-ଏଜେଣ୍ଟ ଉଠାଣକୁ ପ୍ରତ୍ୟାଖ୍ୟାନ କରିବେ?',
  'agent.lockout.queue.toast.rechargeApproved': 'ରିଚାର୍ଜ୍ ଅନୁମୋଦିତ — ଖେଳାଳିଙ୍କୁ କ୍ରେଡିଟ୍ କରାଗଲା',
  'agent.lockout.queue.toast.rechargeRejected': 'ରିଚାର୍ଜ୍ ପ୍ରତ୍ୟାଖ୍ୟାତ',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'ଉପ-ଏଜେଣ୍ଟ ରିଚାର୍ଜ୍ ଅନୁମୋଦିତ',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'ରିଚାର୍ଜ୍ ପ୍ରତ୍ୟାଖ୍ୟାତ',
  'agent.lockout.queue.toast.withdrawalPaid': 'ଉଠାଣ ପ୍ରଦତ୍ତ ଭାବେ ଚିହ୍ନିତ — ଆପଣଙ୍କ ୱାଲେଟ୍ କ୍ରେଡିଟ୍ ହେଲା',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'ଉପ-ଏଜେଣ୍ଟ ଉଠାଣ ପ୍ରଦତ୍ତ ଭାବେ ଚିହ୍ନିତ — ୱାଲେଟ୍ କ୍ରେଡିଟ୍ ହେଲା',
  'agent.lockout.queue.toast.withdrawalRejected': 'ଉଠାଣ ପ୍ରତ୍ୟାଖ୍ୟାତ (ଖେଳାଳିଙ୍କୁ ଫେରସ୍ତ କରାଗଲା)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'ଉଠାଣ ପ୍ରତ୍ୟାଖ୍ୟାତ',
  'agent.lockout.queue.toast.actionFailed': 'କାର୍ଯ୍ୟାନୁଷ୍ଠାନ ବିଫଳ',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'ବର୍ତ୍ତମାନ ଆପଣଙ୍କ ଖେଳାଳିମାନଙ୍କଠାରୁ କିଛି ନାହିଁ।',
  'agent.lockout.queue.empty.subAgent': 'ବର୍ତ୍ତମାନ ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟମାନଙ୍କଠାରୁ କିଛି ନାହିଁ।',
  'agent.lockout.queue.loading': 'କ୍ୟୁ ଲୋଡ୍ ହେଉଛି…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'ସତେଜ କରନ୍ତୁ — ମୁଁ କଣ ଏବେ ଅନ୍‌ଲକ୍?',
  'agent.lockout.refresh.checking': 'ଯାଞ୍ଚ କରୁଛୁ…',
  'agent.lockout.refresh.toast': 'ସ୍ଥିତି ସତେଜ ହେଲା',
  'agent.lockout.refresh.footnote': 'ଆପଣଙ୍କ ୱାଲେଟ୍ + ଡିପୋଜିଟ୍ ଶୂନ୍ୟ ଉପରକୁ ଚଢ଼ିବା ମାତ୍ରେ ଏହି ପୃଷ୍ଠା ସ୍ୱତଃ ବନ୍ଦ ହୋଇଯିବ।',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'ରିଚାର୍ଜ୍ ବିବରଣୀ ଲୋଡ୍ କରାଯାଇପାରିଲା ନାହିଁ',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ଅନ୍‌ଲକ୍ କରିବାକୁ ଏବେ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.emergencyForm.banner.locked.body': 'ଆପଣଙ୍କ ଖେଳାଳିମାନେ ଆପଣଙ୍କ ଡିପୋଜିଟ୍ ଜାମିନ କଭର୍ କରିପାରୁଥିବାଠାରୁ ଅଧିକ ଜିତିଛନ୍ତି। ଆପଣ ନିଜର ନକାରାତ୍ମକ ବାଲାନ୍ସ ଦୂର ନକରିବା ପର୍ଯ୍ୟନ୍ତ ପ୍ଲାଟଫର୍ମ ବ୍ୟବହାର କରିପାରିବେ ନାହିଁ।',
  'agent.emergencyForm.banner.warning.title': 'ଶୀଘ୍ର ଟପ୍-ଅପ୍ କରନ୍ତୁ — ଆପଣ ସୀମା ନିକଟରେ ଅଛନ୍ତି',
  'agent.emergencyForm.banner.warning.body': 'ଆପଣଙ୍କ ୱାଲେଟ୍ ଆପଣଙ୍କ ଡିପୋଜିଟ୍‌ର 80% ପାର କରିଛି। 100% ରେ ପହଞ୍ଚିବା ପୂର୍ବରୁ ଟପ୍-ଅପ୍ କରନ୍ତୁ — ଥରେ ପହଞ୍ଚିଗଲେ ଆପଣଙ୍କ ଆକାଉଣ୍ଟ ଲକ୍ ହୋଇଯିବ।',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'ଆପଣଙ୍କ ୱାଲେଟ୍',
  'agent.emergencyForm.row.deposit': 'ଆପଣଙ୍କ ଡିପୋଜିଟ୍',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'ଆପଣ କାହିଁକି ଅଟକି ଯାଇଛନ୍ତି',
  'agent.emergencyForm.tier.eyebrow.warning': 'ଆପଣ କାହିଁକି ଝୁଁକିରେ ଅଛନ୍ତି',
  'agent.emergencyForm.tier.context.locked': 'ଆପଣଙ୍କ ୱାଲେଟ୍ {wallet} ରେ ପହଞ୍ଚିଛି — ଏହା ଆପଣଙ୍କ {deposit} ର ସୁରକ୍ଷା ଜମା କଭର ବାହାରେ। ପ୍ଲାଟଫର୍ମ ଆପଣଙ୍କୁ ଏଠାରେ ଲକ୍ କରେ ଯାହାଫଳରେ ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନର କ୍ଷତି ଖାଲି ବଫର ଉପରେ ଆଉ ନ ବଢ଼େ।',
  'agent.emergencyForm.tier.context.warning': 'ଆପଣଙ୍କ ୱାଲେଟ୍ {wallet} ରେ ପହଞ୍ଚିଛି — ଏହା ଆପଣଙ୍କ {deposit} ର ସୁରକ୍ଷା ଜମା କଭର‌ର 80% ରୁ ଅଧିକ। ଆଉ ଗୋଟିଏ ଖରାପ ସେଟ୍ଲମେଣ୍ଟ ଆପଣଙ୍କୁ 100% ପାର କରାଇ ଆକାଉଣ୍ଟ ଲକ୍ କରିଦେବ।',
  'agent.emergencyForm.tier.pickHowMuch': 'କେତେ ଭୁଗତାନ କରିବେ ବାଛନ୍ତୁ',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'କେବଳ ଅନ୍‌ଲକ୍',
  'agent.emergencyForm.tier.bareMin.label.warning': 'କେବଳ ଚେତାବନୀରୁ ବାହାର',
  'agent.emergencyForm.tier.bareMin.tag': 'ଝୁଁକିପୂର୍ଣ୍ଣ',
  'agent.emergencyForm.tier.bareMin.note.locked': 'କେବଳ ଆପଣଙ୍କ ୱାଲେଟ୍ + ଡିପୋଜିଟ୍‌କୁ ₹0 କୁ ଆଣିଥାଏ। କୌଣସି ନୂଆ କ୍ଷତି ଆପଣଙ୍କୁ ତୁରନ୍ତ ପୁଣି ଲକ୍ କରିଦେବ।',
  'agent.emergencyForm.tier.bareMin.note.warning': 'କେବଳ ଆପଣଙ୍କୁ 80% ରେଖା ଠାରୁ ସାମାନ୍ୟ ଉପରକୁ ଆଣିଥାଏ। କୌଣସି ନୂଆ କ୍ଷତି ଚେତାବନୀକୁ ପୁଣି ଟ୍ରିଗର କରିବ — କିମ୍ବା ଆପଣଙ୍କୁ ଲକ୍-ଆଉଟ୍‌ରେ ଠେଲିଦେବ।',
  'agent.emergencyForm.tier.safeMin.label': 'ସୁରକ୍ଷିତ ନ୍ୟୂନତମ',
  'agent.emergencyForm.tier.safeMin.tag': 'ସୁପାରିଶ',
  'agent.emergencyForm.tier.safeMin.note': 'ଆପଣଙ୍କ ଡିପୋଜିଟ୍ ({deposit}) + ଆପଣଙ୍କ ନକାରାତ୍ମକ ୱାଲେଟ୍‌ର ଅଧା ({half})। ଆପଣଙ୍କୁ ଏକ ବାସ୍ତବ କୁଶନ୍ ଦେଇଥାଏ।',
  'agent.emergencyForm.tier.full.label': 'ସମ୍ପୂର୍ଣ୍ଣ ପୁନରୁଦ୍ଧାର',
  'agent.emergencyForm.tier.full.tag': 'ସର୍ବୋତ୍ତମ',
  'agent.emergencyForm.tier.full.note': 'ଡିପୋଜିଟ୍ + ଆପଣଙ୍କ ପୂରା ନକାରାତ୍ମକ ୱାଲେଟ୍ ({neg})। ଡିପୋଜିଟ୍ ସମ୍ପୂର୍ଣ୍ଣ ପୁନଃସ୍ଥାପିତ ଏବଂ ୱାଲେଟ୍ ଶୂନ୍ୟକୁ ଫେରିଯିବ।',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'ଆପଣଙ୍କ ଆଡମିନକୁ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.emergencyForm.parent.label.parentAgent': 'ଆପଣଙ୍କ ପ୍ୟାରେଣ୍ଟ ଏଜେଣ୍ଟଙ୍କୁ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.emergencyForm.parent.role.admin': 'ପ୍ଲାଟଫର୍ମ ଆଡମିନ',
  'agent.emergencyForm.parent.role.parentAgent': 'ପ୍ୟାରେଣ୍ଟ ଏଜେଣ୍ଟ',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'WhatsApp: {number}',
  'agent.emergencyForm.parent.notOnFile': 'ଆପଣଙ୍କ ପ୍ୟାରେଣ୍ଟଙ୍କ ଭୁଗତାନ ବିବରଣୀ ବର୍ତ୍ତମାନ ଫାଇଲରେ ନାହିଁ। ଦୟାକରି ସିଧାସଳଖ ପ୍ଲାଟଫର୍ମ ସପୋର୍ଟ ସହ ଯୋଗାଯୋଗ କରନ୍ତୁ।',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'ପ୍ରଦତ୍ତ ରାଶି (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI ଲେଣଦେଣ ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'ଯଥା 412312345678',
  'agent.emergencyForm.field.note.label': 'ଟିପ୍ପଣୀ (ଐଚ୍ଛିକ)',
  'agent.emergencyForm.field.note.placeholder': 'ପ୍ୟାରେଣ୍ଟଙ୍କୁ କିଛି କହିବାକୁ ଅଛି',
  'agent.emergencyForm.validation.amount': 'ଆପଣ ଭୁଗତାନ କରିଥିବା ରାଶି ଦର୍ଜ କରନ୍ତୁ (ସର୍ବନିମ୍ନ ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI ଲେଣଦେଣ ID / UTR ଦର୍ଜ କରନ୍ତୁ (ଅତି କମରେ 6 ଅକ୍ଷର)',
  'agent.emergencyForm.cta': 'ରିଚାର୍ଜ୍ ଅନୁରୋଧ ଦାଖଲ କରନ୍ତୁ',
  'agent.emergencyForm.ctaSubmitting': 'ଦାଖଲ କରୁଛୁ…',
  // Success state.
  'agent.emergencyForm.success.title': 'ରିଚାର୍ଜ୍ ଦାଖଲ ହେଲା',
  'agent.emergencyForm.success.bodyAdmin': 'ଅନୁରୋଧ #{id} ଆଡମିନଙ୍କ ଅନୁମୋଦନ ପାଇଁ ଅପେକ୍ଷା କରୁଛି। ଅନୁମୋଦନ ମିଳିବା ମାତ୍ରେ ଆପଣଙ୍କ ୱାଲେଟ୍ କ୍ରେଡିଟ୍ ହେବ ଏବଂ ଆକାଉଣ୍ଟ ସ୍ୱତଃ ଅନ୍‌ଲକ୍ ହୋଇଯିବ।',
  'agent.emergencyForm.success.bodyAgent': 'ଅନୁରୋଧ #{id} @{username}ଙ୍କ ଅନୁମୋଦନ ପାଇଁ ଅପେକ୍ଷା କରୁଛି। ଅନୁମୋଦନ ମିଳିବା ମାତ୍ରେ ଆପଣଙ୍କ ୱାଲେଟ୍ କ୍ରେଡିଟ୍ ହେବ ଏବଂ ଆକାଉଣ୍ଟ ସ୍ୱତଃ ଅନ୍‌ଲକ୍ ହୋଇଯିବ।',
  'agent.emergencyForm.toast.submitted': 'ରିଚାର୍ଜ୍ ଅନୁରୋଧ #{id} ଦାଖଲ ହେଲା। ଅନୁମୋଦନ ପାଇଁ ଅପେକ୍ଷା କରୁଛୁ।',
  'agent.emergencyForm.toast.cantSave': 'ଭାଷା ସେଭ୍ କରାଯାଇପାରିଲା ନାହିଁ',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'ଏକ-ଥର ସେଟଅପ୍: ଆପଣଙ୍କ ସ୍ଥାନ ନିଶ୍ଚିତ କରନ୍ତୁ',
  'agent.geo.subtitle': 'ଡ୍ୟାସବୋର୍ଡ ବ୍ୟବହାର କରିବା ପୂର୍ବରୁ ଆବଶ୍ୟକ।',
  'agent.geo.body.primary': 'ବାଲି ଗେମ୍ସ ପ୍ଲାଟଫର୍ମ ନୂତନ ଖେଳାଳିମାନଙ୍କୁ ଶାରୀରିକ ଭାବେ ସେମାନଙ୍କ ନିକଟତମ ଏଜେଣ୍ଟ ପାଖକୁ ପଠାଏ। ଯେତେବେଳ ପର୍ଯ୍ୟନ୍ତ ଆପଣ ଆପଣଙ୍କ ସଠିକ୍ GPS ସ୍ଥାନ ସେୟାର୍ ନକରନ୍ତି, ଆପଣ ଲୋକେଟରରେ ଦେଖାଯିବେ ନାହିଁ ଏବଂ ଅର୍ଗାନିକ୍ ସାଇନଅପ୍‌ରୁ ଶୂନ୍ୟ ନୂତନ ଖେଳାଳି ପ୍ରାପ୍ତ କରିବେ — ଆପଣ କେବଳ ସେହି ଖେଳାଳି ପାଇବେ ଯେଉଁମାନେ ସିଧାସଳଖ ଆପଣଙ୍କ ରେଫରାଲ୍ କୋଡ୍ ଟାଇପ୍ କରନ୍ତି।',
  'agent.geo.body.secondary': 'ଥରେ "ମୋର ବର୍ତ୍ତମାନ ସ୍ଥାନ ବ୍ୟବହାର କରନ୍ତୁ" ଟ୍ୟାପ୍ କରନ୍ତୁ। ଆମେ କେବଳ ଆପଣଙ୍କ ସ୍ଥାନାଙ୍କ କ୍ୟାପଚର କରୁ, ଆଉ କିଛି ନୁହେଁ।',
  'agent.geo.hint.disabled': 'ଉପରେ "ମୋର ବର୍ତ୍ତମାନ ସ୍ଥାନ ବ୍ୟବହାର କରନ୍ତୁ" ଟ୍ୟାପ୍ କରନ୍ତୁ। କେବଳ ପିନକୋଡ୍ କିମ୍ବା ମାନୁଆଲ୍ ଏରିଆ ବାଛିବା ଯଥେଷ୍ଟ ନୁହେଁ — ଲୋକେଟରକୁ ସଠିକ୍ GPS ସ୍ଥାନାଙ୍କ ଆବଶ୍ୟକ।',
  'agent.geo.error.fallback': 'ସ୍ଥାନ ସେଭ୍ କରାଯାଇପାରିଲା ନାହିଁ। ଦୟାକରି ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।',
  'agent.geo.cta.save': 'ସେଭ୍ କରନ୍ତୁ ଏବଂ ଜାରି ରଖନ୍ତୁ',
  'agent.geo.cta.saving': 'ସ୍ଥାନ ସେଭ୍ କରୁଛୁ…',
  'agent.geo.toast.success': 'ସ୍ଥାନ ସେଭ୍ ହେଲା — ଏବେ ଆପଣ ଖେଳାଳି ପ୍ରାପ୍ତ କରିପାରିବେ।',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'ପୁଲ୍ ବାଲାନ୍ସ',
  'agent.wallet.hero.helper': 'ଖେଳାଳି ରିଚାର୍ଜ୍ ଏବଂ ଉପ-ଏଜେଣ୍ଟ ଟପ୍-ଅପ୍ ପାଇଁ ଉପଲବ୍ଧ। ଆୟ ଏବଂ ଆଡମିନ୍ ଟପ୍-ଅପ୍ ଏଠାରେ ଆସେ।',

  'agent.wallet.action.recharge': 'ରିଚାର୍ଜ୍ ଅନୁରୋଧ',
  'agent.wallet.action.withdraw': 'ଉଠାଣ ଅନୁରୋଧ',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'ଅପେକ୍ଷାରତ',
  'agent.wallet.tab.history': 'ଇତିହାସ',

  // Pending feed.
  'agent.wallet.pending.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.wallet.pending.empty': 'ଏବେ କୌଣସି ଅପେକ୍ଷାରତ ଅନୁରୋଧ ନାହିଁ।',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'ରିଚାର୍ଜ୍ ଅନୁରୋଧ · +{amount}',
  'agent.wallet.pending.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'ଉଠାଣ ଅନୁରୋଧ · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi} କୁ · {when}',
  'agent.wallet.pending.badge': 'ଅପେକ୍ଷାରତ',

  // History feed.
  'agent.wallet.history.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.wallet.history.empty': 'ଏ ପର୍ଯ୍ୟନ୍ତ କୌଣସି ୱାଲେଟ୍ କାର୍ଯ୍ୟକଳାପ ନାହିଁ।',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'ରିଚାର୍ଜ୍ ଅନୁମୋଦିତ · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'ରିଚାର୍ଜ୍ ପ୍ରତ୍ୟାଖ୍ୟାନ · {amount}',
  'agent.wallet.history.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'ଉଠାଣ ପ୍ରଦାନ · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'ଉଠାଣ ପ୍ରତ୍ୟାଖ୍ୟାନ · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi} କୁ · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'ପ୍ରଦାନ · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'ଅନୁମୋଦିତ',
  'agent.wallet.state.paid': 'ପ୍ରଦାନ ହୋଇଛି',
  'agent.wallet.state.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'agent.wallet.state.expired': 'ସମାପ୍ତ',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'ଆଡମିନ୍ ଠାରୁ ଟପ୍-ଅପ୍',
  'agent.wallet.ledger.kind.topupToPlayer': 'ଖେଳାଳିଙ୍କୁ ଟପ୍-ଅପ୍',
  'agent.wallet.ledger.kind.topupFromPlayer': 'ଖେଳାଳି ରିଚାର୍ଜ୍',
  'agent.wallet.ledger.kind.revenueShare': 'ରାଜସ୍ୱ ଭାଗ',
  'agent.wallet.ledger.kind.coFundOffer': 'ସହ-ପୁଞ୍ଜିଗତ ଅଫର୍',
  'agent.wallet.ledger.kind.adminAdjust': 'ଆଡମିନ୍ ସମନ୍ୱୟ',
  'agent.wallet.ledger.kind.withdrawalHold': 'ଉଠାଣ ହୋଲ୍ଡ',
  'agent.wallet.ledger.kind.withdrawalPaid': 'ଉଠାଣ ପ୍ରଦାନ',
  'agent.wallet.ledger.kind.withdrawalRefund': 'ଉଠାଣ ରିଫଣ୍ଡ',
  'agent.wallet.ledger.balanceAfter': 'ବାଲାନ୍ସ {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'ସୁରକ୍ଷା ଜମା',
  'agent.deposit.hero.helper': 'ଆପଣଙ୍କ ପୁଲ୍ ବିରୁଦ୍ଧରେ ଜାମିନ। ଯେତେବେଳ ପର୍ଯ୍ୟନ୍ତ ଆପଣଙ୍କ ୱାଲେଟ୍ ଭଲ ସ୍ଥିତିରେ ଥାଏ, ଏହା ଅଛୁଆଁ ରହିଥାଏ।',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'ସୁରକ୍ଷିତ',
  'agent.deposit.margin.warning': 'ଚେତାବନୀ',
  'agent.deposit.margin.locked': 'ଲକ୍ଡ୍',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'ଜମା ରିଚାର୍ଜ୍',
  'agent.deposit.action.withdraw': 'ଜମା ଉଠାଣ',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'କଭରେଜ୍ ଗେଜ୍',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'ଚେତାବନୀ',
  'agent.deposit.gauge.thresholdLock': 'ଲକ୍',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct} ରେ ଆପଣ ଚେତାବନୀ ପାଇବେ',
  'agent.deposit.gauge.legend.lock': '{pct} ରେ, ଆପଣଙ୍କ ଖାତା ଲକ୍ ହୋଇଯିବ',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'ପୁଲ୍ ସମ୍ପୂର୍ଣ୍ଣ ଭାବେ ଜମା ଦ୍ୱାରା କଭର୍ — ଆପଣ ଟପ୍ ଅପ୍ ନକରିବା ପର୍ଯ୍ୟନ୍ତ ଉଠାଣ ବନ୍ଦ ରହିବ।',
  'agent.deposit.gauge.caption.warning': 'ପୁଲ୍ ଆପଣଙ୍କ ଜମାର {pct}% ଖାଇ ସାରିଛି। ଲକ୍-ଆଉଟ୍ ଠାରୁ ବଞ୍ଚିବା ପାଇଁ 100% ପୂର୍ବରୁ ଟପ୍ ଅପ୍ କରନ୍ତୁ।',
  'agent.deposit.gauge.caption.caution': 'ପୁଲ୍ ଜମା କଭରେଜ୍ ର {pct}% ପର୍ଯ୍ୟନ୍ତ ଅଛି। ଆପଣ ଏବେ ବି 80% ଚେତାବନୀ ସୀମା ଠାରୁ ସୁରକ୍ଷିତ ଭାବେ ତଳକୁ ଅଛନ୍ତି।',
  'agent.deposit.gauge.caption.safe': 'ଆପଣଙ୍କ ଜମା ସମ୍ପୂର୍ଣ୍ଣ ଅକ୍ଷୁର୍ଣ୍ଣ ଅଛି।',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.deposit.ledger.empty.title': 'ଏ ପର୍ଯ୍ୟନ୍ତ କୌଣସି ଜମା କାର୍ଯ୍ୟକଳାପ ନାହିଁ',
  'agent.deposit.ledger.empty.body': 'ଯେତେବେଳେ ଆଡମିନ୍ ଆପଣଙ୍କ ଜମାକୁ କ୍ରେଡିଟ୍ କରନ୍ତି (କିମ୍ବା ରିଫଣ୍ଡ ପ୍ରୋସେସ୍ କରନ୍ତି), ପ୍ରତ୍ୟେକ ଏଣ୍ଟ୍ରି ଏଠାରେ ଦେଖାଯିବ।',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'ଆପଣଙ୍କ ୱାଲେଟ୍ ରିଚାର୍ଜ୍ କରନ୍ତୁ',
  'agent.recharge.banner.eyebrow.deposit': 'ଆପଣଙ୍କ ଜମା ରିଚାର୍ଜ୍ କରନ୍ତୁ',
  'agent.recharge.banner.body.wallet': 'ପ୍ରଥମେ ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଭୁଗତାନ କରନ୍ତୁ, ତାପରେ ଏଠାରେ ପ୍ରମାଣ ଦାଖଲ କରନ୍ତୁ ଯାହାଫଳରେ ସେ ଆପଣଙ୍କ କାର୍ଯ୍ୟକାରୀ ୱାଲେଟକୁ ଟପ୍ ଅପ୍ କରିପାରିବେ।',
  'agent.recharge.banner.body.deposit': 'ପ୍ରଥମେ ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଭୁଗତାନ କରନ୍ତୁ, ତାପରେ ଏଠାରେ ପ୍ରମାଣ ଦାଖଲ କରନ୍ତୁ ଯାହାଫଳରେ ସେ ଆପଣଙ୍କ ସୁରକ୍ଷା ଜମାକୁ ଟପ୍ ଅପ୍ କରିପାରିବେ।',
  'agent.recharge.banner.closeAria': 'ଫର୍ମ ବନ୍ଦ କରନ୍ତୁ',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'ଆପଣଙ୍କ ଆଡମିନ୍ କୁ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.recharge.parent.label.parentAgent': 'ଆପଣଙ୍କ ପ୍ୟାରେଣ୍ଟ ଏଜେଣ୍ଟକୁ ଭୁଗତାନ କରନ୍ତୁ',
  'agent.recharge.parent.role.admin': 'ପ୍ଲାଟଫର୍ମ ଆଡମିନ୍',
  'agent.recharge.parent.role.parentAgent': 'ପ୍ୟାରେଣ୍ଟ ଏଜେଣ୍ଟ',
  'agent.recharge.parent.noQr': 'କୌଣସି QR ସେଟ୍ ନାହିଁ',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'ଆପଣଙ୍କ ଅପ୍-ଲାଇନର ଭୁଗତାନ ବିବରଣୀ ଲୋଡ୍ ହେଉଛି…',
  'agent.recharge.parent.loadError': 'ଆପଣଙ୍କ ଅପ୍-ଲାଇନର ଭୁଗତାନ ବିବରଣୀ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'ଆପଣ କିପରି ଭୁଗତାନ କଲେ?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ଅନଲାଇନ୍ ଡିଜିଟାଲ୍ ଭୁଗତାନ',
  'agent.recharge.method.cash.label': 'ନଗଦ',
  'agent.recharge.method.cash.note': 'ବ୍ୟକ୍ତିଗତ ଭାବେ ହସ୍ତାନ୍ତର',

  // Form fields.
  'agent.recharge.field.amount.label': 'ରାଶି (₹) · ସର୍ବନିମ୍ନ ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI ଲେଣଦେଣ ID / UTR',
  'agent.recharge.field.txn.label.cash': 'ନଗଦ ରସିଦ୍ ମାର୍କର (କୌଣସି ସଂକ୍ଷିପ୍ତ ସନ୍ଦର୍ଭ)',
  'agent.recharge.field.txn.placeholder.upi': 'ଯଥା 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'ଯଥା CASH-Sun-eve',
  'agent.recharge.field.note.label': 'ନୋଟ୍ (ବୈକଳ୍ପିକ, ଆପଣଙ୍କ ଅପ୍-ଲାଇନକୁ ଦେଖାଯିବ)',
  'agent.recharge.field.note.placeholder': 'ଅପ୍-ଲାଇନ ପାଇଁ କୌଣସି ସନ୍ଦର୍ଭ',

  // Validation errors.
  'agent.recharge.error.amountMin': 'ରାଶି ସମ୍ପୂର୍ଣ୍ଣ ଟଙ୍କାରେ ≥ 10 ହେବା ଆବଶ୍ୟକ',
  'agent.recharge.error.txnUpi': 'ଲେଣଦେଣ ID ବହୁତ ଛୋଟ ଦେଖାଯାଉଛି — UPI ସନ୍ଦର୍ଭ ପେଷ୍ଟ କରନ୍ତୁ',
  'agent.recharge.error.txnCash': 'ଗୋଟିଏ ଛୋଟ ମାର୍କର ଯୋଡନ୍ତୁ (ଯଥା "CASH-Sun-eve") ଯାହାଫଳରେ ଆପଣଙ୍କ ଅପ୍-ଲାଇନ ହସ୍ତାନ୍ତର ମେଳ କରିପାରିବେ',

  // CTAs.
  'agent.recharge.cta.submit': 'ଅନୁରୋଧ ଦାଖଲ କରନ୍ତୁ',
  'agent.recharge.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'ୱାଲେଟରୁ ଉଠାନ୍ତୁ',
  'agent.withdrawal.banner.eyebrow.deposit': 'ଜମାରୁ ଉଠାନ୍ତୁ',
  'agent.withdrawal.banner.body.wallet': 'ରାଶି ତୁରନ୍ତ ରୋକା ଯାଏ ଯାହାଫଳରେ ଅନ୍ୟ କେଉଁଠି ଖର୍ଚ୍ଚ ହୋଇନଥାଏ। ଆପଣଙ୍କ ଅପ୍-ଲାଇନ ଭୁଗତାନ କରନ୍ତି ଏବଂ ଭୁଗତାନ ଚିହ୍ନଟ କରନ୍ତି।',
  'agent.withdrawal.banner.body.deposit': 'ରାଶି ଆପଣଙ୍କ ସୁରକ୍ଷା ଜମାରୁ ତୁରନ୍ତ ରୋକା ଯାଏ। ଆପଣଙ୍କ ଅପ୍-ଲାଇନ ଆପଣଙ୍କୁ ଭୁଗତାନ କରନ୍ତି ଏବଂ ଏହାକୁ ପ୍ରଦାନ ଭାବେ ଚିହ୍ନଟ କରନ୍ତି।',
  'agent.withdrawal.banner.closeAria': 'ଫର୍ମ ବନ୍ଦ କରନ୍ତୁ',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'ଆପଣ କିପରି ଭୁଗତାନ ପାଇବାକୁ ଚାହାଁନ୍ତି?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ଅନଲାଇନ୍ ଡିଜିଟାଲ୍ ଭୁଗତାନ',
  'agent.withdrawal.method.cash.label': 'ନଗଦ',
  'agent.withdrawal.method.cash.note': 'ବ୍ୟକ୍ତିଗତ ଭାବେ ହସ୍ତାନ୍ତର',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'ରାଶି (₹) · ଉପଲବ୍ଧ {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'ଆପଣଙ୍କ UPI ID (ଯେଉଁଠାରେ ଆପଣଙ୍କ ଅପ୍-ଲାଇନ ଆପଣଙ୍କୁ ଭୁଗତାନ କରିବେ)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'ଭେଟିବା ମାର୍କର (ଯଥା "ରବିବାର ସନ୍ଧ୍ୟା ନଗଦ")',
  'agent.withdrawal.field.cashMarker.placeholder': 'ନଗଦ କେବେ/କେଉଁଠି ଦେବାକୁ ହେବ',
  'agent.withdrawal.field.whatsapp.label': 'WhatsApp (ଇଚ୍ଛାଧୀନ, ଆପଣଙ୍କ ଅପ୍-ଲାଇନ୍ ଯୋଗାଯୋଗ କରିବା ପାଇଁ)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'ନୋଟ୍ (ଇଚ୍ଛାଧୀନ)',
  'agent.withdrawal.field.note.placeholder': 'ଆପଣଙ୍କ ଅପ୍-ଲାଇନ୍ ପାଇଁ କୌଣସି ପ୍ରସଙ୍ଗ',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'ରାଶି ପୂର୍ଣ୍ଣ ଟଙ୍କାରେ ≥ 10 ହେବା ଆବଶ୍ୟକ',
  'agent.withdrawal.error.amountOverflow': 'ଆପଣଙ୍କ ପାଖରେ କେବଳ {balance} ଉପଲବ୍ଧ',
  'agent.withdrawal.error.upi': 'ଏକ ବୈଧ UPI ID ପ୍ରବେଶ କରନ୍ତୁ',

  // CTAs.
  'agent.withdrawal.cta.submit': 'ଅନୁରୋଧ ପଠାନ୍ତୁ',
  'agent.withdrawal.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'ଖେଳାଳି',
  'agent.people.hub.players.subtitle': 'ଖେଳାଳି ଏବଂ ସେମାନଙ୍କ ଅନୁରୋଧ ପରିଚାଳନା କରନ୍ତୁ',
  'agent.people.hub.players.subtab.requests': 'ଖେଳାଳି ଅନୁରୋଧ',
  'agent.people.hub.players.subtab.list': 'ଖେଳାଳି',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'ଏଜେଣ୍ଟ',
  'agent.people.hub.agents.subtitle': 'ଉପ-ଏଜେଣ୍ଟ ଏବଂ ସେମାନଙ୍କ ଅନୁରୋଧ ପରିଚାଳନା କରନ୍ତୁ',
  'agent.people.hub.agents.subtab.requests': 'ଏଜେଣ୍ଟ ଅନୁରୋଧ',
  'agent.people.hub.agents.subtab.list': 'ଏଜେଣ୍ଟ',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'ନୂଆ ଖେଳାଳି ଗ୍ରହଣ କରୁଛନ୍ତି',
  'agent.people.acceptingToggle.bodyAccepting': 'ଆପଣ ଭୌଗୋଳିକ ଲୋକେଟର୍‌ରୁ ନୂଆ ଖେଳାଳି ଗ୍ରହଣ କରୁଛନ୍ତି। ଆପଣଙ୍କ ଅଞ୍ଚଳରେ କୋଲ୍ଡ ସାଇନ୍‌ଅପ୍‌ ସ୍ୱତଃ ଆପଣଙ୍କ ପାଖକୁ ଆସିବ।',
  'agent.people.acceptingToggle.bodyOptedOut': 'ଆପଣ ଅପ୍ଟ୍-ଆଉଟ୍ କରିଛନ୍ତି। ଲୋକେଟର୍ ଆପଣଙ୍କୁ ଛାଡ଼ିଯାଉଛି — କେବଳ ସିଧାସଳଖ ରେଫରାଲ୍ ଏବଂ ମାନୁଆଲ୍ ଆସାଇନ୍ କରାଯାଇଥିବା ଖେଳାଳି ଆପଣଙ୍କ ପାଖରେ ପହଞ୍ଚିବେ। ବର୍ତ୍ତମାନର ଖେଳାଳି ପ୍ରଭାବିତ ହେବେ ନାହିଁ।',
  'agent.people.acceptingToggle.error': 'ଅପଡେଟ୍ କରିପାରିଲେ ନାହିଁ',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'ନୂଆ ଖେଳାଳି ଗ୍ରହଣ କରିବା ବନ୍ଦ କରନ୍ତୁ',
  'agent.people.acceptingToggle.aria.start': 'ନୂଆ ଖେଳାଳି ଗ୍ରହଣ କରିବା ଆରମ୍ଭ କରନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'ଖେଳାଳି',
  'agent.qr.audience.subAgent': 'ଏଜେଣ୍ଟ',
  'agent.qr.alt': 'ଆପଣଙ୍କ ପେମେଣ୍ଟ QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'ମୋର QR · ଯାହା ଖେଳାଳି ଦେଖନ୍ତି',
  'agent.qr.cardLabel.subAgent': 'ମୋର QR · ଯାହା ଏଜେଣ୍ଟ ଦେଖନ୍ତି',
  'agent.qr.upi.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି UPI ସେଟ୍ ହୋଇନାହିଁ',
  // Status pill.
  'agent.qr.status.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.qr.status.live': 'ଲାଇଭ୍',
  'agent.qr.status.notSet': 'ସେଟ୍ ହୋଇନାହିଁ',
  'agent.qr.status.paused': 'ବନ୍ଦ ଅଛି',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR ଏବଂ UPI ସମ୍ପାଦନ କରନ୍ତୁ',
  'agent.qr.cta.setup': 'QR ସେଟ୍ ଅପ୍ କରନ୍ତୁ',
  'agent.qr.cta.viewOnly': 'କେବଳ ଦେଖନ୍ତୁ — ସମ୍ପାଦନ ପାଇଁ ଆଡମିନ୍‌କୁ କୁହନ୍ତୁ',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR ଏବଂ UPI ସମ୍ପାଦନ କରନ୍ତୁ',
  'agent.qr.modal.closeAria': 'ବନ୍ଦ କରନ୍ତୁ',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR ଅପଲୋଡ୍ କରନ୍ତୁ',
  'agent.qr.modal.replace': 'QR ବଦଳାନ୍ତୁ',
  'agent.qr.modal.remove': 'ହଟାନ୍ତୁ',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR ଛବି ହଟାନ୍ତୁ? ଆପଣଙ୍କ ଖେଳାଳି କେବଳ ଯୋଗାଯୋଗ ଫଲ୍‌ବ୍ୟାକ୍ ଦେଖିବେ।',
  'agent.qr.modal.confirmRemove.subAgent': 'QR ଛବି ହଟାନ୍ତୁ? ଆପଣଙ୍କ ଏଜେଣ୍ଟ କେବଳ ଯୋଗାଯୋଗ ଫଲ୍‌ବ୍ୟାକ୍ ଦେଖିବେ।',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'ଯଥା baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'WhatsApp ନମ୍ବର (ଫଲ୍‌ବ୍ୟାକ୍)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ଏଜେଣ୍ଟ ପାଇଁ WhatsApp ନମ୍ବର',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'ଖାଲି → {number} କୁ ଫଲ୍‌ବ୍ୟାକ୍ ହେବ',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'ଉପ-ଏଜେଣ୍ଟ {number} ଦେଖିବେ (ଆପଣଙ୍କ ଖେଳାଳି-ମୁଖୀ ନମ୍ବର)।',
  'agent.qr.modal.field.phone.label.player': 'ସହାୟତା ଫୋନ୍ (ଫଲ୍‌ବ୍ୟାକ୍)',
  'agent.qr.modal.field.phone.label.subAgent': 'ଏଜେଣ୍ଟ ପାଇଁ ସହାୟତା ଫୋନ୍',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'ଖାଲି → {number} କୁ ଫଲ୍‌ବ୍ୟାକ୍ ହେବ',
  'agent.qr.modal.field.phone.fallbackHint': 'ଉପ-ଏଜେଣ୍ଟ {number} ଦେଖିବେ (ଆପଣଙ୍କ ଖେଳାଳି-ମୁଖୀ ନମ୍ବର)।',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'ସକ୍ରିୟ',
  'agent.qr.modal.active.description': 'ଖେଳାଳି ରିଚାର୍ଜ୍ ପଠାଇପାରିବେ',
  'agent.qr.modal.active.on': 'ଚାଲୁ',
  'agent.qr.modal.active.paused': 'ବନ୍ଦ ଅଛି',

  // Footer buttons.
  'agent.qr.modal.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.qr.modal.save': 'ସେଭ୍ କରନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'ମାନୁଆଲ୍ ରିଚାର୍ଜ୍',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'ଏକ ',
  'agent.manualRecharge.banner.body.target': 'ଖେଳାଳି',
  'agent.manualRecharge.banner.body.after': 'ଙ୍କୁ ସିଧାସଳଖ ଆପଣଙ୍କ ପୁଲ୍‌ରୁ ଟଙ୍କା ପଠାନ୍ତୁ।',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'ଆପଣଙ୍କ ପୁଲ୍',
  'agent.manualRecharge.pool.dailyCap': 'ସୀମା · {amount}/ଦିନ',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'କେଉଁ ଖେଳାଳିଙ୍କୁ',
  'agent.manualRecharge.field.player.placeholder': 'ଏକ ଖେଳାଳି ଚୟନ କରନ୍ତୁ…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · ବାଲାନ୍ସ {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'କେଉଁ ଖେଳାଳିଙ୍କୁ',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · ବର୍ତ୍ତମାନର ବାଲାନ୍ସ {balance}',
  'agent.manualRecharge.field.player.notFound': 'ଆପଣଙ୍କ ତାଲିକାରେ ଖେଳାଳି ମିଳିଲେ ନାହିଁ।',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'ରାଶି (₹) — {min} ରୁ {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'ଆପଣଙ୍କ ପୁଲ୍ ବାଲାନ୍ସଠାରୁ ଅଧିକ।',
  'agent.manualRecharge.error.belowMin': 'ସର୍ବନିମ୍ନଠାରୁ କମ୍ ({min})।',
  'agent.manualRecharge.error.aboveMax': 'ସର୍ବାଧିକଠାରୁ ଅଧିକ ({max})।',

  // Note field.
  'agent.manualRecharge.field.note.label': 'ନୋଟ୍ (ଇଚ୍ଛାଧୀନ, ଖେଳାଳି ଲେଜର୍‌ରେ ଦେଖାଯିବ)',
  'agent.manualRecharge.field.note.placeholder': 'ଯଥା ଦୋକାନରେ ନଗଦ ସଂଗ୍ରହ କରାଗଲା',

  // Loading + success states.
  'agent.manualRecharge.loading': 'ଆପଣଙ୍କ ପୁଲ୍ ଲୋଡ୍ ହେଉଛି…',
  'agent.manualRecharge.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.manualRecharge.cta.send': '{amount} ପଠାନ୍ତୁ',
  'agent.manualRecharge.success.title': '@{username}ଙ୍କୁ {amount} ପଠାଗଲା',
  'agent.manualRecharge.success.meta': 'ଆପଣଙ୍କ ପୁଲ୍ · {pool} · ସେମାନଙ୍କ ୱାଲେଟ୍ · {playerBal}',
  'agent.manualRecharge.success.again': 'ଆଉ ପଠାନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'ମାନୁଆଲ୍ ରିଚାର୍ଜ୍',
  'agent.manualSubAgentRecharge.banner.body.before': 'ଏକ ',
  'agent.manualSubAgentRecharge.banner.body.target': 'ଉପ-ଏଜେଣ୍ଟ',
  'agent.manualSubAgentRecharge.banner.body.after': 'ଙ୍କୁ ସିଧାସଳଖ ଆପଣଙ୍କ ପୁଲ୍‌ରୁ ଟଙ୍କା ପଠାନ୍ତୁ।',

  'agent.manualSubAgentRecharge.pool.label': 'ଆପଣଙ୍କ ପୁଲ୍',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'କେଉଁ ଉପ-ଏଜେଣ୍ଟଙ୍କୁ',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'ଏକ ଉପ-ଏଜେଣ୍ଟ ଚୟନ କରନ୍ତୁ…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · ପୁଲ୍ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'କୌଣସି ସକ୍ରିୟ ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ନାହାନ୍ତି',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'କେଉଁ ଉପ-ଏଜେଣ୍ଟଙ୍କୁ',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · ବର୍ତ୍ତମାନର ପୁଲ୍ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'ଆପଣଙ୍କ ସିଧାସଳଖ ଡାଉନ୍-ଲାଇନ୍‌ରେ ଉପ-ଏଜେଣ୍ଟ ମିଳିଲେ ନାହିଁ।',

  'agent.manualSubAgentRecharge.field.amount.label': 'ରାଶି (₹) — {min} ରୁ {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'ଆପଣଙ୍କ ପୁଲ୍ ବାଲାନ୍ସଠାରୁ ଅଧିକ।',
  'agent.manualSubAgentRecharge.error.belowMin': 'ସର୍ବନିମ୍ନଠାରୁ କମ୍ ({min})।',

  'agent.manualSubAgentRecharge.field.note.label': 'ନୋଟ୍ (ବୈକଳ୍ପିକ, ଉଭୟ ଲେଜରରେ ଦେଖାଯିବ)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'ଯଥା ଫ୍ଲୋଟ୍ ଅଗ୍ରୀମ',

  'agent.manualSubAgentRecharge.loading': 'ଆପଣଙ୍କ ପୁଲ୍ ଲୋଡ୍ ହେଉଛି…',
  'agent.manualSubAgentRecharge.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.manualSubAgentRecharge.cta.send': '{amount} ପଠାନ୍ତୁ',
  'agent.manualSubAgentRecharge.success.title': '@{username}ଙ୍କୁ {amount} ପଠାଗଲା',
  'agent.manualSubAgentRecharge.success.meta': 'ଆପଣଙ୍କ ପୁଲ୍ · {parent} · ସେମାନଙ୍କ ପୁଲ୍ · {child}',
  'agent.manualSubAgentRecharge.success.again': 'ଆଉ ପଠାନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'ରିଚାର୍ଜ୍',
  'agent.playerRequests.direction.withdrawal': 'ଉଠାଣ',
  'agent.playerRequests.tab.pending': 'ଅପେକ୍ଷାରତ',
  'agent.playerRequests.tab.history': 'ଇତିହାସ',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.playerRequests.empty.pending.recharge': 'ଏବେ କୌଣସି ଅପେକ୍ଷାରତ ରିଚାର୍ଜ୍ ନାହିଁ।',
  'agent.playerRequests.empty.pending.withdrawal': 'ଏବେ କୌଣସି ଅପେକ୍ଷାରତ ଉଠାଣ ନାହିଁ।',
  'agent.playerRequests.empty.history': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଇତିହାସ ନାହିଁ।',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'ପ୍ରତ୍ୟାଖ୍ୟାନର କାରଣ (ବୈକଳ୍ପିକ):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'ପ୍ରତ୍ୟାଖ୍ୟାନର କାରଣ (ଖେଳାଳିଙ୍କ ଅଟକାଯାଇଥିବା ରାଶି ଫେରସ୍ତ କରାଯିବ):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ କରାଗଲା',
  'agent.playerRequests.toast.rejectedRefunded': 'ପ୍ରତ୍ୟାଖ୍ୟାନ · ଅଟକାଯାଇଥିବା ବାଲାନ୍ସ ଫେରସ୍ତ କରାଗଲା',
  'agent.playerRequests.toast.approved': '{amount} ଅନୁମୋଦନ ହେଲା',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} ଅନୁମୋଦନ · ମୂଳ + ₹{bonus} ବୋନସ୍',
  'agent.playerRequests.toast.markedPaid': '{amount} ଭୁଗତାନ ଦର୍ଜ କରାଗଲା',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel': 'ଭୁଗତାନ କରନ୍ତୁ (UPI)',
  'agent.playerRequests.card.whatsapp': 'WhatsApp',
  'agent.playerRequests.card.viewQr': 'QR ଦେଖନ୍ତୁ',
  'agent.playerRequests.card.bonusChip': 'ବୋନସ୍',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'ଅନୁରୋଧିତ {requested} ମଧ୍ୟରୁ {approved} ଅନୁମୋଦନ',
  'agent.playerRequests.card.partialPaid': 'ଅନୁରୋଧିତ {requested} ମଧ୍ୟରୁ {approved} ଭୁଗତାନ · {refund} ଫେରସ୍ତ',
  'agent.playerRequests.card.paidTxnPrefix': 'ଭୁଗତାନ · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'ପ୍ରତ୍ୟାଖ୍ୟାନ କରନ୍ତୁ',
  'agent.playerRequests.action.approve': 'ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.playerRequests.action.markPaid': 'ଭୁଗତାନ ଦର୍ଜ କରନ୍ତୁ',
  'agent.playerRequests.state.pending': 'ଅପେକ୍ଷାରତ',
  'agent.playerRequests.state.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'agent.playerRequests.state.expired': 'ସମାପ୍ତ',
  'agent.playerRequests.state.accepted': 'ଗ୍ରହଣ କରାଗଲା',
  'agent.playerRequests.state.manuallyPaid': 'ମାନୁଆଲ୍ ଭୁଗତାନ',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': '{total} ମଧ୍ୟରୁ {from}–{to}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'ରିଚାର୍ଜ୍',
  'agent.agentRequests.direction.withdrawal': 'ଉଠାଣ',
  'agent.agentRequests.tab.pending': 'ଅପେକ୍ଷାରତ',
  'agent.agentRequests.tab.history': 'ଇତିହାସ',
  'agent.agentRequests.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.agentRequests.empty.pending.recharge': 'ଏବେ କୌଣସି ଅପେକ୍ଷାରତ ରିଚାର୍ଜ୍ ନାହିଁ।',
  'agent.agentRequests.empty.pending.withdrawal': 'ଏବେ କୌଣସି ଅପେକ୍ଷାରତ ଉଠାଣ ନାହିଁ।',
  'agent.agentRequests.empty.history': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଇତିହାସ ନାହିଁ।',

  'agent.agentRequests.prompt.rejectRecharge': 'ପ୍ରତ୍ୟାଖ୍ୟାନର କାରଣ (ବୈକଳ୍ପିକ):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'ପ୍ରତ୍ୟାଖ୍ୟାନର କାରଣ (ଉପ-ଏଜେଣ୍ଟଙ୍କ ଅଟକାଯାଇଥିବା ରାଶି ଫେରସ୍ତ କରାଯିବ):',

  'agent.agentRequests.toast.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ କରାଗଲା',
  'agent.agentRequests.toast.rejectedRefunded': 'ପ୍ରତ୍ୟାଖ୍ୟାନ · ଅଟକାଯାଇଥିବା ବାଲାନ୍ସ ଫେରସ୍ତ କରାଗଲା',
  'agent.agentRequests.toast.approved': '{amount} ଅନୁମୋଦନ · ପୁଲ୍ ରେ କ୍ରେଡିଟ୍ କରାଗଲା',
  'agent.agentRequests.toast.markedPaid': '{amount} ଭୁଗତାନ ଦର୍ଜ କରାଗଲା',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'ଏଜେଣ୍ଟ',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'କ୍ୟାଶ୍ ମାର୍କର୍',
  'agent.agentRequests.card.payToUpiLabel': 'ଭୁଗତାନ କରନ୍ତୁ (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'ହ୍ୟାଣ୍ଡ-ଅଫ୍ ମାର୍କର୍',
  'agent.agentRequests.card.whatsapp': 'WhatsApp {number}',
  'agent.agentRequests.card.partialApproved': 'ଅନୁରୋଧିତ {requested} ମଧ୍ୟରୁ {approved} ଅନୁମୋଦନ',
  'agent.agentRequests.card.partialPaid': 'ଅନୁରୋଧିତ {requested} ମଧ୍ୟରୁ {approved} ଭୁଗତାନ · {refund} ଫେରସ୍ତ',
  'agent.agentRequests.card.paidTxnPrefix': 'ଭୁଗତାନ · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'ଡିପୋଜିଟ୍',
  'agent.agentRequests.card.chip.cash': 'କ୍ୟାଶ୍',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'ପ୍ରତ୍ୟାଖ୍ୟାନ କରନ୍ତୁ',
  'agent.agentRequests.action.approve': 'ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.agentRequests.action.markPaid': 'ଭୁଗତାନ ଦର୍ଜ କରନ୍ତୁ',
  'agent.agentRequests.state.pending': 'ଅପେକ୍ଷାରତ',
  'agent.agentRequests.state.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'agent.agentRequests.state.expired': 'ସମାପ୍ତ',
  'agent.agentRequests.state.accepted': 'ଗ୍ରହଣ କରାଗଲା',
  'agent.agentRequests.state.manuallyPaid': 'ମାନୁଆଲ୍ ଭୁଗତାନ',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'ଖେଳାଳି ଯୋଡ଼ନ୍ତୁ',
  'agent.playersList.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.playersList.empty': 'କୌଣସି ଖେଳାଳି ମିଳିଲେ ନାହିଁ',
  'agent.playersList.results': '{total} ମଧ୍ୟରୁ {from}–{to} ଦେଖାଯାଉଛି',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'ୟୁଜରନେମ୍, ଇମେଲ୍, ଫୋନ୍ ଖୋଜନ୍ତୁ…',

  // Status filter pills.
  'agent.playersList.filter.all': 'ସମସ୍ତ',
  'agent.playersList.filter.active': 'ସକ୍ରିୟ',
  'agent.playersList.filter.pending': 'ଅପେକ୍ଷାରତ',
  'agent.playersList.filter.suspended': 'ସ୍ଥଗିତ',
  'agent.playersList.filter.banned': 'ନିଷିଦ୍ଧ',

  // Empty-results panel.
  'agent.playersList.empty.title': 'କୌଣସି ଖେଳାଳି ମିଳିଲେ ନାହିଁ',
  'agent.playersList.empty.body': 'ଭିନ୍ନ ଖୋଜ କିମ୍ବା ସ୍ଥିତି ଫିଲ୍ଟର୍ ଚେଷ୍ଟା କରନ୍ତୁ।',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'ପୃଷ୍ଠା {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'ସକ୍ରିୟ',
  'agent.playersList.card.status.pending': 'ଅପେକ୍ଷାରତ',
  'agent.playersList.card.status.suspended': 'ସ୍ଥଗିତ',
  'agent.playersList.card.status.banned': 'ନିଷିଦ୍ଧ',
  'agent.playersList.card.verifiedBadge': 'ଯାଞ୍ଚିତ',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'ୱାଲେଟ୍',
  'agent.playersList.column.joined': 'ଯୋଗ ଦେଲେ',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'ଆପଣ ଯେଉଁ ଏଜେଣ୍ଟ ସୃଷ୍ଟି କରନ୍ତି ସେମାନେ ଆପଣଙ୍କ ତଳେ କାମ କରନ୍ତି। ସେମାନେ ପ୍ରତ୍ୟେକ ପିଲାର୍ ରେ 0%ରୁ ଆରମ୍ଭ କରନ୍ତି — ସେମାନଙ୍କ ବିବରଣୀ ପୃଷ୍ଠାରୁ ପ୍ରତି-ଗେମ୍ ଦର ପ୍ରଦାନ କରନ୍ତୁ।',
  'agent.subAgentsList.cta.new': 'ନୂଆ ଉପ-ଏଜେଣ୍ଟ',
  'agent.subAgentsList.loading': 'ଲୋଡ୍ ହେଉଛି…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଉପ-ଏଜେଣ୍ଟ ନାହାନ୍ତି',
  'agent.subAgentsList.empty.body.canCreate': 'ଗୋଟିଏ ତିଆରି କରିବାକୁ "+ ନୂଆ ଉପ-ଏଜେଣ୍ଟ" ଉପରେ ଟ୍ୟାପ୍ କରନ୍ତୁ। ସେମାନେ ଏଠାରେ ନିଜର ପୁଲ୍, ଖେଳାଳି ଏବଂ ରୋଜଗାର ସହିତ ଦେଖାଯିବେ — ଆପଣଙ୍କ ଦ୍ୱାରା ପରିଚାଳିତ।',
  'agent.subAgentsList.empty.body.cantCreate': 'ଆପଣଙ୍କ ଆଡମିନ୍ ଏପର୍ଯ୍ୟନ୍ତ ଆପଣଙ୍କୁ ଉପ-ଏଜେଣ୍ଟ ତିଆରି କରିବାର ଅଧିକାର ଦେଇନାହାନ୍ତି। ସେମାନଙ୍କୁ କୁହନ୍ତୁ ଯେ ସେମାନେ ଆପଣଙ୍କ ଅନୁମତିରେ create_subagent କୁ "edit" ଉପରେ ସେଟ୍ କରନ୍ତୁ।',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'ସ୍ଥଗିତ',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'ଏଜେଣ୍ଟ',
  'agent.subAgentsList.column.players': 'ଖେଳାଳି',
  'agent.subAgentsList.column.pool': 'ପୁଲ୍',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'ସିଧାସଳଖ',
  'agent.subAgentsList.row.indirectLabel': 'ପରୋକ୍ଷ',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'ନୂଆ ଉପ-ଏଜେଣ୍ଟ',
  'agent.subAgentsList.modal.body': 'ସେମାନେ ପ୍ରତ୍ୟେକ ପିଲାରରେ 0%ରୁ ଆରମ୍ଭ କରିବେ। ତିଆରି କରିବା ପରେ ସେମାନଙ୍କ ବିବରଣୀ ପୃଷ୍ଠାରୁ ପ୍ରତି-ଗେମ୍ ରେଟ୍ ଦିଅନ୍ତୁ।',
  'agent.subAgentsList.modal.field.username.label': 'ୟୁଜରନେମ୍',
  'agent.subAgentsList.modal.field.username.placeholder': 'ଅକ୍ଷର-ସଂଖ୍ୟା, ._- ଅନୁମତିପ୍ରାପ୍ତ; ସର୍ବନିମ୍ନ 3 ଅକ୍ଷର',
  'agent.subAgentsList.modal.field.email.label': 'ଇମେଲ୍',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'ପ୍ରାରମ୍ଭିକ ପାସୱାର୍ଡ',
  'agent.subAgentsList.modal.field.password.placeholder': 'ସର୍ବନିମ୍ନ 8 ଅକ୍ଷର',
  'agent.subAgentsList.modal.field.password.hint': 'ଏହାକୁ ନୂଆ ଏଜେଣ୍ଟ ସହ ସେୟାର କରନ୍ତୁ ଯାହାଫଳରେ ସେମାନେ ଲଗ୍-ଇନ୍ କରିପାରିବେ। ସେମାନେ ପ୍ରଥମ ଲଗ୍-ଇନ୍ ସମୟରେ ଏହାକୁ ବଦଳାଇବା ଉଚିତ୍।',
  'agent.subAgentsList.modal.field.location.label': 'ଉପ-ଏଜେଣ୍ଟଙ୍କ ସ୍ଥାନ',
  'agent.subAgentsList.modal.field.location.optional': '(ଅତ୍ୟନ୍ତ ସୁପାରିଶ କରାଯାଏ)',
  'agent.subAgentsList.modal.field.location.missing': 'ସ୍ଥାନ ବିନା ଭୌଗୋଳିକ ଲୋକେଟର୍ ଏହି ଉପ-ଏଜେଣ୍ଟଙ୍କୁ କୌଣସି ନୂଆ ଖେଳାଳି ପଠାଇ ପାରିବ ନାହିଁ। ଯେପର୍ଯ୍ୟନ୍ତ ସେମାନେ ସାଇନ୍ ଇନ୍ କରି ନିଜର GPS ପୂରଣ ନ କରନ୍ତି, ସେମାନେ କେବଳ ସିଧାସଳଖ ରେଫରାଲ୍ ମାଧ୍ୟମରେ ଖେଳାଳି ପାଇବେ।',
  'agent.subAgentsList.modal.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.subAgentsList.modal.cta.create': 'ଉପ-ଏଜେଣ୍ଟ ତିଆରି କରନ୍ତୁ',
  'agent.subAgentsList.modal.cta.creating': 'ତିଆରି କରୁଛୁ…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who}ଙ୍କଠାରୁ ରିଚାର୍ଜ୍ ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.approvalEdit.title.withdrawal': '{who}ଙ୍କ ଉଠାଣ ପରିଶୋଧିତ ଚିହ୍ନଟ କରନ୍ତୁ',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'ଅନୁରୋଧିତ:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'ଅନୁମୋଦନ ରାଶି (₹)',
  'agent.approvalEdit.field.amount.hint': 'ଅନୁରୋଧିତ {amount}ଠାରୁ ଅଧିକ ହୋଇପାରିବ ନାହିଁ।',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} ସ୍ୱୟଂଚାଳିତ ଭାବେ {who}ଙ୍କ ୱାଲେଟ୍‌କୁ ଫେରସ୍ତ କରାଯିବ।',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'ଆଂଶିକ ଅନୁମୋଦନ। କୌଣସି ସଂଲଗ୍ନ ବୋନସ୍ ସ୍ୱୟଂଚାଳିତ ଭାବେ ପ୍ରତ୍ୟାଖ୍ୟାନ ହେବ।',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'କାରଣ',
  'agent.approvalEdit.field.reason.requiredHint': '(ଆବଶ୍ୟକ, ≥5 ଅକ୍ଷର)',
  'agent.approvalEdit.field.reason.optionalHint': '(ବୈକଳ୍ପିକ)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'ଯଥା ଖେଳାଳି କେବଳ 30 ଟଙ୍କା ଦେଇଛନ୍ତି',
  'agent.approvalEdit.field.reason.placeholder.optional': 'ବୈକଳ୍ପିକ ନୋଟ୍',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI ଲେଣଦେଣ ID',
  'agent.approvalEdit.field.txn.placeholder': 'ଯଥା 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'ଆପଣଙ୍କ ପୁଲ୍ ({balance}) ଏହି ଅନୁମୋଦନକୁ କଭର କରିପାରିବ ନାହିଁ। ପ୍ରଥମେ ଟପ୍-ଅପ୍ କରନ୍ତୁ।',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'ଅନୁମୋଦିତ ରାଶି ସର୍ବନିମ୍ନ ₹1 ହେବା ଆବଶ୍ୟକ',
  'agent.approvalEdit.error.amountMax': 'ଅନୁମୋଦିତ ରାଶି ଅନୁରୋଧିତ {amount}ଠାରୁ ଅଧିକ ହୋଇପାରିବ ନାହିଁ',
  'agent.approvalEdit.error.reasonRequired': 'ଭିନ୍ନ ରାଶି ଅନୁମୋଦନ କରୁଥିଲାବେଳେ କାରଣ (≥5 ଅକ୍ଷର) ଆବଶ୍ୟକ',
  'agent.approvalEdit.error.txnRequired': 'UPI ଲେଣଦେଣ ID ଆବଶ୍ୟକ (≥6 ଅକ୍ଷର)',
  'agent.approvalEdit.error.fallback': 'ବିଫଳ — ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.approvalEdit.cta.approve': 'ଅନୁମୋଦନ କରନ୍ତୁ',
  'agent.approvalEdit.cta.markPaid': 'ପରିଶୋଧିତ ଚିହ୍ନଟ',
  'agent.approvalEdit.cta.submitting': 'ପଠାଉଛୁ…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'ମୋର ଆକାଉଣ୍ଟ',
  'agent.profile.hub.subtitle': 'ପରିଚୟ, ୱାଲେଟ୍ ଓ ଡିପୋଜିଟ୍ ଏକା ସ୍ଥାନରେ',
  'agent.profile.hub.tab.wallet': 'ୱାଲେଟ୍',
  'agent.profile.hub.tab.deposit': 'ଡିପୋଜିଟ୍',
  'agent.profile.hub.tab.account': 'ଆକାଉଣ୍ଟ',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'ସାଇନ୍ ଆଉଟ୍',
  'agent.profile.signOut.modal.title': 'ସାଇନ୍ ଆଉଟ୍?',
  'agent.profile.signOut.modal.body': 'ଆକାଉଣ୍ଟ‌କୁ ପ୍ରବେଶ କରିବାକୁ ଆପଣଙ୍କୁ ପୁଣି ସାଇନ୍ ଇନ୍ କରିବାକୁ ପଡ଼ିବ।',
  'agent.profile.signOut.modal.stay': 'ସାଇନ୍ ଇନ୍ ରୁହନ୍ତୁ',
  'agent.profile.signOut.modal.confirm': 'ହଁ, ସାଇନ୍ ଆଉଟ୍',
  'agent.profile.signOut.modal.busy': 'ସାଇନ୍ ଆଉଟ୍ ହେଉଛି…',
  'agent.profile.signOut.error': 'ସାଇନ୍ ଆଉଟ୍ କରିପାରିଲୁ ନାହିଁ',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'ସାଇନ୍ ଇନ୍ ନାହିଁ।',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'ଏଜେଣ୍ଟ',
  'agent.account.hero.role.admin': 'ପ୍ଲାଟଫର୍ମ ଆଡମିନ୍',
  'agent.account.hero.changePhoto': 'ଫଟୋ ବଦଳାନ୍ତୁ',
  'agent.account.hero.removePhoto': 'ଫଟୋ ହଟାନ୍ତୁ',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'ଆପଣଙ୍କ ଫଟୋ ସମନ୍ୱୟ କରନ୍ତୁ',
  'agent.account.avatar.confirmRemove': 'ଆପଣଙ୍କ ପ୍ରୋଫାଇଲ୍ ଫଟୋ ହଟାନ୍ତୁ?',
  'agent.account.toast.photoUpdated': 'ଫଟୋ ଅପଡେଟ୍ ହେଲା',
  'agent.account.toast.photoRemoved': 'ଫଟୋ ହଟାଗଲା',
  'agent.account.toast.saved': 'ସେଭ୍ ହେଲା',
  'agent.account.toast.locationSaved': 'ସ୍ଥାନ ସେଭ୍ ହେଲା',
  'agent.account.toast.passwordChanged': 'ପାସୱାର୍ଡ ବଦଳାଗଲା — ପୁଣି ସାଇନ୍ ଇନ୍ କରନ୍ତୁ',
  'agent.account.error.uploadFailed': 'ଅପଲୋଡ୍ ବିଫଳ',
  'agent.account.error.removeFailed': 'ବିଫଳ',
  'agent.account.error.saveFailed': 'ସେଭ୍ ବିଫଳ',
  'agent.account.error.changeFailed': 'ବଦଳାଇବା ବିଫଳ',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'ଆକାଉଣ୍ଟ ତଥ୍ୟ',
  'agent.account.info.subtitle': 'କେବଳ ପଠନଯୋଗ୍ୟ ପ୍ଲାଟଫର୍ମ ମେଟାଡାଟା',
  'agent.account.info.memberSince': 'ସଦସ୍ୟ ହେବାଠାରୁ',
  'agent.account.info.lastLogin': 'ଶେଷ ଲଗ୍-ଇନ୍',
  'agent.account.info.lastLoginIp': 'ଶେଷ ଲଗ୍-ଇନ୍ IP',
  'agent.account.info.reportsTo': 'କାହାକୁ ରିପୋର୍ଟ କରନ୍ତି',
  'agent.account.info.reportsToValue': 'ଆଡମିନ୍ #{id}',
  'agent.account.info.agentId': 'ଏଜେଣ୍ଟ ID',
  'agent.account.info.firstLogin': 'ପ୍ରଥମ ଥର',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'ବ୍ୟକ୍ତିଗତ ବିବରଣୀ',
  'agent.account.personal.subtitle.locked': 'ଆଡମିନ୍ ଦ୍ୱାରା ପରିଚୟ ଲକ୍ କରାଯାଇଛି · କେବଳ ଦେଖନ୍ତୁ',
  'agent.account.personal.subtitle.agent': 'ୟୁଜରନେମ୍, ଇମେଲ୍ ଓ ଯୋଗାଯୋଗ ନମ୍ବର',
  'agent.account.personal.subtitle.admin': 'ୟୁଜରନେମ୍ ଓ ଇମେଲ୍',
  'agent.account.personal.field.username': 'ୟୁଜରନେମ୍',
  'agent.account.personal.field.email': 'ଇମେଲ୍',
  'agent.account.personal.field.phone': 'ଯୋଗାଯୋଗ ନମ୍ବର',
  'agent.account.personal.field.phone.placeholder': 'ଯଥା +91 9876543210',
  'agent.account.personal.field.phone.hint': 'ଖେଳାଳି ଓ ଆଡମିନ୍ ଆପଣଙ୍କୁ ଏହି ନମ୍ବରରେ ଯୋଗାଯୋଗ କରନ୍ତି। ସଫା କରିବାକୁ ଖାଲି ଛାଡ଼ନ୍ତୁ।',
  'agent.account.personal.fieldLockedNote': 'ଲକ୍',
  'agent.account.personal.cta.save': 'ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',
  'agent.account.personal.cta.saving': 'ସେଭ୍ କରୁଛୁ…',
  'agent.account.personal.cta.noChanges': 'କୌଣସି ପରିବର୍ତ୍ତନ ନାହିଁ',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'ଆପଣଙ୍କ ସ୍ଥାନ',
  'agent.account.location.subtitle.summary': 'ଲୋକେଟର୍ ନୂଆ ଖେଳାଳିଙ୍କୁ ଆପଣଙ୍କ ନିକଟତମ ଅଞ୍ଚଳକୁ ପଠାଏ',
  'agent.account.location.subtitle.edit': 'ଆପଣଙ୍କ ଅଞ୍ଚଳ ଅପଡେଟ୍ କରନ୍ତୁ ଯାହାଫଳରେ ଲୋକେଟର୍ ସଠିକ୍ ଖେଳାଳି ପଠାଇବ',
  'agent.account.location.cta.edit': 'ସମ୍ପାଦନ',
  'agent.account.location.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.account.location.cta.save': 'ସେଭ୍',
  'agent.account.location.areaLabel': 'ଅଞ୍ଚଳ',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'ସ୍ଥାନ ସେଟ୍ ହୋଇଛି',
  'agent.account.location.setBody': 'ଫାଇଲରେ GPS ସ୍ଥାନାଙ୍କ — ଅଞ୍ଚଳ ବିବରଣୀ ଦେଖିବାକୁ ଏଡିଟ୍ ଟ୍ୟାପ୍ କରନ୍ତୁ।',
  'agent.account.location.lastUpdated': 'ଶେଷ ଅପଡେଟ୍ · {date}',

  // Security card (password change).
  'agent.account.security.title': 'ସୁରକ୍ଷା',
  'agent.account.security.subtitle.locked': 'ଆଡମିନ୍ ଦ୍ୱାରା ଲକ୍ · କେବଳ ଦେଖନ୍ତୁ',
  'agent.account.security.subtitle.unlocked': 'ଆପଣଙ୍କ ପାସୱାର୍ଡ ବଦଳାନ୍ତୁ · ଅନ୍ୟ ଡିଭାଇସରୁ ସାଇନ୍ ଆଉଟ୍ ହେବ',
  'agent.account.security.field.current': 'ବର୍ତ୍ତମାନର ପାସୱାର୍ଡ',
  'agent.account.security.field.new': 'ନୂଆ ପାସୱାର୍ଡ',
  'agent.account.security.field.new.hint': 'ଅତି କମରେ 8 ଅକ୍ଷର, ଗୋଟିଏ ଅକ୍ଷର ଓ ଗୋଟିଏ ଅଙ୍କ ସହିତ',
  'agent.account.security.field.confirm': 'ନୂଆଟି ନିଶ୍ଚିତ କରନ୍ତୁ',
  'agent.account.security.cta.update': 'ପାସୱାର୍ଡ ଅପଡେଟ୍ କରନ୍ତୁ',
  'agent.account.security.cta.updating': 'ଅପଡେଟ୍ ହେଉଛି…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'ନୂଆ ପାସୱାର୍ଡ ମିଶୁନାହିଁ',
  'agent.account.security.error.tooShort': 'ପାସୱାର୍ଡ ≥ 8 ଅକ୍ଷର ହେବା ଆବଶ୍ୟକ',
  'agent.account.security.error.weak': 'ପାସୱାର୍ଡରେ ଅତି କମରେ ଗୋଟିଏ ଅକ୍ଷର ଓ ଗୋଟିଏ ସଂଖ୍ୟା ଥିବା ଆବଶ୍ୟକ',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'ସେୟାର୍ କରିବା ଯୋଗ୍ୟ ଏଜେଣ୍ଟ ID',
  'agent.shareableId.tooltip.copy': 'UAID କପି କରନ୍ତୁ',
  'agent.shareableId.tooltip.copied': 'UAID କପି ହୋଇଗଲା',
  'agent.shareableId.cta.copy': 'କପି',
  'agent.shareableId.cta.copied': 'କପି ହୋଇଗଲା!',
  'agent.shareableId.body.before': 'ଏହି କୋଡ୍ ଅନ୍ୟ ଏଜେଣ୍ଟ ସହିତ ସେୟାର୍ କରନ୍ତୁ ଯାହାଫଳରେ ସେମାନେ ଆପଣଙ୍କୁ ଖେଳାଳି ଟ୍ରାନ୍ସଫର୍ କରିପାରିବେ। ଚାଟ୍ କିମ୍ବା WhatsApp ମାଧ୍ୟମରେ ପଠାଇବା ସୁରକ୍ଷିତ — ଏହାକୁ ସେୟାର୍ କରିବା ଆପଣଙ୍କ ଆକାଉଣ୍ଟକୁ କୌଣସି ପ୍ରବେଶ ',
  'agent.shareableId.body.not': 'ଦେଇ ନଥାଏ',
  'agent.shareableId.body.after': '।',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'ପୁରସ୍କାର',
  'agent.rewards.hub.subtitle': 'ଅଫର୍ ବ୍ରାଉଜ୍ କରନ୍ତୁ ଓ ଆପଣଙ୍କ ଏକ୍ସପୋଜର୍ ଟ୍ରାକ୍ କରନ୍ତୁ',
  'agent.rewards.hub.tab.browse': 'ଅଫର୍ ବ୍ରାଉଜ୍ କରନ୍ତୁ',
  'agent.rewards.hub.tab.exposure': 'ମୋର ଏକ୍ସପୋଜର୍',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'ଅପ୍ଟ୍ ଇନ୍ ହୋଇଛି',
  'agent.rewards.status.optedOut': 'ଅପ୍ଟ୍ ଆଉଟ୍ ହୋଇଛି',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'ଆପଣଙ୍କ ଖେଳାଳିଙ୍କ ପାଇଁ ବିଶେଷ',
  'agent.rewards.browse.empty.title': 'ବର୍ତ୍ତମାନ କୌଣସି ସକ୍ରିୟ ପୁରସ୍କାର ନାହିଁ',
  'agent.rewards.browse.empty.body': 'ଆଡମିନ୍ ନିୟମିତ ଭାବେ ନୂଆ ଅଫର୍ ପୋଷ୍ଟ କରନ୍ତି। କିଛି ସମୟ ପରେ ଦେଖନ୍ତୁ — କିମ୍ବା ଯାହା ମିସ୍ ହୋଇଯାଇଛି ସେଥିରେ ଆପଣଙ୍କୁ ଅପ୍ଟ୍ ଇନ୍ କରିବାକୁ ଆପଣଙ୍କ ଅପ୍-ଲାଇନ୍କୁ କୁହନ୍ତୁ।',
  'agent.rewards.browse.error.loadFailed': 'ପୁରସ୍କାର ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ',
  'agent.rewards.browse.card.aria.manage': '{title} ପାଇଁ ଅପ୍ଟ୍-ଇନ୍ ପରିଚାଳନା କରନ୍ତୁ',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'ଅପ୍ଟ୍ ଇନ୍ ହୋଇଛି',
  'agent.rewards.browse.chip.optInPrompt': 'ଅପ୍ଟ୍ ଇନ୍ ପାଇଁ ଟ୍ୟାପ୍ କରନ୍ତୁ',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'ଆପଣଙ୍କ ଏକ୍ସପୋଜର୍ ଗଣନା କରୁଛୁ…',
  'agent.rewards.exposure.error.loadFailed': 'ଏକ୍ସପୋଜର୍ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'ଅଫର୍ ଏକ୍ସପୋଜର୍',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} ସକ୍ରିୟ ଅପ୍ଟ୍-ଇନ୍',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} ସକ୍ରିୟ ଅପ୍ଟ୍-ଇନ୍',

  'agent.rewards.exposure.hero.actualSpend.label': 'ବାସ୍ତବ ଖର୍ଚ୍ଚ',
  'agent.rewards.exposure.hero.actualSpend.helper': 'ଏ ପର୍ଯ୍ୟନ୍ତ ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ଦ୍ୱାରା ଦାବି କରାଯାଇଛି।',

  'agent.rewards.exposure.hero.maxExposure.label': 'ସର୍ବାଧିକ ଏକ୍ସପୋଜର୍',
  'agent.rewards.exposure.hero.maxExposure.helper': 'ସବୁଠାରୁ ଖରାପ ସ୍ଥିତିର ସୀମା — ବଳକା ବଜେଟ୍ର ପ୍ରତ୍ୟେକ ଟଙ୍କା ଦାବି କରାଯାଇଛି।',

  'agent.rewards.exposure.hero.ceiling.label': 'ସୀମା ଖର୍ଚ୍ଚ',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'ବିପଦ ମିଟର୍',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} ସର୍ବାଧିକ',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': 'ଠାରେ ',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': ', ଆପଣଙ୍କ ଅପ୍ଟ୍-ଇନ୍ ଉପରେ ଧ୍ୟାନ ଦିଅନ୍ତୁ',
  'agent.rewards.exposure.gauge.legend.rose.before': 'ଠାରେ ',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': ', ଆପଣ ଆପଣଙ୍କ ସୀମା ନିକଟରେ ଅଛନ୍ତି',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'ଆପଣ ବର୍ତ୍ତମାନ ଏପରି କୌଣସି ଅଫର୍ରେ ଅପ୍ଟ୍ ଇନ୍ ନୁହଁନ୍ତି ଯାହାର ଅନୁମାନିତ ସୀମା ଅଛି।',
  'agent.rewards.exposure.gauge.caption.high': 'ଆପଣ ଆପଣଙ୍କ ସର୍ବାଧିକ ଏକ୍ସପୋଜର୍ର {pct}% ଖର୍ଚ୍ଚ କରିସାରିଛନ୍ତି। ଅଧିକାଂଶ ଅପ୍ଟ୍-ଇନ୍ ଭାରୀଭାବେ ଦାବି କରାଯାଇଛି।',
  'agent.rewards.exposure.gauge.caption.mid': 'ଆପଣ ଆପଣଙ୍କ ସର୍ବାଧିକ ସୀମାର {pct}%ରେ ଅଛନ୍ତି। ପ୍ରତି-ଅଫର୍ ବିବରଣୀ ଉପରେ ଧ୍ୟାନ ଦିଅନ୍ତୁ।',
  'agent.rewards.exposure.gauge.caption.low': 'ଆରାମଦାୟକ — ଏ ପର୍ଯ୍ୟନ୍ତ ଆପଣଙ୍କ ସର୍ବାଧିକ ସୀମାର କେବଳ {pct}% ଖର୍ଚ୍ଚ ହୋଇଛି।',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'ସର୍ବାଧିକ ଏକ୍ସପୋଜର୍ କିପରି ଗଣନା ହୁଏ:',
  'agent.rewards.exposure.footnote.prefix': ' ପ୍ରତ୍ୟେକ ଅପ୍ଟ୍-ଇନ୍ ଅଫର୍ ପାଇଁ: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — ରକ୍ଷଣଶୀଳ ଉପର ସୀମା। ଆପଣଙ୍କ ବାସ୍ତବ ଦାୟିତ୍ୱ ସାଧାରଣତଃ ',
  'agent.rewards.exposure.footnote.emphasis': 'କମ୍',
  'agent.rewards.exposure.footnote.suffix2': ' ହୁଏ ଯେତେବେଳେ ଆପଣଙ୍କ ତଳର ଉପ-ଏଜେଣ୍ଟ ମଧ୍ୟ ଅପ୍ଟ୍ ଇନ୍ କରନ୍ତି (ସେମାନେ ବଡ଼ ଅଂଶ ଦିଅନ୍ତି; ଆପଣ ବକାୟା ଦିଅନ୍ତି)।',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'କୌଣସି ସକ୍ରିୟ ଅପ୍ଟ୍-ଇନ୍ ନାହିଁ',
  'agent.rewards.exposure.empty.body': 'ଯେତେବେଳେ ଆପଣ ବ୍ରାଉଜ୍ ଅଫର୍ ଟ୍ୟାବରୁ କୌଣସି ଅଫର୍ରେ ଅପ୍ଟ୍ ଇନ୍ କରନ୍ତି, ଏହା ଏଠାରେ ଆପଣଙ୍କ ଖର୍ଚ୍ଚ ଓ ଅନୁମାନିତ ଏକ୍ସପୋଜର୍ ସହିତ ଦେଖାଯାଏ।',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% ଅଂଶ',
  'agent.rewards.exposure.row.claims.one': '{count} ଦାବି',
  'agent.rewards.exposure.row.claims.other': '{count} ଦାବି',
  'agent.rewards.exposure.row.overridden': 'ଆଡମିନ୍-ଓଭରରାଇଡ୍ ହୋଇଛି',
  'agent.rewards.exposure.row.actualMaxLabel': 'ବାସ୍ତବ / ସର୍ବାଧିକ',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'ଖର୍ଚ୍ଚ-ଅଂଶୀଦାରୀ ଅପ୍ଟ୍-ଇନ୍',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'ଗେମ୍ ଲାଭ',
  'agent.profile.rates.infoBanner.standardPctText': ' ମାନକ ପ୍ରତିଶତ ବ୍ୟବହାର କରେ। ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'ପ୍ରଚାରଗତ ଖର୍ଚ୍ଚ',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ଅଫର, କ୍ୟାଶବ୍ୟାକ) ଏକ ଡାଇନାମିକ ଅପଲାଇନ ପୁଲ ବ୍ୟବହାର କରନ୍ତି। ଉଭୟ ଆପଣଙ୍କ ସ୍ତର (L{level}) ଉପରେ ଆଧାରିତ ପ୍ଲାଟଫର୍ମ ଆଡମିନଙ୍କ ଦ୍ୱାରା ବିଶ୍ୱସ୍ତରୀୟ ଭାବେ ପରିଚାଳିତ ହୁଅନ୍ତି।',
  'agent.profile.rates.section.gameProfits': 'ଗେମ୍ ଲାଭ — ପ୍ରତି ମ୍ୟାଚ୍ ରେ ଆପଣ କ’ଣ ଅର୍ଜନ କରନ୍ତି',
  'agent.profile.rates.section.promotionalCosts': 'ପ୍ରଚାରଗତ ଖର୍ଚ୍ଚ — ଆପଣଙ୍କର ଅପଲାଇନ ସବସିଡି ଅବଦାନ',
  'agent.profile.rates.pillar.platformFee': 'ପ୍ଲାଟଫର୍ମ ଶୁଳ୍କ %',
  'agent.profile.rates.pillar.winLoss': 'ଜିତ / ହାର ଲାଭ %',
  'agent.profile.rates.pillar.promoWeight': 'ପ୍ରଚାରଗତ ପୁଲ ଓଜନ',
  'agent.profile.rates.guarantee.title': 'ସିଧାସଳଖ ରେଫରାଲ ଗ୍ୟାରେଣ୍ଟି',
  'agent.profile.rates.guarantee.body': 'ଯଦି ଆପଣ ସିଧାସଳଖ ଅଫର ଦାବି କରୁଥିବା ଖେଳାଳିଙ୍କୁ ରେଫର କରିଛନ୍ତି, ଆପଣ ₹0.00 ଦେବେ। ଆପଣଙ୍କ ନେଟୱର୍କ ବଢ଼ାଇବାରେ ସାହାଯ୍ୟ କରିବାକୁ ଆପଣଙ୍କ ଅପଲାଇନମାନେ ଖର୍ଚ୍ଚର 100% ସବସିଡି ଦିଅନ୍ତି।',
  'agent.profile.rates.poolExplainer.title': 'ପୁଲ କିପରି କାମ କରେ:',
  'agent.profile.rates.poolExplainer.body': 'ଯେତେବେଳେ ଏକ ସବ-ଏଜେଣ୍ଟଙ୍କ ଖେଳାଳି ଅଫର ଦାବି କରନ୍ତି, ଆପଣଙ୍କ ଓଜନ ବାକି ସକ୍ରିୟ ଅପଲାଇନ + ପ୍ଲାଟଫର୍ମ ସହିତ ଏକ ପୁଲରେ ଯୋଗ ହୁଏ। ଆପଣ (ଆପଣଙ୍କ ଓଜନ / ମୋଟ ପୁଲ) × ଅଫର ଖର୍ଚ୍ଚ ଦେବେ। ଅଧିକ ଓଜନ = ବଡ ଅଂଶ।',
  'agent.profile.rates.weightLabel': 'ଓଜନ:',
  'agent.profile.rates.updatedPrefix': 'ଅଦ୍ୟତିତ',
  'agent.profile.rates.notSetYet': 'ଏପର୍ଯ୍ୟନ୍ତ ସେଟ୍ ହୋଇନାହିଁ',
  'agent.rewards.optInModal.guarantee.title': 'ସିଧାସଳଖ ରେଫରାଲ ଗ୍ୟାରେଣ୍ଟି',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'ଯଦି <strong>ଆପଣ ସିଧାସଳଖ ରେଫର କରିଛନ୍ତି</strong> ଏହି ଅଫର ଦାବି କରୁଥିବା ଖେଳାଳିଙ୍କୁ, ତେବେ ଆପଣ <strong>₹0.00</strong> ଦେବେ। ଆପଣଙ୍କ ନେଟୱର୍କ ବଢ଼ାଇବାରେ ସାହାଯ୍ୟ କରିବାକୁ ଆପଣଙ୍କ ଅପଲାଇନମାନେ ଖର୍ଚ୍ଚର 100% ସବସିଡି ଦିଅନ୍ତି।',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'ଯେତେବେଳେ <strong>ଆପଣଙ୍କ ଖେଳାଳିମାନେ</strong> ଏହି ଅଫର ଦାବି କରନ୍ତି, ଆପଣ <strong>₹0.00</strong> ଦେବେ। ଆପଣଙ୍କ ନେଟୱର୍କ ବଢ଼ାଇବାରେ ସାହାଯ୍ୟ କରିବାକୁ ଆପଣଙ୍କ ଅପଲାଇନମାନେ ଖର୍ଚ୍ଚର 100% ସବସିଡି ଦିଅନ୍ତି।',
  'agent.rewards.optInModal.uplineReality.title': 'ଅପଲାଇନ ବାସ୍ତବତା ଯାଞ୍ଚ',
  'agent.rewards.optInModal.uplineReality.body': 'ଆପଣଙ୍କର <strong>{count}</strong> ସିଧାସଳଖ ସବ-ଏଜେଣ୍ଟ{plural} ଅଛନ୍ତି। ଯଦି ଏକ ସବ-ଏଜେଣ୍ଟଙ୍କ ଖେଳାଳି ଏହି ଅଫର ଦାବି କରନ୍ତି, ତେବେ ଆପଣ ବାକି ସକ୍ରିୟ ଅପଲାଇନ ଏବଂ ପ୍ଲାଟଫର୍ମ ତୁଳନାରେ ଆପଣଙ୍କର <strong>ପୁଲ ଓଜନ ({weight})</strong> ଆଧାରରେ ଅନୁପାତିକ ଅଂଶ ଦେବେ।',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ସିଧାସଳଖ',
  'agent.rates.col.indirect': 'ପରୋକ୍ଷ',
  'agent.rates.indirect.dynamic': 'ଡାଇନାମିକ',
  'agent.rates.indirect.notApplicable': 'ପ୍ରଯୁଜ୍ୟ ନୁହେଁ',
  'agent.rates.indirect.rangeTo': 'ରୁ',
  'agent.rewards.optInModal.close': 'ବନ୍ଦ କରନ୍ତୁ',
  'agent.rewards.optInModal.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.rewards.optInModal.loading': 'ଅଫର୍ ବିବରଣୀ ଲୋଡ୍ ହେଉଛି…',
  'agent.rewards.optInModal.error.loadFailed': 'ଅଫର୍ ବିବରଣୀ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ',
  'agent.rewards.optInModal.error.optInFailed': 'ଅପ୍ଟ୍-ଇନ୍ ବିଫଳ',
  'agent.rewards.optInModal.error.optOutFailed': 'ଅପ୍ଟ୍-ଆଉଟ୍ ବିଫଳ',
  'agent.rewards.optInModal.error.saveFailed': 'ଉପ-ଏଜେଣ୍ଟ ଦର ସେଭ୍ ହୋଇପାରିଲା ନାହିଁ',
  'agent.rewards.optInModal.toast.optedIn': 'ଅପ୍ଟ୍ ଇନ୍ ହୋଇଛି। ଏବେ ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ଏହି ଅଫର୍ ଦେଖିପାରିବ।',
  'agent.rewards.optInModal.toast.optedOut': 'ଅପ୍ଟ୍ ଆଉଟ୍ ହୋଇଛି। ଅଫର୍ ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ରୁ ହଟାଯାଇଛି।',
  'agent.rewards.optInModal.toast.saved.one': '{count} ଉପ-ଏଜେଣ୍ଟ ଦର ପରିବର୍ତ୍ତନ ସେଭ୍ ହୋଇଛି।',
  'agent.rewards.optInModal.toast.saved.other': '{count} ଉପ-ଏଜେଣ୍ଟ ଦର ପରିବର୍ତ୍ତନ ସେଭ୍ ହୋଇଛି।',
  'agent.rewards.optInModal.confirmOptOut': 'ଏହି ଅଫର୍ରୁ ଅପ୍ଟ୍ ଆଉଟ୍? ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ଏହାକୁ ଆଉ ଦେଖିପାରିବ ନାହିଁ।',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'ଅଫର୍ #{id} ପାଇଁ ଅପ୍ଟ୍-ଇନ୍ ମୋଡାଲରୁ ସେଟ୍',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'ଆପଣଙ୍କ ଅଂଶ',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'ଅପ୍ଟ୍-ଇନ୍ ସମୟରେ ଲକ୍',
  'agent.rewards.optInModal.stake.current': 'ବର୍ତ୍ତମାନ ଦର',
  'agent.rewards.optInModal.stake.noRate': 'ଆଡମିନ୍ ଆପଣଙ୍କୁ ବର୍ତ୍ତମାନ କୌଣସି ଦର ଦେଇନାହାନ୍ତି',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'ଏ ପର୍ଯ୍ୟନ୍ତ ଖର୍ଚ୍ଚ',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'ଏହି ଅଫର୍ରେ ଡାଉନ୍-ଲାଇନ୍ ଦାବି ପାଇଁ ଆପଣଙ୍କ ୱାଲେଟ୍ରୁ ଏ ପର୍ଯ୍ୟନ୍ତ ପ୍ରଦାନ କରାଯାଇଥିବା ସମୁଦାୟ ଟଙ୍କା।',
  'agent.rewards.optInModal.stake.maxPossible.label': 'ସର୍ବାଧିକ ସମ୍ଭବ',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'ସବୁଠାରୁ ଖରାପ ସ୍ଥିତିରେ ଆପଣ ଯେତିକି ଟଙ୍କା ଦେଇପାରନ୍ତି ଯଦି ବଳକା ବଜେଟ୍ର ପ୍ରତ୍ୟେକ ଟଙ୍କା ଦାବି କରାଯାଏ।',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— କୌଣସି ବଜେଟ୍ ସୀମା ନାହିଁ —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'ସବୁଠାରୁ ଖରାପ ସ୍ଥିତି: ଯଦି ପ୍ଲାଟଫର୍ମ ବଜେଟ୍ର ବଳକା ପ୍ରତ୍ୟେକ ₹{amount} ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ଦ୍ୱାରା ଦାବି କରାଯାଏ, ଆପଣ ତାହାର {pct}% ପ୍ରଦାନ କରନ୍ତି।',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'ଆପଣ ଅପ୍ଟ୍ ଇନ୍ ଅଛନ୍ତି',
  'agent.rewards.optInModal.toggle.title.optedOut': 'ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ସହ ସେୟାର୍ କରିବାକୁ ଅପ୍ଟ୍ ଇନ୍ କରନ୍ତୁ',
  'agent.rewards.optInModal.toggle.body.optedIn': 'ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ର ଖେଳାଳିମାନେ ଏହି ଅଫର୍ ଦେଖନ୍ତି। ଆପଣ ସେମାନଙ୍କର ପ୍ରତ୍ୟେକ କ୍ଲେମ୍ କୁ ଆପଣଙ୍କ ଲକ୍ଡ୍ ସେୟାର୍ ରେ ସହ-ପୁଞ୍ଜି କରନ୍ତି।',
  'agent.rewards.optInModal.toggle.body.optedOut': 'ଯେପର୍ଯ୍ୟନ୍ତ ଆପଣ ଅପ୍ଟ୍ ଇନ୍ ନ କରନ୍ତି, ଆପଣଙ୍କର କୌଣସି ଖେଳାଳି ଏହି ଅଫର୍ ଦେଖିବେ ନାହିଁ। ଅପ୍ଟ୍ ଇନ୍ କଲା ମାତ୍ରେ, ଆପଣଙ୍କ ସେୟାର୍ ରେଟ୍ ଏହି ଅଫର୍ ପାଇଁ ଲକ୍ଡ୍ ହୋଇଯିବ।',
  'agent.rewards.optInModal.toggle.optOutLink': 'ଏହି ଅଫର୍ ରୁ ଅପ୍ଟ୍ ଆଉଟ୍ କରନ୍ତୁ',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'ଉପ-ଏଜେଣ୍ଟ ରେଟ୍ ଗୁଡ଼ିକ',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ପ୍ରତ୍ୟେକ ସର୍ବାଧିକ ନେଇପାରିବେ: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} ଅସଞ୍ଚିତ',
  'agent.rewards.optInModal.subAgents.empty': 'କୌଣସି ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ନାହାନ୍ତି।',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'ସେଭ୍ କରିବା କ୍ୟାସ୍କେଡ୍ ହୁଏ — ଯଦି ଆପଣ ଏଠାରେ ଏକ ଚାଇଲ୍ଡ୍ କୁ ବଢ଼ାନ୍ତି ଯାହାର ପୂର୍ବରୁ ଅଧିକ ଗଭୀର ଉପ-ଏଜେଣ୍ଟ ଅଛନ୍ତି, ଚେନ୍ କୁ ସୁସଙ୍ଗତ ରଖିବା ପାଇଁ ସେହି ରେଟ୍ ଗୁଡ଼ିକ ସ୍ୱୟଂ-କ୍ଲାମ୍ପ୍ ହୋଇପାରେ।',
  'agent.rewards.optInModal.subAgents.noChildren': 'ଆପଣଙ୍କର କୌଣସି ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ ନାହାନ୍ତି। ଅଫର୍ ଆପଣଙ୍କ ନିଜ ଖେଳାଳିଙ୍କୁ ଦୃଶ୍ୟମାନ; ଆଡ୍ମିନ୍ ସମ୍ପୂର୍ଣ୍ଣ ଅବଶିଷ୍ଟ ପ୍ରଦାନ କରନ୍ତି।',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'ଏକ ରେଟ୍ ପ୍ରବେଶ କରନ୍ତୁ (ସଫା କରିବାକୁ 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'କେବଳ ସଂଖ୍ୟା',
  'agent.rewards.optInModal.error.negative': 'ଋଣାତ୍ମକ ହୋଇପାରିବ ନାହିଁ',
  'agent.rewards.optInModal.error.max100': 'ସର୍ବାଧିକ 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'ସର୍ବାଧିକ {ceiling}% (ଆପଣଙ୍କ ସେୟାର୍)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'ସେଭ୍ କରୁଛି…',
  'agent.rewards.optInModal.cta.done': 'ହୋଇଗଲା',
  'agent.rewards.optInModal.cta.save.one': '{count} ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',
  'agent.rewards.optInModal.cta.save.other': '{count} ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'ଖେଳାଳିଙ୍କୁ ରିଚାର୍ଜ୍ କରନ୍ତୁ',
  'agent.adminRechargePlayer.subtitle': 'ଆପଣଙ୍କ ପୁଲ୍ ରୁ ଖେଳାଳିଙ୍କ ୱାଲେଟ୍ କୁ',
  'agent.adminRechargePlayer.backLink': 'ଖେଳାଳି ପାଖକୁ ଫେରନ୍ତୁ',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'ଏକ ନୂତନ ଖେଳାଳି ଅନ୍ବୋର୍ଡ୍ କରନ୍ତୁ',
  'agent.createPlayer.subtitle.agent': 'ସେମାନେ ସ୍ୱୟଂ ଆପଣଙ୍କ ଅଧୀନରେ ହେବେ।',
  'agent.createPlayer.subtitle.admin': 'ତଳେ ଏକ ମାଲିକ ଏଜେଣ୍ଟ ବାଛନ୍ତୁ, କିମ୍ବା ପ୍ଲାଟଫର୍ମ-ଡାଇରେକ୍ଟ ପାଇଁ ଖାଲି ଛାଡ଼ନ୍ତୁ।',
  'agent.createPlayer.close': 'ବନ୍ଦ କରନ୍ତୁ',
  'agent.createPlayer.section.identity.title': 'ପରିଚୟ',
  'agent.createPlayer.section.identity.hint': 'ଆକାଉଣ୍ଟ ତିଆରି କରିବାକୁ ଆବଶ୍ୟକ',
  'agent.createPlayer.field.username.label': 'ୟୁଜରନେମ୍',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ଇମେଲ୍',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'ମୋବାଇଲ୍',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-ଅଙ୍କର ଭାରତୀୟ ମୋବାଇଲ୍, +91 ସ୍ୱୟଂ ଯୋଡ଼ାଯିବ',
  'agent.createPlayer.field.password.label': 'ପ୍ରାରମ୍ଭିକ ପାସୱାର୍ଡ୍',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ ଅକ୍ଷର, ଅକ୍ଷର + ଅଙ୍କ',
  'agent.createPlayer.section.owner.title': 'ମାଲିକ ଏଜେଣ୍ଟ',
  'agent.createPlayer.section.owner.hint': 'ବୈକଳ୍ପିକ — ପ୍ଲାଟଫର୍ମ-ଡାଇରେକ୍ଟ ଖେଳାଳି ପାଇଁ ଖାଲି ଛାଡ଼ନ୍ତୁ',
  'agent.createPlayer.field.owner.label': 'ଏଜେଣ୍ଟ',
  'agent.createPlayer.field.owner.none': '— କେହି ନୁହଁନ୍ତି (ପ୍ଲାଟଫର୍ମ-ଡାଇରେକ୍ଟ) —',
  'agent.createPlayer.section.profile.title': 'ପ୍ରୋଫାଇଲ୍',
  'agent.createPlayer.section.profile.hint': 'ବୈକଳ୍ପିକ — ବର୍ତ୍ତମାନ ଯାହା ଜାଣିଛନ୍ତି ଭରନ୍ତୁ; ଖେଳାଳି ପ୍ରଥମ ଲଗଇନ୍ ରେ ସମ୍ପୂର୍ଣ୍ଣ କରିପାରିବେ',
  'agent.createPlayer.field.firstName.label': 'ପ୍ରଥମ ନାମ',
  'agent.createPlayer.field.lastName.label': 'ଶେଷ ନାମ',
  'agent.createPlayer.field.dob.label': 'ଜନ୍ମ ତାରିଖ',
  'agent.createPlayer.field.gender.label': 'ଲିଙ୍ଗ',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— ନିର୍ଦ୍ଦିଷ୍ଟ ନୁହେଁ —',
  'agent.createPlayer.gender.male': 'ପୁରୁଷ',
  'agent.createPlayer.gender.female': 'ମହିଳା',
  'agent.createPlayer.gender.other': 'ଅନ୍ୟ',
  'agent.createPlayer.gender.preferNot': 'କହିବାକୁ ଚାହାନ୍ତି ନାହିଁ',
  'agent.createPlayer.field.language.label': 'ପସନ୍ଦର ଭାଷା',
  'agent.createPlayer.section.location.title': 'ସ୍ଥାନ',
  'agent.createPlayer.section.location.hint': 'ବୈକଳ୍ପିକ — ଖେଳାଳି ପ୍ରଥମ ଲଗଇନ୍ ରେ ସମ୍ପୂର୍ଣ୍ଣ କରିପାରିବେ',
  // Footer.
  'agent.createPlayer.disclaimer': 'ଖେଳାଳିଙ୍କୁ status=active ଏବଂ ଇମେଲ୍ ସ୍ୱୟଂ-ସତ୍ୟାପିତ ସହ ତିଆରି କରାଯାଏ — କୌଣସି ଲିଙ୍କ୍ ପଠାଯାଏ ନାହିଁ। ନିଶ୍ଚିତ କରନ୍ତୁ ଯେ ଆପଣ ଯୋଗାଯୋଗ ତଥ୍ୟ ଉପରେ ଭରସା କରନ୍ତି।',
  'agent.createPlayer.cta.cancel': 'ବାତିଲ୍ କରନ୍ତୁ',
  'agent.createPlayer.cta.creating': 'ତିଆରି କରୁଛି…',
  'agent.createPlayer.cta.submit': 'ଖେଳାଳି ତିଆରି କରନ୍ତୁ',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'ଏଜେଣ୍ଟ ID ନାହିଁ',
  'agent.createPlayer.error.createFailed': 'ଖେଳାଳି ତିଆରି କରିପାରିଲେ ନାହିଁ',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'ଆଜି',
  'agent.adminHome.range.yesterday': 'ଗତକାଲି',
  'agent.adminHome.range.week': 'ଏହି ସପ୍ତାହ',
  'agent.adminHome.range.lastWeek': 'ଗତ ସପ୍ତାହ',
  'agent.adminHome.range.month': 'ଏହି ମାସ',
  'agent.adminHome.range.lastMonth': 'ଗତ ମାସ',
  'agent.adminHome.range.all': 'ସମ୍ପୂର୍ଣ୍ଣ',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'ସ୍ୱାଗତ',
  'agent.adminHome.greeting.fallback': 'ବନ୍ଧୁ',
  'agent.adminHome.errorBanner.fallback': 'ଲୋଡ୍ କରିବାରେ ବିଫଳ',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'ପ୍ଲାଟଫର୍ମ ଇନ୍-ଫ୍ଲୋ',
  'agent.adminHome.admin.action.moneyQueue': 'ମନି କ୍ୟୁ',
  'agent.adminHome.admin.action.agents': 'ଏଜେଣ୍ଟ',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'ମ୍ୟାଚ୍',
  'agent.adminHome.admin.snapshot.newSignups': 'ନୂତନ ସାଇନ୍-ଅପ୍',
  'agent.adminHome.admin.snapshot.active': 'ସକ୍ରିୟ',
  'agent.adminHome.admin.snapshot.pending': 'ଅପେକ୍ଷାରତ',

  // Section titles between rows.
  'agent.adminHome.section.games': 'ଗେମ୍',
  'agent.adminHome.section.games.subtitle': 'ମ୍ୟାଚ୍ ଏବଂ ପ୍ଲାଟଫର୍ମ ଫିସ୍ · {range}',
  'agent.adminHome.section.activity': 'କାର୍ଯ୍ୟକଳାପ',
  'agent.adminHome.section.ops': 'ସଞ୍ଚାଳନ ଓ ଅନ୍ତର୍ଦୃଷ୍ଟି',
  'agent.adminHome.section.ops.subtitle': 'ଲାଇଭ୍ ସ୍ନାପସଟ୍ — ସମ୍ପୂର୍ଣ୍ଣ ପୃଷ୍ଠାକୁ ଯିବାକୁ କୌଣସି କାର୍ଡ୍ ଉପରେ କ୍ଲିକ୍ କରନ୍ତୁ',
  'agent.adminHome.section.gameSettings': 'ଗେମ୍ ସେଟିଂସ୍',
  'agent.adminHome.section.gameSettings.subtitle': 'ପ୍ରତ୍ୟେକ ଗେମ୍ କନଫିଗର୍ କରନ୍ତୁ',
  'agent.adminHome.section.downline': 'ମୋର ଡାଉନ୍-ଲାଇନ୍ ଅନ୍ତର୍ଦୃଷ୍ଟି',
  'agent.adminHome.section.downline.subtitle': 'ସମ୍ପୂର୍ଣ୍ଣ ପୃଷ୍ଠାକୁ ଯିବାକୁ କୌଣସି କାର୍ଡ୍ ଉପରେ କ୍ଲିକ୍ କରନ୍ତୁ',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'ଖେଳାଳି ୱାଲେଟ୍',
  'agent.adminHome.admin.card.players.headlineLabel': 'ସମୟସୀମା ମଧ୍ୟରେ ରିଚାର୍ଜ୍',
  'agent.adminHome.admin.card.players.flow.recharges': 'ରିଚାର୍ଜ୍',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'ଉଠାଣ',
  'agent.adminHome.admin.card.players.pending.recharges': 'ଅପେକ୍ଷାରତ ରିଚାର୍ଜ୍',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'ଅପେକ୍ଷାରତ ଉଠାଣ',
  'agent.adminHome.admin.card.players.stat.total': 'ମୋଟ ଖେଳାଳି',
  'agent.adminHome.admin.card.players.stat.active': 'ସକ୍ରିୟ',
  'agent.adminHome.admin.card.players.stat.suspended': 'ସ୍ଥଗିତ',

  'agent.adminHome.admin.card.agents.title': 'ଏଜେଣ୍ଟ ୱାଲେଟ୍',
  'agent.adminHome.admin.card.agents.headlineLabel': 'ଏଜେଣ୍ଟ ପୁଲ୍‌ରେ',
  'agent.adminHome.admin.card.agents.flow.topups': 'ଟପ୍-ଅପ୍ (ଆଡମିନ୍ → ଏଜେଣ୍ଟ)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'ଆଉଟ୍‌ଫ୍ଲୋ (ପେମେଣ୍ଟ / ଟପ୍-ଅପ୍)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'ଅପେକ୍ଷାରତ ଏଜେଣ୍ଟ ଉଠାଣ',
  'agent.adminHome.admin.card.agents.stat.total': 'ମୋଟ ଏଜେଣ୍ଟ',
  'agent.adminHome.admin.card.agents.stat.active': 'ସକ୍ରିୟ',
  'agent.adminHome.admin.card.agents.stat.suspended': 'ସ୍ଥଗିତ',

  'agent.adminHome.admin.card.bots.title': 'ବଟ୍ ୱାଲେଟ୍',
  'agent.adminHome.admin.card.bots.headlineLabel': 'ବଟ୍ ୱାଲେଟ୍‌ରେ',
  'agent.adminHome.admin.card.bots.stat.total': 'ମୋଟ ବଟ୍',
  'agent.adminHome.admin.card.bots.stat.active': 'ସକ୍ରିୟ',
  'agent.adminHome.admin.card.bots.stat.matches': 'ମ୍ୟାଚ୍',

  'agent.adminHome.admin.card.rewards.title': 'ପୁରସ୍କାର',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'ଏହି ସପ୍ତାହର ପେମେଣ୍ଟ',
  'agent.adminHome.admin.card.rewards.pending.claims': 'ଅପେକ୍ଷାରତ ଦାବି',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'ସକ୍ରିୟ ଅଫର୍',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'ବଲ୍କ ଜବ୍',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'ମ୍ୟାଚ୍',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'ଲୁଡୋ',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'ରମି',
  'agent.adminHome.game.carrom': 'କେରମ୍',
  'agent.adminHome.game.chess': 'ଚେସ୍',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'ଟପ୍ ଖେଳାଳି',
  'agent.adminHome.activity.topPlayers.empty': 'ଏହି ଅବଧିରେ କୌଣସି ମ୍ୟାଚ୍ ନାହିଁ।',
  'agent.adminHome.activity.topPlayers.metric': '{count} ମ୍ୟାଚ୍',
  'agent.adminHome.activity.topAgents.title': 'ଟପ୍ ଏଜେଣ୍ଟ',
  'agent.adminHome.activity.topAgents.subtitle': 'ମାଲିକାନା ଖେଳାଳି ଅନୁସାରେ',
  'agent.adminHome.activity.topAgents.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ସକ୍ରିୟ ଏଜେଣ୍ଟ ନାହିଁ।',
  'agent.adminHome.activity.topAgents.metric': '{count} ଖେଳାଳି',
  'agent.adminHome.activity.recent.title': 'ସାମ୍ପ୍ରତିକ ଲେଣଦେଣ',
  'agent.adminHome.activity.recent.subtitle': 'ଶେଷ 5',
  'agent.adminHome.activity.recent.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଲେଣଦେଣ ନାହିଁ।',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'ଟପ୍ ଆୟକାରୀ (7d)',
  'agent.adminHome.ops.topEarners.subtitle': 'True Net ଅନୁସାରେ',
  'agent.adminHome.ops.topEarners.empty': 'ଗତ 7 ଦିନରେ କୌଣସି ଆୟ ନାହିଁ।',
  'agent.adminHome.ops.liveActivity.title': 'ଲାଇଭ୍ କାର୍ଯ୍ୟକଳାପ',
  'agent.adminHome.ops.liveActivity.subtitle': 'ପ୍ଲାଟଫର୍ମ ବ୍ୟାପୀ ଶେଷ 5 ଇଭେଣ୍ଟ',
  'agent.adminHome.ops.liveActivity.empty': 'ଶାନ୍ତ — ଗତ 24h ରେ କୌଣସି ଇଭେଣ୍ଟ ନାହିଁ।',
  'agent.adminHome.ops.botPerf.title': 'ବଟ୍ ପ୍ରଦର୍ଶନ (7d)',
  'agent.adminHome.ops.botPerf.subtitle': 'ପ୍ରତି-ଶ୍ରେଣୀ P&L',
  'agent.adminHome.ops.rateChanges.title': 'ସାମ୍ପ୍ରତିକ ରେଟ୍ ପରିବର୍ତ୍ତନ',
  'agent.adminHome.ops.rateChanges.subtitle': 'ନୂତନତମ ପାର୍ଟନରଶିପ୍ ସମ୍ପାଦନା',
  'agent.adminHome.ops.rateChanges.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ରେଟ୍-ପରିବର୍ତ୍ତନ ଇଭେଣ୍ଟ ନାହିଁ।',
  'agent.adminHome.ops.opsHealth.title': 'ଅପରେସନ୍ସ ସ୍ୱାସ୍ଥ୍ୟ',
  'agent.adminHome.ops.opsHealth.subtitle': 'ଡେଡ୍-ଲେଟର୍ କ୍ୟୁ · ଅପେକ୍ଷାରତ ସେଟଲମେଣ୍ଟ',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'ଅର୍ଜନ କଲେ',
  'agent.adminHome.feed.match.onGameSuffix': '{game} ରେ',
  'agent.adminHome.feed.recharge.label': 'ରିଚାର୍ଜ୍',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'ମାଧ୍ୟମରେ',
  'agent.adminHome.feed.rateChange.label': 'ରେଟ୍ ପରିବର୍ତ୍ତନ',
  'agent.adminHome.feed.cascadeBadge': 'କାସ୍କେଡ୍',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'ଅନୁମୋଦିତ',
  'agent.adminHome.feed.recharge.state.rejected': 'ପ୍ରତ୍ୟାଖ୍ୟାନ',
  'agent.adminHome.feed.recharge.state.pending': 'ଅପେକ୍ଷାରତ',
  'agent.adminHome.feed.recharge.state.paid': 'ପେମେଣ୍ଟ ହୋଇଛି',
  'agent.adminHome.feed.recharge.state.expired': 'ସମାପ୍ତ',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} ମ୍ୟାଚ୍',
  'agent.adminHome.leaderboard.matches.other': '{count} ମ୍ୟାଚ୍',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'ପ୍ଲାଟଫର୍ମ ଲାଭ (7d)',
  'agent.adminHome.botMatrix.cat.easy': 'ସହଜ',
  'agent.adminHome.botMatrix.cat.medium': 'ମଧ୍ୟମ',
  'agent.adminHome.botMatrix.cat.hard': 'କଠିନ',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}m',
  'agent.adminHome.botMatrix.activeSummary': '{total} ମଧ୍ୟରୁ {active} ବଟ୍ ସକ୍ରିୟ',
  'agent.adminHome.botMatrix.loading': 'ଲୋଡ୍ ହେଉଛି…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ ସଫା',
  'agent.adminHome.opsHealth.dlqClean.body': 'କୌଣସି ବିଫଳ ସେଟଲମେଣ୍ଟ ଜବ୍ ନାହିଁ।',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} ଅସମାଧାନ DLQ ଜବ୍',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} ଅସମାଧାନ DLQ ଜବ୍',
  'agent.adminHome.opsHealth.dlqDirty.body': 'ପୁଣି ଚେଷ୍ଟା / ସମାଧାନ ପାଇଁ କ୍ଲିକ୍ କରନ୍ତୁ।',
  'agent.adminHome.opsHealth.replay.eyebrow': 'ପୁଣି ଚଲାନ୍ତୁ',
  'agent.adminHome.opsHealth.replay.body': 'ସେଟଲମେଣ୍ଟ ପୁଣି ଚଲାନ୍ତୁ',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'ପୁନଃ ଆସାଇନ୍',
  'agent.adminHome.opsHealth.reassign.body': 'ବଲ୍କ ମୁଭ୍',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'ମୋ ପୁଲ୍ ବାଲାନ୍ସ',
  'agent.adminHome.agentDash.hero.netPrefix': 'ନେଟ୍',
  'agent.adminHome.agentDash.action.myWallet': 'ମୋ ୱାଲେଟ୍',
  'agent.adminHome.agentDash.action.myPlayers': 'ମୋ ଖେଳାଳି',
  'agent.adminHome.agentDash.snapshot.myMatches': 'ମୋ ମ୍ୟାଚ୍',
  'agent.adminHome.agentDash.snapshot.active': 'ସକ୍ରିୟ',
  'agent.adminHome.agentDash.snapshot.netRange': 'ନେଟ୍ (ଅବଧି)',
  'agent.adminHome.agentDash.snapshot.pending': 'ଅପେକ୍ଷାରତ',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'ମୋ ଖେଳାଳି ୱାଲେଟ୍',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'ଅବଧିରେ ରିଚାର୍ଜ୍',
  'agent.adminHome.agentDash.card.players.stat.total': 'ମୋ ଖେଳାଳି',
  'agent.adminHome.agentDash.card.wallet.title': 'ମୋ ୱାଲେଟ୍',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'ପୁଲ୍ ବାଲାନ୍ସ',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'ଅର୍ଜିତ (କମିଶନ୍)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'ପେମେଣ୍ଟ ହୋଇଛି',
  'agent.adminHome.agentDash.card.wallet.pending': 'ମୋର ଅପେକ୍ଷାରତ ଉଠାଣ',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'ପ୍ରାପ୍ତ ଟପ୍-ଅପ୍',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'ନେଟ୍ (ଅବଧି)',
  'agent.adminHome.agentDash.card.activity.title': 'କାର୍ଯ୍ୟକଳାପ',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'ଅବଧିରେ ମୋ ମ୍ୟାଚ୍',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'ଟପ୍ ଖର୍ଚ୍ଚକାରୀ',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'ସେମାନଙ୍କ ଖର୍ଚ୍ଚ',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'ଅପେକ୍ଷାରତ ଆଇଟମ୍',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'ଶୀର୍ଷ ଖର୍ଚ୍ଚକାରୀ',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'ଏହି ସମୟସୀମାରେ ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଖର୍ଚ୍ଚ ନାହିଁ।',
  'agent.adminHome.agentDash.activity.recent.title': 'ସାମ୍ପ୍ରତିକ କାର୍ଯ୍ୟକଳାପ',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'ଆପଣଙ୍କ ଖେଳାଳିଙ୍କଠାରୁ ଶେଷ 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଲେଣଦେଣ ନାହିଁ।',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'ଶୀର୍ଷ ଆୟକାରୀ (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'ଆପଣଙ୍କ ଉପ-ଏଜେଣ୍ଟ କ୍ରମରେ',
  'agent.adminHome.agentDash.insights.downline.title': 'ଡାଉନ୍-ଲାଇନ୍ କାର୍ଯ୍ୟକଳାପ',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍‌ରୁ ଶେଷ 5 ଇଭେଣ୍ଟ',
  'agent.adminHome.agentDash.insights.deposit.title': 'ଡିପୋଜିଟ୍ ଲେଜର୍',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'ସାମ୍ପ୍ରତିକ ଡିପୋଜିଟ୍ କ୍ରେଡିଟ୍ / ରିଫଣ୍ଡ',
  'agent.adminHome.agentDash.insights.deposit.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଡିପୋଜିଟ୍ କାର୍ଯ୍ୟକଳାପ ନାହିଁ।',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'ଉପ-ଏଜେଣ୍ଟ',
  'agent.subAgentDetail.subtitle': 'ସିଧାସଳଖ ଉପ-ଏଜେଣ୍ଟ',
  'agent.subAgentDetail.error.loadFailed': 'ଉପ-ଏଜେଣ୍ଟ ଲୋଡ୍ କରିବାରେ ବିଫଳ',
  'agent.subAgentDetail.backToList': 'ଉପ-ଏଜେଣ୍ଟ ତାଲିକାକୁ ଫେରନ୍ତୁ',
  'agent.subAgentDetail.loading': 'ଲୋଡ୍ ହେଉଛି…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'ପରିଚୟ',
  'agent.subAgentDetail.tab.players': 'ଖେଳାଳି',
  'agent.subAgentDetail.tab.wallet': 'ୱାଲେଟ୍',
  'agent.subAgentDetail.tab.rates': 'ଦର',
  'agent.subAgentDetail.tab.ops': 'ଅପରେସନ୍',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} ଖେଳାଳି',
  'agent.subAgentDetail.header.players.other': '{count} ଖେଳାଳି',
  'agent.subAgentDetail.header.pool': 'ପୁଲ୍: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'ଶେଷ ଲଗଇନ୍ {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'ସକ୍ରିୟ',
  'agent.subAgentDetail.status.suspended': 'ସ୍ଥଗିତ',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'ୟୁଜରନେମ୍',
  'agent.subAgentDetail.identity.row.email': 'ଇମେଲ୍',
  'agent.subAgentDetail.identity.row.status': 'ସ୍ଥିତି',
  'agent.subAgentDetail.identity.row.parent': 'ପ୍ୟାରେଣ୍ଟ ଏଜେଣ୍ଟ',
  'agent.subAgentDetail.identity.parentValue': '@{username} (ଆପଣ)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'ମାଲିକାନା ଖେଳାଳି',
  'agent.subAgentDetail.identity.row.wallet': 'ୱାଲେଟ୍ ବାଲାନ୍ସ',
  'agent.subAgentDetail.identity.row.created': 'ସୃଷ୍ଟି ହୋଇଛି',
  'agent.subAgentDetail.identity.row.lastLogin': 'ଶେଷ ଲଗଇନ୍',
  'agent.subAgentDetail.identity.row.identityLocked': 'ପରିଚୟ ଲକ୍',
  'agent.subAgentDetail.identity.identityLocked.yes': 'ହଁ (କେବଳ ଆଡମିନ୍ ସମ୍ପାଦନା)',
  'agent.subAgentDetail.identity.identityLocked.no': 'ନା (ଉପ-ଏଜେଣ୍ଟ ନିଜେ ସମ୍ପାଦନା କରିପାରିବେ)',
  'agent.subAgentDetail.identity.row.receiving': 'ଖେଳାଳି ଗ୍ରହଣ କରୁଛନ୍ତି',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'ଗ୍ରହଣ କରୁଛନ୍ତି',
  'agent.subAgentDetail.optOut.optedOut': 'ଅପ୍ଟ୍ ଆଉଟ୍',
  'agent.subAgentDetail.optOut.toast.accepting': 'ଉପ-ଏଜେଣ୍ଟ ବର୍ତ୍ତମାନ ଖେଳାଳି ଗ୍ରହଣ କରୁଛନ୍ତି',
  'agent.subAgentDetail.optOut.toast.optedOut': 'ଉପ-ଏଜେଣ୍ଟ ଲୋକେଟର୍‌ରୁ ଅପ୍ଟ୍ ଆଉଟ୍ କଲେ',
  'agent.subAgentDetail.optOut.error.updateFailed': 'ଅପଡେଟ୍ କରିପାରିଲେ ନାହିଁ',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'ଲୋକେସନ୍',
  'agent.subAgentDetail.location.lastUpdated': '· ଶେଷ ଅପଡେଟ୍ {date}',
  'agent.subAgentDetail.location.cta.save': 'ଲୋକେସନ୍ ସେଭ୍ କରନ୍ତୁ',
  'agent.subAgentDetail.location.nothingToSave': 'ସେଭ୍ କରିବାକୁ କିଛି ନାହିଁ',
  'agent.subAgentDetail.location.toast.saved.one': 'ଲୋକେସନ୍ ସେଭ୍ ହେଲା ({count} ଫିଲ୍ଡ)',
  'agent.subAgentDetail.location.toast.saved.other': 'ଲୋକେସନ୍ ସେଭ୍ ହେଲା ({count} ଫିଲ୍ଡ)',
  'agent.subAgentDetail.location.hint': 'ସେଭ୍ ସକ୍ଷମ କରିବାକୁ ଭିନ୍ନ ରାଜ୍ୟ / ଜିଲ୍ଲା / କ୍ଷେତ୍ର ବାଛନ୍ତୁ। (ଉପ-ଏଜେଣ୍ଟ ନିଜେ ସେମାନଙ୍କ ପ୍ରଥମ ଲଗଇନ୍‌ରେ ବାଧ୍ୟତାମୂଳକ ପପ୍-ଅପ୍ ମାଧ୍ୟମରେ ସଠିକ୍ GPS ଓଭର୍‌ଲେ କରନ୍ତି।)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'ଏହି ଉପ-ଏଜେଣ୍ଟଙ୍କ ପାଖରେ ଏବେ କୌଣସି ଖେଳାଳି ନାହାନ୍ତି।',
  'agent.subAgentDetail.players.count.one': '{count} ଖେଳାଳି',
  'agent.subAgentDetail.players.count.other': '{count} ଖେଳାଳି',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'ବର୍ତ୍ତମାନର ଉପ-ଏଜେଣ୍ଟ ବାଲାନ୍ସ',
  'agent.subAgentDetail.wallet.ledger.title': '@{username}ଙ୍କର ସମ୍ପୂର୍ଣ୍ଣ ୱାଲେଟ୍ ଲେଜର୍',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'ପ୍ରତ୍ୟେକ ଗତିବିଧି · ଉପ-ଏଜେଣ୍ଟ ସେମାନଙ୍କ ୱାଲେଟ୍‌ରେ ସେହି ସମାନ ଦେଖନ୍ତି',
  'agent.subAgentDetail.wallet.ledger.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ୱାଲେଟ୍ କାର୍ଯ୍ୟକଳାପ ନାହିଁ।',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'ଆପଣ ↔ ସେମାନେ',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'ବାଲାନ୍ସ ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'ଆଡମିନ୍‌ଠାରୁ ଟପ୍-ଅପ୍',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'ଖେଳାଳିଙ୍କୁ ପେଆଉଟ୍',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'ଖେଳାଳି ରିଚାର୍ଜ୍',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'ରାଜସ୍ୱ ହିସ୍ସା',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'ରିଚାର୍ଜ୍ କମିଶନ୍',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'ସହ-ପୁଞ୍ଜିଗତ ଅଫର୍',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'ଆଡମିନ୍ ସମନ୍ୱୟ',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'ଉଠାଣ ହୋଲ୍ଡ',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'ଉଠାଣ ରିଫଣ୍ଡ',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'ପ୍ଲାଟଫର୍ମ ଫିସ୍ ହିସ୍ସା',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'ୱିନ୍/ଲସ୍ ହିସ୍ସା',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'କଏନ୍ ଫେରାଇ ଆଣୁଛନ୍ତି?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'କୁହନ୍ତୁ ',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' କୁ ସେମାନଙ୍କ ନିଜ ପୁଲ୍‌ରୁ ଉଠାଣ ଅନୁରୋଧ ଦାଖଲ କରିବାକୁ। ଆପଣ ଏହାକୁ (ସମ୍ପୂର୍ଣ୍ଣ କିମ୍ବା ଆଂଶିକ) ଦେୟ ଭାବେ ଚିହ୍ନଟ କରିପାରିବେ ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'ଫାଇନାନ୍ସ › ଉପ-ଏଜେଣ୍ଟ',
  'agent.subAgentDetail.wallet.pullback.body.after': 'ରୁ।',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username}କୁ ଟ୍ରାନ୍ସଫର୍',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username}ଠାରୁ ଉଠାଣ',
  'agent.subAgentDetail.transfer.topup.body': 'ପରମାଣୁ ଟ୍ରାନ୍ସଫର୍: ଆପଣଙ୍କ ୱାଲେଟ୍ କମ୍, ସେମାନଙ୍କର ୱାଲେଟ୍ ଅଧିକ। ଉଭୟ ପୁଲ୍ ଋଣାତ୍ମକ ହୋଇପାରନ୍ତି (ସ୍ୱାକ୍ଷରିତ-ପୁଲ୍ ଡିଜାଇନ୍)।',
  'agent.subAgentDetail.transfer.withdraw.body': 'ପରମାଣୁ ଓଲଟା ଟ୍ରାନ୍ସଫର୍: ସେମାନଙ୍କର ୱାଲେଟ୍ କମ୍, ଆପଣଙ୍କ ୱାଲେଟ୍ ଅଧିକ। ଉପ-ଏଜେଣ୍ଟଙ୍କଠାରୁ ପେଆଉଟ୍ ଅନୁରୋଧ ସମାଧାନ କଲାବେଳେ ଏହାକୁ ବ୍ୟବହାର କରନ୍ତୁ।',
  'agent.subAgentDetail.transfer.amountLabel': 'ରାଶି (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'ନୋଟ୍ (ବୈକଳ୍ପିକ)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'ଯଥା ସାପ୍ତାହିକ ଟପ୍-ଅପ୍',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'ଯଥା ପେଆଉଟ୍ ସମାଧାନ',
  'agent.subAgentDetail.transfer.error.amountPositive': 'ରାଶି ଏକ ଧନାତ୍ମକ ପୂର୍ଣ୍ଣ ସଂଖ୍ୟା ହେବା ଆବଶ୍ୟକ।',
  'agent.subAgentDetail.transfer.confirm.topup': 'ଆପଣଙ୍କ ପୁଲ୍‌ରୁ @{username}କୁ {amount} ଟ୍ରାନ୍ସଫର୍? ଆପଣଙ୍କ ବାଲାନ୍ସ ଠିକ୍ ସେତିକି କମିଯିବ।',
  'agent.subAgentDetail.transfer.confirm.withdraw': '@{username}ଙ୍କ ପୁଲ୍‌ରୁ {amount} ଆପଣଙ୍କ ପାଖକୁ ଫେରାଇ ଆଣିବେ? ସେମାନଙ୍କ ବାଲାନ୍ସ ଠିକ୍ ସେତିକି କମିଯିବ।',
  'agent.subAgentDetail.transfer.toast.topup': '@{username}କୁ {amount} ପଠାଗଲା। ସେମାନଙ୍କ ବାଲାନ୍ସ: {childBalance}।',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username}ଠାରୁ {amount} ଫେରାଇ ଅଣାଗଲା। ସେମାନଙ୍କ ବାଲାନ୍ସ: {childBalance}।',
  'agent.subAgentDetail.transfer.cta.working': 'କାର୍ଯ୍ୟ ଚାଲିଛି…',
  'agent.subAgentDetail.transfer.cta.suspended': 'ଉପ-ଏଜେଣ୍ଟ ସ୍ଥଗିତ',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} ଟ୍ରାନ୍ସଫର୍',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} ଉଠାନ୍ତୁ',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'ଦର ଲୋଡ୍ ହେଉଛି…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'ପ୍ରତ୍ୟେକ କାର୍ଡ ଗୋଟିଏ କମିଶନ୍ ପିଲାର୍; ଭିତରର ପ୍ରତ୍ୟେକ ପଂକ୍ତି ଗୋଟିଏ (ପିଲାର୍ × ଗେମ୍) ହିସ୍ସା। ',
  'agent.subAgentDetail.rates.intro.ceiling': 'ସୀମା',
  'agent.subAgentDetail.rates.intro.part2': ' ଯାହା ପ୍ରତ୍ୟେକ ଇନପୁଟ୍ ପାଖରେ ଦେଖାଯାଉଛି ତାହା ଆପଣଙ୍କ ନିଜର ରେଟ୍ — ଗୋଲ୍ଡେନ୍ ଲିମିଟ୍ ଅନୁସାରେ ଆପଣ ତାହାଠାରୁ ଅଧିକ ଶିଶୁକୁ ଦେଇ ପାରିବେ ନାହିଁ। ଆପଣ ଯାହା ବଦଳାଉଛନ୍ତି ତାହା ସମ୍ପାଦନଯୋଗ୍ୟ ହୋଇଯାଏ; କ୍ଲିକ୍ କରନ୍ତୁ ',
  'agent.subAgentDetail.rates.intro.applyWord': 'ପ୍ରୟୋଗ କରନ୍ତୁ',
  'agent.subAgentDetail.rates.intro.part3': ' କମିଟ୍ କରିବା ପାଇଁ। କ୍ୟାସ୍କେଡ୍ କ୍ଲାମ୍ପ୍ ସର୍ଭର-ସାଇଡ୍ ରେ ଲାଗୁ ହୁଏ।',
  'agent.subAgentDetail.rates.toast.applied': 'ରେଟ୍ ପ୍ରୟୋଗ କରାଗଲା।',
  'agent.subAgentDetail.rates.confirm.one': '{count} ରେଟ୍ ପରିବର୍ତ୍ତନ ପ୍ରୟୋଗ କରନ୍ତୁ? ଗୋଲ୍ଡେନ୍-ଲିମିଟ୍ + କ୍ୟାସ୍କେଡ୍ କ୍ଲାମ୍ପ୍ ସର୍ଭର-ସାଇଡ୍ ରେ ଲାଗୁ ହେବ।',
  'agent.subAgentDetail.rates.confirm.other': '{count} ରେଟ୍ ପରିବର୍ତ୍ତନ ପ୍ରୟୋଗ କରନ୍ତୁ? ଗୋଲ୍ଡେନ୍-ଲିମିଟ୍ + କ୍ୟାସ୍କେଡ୍ କ୍ଲାମ୍ପ୍ ସର୍ଭର-ସାଇଡ୍ ରେ ଲାଗୁ ହେବ।',
  'agent.subAgentDetail.rates.error.blocked': 'ବନ୍ଦ: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'ପ୍ଲାଟଫର୍ମ ଫି %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'ୱିନ୍/ଲସ୍ ଲାଭ %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ଅଫର୍ ପୁରସ୍କାର %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— ଗ୍ଲୋବାଲ୍ —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ ସୀମା {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'ପ୍ୟାରେଣ୍ଟଠାରୁ ଅଧିକ (ଗୋଲ୍ଡେନ୍ ଲିମିଟ୍)',
  'agent.subAgentDetail.rates.row.editedBadge': 'ସମ୍ପାଦିତ',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} ପରିବର୍ତ୍ତନ ବାତିଲ୍ କରନ୍ତୁ',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} ପରିବର୍ତ୍ତନ ବାତିଲ୍ କରନ୍ତୁ',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} ପରିବର୍ତ୍ତନ ପ୍ରୟୋଗ କରନ୍ତୁ',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} ପରିବର୍ତ୍ତନ ପ୍ରୟୋଗ କରନ୍ତୁ',
  'agent.subAgentDetail.rates.cta.applying': 'ପ୍ରୟୋଗ କରୁଛି…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'ସ୍ଥିତି',
  'agent.subAgentDetail.ops.status.body': 'ସ୍ଥଗିତ କରିବା ଉପ-ଏଜେଣ୍ଟଙ୍କ ଲଗଇନ୍ ଏବଂ ଡାଉନ୍-ଲାଇନ୍ ସଞ୍ଚାଳନକୁ ଆପଣ ପୁନଃ ସକ୍ରିୟ ନକରିବା ପର୍ଯ୍ୟନ୍ତ ବନ୍ଦ କରେ। କାରଣ ଇଚ୍ଛାଧୀନ କିନ୍ତୁ ଅଡିଟ୍ ଟ୍ରେଲରେ ଆସେ।',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'କାରଣ (ଇଚ୍ଛାଧୀନ)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'ଉପ-ଏଜେଣ୍ଟ ସ୍ଥଗିତ କରନ୍ତୁ',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'ଉପ-ଏଜେଣ୍ଟ ସ୍ଥଗିତ ହଟାନ୍ତୁ',
  'agent.subAgentDetail.ops.status.cta.working': 'କାମ ଚାଲିଛି…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username} ଙ୍କୁ ସ୍ଥଗିତ କରନ୍ତୁ? ସ୍ଥଗିତ ଥିବାବେଳେ ସେମାନେ ଲଗଇନ୍ କିମ୍ବା କାମ କରିପାରିବେ ନାହିଁ।',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username} ଙ୍କ ସ୍ଥଗିତ ହଟାନ୍ତୁ? ସେମାନେ ପୁରା ଆକ୍ସେସ୍ ପୁନଃ ପାଇବେ।',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} ବର୍ତ୍ତମାନ ସ୍ଥଗିତ ଅଛନ୍ତି।',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} ବର୍ତ୍ତମାନ ସକ୍ରିୟ ଅଛନ୍ତି।',

  'agent.subAgentDetail.ops.password.title': 'ପାସୱାର୍ଡ ରିସେଟ୍ କରନ୍ତୁ',
  'agent.subAgentDetail.ops.password.body': 'ଉପ-ଏଜେଣ୍ଟଙ୍କ ପାଇଁ ନୂଆ ପାସୱାର୍ଡ ସେଟ୍ କରେ ଏବଂ ସେମାନଙ୍କ ବର୍ତ୍ତମାନର ରିଫ୍ରେସ୍ ଟୋକନ୍ ରଦ୍ଦ କରେ। ସେମାନଙ୍କୁ ନୂଆ ପାସୱାର୍ଡ ସହ ପୁନଃ ଲଗଇନ୍ କରିବାକୁ ପଡ଼ିବ। ଏହାକୁ ସୁରକ୍ଷିତ ଚ୍ୟାନେଲ୍ ମାଧ୍ୟମରେ ସେୟାର୍ କରନ୍ତୁ।',
  'agent.subAgentDetail.ops.password.placeholder': 'ନୂଆ ପାସୱାର୍ଡ (ସର୍ବନିମ୍ନ 8 ଅକ୍ଷର)',
  'agent.subAgentDetail.ops.password.confirm': '@{username} ଙ୍କ ପାସୱାର୍ଡ ରିସେଟ୍ କରନ୍ତୁ? ସେମାନଙ୍କ ବର୍ତ୍ତମାନର ସେସନ୍ ଅବୈଧ ହୋଇଯିବ ଏବଂ ସେମାନଙ୍କୁ ନୂଆ ପାସୱାର୍ଡ ସହ ଲଗଇନ୍ କରିବାକୁ ପଡ଼ିବ।',
  'agent.subAgentDetail.ops.password.toast.reset': 'ପାସୱାର୍ଡ ରିସେଟ୍ ହେଲା। ନୂଆ ପାସୱାର୍ଡ @{username} ସହ ସେୟାର୍ କରନ୍ତୁ।',
  'agent.subAgentDetail.ops.password.error.tooShort': 'ପାସୱାର୍ଡ ସର୍ବନିମ୍ନ 8 ଅକ୍ଷରର ହେବା ଆବଶ୍ୟକ।',
  'agent.subAgentDetail.ops.password.cta.reset': 'ପାସୱାର୍ଡ ରିସେଟ୍ କରନ୍ତୁ',
  'agent.subAgentDetail.ops.password.cta.resetting': 'ରିସେଟ୍ ହେଉଛି…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'ଖେଳାଳିମାନଙ୍କ ପାଖକୁ ଫେରନ୍ତୁ',
  'agent.playerDetail.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.playerDetail.error.loadFailed': 'ୟୁଜର୍ ଲୋଡ୍ କରିବାରେ ବିଫଳ',

  // Header card.
  'agent.playerDetail.header.noName': '(କୌଣସି ନାମ ସେଟ୍ ନୁହେଁ)',
  'agent.playerDetail.header.joined': '{date} ରେ ଯୋଗ ଦେଲେ',
  'agent.playerDetail.header.ownedBy.prefix': ' · ମାଲିକ ',
  'agent.playerDetail.header.walletLabel': 'ୱାଲେଟ୍',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'ଅପେକ୍ଷାରତ',
  'agent.playerDetail.status.active': 'ସକ୍ରିୟ',
  'agent.playerDetail.status.suspended': 'ସ୍ଥଗିତ',
  'agent.playerDetail.status.banned': 'ନିଷିଦ୍ଧ',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'ପରିଚୟ',
  'agent.playerDetail.tab.profile': 'ପ୍ରୋଫାଇଲ୍',
  'agent.playerDetail.tab.wallet': 'ୱାଲେଟ୍',
  'agent.playerDetail.tab.security': 'ସୁରକ୍ଷା',
  'agent.playerDetail.tab.ownership': 'ମାଲିକାନା',
  'agent.playerDetail.tab.operations': 'ସଞ୍ଚାଳନ',

  // Identity tab.
  'agent.playerDetail.identity.title': 'ପରିଚୟ',
  'agent.playerDetail.identity.row.username': 'ୟୁଜର୍ ନେମ୍',
  'agent.playerDetail.identity.row.email': 'ଇମେଲ୍',
  'agent.playerDetail.identity.row.phone': 'ଫୋନ୍',
  'agent.playerDetail.identity.row.country': 'ଦେଶ',
  'agent.playerDetail.identity.row.joined': 'ଯୋଗ ଦେଲେ',
  'agent.playerDetail.identity.row.emailVerified': 'ଇମେଲ୍ ସତ୍ୟାପିତ',
  'agent.playerDetail.identity.row.telegram': 'ଟେଲିଗ୍ରାମ୍',
  'agent.playerDetail.identity.row.failedLogins': 'ବିଫଳ ଲଗଇନ୍',
  'agent.playerDetail.identity.row.lockedUntil': 'ଏହି ସମୟ ପର୍ଯ୍ୟନ୍ତ ଲକ୍',
  'agent.playerDetail.identity.value.yes': 'ହଁ',
  'agent.playerDetail.identity.value.no': 'ନା',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'ଏହି ଖେଳାଳିଙ୍କୁ ଟ୍ରାନ୍ସଫର୍ କରନ୍ତୁ',
  'agent.playerDetail.identity.transfer.body': 'ସେମାନଙ୍କ Unique Agent ID ଦ୍ୱାରା ଅନ୍ୟ ଏଜେଣ୍ଟଙ୍କୁ ହସ୍ତାନ୍ତର କରନ୍ତୁ — ସମଗ୍ର ନେଟୱାର୍କରେ କାମ କରେ, କେବଳ ଆପଣଙ୍କ ଡାଉନ୍-ଲାଇନ୍ ରେ ନୁହେଁ।',
  'agent.playerDetail.identity.transfer.cta': 'UAID ଦ୍ୱାରା ଟ୍ରାନ୍ସଫର୍',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'ଫଟୋ ବଦଳାନ୍ତୁ',
  'agent.playerDetail.avatar.cropTitle': '@{username} ଙ୍କ ଫଟୋ ସଜାଡ଼ନ୍ତୁ',
  'agent.playerDetail.avatar.toast.updated': 'ଫଟୋ ଅପଡେଟ୍ ହେଲା',
  'agent.playerDetail.avatar.error.uploadFailed': 'ଅପଲୋଡ୍ ବିଫଳ',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'ଖେଳାଳି UAID ଦ୍ୱାରା ଟ୍ରାନ୍ସଫର୍ ହେଲେ',
  'agent.playerDetail.transfer.toast.agent': '@{username} ସଫଳତାର ସହ ଟ୍ରାନ୍ସଫର୍ ହେଲେ',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'ଆକାଉଣ୍ଟ ସ୍ଥିତି',
  'agent.playerDetail.ops.accountStatus.body': 'ସକ୍ରିୟର ଅର୍ଥ ଖେଳାଳି ଲଗଇନ୍ କରି ଖେଳି ପାରିବେ। ସ୍ଥଗିତ ଆପଣ ପୁନଃ ଫେରାଇବା ପର୍ଯ୍ୟନ୍ତ ଲଗଇନ୍ ବନ୍ଦ କରେ। ନିଷେଧ ଏକ ଅଧିକ ଶକ୍ତ ଲକ୍ — ନୀତି ଲଙ୍ଘନ ପାଇଁ ଏହାକୁ ରଖନ୍ତୁ।',
  'agent.playerDetail.ops.statusBtn.activate': 'ସକ୍ରିୟ କରନ୍ତୁ',
  'agent.playerDetail.ops.statusBtn.suspend': 'ସ୍ଥଗିତ କରନ୍ତୁ',
  'agent.playerDetail.ops.statusBtn.ban': 'ନିଷେଧ କରନ୍ତୁ',
  'agent.playerDetail.ops.statusBtn.current': 'ବର୍ତ୍ତମାନ',
  'agent.playerDetail.ops.statusBtn.toast': 'ୟୁଜର୍ ବର୍ତ୍ତମାନ {status} ଅଛନ୍ତି',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'ବିପଦ ଅଞ୍ଚଳ',
  'agent.playerDetail.ops.danger.body': 'ହାର୍ଡ-ଡିଲିଟ୍ ୟୁଜର୍ ରେକର୍ଡ ହଟାଏ। ୱାଲେଟ୍ ଲେଜର୍ ଅଡିଟ୍ ପାଇଁ ସୁରକ୍ଷିତ ରହେ, କିନ୍ତୁ ଖେଳାଳି ସମସ୍ତ ଆକ୍ସେସ୍ ହରାଇବେ। ଏହି କାର୍ଯ୍ୟ ଫେରାଇ ହେବ ନାହିଁ।',
  'agent.playerDetail.ops.delete.eyebrow': 'ବିପଦ ଅଞ୍ଚଳ',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'ଏହି ଆକାଉଣ୍ଟକୁ ସ୍ଥାୟୀ ଭାବେ ଡିଲିଟ୍ କରନ୍ତୁ। ଏହା ଫେରାଇ ହେବ ନାହିଁ। ନିଶ୍ଚିତ କରିବାକୁ ଟାଇପ୍ କରନ୍ତୁ ',
  'agent.playerDetail.ops.delete.confirm.after': ' ନିଶ୍ଚିତ କରିବାକୁ।',
  'agent.playerDetail.ops.delete.cta': 'ଡିଲିଟ୍ କରନ୍ତୁ',
  'agent.playerDetail.ops.delete.toast': 'ୟୁଜର୍ ଡିଲିଟ୍ ହେଲା',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'ୱାଲେଟ୍ ସଜାଡ଼ନ୍ତୁ',
  'agent.playerDetail.walletAdjust.mode.credit': '+ କ୍ରେଡିଟ୍',
  'agent.playerDetail.walletAdjust.mode.debit': '− ଡେବିଟ୍',
  'agent.playerDetail.walletAdjust.field.amount': 'ରାଶି (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'କାରଣ · ଲେଜର୍ ରେ ଦର୍ଜ',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'ଯଥା ଡିପୋଜିଟ୍ ରିଭର୍ସାଲ୍, ବୋନସ୍, ରିଫଣ୍ଡ୍',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'ରିଚାର୍ଜ୍ ଭାବେ ଚିହ୍ନଟ କରନ୍ତୁ',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'ଏହି କ୍ରେଡିଟ୍ କୁ ଲେଜର୍ ରେ ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'ରିଚାର୍ଜ୍',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' ଭାବେ ଦର୍ଜ କରନ୍ତୁ। ଯଦି ଏହି ୟୁଜର୍ କୁ କେହି ରେଫର୍ କରିଥିଲେ, ତେବେ ରେଫର୍ କରୁଥିବା ବ୍ୟକ୍ତିଙ୍କୁ ସ୍ୱତଃ ବୋନସ୍ ମିଳେ।',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'ଏକ ଧନାତ୍ମକ ରାଶି ପ୍ରବେଶ କରନ୍ତୁ',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'କାରଣ ଆବଶ୍ୟକ',
  'agent.playerDetail.walletAdjust.cta.applying': 'ଲାଗୁ କରୁଛି…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'କ୍ରେଡିଟ୍ ଲାଗୁ କରନ୍ତୁ',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'ଡେବିଟ୍ ଲାଗୁ କରନ୍ତୁ',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} କ୍ରେଡିଟ୍ କରାଗଲା',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} ଡେବିଟ୍ କରାଗଲା',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · ରେଫର୍ କରିଥିବା ବ୍ୟକ୍ତି {bonus} ବୋନସ୍ ଅର୍ଜନ କଲେ',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'ଏହି ଖେଳାଳିଙ୍କୁ ରିଚାର୍ଜ୍ କରନ୍ତୁ',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': ' କୁ ଟପ୍-ଅପ୍ କଲେ କଏନ୍ ସ୍ଥାନାନ୍ତରିତ ହୁଏ ',
  'agent.playerDetail.rechargeCta.body.bold': 'ଆପଣଙ୍କ ୱାଲେଟ୍',
  'agent.playerDetail.rechargeCta.body.after': ' ରୁ ସେମାନଙ୍କ ପାଖକୁ। ଆପଣଙ୍କ ପୁଲ୍ ପ୍ରଥମେ ଯାଞ୍ଚ କରାଯାଏ — ଏହି କାର୍ଯ୍ୟ ପରମାଣୁ ଏବଂ ଯଦି ବାଲାନ୍ସ ଅପର୍ଯ୍ୟାପ୍ତ ତେବେ ପ୍ରତ୍ୟାଖ୍ୟାନ ହୁଏ।',
  'agent.playerDetail.rechargeCta.thisPlayer': 'ଏହି ଖେଳାଳି',
  'agent.playerDetail.rechargeCta.cta': 'ରିଚାର୍ଜ୍ ଫର୍ମ ଖୋଲନ୍ତୁ',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'ପାସୱାର୍ଡ ରିସେଟ୍ କରନ୍ତୁ',
  'agent.playerDetail.security.subtitle': 'ଏକ ନୂତନ ପାସୱାର୍ଡ ସେଟ୍ କରେ ଏବଂ ୟୁଜରଙ୍କୁ ପ୍ରତ୍ୟେକ ଡିଭାଇସରୁ ସାଇନ୍ ଆଉଟ୍ କରେ।',
  'agent.playerDetail.security.field.new': 'ନୂତନ ପାସୱାର୍ଡ',
  'agent.playerDetail.security.field.confirm': 'ନିଶ୍ଚିତ କରନ୍ତୁ',
  'agent.playerDetail.security.error.mismatch': 'ପାସୱାର୍ଡ ମେଳ ଖାଉନାହିଁ',
  'agent.playerDetail.security.error.tooShort': 'ଅତି କମ୍ ୮ ଅକ୍ଷର, ଅକ୍ଷର ଏବଂ ଅଙ୍କ ସହିତ',
  'agent.playerDetail.security.toast.reset': 'ପାସୱାର୍ଡ ରିସେଟ୍ ଏବଂ ସମସ୍ତ ସେସନ୍ ବାତିଲ୍',
  'agent.playerDetail.security.cta.reset': 'ପାସୱାର୍ଡ ରିସେଟ୍ କରନ୍ତୁ',
  'agent.playerDetail.security.cta.resetting': 'ରିସେଟ୍ ହେଉଛି…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'ୱାଲେଟ୍ ଲେଜର',
  'agent.playerDetail.ledger.entries.one': '{count} ଏଣ୍ଟ୍ରି',
  'agent.playerDetail.ledger.entries.other': '{count} ଏଣ୍ଟ୍ରି',
  'agent.playerDetail.ledger.empty': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଲେଣଦେଣ ନାହିଁ।',
  'agent.playerDetail.ledger.loading': 'ଲୋଡ୍ ହେଉଛି…',
  'agent.playerDetail.ledger.actorSuffix': ' · ଆଡମିନ୍ #{id} ଦ୍ୱାରା',
  'agent.playerDetail.ledger.pagination.prev': 'ପୂର୍ବ',
  'agent.playerDetail.ledger.pagination.next': 'ପର',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'ମ୍ୟାଚ୍ ବେଟ୍',
  'agent.playerDetail.ledger.kind.win': 'ମ୍ୟାଚ୍ ପୁରସ୍କାର',
  'agent.playerDetail.ledger.kind.refund': 'ରିଫଣ୍ଡ',
  'agent.playerDetail.ledger.kind.fee': 'ପ୍ଲାଟଫର୍ମ ଫି',
  'agent.playerDetail.ledger.kind.adminCredit': 'ଆଡମିନ୍ କ୍ରେଡିଟ୍',
  'agent.playerDetail.ledger.kind.adminDebit': 'ଆଡମିନ୍ ଡେବିଟ୍',
  'agent.playerDetail.ledger.kind.recharge': 'ରିଚାର୍ଜ୍',
  'agent.playerDetail.ledger.kind.referralBonus': 'ରେଫରାଲ୍ ବୋନସ୍',
  'agent.playerDetail.ledger.kind.signupBonus': 'ସାଇନଅପ୍ ବୋନସ୍',
  'agent.playerDetail.ledger.kind.offerCredit': 'ଅଫର୍ କ୍ରେଡିଟ୍',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'ଉଠାଣ',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'ରିଫଣ୍ଡ',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'ପ୍ଲାଟଫର୍ମ',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'ପ୍ରୋଫାଇଲ୍ ସମ୍ପାଦନ କରନ୍ତୁ',
  'agent.playerDetail.profile.field.username': 'ୟୁଜରନେମ୍',
  'agent.playerDetail.profile.field.email': 'ଇମେଲ୍',
  'agent.playerDetail.profile.field.phone': 'ଫୋନ୍',
  'agent.playerDetail.profile.field.firstName': 'ପ୍ରଥମ ନାମ',
  'agent.playerDetail.profile.field.lastName': 'ଶେଷ ନାମ',
  'agent.playerDetail.profile.field.dob': 'ଜନ୍ମ ତାରିଖ',
  'agent.playerDetail.profile.field.gender': 'ଲିଙ୍ଗ',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'ପୁରୁଷ',
  'agent.playerDetail.profile.gender.female': 'ମହିଳା',
  'agent.playerDetail.profile.gender.other': 'ଅନ୍ୟ',
  'agent.playerDetail.profile.gender.preferNot': 'କହିବାକୁ ଚାହାଁନ୍ତି ନାହିଁ',
  // Location section.
  'agent.playerDetail.profile.location.title': 'ଅବସ୍ଥାନ',
  'agent.playerDetail.profile.location.lastUpdated': '· ଶେଷ ଅପଡେଟ୍ {date}',
  'agent.playerDetail.profile.location.legacy.label': 'ଫାଇଲରେ ପୁରୁଣା ଠିକଣା',
  'agent.playerDetail.profile.location.legacy.note': ' (କେବଳ-ପଢ଼ିବା — pre-R3, ଲୋକେଟର୍ ଦ୍ୱାରା ବ୍ୟବହୃତ ନୁହେଁ):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'ସେଭ୍ କରିବାକୁ କିଛି ନାହିଁ',
  'agent.playerDetail.profile.toast.saved.one': '{count} ଫିଲ୍ଡ ସେଭ୍ କରାଗଲା',
  'agent.playerDetail.profile.toast.saved.other': '{count} ଫିଲ୍ଡ ସେଭ୍ କରାଗଲା',
  'agent.playerDetail.profile.cta.save': 'ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',
  'agent.playerDetail.profile.cta.saving': 'ସେଭ୍ ହେଉଛି…',
  'agent.playerDetail.profile.cta.reset': 'ରିସେଟ୍ କରନ୍ତୁ',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'ମାଲିକ ଏଜେଣ୍ଟ',
  'agent.playerDetail.ownership.row.eyebrow': 'ମାଲିକାନା',
  'agent.playerDetail.ownership.platform.title': 'ପ୍ଲାଟଫର୍ମ-ଡାଇରେକ୍ଟ',
  'agent.playerDetail.ownership.platform.body': 'କୌଣସି ଏଜେଣ୍ଟ ଏହି ଖେଳାଳିଙ୍କ ମାଲିକ ନୁହଁନ୍ତି। ଆଡମିନ୍ ସିଧାସଳଖ ପରିଚାଳନା କରନ୍ତି।',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ଏଜେଣ୍ଟ #{id}',
  'agent.playerDetail.ownership.owned.body': 'ଏହି ଖେଳାଳିଙ୍କ ରିଚାର୍ଜ୍ + ମ୍ୟାଚ୍ ଉପରେ କମିଶନ୍ ଅର୍ଜନ କରେ।',
  'agent.playerDetail.ownership.cta.change': 'ବଦଳାନ୍ତୁ',
  'agent.playerDetail.ownership.toast.changedTo': 'ମାଲିକାନା → {label}',
  'agent.playerDetail.ownership.platformLabel': 'ପ୍ଲାଟଫର୍ମ-ଡାଇରେକ୍ଟ',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username} କୁ ପୁନଃ ନ୍ୟସ୍ତ କରନ୍ତୁ',
  'agent.playerDetail.ownership.picker.body': 'ନୂତନ ଏଜେଣ୍ଟ ବାଛନ୍ତୁ — କିମ୍ବା ପ୍ଲାଟଫର୍ମ-ଡାଇରେକ୍ଟରେ ଅଲଗା କରନ୍ତୁ (କୌଣସି ଏଜେଣ୍ଟ ମାଲିକ ନୁହଁନ୍ତି)। ଭବିଷ୍ୟତର କମିଶନ୍ ନୂତନ ମାଲିକଙ୍କ ପାଖକୁ ଯିବ; ପୂର୍ବରୁ କ୍ରେଡିଟ୍ ହୋଇଥିବା ଧାଡ଼ିଗୁଡ଼ିକୁ ସ୍ପର୍ଶ କରାଯିବ ନାହିଁ।',
  'agent.playerDetail.ownership.picker.detach.title': 'ପ୍ଲାଟଫର୍ମରେ ଅଲଗା କରନ୍ତୁ',
  'agent.playerDetail.ownership.picker.detach.body': 'ଏଜେଣ୍ଟ ମାଲିକାନା ହଟାନ୍ତୁ। ଆଡମିନ୍ ସିଧାସଳଖ ପରିଚାଳନା କରନ୍ତି।',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ଏଜେଣ୍ଟ ଖୋଜନ୍ତୁ…',
  'agent.playerDetail.ownership.picker.loading': 'ଏଜେଣ୍ଟ ଲୋଡ୍ ହେଉଛନ୍ତି…',
  'agent.playerDetail.ownership.picker.empty.search': 'ଆପଣଙ୍କ ସନ୍ଧାନ ସହିତ କୌଣସି ଏଜେଣ୍ଟ ମେଳ ଖାଉନାହାଁନ୍ତି।',
  'agent.playerDetail.ownership.picker.empty.none': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ସକ୍ରିୟ ଏଜେଣ୍ଟ ନାହାଁନ୍ତି।',
  'agent.playerDetail.ownership.picker.players.one': '{count} ଖେଳାଳି',
  'agent.playerDetail.ownership.picker.players.other': '{count} ଖେଳାଳି',
  'agent.playerDetail.ownership.picker.current': 'ବର୍ତ୍ତମାନ',
  'agent.playerDetail.ownership.picker.currentUpper': 'ବର୍ତ୍ତମାନ',
  'agent.playerDetail.ownership.picker.close': 'ବନ୍ଦ କରନ୍ତୁ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'ବାଲି ଏଜେଣ୍ଟ',
  'agent.login.subtitle': 'ଆପଣଙ୍କ ଖେଳାଳି ପରିଚାଳନା କରିବାକୁ ସାଇନ୍ ଇନ୍ କରନ୍ତୁ',
  'agent.login.field.identifier': 'ୟୁଜରନେମ୍ କିମ୍ବା ଇମେଲ୍',
  'agent.login.field.password': 'ପାସୱାର୍ଡ',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'କେବଳ ଏଜେଣ୍ଟଙ୍କ ପାଇଁ। ଯଦି ଆପଣ ପ୍ଲାଟଫର୍ମ ମାଲିକ, ତେବେ ବ୍ୟବହାର କରନ୍ତୁ ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '।',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'ଲଗଇନ୍ ବିଫଳ',
  'agent.login.error.invalidCredentials': 'ଅବୈଧ କ୍ରେଡେନଶିଆଲ୍',
  'agent.login.error.accountSuspended': 'ଆକାଉଣ୍ଟ ସ୍ଥଗିତ — ଆପଣଙ୍କ ଆଡମିନିଷ୍ଟ୍ରେଟରଙ୍କ ସହ ଯୋଗାଯୋଗ କରନ୍ତୁ।',
  'agent.login.error.networkError': 'ନେଟୱାର୍କ ତ୍ରୁଟି — ଆପଣଙ୍କ କନେକ୍ସନ ଯାଞ୍ଚ କରନ୍ତୁ ଏବଂ ପୁନଃ ଚେଷ୍ଟା କରନ୍ତୁ।',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'ଗେମ୍',
  'agent.misc.gameTile.actionSuffix': '· କନଫିଗର୍ କରନ୍ତୁ',
};

const pa: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "ਮੇਰੀ ਜਾਣਕਾਰੀ",
  'profile.group.security': "ਸਾਈਨ-ਇਨ ਤੇ ਸੁਰੱਖਿਆ",
  'profile.personal.title': "ਨਿੱਜੀ ਜਾਣਕਾਰੀ",
  'profile.personal.editTitle': "ਨਿੱਜੀ ਜਾਣਕਾਰੀ ਸੋਧੋ",
  'common.edit': "ਸੋਧੋ",
  'profile.field.name': "ਨਾਂ",
  'profile.field.phone': "ਫ਼ੋਨ ਨੰਬਰ",
  'profile.field.gender': "ਲਿੰਗ",
  'profile.field.location': "ਟਿਕਾਣਾ",
  'profile.gender.male': "ਮਰਦ",
  'profile.gender.female': "ਔਰਤ",
  'profile.gender.other': "ਹੋਰ",
  'profile.gender.preferNot': "ਦੱਸਣਾ ਨਹੀਂ ਚਾਹੁੰਦੇ",
  'profile.gender.select': "ਚੁਣੋ…",
  'profile.location.optionalHint': "ਚੋਣਵਾਂ — ਆਪਣਾ ਇਲਾਕਾ ਚੁਣੋ ਜਾਂ ਆਪਣਾ ਪਿਨਕੋਡ ਲਿਖੋ (GPS ਨਾਲ ਇਹ ਹੋਰ ਸਹੀ ਹੋ ਜਾਂਦਾ ਹੈ)।",
  'profile.lang.title': "ਭਾਸ਼ਾ",
  'profile.password.title': "ਪਾਸਵਰਡ",
  'profile.password.desc': "ਆਪਣੇ ਖਾਤੇ ਦਾ ਪਾਸਵਰਡ ਬਦਲੋ। ਬਾਕੀ ਡਿਵਾਈਸਾਂ ਸਾਈਨ-ਆਊਟ ਹੋ ਜਾਣਗੀਆਂ।",
  'profile.password.cta': "ਬਦਲੋ",
  'profile.password.editTitle': "ਪਾਸਵਰਡ ਬਦਲੋ",
  'profile.password.current': "ਮੌਜੂਦਾ ਪਾਸਵਰਡ",
  'profile.password.new': "ਨਵਾਂ ਪਾਸਵਰਡ",
  'profile.password.confirm': "ਨਵਾਂ ਪਾਸਵਰਡ ਪੱਕਾ ਕਰੋ",
  'profile.password.currentPh': "ਤੁਹਾਡਾ ਮੌਜੂਦਾ ਪਾਸਵਰਡ",
  'profile.password.newPh': "ਇੱਕ ਮਜ਼ਬੂਤ ਪਾਸਵਰਡ ਚੁਣੋ",
  'profile.password.confirmPh': "ਨਵਾਂ ਪਾਸਵਰਡ ਦੁਬਾਰਾ ਲਿਖੋ",
  'profile.password.rule': "ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ, ਜਿਨ੍ਹਾਂ ਵਿੱਚ ਇੱਕ ਅੱਖਰ ਤੇ ਇੱਕ ਨੰਬਰ ਹੋਵੇ।",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "ਇਸ ਵਜੋਂ ਲਿੰਕ ਹੈ",
  'profile.telegram.otpHere': "ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਦੇ OTP ਇੱਥੇ ਆਉਂਦੇ ਹਨ।",
  'profile.telegram.unlinkChange': "ਅਨਲਿੰਕ ਕਰੋ ਤੇ ਬਦਲੋ",
  'profile.telegram.unlinkTitle': "Telegram ਅਨਲਿੰਕ ਕਰੀਏ?",
  'profile.telegram.unlink': "ਅਨਲਿੰਕ ਕਰੋ",
  'profile.telegram.keepLinked': "ਲਿੰਕ ਰਹਿਣ ਦਿਓ",
  'profile.telegram.unlinkVerified': "ਬਾਅਦ ਵਿੱਚ ਤੁਸੀਂ ਕੋਈ ਹੋਰ Telegram ਖਾਤਾ ਦੁਬਾਰਾ ਲਿੰਕ ਕਰ ਸਕਦੇ ਹੋ। ਈਮੇਲ ਤਸਦੀਕ ਬਰਕਰਾਰ ਰਹੇਗੀ।",
  'profile.telegram.unlinkOnly': "Telegram ਤੁਹਾਡਾ ਇੱਕੋ-ਇੱਕ ਤਸਦੀਕਸ਼ੁਦਾ ਜ਼ਰੀਆ ਹੈ — ਖੇਡਣਾ ਜਾਰੀ ਰੱਖਣ ਤੋਂ ਪਹਿਲਾਂ ਤੁਹਾਨੂੰ ਆਪਣਾ ਖਾਤਾ ਦੁਬਾਰਾ ਤਸਦੀਕ ਕਰਨਾ ਪੈ ਸਕਦਾ ਹੈ।",
  'profile.telegram.notLinkedDesc': "ਬੈਕਅੱਪ ਵਜੋਂ ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਦੇ OTP ਪਾਉਣ ਲਈ Telegram ਲਿੰਕ ਕਰੋ।",
  'profile.telegram.openBot': "@BaaliGames_bot ਖੋਲ੍ਹੋ",
  'profile.telegram.generate': "ਲਿੰਕ ਬਣਾਓ",
  'profile.telegram.generating': "ਬਣ ਰਿਹਾ ਹੈ…",
  'profile.avatar.cropTitle': "ਆਪਣੀ ਫ਼ੋਟੋ ਠੀਕ ਕਰੋ",
  'profile.toast.detailsSaved': "ਜਾਣਕਾਰੀ ਸੇਵ ਹੋ ਗਈ",
  'profile.toast.photoUpdated': "ਫ਼ੋਟੋ ਅੱਪਡੇਟ ਹੋ ਗਈ",
  'profile.toast.pwChanged': "ਪਾਸਵਰਡ ਬਦਲ ਗਿਆ। ਬਾਕੀ ਡਿਵਾਈਸਾਂ ਸਾਈਨ-ਆਊਟ ਹੋ ਗਈਆਂ।",
  'profile.toast.tgUnlinked': "Telegram ਅਨਲਿੰਕ ਹੋ ਗਿਆ। ਕੋਈ ਹੋਰ ਚੈਟ ਜੋੜਨ ਲਈ ਨਵਾਂ ਲਿੰਕ ਬਣਾਓ।",
  'profile.err.phone': "10 ਅੰਕਾਂ ਦਾ ਮੋਬਾਈਲ ਨੰਬਰ ਭਰੋ (ਜਾਂ ਜਿਵੇਂ ਹੈ ਰਹਿਣ ਦਿਓ)",
  'profile.err.pwMismatch': "ਨਵੇਂ ਪਾਸਵਰਡ ਮੇਲ ਨਹੀਂ ਖਾਂਦੇ",
  'profile.err.pwWeak': "ਪਾਸਵਰਡ 8+ ਅੱਖਰਾਂ ਦਾ ਹੋਵੇ, ਇੱਕ ਅੱਖਰ ਤੇ ਇੱਕ ਨੰਬਰ ਨਾਲ",
  'profile.err.pwSame': "ਨਵਾਂ ਪਾਸਵਰਡ ਮੌਜੂਦਾ ਤੋਂ ਵੱਖਰਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ",
  'wallet.payout.title': "ਸੇਵ ਕੀਤੀ ਭੁਗਤਾਨ ਜਾਣਕਾਰੀ",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "ਹਾਲੇ ਕੁਝ ਸੇਵ ਨਹੀਂ ਹੋਇਆ",
  'wallet.payout.hint': "ਜਦੋਂ ਤੁਸੀਂ ਪੈਸੇ ਕਢਵਾਉਂਦੇ ਹੋ ਤਾਂ ਤੁਹਾਡੀ UPI ID ਆਪੇ ਸੇਵ ਹੋ ਜਾਂਦੀ ਹੈ। ਤੁਸੀਂ ਅਗਲੀ ਵਾਰ ਪੈਸੇ ਕਢਵਾਉਣ ਦੀ ਬੇਨਤੀ ਵੇਲੇ ਇਸਨੂੰ ਬਦਲ ਸਕਦੇ ਹੋ।",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ਇੰਸਟਾਲ ਕਰੋ",
  "pwa.install.subhint": "ਤੁਰੰਤ, ਫੁੱਲ-ਸਕ੍ਰੀਨ ਪਹੁੰਚ ਲਈ ਇਸਨੂੰ ਆਪਣੀ ਹੋਮ ਸਕ੍ਰੀਨ 'ਤੇ ਸ਼ਾਮਲ ਕਰੋ — ਕਿਸੇ ਐਪ ਸਟੋਰ ਦੀ ਲੋੜ ਨਹੀਂ।",
  "pwa.install.listen": "ਸੁਣੋ",
  "pwa.install.listening": "ਰੋਕੋ",
  "pwa.install.installBtn": "ਇੰਸਟਾਲ ਕਰੋ",
  "pwa.install.notNow": "ਹੁਣੇ ਨਹੀਂ",
  "pwa.install.howToShow": "ਕਿਵੇਂ ਇੰਸਟਾਲ ਕਰਨਾ ਹੈ",
  "pwa.install.howToHide": "ਕਦਮ ਲੁਕਾਓ",
  "pwa.install.guide.iosSafari": "Safari ਸਕ੍ਰੀਨ ਦੇ ਹੇਠਾਂ, Share ਬਟਨ 'ਤੇ ਟੈਪ ਕਰੋ — ਇਹ ਉੱਪਰ ਵੱਲ ਤੀਰ ਵਾਲੇ ਚੌਰਸ ਵਰਗਾ ਦਿਖਦਾ ਹੈ।\nਖੁੱਲ੍ਹਣ ਵਾਲੇ ਮੀਨੂ ਵਿੱਚ ਥੋੜ੍ਹਾ ਹੇਠਾਂ ਸਕ੍ਰੋਲ ਕਰੋ।\n\"Add to Home Screen\" ਵਿਕਲਪ 'ਤੇ ਟੈਪ ਕਰੋ।\nਉੱਪਰ ਸੱਜੇ ਕੋਨੇ ਵਿੱਚ Add 'ਤੇ ਟੈਪ ਕਰੋ। ਹੁਣ ਐਪ ਕਿਸੇ ਆਮ ਐਪ ਵਾਂਗ ਤੁਹਾਡੀ ਹੋਮ ਸਕ੍ਰੀਨ 'ਤੇ ਹੋਵੇਗੀ।",
  "pwa.install.guide.iosOther": "iPhone ਜਾਂ iPad 'ਤੇ, ਹੋਮ ਸਕ੍ਰੀਨ 'ਤੇ ਸ਼ਾਮਲ ਕਰਨਾ ਸਿਰਫ਼ Safari ਬ੍ਰਾਊਜ਼ਰ ਵਿੱਚ ਕੰਮ ਕਰਦਾ ਹੈ।\nSafari ਖੋਲ੍ਹੋ ਅਤੇ ਇਸੇ ਵੈੱਬਸਾਈਟ 'ਤੇ ਜਾਓ।\nSafari ਦੇ ਹੇਠਾਂ Share ਬਟਨ 'ਤੇ ਟੈਪ ਕਰੋ — ਉੱਪਰ ਵੱਲ ਤੀਰ ਵਾਲਾ ਚੌਰਸ।\nਹੇਠਾਂ ਸਕ੍ਰੋਲ ਕਰੋ, \"Add to Home Screen\" 'ਤੇ ਟੈਪ ਕਰੋ, ਫਿਰ Add 'ਤੇ ਟੈਪ ਕਰੋ।",
  "pwa.install.guide.android": "ਉੱਪਰ ਦਿੱਤੇ Install ਬਟਨ 'ਤੇ ਟੈਪ ਕਰੋ।\nਜਦੋਂ ਤੁਹਾਡਾ ਬ੍ਰਾਊਜ਼ਰ ਪੁੱਛੇ, ਪੁਸ਼ਟੀ ਲਈ Install 'ਤੇ ਟੈਪ ਕਰੋ।\nਹੋ ਗਿਆ — ਹੁਣ ਐਪ ਤੁਹਾਡੀ ਹੋਮ ਸਕ੍ਰੀਨ 'ਤੇ ਹੈ। ਕਿਸੇ ਆਮ ਐਪ ਵਾਂਗ ਇਸਨੂੰ ਕਿਸੇ ਵੀ ਸਮੇਂ ਖੋਲ੍ਹੋ।\nਪੌਪ-ਅੱਪ ਨਹੀਂ ਦਿਖ ਰਿਹਾ? ਆਪਣੇ ਬ੍ਰਾਊਜ਼ਰ ਦਾ ਮੀਨੂ ਖੋਲ੍ਹੋ — ਤਿੰਨ ਬਿੰਦੂ, ਆਮ ਤੌਰ 'ਤੇ ਉੱਪਰ ਸੱਜੇ — ਅਤੇ \"Install app\" 'ਤੇ ਟੈਪ ਕਰੋ।",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "ਸਿੱਧਾ ਰੀਚਾਰਜ",
  "agent.directRecharge.subtitle.player": "ਇੱਕ ਖਿਡਾਰੀ ਚੁਣੋ ਅਤੇ ਆਪਣੇ ਪੂਲ ਤੋਂ ਰੀਚਾਰਜ ਕਰੋ। ਕਿਸੇ ਬੇਨਤੀ ਦੀ ਲੋੜ ਨਹੀਂ।",
  "agent.directRecharge.subtitle.subagent": "ਇੱਕ ਸਬ-ਏਜੰਟ ਚੁਣੋ ਅਤੇ ਆਪਣੇ ਪੂਲ ਤੋਂ ਉਹਨਾਂ ਦੇ ਪੂਲ ਵਿੱਚ ਰੀਚਾਰਜ ਕਰੋ।",
  "agent.directRecharge.search.player": "ਰੀਚਾਰਜ ਲਈ ਖਿਡਾਰੀ ਖੋਜੋ (ਨਾਮ · ਫ਼ੋਨ · ਈਮੇਲ)…",
  "agent.directRecharge.search.subagent": "ਰੀਚਾਰਜ ਲਈ ਸਬ-ਏਜੰਟ ਖੋਜੋ (ਨਾਮ · ਈਮੇਲ · UAID)…",
  "agent.directRecharge.clearSearch": "ਖੋਜ ਸਾਫ਼ ਕਰੋ",
  "agent.directRecharge.searching": "ਖੋਜ ਰਿਹਾ ਹੈ…",
  "agent.directRecharge.noMatches": "ਕੋਈ ਮੇਲ ਨਹੀਂ।",
  "agent.directRecharge.empty.player": "ਤੁਹਾਡੀ ਸੂਚੀ ਵਿੱਚ ਅਜੇ ਕੋਈ ਖਿਡਾਰੀ ਨਹੀਂ।",
  "agent.directRecharge.empty.subagent": "ਅਜੇ ਕੋਈ ਸਿੱਧਾ ਸਬ-ਏਜੰਟ ਨਹੀਂ।",
  "agent.directRecharge.searchFailed": "ਖੋਜ ਅਸਫਲ",
  "agent.directRecharge.label.wallet": "ਵਾਲੇਟ",
  "agent.directRecharge.label.pool": "ਪੂਲ",
  "agent.directRecharge.modal.title.player": "ਸਿੱਧਾ ਖਿਡਾਰੀ ਰੀਚਾਰਜ",
  "agent.directRecharge.modal.title.subagent": "ਸਿੱਧਾ ਸਬ-ਏਜੰਟ ਰੀਚਾਰਜ",
  "agent.directRecharge.modal.close": "ਬੰਦ ਕਰੋ",
  "agent.directRecharge.modal.targetPool": "ਟੀਚਾ ਪੂਲ",
  "agent.directRecharge.modal.operatingWallet": "ਓਪਰੇਟਿੰਗ ਵਾਲੇਟ",
  "agent.directRecharge.modal.dayToDay": "ਰੋਜ਼ਾਨਾ ਬਕਾਇਆ",
  "agent.directRecharge.modal.securityDeposit": "ਸੁਰੱਖਿਆ ਜਮ੍ਹਾਂ",
  "agent.directRecharge.modal.collateral": "ਜ਼ਮਾਨਤ ਪੂਲ",
  "agent.directRecharge.modal.amount": "ਰਕਮ (₹)",
  "agent.directRecharge.modal.overdraw": "ਤੁਸੀਂ ਆਪਣੇ ਪੂਲ (₹{pool}) ਤੋਂ ਵੱਧ ਨਹੀਂ ਦੇ ਸਕਦੇ। ਰਕਮ ਘਟਾਓ।",
  "agent.directRecharge.modal.reason": "ਕਾਰਨ / ਨੋਟ",
  "agent.directRecharge.modal.reasonPlaceholder": "ਨਕਦ ਇਕੱਠਾ · ref 4291",
  "agent.directRecharge.modal.reasonHint": "ਤੁਹਾਡੇ ਵਾਲੇਟ ਲੈਜਰ + ਆਡਿਟ ਲੌਗ ਵਿੱਚ ਲਿਖਿਆ ਜਾਂਦਾ ਹੈ।",
  "agent.directRecharge.modal.proofLabel": "ਭੁਗਤਾਨ ਦਾ ਸਕ੍ਰੀਨਸ਼ਾਟ ਅੱਪਲੋਡ ਕਰੋ (ਵਿਕਲਪਿਕ)",
  "agent.directRecharge.modal.proofHint": "ਇਤਿਹਾਸ ਰਾਹੀਂ ਪ੍ਰਾਪਤਕਰਤਾ ਨੂੰ ਦਿਖਾਈ ਦਿੰਦਾ ਹੈ।",
  "agent.directRecharge.modal.previewEmpty": "ਲੈਣ-ਦੇਣ ਦੇਖਣ ਲਈ ਰਕਮ ਦਰਜ ਕਰੋ।",
  "agent.directRecharge.modal.previewBody": "ਤੁਹਾਡੇ ਵਾਲੇਟ ਤੋਂ {name} ਦੇ {pool} ਵਿੱਚ ਭੇਜਿਆ ਗਿਆ। ਸੁਰੱਖਿਅਤ ਟ੍ਰਾਂਸਫਰ; ਲੈਜਰ + ਆਡਿਟ ਵਿੱਚ ਦਰਜ।",
  "agent.directRecharge.modal.pool.playerWallet": "ਵਾਲੇਟ",
  "agent.directRecharge.modal.pool.operatingWallet": "ਓਪਰੇਟਿੰਗ ਵਾਲੇਟ",
  "agent.directRecharge.modal.pool.securityDeposit": "ਸੁਰੱਖਿਆ ਜਮ੍ਹਾਂ",
  "agent.directRecharge.modal.cancel": "ਰੱਦ ਕਰੋ",
  "agent.directRecharge.modal.send": "₹{amount} ਭੇਜੋ",
  "agent.directRecharge.badge.player": "ਖਿਡਾਰੀ",
  "agent.directRecharge.badge.subagent": "ਸਬ-ਏਜੰਟ",
  "agent.directRecharge.modal.yourDirectSubagent": "ਤੁਹਾਡਾ ਸਿੱਧਾ ਸਬ-ਏਜੰਟ",
  "agent.directRecharge.modal.fundingSource": "ਫੰਡਿੰਗ ਸਰੋਤ",
  "agent.directRecharge.modal.you": "(ਤੁਸੀਂ)",
  "agent.directRecharge.modal.yourPool": "ਤੁਹਾਡਾ ਪੂਲ",
  "agent.directRecharge.modal.notSignedIn": "ਏਜੰਟ ਵਜੋਂ ਸਾਈਨ ਇਨ ਨਹੀਂ",
  "agent.directRecharge.modal.failed": "ਅਸਫਲ",

  // Common controls
  'common.signIn': 'ਸਾਈਨ ਇਨ ਕਰੋ',
  'common.signUp': 'ਸਾਈਨ ਅੱਪ ਕਰੋ',
  'common.login': 'ਲਾਗਇਨ',
  'common.logout': 'ਲਾਗ ਆਉਟ',
  'common.continue': 'ਜਾਰੀ ਰੱਖੋ',
  'common.cancel': 'ਰੱਦ ਕਰੋ',
  'common.save': 'ਸੰਭਾਲੋ',
  'common.update': 'ਅੱਪਡੇਟ',
  'common.clear': 'ਸਾਫ਼ ਕਰੋ',
  'common.back': 'ਵਾਪਸ',
  'common.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'common.sending': 'ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'common.saving': 'ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'common.optional': 'ਵਿਕਲਪਿਕ',
  'common.show': 'ਦਿਖਾਓ',
  'common.hide': 'ਲੁਕਾਓ',
  'common.profile': 'ਪ੍ਰੋਫਾਈਲ',
  'common.settings': 'ਸੈਟਿੰਗਜ਼',

  // NavBar / BottomNav
  'nav.brand': 'ਬਾਲੀ ਗੇਮਜ਼',
  'nav.home': 'ਮੁੱਖ',
  'nav.games': 'ਗੇਮਜ਼',
  'nav.wallet': 'ਵਾਲਿਟ',
  'nav.rewards': 'ਇਨਾਮ',
  'nav.profile': 'ਪ੍ਰੋਫਾਈਲ',

  // Login page
  'login.title': 'ਮੁੜ ਸਵਾਗਤ ਹੈ',
  'login.subtitle': 'ਖੇਡ ਜਾਰੀ ਰੱਖਣ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ।',
  'login.idLabel': 'ਯੂਜ਼ਰਨੇਮ, ਈਮੇਲ ਜਾਂ ਫ਼ੋਨ',
  'login.idPlaceholder': 'ਯੂਜ਼ਰਨੇਮ, ਈਮੇਲ ਜਾਂ ਮੋਬਾਈਲ',
  'login.passwordLabel': 'ਪਾਸਵਰਡ',
  'login.passwordPlaceholder': 'ਆਪਣਾ ਪਾਸਵਰਡ ਦਰਜ ਕਰੋ',
  'login.forgot': 'ਭੁੱਲ ਗਏ?',
  'login.cta': 'ਸਾਈਨ ਇਨ',
  'login.signingIn': 'ਸਾਈਨ ਇਨ ਹੋ ਰਿਹਾ ਹੈ…',
  'login.newHere': 'ਨਵੇਂ ਹੋ?',
  'login.createAccount': 'ਖਾਤਾ ਬਣਾਓ',

  // Register page
  'register.title': 'ਆਪਣਾ ਖਾਤਾ ਬਣਾਓ',
  'register.subtitle': 'ਇੱਕ ਟੈਗ ਚੁਣੋ ਅਤੇ ਅੰਦਰ ਆ ਜਾਓ।',
  'register.usernameLabel': 'ਯੂਜ਼ਰਨੇਮ',
  'register.usernamePlaceholder': 'ਵਿਲੱਖਣ ਨਾਮ ਚੁਣੋ',
  'register.usernameHelp': 'ਅੱਖਰ, ਨੰਬਰ ਅਤੇ ਅੰਡਰਸਕੋਰ। 3–24 ਅੱਖਰ।',
  'register.emailLabel': 'ਈਮੇਲ',
  'register.emailPlaceholder': 'ਤੁਹਾਡਾ ਈਮੇਲ ਪਤਾ',
  'register.mobileLabel': 'ਮੋਬਾਈਲ ਨੰਬਰ',
  'register.mobilePlaceholder': '10-ਅੰਕਾਂ ਦਾ ਮੋਬਾਈਲ',
  'register.mobileHelp': 'ਦੇਸ਼ ਦਾ ਕੋਡ ਅਸੀਂ ਆਪਣੇ ਆਪ ਜੋੜਾਂਗੇ।',
  'register.passwordLabel': 'ਪਾਸਵਰਡ',
  'register.passwordPlaceholder': 'ਇੱਕ ਮਜ਼ਬੂਤ ਪਾਸਵਰਡ ਚੁਣੋ',
  'register.passwordHelp': 'ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ, ਇੱਕ ਅੱਖਰ ਅਤੇ ਇੱਕ ਅੰਕ ਨਾਲ।',
  'register.referralLabel': 'ਰੈਫ਼ਰਲ ਕੋਡ',
  'register.referralPlaceholder': 'ਦੋਸਤ ਦਾ ਸੱਦਾ ਕੋਡ',
  'register.cta': 'ਖਾਤਾ ਬਣਾਓ',
  'register.creating': 'ਬਣਾਇਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'register.alreadyPlayer': 'ਪਹਿਲਾਂ ਤੋਂ ਪਲੇਅਰ ਹੋ?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'ਤੁਹਾਡੇ ਬਾਰੇ', 'register.step.signin': 'ਸਾਈਨ-ਇਨ', 'register.step.finish': 'ਪੂਰਾ',
  'register.step.aboutSubtitle': 'ਆਪਣੇ ਬਾਰੇ ਥੋੜ੍ਹਾ ਦੱਸੋ', 'register.step.signinSubtitle': 'ਸਾਈਨ-ਇਨ ਕਿਵੇਂ ਕਰੋਗੇ ਸੈੱਟ ਕਰੋ', 'register.step.finishSubtitle': 'ਟਿਕਾਣਾ ਚੁਣੋ ਅਤੇ ਮੁਕੰਮਲ ਕਰੋ',
  'register.firstName': 'ਪਹਿਲਾ ਨਾਮ', 'register.firstNamePh': 'ਆਰਵ',
  'register.lastName': 'ਆਖਰੀ ਨਾਮ', 'register.lastNamePh': 'ਸ਼ਰਮਾ',
  'register.dob': 'ਜਨਮ ਮਿਤੀ', 'register.dobHelp': 'ਖੇਡਣ ਲਈ ਤੁਹਾਡੀ ਉਮਰ 18 ਜਾਂ ਵੱਧ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ',
  'register.v.required': 'ਲੋੜੀਂਦਾ', 'register.v.nameMin': 'ਘੱਟੋ ਘੱਟ 2 ਅੱਖਰ',
  'register.v.tooLong': 'ਬਹੁਤ ਲੰਮਾ', 'register.v.nameChars': 'ਸਿਰਫ਼ ਅੱਖਰ, ਸਪੇਸ ਅਤੇ ਹਾਈਫਨ',
  'register.v.invalidDate': 'ਅਵੈਧ ਮਿਤੀ', 'register.v.minAge': 'ਤੁਹਾਡੀ ਉਮਰ ਘੱਟੋ ਘੱਟ 18 ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ',
  'register.v.tooOld': 'ਕਿਰਪਾ ਕਰਕੇ ਸਾਲ ਚੈੱਕ ਕਰੋ',
  'register.v.usernameMin': 'ਘੱਟੋ ਘੱਟ 3 ਅੱਖਰ', 'register.v.usernameMax': 'ਬਹੁਤ ਲੰਮਾ (ਵੱਧ ਤੋਂ ਵੱਧ 24)',
  'register.v.usernameChars': 'ਸਿਰਫ਼ ਅੱਖਰ, ਨੰਬਰ ਅਤੇ ਅੰਡਰਸਕੋਰ', 'register.v.emailShape': 'ਅਵੈਧ ਈਮੇਲ ਲੱਗਦੀ ਹੈ',
  'register.v.phoneDigits': '10 ਅੰਕਾਂ ਦਾ ਨੰਬਰ ਹੋਣਾ ਚਾਹੀਦਾ', 'register.v.phonePrefix': 'ਭਾਰਤੀ ਮੋਬਾਈਲ 6–9 ਨਾਲ ਸ਼ੁਰੂ ਹੁੰਦਾ',
  'register.v.pwdMin': 'ਘੱਟੋ ਘੱਟ 8 ਅੱਖਰ', 'register.v.pwdLetter': 'ਘੱਟੋ ਘੱਟ ਇੱਕ ਅੱਖਰ ਹੋਣਾ ਚਾਹੀਦਾ',
  'register.v.pwdNumber': 'ਘੱਟੋ ਘੱਟ ਇੱਕ ਨੰਬਰ ਹੋਣਾ ਚਾਹੀਦਾ',
  'register.v.referralShort': 'ਬਹੁਤ ਛੋਟਾ', 'register.v.referralChars': 'ਸਿਰਫ਼ ਅੱਖਰ ਅਤੇ ਨੰਬਰ',
  'register.v.usernameTaken': 'ਇਹ ਯੂਜ਼ਰਨੇਮ ਪਹਿਲਾਂ ਹੀ ਲਿਆ ਜਾ ਚੁੱਕਿਆ ਹੈ',
  'register.v.emailTaken': 'ਇਹ ਈਮੇਲ ਪਹਿਲਾਂ ਹੀ ਰਜਿਸਟਰ ਹੈ',
  'register.v.phoneTaken': 'ਇਹ ਨੰਬਰ ਪਹਿਲਾਂ ਹੀ ਰਜਿਸਟਰ ਹੈ',
  'register.v.noLocation': 'ਜਾਰੀ ਰੱਖਣ ਲਈ ਘੱਟੋ ਘੱਟ ਇੱਕ ਟਿਕਾਣਾ ਵਿਧੀ ਸਾਂਝੀ ਕਰੋ।',
  'register.welcome.title': 'ਜੀ ਆਇਆਂ ਨੂੰ', 'register.welcome.subtitle': 'ਤੁਹਾਡਾ ਖਾਤਾ ਤਿਆਰ ਹੈ। ਹੁਣੇ ਸੁਰੱਖਿਅਤ ਕਰੋ ਜਾਂ ਸਿੱਧੇ ਸ਼ੁਰੂ ਕਰੋ?',
  'register.welcome.verifyTelegram': 'ਟੈਲੀਗ੍ਰਾਮ ਰਾਹੀਂ ਪੁਸ਼ਟੀ ਕਰੋ',
  'register.welcome.verifyExplain': '⚠ ਮਹੱਤਵਪੂਰਨ: ਹੁਣੇ ਟੈਲੀਗ੍ਰਾਮ ਰਾਹੀਂ ਤਸਦੀਕ ਕਰੋ। ਤਸਦੀਕ ਤੋਂ ਬਿਨਾਂ, ਪਾਸਵਰਡ ਭੁੱਲਣ ’ਤੇ ਤੁਸੀਂ ਆਪ ਰੀਸੈੱਟ ਨਹੀਂ ਕਰ ਸਕੋਗੇ — ਸਿਰਫ਼ ਤੁਹਾਡਾ ਏਜੰਟ ਜਾਂ ਐਡਮਿਨ ਹੀ ਰੀਸੈੱਟ ਕਰ ਸਕੇਗਾ। ਬਾਅਦ ਵਿੱਚ ਪ੍ਰੋਫਾਈਲ ਤੋਂ ਵੀ ਤਸਦੀਕ ਕਰ ਸਕਦੇ ਹੋ।',
  'register.welcome.verifyHeads': 'ਇਸਨੂੰ ਬਾਅਦ ਲਈ ਸੰਭਾਲੋ',
  'register.welcome.verifyConsequence': 'ਈਮੇਲ ਤਸਦੀਕ ਹੁਣੇ ਉਪਲਬਧ ਨਹੀਂ ਹੈ। ਬਾਅਦ ਵਿੱਚ ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਕਰਨ ਲਈ ਟੈਲੀਗ੍ਰਾਮ ਰਾਹੀਂ ਤਸਦੀਕ ਕਰੋ। ਨਾ ਕਰੋ ਤਾਂ, ਪਾਸਵਰਡ ਭੁੱਲਣ ’ਤੇ ਸਿਰਫ਼ ਤੁਹਾਡਾ ਏਜੰਟ ਜਾਂ ਐਡਮਿਨ ਹੀ ਰੀਸੈੱਟ ਕਰ ਸਕੇਗਾ।',
  'register.welcome.continue': 'ਐਪ ਵਿੱਚ ਜਾਓ', 'register.welcome.continueLogin': 'ਜਾਰੀ ਰੱਖਣ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ', 'register.welcome.useDifferent': 'ਵੱਖਰਾ ਖਾਤਾ ਵਰਤੋ',
  'register.geo.title': 'ਤੁਸੀਂ ਕਿੱਥੋਂ ਖੇਡ ਰਹੇ ਹੋ?', 'register.geo.subtitle': 'ਨੇੜਲੇ ਏਜੰਟ ਨਾਲ ਜੋੜਨ ਲਈ ਅਸੀਂ ਤੁਹਾਡੇ ਟਿਕਾਣੇ ਦੀ ਵਰਤੋਂ ਕਰਦੇ ਹਾਂ।',
  'register.geo.showAlt': 'ਟਿਕਾਣਾ ਸਾਂਝਾ ਨਹੀਂ ਕਰ ਸਕਦੇ? ਵੱਖਰਾ ਤਰੀਕਾ ਵਰਤੋ',
  'register.geo.hideAlt': 'ਹੋਰ ਤਰੀਕੇ ਲੁਕਾਓ', 'register.geo.captured': 'ਟਿਕਾਣਾ ਕੈਪਚਰ ਕੀਤਾ',
  'register.geo.capturedHint': 'ਤੁਸੀਂ ਪ੍ਰੋਫਾਈਲ ਤੋਂ ਕਿਸੇ ਵੀ ਸਮੇਂ ਬਦਲ ਸਕਦੇ ਹੋ।',
  'register.geo.verifyNote': 'ਸਾਈਨ-ਅੱਪ ਤੋਂ ਬਾਅਦ, ਪ੍ਰੋਫਾਈਲ ਤੋਂ ਟੈਲੀਗ੍ਰਾਮ (ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਲਈ) ਜਾਂ ਈਮੇਲ ਰਾਹੀਂ ਕਿਸੇ ਵੀ ਸਮੇਂ ਪੁਸ਼ਟੀ ਕਰ ਸਕਦੇ ਹੋ। ਪੁਸ਼ਟੀ ਚੋਣਵੀਂ ਹੈ।',

  // Verify (post-signup)
  'verify.title': 'ਆਪਣਾ ਖਾਤਾ ਤਸਦੀਕ ਕਰੋ',
  'verify.subtitle': 'ਇੱਕ ਵਿਕਲਪ ਚੁਣੋ — ਬੱਸ ਇੰਨਾ ਹੀ ਚਾਹੀਦਾ ਹੈ।',
  'verify.tabTelegram': 'ਟੈਲੀਗ੍ਰਾਮ',
  'verify.tabEmail': 'ਈਮੇਲ',
  'verify.emailSent': 'ਅਸੀਂ {email} ਉੱਤੇ ਤਸਦੀਕੀ ਲਿੰਕ ਭੇਜ ਦਿੱਤਾ ਹੈ।',
  'verify.emailHint': 'ਇਸੇ ਡਿਵਾਈਸ ਉੱਤੇ ਖੋਲ੍ਹੋ। ਨਹੀਂ ਦਿਸ ਰਿਹਾ? ਸਪੈਮ / ਪ੍ਰਮੋਸ਼ਨ ਚੈੱਕ ਕਰੋ।',
  'verify.telegramOpen': '@BaaliGames_bot ਖੋਲ੍ਹੋ',
  'verify.telegramExpiry': 'ਲਿੰਕ 30 ਮਿੰਟਾਂ ਵਿੱਚ ਮਿਆਦ ਪੁੱਗ ਜਾਵੇਗਾ। ਟੈਲੀਗ੍ਰਾਮ ਵਿੱਚ ਇੱਕ ਵਾਰ Start ਦਬਾਉਣਾ ਜ਼ਰੂਰੀ ਹੈ।',
  'verify.goToLogin': 'ਲਾਗਇਨ ਉੱਤੇ ਜਾਓ',
  'verify.email.title': 'ਈਮੇਲ ਤਸਦੀਕ ਹੋਈ',
  'verify.email.sub': 'ਤੁਹਾਡਾ ਖਾਤਾ ਚਾਲੂ ਹੈ। ਹੁਣ ਖੇਡਣ ਦਾ ਸਮਾਂ ਹੈ।',
  'verify.email.failed': 'ਤਸਦੀਕੀ ਅਸਫਲ',
  'verify.email.verifying': 'ਤਸਦੀਕ ਹੋ ਰਹੀ ਹੈ…',
  'verify.backRegister': 'ਰਜਿਸਟਰ ਉੱਤੇ ਵਾਪਸ',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ਈਮੇਲ ਮੁੜ ਭੇਜੋ',
  'verify.gate.resent': 'ਭੇਜ ਦਿੱਤੀ!',
  'verify.gate.checking': 'ਕੰਮ ਹੋ ਰਿਹਾ ਹੈ…',
  'verify.gate.tgFallback': 'ਲਿੰਕ ਹੱਥੀਂ ਖੋਲ੍ਹੋ',
  'verify.gate.waiting': 'ਅਸੀਂ ਤਸਦੀਕੀ ਆਪਣੇ ਆਪ ਪਛਾਣ ਲਵਾਂਗੇ।',
  'verify.gate.signOut': 'ਸਾਈਨ ਆਉਟ',

  // Forgot password
  'forgot.title': 'ਆਪਣਾ ਖਾਤਾ ਮੁੜ-ਪ੍ਰਾਪਤ ਕਰੋ',
  'forgot.subtitle': 'ਅਸੀਂ ਤੁਹਾਨੂੰ ਵਾਪਸ ਆਉਣ ਦਾ ਰਾਹ ਭੇਜਾਂਗੇ।',
  'forgot.idLabel': 'ਯੂਜ਼ਰਨੇਮ, ਈਮੇਲ ਜਾਂ ਫ਼ੋਨ',
  'forgot.idPlaceholder': 'ਯੂਜ਼ਰਨੇਮ, ਈਮੇਲ ਜਾਂ ਮੋਬਾਈਲ',
  'forgot.sendVia': 'ਇਸ ਰਾਹੀਂ ਭੇਜੋ',
  'forgot.email': 'ਈਮੇਲ',
  'forgot.telegram': 'ਟੈਲੀਗ੍ਰਾਮ OTP',
  'forgot.telegramHelp': 'ਟੈਲੀਗ੍ਰਾਮ ਉਦੋਂ ਹੀ ਕੰਮ ਕਰੇਗਾ ਜੇ ਤੁਹਾਡਾ ਖਾਤਾ ਸਾਡੇ ਬੌਟ ਨਾਲ ਜੁੜਿਆ ਹੋਵੇ।',
  'forgot.cta': 'ਰਿਕਵਰੀ ਭੇਜੋ',
  'forgot.sentEmail': 'ਜੇ ਖਾਤਾ ਮੌਜੂਦ ਹੈ, ਤਾਂ ਰੀਸੈੱਟ ਲਿੰਕ ਈਮੇਲ ਕਰ ਦਿੱਤਾ ਗਿਆ ਹੈ।',
  'forgot.sentTelegram': 'ਜੇ ਖਾਤਾ ਮੌਜੂਦ ਹੈ, ਤਾਂ 6-ਅੰਕਾਂ ਦਾ ਕੋਡ ਟੈਲੀਗ੍ਰਾਮ ਰਾਹੀਂ ਭੇਜ ਦਿੱਤਾ ਗਿਆ ਹੈ।',
  'forgot.redirecting': 'ਰੀਸੈੱਟ ਪੰਨੇ ਉੱਤੇ ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'forgot.back': 'ਲਾਗਇਨ ਉੱਤੇ ਵਾਪਸ',

  // Reset password
  'reset.title': 'ਨਵਾਂ ਪਾਸਵਰਡ ਸੈੱਟ ਕਰੋ',
  'reset.subOtp': 'ਟੈਲੀਗ੍ਰਾਮ ਉੱਤੇ ਭੇਜਿਆ ਕੋਡ ਵਰਤੋ।',
  'reset.subLink': 'ਨਵਾਂ ਕੋਮਬੋ ਚੁਣੋ ਅਤੇ ਮੁੜ ਅੰਦਰ ਆਓ।',
  'reset.otpLabel': '6-ਅੰਕਾਂ ਦਾ ਕੋਡ',
  'reset.newPasswordLabel': 'ਨਵਾਂ ਪਾਸਵਰਡ',
  'reset.cta': 'ਪਾਸਵਰਡ ਅੱਪਡੇਟ ਕਰੋ',
  'reset.updating': 'ਅੱਪਡੇਟ ਹੋ ਰਿਹਾ ਹੈ…',
  'reset.successTitle': 'ਪਾਸਵਰਡ ਅੱਪਡੇਟ ਹੋ ਗਿਆ',
  'reset.successSub': 'ਤੁਸੀਂ ਤਿਆਰ ਹੋ। ਖੇਡਣ ਦਾ ਸਮਾਂ।',

  // Home (logged-out)
  'home.welcomeTo': 'ਸਵਾਗਤ ਹੈ',
  'home.heroSub': 'ਇੱਕ ਹੁਨਰ-ਅਧਾਰਿਤ ਗੇਮਿੰਗ ਹੱਬ। ਅੰਦਰ ਆਉਣ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ ਜਾਂ ਪਲੇਅਰ ਖਾਤਾ ਬਣਾਓ।',
  'home.skillBased': 'ਹੁਨਰ-ਅਧਾਰਿਤ',
  'home.skillBasedSub': 'ਰਣਨੀਤੀ ਨਾਲ ਜਿੱਤੋ',
  'home.fairPlay': 'ਨਿਆਂਪੂਰਨ ਖੇਡ',
  'home.fairPlaySub': 'ਚੀਟਿੰਗ-ਵਿਰੋਧੀ ਚਾਲੂ',
  'home.instantPayout': 'UPI ਪੇਆਉਟ',
  'home.instantPayoutSub': '24 ਘੰਟਿਆਂ ਦੇ ਅੰਦਰ',
  'home.featured': 'ਚੁਣੀਆਂ ਗੇਮਜ਼',
  'home.tapToPlay': 'ਖੇਡਣ ਲਈ ਟੈਪ ਕਰੋ',
  'home.ready': 'ਖੇਡਣ ਲਈ ਤਿਆਰ?',
  'home.dropIn': 'ਅੰਦਰ ਆਓ ਅਤੇ ਅਸਲ ਇਨਾਮ ਜਿੱਤੋ।',
  'home.start': 'ਸ਼ੁਰੂ ਕਰੋ',

  // Home (logged-in)
  'home.welcomeBack': 'ਮੁੜ ਸਵਾਗਤ ਹੈ',
  'home.balance': 'ਤੁਹਾਡਾ ਬੈਲੇਂਸ',
  'home.live': 'ਲਾਈਵ',
  'home.add': 'ਜੋੜੋ',
  'home.manage': 'ਪ੍ਰਬੰਧ',
  'home.rewards': 'ਇਨਾਮ',
  'home.todayChallenge': 'ਅੱਜ ਦੀ ਚੁਣੌਤੀ',
  'home.todayChallengeSub': 'ਇੱਕ ਮੈਚ ਜਿੱਤੋ — ਸਟ੍ਰੀਕ ਬੋਨਸ ਕਮਾਓ।',
  'home.view': 'ਦੇਖੋ',
  'home.yourGames': 'ਤੁਹਾਡੀਆਂ ਗੇਮਜ਼',
  'home.viewAll': 'ਸਭ ਦੇਖੋ',

  // Profile tabs
  'profile.details': 'ਵੇਰਵੇ',
  'profile.address': 'ਪਤਾ',
  'profile.languages': 'ਭਾਸ਼ਾਵਾਂ',
  'profile.account': 'ਖਾਤਾ',
  'profile.photo': 'ਫੋਟੋ',
  'profile.preferred': 'ਪਸੰਦੀਦਾ ਭਾਸ਼ਾ',

  // Profile → Languages tab
  'profile.lang.primary': 'ਮੁੱਖ ਭਾਸ਼ਾ',
  'profile.lang.primarySub': 'ਐਪ-ਵਿਆਪਕ ਡਿਫਾਲਟ',
  'profile.lang.locked': 'ਲੌਕ ਕੀਤਾ',
  'profile.lang.second': 'ਦੂਜੀ ਭਾਸ਼ਾ',
  'profile.lang.pickHelp': 'ਜੋ ਭਾਸ਼ਾ ਤੁਸੀਂ ਬੋਲਦੇ ਹੋ ਉਹ ਚੁਣੋ। ਚੁਣਨ ਲਈ ਕਾਰਡ ਉੱਤੇ ਟੈਪ ਕਰੋ।',
  'profile.lang.saved': 'ਸੈਕੰਡਰੀ ਭਾਸ਼ਾ ਸੰਭਾਲੀ ਗਈ',
  'profile.lang.cleared': 'ਸੈਕੰਡਰੀ ਭਾਸ਼ਾ ਸਾਫ਼ ਕੀਤੀ ਗਈ',

  // Language switcher
  'lang.switcher.aria': 'ਭਾਸ਼ਾ ਚੁਣੋ',
  'lang.switcher.title': 'ਆਪਣੀ ਭਾਸ਼ਾ ਚੁਣੋ',
  'lang.switcher.sub': 'ਪੂਰੀ ਐਪ ਤੁਰੰਤ ਬਦਲ ਜਾਵੇਗੀ।',
  'lang.switcher.current': 'ਵਰਤਮਾਨ',

  // Rewards page
  'rewards.title': 'ਇਨਾਮ ਅਤੇ ਆਫ਼ਰ',
  'rewards.subtitle': 'ਹੁਣੇ ਕਮਾਉਣ ਜਾਂ ਕਲੇਮ ਕਰਨ ਲਈ ਬੋਨਸ।',
  'rewards.tab.available': 'ਉਪਲਬਧ',
  'rewards.tab.inReview': 'ਸਮੀਖਿਆ ਅਧੀਨ',
  'rewards.tab.claimed': 'ਕਲੇਮ ਕੀਤੇ',
  'rewards.empty.available.title': 'ਇਸ ਵੇਲੇ ਕੋਈ ਆਫ਼ਰ ਨਹੀਂ',
  'rewards.empty.available.body': 'ਥੋੜ੍ਹੀ ਦੇਰ ਬਾਅਦ ਮੁੜ ਚੈੱਕ ਕਰੋ — ਐਡਮਿਨ ਨਿਯਮਿਤ ਤੌਰ ਤੇ ਨਵੇਂ ਆਫ਼ਰ ਪੋਸਟ ਕਰਦੇ ਹਨ।',
  'rewards.empty.review.title': 'ਸਮੀਖਿਆ ਵਿੱਚ ਕੁਝ ਨਹੀਂ',
  'rewards.empty.review.body': 'ਜਿਨ੍ਹਾਂ ਕਲੇਮਾਂ ਨੂੰ ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਚਾਹੀਦੀ ਹੈ, ਉਹ ਇੱਥੇ ਦਿਖਾਈ ਦੇਣਗੇ।',
  'rewards.empty.claimed.title': 'ਅਜੇ ਕੋਈ ਕਲੇਮ ਨਹੀਂ',
  'rewards.empty.claimed.body': 'ਜਦੋਂ ਤੁਸੀਂ ਬੋਨਸ ਕਮਾਓਗੇ, ਇਹ ਇੱਥੇ ਕਲੇਮ ਵਜੋਂ ਦਿਖਾਈ ਦੇਵੇਗਾ।',
  'rewards.expires': '{date} ਨੂੰ ਖਤਮ',
  'rewards.remaining': '{amount} ਬਾਕੀ',
  'rewards.cap': 'ਸੀਮਾ',
  'rewards.claim.cta': 'ਕਲੇਮ',
  'rewards.claim.submit': 'ਜਮ੍ਹਾਂ',
  'rewards.claimed.credited': '{date} ਨੂੰ {amount} ਕਲੇਮ ਕੀਤਾ',
  'rewards.claimed.pending': 'ਸਮੀਖਿਆ ਬਾਕੀ',
  'rewards.claimed.rejected': 'ਰੱਦ ਕੀਤਾ',
  'rewards.auto.willCredit': 'ਸ਼ਰਤਾਂ ਪੂਰੀਆਂ ਹੋਣ ਉੱਤੇ ਆਪਣੇ ਆਪ ਜਮ੍ਹਾਂ ਹੋਵੇਗਾ',
  'rewards.auto.willReview': 'ਐਡਮਿਨ ਸਮੀਖਿਆ ਕਤਾਰ ਵਿੱਚ ਆਏਗਾ',
  'rewards.row.creditedOn': '{date} ਨੂੰ ਜਮ੍ਹਾਂ',
  'rewards.row.submittedOn': '{date} ਨੂੰ ਜਮ੍ਹਾਂ',
  'rewards.toast.credited': 'ਬੋਨਸ ਜਮ੍ਹਾਂ ਹੋਇਆ — {amount} ਤੁਹਾਡੇ ਵਾਲਿਟ ਵਿੱਚ',
  'rewards.toast.submitted': 'ਸਮੀਖਿਆ ਲਈ ਭੇਜਿਆ',
  'rewards.toast.alreadyClaimed': 'ਪਹਿਲਾਂ ਹੀ ਕਲੇਮ ਕੀਤਾ ਜਾ ਚੁੱਕਾ ਹੈ',
  'rewards.toast.rejected': 'ਕਲੇਮ ਰੱਦ ਕੀਤਾ ਗਿਆ',

  // Wallet page
  'wallet.balance': 'ਬੈਲੇਂਸ',
  'wallet.balance.note': 'ਐਂਟਰੀਆਂ ਤੁਰੰਤ ਡੈਬਿਟ ਹੁੰਦੀਆਂ ਹਨ; ਇਨਾਮ ਮੈਚ ਦੇ ਅੰਤ ਉੱਤੇ ਕ੍ਰੈਡਿਟ ਹੁੰਦੇ ਹਨ।',
  'wallet.addMoney': 'ਪੈਸੇ ਜੋੜੋ',
  'wallet.withdraw': 'ਕਢਵਾਓ',
  'wallet.withdraw.minBalance': 'ਕਢਵਾਉਣ ਲਈ ਘੱਟੋ-ਘੱਟ ₹200 ਚਾਹੀਦੇ ਹਨ',
  'wallet.history.byGame': 'ਗੇਮ ਅਨੁਸਾਰ ਤੁਹਾਡਾ ਇਤਿਹਾਸ',
  'wallet.history.empty': 'ਅਜੇ ਕੋਈ ਗਤੀਵਿਧੀ ਨਹੀਂ। ਤੁਹਾਡਾ ਪਹਿਲਾ ਮੈਚ ਇੱਥੇ ਦਿਖਾਈ ਦੇਵੇਗਾ।',
  'wallet.activity': 'ਹਾਲੀਆ ਗਤੀਵਿਧੀ',
  'wallet.activity.empty': 'ਅਜੇ ਕੋਈ ਲੈਣ-ਦੇਣ ਨਹੀਂ।',
  'wallet.net': 'ਨੈੱਟ',
  'wallet.newer': 'ਨਵੇਂ',
  'wallet.older': 'ਪੁਰਾਣੇ',
  'wallet.pending.title': 'ਬਕਾਇਆ ਬੇਨਤੀਆਂ',
  'wallet.pending.recharge': '{amount} ਦਾ ਰਿਚਾਰਜ — ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਦੀ ਉਡੀਕ',
  'wallet.pending.withdrawal': '{amount} ਦਾ ਉਪਾੜ — ਭੁਗਤਾਨ ਦੀ ਉਡੀਕ',
  'wallet.state.pending': 'ਬਕਾਇਆ',
  'wallet.state.paid': 'ਅਦਾ ਕੀਤਾ',
  'wallet.pending.txnPrefix': 'ਲੈਣ-ਦੇਣ:',
  'wallet.pending.upload.label': 'ਤੇਜ਼ ਕਾਰਵਾਈ ਲਈ ਅਦਾਇਗੀ ਦਾ ਸਕਰੀਨਸ਼ਾਟ ਅਪਲੋਡ ਕਰੋ।',
  'wallet.pending.upload.hint':  'ਬੇਨਤੀ ਬਣਾਉਣ ਤੋਂ ਬਾਅਦ ਤੁਹਾਡੇ ਕੋਲ 24 ਘੰਟੇ ਹਨ।',
  'wallet.pending.upload.failed': 'ਅਪਲੋਡ ਅਸਫਲ',
  'wallet.activity.balanceAfter': 'ਬੈਲੰਸ {balance}',
  'wallet.activity.range': '{total} ਵਿੱਚੋਂ {from}–{to}',
  'wallet.activity.window': 'ਤੁਹਾਡੀ ਪਿਛਲੇ {days} ਦਿਨਾਂ ਦੀ ਗਤੀਵਿਧੀ',
  'wallet.history.summary.one':   '{matches} ਮੈਚ · ਜਿੱਤੇ {wins} · ਖਰਚ {bets}',
  'wallet.history.summary.other': '{matches} ਮੈਚ · ਜਿੱਤੇ {wins} · ਖਰਚ {bets}',
  'wallet.history.summary.refunded': ' · ਵਾਪਸੀ {refunds}',
  'ledger.kind.bet': 'ਮੈਚ ਐਂਟਰੀ', 'ledger.kind.win': 'ਮੈਚ ਇਨਾਮ',
  'ledger.kind.refund': 'ਮੈਚ ਵਾਪਸੀ', 'ledger.kind.forfeit': 'ਮੈਚ ਜ਼ਬਤੀ',
  'ledger.kind.fee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ', 'ledger.kind.recharge': 'ਟਾਪ-ਅਪ',
  'ledger.kind.admin_credit': 'ਐਡਮਿਨ ਕ੍ਰੈਡਿਟ', 'ledger.kind.admin_debit': 'ਐਡਮਿਨ ਡੈਬਿਟ',
  'ledger.kind.referral_bonus': 'ਰੈਫਰਲ ਬੋਨਸ', 'ledger.kind.signup_bonus': 'ਸਾਈਨ-ਅਪ ਬੋਨਸ',
  'ledger.kind.offer_credit': 'ਆਫਰ ਇਨਾਮ',
  'ledger.kind.withdrawal_hold': 'ਨਿਕਾਸੀ ਹੋਲਡ', 'ledger.kind.withdrawal_refund': 'ਨਿਕਾਸੀ ਵਾਪਸੀ',
  'ledger.kind.agent_topup_in': 'ਏਜੰਟ ਟਾਪ-ਅਪ', 'ledger.kind.agent_co_fund': 'ਏਜੰਟ ਯੋਗਦਾਨ', 'ledger.kind.turnover_reward': 'ਕੈਸ਼ਬੈਕ',
  'ledger.withdrawalPaid': 'ਨਿਕਾਸੀ ਅਦਾ ਕੀਤੀ', 'ledger.withdrawalPaidPartial': 'ਨਿਕਾਸੀ ਅਦਾ ਕੀਤੀ (ਅੰਸ਼ਕ)',
  'ledger.withdrawalPaid.txn': 'ਅਦਾ ਕੀਤੀ · ਲੈਣ-ਦੇਣ {txnId}',
  'ledger.withdrawalRejected': 'ਨਿਕਾਸੀ ਅਸਵੀਕਾਰ', 'ledger.withdrawalRejected.note': 'ਰੋਕੀ ਰਕਮ ਵਾਪਸ',
  'ledger.withdrawalExpired': 'ਨਿਕਾਸੀ ਮਿਆਦ ਖਤਮ', 'ledger.awaitingProof': 'ਸਬੂਤ ਦੀ ਉਡੀਕ',
  'proofPicker.button.compact': 'ਸਕਰੀਨਸ਼ਾਟ ਚੁਣੋ', 'proofPicker.button.prominent': 'ਸਕਰੀਨਸ਼ਾਟ ਸ਼ਾਮਲ ਕਰੋ',
  'proofPicker.processing': 'ਪ੍ਰਕਿਰਿਆ ਹੋ ਰਹੀ ਹੈ…',
  'proofPicker.error.mime': 'ਕੇਵਲ JPG, PNG ਜਾਂ WebP ਚਿੱਤਰਾਂ ਦੀ ਆਗਿਆ ਹੈ।',
  'proofPicker.error.size': 'ਫਾਈਲ ਬਹੁਤ ਵੱਡੀ — ਵੱਧ ਤੋਂ ਵੱਧ 5 MB।',
  'proofPicker.error.processing': 'ਚਿੱਤਰ ਪ੍ਰਕਿਰਿਆ ਨਹੀਂ ਹੋ ਸਕਿਆ',
  'proofPicker.previewAlt': 'ਚੁਣਿਆ ਸਕਰੀਨਸ਼ਾਟ ਝਲਕ',
  'proofViewer.state.none': 'ਸਕਰੀਨਸ਼ਾਟ ਦੀ ਉਡੀਕ', 'proofViewer.state.available': 'ਸਕਰੀਨਸ਼ਾਟ ਦੇਖੋ',
  'proofViewer.state.expired': 'ਸਕਰੀਨਸ਼ਾਟ ਉਪਲਬਧ ਨਹੀਂ', 'proofViewer.state.viewLimit': 'ਦੇਖਣ ਦੀ ਸੀਮਾ ਪੂਰੀ',
  'proofViewer.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'proofViewer.title.viewLimit': 'ਤੁਸੀਂ ਇਹ ਸਕਰੀਨਸ਼ਾਟ ਵੱਧ ਤੋਂ ਵੱਧ 2 ਵਾਰ ਖੋਲ੍ਹਿਆ ਹੈ',
  'proofViewer.error.load': 'ਸਕਰੀਨਸ਼ਾਟ ਲੋਡ ਨਹੀਂ ਹੋ ਸਕਿਆ',
  'proofViewer.lastView': 'ਆਖਰੀ ਵਾਰ — ਚਿੱਤਰ ਖਤਮ ਹੋ ਜਾਵੇਗਾ',
  'proofViewer.viewsRemaining.one': '{n} ਵਾਰ ਹੋਰ ਦੇਖ ਸਕਦੇ ਹੋ',
  'proofViewer.viewsRemaining.other': '{n} ਵਾਰ ਹੋਰ ਦੇਖ ਸਕਦੇ ਹੋ',
  'proofViewer.imageAlt': 'ਅਦਾਇਗੀ ਸਬੂਤ', 'proofViewer.close': 'ਬੰਦ ਕਰੋ',

  // Recharge flow
  'recharge.title': 'ਪੈਸੇ ਜੋੜੋ',
  'recharge.step1.subtitle': 'ਤੁਸੀਂ ਕਿੰਨੇ ਜੋੜਨਾ ਚਾਹੁੰਦੇ ਹੋ?',
  'recharge.step1.amount': 'ਰਕਮ',
  'recharge.step1.range': 'ਘੱਟੋ-ਘੱਟ {min} · ਵੱਧੋ-ਵੱਧ {max}',
  'recharge.step1.minHint': 'ਘੱਟੋ-ਘੱਟ {min} ਰਿਚਾਰਜ',
  'recharge.step1.maxHint': 'ਵੱਧੋ-ਵੱਧ {max}',
  'recharge.step1.quickPick': 'ਛੇਤੀ ਚੁਣੋ',
  'recharge.step2.subtitle': 'UPI ਨਾਲ ਅਦਾ ਕਰੋ, ਫਿਰ ਜਾਰੀ ਰੱਖੋ',
  'recharge.step2.payAmount': 'ਇਹ ਰਕਮ ਅਦਾ ਕਰੋ',
  'recharge.step2.iPaid': 'ਮੈਂ ਅਦਾ ਕਰ ਦਿੱਤਾ',
  'recharge.step2.howto.1': 'ਆਪਣੀ UPI ਐਪ ਖੋਲ੍ਹੋ (Google Pay / PhonePe / Paytm)।',
  'recharge.step2.howto.2': 'ਉੱਪਰ ਦਿੱਤੀ ਰਕਮ UPI ID ਉੱਤੇ ਅਦਾ ਕਰੋ ਜਾਂ QR ਸਕੈਨ ਕਰੋ।',
  'recharge.step2.howto.3': 'ਆਪਣੀ UPI ਐਪ ਤੋਂ ਲੈਣ-ਦੇਣ ID ਕਾਪੀ ਕਰੋ — ਅੱਗੇ ਚਾਹੀਦੀ ਹੋਵੇਗੀ।',
  "recharge.locked.title": "ਰੀਚਾਰਜ ਅਸਥਾਈ ਤੌਰ ਉੱਤੇ ਉਪਲਬਧ ਨਹੀਂ",
  "recharge.locked.body": "ਅਸੀਂ ਇਸ ਰੀਚਾਰਜ ਰਸਤੇ ਵਿੱਚ ਸਮੱਸਿਆ ਨੂੰ ਹੱਲ ਕਰ ਰਹੇ ਹਾਂ। ਤੁਹਾਡੇ ਵਾਲੇਟ ਅਤੇ ਨਿਕਾਸੀ ਉੱਤੇ ਅਸਰ ਨਹੀਂ ਹੋਇਆ। ਕਿਰਪਾ ਕਰਕੇ ਥੋੜ੍ਹੀ ਦੇਰ ਬਾਅਦ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।",
  "recharge.locked.reassure.withdrawals": "ਨਿਕਾਸੀ ਆਮ ਤਰੀਕੇ ਨਾਲ ਚੱਲ ਰਹੀ ਹੈ",
  "recharge.locked.reassure.existing": "ਤੁਸੀਂ ਪਹਿਲਾਂ ਭੇਜੀ ਅਦਾਇਗੀ ਪ੍ਰਕਿਰਿਆ ਅਧੀਨ ਹੈ",
  "recharge.cap.banner": "ਹੁਣ ਵੱਧ ਤੋਂ ਵੱਧ ਰੀਚਾਰਜ: {max}. ਪ੍ਰਾਪਤਕਰਤਾ ਦੀ ਸਮਰੱਥਾ ਅਸਥਾਈ ਤੌਰ 'ਤੇ ਸੀਮਿਤ ਹੈ।",
  "recharge.cap.tooHigh": "ਰਕਮ ਉਪਲਬਧ ਹੱਦ ਤੋਂ ਵੱਧ ਹੈ। ਹੁਣ ਤੁਸੀਂ ਵੱਧ ਤੋਂ ਵੱਧ {max} ਰੀਚਾਰਜ ਕਰ ਸਕਦੇ ਹੋ।",
  "recharge.notConfigured.title": "ਰੀਚਾਰਜ ਜਲਦੀ ਉਪਲਬਧ ਹੋਵੇਗਾ",
  "recharge.notConfigured.body": "ਅਸੀਂ ਇਸ ਰੀਚਾਰਜ ਸੇਵਾ ਦਾ ਸੈੱਟਅੱਪ ਪੂਰਾ ਕਰ ਰਹੇ ਹਾਂ। ਤੁਹਾਡੇ ਵਾਲੇਟ ਅਤੇ ਨਿਕਾਸੀ ਉੱਤੇ ਅਸਰ ਨਹੀਂ ਹੋਇਆ। ਕਿਰਪਾ ਕਰ ਕੇ ਥੋੜ੍ਹੀ ਦੇਰ ਬਾਅਦ ਚੈੱਕ ਕਰੋ।",
  'recharge.step3.subtitle': 'ਆਪਣੀ ਅਦਾਇਗੀ ਦੀ ਪੁਸ਼ਟੀ ਕਰੋ',
  'recharge.step3.amountPaid': 'ਤੁਸੀਂ ਜੋ ਰਕਮ ਅਦਾ ਕੀਤੀ',
  'recharge.step3.txnId': 'UPI ਲੈਣ-ਦੇਣ ID',
  'recharge.step3.txnIdHelp': 'ਆਪਣੀ UPI ਐਪ ਵਿੱਚ ਅਦਾਇਗੀ ਰਸੀਦ ਵਿੱਚ ਲੱਭੋ — ਆਮ ਤੌਰ ਉੱਤੇ 12 ਅੰਕ।',
  'recharge.step3.note': 'ਐਡਮਿਨ ਲਈ ਨੋਟ',
  'recharge.step3.notePlaceholder': 'ਉਦਾਹਰਨ: ਪਤਨੀ ਦੇ ਨੰਬਰ ਤੋਂ ਅਦਾ ਕੀਤਾ',
  'recharge.step3.submit': 'ਸਮੀਖਿਆ ਲਈ ਭੇਜੋ',
  'recharge.step3.disclaimer': 'ਐਡਮਿਨ UPI ਐਪ ਵਿੱਚ ਅਦਾਇਗੀ ਦੀ ਜਾਂਚ ਕਰਦਾ ਹੈ। ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ 24 ਘੰਟਿਆਂ ਵਿੱਚ ਵਾਲਿਟ ਜਮ੍ਹਾਂ ਹੋਵੇਗਾ (ਆਮ ਤੌਰ ਤੇ ਜਲਦੀ)।',
  'recharge.success.title': 'ਭੇਜਿਆ — ਸਮੀਖਿਆ ਦੀ ਉਡੀਕ',
  'recharge.success.body': '{amount} ਦਾ ਤੁਹਾਡਾ ਰਿਚਾਰਜ ਐਡਮਿਨ ਕਤਾਰ ਵਿੱਚ ਹੈ। 24 ਘੰਟਿਆਂ ਵਿੱਚ ਜਮ੍ਹਾਂ ਹੋਵੇਗਾ (ਆਮ ਤੌਰ ਤੇ ਜਲਦੀ)।',
  'recharge.success.refId': 'ਹਵਾਲਾ',
  'recharge.toast.submitted': 'ਐਡਮਿਨ ਸਮੀਖਿਆ ਲਈ ਰਿਚਾਰਜ ਭੇਜਿਆ',

  // Withdraw flow
  'withdraw.title': 'ਕਢਵਾਓ',
  'withdraw.step1.subtitle': 'ਅਸੀਂ ਪੈਸੇ ਕਿੱਥੇ ਭੇਜੀਏ?',
  'withdraw.step2.subtitle': 'ਇੱਕ ਅੰਤਮ ਜਾਂਚ',
  'withdraw.balance': 'ਉਪਲਬਧ ਬੈਲੇਂਸ',
  'withdraw.amount': 'ਰਕਮ',
  'withdraw.max': 'ਵੱਧੋ-ਵੱਧ',
  'withdraw.upiId': 'ਤੁਹਾਡੀ UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'ਅਸੀਂ ਇੱਥੇ ਪੈਸੇ ਭੇਜਾਂਗੇ। ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਦੋਬਾਰਾ ਜਾਂਚ ਕਰੋ।',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'ਤੁਹਾਡੀ ਪਿਛਲੀ ਨਿਕਾਸੀ ਤੋਂ ਪਹਿਲਾਂ ਤੋਂ ਭਰਿਆ ਹੈ',
  'withdraw.upiShapeErr': 'UPI ID yourname@psp ਵਰਗੀ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ (ਉਦਾਹਰਨ: lucky@oksbi)',
  'withdraw.phoneShapeErr': 'ਇੱਕ ਜਾਇਜ਼ 10-ਅੰਕਾਂ ਦਾ ਭਾਰਤੀ ਮੋਬਾਈਲ ਦਰਜ ਕਰੋ (6, 7, 8 ਜਾਂ 9 ਨਾਲ ਸ਼ੁਰੂ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ)',
  'withdraw.whatsapp': 'ਵਟਸਐਪ ਨੰਬਰ',
  'withdraw.errMin': 'ਘੱਟੋ-ਘੱਟ ਨਿਕਾਸੀ {min} ਹੈ',
  'withdraw.errExceed': 'ਰਕਮ ਤੁਹਾਡੇ ਬੈਲੇਂਸ ਤੋਂ ਵੱਧ ਹੈ',
  'withdraw.minHint': 'ਘੱਟੋ-ਘੱਟ {min} ਨਿਕਾਸੀ',
  'withdraw.step2.review': 'ਸਮੀਖਿਆ',
  'withdraw.step2.heldNotice': 'ਜਦੋਂ ਤੁਸੀਂ ਭੇਜੋਗੇ, ਇਹ ਰਕਮ ਤੁਰੰਤ ਰੋਕ ਲਈ ਜਾਵੇਗੀ ਤਾਂ ਕਿ ਕਿਤੇ ਹੋਰ ਖਰਚ ਨਾ ਹੋ ਸਕੇ। ਐਡਮਿਨ ਤੁਹਾਡੀ UPI ID ਉੱਤੇ ਅਦਾ ਕਰਕੇ ਪੇਡ ਮਾਰਕ ਕਰੇਗਾ।',
  'withdraw.heldAfter': 'ਰੋਕਿਆ',
  'withdraw.spendableAfter': 'ਖਰਚਣਯੋਗ',
  'withdraw.step2.disclaimer': 'ਜੇ ਐਡਮਿਨ ਰੱਦ ਕਰਦਾ ਹੈ, ਰੋਕੀ ਗਈ ਰਕਮ ਤੁਹਾਡੇ ਵਾਲਿਟ ਵਿੱਚ ਵਾਪਸ ਆ ਜਾਂਦੀ ਹੈ।',
  'withdraw.submit': 'ਜਮ੍ਹਾਂ',
  'withdraw.success.title': 'ਭੇਜਿਆ — ਭੁਗਤਾਨ ਦੀ ਉਡੀਕ',
  'withdraw.success.body': '{amount} ਦੀ ਤੁਹਾਡੀ ਨਿਕਾਸੀ ਕਤਾਰ ਵਿੱਚ ਹੈ। 24 ਘੰਟਿਆਂ ਵਿੱਚ ਅਦਾਇਗੀ (ਆਮ ਤੌਰ ਤੇ ਜਲਦੀ)। ਉਦੋਂ ਤੱਕ ਰੋਕੀ ਰਕਮ ਰਾਖਵੀਂ ਰਹੇਗੀ।',
  'withdraw.success.refId': 'ਹਵਾਲਾ',
  'withdraw.toast.submitted': 'ਨਿਕਾਸੀ ਕਤਾਰ ਵਿੱਚ — ਤੁਹਾਡੇ ਵਾਲਿਟ ਵਿੱਚ ਰੋਕੀ ਗਈ',

  // Payment receiver card
  'payment.upi.label': 'ਇਸ UPI ID ਉੱਤੇ ਅਦਾ ਕਰੋ',
  'payment.upi.copy': 'ਕਾਪੀ',
  'payment.upiCopied': 'UPI ID ਕਾਪੀ ਹੋਈ',
  'payment.openUpiApp': 'ਹੁਣੇ ਅਦਾ ਕਰੋ',
  'payment.openWhatsapp': 'ਵਟਸਐਪ',
  'payment.helpAt': 'ਮਦਦ ਚਾਹੀਦੀ ਹੈ? ਕਾਲ ਕਰੋ',
  'payment.qr.label': 'ਅਦਾ ਕਰਨ ਲਈ QR ਸਕੈਨ ਕਰੋ',
  'payment.qr.tap': 'ਵੱਡਾ ਕਰਨ ਲਈ ਟੈਪ ਕਰੋ',
  'payment.qr.tapToClose': 'ਬੰਦ ਕਰਨ ਲਈ ਕਿਤੇ ਵੀ ਟੈਪ ਕਰੋ',
  'payment.notConfigured.title': 'ਰਿਚਾਰਜ ਇਸ ਵੇਲੇ ਉਪਲਬਧ ਨਹੀਂ',
  'payment.notConfigured.contactBody': 'ਅਜੇ ਕੋਈ UPI ਸੈੱਟ ਨਹੀਂ ਹੈ। ਵਟਸਐਪ ਜਾਂ ਫ਼ੋਨ ਰਾਹੀਂ ਸਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰੋ, ਅਸੀਂ ਰਿਚਾਰਜ ਵਿੱਚ ਮਦਦ ਕਰਾਂਗੇ।',
  'payment.notConfigured.unavailable': 'ਰਿਚਾਰਜ ਅਸਥਾਈ ਤੌਰ ਉੱਤੇ ਉਪਲਬਧ ਨਹੀਂ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਬਾਅਦ ਵਿੱਚ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
  'payment.chooser.title': 'ਆਪਣੀ UPI ਐਪ ਚੁਣੋ',
  'payment.chooser.subtitle': 'ਤੁਸੀਂ ਅਦਾ ਕਰੋਗੇ',
  'payment.chooser.hint': 'ਰਕਮ ਪਹਿਲਾਂ ਤੋਂ ਭਰੀ ਨਾਲ ਖੋਲ੍ਹਣ ਲਈ ਐਪ ਉੱਤੇ ਟੈਪ ਕਰੋ। ਐਪ ਪਹਿਲਾਂ ਤੋਂ ਤੁਹਾਡੇ ਫ਼ੋਨ ਉੱਤੇ ਇੰਸਟਾਲ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ — ਜੇ "address invalid" ਗ਼ਲਤੀ ਦਿਸੇ ਤਾਂ ਪਹਿਲਾਂ ਐਪ ਸਟੋਰ / ਪਲੇ ਸਟੋਰ ਤੋਂ ਐਪ ਇੰਸਟਾਲ ਕਰੋ।',

  // Common
  'common.next': 'ਅੱਗੇ',
  'common.close': 'ਬੰਦ',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'ਤੇਜ਼ ਅਤੇ ਸੁਚਾਰੂ ਭੁਗਤਾਨ ਸੇਵਾ ਲਈ ਸਹੀ ਟਿਕਾਣਾ ਅਤੇ ਸੰਪਰਕ ਨੰਬਰ ਦਿਓ।',
  'geo.change': 'ਬਦਲੋ',
  'geo.gps.button': 'ਮੇਰਾ ਮੌਜੂਦਾ ਟਿਕਾਣਾ ਵਰਤੋ',
  'geo.gps.benefit': 'ਸਭ ਤੋਂ ਤੇਜ਼ — ਇੱਕ ਟੈਪ, ਕੋਈ ਟਾਈਪਿੰਗ ਨਹੀਂ।',
  'geo.gps.denied': 'ਟਿਕਾਣੇ ਦੀ ਇਜਾਜ਼ਤ ਰੱਦ। ਪਿਨਕੋਡ ਵਰਤੋ ਜਾਂ ਹੇਠਾਂ ਚੁਣੋ।',
  'geo.gps.failed': 'ਟਿਕਾਣਾ ਨਹੀਂ ਮਿਲਿਆ। ਪਿਨਕੋਡ ਅਜ਼ਮਾਓ।',
  'geo.gps.notResolved': 'ਤੁਹਾਡਾ ਟਿਕਾਣਾ ਕਿਸੇ ਖੇਤਰ ਨਾਲ ਮੇਲ ਨਹੀਂ ਖਾਂਦਾ। ਕਿਰਪਾ ਕਰਕੇ ਚੁਣੋ।',
  'geo.pincode.label': 'ਪਿਨਕੋਡ',
  'geo.pincode.notFound': 'ਪਿਨਕੋਡ ਨਹੀਂ ਮਿਲਿਆ। ਕਿਰਪਾ ਕਰਕੇ ਆਪਣਾ ਖੇਤਰ ਚੁਣੋ।',
  'geo.pincode.lookupFailed': 'ਪਿਨਕੋਡ ਖੋਜ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਚੁਣੋ।',
  'geo.multipleAreas.label': 'ਇਸ ਪਿਨਕੋਡ ਵਿੱਚ ਕਈ ਖੇਤਰ — ਆਪਣਾ ਚੁਣੋ',
  'geo.multipleAreas.placeholder': 'ਆਪਣਾ ਖੇਤਰ ਚੁਣੋ',
  'geo.manual.title': 'ਹੱਥੀਂ ਚੁਣੋ',
  'geo.manual.close': 'ਹੱਥੀਂ ਚੋਣ ਲੁਕਾਓ',
  'geo.state.label': 'ਰਾਜ',
  'geo.state.placeholder': 'ਰਾਜ ਚੁਣੋ',
  'geo.district.label': 'ਜ਼ਿਲ੍ਹਾ',
  'geo.district.placeholder': 'ਜ਼ਿਲ੍ਹਾ ਚੁਣੋ',
  'geo.area.label': 'ਖੇਤਰ / ਸ਼ਹਿਰ',
  'geo.area.placeholder': 'ਖੇਤਰ ਚੁਣੋ',
  'geo.privacy.note': 'ਸਿਰਫ਼ ਸਥਾਨਕ ਏਜੰਟ ਦੇਣ ਲਈ। ਸਾਂਝਾ ਨਹੀਂ ਕੀਤਾ ਜਾਂਦਾ।',
  'geo.required.hint': 'ਰੈਫ਼ਰਲ ਕੋਡ ਨਾ ਹੋਣ ਉੱਤੇ ਟਿਕਾਣਾ ਜ਼ਰੂਰੀ ਹੈ।',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'ਡੈਸ਼ਬੋਰਡ',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'ਹਿਸਾਬ',
  'agent.nav.players': 'ਪਲੇਅਰਜ਼',
  'agent.nav.agents': 'ਏਜੰਟ',
  'agent.nav.rewards': 'ਇਨਾਮ',
  'agent.nav.profile':   'ਪ੍ਰੋਫਾਈਲ',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'ਹੋਮ',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'ਹਿਸਾਬ',
  'agent.bottomnav.players': 'ਪਲੇਅਰਜ਼',
  'agent.bottomnav.agents': 'ਏਜੰਟ',
  'agent.bottomnav.rewards': 'ਇਨਾਮ',
  'agent.bottomnav.profile': 'ਪ੍ਰੋਫਾਈਲ',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'ਮੁੜ ਸਵਾਗਤ ਹੈ, ',
  'agent.dashboard.header.welcomeFallback': 'ਮਿੱਤਰ',
  'agent.dashboard.header.subtitle': 'ਤੁਹਾਡਾ ਕਾਰੋਬਾਰ ਇੱਕ ਨਜ਼ਰ ਵਿੱਚ — ਪੂਲ, ਡਿਪਾਜ਼ਿਟ, ਪਲੇਅਰਜ਼, ਸਬ-ਏਜੰਟ, P&L, ਐਕਸਪੋਜ਼ਰ',
  'agent.dashboard.range.today': 'ਅੱਜ',
  'agent.dashboard.range.yesterday': 'ਕੱਲ੍ਹ',
  'agent.dashboard.range.thisWeek': 'ਇਹ ਹਫ਼ਤਾ',
  'agent.dashboard.range.lastWeek': 'ਪਿਛਲਾ ਹਫ਼ਤਾ',
  'agent.dashboard.range.thisMonth': 'ਇਹ ਮਹੀਨਾ',
  'agent.dashboard.range.lastMonth': 'ਪਿਛਲਾ ਮਹੀਨਾ',
  'agent.dashboard.range.complete': 'ਸੰਪੂਰਨ',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'ਡਿਪਾਜ਼ਿਟ ਕਵਰੇਜ ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.dashboard.depositBar.label': 'ਡਿਪਾਜ਼ਿਟ ਕਵਰੇਜ',
  'agent.dashboard.depositBar.balanceOfTarget': '· {target} ਟਾਰਗੇਟ ਵਿੱਚੋਂ {balance}',
  'agent.dashboard.depositBar.tapToView': 'ਪੂਰਾ ਡਿਪਾਜ਼ਿਟ ਪੰਨਾ ਦੇਖਣ ਲਈ ਟੈਪ ਕਰੋ।',
  'agent.dashboard.depositBar.caption.suspended': 'ਖਾਤਾ ਮੁਅੱਤਲ ਹੈ। ਐਡਮਿਨ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।',
  'agent.dashboard.depositBar.caption.breach': 'ਤੁਹਾਡੇ ਵਾਲਿਟ ਨੇ ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਸੀਮਾ ਦਾ 100% ਖਪਤ ਕਰ ਲਿਆ ਹੈ। ਨਿਕਾਸੀ ਲੌਕ ਹੈ। ਤੁਰੰਤ ਟੌਪ-ਅੱਪ ਕਰੋ।',
  'agent.dashboard.depositBar.caption.warning': 'ਤੁਹਾਡਾ ਵਾਲਿਟ ਡਿਪਾਜ਼ਿਟ ਸੀਮਾ ਦੇ {pct}% ਉੱਤੇ ਹੈ। ਚੇਤਾਵਨੀ 80% ਉੱਤੇ, ਲੌਕਡਾਊਨ 100% ਉੱਤੇ।',
  'agent.dashboard.depositBar.caption.caution': 'ਤੁਹਾਡੇ ਵਾਲਿਟ ਨੇ ਡਿਪਾਜ਼ਿਟ ਕਵਰੇਜ ਦਾ {pct}% ਵਰਤਿਆ ਹੈ। 80% ਤੋਂ ਘੱਟ — ਅਜੇ ਸੁਰੱਖਿਅਤ।',
  'agent.dashboard.depositBar.caption.safe': 'ਡਿਪਾਜ਼ਿਟ ਪੂਰੀ ਤਰ੍ਹਾਂ ਅਛੂਤ। ਕੋਈ ਕਵਰੇਜ ਨਹੀਂ ਵਰਤੀ ਜਾ ਰਹੀ।',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'ਚੇਤਾਵਨੀ · ਜ਼ੀਰੋ ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ',
  'agent.dashboard.locatorSkip.body': 'ਤੁਹਾਡੀ ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ {amount} ਹੈ। ਜਦੋਂ ਤੱਕ ਤੁਸੀਂ ਇਸ ਨੂੰ ਫੰਡ ਨਹੀਂ ਕਰਦੇ, ਭੂਗੋਲਿਕ ਲੋਕੇਟਰ ਤੋਂ ਤੁਹਾਨੂੰ ਨਵੇਂ ਪਲੇਅਰ ਨਹੀਂ ਮਿਲਣਗੇ। ਮੌਜੂਦਾ ਪਲੇਅਰ + ਸਿੱਧੇ ਰੈਫ਼ਰਲ + ਹੱਥੀਂ ਨਿਯੁਕਤੀਆਂ ਉੱਤੇ ਅਸਰ ਨਹੀਂ ਪੈਂਦਾ — ਸਿਰਫ਼ ਜੈਵਿਕ ਜਿਓ-ਰੂਟਡ ਸਾਈਨਅੱਪ ਤੁਹਾਨੂੰ ਛੱਡ ਦਿੰਦੇ ਹਨ।',
  'agent.dashboard.locatorSkip.cta': 'ਡਿਪਾਜ਼ਿਟ ਫੰਡ ਕਰੋ',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'ਡਾਊਨਲਾਈਨ ਡਿਫਾਲਟਰ ਚੇਤਾਵਨੀ',
  'agent.dashboard.defaulter.summary.critical.one': 'ਤੁਹਾਡਾ {n} ਸਿੱਧਾ ਸਬ-ਏਜੰਟ ਡਿਫਾਲਟ ਵਿੱਚ ਹੈ — ਉਹ ਲੌਕ ਹੈ ਜਾਂ ਉਸ ਦੀ ਡਿਪਾਜ਼ਿਟ ਟੁੱਟ ਚੁੱਕੀ ਹੈ।',
  'agent.dashboard.defaulter.summary.critical.other': 'ਤੁਹਾਡੇ {n} ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਡਿਫਾਲਟ ਵਿੱਚ ਹਨ — ਉਹ ਲੌਕ ਹਨ ਜਾਂ ਉਨ੍ਹਾਂ ਦੀ ਡਿਪਾਜ਼ਿਟ ਟੁੱਟ ਚੁੱਕੀ ਹੈ।',
  'agent.dashboard.defaulter.summary.warning.one': 'ਤੁਹਾਡਾ {n} ਸਿੱਧਾ ਸਬ-ਏਜੰਟ ਆਪਣੀ ਡਿਪਾਜ਼ਿਟ ਸੀਮਾ ਦੇ 80%+ ਉੱਤੇ ਹੈ — ਡਿਫਾਲਟਰ ਜੋਖਮ।',
  'agent.dashboard.defaulter.summary.warning.other': 'ਤੁਹਾਡੇ {n} ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਆਪਣੀ ਡਿਪਾਜ਼ਿਟ ਸੀਮਾ ਦੇ 80%+ ਉੱਤੇ ਹਨ — ਡਿਫਾਲਟਰ ਜੋਖਮ।',
  'agent.dashboard.defaulter.worst': 'ਸਭ ਤੋਂ ਮਾੜਾ: @{username} ਡਿਪਾਜ਼ਿਟ ਸੀਮਾ ਦੇ {pct}% ਉੱਤੇ।',
  'agent.dashboard.defaulter.tapHint': 'ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਸੂਚੀ ਉੱਤੇ ਜਾਣ ਲਈ ਟੈਪ ਕਰੋ। ਡਿਫਾਲਟਰ ਕਤਾਰਾਂ ਹਾਈਲਾਈਟ ਹਨ।',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'ਲੌਕ',
  'agent.dashboard.marginPill.caution': 'ਸਾਵਧਾਨ',
  'agent.dashboard.marginPill.warning': 'ਚੇਤਾਵਨੀ',
  'agent.dashboard.marginPill.breach': 'ਉਲੰਘਣਾ',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'ਸਵੇਰ ਦੀ ਬ੍ਰੀਫ਼ਿੰਗ',
  'agent.dashboard.sections.morning.subtitle': 'ਲਾਈਵ ਸਨੈਪਸ਼ਾਟ — ਹਰ ਨੰਬਰ ਆਪਣੇ ਆਪ ਰਿਫ੍ਰੈਸ਼ ਹੁੰਦਾ ਹੈ',
  'agent.dashboard.sections.activity.title': 'ਗਤੀਵਿਧੀ',
  'agent.dashboard.sections.activity.subtitle': 'ਟੌਪ ਖਰਚਣ ਵਾਲੇ ਅਤੇ ਸਭ ਤੋਂ ਨਵੀਆਂ ਪਲੇਅਰ ਪੇਮੈਂਟ ਘਟਨਾਵਾਂ',
  'agent.dashboard.sections.forensic.title': 'ਫੋਰੈਂਸਿਕ ਵੇਰਵੇ',
  'agent.dashboard.sections.forensic.subtitle': 'ਕੋਈ ਵੀ ਸੈਕਸ਼ਨ ਫੈਲਾਉਣ ਲਈ ਟੈਪ ਕਰੋ — ਇਹ ਪੰਨਾ ਸ਼ਾਂਤ ਰੱਖਣ ਲਈ ਡਿਫਾਲਟ ਤੌਰ ਉੱਤੇ ਬੰਦ',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'ਨਫ਼ਾ-ਨੁਕਸਾਨ ਡੀਪ-ਡਾਈਵ',
  'agent.dashboard.accordions.pnlDeep.helper': 'ਤੁਹਾਡੀ ਕਮਾਈ ਦਾ ਪਿੱਲਰ + ਗੇਮ ਅਨੁਸਾਰ ਬ੍ਰੇਕਡਾਊਨ। ਅੰਡਰਲਾਈੰਗ ਮੈਚਾਂ ਲਈ ਗੇਮ ਕਤਾਰ ਕਲਿੱਕ ਕਰੋ।',
  'agent.dashboard.accordions.leaderboard.title': 'ਡਾਊਨਲਾਈਨ ਲੀਡਰਬੋਰਡ',
  'agent.dashboard.accordions.leaderboard.helper': 'ਨੈੱਟ ਕਮਾਈ ਅਨੁਸਾਰ ਰੈਂਕ ਕੀਤੇ ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟ। ਨਕਾਰਾਤਮਕ ਨੰਬਰ ਦਾ ਮਤਲਬ ਹੈ ਉਸ ਸਬ-ਏਜੰਟ ਨੇ ਲਿਆਉਣ ਤੋਂ ਵੱਧ ਅਦਾ ਕੀਤਾ।',
  'agent.dashboard.accordions.activity.title': 'ਡਾਊਨਲਾਈਨ ਗਤੀਵਿਧੀ ਫੀਡ',
  'agent.dashboard.accordions.activity.helper': 'ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਵਿੱਚ ਹਰ ਮੈਚ ਸੈਟਲਮੈਂਟ, ਰਿਚਾਰਜ ਅਤੇ ਰੇਟ ਬਦਲਾਅ — ਫੋਰੈਂਸਿਕ ਟਾਈਮਲਾਈਨ।',
  'agent.dashboard.accordions.earnings.title': 'ਕਮਾਈ ਬ੍ਰੇਕਡਾਊਨ (ਲਾਈਫਟਾਈਮ)',
  'agent.dashboard.accordions.earnings.helper': 'ਕਿਸਮ ਅਨੁਸਾਰ ਲਾਈਫਟਾਈਮ ਜੋੜ। ਉੱਪਰਲੇ ਰੇਂਜ ਟੌਗਲ ਨੂੰ ਅਣਡਿੱਠ ਕਰਦਾ ਹੈ — ਇਹ ਟਾਈਮ ਫਿਲਟਰ ਨਾਲ ਨਹੀਂ ਬਦਲਦੇ।',
  'agent.dashboard.accordions.directSubAgents.title': 'ਸਿੱਧੇ ਸਬ-ਏਜੰਟ',
  'agent.dashboard.accordions.directSubAgents.helper': 'ਤੁਹਾਡੇ ਸਿੱਧੇ ਸਹਾਇਕ। ਹਰ ਇੱਕ ਆਪਣੇ ਪਲੇਅਰ + ਆਪਣੇ ਸਬ-ਏਜੰਟ ਚਲਾਉਂਦਾ ਹੈ। ਗੁਲਾਬੀ/ਅੰਬਰ ਹਾਈਲਾਈਟ ਕਤਾਰਾਂ ਡਿਫਾਲਟ ਖੇਤਰ ਵਿੱਚ ਹਨ।',
  'agent.dashboard.accordions.chain.title': 'ਚੇਨ ਵਿਜ਼ੀਬਿਲਟੀ',
  'agent.dashboard.accordions.chain.helper': 'ਤੁਹਾਡੀ ਕੁੱਲ ਡਾਊਨਲਾਈਨ ਫੁੱਟਪ੍ਰਿੰਟ, ਸਿੱਧੇ (ਤੁਹਾਡੇ ਦੁਆਰਾ ਸੋਧਯੋਗ) ਅਤੇ ਅਸਿੱਧੇ (ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਦੁਆਰਾ ਪ੍ਰਬੰਧਿਤ) ਵਿੱਚ ਵੰਡੀ ਹੋਈ।',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'ਲਾਈਵ ਡਾਟਾ — ਫੋਕਸ ਉੱਤੇ, ਹਰ 60 ਸਕਿੰਟ ਤੇ, ਅਤੇ ਜਦੋਂ ਪੈਸੇ ਚੱਲਣ ਉਦੋਂ ਰਿਫ੍ਰੈਸ਼ ਹੁੰਦਾ ਹੈ।',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'ਮੇਰਾ ਪੂਲ ਬੈਲੇਂਸ',
  'agent.dashboard.cards.poolBalance.helper': 'ਤੁਹਾਡਾ ਖਰਚਣਯੋਗ ਬੈਲੇਂਸ। ਪਲੇਅਰਾਂ ਨੂੰ ਰਿਚਾਰਜ ਕਰਨ ਜਾਂ ਸਬ-ਏਜੰਟਾਂ ਨੂੰ ਭੇਜਣ ਲਈ ਪੈਸੇ।',
  'agent.dashboard.cards.poolBalance.netInRange': '{range} ਵਿੱਚ ਨੈੱਟ {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'ਡਿਪਾਜ਼ਿਟ ਸਥਿਤੀ',
  'agent.dashboard.cards.depositStatus.helperLoading': 'ਡਿਪਾਜ਼ਿਟ ਸਥਿਤੀ ਲੋਡ ਹੋ ਰਹੀ ਹੈ…',
  'agent.dashboard.cards.depositStatus.helper': 'ਤੁਹਾਡੇ ਪੂਲ ਵਿਰੁੱਧ ਜ਼ਮਾਨਤ। ਟਾਰਗੇਟ ਦੇ 50% ਉੱਤੇ ਸਥਿਤੀ ਸਾਵਧਾਨ ਵਿੱਚ, 80% ਉੱਤੇ ਚੇਤਾਵਨੀ, 100% ਉੱਤੇ ਉਲੰਘਣਾ।',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} ਤੋਂ',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'ਓਪਰੇਟਿੰਗ ਬੈਲੇਂਸ',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'ਡਿਪਾਜ਼ਿਟ ਬੈਲੇਂਸ',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'ਡਿਪਾਜ਼ਿਟ ਟਾਰਗੇਟ',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'ਨਕਾਰਾਤਮਕ ਐਕਸਪੋਜ਼ਰ',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'ਕਵਰੇਜ ਅਨੁਪਾਤ',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'ਨਕਾਰਾਤਮਕ ਐਕਸਪੋਜ਼ਰ ਦੁਆਰਾ ਖਪਤ ਕੀਤੇ ਡਿਪਾਜ਼ਿਟ ਟਾਰਗੇਟ ਦਾ',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'ਸੁਰੱਖਿਅਤ',
  'agent.dashboard.depositStatus.status.caution': 'ਸਾਵਧਾਨ',
  'agent.dashboard.depositStatus.status.warning': 'ਚੇਤਾਵਨੀ',
  'agent.dashboard.depositStatus.status.breach': 'ਉਲੰਘਣਾ',
  'agent.dashboard.depositStatus.status.suspended': 'ਮੁਅੱਤਲ',
  'agent.dashboard.depositStatus.status.healthy': 'ਤੰਦਰੁਸਤ',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'ਪਲੇਅਰਾਂ ਦਾ ਵਾਲਿਟ ਕੁੱਲ',
  'agent.dashboard.cards.playersWallet.helper': 'ਇਸ ਵੇਲੇ ਤੁਹਾਡੇ ਹਰ ਪਲੇਅਰ ਦੇ ਵਾਲਿਟ ਦਾ ਜੋੜ ਲਾਈਵ ਬੈਲੇਂਸ। ਤੁਹਾਡੇ ਨਾਲ ਜੁੜੀ ਹਰ wallet_points ਕਤਾਰ ਦਾ ਜੋੜ।',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} ਪਲੇਅਰ · {active} ਸਰਗਰਮ',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} ਪਲੇਅਰ · {active} ਸਰਗਰਮ',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'ਸਬ-ਏਜੰਟ ਵਾਲਿਟ + ਡਿਪਾਜ਼ਿਟ',
  'agent.dashboard.cards.subAgents.helper': 'ਤੁਹਾਡੇ ਸਿੱਧੇ ਸਬ-ਏਜੰਟਾਂ ਦੁਆਰਾ ਰੱਖਿਆ ਜੋੜ ਪੂਲ ਬੈਲੇਂਸ ਅਤੇ ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ। ਹਰ ਸਬ-ਏਜੰਟ ਆਪਣੇ ਪਲੇਅਰ + ਆਪਣੀ ਡਾਊਨਲਾਈਨ ਚਲਾਉਂਦਾ ਹੈ।',
  'agent.dashboard.cards.subAgents.field.pool': 'ਪੂਲ',
  'agent.dashboard.cards.subAgents.field.deposit': 'ਡਿਪਾਜ਼ਿਟ',
  'agent.dashboard.cards.subAgents.foot.one': '{n} ਸਿੱਧਾ ਸਬ-ਏਜੰਟ',
  'agent.dashboard.cards.subAgents.foot.other': '{n} ਸਿੱਧੇ ਸਬ-ਏਜੰਟ',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'ਮੇਰੀਆਂ ਬਕਾਇਆ ਬੇਨਤੀਆਂ',
  'agent.dashboard.cards.myPending.helper': 'ਪ੍ਰਾਥਮਿਕਤਾ 3। ਐਡਮਿਨ ਨੂੰ ਤੁਹਾਡੀਆਂ ਆਪਣੀਆਂ ਸਬਮਿਸ਼ਨਾਂ — ਤੁਸੀਂ ਖੁਦ ਕਾਰਵਾਈ ਨਹੀਂ ਕਰ ਸਕਦੇ, ਉਨ੍ਹਾਂ ਦੀ ਉਡੀਕ ਹੋ ਰਹੀ ਹੈ।',
  'agent.dashboard.cards.myPending.recharge.one': '{n} ਰਿਚਾਰਜ ਐਡਮਿਨ ਦੀ ਉਡੀਕ ਵਿੱਚ',
  'agent.dashboard.cards.myPending.recharge.other': '{n} ਰਿਚਾਰਜ ਐਡਮਿਨ ਦੀ ਉਡੀਕ ਵਿੱਚ',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} ਨਿਕਾਸੀ ਐਡਮਿਨ ਦੀ ਉਡੀਕ ਵਿੱਚ',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} ਨਿਕਾਸੀਆਂ ਐਡਮਿਨ ਦੀ ਉਡੀਕ ਵਿੱਚ',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'ਪਲੇਅਰਾਂ ਦੀਆਂ ਬਕਾਇਆ ਬੇਨਤੀਆਂ',
  'agent.dashboard.cards.playersPending.helper': 'ਪ੍ਰਾਥਮਿਕਤਾ 1। ਤੁਹਾਡੇ ਪਲੇਅਰ ਇਹਨਾਂ ਨੂੰ ਮਨਜ਼ੂਰ ਕਰਨ ਜਾਂ ਅਦਾ ਕਰਨ ਲਈ ਤੁਹਾਡੀ ਉਡੀਕ ਕਰ ਰਹੇ ਹਨ। ਕਾਰਵਾਈ ਲਈ ਟੈਪ ਕਰੋ।',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ ਕਰਨ ਲਈ',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ ਕਰਨ ਲਈ',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} ਨਿਕਾਸੀ ਅਦਾ ਕਰਨ ਲਈ',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} ਨਿਕਾਸੀਆਂ ਅਦਾ ਕਰਨ ਲਈ',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'ਸਬ-ਏਜੰਟਾਂ ਦੀਆਂ ਬਕਾਇਆ ਬੇਨਤੀਆਂ',
  'agent.dashboard.cards.subAgentsPending.helper': 'ਪ੍ਰਾਥਮਿਕਤਾ 2। ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟ (ਪੂਰੀ ਡਾਊਨਲਾਈਨ) ਮਨਜ਼ੂਰੀ ਜਾਂ ਅਦਾਇਗੀ ਲਈ ਤੁਹਾਡੀ ਉਡੀਕ ਕਰ ਰਹੇ ਹਨ। ਕਾਰਵਾਈ ਲਈ ਟੈਪ ਕਰੋ।',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'ਅਸਲ ਨੈੱਟ P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'ਇਸ ਰੇਂਜ ਵਿੱਚ ਤੁਹਾਡਾ ਅਸਲੀ ਨਫ਼ਾ/ਨੁਕਸਾਨ। ਪਲੇਟਫਾਰਮ ਫੀਸ ਸ਼ੇਅਰ + ਜਿੱਤ/ਹਾਰ ਸ਼ੇਅਰ − ਆਫ਼ਰ ਸਹਿ-ਫੰਡਿੰਗ। ਪ੍ਰਤੀ-ਗੇਮ ਵੇਰਵਿਆਂ ਲਈ ਹੇਠਾਂ P&L ਡੀਪ-ਡਾਈਵ ਫੈਲਾਓ।',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'ਪਲੈਟ',
  'agent.dashboard.pillarChip.winLoss': 'W/L',
  'agent.dashboard.pillarChip.offer': 'ਆਫ਼ਰ',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'ਆਫ਼ਰ ਉਪਯੋਗਤਾ · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'ਇਸ ਰੇਂਜ ਵਿੱਚ ਆਫ਼ਰਾਂ ਰਾਹੀਂ ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਨੂੰ ਦਿੱਤੇ ਕੁੱਲ ਇਨਾਮ ₹। ਉਸ ਲਾਗਤ ਵਿੱਚ ਆਪਣਾ ਹਿੱਸਾ ਦੇਖਣ ਲਈ ਆਪਣੇ ਐਕਸਪੋਜ਼ਰ ਪੰਨੇ ਨਾਲ ਮੇਲ ਕਰੋ।',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} ਕਲੇਮ ਜਮ੍ਹਾਂ ਹੋਇਆ',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} ਕਲੇਮ ਜਮ੍ਹਾਂ ਹੋਏ',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'ਟੌਪ ਖਰਚਣ ਵਾਲੇ',
  'agent.dashboard.activity.topSpenders.helper': '{range} ਵਿੱਚ ਤੁਹਾਡੇ ਸਭ ਤੋਂ ਵੱਧ ਖਰਚਣ ਵਾਲੇ ਪਲੇਅਰ।',
  'agent.dashboard.activity.topSpenders.empty': 'ਇਸ ਰੇਂਜ ਵਿੱਚ ਅਜੇ ਕੋਈ ਖਰਚ ਨਹੀਂ।',
  'agent.dashboard.activity.recent.title': 'ਹਾਲੀਆ ਗਤੀਵਿਧੀ',
  'agent.dashboard.activity.recent.helper': 'ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਤੋਂ ਪੰਜ ਸਭ ਤੋਂ ਨਵੀਆਂ ਰਿਚਾਰਜ ਘਟਨਾਵਾਂ।',
  'agent.dashboard.activity.recent.empty': 'ਕੋਈ ਹਾਲੀਆ ਗਤੀਵਿਧੀ ਨਹੀਂ।',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'ਮਨਜ਼ੂਰ',
  'agent.dashboard.activity.recent.state.rejected': 'ਰੱਦ',
  'agent.dashboard.activity.recent.state.pending': 'ਬਕਾਇਆ',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'ਪ੍ਰਤੀ ਗੇਮ · ਵਿਆਖਿਆ ਲਈ ਕਤਾਰ ਉੱਤੇ ਟੈਪ ਕਰੋ',
  'agent.dashboard.pnl.noEarnings': 'ਇਸ ਰੇਂਜ ਵਿੱਚ ਕੋਈ ਕਮਾਈ ਨਹੀਂ।',
  'agent.dashboard.pnl.table.col.pillarGame': 'ਪਿੱਲਰ / ਗੇਮ',
  'agent.dashboard.pnl.table.col.net': 'ਨੈੱਟ',
  'agent.dashboard.pnl.table.col.matches': 'ਮੈਚ',
  'agent.dashboard.pnl.ledgerRows.one': '{n} ਲੈਜਰ ਕਤਾਰ',
  'agent.dashboard.pnl.ledgerRows.other': '{n} ਲੈਜਰ ਕਤਾਰਾਂ',
  'agent.dashboard.pnl.pillar.platformFee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ',
  'agent.dashboard.pnl.pillar.winLoss': 'ਜਿੱਤ / ਹਾਰ',
  'agent.dashboard.pnl.pillar.offerReward': 'ਆਫ਼ਰ ਇਨਾਮ',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'ਵਿੰਡੋ: {range} — ਡੈਸ਼ਬੋਰਡ ਦੀ ਰੇਂਜ ਦਾ ਸਖਤ ਉਪ-ਸਮੂਹ',
  'agent.dashboard.pnlDrill.loading': 'ਮੈਚ-ਪੱਧਰ ਵੇਰਵੇ ਲੋਡ ਹੋ ਰਹੇ ਹਨ…',
  'agent.dashboard.pnlDrill.windowTotal': 'ਵਿੰਡੋ ਕੁੱਲ:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} ਮੈਚ ਵਿੱਚ',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} ਮੈਚਾਂ ਵਿੱਚ',
  'agent.dashboard.pnlDrill.noContribution': 'ਕਿਸੇ ਮੈਚ ਨੇ ਇਸ ਸੈੱਲ ਵਿੱਚ ਯੋਗਦਾਨ ਨਹੀਂ ਪਾਇਆ।',
  'agent.dashboard.pnlDrill.errorFallback': 'ਡ੍ਰਿਲਡਾਊਨ ਲੋਡ ਨਹੀਂ ਹੋ ਸਕਿਆ',
  'agent.dashboard.pnlDrill.table.col.match': 'ਮੈਚ',
  'agent.dashboard.pnlDrill.table.col.role': 'ਭੂਮਿਕਾ',
  'agent.dashboard.pnlDrill.table.col.base': 'ਬੇਸ',
  'agent.dashboard.pnlDrill.table.col.rate': 'ਰੇਟ',
  'agent.dashboard.pnlDrill.table.col.earned': 'ਕਮਾਇਆ',
  'agent.dashboard.pnlDrill.table.col.settled': 'ਨਿਪਟਾਇਆ',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.dashboard.directDownline.empty': 'ਅਜੇ ਕੋਈ ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਨਹੀਂ।',
  'agent.dashboard.directDownline.row.player.one': '{n} ਪਲੇਅਰ',
  'agent.dashboard.directDownline.row.player.other': '{n} ਪਲੇਅਰ',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} ਸਬ-ਏਜੰਟ',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} ਸਬ-ਏਜੰਟ',
  'agent.dashboard.directDownline.row.walletDeposit': 'ਵਾਲਿਟ {wallet} · ਡਿਪਾਜ਼ਿਟ {deposit}',
  'agent.dashboard.directDownline.footer': 'ਅਸਲ ਨੈੱਟ {range} ਵਿੰਡੋ ਲਈ ਹੈ।',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.dashboard.chain.directAgents.title': 'ਸਿੱਧੇ ਏਜੰਟ',
  'agent.dashboard.chain.directAgents.subtitle': 'ਤੁਹਾਡੇ ਸਿੱਧੇ ਸਬ-ਏਜੰਟ — ਤੁਸੀਂ ਉਨ੍ਹਾਂ ਦੀਆਂ ਬੇਨਤੀਆਂ ਉੱਤੇ ਕਾਰਵਾਈ ਕਰ ਸਕਦੇ ਹੋ।',
  'agent.dashboard.chain.indirectAgents.title': 'ਅਸਿੱਧੇ ਏਜੰਟ',
  'agent.dashboard.chain.indirectAgents.subtitle': 'ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਦੁਆਰਾ ਪ੍ਰਬੰਧਿਤ। ਤੁਹਾਡੇ ਲਈ ਸਿਰਫ਼-ਪੜ੍ਹਨਯੋਗ।',
  'agent.dashboard.chain.directPlayers.title': 'ਸਿੱਧੇ ਪਲੇਅਰ',
  'agent.dashboard.chain.directPlayers.subtitle': 'ਤੁਹਾਡੇ ਪਲੇਅਰ — ਤੁਸੀਂ ਉਨ੍ਹਾਂ ਦੇ ਰਿਚਾਰਜ ਅਤੇ ਨਿਕਾਸੀਆਂ ਉੱਤੇ ਕਾਰਵਾਈ ਕਰਦੇ ਹੋ।',
  'agent.dashboard.chain.indirectPlayers.title': 'ਅਸਿੱਧੇ ਪਲੇਅਰ',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਦੇ ਪਲੇਅਰ। ਤੁਹਾਡੇ ਲਈ ਸਿਰਫ਼-ਪੜ੍ਹਨਯੋਗ।',
  'agent.dashboard.chain.field.walletTotal': 'ਵਾਲਿਟ ਕੁੱਲ',
  'agent.dashboard.chain.field.depositTotal': 'ਡਿਪਾਜ਼ਿਟ ਕੁੱਲ',
  'agent.dashboard.chain.field.avgPerHead': 'ਔਸਤ ਪ੍ਰਤੀ ਜੀਅ',
  'agent.dashboard.chain.field.pendingRecharge': 'ਬਕਾਇਆ ਰਿਚਾਰਜ',
  'agent.dashboard.chain.field.pendingWithdrawal': 'ਬਕਾਇਆ ਨਿਕਾਸੀ',
  'agent.dashboard.chain.pendingBadge': '{n} ਬਕਾਇਆ',
  'agent.dashboard.chain.openInbox': 'ਇਨਬਾਕਸ ਖੋਲ੍ਹੋ',
  'agent.dashboard.chain.footer': 'ਸਿੱਧੇ = ਤੁਸੀਂ ਖੁਦ ਪ੍ਰਬੰਧਿਤ ਕਰਦੇ ਹੋ ਉਹ ਗਿਣਤੀ। ਅਸਿੱਧੇ = ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਦੁਆਰਾ ਪ੍ਰਬੰਧਿਤ (ਤੁਸੀਂ ਨੰਬਰ ਦੇਖਦੇ ਹੋ ਪਰ ਉਨ੍ਹਾਂ ਦੀਆਂ ਬਕਾਇਆ ਬੇਨਤੀਆਂ ਉੱਤੇ ਕਾਰਵਾਈ ਨਹੀਂ ਕਰ ਸਕਦੇ — ਉਹ ਉਨ੍ਹਾਂ ਦੇ ਪੇਰੈਂਟ ਨੂੰ ਜਾਂਦੀਆਂ ਹਨ)।',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'ਲੋਡ ਕਰਨ ਵਿੱਚ ਅਸਫਲ',
  'agent.activity.filter.window': 'ਵਿੰਡੋ',
  'agent.activity.filter.kind': 'ਕਿਸਮ',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'ਸਾਰੇ',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'ਸਾਰੇ',
  'agent.activity.kind.matches': 'ਮੈਚ',
  'agent.activity.kind.recharges': 'ਰਿਚਾਰਜ',
  'agent.activity.kind.rateChanges': 'ਰੇਟ ਬਦਲਾਅ',
  // Empty + loading states.
  'agent.activity.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.activity.empty.title': 'ਸ਼ਾਂਤ',
  'agent.activity.empty.subtitle': '{window} ਵਿੰਡੋ ਵਿੱਚ ਕੋਈ {kindWord}ਘਟਨਾਵਾਂ ਨਹੀਂ।',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'ਮੈਚ ',
  'agent.activity.empty.kindWord.recharge': 'ਰਿਚਾਰਜ ',
  'agent.activity.empty.kindWord.partnership_change': 'ਪਾਰਟਨਰਸ਼ਿਪ ਬਦਲਾਅ ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'ਸੈਟਲਮੈਂਟ',
  'agent.activity.row.earned': 'ਕਮਾਇਆ',
  'agent.activity.row.fromPillarOn': '{pillar} ਤੋਂ ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ',
  'agent.activity.row.pillar.winLoss': 'ਜਿੱਤ/ਹਾਰ',
  'agent.activity.row.pillar.offerReward': 'ਆਫ਼ਰ ਇਨਾਮ',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'ਰਿਚਾਰਜ',
  'agent.activity.row.recharge.of': 'ਦਾ',
  'agent.activity.row.recharge.byUser': 'ਯੂਜ਼ਰ #{id} ਵੱਲੋਂ',
  'agent.activity.row.recharge.via': ' ਰਾਹੀਂ ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'ਮਨਜ਼ੂਰ',
  'agent.activity.row.state.rejected': 'ਰੱਦ',
  'agent.activity.row.state.pending': 'ਬਕਾਇਆ',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'ਰੇਟ ਬਦਲਾਅ',
  'agent.activity.row.rateChange.dashOn': '— {pillar} ਉੱਤੇ ',
  'agent.activity.row.rateChange.cascade': 'ਕੈਸਕੇਡ',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'ਇਹ ਦ੍ਰਿਸ਼ ਸਿਰਫ਼ ਏਜੰਟਾਂ ਲਈ ਹੈ।',
  'agent.earnings.stat.poolBalance': 'ਪੂਲ ਬੈਲੇਂਸ',
  'agent.earnings.stat.totalIn': 'ਕੁੱਲ ਇਨ',
  'agent.earnings.stat.totalOut': 'ਕੁੱਲ ਆਉਟ',
  'agent.earnings.section.in': 'ਕਮਾਈ (ਇਨ)',
  'agent.earnings.section.out': 'ਨਿਕਾਸ (ਆਉਟ)',
  'agent.earnings.empty.in': 'ਅਜੇ ਕੋਈ ਕਮਾਈ ਨਹੀਂ। ਜਦੋਂ ਤੁਹਾਡੇ ਪਲੇਅਰ ਰਿਚਾਰਜ ਕਰਨ ਜਾਂ ਖੇਡਣ, ਕੱਟ ਇੱਥੇ ਦਿਸਣਗੇ।',
  'agent.earnings.empty.out': 'ਅਜੇ ਕੋਈ ਨਿਕਾਸ ਨਹੀਂ।',
  'agent.earnings.viewLedger': 'ਪੂਰਾ ਲੈਜਰ ਦੇਖੋ',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'ਐਡਮਿਨ ਤੋਂ ਟੌਪਅੱਪ',
  'agent.earnings.kind.topupFromAdmin.hint': 'ਐਡਮਿਨ ਨੇ ਤੁਹਾਡੇ ਪੂਲ ਵਿੱਚ ਕ੍ਰੈਡਿਟ ਕੀਤਾ',
  'agent.earnings.kind.topupToPlayer.label': 'ਪਲੇਅਰ ਨੂੰ ਟੌਪਅੱਪ',
  'agent.earnings.kind.topupToPlayer.hint': 'ਤੁਸੀਂ ਪਲੇਅਰ ਰਿਚਾਰਜ ਕੀਤਾ',
  'agent.earnings.kind.topupFromPlayer.label': 'ਪਲੇਅਰ ਰਿਚਾਰਜ',
  'agent.earnings.kind.topupFromPlayer.hint': 'ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਨੇ ਤੁਹਾਡੇ ਪੂਲ ਵਿੱਚ ਅਦਾ ਕੀਤਾ',
  'agent.earnings.kind.revenueShare.label': 'ਰੈਵੇਨਿਊ ਸ਼ੇਅਰ',
  'agent.earnings.kind.revenueShare.hint': 'ਮੈਚਾਂ ਦੀ ਪਲੇਟਫਾਰਮ ਫੀਸ ਵਿੱਚੋਂ ਹਿੱਸਾ',
  'agent.earnings.kind.coFundOffer.label': 'ਕੋ-ਫੰਡ ਆਫ਼ਰ',
  'agent.earnings.kind.coFundOffer.hint': 'ਆਫ਼ਰ ਪੇਆਉਟ ਵਿੱਚ ਤੁਹਾਡਾ ਹਿੱਸਾ',
  'agent.earnings.kind.adminAdjust.label': 'ਐਡਮਿਨ ਐਡਜਸਟਮੈਂਟ',
  'agent.earnings.kind.adminAdjust.hint': 'ਹੱਥੀਂ ਸੁਧਾਰ',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'ਐਂਟਰੀ',
  'agent.earnings.row.entry.other': 'ਐਂਟਰੀਆਂ',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'ਵਿੰਡੋ',
  'agent.leaderboard.filter.pillar': 'ਪਿੱਲਰ',
  'agent.leaderboard.window.all': 'ਸਾਰੇ',
  'agent.leaderboard.pillar.all': 'ਸਾਰੇ ਪਿੱਲਰ',
  'agent.leaderboard.pillar.platformFee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ',
  'agent.leaderboard.pillar.winLoss': 'ਜਿੱਤ / ਹਾਰ',
  'agent.leaderboard.pillar.offerReward': 'ਆਫ਼ਰ ਇਨਾਮ',
  'agent.leaderboard.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.leaderboard.empty.title': 'ਅਜੇ ਕੋਈ ਕਮਾਈ ਨਹੀਂ',
  'agent.leaderboard.empty.subtitle.admin': '{window} ਵਿੰਡੋ ਵਿੱਚ ਸਾਰੇ ਏਜੰਟਾਂ ਵਿੱਚ ਕੋਈ ਸੈਟਲਮੈਂਟ ਗਤੀਵਿਧੀ ਨਹੀਂ।',
  'agent.leaderboard.empty.subtitle.downline': '{window} ਵਿੰਡੋ ਵਿੱਚ ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਤੋਂ ਕੋਈ ਕਮਾਈ ਨਹੀਂ। ਜਿਉਂ-ਜਿਉਂ ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟ ਮੈਚ ਚਲਾਉਣਗੇ, ਇਹ ਬੋਰਡ ਭਰੇਗਾ।',
  'agent.leaderboard.row.matches.one': '{n} ਮੈਚ',
  'agent.leaderboard.row.matches.other': '{n} ਮੈਚ',
  'agent.leaderboard.footer': 'ਕਮਾਈ match_settlement_snapshots ਤੋਂ ਆਉਂਦੀ ਹੈ। ਨਕਾਰਾਤਮਕ ਨੰਬਰ ਦਾ ਮਤਲਬ ਹੈ ਉਸ ਏਜੰਟ ਨੇ ਵਿੰਡੋ ਵਿੱਚ ਲਿਆਉਣ ਤੋਂ ਵੱਧ ਅਦਾ ਕੀਤਾ।',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'ਪਲੈਟ',
  'agent.leaderboard.chip.winLoss': 'W/L',
  'agent.leaderboard.chip.offer': 'ਆਫ਼ਰ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'ਮਾਰਜਿਨ ਚੇਤਾਵਨੀ · 80% ਥ੍ਰੈਸ਼ਹੋਲਡ ਪਹੁੰਚਿਆ',
  'agent.warning.header.title': 'ਇੱਕ ਮਾੜੇ ਸੈਟਲਮੈਂਟ ਨਾਲ ਲੌਕਆਉਟ।',
  'agent.warning.header.body': 'ਤੁਸੀਂ ਅਜੇ ਲੌਕ ਨਹੀਂ ਹੋ — ਪਰ ਤੁਹਾਡਾ ਡਿਪਾਜ਼ਿਟ ਸੁਰੱਖਿਆ ਗੱਦਾ 80% ਤੋਂ ਵੱਧ ਖਰਚ ਹੋ ਚੁੱਕਾ ਹੈ। ਹੁਣੇ ਟੌਪ-ਅੱਪ ਕਰੋ ਅਤੇ ਕੰਟਰੋਲ ਵਿੱਚ ਰਹੋ।',
  'agent.warning.header.dismissAria': 'ਖਾਰਜ ਕਰੋ',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'ਇਹ ਕਿਉਂ ਦਿਸ ਰਿਹਾ ਹੈ',
  'agent.warning.why.body': 'ਹਾਲੀਆ ਗੇਮ ਸੈਟਲਮੈਂਟਾਂ ਨੇ ਤੁਹਾਡੇ ਵਾਲਿਟ ਨੂੰ ਨਕਾਰਾਤਮਕ ਵਿੱਚ ਧੱਕ ਦਿੱਤਾ। {deposit} ਦੀ ਤੁਹਾਡੀ ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ ਉਹ ਸੁਰੱਖਿਆ ਜਾਲ ਹੈ ਜੋ ਇਸ ਨੂੰ ਸੋਖਦੀ ਹੈ — ਅਤੇ ਹੁਣ ਇਹ 80% ਤੋਂ ਵੱਧ ਖਰਚ ਹੋ ਚੁੱਕੀ ਹੈ। ਜੇ ਥੋੜ੍ਹਾ ਹੋਰ ਨਕਾਰਾਤਮਕ ਆਇਆ, ਤਾਂ ਤੁਹਾਡਾ ਵਾਲਿਟ + ਡਿਪਾਜ਼ਿਟ ਜ਼ੀਰੋ ਉੱਤੇ ਆ ਜਾਵੇਗਾ ਅਤੇ ਪਲੇਟਫਾਰਮ ਖਾਤਾ ਲੌਕ ਕਰ ਦੇਵੇਗਾ।',
  'agent.warning.why.reassurance': 'ਇਹ ਚੇਤਾਵਨੀ ਜਾਣ-ਬੁੱਝ ਕੇ ਜਲਦੀ ਫਾਇਰ ਹੁੰਦੀ ਹੈ — ਜਦੋਂ ਤੁਸੀਂ ਅਜੇ ਪੂਰੀ ਐਪ ਵਰਤ ਸਕਦੇ ਹੋ। ਲੌਕਆਉਟ ਸਕ੍ਰੀਨ ਸਿਰਫ਼ ਉਦੋਂ ਆਉਂਦੀ ਹੈ ਜਦੋਂ ਤੁਸੀਂ 100% ਪਾਰ ਕਰਦੇ ਹੋ, ਅਤੇ ਇੱਕ ਵਾਰ ਅਜਿਹਾ ਹੋਣ ਉੱਤੇ, ਜਦ ਤੱਕ ਤੁਸੀਂ ਰਿਕਵਰ ਨਹੀਂ ਹੁੰਦੇ ਜ਼ਿਆਦਾਤਰ ਫੀਚਰ ਬਲਾਕ ਹੋ ਜਾਂਦੇ ਹਨ। ਹੁਣੇ ਕਾਰਵਾਈ ਕਰਨੀ ਬਾਅਦ ਵਿੱਚ ਕਰਨ ਨਾਲੋਂ ਕਿਤੇ ਘੱਟ ਵਿਘਨ ਪਾਉਂਦੀ ਹੈ।',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'ਲੌਕਆਉਟ ਤੋਂ ਪਹਿਲਾਂ ਜਗ੍ਹਾ',
  'agent.warning.gauge.suffix': 'ਜਗ੍ਹਾ ਬਾਕੀ',
  'agent.warning.gauge.dangerBadge': 'ਖ਼ਤਰਾ ਖੇਤਰ ਵਿੱਚ {pct}%',
  'agent.warning.gauge.field.wallet': 'ਤੁਹਾਡਾ ਵਾਲਿਟ',
  'agent.warning.gauge.field.deposit': 'ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'ਜੇ ਤੁਸੀਂ ਇਸ ਨੂੰ ਅਣਡਿੱਠ ਕਰਕੇ 100% ਉੱਤੇ ਪਹੁੰਚੇ',
  'agent.warning.ignore.cardTitle': 'ਤੁਸੀਂ ਤੁਰੰਤ ਗੁਆ ਬੈਠੋਗੇ',
  'agent.warning.ignore.item.dashboard': 'ਤੁਹਾਡੇ ਡੈਸ਼ਬੋਰਡ, P&L ਅਤੇ ਲੀਡਰਬੋਰਡਾਂ ਤੱਕ ਪਹੁੰਚ',
  'agent.warning.ignore.item.pages': 'ਪਲੇਅਰ ਅਤੇ ਏਜੰਟ ਪੰਨੇ',
  'agent.warning.ignore.item.manualTopup': 'ਤੁਹਾਡੇ ਪੂਲ ਤੋਂ ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਨੂੰ ਹੱਥੀਂ ਟੌਪ-ਅੱਪ',
  'agent.warning.ignore.item.newWithdrawal': 'ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਨੂੰ ਨਵੀਆਂ ਨਿਕਾਸੀਆਂ ਭੇਜਣਾ',
  'agent.warning.ignore.item.subAgents': 'ਸਬ-ਏਜੰਟ ਜੋੜਨਾ ਜਾਂ ਪ੍ਰਬੰਧਿਤ ਕਰਨਾ',
  'agent.warning.ignore.item.profile': 'ਤੁਹਾਡੀ ਪ੍ਰੋਫਾਈਲ ਜਾਂ ਪੇਮੈਂਟ ਸੈਟਿੰਗਜ਼ ਸੋਧਣੀਆਂ',
  'agent.warning.ignore.footer': 'ਤੁਸੀਂ ਅਜੇ ਵੀ ਬਕਾਇਆ ਬੇਨਤੀਆਂ ਮਨਜ਼ੂਰ ਕਰ ਸਕੋਗੇ + ਐਮਰਜੈਂਸੀ ਟੌਪ-ਅੱਪ ਭੇਜ ਸਕੋਗੇ — ਪਰ ਜਦੋਂ ਤੱਕ ਤੁਹਾਡਾ ਵਾਲਿਟ + ਡਿਪਾਜ਼ਿਟ ਜ਼ੀਰੋ ਤੋਂ ਉੱਪਰ ਨਹੀਂ ਚੜ੍ਹਦਾ, ਬਾਕੀ ਸਭ ਕੁਝ ਬੰਦ ਰਹੇਗਾ।',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'ਰਿਕਵਰ ਕਰਨ ਲਈ ਹੁਣੇ ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਅਦਾ ਕਰੋ',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'ਮੈਂ ਇਹ ਬਾਅਦ ਵਿੱਚ ਦੇਖਾਂਗਾ',
  'agent.warning.dismiss.footnote': 'ਜੇ ਤੁਸੀਂ ਚੇਤਾਵਨੀ ਖੇਤਰ ਵਿੱਚ ਰਹੇ ਤਾਂ ਅਸੀਂ 10 ਮਿੰਟਾਂ ਵਿੱਚ ਮੁੜ ਯਾਦ ਕਰਾਵਾਂਗੇ। ਜਿਵੇਂ ਹੀ ਤੁਸੀਂ ਰਿਕਵਰ ਹੁੰਦੇ ਹੋ, ਆਪਣੇ ਆਪ ਬੰਦ ਹੋ ਜਾਂਦਾ ਹੈ।',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'ਖਾਤਾ ਲੌਕ · ਬੇਲਆਉਟ ਪੋਰਟਲ',
  'agent.lockout.header.title': 'ਤੁਹਾਡੇ ਉੱਤੇ ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਦੇ ਕਵਰ ਨਾਲੋਂ ਵੱਧ ਬਕਾਇਆ ਹੈ।',
  'agent.lockout.header.body': 'ਚਿੰਤਾ ਨਾ ਕਰੋ — ਇਹ ਰਿਕਵਰ ਹੋ ਸਕਦਾ ਹੈ। ਹੇਠਾਂ ਦਿੱਤੇ ਦੋ ਕਦਮ ਚੁੱਕੋ ਅਤੇ ਤੁਸੀਂ ਆਪਣੇ ਆਪ ਅਨਲੌਕ ਹੋ ਜਾਓਗੇ।',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'ਇਹ ਕਿਉਂ ਹੋਇਆ',
  'agent.lockout.why.body': 'ਗੇਮ ਸੈਟਲਮੈਂਟਾਂ ਨਾਲ ਤੁਹਾਡਾ ਵਾਲਿਟ ਜ਼ੀਰੋ ਤੋਂ ਹੇਠਾਂ ਚਲਾ ਗਿਆ।',
  'agent.lockout.why.reassurance': 'ਪਲੇਟਫਾਰਮ ਤੁਹਾਨੂੰ ਇਸ ਲਈ ਲੌਕ ਨਹੀਂ ਕਰਦਾ ਕਿ ਤੁਸੀਂ ਕੁਝ ਗ਼ਲਤ ਕੀਤਾ। ਇਹ ਤੁਹਾਨੂੰ ਇਸ ਲਈ ਲੌਕ ਕਰਦਾ ਹੈ ਤਾਂ ਜੋ ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਵਿਰੁੱਧ ਹੋਰ ਕਰਜ਼ ਨਾ ਜੁੜੇ ਜਦੋਂ ਤੁਸੀਂ ਸਥਿਰ ਹੁੰਦੇ ਹੋ।',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'ਸੁਰੱਖਿਅਤ ਰਿਕਵਰੀ ਟਾਰਗੇਟ',
  'agent.lockout.gauge.suffix': 'ਸੁਰੱਖਿਅਤ ਅਦਾ ਕਰਨ ਲਈ',
  'agent.lockout.gauge.recoveredBadge': '{pct}% ਰਿਕਵਰ ਹੋਇਆ',
  'agent.lockout.gauge.field.wallet': 'ਤੁਹਾਡਾ ਵਾਲਿਟ',
  'agent.lockout.gauge.field.deposit': 'ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'ਸਿਰਫ਼ ਅਨਲੌਕ',
  'agent.lockout.tier.justUnlock.tag': 'ਜੋਖਮ ਭਰਿਆ',
  'agent.lockout.tier.justUnlock.note': 'ਤੁਹਾਨੂੰ ₹0 ਉੱਤੇ ਵਾਪਸ ਲਿਆਉਂਦਾ ਹੈ — ਕੋਈ ਵੀ ਨਵਾਂ ਨੁਕਸਾਨ ਤੁਹਾਨੂੰ ਤੁਰੰਤ ਮੁੜ-ਲੌਕ ਕਰ ਦੇਵੇਗਾ।',
  'agent.lockout.tier.safeMin.label': 'ਸੁਰੱਖਿਅਤ ਘੱਟੋ-ਘੱਟ',
  'agent.lockout.tier.safeMin.tag': 'ਸਿਫ਼ਾਰਸ਼ੀ',
  'agent.lockout.tier.safeMin.note': 'ਡਿਪਾਜ਼ਿਟ ({deposit}) + ਤੁਹਾਡੇ ਨਕਾਰਾਤਮਕ ਵਾਲਿਟ ਦਾ ਅੱਧਾ। ਇੱਕ ਅਸਲ ਗੱਦਾ ਛੱਡਦਾ ਹੈ।',
  'agent.lockout.tier.fullRecovery.label': 'ਪੂਰੀ ਰਿਕਵਰੀ',
  'agent.lockout.tier.fullRecovery.tag': 'ਸਭ ਤੋਂ ਵਧੀਆ',
  'agent.lockout.tier.fullRecovery.note': 'ਡਿਪਾਜ਼ਿਟ ਪੂਰੀ ਤਰ੍ਹਾਂ ਬਹਾਲ ਅਤੇ ਵਾਲਿਟ ਜ਼ੀਰੋ ਉੱਤੇ ਵਾਪਸ।',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'ਰਿਕਵਰ ਕਰਨ ਦੇ ਦੋ ਰਾਹ — ਜੋ ਵੀ (ਜਾਂ ਦੋਵੇਂ) ਕਰੋ',
  'agent.lockout.waysOut.inbox.title': 'ਆਪਣੇ ਇਨਬਾਕਸ ਉੱਤੇ ਕਾਰਵਾਈ ਕਰੋ',
  'agent.lockout.waysOut.inbox.body': 'ਪਲੇਅਰ ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ ਕਰੋ (ਨਕਦ ਤੁਹਾਡੇ ਕੋਲ ਪਹਿਲਾਂ ਹੀ ਹੈ) ਅਤੇ ਪਲੇਅਰ ਨਿਕਾਸੀਆਂ ਨੂੰ ਪੇਡ ਮਾਰਕ ਕਰੋ। ਹਰ ਮਨਜ਼ੂਰੀ ਤੁਹਾਡੇ ਵਾਲਿਟ ਨੂੰ ਜਮ੍ਹਾਂ ਕਰਦੀ ਹੈ।',
  'agent.lockout.waysOut.inbox.ctaOpen': 'ਇਨਬਾਕਸ ਖੋਲ੍ਹੋ',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ਇਨਬਾਕਸ ਹੇਠਾਂ ਖੁੱਲ੍ਹਾ ਹੈ',
  'agent.lockout.waysOut.upline.title': 'ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਅਦਾ ਕਰੋ',
  'agent.lockout.waysOut.upline.body': 'ਆਪਣੇ ਪੇਰੈਂਟ ਏਜੰਟ (ਜਾਂ ਐਡਮਿਨ) ਨੂੰ UPI ਰਾਹੀਂ ਨਕਦ ਭੇਜੋ, ਫਿਰ txn id ਜਮ੍ਹਾਂ ਕਰੋ। ਉਨ੍ਹਾਂ ਦੀ ਮਨਜ਼ੂਰੀ ਮਿਲਣ ਉੱਤੇ ਤੁਹਾਡਾ ਵਾਲਿਟ ਤੁਰੰਤ ਜਮ੍ਹਾਂ ਹੋ ਜਾਂਦਾ ਹੈ।',
  'agent.lockout.waysOut.upline.ctaOpen': 'ਫਾਰਮ ਖੋਲ੍ਹੋ',
  'agent.lockout.waysOut.upline.ctaOpened': 'ਫਾਰਮ ਹੇਠਾਂ ਖੁੱਲ੍ਹਾ ਹੈ',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'ਤੁਸੀਂ ਕੀ ਕਰ ਸਕਦੇ ਹੋ (ਅਤੇ ਕੀ ਬਲਾਕ ਹੈ)',
  'agent.lockout.capabilities.canHeader': 'ਤੁਸੀਂ ਅਜੇ ਵੀ ਕਰ ਸਕਦੇ ਹੋ',
  'agent.lockout.capabilities.cantHeader': 'ਤੁਸੀਂ ਨਹੀਂ ਕਰ ਸਕਦੇ (ਅਸਥਾਈ ਤੌਰ ਉੱਤੇ)',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'ਆਉਣ ਵਾਲੇ ਪਲੇਅਰ ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ ਕਰਨੇ',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'ਪਲੇਅਰ ਨਿਕਾਸੀਆਂ ਨੂੰ ਪੇਡ ਮਾਰਕ ਕਰਨਾ',
  'agent.lockout.can.approveSubAgentRecharge': 'ਸਬ-ਏਜੰਟ ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ ਕਰਨੇ',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'ਸਬ-ਏਜੰਟ ਨਿਕਾਸੀਆਂ ਨੂੰ ਪੇਡ ਮਾਰਕ ਕਰਨਾ',
  'agent.lockout.can.submitToUpline': 'ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਰਿਚਾਰਜ ਭੇਜਣਾ',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE ਅਲਰਟ ਪ੍ਰਾਪਤ ਕਰਨੇ + ਸਾਈਨ ਆਉਟ',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'ਕਦਮ {n} · {title}',
  'agent.lockout.section.inbox.title': 'ਤੁਹਾਡਾ ਇਨਬਾਕਸ — ਹਰ ਮਨਜ਼ੂਰੀ ਤੁਹਾਡੇ ਵਾਲਿਟ ਨੂੰ ਜਮ੍ਹਾਂ ਕਰਦੀ ਹੈ',
  'agent.lockout.section.inbox.subtitle': 'ਪਲੇਅਰਾਂ ਅਤੇ ਸਬ-ਏਜੰਟਾਂ ਤੋਂ ਬਕਾਇਆ ਪੇਮੈਂਟ ਬੇਨਤੀਆਂ ਜੋ ਤੁਹਾਡੀ ਕਾਰਵਾਈ ਦੀ ਉਡੀਕ ਵਿੱਚ ਹਨ।',
  'agent.lockout.section.inbox.fromPlayers': 'ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਵੱਲੋਂ',
  'agent.lockout.section.inbox.fromSubAgents': 'ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਵੱਲੋਂ',
  'agent.lockout.section.upline.title': 'ਆਪਣਾ ਵਾਲਿਟ ਟੌਪ-ਅੱਪ ਕਰਨ ਲਈ ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਅਦਾ ਕਰੋ',
  'agent.lockout.section.upline.subtitle': 'ਜਦੋਂ ਪੇਰੈਂਟ ਦੀ UPI ਤੁਹਾਡੇ ਸਾਹਮਣੇ ਹੈ ਤਾਂ ਛੇਤੀ। ਮਨਜ਼ੂਰੀ ਆਮ ਤੌਰ ਉੱਤੇ ਕੁਝ ਮਿੰਟਾਂ ਵਿੱਚ ਮਿਲ ਜਾਂਦੀ ਹੈ।',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'ਰਿਚਾਰਜ · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · {when} ਨੂੰ ਜਮ੍ਹਾਂ',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · {when} ਨੂੰ ਜਮ੍ਹਾਂ · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'ਨਿਕਾਸੀ · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi} ਨੂੰ ਅਦਾ ਕਰੋ · {when} ਨੂੰ ਜਮ੍ਹਾਂ',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi} ਨੂੰ ਅਦਾ ਕਰੋ · {whatsapp} · {when} ਨੂੰ ਜਮ੍ਹਾਂ',
  'agent.lockout.queue.subAgent.recharge.title': 'ਸਬ-ਏਜੰਟ ਰਿਚਾਰਜ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · {when} ਨੂੰ ਜਮ੍ਹਾਂ',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · {when} ਨੂੰ ਜਮ੍ਹਾਂ · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'ਸਬ-ਏਜੰਟ ਨਿਕਾਸੀ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi} ਨੂੰ ਅਦਾ ਕਰੋ · {when} ਨੂੰ ਜਮ੍ਹਾਂ',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi} ਨੂੰ ਅਦਾ ਕਰੋ · {when} ਨੂੰ ਜਮ੍ਹਾਂ · "{note}"',

  'agent.lockout.queue.action.approve': 'ਮਨਜ਼ੂਰ ਕਰੋ',
  'agent.lockout.queue.action.reject': 'ਰੱਦ ਕਰੋ',
  'agent.lockout.queue.action.markPaid': 'ਪੇਡ ਮਾਰਕ ਕਰੋ',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'ਪਲੇਅਰ ਨੂੰ ਅਦਾ ਕਰਨ ਲਈ ਵਰਤੀ UPI ਲੈਣ-ਦੇਣ ID ਦਰਜ ਕਰੋ:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'ਸਬ-ਏਜੰਟ ਨੂੰ ਅਦਾ ਕਰਨ ਲਈ ਵਰਤੀ UPI ਲੈਣ-ਦੇਣ ID ਦਰਜ ਕਰੋ:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'ਇਹ ਨਿਕਾਸੀ ਰੱਦ ਕਰਨੀ ਹੈ? ਰੋਕੀ ਰਕਮ ਪਲੇਅਰ ਨੂੰ ਵਾਪਸ ਹੋ ਜਾਵੇਗੀ।',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'ਇਹ ਸਬ-ਏਜੰਟ ਨਿਕਾਸੀ ਰੱਦ ਕਰਨੀ ਹੈ?',
  'agent.lockout.queue.toast.rechargeApproved': 'ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ — ਪਲੇਅਰ ਨੂੰ ਜਮ੍ਹਾਂ ਕੀਤਾ',
  'agent.lockout.queue.toast.rechargeRejected': 'ਰਿਚਾਰਜ ਰੱਦ',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'ਸਬ-ਏਜੰਟ ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'ਰਿਚਾਰਜ ਰੱਦ',
  'agent.lockout.queue.toast.withdrawalPaid': 'ਨਿਕਾਸੀ ਪੇਡ ਮਾਰਕ — ਤੁਹਾਡੇ ਵਾਲਿਟ ਨੂੰ ਜਮ੍ਹਾਂ ਕੀਤਾ ਗਿਆ',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'ਸਬ-ਏਜੰਟ ਨਿਕਾਸੀ ਪੇਡ — ਵਾਲਿਟ ਜਮ੍ਹਾਂ',
  'agent.lockout.queue.toast.withdrawalRejected': 'ਨਿਕਾਸੀ ਰੱਦ (ਪਲੇਅਰ ਨੂੰ ਵਾਪਸ)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'ਨਿਕਾਸੀ ਰੱਦ',
  'agent.lockout.queue.toast.actionFailed': 'ਕਾਰਵਾਈ ਅਸਫਲ',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'ਇਸ ਵੇਲੇ ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਤੋਂ ਕੁਝ ਨਹੀਂ।',
  'agent.lockout.queue.empty.subAgent': 'ਇਸ ਵੇਲੇ ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟਾਂ ਤੋਂ ਕੁਝ ਨਹੀਂ।',
  'agent.lockout.queue.loading': 'ਕਤਾਰ ਲੋਡ ਹੋ ਰਹੀ ਹੈ…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'ਰਿਫ੍ਰੈਸ਼ — ਕੀ ਮੈਂ ਅਜੇ ਅਨਲੌਕ ਹੋਇਆ?',
  'agent.lockout.refresh.checking': 'ਜਾਂਚ ਹੋ ਰਹੀ ਹੈ…',
  'agent.lockout.refresh.toast': 'ਸਥਿਤੀ ਰਿਫ੍ਰੈਸ਼ ਹੋਈ',
  'agent.lockout.refresh.footnote': 'ਜਿਵੇਂ ਹੀ ਤੁਹਾਡਾ ਵਾਲਿਟ + ਡਿਪਾਜ਼ਿਟ ਜ਼ੀਰੋ ਤੋਂ ਉੱਪਰ ਚੜ੍ਹਦਾ ਹੈ, ਇਹ ਪੰਨਾ ਆਪਣੇ ਆਪ ਬੰਦ ਹੋ ਜਾਂਦਾ ਹੈ।',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'ਰਿਚਾਰਜ ਵੇਰਵੇ ਲੋਡ ਨਹੀਂ ਹੋ ਸਕੇ',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'ਆਪਣਾ ਖਾਤਾ ਅਨਲੌਕ ਕਰਨ ਲਈ ਹੁਣੇ ਅਦਾ ਕਰੋ',
  'agent.emergencyForm.banner.locked.body': 'ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਨੇ ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਜ਼ਮਾਨਤ ਦੇ ਕਵਰ ਨਾਲੋਂ ਵੱਧ ਜਿੱਤਿਆ ਹੈ। ਜਦ ਤੱਕ ਤੁਸੀਂ ਆਪਣਾ ਨਕਾਰਾਤਮਕ ਬੈਲੇਂਸ ਸਾਫ਼ ਨਹੀਂ ਕਰਦੇ, ਤੁਸੀਂ ਪਲੇਟਫਾਰਮ ਨਹੀਂ ਵਰਤ ਸਕਦੇ।',
  'agent.emergencyForm.banner.warning.title': 'ਜਲਦੀ ਟੌਪ-ਅੱਪ ਕਰੋ — ਤੁਸੀਂ ਸੀਮਾ ਦੇ ਨੇੜੇ ਹੋ',
  'agent.emergencyForm.banner.warning.body': 'ਤੁਹਾਡਾ ਵਾਲਿਟ ਡਿਪਾਜ਼ਿਟ ਦੇ 80% ਨੂੰ ਪਾਰ ਕਰ ਚੁੱਕਾ ਹੈ। 100% ਉੱਤੇ ਪਹੁੰਚਣ ਤੋਂ ਪਹਿਲਾਂ ਟੌਪ-ਅੱਪ ਕਰੋ — ਇੱਕ ਵਾਰ ਅਜਿਹਾ ਹੋਣ ਉੱਤੇ ਤੁਹਾਡਾ ਖਾਤਾ ਲੌਕ ਹੋ ਜਾਵੇਗਾ।',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'ਤੁਹਾਡਾ ਵਾਲਿਟ',
  'agent.emergencyForm.row.deposit': 'ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'ਤੁਸੀਂ ਕਿਉਂ ਫਸੇ ਹੋ',
  'agent.emergencyForm.tier.eyebrow.warning': 'ਤੁਸੀਂ ਕਿਉਂ ਜੋਖਮ ਵਿੱਚ ਹੋ',
  'agent.emergencyForm.tier.context.locked': 'ਤੁਹਾਡਾ ਵਾਲਿਟ {wallet} ਉੱਤੇ ਚਲਾ ਗਿਆ — ਇਹ {deposit} ਦੇ ਤੁਹਾਡੇ ਡਿਪਾਜ਼ਿਟ ਸੁਰੱਖਿਆ ਗੱਦੇ ਤੋਂ ਪਰੇ ਹੈ। ਪਲੇਟਫਾਰਮ ਤੁਹਾਨੂੰ ਇੱਥੇ ਲੌਕ ਕਰ ਦਿੰਦਾ ਹੈ ਤਾਂ ਜੋ ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਦੇ ਨੁਕਸਾਨ ਖਾਲੀ ਬਫ਼ਰ ਵਿਰੁੱਧ ਚੜ੍ਹਦੇ ਨਾ ਰਹਿਣ।',
  'agent.emergencyForm.tier.context.warning': 'ਤੁਹਾਡਾ ਵਾਲਿਟ {wallet} ਉੱਤੇ ਚਲਾ ਗਿਆ — ਇਹ {deposit} ਦੇ ਤੁਹਾਡੇ ਡਿਪਾਜ਼ਿਟ ਸੁਰੱਖਿਆ ਗੱਦੇ ਦੇ 80% ਤੋਂ ਵੱਧ ਹੈ। ਇੱਕ ਹੋਰ ਮਾੜਾ ਸੈਟਲਮੈਂਟ ਤੁਹਾਨੂੰ 100% ਤੋਂ ਉੱਪਰ ਧੱਕ ਕੇ ਖਾਤਾ ਲੌਕ ਕਰ ਦੇਵੇਗਾ।',
  'agent.emergencyForm.tier.pickHowMuch': 'ਕਿੰਨਾ ਅਦਾ ਕਰਨਾ ਹੈ ਚੁਣੋ',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'ਸਿਰਫ਼ ਅਨਲੌਕ',
  'agent.emergencyForm.tier.bareMin.label.warning': 'ਸਿਰਫ਼ ਚੇਤਾਵਨੀ ਤੋਂ ਬਚੋ',
  'agent.emergencyForm.tier.bareMin.tag': 'ਜੋਖਮ ਭਰਿਆ',
  'agent.emergencyForm.tier.bareMin.note.locked': 'ਸਿਰਫ਼ ਤੁਹਾਡਾ ਵਾਲਿਟ + ਡਿਪਾਜ਼ਿਟ ₹0 ਉੱਤੇ ਵਾਪਸ ਲਿਆਉਂਦਾ ਹੈ। ਕੋਈ ਵੀ ਨਵਾਂ ਨੁਕਸਾਨ ਤੁਹਾਨੂੰ ਤੁਰੰਤ ਮੁੜ ਲੌਕ ਕਰ ਦਿੰਦਾ ਹੈ।',
  'agent.emergencyForm.tier.bareMin.note.warning': 'ਸਿਰਫ਼ ਤੁਹਾਨੂੰ 80% ਲਾਈਨ ਤੋਂ ਥੋੜ੍ਹਾ ਉੱਪਰ ਲਿਆਉਂਦਾ ਹੈ। ਕੋਈ ਵੀ ਨਵਾਂ ਨੁਕਸਾਨ ਚੇਤਾਵਨੀ ਮੁੜ-ਚਾਲੂ ਕਰਦਾ ਹੈ — ਜਾਂ ਤੁਹਾਨੂੰ ਲੌਕਆਉਟ ਵਿੱਚ ਧੱਕ ਦਿੰਦਾ ਹੈ।',
  'agent.emergencyForm.tier.safeMin.label': 'ਸੁਰੱਖਿਅਤ ਘੱਟੋ-ਘੱਟ',
  'agent.emergencyForm.tier.safeMin.tag': 'ਸਿਫ਼ਾਰਸ਼ੀ',
  'agent.emergencyForm.tier.safeMin.note': 'ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ({deposit}) + ਤੁਹਾਡੇ ਨਕਾਰਾਤਮਕ ਵਾਲਿਟ ਦਾ ਅੱਧਾ ({half})। ਤੁਹਾਨੂੰ ਇੱਕ ਅਸਲ ਗੱਦਾ ਦਿੰਦਾ ਹੈ।',
  'agent.emergencyForm.tier.full.label': 'ਪੂਰੀ ਰਿਕਵਰੀ',
  'agent.emergencyForm.tier.full.tag': 'ਸਭ ਤੋਂ ਵਧੀਆ',
  'agent.emergencyForm.tier.full.note': 'ਡਿਪਾਜ਼ਿਟ + ਤੁਹਾਡਾ ਪੂਰਾ ਨਕਾਰਾਤਮਕ ਵਾਲਿਟ ({neg})। ਡਿਪਾਜ਼ਿਟ ਪੂਰੀ ਤਰ੍ਹਾਂ ਬਹਾਲ ਅਤੇ ਵਾਲਿਟ ਜ਼ੀਰੋ ਉੱਤੇ ਵਾਪਸ।',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'ਆਪਣੇ ਐਡਮਿਨ ਨੂੰ ਅਦਾ ਕਰੋ',
  'agent.emergencyForm.parent.label.parentAgent': 'ਆਪਣੇ ਪੇਰੈਂਟ ਏਜੰਟ ਨੂੰ ਅਦਾ ਕਰੋ',
  'agent.emergencyForm.parent.role.admin': 'ਪਲੇਟਫਾਰਮ ਐਡਮਿਨ',
  'agent.emergencyForm.parent.role.parentAgent': 'ਪੇਰੈਂਟ ਏਜੰਟ',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'ਵਟਸਐਪ: {number}',
  'agent.emergencyForm.parent.notOnFile': 'ਤੁਹਾਡੇ ਪੇਰੈਂਟ ਦੇ ਪੇਮੈਂਟ ਵੇਰਵੇ ਅਜੇ ਫ਼ਾਈਲ ਉੱਤੇ ਨਹੀਂ ਹਨ। ਕਿਰਪਾ ਕਰਕੇ ਸਿੱਧੇ ਪਲੇਟਫਾਰਮ ਸਪੋਰਟ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'ਅਦਾ ਕੀਤੀ ਰਕਮ (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI ਲੈਣ-ਦੇਣ ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'ਉਦਾਹਰਨ: 412312345678',
  'agent.emergencyForm.field.note.label': 'ਨੋਟ (ਵਿਕਲਪਿਕ)',
  'agent.emergencyForm.field.note.placeholder': 'ਜੋ ਵੀ ਤੁਹਾਡੇ ਪੇਰੈਂਟ ਨੂੰ ਪਤਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ',
  'agent.emergencyForm.validation.amount': 'ਤੁਸੀਂ ਅਦਾ ਕੀਤੀ ਰਕਮ ਦਰਜ ਕਰੋ (ਘੱਟੋ-ਘੱਟ ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI ਲੈਣ-ਦੇਣ ID / UTR ਦਰਜ ਕਰੋ (ਘੱਟੋ-ਘੱਟ 6 ਅੱਖਰ)',
  'agent.emergencyForm.cta': 'ਰਿਚਾਰਜ ਬੇਨਤੀ ਭੇਜੋ',
  'agent.emergencyForm.ctaSubmitting': 'ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  // Success state.
  'agent.emergencyForm.success.title': 'ਰਿਚਾਰਜ ਭੇਜਿਆ',
  'agent.emergencyForm.success.bodyAdmin': 'ਬੇਨਤੀ #{id} ਐਡਮਿਨ ਦੀ ਮਨਜ਼ੂਰੀ ਦੀ ਉਡੀਕ ਵਿੱਚ ਹੈ। ਮਨਜ਼ੂਰੀ ਮਿਲਣ ਉੱਤੇ ਤੁਹਾਡਾ ਵਾਲਿਟ ਜਮ੍ਹਾਂ ਹੋ ਜਾਵੇਗਾ ਅਤੇ ਤੁਹਾਡਾ ਖਾਤਾ ਆਪਣੇ ਆਪ ਅਨਲੌਕ ਹੋ ਜਾਵੇਗਾ।',
  'agent.emergencyForm.success.bodyAgent': 'ਬੇਨਤੀ #{id} @{username} ਦੀ ਮਨਜ਼ੂਰੀ ਦੀ ਉਡੀਕ ਵਿੱਚ ਹੈ। ਮਨਜ਼ੂਰੀ ਮਿਲਣ ਉੱਤੇ ਤੁਹਾਡਾ ਵਾਲਿਟ ਜਮ੍ਹਾਂ ਹੋ ਜਾਵੇਗਾ ਅਤੇ ਤੁਹਾਡਾ ਖਾਤਾ ਆਪਣੇ ਆਪ ਅਨਲੌਕ ਹੋ ਜਾਵੇਗਾ।',
  'agent.emergencyForm.toast.submitted': 'ਰਿਚਾਰਜ ਬੇਨਤੀ #{id} ਭੇਜੀ। ਮਨਜ਼ੂਰੀ ਦੀ ਉਡੀਕ।',
  'agent.emergencyForm.toast.cantSave': 'ਭਾਸ਼ਾ ਸੰਭਾਲੀ ਨਹੀਂ ਜਾ ਸਕੀ',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'ਇੱਕ-ਵਾਰ ਸੈੱਟਅੱਪ: ਆਪਣੇ ਟਿਕਾਣੇ ਦੀ ਪੁਸ਼ਟੀ ਕਰੋ',
  'agent.geo.subtitle': 'ਡੈਸ਼ਬੋਰਡ ਵਰਤਣ ਤੋਂ ਪਹਿਲਾਂ ਜ਼ਰੂਰੀ।',
  'agent.geo.body.primary': 'ਬਾਲੀ ਗੇਮਜ਼ ਪਲੇਟਫਾਰਮ ਨਵੇਂ ਪਲੇਅਰਾਂ ਨੂੰ ਉਨ੍ਹਾਂ ਦੇ ਸਭ ਤੋਂ ਨੇੜਲੇ ਏਜੰਟ ਕੋਲ ਰੂਟ ਕਰਦਾ ਹੈ। ਜਦ ਤੱਕ ਤੁਸੀਂ ਆਪਣਾ ਸਹੀ GPS ਟਿਕਾਣਾ ਸਾਂਝਾ ਨਹੀਂ ਕਰਦੇ, ਤੁਸੀਂ ਲੋਕੇਟਰ ਵਿੱਚ ਨਹੀਂ ਆਓਗੇ ਅਤੇ ਜੈਵਿਕ ਸਾਈਨਅੱਪ ਤੋਂ ਜ਼ੀਰੋ ਨਵੇਂ ਪਲੇਅਰ ਮਿਲਣਗੇ — ਸਿਰਫ਼ ਉਹ ਪਲੇਅਰ ਮਿਲਣਗੇ ਜੋ ਸਿੱਧੇ ਤੁਹਾਡਾ ਰੈਫ਼ਰਲ ਕੋਡ ਟਾਈਪ ਕਰਦੇ ਹਨ।',
  'agent.geo.body.secondary': 'ਇੱਕ ਵਾਰ "ਮੇਰਾ ਮੌਜੂਦਾ ਟਿਕਾਣਾ ਵਰਤੋ" ਉੱਤੇ ਟੈਪ ਕਰੋ। ਅਸੀਂ ਸਿਰਫ਼ ਤੁਹਾਡੇ ਕੋਆਰਡੀਨੇਟ ਲੈਂਦੇ ਹਾਂ, ਹੋਰ ਕੁਝ ਨਹੀਂ।',
  'agent.geo.hint.disabled': 'ਉੱਪਰ "ਮੇਰਾ ਮੌਜੂਦਾ ਟਿਕਾਣਾ ਵਰਤੋ" ਟੈਪ ਕਰੋ। ਇਕੱਲਾ ਪਿਨਕੋਡ ਜਾਂ ਹੱਥੀਂ ਖੇਤਰ ਚੁਣਨਾ ਕਾਫ਼ੀ ਨਹੀਂ — ਲੋਕੇਟਰ ਨੂੰ ਸਹੀ GPS ਕੋਆਰਡੀਨੇਟ ਚਾਹੀਦੇ ਹਨ।',
  'agent.geo.error.fallback': 'ਟਿਕਾਣਾ ਸੰਭਾਲਿਆ ਨਹੀਂ ਜਾ ਸਕਿਆ। ਕਿਰਪਾ ਕਰਕੇ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
  'agent.geo.cta.save': 'ਸੰਭਾਲੋ ਅਤੇ ਜਾਰੀ ਰੱਖੋ',
  'agent.geo.cta.saving': 'ਟਿਕਾਣਾ ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'agent.geo.toast.success': 'ਟਿਕਾਣਾ ਸੰਭਾਲਿਆ — ਹੁਣ ਤੁਸੀਂ ਪਲੇਅਰ ਪ੍ਰਾਪਤ ਕਰ ਸਕਦੇ ਹੋ।',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'ਪੂਲ ਬੈਲੇਂਸ',
  'agent.wallet.hero.helper': 'ਪਲੇਅਰ ਰਿਚਾਰਜਾਂ ਅਤੇ ਸਬ-ਏਜੰਟ ਟੌਪ-ਅੱਪਾਂ ਉੱਤੇ ਖਰਚਣ ਲਈ ਉਪਲਬਧ। ਕਮਾਈਆਂ ਅਤੇ ਐਡਮਿਨ ਟੌਪ-ਅੱਪ ਇੱਥੇ ਆਉਂਦੇ ਹਨ।',

  'agent.wallet.action.recharge': 'ਰਿਚਾਰਜ ਬੇਨਤੀ',
  'agent.wallet.action.withdraw': 'ਨਿਕਾਸੀ ਬੇਨਤੀ',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'ਬਕਾਇਆ',
  'agent.wallet.tab.history': 'ਇਤਿਹਾਸ',

  // Pending feed.
  'agent.wallet.pending.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.wallet.pending.empty': 'ਇਸ ਵੇਲੇ ਕੋਈ ਬਕਾਇਆ ਬੇਨਤੀਆਂ ਨਹੀਂ।',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'ਰਿਚਾਰਜ ਬੇਨਤੀ · +{amount}',
  'agent.wallet.pending.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'ਨਿਕਾਸੀ ਬੇਨਤੀ · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi} ਨੂੰ · {when}',
  'agent.wallet.pending.badge': 'ਬਕਾਇਆ',

  // History feed.
  'agent.wallet.history.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.wallet.history.empty': 'ਅਜੇ ਕੋਈ ਵਾਲਿਟ ਗਤੀਵਿਧੀ ਨਹੀਂ।',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'ਰਿਚਾਰਜ ਰੱਦ · {amount}',
  'agent.wallet.history.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'ਨਿਕਾਸੀ ਪੇਡ · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'ਨਿਕਾਸੀ ਰੱਦ · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi} ਨੂੰ · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'ਪੇਡ · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'ਮਨਜ਼ੂਰ',
  'agent.wallet.state.paid': 'ਪੇਡ',
  'agent.wallet.state.rejected': 'ਰੱਦ',
  'agent.wallet.state.expired': 'ਮਿਆਦ ਪੁੱਗੀ',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'ਐਡਮਿਨ ਤੋਂ ਟੌਪਅੱਪ',
  'agent.wallet.ledger.kind.topupToPlayer': 'ਪਲੇਅਰ ਨੂੰ ਟੌਪਅੱਪ',
  'agent.wallet.ledger.kind.topupFromPlayer': 'ਪਲੇਅਰ ਰਿਚਾਰਜ',
  'agent.wallet.ledger.kind.revenueShare': 'ਰੈਵੇਨਿਊ ਸ਼ੇਅਰ',
  'agent.wallet.ledger.kind.coFundOffer': 'ਕੋ-ਫੰਡ ਆਫ਼ਰ',
  'agent.wallet.ledger.kind.adminAdjust': 'ਐਡਮਿਨ ਐਡਜਸਟਮੈਂਟ',
  'agent.wallet.ledger.kind.withdrawalHold': 'ਨਿਕਾਸੀ ਹੋਲਡ',
  'agent.wallet.ledger.kind.withdrawalPaid': 'ਨਿਕਾਸੀ ਪੇਡ',
  'agent.wallet.ledger.kind.withdrawalRefund': 'ਨਿਕਾਸੀ ਰਿਫੰਡ',
  'agent.wallet.ledger.balanceAfter': 'ਬਾਲ {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ',
  'agent.deposit.hero.helper': 'ਤੁਹਾਡੇ ਪੂਲ ਵਿਰੁੱਧ ਜ਼ਮਾਨਤ। ਜਦ ਤੱਕ ਤੁਹਾਡਾ ਵਾਲਿਟ ਠੀਕ ਹੈ, ਅਛੂਤ ਰਹਿੰਦੀ ਹੈ।',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'ਸੁਰੱਖਿਅਤ',
  'agent.deposit.margin.warning': 'ਚੇਤਾਵਨੀ',
  'agent.deposit.margin.locked': 'ਲੌਕ',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'ਡਿਪਾਜ਼ਿਟ ਰਿਚਾਰਜ',
  'agent.deposit.action.withdraw': 'ਡਿਪਾਜ਼ਿਟ ਕਢਵਾਓ',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'ਕਵਰੇਜ ਗੇਜ',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'ਚੇਤਾਵਨੀ',
  'agent.deposit.gauge.thresholdLock': 'ਲੌਕ',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct} ਉੱਤੇ, ਤੁਹਾਨੂੰ ਚੇਤਾਵਨੀ ਮਿਲੇਗੀ',
  'agent.deposit.gauge.legend.lock': '{pct} ਉੱਤੇ, ਤੁਹਾਡਾ ਖਾਤਾ ਲੌਕ ਹੋ ਜਾਂਦਾ ਹੈ',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'ਪੂਲ ਪੂਰੀ ਤਰ੍ਹਾਂ ਡਿਪਾਜ਼ਿਟ ਦੁਆਰਾ ਕਵਰ — ਜਦ ਤੱਕ ਤੁਸੀਂ ਟੌਪ-ਅੱਪ ਨਹੀਂ ਕਰਦੇ ਨਿਕਾਸੀਆਂ ਬਲਾਕ ਹਨ।',
  'agent.deposit.gauge.caption.warning': 'ਪੂਲ ਨੇ ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਦਾ {pct}% ਵਰਤ ਲਿਆ ਹੈ। ਲੌਕਆਉਟ ਤੋਂ ਬਚਣ ਲਈ 100% ਮਾਰਨ ਤੋਂ ਪਹਿਲਾਂ ਟੌਪ-ਅੱਪ ਕਰੋ।',
  'agent.deposit.gauge.caption.caution': 'ਪੂਲ ਡਿਪਾਜ਼ਿਟ ਕਵਰੇਜ ਦੇ {pct}% ਉੱਤੇ ਹੈ। ਤੁਸੀਂ ਅਜੇ 80% ਚੇਤਾਵਨੀ ਤੋਂ ਸੁਰੱਖਿਅਤ ਹੇਠਾਂ ਹੋ।',
  'agent.deposit.gauge.caption.safe': 'ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਪੂਰੀ ਤਰ੍ਹਾਂ ਅਛੂਤ ਹੈ।',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.deposit.ledger.empty.title': 'ਅਜੇ ਕੋਈ ਡਿਪਾਜ਼ਿਟ ਗਤੀਵਿਧੀ ਨਹੀਂ',
  'agent.deposit.ledger.empty.body': 'ਜਦੋਂ ਐਡਮਿਨ ਤੁਹਾਡੀ ਡਿਪਾਜ਼ਿਟ ਨੂੰ ਕ੍ਰੈਡਿਟ ਕਰੇਗਾ (ਜਾਂ ਰਿਫੰਡ ਪ੍ਰੋਸੈਸ ਕਰੇਗਾ), ਹਰ ਐਂਟਰੀ ਇੱਥੇ ਦਿਖਾਈ ਦੇਵੇਗੀ।',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'ਆਪਣਾ ਵਾਲਿਟ ਰਿਚਾਰਜ ਕਰੋ',
  'agent.recharge.banner.eyebrow.deposit': 'ਆਪਣੀ ਡਿਪਾਜ਼ਿਟ ਰਿਚਾਰਜ ਕਰੋ',
  'agent.recharge.banner.body.wallet': 'ਪਹਿਲਾਂ ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਅਦਾ ਕਰੋ, ਫਿਰ ਇੱਥੇ ਸਬੂਤ ਜਮ੍ਹਾਂ ਕਰੋ ਤਾਂ ਜੋ ਉਹ ਤੁਹਾਡੇ ਓਪਰੇਟਿੰਗ ਵਾਲਿਟ ਨੂੰ ਟੌਪ-ਅੱਪ ਕਰ ਸਕਣ।',
  'agent.recharge.banner.body.deposit': 'ਪਹਿਲਾਂ ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਅਦਾ ਕਰੋ, ਫਿਰ ਇੱਥੇ ਸਬੂਤ ਜਮ੍ਹਾਂ ਕਰੋ ਤਾਂ ਜੋ ਉਹ ਤੁਹਾਡੀ ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ ਨੂੰ ਟੌਪ-ਅੱਪ ਕਰ ਸਕਣ।',
  'agent.recharge.banner.closeAria': 'ਫਾਰਮ ਬੰਦ ਕਰੋ',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'ਆਪਣੇ ਐਡਮਿਨ ਨੂੰ ਅਦਾ ਕਰੋ',
  'agent.recharge.parent.label.parentAgent': 'ਆਪਣੇ ਪੇਰੈਂਟ ਏਜੰਟ ਨੂੰ ਅਦਾ ਕਰੋ',
  'agent.recharge.parent.role.admin': 'ਪਲੇਟਫਾਰਮ ਐਡਮਿਨ',
  'agent.recharge.parent.role.parentAgent': 'ਪੇਰੈਂਟ ਏਜੰਟ',
  'agent.recharge.parent.noQr': 'ਕੋਈ QR ਸੈੱਟ ਨਹੀਂ',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਦੇ ਪੇਮੈਂਟ ਵੇਰਵੇ ਲੋਡ ਹੋ ਰਹੇ ਹਨ…',
  'agent.recharge.parent.loadError': 'ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਦੇ ਪੇਮੈਂਟ ਵੇਰਵੇ ਲੋਡ ਨਹੀਂ ਹੋ ਸਕੇ',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'ਤੁਸੀਂ ਕਿਵੇਂ ਅਦਾ ਕੀਤਾ?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ਔਨਲਾਈਨ ਡਿਜੀਟਲ ਅਦਾਇਗੀ',
  'agent.recharge.method.cash.label': 'ਨਕਦ',
  'agent.recharge.method.cash.note': 'ਵਿਅਕਤੀਗਤ ਹੈਂਡ-ਆਫ਼',

  // Form fields.
  'agent.recharge.field.amount.label': 'ਰਕਮ (₹) · ਘੱਟੋ-ਘੱਟ ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI ਲੈਣ-ਦੇਣ ID / UTR',
  'agent.recharge.field.txn.label.cash': 'ਨਕਦ ਰਸੀਦ ਮਾਰਕਰ (ਕੋਈ ਛੋਟਾ ਹਵਾਲਾ)',
  'agent.recharge.field.txn.placeholder.upi': 'ਉਦਾਹਰਨ: 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'ਉਦਾਹਰਨ: CASH-Sun-eve',
  'agent.recharge.field.note.label': 'ਨੋਟ (ਵਿਕਲਪਿਕ, ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਨੂੰ ਦਿਖਾਇਆ ਜਾਂਦਾ ਹੈ)',
  'agent.recharge.field.note.placeholder': 'ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਲਈ ਕੋਈ ਪ੍ਰਸੰਗ',

  // Validation errors.
  'agent.recharge.error.amountMin': 'ਰਕਮ ਪੂਰਾ ਰੁਪਇਆ ≥ 10 ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ',
  'agent.recharge.error.txnUpi': 'ਲੈਣ-ਦੇਣ ID ਬਹੁਤ ਛੋਟੀ ਲੱਗਦੀ ਹੈ — UPI ਹਵਾਲਾ ਪੇਸਟ ਕਰੋ',
  'agent.recharge.error.txnCash': 'ਇੱਕ ਛੋਟਾ ਮਾਰਕਰ ਜੋੜੋ (ਉਦਾਹਰਨ: "CASH-Sun-eve") ਤਾਂ ਜੋ ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਹੈਂਡ-ਆਫ਼ ਨੂੰ ਮੇਲ ਸਕੇ',

  // CTAs.
  'agent.recharge.cta.submit': 'ਬੇਨਤੀ ਭੇਜੋ',
  'agent.recharge.cta.cancel': 'ਰੱਦ ਕਰੋ',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'ਵਾਲਿਟ ਤੋਂ ਕਢਵਾਓ',
  'agent.withdrawal.banner.eyebrow.deposit': 'ਡਿਪਾਜ਼ਿਟ ਤੋਂ ਕਢਵਾਓ',
  'agent.withdrawal.banner.body.wallet': 'ਰਕਮ ਤੁਰੰਤ ਰੋਕ ਲਈ ਜਾਂਦੀ ਹੈ ਤਾਂ ਜੋ ਤੁਸੀਂ ਡਬਲ-ਸਪੈਂਡ ਨਾ ਕਰ ਸਕੋ। ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਤੁਹਾਨੂੰ ਅਦਾ ਕਰਦੀ ਹੈ ਅਤੇ ਪੇਡ ਮਾਰਕ ਕਰਦੀ ਹੈ।',
  'agent.withdrawal.banner.body.deposit': 'ਰਕਮ ਤੁਰੰਤ ਤੁਹਾਡੀ ਸਿਕਿਉਰਿਟੀ ਡਿਪਾਜ਼ਿਟ ਤੋਂ ਰੋਕੀ ਜਾਂਦੀ ਹੈ। ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਤੁਹਾਨੂੰ ਅਦਾ ਕਰਦੀ ਹੈ ਅਤੇ ਪੇਡ ਮਾਰਕ ਕਰਦੀ ਹੈ।',
  'agent.withdrawal.banner.closeAria': 'ਫਾਰਮ ਬੰਦ ਕਰੋ',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'ਤੁਸੀਂ ਕਿਵੇਂ ਅਦਾਇਗੀ ਚਾਹੁੰਦੇ ਹੋ?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ਔਨਲਾਈਨ ਡਿਜੀਟਲ ਅਦਾਇਗੀ',
  'agent.withdrawal.method.cash.label': 'ਨਕਦ',
  'agent.withdrawal.method.cash.note': 'ਵਿਅਕਤੀਗਤ ਹੈਂਡ-ਆਫ਼',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'ਰਕਮ (₹) · ਉਪਲਬਧ {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'ਤੁਹਾਡੀ UPI ID (ਜਿੱਥੇ ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਤੁਹਾਨੂੰ ਅਦਾ ਕਰੇਗੀ)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'ਮਿਲਣ ਦਾ ਮਾਰਕਰ (ਉਦਾਹਰਨ: "Sun evening cash")',
  'agent.withdrawal.field.cashMarker.placeholder': 'ਕਦੋਂ/ਕਿੱਥੇ ਨਕਦ ਸੌਂਪਣਾ ਹੈ',
  'agent.withdrawal.field.whatsapp.label': 'ਵਟਸਐਪ (ਵਿਕਲਪਿਕ, ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਨੂੰ ਤੁਹਾਡੇ ਤੱਕ ਪਹੁੰਚਣ ਲਈ)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'ਨੋਟ (ਵਿਕਲਪਿਕ)',
  'agent.withdrawal.field.note.placeholder': 'ਤੁਹਾਡੀ ਅੱਪਲਾਈਨ ਲਈ ਕੋਈ ਪ੍ਰਸੰਗ',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'ਰਕਮ ਪੂਰਾ ਰੁਪਇਆ ≥ 10 ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ',
  'agent.withdrawal.error.amountOverflow': 'ਤੁਹਾਡੇ ਕੋਲ ਸਿਰਫ਼ {balance} ਉਪਲਬਧ ਹਨ',
  'agent.withdrawal.error.upi': 'ਜਾਇਜ਼ UPI ID ਦਰਜ ਕਰੋ',

  // CTAs.
  'agent.withdrawal.cta.submit': 'ਬੇਨਤੀ ਭੇਜੋ',
  'agent.withdrawal.cta.cancel': 'ਰੱਦ ਕਰੋ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'ਪਲੇਅਰਜ਼',
  'agent.people.hub.players.subtitle': 'ਪਲੇਅਰ ਅਤੇ ਉਨ੍ਹਾਂ ਦੀਆਂ ਬੇਨਤੀਆਂ ਪ੍ਰਬੰਧਿਤ ਕਰੋ',
  'agent.people.hub.players.subtab.requests': 'ਪਲੇਅਰ ਬੇਨਤੀਆਂ',
  'agent.people.hub.players.subtab.list': 'ਪਲੇਅਰ',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'ਏਜੰਟ',
  'agent.people.hub.agents.subtitle': 'ਸਬ-ਏਜੰਟ ਅਤੇ ਉਨ੍ਹਾਂ ਦੀਆਂ ਬੇਨਤੀਆਂ ਪ੍ਰਬੰਧਿਤ ਕਰੋ',
  'agent.people.hub.agents.subtab.requests': 'ਏਜੰਟ ਬੇਨਤੀਆਂ',
  'agent.people.hub.agents.subtab.list': 'ਏਜੰਟ',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'ਨਵੇਂ ਪਲੇਅਰ ਪ੍ਰਾਪਤ ਕਰ ਰਹੇ ਹੋ',
  'agent.people.acceptingToggle.bodyAccepting': 'ਤੁਸੀਂ ਭੂਗੋਲਿਕ ਲੋਕੇਟਰ ਤੋਂ ਨਵੇਂ ਪਲੇਅਰ ਸਵੀਕਾਰ ਕਰ ਰਹੇ ਹੋ। ਤੁਹਾਡੇ ਖੇਤਰ ਵਿੱਚ ਠੰਡੇ ਸਾਈਨਅੱਪ ਆਪਣੇ ਆਪ ਤੁਹਾਡੇ ਕੋਲ ਰੂਟ ਹੁੰਦੇ ਹਨ।',
  'agent.people.acceptingToggle.bodyOptedOut': 'ਤੁਸੀਂ ਆਪਟ ਆਉਟ ਕਰ ਚੁੱਕੇ ਹੋ। ਲੋਕੇਟਰ ਤੁਹਾਨੂੰ ਛੱਡ ਰਿਹਾ ਹੈ — ਸਿਰਫ਼ ਸਿੱਧੇ ਰੈਫ਼ਰਲ + ਹੱਥੀਂ-ਨਿਯੁਕਤ ਪਲੇਅਰ ਤੁਹਾਡੇ ਤੱਕ ਪਹੁੰਚਣਗੇ। ਮੌਜੂਦਾ ਪਲੇਅਰਾਂ ਉੱਤੇ ਅਸਰ ਨਹੀਂ।',
  'agent.people.acceptingToggle.error': 'ਅੱਪਡੇਟ ਨਹੀਂ ਹੋ ਸਕਿਆ',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'ਨਵੇਂ ਪਲੇਅਰ ਸਵੀਕਾਰ ਕਰਨੇ ਬੰਦ ਕਰੋ',
  'agent.people.acceptingToggle.aria.start': 'ਨਵੇਂ ਪਲੇਅਰ ਸਵੀਕਾਰ ਕਰਨੇ ਸ਼ੁਰੂ ਕਰੋ',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'ਪਲੇਅਰਜ਼',
  'agent.qr.audience.subAgent': 'ਏਜੰਟ',
  'agent.qr.alt': 'ਤੁਹਾਡਾ ਪੇਮੈਂਟ QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'ਮੇਰਾ QR · ਪਲੇਅਰ ਜੋ ਦੇਖਦੇ ਹਨ',
  'agent.qr.cardLabel.subAgent': 'ਮੇਰਾ QR · ਏਜੰਟ ਜੋ ਦੇਖਦੇ ਹਨ',
  'agent.qr.upi.empty': 'ਅਜੇ ਕੋਈ UPI ਸੈੱਟ ਨਹੀਂ',
  // Status pill.
  'agent.qr.status.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.qr.status.live': 'ਲਾਈਵ',
  'agent.qr.status.notSet': 'ਸੈੱਟ ਨਹੀਂ',
  'agent.qr.status.paused': 'ਰੋਕਿਆ',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR ਅਤੇ UPI ਸੋਧੋ',
  'agent.qr.cta.setup': 'QR ਸੈੱਟਅੱਪ',
  'agent.qr.cta.viewOnly': 'ਸਿਰਫ਼ ਵੇਖੋ — ਸੋਧਣ ਲਈ ਐਡਮਿਨ ਨੂੰ ਪੁੱਛੋ',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR ਅਤੇ UPI ਸੋਧੋ',
  'agent.qr.modal.closeAria': 'ਬੰਦ',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR ਅੱਪਲੋਡ ਕਰੋ',
  'agent.qr.modal.replace': 'QR ਬਦਲੋ',
  'agent.qr.modal.remove': 'ਹਟਾਓ',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR ਚਿੱਤਰ ਹਟਾਉਣਾ ਹੈ? ਤੁਹਾਡੇ ਪਲੇਅਰ ਸਿਰਫ਼ ਸੰਪਰਕ ਫਾਲਬੈਕ ਦੇਖਣਗੇ।',
  'agent.qr.modal.confirmRemove.subAgent': 'QR ਚਿੱਤਰ ਹਟਾਉਣਾ ਹੈ? ਤੁਹਾਡੇ ਏਜੰਟ ਸਿਰਫ਼ ਸੰਪਰਕ ਫਾਲਬੈਕ ਦੇਖਣਗੇ।',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'ਉਦਾਹਰਨ: baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'ਵਟਸਐਪ ਨੰਬਰ (ਫਾਲਬੈਕ)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ਏਜੰਟਾਂ ਲਈ ਵਟਸਐਪ ਨੰਬਰ',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'ਖਾਲੀ → {number} ਉੱਤੇ ਵਾਪਸ',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'ਸਬ-ਏਜੰਟ {number} ਦੇਖਣਗੇ (ਤੁਹਾਡਾ ਪਲੇਅਰ-ਫੇਸਿੰਗ ਨੰਬਰ)।',
  'agent.qr.modal.field.phone.label.player': 'ਸਪੋਰਟ ਫ਼ੋਨ (ਫਾਲਬੈਕ)',
  'agent.qr.modal.field.phone.label.subAgent': 'ਏਜੰਟਾਂ ਲਈ ਸਪੋਰਟ ਫ਼ੋਨ',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'ਖਾਲੀ → {number} ਉੱਤੇ ਵਾਪਸ',
  'agent.qr.modal.field.phone.fallbackHint': 'ਸਬ-ਏਜੰਟ {number} ਦੇਖਣਗੇ (ਤੁਹਾਡਾ ਪਲੇਅਰ-ਫੇਸਿੰਗ ਨੰਬਰ)।',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'ਚਾਲੂ',
  'agent.qr.modal.active.description': 'ਪਲੇਅਰ ਰਿਚਾਰਜ ਜਮ੍ਹਾਂ ਕਰ ਸਕਦੇ ਹਨ',
  'agent.qr.modal.active.on': 'ਚਾਲੂ',
  'agent.qr.modal.active.paused': 'ਰੋਕਿਆ',

  // Footer buttons.
  'agent.qr.modal.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.qr.modal.save': 'ਸੰਭਾਲੋ',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'ਹੱਥੀਂ ਰਿਚਾਰਜ',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'ਆਪਣੇ ਪੂਲ ਤੋਂ ਸਿੱਧੇ ',
  'agent.manualRecharge.banner.body.target': 'ਪਲੇਅਰ',
  'agent.manualRecharge.banner.body.after': ' ਨੂੰ ਪੈਸੇ ਭੇਜੋ।',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'ਤੁਹਾਡਾ ਪੂਲ',
  'agent.manualRecharge.pool.dailyCap': 'ਸੀਮਾ · {amount}/ਦਿਨ',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'ਪਲੇਅਰ ਨੂੰ',
  'agent.manualRecharge.field.player.placeholder': 'ਇੱਕ ਪਲੇਅਰ ਚੁਣੋ…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · ਬਾਲ {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'ਪਲੇਅਰ ਨੂੰ',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · ਮੌਜੂਦਾ ਬਾਲ {balance}',
  'agent.manualRecharge.field.player.notFound': 'ਪਲੇਅਰ ਤੁਹਾਡੀ ਮਾਲਕੀ ਸੂਚੀ ਵਿੱਚ ਨਹੀਂ ਮਿਲਿਆ।',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'ਰਕਮ (₹) — {min} ਤੋਂ {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'ਤੁਹਾਡੇ ਪੂਲ ਬੈਲੇਂਸ ਤੋਂ ਵੱਧ।',
  'agent.manualRecharge.error.belowMin': 'ਘੱਟੋ-ਘੱਟ ({min}) ਤੋਂ ਘੱਟ।',
  'agent.manualRecharge.error.aboveMax': 'ਵੱਧੋ-ਵੱਧ ({max}) ਤੋਂ ਉੱਪਰ।',

  // Note field.
  'agent.manualRecharge.field.note.label': 'ਨੋਟ (ਵਿਕਲਪਿਕ, ਪਲੇਅਰ ਲੈਜਰ ਉੱਤੇ ਦਿਖਾਇਆ ਜਾਂਦਾ ਹੈ)',
  'agent.manualRecharge.field.note.placeholder': 'ਉਦਾਹਰਨ: ਦੁਕਾਨ ਉੱਤੇ ਨਕਦ ਇਕੱਠਾ ਕੀਤਾ',

  // Loading + success states.
  'agent.manualRecharge.loading': 'ਤੁਹਾਡਾ ਪੂਲ ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.manualRecharge.cta.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.manualRecharge.cta.send': '{amount} ਭੇਜੋ',
  'agent.manualRecharge.success.title': '@{username} ਨੂੰ {amount} ਭੇਜੇ',
  'agent.manualRecharge.success.meta': 'ਤੁਹਾਡਾ ਪੂਲ · {pool} · ਉਨ੍ਹਾਂ ਦਾ ਵਾਲਿਟ · {playerBal}',
  'agent.manualRecharge.success.again': 'ਹੋਰ ਭੇਜੋ',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'ਹੱਥੀਂ ਰਿਚਾਰਜ',
  'agent.manualSubAgentRecharge.banner.body.before': 'ਆਪਣੇ ਪੂਲ ਤੋਂ ਸਿੱਧੇ ',
  'agent.manualSubAgentRecharge.banner.body.target': 'ਸਬ-ਏਜੰਟ',
  'agent.manualSubAgentRecharge.banner.body.after': ' ਨੂੰ ਪੈਸੇ ਭੇਜੋ।',

  'agent.manualSubAgentRecharge.pool.label': 'ਤੁਹਾਡਾ ਪੂਲ',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'ਸਬ-ਏਜੰਟ ਨੂੰ',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'ਇੱਕ ਸਬ-ਏਜੰਟ ਚੁਣੋ…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · ਪੂਲ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'ਕੋਈ ਸਰਗਰਮ ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਨਹੀਂ',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'ਸਬ-ਏਜੰਟ ਨੂੰ',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · ਮੌਜੂਦਾ ਪੂਲ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'ਸਬ-ਏਜੰਟ ਤੁਹਾਡੀ ਸਿੱਧੀ ਡਾਊਨਲਾਈਨ ਵਿੱਚ ਨਹੀਂ ਮਿਲਿਆ।',

  'agent.manualSubAgentRecharge.field.amount.label': 'ਰਕਮ (₹) — {min} ਤੋਂ {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'ਤੁਹਾਡੇ ਪੂਲ ਬੈਲੇਂਸ ਤੋਂ ਵੱਧ।',
  'agent.manualSubAgentRecharge.error.belowMin': 'ਘੱਟੋ-ਘੱਟ ({min}) ਤੋਂ ਘੱਟ।',

  'agent.manualSubAgentRecharge.field.note.label': 'ਨੋਟ (ਵਿਕਲਪਿਕ, ਦੋਨੋ ਲੈਜਰਾਂ ਉੱਤੇ ਦਿਖਾਇਆ ਜਾਂਦਾ ਹੈ)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'ਉਦਾਹਰਨ: ਫਲੋਟ ਐਡਵਾਂਸ',

  'agent.manualSubAgentRecharge.loading': 'ਤੁਹਾਡਾ ਪੂਲ ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.manualSubAgentRecharge.cta.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.manualSubAgentRecharge.cta.send': '{amount} ਭੇਜੋ',
  'agent.manualSubAgentRecharge.success.title': '@{username} ਨੂੰ {amount} ਭੇਜੇ',
  'agent.manualSubAgentRecharge.success.meta': 'ਤੁਹਾਡਾ ਪੂਲ · {parent} · ਉਨ੍ਹਾਂ ਦਾ ਪੂਲ · {child}',
  'agent.manualSubAgentRecharge.success.again': 'ਹੋਰ ਭੇਜੋ',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'ਰਿਚਾਰਜ',
  'agent.playerRequests.direction.withdrawal': 'ਨਿਕਾਸੀ',
  'agent.playerRequests.tab.pending': 'ਬਕਾਇਆ',
  'agent.playerRequests.tab.history': 'ਇਤਿਹਾਸ',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.playerRequests.empty.pending.recharge': 'ਇਸ ਵੇਲੇ ਕੋਈ ਬਕਾਇਆ ਰਿਚਾਰਜ ਨਹੀਂ।',
  'agent.playerRequests.empty.pending.withdrawal': 'ਇਸ ਵੇਲੇ ਕੋਈ ਬਕਾਇਆ ਨਿਕਾਸੀ ਨਹੀਂ।',
  'agent.playerRequests.empty.history': 'ਅਜੇ ਕੋਈ ਇਤਿਹਾਸ ਨਹੀਂ।',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'ਰੱਦ ਕਰਨ ਦਾ ਕਾਰਨ (ਵਿਕਲਪਿਕ):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'ਰੱਦ ਕਰਨ ਦਾ ਕਾਰਨ (ਪਲੇਅਰ ਦੀ ਰੋਕੀ ਰਕਮ ਵਾਪਸ ਕਰ ਦਿੱਤੀ ਜਾਵੇਗੀ):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'ਰੱਦ',
  'agent.playerRequests.toast.rejectedRefunded': 'ਰੱਦ · ਰੋਕੀ ਰਕਮ ਵਾਪਸ',
  'agent.playerRequests.toast.approved': '{amount} ਮਨਜ਼ੂਰ',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} ਮਨਜ਼ੂਰ · ਮੁੱਖ + ₹{bonus} ਬੋਨਸ',
  'agent.playerRequests.toast.markedPaid': '{amount} ਪੇਡ ਮਾਰਕ',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel': 'ਅਦਾ ਕਰੋ (UPI)',
  'agent.playerRequests.card.whatsapp': 'ਵਟਸਐਪ',
  'agent.playerRequests.card.viewQr': 'QR ਦੇਖੋ',
  'agent.playerRequests.card.bonusChip': 'ਬੋਨਸ',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'ਮੰਗੇ ਗਏ {requested} ਵਿੱਚੋਂ {approved} ਮਨਜ਼ੂਰ',
  'agent.playerRequests.card.partialPaid': 'ਮੰਗੇ ਗਏ {requested} ਵਿੱਚੋਂ {approved} ਪੇਡ · ਵਾਪਸ {refund}',
  'agent.playerRequests.card.paidTxnPrefix': 'ਪੇਡ · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'ਰੱਦ ਕਰੋ',
  'agent.playerRequests.action.approve': 'ਮਨਜ਼ੂਰ ਕਰੋ',
  'agent.playerRequests.action.markPaid': 'ਪੇਡ ਮਾਰਕ',
  'agent.playerRequests.state.pending': 'ਬਕਾਇਆ',
  'agent.playerRequests.state.rejected': 'ਰੱਦ',
  'agent.playerRequests.state.expired': 'ਮਿਆਦ ਪੁੱਗੀ',
  'agent.playerRequests.state.accepted': 'ਮਨਜ਼ੂਰ',
  'agent.playerRequests.state.manuallyPaid': 'ਹੱਥੀਂ ਪੇਡ',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': '{total} ਵਿੱਚੋਂ {from}–{to}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'ਰਿਚਾਰਜ',
  'agent.agentRequests.direction.withdrawal': 'ਨਿਕਾਸੀ',
  'agent.agentRequests.tab.pending': 'ਬਕਾਇਆ',
  'agent.agentRequests.tab.history': 'ਇਤਿਹਾਸ',
  'agent.agentRequests.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.agentRequests.empty.pending.recharge': 'ਇਸ ਵੇਲੇ ਕੋਈ ਬਕਾਇਆ ਰਿਚਾਰਜ ਨਹੀਂ।',
  'agent.agentRequests.empty.pending.withdrawal': 'ਇਸ ਵੇਲੇ ਕੋਈ ਬਕਾਇਆ ਨਿਕਾਸੀ ਨਹੀਂ।',
  'agent.agentRequests.empty.history': 'ਅਜੇ ਕੋਈ ਇਤਿਹਾਸ ਨਹੀਂ।',

  'agent.agentRequests.prompt.rejectRecharge': 'ਰੱਦ ਕਰਨ ਦਾ ਕਾਰਨ (ਵਿਕਲਪਿਕ):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'ਰੱਦ ਕਰਨ ਦਾ ਕਾਰਨ (ਸਬ-ਏਜੰਟ ਦੀ ਰੋਕੀ ਰਕਮ ਵਾਪਸ ਕਰ ਦਿੱਤੀ ਜਾਵੇਗੀ):',

  'agent.agentRequests.toast.rejected': 'ਰੱਦ',
  'agent.agentRequests.toast.rejectedRefunded': 'ਰੱਦ · ਰੋਕੀ ਰਕਮ ਵਾਪਸ',
  'agent.agentRequests.toast.approved': '{amount} ਮਨਜ਼ੂਰ · ਪੂਲ ਜਮ੍ਹਾਂ',
  'agent.agentRequests.toast.markedPaid': '{amount} ਪੇਡ ਮਾਰਕ',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'ਏਜੰਟ',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'ਨਕਦ ਮਾਰਕਰ',
  'agent.agentRequests.card.payToUpiLabel': 'ਅਦਾ ਕਰੋ (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'ਹੈਂਡ-ਆਫ਼ ਮਾਰਕਰ',
  'agent.agentRequests.card.whatsapp': 'ਵਟਸਐਪ {number}',
  'agent.agentRequests.card.partialApproved': 'ਮੰਗੇ ਗਏ {requested} ਵਿੱਚੋਂ {approved} ਮਨਜ਼ੂਰ',
  'agent.agentRequests.card.partialPaid': 'ਮੰਗੇ ਗਏ {requested} ਵਿੱਚੋਂ {approved} ਪੇਡ · ਵਾਪਸ {refund}',
  'agent.agentRequests.card.paidTxnPrefix': 'ਪੇਡ · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'ਡਿਪਾਜ਼ਿਟ',
  'agent.agentRequests.card.chip.cash': 'ਨਕਦ',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'ਰੱਦ ਕਰੋ',
  'agent.agentRequests.action.approve': 'ਮਨਜ਼ੂਰ ਕਰੋ',
  'agent.agentRequests.action.markPaid': 'ਪੇਡ ਮਾਰਕ',
  'agent.agentRequests.state.pending': 'ਬਕਾਇਆ',
  'agent.agentRequests.state.rejected': 'ਰੱਦ',
  'agent.agentRequests.state.expired': 'ਮਿਆਦ ਪੁੱਗੀ',
  'agent.agentRequests.state.accepted': 'ਮਨਜ਼ੂਰ',
  'agent.agentRequests.state.manuallyPaid': 'ਹੱਥੀਂ ਪੇਡ',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'ਪਲੇਅਰ ਜੋੜੋ',
  'agent.playersList.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.playersList.empty': 'ਕੋਈ ਪਲੇਅਰ ਮੇਲ ਨਹੀਂ ਖਾਂਦਾ',
  'agent.playersList.results': '{total} ਵਿੱਚੋਂ {from}–{to} ਦਿਖਾ ਰਿਹਾ ਹੈ',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'ਯੂਜ਼ਰਨੇਮ, ਈਮੇਲ, ਫ਼ੋਨ ਖੋਜੋ…',

  // Status filter pills.
  'agent.playersList.filter.all': 'ਸਾਰੇ',
  'agent.playersList.filter.active': 'ਸਰਗਰਮ',
  'agent.playersList.filter.pending': 'ਬਕਾਇਆ',
  'agent.playersList.filter.suspended': 'ਮੁਅੱਤਲ',
  'agent.playersList.filter.banned': 'ਬੈਨ',

  // Empty-results panel.
  'agent.playersList.empty.title': 'ਕੋਈ ਪਲੇਅਰ ਨਹੀਂ ਮਿਲਿਆ',
  'agent.playersList.empty.body': 'ਕੋਈ ਹੋਰ ਖੋਜ ਜਾਂ ਸਥਿਤੀ ਫਿਲਟਰ ਅਜ਼ਮਾਓ।',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'ਪੰਨਾ {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'ਸਰਗਰਮ',
  'agent.playersList.card.status.pending': 'ਬਕਾਇਆ',
  'agent.playersList.card.status.suspended': 'ਮੁਅੱਤਲ',
  'agent.playersList.card.status.banned': 'ਬੈਨ',
  'agent.playersList.card.verifiedBadge': 'ਤਸਦੀਕ',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'ਵਾਲਿਟ',
  'agent.playersList.column.joined': 'ਜੁੜੇ',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'ਤੁਹਾਡੇ ਵੱਲੋਂ ਬਣਾਏ ਏਜੰਟ ਤੁਹਾਡੇ ਅਧੀਨ ਕੰਮ ਕਰਦੇ ਹਨ। ਉਹ ਹਰ ਪਿੱਲਰ ਉੱਤੇ 0% ਨਾਲ ਸ਼ੁਰੂ ਕਰਦੇ ਹਨ — ਉਨ੍ਹਾਂ ਦੇ ਵੇਰਵੇ ਪੰਨੇ ਤੋਂ ਪ੍ਰਤੀ ਗੇਮ ਰੇਟ ਦਿਓ।',
  'agent.subAgentsList.cta.new': 'ਨਵਾਂ ਸਬ-ਏਜੰਟ',
  'agent.subAgentsList.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'ਅਜੇ ਕੋਈ ਸਬ-ਏਜੰਟ ਨਹੀਂ',
  'agent.subAgentsList.empty.body.canCreate': 'ਇੱਕ ਬਣਾਉਣ ਲਈ "+ ਨਵਾਂ ਸਬ-ਏਜੰਟ" ਟੈਪ ਕਰੋ। ਉਹ ਆਪਣੇ ਪੂਲ, ਪਲੇਅਰਾਂ ਅਤੇ ਕਮਾਈ ਨਾਲ ਇੱਥੇ ਆਉਣਗੇ — ਤੁਹਾਡੇ ਦੁਆਰਾ ਪ੍ਰਬੰਧਿਤ।',
  'agent.subAgentsList.empty.body.cantCreate': 'ਤੁਹਾਡੇ ਐਡਮਿਨ ਨੇ ਅਜੇ ਤੁਹਾਨੂੰ ਸਬ-ਏਜੰਟ ਬਣਾਉਣ ਦੇ ਅਧਿਕਾਰ ਨਹੀਂ ਦਿੱਤੇ। ਉਨ੍ਹਾਂ ਨੂੰ ਆਪਣੀ ਅਨੁਮਤੀ ਉੱਤੇ createsubagent ਨੂੰ "edit" ਉੱਤੇ ਫਲਿੱਪ ਕਰਨ ਨੂੰ ਕਹੋ।',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'ਮੁਅੱਤਲ',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'ਏਜੰਟ',
  'agent.subAgentsList.column.players': 'ਪਲੇਅਰ',
  'agent.subAgentsList.column.pool': 'ਪੂਲ',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'ਸਿੱਧੇ',
  'agent.subAgentsList.row.indirectLabel': 'ਅਸਿੱਧੇ',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'ਨਵਾਂ ਸਬ-ਏਜੰਟ',
  'agent.subAgentsList.modal.body': 'ਉਹ ਹਰ ਪਿੱਲਰ ਉੱਤੇ 0% ਉੱਤੇ ਸ਼ੁਰੂ ਕਰਨਗੇ। ਬਣਾਉਣ ਤੋਂ ਬਾਅਦ ਉਨ੍ਹਾਂ ਦੇ ਵੇਰਵੇ ਪੰਨੇ ਤੋਂ ਪ੍ਰਤੀ ਗੇਮ ਰੇਟ ਦਿਓ।',
  'agent.subAgentsList.modal.field.username.label': 'ਯੂਜ਼ਰਨੇਮ',
  'agent.subAgentsList.modal.field.username.placeholder': 'ਅਲਫ਼ਾਨਿਊਮੈਰਿਕ, ._- ਮਨਜ਼ੂਰ; ਘੱਟੋ-ਘੱਟ 3 ਅੱਖਰ',
  'agent.subAgentsList.modal.field.email.label': 'ਈਮੇਲ',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'ਸ਼ੁਰੂਆਤੀ ਪਾਸਵਰਡ',
  'agent.subAgentsList.modal.field.password.placeholder': 'ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ',
  'agent.subAgentsList.modal.field.password.hint': 'ਨਵੇਂ ਏਜੰਟ ਨਾਲ ਇਹ ਸਾਂਝਾ ਕਰੋ ਤਾਂ ਜੋ ਉਹ ਲਾਗਇਨ ਕਰ ਸਕਣ। ਉਨ੍ਹਾਂ ਨੂੰ ਪਹਿਲੀ ਲਾਗਇਨ ਉੱਤੇ ਇਹ ਬਦਲ ਲੈਣਾ ਚਾਹੀਦਾ ਹੈ।',
  'agent.subAgentsList.modal.field.location.label': 'ਸਬ-ਏਜੰਟ ਦਾ ਟਿਕਾਣਾ',
  'agent.subAgentsList.modal.field.location.optional': '(ਜ਼ੋਰਦਾਰ ਸਿਫ਼ਾਰਸ਼)',
  'agent.subAgentsList.modal.field.location.missing': 'ਟਿਕਾਣੇ ਤੋਂ ਬਿਨਾਂ, ਭੂਗੋਲਿਕ ਲੋਕੇਟਰ ਇਸ ਸਬ-ਏਜੰਟ ਕੋਲ ਕੋਈ ਨਵੇਂ ਪਲੇਅਰ ਰੂਟ ਨਹੀਂ ਕਰ ਸਕੇਗਾ। ਜਦ ਤੱਕ ਉਹ ਸਾਈਨ ਇਨ ਕਰ ਕੇ ਆਪਣਾ GPS ਪੂਰਾ ਨਹੀਂ ਕਰਦੇ, ਉਹ ਸਿਰਫ਼ ਸਿੱਧੇ ਰੈਫ਼ਰਲ ਰਾਹੀਂ ਪਲੇਅਰ ਪ੍ਰਾਪਤ ਕਰਨਗੇ।',
  'agent.subAgentsList.modal.cta.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.subAgentsList.modal.cta.create': 'ਸਬ-ਏਜੰਟ ਬਣਾਓ',
  'agent.subAgentsList.modal.cta.creating': 'ਬਣਾਇਆ ਜਾ ਰਿਹਾ ਹੈ…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who} ਤੋਂ ਰਿਚਾਰਜ ਮਨਜ਼ੂਰ ਕਰੋ',
  'agent.approvalEdit.title.withdrawal': '{who} ਲਈ ਨਿਕਾਸੀ ਪੇਡ ਮਾਰਕ ਕਰੋ',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'ਮੰਗੀ ਗਈ:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'ਮਨਜ਼ੂਰ ਰਕਮ (₹)',
  'agent.approvalEdit.field.amount.hint': 'ਮੰਗੀ ਗਈ {amount} ਤੋਂ ਵੱਧ ਨਹੀਂ ਹੋ ਸਕਦੀ।',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} ਆਪਣੇ ਆਪ {who} ਦੇ ਵਾਲਿਟ ਨੂੰ ਵਾਪਸ ਕਰ ਦਿੱਤੇ ਜਾਣਗੇ।',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'ਅੰਸ਼ਕ ਮਨਜ਼ੂਰੀ। ਕੋਈ ਵੀ ਜੁੜਿਆ ਬੋਨਸ ਆਪਣੇ ਆਪ ਰੱਦ ਹੋ ਜਾਵੇਗਾ।',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'ਕਾਰਨ',
  'agent.approvalEdit.field.reason.requiredHint': '(ਜ਼ਰੂਰੀ, ≥5 ਅੱਖਰ)',
  'agent.approvalEdit.field.reason.optionalHint': '(ਵਿਕਲਪਿਕ)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'ਉਦਾਹਰਨ: ਪਲੇਅਰ ਨੇ ਸਿਰਫ਼ 30 ਰੁਪਏ ਅਦਾ ਕੀਤੇ',
  'agent.approvalEdit.field.reason.placeholder.optional': 'ਵਿਕਲਪਿਕ ਨੋਟ',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI ਲੈਣ-ਦੇਣ ID',
  'agent.approvalEdit.field.txn.placeholder': 'ਉਦਾਹਰਨ: 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'ਤੁਹਾਡਾ ਪੂਲ ({balance}) ਇਸ ਮਨਜ਼ੂਰੀ ਨੂੰ ਕਵਰ ਨਹੀਂ ਕਰ ਸਕਦਾ। ਪਹਿਲਾਂ ਟੌਪ ਅੱਪ ਕਰੋ।',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'ਮਨਜ਼ੂਰ ਰਕਮ ਘੱਟੋ-ਘੱਟ ₹1 ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ',
  'agent.approvalEdit.error.amountMax': 'ਮਨਜ਼ੂਰ ਰਕਮ ਮੰਗੀ ਗਈ {amount} ਤੋਂ ਵੱਧ ਨਹੀਂ ਹੋ ਸਕਦੀ',
  'agent.approvalEdit.error.reasonRequired': 'ਵੱਖਰੀ ਰਕਮ ਮਨਜ਼ੂਰ ਕਰਦੇ ਵੇਲੇ ਕਾਰਨ (≥5 ਅੱਖਰ) ਜ਼ਰੂਰੀ ਹੈ',
  'agent.approvalEdit.error.txnRequired': 'UPI ਲੈਣ-ਦੇਣ ID ਜ਼ਰੂਰੀ ਹੈ (≥6 ਅੱਖਰ)',
  'agent.approvalEdit.error.fallback': 'ਅਸਫਲ — ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.approvalEdit.cta.approve': 'ਮਨਜ਼ੂਰ ਕਰੋ',
  'agent.approvalEdit.cta.markPaid': 'ਪੇਡ ਮਾਰਕ',
  'agent.approvalEdit.cta.submitting': 'ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'ਮੇਰਾ ਖਾਤਾ',
  'agent.profile.hub.subtitle': 'ਪਛਾਣ, ਵਾਲਿਟ ਅਤੇ ਡਿਪਾਜ਼ਿਟ ਇੱਕ ਥਾਂ',
  'agent.profile.hub.tab.wallet': 'ਵਾਲਿਟ',
  'agent.profile.hub.tab.deposit': 'ਡਿਪਾਜ਼ਿਟ',
  'agent.profile.hub.tab.account': 'ਖਾਤਾ',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'ਸਾਈਨ ਆਉਟ',
  'agent.profile.signOut.modal.title': 'ਸਾਈਨ ਆਉਟ?',
  'agent.profile.signOut.modal.body': 'ਆਪਣਾ ਖਾਤਾ ਵਰਤਣ ਲਈ ਤੁਹਾਨੂੰ ਮੁੜ ਸਾਈਨ ਇਨ ਕਰਨਾ ਪਵੇਗਾ।',
  'agent.profile.signOut.modal.stay': 'ਸਾਈਨ ਇਨ ਰਹੋ',
  'agent.profile.signOut.modal.confirm': 'ਹਾਂ, ਸਾਈਨ ਆਉਟ',
  'agent.profile.signOut.modal.busy': 'ਸਾਈਨ ਆਉਟ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.profile.signOut.error': 'ਸਾਈਨ ਆਉਟ ਨਹੀਂ ਹੋ ਸਕਿਆ',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'ਸਾਈਨ ਇਨ ਨਹੀਂ।',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'ਏਜੰਟ',
  'agent.account.hero.role.admin': 'ਪਲੇਟਫਾਰਮ ਐਡਮਿਨ',
  'agent.account.hero.changePhoto': 'ਫੋਟੋ ਬਦਲੋ',
  'agent.account.hero.removePhoto': 'ਫੋਟੋ ਹਟਾਓ',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'ਆਪਣੀ ਫੋਟੋ ਅਡਜਸਟ ਕਰੋ',
  'agent.account.avatar.confirmRemove': 'ਆਪਣੀ ਪ੍ਰੋਫਾਈਲ ਫੋਟੋ ਹਟਾਉਣੀ ਹੈ?',
  'agent.account.toast.photoUpdated': 'ਫੋਟੋ ਅੱਪਡੇਟ ਹੋਈ',
  'agent.account.toast.photoRemoved': 'ਫੋਟੋ ਹਟਾਈ ਗਈ',
  'agent.account.toast.saved': 'ਸੰਭਾਲਿਆ',
  'agent.account.toast.locationSaved': 'ਟਿਕਾਣਾ ਸੰਭਾਲਿਆ',
  'agent.account.toast.passwordChanged': 'ਪਾਸਵਰਡ ਬਦਲਿਆ — ਮੁੜ ਸਾਈਨ ਇਨ ਕਰੋ',
  'agent.account.error.uploadFailed': 'ਅੱਪਲੋਡ ਅਸਫਲ',
  'agent.account.error.removeFailed': 'ਅਸਫਲ',
  'agent.account.error.saveFailed': 'ਸੰਭਾਲਣਾ ਅਸਫਲ',
  'agent.account.error.changeFailed': 'ਬਦਲਣਾ ਅਸਫਲ',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'ਖਾਤਾ ਜਾਣਕਾਰੀ',
  'agent.account.info.subtitle': 'ਸਿਰਫ਼-ਪੜ੍ਹਨਯੋਗ ਪਲੇਟਫਾਰਮ ਮੈਟਾਡਾਟਾ',
  'agent.account.info.memberSince': 'ਮੈਂਬਰ ਤੋਂ',
  'agent.account.info.lastLogin': 'ਪਿਛਲੀ ਲਾਗਇਨ',
  'agent.account.info.lastLoginIp': 'ਪਿਛਲੀ ਲਾਗਇਨ IP',
  'agent.account.info.reportsTo': 'ਅਧੀਨ',
  'agent.account.info.reportsToValue': 'ਐਡਮਿਨ #{id}',
  'agent.account.info.agentId': 'ਏਜੰਟ ID',
  'agent.account.info.firstLogin': 'ਪਹਿਲੀ ਵਾਰ',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'ਨਿੱਜੀ ਵੇਰਵੇ',
  'agent.account.personal.subtitle.locked': 'ਪਛਾਣ ਐਡਮਿਨ ਦੁਆਰਾ ਲੌਕ · ਸਿਰਫ਼-ਵੇਖਣਯੋਗ',
  'agent.account.personal.subtitle.agent': 'ਯੂਜ਼ਰਨੇਮ, ਈਮੇਲ ਅਤੇ ਸੰਪਰਕ ਨੰਬਰ',
  'agent.account.personal.subtitle.admin': 'ਯੂਜ਼ਰਨੇਮ ਅਤੇ ਈਮੇਲ',
  'agent.account.personal.field.username': 'ਯੂਜ਼ਰਨੇਮ',
  'agent.account.personal.field.email': 'ਈਮੇਲ',
  'agent.account.personal.field.phone': 'ਸੰਪਰਕ ਨੰਬਰ',
  'agent.account.personal.field.phone.placeholder': 'ਉਦਾਹਰਨ: +91 9876543210',
  'agent.account.personal.field.phone.hint': 'ਪਲੇਅਰ ਅਤੇ ਐਡਮਿਨ ਇਸ ਨੰਬਰ ਉੱਤੇ ਤੁਹਾਡੇ ਤੱਕ ਪਹੁੰਚਦੇ ਹਨ। ਸਾਫ਼ ਕਰਨ ਲਈ ਖਾਲੀ ਛੱਡੋ।',
  'agent.account.personal.fieldLockedNote': 'ਲੌਕ',
  'agent.account.personal.cta.save': 'ਬਦਲਾਅ ਸੰਭਾਲੋ',
  'agent.account.personal.cta.saving': 'ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'agent.account.personal.cta.noChanges': 'ਕੋਈ ਬਦਲਾਅ ਨਹੀਂ',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'ਤੁਹਾਡਾ ਟਿਕਾਣਾ',
  'agent.account.location.subtitle.summary': 'ਲੋਕੇਟਰ ਨਵੇਂ ਪਲੇਅਰਾਂ ਨੂੰ ਤੁਹਾਡੇ ਨੇੜਲੇ ਖੇਤਰ ਉੱਤੇ ਰੂਟ ਕਰਦਾ ਹੈ',
  'agent.account.location.subtitle.edit': 'ਆਪਣਾ ਖੇਤਰ ਅੱਪਡੇਟ ਕਰੋ ਤਾਂ ਜੋ ਲੋਕੇਟਰ ਸਹੀ ਪਲੇਅਰ ਤੁਹਾਨੂੰ ਰੂਟ ਕਰੇ',
  'agent.account.location.cta.edit': 'ਸੋਧੋ',
  'agent.account.location.cta.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.account.location.cta.save': 'ਸੰਭਾਲੋ',
  'agent.account.location.areaLabel': 'ਖੇਤਰ',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'ਟਿਕਾਣਾ ਸੈੱਟ',
  'agent.account.location.setBody': 'ਫ਼ਾਈਲ ਉੱਤੇ GPS ਕੋਆਰਡੀਨੇਟ — ਖੇਤਰ ਵੇਰਵੇ ਦੇਖਣ ਲਈ ਸੋਧੋ ਟੈਪ ਕਰੋ।',
  'agent.account.location.lastUpdated': 'ਆਖਰੀ ਅੱਪਡੇਟ · {date}',

  // Security card (password change).
  'agent.account.security.title': 'ਸੁਰੱਖਿਆ',
  'agent.account.security.subtitle.locked': 'ਐਡਮਿਨ ਦੁਆਰਾ ਲੌਕ · ਸਿਰਫ਼-ਵੇਖਣਯੋਗ',
  'agent.account.security.subtitle.unlocked': 'ਆਪਣਾ ਪਾਸਵਰਡ ਬਦਲੋ · ਹੋਰ ਡਿਵਾਈਸਾਂ ਨੂੰ ਸਾਈਨ ਆਉਟ ਕਰਦਾ ਹੈ',
  'agent.account.security.field.current': 'ਮੌਜੂਦਾ ਪਾਸਵਰਡ',
  'agent.account.security.field.new': 'ਨਵਾਂ ਪਾਸਵਰਡ',
  'agent.account.security.field.new.hint': 'ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ, ਇੱਕ ਅੱਖਰ ਅਤੇ ਇੱਕ ਅੰਕ ਨਾਲ',
  'agent.account.security.field.confirm': 'ਨਵਾਂ ਪੁਸ਼ਟੀ ਕਰੋ',
  'agent.account.security.cta.update': 'ਪਾਸਵਰਡ ਅੱਪਡੇਟ',
  'agent.account.security.cta.updating': 'ਅੱਪਡੇਟ ਹੋ ਰਿਹਾ ਹੈ…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'ਨਵੇਂ ਪਾਸਵਰਡ ਮੇਲ ਨਹੀਂ ਖਾਂਦੇ',
  'agent.account.security.error.tooShort': 'ਪਾਸਵਰਡ ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰਾਂ ਦਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ',
  'agent.account.security.error.weak': 'ਪਾਸਵਰਡ ਵਿੱਚ ਘੱਟੋ-ਘੱਟ ਇੱਕ ਅੱਖਰ ਅਤੇ ਇੱਕ ਨੰਬਰ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'ਸਾਂਝਾ ਕਰਨ ਯੋਗ ਏਜੰਟ ID',
  'agent.shareableId.tooltip.copy': 'UAID ਕਾਪੀ ਕਰੋ',
  'agent.shareableId.tooltip.copied': 'UAID ਕਾਪੀ ਹੋਈ',
  'agent.shareableId.cta.copy': 'ਕਾਪੀ',
  'agent.shareableId.cta.copied': 'ਕਾਪੀ ਹੋਈ!',
  'agent.shareableId.body.before': 'ਇਹ ਕੋਡ ਕਿਸੇ ਹੋਰ ਏਜੰਟ ਨਾਲ ਸਾਂਝਾ ਕਰੋ ਤਾਂ ਜੋ ਉਹ ਤੁਹਾਨੂੰ ਪਲੇਅਰ ਟ੍ਰਾਂਸਫ਼ਰ ਕਰ ਸਕਣ। ਚੈਟ ਜਾਂ ਵਟਸਐਪ ਰਾਹੀਂ ਭੇਜਣਾ ਸੁਰੱਖਿਅਤ ਹੈ — ਇਸ ਨੂੰ ਸਾਂਝਾ ਕਰਨ ਨਾਲ ',
  'agent.shareableId.body.not': 'ਨਹੀਂ',
  'agent.shareableId.body.after': ' ਤੁਹਾਡੇ ਖਾਤੇ ਤੱਕ ਕੋਈ ਪਹੁੰਚ ਮਿਲਦੀ।',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'ਇਨਾਮ',
  'agent.rewards.hub.subtitle': 'ਆਫ਼ਰ ਬ੍ਰਾਊਜ਼ ਕਰੋ ਅਤੇ ਆਪਣਾ ਐਕਸਪੋਜ਼ਰ ਟ੍ਰੈਕ ਕਰੋ',
  'agent.rewards.hub.tab.browse': 'ਆਫ਼ਰ ਬ੍ਰਾਊਜ਼ ਕਰੋ',
  'agent.rewards.hub.tab.exposure': 'ਮੇਰਾ ਐਕਸਪੋਜ਼ਰ',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'ਆਪਟ ਇਨ',
  'agent.rewards.status.optedOut': 'ਆਪਟ ਆਉਟ',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਲਈ ਚੁਣੇ',
  'agent.rewards.browse.empty.title': 'ਇਸ ਵੇਲੇ ਕੋਈ ਸਰਗਰਮ ਇਨਾਮ ਨਹੀਂ',
  'agent.rewards.browse.empty.body': 'ਐਡਮਿਨ ਨਿਯਮਿਤ ਨਵੇਂ ਆਫ਼ਰ ਪੋਸਟ ਕਰਦੇ ਹਨ। ਥੋੜ੍ਹੀ ਦੇਰ ਬਾਅਦ ਮੁੜ ਚੈੱਕ ਕਰੋ — ਜਾਂ ਆਪਣੀ ਅੱਪਲਾਈਨ ਨੂੰ ਕਹੋ ਕਿ ਤੁਹਾਡੇ ਵੱਲੋਂ ਛੱਡੇ ਕਿਸੇ ਆਫ਼ਰ ਵਿੱਚ ਤੁਹਾਨੂੰ ਆਪਟ ਇਨ ਕਰੇ।',
  'agent.rewards.browse.error.loadFailed': 'ਇਨਾਮ ਲੋਡ ਨਹੀਂ ਹੋਏ',
  'agent.rewards.browse.card.aria.manage': '{title} ਲਈ ਆਪਟ-ਇਨ ਪ੍ਰਬੰਧਿਤ ਕਰੋ',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'ਆਪਟ ਇਨ',
  'agent.rewards.browse.chip.optInPrompt': 'ਆਪਟ ਇਨ ਕਰਨ ਲਈ ਟੈਪ ਕਰੋ',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'ਤੁਹਾਡਾ ਐਕਸਪੋਜ਼ਰ ਗਣਨਾ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.rewards.exposure.error.loadFailed': 'ਐਕਸਪੋਜ਼ਰ ਲੋਡ ਨਹੀਂ ਹੋਇਆ',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'ਆਫ਼ਰ ਐਕਸਪੋਜ਼ਰ',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} ਸਰਗਰਮ ਆਪਟ-ਇਨ',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} ਸਰਗਰਮ ਆਪਟ-ਇਨ',

  'agent.rewards.exposure.hero.actualSpend.label': 'ਅਸਲ ਖਰਚ',
  'agent.rewards.exposure.hero.actualSpend.helper': 'ਹੁਣ ਤੱਕ ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਦੁਆਰਾ ਕਲੇਮ।',

  'agent.rewards.exposure.hero.maxExposure.label': 'ਵੱਧੋ-ਵੱਧ ਐਕਸਪੋਜ਼ਰ',
  'agent.rewards.exposure.hero.maxExposure.helper': 'ਸਭ ਤੋਂ ਬੁਰੀ-ਹਾਲਤ ਸੀਮਾ — ਹਰ ਬਾਕੀ ਬਜਟ ਰੁਪਇਆ ਕਲੇਮ ਹੋਇਆ।',

  'agent.rewards.exposure.hero.ceiling.label': 'ਸੀਮਾ ਵਰਤੀ',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'ਜੋਖਮ ਗੇਜ',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ ਵੱਧੋ-ਵੱਧ {amount}',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': ' ',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': ' ਉੱਤੇ, ਆਪਣੇ ਆਪਟ-ਇਨ ਉੱਤੇ ਨਜ਼ਰ ਰੱਖੋ',
  'agent.rewards.exposure.gauge.legend.rose.before': ' ',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': ' ਉੱਤੇ, ਤੁਸੀਂ ਆਪਣੀ ਸੀਮਾ ਨੇੜੇ ਹੋ',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'ਤੁਸੀਂ ਅਜੇ ਅਜਿਹੇ ਕਿਸੇ ਆਫ਼ਰ ਵਿੱਚ ਆਪਟ-ਇਨ ਨਹੀਂ ਹੋ ਜਿਸ ਦੀ ਪ੍ਰੋਜੈਕਟਡ ਸੀਮਾ ਹੋਵੇ।',
  'agent.rewards.exposure.gauge.caption.high': 'ਤੁਸੀਂ ਆਪਣੇ ਵੱਧੋ-ਵੱਧ ਐਕਸਪੋਜ਼ਰ ਦਾ {pct}% ਖਪਤ ਕਰ ਚੁੱਕੇ ਹੋ। ਜ਼ਿਆਦਾਤਰ ਆਪਟ-ਇਨ ਭਾਰੀ ਕਲੇਮ ਹੋ ਚੁੱਕੇ ਹਨ।',
  'agent.rewards.exposure.gauge.caption.mid': 'ਤੁਸੀਂ ਆਪਣੀ ਵੱਧੋ-ਵੱਧ ਸੀਮਾ ਦੇ {pct}% ਉੱਤੇ ਹੋ। ਪ੍ਰਤੀ-ਆਫ਼ਰ ਬ੍ਰੇਕਡਾਊਨ ਉੱਤੇ ਨਜ਼ਰ ਰੱਖੋ।',
  'agent.rewards.exposure.gauge.caption.low': 'ਆਰਾਮਦਾਇਕ — ਹੁਣ ਤੱਕ ਆਪਣੀ ਵੱਧੋ-ਵੱਧ ਸੀਮਾ ਦਾ ਸਿਰਫ਼ {pct}% ਵਰਤਿਆ।',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'ਵੱਧੋ-ਵੱਧ ਐਕਸਪੋਜ਼ਰ ਕਿਵੇਂ ਗਣਨਾ ਹੁੰਦਾ ਹੈ:',
  'agent.rewards.exposure.footnote.prefix': ' ਹਰ ਆਪਟ-ਇਨ ਆਫ਼ਰ ਲਈ: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — ਸੁਰੱਖਿਅਤ ਉੱਪਰੀ ਹੱਦ। ਤੁਹਾਡੀ ਅਸਲ ਜ਼ਿੰਮੇਵਾਰੀ ਆਮ ਤੌਰ ਉੱਤੇ ',
  'agent.rewards.exposure.footnote.emphasis': 'ਘੱਟ',
  'agent.rewards.exposure.footnote.suffix2': ' ਹੁੰਦੀ ਹੈ ਜਦੋਂ ਤੁਹਾਡੇ ਹੇਠਲੇ ਸਬ-ਏਜੰਟ ਵੀ ਆਪਟ ਇਨ ਕਰਦੇ ਹਨ (ਉਹ ਵੱਡਾ ਹਿੱਸਾ ਅਦਾ ਕਰਦੇ ਹਨ; ਤੁਸੀਂ ਬਚਿਆ-ਖੁਚਿਆ ਅਦਾ ਕਰਦੇ ਹੋ)।',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'ਕੋਈ ਸਰਗਰਮ ਆਪਟ-ਇਨ ਨਹੀਂ',
  'agent.rewards.exposure.empty.body': 'ਜਦੋਂ ਤੁਸੀਂ ਆਫ਼ਰ ਬ੍ਰਾਊਜ਼ ਟੈਬ ਤੋਂ ਕਿਸੇ ਆਫ਼ਰ ਵਿੱਚ ਆਪਟ ਇਨ ਕਰਦੇ ਹੋ, ਇਹ ਤੁਹਾਡੇ ਖਰਚ ਅਤੇ ਪ੍ਰੋਜੈਕਟਡ ਐਕਸਪੋਜ਼ਰ ਨਾਲ ਇੱਥੇ ਆਉਂਦਾ ਹੈ।',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% ਹਿੱਸਾ',
  'agent.rewards.exposure.row.claims.one': '{count} ਕਲੇਮ',
  'agent.rewards.exposure.row.claims.other': '{count} ਕਲੇਮ',
  'agent.rewards.exposure.row.overridden': 'ਐਡਮਿਨ-ਓਵਰਰਾਈਡ',
  'agent.rewards.exposure.row.actualMaxLabel': 'ਅਸਲ / ਵੱਧੋ-ਵੱਧ',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'ਲਾਗਤ-ਹਿੱਸਾ ਆਪਟ-ਇਨ',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'ਗੇਮ ਮੁਨਾਫ਼ਾ',
  'agent.profile.rates.infoBanner.standardPctText': ' ਮਿਆਰੀ ਪ੍ਰਤੀਸ਼ਤ ਵਰਤਦਾ ਹੈ। ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'ਪ੍ਰਚਾਰ ਖਰਚੇ',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ਆਫਰਾਂ, ਕੈਸ਼ਬੈਕ) ਇੱਕ ਡਾਇਨਾਮਿਕ ਅੱਪਲਾਈਨ ਪੂਲ ਵਰਤਦੇ ਹਨ। ਦੋਵੇਂ ਤੁਹਾਡੇ ਪੱਧਰ (L{level}) ਦੇ ਆਧਾਰ ’ਤੇ ਪਲੇਟਫਾਰਮ ਐਡਮਿਨ ਦੁਆਰਾ ਗਲੋਬਲ ਤੌਰ ’ਤੇ ਪ੍ਰਬੰਧਿਤ ਹਨ।',
  'agent.profile.rates.section.gameProfits': 'ਗੇਮ ਮੁਨਾਫ਼ਾ — ਤੁਸੀਂ ਹਰ ਮੈਚ ’ਤੇ ਕੀ ਕਮਾਉਂਦੇ ਹੋ',
  'agent.profile.rates.section.promotionalCosts': 'ਪ੍ਰਚਾਰ ਖਰਚੇ — ਤੁਹਾਡਾ ਅੱਪਲਾਈਨ ਸਬਸਿਡੀ ਯੋਗਦਾਨ',
  'agent.profile.rates.pillar.platformFee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ %',
  'agent.profile.rates.pillar.winLoss': 'ਜਿੱਤ / ਹਾਰ ਮੁਨਾਫ਼ਾ %',
  'agent.profile.rates.pillar.promoWeight': 'ਪ੍ਰਚਾਰ ਪੂਲ ਵਜ਼ਨ',
  'agent.profile.rates.guarantee.title': 'ਡਾਇਰੈਕਟ ਰੈਫਰਲ ਗਾਰੰਟੀ',
  'agent.profile.rates.guarantee.body': 'ਜੇ ਤੁਸੀਂ ਆਫਰ ਦਾਅਵਾ ਕਰਨ ਵਾਲੇ ਖਿਡਾਰੀ ਨੂੰ ਸਿੱਧਾ ਰੈਫਰ ਕੀਤਾ ਹੈ, ਤਾਂ ਤੁਸੀਂ ₹0.00 ਅਦਾ ਕਰਦੇ ਹੋ। ਤੁਹਾਡੀਆਂ ਅੱਪਲਾਈਨਾਂ ਤੁਹਾਡੇ ਨੈੱਟਵਰਕ ਨੂੰ ਵਧਾਉਣ ਵਿੱਚ ਮਦਦ ਲਈ ਲਾਗਤ ਦੀ 100% ਸਬਸਿਡੀ ਦਿੰਦੀਆਂ ਹਨ।',
  'agent.profile.rates.poolExplainer.title': 'ਪੂਲ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ:',
  'agent.profile.rates.poolExplainer.body': 'ਜਦੋਂ ਇੱਕ ਸਬ-ਏਜੰਟ ਦਾ ਖਿਡਾਰੀ ਆਫਰ ਦਾਅਵਾ ਕਰਦਾ ਹੈ, ਤਾਂ ਤੁਹਾਡਾ ਵਜ਼ਨ ਬਾਕੀ ਸਰਗਰਮ ਅੱਪਲਾਈਨ + ਪਲੇਟਫਾਰਮ ਨਾਲ ਇੱਕ ਪੂਲ ਵਿੱਚ ਜੋੜਿਆ ਜਾਂਦਾ ਹੈ। ਤੁਸੀਂ (ਤੁਹਾਡਾ ਵਜ਼ਨ / ਕੁੱਲ ਪੂਲ) × ਆਫਰ ਖਰਚ ਅਦਾ ਕਰਦੇ ਹੋ। ਜ਼ਿਆਦਾ ਵਜ਼ਨ = ਵੱਡਾ ਹਿੱਸਾ।',
  'agent.profile.rates.weightLabel': 'ਵਜ਼ਨ:',
  'agent.profile.rates.updatedPrefix': 'ਅੱਪਡੇਟ ਕੀਤਾ',
  'agent.profile.rates.notSetYet': 'ਅਜੇ ਸੈੱਟ ਨਹੀਂ ਕੀਤਾ',
  'agent.rewards.optInModal.guarantee.title': 'ਡਾਇਰੈਕਟ ਰੈਫਰਲ ਗਾਰੰਟੀ',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'ਜੇ <strong>ਤੁਸੀਂ ਸਿੱਧਾ ਰੈਫਰ ਕੀਤਾ</strong> ਇਸ ਆਫਰ ਦਾਅਵਾ ਕਰਨ ਵਾਲੇ ਖਿਡਾਰੀ ਨੂੰ, ਤਾਂ ਤੁਸੀਂ <strong>₹0.00</strong> ਅਦਾ ਕਰਦੇ ਹੋ। ਤੁਹਾਡੀਆਂ ਅੱਪਲਾਈਨਾਂ ਤੁਹਾਡੇ ਨੈੱਟਵਰਕ ਨੂੰ ਵਧਾਉਣ ਵਿੱਚ ਮਦਦ ਲਈ ਲਾਗਤ ਦੀ 100% ਸਬਸਿਡੀ ਦਿੰਦੀਆਂ ਹਨ।',
  'agent.rewards.optInModal.guarantee.body.noDownline': 'ਜਦੋਂ <strong>ਤੁਹਾਡੇ ਖਿਡਾਰੀ</strong> ਇਹ ਆਫਰ ਦਾਅਵਾ ਕਰਦੇ ਹਨ, ਤਾਂ ਤੁਸੀਂ <strong>₹0.00</strong> ਅਦਾ ਕਰਦੇ ਹੋ। ਤੁਹਾਡੀਆਂ ਅੱਪਲਾਈਨਾਂ ਤੁਹਾਡੇ ਨੈੱਟਵਰਕ ਨੂੰ ਵਧਾਉਣ ਵਿੱਚ ਮਦਦ ਲਈ ਲਾਗਤ ਦੀ 100% ਸਬਸਿਡੀ ਦਿੰਦੀਆਂ ਹਨ।',
  'agent.rewards.optInModal.uplineReality.title': 'ਅੱਪਲਾਈਨ ਰਿਐਲਿਟੀ ਚੈੱਕ',
  'agent.rewards.optInModal.uplineReality.body': 'ਤੁਹਾਡੇ ਕੋਲ <strong>{count}</strong> ਡਾਇਰੈਕਟ ਸਬ-ਏਜੰਟ{plural} ਹਨ। ਜੇ ਕੋਈ ਸਬ-ਏਜੰਟ ਦਾ ਖਿਡਾਰੀ ਇਹ ਆਫਰ ਦਾਅਵਾ ਕਰਦਾ ਹੈ, ਤਾਂ ਤੁਸੀਂ ਆਪਣੇ <strong>ਪੂਲ ਵਜ਼ਨ ({weight})</strong> ਦੇ ਆਧਾਰ ’ਤੇ ਬਾਕੀ ਸਰਗਰਮ ਅੱਪਲਾਈਨ ਅਤੇ ਪਲੇਟਫਾਰਮ ਦੇ ਮੁਕਾਬਲੇ ਆਨੁਪਾਤਕ ਹਿੱਸਾ ਅਦਾ ਕਰੋਗੇ।',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ਡਾਇਰੈਕਟ',
  'agent.rates.col.indirect': 'ਇਨਡਾਇਰੈਕਟ',
  'agent.rates.indirect.dynamic': 'ਡਾਇਨਾਮਿਕ',
  'agent.rates.indirect.notApplicable': 'ਲਾਗੂ ਨਹੀਂ',
  'agent.rates.indirect.rangeTo': 'ਤੋਂ',
  'agent.rewards.optInModal.close': 'ਬੰਦ',
  'agent.rewards.optInModal.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.rewards.optInModal.loading': 'ਆਫ਼ਰ ਵੇਰਵੇ ਲੋਡ ਹੋ ਰਹੇ ਹਨ…',
  'agent.rewards.optInModal.error.loadFailed': 'ਆਫ਼ਰ ਵੇਰਵੇ ਲੋਡ ਨਹੀਂ ਹੋਏ',
  'agent.rewards.optInModal.error.optInFailed': 'ਆਪਟ-ਇਨ ਅਸਫਲ',
  'agent.rewards.optInModal.error.optOutFailed': 'ਆਪਟ-ਆਉਟ ਅਸਫਲ',
  'agent.rewards.optInModal.error.saveFailed': 'ਸਬ-ਏਜੰਟ ਰੇਟ ਸੰਭਾਲ ਨਹੀਂ ਸਕੇ',
  'agent.rewards.optInModal.toast.optedIn': 'ਆਪਟ ਇਨ। ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਹੁਣ ਇਹ ਆਫ਼ਰ ਦੇਖ ਸਕਦੀ ਹੈ।',
  'agent.rewards.optInModal.toast.optedOut': 'ਆਪਟ ਆਉਟ। ਆਫ਼ਰ ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਤੋਂ ਹਟਾਇਆ ਗਿਆ।',
  'agent.rewards.optInModal.toast.saved.one': '{count} ਸਬ-ਏਜੰਟ ਰੇਟ ਬਦਲਾਅ ਸੰਭਾਲਿਆ।',
  'agent.rewards.optInModal.toast.saved.other': '{count} ਸਬ-ਏਜੰਟ ਰੇਟ ਬਦਲਾਅ ਸੰਭਾਲੇ।',
  'agent.rewards.optInModal.confirmOptOut': 'ਇਸ ਆਫ਼ਰ ਤੋਂ ਆਪਟ ਆਉਟ ਕਰਨਾ ਹੈ? ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਇਸ ਨੂੰ ਹੋਰ ਨਹੀਂ ਦੇਖੇਗੀ।',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'ਆਫ਼ਰ #{id} ਲਈ ਆਪਟ-ਇਨ ਮੋਡਲ ਰਾਹੀਂ ਸੈੱਟ',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'ਤੁਹਾਡਾ ਹਿੱਸਾ',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'ਆਪਟ-ਇਨ ਉੱਤੇ ਲੌਕ',
  'agent.rewards.optInModal.stake.current': 'ਮੌਜੂਦਾ ਰੇਟ',
  'agent.rewards.optInModal.stake.noRate': 'ਐਡਮਿਨ ਨੇ ਤੁਹਾਨੂੰ ਅਜੇ ਰੇਟ ਨਹੀਂ ਦਿੱਤਾ',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'ਹੁਣ ਤੱਕ ਖਰਚਿਆ',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'ਇਸ ਆਫ਼ਰ ਉੱਤੇ ਡਾਊਨਲਾਈਨ ਕਲੇਮਾਂ ਲਈ ਤੁਹਾਡੇ ਵਾਲਿਟ ਤੋਂ ਪਹਿਲਾਂ ਹੀ ਅਦਾ ਕੀਤੇ ਸੰਚਿਤ ਰੁਪਏ।',
  'agent.rewards.optInModal.stake.maxPossible.label': 'ਵੱਧੋ-ਵੱਧ ਸੰਭਾਵਿਤ',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'ਜੇ ਹਰ ਬਾਕੀ ਬਜਟ ਰੁਪਇਆ ਕਲੇਮ ਹੋਇਆ ਤਾਂ ਤੁਸੀਂ ਜੋ ਸਭ ਤੋਂ ਬੁਰੀ-ਹਾਲਤ ਵਿੱਚ ਅਦਾ ਕਰ ਸਕਦੇ ਹੋ।',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— ਕੋਈ ਬਜਟ ਸੀਮਾ ਨਹੀਂ —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'ਸਭ ਤੋਂ ਬੁਰੀ ਹਾਲਤ: ਜੇ ਪਲੇਟਫਾਰਮ ਬਜਟ ਦਾ ਹਰ ਬਾਕੀ ₹{amount} ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਦੁਆਰਾ ਕਲੇਮ ਹੁੰਦਾ ਹੈ, ਤਾਂ ਤੁਸੀਂ ਉਸ ਦਾ {pct}% ਅਦਾ ਕਰੋਗੇ।',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'ਤੁਸੀਂ ਆਪਟ ਇਨ ਹੋ',
  'agent.rewards.optInModal.toggle.title.optedOut': 'ਆਪਣੀ ਡਾਊਨਲਾਈਨ ਨਾਲ ਸਾਂਝਾ ਕਰਨ ਲਈ ਆਪਟ ਇਨ ਕਰੋ',
  'agent.rewards.optInModal.toggle.body.optedIn': 'ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਦੇ ਪਲੇਅਰ ਇਹ ਆਫ਼ਰ ਦੇਖਦੇ ਹਨ। ਤੁਸੀਂ ਉਨ੍ਹਾਂ ਦੇ ਹਰ ਕਲੇਮ ਨੂੰ ਆਪਣੇ ਲੌਕ ਹਿੱਸੇ ਉੱਤੇ ਕੋ-ਫੰਡ ਕਰਦੇ ਹੋ।',
  'agent.rewards.optInModal.toggle.body.optedOut': 'ਜਦ ਤੱਕ ਤੁਸੀਂ ਆਪਟ ਇਨ ਨਹੀਂ ਹੁੰਦੇ, ਤੁਹਾਡਾ ਕੋਈ ਪਲੇਅਰ ਇਹ ਆਫ਼ਰ ਨਹੀਂ ਦੇਖਦਾ। ਇੱਕ ਵਾਰ ਆਪਟ ਇਨ ਕਰਨ ਉੱਤੇ, ਤੁਹਾਡਾ ਹਿੱਸਾ ਰੇਟ ਇਸ ਆਫ਼ਰ ਲਈ ਲੌਕ ਹੁੰਦਾ ਹੈ।',
  'agent.rewards.optInModal.toggle.optOutLink': 'ਇਸ ਆਫ਼ਰ ਤੋਂ ਆਪਟ ਆਉਟ',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'ਸਬ-ਏਜੰਟ ਰੇਟ',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ਹਰ ਇੱਕ ਵੱਧੋ-ਵੱਧ ਲੈ ਸਕਦਾ ਹੈ: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} ਅਣ-ਸੰਭਾਲੇ',
  'agent.rewards.optInModal.subAgents.empty': 'ਕੋਈ ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਨਹੀਂ।',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'ਸੰਭਾਲਣਾ ਕੈਸਕੇਡ ਕਰਦਾ ਹੈ — ਜੇ ਤੁਸੀਂ ਇੱਥੇ ਅਜਿਹੇ ਬੱਚੇ ਨੂੰ ਉੱਚਾ ਕਰਦੇ ਹੋ ਜਿਸ ਦੇ ਹੇਠਾਂ ਪਹਿਲਾਂ ਤੋਂ ਡੂੰਘੇ ਸਬ-ਏਜੰਟ ਹਨ, ਉਹ ਰੇਟ ਚੇਨ ਨੂੰ ਇਕਸਾਰ ਰੱਖਣ ਲਈ ਆਪਣੇ ਆਪ ਕਲੈਂਪ ਹੋ ਸਕਦੇ ਹਨ।',
  'agent.rewards.optInModal.subAgents.noChildren': 'ਤੁਹਾਡੇ ਕੋਈ ਸਿੱਧੇ ਸਬ-ਏਜੰਟ ਨਹੀਂ ਹਨ। ਆਫ਼ਰ ਤੁਹਾਡੇ ਆਪਣੇ ਪਲੇਅਰਾਂ ਨੂੰ ਦਿਖਾਈ ਦਿੰਦਾ ਹੈ; ਐਡਮਿਨ ਪੂਰਾ ਬਚਿਆ ਅਦਾ ਕਰਦਾ ਹੈ।',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'ਰੇਟ ਦਰਜ ਕਰੋ (ਸਾਫ਼ ਕਰਨ ਲਈ 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'ਸਿਰਫ਼ ਨੰਬਰ',
  'agent.rewards.optInModal.error.negative': 'ਨਕਾਰਾਤਮਕ ਨਹੀਂ ਹੋ ਸਕਦਾ',
  'agent.rewards.optInModal.error.max100': 'ਵੱਧੋ-ਵੱਧ 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'ਵੱਧੋ-ਵੱਧ {ceiling}% (ਤੁਹਾਡਾ ਹਿੱਸਾ)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'agent.rewards.optInModal.cta.done': 'ਹੋ ਗਿਆ',
  'agent.rewards.optInModal.cta.save.one': '{count} ਬਦਲਾਅ ਸੰਭਾਲੋ',
  'agent.rewards.optInModal.cta.save.other': '{count} ਬਦਲਾਅ ਸੰਭਾਲੋ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'ਪਲੇਅਰ ਨੂੰ ਰਿਚਾਰਜ ਕਰੋ',
  'agent.adminRechargePlayer.subtitle': 'ਤੁਹਾਡੇ ਪੂਲ ਤੋਂ ਪਲੇਅਰ ਦੇ ਵਾਲਿਟ ਤੱਕ',
  'agent.adminRechargePlayer.backLink': 'ਪਲੇਅਰ ਉੱਤੇ ਵਾਪਸ',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'ਨਵੇਂ ਪਲੇਅਰ ਨੂੰ ਆਨਬੋਰਡ ਕਰੋ',
  'agent.createPlayer.subtitle.agent': 'ਉਹ ਆਪਣੇ ਆਪ ਤੁਹਾਡੀ ਮਾਲਕੀ ਵਾਲੇ ਹੋਣਗੇ।',
  'agent.createPlayer.subtitle.admin': 'ਹੇਠਾਂ ਮਾਲਕ ਏਜੰਟ ਚੁਣੋ, ਜਾਂ ਪਲੇਟਫਾਰਮ-ਸਿੱਧੇ ਲਈ ਖਾਲੀ ਛੱਡੋ।',
  'agent.createPlayer.close': 'ਬੰਦ',
  'agent.createPlayer.section.identity.title': 'ਪਛਾਣ',
  'agent.createPlayer.section.identity.hint': 'ਖਾਤਾ ਬਣਾਉਣ ਲਈ ਜ਼ਰੂਰੀ',
  'agent.createPlayer.field.username.label': 'ਯੂਜ਼ਰਨੇਮ',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ਈਮੇਲ',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'ਮੋਬਾਈਲ',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-ਅੰਕਾਂ ਦਾ ਭਾਰਤੀ ਮੋਬਾਈਲ, +91 ਆਪਣੇ ਆਪ ਜੁੜਦਾ ਹੈ',
  'agent.createPlayer.field.password.label': 'ਸ਼ੁਰੂਆਤੀ ਪਾਸਵਰਡ',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ ਅੱਖਰ, ਅੱਖਰ + ਅੰਕ',
  'agent.createPlayer.section.owner.title': 'ਮਾਲਕ ਏਜੰਟ',
  'agent.createPlayer.section.owner.hint': 'ਵਿਕਲਪਿਕ — ਪਲੇਟਫਾਰਮ-ਸਿੱਧੇ ਪਲੇਅਰ ਲਈ ਖਾਲੀ ਛੱਡੋ',
  'agent.createPlayer.field.owner.label': 'ਏਜੰਟ',
  'agent.createPlayer.field.owner.none': '— ਕੋਈ ਨਹੀਂ (ਪਲੇਟਫਾਰਮ-ਸਿੱਧਾ) —',
  'agent.createPlayer.section.profile.title': 'ਪ੍ਰੋਫਾਈਲ',
  'agent.createPlayer.section.profile.hint': 'ਵਿਕਲਪਿਕ — ਜੋ ਪਤਾ ਹੈ ਹੁਣ ਭਰੋ; ਪਲੇਅਰ ਪਹਿਲੀ ਲਾਗਇਨ ਉੱਤੇ ਪੂਰਾ ਕਰ ਸਕਦਾ ਹੈ',
  'agent.createPlayer.field.firstName.label': 'ਪਹਿਲਾ ਨਾਮ',
  'agent.createPlayer.field.lastName.label': 'ਆਖਰੀ ਨਾਮ',
  'agent.createPlayer.field.dob.label': 'ਜਨਮ ਮਿਤੀ',
  'agent.createPlayer.field.gender.label': 'ਲਿੰਗ',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— ਨਿਰਧਾਰਤ ਨਹੀਂ —',
  'agent.createPlayer.gender.male': 'ਪੁਰਸ਼',
  'agent.createPlayer.gender.female': 'ਔਰਤ',
  'agent.createPlayer.gender.other': 'ਹੋਰ',
  'agent.createPlayer.gender.preferNot': 'ਨਹੀਂ ਦੱਸਣਾ',
  'agent.createPlayer.field.language.label': 'ਪਸੰਦੀਦਾ ਭਾਸ਼ਾ',
  'agent.createPlayer.section.location.title': 'ਟਿਕਾਣਾ',
  'agent.createPlayer.section.location.hint': 'ਵਿਕਲਪਿਕ — ਪਲੇਅਰ ਪਹਿਲੀ ਲਾਗਇਨ ਉੱਤੇ ਪੂਰਾ ਕਰ ਸਕਦਾ ਹੈ',
  // Footer.
  'agent.createPlayer.disclaimer': 'ਪਲੇਅਰ status=active ਅਤੇ ਈਮੇਲ ਆਪਣੇ ਆਪ ਤਸਦੀਕ ਨਾਲ ਬਣਾਇਆ ਜਾਂਦਾ ਹੈ — ਕੋਈ ਲਿੰਕ ਨਹੀਂ ਭੇਜਿਆ ਜਾਂਦਾ। ਯਕੀਨੀ ਬਣਾਓ ਕਿ ਤੁਸੀਂ ਸੰਪਰਕ ਜਾਣਕਾਰੀ ਉੱਤੇ ਭਰੋਸਾ ਕਰਦੇ ਹੋ।',
  'agent.createPlayer.cta.cancel': 'ਰੱਦ ਕਰੋ',
  'agent.createPlayer.cta.creating': 'ਬਣਾਇਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'agent.createPlayer.cta.submit': 'ਪਲੇਅਰ ਬਣਾਓ',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'ਏਜੰਟ id ਗੁੰਮ',
  'agent.createPlayer.error.createFailed': 'ਪਲੇਅਰ ਬਣਾਇਆ ਨਹੀਂ ਜਾ ਸਕਿਆ',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'ਅੱਜ',
  'agent.adminHome.range.yesterday': 'ਕੱਲ੍ਹ',
  'agent.adminHome.range.week': 'ਇਹ ਹਫ਼ਤਾ',
  'agent.adminHome.range.lastWeek': 'ਪਿਛਲਾ ਹਫ਼ਤਾ',
  'agent.adminHome.range.month': 'ਇਹ ਮਹੀਨਾ',
  'agent.adminHome.range.lastMonth': 'ਪਿਛਲਾ ਮਹੀਨਾ',
  'agent.adminHome.range.all': 'ਸੰਪੂਰਨ',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'ਮੁੜ ਸਵਾਗਤ ਹੈ',
  'agent.adminHome.greeting.fallback': 'ਮਿੱਤਰ',
  'agent.adminHome.errorBanner.fallback': 'ਲੋਡ ਕਰਨ ਵਿੱਚ ਅਸਫਲ',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'ਪਲੇਟਫਾਰਮ ਇਨਫਲੋ',
  'agent.adminHome.admin.action.moneyQueue': 'ਮਨੀ ਕਤਾਰ',
  'agent.adminHome.admin.action.agents': 'ਏਜੰਟ',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'ਮੈਚ',
  'agent.adminHome.admin.snapshot.newSignups': 'ਨਵੇਂ ਸਾਈਨਅੱਪ',
  'agent.adminHome.admin.snapshot.active': 'ਸਰਗਰਮ',
  'agent.adminHome.admin.snapshot.pending': 'ਬਕਾਇਆ',

  // Section titles between rows.
  'agent.adminHome.section.games': 'ਗੇਮਜ਼',
  'agent.adminHome.section.games.subtitle': 'ਮੈਚ ਅਤੇ ਪਲੇਟਫਾਰਮ ਫੀਸ · {range}',
  'agent.adminHome.section.activity': 'ਗਤੀਵਿਧੀ',
  'agent.adminHome.section.ops': 'ਓਪਰੇਸ਼ਨ ਅਤੇ ਜਾਣਕਾਰੀ',
  'agent.adminHome.section.ops.subtitle': 'ਲਾਈਵ ਸਨੈਪਸ਼ਾਟ — ਪੂਰੇ ਪੰਨੇ ਉੱਤੇ ਡ੍ਰਿਲ ਕਰਨ ਲਈ ਕਿਸੇ ਵੀ ਕਾਰਡ ਨੂੰ ਕਲਿੱਕ ਕਰੋ',
  'agent.adminHome.section.gameSettings': 'ਗੇਮ ਸੈਟਿੰਗਜ਼',
  'agent.adminHome.section.gameSettings.subtitle': 'ਹਰ ਗੇਮ ਨੂੰ ਕੌਨਫ਼ਿਗਰ ਕਰੋ',
  'agent.adminHome.section.downline': 'ਮੇਰੀ ਡਾਊਨਲਾਈਨ ਜਾਣਕਾਰੀ',
  'agent.adminHome.section.downline.subtitle': 'ਪੂਰੇ ਪੰਨੇ ਉੱਤੇ ਡ੍ਰਿਲ ਕਰਨ ਲਈ ਕਿਸੇ ਵੀ ਕਾਰਡ ਨੂੰ ਕਲਿੱਕ ਕਰੋ',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'ਪਲੇਅਰ ਵਾਲਿਟ',
  'agent.adminHome.admin.card.players.headlineLabel': 'ਰੇਂਜ ਵਿੱਚ ਰਿਚਾਰਜ',
  'agent.adminHome.admin.card.players.flow.recharges': 'ਰਿਚਾਰਜ',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'ਨਿਕਾਸੀਆਂ',
  'agent.adminHome.admin.card.players.pending.recharges': 'ਬਕਾਇਆ ਰਿਚਾਰਜ',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'ਬਕਾਇਆ ਨਿਕਾਸੀਆਂ',
  'agent.adminHome.admin.card.players.stat.total': 'ਕੁੱਲ ਪਲੇਅਰ',
  'agent.adminHome.admin.card.players.stat.active': 'ਸਰਗਰਮ',
  'agent.adminHome.admin.card.players.stat.suspended': 'ਮੁਅੱਤਲ',

  'agent.adminHome.admin.card.agents.title': 'ਏਜੰਟ ਵਾਲਿਟ',
  'agent.adminHome.admin.card.agents.headlineLabel': 'ਏਜੰਟ ਪੂਲਾਂ ਵਿੱਚ',
  'agent.adminHome.admin.card.agents.flow.topups': 'ਟੌਪਅੱਪ (ਐਡਮਿਨ → ਏਜੰਟ)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'ਆਉਟਫਲੋ (ਅਦਾ / ਟੌਪਅੱਪ)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'ਬਕਾਇਆ ਏਜੰਟ ਨਿਕਾਸੀਆਂ',
  'agent.adminHome.admin.card.agents.stat.total': 'ਕੁੱਲ ਏਜੰਟ',
  'agent.adminHome.admin.card.agents.stat.active': 'ਸਰਗਰਮ',
  'agent.adminHome.admin.card.agents.stat.suspended': 'ਮੁਅੱਤਲ',

  'agent.adminHome.admin.card.bots.title': 'ਬੌਟ ਵਾਲਿਟ',
  'agent.adminHome.admin.card.bots.headlineLabel': 'ਬੌਟ ਵਾਲਿਟਾਂ ਵਿੱਚ',
  'agent.adminHome.admin.card.bots.stat.total': 'ਕੁੱਲ ਬੌਟ',
  'agent.adminHome.admin.card.bots.stat.active': 'ਸਰਗਰਮ',
  'agent.adminHome.admin.card.bots.stat.matches': 'ਮੈਚ',

  'agent.adminHome.admin.card.rewards.title': 'ਇਨਾਮ',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'ਇਸ ਹਫ਼ਤੇ ਪੇਡ',
  'agent.adminHome.admin.card.rewards.pending.claims': 'ਬਕਾਇਆ ਕਲੇਮ',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'ਸਰਗਰਮ ਆਫ਼ਰ',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'ਬਲਕ ਜੌਬਜ਼',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'ਮੈਚ',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'ਲੂਡੋ',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'ਰਮੀ',
  'agent.adminHome.game.carrom': 'ਕੈਰਮ',
  'agent.adminHome.game.chess': 'ਸ਼ਤਰੰਜ',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'ਟੌਪ ਪਲੇਅਰ',
  'agent.adminHome.activity.topPlayers.empty': 'ਇਸ ਰੇਂਜ ਵਿੱਚ ਕੋਈ ਮੈਚ ਨਹੀਂ।',
  'agent.adminHome.activity.topPlayers.metric': '{count} ਮੈਚ',
  'agent.adminHome.activity.topAgents.title': 'ਟੌਪ ਏਜੰਟ',
  'agent.adminHome.activity.topAgents.subtitle': 'ਮਾਲਕੀ ਵਾਲੇ ਪਲੇਅਰ ਅਨੁਸਾਰ',
  'agent.adminHome.activity.topAgents.empty': 'ਅਜੇ ਕੋਈ ਸਰਗਰਮ ਏਜੰਟ ਨਹੀਂ।',
  'agent.adminHome.activity.topAgents.metric': '{count} ਪਲੇਅਰ',
  'agent.adminHome.activity.recent.title': 'ਹਾਲੀਆ ਲੈਣ-ਦੇਣ',
  'agent.adminHome.activity.recent.subtitle': 'ਆਖਰੀ 5',
  'agent.adminHome.activity.recent.empty': 'ਅਜੇ ਕੋਈ ਲੈਣ-ਦੇਣ ਨਹੀਂ।',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'ਟੌਪ ਕਮਾਉਣ ਵਾਲੇ (7d)',
  'agent.adminHome.ops.topEarners.subtitle': 'ਅਸਲ ਨੈੱਟ ਅਨੁਸਾਰ',
  'agent.adminHome.ops.topEarners.empty': 'ਪਿਛਲੇ 7 ਦਿਨਾਂ ਵਿੱਚ ਕੋਈ ਕਮਾਈ ਨਹੀਂ।',
  'agent.adminHome.ops.liveActivity.title': 'ਲਾਈਵ ਗਤੀਵਿਧੀ',
  'agent.adminHome.ops.liveActivity.subtitle': 'ਆਖਰੀ 5 ਘਟਨਾਵਾਂ ਪਲੇਟਫਾਰਮ-ਵਿਆਪਕ',
  'agent.adminHome.ops.liveActivity.empty': 'ਸ਼ਾਂਤ — ਪਿਛਲੇ 24h ਵਿੱਚ ਕੋਈ ਘਟਨਾਵਾਂ ਨਹੀਂ।',
  'agent.adminHome.ops.botPerf.title': 'ਬੌਟ ਪ੍ਰਦਰਸ਼ਨ (7d)',
  'agent.adminHome.ops.botPerf.subtitle': 'ਪ੍ਰਤੀ-ਸ਼੍ਰੇਣੀ P&L',
  'agent.adminHome.ops.rateChanges.title': 'ਹਾਲੀਆ ਰੇਟ ਬਦਲਾਅ',
  'agent.adminHome.ops.rateChanges.subtitle': 'ਨਵੇਂ ਪਾਰਟਨਰਸ਼ਿਪ ਸੋਧ',
  'agent.adminHome.ops.rateChanges.empty': 'ਅਜੇ ਕੋਈ ਰੇਟ-ਬਦਲਾਅ ਘਟਨਾਵਾਂ ਨਹੀਂ।',
  'agent.adminHome.ops.opsHealth.title': 'ਓਪਰੇਸ਼ਨ ਸਿਹਤ',
  'agent.adminHome.ops.opsHealth.subtitle': 'ਡੈੱਡ-ਲੈਟਰ ਕਤਾਰ · ਬਕਾਇਆ ਸੈਟਲਮੈਂਟ',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'ਕਮਾਇਆ',
  'agent.adminHome.feed.match.onGameSuffix': '{game} ਉੱਤੇ',
  'agent.adminHome.feed.recharge.label': 'ਰਿਚਾਰਜ',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'ਰਾਹੀਂ',
  'agent.adminHome.feed.rateChange.label': 'ਰੇਟ ਬਦਲਾਅ',
  'agent.adminHome.feed.cascadeBadge': 'ਕੈਸਕੇਡ',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'ਮਨਜ਼ੂਰ',
  'agent.adminHome.feed.recharge.state.rejected': 'ਰੱਦ',
  'agent.adminHome.feed.recharge.state.pending': 'ਬਕਾਇਆ',
  'agent.adminHome.feed.recharge.state.paid': 'ਪੇਡ',
  'agent.adminHome.feed.recharge.state.expired': 'ਮਿਆਦ ਪੁੱਗੀ',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} ਮੈਚ',
  'agent.adminHome.leaderboard.matches.other': '{count} ਮੈਚ',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'ਪਲੇਟਫਾਰਮ ਨਫ਼ਾ (7d)',
  'agent.adminHome.botMatrix.cat.easy': 'ਸੌਖਾ',
  'agent.adminHome.botMatrix.cat.medium': 'ਦਰਮਿਆਨਾ',
  'agent.adminHome.botMatrix.cat.hard': 'ਔਖਾ',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}m',
  'agent.adminHome.botMatrix.activeSummary': '{total} ਵਿੱਚੋਂ {active} ਬੌਟ ਸਰਗਰਮ',
  'agent.adminHome.botMatrix.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ ਸਾਫ਼ ਹੈ',
  'agent.adminHome.opsHealth.dlqClean.body': 'ਕੋਈ ਅਸਫਲ ਸੈਟਲਮੈਂਟ ਜੌਬਜ਼ ਨਹੀਂ।',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} ਅਣਸੁਲਝਿਆ DLQ ਜੌਬ',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} ਅਣਸੁਲਝੇ DLQ ਜੌਬਜ਼',
  'agent.adminHome.opsHealth.dlqDirty.body': 'ਮੁੜ-ਕੋਸ਼ਿਸ਼ / ਹੱਲ ਕਰਨ ਲਈ ਕਲਿੱਕ ਕਰੋ।',
  'agent.adminHome.opsHealth.replay.eyebrow': 'ਰਿਪਲੇ',
  'agent.adminHome.opsHealth.replay.body': 'ਸੈਟਲਮੈਂਟ ਮੁੜ-ਚਲਾਓ',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'ਮੁੜ-ਨਿਯੁਕਤੀ',
  'agent.adminHome.opsHealth.reassign.body': 'ਬਲਕ ਮੂਵ',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'ਮੇਰਾ ਪੂਲ ਬੈਲੇਂਸ',
  'agent.adminHome.agentDash.hero.netPrefix': 'ਨੈੱਟ',
  'agent.adminHome.agentDash.action.myWallet': 'ਮੇਰਾ ਵਾਲਿਟ',
  'agent.adminHome.agentDash.action.myPlayers': 'ਮੇਰੇ ਪਲੇਅਰ',
  'agent.adminHome.agentDash.snapshot.myMatches': 'ਮੇਰੇ ਮੈਚ',
  'agent.adminHome.agentDash.snapshot.active': 'ਸਰਗਰਮ',
  'agent.adminHome.agentDash.snapshot.netRange': 'ਨੈੱਟ (ਰੇਂਜ)',
  'agent.adminHome.agentDash.snapshot.pending': 'ਬਕਾਇਆ',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'ਮੇਰਾ ਪਲੇਅਰ ਵਾਲਿਟ',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'ਰੇਂਜ ਵਿੱਚ ਰਿਚਾਰਜ',
  'agent.adminHome.agentDash.card.players.stat.total': 'ਮੇਰੇ ਪਲੇਅਰ',
  'agent.adminHome.agentDash.card.wallet.title': 'ਮੇਰਾ ਵਾਲਿਟ',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'ਪੂਲ ਬੈਲੇਂਸ',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'ਕਮਾਈ (ਕਮਿਸ਼ਨ)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'ਅਦਾ',
  'agent.adminHome.agentDash.card.wallet.pending': 'ਮੇਰੀ ਬਕਾਇਆ ਨਿਕਾਸੀ',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'ਮਿਲੇ ਟੌਪਅੱਪ',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'ਨੈੱਟ (ਰੇਂਜ)',
  'agent.adminHome.agentDash.card.activity.title': 'ਗਤੀਵਿਧੀ',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'ਰੇਂਜ ਵਿੱਚ ਮੇਰੇ ਮੈਚ',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'ਟੌਪ ਖਰਚਣ ਵਾਲਾ',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'ਉਨ੍ਹਾਂ ਦਾ ਖਰਚ',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'ਬਕਾਇਆ ਆਈਟਮਾਂ',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'ਟੌਪ ਖਰਚਣ ਵਾਲੇ',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'ਇਸ ਰੇਂਜ ਵਿੱਚ ਅਜੇ ਕੋਈ ਖਰਚ ਨਹੀਂ।',
  'agent.adminHome.agentDash.activity.recent.title': 'ਹਾਲੀਆ ਗਤੀਵਿਧੀ',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'ਤੁਹਾਡੇ ਪਲੇਅਰਾਂ ਵੱਲੋਂ ਆਖਰੀ 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'ਅਜੇ ਕੋਈ ਲੈਣ-ਦੇਣ ਨਹੀਂ।',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'ਟੌਪ ਕਮਾਉਣ ਵਾਲੇ (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'ਤੁਹਾਡੇ ਸਬ-ਏਜੰਟ ਰੈਂਕ ਕੀਤੇ',
  'agent.adminHome.agentDash.insights.downline.title': 'ਡਾਊਨਲਾਈਨ ਗਤੀਵਿਧੀ',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਤੋਂ ਆਖਰੀ 5 ਘਟਨਾਵਾਂ',
  'agent.adminHome.agentDash.insights.deposit.title': 'ਡਿਪਾਜ਼ਿਟ ਲੈਜਰ',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'ਹਾਲੀਆ ਡਿਪਾਜ਼ਿਟ ਕ੍ਰੈਡਿਟ / ਰਿਫੰਡ',
  'agent.adminHome.agentDash.insights.deposit.empty': 'ਅਜੇ ਕੋਈ ਡਿਪਾਜ਼ਿਟ ਗਤੀਵਿਧੀ ਨਹੀਂ।',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'ਸਬ-ਏਜੰਟ',
  'agent.subAgentDetail.subtitle': 'ਸਿੱਧਾ ਸਬ-ਏਜੰਟ',
  'agent.subAgentDetail.error.loadFailed': 'ਸਬ-ਏਜੰਟ ਲੋਡ ਕਰਨ ਵਿੱਚ ਅਸਫਲ',
  'agent.subAgentDetail.backToList': 'ਸਬ-ਏਜੰਟ ਸੂਚੀ ਉੱਤੇ ਵਾਪਸ',
  'agent.subAgentDetail.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'ਪਛਾਣ',
  'agent.subAgentDetail.tab.players': 'ਪਲੇਅਰ',
  'agent.subAgentDetail.tab.wallet': 'ਵਾਲਿਟ',
  'agent.subAgentDetail.tab.rates': 'ਰੇਟ',
  'agent.subAgentDetail.tab.ops': 'ਓਪਰੇਸ਼ਨ',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} ਪਲੇਅਰ',
  'agent.subAgentDetail.header.players.other': '{count} ਪਲੇਅਰ',
  'agent.subAgentDetail.header.pool': 'ਪੂਲ: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'ਪਿਛਲੀ ਲਾਗਇਨ {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'ਸਰਗਰਮ',
  'agent.subAgentDetail.status.suspended': 'ਮੁਅੱਤਲ',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'ਯੂਜ਼ਰਨੇਮ',
  'agent.subAgentDetail.identity.row.email': 'ਈਮੇਲ',
  'agent.subAgentDetail.identity.row.status': 'ਸਥਿਤੀ',
  'agent.subAgentDetail.identity.row.parent': 'ਪੇਰੈਂਟ ਏਜੰਟ',
  'agent.subAgentDetail.identity.parentValue': '@{username} (ਤੁਸੀਂ)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'ਮਾਲਕੀ ਵਾਲੇ ਪਲੇਅਰ',
  'agent.subAgentDetail.identity.row.wallet': 'ਵਾਲਿਟ ਬੈਲੇਂਸ',
  'agent.subAgentDetail.identity.row.created': 'ਬਣਾਇਆ',
  'agent.subAgentDetail.identity.row.lastLogin': 'ਪਿਛਲੀ ਲਾਗਇਨ',
  'agent.subAgentDetail.identity.row.identityLocked': 'ਪਛਾਣ ਲੌਕ',
  'agent.subAgentDetail.identity.identityLocked.yes': 'ਹਾਂ (ਸਿਰਫ਼ ਐਡਮਿਨ ਸੋਧ)',
  'agent.subAgentDetail.identity.identityLocked.no': 'ਨਹੀਂ (ਸਬ-ਏਜੰਟ ਖੁਦ ਸੋਧ ਸਕਦਾ ਹੈ)',
  'agent.subAgentDetail.identity.row.receiving': 'ਪਲੇਅਰ ਪ੍ਰਾਪਤ ਕਰ ਰਿਹਾ',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'ਸਵੀਕਾਰ ਕਰ ਰਿਹਾ',
  'agent.subAgentDetail.optOut.optedOut': 'ਆਪਟ ਆਉਟ',
  'agent.subAgentDetail.optOut.toast.accepting': 'ਸਬ-ਏਜੰਟ ਹੁਣ ਪਲੇਅਰ ਸਵੀਕਾਰ ਕਰ ਰਿਹਾ ਹੈ',
  'agent.subAgentDetail.optOut.toast.optedOut': 'ਸਬ-ਏਜੰਟ ਲੋਕੇਟਰ ਤੋਂ ਆਪਟ ਆਉਟ ਹੋਇਆ',
  'agent.subAgentDetail.optOut.error.updateFailed': 'ਅੱਪਡੇਟ ਨਹੀਂ ਹੋ ਸਕਿਆ',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'ਟਿਕਾਣਾ',
  'agent.subAgentDetail.location.lastUpdated': '· ਆਖਰੀ ਅੱਪਡੇਟ {date}',
  'agent.subAgentDetail.location.cta.save': 'ਟਿਕਾਣਾ ਸੰਭਾਲੋ',
  'agent.subAgentDetail.location.nothingToSave': 'ਸੰਭਾਲਣ ਨੂੰ ਕੁਝ ਨਹੀਂ',
  'agent.subAgentDetail.location.toast.saved.one': 'ਟਿਕਾਣਾ ਸੰਭਾਲਿਆ ({count} ਖੇਤਰ)',
  'agent.subAgentDetail.location.toast.saved.other': 'ਟਿਕਾਣਾ ਸੰਭਾਲਿਆ ({count} ਖੇਤਰ)',
  'agent.subAgentDetail.location.hint': 'ਸੰਭਾਲੋ ਚਾਲੂ ਕਰਨ ਲਈ ਵੱਖਰਾ ਰਾਜ / ਜ਼ਿਲ੍ਹਾ / ਖੇਤਰ ਚੁਣੋ। (ਸਬ-ਏਜੰਟ ਖੁਦ ਆਪਣੀ ਪਹਿਲੀ ਲਾਗਇਨ ਉੱਤੇ ਜਬਰਨ ਪਾਪ-ਅੱਪ ਰਾਹੀਂ ਸਹੀ GPS ਉੱਪਰ ਲਗਾਉਂਦਾ ਹੈ।)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'ਇਸ ਸਬ-ਏਜੰਟ ਦੀ ਅਜੇ ਕੋਈ ਪਲੇਅਰ ਮਾਲਕੀ ਨਹੀਂ।',
  'agent.subAgentDetail.players.count.one': '{count} ਪਲੇਅਰ',
  'agent.subAgentDetail.players.count.other': '{count} ਪਲੇਅਰ',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'ਮੌਜੂਦਾ ਸਬ-ਏਜੰਟ ਬੈਲੇਂਸ',
  'agent.subAgentDetail.wallet.ledger.title': '@{username} ਦਾ ਪੂਰਾ ਵਾਲਿਟ ਲੈਜਰ',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'ਹਰ ਹਰਕਤ · ਸਬ-ਏਜੰਟ ਆਪਣੇ ਵਾਲਿਟ ਵਿੱਚ ਉਹੀ ਦੇਖਦਾ ਹੈ',
  'agent.subAgentDetail.wallet.ledger.empty': 'ਅਜੇ ਕੋਈ ਵਾਲਿਟ ਗਤੀਵਿਧੀ ਨਹੀਂ।',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'ਤੁਸੀਂ ↔ ਉਹ',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'ਬਾਲ ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'ਐਡਮਿਨ ਤੋਂ ਟੌਪਅੱਪ',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'ਪਲੇਅਰ ਨੂੰ ਪੇਆਉਟ',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'ਪਲੇਅਰ ਰਿਚਾਰਜ',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'ਰੈਵੇਨਿਊ ਸ਼ੇਅਰ',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'ਰਿਚਾਰਜ ਕਮਿਸ਼ਨ',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'ਕੋ-ਫੰਡ ਆਫ਼ਰ',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'ਐਡਮਿਨ ਐਡਜਸਟਮੈਂਟ',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'ਨਿਕਾਸੀ ਹੋਲਡ',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'ਨਿਕਾਸੀ ਰਿਫੰਡ',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'ਪਲੇਟਫਾਰਮ ਫੀਸ ਸ਼ੇਅਰ',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'ਜਿੱਤ/ਹਾਰ ਸ਼ੇਅਰ',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'ਕੋਇਨ ਵਾਪਸ ਖਿੱਚਣੇ?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'ਪੁੱਛੋ ',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' ਆਪਣੇ ਪੂਲ ਤੋਂ ਨਿਕਾਸੀ ਬੇਨਤੀ ਜਮ੍ਹਾਂ ਕਰਨ। ਤੁਸੀਂ ਇਸ ਨੂੰ ਪੇਡ (ਪੂਰਾ ਜਾਂ ਅੰਸ਼ਕ) ਮਾਰਕ ਕਰ ਸਕਦੇ ਹੋ ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'ਫਾਈਨੈਂਸ › ਸਬ-ਏਜੰਟ',
  'agent.subAgentDetail.wallet.pullback.body.after': ' ਤੋਂ।',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username} ਨੂੰ ਟ੍ਰਾਂਸਫ਼ਰ',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username} ਤੋਂ ਕਢਵਾਓ',
  'agent.subAgentDetail.transfer.topup.body': 'ਐਟਮਿਕ ਟ੍ਰਾਂਸਫ਼ਰ: ਤੁਹਾਡਾ ਵਾਲਿਟ ਘਟਦਾ ਹੈ, ਉਨ੍ਹਾਂ ਦਾ ਵਧਦਾ ਹੈ। ਦੋਨੋਂ ਪੂਲ ਨਕਾਰਾਤਮਕ ਹੋ ਸਕਦੇ ਹਨ (ਸਾਈਨਡ-ਪੂਲ ਡਿਜ਼ਾਈਨ)।',
  'agent.subAgentDetail.transfer.withdraw.body': 'ਐਟਮਿਕ ਉਲਟ ਟ੍ਰਾਂਸਫ਼ਰ: ਉਨ੍ਹਾਂ ਦਾ ਵਾਲਿਟ ਘਟਦਾ ਹੈ, ਤੁਹਾਡਾ ਵਧਦਾ ਹੈ। ਜਦੋਂ ਸਬ-ਏਜੰਟ ਤੋਂ ਪੇਆਉਟ ਬੇਨਤੀ ਸੈਟਲ ਕਰਨੀ ਹੋਵੇ ਤਾਂ ਇਹ ਵਰਤੋ।',
  'agent.subAgentDetail.transfer.amountLabel': 'ਰਕਮ (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'ਨੋਟ (ਵਿਕਲਪਿਕ)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'ਉਦਾਹਰਨ: ਹਫ਼ਤਾਵਾਰੀ ਟੌਪ-ਅੱਪ',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'ਉਦਾਹਰਨ: ਪੇਆਉਟ ਸੈਟਲਮੈਂਟ',
  'agent.subAgentDetail.transfer.error.amountPositive': 'ਰਕਮ ਸਕਾਰਾਤਮਕ ਪੂਰਨ ਅੰਕ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ।',
  'agent.subAgentDetail.transfer.confirm.topup': 'ਆਪਣੇ ਪੂਲ ਤੋਂ @{username} ਨੂੰ {amount} ਟ੍ਰਾਂਸਫ਼ਰ ਕਰਨੇ ਹਨ? ਤੁਹਾਡਾ ਬੈਲੇਂਸ ਠੀਕ ਇਸੇ ਰਕਮ ਨਾਲ ਘਟੇਗਾ।',
  'agent.subAgentDetail.transfer.confirm.withdraw': '@{username} ਦੇ ਪੂਲ ਤੋਂ {amount} ਆਪਣੇ ਵਿੱਚ ਖਿੱਚਣੇ ਹਨ? ਉਨ੍ਹਾਂ ਦਾ ਬੈਲੇਂਸ ਠੀਕ ਇਸੇ ਰਕਮ ਨਾਲ ਘਟੇਗਾ।',
  'agent.subAgentDetail.transfer.toast.topup': '@{username} ਨੂੰ {amount} ਭੇਜੇ। ਉਨ੍ਹਾਂ ਦਾ ਬੈਲੇਂਸ: {childBalance}।',
  'agent.subAgentDetail.transfer.toast.withdraw': '@{username} ਤੋਂ {amount} ਵਾਪਸ ਖਿੱਚੇ। ਉਨ੍ਹਾਂ ਦਾ ਬੈਲੇਂਸ: {childBalance}।',
  'agent.subAgentDetail.transfer.cta.working': 'ਕੰਮ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.subAgentDetail.transfer.cta.suspended': 'ਸਬ-ਏਜੰਟ ਮੁਅੱਤਲ',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} ਟ੍ਰਾਂਸਫ਼ਰ',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} ਕਢਵਾਓ',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'ਰੇਟ ਲੋਡ ਹੋ ਰਹੇ ਹਨ…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'ਹਰ ਕਾਰਡ ਇੱਕ ਕਮਿਸ਼ਨ ਪਿੱਲਰ ਹੈ; ਅੰਦਰ ਹਰ ਕਤਾਰ ਇੱਕ (ਪਿੱਲਰ × ਗੇਮ) ਹਿੱਸਾ ਹੈ। ਹਰ ਇਨਪੁਟ ਦੇ ਅੱਗੇ ਦਿਖਾਈ ',
  'agent.subAgentDetail.rates.intro.ceiling': 'ਸੀਮਾ',
  'agent.subAgentDetail.rates.intro.part2': ' ਤੁਹਾਡਾ ਆਪਣਾ ਰੇਟ ਹੈ — ਗੋਲਡਨ ਲਿਮਿਟ ਅਨੁਸਾਰ, ਤੁਸੀਂ ਬੱਚੇ ਨੂੰ ਉਸ ਤੋਂ ਵੱਧ ਨਹੀਂ ਦੇ ਸਕਦੇ। ਜੋ ਵੀ ਮੁੱਲ ਤੁਸੀਂ ਬਦਲਦੇ ਹੋ ਸੋਧਯੋਗ ਬਣ ਜਾਂਦਾ ਹੈ; ਕਮਿਟ ਲਈ ',
  'agent.subAgentDetail.rates.intro.applyWord': 'ਲਾਗੂ',
  'agent.subAgentDetail.rates.intro.part3': ' ਕਲਿੱਕ ਕਰੋ। ਕੈਸਕੇਡ ਕਲੈਂਪ ਸਰਵਰ-ਸਾਈਡ ਉੱਤੇ ਲਾਗੂ ਹੁੰਦਾ ਹੈ।',
  'agent.subAgentDetail.rates.toast.applied': 'ਰੇਟ ਲਾਗੂ ਹੋਏ।',
  'agent.subAgentDetail.rates.confirm.one': '{count} ਰੇਟ ਬਦਲਾਅ ਲਾਗੂ ਕਰਨਾ ਹੈ? ਗੋਲਡਨ-ਲਿਮਿਟ + ਕੈਸਕੇਡ ਕਲੈਂਪ ਸਰਵਰ-ਸਾਈਡ ਲਾਗੂ ਹੋਣਗੇ।',
  'agent.subAgentDetail.rates.confirm.other': '{count} ਰੇਟ ਬਦਲਾਅ ਲਾਗੂ ਕਰਨੇ ਹਨ? ਗੋਲਡਨ-ਲਿਮਿਟ + ਕੈਸਕੇਡ ਕਲੈਂਪ ਸਰਵਰ-ਸਾਈਡ ਲਾਗੂ ਹੋਣਗੇ।',
  'agent.subAgentDetail.rates.error.blocked': 'ਬਲਾਕ: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'ਜਿੱਤ/ਹਾਰ ਨਫ਼ਾ %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ਆਫ਼ਰ ਇਨਾਮ %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— ਗਲੋਬਲ —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ ਸੀਮਾ {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'ਪੇਰੈਂਟ ਤੋਂ ਵੱਧ (ਗੋਲਡਨ ਲਿਮਿਟ)',
  'agent.subAgentDetail.rates.row.editedBadge': 'ਸੋਧਿਆ',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} ਬਦਲਾਅ ਖਾਰਜ',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} ਬਦਲਾਅ ਖਾਰਜ',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} ਬਦਲਾਅ ਲਾਗੂ',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} ਬਦਲਾਅ ਲਾਗੂ',
  'agent.subAgentDetail.rates.cta.applying': 'ਲਾਗੂ ਹੋ ਰਿਹਾ ਹੈ…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'ਸਥਿਤੀ',
  'agent.subAgentDetail.ops.status.body': 'ਮੁਅੱਤਲ ਕਰਨਾ ਸਬ-ਏਜੰਟ ਦੀਆਂ ਲਾਗਇਨਾਂ ਅਤੇ ਡਾਊਨਲਾਈਨ ਓਪਰੇਸ਼ਨ ਨੂੰ ਬਲਾਕ ਕਰ ਦਿੰਦਾ ਹੈ ਜਦ ਤੱਕ ਤੁਸੀਂ ਮੁੜ ਸਰਗਰਮ ਉੱਤੇ ਨਹੀਂ ਫਲਿੱਪ ਕਰਦੇ। ਕਾਰਨ ਵਿਕਲਪਿਕ ਹੈ ਪਰ ਆਡਿਟ ਟ੍ਰੇਲ ਵਿੱਚ ਜਾਂਦਾ ਹੈ।',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'ਕਾਰਨ (ਵਿਕਲਪਿਕ)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'ਸਬ-ਏਜੰਟ ਮੁਅੱਤਲ',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'ਸਬ-ਏਜੰਟ ਅਣ-ਮੁਅੱਤਲ',
  'agent.subAgentDetail.ops.status.cta.working': 'ਕੰਮ ਹੋ ਰਿਹਾ ਹੈ…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username} ਮੁਅੱਤਲ ਕਰਨਾ ਹੈ? ਮੁਅੱਤਲ ਹੋਣ ਉੱਤੇ ਉਹ ਲਾਗਇਨ ਜਾਂ ਕੰਮ ਨਹੀਂ ਕਰ ਸਕਣਗੇ।',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username} ਅਣ-ਮੁਅੱਤਲ ਕਰਨਾ ਹੈ? ਉਨ੍ਹਾਂ ਨੂੰ ਪੂਰੀ ਪਹੁੰਚ ਮਿਲ ਜਾਵੇਗੀ।',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} ਹੁਣ ਮੁਅੱਤਲ ਹੈ।',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} ਹੁਣ ਸਰਗਰਮ ਹੈ।',

  'agent.subAgentDetail.ops.password.title': 'ਪਾਸਵਰਡ ਰੀਸੈੱਟ',
  'agent.subAgentDetail.ops.password.body': 'ਸਬ-ਏਜੰਟ ਲਈ ਨਵਾਂ ਪਾਸਵਰਡ ਸੈੱਟ ਕਰਦਾ ਹੈ ਅਤੇ ਉਨ੍ਹਾਂ ਦੇ ਮੌਜੂਦਾ ਰਿਫ੍ਰੈਸ਼ ਟੋਕਨ ਰੱਦ ਕਰਦਾ ਹੈ। ਉਨ੍ਹਾਂ ਨੂੰ ਨਵੇਂ ਪਾਸਵਰਡ ਨਾਲ ਮੁੜ ਲਾਗਇਨ ਕਰਨ ਲਈ ਮਜਬੂਰ ਕੀਤਾ ਜਾਵੇਗਾ। ਇਹ ਸੁਰੱਖਿਅਤ ਚੈਨਲ ਰਾਹੀਂ ਉਨ੍ਹਾਂ ਨਾਲ ਸਾਂਝਾ ਕਰੋ।',
  'agent.subAgentDetail.ops.password.placeholder': 'ਨਵਾਂ ਪਾਸਵਰਡ (ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ)',
  'agent.subAgentDetail.ops.password.confirm': '@{username} ਦਾ ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਕਰਨਾ ਹੈ? ਉਨ੍ਹਾਂ ਦੇ ਮੌਜੂਦਾ ਸੈਸ਼ਨ ਅਯੋਗ ਕਰ ਦਿੱਤੇ ਜਾਣਗੇ ਅਤੇ ਉਨ੍ਹਾਂ ਨੂੰ ਨਵੇਂ ਪਾਸਵਰਡ ਨਾਲ ਲਾਗਇਨ ਕਰਨਾ ਪਵੇਗਾ।',
  'agent.subAgentDetail.ops.password.toast.reset': 'ਪਾਸਵਰਡ ਰੀਸੈੱਟ। ਨਵਾਂ ਪਾਸਵਰਡ @{username} ਨਾਲ ਸਾਂਝਾ ਕਰੋ।',
  'agent.subAgentDetail.ops.password.error.tooShort': 'ਪਾਸਵਰਡ ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰਾਂ ਦਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ।',
  'agent.subAgentDetail.ops.password.cta.reset': 'ਪਾਸਵਰਡ ਰੀਸੈੱਟ',
  'agent.subAgentDetail.ops.password.cta.resetting': 'ਰੀਸੈੱਟ ਹੋ ਰਿਹਾ ਹੈ…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'ਪਲੇਅਰਾਂ ਉੱਤੇ ਵਾਪਸ',
  'agent.playerDetail.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.playerDetail.error.loadFailed': 'ਯੂਜ਼ਰ ਲੋਡ ਕਰਨ ਵਿੱਚ ਅਸਫਲ',

  // Header card.
  'agent.playerDetail.header.noName': '(ਕੋਈ ਨਾਮ ਸੈੱਟ ਨਹੀਂ)',
  'agent.playerDetail.header.joined': '{date} ਨੂੰ ਜੁੜੇ',
  'agent.playerDetail.header.ownedBy.prefix': ' · ਮਾਲਕ ',
  'agent.playerDetail.header.walletLabel': 'ਵਾਲਿਟ',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'ਬਕਾਇਆ',
  'agent.playerDetail.status.active': 'ਸਰਗਰਮ',
  'agent.playerDetail.status.suspended': 'ਮੁਅੱਤਲ',
  'agent.playerDetail.status.banned': 'ਬੈਨ',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'ਪਛਾਣ',
  'agent.playerDetail.tab.profile': 'ਪ੍ਰੋਫਾਈਲ',
  'agent.playerDetail.tab.wallet': 'ਵਾਲਿਟ',
  'agent.playerDetail.tab.security': 'ਸੁਰੱਖਿਆ',
  'agent.playerDetail.tab.ownership': 'ਮਾਲਕੀ',
  'agent.playerDetail.tab.operations': 'ਓਪਰੇਸ਼ਨ',

  // Identity tab.
  'agent.playerDetail.identity.title': 'ਪਛਾਣ',
  'agent.playerDetail.identity.row.username': 'ਯੂਜ਼ਰਨੇਮ',
  'agent.playerDetail.identity.row.email': 'ਈਮੇਲ',
  'agent.playerDetail.identity.row.phone': 'ਫ਼ੋਨ',
  'agent.playerDetail.identity.row.country': 'ਦੇਸ਼',
  'agent.playerDetail.identity.row.joined': 'ਜੁੜੇ',
  'agent.playerDetail.identity.row.emailVerified': 'ਈਮੇਲ ਤਸਦੀਕ',
  'agent.playerDetail.identity.row.telegram': 'ਟੈਲੀਗ੍ਰਾਮ',
  'agent.playerDetail.identity.row.failedLogins': 'ਅਸਫਲ ਲਾਗਇਨ',
  'agent.playerDetail.identity.row.lockedUntil': 'ਲੌਕ ਤੱਕ',
  'agent.playerDetail.identity.value.yes': 'ਹਾਂ',
  'agent.playerDetail.identity.value.no': 'ਨਹੀਂ',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'ਇਸ ਪਲੇਅਰ ਨੂੰ ਟ੍ਰਾਂਸਫ਼ਰ ਕਰੋ',
  'agent.playerDetail.identity.transfer.body': 'ਉਨ੍ਹਾਂ ਦੇ ਯੂਨੀਕ ਏਜੰਟ ID ਰਾਹੀਂ ਕਿਸੇ ਹੋਰ ਏਜੰਟ ਨੂੰ ਸੌਂਪੋ — ਪੂਰੇ ਨੈੱਟਵਰਕ ਵਿੱਚ ਕੰਮ ਕਰਦਾ ਹੈ, ਸਿਰਫ਼ ਤੁਹਾਡੀ ਡਾਊਨਲਾਈਨ ਵਿੱਚ ਨਹੀਂ।',
  'agent.playerDetail.identity.transfer.cta': 'UAID ਰਾਹੀਂ ਟ੍ਰਾਂਸਫ਼ਰ ਕਰੋ',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'ਫੋਟੋ ਬਦਲੋ',
  'agent.playerDetail.avatar.cropTitle': '@{username} ਲਈ ਫੋਟੋ ਅਡਜਸਟ ਕਰੋ',
  'agent.playerDetail.avatar.toast.updated': 'ਫੋਟੋ ਅੱਪਡੇਟ ਹੋਈ',
  'agent.playerDetail.avatar.error.uploadFailed': 'ਅੱਪਲੋਡ ਅਸਫਲ',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'ਪਲੇਅਰ UAID ਰਾਹੀਂ ਟ੍ਰਾਂਸਫ਼ਰ ਹੋਇਆ',
  'agent.playerDetail.transfer.toast.agent': '@{username} ਸਫਲਤਾਪੂਰਵਕ ਟ੍ਰਾਂਸਫ਼ਰ ਹੋਇਆ',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'ਖਾਤਾ ਸਥਿਤੀ',
  'agent.playerDetail.ops.accountStatus.body': 'ਸਰਗਰਮ ਦਾ ਮਤਲਬ ਪਲੇਅਰ ਲਾਗਇਨ ਅਤੇ ਖੇਡ ਸਕਦਾ ਹੈ। ਮੁਅੱਤਲ ਲਾਗਇਨ ਬਲਾਕ ਕਰਦਾ ਹੈ ਜਦ ਤੱਕ ਤੁਸੀਂ ਮੁੜ ਫਲਿੱਪ ਨਹੀਂ ਕਰਦੇ। ਬੈਨ ਇੱਕ ਮਜ਼ਬੂਤ ਲੌਕ ਹੈ — ਨੀਤੀ ਉਲੰਘਣਾ ਲਈ ਰੱਖੋ।',
  'agent.playerDetail.ops.statusBtn.activate': 'ਸਰਗਰਮ',
  'agent.playerDetail.ops.statusBtn.suspend': 'ਮੁਅੱਤਲ',
  'agent.playerDetail.ops.statusBtn.ban': 'ਬੈਨ',
  'agent.playerDetail.ops.statusBtn.current': 'ਮੌਜੂਦਾ',
  'agent.playerDetail.ops.statusBtn.toast': 'ਯੂਜ਼ਰ ਹੁਣ {status} ਹੈ',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'ਖ਼ਤਰਾ ਖੇਤਰ',
  'agent.playerDetail.ops.danger.body': 'ਹਾਰਡ-ਡਿਲੀਟ ਕਰਨ ਨਾਲ ਯੂਜ਼ਰ ਰਿਕਾਰਡ ਹਟ ਜਾਂਦਾ ਹੈ। ਆਡਿਟ ਲਈ ਵਾਲਿਟ ਲੈਜਰ ਸੁਰੱਖਿਅਤ ਰਹਿੰਦਾ ਹੈ, ਪਰ ਪਲੇਅਰ ਸਾਰੀ ਪਹੁੰਚ ਗੁਆ ਬੈਠੇਗਾ। ਇਹ ਕਾਰਵਾਈ ਵਾਪਸ ਨਹੀਂ ਹੋ ਸਕਦੀ।',
  'agent.playerDetail.ops.delete.eyebrow': 'ਖ਼ਤਰਾ ਖੇਤਰ',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'ਇਸ ਖਾਤੇ ਨੂੰ ਸਥਾਈ ਤੌਰ ਉੱਤੇ ਮਿਟਾਓ। ਇਹ ਵਾਪਸ ਨਹੀਂ ਹੋ ਸਕਦਾ। ਪੁਸ਼ਟੀ ਲਈ ',
  'agent.playerDetail.ops.delete.confirm.after': ' ਟਾਈਪ ਕਰੋ।',
  'agent.playerDetail.ops.delete.cta': 'ਮਿਟਾਓ',
  'agent.playerDetail.ops.delete.toast': 'ਯੂਜ਼ਰ ਮਿਟਾਇਆ',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'ਵਾਲਿਟ ਅਡਜਸਟ ਕਰੋ',
  'agent.playerDetail.walletAdjust.mode.credit': '+ ਕ੍ਰੈਡਿਟ',
  'agent.playerDetail.walletAdjust.mode.debit': '− ਡੈਬਿਟ',
  'agent.playerDetail.walletAdjust.field.amount': 'ਰਕਮ (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'ਕਾਰਨ · ਲੈਜਰ ਵਿੱਚ ਸਟੋਰ',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'ਉਦਾਹਰਨ: ਡਿਪਾਜ਼ਿਟ ਉਲਟਾਈ, ਬੋਨਸ, ਰਿਫੰਡ',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'ਰਿਚਾਰਜ ਵਜੋਂ ਮਾਰਕ ਕਰੋ',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'ਇਸ ਕ੍ਰੈਡਿਟ ਨੂੰ ਲੈਜਰ ਵਿੱਚ ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'ਰਿਚਾਰਜ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' ਵਜੋਂ ਰਿਕਾਰਡ ਕਰੋ। ਜੇ ਇਸ ਯੂਜ਼ਰ ਨੂੰ ਕਿਸੇ ਨੇ ਰੈਫ਼ਰ ਕੀਤਾ ਸੀ, ਤਾਂ ਰੈਫ਼ਰਰ ਨੂੰ ਆਪਣੇ ਆਪ ਬੋਨਸ ਮਿਲਦਾ ਹੈ।',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'ਸਕਾਰਾਤਮਕ ਰਕਮ ਦਰਜ ਕਰੋ',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'ਕਾਰਨ ਜ਼ਰੂਰੀ ਹੈ',
  'agent.playerDetail.walletAdjust.cta.applying': 'ਲਾਗੂ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'ਕ੍ਰੈਡਿਟ ਲਾਗੂ ਕਰੋ',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'ਡੈਬਿਟ ਲਾਗੂ ਕਰੋ',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} ਕ੍ਰੈਡਿਟ',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} ਡੈਬਿਟ',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · ਰੈਫ਼ਰਰ ਨੇ {bonus} ਬੋਨਸ ਕਮਾਇਆ',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'ਇਸ ਪਲੇਅਰ ਨੂੰ ਰਿਚਾਰਜ ਕਰੋ',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': ' ਨੂੰ ਟੌਪ-ਅੱਪ ਕਰਨ ਨਾਲ ',
  'agent.playerDetail.rechargeCta.body.bold': 'ਤੁਹਾਡੇ ਵਾਲਿਟ',
  'agent.playerDetail.rechargeCta.body.after': ' ਤੋਂ ਉਨ੍ਹਾਂ ਦੇ ਵਿੱਚ ਕੋਇਨ ਟ੍ਰਾਂਸਫ਼ਰ ਹੁੰਦੇ ਹਨ। ਪਹਿਲਾਂ ਤੁਹਾਡਾ ਪੂਲ ਚੈੱਕ ਹੁੰਦਾ ਹੈ — ਟ੍ਰਾਂਸਫ਼ਰ ਐਟਮਿਕ ਹੈ ਅਤੇ ਜੇ ਤੁਹਾਡੇ ਕੋਲ ਨਾਕਾਫ਼ੀ ਬੈਲੇਂਸ ਹੈ ਤਾਂ ਰੱਦ ਹੋ ਜਾਂਦਾ ਹੈ।',
  'agent.playerDetail.rechargeCta.thisPlayer': 'ਇਸ ਪਲੇਅਰ',
  'agent.playerDetail.rechargeCta.cta': 'ਰਿਚਾਰਜ ਫਾਰਮ ਖੋਲ੍ਹੋ',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'ਪਾਸਵਰਡ ਰੀਸੈੱਟ',
  'agent.playerDetail.security.subtitle': 'ਨਵਾਂ ਪਾਸਵਰਡ ਸੈੱਟ ਕਰਦਾ ਹੈ ਅਤੇ ਯੂਜ਼ਰ ਨੂੰ ਹਰ ਡਿਵਾਈਸ ਤੋਂ ਸਾਈਨ ਆਉਟ ਕਰਦਾ ਹੈ।',
  'agent.playerDetail.security.field.new': 'ਨਵਾਂ ਪਾਸਵਰਡ',
  'agent.playerDetail.security.field.confirm': 'ਪੁਸ਼ਟੀ ਕਰੋ',
  'agent.playerDetail.security.error.mismatch': 'ਪਾਸਵਰਡ ਮੇਲ ਨਹੀਂ ਖਾਂਦੇ',
  'agent.playerDetail.security.error.tooShort': 'ਘੱਟੋ-ਘੱਟ 8 ਅੱਖਰ, ਅੱਖਰਾਂ ਅਤੇ ਅੰਕਾਂ ਨਾਲ',
  'agent.playerDetail.security.toast.reset': 'ਪਾਸਵਰਡ ਰੀਸੈੱਟ ਅਤੇ ਸਾਰੇ ਸੈਸ਼ਨ ਰੱਦ',
  'agent.playerDetail.security.cta.reset': 'ਪਾਸਵਰਡ ਰੀਸੈੱਟ',
  'agent.playerDetail.security.cta.resetting': 'ਰੀਸੈੱਟ ਹੋ ਰਿਹਾ ਹੈ…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'ਵਾਲਿਟ ਲੈਜਰ',
  'agent.playerDetail.ledger.entries.one': '{count} ਐਂਟਰੀ',
  'agent.playerDetail.ledger.entries.other': '{count} ਐਂਟਰੀਆਂ',
  'agent.playerDetail.ledger.empty': 'ਅਜੇ ਕੋਈ ਲੈਣ-ਦੇਣ ਨਹੀਂ।',
  'agent.playerDetail.ledger.loading': 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…',
  'agent.playerDetail.ledger.actorSuffix': ' · ਐਡਮਿਨ #{id} ਵੱਲੋਂ',
  'agent.playerDetail.ledger.pagination.prev': 'ਪਿਛਲਾ',
  'agent.playerDetail.ledger.pagination.next': 'ਅਗਲਾ',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'ਮੈਚ ਬੇਟ',
  'agent.playerDetail.ledger.kind.win': 'ਮੈਚ ਇਨਾਮ',
  'agent.playerDetail.ledger.kind.refund': 'ਰਿਫੰਡ',
  'agent.playerDetail.ledger.kind.fee': 'ਪਲੇਟਫਾਰਮ ਫੀਸ',
  'agent.playerDetail.ledger.kind.adminCredit': 'ਐਡਮਿਨ ਕ੍ਰੈਡਿਟ',
  'agent.playerDetail.ledger.kind.adminDebit': 'ਐਡਮਿਨ ਡੈਬਿਟ',
  'agent.playerDetail.ledger.kind.recharge': 'ਰਿਚਾਰਜ',
  'agent.playerDetail.ledger.kind.referralBonus': 'ਰੈਫ਼ਰਲ ਬੋਨਸ',
  'agent.playerDetail.ledger.kind.signupBonus': 'ਸਾਈਨਅੱਪ ਬੋਨਸ',
  'agent.playerDetail.ledger.kind.offerCredit': 'ਆਫ਼ਰ ਕ੍ਰੈਡਿਟ',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'ਨਿਕਾਸੀ',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'ਰਿਫੰਡ',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'ਪਲੇਟਫਾਰਮ',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'ਪ੍ਰੋਫਾਈਲ ਸੋਧੋ',
  'agent.playerDetail.profile.field.username': 'ਯੂਜ਼ਰਨੇਮ',
  'agent.playerDetail.profile.field.email': 'ਈਮੇਲ',
  'agent.playerDetail.profile.field.phone': 'ਫ਼ੋਨ',
  'agent.playerDetail.profile.field.firstName': 'ਪਹਿਲਾ ਨਾਮ',
  'agent.playerDetail.profile.field.lastName': 'ਆਖਰੀ ਨਾਮ',
  'agent.playerDetail.profile.field.dob': 'ਜਨਮ ਮਿਤੀ',
  'agent.playerDetail.profile.field.gender': 'ਲਿੰਗ',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'ਪੁਰਸ਼',
  'agent.playerDetail.profile.gender.female': 'ਔਰਤ',
  'agent.playerDetail.profile.gender.other': 'ਹੋਰ',
  'agent.playerDetail.profile.gender.preferNot': 'ਨਹੀਂ ਦੱਸਣਾ',
  // Location section.
  'agent.playerDetail.profile.location.title': 'ਟਿਕਾਣਾ',
  'agent.playerDetail.profile.location.lastUpdated': '· ਆਖਰੀ ਅੱਪਡੇਟ {date}',
  'agent.playerDetail.profile.location.legacy.label': 'ਫ਼ਾਈਲ ਉੱਤੇ ਪੁਰਾਣਾ ਪਤਾ',
  'agent.playerDetail.profile.location.legacy.note': ' (ਸਿਰਫ਼-ਪੜ੍ਹਨਯੋਗ — ਪੂਰਵ-R3, ਲੋਕੇਟਰ ਦੁਆਰਾ ਨਹੀਂ ਵਰਤਿਆ ਜਾਂਦਾ):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'ਸੰਭਾਲਣ ਨੂੰ ਕੁਝ ਨਹੀਂ',
  'agent.playerDetail.profile.toast.saved.one': '{count} ਖੇਤਰ ਸੰਭਾਲਿਆ',
  'agent.playerDetail.profile.toast.saved.other': '{count} ਖੇਤਰ ਸੰਭਾਲੇ',
  'agent.playerDetail.profile.cta.save': 'ਬਦਲਾਅ ਸੰਭਾਲੋ',
  'agent.playerDetail.profile.cta.saving': 'ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…',
  'agent.playerDetail.profile.cta.reset': 'ਰੀਸੈੱਟ',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'ਮਾਲਕ ਏਜੰਟ',
  'agent.playerDetail.ownership.row.eyebrow': 'ਮਾਲਕੀ',
  'agent.playerDetail.ownership.platform.title': 'ਪਲੇਟਫਾਰਮ-ਸਿੱਧਾ',
  'agent.playerDetail.ownership.platform.body': 'ਇਸ ਪਲੇਅਰ ਦਾ ਕੋਈ ਏਜੰਟ ਮਾਲਕ ਨਹੀਂ। ਐਡਮਿਨ ਸਿੱਧਾ ਪ੍ਰਬੰਧਿਤ ਕਰਦਾ ਹੈ।',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ਏਜੰਟ #{id}',
  'agent.playerDetail.ownership.owned.body': 'ਇਸ ਪਲੇਅਰ ਦੇ ਰਿਚਾਰਜ + ਮੈਚਾਂ ਉੱਤੇ ਕਮਿਸ਼ਨ ਕਮਾਉਂਦਾ ਹੈ।',
  'agent.playerDetail.ownership.cta.change': 'ਬਦਲੋ',
  'agent.playerDetail.ownership.toast.changedTo': 'ਮਾਲਕੀ → {label}',
  'agent.playerDetail.ownership.platformLabel': 'ਪਲੇਟਫਾਰਮ-ਸਿੱਧਾ',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username} ਨੂੰ ਮੁੜ-ਨਿਯੁਕਤ ਕਰੋ',
  'agent.playerDetail.ownership.picker.body': 'ਨਵਾਂ ਏਜੰਟ ਚੁਣੋ — ਜਾਂ ਪਲੇਟਫਾਰਮ-ਸਿੱਧੇ ਲਈ ਡਿਟੈਚ ਕਰੋ (ਇਸ ਪਲੇਅਰ ਦਾ ਕੋਈ ਏਜੰਟ ਮਾਲਕ ਨਹੀਂ)। ਭਵਿੱਖ ਦੇ ਕਮਿਸ਼ਨ ਨਵੇਂ ਮਾਲਕ ਨੂੰ ਜਾਂਦੇ ਹਨ; ਪਹਿਲਾਂ ਹੀ ਕ੍ਰੈਡਿਟ ਕੀਤੀਆਂ ਕਤਾਰਾਂ ਨੂੰ ਛੂਹਿਆ ਨਹੀਂ ਜਾਂਦਾ।',
  'agent.playerDetail.ownership.picker.detach.title': 'ਪਲੇਟਫਾਰਮ ਨੂੰ ਡਿਟੈਚ',
  'agent.playerDetail.ownership.picker.detach.body': 'ਏਜੰਟ ਮਾਲਕੀ ਹਟਾਓ। ਐਡਮਿਨ ਸਿੱਧਾ ਪ੍ਰਬੰਧਿਤ ਕਰਦਾ ਹੈ।',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ਏਜੰਟ ਖੋਜੋ…',
  'agent.playerDetail.ownership.picker.loading': 'ਏਜੰਟ ਲੋਡ ਹੋ ਰਹੇ ਹਨ…',
  'agent.playerDetail.ownership.picker.empty.search': 'ਤੁਹਾਡੀ ਖੋਜ ਨਾਲ ਕੋਈ ਏਜੰਟ ਮੇਲ ਨਹੀਂ ਖਾਂਦਾ।',
  'agent.playerDetail.ownership.picker.empty.none': 'ਅਜੇ ਕੋਈ ਸਰਗਰਮ ਏਜੰਟ ਨਹੀਂ।',
  'agent.playerDetail.ownership.picker.players.one': '{count} ਪਲੇਅਰ',
  'agent.playerDetail.ownership.picker.players.other': '{count} ਪਲੇਅਰ',
  'agent.playerDetail.ownership.picker.current': 'ਮੌਜੂਦਾ',
  'agent.playerDetail.ownership.picker.currentUpper': 'ਮੌਜੂਦਾ',
  'agent.playerDetail.ownership.picker.close': 'ਬੰਦ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'ਬਾਲੀ ਏਜੰਟ',
  'agent.login.subtitle': 'ਆਪਣੇ ਪਲੇਅਰ ਪ੍ਰਬੰਧਿਤ ਕਰਨ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ',
  'agent.login.field.identifier': 'ਯੂਜ਼ਰਨੇਮ ਜਾਂ ਈਮੇਲ',
  'agent.login.field.password': 'ਪਾਸਵਰਡ',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'ਸਿਰਫ਼ ਏਜੰਟ। ਜੇ ਤੁਸੀਂ ਪਲੇਟਫਾਰਮ ਮਾਲਕ ਹੋ, ਵਰਤੋ ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '।',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'ਲਾਗਇਨ ਅਸਫਲ',
  'agent.login.error.invalidCredentials': 'ਅਯੋਗ ਪ੍ਰਮਾਣ-ਪੱਤਰ',
  'agent.login.error.accountSuspended': 'ਖਾਤਾ ਮੁਅੱਤਲ — ਆਪਣੇ ਪ੍ਰਸ਼ਾਸਕ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।',
  'agent.login.error.networkError': 'ਨੈੱਟਵਰਕ ਗਲਤੀ — ਆਪਣਾ ਕੁਨੈਕਸ਼ਨ ਚੈੱਕ ਕਰੋ ਅਤੇ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'ਗੇਮ',
  'agent.misc.gameTile.actionSuffix': '· ਕੌਨਫ਼ਿਗਰ',
};

const ml: Dict = {
  // --- UX Phase 9 (2026-08-08): player profile + wallet i18n ---
  'profile.group.myDetails': "എന്റെ വിവരങ്ങൾ",
  'profile.group.security': "സൈൻ-ഇൻ & സുരക്ഷ",
  'profile.personal.title': "വ്യക്തിഗത വിവരങ്ങൾ",
  'profile.personal.editTitle': "വ്യക്തിഗത വിവരങ്ങൾ എഡിറ്റ് ചെയ്യുക",
  'common.edit': "എഡിറ്റ്",
  'profile.field.name': "പേര്",
  'profile.field.phone': "ഫോൺ നമ്പർ",
  'profile.field.gender': "ലിംഗം",
  'profile.field.location': "സ്ഥലം",
  'profile.gender.male': "പുരുഷൻ",
  'profile.gender.female': "സ്ത്രീ",
  'profile.gender.other': "മറ്റുള്ളവ",
  'profile.gender.preferNot': "പറയാൻ ആഗ്രഹിക്കുന്നില്ല",
  'profile.gender.select': "തിരഞ്ഞെടുക്കുക…",
  'profile.location.optionalHint': "ഓപ്ഷണൽ — നിങ്ങളുടെ ഏരിയ തിരഞ്ഞെടുക്കുക അല്ലെങ്കിൽ പിൻകോഡ് ടൈപ്പ് ചെയ്യുക (GPS കൂടുതൽ കൃത്യമാക്കും).",
  'profile.lang.title': "ഭാഷ",
  'profile.password.title': "പാസ്‌വേഡ്",
  'profile.password.desc': "നിങ്ങളുടെ അക്കൗണ്ട് പാസ്‌വേഡ് മാറ്റുക. മറ്റ് ഉപകരണങ്ങൾ സൈൻ ഔട്ട് ചെയ്യപ്പെടും.",
  'profile.password.cta': "മാറ്റുക",
  'profile.password.editTitle': "പാസ്‌വേഡ് മാറ്റുക",
  'profile.password.current': "നിലവിലെ പാസ്‌വേഡ്",
  'profile.password.new': "പുതിയ പാസ്‌വേഡ്",
  'profile.password.confirm': "പുതിയ പാസ്‌വേഡ് സ്ഥിരീകരിക്കുക",
  'profile.password.currentPh': "നിങ്ങളുടെ നിലവിലെ പാസ്‌വേഡ്",
  'profile.password.newPh': "ശക്തമായ പാസ്‌വേഡ് തിരഞ്ഞെടുക്കുക",
  'profile.password.confirmPh': "പുതിയ പാസ്‌വേഡ് വീണ്ടും ടൈപ്പ് ചെയ്യുക",
  'profile.password.rule': "കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ, ഒരു അക്ഷരവും ഒരു അക്കവും ഉൾപ്പെടെ.",
  'profile.telegram.title': "Telegram",
  'profile.telegram.linkedPrefix': "ലിങ്ക് ചെയ്തത്",
  'profile.telegram.otpHere': "പാസ്‌വേഡ് റീസെറ്റ് OTP-കൾ ഇവിടെ ലഭിക്കും.",
  'profile.telegram.unlinkChange': "അൺലിങ്ക് ചെയ്ത് മാറ്റുക",
  'profile.telegram.unlinkTitle': "Telegram അൺലിങ്ക് ചെയ്യണോ?",
  'profile.telegram.unlink': "അൺലിങ്ക്",
  'profile.telegram.keepLinked': "ലിങ്ക് നിലനിർത്തുക",
  'profile.telegram.unlinkVerified': "പിന്നീട് നിങ്ങൾക്ക് മറ്റൊരു Telegram അക്കൗണ്ട് വീണ്ടും ലിങ്ക് ചെയ്യാം. ഇമെയിൽ വെരിഫിക്കേഷൻ അതേപടി തുടരും.",
  'profile.telegram.unlinkOnly': "Telegram മാത്രമാണ് നിങ്ങളുടെ വെരിഫൈ ചെയ്ത ചാനൽ — കളി തുടരുന്നതിന് മുമ്പ് നിങ്ങളുടെ അക്കൗണ്ട് വീണ്ടും വെരിഫൈ ചെയ്യേണ്ടി വന്നേക്കാം.",
  'profile.telegram.notLinkedDesc': "ബാക്കപ്പായി പാസ്‌വേഡ് റീസെറ്റ് OTP-കൾ ലഭിക്കാൻ Telegram ലിങ്ക് ചെയ്യുക.",
  'profile.telegram.openBot': "@BaaliGames_bot തുറക്കുക",
  'profile.telegram.generate': "ലിങ്ക് ജനറേറ്റ് ചെയ്യുക",
  'profile.telegram.generating': "ജനറേറ്റ് ചെയ്യുന്നു…",
  'profile.avatar.cropTitle': "നിങ്ങളുടെ ഫോട്ടോ ക്രമീകരിക്കുക",
  'profile.toast.detailsSaved': "വിവരങ്ങൾ സേവ് ചെയ്തു",
  'profile.toast.photoUpdated': "ഫോട്ടോ അപ്‌ഡേറ്റ് ചെയ്തു",
  'profile.toast.pwChanged': "പാസ്‌വേഡ് മാറ്റി. മറ്റ് ഉപകരണങ്ങൾ സൈൻ ഔട്ട് ചെയ്തു.",
  'profile.toast.tgUnlinked': "Telegram അൺലിങ്ക് ചെയ്തു. മറ്റൊരു ചാറ്റ് കണക്റ്റ് ചെയ്യാൻ പുതിയ ലിങ്ക് ജനറേറ്റ് ചെയ്യുക.",
  'profile.err.phone': "10 അക്ക മൊബൈൽ നമ്പർ നൽകുക (അല്ലെങ്കിൽ മാറ്റാതെ വിടുക)",
  'profile.err.pwMismatch': "പുതിയ പാസ്‌വേഡുകൾ പൊരുത്തപ്പെടുന്നില്ല",
  'profile.err.pwWeak': "പാസ്‌വേഡിൽ 8+ പ്രതീകങ്ങളും ഒരു അക്ഷരവും ഒരു അക്കവും വേണം",
  'profile.err.pwSame': "പുതിയ പാസ്‌വേഡ് നിലവിലേതിൽ നിന്ന് വ്യത്യസ്തമായിരിക്കണം",
  'wallet.payout.title': "സേവ് ചെയ്ത പണം പിൻവലിക്കൽ വിവരങ്ങൾ",
  'wallet.payout.upiLabel': "UPI ID",
  'wallet.payout.none': "ഇതുവരെ ഒന്നും സേവ് ചെയ്തിട്ടില്ല",
  'wallet.payout.hint': "നിങ്ങൾ പണം പിൻവലിക്കുമ്പോൾ നിങ്ങളുടെ UPI ID സ്വയമേവ സേവ് ചെയ്യപ്പെടും. അടുത്ത പിൻവലിക്കൽ അഭ്യർത്ഥനയിൽ നിങ്ങൾക്ക് അത് അപ്‌ഡേറ്റ് ചെയ്യാം.",
  // ─── PWA install prompt — i18n 2026-05-25 ───
  "pwa.install.title": "{app} ഇൻസ്റ്റാൾ ചെയ്യുക",
  "pwa.install.subhint": "തൽക്ഷണ, ഫുൾ-സ്ക്രീൻ ആക്സസിനായി ഇത് നിങ്ങളുടെ ഹോം സ്ക്രീനിലേക്ക് ചേർക്കുക — ആപ്പ് സ്റ്റോർ ആവശ്യമില്ല.",
  "pwa.install.listen": "കേൾക്കുക",
  "pwa.install.listening": "നിർത്തുക",
  "pwa.install.installBtn": "ഇൻസ്റ്റാൾ ചെയ്യുക",
  "pwa.install.notNow": "ഇപ്പോൾ വേണ്ട",
  "pwa.install.howToShow": "എങ്ങനെ ഇൻസ്റ്റാൾ ചെയ്യാം",
  "pwa.install.howToHide": "ഘട്ടങ്ങൾ മറയ്ക്കുക",
  "pwa.install.guide.iosSafari": "Safari സ്ക്രീനിന്റെ താഴെ, Share ബട്ടൺ ടാപ്പ് ചെയ്യുക — ഇത് മുകളിലേക്ക് അമ്പടയാളമുള്ള ഒരു ചതുരം പോലെ കാണപ്പെടും.\nതുറക്കുന്ന മെനുവിൽ അൽപ്പം താഴേക്ക് സ്ക്രോൾ ചെയ്യുക.\n\"Add to Home Screen\" ഓപ്ഷൻ ടാപ്പ് ചെയ്യുക.\nമുകളിൽ വലത് മൂലയിൽ Add ടാപ്പ് ചെയ്യുക. ഇപ്പോൾ ആപ്പ് ഏതൊരു സാധാരണ ആപ്പും പോലെ നിങ്ങളുടെ ഹോം സ്ക്രീനിൽ ഉണ്ടാകും.",
  "pwa.install.guide.iosOther": "iPhone അല്ലെങ്കിൽ iPad-ൽ, ഹോം സ്ക്രീനിലേക്ക് ചേർക്കുന്നത് Safari ബ്രൗസറിൽ മാത്രമേ പ്രവർത്തിക്കൂ.\nSafari തുറന്ന് ഇതേ വെബ്സൈറ്റിലേക്ക് പോകുക.\nSafari-യുടെ താഴെ Share ബട്ടൺ ടാപ്പ് ചെയ്യുക — മുകളിലേക്ക് അമ്പടയാളമുള്ള ചതുരം.\nതാഴേക്ക് സ്ക്രോൾ ചെയ്ത് \"Add to Home Screen\" ടാപ്പ് ചെയ്ത്, പിന്നെ Add ടാപ്പ് ചെയ്യുക.",
  "pwa.install.guide.android": "മുകളിലുള്ള Install ബട്ടൺ ടാപ്പ് ചെയ്യുക.\nനിങ്ങളുടെ ബ്രൗസർ ചോദിക്കുമ്പോൾ, സ്ഥിരീകരിക്കാൻ Install ടാപ്പ് ചെയ്യുക.\nകഴിഞ്ഞു — ഇപ്പോൾ ആപ്പ് നിങ്ങളുടെ ഹോം സ്ക്രീനിലുണ്ട്. സാധാരണ ആപ്പ് പോലെ എപ്പോൾ വേണമെങ്കിലും തുറക്കുക.\nപോപ്പ്-അപ്പ് കാണുന്നില്ലേ? നിങ്ങളുടെ ബ്രൗസർ മെനു തുറക്കുക — മൂന്ന് കുത്തുകൾ, സാധാരണയായി മുകളിൽ വലത് — എന്നിട്ട് \"Install app\" ടാപ്പ് ചെയ്യുക.",
  // ─── Agent direct-recharge block — i18n 2026-05-25 ───
  "agent.directRecharge.title": "നേരിട്ടുള്ള റീചാർജ്",
  "agent.directRecharge.subtitle.player": "ഒരു കളിക്കാരനെ തിരഞ്ഞെടുത്ത് നിങ്ങളുടെ പൂളിൽ നിന്ന് റീചാർജ് ചെയ്യുക. അഭ്യർത്ഥന ആവശ്യമില്ല.",
  "agent.directRecharge.subtitle.subagent": "ഒരു സബ്-ഏജന്റിനെ തിരഞ്ഞെടുത്ത് നിങ്ങളുടെ പൂളിൽ നിന്ന് അവരുടെ പൂളിലേക്ക് റീചാർജ് ചെയ്യുക.",
  "agent.directRecharge.search.player": "റീചാർജ് ചെയ്യാൻ കളിക്കാരനെ തിരയുക (പേര് · ഫോൺ · ഇമെയിൽ)…",
  "agent.directRecharge.search.subagent": "റീചാർജ് ചെയ്യാൻ സബ്-ഏജന്റിനെ തിരയുക (പേര് · ഇമെയിൽ · UAID)…",
  "agent.directRecharge.clearSearch": "തിരയൽ മായ്ക്കുക",
  "agent.directRecharge.searching": "തിരയുന്നു…",
  "agent.directRecharge.noMatches": "പൊരുത്തങ്ങളില്ല.",
  "agent.directRecharge.empty.player": "നിങ്ങളുടെ പട്ടികയിൽ ഇതുവരെ കളിക്കാരില്ല.",
  "agent.directRecharge.empty.subagent": "ഇതുവരെ നേരിട്ടുള്ള സബ്-ഏജന്റുമാരില്ല.",
  "agent.directRecharge.searchFailed": "തിരയൽ പരാജയപ്പെട്ടു",
  "agent.directRecharge.label.wallet": "വാലറ്റ്",
  "agent.directRecharge.label.pool": "പൂൾ",
  "agent.directRecharge.modal.title.player": "നേരിട്ടുള്ള കളിക്കാരൻ റീചാർജ്",
  "agent.directRecharge.modal.title.subagent": "നേരിട്ടുള്ള സബ്-ഏജന്റ് റീചാർജ്",
  "agent.directRecharge.modal.close": "അടയ്ക്കുക",
  "agent.directRecharge.modal.targetPool": "ലക്ഷ്യ പൂൾ",
  "agent.directRecharge.modal.operatingWallet": "ഓപ്പറേറ്റിംഗ് വാലറ്റ്",
  "agent.directRecharge.modal.dayToDay": "ദൈനംദിന ബാലൻസ്",
  "agent.directRecharge.modal.securityDeposit": "സെക്യൂരിറ്റി ഡിപ്പോസിറ്റ്",
  "agent.directRecharge.modal.collateral": "ഈട് പൂൾ",
  "agent.directRecharge.modal.amount": "തുക (₹)",
  "agent.directRecharge.modal.overdraw": "നിങ്ങളുടെ പൂളിനെക്കാൾ (₹{pool}) കൂടുതൽ നൽകാനാവില്ല. തുക കുറയ്ക്കുക.",
  "agent.directRecharge.modal.reason": "കാരണം / കുറിപ്പ്",
  "agent.directRecharge.modal.reasonPlaceholder": "പണം ശേഖരിച്ചു · ref 4291",
  "agent.directRecharge.modal.reasonHint": "നിങ്ങളുടെ വാലറ്റ് ലെഡ്ജർ + ഓഡിറ്റ് ലോഗിൽ രേഖപ്പെടുത്തുന്നു.",
  "agent.directRecharge.modal.proofLabel": "പേയ്‌മെന്റിന്റെ സ്ക്രീൻഷോട്ട് അപ്‌ലോഡ് ചെയ്യുക (ഓപ്ഷണൽ)",
  "agent.directRecharge.modal.proofHint": "ചരിത്രം വഴി സ്വീകർത്താവിന് കാണാം.",
  "agent.directRecharge.modal.previewEmpty": "ഇടപാട് പ്രിവ്യൂ ചെയ്യാൻ തുക നൽകുക.",
  "agent.directRecharge.modal.previewBody": "നിങ്ങളുടെ വാലറ്റിൽ നിന്ന് {name} ന്റെ {pool}ലേക്ക് അയച്ചു. സുരക്ഷിത കൈമാറ്റം; ലെഡ്ജർ + ഓഡിറ്റിൽ രേഖപ്പെടുത്തി.",
  "agent.directRecharge.modal.pool.playerWallet": "വാലറ്റ്",
  "agent.directRecharge.modal.pool.operatingWallet": "ഓപ്പറേറ്റിംഗ് വാലറ്റ്",
  "agent.directRecharge.modal.pool.securityDeposit": "സെക്യൂരിറ്റി ഡിപ്പോസിറ്റ്",
  "agent.directRecharge.modal.cancel": "റദ്ദാക്കുക",
  "agent.directRecharge.modal.send": "₹{amount} അയയ്ക്കുക",
  "agent.directRecharge.badge.player": "കളിക്കാരൻ",
  "agent.directRecharge.badge.subagent": "സബ്-ഏജന്റ്",
  "agent.directRecharge.modal.yourDirectSubagent": "നിങ്ങളുടെ നേരിട്ടുള്ള സബ്-ഏജന്റ്",
  "agent.directRecharge.modal.fundingSource": "ഫണ്ടിംഗ് ഉറവിടം",
  "agent.directRecharge.modal.you": "(നിങ്ങൾ)",
  "agent.directRecharge.modal.yourPool": "നിങ്ങളുടെ പൂൾ",
  "agent.directRecharge.modal.notSignedIn": "ഏജന്റായി സൈൻ ഇൻ ചെയ്തിട്ടില്ല",
  "agent.directRecharge.modal.failed": "പരാജയപ്പെട്ടു",

  // Common controls
  'common.signIn': 'സൈൻ ഇൻ ചെയ്യുക',
  'common.signUp': 'സൈൻ അപ്പ് ചെയ്യുക',
  'common.login': 'ലോഗിൻ',
  'common.logout': 'ലോഗ് ഔട്ട്',
  'common.continue': 'തുടരുക',
  'common.cancel': 'റദ്ദാക്കുക',
  'common.save': 'സേവ് ചെയ്യുക',
  'common.update': 'അപ്‌ഡേറ്റ്',
  'common.clear': 'മായ്ക്കുക',
  'common.back': 'പിന്നോട്ട്',
  'common.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'common.sending': 'അയയ്ക്കുന്നു…',
  'common.saving': 'സേവ് ചെയ്യുന്നു…',
  'common.optional': 'ഐച്ഛികം',
  'common.show': 'കാണിക്കുക',
  'common.hide': 'മറയ്ക്കുക',
  'common.profile': 'പ്രൊഫൈൽ',
  'common.settings': 'ക്രമീകരണങ്ങൾ',

  // NavBar / BottomNav
  'nav.brand': 'ബാലി ഗെയിംസ്',
  'nav.home': 'ഹോം',
  'nav.games': 'ഗെയിംസ്',
  'nav.wallet': 'വാലറ്റ്',
  'nav.rewards': 'പുരസ്കാരങ്ങൾ',
  'nav.profile': 'പ്രൊഫൈൽ',

  // Login page
  'login.title': 'വീണ്ടും സ്വാഗതം',
  'login.subtitle': 'കളി തുടരാൻ സൈൻ ഇൻ ചെയ്യുക.',
  'login.idLabel': 'യൂസർനെയിം, ഇമെയിൽ അല്ലെങ്കിൽ ഫോൺ',
  'login.idPlaceholder': 'യൂസർനെയിം, ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ',
  'login.passwordLabel': 'പാസ്‌വേഡ്',
  'login.passwordPlaceholder': 'നിങ്ങളുടെ പാസ്‌വേഡ് നൽകുക',
  'login.forgot': 'മറന്നോ?',
  'login.cta': 'സൈൻ ഇൻ',
  'login.signingIn': 'സൈൻ ഇൻ ചെയ്യുന്നു…',
  'login.newHere': 'പുതിയത്?',
  'login.createAccount': 'അക്കൗണ്ട് സൃഷ്ടിക്കുക',

  // Register page
  'register.title': 'നിങ്ങളുടെ അക്കൗണ്ട് സൃഷ്ടിക്കുക',
  'register.subtitle': 'ഒരു ടാഗ് തിരഞ്ഞെടുക്കൂ, നിങ്ങൾ അകത്താണ്.',
  'register.usernameLabel': 'യൂസർനെയിം',
  'register.usernamePlaceholder': 'അനന്യമായ പേര് തിരഞ്ഞെടുക്കുക',
  'register.usernameHelp': 'അക്ഷരങ്ങൾ, അക്കങ്ങൾ, അണ്ടർസ്കോറുകൾ. 3–24 പ്രതീകങ്ങൾ.',
  'register.emailLabel': 'ഇമെയിൽ',
  'register.emailPlaceholder': 'നിങ്ങളുടെ ഇമെയിൽ വിലാസം',
  'register.mobileLabel': 'മൊബൈൽ നമ്പർ',
  'register.mobilePlaceholder': '10-അക്ക മൊബൈൽ',
  'register.mobileHelp': 'രാജ്യ കോഡ് ഞങ്ങൾ സ്വയമേവ ചേർക്കും.',
  'register.passwordLabel': 'പാസ്‌വേഡ്',
  'register.passwordPlaceholder': 'ശക്തമായ പാസ്‌വേഡ് തിരഞ്ഞെടുക്കുക',
  'register.passwordHelp': 'ഒരു അക്ഷരവും ഒരു അക്കവും ഉള്ള കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ.',
  'register.referralLabel': 'റഫറൽ കോഡ്',
  'register.referralPlaceholder': 'സുഹൃത്തിന്റെ ക്ഷണക്കോഡ്',
  'register.cta': 'അക്കൗണ്ട് സൃഷ്ടിക്കുക',
  'register.creating': 'സൃഷ്ടിക്കുന്നു…',
  'register.alreadyPlayer': 'ഇതിനകം ഒരു പ്ലെയറാണോ?',
  // Phase 22 (2026-05-21) — Signup wizard.
  'register.step.about': 'നിങ്ങളെക്കുറിച്ച്', 'register.step.signin': 'സൈൻ-ഇൻ', 'register.step.finish': 'പൂർത്തിയാക്കുക',
  'register.step.aboutSubtitle': 'നിങ്ങളെക്കുറിച്ച് അൽപ്പം പറയൂ', 'register.step.signinSubtitle': 'എങ്ങനെ സൈൻ-ഇൻ ചെയ്യാമെന്ന് സജ്ജമാക്കുക', 'register.step.finishSubtitle': 'സ്ഥലം തിരഞ്ഞെടുത്ത് പൂർത്തിയാക്കുക',
  'register.firstName': 'ആദ്യ പേര്', 'register.firstNamePh': 'ആരവ്',
  'register.lastName': 'അവസാന പേര്', 'register.lastNamePh': 'ശർമ്മ',
  'register.dob': 'ജനന തീയതി', 'register.dobHelp': 'കളിക്കാൻ നിങ്ങളുടെ പ്രായം 18 അല്ലെങ്കിൽ അതിൽ കൂടുതലായിരിക്കണം',
  'register.v.required': 'ആവശ്യമാണ്', 'register.v.nameMin': 'കുറഞ്ഞത് 2 അക്ഷരങ്ങൾ',
  'register.v.tooLong': 'വളരെ നീളം', 'register.v.nameChars': 'അക്ഷരങ്ങൾ, സ്പേസ്, ഹൈഫൻ മാത്രം',
  'register.v.invalidDate': 'അസാധുവായ തീയതി', 'register.v.minAge': 'നിങ്ങളുടെ പ്രായം കുറഞ്ഞത് 18 ആയിരിക്കണം',
  'register.v.tooOld': 'വർഷം പരിശോധിക്കുക',
  'register.v.usernameMin': 'കുറഞ്ഞത് 3 അക്ഷരങ്ങൾ', 'register.v.usernameMax': 'വളരെ നീളം (പരമാവധി 24)',
  'register.v.usernameChars': 'അക്ഷരങ്ങൾ, അക്കങ്ങൾ, അണ്ടർസ്‌കോർ മാത്രം', 'register.v.emailShape': 'അസാധുവായ ഇമെയിൽ പോലെ',
  'register.v.phoneDigits': '10 അക്കങ്ങളുള്ള നമ്പർ വേണം', 'register.v.phonePrefix': 'ഇന്ത്യൻ മൊബൈൽ 6–9-ൽ തുടങ്ങുന്നു',
  'register.v.pwdMin': 'കുറഞ്ഞത് 8 അക്ഷരങ്ങൾ', 'register.v.pwdLetter': 'കുറഞ്ഞത് ഒരു അക്ഷരം വേണം',
  'register.v.pwdNumber': 'കുറഞ്ഞത് ഒരു അക്കം വേണം',
  'register.v.referralShort': 'വളരെ ചെറുത്', 'register.v.referralChars': 'അക്ഷരങ്ങളും അക്കങ്ങളും മാത്രം',
  'register.v.usernameTaken': 'ഈ യൂസർനെയിം ഇതിനകം എടുത്തിട്ടുണ്ട്',
  'register.v.emailTaken': 'ഈ ഇമെയിൽ ഇതിനകം രജിസ്റ്റർ ചെയ്തിട്ടുണ്ട്',
  'register.v.phoneTaken': 'ഈ നമ്പർ ഇതിനകം രജിസ്റ്റർ ചെയ്തിട്ടുണ്ട്',
  'register.v.noLocation': 'തുടരാൻ കുറഞ്ഞത് ഒരു സ്ഥല രീതി പങ്കിടുക.',
  'register.welcome.title': 'സ്വാഗതം', 'register.welcome.subtitle': 'നിങ്ങളുടെ അക്കൗണ്ട് തയ്യാർ. ഇപ്പോൾ സുരക്ഷിതമാക്കുക അല്ലെങ്കിൽ നേരിട്ട് തുടങ്ങുക?',
  'register.welcome.verifyTelegram': 'ടെലിഗ്രാം വഴി പരിശോധിക്കുക',
  'register.welcome.verifyExplain': '⚠ പ്രധാനം: ഇപ്പോൾ തന്നെ ടെലിഗ്രാം വഴി പരിശോധിക്കുക. പരിശോധന ഇല്ലാതെ, പാസ്‌വേഡ് മറന്നാൽ നിങ്ങൾക്ക് സ്വയം പുനഃസജ്ജമാക്കാൻ കഴിയില്ല — നിങ്ങളുടെ ഏജന്റ് അല്ലെങ്കിൽ അഡ്മിന് മാത്രമേ പുനഃസജ്ജമാക്കാൻ കഴിയൂ. പിന്നീട് പ്രൊഫൈലിൽ നിന്നും പരിശോധിക്കാം.',
  'register.welcome.verifyHeads': 'ഇത് പിന്നീടേക്ക് സൂക്ഷിക്കുക',
  'register.welcome.verifyConsequence': 'ഇമെയിൽ പരിശോധന ഇപ്പോൾ ലഭ്യമല്ല. പിന്നീട് പാസ്‌വേഡ് പുനഃസജ്ജമാക്കാൻ ടെലിഗ്രാം വഴി പരിശോധിക്കുക. ചെയ്തില്ലെങ്കിൽ, പാസ്‌വേഡ് മറന്നാൽ നിങ്ങളുടെ ഏജന്റ് അല്ലെങ്കിൽ അഡ്മിന് മാത്രമേ പുനഃസജ്ജമാക്കാൻ കഴിയൂ.',
  'register.welcome.continue': 'ആപ്പിലേക്ക് തുടരുക', 'register.welcome.continueLogin': 'തുടരാൻ സൈൻ ഇൻ ചെയ്യുക', 'register.welcome.useDifferent': 'വ്യത്യസ്ത അക്കൗണ്ട് ഉപയോഗിക്കുക',
  'register.geo.title': 'നിങ്ങൾ എവിടെ നിന്ന് കളിക്കുന്നു?', 'register.geo.subtitle': 'അടുത്തുള്ള ഏജന്റിനോട് ചേർക്കാൻ നിങ്ങളുടെ സ്ഥാനം ഉപയോഗിക്കുന്നു.',
  'register.geo.showAlt': 'സ്ഥാനം പങ്കിടാൻ കഴിയില്ലേ? വ്യത്യസ്ത രീതി ഉപയോഗിക്കുക',
  'register.geo.hideAlt': 'മറ്റ് രീതികൾ മറയ്ക്കുക', 'register.geo.captured': 'സ്ഥാനം പിടിച്ചെടുത്തു',
  'register.geo.capturedHint': 'പ്രൊഫൈലിൽ നിന്ന് എപ്പോൾ വേണമെങ്കിലും മാറ്റാം.',
  'register.geo.verifyNote': 'സൈൻ-അപ്പിന് ശേഷം, പ്രൊഫൈലിൽ നിന്ന് ടെലിഗ്രാം (പാസ്‌വേഡ് റീസെറ്റിന്) അല്ലെങ്കിൽ ഇമെയിൽ വഴി എപ്പോൾ വേണമെങ്കിലും പരിശോധിക്കാം. പരിശോധന ഐച്ഛികം.',

  // Verify (post-signup)
  'verify.title': 'നിങ്ങളുടെ അക്കൗണ്ട് സ്ഥിരീകരിക്കുക',
  'verify.subtitle': 'ഒരു ഓപ്ഷൻ തിരഞ്ഞെടുക്കൂ — അത് മതി.',
  'verify.tabTelegram': 'ടെലിഗ്രാം',
  'verify.tabEmail': 'ഇമെയിൽ',
  'verify.emailSent': '{email} എന്നതിലേക്ക് സ്ഥിരീകരണ ലിങ്ക് അയച്ചു.',
  'verify.emailHint': 'ഈ ഉപകരണത്തിൽ തുറക്കുക. കാണുന്നില്ലേ? സ്പാം / പ്രമോഷനുകൾ പരിശോധിക്കുക.',
  'verify.telegramOpen': '@BaaliGames_bot തുറക്കുക',
  'verify.telegramExpiry': 'ലിങ്ക് 30 മിനിറ്റിൽ കാലഹരണപ്പെടും. ടെലിഗ്രാമിൽ ഒരിക്കൽ Start അമർത്തണം.',
  'verify.goToLogin': 'ലോഗിനിലേക്ക് പോകുക',
  'verify.email.title': 'ഇമെയിൽ സ്ഥിരീകരിച്ചു',
  'verify.email.sub': 'നിങ്ങളുടെ അക്കൗണ്ട് സജീവമാണ്. കളിയിലേക്ക് ഇറങ്ങാം.',
  'verify.email.failed': 'സ്ഥിരീകരണം പരാജയപ്പെട്ടു',
  'verify.email.verifying': 'സ്ഥിരീകരിക്കുന്നു…',
  'verify.backRegister': 'രജിസ്റ്റർ ചെയ്യാൻ മടങ്ങുക',
  // Task C — Verification Gate (post-login holding page).
  'verify.gate.resend': 'ഇമെയിൽ വീണ്ടും അയയ്ക്കുക',
  'verify.gate.resent': 'അയച്ചു!',
  'verify.gate.checking': 'പ്രവർത്തിക്കുന്നു…',
  'verify.gate.tgFallback': 'ലിങ്ക് സ്വയം തുറക്കുക',
  'verify.gate.waiting': 'സ്ഥിരീകരണം ഞങ്ങൾ സ്വയമേവ കണ്ടെത്തും.',
  'verify.gate.signOut': 'സൈൻ ഔട്ട്',

  // Forgot password
  'forgot.title': 'നിങ്ങളുടെ അക്കൗണ്ട് വീണ്ടെടുക്കുക',
  'forgot.subtitle': 'നിങ്ങൾക്ക് തിരിച്ച് വരാനുള്ള വഴി ഞങ്ങൾ അയയ്ക്കും.',
  'forgot.idLabel': 'യൂസർനെയിം, ഇമെയിൽ അല്ലെങ്കിൽ ഫോൺ',
  'forgot.idPlaceholder': 'യൂസർനെയിം, ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ',
  'forgot.sendVia': 'ഇതുവഴി അയയ്ക്കുക',
  'forgot.email': 'ഇമെയിൽ',
  'forgot.telegram': 'ടെലിഗ്രാം OTP',
  'forgot.telegramHelp': 'നിങ്ങളുടെ അക്കൗണ്ട് ഞങ്ങളുടെ ബോട്ടുമായി ലിങ്ക് ചെയ്തിട്ടുണ്ടെങ്കിൽ മാത്രമേ ടെലിഗ്രാം പ്രവർത്തിക്കൂ.',
  'forgot.cta': 'വീണ്ടെടുക്കൽ അയയ്ക്കുക',
  'forgot.sentEmail': 'അക്കൗണ്ട് നിലവിലുണ്ടെങ്കിൽ, ഒരു റീസെറ്റ് ലിങ്ക് ഇമെയിലിൽ അയച്ചിട്ടുണ്ട്.',
  'forgot.sentTelegram': 'അക്കൗണ്ട് നിലവിലുണ്ടെങ്കിൽ, ഒരു 6-അക്ക കോഡ് ടെലിഗ്രാം വഴി അയച്ചിട്ടുണ്ട്.',
  'forgot.redirecting': 'റീസെറ്റ് പേജിലേക്ക് റീഡയറക്റ്റ് ചെയ്യുന്നു…',
  'forgot.back': 'ലോഗിനിലേക്ക് മടങ്ങുക',

  // Reset password
  'reset.title': 'ഒരു പുതിയ പാസ്‌വേഡ് സജ്ജമാക്കുക',
  'reset.subOtp': 'ടെലിഗ്രാമിൽ ഞങ്ങൾ അയച്ച കോഡ് ഉപയോഗിക്കുക.',
  'reset.subLink': 'ഒരു പുതിയ കോമ്പോ തിരഞ്ഞെടുത്ത് വീണ്ടും അകത്ത് കടക്കുക.',
  'reset.otpLabel': '6-അക്ക കോഡ്',
  'reset.newPasswordLabel': 'പുതിയ പാസ്‌വേഡ്',
  'reset.cta': 'പാസ്‌വേഡ് അപ്‌ഡേറ്റ് ചെയ്യുക',
  'reset.updating': 'അപ്‌ഡേറ്റ് ചെയ്യുന്നു…',
  'reset.successTitle': 'പാസ്‌വേഡ് അപ്‌ഡേറ്റ് ചെയ്തു',
  'reset.successSub': 'എല്ലാം സജ്ജം. കളിക്കാനുള്ള സമയം.',

  // Home (logged-out)
  'home.welcomeTo': 'സ്വാഗതം',
  'home.heroSub': 'ഒരു സ്കിൽ-ബേസ്ഡ് ഗെയിമിംഗ് ഹബ്. സൈൻ ഇൻ ചെയ്യുക അല്ലെങ്കിൽ ഇറങ്ങാൻ നിങ്ങളുടെ പ്ലെയർ അക്കൗണ്ട് സൃഷ്ടിക്കുക.',
  'home.skillBased': 'സ്കിൽ-ബേസ്ഡ്',
  'home.skillBasedSub': 'തന്ത്രം ജയിക്കുന്നു',
  'home.fairPlay': 'ന്യായമായ കളി',
  'home.fairPlaySub': 'ആന്റി-ചീറ്റ് ON',
  'home.instantPayout': 'UPI പേയ്മെന്റ്',
  'home.instantPayoutSub': '24 മണിക്കൂറിനുള്ളിൽ',
  'home.featured': 'തിരഞ്ഞെടുത്ത ഗെയിംസ്',
  'home.tapToPlay': 'കളിക്കാൻ ടാപ്പ് ചെയ്യുക',
  'home.ready': 'കളിക്കാൻ തയ്യാറാണോ?',
  'home.dropIn': 'ഇറങ്ങി യഥാർത്ഥ പുരസ്കാരങ്ങൾ നേടുക.',
  'home.start': 'ആരംഭിക്കുക',

  // Home (logged-in)
  'home.welcomeBack': 'വീണ്ടും സ്വാഗതം',
  'home.balance': 'നിങ്ങളുടെ ബാലൻസ്',
  'home.live': 'ലൈവ്',
  'home.add': 'ചേർക്കുക',
  'home.manage': 'കൈകാര്യം',
  'home.rewards': 'പുരസ്കാരങ്ങൾ',
  'home.todayChallenge': 'ഇന്നത്തെ വെല്ലുവിളി',
  'home.todayChallengeSub': 'ഒരു മത്സരം ജയിക്കുക — സ്ട്രീക്ക് ബോണസ് നേടുക.',
  'home.view': 'കാണുക',
  'home.yourGames': 'നിങ്ങളുടെ ഗെയിംസ്',
  'home.viewAll': 'എല്ലാം കാണുക',

  // Profile tabs
  'profile.details': 'വിശദാംശങ്ങൾ',
  'profile.address': 'വിലാസം',
  'profile.languages': 'ഭാഷകൾ',
  'profile.account': 'അക്കൗണ്ട്',
  'profile.photo': 'ഫോട്ടോ',
  'profile.preferred': 'മുൻഗണനാ ഭാഷ',

  // Profile → Languages tab
  'profile.lang.primary': 'പ്രാഥമിക ഭാഷ',
  'profile.lang.primarySub': 'ആപ്പ്-വൈഡ് സ്ഥിരസ്ഥിതി',
  'profile.lang.locked': 'പൂട്ടിയിരിക്കുന്നു',
  'profile.lang.second': 'രണ്ടാം ഭാഷ',
  'profile.lang.pickHelp': 'നിങ്ങൾ സംസാരിക്കുന്ന ഭാഷ തിരഞ്ഞെടുക്കുക. തിരഞ്ഞെടുക്കാൻ ഒരു കാർഡിൽ ടാപ്പ് ചെയ്യുക.',
  'profile.lang.saved': 'സെക്കണ്ടറി ഭാഷ സേവ് ചെയ്തു',
  'profile.lang.cleared': 'സെക്കണ്ടറി ഭാഷ മായ്ച്ചു',

  // Language switcher
  'lang.switcher.aria': 'ഭാഷ തിരഞ്ഞെടുക്കുക',
  'lang.switcher.title': 'നിങ്ങളുടെ ഭാഷ തിരഞ്ഞെടുക്കുക',
  'lang.switcher.sub': 'പൂർണ്ണ ആപ്പ് തൽക്ഷണം മാറും.',
  'lang.switcher.current': 'നിലവിലുള്ളത്',

  // Rewards page
  'rewards.title': 'പുരസ്കാരങ്ങളും ഓഫറുകളും',
  'rewards.subtitle': 'ഇപ്പോൾ നേടാനോ ക്ലെയിം ചെയ്യാനോ ഉള്ള ബോണസുകൾ.',
  'rewards.tab.available': 'ലഭ്യം',
  'rewards.tab.inReview': 'അവലോകനത്തിൽ',
  'rewards.tab.claimed': 'ക്ലെയിം ചെയ്തത്',
  'rewards.empty.available.title': 'ഇപ്പോൾ ഓഫറുകളില്ല',
  'rewards.empty.available.body': 'ഉടൻ വീണ്ടും പരിശോധിക്കുക — അഡ്മിൻ പതിവായി പുതിയ ഓഫറുകൾ പോസ്റ്റ് ചെയ്യുന്നു.',
  'rewards.empty.review.title': 'അവലോകനത്തിൽ ഒന്നുമില്ല',
  'rewards.empty.review.body': 'അഡ്മിൻ അംഗീകാരം ആവശ്യമുള്ള ക്ലെയിമുകൾ ഇവിടെ ദൃശ്യമാകും.',
  'rewards.empty.claimed.title': 'ഇതുവരെ ക്ലെയിമുകളില്ല',
  'rewards.empty.claimed.body': 'നിങ്ങൾ ഒരു ബോണസ് നേടിയാൽ, അത് ഒരു ക്ലെയിമായി ഇവിടെ കാണിക്കും.',
  'rewards.expires': '{date}-ന് അവസാനിക്കുന്നു',
  'rewards.remaining': '{amount} ബാക്കി',
  'rewards.cap': 'പരിധി',
  'rewards.claim.cta': 'ക്ലെയിം',
  'rewards.claim.submit': 'സമർപ്പിക്കുക',
  'rewards.claimed.credited': '{date}-ന് {amount} ക്ലെയിം ചെയ്തു',
  'rewards.claimed.pending': 'അവലോകനം തീർച്ചപ്പെടാത്തത്',
  'rewards.claimed.rejected': 'നിരസിച്ചു',
  'rewards.auto.willCredit': 'നിബന്ധനകൾ പാലിക്കുമ്പോൾ സ്വയം ക്രെഡിറ്റ് ചെയ്യും',
  'rewards.auto.willReview': 'അഡ്മിൻ അവലോകന ക്യൂവിൽ എത്തുന്നു',
  'rewards.row.creditedOn': '{date}-ന് ക്രെഡിറ്റ് ചെയ്തു',
  'rewards.row.submittedOn': '{date}-ന് സമർപ്പിച്ചു',
  'rewards.toast.credited': 'ബോണസ് ചേർത്തു — {amount} നിങ്ങളുടെ വാലറ്റിൽ',
  'rewards.toast.submitted': 'അവലോകനത്തിന് സമർപ്പിച്ചു',
  'rewards.toast.alreadyClaimed': 'ഇതിനകം ക്ലെയിം ചെയ്തു',
  'rewards.toast.rejected': 'ക്ലെയിം നിരസിച്ചു',

  // Wallet page
  'wallet.balance': 'ബാലൻസ്',
  'wallet.balance.note': 'എൻട്രികൾ തൽക്ഷണം ഡെബിറ്റ് ചെയ്യും; സമ്മാനങ്ങൾ മത്സരം അവസാനിക്കുമ്പോൾ ക്രെഡിറ്റ് ചെയ്യും.',
  'wallet.addMoney': 'പണം ചേർക്കുക',
  'wallet.withdraw': 'പിൻവലിക്കുക',
  'wallet.withdraw.minBalance': 'പിൻവലിക്കാൻ കുറഞ്ഞത് ₹200 ആവശ്യമാണ്',
  'wallet.history.byGame': 'ഗെയിം അനുസരിച്ച് നിങ്ങളുടെ ചരിത്രം',
  'wallet.history.empty': 'ഇതുവരെ പ്രവർത്തനങ്ങളില്ല. നിങ്ങളുടെ ആദ്യ മത്സരം ഇവിടെ ദൃശ്യമാകും.',
  'wallet.activity': 'സമീപകാല പ്രവർത്തനം',
  'wallet.activity.empty': 'ഇതുവരെ ഇടപാടുകളില്ല.',
  'wallet.net': 'നെറ്റ്',
  'wallet.newer': 'പുതിയത്',
  'wallet.older': 'പഴയത്',
  'wallet.pending.title': 'തീരുമാനമാകാത്ത അഭ്യർത്ഥനകൾ',
  'wallet.pending.recharge': '{amount} റീചാർജ് — അഡ്മിൻ അംഗീകാരം കാത്തിരിക്കുന്നു',
  'wallet.pending.withdrawal': '{amount} പിൻവലിക്കൽ — പേയ്മെന്റ് കാത്തിരിക്കുന്നു',
  'wallet.state.pending': 'തീരുമാനമാകാത്തത്',
  'wallet.state.paid': 'നൽകി',
  'wallet.pending.txnPrefix': 'ഇടപാട്:',
  'wallet.pending.upload.label': 'വേഗത്തിലുള്ള നടപടിക്കായി പേയ്മെന്റ് സ്ക്രീൻഷോട്ട് അപ്‌ലോഡ് ചെയ്യുക.',
  'wallet.pending.upload.hint':  'അഭ്യർത്ഥന സൃഷ്ടിച്ച ശേഷം നിങ്ങൾക്ക് 24 മണിക്കൂറുണ്ട്.',
  'wallet.pending.upload.failed': 'അപ്‌ലോഡ് പരാജയപ്പെട്ടു',
  'wallet.activity.balanceAfter': 'ബാലൻസ് {balance}',
  'wallet.activity.range': '{total} ൽ {from}–{to}',
  'wallet.activity.window': 'നിങ്ങളുടെ കഴിഞ്ഞ {days} ദിവസത്തെ പ്രവർത്തനം',
  'wallet.history.summary.one':   '{matches} മത്സരം · ജയിച്ചത് {wins} · ചെലവ് {bets}',
  'wallet.history.summary.other': '{matches} മത്സരങ്ങൾ · ജയിച്ചത് {wins} · ചെലവ് {bets}',
  'wallet.history.summary.refunded': ' · റീഫണ്ട് {refunds}',
  'ledger.kind.bet': 'മത്സര എൻട്രി', 'ledger.kind.win': 'മത്സര സമ്മാനം',
  'ledger.kind.refund': 'മത്സര റീഫണ്ട്', 'ledger.kind.forfeit': 'മത്സര നഷ്ടം',
  'ledger.kind.fee': 'പ്ലാറ്റ്ഫോം ഫീസ്', 'ledger.kind.recharge': 'ടോപ്പ്-അപ്പ്',
  'ledger.kind.admin_credit': 'അഡ്മിൻ ക്രെഡിറ്റ്', 'ledger.kind.admin_debit': 'അഡ്മിൻ ഡെബിറ്റ്',
  'ledger.kind.referral_bonus': 'റഫറൽ ബോണസ്', 'ledger.kind.signup_bonus': 'സൈൻ-അപ്പ് ബോണസ്',
  'ledger.kind.offer_credit': 'ഓഫർ സമ്മാനം',
  'ledger.kind.withdrawal_hold': 'പിൻവലിക്കൽ ഹോൾഡ്', 'ledger.kind.withdrawal_refund': 'പിൻവലിക്കൽ റീഫണ്ട്',
  'ledger.kind.agent_topup_in': 'ഏജന്റ് ടോപ്പ്-അപ്പ്', 'ledger.kind.agent_co_fund': 'ഏജന്റ് സംഭാവന', 'ledger.kind.turnover_reward': 'ക്യാഷ്ബാക്ക്',
  'ledger.withdrawalPaid': 'പിൻവലിക്കൽ നൽകി', 'ledger.withdrawalPaidPartial': 'പിൻവലിക്കൽ നൽകി (ഭാഗികം)',
  'ledger.withdrawalPaid.txn': 'നൽകി · ഇടപാട് {txnId}',
  'ledger.withdrawalRejected': 'പിൻവലിക്കൽ നിരസിച്ചു', 'ledger.withdrawalRejected.note': 'പിടിച്ച തുക തിരികെ',
  'ledger.withdrawalExpired': 'പിൻവലിക്കൽ കാലഹരണപ്പെട്ടു', 'ledger.awaitingProof': 'തെളിവിനായി കാത്തിരിക്കുന്നു',
  'proofPicker.button.compact': 'സ്ക്രീൻഷോട്ട് തിരഞ്ഞെടുക്കുക', 'proofPicker.button.prominent': 'സ്ക്രീൻഷോട്ട് ചേർക്കുക',
  'proofPicker.processing': 'പ്രോസസ് ചെയ്യുന്നു…',
  'proofPicker.error.mime': 'JPG, PNG അല്ലെങ്കിൽ WebP ചിത്രങ്ങൾ മാത്രം അനുവദനീയം.',
  'proofPicker.error.size': 'ഫയൽ വളരെ വലുത് — പരമാവധി 5 MB.',
  'proofPicker.error.processing': 'ചിത്രം പ്രോസസ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
  'proofPicker.previewAlt': 'തിരഞ്ഞെടുത്ത സ്ക്രീൻഷോട്ട് പ്രിവ്യൂ',
  'proofViewer.state.none': 'സ്ക്രീൻഷോട്ടിനായി കാത്തിരിക്കുന്നു', 'proofViewer.state.available': 'സ്ക്രീൻഷോട്ട് കാണുക',
  'proofViewer.state.expired': 'സ്ക്രീൻഷോട്ട് ലഭ്യമല്ല', 'proofViewer.state.viewLimit': 'കാഴ്ച പരിധി പൂർത്തിയായി',
  'proofViewer.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'proofViewer.title.viewLimit': 'നിങ്ങൾ ഈ സ്ക്രീൻഷോട്ട് പരമാവധി 2 തവണ തുറന്നു',
  'proofViewer.error.load': 'സ്ക്രീൻഷോട്ട് ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
  'proofViewer.lastView': 'അവസാന കാഴ്ച — ചിത്രം കാലഹരണപ്പെടും',
  'proofViewer.viewsRemaining.one': '{n} തവണ കൂടി കാണാം',
  'proofViewer.viewsRemaining.other': '{n} തവണ കൂടി കാണാം',
  'proofViewer.imageAlt': 'പേയ്മെന്റ് തെളിവ്', 'proofViewer.close': 'അടയ്ക്കുക',

  // Recharge flow
  'recharge.title': 'പണം ചേർക്കുക',
  'recharge.step1.subtitle': 'എത്ര തുക ചേർക്കണം?',
  'recharge.step1.amount': 'തുക',
  'recharge.step1.range': 'കുറഞ്ഞത് {min} · കൂടിയത് {max}',
  'recharge.step1.minHint': 'കുറഞ്ഞത് {min} റീചാർജ്',
  'recharge.step1.maxHint': 'കൂടിയത് {max}',
  'recharge.step1.quickPick': 'വേഗത്തിൽ തിരഞ്ഞെടുക്കുക',
  'recharge.step2.subtitle': 'UPI ഉപയോഗിച്ച് പണമടച്ച ശേഷം തുടരുക',
  'recharge.step2.payAmount': 'ഈ തുക അടയ്ക്കുക',
  'recharge.step2.iPaid': 'ഞാൻ അടച്ചു',
  'recharge.step2.howto.1': 'നിങ്ങളുടെ UPI ആപ്പ് തുറക്കുക (Google Pay / PhonePe / Paytm).',
  'recharge.step2.howto.2': 'മുകളിലെ തുക UPI ID-ലേക്ക് അടയ്ക്കുക അല്ലെങ്കിൽ QR സ്കാൻ ചെയ്യുക.',
  'recharge.step2.howto.3': 'നിങ്ങളുടെ UPI ആപ്പിൽ നിന്ന് ഇടപാട് ID പകർത്തുക — അടുത്തത് ആവശ്യമാണ്.',
  "recharge.locked.title": "റീചാർജ് താൽക്കാലികമായി ലഭ്യമല്ല",
  "recharge.locked.body": "ഈ റീചാർജ് വഴിയിലെ ഒരു പ്രശ്നം ഞങ്ങൾ പരിഹരിക്കുകയാണ്. നിങ്ങളുടെ വാലറ്റും പിൻവലിക്കലും ബാധിച്ചിട്ടില്ല. ദയവായി കുറച്ച് കഴിഞ്ഞ് വീണ്ടും ശ്രമിക്കുക.",
  "recharge.locked.reassure.withdrawals": "പിൻവലിക്കൽ സാധാരണ പോലെ പ്രവർത്തിക്കുന്നു",
  "recharge.locked.reassure.existing": "നിങ്ങൾ ഇതിനോടകം അയച്ച പണം പ്രക്രിയയിലാണ്",
  "recharge.cap.banner": "ഇപ്പോൾ പരമാവധി റീചാർജ്: {max}. സ്വീകർത്താവിന്റെ ശേഷി താൽക്കാലികമായി പരിമിതമാണ്.",
  "recharge.cap.tooHigh": "തുക ലഭ്യമായ പരിധിയേക്കാൾ കൂടുതലാണ്. ഇപ്പോൾ നിങ്ങൾക്ക് പരമാവധി {max} റീചാർജ് ചെയ്യാം.",
  "recharge.notConfigured.title": "റീചാർജ് ഉടൻ ലഭ്യമാകും",
  "recharge.notConfigured.body": "ഈ റീചാർജ് വഴിയിലെ സജ്ജീകരണം ഞങ്ങൾ പൂർത്തിയാക്കുകയാണ്. നിങ്ങളുടെ വാലറ്റും പിൻവലിക്കലും ബാധിച്ചിട്ടില്ല. ദയവായി കുറച്ച് കഴിഞ്ഞ് വീണ്ടും പരിശോധിക്കുക.",
  'recharge.step3.subtitle': 'നിങ്ങളുടെ പേയ്മെന്റ് സ്ഥിരീകരിക്കുക',
  'recharge.step3.amountPaid': 'നിങ്ങൾ അടച്ച തുക',
  'recharge.step3.txnId': 'UPI ഇടപാട് ID',
  'recharge.step3.txnIdHelp': 'ഇത് നിങ്ങളുടെ UPI ആപ്പിലെ പേയ്മെന്റ് രസീതിന് കീഴിൽ കണ്ടെത്തുക — സാധാരണ 12 അക്കം.',
  'recharge.step3.note': 'അഡ്മിന് കുറിപ്പ്',
  'recharge.step3.notePlaceholder': 'ഉദാ. പങ്കാളിയുടെ നമ്പറിൽ നിന്ന് അടച്ചു',
  'recharge.step3.submit': 'അവലോകനത്തിന് അയയ്ക്കുക',
  'recharge.step3.disclaimer': 'അഡ്മിൻ UPI ആപ്പിൽ പേയ്മെന്റ് സ്ഥിരീകരിക്കും. അംഗീകാരത്തിന് ശേഷം 24 മണിക്കൂറിനുള്ളിൽ വാലറ്റിൽ ക്രെഡിറ്റ് ചെയ്യും (പലപ്പോഴും വേഗത്തിൽ).',
  'recharge.success.title': 'അയച്ചു — അവലോകനം പ്രതീക്ഷിക്കുന്നു',
  'recharge.success.body': 'നിങ്ങളുടെ {amount} റീചാർജ് അഡ്മിൻ ക്യൂവിലാണ്. 24 മണിക്കൂറിനുള്ളിൽ ക്രെഡിറ്റ് ചെയ്യും (പലപ്പോഴും വേഗത്തിൽ).',
  'recharge.success.refId': 'റഫറൻസ്',
  'recharge.toast.submitted': 'റീചാർജ് അഡ്മിൻ അവലോകനത്തിന് സമർപ്പിച്ചു',

  // Withdraw flow
  'withdraw.title': 'പിൻവലിക്കുക',
  'withdraw.step1.subtitle': 'പണം എവിടേക്ക് അയയ്ക്കണം?',
  'withdraw.step2.subtitle': 'ഒരു അവസാന പരിശോധന',
  'withdraw.balance': 'ലഭ്യമായ ബാലൻസ്',
  'withdraw.amount': 'തുക',
  'withdraw.max': 'പരമാവധി',
  'withdraw.upiId': 'നിങ്ങളുടെ UPI ID',
  'withdraw.upiIdPh': 'yourname@oksbi',
  'withdraw.upiIdHelp': 'ഞങ്ങൾ ഇവിടേക്ക് പണം അയയ്ക്കും. സമർപ്പിക്കുന്നതിന് മുമ്പ് രണ്ടുവട്ടം പരിശോധിക്കുക.',
  // Task D-2 — surfaced when the input was pre-filled from
  // user.saved_upi_id, so the player understands why it's already populated.
  'withdraw.upiSaved': 'നിങ്ങളുടെ കഴിഞ്ഞ പിൻവലിക്കലിൽ നിന്ന് മുൻകൂട്ടി പൂരിപ്പിച്ചു',
  'withdraw.upiShapeErr': 'UPI ID yourname@psp പോലെയായിരിക്കണം (ഉദാ. lucky@oksbi)',
  'withdraw.phoneShapeErr': 'സാധുവായ 10-അക്ക ഇന്ത്യൻ മൊബൈൽ നൽകുക (6, 7, 8 അല്ലെങ്കിൽ 9-ൽ തുടങ്ങണം)',
  'withdraw.whatsapp': 'വാട്ട്സാപ്പ് നമ്പർ',
  'withdraw.errMin': 'കുറഞ്ഞ പിൻവലിക്കൽ {min}',
  'withdraw.errExceed': 'തുക നിങ്ങളുടെ ബാലൻസിനെ കവിയുന്നു',
  'withdraw.minHint': 'കുറഞ്ഞത് {min} പിൻവലിക്കൽ',
  'withdraw.step2.review': 'അവലോകനം',
  'withdraw.step2.heldNotice': 'നിങ്ങൾ സമർപ്പിക്കുമ്പോൾ, ഈ തുക തൽക്ഷണം ഹോൾഡ് ചെയ്യും, അതിനാൽ മറ്റെവിടെയും ചെലവഴിക്കാൻ കഴിയില്ല. അഡ്മിൻ ഇത് നിങ്ങളുടെ UPI ID-ലേക്ക് അടച്ച് പെയ്ഡ് മാർക്ക് ചെയ്യും.',
  'withdraw.heldAfter': 'ഹോൾഡ്',
  'withdraw.spendableAfter': 'ചെലവഴിക്കാവുന്നത്',
  'withdraw.step2.disclaimer': 'അഡ്മിൻ നിരസിച്ചാൽ, ഹോൾഡ് ചെയ്ത തുക നിങ്ങളുടെ വാലറ്റിലേക്ക് മടങ്ങും.',
  'withdraw.submit': 'സമർപ്പിക്കുക',
  'withdraw.success.title': 'അയച്ചു — പേയ്മെന്റ് കാത്തിരിക്കുന്നു',
  'withdraw.success.body': 'നിങ്ങളുടെ {amount} പിൻവലിക്കൽ ക്യൂവിലാണ്. 24 മണിക്കൂറിനുള്ളിൽ പേയ്മെന്റ് (പലപ്പോഴും വേഗത്തിൽ). അതുവരെ ഹോൾഡ് ചെയ്ത തുക സംരക്ഷിതമായി തുടരും.',
  'withdraw.success.refId': 'റഫറൻസ്',
  'withdraw.toast.submitted': 'പിൻവലിക്കൽ ക്യൂവിൽ — നിങ്ങളുടെ വാലറ്റിൽ ഹോൾഡ് ചെയ്തു',

  // Payment receiver card
  'payment.upi.label': 'ഈ UPI ID-ലേക്ക് അടയ്ക്കുക',
  'payment.upi.copy': 'പകർത്തുക',
  'payment.upiCopied': 'UPI ID പകർത്തി',
  'payment.openUpiApp': 'ഇപ്പോൾ പണമടയ്ക്കൂ',
  'payment.openWhatsapp': 'വാട്ട്സാപ്പ്',
  'payment.helpAt': 'സഹായം വേണോ? വിളിക്കൂ',
  'payment.qr.label': 'പണമടയ്ക്കാൻ QR സ്കാൻ ചെയ്യൂ',
  'payment.qr.tap': 'വലുതാക്കാൻ ടാപ്പ് ചെയ്യൂ',
  'payment.qr.tapToClose': 'അടയ്ക്കാൻ എവിടെയും ടാപ്പ് ചെയ്യൂ',
  'payment.notConfigured.title': 'ഇപ്പോൾ റീചാർജ് ലഭ്യമല്ല',
  'payment.notConfigured.contactBody': 'ഇതുവരെ UPI സജ്ജമാക്കിയിട്ടില്ല. വാട്ട്സാപ്പ് അല്ലെങ്കിൽ ഫോൺ വഴി ഞങ്ങളെ ബന്ധപ്പെടൂ, റീചാർജ് ചെയ്യാൻ ഞങ്ങൾ സഹായിക്കും.',
  'payment.notConfigured.unavailable': 'റീചാർജ് താൽക്കാലികമായി ലഭ്യമല്ല. ദയവായി പിന്നീട് ശ്രമിക്കൂ.',
  'payment.chooser.title': 'നിങ്ങളുടെ UPI ആപ്പ് തിരഞ്ഞെടുക്കൂ',
  'payment.chooser.subtitle': 'നിങ്ങൾ അടയ്ക്കും',
  'payment.chooser.hint': 'തുക മുൻകൂട്ടി പൂരിപ്പിച്ച് അത് തുറക്കാൻ ഒരു ആപ്പിൽ ടാപ്പ് ചെയ്യൂ. ആപ്പ് നിങ്ങളുടെ ഫോണിൽ ഇതിനകം ഇൻസ്റ്റാൾ ചെയ്തിരിക്കണം — "address invalid" പിശക് കണ്ടാൽ, ആദ്യം App Store / Play Store-ൽ നിന്ന് ആപ്പ് ഇൻസ്റ്റാൾ ചെയ്യൂ.',

  // Common
  'common.next': 'അടുത്തത്',
  'common.close': 'അടയ്ക്കുക',

  // Location capture (Phase R3 — Hyper-Local Mesh Network)
  // Strings live on the LocationCapture component shared by signup,
  // profile, admin/agent player-create modals. Keep them short — the
  // component renders them in tight rows on mobile.
  'geo.purpose': 'വേഗവും സുഗമവുമായ പേയ്മെന്റ് സേവനത്തിനായി കൃത്യമായ സ്ഥാനം ഒപ്പം ബന്ധപ്പെടാനുള്ള നമ്പർ നൽകുക.',
  'geo.change': 'മാറ്റുക',
  'geo.gps.button': 'എന്റെ നിലവിലുള്ള സ്ഥാനം ഉപയോഗിക്കുക',
  'geo.gps.benefit': 'വേഗത്തിൽ — ഒരു ടാപ്പ്, ടൈപ്പിംഗ് ഇല്ല.',
  'geo.gps.denied': 'സ്ഥാന അനുമതി നിരസിച്ചു. പിൻകോഡ് ഉപയോഗിക്കുക അല്ലെങ്കിൽ താഴെ തിരഞ്ഞെടുക്കുക.',
  'geo.gps.failed': 'സ്ഥാനം ലഭിച്ചില്ല. പിൻകോഡ് ശ്രമിക്കൂ.',
  'geo.gps.notResolved': 'നിങ്ങളുടെ സ്ഥാനം ഏതെങ്കിലും പ്രദേശവുമായി പൊരുത്തപ്പെട്ടില്ല. ദയവായി തിരഞ്ഞെടുക്കൂ.',
  'geo.pincode.label': 'പിൻകോഡ്',
  'geo.pincode.notFound': 'പിൻകോഡ് കണ്ടെത്തിയില്ല. ദയവായി പ്രദേശം തിരഞ്ഞെടുക്കൂ.',
  'geo.pincode.lookupFailed': 'പിൻകോഡ് തിരയൽ പരാജയപ്പെട്ടു. ദയവായി തിരഞ്ഞെടുക്കൂ.',
  'geo.multipleAreas.label': 'ഈ പിൻകോഡിൽ ഒന്നിലധികം പ്രദേശങ്ങൾ — നിങ്ങളുടേത് തിരഞ്ഞെടുക്കൂ',
  'geo.multipleAreas.placeholder': 'നിങ്ങളുടെ പ്രദേശം തിരഞ്ഞെടുക്കൂ',
  'geo.manual.title': 'നേരിട്ട് തിരഞ്ഞെടുക്കൂ',
  'geo.manual.close': 'നേരിട്ടുള്ളത് മറയ്ക്കുക',
  'geo.state.label': 'സംസ്ഥാനം',
  'geo.state.placeholder': 'സംസ്ഥാനം തിരഞ്ഞെടുക്കൂ',
  'geo.district.label': 'ജില്ല',
  'geo.district.placeholder': 'ജില്ല തിരഞ്ഞെടുക്കൂ',
  'geo.area.label': 'പ്രദേശം / പട്ടണം',
  'geo.area.placeholder': 'പ്രദേശം തിരഞ്ഞെടുക്കൂ',
  'geo.privacy.note': 'പ്രാദേശിക ഏജന്റിനെ നിയോഗിക്കാൻ മാത്രം. പങ്കിടാറില്ല.',
  'geo.required.hint': 'റഫറൽ കോഡ് ഇല്ലാത്തപ്പോൾ സ്ഥാനം ആവശ്യമാണ്.',

  // ────────────────────────────────────────────────────────────────────
  // AGENT APP — keys consumed exclusively by agent-frontend.
  //
  // Phase 10 follow-up (2026-05-12), Phase B Group 1 = navigation chrome.
  //
  // Namespacing: `agent.<surface>.<section>.<element>`. Keys live at the
  // BOTTOM of the `en` block so they're easy to find, easy to extend in
  // grouped batches, and never collide with player-side namespaces
  // (`nav.*`, `home.*`, `wallet.*`, …).
  //
  // Player apps DO NOT reference any `agent.*` key — these strings only
  // render inside the agent frontend. Reusable generics (Save, Cancel,
  // Close, etc.) reuse the existing `common.*` keys instead of
  // duplicating into `agent.common.*` — translators only translate them
  // once.
  //
  // Top-bar pills (desktop only, lg+) — AgentNavBar.tsx.
  'agent.nav.dashboard': 'ഡാഷ്ബോർഡ്',
  'agent.nav.cricket':   'Cricket',
  'agent.nav.games':   'കണക്ക്',
  'agent.nav.players': 'പ്ലെയർമാർ',
  'agent.nav.agents': 'ഏജന്റുമാർ',
  'agent.nav.rewards': 'പുരസ്കാരങ്ങൾ',
  'agent.nav.profile':   'പ്രൊഫൈൽ',

  // Bottom-nav tabs (mobile/tablet < lg) — AgentBottomNav.tsx. The
  // "Home" tab corresponds to the same route ('/') that the top-bar
  // labels "Dashboard"; we intentionally keep them as separate keys
  // because some languages may prefer a shorter word in the cramped
  // bottom-nav strip than in the desktop pill row.
  'agent.bottomnav.home': 'ഹോം',
  'agent.bottomnav.cricket': 'Cricket',
  'agent.bottomnav.games': 'കണക്ക്',
  'agent.bottomnav.players': 'പ്ലെയർമാർ',
  'agent.bottomnav.agents': 'ഏജന്റുമാർ',
  'agent.bottomnav.rewards': 'പുരസ്കാരങ്ങൾ',
  'agent.bottomnav.profile': 'പ്രൊഫൈൽ',

  // ────────────────────────────────────────────────────────────────────
  // AGENT DASHBOARD — Phase B Group 2 (2026-05-12).
  //
  // Covers AgentDashboard.tsx + the three dashboard panels embedded
  // inside its accordions (ActivityPanel, EarningsPanel,
  // LeaderboardPanel). The dashboard is dense — ~400 strings — so the
  // namespace is broken down by surface:
  //
  //   agent.dashboard.header.*          Welcome banner + range toggle
  //   agent.dashboard.range.*           7-window pill labels
  //   agent.dashboard.depositBar.*      Permanent deposit coverage bar
  //                                     (one key per margin_status state)
  //   agent.dashboard.locatorSkip.*     Zero-deposit locator-skip banner
  //   agent.dashboard.defaulter.*       Downline defaulter alert banner
  //   agent.dashboard.marginPill.*      Per-row margin-status pill labels
  //   agent.dashboard.sections.*        Three SectionTitle headers + their subtitles
  //   agent.dashboard.cards.*           The 8 hero cards in MorningBriefing
  //   agent.dashboard.accordions.*      6 collapsible section titles + helpers
  //   agent.dashboard.activity.*        TopSpenders + RecentActivity cards
  //   agent.dashboard.pnl.*             P&L deep-dive table
  //   agent.dashboard.pnlDrill.*        P&L drilldown modal
  //   agent.dashboard.directDownline.*  Direct sub-agents list
  //   agent.dashboard.chain.*           4-quadrant chain visibility
  //   agent.dashboard.liveFootnote      "Live data — refreshes on focus…"
  //
  //   agent.activity.*                  ActivityPanel — filters, feed, empty states
  //   agent.earnings.*                  EarningsPanel — kinds, hints, stat tiles
  //   agent.leaderboard.*               LeaderboardPanel — windows, pillars, empty states
  //
  // Plural strategy: for "{n} X" patterns where X has an English plural,
  // we use TWO keys (`*.one` / `*.other`) and let the call-site pick
  // based on `count === 1`. This is the minimum that lets a translator
  // produce correct Hindi/Marathi/Tamil/etc. agreement without forcing
  // any plural-aware library at the call site.
  //
  // Interpolation: `{name}`, `{n}`, `{pct}`, `{range}`, etc. are
  // replaced via the i18n.tsx t() function's tiny `{placeholder}`
  // substitution. Translators MUST preserve the brace tokens verbatim.

  // ── Header + range toggle ─────────────────────────────────────────
  'agent.dashboard.header.welcomePrefix': 'വീണ്ടും സ്വാഗതം, ',
  'agent.dashboard.header.welcomeFallback': 'സുഹൃത്തേ',
  'agent.dashboard.header.subtitle': 'നിങ്ങളുടെ ബിസിനസ് ഒറ്റ നോട്ടത്തിൽ — പൂൾ, ഡെപ്പോസിറ്റ്, പ്ലെയർമാർ, സബ്-ഏജന്റുമാർ, P&L, എക്സ്പോഷർ',
  'agent.dashboard.range.today': 'ഇന്ന്',
  'agent.dashboard.range.yesterday': 'ഇന്നലെ',
  'agent.dashboard.range.thisWeek': 'ഈ ആഴ്ച',
  'agent.dashboard.range.lastWeek': 'കഴിഞ്ഞ ആഴ്ച',
  'agent.dashboard.range.thisMonth': 'ഈ മാസം',
  'agent.dashboard.range.lastMonth': 'കഴിഞ്ഞ മാസം',
  'agent.dashboard.range.complete': 'സമ്പൂർണ്ണം',

  // ── Permanent deposit coverage bar ────────────────────────────────
  'agent.dashboard.depositBar.loading': 'ഡെപ്പോസിറ്റ് കവറേജ് ലോഡ് ചെയ്യുന്നു…',
  'agent.dashboard.depositBar.label': 'ഡെപ്പോസിറ്റ് കവറേജ്',
  'agent.dashboard.depositBar.balanceOfTarget': '· {target} ടാർഗെറ്റിൽ നിന്ന് {balance}',
  'agent.dashboard.depositBar.tapToView': 'സമ്പൂർണ്ണ ഡെപ്പോസിറ്റ് പേജ് കാണാൻ ടാപ്പ് ചെയ്യൂ.',
  'agent.dashboard.depositBar.caption.suspended': 'അക്കൗണ്ട് സസ്പെൻഡ് ചെയ്തു. അഡ്മിനെ ബന്ധപ്പെടൂ.',
  'agent.dashboard.depositBar.caption.breach': 'നിങ്ങളുടെ വാലറ്റ് ഡെപ്പോസിറ്റ് സീലിംഗിന്റെ 100% ഉപയോഗിച്ചു. പിൻവലിക്കലുകൾ പൂട്ടി. ഉടൻ ടോപ്പ്-അപ്പ് ചെയ്യൂ.',
  'agent.dashboard.depositBar.caption.warning': 'നിങ്ങളുടെ വാലറ്റ് ഡെപ്പോസിറ്റ് സീലിംഗിന്റെ {pct}%-ൽ ആണ്. 80%-ൽ മുന്നറിയിപ്പ്, 100%-ൽ ലോക്ക്ഡൗൺ.',
  'agent.dashboard.depositBar.caption.caution': 'നിങ്ങളുടെ വാലറ്റ് ഡെപ്പോസിറ്റ് കവറേജിന്റെ {pct}% ഉപയോഗിച്ചു. 80%-ൽ താഴെ — ഇപ്പോഴും സുരക്ഷിതം.',
  'agent.dashboard.depositBar.caption.safe': 'ഡെപ്പോസിറ്റ് സമ്പൂർണ്ണമായി കേടുകൂടാതെ. കവറേജ് ഒന്നും ഉപയോഗിക്കുന്നില്ല.',

  // ── Zero-deposit locator skip banner ──────────────────────────────
  'agent.dashboard.locatorSkip.title': 'മുന്നറിയിപ്പ് · പൂജ്യം സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ്',
  'agent.dashboard.locatorSkip.body': 'നിങ്ങളുടെ സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ് {amount} ആണ്. നിങ്ങൾ അത് ഫണ്ട് ചെയ്യുന്നതുവരെ ജിയോഗ്രാഫിക് ലൊക്കേറ്ററിൽ നിന്ന് പുതിയ പ്ലെയർമാർ ലഭിക്കില്ല. നിലവിലുള്ള പ്ലെയർമാർ + ഡയറക്റ്റ് റഫറലുകൾ + മാനുവൽ അസൈൻമെന്റുകൾ ബാധിക്കില്ല — ഓർഗാനിക് ജിയോ-റൂട്ടഡ് സൈൻഅപ്പുകൾ മാത്രം നിങ്ങളെ ഒഴിവാക്കും.',
  'agent.dashboard.locatorSkip.cta': 'ഡെപ്പോസിറ്റ് ഫണ്ട് ചെയ്യൂ',

  // ── Downline defaulter banner ─────────────────────────────────────
  'agent.dashboard.defaulter.title': 'ഡൗൺലൈൻ ഡിഫോൾട്ടർ മുന്നറിയിപ്പ്',
  'agent.dashboard.defaulter.summary.critical.one': 'നിങ്ങളുടെ {n} ഡയറക്റ്റ് സബ്-ഏജന്റ് ഡിഫോൾട്ടിലാണ് — അവർ പൂട്ടി അല്ലെങ്കിൽ ഡെപ്പോസിറ്റ് ലംഘിച്ചു.',
  'agent.dashboard.defaulter.summary.critical.other': 'നിങ്ങളുടെ {n} ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ ഡിഫോൾട്ടിലാണ് — അവർ പൂട്ടി അല്ലെങ്കിൽ ഡെപ്പോസിറ്റ് ലംഘിച്ചു.',
  'agent.dashboard.defaulter.summary.warning.one': 'നിങ്ങളുടെ {n} ഡയറക്റ്റ് സബ്-ഏജന്റ് അവരുടെ ഡെപ്പോസിറ്റ് സീലിംഗിന്റെ 80%+ ആണ് — ഡിഫോൾട്ടർ റിസ്ക്.',
  'agent.dashboard.defaulter.summary.warning.other': 'നിങ്ങളുടെ {n} ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ അവരുടെ ഡെപ്പോസിറ്റ് സീലിംഗിന്റെ 80%+ ആണ് — ഡിഫോൾട്ടർ റിസ്ക്.',
  'agent.dashboard.defaulter.worst': 'ഏറ്റവും മോശം: @{username} ഡെപ്പോസിറ്റ് സീലിംഗിന്റെ {pct}%-ൽ.',
  'agent.dashboard.defaulter.tapHint': 'ഡയറക്റ്റ് സബ്-ഏജന്റ് ലിസ്റ്റിലേക്ക് പോകാൻ ടാപ്പ് ചെയ്യൂ. ഡിഫോൾട്ടർ വരികൾ ഹൈലൈറ്റ് ചെയ്തിരിക്കുന്നു.',

  // ── Margin-status pill labels (per-row + per-card) ────────────────
  // "Suspended" is rendered as "Locked" intentionally — the agent
  // can't act with admin permission, so the player-facing word is
  // more useful than the internal status name.
  'agent.dashboard.marginPill.locked': 'പൂട്ടി',
  'agent.dashboard.marginPill.caution': 'ജാഗ്രത',
  'agent.dashboard.marginPill.warning': 'മുന്നറിയിപ്പ്',
  'agent.dashboard.marginPill.breach': 'ലംഘനം',

  // ── Section titles + their inline subtitles ───────────────────────
  'agent.dashboard.sections.morning.title': 'പ്രഭാത ബ്രീഫിംഗ്',
  'agent.dashboard.sections.morning.subtitle': 'ലൈവ് സ്നാപ്ഷോട്ട് — എല്ലാ നമ്പറും സ്വയമേവ പുതുക്കും',
  'agent.dashboard.sections.activity.title': 'പ്രവർത്തനം',
  'agent.dashboard.sections.activity.subtitle': 'ടോപ്പ് സ്പെൻഡർമാരും ഏറ്റവും സമീപകാല പ്ലെയർ പേയ്മെന്റ് ഇവന്റുകളും',
  'agent.dashboard.sections.forensic.title': 'ഫോറൻസിക് വിശദാംശങ്ങൾ',
  'agent.dashboard.sections.forensic.subtitle': 'ഏതെങ്കിലും വിഭാഗം വികസിപ്പിക്കാൻ ടാപ്പ് ചെയ്യൂ — ഈ പേജ് ശാന്തമായി സൂക്ഷിക്കാൻ സ്ഥിരസ്ഥിതിയായി അടച്ചിരിക്കുന്നു',

  // ── Accordion titles + their helper subtitles ─────────────────────
  'agent.dashboard.accordions.pnlDeep.title': 'ലാഭ നഷ്ട ഡീപ്-ഡൈവ്',
  'agent.dashboard.accordions.pnlDeep.helper': 'നിങ്ങളുടെ വരുമാനത്തിന്റെ പിലർ + ഗെയിം തിരിച്ചുള്ള വിശകലനം. അടിസ്ഥാന മത്സരങ്ങൾക്കായി ഒരു ഗെയിം വരി ക്ലിക്ക് ചെയ്യൂ.',
  'agent.dashboard.accordions.leaderboard.title': 'ഡൗൺലൈൻ ലീഡർബോർഡ്',
  'agent.dashboard.accordions.leaderboard.helper': 'നെറ്റ് വരുമാനത്തിന്റെ അടിസ്ഥാനത്തിൽ റാങ്ക് ചെയ്ത നിങ്ങളുടെ സബ്-ഏജന്റുമാർ. നെഗറ്റീവ് നമ്പറുകൾ അർത്ഥമാക്കുന്നത് ആ സബ്-ഏജന്റ് കൊണ്ടുവന്നതിലും കൂടുതൽ പണം നൽകി എന്നാണ്.',
  'agent.dashboard.accordions.activity.title': 'ഡൗൺലൈൻ പ്രവർത്തന ഫീഡ്',
  'agent.dashboard.accordions.activity.helper': 'നിങ്ങളുടെ സബ്-ഏജന്റുമാർക്കിടയിൽ എല്ലാ മത്സര സെറ്റിൽമെന്റ്, റീചാർജ്, റേറ്റ് മാറ്റവും — ഫോറൻസിക് ടൈംലൈൻ.',
  'agent.dashboard.accordions.earnings.title': 'വരുമാന വിശകലനം (ലൈഫ്ടൈം)',
  'agent.dashboard.accordions.earnings.helper': 'തരം അനുസരിച്ച് ലൈഫ്ടൈം ടോട്ടലുകൾ. മുകളിലെ റേഞ്ച് ടോഗിൾ അവഗണിക്കുന്നു — ഇത് സമയ ഫിൽട്ടറിനൊപ്പം മാറില്ല.',
  'agent.dashboard.accordions.directSubAgents.title': 'ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ',
  'agent.dashboard.accordions.directSubAgents.helper': 'നിങ്ങളുടെ ഡയറക്റ്റ് സഹായികൾ. ഓരോരുത്തരും സ്വന്തം പ്ലെയർമാർ + സ്വന്തം സബ്-ഏജന്റുമാർ പ്രവർത്തിപ്പിക്കുന്നു. റോസ്/ആമ്പർ ഹൈലൈറ്റ് ചെയ്ത വരികൾ ഡിഫോൾട്ട് മേഖലയിലാണ്.',
  'agent.dashboard.accordions.chain.title': 'ചെയിൻ ദൃശ്യപരത',
  'agent.dashboard.accordions.chain.helper': 'നിങ്ങളുടെ മൊത്തം ഡൗൺലൈൻ ഫുട്പ്രിന്റ്, ഡയറക്റ്റ് (നിങ്ങൾ എഡിറ്റ് ചെയ്യുന്നത്), ഇൻഡയറക്റ്റ് (നിങ്ങളുടെ സബ്-ഏജന്റുമാർ കൈകാര്യം ചെയ്യുന്നത്) എന്നിങ്ങനെ വിഭജിച്ചിരിക്കുന്നു.',

  // ── Live-data footnote ────────────────────────────────────────────
  'agent.dashboard.liveFootnote': 'ലൈവ് ഡാറ്റ — ഫോക്കസിൽ, ഓരോ 60 സെക്കൻഡിലും, പണം നീങ്ങുമ്പോഴും പുതുക്കും.',

  // ── Card: Pool balance (PoolBalanceHero) ──────────────────────────
  'agent.dashboard.cards.poolBalance.label': 'എന്റെ പൂൾ ബാലൻസ്',
  'agent.dashboard.cards.poolBalance.helper': 'നിങ്ങൾക്ക് ചെലവഴിക്കാവുന്ന ബാലൻസ്. പ്ലെയർമാരെ റീചാർജ് ചെയ്യാനോ സബ്-ഏജന്റുമാർക്ക് അയയ്ക്കാനോ ഉള്ള പണം.',
  'agent.dashboard.cards.poolBalance.netInRange': '{range}-ൽ നെറ്റ് {value}',

  // ── Card: Deposit status ──────────────────────────────────────────
  'agent.dashboard.cards.depositStatus.titleLoading': 'സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ്',
  'agent.dashboard.cards.depositStatus.titleLoaded': 'ഡെപ്പോസിറ്റ് സ്റ്റാറ്റസ്',
  'agent.dashboard.cards.depositStatus.helperLoading': 'ഡെപ്പോസിറ്റ് സ്റ്റാറ്റസ് ലോഡ് ചെയ്യുന്നു…',
  'agent.dashboard.cards.depositStatus.helper': 'നിങ്ങളുടെ പൂളിനെതിരെയുള്ള കൊളാറ്ററൽ. ടാർഗെറ്റിന്റെ 50%-ൽ സ്റ്റാറ്റസ് ജാഗ്രതയിലേക്ക് മാറും, 80%-ൽ മുന്നറിയിപ്പ്, 100%-ൽ ലംഘനം.',
  'agent.dashboard.cards.depositStatus.suspendedSince': '{when} മുതൽ',
  'agent.dashboard.cards.depositStatus.field.operatingBalance': 'ഓപ്പറേറ്റിംഗ് ബാലൻസ്',
  'agent.dashboard.cards.depositStatus.field.depositBalance': 'ഡെപ്പോസിറ്റ് ബാലൻസ്',
  'agent.dashboard.cards.depositStatus.field.depositTarget': 'ഡെപ്പോസിറ്റ് ടാർഗെറ്റ്',
  'agent.dashboard.cards.depositStatus.field.negativeExposure': 'നെഗറ്റീവ് എക്സ്പോഷർ',
  'agent.dashboard.cards.depositStatus.field.coverageRatio': 'കവറേജ് അനുപാതം',
  'agent.dashboard.cards.depositStatus.field.coverageRatioHint': 'നെഗറ്റീവ് എക്സ്പോഷർ ഉപയോഗിച്ച ഡെപ്പോസിറ്റ് ടാർഗെറ്റിന്റെ',
  // Margin-status enum surface (lower-case word displayed in the pill on the card).
  'agent.dashboard.depositStatus.status.safe': 'സുരക്ഷിതം',
  'agent.dashboard.depositStatus.status.caution': 'ജാഗ്രത',
  'agent.dashboard.depositStatus.status.warning': 'മുന്നറിയിപ്പ്',
  'agent.dashboard.depositStatus.status.breach': 'ലംഘനം',
  'agent.dashboard.depositStatus.status.suspended': 'സസ്പെൻഡ് ചെയ്തു',
  'agent.dashboard.depositStatus.status.healthy': 'ആരോഗ്യകരം',

  // ── Card: Players' wallet total ───────────────────────────────────
  'agent.dashboard.cards.playersWallet.title': 'പ്ലെയർമാരുടെ വാലറ്റ് ടോട്ടൽ',
  'agent.dashboard.cards.playersWallet.helper': 'നിങ്ങളുടെ ഓരോ പ്ലെയറുടെയും വാലറ്റിലെ ഇപ്പോഴത്തെ ലൈവ് ബാലൻസിന്റെ സംയോജിതം. നിങ്ങൾക്ക് സ്കോപ്പ് ചെയ്ത ഓരോ wallet_points വരിയുടെയും ആകെത്തുക.',
  'agent.dashboard.cards.playersWallet.foot.one': '{n} പ്ലെയർ · {active} സജീവം',
  'agent.dashboard.cards.playersWallet.foot.other': '{n} പ്ലെയർമാർ · {active} സജീവം',

  // ── Card: Sub-agents wallet + deposit ─────────────────────────────
  'agent.dashboard.cards.subAgents.title': 'സബ്-ഏജന്റ് വാലറ്റ് + ഡെപ്പോസിറ്റ്',
  'agent.dashboard.cards.subAgents.helper': 'നിങ്ങളുടെ ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ കൈവശം വയ്ക്കുന്ന സംയോജിത പൂൾ ബാലൻസും സെക്യൂരിറ്റി ഡെപ്പോസിറ്റും. ഓരോ സബ്-ഏജന്റും സ്വന്തം പ്ലെയർമാർ + സ്വന്തം ഡൗൺലൈൻ പ്രവർത്തിപ്പിക്കുന്നു.',
  'agent.dashboard.cards.subAgents.field.pool': 'പൂൾ',
  'agent.dashboard.cards.subAgents.field.deposit': 'ഡെപ്പോസിറ്റ്',
  'agent.dashboard.cards.subAgents.foot.one': '{n} ഡയറക്റ്റ് സബ്-ഏജന്റ്',
  'agent.dashboard.cards.subAgents.foot.other': '{n} ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ',

  // ── Card: My pending requests (priority 3) ────────────────────────
  'agent.dashboard.cards.myPending.title': 'എന്റെ തീരുമാനമാകാത്ത അഭ്യർത്ഥനകൾ',
  'agent.dashboard.cards.myPending.helper': 'മുൻഗണന 3. നിങ്ങളുടെ സ്വന്തം സമർപ്പണങ്ങൾ അഡ്മിന് — നിങ്ങൾക്ക് ഇവയിൽ പ്രവർത്തിക്കാനാവില്ല, അവർക്കായി കാത്തിരിക്കുന്നു.',
  'agent.dashboard.cards.myPending.recharge.one': '{n} റീചാർജ് അഡ്മിനെ കാത്തിരിക്കുന്നു',
  'agent.dashboard.cards.myPending.recharge.other': '{n} റീചാർജുകൾ അഡ്മിനെ കാത്തിരിക്കുന്നു',
  'agent.dashboard.cards.myPending.withdrawal.one': '{n} പിൻവലിക്കൽ അഡ്മിനെ കാത്തിരിക്കുന്നു',
  'agent.dashboard.cards.myPending.withdrawal.other': '{n} പിൻവലിക്കലുകൾ അഡ്മിനെ കാത്തിരിക്കുന്നു',

  // ── Card: Players' pending requests (priority 1) ──────────────────
  'agent.dashboard.cards.playersPending.title': 'പ്ലെയർമാരുടെ തീരുമാനമാകാത്ത അഭ്യർത്ഥനകൾ',
  'agent.dashboard.cards.playersPending.helper': 'മുൻഗണന 1. നിങ്ങളുടെ പ്ലെയർമാർ ഇവ അംഗീകരിക്കാനോ പണമടയ്ക്കാനോ നിങ്ങളെ കാത്തിരിക്കുന്നു. പ്രവർത്തിക്കാൻ ടാപ്പ് ചെയ്യൂ.',
  'agent.dashboard.cards.playersPending.recharge.one': '{n} റീചാർജ് അംഗീകരിക്കാൻ',
  'agent.dashboard.cards.playersPending.recharge.other': '{n} റീചാർജുകൾ അംഗീകരിക്കാൻ',
  'agent.dashboard.cards.playersPending.withdrawal.one': '{n} പിൻവലിക്കൽ പണമടയ്ക്കാൻ',
  'agent.dashboard.cards.playersPending.withdrawal.other': '{n} പിൻവലിക്കലുകൾ പണമടയ്ക്കാൻ',

  // ── Card: Sub-agents' pending requests (priority 2) ───────────────
  // Recharge / withdrawal countdown reuses the playersPending keys —
  // same wording, just a different card title + helper.
  'agent.dashboard.cards.subAgentsPending.title': 'സബ്-ഏജന്റുമാരുടെ തീരുമാനമാകാത്ത അഭ്യർത്ഥനകൾ',
  'agent.dashboard.cards.subAgentsPending.helper': 'മുൻഗണന 2. നിങ്ങളുടെ സബ്-ഏജന്റുമാർ (മുഴുവൻ ഡൗൺലൈൻ) ഇവ അംഗീകരിക്കാനോ പണമടയ്ക്കാനോ നിങ്ങളെ കാത്തിരിക്കുന്നു. പ്രവർത്തിക്കാൻ ടാപ്പ് ചെയ്യൂ.',

  // ── Card: True Net P&L ────────────────────────────────────────────
  'agent.dashboard.cards.trueNet.title': 'ട്രൂ നെറ്റ് P&L · {range}',
  'agent.dashboard.cards.trueNet.helper': 'ഈ റേഞ്ചിലെ നിങ്ങളുടെ യഥാർത്ഥ ലാഭം/നഷ്ടം. പ്ലാറ്റ്ഫോം ഫീ ഷെയർ + ജയം/തോൽവി ഷെയർ − ഓഫർ കോ-ഫണ്ടിംഗ്. പ്രതി-ഗെയിം വിശദാംശങ്ങൾക്ക് താഴെയുള്ള P&L ഡീപ്-ഡൈവ് വികസിപ്പിക്കൂ.',
  // Three short chip labels under the True Net hero — capped at 5
  // chars in English to fit the grid. Translators may use a tighter
  // abbreviation if their script is wider.
  'agent.dashboard.pillarChip.platformFee': 'പ്ലാറ്റ്',
  'agent.dashboard.pillarChip.winLoss': 'W/L',
  'agent.dashboard.pillarChip.offer': 'ഓഫർ',

  // ── Card: Offer utilization ───────────────────────────────────────
  'agent.dashboard.cards.rewardUtil.title': 'ഓഫർ ഉപയോഗം · {range}',
  'agent.dashboard.cards.rewardUtil.helper': 'ഈ റേഞ്ചിൽ നിങ്ങളുടെ പ്ലെയർമാർക്ക് ഓഫറുകൾ വഴി നൽകിയ മൊത്തം പുരസ്കാര ₹. ആ ചെലവിലെ നിങ്ങളുടെ വിഹിതം കാണാൻ നിങ്ങളുടെ എക്സ്പോഷർ പേജുമായി ക്രോസ്-റഫറൻസ് ചെയ്യൂ.',
  'agent.dashboard.cards.rewardUtil.foot.one': '{n} ക്ലെയിം ക്രെഡിറ്റ് ചെയ്തു',
  'agent.dashboard.cards.rewardUtil.foot.other': '{n} ക്ലെയിമുകൾ ക്രെഡിറ്റ് ചെയ്തു',

  // ── Activity row (two side-by-side cards) ─────────────────────────
  'agent.dashboard.activity.topSpenders.title': 'ടോപ്പ് സ്പെൻഡർമാർ',
  'agent.dashboard.activity.topSpenders.helper': '{range}-ൽ ഏറ്റവും കൂടുതൽ ചെലവഴിക്കുന്ന നിങ്ങളുടെ പ്ലെയർമാർ.',
  'agent.dashboard.activity.topSpenders.empty': 'ഈ റേഞ്ചിൽ ഇതുവരെ ചെലവുകളില്ല.',
  'agent.dashboard.activity.recent.title': 'സമീപകാല പ്രവർത്തനം',
  'agent.dashboard.activity.recent.helper': 'നിങ്ങളുടെ പ്ലെയർമാരിൽ നിന്നുള്ള ഏറ്റവും സമീപകാല അഞ്ച് റീചാർജ് ഇവന്റുകൾ.',
  'agent.dashboard.activity.recent.empty': 'സമീപകാല പ്രവർത്തനങ്ങളില്ല.',
  // Recharge state enum surfaced on the row chip.
  'agent.dashboard.activity.recent.state.approved': 'അംഗീകരിച്ചു',
  'agent.dashboard.activity.recent.state.rejected': 'നിരസിച്ചു',
  'agent.dashboard.activity.recent.state.pending': 'തീരുമാനമാകാത്തത്',

  // ── P&L deep-dive (per-pillar tiles + per-game table) ─────────────
  'agent.dashboard.pnl.perGameLabel': 'ഓരോ ഗെയിമും · വിശദീകരണത്തിന് ഒരു വരി ടാപ്പ് ചെയ്യൂ',
  'agent.dashboard.pnl.noEarnings': 'ഈ റേഞ്ചിൽ വരുമാനമില്ല.',
  'agent.dashboard.pnl.table.col.pillarGame': 'പിലർ / ഗെയിം',
  'agent.dashboard.pnl.table.col.net': 'നെറ്റ്',
  'agent.dashboard.pnl.table.col.matches': 'മത്സരങ്ങൾ',
  'agent.dashboard.pnl.ledgerRows.one': '{n} ലെഡ്ജർ വരി',
  'agent.dashboard.pnl.ledgerRows.other': '{n} ലെഡ്ജർ വരികൾ',
  'agent.dashboard.pnl.pillar.platformFee': 'പ്ലാറ്റ്ഫോം ഫീ',
  'agent.dashboard.pnl.pillar.winLoss': 'ജയം / തോൽവി',
  'agent.dashboard.pnl.pillar.offerReward': 'ഓഫർ പുരസ്കാരം',

  // ── P&L drilldown modal ───────────────────────────────────────────
  'agent.dashboard.pnlDrill.windowLabel': 'വിൻഡോ: {range} — ഡാഷ്ബോർഡിന്റെ റേഞ്ചിന്റെ കർശന ഉപഗണം',
  'agent.dashboard.pnlDrill.loading': 'മത്സര തല വിശദാംശങ്ങൾ ലോഡ് ചെയ്യുന്നു…',
  'agent.dashboard.pnlDrill.windowTotal': 'വിൻഡോ ടോട്ടൽ:',
  'agent.dashboard.pnlDrill.matchCount.one': '{n} മത്സരത്തിലുടനീളം',
  'agent.dashboard.pnlDrill.matchCount.other': '{n} മത്സരങ്ങളിലുടനീളം',
  'agent.dashboard.pnlDrill.noContribution': 'ഈ സെല്ലിലേക്ക് മത്സരങ്ങളൊന്നും സംഭാവന ചെയ്തിട്ടില്ല.',
  'agent.dashboard.pnlDrill.errorFallback': 'ഡ്രിൽഡൗൺ ലോഡ് ചെയ്യാനായില്ല',
  'agent.dashboard.pnlDrill.table.col.match': 'മത്സരം',
  'agent.dashboard.pnlDrill.table.col.role': 'റോൾ',
  'agent.dashboard.pnlDrill.table.col.base': 'ബേസ്',
  'agent.dashboard.pnlDrill.table.col.rate': 'നിരക്ക്',
  'agent.dashboard.pnlDrill.table.col.earned': 'നേടിയത്',
  'agent.dashboard.pnlDrill.table.col.settled': 'സെറ്റിൽ ചെയ്തത്',

  // ── Direct sub-agents accordion ───────────────────────────────────
  'agent.dashboard.directDownline.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.dashboard.directDownline.empty': 'ഇതുവരെ ഡയറക്റ്റ് സബ്-ഏജന്റുമാരില്ല.',
  'agent.dashboard.directDownline.row.player.one': '{n} പ്ലെയർ',
  'agent.dashboard.directDownline.row.player.other': '{n} പ്ലെയർമാർ',
  'agent.dashboard.directDownline.row.subAgent.one': '{n} സബ്-ഏജന്റ്',
  'agent.dashboard.directDownline.row.subAgent.other': '{n} സബ്-ഏജന്റുമാർ',
  'agent.dashboard.directDownline.row.walletDeposit': 'വാലറ്റ് {wallet} · ഡെപ്പോസിറ്റ് {deposit}',
  'agent.dashboard.directDownline.footer': 'ട്രൂ നെറ്റ് {range} വിൻഡോയ്ക്കുള്ളതാണ്.',

  // ── 4-quadrant chain visibility ───────────────────────────────────
  'agent.dashboard.chain.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.dashboard.chain.directAgents.title': 'ഡയറക്റ്റ് ഏജന്റുമാർ',
  'agent.dashboard.chain.directAgents.subtitle': 'നിങ്ങളുടെ ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ — അവരുടെ അഭ്യർത്ഥനകളിൽ നിങ്ങൾക്ക് നടപടി എടുക്കാം.',
  'agent.dashboard.chain.indirectAgents.title': 'ഇൻഡയറക്റ്റ് ഏജന്റുമാർ',
  'agent.dashboard.chain.indirectAgents.subtitle': 'നിങ്ങളുടെ സബ്-ഏജന്റുമാർ കൈകാര്യം ചെയ്യുന്നു. നിങ്ങൾക്ക് റീഡ്-ഒൺലി.',
  'agent.dashboard.chain.directPlayers.title': 'ഡയറക്റ്റ് പ്ലെയർമാർ',
  'agent.dashboard.chain.directPlayers.subtitle': 'നിങ്ങളുടെ പ്ലെയർമാർ — അവരുടെ റീചാർജുകളിലും പിൻവലിക്കലുകളിലും നിങ്ങൾ നടപടി എടുക്കുന്നു.',
  'agent.dashboard.chain.indirectPlayers.title': 'ഇൻഡയറക്റ്റ് പ്ലെയർമാർ',
  'agent.dashboard.chain.indirectPlayers.subtitle': 'നിങ്ങളുടെ സബ്-ഏജന്റുമാരുടെ പ്ലെയർമാർ. നിങ്ങൾക്ക് റീഡ്-ഒൺലി.',
  'agent.dashboard.chain.field.walletTotal': 'വാലറ്റ് ടോട്ടൽ',
  'agent.dashboard.chain.field.depositTotal': 'ഡെപ്പോസിറ്റ് ടോട്ടൽ',
  'agent.dashboard.chain.field.avgPerHead': 'ഒരാൾക്ക് ശരാശരി',
  'agent.dashboard.chain.field.pendingRecharge': 'തീരുമാനമാകാത്ത റീചാർജ്',
  'agent.dashboard.chain.field.pendingWithdrawal': 'തീരുമാനമാകാത്ത പിൻവലിക്കൽ',
  'agent.dashboard.chain.pendingBadge': '{n} തീരുമാനമാകാത്തത്',
  'agent.dashboard.chain.openInbox': 'ഇൻബോക്സ് തുറക്കുക',
  'agent.dashboard.chain.footer': 'ഡയറക്റ്റ് = നിങ്ങൾ സ്വയം കൈകാര്യം ചെയ്യുന്ന അംഗങ്ങൾ. ഇൻഡയറക്റ്റ് = നിങ്ങളുടെ സബ്-ഏജന്റുമാർ കൈകാര്യം ചെയ്യുന്നു (നിങ്ങൾക്ക് നമ്പറുകൾ കാണാം പക്ഷേ അവരുടെ തീരുമാനമാകാത്ത അഭ്യർത്ഥനകളിൽ നടപടി എടുക്കാനാവില്ല — അവ അവരുടെ പേരന്റിലേക്ക് പോകുന്നു).',

  // ────────────────────────────────────────────────────────────────────
  // ActivityPanel — downline activity feed embedded in the dashboard
  // accordion. Also rendered standalone (scope='admin') by the admin
  // SPA, so keys carry an `agent.activity.*` prefix even though the
  // file lives in shared-ui-adjacent code.

  'agent.activity.error.loadFailed': 'ലോഡ് ചെയ്യാനായില്ല',
  'agent.activity.filter.window': 'വിൻഡോ',
  'agent.activity.filter.kind': 'തരം',
  // Window pill labels (`24h` / `7d` / `30d` are universal — no
  // translation needed — but `All` is a word; keep it as a key.)
  'agent.activity.window.all': 'എല്ലാം',
  // Kind filter pill labels.
  'agent.activity.kind.all': 'എല്ലാം',
  'agent.activity.kind.matches': 'മത്സരങ്ങൾ',
  'agent.activity.kind.recharges': 'റീചാർജുകൾ',
  'agent.activity.kind.rateChanges': 'നിരക്ക് മാറ്റങ്ങൾ',
  // Empty + loading states.
  'agent.activity.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.activity.empty.title': 'ശാന്തം',
  'agent.activity.empty.subtitle': '{window} വിൻഡോയിൽ {kindWord}ഇവന്റുകളില്ല.',
  'agent.activity.empty.kindWord.all': '',
  'agent.activity.empty.kindWord.match': 'മത്സര ',
  'agent.activity.empty.kindWord.recharge': 'റീചാർജ് ',
  'agent.activity.empty.kindWord.partnership_change': 'പങ്കാളിത്ത മാറ്റം ',

  // Feed-row descriptions. The body is composed in JSX from these
  // building-block keys + interpolated dynamic values (@username,
  // amounts, game id, etc.) so each language can preserve the right
  // word order for "settled / earned / from / on …".
  'agent.activity.row.settlementFallback': 'സെറ്റിൽമെന്റ്',
  'agent.activity.row.earned': 'നേടി',
  'agent.activity.row.fromPillarOn': '{pillar}-ൽ നിന്ന് ',
  // Pillar words used inside row.fromPillarOn — kept lowercase to
  // read naturally in mid-sentence English.
  'agent.activity.row.pillar.platformFee': 'പ്ലാറ്റ്ഫോം ഫീ',
  'agent.activity.row.pillar.winLoss': 'ജയം/തോൽവി',
  'agent.activity.row.pillar.offerReward': 'ഓഫർ പുരസ്കാരം',
  // Recharge row composition.
  'agent.activity.row.recharge.prefix': 'റീചാർജ്',
  'agent.activity.row.recharge.of': 'യുടെ',
  'agent.activity.row.recharge.byUser': 'ഉപയോക്താവ് #{id} വഴി',
  'agent.activity.row.recharge.via': ' വഴി ',
  // Recharge state enum (same as dashboard activity card states).
  'agent.activity.row.state.approved': 'അംഗീകരിച്ചു',
  'agent.activity.row.state.rejected': 'നിരസിച്ചു',
  'agent.activity.row.state.pending': 'തീരുമാനമാകാത്തത്',
  // Partnership rate-change row composition.
  'agent.activity.row.rateChange.prefix': 'നിരക്ക് മാറ്റം',
  'agent.activity.row.rateChange.dashOn': '— {pillar}-ൽ ',
  'agent.activity.row.rateChange.cascade': 'കാസ്കേഡ്',

  // ────────────────────────────────────────────────────────────────────
  // EarningsPanel — lifetime earnings breakdown embedded in the
  // dashboard accordion.

  'agent.earnings.notAgent': 'ഈ കാഴ്ച ഏജന്റുമാർക്ക് മാത്രമാണ്.',
  'agent.earnings.stat.poolBalance': 'പൂൾ ബാലൻസ്',
  'agent.earnings.stat.totalIn': 'മൊത്തം വന്നത്',
  'agent.earnings.stat.totalOut': 'മൊത്തം പോയത്',
  'agent.earnings.section.in': 'വരുമാനം (in)',
  'agent.earnings.section.out': 'ഔട്ട്ഫ്ലോകൾ (out)',
  'agent.earnings.empty.in': 'ഇതുവരെ വരുമാനമില്ല. നിങ്ങളുടെ പ്ലെയർമാർ റീചാർജ് ചെയ്യുകയോ കളിക്കുകയോ ചെയ്താൽ, വിഹിതങ്ങൾ ഇവിടെ ദൃശ്യമാകും.',
  'agent.earnings.empty.out': 'ഇതുവരെ ഔട്ട്ഫ്ലോകളില്ല.',
  'agent.earnings.viewLedger': 'സമ്പൂർണ്ണ ലെഡ്ജർ കാണുക',
  // Ledger-kind metadata — these label + hint pairs explain what each
  // earnings ledger row type means. Translators get full context for
  // each financial concept (admin topup, player recharge, rev share,
  // co-fund, manual correction).
  'agent.earnings.kind.topupFromAdmin.label': 'അഡ്മിനിൽ നിന്ന് ടോപ്പ്അപ്പ്',
  'agent.earnings.kind.topupFromAdmin.hint': 'അഡ്മിൻ നിങ്ങളുടെ പൂളിലേക്ക് ക്രെഡിറ്റ് ചെയ്തു',
  'agent.earnings.kind.topupToPlayer.label': 'പ്ലെയറിലേക്ക് ടോപ്പ്അപ്പ്',
  'agent.earnings.kind.topupToPlayer.hint': 'നിങ്ങൾ ഒരു പ്ലെയറെ റീചാർജ് ചെയ്തു',
  'agent.earnings.kind.topupFromPlayer.label': 'പ്ലെയർ റീചാർജ്',
  'agent.earnings.kind.topupFromPlayer.hint': 'നിങ്ങളുടെ പ്ലെയർമാർ നിങ്ങളുടെ പൂളിലേക്ക് അടച്ചു',
  'agent.earnings.kind.revenueShare.label': 'റവന്യൂ ഷെയർ',
  'agent.earnings.kind.revenueShare.hint': 'മത്സരങ്ങളിൽ നിന്നുള്ള പ്ലാറ്റ്ഫോം ഫീയുടെ വിഹിതം',
  'agent.earnings.kind.coFundOffer.label': 'കോ-ഫണ്ട് ഓഫർ',
  'agent.earnings.kind.coFundOffer.hint': 'ഓഫർ പേയ്ഔട്ടുകളിലെ നിങ്ങളുടെ വിഹിതം',
  'agent.earnings.kind.adminAdjust.label': 'അഡ്മിൻ ക്രമീകരണം',
  'agent.earnings.kind.adminAdjust.hint': 'മാനുവൽ തിരുത്തൽ',
  // Plural for "X entry / entries" in the row hint suffix.
  'agent.earnings.row.entry.one': 'എൻട്രി',
  'agent.earnings.row.entry.other': 'എൻട്രികൾ',

  // ────────────────────────────────────────────────────────────────────
  // LeaderboardPanel — downline-scoped ranking embedded in the
  // dashboard accordion. Same keys also drive the admin-scope copy
  // when an admin lands on this surface — empty-state copy differs
  // by scope, hence the two separate `.subtitle.*` keys.

  'agent.leaderboard.filter.window': 'വിൻഡോ',
  'agent.leaderboard.filter.pillar': 'പിലർ',
  'agent.leaderboard.window.all': 'എല്ലാം',
  'agent.leaderboard.pillar.all': 'എല്ലാ പിലറുകളും',
  'agent.leaderboard.pillar.platformFee': 'പ്ലാറ്റ്ഫോം ഫീ',
  'agent.leaderboard.pillar.winLoss': 'ജയം / തോൽവി',
  'agent.leaderboard.pillar.offerReward': 'ഓഫർ പുരസ്കാരം',
  'agent.leaderboard.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.leaderboard.empty.title': 'ഇതുവരെ വരുമാനമില്ല',
  'agent.leaderboard.empty.subtitle.admin': '{window} വിൻഡോയിൽ എല്ലാ ഏജന്റുമാരിലുടനീളം സെറ്റിൽമെന്റ് പ്രവർത്തനമില്ല.',
  'agent.leaderboard.empty.subtitle.downline': '{window} വിൻഡോയിൽ നിങ്ങളുടെ ഡൗൺലൈനിൽ നിന്ന് വരുമാനമില്ല. നിങ്ങളുടെ സബ്-ഏജന്റുമാർ മത്സരങ്ങൾ നടത്തുമ്പോൾ, ഈ ബോർഡ് നിറയും.',
  'agent.leaderboard.row.matches.one': '{n} മത്സരം',
  'agent.leaderboard.row.matches.other': '{n} മത്സരങ്ങൾ',
  'agent.leaderboard.footer': 'വരുമാനം match_settlement_snapshots-ൽ നിന്നുള്ളതാണ്. നെഗറ്റീവ് നമ്പർ അർത്ഥമാക്കുന്നത് ആ ഏജന്റ് വിൻഡോയിലുടനീളം എടുത്തതിലും കൂടുതൽ പണം നൽകി എന്നാണ്.',
  // Short pillar chip labels in the rightmost cluster of each row.
  'agent.leaderboard.chip.platformFee': 'പ്ലാറ്റ്',
  'agent.leaderboard.chip.winLoss': 'W/L',
  'agent.leaderboard.chip.offer': 'ഓഫർ',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 3 (2026-05-12) — EMERGENCY / GATE MODALS
  //
  // These surfaces are the platform's loudest copy. The English wording
  // is intentionally pointed ("locked", "one bad settlement away", "stuck",
  // "blocked") because the agent needs to FEEL the severity. Translators
  // should preserve that urgency in their target language — don't soften
  // "locked" to "paused" or "blocked" to "limited". The whole point of
  // these screens is to interrupt the agent's flow until they act.
  //
  // Five surfaces share this section:
  //
  //   agent.warning.*         MarginWarningModal — 80% threshold soft warning
  //   agent.lockout.*         EmergencyLockoutGate — 100% account-locked portal
  //   agent.emergencyForm.*   EmergencyRechargeForm — the recharge form used by
  //                           BOTH the warning modal and the lockout gate
  //   agent.geo.*             GeoForcedModal — non-dismissible GPS-capture gate
  //                           that fires for legacy agents on first login
  //
  // ── MarginWarningModal ─────────────────────────────────────────────
  //
  // The warning modal is DISMISSIBLE but re-pops every 10 min in the
  // warning state. Header copy must scream "act now" without claiming
  // the account is already locked (the lockout gate handles that).
  'agent.warning.header.eyebrow': 'മാർജിൻ മുന്നറിയിപ്പ് · 80% പരിധി എത്തി',
  'agent.warning.header.title': 'ലോക്ക്ഔട്ടിലേക്ക് ഒരു മോശം സെറ്റിൽമെന്റ് മാത്രം ദൂരത്ത്.',
  'agent.warning.header.body': 'നിങ്ങൾ ഇതുവരെ ലോക്ക് ചെയ്തിട്ടില്ല — പക്ഷേ നിങ്ങളുടെ ഡെപ്പോസിറ്റ് സുരക്ഷാ കുഷ്യൻ 80%-ൽ കൂടുതൽ ചെലവഴിച്ചു. ഇപ്പോൾ ടോപ്പ് അപ്പ് ചെയ്യൂ, നിങ്ങൾ നിയന്ത്രണത്തിൽ തുടരും.',
  'agent.warning.header.dismissAria': 'ഡിസ്മിസ് ചെയ്യുക',

  // "Why this is showing" — two paragraphs of plain language explaining
  // the cause + the reassurance that early alerts are by design.
  // Interpolation: {deposit} renders the agent's deposit balance (e.g. ₹100).
  'agent.warning.why.eyebrow': 'എന്തുകൊണ്ട് ഇത് കാണിക്കുന്നു',
  'agent.warning.why.body': 'സമീപകാല ഗെയിം സെറ്റിൽമെന്റുകൾ നിങ്ങളുടെ വാലറ്റ് നെഗറ്റീവിലേക്ക് തള്ളി. {deposit}-ന്റെ നിങ്ങളുടെ സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ് ആണ് ഇത് ആഗിരണം ചെയ്യുന്ന സുരക്ഷാ വല — അത് ഇപ്പോൾ 80%-ൽ കൂടുതൽ ചെലവഴിച്ചു. കുറച്ച് കൂടി നെഗറ്റീവ് വന്നാൽ, നിങ്ങളുടെ വാലറ്റ് + ഡെപ്പോസിറ്റ് പൂജ്യത്തിലെത്തും, പ്ലാറ്റ്ഫോം അക്കൗണ്ട് ലോക്ക് ചെയ്യും.',
  'agent.warning.why.reassurance': 'ഈ അലേർട്ട് മനഃപൂർവ്വം നേരത്തെ ഫയർ ചെയ്യുന്നു — നിങ്ങൾക്ക് ഇപ്പോഴും മുഴുവൻ ആപ്പും ഉപയോഗിക്കാം. നിങ്ങൾ 100% കടന്നാൽ മാത്രമേ ലോക്ക്ഔട്ട് സ്ക്രീൻ ദൃശ്യമാകൂ, ഒരിക്കൽ അത് ചെയ്താൽ, നിങ്ങൾ വീണ്ടെടുക്കുന്നതുവരെ മിക്ക സവിശേഷതകളും തടയും. ഇപ്പോൾ പ്രവർത്തിക്കുന്നത് പിന്നീട് പ്രവർത്തിക്കുന്നതിനേക്കാൾ വളരെ കുറവ് തടസ്സം ഉണ്ടാക്കും.',

  // Headroom-to-lockout gauge. {headroom} = how much more negative the
  // wallet can absorb before lockout fires (always ≥ 1 in warning).
  // {pct} = percentage of the danger zone (between 80% and 100%) the
  // agent has descended into. Both interpolated at the call site.
  'agent.warning.gauge.label': 'ലോക്ക്ഔട്ടിന് മുമ്പുള്ള ഇടം',
  'agent.warning.gauge.suffix': 'ഇടം ബാക്കി',
  'agent.warning.gauge.dangerBadge': 'അപകട മേഖലയിലേക്ക് {pct}%',
  'agent.warning.gauge.field.wallet': 'നിങ്ങളുടെ വാലറ്റ്',
  'agent.warning.gauge.field.deposit': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ്',

  // "If you ignore this" panel — concrete list of what locks out at
  // 100%. Same items the lockout gate's "You CAN'T" list shows, so
  // the words match across the two screens.
  'agent.warning.ignore.eyebrow': 'ഇത് അവഗണിച്ച് 100% എത്തിയാൽ',
  'agent.warning.ignore.cardTitle': 'നിങ്ങൾ ഉടൻ നഷ്ടപ്പെടും',
  'agent.warning.ignore.item.dashboard': 'നിങ്ങളുടെ ഡാഷ്ബോർഡ്, P&L, ലീഡർബോർഡുകൾ എന്നിവയിലേക്കുള്ള ആക്സസ്',
  'agent.warning.ignore.item.pages': 'പ്ലെയർമാർ, ഏജന്റുമാർ പേജുകൾ',
  'agent.warning.ignore.item.manualTopup': 'നിങ്ങളുടെ പൂളിൽ നിന്ന് നിങ്ങളുടെ പ്ലെയർമാർക്ക് മാനുവൽ ടോപ്പ്-അപ്പ്',
  'agent.warning.ignore.item.newWithdrawal': 'നിങ്ങളുടെ അപ്പ്ലൈനിലേക്ക് പുതിയ പിൻവലിക്കലുകൾ സമർപ്പിക്കുക',
  'agent.warning.ignore.item.subAgents': 'സബ്-ഏജന്റുമാരെ ചേർക്കുകയോ കൈകാര്യം ചെയ്യുകയോ',
  'agent.warning.ignore.item.profile': 'നിങ്ങളുടെ പ്രൊഫൈൽ അല്ലെങ്കിൽ പേയ്മെന്റ് ക്രമീകരണങ്ങൾ എഡിറ്റ് ചെയ്യുക',
  'agent.warning.ignore.footer': 'നിങ്ങൾക്ക് ഇപ്പോഴും തീരുമാനമാകാത്ത അഭ്യർത്ഥനകൾ അംഗീകരിക്കാനും അടിയന്തിര ടോപ്പ്-അപ്പ് സമർപ്പിക്കാനും കഴിയും — പക്ഷേ നിങ്ങളുടെ വാലറ്റ് + ഡെപ്പോസിറ്റ് പൂജ്യത്തിന് മുകളിലേക്ക് കയറുന്നതുവരെ മറ്റെല്ലാം നിലയ്ക്കും.',

  // Recovery form section heading (above the embedded EmergencyRechargeForm).
  'agent.warning.recoverEyebrow': 'വീണ്ടെടുക്കാൻ ഇപ്പോൾ നിങ്ങളുടെ അപ്പ്ലൈനിന് അടയ്ക്കൂ',

  // Dismiss footer. Wording is deliberately calm so the agent doesn't
  // feel cornered — the 10-min re-pop is the actual pressure mechanism.
  'agent.warning.dismiss.cta': 'ഞാൻ പിന്നീട് ഇത് കൈകാര്യം ചെയ്യാം',
  'agent.warning.dismiss.footnote': 'നിങ്ങൾ മുന്നറിയിപ്പ് മേഖലയിൽ തുടർന്നാൽ 10 മിനിറ്റിൽ ഞങ്ങൾ വീണ്ടും ഓർമിപ്പിക്കും. നിങ്ങൾ വീണ്ടെടുത്താൽ സ്വയം അടയ്ക്കും.',

  // ── EmergencyLockoutGate ──────────────────────────────────────────
  //
  // Non-dismissible. Agent CANNOT navigate the rest of the app until
  // their wallet + deposit climbs back > 0. Header must be unambiguous
  // about both the severity AND the path out (so the agent doesn't
  // panic and assume the account is permanently closed).
  'agent.lockout.header.eyebrow': 'അക്കൗണ്ട് ലോക്ക് ചെയ്തു · ബെയിൽഔട്ട് പോർട്ടൽ',
  'agent.lockout.header.title': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ് കവർ ചെയ്യുന്നതിലും കൂടുതൽ നിങ്ങൾ കടപ്പെട്ടിരിക്കുന്നു.',
  'agent.lockout.header.body': 'വിഷമിക്കേണ്ട — ഇത് വീണ്ടെടുക്കാവുന്നതാണ്. താഴെയുള്ള രണ്ട് ഘട്ടങ്ങൾ പിന്തുടരുക, നിങ്ങൾ സ്വയമേവ അൺലോക്ക് ചെയ്യപ്പെടും.',

  // Why-this-happened panel.
  'agent.lockout.why.eyebrow': 'എന്തുകൊണ്ട് ഇത് സംഭവിച്ചു',
  'agent.lockout.why.body': 'ഗെയിം സെറ്റിൽമെന്റുകൾ നിങ്ങളുടെ വാലറ്റിനെ പൂജ്യത്തിന് താഴെയാക്കി. നിങ്ങളുടെ സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ് ആണ് ഇത് ആഗിരണം ചെയ്യുന്ന സുരക്ഷാ വല — പക്ഷേ നിങ്ങൾ അതിന്റെ 100% ഇപ്പോൾ ഉപയോഗിച്ചു. നിങ്ങളുടെ വാലറ്റും ഡെപ്പോസിറ്റും പൂജ്യത്തിന് മുകളിലേക്ക് കയറുന്നതുവരെ, നിങ്ങൾക്ക് സാധാരണ പ്രവർത്തിക്കാനാവില്ല.',
  'agent.lockout.why.reassurance': 'നിങ്ങൾ എന്തെങ്കിലും തെറ്റ് ചെയ്തതുകൊണ്ടല്ല പ്ലാറ്റ്ഫോം നിങ്ങളെ ലോക്ക് ചെയ്യുന്നത്. നിങ്ങൾ സ്ഥിരപ്പെടുമ്പോൾ നെറ്റ്വർക്കിന് നിങ്ങളുടെ ഡെപ്പോസിറ്റിനെതിരെ കൂടുതൽ കടം ശേഖരിക്കാൻ കഴിയാത്തതിനാലാണ് ഇത് നിങ്ങളെ ലോക്ക് ചെയ്യുന്നത്.',

  // Recovery progress gauge. {pct} = percentage of safeMinimum already
  // paid (live, animates as approvals land + cash flows in).
  'agent.lockout.gauge.label': 'സുരക്ഷിത വീണ്ടെടുക്കൽ ലക്ഷ്യം',
  'agent.lockout.gauge.suffix': 'സുരക്ഷിതമായി അടയ്ക്കാൻ',
  'agent.lockout.gauge.recoveredBadge': '{pct}% വീണ്ടെടുത്തു',
  'agent.lockout.gauge.field.wallet': 'നിങ്ങളുടെ വാലറ്റ്',
  'agent.lockout.gauge.field.deposit': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ്',

  // Three-tier explainer table next to the gauge. The actual interactive
  // picker is inside EmergencyRechargeForm — these are read-only
  // summary rows so the agent sees their options BEFORE scrolling.
  'agent.lockout.tier.justUnlock.label': 'വെറും അൺലോക്ക്',
  'agent.lockout.tier.justUnlock.tag': 'അപകടകരം',
  'agent.lockout.tier.justUnlock.note': 'നിങ്ങളെ തിരികെ ₹0-ലേക്ക് കൊണ്ടുവരുന്നു — ഏതെങ്കിലും പുതിയ നഷ്ടം നിങ്ങളെ തൽക്ഷണം വീണ്ടും ലോക്ക് ചെയ്യും.',
  'agent.lockout.tier.safeMin.label': 'സുരക്ഷിത മിനിമം',
  'agent.lockout.tier.safeMin.tag': 'ശുപാർശ ചെയ്യുന്നു',
  'agent.lockout.tier.safeMin.note': 'ഡെപ്പോസിറ്റ് ({deposit}) + നിങ്ങളുടെ നെഗറ്റീവ് വാലറ്റിന്റെ പകുതി. ഒരു യഥാർത്ഥ കുഷ്യൻ വിടുന്നു.',
  'agent.lockout.tier.fullRecovery.label': 'സമ്പൂർണ്ണ വീണ്ടെടുക്കൽ',
  'agent.lockout.tier.fullRecovery.tag': 'മികച്ചത്',
  'agent.lockout.tier.fullRecovery.note': 'ഡെപ്പോസിറ്റ് സമ്പൂർണ്ണമായി പുനഃസ്ഥാപിക്കുന്നു, വാലറ്റ് പൂജ്യത്തിലേക്ക് തിരികെ കൊണ്ടുവരുന്നു.',

  // "Two ways out" CTA cards — bridge between the gauge and the
  // sections below.
  'agent.lockout.waysOut.eyebrow': 'വീണ്ടെടുക്കാൻ രണ്ട് വഴികൾ — ഏതെങ്കിലും (അല്ലെങ്കിൽ രണ്ടും) ചെയ്യൂ',
  'agent.lockout.waysOut.inbox.title': 'നിങ്ങളുടെ ഇൻബോക്സിൽ നടപടി എടുക്കൂ',
  'agent.lockout.waysOut.inbox.body': 'പ്ലെയർ റീചാർജുകൾ അംഗീകരിക്കൂ (പണം ഇതിനകം നിങ്ങളുടെ കൈയിലുണ്ട്) ഒപ്പം പ്ലെയർ പിൻവലിക്കലുകൾ പണമടച്ചതായി മാർക്ക് ചെയ്യൂ. ഓരോ അംഗീകാരവും നിങ്ങളുടെ വാലറ്റിലേക്ക് ക്രെഡിറ്റ് ചെയ്യും.',
  'agent.lockout.waysOut.inbox.ctaOpen': 'ഇൻബോക്സ് തുറക്കുക',
  'agent.lockout.waysOut.inbox.ctaOpened': 'ഇൻബോക്സ് താഴെ തുറന്നിരിക്കുന്നു',
  'agent.lockout.waysOut.upline.title': 'നിങ്ങളുടെ അപ്പ്ലൈനിന് അടയ്ക്കൂ',
  'agent.lockout.waysOut.upline.body': 'UPI വഴി നിങ്ങളുടെ പേരന്റ് ഏജന്റിന് (അല്ലെങ്കിൽ അഡ്മിന്) പണം അയയ്ക്കൂ, പിന്നെ txn id സമർപ്പിക്കൂ. അവർ അംഗീകരിച്ച ഉടൻ, നിങ്ങളുടെ വാലറ്റ് തൽക്ഷണം ക്രെഡിറ്റ് ചെയ്യും.',
  'agent.lockout.waysOut.upline.ctaOpen': 'ഫോം തുറക്കുക',
  'agent.lockout.waysOut.upline.ctaOpened': 'ഫോം താഴെ തുറന്നിരിക്കുന്നു',

  // CAN / CANNOT capability lists.
  'agent.lockout.capabilities.eyebrow': 'നിങ്ങൾക്ക് ഇപ്പോഴും ചെയ്യാൻ കഴിയുന്നത് (തടഞ്ഞത് എന്താണ്)',
  'agent.lockout.capabilities.canHeader': 'നിങ്ങൾക്ക് ഇപ്പോഴും കഴിയും',
  'agent.lockout.capabilities.cantHeader': 'നിങ്ങൾക്ക് (താൽക്കാലികമായി) കഴിയില്ല',
  // Reuse the warning modal's "ignore" items for the CAN'T list —
  // same blocked features list either way.
  'agent.lockout.can.approvePlayerRecharge': 'ഇൻകമിംഗ് പ്ലെയർ റീചാർജുകൾ അംഗീകരിക്കുക',
  'agent.lockout.can.markPlayerWithdrawalPaid': 'പ്ലെയർ പിൻവലിക്കലുകൾ പണമടച്ചതായി മാർക്ക് ചെയ്യുക',
  'agent.lockout.can.approveSubAgentRecharge': 'സബ്-ഏജന്റ് റീചാർജുകൾ അംഗീകരിക്കുക',
  'agent.lockout.can.markSubAgentWithdrawalPaid': 'സബ്-ഏജന്റ് പിൻവലിക്കലുകൾ പണമടച്ചതായി മാർക്ക് ചെയ്യുക',
  'agent.lockout.can.submitToUpline': 'നിങ്ങളുടെ അപ്പ്ലൈനിലേക്ക് ഒരു റീചാർജ് സമർപ്പിക്കുക',
  'agent.lockout.can.receiveSseAndSignOut': 'SSE അലേർട്ടുകൾ സ്വീകരിക്കുകയും സൈൻ ഔട്ട് ചെയ്യുകയും',

  // Section A/B/C headers.
  'agent.lockout.section.step': 'ഘട്ടം {n} · {title}',
  'agent.lockout.section.inbox.title': 'നിങ്ങളുടെ ഇൻബോക്സ് — ഓരോ അംഗീകാരവും നിങ്ങളുടെ വാലറ്റിലേക്ക് ക്രെഡിറ്റ് ചെയ്യും',
  'agent.lockout.section.inbox.subtitle': 'നടപടി എടുക്കാൻ നിങ്ങളെ കാത്തിരിക്കുന്ന പ്ലെയർമാരിൽ നിന്നും സബ്-ഏജന്റുമാരിൽ നിന്നുമുള്ള തീരുമാനമാകാത്ത പേയ്മെന്റ് അഭ്യർത്ഥനകൾ.',
  'agent.lockout.section.inbox.fromPlayers': 'നിങ്ങളുടെ പ്ലെയർമാരിൽ നിന്ന്',
  'agent.lockout.section.inbox.fromSubAgents': 'നിങ്ങളുടെ സബ്-ഏജന്റുമാരിൽ നിന്ന്',
  'agent.lockout.section.upline.title': 'നിങ്ങളുടെ വാലറ്റ് ടോപ്പ് അപ്പ് ചെയ്യാൻ നിങ്ങളുടെ അപ്പ്ലൈനിന് അടയ്ക്കൂ',
  'agent.lockout.section.upline.subtitle': 'നിങ്ങളുടെ പേരന്റിന്റെ UPI നിങ്ങളുടെ മുന്നിലുള്ളപ്പോൾ വേഗത്തിലാണ്. അംഗീകാരം സാധാരണ മിനിറ്റുകൾക്കുള്ളിലാണ്.',

  // Queue row labels + actions.
  'agent.lockout.queue.player.recharge.title': 'റീചാർജ് · {amount} · @{username}',
  'agent.lockout.queue.player.recharge.subtitle': 'Txn {txn} · {when} സമർപ്പിച്ചു',
  'agent.lockout.queue.player.recharge.subtitleNote': 'Txn {txn} · {when} സമർപ്പിച്ചു · "{note}"',
  'agent.lockout.queue.player.withdrawal.title': 'പിൻവലിക്കൽ · {amount} · @{username}',
  'agent.lockout.queue.player.withdrawal.subtitle': '{upi}-ലേക്ക് അടയ്ക്കുക · {when} സമർപ്പിച്ചു',
  'agent.lockout.queue.player.withdrawal.subtitleWa': '{upi}-ലേക്ക് അടയ്ക്കുക · {whatsapp} · {when} സമർപ്പിച്ചു',
  'agent.lockout.queue.subAgent.recharge.title': 'സബ്-ഏജന്റ് റീചാർജ് · {amount} · @{username}',
  'agent.lockout.queue.subAgent.recharge.subtitle': 'Txn {txn} · {when} സമർപ്പിച്ചു',
  'agent.lockout.queue.subAgent.recharge.subtitleNote': 'Txn {txn} · {when} സമർപ്പിച്ചു · "{note}"',
  'agent.lockout.queue.subAgent.withdrawal.title': 'സബ്-ഏജന്റ് പിൻവലിക്കൽ · {amount} · @{username}',
  'agent.lockout.queue.subAgent.withdrawal.subtitle': '{upi}-ലേക്ക് അടയ്ക്കുക · {when} സമർപ്പിച്ചു',
  'agent.lockout.queue.subAgent.withdrawal.subtitleNote': '{upi}-ലേക്ക് അടയ്ക്കുക · {when} സമർപ്പിച്ചു · "{note}"',

  'agent.lockout.queue.action.approve': 'അംഗീകരിക്കുക',
  'agent.lockout.queue.action.reject': 'നിരസിക്കുക',
  'agent.lockout.queue.action.markPaid': 'പണമടച്ചതായി മാർക്ക് ചെയ്യുക',

  // Approve/Reject prompts + toasts. The `prompt()` for the
  // mark-paid flow has to be a STRING (browser native dialog), so
  // it goes through t() at the call site.
  'agent.lockout.queue.prompt.markPlayerPaid': 'പ്ലെയറിന് അടയ്ക്കാൻ നിങ്ങൾ ഉപയോഗിച്ച UPI ഇടപാട് ID നൽകുക:',
  'agent.lockout.queue.prompt.markSubAgentPaid': 'സബ്-ഏജന്റിന് അടയ്ക്കാൻ നിങ്ങൾ ഉപയോഗിച്ച UPI ഇടപാട് ID നൽകുക:',
  'agent.lockout.queue.confirm.rejectPlayerWithdrawal': 'ഈ പിൻവലിക്കൽ നിരസിക്കണോ? ഹോൾഡ് ചെയ്ത തുക പ്ലെയറിന് റീഫണ്ട് ചെയ്യും.',
  'agent.lockout.queue.confirm.rejectSubAgentWithdrawal': 'ഈ സബ്-ഏജന്റ് പിൻവലിക്കൽ നിരസിക്കണോ?',
  'agent.lockout.queue.toast.rechargeApproved': 'റീചാർജ് അംഗീകരിച്ചു — പ്ലെയർ ക്രെഡിറ്റ് ചെയ്തു',
  'agent.lockout.queue.toast.rechargeRejected': 'റീചാർജ് നിരസിച്ചു',
  'agent.lockout.queue.toast.subAgentRechargeApproved': 'സബ്-ഏജന്റ് റീചാർജ് അംഗീകരിച്ചു',
  'agent.lockout.queue.toast.subAgentRechargeRejected': 'റീചാർജ് നിരസിച്ചു',
  'agent.lockout.queue.toast.withdrawalPaid': 'പിൻവലിക്കൽ പണമടച്ചതായി മാർക്ക് ചെയ്തു — നിങ്ങളുടെ വാലറ്റ് ക്രെഡിറ്റ് ചെയ്തു',
  'agent.lockout.queue.toast.subAgentWithdrawalPaid': 'സബ്-ഏജന്റ് പിൻവലിക്കൽ പണമടച്ചതായി മാർക്ക് ചെയ്തു — വാലറ്റ് ക്രെഡിറ്റ് ചെയ്തു',
  'agent.lockout.queue.toast.withdrawalRejected': 'പിൻവലിക്കൽ നിരസിച്ചു (പ്ലെയറിന് റീഫണ്ട് ചെയ്തു)',
  'agent.lockout.queue.toast.subAgentWithdrawalRejected': 'പിൻവലിക്കൽ നിരസിച്ചു',
  'agent.lockout.queue.toast.actionFailed': 'നടപടി പരാജയപ്പെട്ടു',

  // Queue states + empty/loading/error fallbacks.
  'agent.lockout.queue.empty.player': 'ഇപ്പോൾ നിങ്ങളുടെ പ്ലെയർമാരിൽ നിന്ന് ഒന്നുമില്ല.',
  'agent.lockout.queue.empty.subAgent': 'ഇപ്പോൾ നിങ്ങളുടെ സബ്-ഏജന്റുമാരിൽ നിന്ന് ഒന്നുമില്ല.',
  'agent.lockout.queue.loading': 'ക്യൂ ലോഡ് ചെയ്യുന്നു…',

  // Refresh button at the bottom of the gate.
  'agent.lockout.refresh.cta': 'പുതുക്കുക — ഞാൻ ഇപ്പോഴും അൺലോക്ക് ചെയ്തിട്ടുണ്ടോ?',
  'agent.lockout.refresh.checking': 'പരിശോധിക്കുന്നു…',
  'agent.lockout.refresh.toast': 'സ്റ്റാറ്റസ് പുതുക്കി',
  'agent.lockout.refresh.footnote': 'നിങ്ങളുടെ വാലറ്റ് + ഡെപ്പോസിറ്റ് പൂജ്യത്തിന് മുകളിലേക്ക് കയറുന്ന നിമിഷം ഈ പേജ് സ്വയം അടയ്ക്കും.',

  // ── EmergencyRechargeForm (shared between warning + lockout) ─────
  //
  // Same form, two callers. The `severity` prop ('warning' | 'locked')
  // flips banner copy + accent colour, but the tier picker math + the
  // submit flow is identical.
  'agent.emergencyForm.error.loadFailed': 'റീചാർജ് വിശദാംശങ്ങൾ ലോഡ് ചെയ്യാനായില്ല',

  // Banner copy (the small panel just under the tier picker).
  'agent.emergencyForm.banner.locked.title': 'നിങ്ങളുടെ അക്കൗണ്ട് അൺലോക്ക് ചെയ്യാൻ ഇപ്പോൾ അടയ്ക്കൂ',
  'agent.emergencyForm.banner.locked.body': 'നിങ്ങളുടെ പ്ലെയർമാർ നിങ്ങളുടെ ഡെപ്പോസിറ്റ് കൊളാറ്ററൽ കവർ ചെയ്യാൻ കഴിയുന്നതിലും കൂടുതൽ ജയിച്ചു. നിങ്ങളുടെ നെഗറ്റീവ് ബാലൻസ് മായ്ക്കുന്നതുവരെ നിങ്ങൾക്ക് പ്ലാറ്റ്ഫോം ഉപയോഗിക്കാനാവില്ല.',
  'agent.emergencyForm.banner.warning.title': 'ഉടൻ ടോപ്പ് അപ്പ് ചെയ്യൂ — നിങ്ങൾ പരിധിയോട് അടുത്താണ്',
  'agent.emergencyForm.banner.warning.body': 'നിങ്ങളുടെ വാലറ്റ് നിങ്ങളുടെ ഡെപ്പോസിറ്റിന്റെ 80% കടന്നു. 100% എത്തുന്നതിന് മുമ്പ് ടോപ്പ് അപ്പ് ചെയ്യൂ — ഒരിക്കൽ അത് ചെയ്താൽ, നിങ്ങളുടെ അക്കൗണ്ട് ലോക്ക് ചെയ്യും.',

  // Wallet/deposit summary row above the picker.
  'agent.emergencyForm.row.wallet': 'നിങ്ങളുടെ വാലറ്റ്',
  'agent.emergencyForm.row.deposit': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ്',

  // Tier picker panel — same picker, two title/copy paths.
  'agent.emergencyForm.tier.eyebrow.locked': 'നിങ്ങൾ എന്തുകൊണ്ട് കുടുങ്ങി',
  'agent.emergencyForm.tier.eyebrow.warning': 'നിങ്ങൾ എന്തുകൊണ്ട് റിസ്കിലാണ്',
  'agent.emergencyForm.tier.context.locked': 'നിങ്ങളുടെ വാലറ്റ് {wallet} ആയി — അത് {deposit}-ന്റെ നിങ്ങളുടെ ഡെപ്പോസിറ്റ് സുരക്ഷാ കുഷ്യൻ കടന്നു. നിങ്ങളുടെ ഡൗൺലൈൻ നഷ്ടങ്ങൾ ശൂന്യമായ ബഫറിനെതിരെ കൂടുതൽ കൂട്ടാൻ കഴിയാത്തതിനാൽ പ്ലാറ്റ്ഫോം ഇവിടെ നിങ്ങളെ ലോക്ക് ചെയ്യുന്നു.',
  'agent.emergencyForm.tier.context.warning': 'നിങ്ങളുടെ വാലറ്റ് {wallet} ആയി — അത് {deposit}-ന്റെ നിങ്ങളുടെ ഡെപ്പോസിറ്റ് സുരക്ഷാ കുഷ്യന്റെ 80%-ൽ കൂടുതലാണ്. ഇനിയും ഒരു മോശം സെറ്റിൽമെന്റ് നിങ്ങളെ 100% കടത്തി അക്കൗണ്ട് ലോക്ക് ചെയ്യും.',
  'agent.emergencyForm.tier.pickHowMuch': 'എത്ര അടയ്ക്കണമെന്ന് തിരഞ്ഞെടുക്കുക',
  // Tier rows — labels differ between locked and warning so the
  // user reads "just unlock" vs "just escape warning" appropriately.
  'agent.emergencyForm.tier.bareMin.label.locked': 'വെറും അൺലോക്ക്',
  'agent.emergencyForm.tier.bareMin.label.warning': 'മുന്നറിയിപ്പിൽ നിന്ന് മാത്രം രക്ഷപ്പെടുക',
  'agent.emergencyForm.tier.bareMin.tag': 'അപകടകരം',
  'agent.emergencyForm.tier.bareMin.note.locked': 'നിങ്ങളുടെ വാലറ്റ് + ഡെപ്പോസിറ്റ് ₹0-ലേക്ക് മാത്രമേ തിരികെ കൊണ്ടുവരൂ. ഏതെങ്കിലും പുതിയ നഷ്ടം നിങ്ങളെ വീണ്ടും ലോക്ക് ചെയ്യും.',
  'agent.emergencyForm.tier.bareMin.note.warning': 'നിങ്ങളെ 80% ലൈനിന് മുകളിലേക്ക് മാത്രമേ തിരികെ കൊണ്ടുവരൂ. ഏതെങ്കിലും പുതിയ നഷ്ടം മുന്നറിയിപ്പ് വീണ്ടും ട്രിഗർ ചെയ്യും — അല്ലെങ്കിൽ നിങ്ങളെ ലോക്ക്ഔട്ടിലേക്ക് തള്ളും.',
  'agent.emergencyForm.tier.safeMin.label': 'സുരക്ഷിത മിനിമം',
  'agent.emergencyForm.tier.safeMin.tag': 'ശുപാർശ ചെയ്യുന്നു',
  'agent.emergencyForm.tier.safeMin.note': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ് ({deposit}) + നിങ്ങളുടെ നെഗറ്റീവ് വാലറ്റിന്റെ പകുതി ({half}). യഥാർത്ഥ കുഷ്യൻ വിടുന്നു.',
  'agent.emergencyForm.tier.full.label': 'സമ്പൂർണ്ണ വീണ്ടെടുക്കൽ',
  'agent.emergencyForm.tier.full.tag': 'മികച്ചത്',
  'agent.emergencyForm.tier.full.note': 'ഡെപ്പോസിറ്റ് + നിങ്ങളുടെ സമ്പൂർണ്ണ നെഗറ്റീവ് വാലറ്റ് ({neg}). ഡെപ്പോസിറ്റ് സമ്പൂർണ്ണമായി പുനഃസ്ഥാപിക്കുന്നു, വാലറ്റ് പൂജ്യത്തിലേക്ക് തിരികെ കൊണ്ടുവരുന്നു.',

  // Parent-payment card (rendered inside the form, separate from the
  // identical card in the wallet/deposit recharge forms because the
  // emergency form has a slightly different layout). Keys are
  // distinct so the warning/lockout copy can drift from the routine
  // recharge copy if needed.
  'agent.emergencyForm.parent.label.admin': 'നിങ്ങളുടെ അഡ്മിന് അടയ്ക്കൂ',
  'agent.emergencyForm.parent.label.parentAgent': 'നിങ്ങളുടെ പേരന്റ് ഏജന്റിന് അടയ്ക്കൂ',
  'agent.emergencyForm.parent.role.admin': 'പ്ലാറ്റ്ഫോം അഡ്മിൻ',
  'agent.emergencyForm.parent.role.parentAgent': 'പേരന്റ് ഏജന്റ്',
  'agent.emergencyForm.parent.upi': 'UPI:',
  'agent.emergencyForm.parent.whatsapp': 'വാട്ട്സാപ്പ്: {number}',
  'agent.emergencyForm.parent.notOnFile': 'നിങ്ങളുടെ പേരന്റിന്റെ പേയ്മെന്റ് വിശദാംശങ്ങൾ ഇതുവരെ ഫയലിലില്ല. ദയവായി പ്ലാറ്റ്ഫോം പിന്തുണയെ നേരിട്ട് ബന്ധപ്പെടുക.',

  // Submit form.
  'agent.emergencyForm.field.amount.label': 'അടച്ച തുക (₹)',
  'agent.emergencyForm.field.txn.label': 'UPI ഇടപാട് ID / UTR',
  'agent.emergencyForm.field.txn.placeholder': 'ഉദാ. 412312345678',
  'agent.emergencyForm.field.note.label': 'കുറിപ്പ് (ഐച്ഛികം)',
  'agent.emergencyForm.field.note.placeholder': 'നിങ്ങളുടെ പേരന്റ് അറിയേണ്ടത് എന്തെങ്കിലും',
  'agent.emergencyForm.validation.amount': 'നിങ്ങൾ അടച്ച തുക നൽകുക (കുറഞ്ഞത് ₹10)',
  'agent.emergencyForm.validation.txn': 'UPI ഇടപാട് ID / UTR നൽകുക (കുറഞ്ഞത് 6 പ്രതീകങ്ങൾ)',
  'agent.emergencyForm.cta': 'റീചാർജ് അഭ്യർത്ഥന സമർപ്പിക്കുക',
  'agent.emergencyForm.ctaSubmitting': 'സമർപ്പിക്കുന്നു…',
  // Success state.
  'agent.emergencyForm.success.title': 'റീചാർജ് സമർപ്പിച്ചു',
  'agent.emergencyForm.success.bodyAdmin': 'അഭ്യർത്ഥന #{id} അംഗീകരിക്കാൻ അഡ്മിനെ കാത്തിരിക്കുന്നു. അംഗീകരിച്ച ഉടൻ, നിങ്ങളുടെ വാലറ്റ് ക്രെഡിറ്റ് ചെയ്യും, നിങ്ങളുടെ അക്കൗണ്ട് സ്വയം അൺലോക്ക് ചെയ്യും.',
  'agent.emergencyForm.success.bodyAgent': 'അഭ്യർത്ഥന #{id} അംഗീകരിക്കാൻ @{username}-നെ കാത്തിരിക്കുന്നു. അംഗീകരിച്ച ഉടൻ, നിങ്ങളുടെ വാലറ്റ് ക്രെഡിറ്റ് ചെയ്യും, നിങ്ങളുടെ അക്കൗണ്ട് സ്വയം അൺലോക്ക് ചെയ്യും.',
  'agent.emergencyForm.toast.submitted': 'റീചാർജ് അഭ്യർത്ഥന #{id} സമർപ്പിച്ചു. അംഗീകാരത്തിനായി കാത്തിരിക്കുന്നു.',
  'agent.emergencyForm.toast.cantSave': 'ഭാഷ സേവ് ചെയ്യാനായില്ല',

  // ── GeoForcedModal ────────────────────────────────────────────────
  //
  // Non-dismissible until lat/lng land. Copy is firm but not panicked
  // because the cause (one-tap-GPS not done yet) isn't a financial
  // crisis — just an onboarding gap. Wording matches the warning-
  // not-emergency tone.
  'agent.geo.title': 'ഒറ്റത്തവണ സജ്ജീകരണം: നിങ്ങളുടെ സ്ഥാനം സ്ഥിരീകരിക്കുക',
  'agent.geo.subtitle': 'ഡാഷ്ബോർഡ് ഉപയോഗിക്കാൻ കഴിയുന്നതിന് മുമ്പ് ആവശ്യമാണ്.',
  'agent.geo.body.primary': 'ബാലി ഗെയിംസ് പ്ലാറ്റ്ഫോം പുതിയ പ്ലെയർമാരെ ഫിസിക്കലായി ഏറ്റവും അടുത്തുള്ള ഏജന്റിലേക്ക് റൂട്ട് ചെയ്യുന്നു. നിങ്ങൾ കൃത്യമായ GPS സ്ഥാനം പങ്കിടുന്നതുവരെ, നിങ്ങൾ ലൊക്കേറ്ററിൽ ദൃശ്യമാകില്ല, ഓർഗാനിക് സൈൻഅപ്പുകളിൽ നിന്ന് പുതിയ പ്ലെയർമാർ പൂജ്യം ലഭിക്കും — നിങ്ങളുടെ റഫറൽ കോഡ് നേരിട്ട് ടൈപ്പ് ചെയ്യുന്ന പ്ലെയർമാർ മാത്രമേ ലഭിക്കൂ.',
  'agent.geo.body.secondary': 'ഒരിക്കൽ "എന്റെ നിലവിലുള്ള സ്ഥാനം ഉപയോഗിക്കുക" ടാപ്പ് ചെയ്യൂ. ഞങ്ങൾ നിങ്ങളുടെ കോർഡിനേറ്റുകൾ മാത്രമേ ക്യാപ്ചർ ചെയ്യൂ, മറ്റൊന്നുമല്ല.',
  'agent.geo.hint.disabled': 'മുകളിൽ "എന്റെ നിലവിലുള്ള സ്ഥാനം ഉപയോഗിക്കുക" ടാപ്പ് ചെയ്യൂ. പിൻകോഡ് അല്ലെങ്കിൽ മാനുവൽ പ്രദേശ പിക്ക് മാത്രം പോരാ — ലൊക്കേറ്റർക്ക് കൃത്യമായ GPS കോർഡിനേറ്റുകൾ ആവശ്യമാണ്.',
  'agent.geo.error.fallback': 'സ്ഥാനം സേവ് ചെയ്യാനായില്ല. ദയവായി വീണ്ടും ശ്രമിക്കൂ.',
  'agent.geo.cta.save': 'സേവ് ചെയ്ത് തുടരുക',
  'agent.geo.cta.saving': 'സ്ഥാനം സേവ് ചെയ്യുന്നു…',
  'agent.geo.toast.success': 'സ്ഥാനം സേവ് ചെയ്തു — നിങ്ങൾക്ക് ഇപ്പോൾ പ്ലെയർമാരെ സ്വീകരിക്കാം.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 4 (2026-05-12) — WALLET / DEPOSIT PANELS + FORMS
  //
  // Four surfaces:
  //   agent.wallet.*       MyWalletPanel — pool balance + pending + history
  //   agent.deposit.*      MyDepositPanel — deposit balance + coverage gauge + ledger
  //   agent.recharge.*     RequestRechargeForm — inline recharge submission (wallet OR deposit)
  //   agent.withdrawal.*   RequestWithdrawalForm — inline withdrawal submission (wallet OR deposit)
  //
  // Both forms accept a `target` prop ('wallet'|'deposit') so the same
  // form embeds in both panels. Banner + CTA copy varies by target.

  // ── MyWalletPanel ────────────────────────────────────────────────
  'agent.wallet.hero.label': 'പൂൾ ബാലൻസ്',
  'agent.wallet.hero.helper': 'പ്ലെയർ റീചാർജുകൾക്കും സബ്-ഏജന്റ് ടോപ്പ്-അപ്പുകൾക്കും ചെലവഴിക്കാൻ ലഭ്യം. വരുമാനവും അഡ്മിൻ ടോപ്പ്-അപ്പുകളും ഇവിടെ എത്തുന്നു.',

  'agent.wallet.action.recharge': 'റീചാർജ് അഭ്യർത്ഥിക്കുക',
  'agent.wallet.action.withdraw': 'പിൻവലിക്കൽ അഭ്യർത്ഥിക്കുക',

  // Sub-tabs.
  'agent.wallet.tab.pending': 'തീരുമാനമാകാത്തത്',
  'agent.wallet.tab.history': 'ചരിത്രം',

  // Pending feed.
  'agent.wallet.pending.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.wallet.pending.empty': 'ഇപ്പോൾ തീരുമാനമാകാത്ത അഭ്യർത്ഥനകളില്ല.',
  // Each pending row composes a title + a meta line.
  'agent.wallet.pending.recharge.title': 'റീചാർജ് അഭ്യർത്ഥന · +{amount}',
  'agent.wallet.pending.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.pending.withdrawal.title': 'പിൻവലിക്കൽ അഭ്യർത്ഥന · −{amount}',
  'agent.wallet.pending.withdrawal.meta': '{upi}-ലേക്ക് · {when}',
  'agent.wallet.pending.badge': 'തീരുമാനമാകാത്തത്',

  // History feed.
  'agent.wallet.history.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.wallet.history.empty': 'ഇതുവരെ വാലറ്റ് പ്രവർത്തനങ്ങളില്ല.',
  // History recharge / withdrawal cards. The state-based labels
  // ("Recharge accepted" vs "Recharge rejected") are encoded as two
  // separate keys so translators can pick natural phrasing for each.
  'agent.wallet.history.recharge.titleAccepted': 'റീചാർജ് സ്വീകരിച്ചു · {amount}',
  'agent.wallet.history.recharge.titleRejected': 'റീചാർജ് നിരസിച്ചു · {amount}',
  'agent.wallet.history.recharge.meta': 'Txn {txn} · {when}',
  'agent.wallet.history.withdrawal.titlePaid': 'പിൻവലിക്കൽ പണമടച്ചു · {amount}',
  'agent.wallet.history.withdrawal.titleRejected': 'പിൻവലിക്കൽ നിരസിച്ചു · {amount}',
  'agent.wallet.history.withdrawal.meta': '{upi}-ലേക്ക് · {when}',
  'agent.wallet.history.withdrawal.paidTxn': 'പണമടച്ചു · {txn}',

  // State pill on history rows.
  'agent.wallet.state.accepted': 'സ്വീകരിച്ചു',
  'agent.wallet.state.paid': 'പണമടച്ചു',
  'agent.wallet.state.rejected': 'നിരസിച്ചു',
  'agent.wallet.state.expired': 'കാലഹരണപ്പെട്ടു',

  // Ledger row labels + balance-after annotation. The ledger kinds
  // mirror the keys in agent.earnings.kind.* but the wallet panel
  // also has wallet-specific kinds (withdrawal_hold, withdrawal_paid,
  // withdrawal_refund) which earnings doesn't carry.
  'agent.wallet.ledger.kind.topupFromAdmin': 'അഡ്മിനിൽ നിന്ന് ടോപ്പ്അപ്പ്',
  'agent.wallet.ledger.kind.topupToPlayer': 'പ്ലെയറിലേക്ക് ടോപ്പ്അപ്പ്',
  'agent.wallet.ledger.kind.topupFromPlayer': 'പ്ലെയർ റീചാർജ്',
  'agent.wallet.ledger.kind.revenueShare': 'റവന്യൂ ഷെയർ',
  'agent.wallet.ledger.kind.coFundOffer': 'കോ-ഫണ്ട് ഓഫർ',
  'agent.wallet.ledger.kind.adminAdjust': 'അഡ്മിൻ ക്രമീകരണം',
  'agent.wallet.ledger.kind.withdrawalHold': 'പിൻവലിക്കൽ ഹോൾഡ്',
  'agent.wallet.ledger.kind.withdrawalPaid': 'പിൻവലിക്കൽ പണമടച്ചു',
  'agent.wallet.ledger.kind.withdrawalRefund': 'പിൻവലിക്കൽ റീഫണ്ട്',
  'agent.wallet.ledger.balanceAfter': 'ബാൽ {amount}',

  // ── MyDepositPanel ──────────────────────────────────────────────
  'agent.deposit.hero.label': 'സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ്',
  'agent.deposit.hero.helper': 'നിങ്ങളുടെ പൂളിനെതിരെയുള്ള കൊളാറ്ററൽ. നിങ്ങളുടെ വാലറ്റ് നല്ല നിലയിലുള്ളിടത്തോളം തൊടാതെ തുടരും.',
  // Margin status pill (Safe / Warning / Locked).
  'agent.deposit.margin.safe': 'സുരക്ഷിതം',
  'agent.deposit.margin.warning': 'മുന്നറിയിപ്പ്',
  'agent.deposit.margin.locked': 'ലോക്ക് ചെയ്തു',

  // Deposit action buttons (Phase 10).
  'agent.deposit.action.recharge': 'ഡെപ്പോസിറ്റ് റീചാർജ്',
  'agent.deposit.action.withdraw': 'ഡെപ്പോസിറ്റ് പിൻവലിക്കുക',

  // Coverage gauge.
  'agent.deposit.gauge.label': 'കവറേജ് ഗേജ്',
  // 80%/100% labels + their suffixes (suffix shows on sm+ screens).
  'agent.deposit.gauge.thresholdWarn': 'മുന്നറിയിപ്പ്',
  'agent.deposit.gauge.thresholdLock': 'ലോക്ക്',
  // Legend lines below the bar.
  'agent.deposit.gauge.legend.warn': '{pct}-ൽ, നിങ്ങൾക്ക് മുന്നറിയിപ്പ് ലഭിക്കും',
  'agent.deposit.gauge.legend.lock': '{pct}-ൽ, നിങ്ങളുടെ അക്കൗണ്ട് ലോക്ക് ചെയ്യും',
  // Captions — one per margin state. {pct} = current coverage percentage.
  'agent.deposit.gauge.caption.locked': 'പൂൾ സമ്പൂർണ്ണമായി ഡെപ്പോസിറ്റ് കൊണ്ട് കവർ ചെയ്തു — നിങ്ങൾ ടോപ്പ് അപ്പ് ചെയ്യുന്നതുവരെ പിൻവലിക്കലുകൾ തടഞ്ഞിരിക്കുന്നു.',
  'agent.deposit.gauge.caption.warning': 'പൂൾ നിങ്ങളുടെ ഡെപ്പോസിറ്റിന്റെ {pct}% ഉപയോഗിച്ചു. ലോക്ക്ഔട്ട് ഒഴിവാക്കാൻ 100% എത്തുന്നതിന് മുമ്പ് ടോപ്പ് അപ്പ് ചെയ്യൂ.',
  'agent.deposit.gauge.caption.caution': 'പൂൾ ഡെപ്പോസിറ്റ് കവറേജിന്റെ {pct}%-ൽ. നിങ്ങൾ 80% മുന്നറിയിപ്പിന് സുരക്ഷിതമായി താഴെയാണ്.',
  'agent.deposit.gauge.caption.safe': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ് സമ്പൂർണ്ണമായി കേടുകൂടാതെ.',

  // Ledger feed.
  'agent.deposit.ledger.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.deposit.ledger.empty.title': 'ഇതുവരെ ഡെപ്പോസിറ്റ് പ്രവർത്തനങ്ങളില്ല',
  'agent.deposit.ledger.empty.body': 'അഡ്മിൻ നിങ്ങളുടെ ഡെപ്പോസിറ്റ് ക്രെഡിറ്റ് ചെയ്താൽ (അല്ലെങ്കിൽ ഒരു റീഫണ്ട് പ്രോസസ് ചെയ്താൽ), എല്ലാ എൻട്രിയും ഇവിടെ ദൃശ്യമാകും.',

  // ── RequestRechargeForm (target='wallet' OR target='deposit') ────
  // Banner copy flips on target.
  'agent.recharge.banner.eyebrow.wallet': 'നിങ്ങളുടെ വാലറ്റ് റീചാർജ് ചെയ്യുക',
  'agent.recharge.banner.eyebrow.deposit': 'നിങ്ങളുടെ ഡെപ്പോസിറ്റ് റീചാർജ് ചെയ്യുക',
  'agent.recharge.banner.body.wallet': 'ആദ്യം നിങ്ങളുടെ അപ്പ്ലൈനിന് അടയ്ക്കൂ, പിന്നെ ഇവിടെ തെളിവ് സമർപ്പിക്കൂ അതുവഴി അവർക്ക് നിങ്ങളുടെ ഓപ്പറേറ്റിംഗ് വാലറ്റ് ടോപ്പ് അപ്പ് ചെയ്യാം.',
  'agent.recharge.banner.body.deposit': 'ആദ്യം നിങ്ങളുടെ അപ്പ്ലൈനിന് അടയ്ക്കൂ, പിന്നെ ഇവിടെ തെളിവ് സമർപ്പിക്കൂ അതുവഴി അവർക്ക് നിങ്ങളുടെ സെക്യൂരിറ്റി ഡെപ്പോസിറ്റ് ടോപ്പ് അപ്പ് ചെയ്യാം.',
  'agent.recharge.banner.closeAria': 'ഫോം അടയ്ക്കുക',

  // Parent QR card (small variant rendered above the form fields).
  'agent.recharge.parent.label.admin': 'നിങ്ങളുടെ അഡ്മിന് അടയ്ക്കൂ',
  'agent.recharge.parent.label.parentAgent': 'നിങ്ങളുടെ പേരന്റ് ഏജന്റിന് അടയ്ക്കൂ',
  'agent.recharge.parent.role.admin': 'പ്ലാറ്റ്ഫോം അഡ്മിൻ',
  'agent.recharge.parent.role.parentAgent': 'പേരന്റ് ഏജന്റ്',
  'agent.recharge.parent.noQr': 'QR സജ്ജമാക്കിയിട്ടില്ല',
  'agent.recharge.parent.upi': 'UPI:',
  'agent.recharge.parent.loading': 'നിങ്ങളുടെ അപ്പ്ലൈനിന്റെ പേയ്മെന്റ് വിശദാംശങ്ങൾ ലോഡ് ചെയ്യുന്നു…',
  'agent.recharge.parent.loadError': 'നിങ്ങളുടെ അപ്‌ലൈൻ പേയ്മെന്റ് വിശദാംശങ്ങൾ ലോഡ് ചെയ്യാനായില്ല',

  // Payment-method toggle.
  'agent.recharge.method.eyebrow': 'നിങ്ങൾ എങ്ങനെ അടച്ചു?',
  'agent.recharge.method.upi.label': 'UPI / QR',
  'agent.recharge.method.upi.note': 'ഓൺലൈൻ ഡിജിറ്റൽ പേയ്മെന്റ്',
  'agent.recharge.method.cash.label': 'പണം',
  'agent.recharge.method.cash.note': 'നേരിട്ടുള്ള കൈമാറ്റം',

  // Form fields.
  'agent.recharge.field.amount.label': 'തുക (₹) · കുറഞ്ഞത് ₹10',
  'agent.recharge.field.amount.placeholder': '500',
  'agent.recharge.field.txn.label.upi': 'UPI ഇടപാട് ID / UTR',
  'agent.recharge.field.txn.label.cash': 'പണ രസീത് മാർക്കർ (ഏതെങ്കിലും ഹ്രസ്വ റഫറൻസ്)',
  'agent.recharge.field.txn.placeholder.upi': 'ഉദാ. 412312345678',
  'agent.recharge.field.txn.placeholder.cash': 'ഉദാ. CASH-Sun-eve',
  'agent.recharge.field.note.label': 'കുറിപ്പ് (ഐച്ഛികം, നിങ്ങളുടെ അപ്പ്ലൈനിന് കാണിക്കും)',
  'agent.recharge.field.note.placeholder': 'നിങ്ങളുടെ അപ്പ്ലൈനിന് ഏതെങ്കിലും സന്ദർഭം',

  // Validation errors.
  'agent.recharge.error.amountMin': 'തുക ഒരു മുഴുവൻ രൂപയും ≥ 10 ആയിരിക്കണം',
  'agent.recharge.error.txnUpi': 'ഇടപാട് ID വളരെ ചെറുതാണ് — UPI റഫറൻസ് പേസ്റ്റ് ചെയ്യൂ',
  'agent.recharge.error.txnCash': 'ഒരു ഹ്രസ്വ മാർക്കർ ചേർക്കൂ (ഉദാ. "CASH-Sun-eve") അതുവഴി നിങ്ങളുടെ അപ്പ്ലൈനിന് കൈമാറ്റം പൊരുത്തപ്പെടുത്താം',

  // CTAs.
  'agent.recharge.cta.submit': 'അഭ്യർത്ഥന സമർപ്പിക്കുക',
  'agent.recharge.cta.cancel': 'റദ്ദാക്കുക',

  // ── RequestWithdrawalForm (target='wallet' OR target='deposit') ──
  'agent.withdrawal.banner.eyebrow.wallet': 'വാലറ്റിൽ നിന്ന് പിൻവലിക്കുക',
  'agent.withdrawal.banner.eyebrow.deposit': 'ഡെപ്പോസിറ്റിൽ നിന്ന് പിൻവലിക്കുക',
  'agent.withdrawal.banner.body.wallet': 'നിങ്ങൾക്ക് ഇരട്ട ചെലവ് ചെയ്യാൻ കഴിയാത്തവിധം തുക തൽക്ഷണം ഹോൾഡ് ചെയ്യും. നിങ്ങളുടെ അപ്‌ലൈൻ നിങ്ങൾക്ക് അടച്ച് പണമടച്ചതായി മാർക്ക് ചെയ്യും.',
  'agent.withdrawal.banner.body.deposit': 'നിങ്ങളുടെ സെക്യൂരിറ്റി ഡെപ്പോസിറ്റിൽ നിന്ന് തുക ഉടനെ ഹോൾഡ് ചെയ്യും. നിങ്ങളുടെ അപ്‌ലൈൻ നിങ്ങൾക്ക് അടച്ച് പണമടച്ചതായി മാർക്ക് ചെയ്യും.',
  'agent.withdrawal.banner.closeAria': 'ഫോം അടയ്ക്കുക',

  // Pay-method toggle.
  'agent.withdrawal.method.eyebrow': 'നിങ്ങൾ എങ്ങനെ പണമടയ്ക്കാൻ ആഗ്രഹിക്കുന്നു?',
  'agent.withdrawal.method.upi.label': 'UPI / QR',
  'agent.withdrawal.method.upi.note': 'ഓൺലൈൻ ഡിജിറ്റൽ പേയ്മെന്റ്',
  'agent.withdrawal.method.cash.label': 'പണം',
  'agent.withdrawal.method.cash.note': 'നേരിട്ടുള്ള കൈമാറ്റം',

  // Form fields. {balance} interpolates the formatted available number.
  'agent.withdrawal.field.amount.label': 'തുക (₹) · ലഭ്യം {balance}',
  'agent.withdrawal.field.amount.placeholder': '500',
  'agent.withdrawal.field.upi.label': 'നിങ്ങളുടെ UPI ID (നിങ്ങളുടെ അപ്‌ലൈൻ നിങ്ങൾക്ക് അടയ്ക്കുന്നിടത്ത്)',
  'agent.withdrawal.field.upi.placeholder': 'you@bank',
  'agent.withdrawal.field.cashMarker.label': 'മീറ്റിംഗ് മാർക്കർ (ഉദാ. "Sun evening cash")',
  'agent.withdrawal.field.cashMarker.placeholder': 'എപ്പോൾ/എവിടെ പണം കൈമാറണം',
  'agent.withdrawal.field.whatsapp.label': 'വാട്ട്സാപ്പ് (ഐച്ഛികം, നിങ്ങളുടെ അപ്‌ലൈൻ നിങ്ങളെ സമീപിക്കാൻ)',
  'agent.withdrawal.field.whatsapp.placeholder': '+919876543210',
  'agent.withdrawal.field.note.label': 'കുറിപ്പ് (ഐച്ഛികം)',
  'agent.withdrawal.field.note.placeholder': 'നിങ്ങളുടെ അപ്പ്ലൈനിന് ഏതെങ്കിലും സന്ദർഭം',

  // Validation errors.
  'agent.withdrawal.error.amountMin': 'തുക ഒരു മുഴുവൻ രൂപയും ≥ 10 ആയിരിക്കണം',
  'agent.withdrawal.error.amountOverflow': 'നിങ്ങൾക്ക് {balance} മാത്രമേ ലഭ്യമുള്ളൂ',
  'agent.withdrawal.error.upi': 'സാധുവായ UPI ID നൽകുക',

  // CTAs.
  'agent.withdrawal.cta.submit': 'അഭ്യർത്ഥന സമർപ്പിക്കുക',
  'agent.withdrawal.cta.cancel': 'റദ്ദാക്കുക',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 5 (2026-05-12) — PEOPLE HUB
  //
  // Players + Agents tabs in the bottom nav and everything inside them.
  // 11 surfaces, ~500 strings:
  //
  //   agent.people.hub.players.*       PlayersHub page header + sub-tabs
  //   agent.people.hub.agents.*        AgentsHub page header + sub-tabs
  //   agent.people.acceptingToggle.*   AcceptingPlayersToggleCard
  //   agent.qr.*                       QRCodeModule (audience-aware,
  //                                    with player.* and subAgent.*
  //                                    variants per the directive)
  //   agent.manualRecharge.*           ManualRechargeForm (player target)
  //   agent.manualSubAgentRecharge.*   ManualSubAgentRechargeForm (sub-agent)
  //   agent.playerRequests.*           PlayerRequestsPanel — queue + cards
  //   agent.agentRequests.*            AgentRequestsPanel — queue + cards
  //   agent.playersList.*              PlayersListPanel (grid + filters + card
  //                                    fields, with column.* sub-namespace)
  //   agent.subAgentsList.*            SubagentsListPanel (grid + create modal,
  //                                    with column.* sub-namespace)
  //   agent.approvalEdit.*             ApprovalEditModal — partial-approval UI
  //
  // DUAL-QR DISCIPLINE (per directive). The QRCodeModule renders the
  // same JSX twice in the app — once with audience='players' on the
  // Players hub and once with audience='agents' on the Agents hub.
  // Translators get TWO PARALLEL KEY SETS so they can phrase the
  // "what players see" copy independently from "what sub-agents see":
  //
  //   agent.qr.audience.player        "players"  — used inside the label
  //   agent.qr.audience.subAgent      "agents"   — used inside the label
  //   agent.qr.confirmRemove.player   "Remove the QR image? Your players will see…"
  //   agent.qr.confirmRemove.subAgent "Remove the QR image? Your sub-agents will see…"
  //
  // This way "player" and "sub-agent" are never composed at runtime from a
  // single ambiguous word — the surface gets explicit per-audience copy
  // and the call-site picks via `t(audience === 'agents' ? '…subAgent' : '…player')`.
  //
  // DATA GRID HEADERS (per directive). Each list panel has a dedicated
  //   agent.<panel>.column.* sub-namespace
  // so translators see exactly which column they're translating without
  // having to guess from context. Even short column words ("Wallet",
  // "Joined", "Status") are namespaced — they may legitimately translate
  // differently from a "Wallet" balance pill elsewhere.

  // ── PlayersHub page (header + 2 sub-tab labels) ───────────────────
  'agent.people.hub.players.title': 'പ്ലെയർമാർ',
  'agent.people.hub.players.subtitle': 'പ്ലെയർമാരെയും അവരുടെ അഭ്യർത്ഥനകളെയും കൈകാര്യം ചെയ്യുക',
  'agent.people.hub.players.subtab.requests': 'പ്ലെയർ അഭ്യർത്ഥനകൾ',
  'agent.people.hub.players.subtab.list': 'പ്ലെയർമാർ',

  // ── AgentsHub page (header + 2 sub-tab labels) ────────────────────
  'agent.people.hub.agents.title': 'ഏജന്റുമാർ',
  'agent.people.hub.agents.subtitle': 'സബ്-ഏജന്റുമാരെയും അവരുടെ അഭ്യർത്ഥനകളെയും കൈകാര്യം ചെയ്യുക',
  'agent.people.hub.agents.subtab.requests': 'ഏജന്റ് അഭ്യർത്ഥനകൾ',
  'agent.people.hub.agents.subtab.list': 'ഏജന്റുമാർ',

  // ── AcceptingPlayersToggleCard ────────────────────────────────────
  // Two copy variants — accepting vs opted-out — kept as two keys
  // because the wording flips a critical "ACCEPTING" vs "OPTED OUT"
  // emphasis that translators should preserve.
  'agent.people.acceptingToggle.label': 'പുതിയ പ്ലെയർമാരെ സ്വീകരിക്കുന്നു',
  'agent.people.acceptingToggle.bodyAccepting': 'നിങ്ങൾ ജിയോഗ്രാഫിക് ലൊക്കേറ്ററിൽ നിന്ന് പുതിയ പ്ലെയർമാരെ സ്വീകരിക്കുന്നു. നിങ്ങളുടെ പ്രദേശത്തെ കോൾഡ് സൈൻഅപ്പുകൾ സ്വയമേവ നിങ്ങളിലേക്ക് റൂട്ട് ചെയ്യും.',
  'agent.people.acceptingToggle.bodyOptedOut': 'നിങ്ങൾ ഒഴിവായി. ലൊക്കേറ്റർ നിങ്ങളെ ഒഴിവാക്കുന്നു — നേരിട്ടുള്ള റഫറലുകളും മാനുവലായി അസൈൻ ചെയ്ത പ്ലെയർമാരും മാത്രമേ നിങ്ങളിലെത്തൂ. നിലവിലുള്ള പ്ലെയർമാർ ബാധിക്കില്ല.',
  'agent.people.acceptingToggle.error': 'അപ്ഡേറ്റ് ചെയ്യാനായില്ല',
  // ARIA labels for the toggle pill — used by screen readers; both
  // states get their own key so the announcement is precise.
  'agent.people.acceptingToggle.aria.stop': 'പുതിയ പ്ലെയർമാരെ സ്വീകരിക്കുന്നത് നിർത്തുക',
  'agent.people.acceptingToggle.aria.start': 'പുതിയ പ്ലെയർമാരെ സ്വീകരിക്കാൻ തുടങ്ങുക',

  // ════════════════════════════════════════════════════════════════════
  // QRCodeModule — TWO parallel key sets per audience.

  // Trigger label + status pill (shared across audiences except where noted).
  'agent.qr.audience.player': 'പ്ലെയർമാർ',
  'agent.qr.audience.subAgent': 'ഏജന്റുമാർ',
  'agent.qr.alt': 'നിങ്ങളുടെ പേയ്മെന്റ് QR ({audience})',
  // "My QR · what players see" / "My QR · what agents see" — built
  // as two keys with the audience-word interpolated by useT().
  'agent.qr.cardLabel.player': 'എന്റെ QR · പ്ലെയർമാർ കാണുന്നത്',
  'agent.qr.cardLabel.subAgent': 'എന്റെ QR · ഏജന്റുമാർ കാണുന്നത്',
  'agent.qr.upi.empty': 'ഇതുവരെ UPI സജ്ജമാക്കിയിട്ടില്ല',
  // Status pill.
  'agent.qr.status.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.qr.status.live': 'ലൈവ്',
  'agent.qr.status.notSet': 'സജ്ജമാക്കിയിട്ടില്ല',
  'agent.qr.status.paused': 'നിർത്തി',
  // Edit CTA — three variants based on what's set + permission.
  'agent.qr.cta.edit': 'QR & UPI എഡിറ്റ് ചെയ്യുക',
  'agent.qr.cta.setup': 'QR സജ്ജമാക്കുക',
  'agent.qr.cta.viewOnly': 'കാണാൻ മാത്രം — എഡിറ്റ് ചെയ്യാൻ അഡ്മിനോട് അഭ്യർത്ഥിക്കുക',

  // ── Edit modal ────────────────────────────────────────────────────
  'agent.qr.modal.title': 'QR & UPI എഡിറ്റ് ചെയ്യുക',
  'agent.qr.modal.closeAria': 'അടയ്ക്കുക',
  // Upload + remove buttons.
  'agent.qr.modal.upload': 'QR അപ്‌ലോഡ് ചെയ്യുക',
  'agent.qr.modal.replace': 'QR മാറ്റിസ്ഥാപിക്കുക',
  'agent.qr.modal.remove': 'നീക്കം ചെയ്യുക',
  // confirm() prompt before deleting. Two parallel keys so each
  // language gets per-audience phrasing for who sees the fallback.
  'agent.qr.modal.confirmRemove.player': 'QR ഇമേജ് നീക്കം ചെയ്യണോ? നിങ്ങളുടെ പ്ലെയർമാർ കോൺടാക്റ്റ് ഫാൾബാക്ക് മാത്രമേ കാണൂ.',
  'agent.qr.modal.confirmRemove.subAgent': 'QR ഇമേജ് നീക്കം ചെയ്യണോ? നിങ്ങളുടെ ഏജന്റുമാർ കോൺടാക്റ്റ് ഫാൾബാക്ക് മാത്രമേ കാണൂ.',

  // Form fields inside the edit modal.
  'agent.qr.modal.field.upi.label': 'UPI ID',
  'agent.qr.modal.field.upi.placeholder': 'ഉദാ. baaligames@upi',
  // WhatsApp + Phone fields — audience-specific (Phase 10 migration 067)
  // so we have FOUR keys per pair: two labels (player vs subagent)
  // and two placeholder phrasings.
  'agent.qr.modal.field.whatsapp.label.player': 'വാട്ട്സാപ്പ് നമ്പർ (ഫാൾബാക്ക്)',
  'agent.qr.modal.field.whatsapp.label.subAgent': 'ഏജന്റുമാർക്കുള്ള വാട്ട്സാപ്പ് നമ്പർ',
  'agent.qr.modal.field.whatsapp.placeholder.default': '+919876543210',
  'agent.qr.modal.field.whatsapp.placeholder.fallback': 'ശൂന്യം → {number}-ലേക്ക് ഫാൾബാക്ക്',
  'agent.qr.modal.field.whatsapp.fallbackHint': 'സബ്-ഏജന്റുമാർ {number} കാണും (നിങ്ങളുടെ പ്ലെയർ-ഫേസിംഗ് നമ്പർ).',
  'agent.qr.modal.field.phone.label.player': 'സപ്പോർട്ട് ഫോൺ (ഫാൾബാക്ക്)',
  'agent.qr.modal.field.phone.label.subAgent': 'ഏജന്റുമാർക്കുള്ള സപ്പോർട്ട് ഫോൺ',
  'agent.qr.modal.field.phone.placeholder.default': '+919876543210',
  'agent.qr.modal.field.phone.placeholder.fallback': 'ശൂന്യം → {number}-ലേക്ക് ഫാൾബാക്ക്',
  'agent.qr.modal.field.phone.fallbackHint': 'സബ്-ഏജന്റുമാർ {number} കാണും (നിങ്ങളുടെ പ്ലെയർ-ഫേസിംഗ് നമ്പർ).',

  // Active toggle row + status pill on the right.
  'agent.qr.modal.active.label': 'സജീവം',
  'agent.qr.modal.active.description': 'പ്ലെയർമാർക്ക് റീചാർജുകൾ സമർപ്പിക്കാം',
  'agent.qr.modal.active.on': 'ഓൺ',
  'agent.qr.modal.active.paused': 'നിർത്തി',

  // Footer buttons.
  'agent.qr.modal.cancel': 'റദ്ദാക്കുക',
  'agent.qr.modal.save': 'സേവ് ചെയ്യുക',

  // ════════════════════════════════════════════════════════════════════
  // ManualRechargeForm (player target)

  // Banner — names the card so it isn't mistaken for an approval row.
  'agent.manualRecharge.banner.eyebrow': 'മാനുവൽ റീചാർജ്',
  // The body sentence has a bolded "player" word baked into the JSX.
  // We expose the surrounding sentence as two halves so the bold can
  // stay inline in any word order the language needs.
  'agent.manualRecharge.banner.body.before': 'ഒരു ',
  'agent.manualRecharge.banner.body.target': 'പ്ലെയറിന്',
  'agent.manualRecharge.banner.body.after': ' നിങ്ങളുടെ പൂളിൽ നിന്ന് നേരിട്ട് പണം അയയ്ക്കൂ.',

  // Pool snapshot strip.
  'agent.manualRecharge.pool.label': 'നിങ്ങളുടെ പൂൾ',
  'agent.manualRecharge.pool.dailyCap': 'പരിധി · {amount}/ദിവസം',

  // Player picker.
  'agent.manualRecharge.field.player.label': 'പ്ലെയറിലേക്ക്',
  'agent.manualRecharge.field.player.placeholder': 'ഒരു പ്ലെയറെ തിരഞ്ഞെടുക്കുക…',
  'agent.manualRecharge.field.player.option': '@{username} · {contact} · ബാൽ {balance}',
  'agent.manualRecharge.field.player.lockedLabel': 'പ്ലെയറിലേക്ക്',
  'agent.manualRecharge.field.player.lockedMeta': '{contact} · നിലവിലെ ബാൽ {balance}',
  'agent.manualRecharge.field.player.notFound': 'നിങ്ങളുടെ ഉടമസ്ഥതയിലുള്ള ലിസ്റ്റിൽ പ്ലെയറെ കണ്ടെത്തിയില്ല.',

  // Amount field + range hint + 3 validation messages.
  'agent.manualRecharge.field.amount.label': 'തുക (₹) — {min} മുതൽ {max}',
  'agent.manualRecharge.field.amount.placeholder': '0',
  'agent.manualRecharge.error.exceedsPool': 'നിങ്ങളുടെ പൂൾ ബാലൻസ് കവിയുന്നു.',
  'agent.manualRecharge.error.belowMin': 'മിനിമത്തിന് താഴെ ({min}).',
  'agent.manualRecharge.error.aboveMax': 'പരമാവധിക്ക് മുകളിൽ ({max}).',

  // Note field.
  'agent.manualRecharge.field.note.label': 'കുറിപ്പ് (ഐച്ഛികം, പ്ലെയർ ലെഡ്ജറിൽ കാണിക്കും)',
  'agent.manualRecharge.field.note.placeholder': 'ഉദാ. കടയിൽ പണം ശേഖരിച്ചു',

  // Loading + success states.
  'agent.manualRecharge.loading': 'നിങ്ങളുടെ പൂൾ ലോഡ് ചെയ്യുന്നു…',
  'agent.manualRecharge.cta.cancel': 'റദ്ദാക്കുക',
  'agent.manualRecharge.cta.send': '{amount} അയയ്ക്കുക',
  'agent.manualRecharge.success.title': '@{username}-ന് {amount} അയച്ചു',
  'agent.manualRecharge.success.meta': 'നിങ്ങളുടെ പൂൾ · {pool} · അവരുടെ വാലറ്റ് · {playerBal}',
  'agent.manualRecharge.success.again': 'ഇനിയും അയയ്ക്കുക',

  // ════════════════════════════════════════════════════════════════════
  // ManualSubAgentRechargeForm (sub-agent target) — mirrors the player
  // form but with sub-agent terminology and a different error set.

  'agent.manualSubAgentRecharge.banner.eyebrow': 'മാനുവൽ റീചാർജ്',
  'agent.manualSubAgentRecharge.banner.body.before': 'ഒരു ',
  'agent.manualSubAgentRecharge.banner.body.target': 'സബ്-ഏജന്റിന്',
  'agent.manualSubAgentRecharge.banner.body.after': ' നിങ്ങളുടെ പൂളിൽ നിന്ന് നേരിട്ട് പണം അയയ്ക്കൂ.',

  'agent.manualSubAgentRecharge.pool.label': 'നിങ്ങളുടെ പൂൾ',

  'agent.manualSubAgentRecharge.field.subAgent.label': 'സബ്-ഏജന്റിലേക്ക്',
  'agent.manualSubAgentRecharge.field.subAgent.placeholder': 'ഒരു സബ്-ഏജന്റിനെ തിരഞ്ഞെടുക്കുക…',
  'agent.manualSubAgentRecharge.field.subAgent.option': '@{username} · {email} · പൂൾ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.emptyOption': 'സജീവമായ ഡയറക്റ്റ് സബ്-ഏജന്റുമാരില്ല',
  'agent.manualSubAgentRecharge.field.subAgent.lockedLabel': 'സബ്-ഏജന്റിലേക്ക്',
  'agent.manualSubAgentRecharge.field.subAgent.lockedMeta': '{email} · നിലവിലെ പൂൾ {balance}',
  'agent.manualSubAgentRecharge.field.subAgent.notFound': 'നിങ്ങളുടെ ഡയറക്റ്റ് ഡൗൺലൈനിൽ സബ്-ഏജന്റിനെ കണ്ടെത്തിയില്ല.',

  'agent.manualSubAgentRecharge.field.amount.label': 'തുക (₹) — {min} മുതൽ {max}',
  'agent.manualSubAgentRecharge.field.amount.placeholder': '0',
  'agent.manualSubAgentRecharge.error.exceedsPool': 'നിങ്ങളുടെ പൂൾ ബാലൻസ് കവിയുന്നു.',
  'agent.manualSubAgentRecharge.error.belowMin': 'മിനിമത്തിന് താഴെ ({min}).',

  'agent.manualSubAgentRecharge.field.note.label': 'കുറിപ്പ് (ഐച്ഛികം, രണ്ട് ലെഡ്ജറുകളിലും കാണിക്കും)',
  'agent.manualSubAgentRecharge.field.note.placeholder': 'ഉദാ. ഫ്ലോട്ട് അഡ്വാൻസ്',

  'agent.manualSubAgentRecharge.loading': 'നിങ്ങളുടെ പൂൾ ലോഡ് ചെയ്യുന്നു…',
  'agent.manualSubAgentRecharge.cta.cancel': 'റദ്ദാക്കുക',
  'agent.manualSubAgentRecharge.cta.send': '{amount} അയയ്ക്കുക',
  'agent.manualSubAgentRecharge.success.title': '@{username}-ന് {amount} അയച്ചു',
  'agent.manualSubAgentRecharge.success.meta': 'നിങ്ങളുടെ പൂൾ · {parent} · അവരുടെ പൂൾ · {child}',
  'agent.manualSubAgentRecharge.success.again': 'ഇനിയും അയയ്ക്കുക',

  // ════════════════════════════════════════════════════════════════════
  // PlayerRequestsPanel — queue, cards, badges, dialogs, toasts

  // Direction tabs (Recharge / Withdrawal) + Pending/History sub-tabs
  // already use the same words as agent.wallet.tab.* — we add
  // direction-specific keys for clarity even though "Recharge" and
  // "Withdrawal" overlap with other namespaces.
  'agent.playerRequests.direction.recharge': 'റീചാർജ്',
  'agent.playerRequests.direction.withdrawal': 'പിൻവലിക്കൽ',
  'agent.playerRequests.tab.pending': 'തീരുമാനമാകാത്തത്',
  'agent.playerRequests.tab.history': 'ചരിത്രം',

  // Loading + empty states (composed at the call-site based on
  // direction + sub combination).
  'agent.playerRequests.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.playerRequests.empty.pending.recharge': 'ഇപ്പോൾ തീരുമാനമാകാത്ത റീചാർജുകളില്ല.',
  'agent.playerRequests.empty.pending.withdrawal': 'ഇപ്പോൾ തീരുമാനമാകാത്ത പിൻവലിക്കലുകളില്ല.',
  'agent.playerRequests.empty.history': 'ഇതുവരെ ചരിത്രമില്ല.',

  // prompt() + confirm() dialog strings.
  'agent.playerRequests.prompt.rejectRecharge': 'നിരസിക്കാനുള്ള കാരണം (ഐച്ഛികം):',
  'agent.playerRequests.prompt.rejectWithdrawal': 'നിരസിക്കാനുള്ള കാരണം (പ്ലെയറുടെ ഹോൾഡ് ചെയ്ത ബാലൻസ് റീഫണ്ട് ചെയ്യും):',

  // Toasts.
  'agent.playerRequests.toast.rejected': 'നിരസിച്ചു',
  'agent.playerRequests.toast.rejectedRefunded': 'നിരസിച്ചു · ഹോൾഡ് ചെയ്ത ബാലൻസ് റീഫണ്ട് ചെയ്തു',
  'agent.playerRequests.toast.approved': '{amount} അംഗീകരിച്ചു',
  'agent.playerRequests.toast.approvedWithBonus': '{amount} അംഗീകരിച്ചു · പ്രിൻസിപ്പൽ + ₹{bonus} ബോണസ്',
  'agent.playerRequests.toast.markedPaid': '{amount} പണമടച്ചതായി മാർക്ക് ചെയ്തു',

  // Card field labels.
  'agent.playerRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.playerRequests.card.payToUpiLabel': 'അടയ്ക്കാൻ (UPI)',
  'agent.playerRequests.card.whatsapp': 'വാട്ട്സാപ്പ്',
  'agent.playerRequests.card.viewQr': 'QR കാണുക',
  'agent.playerRequests.card.bonusChip': 'ബോണസ്',
  // Approved-of-requested partial split sentence — interpolated.
  'agent.playerRequests.card.partialApproved': 'അഭ്യർത്ഥിച്ച {requested}-ൽ {approved} അംഗീകരിച്ചു',
  'agent.playerRequests.card.partialPaid': 'അഭ്യർത്ഥിച്ച {requested}-ൽ {approved} അടച്ചു · {refund} റീഫണ്ട് ചെയ്തു',
  'agent.playerRequests.card.paidTxnPrefix': 'പണമടച്ചു · {txn}',

  // Action buttons + state badge labels.
  'agent.playerRequests.action.reject': 'നിരസിക്കുക',
  'agent.playerRequests.action.approve': 'അംഗീകരിക്കുക',
  'agent.playerRequests.action.markPaid': 'പണമടച്ചതായി മാർക്ക് ചെയ്യുക',
  'agent.playerRequests.state.pending': 'തീരുമാനമാകാത്തത്',
  'agent.playerRequests.state.rejected': 'നിരസിച്ചു',
  'agent.playerRequests.state.expired': 'കാലഹരണപ്പെട്ടു',
  'agent.playerRequests.state.accepted': 'സ്വീകരിച്ചു',
  'agent.playerRequests.state.manuallyPaid': 'മാനുവലായി പണമടച്ചു',

  // Pagination — used by both player + agent panels.
  'agent.peopleQueue.pagination': '{total}-ൽ {from}–{to}',

  // ════════════════════════════════════════════════════════════════════
  // AgentRequestsPanel — sub-agent recharge/withdrawal queue. Mirrors
  // the player panel's namespace but kept distinct so translators can
  // phrase the sub-agent inbox copy independently.

  'agent.agentRequests.direction.recharge': 'റീചാർജ്',
  'agent.agentRequests.direction.withdrawal': 'പിൻവലിക്കൽ',
  'agent.agentRequests.tab.pending': 'തീരുമാനമാകാത്തത്',
  'agent.agentRequests.tab.history': 'ചരിത്രം',
  'agent.agentRequests.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.agentRequests.empty.pending.recharge': 'ഇപ്പോൾ തീരുമാനമാകാത്ത റീചാർജുകളില്ല.',
  'agent.agentRequests.empty.pending.withdrawal': 'ഇപ്പോൾ തീരുമാനമാകാത്ത പിൻവലിക്കലുകളില്ല.',
  'agent.agentRequests.empty.history': 'ഇതുവരെ ചരിത്രമില്ല.',

  'agent.agentRequests.prompt.rejectRecharge': 'നിരസിക്കാനുള്ള കാരണം (ഐച്ഛികം):',
  'agent.agentRequests.prompt.rejectWithdrawal': 'നിരസിക്കാനുള്ള കാരണം (സബ്-ഏജന്റിന്റെ ഹോൾഡ് ചെയ്ത ബാലൻസ് റീഫണ്ട് ചെയ്യും):',

  'agent.agentRequests.toast.rejected': 'നിരസിച്ചു',
  'agent.agentRequests.toast.rejectedRefunded': 'നിരസിച്ചു · ഹോൾഡ് ചെയ്ത ബാലൻസ് റീഫണ്ട് ചെയ്തു',
  'agent.agentRequests.toast.approved': '{amount} അംഗീകരിച്ചു · പൂൾ ക്രെഡിറ്റ് ചെയ്തു',
  'agent.agentRequests.toast.markedPaid': '{amount} പണമടച്ചതായി മാർക്ക് ചെയ്തു',

  // Pill labels per card.
  'agent.agentRequests.card.agentPill': 'ഏജന്റ്',
  // Card-body field labels (cash vs UPI variants).
  'agent.agentRequests.card.upiTxnLabel': 'UPI Txn ID',
  'agent.agentRequests.card.cashMarkerLabel': 'പണ മാർക്കർ',
  'agent.agentRequests.card.payToUpiLabel': 'അടയ്ക്കാൻ (UPI)',
  'agent.agentRequests.card.handoffMarkerLabel': 'കൈമാറ്റ മാർക്കർ',
  'agent.agentRequests.card.whatsapp': 'വാട്ട്സാപ്പ് {number}',
  'agent.agentRequests.card.partialApproved': 'അഭ്യർത്ഥിച്ച {requested}-ൽ {approved} അംഗീകരിച്ചു',
  'agent.agentRequests.card.partialPaid': 'അഭ്യർത്ഥിച്ച {requested}-ൽ {approved} അടച്ചു · {refund} റീഫണ്ട് ചെയ്തു',
  'agent.agentRequests.card.paidTxnPrefix': 'പണമടച്ചു · {txn}',
  // Phase 10 chips inside the row (DEPOSIT chip + CASH/UPI chip).
  'agent.agentRequests.card.chip.deposit': 'ഡെപ്പോസിറ്റ്',
  'agent.agentRequests.card.chip.cash': 'പണം',
  'agent.agentRequests.card.chip.upi': 'UPI',

  'agent.agentRequests.action.reject': 'നിരസിക്കുക',
  'agent.agentRequests.action.approve': 'അംഗീകരിക്കുക',
  'agent.agentRequests.action.markPaid': 'പണമടച്ചതായി മാർക്ക് ചെയ്യുക',
  'agent.agentRequests.state.pending': 'തീരുമാനമാകാത്തത്',
  'agent.agentRequests.state.rejected': 'നിരസിച്ചു',
  'agent.agentRequests.state.expired': 'കാലഹരണപ്പെട്ടു',
  'agent.agentRequests.state.accepted': 'സ്വീകരിച്ചു',
  'agent.agentRequests.state.manuallyPaid': 'മാനുവലായി പണമടച്ചു',

  // ════════════════════════════════════════════════════════════════════
  // PlayersListPanel — player roster, search, filter pills, pagination.

  // Toolbar header (the "Add player" button + result count).
  'agent.playersList.add': 'പ്ലെയറെ ചേർക്കുക',
  'agent.playersList.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.playersList.empty': 'പ്ലെയർമാർ പൊരുത്തപ്പെടുന്നില്ല',
  'agent.playersList.results': '{total}-ൽ {from}–{to} കാണിക്കുന്നു',

  // Search bar.
  'agent.playersList.searchPlaceholder': 'യൂസർനെയിം, ഇമെയിൽ, ഫോൺ തിരയുക…',

  // Status filter pills.
  'agent.playersList.filter.all': 'എല്ലാം',
  'agent.playersList.filter.active': 'സജീവം',
  'agent.playersList.filter.pending': 'തീരുമാനമാകാത്തത്',
  'agent.playersList.filter.suspended': 'സസ്പെൻഡ് ചെയ്തു',
  'agent.playersList.filter.banned': 'നിരോധിച്ചു',

  // Empty-results panel.
  'agent.playersList.empty.title': 'പ്ലെയർമാർ കണ്ടെത്തിയില്ല',
  'agent.playersList.empty.body': 'മറ്റൊരു തിരയൽ അല്ലെങ്കിൽ സ്റ്റാറ്റസ് ഫിൽട്ടർ പരീക്ഷിക്കൂ.',

  // PAGE_FOOTER pagination (page count format).
  'agent.playersList.pagination': 'പേജ് {page} / {totalPages}',

  // Player card — STATUS pill labels (mirrors filter but with title case).
  'agent.playersList.card.status.active': 'സജീവം',
  'agent.playersList.card.status.pending': 'തീരുമാനമാകാത്തത്',
  'agent.playersList.card.status.suspended': 'സസ്പെൻഡ് ചെയ്തു',
  'agent.playersList.card.status.banned': 'നിരോധിച്ചു',
  'agent.playersList.card.verifiedBadge': 'സ്ഥിരീകരിച്ചു',

  // PER-CARD DATA GRID HEADERS (per directive — each labelled column
  // gets its own key so translators see exactly what they're translating).
  'agent.playersList.column.wallet': 'വാലറ്റ്',
  'agent.playersList.column.joined': 'ചേർന്നത്',

  // ════════════════════════════════════════════════════════════════════
  // SubagentsListPanel — sub-agent roster + create modal.

  // Toolbar.
  'agent.subAgentsList.intro': 'നിങ്ങൾ സൃഷ്ടിക്കുന്ന ഏജന്റുമാർ നിങ്ങളുടെ കീഴിൽ പ്രവർത്തിക്കുന്നു. അവർ എല്ലാ പിലറിലും 0%-ൽ ആരംഭിക്കുന്നു — അവരുടെ വിശദാംശ പേജിൽ നിന്ന് ഓരോ ഗെയിമിനും നിരക്കുകൾ അനുവദിക്കുക.',
  'agent.subAgentsList.cta.new': 'പുതിയ സബ്-ഏജന്റ്',
  'agent.subAgentsList.loading': 'ലോഡ് ചെയ്യുന്നു…',
  // Empty state — two body variants depending on whether the agent has
  // sub-agent creation permission.
  'agent.subAgentsList.empty.title': 'ഇതുവരെ സബ്-ഏജന്റുമാരില്ല',
  'agent.subAgentsList.empty.body.canCreate': 'ഒന്നുണ്ടാക്കാൻ "+ പുതിയ സബ്-ഏജന്റ്" ടാപ്പ് ചെയ്യൂ. അവർ സ്വന്തം പൂൾ, പ്ലെയർമാർ, വരുമാനം എന്നിവയ്ക്കൊപ്പം ഇവിടെ ദൃശ്യമാകും — നിങ്ങൾ കൈകാര്യം ചെയ്യുന്നു.',
  'agent.subAgentsList.empty.body.cantCreate': 'നിങ്ങളുടെ അഡ്മിൻ ഇതുവരെ സബ്-ഏജന്റ് സൃഷ്ടി അവകാശങ്ങൾ നൽകിയിട്ടില്ല. നിങ്ങളുടെ അനുമതികളിൽ createsubagent "edit"-ലേക്ക് മാറ്റാൻ അവരോട് ആവശ്യപ്പെടൂ.',

  // Sub-agent card.
  'agent.subAgentsList.card.suspendedBadge': 'സസ്പെൻഡ് ചെയ്തു',

  // PER-CARD DATA GRID HEADERS.
  'agent.subAgentsList.column.agents': 'ഏജന്റുമാർ',
  'agent.subAgentsList.column.players': 'പ്ലെയർമാർ',
  'agent.subAgentsList.column.pool': 'പൂൾ',
  // Compound count format: "{direct} direct · {indirect} indirect" —
  // expose pieces so translators control word order.
  'agent.subAgentsList.row.directLabel': 'ഡയറക്റ്റ്',
  'agent.subAgentsList.row.indirectLabel': 'ഇൻഡയറക്റ്റ്',

  // ── Create sub-agent modal ────────────────────────────────────────
  'agent.subAgentsList.modal.title': 'പുതിയ സബ്-ഏജന്റ്',
  'agent.subAgentsList.modal.body': 'അവർ എല്ലാ പിലറിലും 0%-ൽ ആരംഭിക്കും. അവരെ സൃഷ്ടിച്ച ശേഷം അവരുടെ വിശദാംശ പേജിൽ നിന്ന് ഓരോ ഗെയിമിനും അവരുടെ നിരക്കുകൾ അനുവദിക്കൂ.',
  'agent.subAgentsList.modal.field.username.label': 'യൂസർനെയിം',
  'agent.subAgentsList.modal.field.username.placeholder': 'ആൽഫാന്യൂമെറിക്, ._- അനുവദനീയം; കുറഞ്ഞത് 3 പ്രതീകങ്ങൾ',
  'agent.subAgentsList.modal.field.email.label': 'ഇമെയിൽ',
  'agent.subAgentsList.modal.field.email.placeholder': 'they@example.com',
  'agent.subAgentsList.modal.field.password.label': 'പ്രാരംഭ പാസ്‌വേഡ്',
  'agent.subAgentsList.modal.field.password.placeholder': 'കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ',
  'agent.subAgentsList.modal.field.password.hint': 'പുതിയ ഏജന്റിന് ലോഗിൻ ചെയ്യാൻ ഇത് അവർക്ക് കൈമാറുക. ആദ്യ ലോഗിനിൽ അവർ ഇത് മാറ്റണം.',
  'agent.subAgentsList.modal.field.location.label': 'സബ്-ഏജന്റിന്റെ സ്ഥാനം',
  'agent.subAgentsList.modal.field.location.optional': '(ശക്തമായി ശുപാർശ ചെയ്യുന്നു)',
  'agent.subAgentsList.modal.field.location.missing': 'സ്ഥാനം ഇല്ലാതെ, ജിയോഗ്രാഫിക് ലൊക്കേറ്റർക്ക് ഈ സബ്-ഏജന്റിലേക്ക് പുതിയ പ്ലെയർമാരെ റൂട്ട് ചെയ്യാനാവില്ല. അവർ സൈൻ ഇൻ ചെയ്ത് സ്വന്തം GPS പൂർത്തിയാക്കുന്നതുവരെ നേരിട്ടുള്ള റഫറൽ വഴി മാത്രമേ പ്ലെയർമാർ ലഭിക്കൂ.',
  'agent.subAgentsList.modal.cta.cancel': 'റദ്ദാക്കുക',
  'agent.subAgentsList.modal.cta.create': 'സബ്-ഏജന്റ് സൃഷ്ടിക്കുക',
  'agent.subAgentsList.modal.cta.creating': 'സൃഷ്ടിക്കുന്നു…',

  // ════════════════════════════════════════════════════════════════════
  // ApprovalEditModal — partial-approval UI.

  // Title is composed at call-site with the counterparty label.
  'agent.approvalEdit.title.recharge': '{who}-യിൽ നിന്നുള്ള റീചാർജ് അംഗീകരിക്കുക',
  'agent.approvalEdit.title.withdrawal': '{who}-യ്ക്കുള്ള പിൻവലിക്കൽ പണമടച്ചതായി മാർക്ക് ചെയ്യുക',
  // Requested-amount line under the title.
  'agent.approvalEdit.requestedLabel': 'അഭ്യർത്ഥിച്ചത്:',

  // Form fields.
  'agent.approvalEdit.field.amount.label': 'അംഗീകരിക്കാനുള്ള തുക (₹)',
  'agent.approvalEdit.field.amount.hint': 'അഭ്യർത്ഥിച്ച {amount} കവിയാൻ പാടില്ല.',

  // Partial-approval banners (withdrawal refund preview + recharge bonus warning).
  'agent.approvalEdit.partial.withdrawal.refund': '{refund} {who}-യുടെ വാലറ്റിലേക്ക് സ്വയമേവ റീഫണ്ട് ചെയ്യും.',
  'agent.approvalEdit.partial.recharge.bonusWarn': 'ഭാഗിക അംഗീകാരം. അറ്റാച്ച് ചെയ്ത ഏതെങ്കിലും ബോണസ് സ്വയം നിരസിക്കും.',

  // Reason field — required-on-partial flag changes the inline indicator.
  'agent.approvalEdit.field.reason.label': 'കാരണം',
  'agent.approvalEdit.field.reason.requiredHint': '(ആവശ്യമാണ്, ≥5 പ്രതീകങ്ങൾ)',
  'agent.approvalEdit.field.reason.optionalHint': '(ഐച്ഛികം)',
  'agent.approvalEdit.field.reason.placeholder.partial': 'ഉദാ. പ്ലെയർ 30 രൂപ മാത്രമേ അടച്ചുള്ളൂ',
  'agent.approvalEdit.field.reason.placeholder.optional': 'ഐച്ഛിക കുറിപ്പ്',

  // UPI txn id field (withdrawal only).
  'agent.approvalEdit.field.txn.label': 'UPI ഇടപാട് ID',
  'agent.approvalEdit.field.txn.placeholder': 'ഉദാ. 412312345678',

  // Solvency error (recharge with insufficient pool).
  'agent.approvalEdit.error.insufficient': 'നിങ്ങളുടെ പൂളിന് ({balance}) ഈ അംഗീകാരം കവർ ചെയ്യാനാവില്ല. ആദ്യം ടോപ്പ് അപ്പ് ചെയ്യൂ.',

  // Validation errors.
  'agent.approvalEdit.error.amountMin': 'അംഗീകരിച്ച തുക കുറഞ്ഞത് ₹1 ആയിരിക്കണം',
  'agent.approvalEdit.error.amountMax': 'അംഗീകരിച്ച തുക അഭ്യർത്ഥിച്ച {amount} കവിയാൻ പാടില്ല',
  'agent.approvalEdit.error.reasonRequired': 'വ്യത്യസ്ത തുക അംഗീകരിക്കുമ്പോൾ കാരണം (≥5 പ്രതീകങ്ങൾ) ആവശ്യമാണ്',
  'agent.approvalEdit.error.txnRequired': 'UPI ഇടപാട് ID ആവശ്യമാണ് (≥6 പ്രതീകങ്ങൾ)',
  'agent.approvalEdit.error.fallback': 'പരാജയപ്പെട്ടു — വീണ്ടും ശ്രമിക്കൂ',

  // Footer buttons.
  'agent.approvalEdit.cta.cancel': 'റദ്ദാക്കുക',
  'agent.approvalEdit.cta.approve': 'അംഗീകരിക്കുക',
  'agent.approvalEdit.cta.markPaid': 'പണമടച്ചതായി മാർക്ക് ചെയ്യുക',
  'agent.approvalEdit.cta.submitting': 'സമർപ്പിക്കുന്നു…',

  // ════════════════════════════════════════════════════════════════════
  // PROFILE HUB + ACCOUNT PANEL + SHAREABLE ID CARD  (Phase B Group 6)
  //
  // Layout:
  //   agent.profile.hub.*       ProfileHub.tsx — header + 3 sub-tabs
  //   agent.profile.signOut.*   Sign-out modal (lives in AccountPanel
  //                             but the experience is shell-level)
  //   agent.account.*           AccountPanel sub-cards: hero, info,
  //                             personal details, location, security
  //   agent.shareableId.*       ShareableIdCard — UAID + copy
  //
  // Form-validation errors live under each card's `*.error.*` sub-namespace
  // so translators can audit them in one place (Group 6 directive).

  // ─── ProfileHub header + sub-tabs ─────────────────────────────────
  'agent.profile.hub.title': 'എന്റെ അക്കൗണ്ട്',
  'agent.profile.hub.subtitle': 'ഐഡന്റിറ്റി, വാലറ്റ്, ഡെപ്പോസിറ്റ് എന്നിവ ഒരിടത്ത്',
  'agent.profile.hub.tab.wallet': 'വാലറ്റ്',
  'agent.profile.hub.tab.deposit': 'ഡെപ്പോസിറ്റ്',
  'agent.profile.hub.tab.account': 'അക്കൗണ്ട്',

  // ─── Sign-out modal ───────────────────────────────────────────────
  'agent.profile.signOut.cta': 'സൈൻ ഔട്ട്',
  'agent.profile.signOut.modal.title': 'സൈൻ ഔട്ട് ചെയ്യണോ?',
  'agent.profile.signOut.modal.body': 'നിങ്ങളുടെ അക്കൗണ്ട് ആക്സസ് ചെയ്യാൻ വീണ്ടും സൈൻ ഇൻ ചെയ്യേണ്ടി വരും.',
  'agent.profile.signOut.modal.stay': 'സൈൻ ഇൻ തുടരുക',
  'agent.profile.signOut.modal.confirm': 'അതെ, സൈൻ ഔട്ട് ചെയ്യുക',
  'agent.profile.signOut.modal.busy': 'സൈൻ ഔട്ട് ചെയ്യുന്നു…',
  'agent.profile.signOut.error': 'സൈൻ ഔട്ട് ചെയ്യാനായില്ല',

  // ─── AccountPanel top-level ───────────────────────────────────────
  'agent.account.notSignedIn': 'സൈൻ ഇൻ ചെയ്തിട്ടില്ല.',

  // Hero card (identity strip with avatar + role pill).
  'agent.account.hero.role.agent': 'ഏജന്റ്',
  'agent.account.hero.role.admin': 'പ്ലാറ്റ്ഫോം അഡ്മിൻ',
  'agent.account.hero.changePhoto': 'ഫോട്ടോ മാറ്റുക',
  'agent.account.hero.removePhoto': 'ഫോട്ടോ നീക്കം ചെയ്യുക',

  // Avatar cropper + flash toasts. Errors carry their own .error.* sub-
  // namespace so translators can audit them together.
  'agent.account.avatar.cropTitle': 'നിങ്ങളുടെ ഫോട്ടോ ക്രമീകരിക്കുക',
  'agent.account.avatar.confirmRemove': 'നിങ്ങളുടെ പ്രൊഫൈൽ ഫോട്ടോ നീക്കം ചെയ്യണോ?',
  'agent.account.toast.photoUpdated': 'ഫോട്ടോ അപ്ഡേറ്റ് ചെയ്തു',
  'agent.account.toast.photoRemoved': 'ഫോട്ടോ നീക്കം ചെയ്തു',
  'agent.account.toast.saved': 'സേവ് ചെയ്തു',
  'agent.account.toast.locationSaved': 'സ്ഥാനം സേവ് ചെയ്തു',
  'agent.account.toast.passwordChanged': 'പാസ്‌വേഡ് മാറ്റി — വീണ്ടും സൈൻ ഇൻ ചെയ്യൂ',
  'agent.account.error.uploadFailed': 'അപ്‌ലോഡ് പരാജയപ്പെട്ടു',
  'agent.account.error.removeFailed': 'പരാജയപ്പെട്ടു',
  'agent.account.error.saveFailed': 'സേവ് പരാജയപ്പെട്ടു',
  'agent.account.error.changeFailed': 'മാറ്റം പരാജയപ്പെട്ടു',

  // Account info card (read-only platform metadata).
  'agent.account.info.title': 'അക്കൗണ്ട് വിവരം',
  'agent.account.info.subtitle': 'റീഡ്-ഒൺലി പ്ലാറ്റ്ഫോം മെറ്റാഡാറ്റ',
  'agent.account.info.memberSince': 'അംഗമായത് മുതൽ',
  'agent.account.info.lastLogin': 'അവസാന ലോഗിൻ',
  'agent.account.info.lastLoginIp': 'അവസാന ലോഗിൻ IP',
  'agent.account.info.reportsTo': 'റിപ്പോർട്ട് ചെയ്യുന്നത്',
  'agent.account.info.reportsToValue': 'അഡ്മിൻ #{id}',
  'agent.account.info.agentId': 'ഏജന്റ് ID',
  'agent.account.info.firstLogin': 'ആദ്യ തവണ',
  'agent.account.info.emptyDash': '—',

  // Personal details card (username/email/phone form).
  'agent.account.personal.title': 'വ്യക്തിഗത വിശദാംശങ്ങൾ',
  'agent.account.personal.subtitle.locked': 'ഐഡന്റിറ്റി അഡ്മിൻ പൂട്ടി · കാണാൻ മാത്രം',
  'agent.account.personal.subtitle.agent': 'യൂസർനെയിം, ഇമെയിൽ, കോൺടാക്റ്റ് നമ്പർ',
  'agent.account.personal.subtitle.admin': 'യൂസർനെയിമും ഇമെയിലും',
  'agent.account.personal.field.username': 'യൂസർനെയിം',
  'agent.account.personal.field.email': 'ഇമെയിൽ',
  'agent.account.personal.field.phone': 'കോൺടാക്റ്റ് നമ്പർ',
  'agent.account.personal.field.phone.placeholder': 'ഉദാ. +91 9876543210',
  'agent.account.personal.field.phone.hint': 'പ്ലെയർമാരും അഡ്മിനും ഈ നമ്പറിൽ നിങ്ങളെ ബന്ധപ്പെടും. മായ്ക്കാൻ ശൂന്യമാക്കുക.',
  'agent.account.personal.fieldLockedNote': 'പൂട്ടി',
  'agent.account.personal.cta.save': 'മാറ്റങ്ങൾ സേവ് ചെയ്യുക',
  'agent.account.personal.cta.saving': 'സേവ് ചെയ്യുന്നു…',
  'agent.account.personal.cta.noChanges': 'മാറ്റങ്ങളില്ല',

  // Location card (summary by default, editor on Edit).
  'agent.account.location.title': 'നിങ്ങളുടെ സ്ഥാനം',
  'agent.account.location.subtitle.summary': 'ലൊക്കേറ്റർ പുതിയ പ്ലെയർമാരെ നിങ്ങളുടെ ഏറ്റവും അടുത്ത പ്രദേശത്തേക്ക് റൂട്ട് ചെയ്യുന്നു',
  'agent.account.location.subtitle.edit': 'ലൊക്കേറ്റർ ശരിയായ പ്ലെയർമാരെ നിങ്ങളിലേക്ക് റൂട്ട് ചെയ്യാൻ നിങ്ങളുടെ പ്രദേശം അപ്ഡേറ്റ് ചെയ്യൂ',
  'agent.account.location.cta.edit': 'എഡിറ്റ് ചെയ്യുക',
  'agent.account.location.cta.cancel': 'റദ്ദാക്കുക',
  'agent.account.location.cta.save': 'സേവ് ചെയ്യുക',
  'agent.account.location.areaLabel': 'പ്രദേശം',
  'agent.account.location.gpsLabel': 'GPS',
  'agent.account.location.setLabel': 'സ്ഥാനം സജ്ജമാക്കി',
  'agent.account.location.setBody': 'GPS കോർഡിനേറ്റുകൾ ഫയലിൽ — പ്രദേശ വിശദാംശങ്ങൾ കാണാൻ എഡിറ്റ് ടാപ്പ് ചെയ്യൂ.',
  'agent.account.location.lastUpdated': 'അവസാനം അപ്ഡേറ്റ് ചെയ്തത് · {date}',

  // Security card (password change).
  'agent.account.security.title': 'സുരക്ഷ',
  'agent.account.security.subtitle.locked': 'അഡ്മിൻ പൂട്ടി · കാണാൻ മാത്രം',
  'agent.account.security.subtitle.unlocked': 'നിങ്ങളുടെ പാസ്‌വേഡ് മാറ്റുക · മറ്റ് ഉപകരണങ്ങളെ സൈൻ ഔട്ട് ചെയ്യും',
  'agent.account.security.field.current': 'നിലവിലുള്ള പാസ്‌വേഡ്',
  'agent.account.security.field.new': 'പുതിയ പാസ്‌വേഡ്',
  'agent.account.security.field.new.hint': 'ഒരു അക്ഷരവും ഒരു അക്കവും ഉള്ള കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ',
  'agent.account.security.field.confirm': 'പുതിയത് സ്ഥിരീകരിക്കുക',
  'agent.account.security.cta.update': 'പാസ്‌വേഡ് അപ്ഡേറ്റ് ചെയ്യുക',
  'agent.account.security.cta.updating': 'അപ്ഡേറ്റ് ചെയ്യുന്നു…',
  // Inline validation — emitted as toasts via onError. Kept under
  // `.error.*` so a translator can review every "what could go wrong"
  // string in one place.
  'agent.account.security.error.mismatch': 'പുതിയ പാസ്‌വേഡുകൾ പൊരുത്തപ്പെടുന്നില്ല',
  'agent.account.security.error.tooShort': 'പാസ്‌വേഡ് ≥ 8 പ്രതീകങ്ങൾ ആയിരിക്കണം',
  'agent.account.security.error.weak': 'പാസ്‌വേഡിൽ കുറഞ്ഞത് ഒരു അക്ഷരവും ഒരു അക്കവും ഉണ്ടായിരിക്കണം',

  // ─── ShareableIdCard (UAID + clipboard) ──────────────────────────
  // The body sentence is split into three pieces so translators can
  // re-order around the bold `<strong>not</strong>` — the emphasis on
  // the negation is essential to the security claim ("sharing does
  // NOT grant access") and we don't want translators to lose it.
  'agent.shareableId.eyebrow': 'പങ്കിടാവുന്ന ഏജന്റ് ID',
  'agent.shareableId.tooltip.copy': 'UAID പകർത്തുക',
  'agent.shareableId.tooltip.copied': 'UAID പകർത്തി',
  'agent.shareableId.cta.copy': 'പകർത്തുക',
  'agent.shareableId.cta.copied': 'പകർത്തി!',
  'agent.shareableId.body.before': 'നിങ്ങൾക്ക് പ്ലെയർമാരെ കൈമാറാൻ മറ്റൊരു ഏജന്റിന് ഈ കോഡ് പങ്കിടൂ. ചാറ്റ് അല്ലെങ്കിൽ വാട്ട്സാപ്പ് വഴി അയയ്ക്കാൻ സുരക്ഷിതമാണ് — ഇത് പങ്കിടുന്നത് നിങ്ങളുടെ അക്കൗണ്ടിലേക്കുള്ള ഏതെങ്കിലും ആക്സസ് ',
  'agent.shareableId.body.not': 'നൽകുന്നില്ല',
  'agent.shareableId.body.after': '.',

  // ════════════════════════════════════════════════════════════════════
  // REWARDS HUB + BROWSE OFFERS + EXPOSURE + OPT-IN MODAL  (Phase B Group 7)
  //
  // Layout:
  //   agent.rewards.hub.*          RewardsHub.tsx — header + 2 sub-tabs
  //   agent.rewards.status.*       SHARED opt-in/opt-out status labels
  //                                (used across BrowseOffersPanel,
  //                                ExposurePanel, OfferOptInModal — kept
  //                                in a single namespace so the verb the
  //                                translator picks for "opt in"/"opt out"
  //                                stays consistent across the surface).
  //   agent.rewards.browse.*       BrowseOffersPanel — hero + chips + empties
  //   agent.rewards.exposure.*     ExposurePanel — hero card, risk gauge,
  //                                footnote (formula), per-offer rows
  //   agent.rewards.optInModal.*   OfferOptInModal — three internal panels
  //                                (YourStake, OptInToggle, SubAgentRates)
  //                                + validation errors + toasts + footer
  //
  // FINANCIAL/RISK CONTEXT (Group 7 directive #1):
  //   The "Actual spend", "Max exposure", "Spent so far", "Max possible"
  //   labels each carry a `.helper` companion that gives the translator
  //   the financial meaning of the term, so "Exposure" doesn't drift to
  //   the photographic/sun sense, "Spend" doesn't drift to leisure
  //   spending, and "Ceiling" doesn't drift to architecture. The label
  //   itself stays short for the data-viz; the helper is the prose hint
  //   that anchors the term.
  //
  // PARALLEL OPT-IN STATES (Group 7 directive #2):
  //   Every state-flip surface (chip on card, modal toggle title/body,
  //   stake panel "Locked at opt-in" vs "Current rate", risk caption
  //   noMax/low/mid/high) uses separate keys per state — never an inline
  //   ternary, never an interpolated verb — so each language can pick
  //   its own grammar per state. ESPECIALLY important for Indic languages
  //   where the verb conjugation differs by state.

  // ─── RewardsHub header + sub-tabs ────────────────────────────────
  'agent.rewards.hub.title': 'പുരസ്കാരങ്ങൾ',
  'agent.rewards.hub.subtitle': 'ഓഫറുകൾ ബ്രൗസ് ചെയ്ത് നിങ്ങളുടെ എക്സ്പോഷർ ട്രാക്ക് ചെയ്യൂ',
  'agent.rewards.hub.tab.browse': 'ഓഫറുകൾ ബ്രൗസ് ചെയ്യുക',
  'agent.rewards.hub.tab.exposure': 'എന്റെ എക്സ്പോഷർ',

  // ─── Shared opt-in status terms ──────────────────────────────────
  'agent.rewards.status.optedIn': 'ഓപ്റ്റ് ഇൻ ചെയ്തു',
  'agent.rewards.status.optedOut': 'ഓപ്റ്റ് ഔട്ട് ചെയ്തു',

  // ─── BrowseOffersPanel ───────────────────────────────────────────
  'agent.rewards.browse.hero.eyebrow': 'നിങ്ങളുടെ പ്ലെയർമാർക്ക് തിരഞ്ഞെടുത്തത്',
  'agent.rewards.browse.empty.title': 'ഇപ്പോൾ സജീവ പുരസ്കാരങ്ങളില്ല',
  'agent.rewards.browse.empty.body': 'അഡ്മിൻ പതിവായി പുതിയ ഓഫറുകൾ പോസ്റ്റ് ചെയ്യുന്നു. ഉടൻ വീണ്ടും പരിശോധിക്കൂ — അല്ലെങ്കിൽ നിങ്ങൾ നഷ്ടപ്പെടുത്തിയ ഏതെങ്കിലും ഒപ്റ്റ് ഇൻ ചെയ്യാൻ നിങ്ങളുടെ അപ്പ്ലൈനോട് ആവശ്യപ്പെടൂ.',
  'agent.rewards.browse.error.loadFailed': 'പുരസ്കാരങ്ങൾ ലോഡ് ചെയ്യാനായില്ല',
  'agent.rewards.browse.card.aria.manage': '{title}-ന്റെ ഓപ്റ്റ് ഇൻ കൈകാര്യം ചെയ്യുക',
  // Card chip — parallel state keys. The "Tap to opt in" prompt and the
  // "Opted in" status are different concepts (action vs. status), so
  // they live on separate keys rather than a single state-flipped value.
  'agent.rewards.browse.chip.optedIn': 'ഓപ്റ്റ് ഇൻ ചെയ്തു',
  'agent.rewards.browse.chip.optInPrompt': 'ഓപ്റ്റ് ഇൻ ചെയ്യാൻ ടാപ്പ് ചെയ്യൂ',
  // Cost-share suffix appended after "Opted in" when the agent has a
  // rate set. Kept as a separate fragment so the bullet + percent reads
  // correctly in every language (the · is a typographic separator).
  'agent.rewards.browse.chip.shareSuffix': '· {pct}%',

  // ─── ExposurePanel — loading + error ────────────────────────────
  'agent.rewards.exposure.loading': 'നിങ്ങളുടെ എക്സ്പോഷർ കണക്കാക്കുന്നു…',
  'agent.rewards.exposure.error.loadFailed': 'എക്സ്പോഷർ ലോഡ് ചെയ്യാനായില്ല',

  // ─── ExposurePanel — premium hero card (yellow gradient) ────────
  // Each label is paired with a `.helper` line that anchors the
  // financial meaning of the term for translators (Group 7 directive).
  'agent.rewards.exposure.hero.eyebrow': 'ഓഫർ എക്സ്പോഷർ',
  // Pluralised opt-in count chip — { count } → number.
  'agent.rewards.exposure.hero.activeOptIns.one': '{count} സജീവ ഓപ്റ്റ്-ഇൻ',
  'agent.rewards.exposure.hero.activeOptIns.other': '{count} സജീവ ഓപ്റ്റ്-ഇന്നുകൾ',

  'agent.rewards.exposure.hero.actualSpend.label': 'യഥാർത്ഥ ചെലവ്',
  'agent.rewards.exposure.hero.actualSpend.helper': 'ഇതുവരെ നിങ്ങളുടെ ഡൗൺലൈൻ ക്ലെയിം ചെയ്തത്.',

  'agent.rewards.exposure.hero.maxExposure.label': 'പരമാവധി എക്സ്പോഷർ',
  'agent.rewards.exposure.hero.maxExposure.helper': 'ഏറ്റവും മോശം സീലിംഗ് — ശേഷിക്കുന്ന എല്ലാ ബജറ്റ് രൂപയും ക്ലെയിം ചെയ്തു.',

  'agent.rewards.exposure.hero.ceiling.label': 'സീലിംഗ് ഉപയോഗിച്ചു',

  // ─── ExposurePanel — risk gauge ─────────────────────────────────
  'agent.rewards.exposure.gauge.label': 'റിസ്ക് ഗേജ്',
  // Suffix on the "₹X / ₹Y max" line under the bar.
  'agent.rewards.exposure.gauge.maxSuffix': '/ {amount} പരമാവധി',
  // Threshold legend — split into pre/value/post pieces so translators
  // can re-order around the bold "50%" / "80%" numerals.
  'agent.rewards.exposure.gauge.legend.amber.before': '',
  'agent.rewards.exposure.gauge.legend.amber.value': '50%',
  'agent.rewards.exposure.gauge.legend.amber.after': '-ൽ, നിങ്ങളുടെ ഓപ്റ്റ്-ഇന്നുകൾ ശ്രദ്ധിക്കുക',
  'agent.rewards.exposure.gauge.legend.rose.before': '',
  'agent.rewards.exposure.gauge.legend.rose.value': '80%',
  'agent.rewards.exposure.gauge.legend.rose.after': '-ൽ, നിങ്ങൾ നിങ്ങളുടെ സീലിംഗിനോട് അടുത്താണ്',
  // Caption — 4 parallel keys, one per gauge state. NEVER a ternary.
  'agent.rewards.exposure.gauge.caption.noMax': 'പ്രൊജക്റ്റ് ചെയ്ത സീലിംഗുള്ള ഒന്നിലും നിങ്ങൾ ഓപ്റ്റ് ഇൻ ചെയ്തിട്ടില്ല.',
  'agent.rewards.exposure.gauge.caption.high': 'നിങ്ങൾ നിങ്ങളുടെ പരമാവധി എക്സ്പോഷറിന്റെ {pct}% ഉപയോഗിച്ചു. മിക്ക ഓപ്റ്റ്-ഇന്നുകളും കൂടുതലായി ക്ലെയിം ചെയ്തു.',
  'agent.rewards.exposure.gauge.caption.mid': 'നിങ്ങൾ നിങ്ങളുടെ പരമാവധി സീലിംഗിന്റെ {pct}%-ൽ ആണ്. ഓരോ-ഓഫർ വിശകലനത്തിൽ ഒരു കണ്ണ് വയ്ക്കൂ.',
  'agent.rewards.exposure.gauge.caption.low': 'സുഖകരം — നിങ്ങളുടെ പരമാവധി സീലിംഗിന്റെ {pct}% മാത്രമേ ഇതുവരെ ഉപയോഗിച്ചുള്ളൂ.',

  // ─── ExposurePanel — formula footnote ───────────────────────────
  // Sentence split into pieces because there's a <code> block in the
  // middle (the literal formula, which is NOT translated). The pieces
  // stitch back together at render time with the code element inline.
  'agent.rewards.exposure.footnote.heading': 'പരമാവധി എക്സ്പോഷർ എങ്ങനെ കണക്കാക്കുന്നു:',
  'agent.rewards.exposure.footnote.prefix': ' ഓരോ ഓപ്റ്റ്-ഇൻ ചെയ്ത ഓഫറിനും: ',
  // NOTE: this is a programmatic identifier — translators MUST keep it
  // verbatim. The reader sees it as a code chip in the UI.
  'agent.rewards.exposure.footnote.formula': 'remaining_budget × your_snapshot_rate',
  'agent.rewards.exposure.footnote.suffix': ' — യാഥാസ്ഥിതിക ഉയർന്ന പരിധി. നിങ്ങളുടെ യഥാർത്ഥ ബാധ്യത സാധാരണയായി ',
  'agent.rewards.exposure.footnote.emphasis': 'കുറവ്',
  'agent.rewards.exposure.footnote.suffix2': ' ആണ് — നിങ്ങളുടെ താഴെയുള്ള സബ്-ഏജന്റുമാരും ഓപ്റ്റ് ഇൻ ചെയ്യുമ്പോൾ (അവർ വലിയ വിഹിതം നൽകുന്നു; നിങ്ങൾ ശേഷിക്കുന്നത് നൽകുന്നു).',

  // ─── ExposurePanel — empty state (no opt-ins) ───────────────────
  'agent.rewards.exposure.empty.title': 'സജീവ ഓപ്റ്റ്-ഇന്നുകളില്ല',
  'agent.rewards.exposure.empty.body': 'ഓഫറുകൾ ബ്രൗസ് ടാബിൽ നിന്ന് നിങ്ങൾ ഒരു ഓഫറിൽ ഓപ്റ്റ് ഇൻ ചെയ്താൽ, അത് ഇവിടെ നിങ്ങളുടെ ചെലവും പ്രൊജക്റ്റ് ചെയ്ത എക്സ്പോഷറും കൂടെ ദൃശ്യമാകും.',

  // ─── ExposurePanel — per-offer row ──────────────────────────────
  'agent.rewards.exposure.row.share': '{pct}% വിഹിതം',
  'agent.rewards.exposure.row.claims.one': '{count} ക്ലെയിം',
  'agent.rewards.exposure.row.claims.other': '{count} ക്ലെയിമുകൾ',
  'agent.rewards.exposure.row.overridden': 'അഡ്മിൻ-ഓവർറൈഡ് ചെയ്തു',
  'agent.rewards.exposure.row.actualMaxLabel': 'യഥാർത്ഥ / പരമാവധി',
  // Dash rendered when an offer has no budget cap → no projected ceiling.
  'agent.rewards.exposure.row.noMaxDash': '—',

  // ─── OfferOptInModal — chrome ───────────────────────────────────
  'agent.rewards.optInModal.eyebrow': 'കോസ്റ്റ്-ഷെയർ ഓപ്റ്റ്-ഇൻ',
  // ─── Phase 5 (2026-05-15): Dynamic Upline Subsidy keys ───
  'agent.profile.rates.infoBanner.gameProfitsLabel': 'ഗെയിം ലാഭം',
  'agent.profile.rates.infoBanner.standardPctText': ' സാധാരണ ശതമാനങ്ങൾ ഉപയോഗിക്കുന്നു. ',
  'agent.profile.rates.infoBanner.promotionalCostsLabel': 'പ്രമോഷണൽ ചെലവുകൾ',
  'agent.profile.rates.infoBanner.dynamicPoolText': ' (ഓഫറുകൾ, ക്യാഷ്ബാക്കുകൾ) ഒരു ഡൈനാമിക് അപ്‌ലൈൻ പൂൾ ഉപയോഗിക്കുന്നു. രണ്ടും നിങ്ങളുടെ ലെവൽ (L{level}) അനുസരിച്ച് പ്ലാറ്റ്‌ഫോം അഡ്മിൻ ഗ്ലോബലായി കൈകാര്യം ചെയ്യുന്നു.',
  'agent.profile.rates.section.gameProfits': 'ഗെയിം ലാഭം — ഓരോ മത്സരത്തിലും നിങ്ങൾ നേടുന്നത്',
  'agent.profile.rates.section.promotionalCosts': 'പ്രമോഷണൽ ചെലവുകൾ — നിങ്ങളുടെ അപ്‌ലൈൻ സബ്സിഡി സംഭാവന',
  'agent.profile.rates.pillar.platformFee': 'പ്ലാറ്റ്‌ഫോം ഫീസ് %',
  'agent.profile.rates.pillar.winLoss': 'വിജയം / പരാജയം ലാഭം %',
  'agent.profile.rates.pillar.promoWeight': 'പ്രമോഷണൽ പൂൾ വെയ്റ്റ്',
  'agent.profile.rates.guarantee.title': 'ഡയറക്റ്റ് റെഫറൽ ഗ്യാരണ്ടി',
  'agent.profile.rates.guarantee.body': 'ഓഫർ ക്ലെയിം ചെയ്യുന്ന കളിക്കാരനെ നിങ്ങൾ നേരിട്ട് റെഫർ ചെയ്തെങ്കിൽ, നിങ്ങൾ ₹0.00 അടയ്ക്കും. നിങ്ങളുടെ നെറ്റ്‌വർക്ക് വളർത്താൻ സഹായിക്കാൻ നിങ്ങളുടെ അപ്‌ലൈനുകൾ ചെലവിന്റെ 100% സബ്സിഡി നൽകുന്നു.',
  'agent.profile.rates.poolExplainer.title': 'പൂൾ എങ്ങനെ പ്രവർത്തിക്കുന്നു:',
  'agent.profile.rates.poolExplainer.body': 'ഒരു സബ്-ഏജന്റിന്റെ കളിക്കാരൻ ഒരു ഓഫർ ക്ലെയിം ചെയ്യുമ്പോൾ, നിങ്ങളുടെ വെയ്റ്റ് ബാക്കിയുള്ള സജീവ അപ്‌ലൈൻ + പ്ലാറ്റ്‌ഫോമിനൊപ്പം ഒരു പൂളിലേക്ക് ചേർക്കപ്പെടുന്നു. നിങ്ങൾ (നിങ്ങളുടെ വെയ്റ്റ് / മൊത്തം പൂൾ) × ഓഫർ ചെലവ് അടയ്ക്കും. ഉയർന്ന വെയ്റ്റ് = വലിയ പങ്ക്.',
  'agent.profile.rates.weightLabel': 'വെയ്റ്റ്:',
  'agent.profile.rates.updatedPrefix': 'അപ്‌ഡേറ്റ് ചെയ്തു',
  'agent.profile.rates.notSetYet': 'ഇതുവരെ സജ്ജമാക്കിയിട്ടില്ല',
  'agent.rewards.optInModal.guarantee.title': 'ഡയറക്റ്റ് റെഫറൽ ഗ്യാരണ്ടി',
  'agent.rewards.optInModal.guarantee.body.withDownline': 'ഈ ഓഫർ ക്ലെയിം ചെയ്യുന്ന കളിക്കാരനെ <strong>നിങ്ങൾ നേരിട്ട് റെഫർ ചെയ്തെങ്കിൽ</strong>, നിങ്ങൾ <strong>₹0.00</strong> അടയ്ക്കും. നിങ്ങളുടെ നെറ്റ്‌വർക്ക് വളർത്താൻ സഹായിക്കാൻ നിങ്ങളുടെ അപ്‌ലൈനുകൾ ചെലവിന്റെ 100% സബ്സിഡി നൽകുന്നു.',
  'agent.rewards.optInModal.guarantee.body.noDownline': '<strong>നിങ്ങളുടെ കളിക്കാർ</strong> ഈ ഓഫർ ക്ലെയിം ചെയ്യുമ്പോൾ, നിങ്ങൾ <strong>₹0.00</strong> അടയ്ക്കും. നിങ്ങളുടെ നെറ്റ്‌വർക്ക് വളർത്താൻ സഹായിക്കാൻ നിങ്ങളുടെ അപ്‌ലൈനുകൾ ചെലവിന്റെ 100% സബ്സിഡി നൽകുന്നു.',
  'agent.rewards.optInModal.uplineReality.title': 'അപ്‌ലൈൻ റിയാലിറ്റി ചെക്ക്',
  'agent.rewards.optInModal.uplineReality.body': 'നിങ്ങൾക്ക് <strong>{count}</strong> ഡയറക്റ്റ് സബ്-ഏജന്റുകൾ{plural} ഉണ്ട്. ഒരു സബ്-ഏജന്റിന്റെ കളിക്കാരൻ ഈ ഓഫർ ക്ലെയിം ചെയ്താൽ, ബാക്കിയുള്ള സജീവ അപ്‌ലൈനും പ്ലാറ്റ്‌ഫോമും താരതമ്യപ്പെടുത്തി നിങ്ങളുടെ <strong>പൂൾ വെയ്റ്റ് ({weight})</strong> അനുസരിച്ച് ആനുപാതിക പങ്ക് അടയ്ക്കും.',
  // ─── Phase 5b (2026-05-15): Direct/Indirect column labels ───
  'agent.rates.col.direct': 'ഡയറക്റ്റ്',
  'agent.rates.col.indirect': 'ഇൻഡയറക്റ്റ്',
  'agent.rates.indirect.dynamic': 'ഡൈനാമിക്',
  'agent.rates.indirect.notApplicable': 'ബാധകമല്ല',
  'agent.rates.indirect.rangeTo': 'മുതൽ',
  'agent.rewards.optInModal.close': 'അടയ്ക്കുക',
  'agent.rewards.optInModal.cancel': 'റദ്ദാക്കുക',
  'agent.rewards.optInModal.loading': 'ഓഫർ വിശദാംശങ്ങൾ ലോഡ് ചെയ്യുന്നു…',
  'agent.rewards.optInModal.error.loadFailed': 'ഓഫർ വിശദാംശങ്ങൾ ലോഡ് ചെയ്യാനായില്ല',
  'agent.rewards.optInModal.error.optInFailed': 'ഓപ്റ്റ്-ഇൻ പരാജയപ്പെട്ടു',
  'agent.rewards.optInModal.error.optOutFailed': 'ഓപ്റ്റ്-ഔട്ട് പരാജയപ്പെട്ടു',
  'agent.rewards.optInModal.error.saveFailed': 'സബ്-ഏജന്റ് നിരക്കുകൾ സേവ് ചെയ്യാനായില്ല',
  'agent.rewards.optInModal.toast.optedIn': 'ഓപ്റ്റ് ഇൻ ചെയ്തു. നിങ്ങളുടെ ഡൗൺലൈൻ ഇപ്പോൾ ഈ ഓഫർ കാണാം.',
  'agent.rewards.optInModal.toast.optedOut': 'ഓപ്റ്റ് ഔട്ട് ചെയ്തു. നിങ്ങളുടെ ഡൗൺലൈനിൽ നിന്ന് ഓഫർ നീക്കം ചെയ്തു.',
  'agent.rewards.optInModal.toast.saved.one': '{count} സബ്-ഏജന്റ് നിരക്ക് മാറ്റം സേവ് ചെയ്തു.',
  'agent.rewards.optInModal.toast.saved.other': '{count} സബ്-ഏജന്റ് നിരക്ക് മാറ്റങ്ങൾ സേവ് ചെയ്തു.',
  'agent.rewards.optInModal.confirmOptOut': 'ഈ ഓഫറിൽ നിന്ന് ഓപ്റ്റ് ഔട്ട് ചെയ്യണോ? നിങ്ങളുടെ ഡൗൺലൈൻ ഇത് ഇനി കാണില്ല.',
  // Audit-trail note (server-side reason field). Translated for UX
  // consistency in case it's ever surfaced back to the user.
  'agent.rewards.optInModal.auditNote': 'ഓഫർ #{id}-നായി ഓപ്റ്റ്-ഇൻ മോഡൽ വഴി സജ്ജമാക്കി',

  // ─── OfferOptInModal — YourStake panel (rate + spend metrics) ───
  'agent.rewards.optInModal.stake.shareLabel': 'നിങ്ങളുടെ വിഹിതം',
  // Parallel state labels — "Locked at opt-in" vs "Current rate".
  'agent.rewards.optInModal.stake.locked': 'ഓപ്റ്റ്-ഇന്നിൽ പൂട്ടി',
  'agent.rewards.optInModal.stake.current': 'നിലവിലെ നിരക്ക്',
  'agent.rewards.optInModal.stake.noRate': 'അഡ്മിൻ നിങ്ങൾക്ക് ഇതുവരെ നിരക്ക് നൽകിയിട്ടില്ല',
  // Two metrics: "Spent so far" + "Max possible". Each carries a helper
  // line that anchors the financial term for translators (directive #1).
  'agent.rewards.optInModal.stake.spentSoFar.label': 'ഇതുവരെ ചെലവഴിച്ചത്',
  'agent.rewards.optInModal.stake.spentSoFar.helper': 'ഈ ഓഫറിലെ ഡൗൺലൈൻ ക്ലെയിമുകൾക്കായി നിങ്ങളുടെ വാലറ്റിൽ നിന്ന് ഇതിനകം നൽകിയ സഞ്ചിത രൂപ.',
  'agent.rewards.optInModal.stake.maxPossible.label': 'പരമാവധി സാധ്യമായത്',
  'agent.rewards.optInModal.stake.maxPossible.helper': 'ശേഷിക്കുന്ന എല്ലാ ബജറ്റ് രൂപയും ക്ലെയിം ചെയ്താൽ നിങ്ങൾ നൽകാവുന്ന ഏറ്റവും മോശം രൂപ.',
  'agent.rewards.optInModal.stake.maxPossible.noCap': '— ബജറ്റ് പരിധിയില്ല —',
  // Worst-case explanatory sentence under the metrics grid.
  'agent.rewards.optInModal.stake.worstCase': 'ഏറ്റവും മോശം കേസ്: പ്ലാറ്റ്ഫോം ബജറ്റിന്റെ ശേഷിക്കുന്ന എല്ലാ ₹{amount}-ഉം നിങ്ങളുടെ ഡൗൺലൈൻ ക്ലെയിം ചെയ്താൽ, നിങ്ങൾ അതിന്റെ {pct}% നൽകുന്നു.',

  // ─── OfferOptInModal — OptInToggle panel ────────────────────────
  // Parallel title + body keys per state.
  'agent.rewards.optInModal.toggle.title.optedIn': 'നിങ്ങൾ ഓപ്റ്റ് ഇൻ ചെയ്തു',
  'agent.rewards.optInModal.toggle.title.optedOut': 'നിങ്ങളുടെ ഡൗൺലൈനുമായി പങ്കിടാൻ ഓപ്റ്റ് ഇൻ ചെയ്യൂ',
  'agent.rewards.optInModal.toggle.body.optedIn': 'നിങ്ങളുടെ ഡൗൺലൈൻ പ്ലെയർമാർ ഈ ഓഫർ കാണുന്നു. നിങ്ങളുടെ പൂട്ടിയ വിഹിതത്തിൽ അവരിൽ നിന്നുള്ള ഓരോ ക്ലെയിമും നിങ്ങൾ കോ-ഫണ്ട് ചെയ്യുന്നു.',
  'agent.rewards.optInModal.toggle.body.optedOut': 'നിങ്ങൾ ഓപ്റ്റ് ഇൻ ചെയ്യുന്നതുവരെ, നിങ്ങളുടെ പ്ലെയർമാർ ആരും ഈ ഓഫർ കാണില്ല. നിങ്ങൾ ഓപ്റ്റ് ഇൻ ചെയ്താൽ, ഈ ഓഫറിനായി നിങ്ങളുടെ വിഹിത നിരക്ക് പൂട്ടും.',
  'agent.rewards.optInModal.toggle.optOutLink': 'ഈ ഓഫറിൽ നിന്ന് ഓപ്റ്റ് ഔട്ട് ചെയ്യുക',

  // ─── OfferOptInModal — SubAgentRates panel ──────────────────────
  'agent.rewards.optInModal.subAgents.title': 'സബ്-ഏജന്റ് നിരക്കുകൾ',
  // "Max each can take: 25%" — sentence is split so the bold {pct}% can
  // keep its emphasis in any word order.
  'agent.rewards.optInModal.subAgents.ceilingHint.before': 'ഓരോരുത്തർക്കും പരമാവധി എടുക്കാവുന്നത്: ',
  'agent.rewards.optInModal.subAgents.ceilingHint.value': '{pct}%',
  'agent.rewards.optInModal.subAgents.unsaved': '{count} സേവ് ചെയ്യാത്തവ',
  'agent.rewards.optInModal.subAgents.empty': 'ഡയറക്റ്റ് സബ്-ഏജന്റുമാരില്ല.',
  'agent.rewards.optInModal.subAgents.cascadeNote': 'സേവിംഗ് കാസ്കേഡുകൾ — നിങ്ങൾ ഇവിടെ ഒരു ചൈൽഡിനെ ഉയർത്തിയാൽ ആ ചൈൽഡിന് ഇതിനകം ആഴത്തിലുള്ള സബ്-ഏജന്റുമാർ ഉണ്ടെങ്കിൽ, ചെയിൻ സ്ഥിരത നിലനിർത്താൻ ആ നിരക്കുകൾ സ്വയം ക്ലാമ്പ് ചെയ്യും.',
  'agent.rewards.optInModal.subAgents.noChildren': 'നിങ്ങൾക്ക് ഡയറക്റ്റ് സബ്-ഏജന്റുമാർ ഇല്ല. ഓഫർ നിങ്ങളുടെ സ്വന്തം പ്ലെയർമാർക്ക് ദൃശ്യമാണ്; അഡ്മിൻ പൂർണ്ണമായ ശേഷിക്കുന്നത് നൽകുന്നു.',

  // Per-input client-side validation errors (live below each field).
  'agent.rewards.optInModal.error.rateRequired': 'ഒരു നിരക്ക് നൽകുക (മായ്ക്കാൻ 0)',
  'agent.rewards.optInModal.error.numbersOnly': 'നമ്പറുകൾ മാത്രം',
  'agent.rewards.optInModal.error.negative': 'നെഗറ്റീവ് ആകാൻ പാടില്ല',
  'agent.rewards.optInModal.error.max100': 'പരമാവധി 100',
  'agent.rewards.optInModal.error.aboveCeiling': 'പരമാവധി {ceiling}% (നിങ്ങളുടെ വിഹിതം)',

  // ─── OfferOptInModal — sticky footer CTA ────────────────────────
  'agent.rewards.optInModal.cta.saving': 'സേവ് ചെയ്യുന്നു…',
  'agent.rewards.optInModal.cta.done': 'പൂർത്തിയായി',
  'agent.rewards.optInModal.cta.save.one': '{count} മാറ്റം സേവ് ചെയ്യുക',
  'agent.rewards.optInModal.cta.save.other': '{count} മാറ്റങ്ങൾ സേവ് ചെയ്യുക',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 8 — Admin-shell pages reused inside the agent SPA.
  //
  // CONTEXT: these five components were originally built for the
  // platform admin (super admin), but they're also mounted as routes
  // inside agent-frontend so agents can manage their own players /
  // sub-agents from the same chrome. We namespace them under
  // `agent.*` (NEVER `admin.*`) so when we later split out a separate
  // admin-frontend i18n bundle, those literals stay independent.
  //
  //   agent.adminRechargePlayer.*   AdminRechargePlayer page (adapter)
  //   agent.createPlayer.*          CreatePlayerModal (shared form)
  //   agent.adminHome.*             AdminHome (admin + legacy agent dash)
  //   agent.playerDetail.*          AdminUserDetail (per-player edit page)
  //   agent.subAgentDetail.*        AgentSubAgentDetail (per-sub-agent edit)
  //
  // AUDIT/HISTORY STRATEGY (Group 8 directive #4): every history feed
  // uses compound-sentence pieces (action verb, actor name, amount,
  // timestamp) as separate keys so a translator can re-order them per
  // language. "Recharge of ₹500 by Agent @bob" might naturally be
  // "एजेंट @bob द्वारा ₹500 का रिचार्ज" in Hindi — and that's
  // achievable because the pieces compose at render time.

  // ─── AdminRechargePlayer (route adapter) ──────────────────────────
  'agent.adminRechargePlayer.title': 'ഒരു പ്ലെയറെ റീചാർജ് ചെയ്യുക',
  'agent.adminRechargePlayer.subtitle': 'നിങ്ങളുടെ പൂളിൽ നിന്ന് ഒരു പ്ലെയറുടെ വാലറ്റിലേക്ക്',
  'agent.adminRechargePlayer.backLink': 'പ്ലെയറിലേക്ക് മടങ്ങുക',

  // ─── CreatePlayerModal (shared form) ──────────────────────────────
  // The modal is in shared-ui because admin-frontend uses it too. We
  // namespace under `agent.*` here because agent-frontend is the only
  // app currently consuming i18n; when admin-frontend gets its own
  // bundle, it can swap in `admin.createPlayer.*` parallel keys.
  //
  // FORM VALIDATION (Group 8 directive #3): every field has a translator-
  // managed label, an example placeholder, and (where appropriate) a
  // hint sentence that doubles as the validation rule. There's no
  // separate error message because the form gates submission on a
  // memoised `valid` flag — the hints ARE the validation copy.
  'agent.createPlayer.title': 'ഒരു പുതിയ പ്ലെയറെ ഓൺബോർഡ് ചെയ്യുക',
  'agent.createPlayer.subtitle.agent': 'അവർ നിങ്ങളുടെ സ്വയം-ഉടമസ്ഥതയിലായിരിക്കും.',
  'agent.createPlayer.subtitle.admin': 'താഴെ ഒരു ഉടമയായ ഏജന്റിനെ തിരഞ്ഞെടുക്കുക, അല്ലെങ്കിൽ പ്ലാറ്റ്ഫോം-ഡയറക്റ്റിന് ശൂന്യമാക്കുക.',
  'agent.createPlayer.close': 'അടയ്ക്കുക',
  'agent.createPlayer.section.identity.title': 'ഐഡന്റിറ്റി',
  'agent.createPlayer.section.identity.hint': 'അക്കൗണ്ട് സൃഷ്ടിക്കാൻ ആവശ്യമാണ്',
  'agent.createPlayer.field.username.label': 'യൂസർനെയിം',
  'agent.createPlayer.field.username.placeholder': 'kumar_42',
  'agent.createPlayer.field.email.label': 'ഇമെയിൽ',
  'agent.createPlayer.field.email.placeholder': 'kumar@example.com',
  'agent.createPlayer.field.phone.label': 'മൊബൈൽ',
  'agent.createPlayer.field.phone.placeholder': '9876543210',
  'agent.createPlayer.field.phone.hint': '10-അക്ക ഇന്ത്യൻ മൊബൈൽ, +91 സ്വയം ചേർക്കും',
  'agent.createPlayer.field.password.label': 'പ്രാരംഭ പാസ്‌വേഡ്',
  'agent.createPlayer.field.password.placeholder': 'StrongP4ss',
  'agent.createPlayer.field.password.hint': '8+ പ്രതീകങ്ങൾ, അക്ഷരം + അക്കം',
  'agent.createPlayer.section.owner.title': 'ഉടമയായ ഏജന്റ്',
  'agent.createPlayer.section.owner.hint': 'ഐച്ഛികം — പ്ലാറ്റ്ഫോം-ഡയറക്റ്റ് പ്ലെയറിന് ശൂന്യമാക്കുക',
  'agent.createPlayer.field.owner.label': 'ഏജന്റ്',
  'agent.createPlayer.field.owner.none': '— ഒന്നുമില്ല (പ്ലാറ്റ്ഫോം-ഡയറക്റ്റ്) —',
  'agent.createPlayer.section.profile.title': 'പ്രൊഫൈൽ',
  'agent.createPlayer.section.profile.hint': 'ഐച്ഛികം — ഇപ്പോൾ അറിയാവുന്നത് പൂരിപ്പിക്കുക; ആദ്യ ലോഗിനിൽ പ്ലെയർക്ക് പൂർത്തിയാക്കാം',
  'agent.createPlayer.field.firstName.label': 'ആദ്യ പേര്',
  'agent.createPlayer.field.lastName.label': 'അവസാന പേര്',
  'agent.createPlayer.field.dob.label': 'ജനന തീയതി',
  'agent.createPlayer.field.gender.label': 'ലിംഗം',
  // Gender select options.
  'agent.createPlayer.gender.unspecified': '— വ്യക്തമാക്കിയിട്ടില്ല —',
  'agent.createPlayer.gender.male': 'പുരുഷൻ',
  'agent.createPlayer.gender.female': 'സ്ത്രീ',
  'agent.createPlayer.gender.other': 'മറ്റുള്ളവ',
  'agent.createPlayer.gender.preferNot': 'പറയാൻ താൽപ്പര്യമില്ല',
  'agent.createPlayer.field.language.label': 'മുൻഗണനാ ഭാഷ',
  'agent.createPlayer.section.location.title': 'സ്ഥാനം',
  'agent.createPlayer.section.location.hint': 'ഐച്ഛികം — ആദ്യ ലോഗിനിൽ പ്ലെയർക്ക് പൂർത്തിയാക്കാം',
  // Footer.
  'agent.createPlayer.disclaimer': 'പ്ലെയർ status=active, email auto-verified ആയി സൃഷ്ടിക്കുന്നു — ലിങ്ക് അയയ്ക്കില്ല. കോൺടാക്റ്റ് വിവരം നിങ്ങൾ വിശ്വസിക്കുന്നുവെന്ന് ഉറപ്പാക്കുക.',
  'agent.createPlayer.cta.cancel': 'റദ്ദാക്കുക',
  'agent.createPlayer.cta.creating': 'സൃഷ്ടിക്കുന്നു…',
  'agent.createPlayer.cta.submit': 'പ്ലെയറെ സൃഷ്ടിക്കുക',
  // Errors.
  'agent.createPlayer.error.missingAgentId': 'ഏജന്റ് id ഇല്ല',
  'agent.createPlayer.error.createFailed': 'പ്ലെയറെ സൃഷ്ടിക്കാനായില്ല',

  // ─── AdminHome (dashboard) ────────────────────────────────────────
  // Range toggle on top of the dashboard. The same 7 labels are used
  // by the rangeLabel() pure helper that renders subtitle copy.
  'agent.adminHome.range.today': 'ഇന്ന്',
  'agent.adminHome.range.yesterday': 'ഇന്നലെ',
  'agent.adminHome.range.week': 'ഈ ആഴ്ച',
  'agent.adminHome.range.lastWeek': 'കഴിഞ്ഞ ആഴ്ച',
  'agent.adminHome.range.month': 'ഈ മാസം',
  'agent.adminHome.range.lastMonth': 'കഴിഞ്ഞ മാസം',
  'agent.adminHome.range.all': 'സമ്പൂർണ്ണം',

  // Greeting. The username is concatenated separately so we don't
  // interpolate a noun into the sentence — translators can drift the
  // verb order around the substituted name freely.
  'agent.adminHome.greeting.subtitle': 'വീണ്ടും സ്വാഗതം',
  'agent.adminHome.greeting.fallback': 'സുഹൃത്തേ',
  'agent.adminHome.errorBanner.fallback': 'ലോഡ് ചെയ്യുന്നതിൽ പരാജയം',

  // Admin variant — hero strip + section titles + entity-themed wallet
  // cards. Every label here is the ADMIN view of the platform totals.
  'agent.adminHome.admin.hero.label': 'പ്ലാറ്റ്ഫോം ഇൻഫ്ലോ',
  'agent.adminHome.admin.action.moneyQueue': 'മണി ക്യൂ',
  'agent.adminHome.admin.action.agents': 'ഏജന്റുമാർ',
  // Snapshot mini stat tiles in the hero's right column.
  'agent.adminHome.admin.snapshot.matches': 'മത്സരങ്ങൾ',
  'agent.adminHome.admin.snapshot.newSignups': 'പുതിയ സൈൻഅപ്പുകൾ',
  'agent.adminHome.admin.snapshot.active': 'സജീവം',
  'agent.adminHome.admin.snapshot.pending': 'തീരുമാനമാകാത്തത്',

  // Section titles between rows.
  'agent.adminHome.section.games': 'ഗെയിമുകൾ',
  'agent.adminHome.section.games.subtitle': 'മത്സരങ്ങളും പ്ലാറ്റ്ഫോം ഫീസും · {range}',
  'agent.adminHome.section.activity': 'പ്രവർത്തനം',
  'agent.adminHome.section.ops': 'ഓപ്പറേഷൻസ് & ഇൻസൈറ്റുകൾ',
  'agent.adminHome.section.ops.subtitle': 'ലൈവ് സ്നാപ്ഷോട്ടുകൾ — സമ്പൂർണ്ണ പേജിലേക്ക് ഡ്രിൽ ചെയ്യാൻ ഏതെങ്കിലും കാർഡ് ക്ലിക്ക് ചെയ്യൂ',
  'agent.adminHome.section.gameSettings': 'ഗെയിം ക്രമീകരണങ്ങൾ',
  'agent.adminHome.section.gameSettings.subtitle': 'ഓരോ ഗെയിമും കോൺഫിഗർ ചെയ്യൂ',
  'agent.adminHome.section.downline': 'എന്റെ ഡൗൺലൈൻ ഇൻസൈറ്റുകൾ',
  'agent.adminHome.section.downline.subtitle': 'സമ്പൂർണ്ണ പേജിലേക്ക് ഡ്രിൽ ചെയ്യാൻ ഏതെങ്കിലും കാർഡ് ക്ലിക്ക് ചെയ്യൂ',

  // Admin wallet cards (4× cards on the dashboard).
  'agent.adminHome.admin.card.players.title': 'പ്ലെയർമാരുടെ വാലറ്റ്',
  'agent.adminHome.admin.card.players.headlineLabel': 'റേഞ്ചിൽ റീചാർജ് ചെയ്തത്',
  'agent.adminHome.admin.card.players.flow.recharges': 'റീചാർജുകൾ',
  'agent.adminHome.admin.card.players.flow.withdrawals': 'പിൻവലിക്കലുകൾ',
  'agent.adminHome.admin.card.players.pending.recharges': 'തീരുമാനമാകാത്ത റീചാർജുകൾ',
  'agent.adminHome.admin.card.players.pending.withdrawals': 'തീരുമാനമാകാത്ത പിൻവലിക്കലുകൾ',
  'agent.adminHome.admin.card.players.stat.total': 'മൊത്തം പ്ലെയർമാർ',
  'agent.adminHome.admin.card.players.stat.active': 'സജീവം',
  'agent.adminHome.admin.card.players.stat.suspended': 'സസ്പെൻഡ് ചെയ്തു',

  'agent.adminHome.admin.card.agents.title': 'ഏജന്റുമാരുടെ വാലറ്റ്',
  'agent.adminHome.admin.card.agents.headlineLabel': 'ഏജന്റ് പൂളുകളിൽ',
  'agent.adminHome.admin.card.agents.flow.topups': 'ടോപ്പ്അപ്പുകൾ (അഡ്മിൻ → ഏജന്റ്)',
  'agent.adminHome.admin.card.agents.flow.outflow': 'ഔട്ട്ഫ്ലോ (പണമടച്ചത് / ടോപ്പ്അപ്പ്)',
  'agent.adminHome.admin.card.agents.pending.withdrawals': 'തീരുമാനമാകാത്ത ഏജന്റ് പിൻവലിക്കലുകൾ',
  'agent.adminHome.admin.card.agents.stat.total': 'മൊത്തം ഏജന്റുമാർ',
  'agent.adminHome.admin.card.agents.stat.active': 'സജീവം',
  'agent.adminHome.admin.card.agents.stat.suspended': 'സസ്പെൻഡ് ചെയ്തു',

  'agent.adminHome.admin.card.bots.title': 'ബോട്ടുകളുടെ വാലറ്റ്',
  'agent.adminHome.admin.card.bots.headlineLabel': 'ബോട്ട് വാലറ്റുകളിൽ',
  'agent.adminHome.admin.card.bots.stat.total': 'മൊത്തം ബോട്ടുകൾ',
  'agent.adminHome.admin.card.bots.stat.active': 'സജീവം',
  'agent.adminHome.admin.card.bots.stat.matches': 'മത്സരങ്ങൾ',

  'agent.adminHome.admin.card.rewards.title': 'പുരസ്കാരങ്ങൾ',
  'agent.adminHome.admin.card.rewards.headlineLabel': 'ഈ ആഴ്ച പണമടച്ചത്',
  'agent.adminHome.admin.card.rewards.pending.claims': 'തീരുമാനമാകാത്ത ക്ലെയിമുകൾ',
  'agent.adminHome.admin.card.rewards.stat.activeOffers': 'സജീവ ഓഫറുകൾ',
  'agent.adminHome.admin.card.rewards.stat.bulkJobs': 'ബൾക്ക് ജോലികൾ',

  // Game tile (per-game row).
  'agent.adminHome.gameTile.matches': 'മത്സരങ്ങൾ',
  // Game registry display labels.
  'agent.adminHome.game.ludo': 'ലൂഡോ',
  'agent.adminHome.game.xo': 'XO',
  'agent.adminHome.game.rummy': 'റമ്മി',
  'agent.adminHome.game.carrom': 'കാരം',
  'agent.adminHome.game.chess': 'ചെസ്സ്',

  // Activity row (Top players / Top agents / Recent transactions).
  'agent.adminHome.activity.topPlayers.title': 'ടോപ്പ് പ്ലെയർമാർ',
  'agent.adminHome.activity.topPlayers.empty': 'ഈ റേഞ്ചിൽ മത്സരങ്ങളില്ല.',
  'agent.adminHome.activity.topPlayers.metric': '{count} മത്സരങ്ങൾ',
  'agent.adminHome.activity.topAgents.title': 'ടോപ്പ് ഏജന്റുമാർ',
  'agent.adminHome.activity.topAgents.subtitle': 'സ്വന്തമാക്കിയ പ്ലെയർമാർ അനുസരിച്ച്',
  'agent.adminHome.activity.topAgents.empty': 'ഇതുവരെ സജീവ ഏജന്റുമാരില്ല.',
  'agent.adminHome.activity.topAgents.metric': '{count} പ്ലെയർമാർ',
  'agent.adminHome.activity.recent.title': 'സമീപകാല ഇടപാടുകൾ',
  'agent.adminHome.activity.recent.subtitle': 'അവസാന 5',
  'agent.adminHome.activity.recent.empty': 'ഇതുവരെ ഇടപാടുകളില്ല.',

  // Ops & insights row.
  'agent.adminHome.ops.topEarners.title': 'ടോപ്പ് വരുമാനക്കാർ (7d)',
  'agent.adminHome.ops.topEarners.subtitle': 'ട്രൂ നെറ്റ് അനുസരിച്ച്',
  'agent.adminHome.ops.topEarners.empty': 'കഴിഞ്ഞ 7 ദിവസത്തിൽ വരുമാനമില്ല.',
  'agent.adminHome.ops.liveActivity.title': 'ലൈവ് പ്രവർത്തനം',
  'agent.adminHome.ops.liveActivity.subtitle': 'പ്ലാറ്റ്ഫോം മുഴുവനായുള്ള അവസാന 5 ഇവന്റുകൾ',
  'agent.adminHome.ops.liveActivity.empty': 'ശാന്തം — കഴിഞ്ഞ 24h-ൽ ഇവന്റുകളില്ല.',
  'agent.adminHome.ops.botPerf.title': 'ബോട്ട് പ്രകടനം (7d)',
  'agent.adminHome.ops.botPerf.subtitle': 'ഓരോ കാറ്റഗറിയിലെയും P&L',
  'agent.adminHome.ops.rateChanges.title': 'സമീപകാല റേറ്റ് മാറ്റങ്ങൾ',
  'agent.adminHome.ops.rateChanges.subtitle': 'ഏറ്റവും പുതിയ പങ്കാളിത്ത എഡിറ്റുകൾ',
  'agent.adminHome.ops.rateChanges.empty': 'ഇതുവരെ റേറ്റ്-മാറ്റ ഇവന്റുകളില്ല.',
  'agent.adminHome.ops.opsHealth.title': 'ഓപ്പറേഷൻസ് ഹെൽത്ത്',
  'agent.adminHome.ops.opsHealth.subtitle': 'ഡെഡ്-ലെറ്റർ ക്യൂ · തീരുമാനമാകാത്ത സെറ്റിൽമെന്റുകൾ',

  // ── Activity feed sentences (Group 8 directive #4 — compound
  //    history copy). Each event kind composes from 2-4 building
  //    blocks so a translator can re-order them per language.
  //
  //    "MATCH: <agent> earned ₹X on <game>" → 3 pieces:
  //       agent (substituted), earnedVerb, amount, onGameSuffix
  //    "RECHARGE: Recharge approved — ₹X via @agent" → 4 pieces:
  //       label, state (substituted), separator + amount, viaSuffix
  //    "PARTNERSHIP: Rate change @agent <old>% → <new>%" → 2 pieces:
  //       label, agent (substituted)
  'agent.adminHome.feed.match.earnedVerb': 'നേടി',
  'agent.adminHome.feed.match.onGameSuffix': '{game}-ൽ',
  'agent.adminHome.feed.recharge.label': 'റീചാർജ്',
  'agent.adminHome.feed.recharge.amountInfix': '— ₹{amount}',
  'agent.adminHome.feed.recharge.viaSuffix': 'വഴി',
  'agent.adminHome.feed.rateChange.label': 'റേറ്റ് മാറ്റം',
  'agent.adminHome.feed.cascadeBadge': 'കാസ്കേഡ്',
  // State value translations for recharge feed lines.
  'agent.adminHome.feed.recharge.state.approved': 'അംഗീകരിച്ചു',
  'agent.adminHome.feed.recharge.state.rejected': 'നിരസിച്ചു',
  'agent.adminHome.feed.recharge.state.pending': 'തീരുമാനമാകാത്തത്',
  'agent.adminHome.feed.recharge.state.paid': 'പണമടച്ചു',
  'agent.adminHome.feed.recharge.state.expired': 'കാലഹരണപ്പെട്ടു',

  // Leaderboard mini — agent row metadata: "L{level} · {n} match(es)".
  'agent.adminHome.leaderboard.matches.one': '{count} മത്സരം',
  'agent.adminHome.leaderboard.matches.other': '{count} മത്സരങ്ങൾ',
  'agent.adminHome.leaderboard.levelPrefix': 'L{level}',

  // Bot matrix mini.
  'agent.adminHome.botMatrix.profitLabel': 'പ്ലാറ്റ്ഫോം ലാഭം (7d)',
  'agent.adminHome.botMatrix.cat.easy': 'എളുപ്പം',
  'agent.adminHome.botMatrix.cat.medium': 'ഇടത്തരം',
  'agent.adminHome.botMatrix.cat.hard': 'കഠിനം',
  // {matches} short form — "12m" on a single line.
  'agent.adminHome.botMatrix.matchesShort': '{matches}m',
  'agent.adminHome.botMatrix.activeSummary': '{total} ബോട്ടുകളിൽ {active} സജീവം',
  'agent.adminHome.botMatrix.loading': 'ലോഡ് ചെയ്യുന്നു…',

  // Ops health mini.
  'agent.adminHome.opsHealth.dlqClean.title': 'DLQ വൃത്തിയാണ്',
  'agent.adminHome.opsHealth.dlqClean.body': 'പരാജയപ്പെട്ട സെറ്റിൽമെന്റ് ജോലികളില്ല.',
  'agent.adminHome.opsHealth.dlqDirty.title.one': '{count} തീരുമാനമാകാത്ത DLQ ജോലി',
  'agent.adminHome.opsHealth.dlqDirty.title.other': '{count} തീരുമാനമാകാത്ത DLQ ജോലികൾ',
  'agent.adminHome.opsHealth.dlqDirty.body': 'വീണ്ടും ശ്രമിക്കാൻ / പരിഹരിക്കാൻ ക്ലിക്ക് ചെയ്യൂ.',
  'agent.adminHome.opsHealth.replay.eyebrow': 'റീപ്ലേ',
  'agent.adminHome.opsHealth.replay.body': 'സെറ്റിൽമെന്റ് വീണ്ടും റൺ ചെയ്യൂ',
  'agent.adminHome.opsHealth.reassign.eyebrow': 'പുനർനിയോഗം',
  'agent.adminHome.opsHealth.reassign.body': 'ബൾക്ക് മൂവ്',

  // ── Agent variant (legacy inline AgentDashboard — left for the
  //    case where role='agent' but the user falls through to this
  //    file's local function). The real agent dashboard lives in
  //    AgentDashboard.tsx.
  'agent.adminHome.agentDash.hero.label': 'എന്റെ പൂൾ ബാലൻസ്',
  'agent.adminHome.agentDash.hero.netPrefix': 'നെറ്റ്',
  'agent.adminHome.agentDash.action.myWallet': 'എന്റെ വാലറ്റ്',
  'agent.adminHome.agentDash.action.myPlayers': 'എന്റെ പ്ലെയർമാർ',
  'agent.adminHome.agentDash.snapshot.myMatches': 'എന്റെ മത്സരങ്ങൾ',
  'agent.adminHome.agentDash.snapshot.active': 'സജീവം',
  'agent.adminHome.agentDash.snapshot.netRange': 'നെറ്റ് (റേഞ്ച്)',
  'agent.adminHome.agentDash.snapshot.pending': 'തീരുമാനമാകാത്തത്',
  // Agent wallet cards.
  'agent.adminHome.agentDash.card.players.title': 'എന്റെ പ്ലെയർമാരുടെ വാലറ്റ്',
  'agent.adminHome.agentDash.card.players.headlineLabel': 'റേഞ്ചിൽ റീചാർജ് ചെയ്തത്',
  'agent.adminHome.agentDash.card.players.stat.total': 'എന്റെ പ്ലെയർമാർ',
  'agent.adminHome.agentDash.card.wallet.title': 'എന്റെ വാലറ്റ്',
  'agent.adminHome.agentDash.card.wallet.headlineLabel': 'പൂൾ ബാലൻസ്',
  'agent.adminHome.agentDash.card.wallet.flow.earned': 'നേടിയത് (കമ്മീഷനുകൾ)',
  'agent.adminHome.agentDash.card.wallet.flow.paidOut': 'പണമടച്ചത്',
  'agent.adminHome.agentDash.card.wallet.pending': 'എന്റെ തീരുമാനമാകാത്ത പിൻവലിക്കൽ',
  'agent.adminHome.agentDash.card.wallet.stat.topups': 'ലഭിച്ച ടോപ്പ്അപ്പുകൾ',
  'agent.adminHome.agentDash.card.wallet.stat.netRange': 'നെറ്റ് (റേഞ്ച്)',
  'agent.adminHome.agentDash.card.activity.title': 'പ്രവർത്തനം',
  'agent.adminHome.agentDash.card.activity.headlineLabel': 'റേഞ്ചിലെ എന്റെ മത്സരങ്ങൾ',
  'agent.adminHome.agentDash.card.activity.stat.topSpender': 'ടോപ്പ് സ്പെൻഡർ',
  'agent.adminHome.agentDash.card.activity.stat.theirSpend': 'അവരുടെ ചെലവ്',
  'agent.adminHome.agentDash.card.activity.stat.pending': 'തീരുമാനമാകാത്ത ഇനങ്ങൾ',

  // Agent activity (legacy variant).
  'agent.adminHome.agentDash.activity.topSpenders.title': 'ടോപ്പ് സ്പെൻഡർമാർ',
  'agent.adminHome.agentDash.activity.topSpenders.empty': 'ഈ റേഞ്ചിൽ ഇതുവരെ ചെലവില്ല.',
  'agent.adminHome.agentDash.activity.recent.title': 'സമീപകാല പ്രവർത്തനം',
  'agent.adminHome.agentDash.activity.recent.subtitle': 'നിങ്ങളുടെ പ്ലെയർമാരിൽ നിന്നുള്ള അവസാന 5',
  'agent.adminHome.agentDash.activity.recent.empty': 'ഇതുവരെ ഇടപാടുകളില്ല.',

  // Agent insights row (legacy variant).
  'agent.adminHome.agentDash.insights.topEarners.title': 'ടോപ്പ് വരുമാനക്കാർ (7d)',
  'agent.adminHome.agentDash.insights.topEarners.subtitle': 'നിങ്ങളുടെ സബ്-ഏജന്റുമാർ റാങ്ക് ചെയ്തത്',
  'agent.adminHome.agentDash.insights.downline.title': 'ഡൗൺലൈൻ പ്രവർത്തനം',
  'agent.adminHome.agentDash.insights.downline.subtitle': 'നിങ്ങളുടെ ഡൗൺലൈനിൽ നിന്നുള്ള അവസാന 5 ഇവന്റുകൾ',
  'agent.adminHome.agentDash.insights.deposit.title': 'ഡെപ്പോസിറ്റ് ലെഡ്ജർ',
  'agent.adminHome.agentDash.insights.deposit.subtitle': 'സമീപകാല ഡെപ്പോസിറ്റ് ക്രെഡിറ്റുകൾ / റീഫണ്ടുകൾ',
  'agent.adminHome.agentDash.insights.deposit.empty': 'ഇതുവരെ ഡെപ്പോസിറ്റ് പ്രവർത്തനങ്ങളില്ല.',

  // ─── AgentSubAgentDetail (per-sub-agent edit page) ────────────────
  // 5-tab page: Identity, Players, Wallet, Rates, Operations. Each tab
  // is its own translator-scoped surface so the verb forms can drift
  // without leaking across.
  'agent.subAgentDetail.title': 'സബ്-ഏജന്റ്',
  'agent.subAgentDetail.subtitle': 'ഡയറക്റ്റ് സബ്-ഏജന്റ്',
  'agent.subAgentDetail.error.loadFailed': 'സബ്-ഏജന്റ് ലോഡ് ചെയ്യുന്നതിൽ പരാജയം',
  'agent.subAgentDetail.backToList': 'സബ്-ഏജന്റുമാരുടെ ലിസ്റ്റിലേക്ക് മടങ്ങുക',
  'agent.subAgentDetail.loading': 'ലോഡ് ചെയ്യുന്നു…',
  // Tab labels.
  'agent.subAgentDetail.tab.identity': 'ഐഡന്റിറ്റി',
  'agent.subAgentDetail.tab.players': 'പ്ലെയർമാർ',
  'agent.subAgentDetail.tab.wallet': 'വാലറ്റ്',
  'agent.subAgentDetail.tab.rates': 'റേറ്റുകൾ',
  'agent.subAgentDetail.tab.ops': 'ഓപ്പറേഷൻസ്',
  // Header card.
  'agent.subAgentDetail.header.players.one': '{count} പ്ലെയർ',
  'agent.subAgentDetail.header.players.other': '{count} പ്ലെയർമാർ',
  'agent.subAgentDetail.header.pool': 'പൂൾ: {amount}',
  'agent.subAgentDetail.header.lastLogin': 'അവസാന ലോഗിൻ {when}',
  // Status pill (shared across tabs).
  'agent.subAgentDetail.status.active': 'സജീവം',
  'agent.subAgentDetail.status.suspended': 'സസ്പെൻഡ് ചെയ്തു',

  // ── Identity tab — Row labels.
  'agent.subAgentDetail.identity.row.uaid': 'UAID',
  'agent.subAgentDetail.identity.row.username': 'യൂസർനെയിം',
  'agent.subAgentDetail.identity.row.email': 'ഇമെയിൽ',
  'agent.subAgentDetail.identity.row.status': 'സ്റ്റാറ്റസ്',
  'agent.subAgentDetail.identity.row.parent': 'പേരന്റ് ഏജന്റ്',
  'agent.subAgentDetail.identity.parentValue': '@{username} (നിങ്ങൾ)',
  'agent.subAgentDetail.identity.parentEmpty': '—',
  'agent.subAgentDetail.identity.row.ownedPlayers': 'സ്വന്തമാക്കിയ പ്ലെയർമാർ',
  'agent.subAgentDetail.identity.row.wallet': 'വാലറ്റ് ബാലൻസ്',
  'agent.subAgentDetail.identity.row.created': 'സൃഷ്ടിച്ചത്',
  'agent.subAgentDetail.identity.row.lastLogin': 'അവസാന ലോഗിൻ',
  'agent.subAgentDetail.identity.row.identityLocked': 'ഐഡന്റിറ്റി പൂട്ടി',
  'agent.subAgentDetail.identity.identityLocked.yes': 'അതെ (അഡ്മിൻ-മാത്രം എഡിറ്റുകൾ)',
  'agent.subAgentDetail.identity.identityLocked.no': 'ഇല്ല (സബ്-ഏജന്റിന് സ്വയം എഡിറ്റ് ചെയ്യാം)',
  'agent.subAgentDetail.identity.row.receiving': 'പ്ലെയർമാരെ സ്വീകരിക്കുന്നു',
  // Opt-out toggle. Parallel state keys + toast variants.
  'agent.subAgentDetail.optOut.accepting': 'സ്വീകരിക്കുന്നു',
  'agent.subAgentDetail.optOut.optedOut': 'ഒഴിവായി',
  'agent.subAgentDetail.optOut.toast.accepting': 'സബ്-ഏജന്റ് ഇപ്പോൾ പ്ലെയർമാരെ സ്വീകരിക്കുന്നു',
  'agent.subAgentDetail.optOut.toast.optedOut': 'സബ്-ഏജന്റ് ലൊക്കേറ്ററിൽ നിന്ന് ഒഴിവായി',
  'agent.subAgentDetail.optOut.error.updateFailed': 'അപ്‌ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
  // Location editor card.
  'agent.subAgentDetail.location.title': 'ലൊക്കേഷൻ',
  'agent.subAgentDetail.location.lastUpdated': '· അവസാനം അപ്‌ഡേറ്റ് ചെയ്തത് {date}',
  'agent.subAgentDetail.location.cta.save': 'ലൊക്കേഷൻ സേവ് ചെയ്യൂ',
  'agent.subAgentDetail.location.nothingToSave': 'സേവ് ചെയ്യാൻ ഒന്നുമില്ല',
  'agent.subAgentDetail.location.toast.saved.one': 'ലൊക്കേഷൻ സേവ് ചെയ്തു ({count} ഫീൽഡ്)',
  'agent.subAgentDetail.location.toast.saved.other': 'ലൊക്കേഷൻ സേവ് ചെയ്തു ({count} ഫീൽഡുകൾ)',
  'agent.subAgentDetail.location.hint': 'Save പ്രവർത്തനക്ഷമമാക്കാൻ വ്യത്യസ്ത സംസ്ഥാനം / ജില്ല / പ്രദേശം തിരഞ്ഞെടുക്കൂ. (സബ്-ഏജന്റ് സ്വയം അവരുടെ ആദ്യ ലോഗിനിലെ നിർബന്ധിത പോപ്പ്-അപ്പ് വഴി കൃത്യമായ GPS ഓവർലേ ചെയ്യും.)',

  // ── Players tab.
  'agent.subAgentDetail.players.empty': 'ഈ സബ്-ഏജന്റ് ഇതുവരെ പ്ലെയർമാരാരെയും സ്വന്തമാക്കിയിട്ടില്ല.',
  'agent.subAgentDetail.players.count.one': '{count} പ്ലെയർ',
  'agent.subAgentDetail.players.count.other': '{count} പ്ലെയർമാർ',

  // ── Wallet tab.
  'agent.subAgentDetail.wallet.balanceLabel': 'നിലവിലെ സബ്-ഏജന്റ് ബാലൻസ്',
  'agent.subAgentDetail.wallet.ledger.title': '@{username}-ന്റെ സമ്പൂർണ്ണ വാലറ്റ് ലെഡ്ജർ',
  'agent.subAgentDetail.wallet.ledger.subtitle': 'ഓരോ നീക്കവും · സബ്-ഏജന്റ് അവരുടെ വാലറ്റിൽ ഇതേത് കാണുന്നു',
  'agent.subAgentDetail.wallet.ledger.empty': 'ഇതുവരെ വാലറ്റ് പ്രവർത്തനങ്ങളില്ല.',
  'agent.subAgentDetail.wallet.ledger.youThemBadge': 'നിങ്ങൾ ↔ അവർ',
  'agent.subAgentDetail.wallet.ledger.balanceAfter': 'ബാൽ ₹{amount}',
  // KIND_META labels.
  'agent.subAgentDetail.wallet.kind.topupFromAdmin': 'അഡ്മിനിൽ നിന്ന് ടോപ്പ്അപ്പ്',
  'agent.subAgentDetail.wallet.kind.topupToPlayer': 'പ്ലെയറിലേക്ക് പേഔട്ട്',
  'agent.subAgentDetail.wallet.kind.topupFromPlayer': 'പ്ലെയർ റീചാർജ്',
  'agent.subAgentDetail.wallet.kind.revenueShare': 'റവന്യൂ ഷെയർ',
  'agent.subAgentDetail.wallet.kind.rechargeCommission': 'റീചാർജ് കമ്മീഷൻ',
  'agent.subAgentDetail.wallet.kind.coFundOffer': 'കോ-ഫണ്ട് ഓഫർ',
  'agent.subAgentDetail.wallet.kind.adminAdjust': 'അഡ്മിൻ ക്രമീകരണം',
  'agent.subAgentDetail.wallet.kind.withdrawalHold': 'പിൻവലിക്കൽ ഹോൾഡ്',
  'agent.subAgentDetail.wallet.kind.withdrawalRefund': 'പിൻവലിക്കൽ റീഫണ്ട്',
  'agent.subAgentDetail.wallet.kind.platformFeeShare': 'പ്ലാറ്റ്ഫോം ഫീ ഷെയർ',
  'agent.subAgentDetail.wallet.kind.winLossShare': 'വിൻ/ലോസ് ഷെയർ',

  // "Pulling coins back?" hint card. Sentence is split into three
  // pieces because the inline username + "Finance > Sub-agents" link
  // must keep their references intact.
  'agent.subAgentDetail.wallet.pullback.title': 'കോയിനുകൾ തിരികെ വലിക്കണോ?',
  'agent.subAgentDetail.wallet.pullback.body.before': 'ആവശ്യപ്പെടൂ ',
  'agent.subAgentDetail.wallet.pullback.body.middle': ' സ്വന്തം പൂളിൽ നിന്ന് ഒരു പിൻവലിക്കൽ അഭ്യർത്ഥന സമർപ്പിക്കാൻ. നിങ്ങൾക്ക് അത് (പൂർണ്ണമോ ഭാഗികമോ ആയി) പണമടച്ചതായി മാർക്ക് ചെയ്യാം ',
  'agent.subAgentDetail.wallet.pullback.financeLink': 'ഫിനാൻസ് › സബ്-ഏജന്റുമാർ',
  'agent.subAgentDetail.wallet.pullback.body.after': '.',

  // TransferForm (only topup is used currently; withdraw kept for
  // completeness). Parallel keys per mode.
  'agent.subAgentDetail.transfer.topup.heading': '@{username}-ലേക്ക് കൈമാറുക',
  'agent.subAgentDetail.transfer.withdraw.heading': '@{username}-ൽ നിന്ന് പിൻവലിക്കുക',
  'agent.subAgentDetail.transfer.topup.body': 'ആറ്റോമിക് കൈമാറ്റം: നിങ്ങളുടെ വാലറ്റ് കുറയും, അവരുടേത് കൂടും. രണ്ട് പൂളുകളും നെഗറ്റീവിലേക്ക് പോകാം (സൈൻഡ്-പൂൾ ഡിസൈൻ).',
  'agent.subAgentDetail.transfer.withdraw.body': 'ആറ്റോമിക് റിവേഴ്സ് കൈമാറ്റം: അവരുടെ വാലറ്റ് കുറയും, നിങ്ങളുടേത് കൂടും. സബ്-ഏജന്റിന്റെ പേഔട്ട് അഭ്യർത്ഥന സെറ്റിൽ ചെയ്യുമ്പോൾ ഇത് ഉപയോഗിക്കൂ.',
  'agent.subAgentDetail.transfer.amountLabel': 'തുക (₹)',
  'agent.subAgentDetail.transfer.noteLabel': 'കുറിപ്പ് (ഐച്ഛികം)',
  'agent.subAgentDetail.transfer.note.placeholder.topup': 'ഉദാ. പ്രതിവാര ടോപ്പ്-അപ്പ്',
  'agent.subAgentDetail.transfer.note.placeholder.withdraw': 'ഉദാ. പേഔട്ട് സെറ്റിൽമെന്റ്',
  'agent.subAgentDetail.transfer.error.amountPositive': 'തുക ഒരു പോസിറ്റീവ് പൂർണ്ണസംഖ്യയായിരിക്കണം.',
  'agent.subAgentDetail.transfer.confirm.topup': '{amount} നിങ്ങളുടെ പൂളിൽ നിന്ന് @{username}-ലേക്ക് കൈമാറണോ? നിങ്ങളുടെ ബാലൻസ് കൃത്യമായി ഈ തുകയിൽ കുറയും.',
  'agent.subAgentDetail.transfer.confirm.withdraw': '{amount} @{username}-ന്റെ പൂളിൽ നിന്ന് നിങ്ങളുടേതിലേക്ക് തിരികെ വലിക്കണോ? അവരുടെ ബാലൻസ് കൃത്യമായി ഈ തുകയിൽ കുറയും.',
  'agent.subAgentDetail.transfer.toast.topup': '{amount} @{username}-ലേക്ക് അയച്ചു. അവരുടെ ബാലൻസ്: {childBalance}.',
  'agent.subAgentDetail.transfer.toast.withdraw': '{amount} @{username}-ൽ നിന്ന് തിരികെ വലിച്ചു. അവരുടെ ബാലൻസ്: {childBalance}.',
  'agent.subAgentDetail.transfer.cta.working': 'പ്രവർത്തിക്കുന്നു…',
  'agent.subAgentDetail.transfer.cta.suspended': 'സബ്-ഏജന്റ് സസ്പെൻഡ് ചെയ്തു',
  'agent.subAgentDetail.transfer.cta.topup': '{amount} കൈമാറുക',
  'agent.subAgentDetail.transfer.cta.withdraw': '{amount} പിൻവലിക്കുക',

  // ── Rates tab.
  'agent.subAgentDetail.rates.loading': 'റേറ്റുകൾ ലോഡ് ചെയ്യുന്നു…',
  // Intro paragraph — sentence is split so the inline <b>ceiling</b>
  // and <b>Apply</b> emphasis can drift in word order per language.
  'agent.subAgentDetail.rates.intro.part1': 'ഓരോ കാർഡും ഒരു കമ്മീഷൻ പിലർ ആണ്; ഉള്ളിലെ ഓരോ വരിയും ഒരു (പിലർ × ഗെയിം) ഷെയർ ആണ്. ',
  'agent.subAgentDetail.rates.intro.ceiling': 'സീലിംഗ്',
  'agent.subAgentDetail.rates.intro.part2': ' ഓരോ ഇൻപുട്ടിന് അടുത്തായി കാണിക്കുന്നത് നിങ്ങളുടെ സ്വന്തം റേറ്റ് ആണ് — ഗോൾഡൻ ലിമിറ്റ് അനുസരിച്ച്, അതിലും കൂടുതൽ കുട്ടിക്ക് അനുവദിക്കാൻ കഴിയില്ല. നിങ്ങൾ മാറ്റുന്ന ഏത് മൂല്യവും എഡിറ്റ് ചെയ്യാവുന്നതാകും; കമ്മിറ്റ് ചെയ്യാൻ ക്ലിക്ക് ചെയ്യൂ ',
  'agent.subAgentDetail.rates.intro.applyWord': 'Apply',
  'agent.subAgentDetail.rates.intro.part3': '. കാസ്കേഡ് ക്ലാമ്പ് സെർവർ-സൈഡിൽ നടപ്പിലാക്കും.',
  'agent.subAgentDetail.rates.toast.applied': 'റേറ്റുകൾ പ്രയോഗിച്ചു.',
  'agent.subAgentDetail.rates.confirm.one': '{count} റേറ്റ് മാറ്റം പ്രയോഗിക്കണോ? ഗോൾഡൻ-ലിമിറ്റ് + കാസ്കേഡ് ക്ലാമ്പ് സെർവർ-സൈഡിൽ നടപ്പിലാക്കും.',
  'agent.subAgentDetail.rates.confirm.other': '{count} റേറ്റ് മാറ്റങ്ങൾ പ്രയോഗിക്കണോ? ഗോൾഡൻ-ലിമിറ്റ് + കാസ്കേഡ് ക്ലാമ്പ് സെർവർ-സൈഡിൽ നടപ്പിലാക്കും.',
  'agent.subAgentDetail.rates.error.blocked': 'തടഞ്ഞു: {detail}',
  // Pillar labels.
  'agent.subAgentDetail.rates.pillar.platformFee': 'പ്ലാറ്റ്ഫോം ഫീ %',
  'agent.subAgentDetail.rates.pillar.winLoss': 'വിൻ/ലോസ് ലാഭം %',
  'agent.subAgentDetail.rates.pillar.offerReward': 'ഓഫർ പുരസ്കാരം %',
  // Per-row.
  'agent.subAgentDetail.rates.row.globalLabel': '— ഗ്ലോബൽ —',
  'agent.subAgentDetail.rates.row.ceilingSuffix': '/ സീലിംഗ് {pct}%',
  'agent.subAgentDetail.rates.row.exceedsBadge': 'പേരന്റിനെ കവിയുന്നു (ഗോൾഡൻ ലിമിറ്റ്)',
  'agent.subAgentDetail.rates.row.editedBadge': 'എഡിറ്റ് ചെയ്തു',
  // CTA bar.
  'agent.subAgentDetail.rates.cta.discard.one': '{count} മാറ്റം ഉപേക്ഷിക്കുക',
  'agent.subAgentDetail.rates.cta.discard.other': '{count} മാറ്റങ്ങൾ ഉപേക്ഷിക്കുക',
  'agent.subAgentDetail.rates.cta.apply.one': '{count} മാറ്റം പ്രയോഗിക്കുക',
  'agent.subAgentDetail.rates.cta.apply.other': '{count} മാറ്റങ്ങൾ പ്രയോഗിക്കുക',
  'agent.subAgentDetail.rates.cta.applying': 'പ്രയോഗിക്കുന്നു…',

  // ── Operations tab.
  'agent.subAgentDetail.ops.status.title': 'സ്റ്റാറ്റസ്',
  'agent.subAgentDetail.ops.status.body': 'സസ്പെൻഡ് ചെയ്യുന്നത് സബ്-ഏജന്റിന്റെ ലോഗിനുകളെയും ഡൗൺലൈൻ പ്രവർത്തനങ്ങളെയും, നിങ്ങൾ വീണ്ടും സജീവമാക്കുന്നതുവരെ, തടയും. കാരണം ഐച്ഛികമാണ് പക്ഷേ ഓഡിറ്റ് ട്രെയിലിൽ രേഖപ്പെടും.',
  'agent.subAgentDetail.ops.status.reasonPlaceholder': 'കാരണം (ഐച്ഛികം)',
  'agent.subAgentDetail.ops.status.cta.suspend': 'സബ്-ഏജന്റിനെ സസ്പെൻഡ് ചെയ്യൂ',
  'agent.subAgentDetail.ops.status.cta.unsuspend': 'സബ്-ഏജന്റിന്റെ സസ്പെൻഷൻ നീക്കൂ',
  'agent.subAgentDetail.ops.status.cta.working': 'പ്രവർത്തിക്കുന്നു…',
  // Confirm sentences — separate per direction so each verb stays
  // translator-managed.
  'agent.subAgentDetail.ops.status.confirm.suspend': '@{username}-നെ സസ്പെൻഡ് ചെയ്യണോ? സസ്പെൻഡ് ചെയ്തിരിക്കുമ്പോൾ അവർക്ക് ലോഗിൻ ചെയ്യാനോ പ്രവർത്തിക്കാനോ കഴിയില്ല.',
  'agent.subAgentDetail.ops.status.confirm.unsuspend': '@{username}-ന്റെ സസ്പെൻഷൻ നീക്കണോ? അവർക്ക് സമ്പൂർണ്ണ ആക്സസ് തിരികെ ലഭിക്കും.',
  'agent.subAgentDetail.ops.status.toast.suspended': '@{username} ഇപ്പോൾ സസ്പെൻഡ് ചെയ്തിരിക്കുന്നു.',
  'agent.subAgentDetail.ops.status.toast.active': '@{username} ഇപ്പോൾ സജീവമാണ്.',

  'agent.subAgentDetail.ops.password.title': 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്യൂ',
  'agent.subAgentDetail.ops.password.body': 'സബ്-ഏജന്റിന് പുതിയ പാസ്‌വേഡ് സജ്ജമാക്കുകയും അവരുടെ നിലവിലുള്ള റിഫ്രഷ് ടോക്കണുകൾ പിൻവലിക്കുകയും ചെയ്യും. അവർ പുതിയ പാസ്‌വേഡുമായി വീണ്ടും ലോഗിൻ ചെയ്യാൻ നിർബന്ധിതരാകും. സുരക്ഷിത ചാനൽ വഴി അവരുമായി പങ്കിടൂ.',
  'agent.subAgentDetail.ops.password.placeholder': 'പുതിയ പാസ്‌വേഡ് (കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ)',
  'agent.subAgentDetail.ops.password.confirm': '@{username}-ന്റെ പാസ്‌വേഡ് റീസെറ്റ് ചെയ്യണോ? അവരുടെ നിലവിലുള്ള സെഷനുകൾ അസാധുവാക്കും, പുതിയ പാസ്‌വേഡുമായി അവർ ലോഗിൻ ചെയ്യേണ്ടിവരും.',
  'agent.subAgentDetail.ops.password.toast.reset': 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്തു. പുതിയ പാസ്‌വേഡ് @{username}-മായി പങ്കിടൂ.',
  'agent.subAgentDetail.ops.password.error.tooShort': 'പാസ്‌വേഡ് കുറഞ്ഞത് 8 പ്രതീകങ്ങളായിരിക്കണം.',
  'agent.subAgentDetail.ops.password.cta.reset': 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്യൂ',
  'agent.subAgentDetail.ops.password.cta.resetting': 'റീസെറ്റ് ചെയ്യുന്നു…',

  // ─── AdminUserDetail (per-player edit page) ──────────────────────
  // 6-tab page: Identity, Profile, Wallet, Security, Ownership, Operations.
  // Originally an admin-only page; in agent-frontend the Ownership tab is
  // hidden (agents can't reassign ownership) and the Wallet tab swaps the
  // admin mint/burn form for a "Recharge from your pool" CTA.
  'agent.playerDetail.backToPlayers': 'പ്ലെയർമാരിലേക്ക് മടങ്ങുക',
  'agent.playerDetail.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.playerDetail.error.loadFailed': 'ഉപയോക്താവിനെ ലോഡ് ചെയ്യുന്നതിൽ പരാജയം',

  // Header card.
  'agent.playerDetail.header.noName': '(പേര് സജ്ജമാക്കിയിട്ടില്ല)',
  'agent.playerDetail.header.joined': '{date}-ന് ചേർന്നു',
  'agent.playerDetail.header.ownedBy.prefix': ' · ഉടമ ',
  'agent.playerDetail.header.walletLabel': 'വാലറ്റ്',

  // Status pill (4 states).
  'agent.playerDetail.status.pending': 'തീരുമാനമാകാത്തത്',
  'agent.playerDetail.status.active': 'സജീവം',
  'agent.playerDetail.status.suspended': 'സസ്പെൻഡ് ചെയ്തു',
  'agent.playerDetail.status.banned': 'നിരോധിച്ചു',

  // Tab labels.
  'agent.playerDetail.tab.identity': 'ഐഡന്റിറ്റി',
  'agent.playerDetail.tab.profile': 'പ്രൊഫൈൽ',
  'agent.playerDetail.tab.wallet': 'വാലറ്റ്',
  'agent.playerDetail.tab.security': 'സുരക്ഷ',
  'agent.playerDetail.tab.ownership': 'ഉടമസ്ഥത',
  'agent.playerDetail.tab.operations': 'ഓപ്പറേഷൻസ്',

  // Identity tab.
  'agent.playerDetail.identity.title': 'ഐഡന്റിറ്റി',
  'agent.playerDetail.identity.row.username': 'യൂസർനെയിം',
  'agent.playerDetail.identity.row.email': 'ഇമെയിൽ',
  'agent.playerDetail.identity.row.phone': 'ഫോൺ',
  'agent.playerDetail.identity.row.country': 'രാജ്യം',
  'agent.playerDetail.identity.row.joined': 'ചേർന്നത്',
  'agent.playerDetail.identity.row.emailVerified': 'ഇമെയിൽ സ്ഥിരീകരിച്ചു',
  'agent.playerDetail.identity.row.telegram': 'ടെലിഗ്രാം',
  'agent.playerDetail.identity.row.failedLogins': 'പരാജയപ്പെട്ട ലോഗിനുകൾ',
  'agent.playerDetail.identity.row.lockedUntil': 'പൂട്ടി വരെ',
  'agent.playerDetail.identity.value.yes': 'അതെ',
  'agent.playerDetail.identity.value.no': 'ഇല്ല',
  'agent.playerDetail.identity.value.dash': '—',
  // Transfer-by-UAID block embedded in the Identity tab for agents.
  'agent.playerDetail.identity.transfer.title': 'ഈ പ്ലെയറെ കൈമാറുക',
  'agent.playerDetail.identity.transfer.body': 'Unique Agent ID ഉപയോഗിച്ച് മറ്റൊരു ഏജന്റിന് കൈമാറുക — നിങ്ങളുടെ ഡൗൺലൈൻ മാത്രമല്ല, മൊത്തം നെറ്റ്‌വർക്കിലുടനീളം പ്രവർത്തിക്കും.',
  'agent.playerDetail.identity.transfer.cta': 'UAID വഴി കൈമാറുക',
  // Photo cropper title.
  'agent.playerDetail.avatar.changeTitle': 'ഫോട്ടോ മാറ്റുക',
  'agent.playerDetail.avatar.cropTitle': '@{username}-നായി ഫോട്ടോ ക്രമീകരിക്കുക',
  'agent.playerDetail.avatar.toast.updated': 'ഫോട്ടോ അപ്‌ഡേറ്റ് ചെയ്തു',
  'agent.playerDetail.avatar.error.uploadFailed': 'അപ്‌ലോഡ് പരാജയപ്പെട്ടു',

  // PlayerTransfer post-success toasts (different per role).
  'agent.playerDetail.transfer.toast.admin': 'UAID വഴി പ്ലെയറെ കൈമാറി',
  'agent.playerDetail.transfer.toast.agent': '@{username}-നെ വിജയകരമായി കൈമാറി',

  // ── Status / Ban controls (Operations tab).
  'agent.playerDetail.ops.accountStatus.title': 'അക്കൗണ്ട് സ്റ്റാറ്റസ്',
  'agent.playerDetail.ops.accountStatus.body': 'സജീവം എന്നാൽ പ്ലെയർക്ക് ലോഗിൻ ചെയ്ത് കളിക്കാം എന്നാണ്. സസ്പെൻഡ് ചെയ്യുന്നത് നിങ്ങൾ വീണ്ടും സജീവമാക്കുന്നതുവരെ ലോഗിനുകൾ തടയും. ബാൻ ഒരു ശക്തമായ പൂട്ടാണ് — നയ ലംഘനങ്ങൾക്കായി മാത്രം ഉപയോഗിക്കൂ.',
  'agent.playerDetail.ops.statusBtn.activate': 'സജീവമാക്കുക',
  'agent.playerDetail.ops.statusBtn.suspend': 'സസ്പെൻഡ് ചെയ്യൂ',
  'agent.playerDetail.ops.statusBtn.ban': 'നിരോധിക്കുക',
  'agent.playerDetail.ops.statusBtn.current': 'നിലവിലുള്ളത്',
  'agent.playerDetail.ops.statusBtn.toast': 'ഉപയോക്താവ് ഇപ്പോൾ {status}',

  // Danger zone (admin-only — Delete user).
  'agent.playerDetail.ops.danger.title': 'ഡേഞ്ചർ സോൺ',
  'agent.playerDetail.ops.danger.body': 'ഹാർഡ്-ഡിലീറ്റ് ചെയ്യുന്നത് ഉപയോക്തൃ റെക്കോർഡ് നീക്കം ചെയ്യും. വാലറ്റ് ലെഡ്ജർ ഓഡിറ്റിനായി സംരക്ഷിക്കും, പക്ഷേ പ്ലെയർക്ക് എല്ലാ ആക്സസും നഷ്ടപ്പെടും. ഈ പ്രവൃത്തി പഴയപടിയാക്കാനാവില്ല.',
  'agent.playerDetail.ops.delete.eyebrow': 'ഡേഞ്ചർ സോൺ',
  // Confirm copy is split so the bold {username} can drift in word
  // order; the username is rendered inline as a <b> for emphasis.
  'agent.playerDetail.ops.delete.confirm.before': 'ഈ അക്കൗണ്ട് ശാശ്വതമായി ഇല്ലാതാക്കുക. ഇത് പഴയപടിയാക്കാനാവില്ല. സ്ഥിരീകരിക്കാൻ ടൈപ്പ് ചെയ്യൂ ',
  'agent.playerDetail.ops.delete.confirm.after': '.',
  'agent.playerDetail.ops.delete.cta': 'ഇല്ലാതാക്കുക',
  'agent.playerDetail.ops.delete.toast': 'ഉപയോക്താവിനെ ഇല്ലാതാക്കി',

  // ── Wallet adjust card (admin-only).
  'agent.playerDetail.walletAdjust.title': 'വാലറ്റ് ക്രമീകരിക്കുക',
  'agent.playerDetail.walletAdjust.mode.credit': '+ ക്രെഡിറ്റ്',
  'agent.playerDetail.walletAdjust.mode.debit': '− ഡെബിറ്റ്',
  'agent.playerDetail.walletAdjust.field.amount': 'തുക (₹)',
  'agent.playerDetail.walletAdjust.field.reason': 'കാരണം · ലെഡ്ജറിൽ സംഭരിക്കും',
  'agent.playerDetail.walletAdjust.field.reason.placeholder': 'ഉദാ. ഡെപ്പോസിറ്റ് റിവേഴ്സൽ, ബോണസ്, റീഫണ്ട്',
  'agent.playerDetail.walletAdjust.markAsRecharge.label': 'റീചാർജായി മാർക്ക് ചെയ്യൂ',
  // Sentence is split — the bold <b>recharge</b> emphasis must survive
  // translation word-order changes.
  'agent.playerDetail.walletAdjust.markAsRecharge.body.before': 'ഈ ക്രെഡിറ്റ് ലെഡ്ജറിൽ ',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.bold': 'റീചാർജ്',
  'agent.playerDetail.walletAdjust.markAsRecharge.body.after': ' ആയി രേഖപ്പെടുത്തുക. ഈ ഉപയോക്താവിനെ ആരെങ്കിലും റഫർ ചെയ്തിട്ടുണ്ടെങ്കിൽ, റഫറർക്ക് സ്വയം ഒരു ബോണസ് ലഭിക്കും.',
  'agent.playerDetail.walletAdjust.error.positiveAmount': 'ഒരു പോസിറ്റീവ് തുക നൽകൂ',
  'agent.playerDetail.walletAdjust.error.reasonRequired': 'കാരണം ആവശ്യമാണ്',
  'agent.playerDetail.walletAdjust.cta.applying': 'പ്രയോഗിക്കുന്നു…',
  'agent.playerDetail.walletAdjust.cta.applyCredit': 'ക്രെഡിറ്റ് പ്രയോഗിക്കുക',
  'agent.playerDetail.walletAdjust.cta.applyDebit': 'ഡെബിറ്റ് പ്രയോഗിക്കുക',
  // Toast — parallel verb keys per mode, plus a referral suffix that
  // gets appended when the backend reports a referrer payout.
  'agent.playerDetail.walletAdjust.toast.credited': '{amount} ക്രെഡിറ്റ് ചെയ്തു',
  'agent.playerDetail.walletAdjust.toast.debited': '{amount} ഡെബിറ്റ് ചെയ്തു',
  'agent.playerDetail.walletAdjust.toast.referralSuffix': ' · റഫറർ {bonus} ബോണസ് നേടി',

  // ── Recharge CTA (agent variant — replaces admin mint/burn).
  'agent.playerDetail.rechargeCta.title': 'ഈ പ്ലെയറെ റീചാർജ് ചെയ്യൂ',
  // 4-piece body sentence so the inline username + bold "your wallet"
  // emphasis stays correct in any language word order.
  'agent.playerDetail.rechargeCta.body.before': '@',
  'agent.playerDetail.rechargeCta.body.middle': '-ന് ടോപ്പ് അപ്പ് ചെയ്യുമ്പോൾ കോയിനുകൾ ',
  'agent.playerDetail.rechargeCta.body.bold': 'നിങ്ങളുടെ വാലറ്റിൽ',
  'agent.playerDetail.rechargeCta.body.after': ' നിന്ന് അവരുടേതിലേക്ക് കൈമാറും. നിങ്ങളുടെ പൂൾ ആദ്യം പരിശോധിക്കും — നീക്കം ആറ്റോമിക് ആണ്, അപര്യാപ്തമായ ബാലൻസ് ഉണ്ടെങ്കിൽ നിരസിക്കും.',
  'agent.playerDetail.rechargeCta.thisPlayer': 'ഈ പ്ലെയർ',
  'agent.playerDetail.rechargeCta.cta': 'റീചാർജ് ഫോം തുറക്കൂ',

  // ── Security tab (password reset).
  'agent.playerDetail.security.title': 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്യൂ',
  'agent.playerDetail.security.subtitle': 'ഒരു പുതിയ പാസ്‌വേഡ് സജ്ജമാക്കുകയും എല്ലാ ഉപകരണത്തിൽ നിന്നും ഉപയോക്താവിനെ സൈൻ ഔട്ട് ചെയ്യുകയും ചെയ്യും.',
  'agent.playerDetail.security.field.new': 'പുതിയ പാസ്‌വേഡ്',
  'agent.playerDetail.security.field.confirm': 'സ്ഥിരീകരിക്കുക',
  'agent.playerDetail.security.error.mismatch': 'പാസ്‌വേഡുകൾ പൊരുത്തപ്പെടുന്നില്ല',
  'agent.playerDetail.security.error.tooShort': 'കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ, അക്ഷരങ്ങളും അക്കങ്ങളും സഹിതം',
  'agent.playerDetail.security.toast.reset': 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്തു, എല്ലാ സെഷനുകളും അസാധുവാക്കി',
  'agent.playerDetail.security.cta.reset': 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്യൂ',
  'agent.playerDetail.security.cta.resetting': 'റീസെറ്റ് ചെയ്യുന്നു…',

  // ── Wallet ledger card.
  'agent.playerDetail.ledger.title': 'വാലറ്റ് ലെഡ്ജർ',
  'agent.playerDetail.ledger.entries.one': '{count} എൻട്രി',
  'agent.playerDetail.ledger.entries.other': '{count} എൻട്രികൾ',
  'agent.playerDetail.ledger.empty': 'ഇതുവരെ ഇടപാടുകളില്ല.',
  'agent.playerDetail.ledger.loading': 'ലോഡ് ചെയ്യുന്നു…',
  'agent.playerDetail.ledger.actorSuffix': ' · അഡ്മിൻ #{id} വഴി',
  'agent.playerDetail.ledger.pagination.prev': 'മുമ്പത്തേത്',
  'agent.playerDetail.ledger.pagination.next': 'അടുത്തത്',
  // Ledger kind labels — 12 wallet movement kinds.
  'agent.playerDetail.ledger.kind.bet': 'മത്സര ബെറ്റ്',
  'agent.playerDetail.ledger.kind.win': 'മത്സര സമ്മാനം',
  'agent.playerDetail.ledger.kind.refund': 'റീഫണ്ട്',
  'agent.playerDetail.ledger.kind.fee': 'പ്ലാറ്റ്ഫോം ഫീ',
  'agent.playerDetail.ledger.kind.adminCredit': 'അഡ്മിൻ ക്രെഡിറ്റ്',
  'agent.playerDetail.ledger.kind.adminDebit': 'അഡ്മിൻ ഡെബിറ്റ്',
  'agent.playerDetail.ledger.kind.recharge': 'റീചാർജ്',
  'agent.playerDetail.ledger.kind.referralBonus': 'റഫറൽ ബോണസ്',
  'agent.playerDetail.ledger.kind.signupBonus': 'സൈൻഅപ്പ് ബോണസ്',
  'agent.playerDetail.ledger.kind.offerCredit': 'ഓഫർ ക്രെഡിറ്റ്',
  'agent.playerDetail.ledger.kind.withdrawalHold': 'പിൻവലിക്കൽ',
  'agent.playerDetail.ledger.kind.withdrawalRefund': 'റീഫണ്ട്',
  // Game badge in ledger rows (id='platform' → 'Platform').
  'agent.playerDetail.ledger.game.platform': 'പ്ലാറ്റ്ഫോം',

  // ── Profile editor.
  'agent.playerDetail.profile.title': 'പ്രൊഫൈൽ എഡിറ്റ് ചെയ്യൂ',
  'agent.playerDetail.profile.field.username': 'യൂസർനെയിം',
  'agent.playerDetail.profile.field.email': 'ഇമെയിൽ',
  'agent.playerDetail.profile.field.phone': 'ഫോൺ',
  'agent.playerDetail.profile.field.firstName': 'ആദ്യ പേര്',
  'agent.playerDetail.profile.field.lastName': 'അവസാന പേര്',
  'agent.playerDetail.profile.field.dob': 'ജനന തീയതി',
  'agent.playerDetail.profile.field.gender': 'ലിംഗം',
  // Gender select options.
  'agent.playerDetail.profile.gender.unspecified': '—',
  'agent.playerDetail.profile.gender.male': 'പുരുഷൻ',
  'agent.playerDetail.profile.gender.female': 'സ്ത്രീ',
  'agent.playerDetail.profile.gender.other': 'മറ്റുള്ളവ',
  'agent.playerDetail.profile.gender.preferNot': 'പറയാൻ താൽപര്യമില്ല',
  // Location section.
  'agent.playerDetail.profile.location.title': 'ലൊക്കേഷൻ',
  'agent.playerDetail.profile.location.lastUpdated': '· അവസാനം അപ്‌ഡേറ്റ് ചെയ്തത് {date}',
  'agent.playerDetail.profile.location.legacy.label': 'ഫയലിലുള്ള ലെഗസി വിലാസം',
  'agent.playerDetail.profile.location.legacy.note': ' (റീഡ്-ഒൺലി — പ്രീ-R3, ലൊക്കേറ്റർ ഉപയോഗിക്കുന്നില്ല):',
  // Save / toast / reset.
  'agent.playerDetail.profile.toast.nothingToSave': 'സേവ് ചെയ്യാൻ ഒന്നുമില്ല',
  'agent.playerDetail.profile.toast.saved.one': '{count} ഫീൽഡ് സേവ് ചെയ്തു',
  'agent.playerDetail.profile.toast.saved.other': '{count} ഫീൽഡുകൾ സേവ് ചെയ്തു',
  'agent.playerDetail.profile.cta.save': 'മാറ്റങ്ങൾ സേവ് ചെയ്യൂ',
  'agent.playerDetail.profile.cta.saving': 'സേവ് ചെയ്യുന്നു…',
  'agent.playerDetail.profile.cta.reset': 'റീസെറ്റ്',

  // ── Ownership card (admin-only tab).
  'agent.playerDetail.ownership.title': 'ഉടമ ഏജന്റ്',
  'agent.playerDetail.ownership.row.eyebrow': 'ഉടമസ്ഥത',
  'agent.playerDetail.ownership.platform.title': 'പ്ലാറ്റ്ഫോം-ഡയറക്റ്റ്',
  'agent.playerDetail.ownership.platform.body': 'ഈ പ്ലെയറുടെ ഉടമയായി ഒരു ഏജന്റും ഇല്ല. അഡ്മിൻ നേരിട്ട് കൈകാര്യം ചെയ്യുന്നു.',
  'agent.playerDetail.ownership.owned.fallbackOwner': 'ഏജന്റ് #{id}',
  'agent.playerDetail.ownership.owned.body': 'ഈ പ്ലെയറുടെ റീചാർജുകളിലും മത്സരങ്ങളിലും കമ്മീഷനുകൾ നേടുന്നു.',
  'agent.playerDetail.ownership.cta.change': 'മാറ്റുക',
  'agent.playerDetail.ownership.toast.changedTo': 'ഉടമസ്ഥത → {label}',
  'agent.playerDetail.ownership.platformLabel': 'പ്ലാറ്റ്ഫോം-ഡയറക്റ്റ്',

  // Ownership picker modal.
  'agent.playerDetail.ownership.picker.title': '@{username}-നെ പുനർനിയോഗിക്കൂ',
  'agent.playerDetail.ownership.picker.body': 'ഒരു പുതിയ ഏജന്റ് തിരഞ്ഞെടുക്കൂ — അല്ലെങ്കിൽ പ്ലാറ്റ്ഫോം-ഡയറക്റ്റിലേക്ക് വേർപ്പെടുത്തൂ (ഈ പ്ലെയറുടെ ഉടമയായി ഒരു ഏജന്റും ഉണ്ടാകില്ല). ഭാവി കമ്മീഷനുകൾ പുതിയ ഉടമയെ പിന്തുടരും; ഇതിനകം ക്രെഡിറ്റ് ചെയ്ത വരികളിൽ തൊടില്ല.',
  'agent.playerDetail.ownership.picker.detach.title': 'പ്ലാറ്റ്ഫോമിലേക്ക് വേർപ്പെടുത്തൂ',
  'agent.playerDetail.ownership.picker.detach.body': 'ഏജന്റ് ഉടമസ്ഥത നീക്കം ചെയ്യൂ. അഡ്മിൻ നേരിട്ട് കൈകാര്യം ചെയ്യും.',
  'agent.playerDetail.ownership.picker.search.placeholder': 'ഏജന്റുമാരെ തിരയൂ…',
  'agent.playerDetail.ownership.picker.loading': 'ഏജന്റുമാരെ ലോഡ് ചെയ്യുന്നു…',
  'agent.playerDetail.ownership.picker.empty.search': 'നിങ്ങളുടെ തിരയലിന് യോജിക്കുന്ന ഏജന്റുമാരില്ല.',
  'agent.playerDetail.ownership.picker.empty.none': 'ഇതുവരെ സജീവ ഏജന്റുമാരില്ല.',
  'agent.playerDetail.ownership.picker.players.one': '{count} പ്ലെയർ',
  'agent.playerDetail.ownership.picker.players.other': '{count} പ്ലെയർമാർ',
  'agent.playerDetail.ownership.picker.current': 'നിലവിലുള്ളത്',
  'agent.playerDetail.ownership.picker.currentUpper': 'നിലവിലുള്ളത്',
  'agent.playerDetail.ownership.picker.close': 'അടയ്ക്കുക',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 9 — Agent login page.
  //
  // The login page is the agent's FIRST touchpoint. Tone is professional
  // and reassuring — no playful copy, no jargon. Auth errors get their
  // own `.error.*` sub-namespace so a translator can audit every "what
  // could go wrong at sign-in" string in one place.
  //
  // Reuses two cross-app keys:
  //   common.signIn     ("Sign in") — the primary CTA
  //   login.signingIn   ("Signing in…") — busy state, already wired
  //                     into the player login so the verb form stays
  //                     consistent across the platform.
  //
  // Agent-specific keys (brand + helper + agent-only suffix line) live
  // under agent.login.*.
  'agent.login.brand': 'ബാലി ഏജന്റ്',
  'agent.login.subtitle': 'നിങ്ങളുടെ പ്ലെയർമാരെ കൈകാര്യം ചെയ്യാൻ സൈൻ ഇൻ ചെയ്യൂ',
  'agent.login.field.identifier': 'യൂസർനെയിം അല്ലെങ്കിൽ ഇമെയിൽ',
  'agent.login.field.password': 'പാസ്‌വേഡ്',
  // Disclaimer sentence is split into 3 pieces so the inline /admin
  // /login link can be re-positioned per language without losing the
  // surrounding sentence structure.
  'agent.login.disclaimer.before': 'ഏജന്റുമാർക്ക് മാത്രം. നിങ്ങൾ പ്ലാറ്റ്ഫോം ഉടമയാണെങ്കിൽ, ഉപയോഗിക്കൂ ',
  'agent.login.disclaimer.link': '/admin/login',
  'agent.login.disclaimer.after': '.',
  // Auth error fallbacks. Server errors flow through as-is; these are
  // the locale-aware fallback strings when the server response doesn't
  // include a friendly message.
  'agent.login.error.loginFailed': 'ലോഗിൻ പരാജയപ്പെട്ടു',
  'agent.login.error.invalidCredentials': 'അസാധുവായ ക്രെഡൻഷ്യലുകൾ',
  'agent.login.error.accountSuspended': 'അക്കൗണ്ട് സസ്പെൻഡ് ചെയ്തു — നിങ്ങളുടെ അഡ്മിനിസ്ട്രേറ്ററെ ബന്ധപ്പെടൂ.',
  'agent.login.error.networkError': 'നെറ്റ്‌വർക്ക് പിശക് — നിങ്ങളുടെ കണക്ഷൻ പരിശോധിച്ച് വീണ്ടും ശ്രമിക്കൂ.',

  // ════════════════════════════════════════════════════════════════════
  // PHASE B GROUP 10 — Long-tail sweep.
  //
  // Catches the lingering hardcoded strings that don't fit any single
  // surface — App shell auth gate, game tile fallback labels, generic
  // empty states. Generic verbs lean on the cross-app `common.*` keys
  // we established in Group 1 (common.loading, common.cancel, ...);
  // everything that's agent-specific lives under agent.misc.*.
  'agent.misc.gameTile.fallbackTag': 'ഗെയിം',
  'agent.misc.gameTile.actionSuffix': '· കോൺഫിഗർ ചെയ്യൂ',
};

export const TRANSLATIONS: Record<Lang, Dict> = {
  en, hi, bn, mr, ta, te, gu, ur, kn, or, pa, ml,
};
